{
  "course": {
    "code": "CS 140",
    "title": "Programming Languages I",
    "subtitle": "JavaScript track",
    "term": "Fall 2026",
    "blurb": "What a language lets you say, and how to say it well. Every outcome is assessable in any supported track; the examples here are JavaScript.\n",
    "footnote": "Two checkpoints along the way; the final re-tests every outcome.",
    "updated": "09 / 2026",
    "outcomes": [
      {
        "n": 1,
        "title": "Types",
        "conversion": null,
        "equality": null
      },
      {
        "n": 2,
        "title": "References and mutability"
      },
      {
        "n": 3,
        "title": "Control flow and iteration"
      },
      {
        "n": 4,
        "title": "Scope and closures"
      },
      {
        "n": 5,
        "title": "Functions as values"
      },
      {
        "n": 6,
        "title": "Object modeling"
      },
      {
        "n": 7,
        "title": "Modules and dependencies"
      },
      {
        "n": 8,
        "title": "Errors and failure"
      },
      {
        "n": 9,
        "title": "Async and runtime"
      },
      {
        "n": 10,
        "title": "Diagnosis and AI evaluation"
      }
    ],
    "orientation": {
      "starts": "2026-09-28",
      "name": "Orientation",
      "blurb": "Do this first. Nothing here is graded.",
      "lessons": [
        {
          "id": "how-this-course-works",
          "title": "How this course works",
          "kind": "read"
        },
        {
          "id": "set-up",
          "title": "Set up where your code runs",
          "kind": "read"
        },
        {
          "id": "how-a-homework-works",
          "title": "How a homework works",
          "kind": "read"
        },
        {
          "id": "choose-a-track",
          "title": "Choose your language track",
          "kind": "read"
        },
        {
          "id": "start-diagnostic",
          "title": "Where you are starting from",
          "kind": "check"
        }
      ]
    },
    "assignments": {
      "org": "WWCC-Ferraro",
      "students_org": "WWCC-Students"
    },
    "modules": [
      {
        "id": "getting-started",
        "name": "First programs",
        "starts": "2026-09-30",
        "blurb": "Where code runs, what a program is, and what to do when it breaks.",
        "outcomes": [
          9,
          10
        ],
        "groups": [
          26,
          1
        ],
        "lessons": [
          {
            "id": "first-program",
            "title": "Your first program",
            "kind": "read"
          },
          {
            "id": "what-js-is",
            "title": "What JavaScript is, and what it is not",
            "kind": "read"
          },
          {
            "id": "where-code-runs",
            "title": "Where your code runs",
            "kind": "read"
          },
          {
            "id": "first-splash",
            "title": "Reading a program you did not write",
            "kind": "read"
          },
          {
            "id": "reading-errors",
            "title": "When it breaks: reading errors",
            "kind": "read"
          },
          {
            "id": "fix-the-random",
            "title": "Fix a bug that never throws",
            "kind": "trial"
          },
          {
            "id": "check-getting-started",
            "title": "First programs check",
            "kind": "check"
          }
        ]
      },
      {
        "id": "types",
        "name": "Values, types and operators",
        "starts": "2026-10-05",
        "blurb": "Numbers, strings and booleans: naming them, combining them, and what the language does when they do not match.",
        "outcomes": [
          1
        ],
        "groups": [
          3,
          4,
          5
        ],
        "assignments": [
          {
            "id": "primitives-game",
            "due": "2026-10-11",
            "title": "Primitives, put to work in a game"
          },
          {
            "id": "types-assignment",
            "due": "2026-10-11",
            "title": "Values, one at a time"
          }
        ],
        "lessons": [
          {
            "id": "variables",
            "title": "Naming values: let and const",
            "kind": "read"
          },
          {
            "id": "operators",
            "title": "Arithmetic operators",
            "kind": "read"
          },
          {
            "id": "function-answers",
            "title": "Writing a function's answer",
            "kind": "read"
          },
          {
            "id": "comparisons",
            "title": "Comparing and combining",
            "kind": "read"
          },
          {
            "id": "strings",
            "title": "Working with strings",
            "kind": "read"
          },
          {
            "id": "where-type-lives",
            "title": "Where the type actually lives",
            "kind": "read"
          },
          {
            "id": "conversion",
            "title": "Conversion — when a value does not fit",
            "kind": "read"
          },
          {
            "id": "equality",
            "title": "Equality: == versus ===",
            "kind": "read"
          },
          {
            "id": "truthiness",
            "title": "Truthiness",
            "kind": "read"
          },
          {
            "id": "absence",
            "title": "When there is nothing there",
            "kind": "read"
          },
          {
            "id": "type-discipline",
            "title": "The type-discipline axis",
            "kind": "read"
          },
          {
            "id": "check-types",
            "title": "Types and values check",
            "kind": "check"
          }
        ]
      },
      {
        "id": "flow",
        "name": "Branching",
        "starts": "2026-10-12",
        "blurb": "Choosing a path: the if statement, the ternary operator, what &&, || and ?? evaluate to, and switch.",
        "outcomes": [
          3
        ],
        "groups": [
          9
        ],
        "assignments": [
          {
            "id": "flow-calendar",
            "due": "2026-10-18",
            "title": "Calendar dates: if, switch, the ternary operator, && and ??"
          }
        ],
        "lessons": [
          {
            "id": "branching",
            "title": "The if statement",
            "kind": "read"
          },
          {
            "id": "ternary",
            "title": "The ternary operator",
            "kind": "read"
          },
          {
            "id": "conditions",
            "title": "What &&, || and ?? evaluate to",
            "kind": "read"
          },
          {
            "id": "multiway",
            "title": "Choosing among many paths",
            "kind": "read"
          },
          {
            "id": "check-flow",
            "title": "Branching check",
            "kind": "check"
          }
        ]
      },
      {
        "id": "loops",
        "name": "Loops",
        "starts": "2026-10-19",
        "blurb": "Repeating work: the while, for, do…while and for…of loops, choosing among them, leaving a loop early with break and continue, and loops inside loops.",
        "outcomes": [
          3
        ],
        "groups": [
          10
        ],
        "assignments": [
          {
            "id": "flow-loops",
            "due": "2026-10-25",
            "title": "Loops that build, count and search"
          },
          {
            "id": "flow-patterns",
            "due": "2026-10-25",
            "title": "Number patterns with loops inside loops"
          }
        ],
        "lessons": [
          {
            "id": "while-loop",
            "title": "The while loop",
            "kind": "read"
          },
          {
            "id": "for-loop",
            "title": "The for loop",
            "kind": "read"
          },
          {
            "id": "do-while",
            "title": "The do…while loop",
            "kind": "read"
          },
          {
            "id": "for-of",
            "title": "The for…of loop",
            "kind": "read"
          },
          {
            "id": "loop-forms",
            "title": "Choosing a loop",
            "kind": "read"
          },
          {
            "id": "break-continue",
            "title": "Leaving a loop early: break and continue",
            "kind": "read"
          },
          {
            "id": "nesting",
            "title": "Loops inside loops",
            "kind": "read"
          },
          {
            "id": "check-loops",
            "title": "Loops check",
            "kind": "check"
          }
        ]
      },
      {
        "id": "arrays",
        "name": "Arrays",
        "starts": "2026-10-26",
        "blurb": "When your data grows past a handful of names.",
        "outcomes": [
          2,
          3
        ],
        "assignments": [
          {
            "id": "arrays-scoreboard",
            "due": null,
            "title": "Arrays at game night: reading, walking, changing and nesting"
          }
        ],
        "lessons": [
          {
            "id": "arrays-basics",
            "title": "Lists of values",
            "kind": "read"
          },
          {
            "id": "changing-arrays",
            "title": "Adding and removing elements",
            "kind": "read"
          },
          {
            "id": "finding-slicing",
            "title": "Finding, slicing and joining",
            "kind": "read"
          },
          {
            "id": "walking-arrays",
            "title": "Walking an array",
            "kind": "read"
          },
          {
            "id": "mutate-traverse",
            "title": "Mutating while traversing",
            "kind": "read"
          },
          {
            "id": "arrays-of-arrays",
            "title": "Arrays of arrays",
            "kind": "read"
          },
          {
            "id": "check-arrays",
            "title": "Arrays check",
            "kind": "check"
          }
        ]
      },
      {
        "id": "objects",
        "name": "Objects",
        "starts": "2026-11-02",
        "blurb": "Packaging several values together under names — a consistent shape for each thing.",
        "outcomes": [
          6
        ],
        "lessons": [
          {
            "id": "objects-basics",
            "title": "Records with named parts",
            "kind": "read"
          },
          {
            "id": "arrays-of-objects",
            "title": "A list of like things",
            "kind": "read"
          },
          {
            "id": "object-keys",
            "title": "Looking up by key",
            "kind": "read"
          }
        ]
      },
      {
        "id": "fn-basics",
        "name": "Functions",
        "starts": "2026-11-02",
        "blurb": "Writing your own functions: parameters, return values, defaults and the rest.",
        "outcomes": [
          3,
          5
        ],
        "groups": [
          2,
          13
        ],
        "lessons": [
          {
            "id": "functions-1",
            "title": "Functions, pass 1 — declare, call, return",
            "kind": "read"
          },
          {
            "id": "params-arity",
            "title": "Parameters, arity and defaults",
            "kind": "read"
          },
          {
            "id": "rest-params",
            "title": "Rest parameters and spread",
            "kind": "read"
          },
          {
            "id": "guard-clauses",
            "title": "Flattening with guard clauses",
            "kind": "read"
          },
          {
            "id": "recursion",
            "title": "Recursion: a function that calls itself",
            "kind": "read"
          },
          {
            "id": "hoisting",
            "title": "Hoisting and the dead zone",
            "kind": "read"
          }
        ]
      },
      {
        "id": "scope",
        "name": "Scope",
        "starts": "2026-11-02",
        "blurb": "What every name refers to, and where it can be seen.",
        "outcomes": [
          4
        ],
        "groups": [
          12
        ],
        "lessons": [
          {
            "id": "declarations",
            "title": "What a declaration creates",
            "kind": "read"
          },
          {
            "id": "name-resolution",
            "title": "Which binding a name means",
            "kind": "read"
          }
        ]
      },
      {
        "id": "data",
        "name": "Values and references",
        "starts": "2026-11-02",
        "blurb": "Is this a copy, or the same thing under two names? And everything that follows from the answer.",
        "outcomes": [
          2,
          10
        ],
        "groups": [
          6,
          7,
          8,
          27
        ],
        "assignments": [
          {
            "id": "data-assignment",
            "due": null,
            "title": "Recipe box and meal plan"
          }
        ],
        "lessons": [
          {
            "id": "copy-vs-alias",
            "title": "Copying versus aliasing",
            "kind": "read"
          },
          {
            "id": "identity",
            "title": "When two objects are equal",
            "kind": "read"
          },
          {
            "id": "what-gets-shared",
            "title": "What a function can change",
            "kind": "read"
          },
          {
            "id": "meet-debugger",
            "title": "Meet the debugger",
            "kind": "read"
          },
          {
            "id": "watch-a-value",
            "title": "Watch a value change",
            "kind": "trial"
          },
          {
            "id": "shallow-and-deep",
            "title": "One level deep, or all the way",
            "kind": "read"
          },
          {
            "id": "nested-recursion",
            "title": "Walking nested data with recursion",
            "kind": "read"
          },
          {
            "id": "immutability",
            "title": "Making data unchangeable",
            "kind": "read"
          },
          {
            "id": "choosing-a-collection",
            "title": "Choosing a collection",
            "kind": "read"
          },
          {
            "id": "check-data",
            "title": "Copying and sharing check",
            "kind": "check"
          },
          {
            "id": "checkpoint-1",
            "title": "Checkpoint 1 practice",
            "kind": "check"
          }
        ]
      },
      {
        "id": "functions",
        "name": "Functions as values",
        "starts": "2026-11-02",
        "blurb": "Passing behaviour around, functions that remember, and what a method loses when you do.",
        "outcomes": [
          4,
          5
        ],
        "groups": [
          14,
          15,
          16
        ],
        "assignments": [
          {
            "id": "functions-assignment",
            "due": null,
            "title": "Chat bot command pipeline"
          }
        ],
        "lessons": [
          {
            "id": "functions-values",
            "title": "Functions as values",
            "kind": "read"
          },
          {
            "id": "callbacks",
            "title": "Who calls a callback, and when",
            "kind": "read"
          },
          {
            "id": "closures",
            "title": "Closures keep their surroundings",
            "kind": "read"
          },
          {
            "id": "loop-capture",
            "title": "A closure keeps the binding, not the value",
            "kind": "read"
          },
          {
            "id": "make-greeter",
            "title": "Return a function that remembers",
            "kind": "trial"
          },
          {
            "id": "method-receiver",
            "title": "How a method finds its object",
            "kind": "read"
          },
          {
            "id": "higher-order",
            "title": "Higher-order functions, built by hand",
            "kind": "read"
          },
          {
            "id": "first-class-or-not",
            "title": "Building functions from functions",
            "kind": "read"
          },
          {
            "id": "loop-next-value",
            "title": "How a loop gets its next value",
            "kind": "read"
          },
          {
            "id": "remove-duplication",
            "title": "Remove the duplication",
            "kind": "trial"
          },
          {
            "id": "check-scope",
            "title": "Scope and closures check",
            "kind": "check"
          }
        ]
      },
      {
        "id": "modeling",
        "name": "Objects and classes",
        "starts": "2026-11-09",
        "blurb": "Designing a type, and justifying inheritance, composition or a contract.",
        "outcomes": [
          6
        ],
        "groups": [
          17,
          18
        ],
        "assignments": [
          {
            "id": "modeling-assignment",
            "due": null,
            "title": "Workshop bookings"
          }
        ],
        "lessons": [
          {
            "id": "designing-a-type",
            "title": "Designing a type",
            "kind": "read"
          },
          {
            "id": "encapsulation",
            "title": "Private state that guards an invariant",
            "kind": "read"
          },
          {
            "id": "prototypes",
            "title": "How a method is found",
            "kind": "read"
          },
          {
            "id": "inherit-compose",
            "title": "Inheritance, and what a subclass commits to",
            "kind": "read"
          },
          {
            "id": "composition",
            "title": "Composition: holding parts instead",
            "kind": "read"
          },
          {
            "id": "contracts",
            "title": "Contracts, and choosing between the three",
            "kind": "read"
          },
          {
            "id": "checkpoint-2",
            "title": "Checkpoint 2 practice",
            "kind": "check"
          }
        ]
      },
      {
        "id": "errors",
        "name": "Errors and failure",
        "starts": "2026-11-16",
        "blurb": "What can go wrong, who deals with it, and what runs on the way out.",
        "outcomes": [
          8,
          10
        ],
        "groups": [
          21,
          22
        ],
        "assignments": [
          {
            "id": "errors-assignment",
            "due": null,
            "title": "Gear locker"
          }
        ],
        "lessons": [
          {
            "id": "failure-kinds",
            "title": "Recoverable and unrecoverable failure",
            "kind": "read"
          },
          {
            "id": "propagation",
            "title": "Throwing, and where it goes",
            "kind": "read"
          },
          {
            "id": "logging-to-decide",
            "title": "Logging to tell two explanations apart",
            "kind": "read"
          },
          {
            "id": "where-to-handle",
            "title": "Where an error should be handled",
            "kind": "read"
          },
          {
            "id": "error-as-value",
            "title": "Returning a failure instead of throwing",
            "kind": "read"
          },
          {
            "id": "cleanup",
            "title": "Cleanup on every way out",
            "kind": "read"
          }
        ]
      },
      {
        "id": "async",
        "name": "Asynchrony and runtime",
        "starts": "2026-11-23",
        "blurb": "Work that does not finish now, how the host schedules it, and the same code elsewhere.",
        "outcomes": [
          9
        ],
        "groups": [
          23,
          24
        ],
        "assignments": [
          {
            "id": "async-assignment",
            "due": null,
            "title": "A bookshop client that waits well"
          }
        ],
        "lessons": [
          {
            "id": "work-that-waits",
            "title": "Promises: a value for work not done yet",
            "kind": "read"
          },
          {
            "id": "await-syntax",
            "title": "async and await",
            "kind": "read"
          },
          {
            "id": "event-loop",
            "title": "The event loop, slowly",
            "kind": "read"
          },
          {
            "id": "same-code-elsewhere",
            "title": "The same code in another host",
            "kind": "read"
          },
          {
            "id": "concurrency-models",
            "title": "Event loops, threads and coroutines",
            "kind": "read"
          }
        ]
      },
      {
        "id": "modules",
        "name": "Modules and dependencies",
        "starts": "2026-11-30",
        "blurb": "Where the seams go, what a module promises, and taking on a dependency.",
        "outcomes": [
          7,
          10
        ],
        "groups": [
          19,
          20
        ],
        "assignments": [
          {
            "id": "modules-assignment",
            "due": null,
            "title": "Study log, in modules"
          }
        ],
        "lessons": [
          {
            "id": "splitting-modules",
            "title": "Where to put the seam",
            "kind": "read"
          },
          {
            "id": "interfaces",
            "title": "What a module promises",
            "kind": "read"
          },
          {
            "id": "dependencies",
            "title": "Adding and pinning a dependency",
            "kind": "read"
          },
          {
            "id": "reading-docs",
            "title": "Answering it from the documentation",
            "kind": "read"
          }
        ]
      },
      {
        "id": "ai-code",
        "name": "Judging generated code",
        "starts": "2026-11-30",
        "blurb": "What AI-generated code characteristically gets wrong, drawing on every module before it.",
        "outcomes": [
          10
        ],
        "groups": [
          25
        ],
        "lessons": [
          {
            "id": "ai-evaluation",
            "title": "What AI-generated code gets wrong",
            "kind": "read"
          }
        ]
      }
    ],
    "final": {
      "starts": "2026-12-07",
      "lessons": [
        {
          "id": "final-checkpoint",
          "title": "Final checkpoint practice",
          "kind": "check"
        }
      ],
      "assignments": [
        {
          "id": "final-toolkit",
          "due": null,
          "title": "The final: a utility library of your own"
        }
      ]
    },
    "weeks": [
      {
        "num": 1,
        "modules": [
          "start-here",
          "getting-started"
        ],
        "assignments": []
      },
      {
        "num": 2,
        "modules": [
          "types"
        ],
        "assignments": [
          "primitives-game",
          "types-assignment"
        ]
      },
      {
        "num": 3,
        "modules": [
          "flow"
        ],
        "assignments": [
          "flow-calendar"
        ]
      },
      {
        "num": 4,
        "modules": [
          "loops"
        ],
        "assignments": [
          "flow-loops",
          "flow-patterns"
        ]
      },
      {
        "num": 5,
        "modules": [
          "arrays"
        ],
        "assignments": []
      },
      {
        "num": 6,
        "modules": [
          "objects",
          "fn-basics",
          "scope",
          "data",
          "functions"
        ],
        "assignments": []
      },
      {
        "num": 7,
        "modules": [
          "modeling"
        ],
        "assignments": []
      },
      {
        "num": 8,
        "modules": [
          "errors"
        ],
        "assignments": []
      },
      {
        "num": 9,
        "modules": [
          "async"
        ],
        "assignments": []
      },
      {
        "num": 10,
        "modules": [
          "modules",
          "ai-code"
        ],
        "assignments": []
      },
      {
        "num": 11,
        "modules": [],
        "assignments": []
      }
    ],
    "calendar": {
      "termStart": "2026-09-28",
      "moduleStart": {
        "start-here": "2026-09-28",
        "getting-started": "2026-09-30",
        "types": "2026-10-05",
        "flow": "2026-10-12",
        "loops": "2026-10-19",
        "arrays": "2026-10-26",
        "objects": "2026-11-02",
        "fn-basics": "2026-11-02",
        "scope": "2026-11-02",
        "data": "2026-11-02",
        "functions": "2026-11-02",
        "modeling": "2026-11-09",
        "errors": "2026-11-16",
        "async": "2026-11-23",
        "modules": "2026-11-30",
        "ai-code": "2026-11-30"
      },
      "moduleWeeks": {
        "start-here": [
          1,
          1
        ],
        "getting-started": [
          1,
          1
        ],
        "types": [
          2,
          2
        ],
        "flow": [
          3,
          3
        ],
        "loops": [
          4,
          4
        ],
        "arrays": [
          5,
          5
        ],
        "objects": [
          6,
          6
        ],
        "fn-basics": [
          6,
          6
        ],
        "scope": [
          6,
          6
        ],
        "data": [
          6,
          6
        ],
        "functions": [
          6,
          6
        ],
        "modeling": [
          7,
          7
        ],
        "errors": [
          8,
          8
        ],
        "async": [
          9,
          9
        ],
        "modules": [
          10,
          10
        ],
        "ai-code": [
          10,
          10
        ]
      },
      "assignmentDue": {
        "primitives-game": "2026-10-11",
        "types-assignment": "2026-10-11",
        "flow-calendar": "2026-10-18",
        "flow-loops": "2026-10-25",
        "flow-patterns": "2026-10-25",
        "arrays-scoreboard": null,
        "data-assignment": null,
        "functions-assignment": null,
        "modeling-assignment": null,
        "errors-assignment": null,
        "async-assignment": null,
        "modules-assignment": null
      },
      "assignmentWeek": {
        "primitives-game": 2,
        "types-assignment": 2,
        "flow-calendar": 3,
        "flow-loops": 4,
        "flow-patterns": 4,
        "arrays-scoreboard": null,
        "data-assignment": null,
        "functions-assignment": null,
        "modeling-assignment": null,
        "errors-assignment": null,
        "async-assignment": null,
        "modules-assignment": null
      },
      "lessonWeek": {
        "how-this-course-works": 1,
        "set-up": 1,
        "how-a-homework-works": 1,
        "choose-a-track": 1,
        "start-diagnostic": 1,
        "first-program": 1,
        "what-js-is": 1,
        "where-code-runs": 1,
        "first-splash": 1,
        "reading-errors": 1,
        "fix-the-random": 1,
        "check-getting-started": 1,
        "variables": 2,
        "operators": 2,
        "function-answers": 2,
        "comparisons": 2,
        "strings": 2,
        "where-type-lives": 2,
        "conversion": 2,
        "equality": 2,
        "truthiness": 2,
        "absence": 2,
        "type-discipline": 2,
        "check-types": 2,
        "branching": 3,
        "ternary": 3,
        "conditions": 3,
        "multiway": 3,
        "check-flow": 3,
        "while-loop": 4,
        "for-loop": 4,
        "do-while": 4,
        "for-of": 4,
        "loop-forms": 4,
        "break-continue": 4,
        "nesting": 4,
        "check-loops": 4,
        "arrays-basics": 5,
        "changing-arrays": 5,
        "finding-slicing": 5,
        "walking-arrays": 5,
        "mutate-traverse": 5,
        "arrays-of-arrays": 5,
        "check-arrays": 5,
        "objects-basics": 6,
        "arrays-of-objects": 6,
        "object-keys": 6,
        "functions-1": 6,
        "params-arity": 6,
        "rest-params": 6,
        "guard-clauses": 6,
        "recursion": 6,
        "hoisting": 6,
        "declarations": 6,
        "name-resolution": 6,
        "copy-vs-alias": 6,
        "identity": 6,
        "what-gets-shared": 6,
        "meet-debugger": 6,
        "watch-a-value": 6,
        "shallow-and-deep": 6,
        "nested-recursion": 6,
        "immutability": 6,
        "choosing-a-collection": 6,
        "check-data": 6,
        "checkpoint-1": 6,
        "functions-values": 6,
        "callbacks": 6,
        "closures": 6,
        "loop-capture": 6,
        "make-greeter": 6,
        "method-receiver": 6,
        "higher-order": 6,
        "first-class-or-not": 6,
        "loop-next-value": 6,
        "remove-duplication": 6,
        "check-scope": 6,
        "designing-a-type": 7,
        "encapsulation": 7,
        "prototypes": 7,
        "inherit-compose": 7,
        "composition": 7,
        "contracts": 7,
        "checkpoint-2": 7,
        "failure-kinds": 8,
        "propagation": 8,
        "logging-to-decide": 8,
        "where-to-handle": 8,
        "error-as-value": 8,
        "cleanup": 8,
        "work-that-waits": 9,
        "await-syntax": 9,
        "event-loop": 9,
        "same-code-elsewhere": 9,
        "concurrency-models": 9,
        "splitting-modules": 10,
        "interfaces": 10,
        "dependencies": 10,
        "reading-docs": 10,
        "ai-evaluation": 10,
        "final-checkpoint": 11
      },
      "finalStart": "2026-12-07",
      "finalWeek": 11
    }
  },
  "bodies": {
    "how-this-course-works": {
      "id": "how-this-course-works",
      "title": "How this course works",
      "kind": "read",
      "shape": "procedural",
      "why": "The course asks students to do things that feel backwards — answer before being taught, predict before running, redo old problems — and unexplained difficulty reads as bad design (05-module-structure rule 15). This page says what each one is and why, before any of them happens.",
      "concepts": [],
      "lede": "Some of what this course asks will feel backwards. You will answer questions before anything has been explained, say what code prints before you run it, and meet old problems mixed in with new ones. Each of those is on purpose. This page says what to do when it happens, and why it is built that way.",
      "body": [
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what to do when a page asks a question before explaining anything",
                "I can say why practice wants a prediction before it runs anything",
                "I can say what decides how much of a practice set I see",
                "I can tell whether something I need is on this site or in Canvas"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Answer first — or say you don't know",
          "body": [
            {
              "p": "Lessons on this site come in two kinds."
            },
            {
              "p": "**Most lessons open with a few questions before any explanation.** Answer them. If you have any idea at all, guess — you will often be wrong, and that is expected. A wrong answer shows you exactly what you were missing, and the explanation that follows is written to land on that gap. If you truly have no idea, press **I don't know** beside the question. That is an honest answer too, and better than a wild guess."
            },
            {
              "p": "Your answers also decide how much of the rest of the page is shown in full. If you get every opening question right, the worked examples written for someone new to the idea are folded up. A folded section stays on the page as a single line with its title — for example:"
            },
            {
              "scaffold": {
                "label": "Worked examples",
                "body": [
                  {
                    "p": "This is a folded section, opened. In a real lesson the worked examples would be\nhere, in full — nothing in them was removed while the section was folded."
                  }
                ],
                "demo": true
              }
            },
            {
              "p": "Click that line and the whole section opens. Try it. Folding hides a section; it never removes one. If a lucky guess folded a section you wanted, click its line to open it. **I don't know** never folds anything."
            },
            {
              "p": "**Other lessons show you how first.** When a lesson is about doing something — installing a tool, reading an error message — there is nothing to work out by guessing. Those lessons walk you through it one small step at a time, with a quick question after each step. This lesson is one of them."
            },
            {
              "p": "Some of these show-you-how lessons start with a few questions about earlier lessons. Answer from memory rather than scrolling back: pulling something back out of memory is what makes it stick. Re-reading feels more productive and does much less."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A lesson opens with four questions. Nothing has been explained yet. What do you do?",
                    "choices": [
                      "Answer them",
                      "Skip to the explanation"
                    ],
                    "accept": [
                      "Answer them"
                    ],
                    "miss": "**If you would skip ahead — the questions are the start of the lesson, not a test of it.** An honest guess gives the explanation something to fix, and so does an honest \"I don't know\". Skipping leaves it nothing.\n",
                    "why": "Answer them. Guess if you have an idea; press I don't know if you have none. Either way the explanation has something to work on.\n"
                  },
                  {
                    "question": "You got every opening question right, and a section now shows as one line marked **Folded**. Is that material gone?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you think it is gone — it is folded, not removed.** Click the line and it opens. It folded because your answers suggested you did not need it, not because you are not allowed it.\n",
                    "why": "No. Click the Folded line and the whole section opens.\n"
                  },
                  {
                    "question": "A lesson teaches how to install a tool. How does it start?",
                    "choices": [
                      "With questions for you to try",
                      "By showing you the steps"
                    ],
                    "accept": [
                      "By showing you the steps"
                    ],
                    "miss": "**If you said questions first — those are for ideas, not for doing something.** A guess at an install command teaches you nothing about installing.\n",
                    "why": "It shows you the steps first, one small step at a time, with a quick question after each.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Predict, then run",
          "body": [
            {
              "p": "After most lessons comes a practice set. When it lets you run code, **the Run button waits until you have said what the code will print.**"
            },
            {
              "p": "The Run button is built to wait. When you have not predicted the output, seeing it just confirms whatever you already believed, right or wrong. A prediction you committed to can be wrong, and a wrong prediction points at the exact rule you have not got yet."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "The **Run** button will not run yet. What does it want from you first?",
                    "choices": [
                      "Nothing — click it again",
                      "Your prediction of what it prints"
                    ],
                    "accept": [
                      "Your prediction of what it prints"
                    ],
                    "why": "Your prediction. Commit to one, then run it and compare.\n"
                  },
                  {
                    "question": "You predicted 3. It printed 4. Was the prediction wasted?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you think it was — the mismatch is the useful part.** It tells you which rule to go back and look at, which a correct run never does.\n",
                    "why": "No. The gap between 3 and 4 is where the learning is. Find the rule that gives 4.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "The first step decides the rest",
          "body": [
            {
              "p": "A practice set climbs in steps: predicting what code does, tracing it line by line, putting shuffled lines in order, filling a gap, changing working code, and finally writing it yourself."
            },
            {
              "p": "**How you do on the first step decides how much of the rest you see.** Get every question in the first step right and you go straight to the harder steps. Get some right and you start in the middle. Get none right and you see every step. The steps you skip are folded, not removed. When you miss a question, you often get a couple more questions of the same kind."
            },
            {
              "p": "There is no button that says \"I know this, skip ahead.\" People are poor judges of what they know, and the ones who know least overrate themselves most. A short check measures what you know more accurately than your own guess does, so the site lets the check decide."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "You get every question in the first practice step right. Do you see every step after it?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes — the easier steps fold away for you.** You showed you did not need them. They are still there if you want them.\n",
                    "why": "No. You skip ahead to the harder steps; the ones you skipped stay one click away.\n"
                  },
                  {
                    "question": "You are sure you already know this topic. Can you skip the practice steps by saying so?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes — nothing on the site asks what you think you know.** Answering the first step is how you skip.\n",
                    "why": "No. Only your answers move you on. If you really know it, the first step is all it costs you.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Old problems and other people's code",
          "body": [
            {
              "p": "Practice sets include two kinds of task you might not expect."
            },
            {
              "p": "**Problems from earlier lessons.** Most of a practice set is about the lesson you just read, but a few problems need an idea from an earlier one — and nothing says which. Leaving them unlabeled is on purpose. A label on a problem would do the hardest part for you: working out which idea the problem needs. Problems you meet outside a course do not come labeled, so the practice does not label them either. Practising one topic at a time feels easier, but you remember much less of it a month later."
            },
            {
              "p": "**Someone else's code, with mistakes in it.** Every topic has at least one critique task. You get code written by someone else — a person or an AI tool — and find what is wrong with it. Most of the code you will read in a job, someone else wrote, and spotting its mistakes only gets easier with practice. Critique tasks always appear, however well you did on the first practice step: using an idea in your own code and finding the mistake in someone else's are different skills."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A practice set comes right after a lesson. Does every problem in it use that lesson's idea?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes — a few problems need an idea from an earlier lesson, and nothing marks which.** If nothing from the lesson you just read fits a problem, try what came before it.\n",
                    "why": "No. Most do; a few need an earlier idea, unlabeled, so that working out which idea fits is part of the practice.\n"
                  },
                  {
                    "question": "You got every question in the first practice step right. Which of these do you still see?",
                    "choices": [
                      "The easier practice steps",
                      "The critique task"
                    ],
                    "accept": [
                      "The critique task"
                    ],
                    "miss": "**If you said the easier steps — those fold away once you have shown you do not need them.** The critique task does not fold, because finding mistakes in someone else's code is a different skill from using the idea yourself.\n",
                    "why": "The critique task. It always appears, however well the first step went.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Where things live",
          "body": [
            {
              "p": "There are two places, and a simple test for which one has what you need."
            },
            {
              "p": "**This site has the material** — lessons, practice, examples. It is the same for anyone who opens it, enrolled or not. It does not grade anything."
            },
            {
              "p": "**Canvas has anything that depends on being enrolled** — deadlines, submissions, grades, announcements for your section, and the course's rules on using AI tools."
            },
            {
              "p": "So ask: *would this be true for someone who is not in my section?* If yes, it is here. If no, it is in Canvas."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "When is the next assignment due — where do you look?",
                    "choices": [
                      "This site",
                      "Canvas"
                    ],
                    "accept": [
                      "Canvas"
                    ],
                    "miss": "**If you said the site — a due date depends on your section.** The site is the same for every reader, so it cannot hold one.\n",
                    "why": "Canvas. Deadlines belong to your enrollment.\n"
                  },
                  {
                    "question": "You want to re-read how an error message is laid out. Where do you look?",
                    "choices": [
                      "This site",
                      "Canvas"
                    ],
                    "accept": [
                      "This site"
                    ],
                    "why": "This site. How an error message reads is true for anyone.\n"
                  },
                  {
                    "question": "May you use an AI tool on an assignment — where do you find out?",
                    "choices": [
                      "This site",
                      "Canvas"
                    ],
                    "accept": [
                      "Canvas"
                    ],
                    "miss": "**If you said the site — the site teaches how to judge code an AI tool wrote.** Whether and how you may use one for graded work is a course rule, and course rules are in Canvas.\n",
                    "why": "Canvas, in its section on using AI tools.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A question before the explanation is **the start of the lesson**. Guess if you have an idea; press **I don't know** if you have none.",
                "**Predict before you run.** A wrong prediction points at the rule you are missing.",
                "**The first practice step decides how much of the rest you see.** Skipped steps fold; nothing is removed, and critique is never skipped.",
                "**Earlier problems are mixed in, unlabeled, on purpose.** Working out which idea a problem needs is the skill.",
                "**Would it be true for someone not in your section?** Then it is on this site. Otherwise it is in Canvas."
              ]
            }
          ]
        }
      ],
      "_source": "lessons/how-this-course-works.md"
    },
    "set-up": {
      "id": "set-up",
      "title": "Set up where your code runs",
      "kind": "read",
      "shape": "procedural",
      "why": "Every exercise, trial and assignment in the course assumes a working JavaScript environment the student can run a file in, pass data to, and pause in a debugger. Nothing else can start until that works, and it has to work for a student without reliable broadband as well as one with it.",
      "concepts": [],
      "lede": "By the end of this page you will have run JavaScript from a terminal, run a file you saved, handed a program some input, and paused it in a debugger. There are two ways to get there. Pick one — both are fully supported, and you can switch later.",
      "margins": [
        {
          "label": "MARGIN NOTE",
          "body": "Every check on this page asks for something your own screen printed. Type what you see, not what you expect — when they differ, that difference is the problem to fix."
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Terminal** — a window where you type a command and the computer prints a reply. **Node** — the program that runs JavaScript outside a browser. You type `node`, then what to run.",
          "entries": [
            "**Terminal** — a window where you type a command and the computer prints a reply.",
            "**Node** — the program that runs JavaScript outside a browser. You type `node`, then what to run."
          ]
        }
      ],
      "body": [
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can open a terminal in my environment and run a line of JavaScript",
                "I can save a file and run it with `node`",
                "I can pass a program input from the command line",
                "I can pause a program at a breakpoint and read a value"
              ]
            }
          ]
        },
        {
          "h": "Pick a route"
        },
        {
          "p": "Both routes end in the same place: VS Code, a terminal, and Node. The steps below work the same in either."
        },
        {
          "p": "**A Codespace** runs VS Code and Node on GitHub's computers, inside your browser. There is nothing to install, and any computer with a browser will do — a library machine included. It needs a steady internet connection the whole time you work."
        },
        {
          "p": "**Local** means installing Node and VS Code on your own computer. It takes longer to set up, and after that it works with no connection at all."
        },
        {
          "h": "Route A: a Codespace"
        },
        {
          "p": "A **repository** is a project folder kept on GitHub. Each homework in this course comes as a repository, private and made for you. The first one, `cs140-start`, is almost empty. You open it in a Codespace."
        },
        {
          "list": [
            "Sign in at [github.com](https://github.com), or create a free account.",
            "If you're enrolled, give your GitHub username in Canvas, then accept the invitation GitHub sends you to join the course's organization — the panel below links to it. Your repository appears once your instructor has made it. Not enrolled? Use the link under the panel to create a private repository of your own.",
            "Type your GitHub username in the panel and click **Open my Codespace**, then **Create new codespace**."
          ],
          "ordered": true
        },
        {
          "repo": {
            "name": "cs140-start",
            "org": "WWCC-Ferraro",
            "studentsOrg": "WWCC-Students"
          }
        },
        {
          "video": {
            "title": "From your repository to a Codespace",
            "caption": "The steps above on a real account: finding your repository, telling it apart from the course's, starting a Codespace on it, and stopping it — plus the options form, in case you land on it instead.\n"
          }
        },
        {
          "p": "The first time you open your Codespace, it has to set itself up, so give it a moment. Then VS Code opens in the browser tab, with a terminal at the bottom. That is your environment. To come back to your Codespace later, use **Open my Codespace** again. The button reopens the Codespace you already have instead of making another."
        },
        {
          "p": "If you reach a Codespace another way, you may see a form asking for a branch, a dev container configuration, a region and a machine type. Leave each one as it is and click **Create codespace**."
        },
        {
          "p": "A free GitHub account includes 120 core hours a month — 60 hours on the standard 2-core machine — plus 15 GB of storage. With no payment method on file, a Codespace stops when those free hours or that storage run out; GitHub does not charge you. To make the hours last, stop a Codespace when you finish: on [github.com/codespaces](https://github.com/codespaces), click **⋯** beside it, then **Stop codespace**."
        },
        {
          "p": "If you're enrolled, the Canvas course has the steps for the Student Developer Pack."
        },
        {
          "h": "Route B: local"
        },
        {
          "list": [
            "**Node.** Go to [nodejs.org/en/download](https://nodejs.org/en/download) and install the version marked **LTS** — long-term support. Take the default for every option.",
            "**VS Code.** Install it from [code.visualstudio.com/download](https://code.visualstudio.com/download).",
            "**A folder.** Make a folder for this course — `cs140` is fine. In VS Code, choose **File › Open Folder…** and open it.",
            "**A terminal.** Choose **Terminal › New Terminal**. It opens at the bottom, already inside your folder."
          ],
          "ordered": true
        },
        {
          "p": "If you had VS Code open while Node installed, quit it and start it again. A terminal only finds programs that were installed before it started."
        },
        {
          "beat": "step",
          "label": "Check that Node is there",
          "body": [
            {
              "p": "Everything else runs through `node`, so check it first. In the terminal, type this and press Enter:"
            },
            {
              "code": "node --version",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "p": "It prints a `v` and three numbers separated by dots — something like `v24.11.0`. The first number is the **major version**. LTS versions have even major numbers."
            },
            {
              "p": "If it says `command not found` or `is not recognized`, the terminal cannot find Node. On the local route, quit VS Code completely, open it again, and open a new terminal. If it still fails, run the Node installer again."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Run `node --version`. Type the first number after the `v`.",
                    "accept": [
                      "22",
                      "24",
                      "26",
                      "28",
                      "30",
                      "v22",
                      "v24",
                      "v26",
                      "v28",
                      "v30"
                    ],
                    "miss": "**If your terminal printed an error instead of a version, Node is not installed where the terminal can find it.** Restart VS Code and open a new terminal, then reinstall if it still fails. **If you got an odd number,** you installed a Current release rather than LTS — go back to the download page and pick the one marked LTS.\n",
                    "why": "An even major number means an LTS release of Node is installed and your terminal can find it. Every later step depends on that.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Run one line",
          "body": [
            {
              "p": "`node -e` runs the JavaScript inside the quotes, then stops. Try it:"
            },
            {
              "code": "node -e \"console.log(2 + 3)\"",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "p": "It prints `5`. `console.log` shows a value in the terminal — it is how a program gets output to you."
            },
            {
              "p": "Now ask Node about the computer it is running on. The answer depends on your computer, so you cannot predict it; you have to run the command:"
            },
            {
              "code": "node -e \"console.log(process.platform)\"",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Run `node -e \"console.log(process.platform)\"`. Type exactly what it printed.",
                    "accept": [
                      "linux",
                      "darwin",
                      "win32"
                    ],
                    "miss": "**Type exactly what the terminal printed, not the name you know the system by.** Node's names are `linux`, `darwin` (macOS) and `win32` (all of Windows).\n",
                    "why": "Whatever it printed is what your code runs on. A Codespace always says `linux` — it is a Linux machine at GitHub, whatever your own computer is.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Save a file and run it",
          "body": [
            {
              "p": "One line at a time does not go far. A program lives in a file."
            },
            {
              "list": [
                "In the **Explorer** on the left, create a new file named `hello.js`.",
                "Type this into it:"
              ],
              "ordered": true
            },
            {
              "code": "console.log(\"hello\");\nconsole.log(6 * 7);",
              "lang": "js",
              "file": "hello.js"
            },
            {
              "list": [
                "**Save it** — Ctrl+S, or Cmd+S on a Mac. A dot on the file's tab means it is not saved yet; Node runs what is on disk, not what is on screen.",
                "In the terminal, run it:"
              ],
              "ordered": true
            },
            {
              "code": "node hello.js",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "p": "It prints `hello`, then `42`. The terminal looks for `hello.js` in the folder the terminal is in. Running from the wrong folder is the most common way `node hello.js` goes wrong, so try it on purpose. `cd ..` moves the terminal up one folder, out of yours:"
            },
            {
              "code": "cd ..\nnode hello.js",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "After `cd ..`, run `node hello.js`. Type the three words after `Error:`.",
                    "accept": [
                      "Cannot find module"
                    ],
                    "miss": "**If it printed `hello` and `42` again, the `cd ..` did not run.** Type it on its own line, press Enter, then run the file.\n",
                    "why": "`Cannot find module` means Node looked for the file in the terminal's folder and it was not there. When you see it, check which folder you are in before anything else. To get back, open a new terminal (**Terminal › New Terminal**) — a new one always starts in your folder.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Hand a program some input",
          "body": [
            {
              "p": "Most programs need data from you. The simplest way to give it is to type it after the file name. Create `greet.js`, save it, and run it:"
            },
            {
              "code": "const name = process.argv[2];\nconsole.log(\"Hello, \" + name);",
              "lang": "js",
              "file": "greet.js"
            },
            {
              "code": "node greet.js Ada",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "p": "It prints `Hello, Ada`. `process.argv` is a list of everything on the command line, counted from 0. Place 0 is Node itself, place 1 is your file, and place 2 is the first word you typed after it. So `process.argv[2]` is your input."
            },
            {
              "p": "Whatever you type arrives as text, even `42`. What that means when you do arithmetic with an input is covered in the lesson on conversion. For now, the goal is just to get data into a program."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`node greet.js Ada Lovelace` — type what it prints.",
                    "accept": [
                      "Hello, Ada",
                      "Hello Ada"
                    ],
                    "miss": "**If you expected `Hello, Ada Lovelace` — a space splits the input into separate words.** `Lovelace` is in place 3, and the program only reads place 2. Put quotes round it — `\"Ada Lovelace\"` — to send it as one.\n",
                    "why": "`Hello, Ada`. Each space-separated word gets its own place in the list.\n"
                  },
                  {
                    "question": "`node greet.js`, with no name after it — what does it print?",
                    "choices": [
                      "Hello, ",
                      "Hello, undefined",
                      "An error"
                    ],
                    "accept": [
                      "Hello, undefined"
                    ],
                    "miss": "**If you expected an error — nothing went wrong that Node can see.** Place 2 is empty, and reading an empty place gives `undefined`, which then gets printed like any other value.\n",
                    "why": "`Hello, undefined`. No error, just a wrong answer. A missing input is one of the quietest bugs there is, and a program that needs input should check for it.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Pause a program in the debugger",
          "body": [
            {
              "p": "A debugger stops your program partway through so you can look at its values. The way to start the debugger that works the same in a Codespace and locally is VS Code's **JavaScript Debug Terminal**: any `node` you run inside it is debugged automatically."
            },
            {
              "list": [
                "Create `total.js`, save it:"
              ],
              "ordered": true
            },
            {
              "code": "const price = 4;\nconst count = 3;\nconst total = price * count;\nconsole.log(total);",
              "lang": "js",
              "file": "total.js"
            },
            {
              "list": [
                "**Set a breakpoint.** Click just left of the line number **4**. A red dot appears. That is where the program will stop.",
                "**Open the debug terminal.** Press **F1** to open the Command Palette, type `JavaScript Debug Terminal`, and choose **Debug: JavaScript Debug Terminal**. A new terminal opens. (This page uses F1 because it also works in a browser. The usual shortcut, Ctrl+Shift+P, can be taken by the browser for itself.)",
                "**Run the file in it:**"
              ],
              "ordered": true
            },
            {
              "code": "node total.js",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "p": "The program stops with line 4 highlighted. On the left, the **Run and Debug** view opens, and under **Variables** you can see the program's values at this moment. Press **Continue** (▶ in the small toolbar at the top, or F5) and the program finishes."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Paused at line 4, under **Variables** — type the value next to `total`.",
                    "accept": [
                      "12"
                    ],
                    "miss": "**If the program printed `12` and never stopped, it did not run under the debugger.** Check that the red dot is on line 4, and that you ran `node total.js` in the JavaScript Debug Terminal, not the ordinary one.\n",
                    "why": "`12`. You read a value out of a program that was still running — which is what the lesson on the debugger builds on.\n"
                  },
                  {
                    "question": "In the debug terminal, type the line Node printed when the debugger connected.",
                    "accept": [
                      "Debugger attached.",
                      "Debugger attached",
                      "debugger attached"
                    ],
                    "miss": "**Look just above your program's output.** Node prints one line of its own when a debugger connects, before your program starts.\n",
                    "why": "`Debugger attached.` When you see it, the debugger is watching. When you expect it and it is not there, you are in the ordinary terminal.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Your environment is **VS Code, a terminal, and Node** — in a Codespace or on your own computer. The steps are the same in both.",
                "`node file.js` runs **the saved file**, found **in the terminal's folder**. `Cannot find module` means check the folder.",
                "Input typed after the file name arrives in **`process.argv`**, from place 2, one word per place.",
                "In the **JavaScript Debug Terminal**, `node` stops at your breakpoints, in either route."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Download Node.js",
              "href": "https://nodejs.org/en/download",
              "note": "Installers for every system. Pick LTS."
            },
            {
              "title": "Visual Studio Code",
              "href": "https://code.visualstudio.com/download",
              "note": "The editor, for Windows, macOS and Linux."
            },
            {
              "title": "Creating a codespace for a repository",
              "href": "https://docs.github.com/en/codespaces/developing-in-a-codespace/creating-a-codespace-for-a-repository",
              "note": "and for opening it in a Codespace, including the options form."
            },
            {
              "title": "Node.js debugging in VS Code",
              "href": "https://code.visualstudio.com/docs/nodejs/nodejs-debugging",
              "note": "The JavaScript Debug Terminal and the other ways to start the debugger."
            },
            {
              "title": "process.argv",
              "href": "https://nodejs.org/api/process.html#processargv",
              "note": "Node's reference for the input list."
            }
          ]
        }
      ],
      "_source": "lessons/set-up.md"
    },
    "how-a-homework-works": {
      "id": "how-a-homework-works",
      "title": "How a homework works",
      "kind": "read",
      "shape": "procedural",
      "why": "Every homework is a repository with a program, tests, and a check that runs on each push. The first homework should be about primitives, not about finding the test command or discovering that a commit never reached GitHub. This lesson runs the whole loop once on a practice repository with nothing to solve (Nick, 2026-09-29).",
      "concepts": [],
      "lede": "Every homework in this course comes the same way: a repository on GitHub with the instructions, a program to finish, and tests that check it. This lesson walks you through one that has nothing to solve. You change one line — your name — and everything else is practice with the tools: opening the files, running the program, running the tests, and sending your work back to GitHub.",
      "margins": [
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**npm** — the tool that came with Node; it runs a project's commands from its `package.json`. **Repository** — a project folder kept on GitHub, with its history. **Clone** — a repository's files, copied onto a computer. **Test** — a small program that checks yours and reports pass or fail. **Commit** — a saved snapshot of your files, with a message. **Push** — sending your commits to GitHub.",
          "entries": [
            "**npm** — the tool that came with Node; it runs a project's commands from its `package.json`.",
            "**Repository** — a project folder kept on GitHub, with its history.",
            "**Clone** — a repository's files, copied onto a computer.",
            "**Test** — a small program that checks yours and reports pass or fail.",
            "**Commit** — a saved snapshot of your files, with a message.",
            "**Push** — sending your commits to GitHub."
          ]
        }
      ],
      "body": [
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can find my own repository for a homework and open it",
                "I can say which part of a homework is the instructions, the program and the tests",
                "I can run a program and its tests, and read which test failed and why",
                "I can commit and push my work, and read the check GitHub shows for it"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Open your repository",
          "body": [
            {
              "p": "Every homework starts from a repository that belongs to the course. For this practice homework, the course's repository is [github.com/WWCC-Ferraro/cs140-start](https://github.com/WWCC-Ferraro/cs140-start). The course's repository is the **template**: you can look at it, but you cannot change it."
            },
            {
              "p": "What you work in is **your repository**, made for you from the template. It is private — only you and your instructor can see it — and its name has the term, the homework and your username in it: `fall26-cs140-start-yourname`, say. It lives on GitHub, under the course's organization, and it is the one place your work counts."
            },
            {
              "p": "Every homework page has this same panel. Here it is for the practice repository:"
            },
            {
              "repo": {
                "name": "cs140-start",
                "org": "WWCC-Ferraro",
                "studentsOrg": "WWCC-Students"
              }
            },
            {
              "p": "**In a Codespace.** Type your username and click **Open my Codespace**. If you followed Route A in *Set up where your code runs*, the button reopens the Codespace you already have."
            },
            {
              "p": "**On your own computer.** You need one more program, GitHub Desktop, which **clones** repositories — copies their files onto your computer — and sends your changes back."
            },
            {
              "list": [
                "Install it from [desktop.github.com](https://desktop.github.com) and sign in with your GitHub account.",
                "Type your username in the panel and open **My repository**. Click **Code**, then **Open with GitHub Desktop**. GitHub Desktop asks where to put the clone; take the default and click **Clone**.",
                "In GitHub Desktop, choose **Repository › Open in Visual Studio Code**, then in VS Code **Terminal › New Terminal**."
              ],
              "ordered": true
            },
            {
              "p": "The clone is files on your computer. Your repository is still the one on GitHub: until you push, nothing you change in the clone reaches it."
            },
            {
              "p": "Either way, check your repository's page on GitHub. Its name ends with your username, it is marked **Private**, and under the name GitHub says it was generated from `WWCC-Ferraro/cs140-start`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "You go to github.com/WWCC-Ferraro/cs140-start and start a Codespace right there. Whose repository are you working in?",
                    "choices": [
                      "Mine",
                      "The course's"
                    ],
                    "accept": [
                      "The course's"
                    ],
                    "miss": "**If you said yours — that is the template, the course's repository.** You cannot push to it, so nothing you do there counts. Open your Codespace from the panel, which always opens your repository.\n",
                    "why": "The course's. Yours is the one whose name ends with your username.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Find your way around",
          "body": [
            {
              "p": "Open the **Explorer** on the left. Every homework has the same parts:"
            },
            {
              "list": [
                "**`README.md`** — the instructions. On GitHub it is shown under the file list. In VS Code, right-click it and choose **Open Preview** to read it formatted.",
                "**`src/`** — the program. In a homework, this is where your work goes. Here, `src/about.js` is the only file you change; open it and look at the line with the two quote marks. Then open `src/welcome.js`, the program itself. You don't need to understand its code yet.",
                "**`test/`** — the tests. They check the program. You never change them, but they are worth opening: each test has a name in quotes, and that name is what you see when it passes or fails.",
                "**`package.json`** — names the commands for this repository, `npm start` and `npm test`."
              ]
            },
            {
              "p": "The `.github` and `.devcontainer` folders are settings for GitHub and for Codespaces. Leave them alone. `.vscode/settings.json` is VS Code's settings for this project. You can add your own to it, but keep the lines already there: they switch off AI code suggestions, the grey code that appears as you type, so the code you hand in is your own. AI chat still works, and the README's section \"Using an AI assistant\" says how AI chat can help."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which folder holds files you never change?",
                    "choices": [
                      "src",
                      "test"
                    ],
                    "accept": [
                      "test"
                    ],
                    "miss": "**If you said `src` — that is where your work goes.** The tests in `test/` are how your work is checked, so changing them would only hide a problem.\n",
                    "why": "`test`. Your work goes in `src/`; the tests stay as they came.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Run the program",
          "body": [
            {
              "p": "You will meet three programs with similar names. Homework in this course uses only the first two."
            },
            {
              "list": [
                "**`node`** runs one JavaScript file: `node src/welcome.js`.",
                "**`npm`** came with Node. It reads the `package.json` **in the folder the terminal is in**, and runs the commands named there — `npm start`, `npm test`. It can also install code other people wrote (`npm install`, or `npm i` for short), but no homework needs anything installed until the Modules module, and the homework says so when it does.",
                "**`nvm`** is a separate tool some people use to switch between versions of Node. This course does not use it: a Codespace comes with Node 24, and on your own computer the installer from nodejs.org is all you need."
              ]
            },
            {
              "p": "`npm start` runs the program. `package.json` says which file the program is, so you do not need to know. In the terminal:"
            },
            {
              "code": "npm start",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "p": "npm first prints two lines of its own, starting with `>`: the repository's name and the command it is running. After them comes what the program printed."
            },
            {
              "p": "On Windows, if the terminal says *running scripts is disabled on this system*, type `npm.cmd` instead of `npm` — `npm.cmd start`, and later `npm.cmd test`."
            },
            {
              "p": "**If the terminal is in the wrong folder, npm cannot find `package.json`,** and every npm command fails the same way:"
            },
            {
              "code": "npm error code ENOENT\nnpm error enoent Could not read package.json: Error: ENOENT: no such file or directory, open '/workspaces/package.json'",
              "lang": "text",
              "file": "terminal"
            },
            {
              "p": "The last part of that path is where npm looked. `Could not read package.json` means what `Cannot find module` meant in *Set up where your code runs*: check which folder you are in, not whether something is broken. A new terminal (**Terminal › New Terminal**) starts in your repository's folder. If you ran `npm i` in the wrong folder, npm may also have left a `package-lock.json` file there; delete that file."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Run `npm start`. Type the first line the program printed — not the lines starting with `>`.",
                    "accept": [
                      "Hello, .",
                      "Hello,."
                    ],
                    "miss": "**Skip npm's own two lines, the ones starting with `>`.** The first line after them is the program's.\n",
                    "why": "`Hello, .` — with a gap where your name should be. That gap is what the tests are about to report.\n"
                  },
                  {
                    "question": "You run `npm test` and get `Could not read package.json`. What do you check first?",
                    "choices": [
                      "Which folder the terminal is in",
                      "Whether Node and npm are installed"
                    ],
                    "accept": [
                      "Which folder the terminal is in"
                    ],
                    "miss": "**If you would reinstall — npm is working: it ran, and it told you what it could not find.** A missing install says `command not found` instead. Open a new terminal, which starts in your repository's folder, and run it again.\n",
                    "why": "The folder. npm reads `package.json` from wherever the terminal is, and there is none there.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Run the tests",
          "body": [
            {
              "p": "`npm test` runs every test in `test/` and reports each one:"
            },
            {
              "code": "npm test",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "p": "Read the output from the top. Each test gets a line: **✔** for passed, **✖** for failed, then its name. Below them is a count — `ℹ tests 2`, `ℹ pass 1`, `ℹ fail 1`. Below that, every failing test is shown again with its **message**: what it expected, and what to do about it. The lines after the message that start with `at` say where in the test code it failed; you can ignore them."
            },
            {
              "p": "Every homework starts with some of its tests failing, or all of them. That is not a mistake: the failing tests are the work still to do."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Run `npm test`. Type the name of the test that failed.",
                    "accept": [
                      "your name is filled in"
                    ],
                    "miss": "**Look for the line that starts with ✖.** The words after it, up to the time in parentheses, are the test's name.\n",
                    "why": "`your name is filled in`. Its message says exactly what to do, which is what a test message in this course is written to do.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Make the change",
          "body": [
            {
              "p": "Open `src/about.js`. Type your name between the two quote marks and save. The dot on the tab goes away when it is saved."
            },
            {
              "p": "Run the tests again:"
            },
            {
              "code": "npm test",
              "lang": "bash",
              "file": "terminal"
            },
            {
              "p": "Both lines now start with ✔. Run `npm start` too: the gap is filled."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Run `npm test` again. Type the number after `ℹ fail`.",
                    "accept": [
                      "0"
                    ],
                    "miss": "**If it still says 1, the file is probably not saved.** A dot on the tab means VS Code has not written it to disk, and the tests read what is on disk. Save and run them again.\n",
                    "why": "`0`. Every test passes. On a homework, that is the moment to push.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "step",
          "label": "Commit and push",
          "body": [
            {
              "p": "So far your change exists only in your Codespace or your clone. Nothing reaches GitHub until you do two things. A **commit** saves a snapshot of your changed files, with a short message saying what changed. A **push** sends your commits to GitHub."
            },
            {
              "p": "**In VS Code** — in a Codespace:"
            },
            {
              "list": [
                "Click the **Source Control** icon on the left (it looks like a branching line), or press Ctrl+Shift+G.",
                "Your changed files are listed. In the message box above them, type what you changed — `Add my name`.",
                "Click **Commit**. If VS Code asks whether to stage all your changes and commit them directly, choose **Yes**.",
                "Click **Sync Changes**. That is the push."
              ],
              "ordered": true
            },
            {
              "p": "If you made `hello.js` and the other files from *Set up where your code runs* in this Codespace, they are listed too. Commit them along with `src/about.js`; no test checks them."
            },
            {
              "p": "**In GitHub Desktop** — on your own computer:"
            },
            {
              "list": [
                "The **Changes** tab lists your changed files.",
                "In the **Summary** box at the bottom left, type what you changed — `Add my name`.",
                "Click **Commit to main**.",
                "Click **Push origin** at the top."
              ],
              "ordered": true
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "You committed your change, but did not push. Can GitHub see it?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes — a commit stays where you made it.** Only a push sends it to GitHub. On a homework, a commit you never pushed is work nobody can see.\n",
                    "why": "No. Commit, then push — every time.\n"
                  }
                ]
              }
            }
          ],
          "n": 6
        },
        {
          "beat": "step",
          "label": "Read the check on GitHub",
          "body": [
            {
              "p": "Open your repository's page on GitHub and refresh it. Beside your latest commit, near the top of the file list, is a small mark. A yellow dot means GitHub is running your tests; wait a minute and refresh. Then it becomes a green **✓** — every test passed — or a red **✗**."
            },
            {
              "p": "Click the mark, then **Details**, to see the output. It is the same `npm test` you ran yourself, run again on GitHub's computer from what you pushed. So a red ✗ after a green run on your screen usually means something did not get pushed."
            },
            {
              "p": "On a homework, there is nothing else to hand in: your work is read from your repository, so what you have pushed is what counts. Canvas has each homework's deadline. This practice repository is not graded."
            },
            {
              "p": "**In a Codespace, stop it when you are done.** A running Codespace uses your free monthly hours, and it keeps running for 30 minutes after you close the tab. Your files are kept when it stops. Push first, then use **My Codespaces** in the panel: click **⋯** beside your Codespace and choose **Stop codespace**."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "The mark beside your latest commit is a red ✗. What does it mean?",
                    "choices": [
                      "At least one test failed",
                      "The push did not work"
                    ],
                    "accept": [
                      "At least one test failed"
                    ],
                    "miss": "**If you said the push did not work — then there would be no new commit to mark.** The mark is the result of running the tests on what you pushed.\n",
                    "why": "At least one test failed on GitHub. Click the mark, then Details, to see which one.\n"
                  }
                ]
              }
            }
          ],
          "n": 7
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A homework is a repository: **`README.md`** says what to do, **`src/`** is where your work goes, **`test/`** checks it and is never changed.",
                "Work in **your repository** — private, made for you, its name ending with your username. A **clone** is its files on a computer; only a push updates the repository.",
                "**`npm start`** runs the program; **`npm test`** runs the tests. A ✖ line names the failing test, and its message says what to do.",
                "Nothing reaches GitHub until you **commit, then push**.",
                "The mark beside your latest commit is **the tests, run again on GitHub**: green ✓ all passed, red ✗ at least one failed.",
                "In a Codespace: **push, then stop it.** A stopped Codespace keeps its files and stops using your hours."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "GitHub Desktop",
              "href": "https://desktop.github.com",
              "note": "The app for cloning a repository to your computer, committing and pushing."
            },
            {
              "title": "Using Git source control in VS Code",
              "href": "https://code.visualstudio.com/docs/sourcecontrol/overview",
              "note": "The Source Control view: committing, and Sync Changes."
            },
            {
              "title": "Node's test runner",
              "href": "https://nodejs.org/api/test.html",
              "note": "What `npm test` runs in this course."
            }
          ]
        }
      ],
      "_source": "lessons/how-a-homework-works.md"
    },
    "choose-a-track": {
      "id": "choose-a-track",
      "title": "Choose your language track",
      "kind": "read",
      "shape": "procedural",
      "why": "A student works in one language for the whole course, chosen under the approval test (program/language-tracks.md) and kept (non-negotiables 1 and 5). They need to know what a track is, which one exists today, and how the choice is made before the first lesson assumes one.",
      "concepts": [],
      "lede": "You write this course in one language, start to finish — that language is your track. Today there is exactly one, JavaScript, so there is nothing to decide. This page explains how tracks will work once there are more.",
      "body": [
        {
          "block": "notice",
          "label": "Right now, this course is JavaScript only",
          "body": [
            {
              "p": "**There is nothing to choose today.** Every lesson, exercise and assignment on this\nsite is in JavaScript, and that is the track you are on."
            },
            {
              "p": "The rest of this page describes how tracks **will** work once other languages are\npublished: what a track is, how one is chosen, and how a language gets approved.\nIt is here so the idea is not a surprise later. You can read it now or skip it —\nnothing in the course depends on it yet."
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what stays the same across tracks and what changes",
                "I can say which track this site publishes today",
                "I can say where a track is chosen, and where it is changed",
                "I can test a proposed language against the three approval questions"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "What a track is",
          "body": [
            {
              "p": "A track is **one language, for the whole course.**"
            },
            {
              "p": "Every track teaches the same ideas and is measured against the same outcomes. No outcome names a language — \"predict whether an operation copies data or shares a reference to it\" is a question every track can answer. Because no outcome names a language, the course can run in more than one language. What your track changes is the examples and the syntax you write."
            },
            {
              "p": "Now and then a lesson shows a few lines of another language, to make a point about yours: a language that checks types at a different moment, say, or one that lacks a feature yours has. You read those lines. Nothing in this course asks you to write them."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Two students on different tracks. Are they measured against the same outcomes?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "miss": "**If you said no — only the language differs.** The outcomes never name one, which is why every track can meet them.\n",
                    "why": "Yes. Same ideas, same outcomes, different syntax.\n"
                  },
                  {
                    "question": "A lesson shows three lines of another language to make a point about yours. Do you have to write code in it?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes — contrast is something you read, not something you write.** You work in one language, all course.\n",
                    "why": "No. It is there to show you something about your own language.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "What is available now",
          "body": [
            {
              "p": "**JavaScript is this course's default track, and it is the only one this site publishes today.** Every lesson here is written for it."
            },
            {
              "p": "Other tracks are being written. None is published yet, and this page will change when one is. A default is only a default: the program is built so that a student can complete it without using the default language. But the material you can open here today is JavaScript."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which track can you follow on this site today?",
                    "choices": [
                      "JavaScript",
                      "Python",
                      "C#"
                    ],
                    "accept": [
                      "JavaScript"
                    ],
                    "why": "JavaScript — the default, and for now the only published track.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "How the choice is made",
          "body": [
            {
              "p": "You declare your track when you register, and **it stays with you for the course.** You are not moved between languages partway through."
            },
            {
              "p": "If you need to change it, that goes through **advising**, not through this site. How that works depends on your enrollment, so the details are in Canvas and with your advisor."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Partway through the course you want to change languages. Where do you go?",
                    "choices": [
                      "This site",
                      "Advising"
                    ],
                    "accept": [
                      "Advising"
                    ],
                    "miss": "**If you said this site — the site is the same for every reader and knows nothing about your registration.** A track change is an enrollment matter.\n",
                    "why": "Advising. Canvas has the details for your section.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Asking for another language",
          "body": [
            {
              "p": "A language outside the published tracks can be approved. The decision is made with three questions, and you can ask them yourself first:"
            },
            {
              "list": [
                "**Is it used in the work you are aiming at** — or is it a direct step towards a language that is?",
                "**Can it do everything the outcomes ask?** Closures, functions passed as values, object modeling, a real package manager, a way to fail and recover, non-blocking code, a runtime environment to run in, a debugger and official documentation. Where it cannot, is the gap named before the course starts?",
                "**Does it check types differently from the language you will use in Programming Languages II?** Between this course and Programming Languages II, you are meant to see both sides of that choice: one language that checks types one way, and one that checks them the other way."
              ],
              "ordered": true
            },
            {
              "p": "The second question does most of the work. It turns \"can I use this?\" into a checklist, and it finds a missing feature before the course starts rather than on the assignment that needs it."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A language is used everywhere in the job you want, but a function cannot be passed as a value. Which question does it fail?",
                    "choices": [
                      "1",
                      "2",
                      "3"
                    ],
                    "accept": [
                      "2"
                    ],
                    "miss": "**If you said 1 — it passes that one.** Being used in your field is question 1. Being able to do what the outcomes ask is question 2, and functions as values is one of those outcomes.\n",
                    "why": "Question 2. It cannot cover an outcome, and the gap has to be named before the language could be approved.\n"
                  },
                  {
                    "question": "A language fits your career and covers every outcome, but checks types the same way as your Programming Languages II language. Which question does it fail?",
                    "choices": [
                      "1",
                      "2",
                      "3"
                    ],
                    "accept": [
                      "3"
                    ],
                    "why": "Question 3. The two courses are meant to put you on different sides of the type-checking choice, and this one would put you on the same side twice.\n"
                  },
                  {
                    "question": "A language covers every outcome, but nobody in the field you are aiming at uses it. Which question does it fail?",
                    "choices": [
                      "1",
                      "2",
                      "3"
                    ],
                    "accept": [
                      "1"
                    ],
                    "why": "Question 1. A language has to lead somewhere you are going.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A track is **one language for the whole course**. Ideas and outcomes are the same in every track.",
                "Another language may appear **to make a point about yours**. You never have to write it.",
                "**JavaScript is the only track published here today.**",
                "You declare a track **when you register**; changing it goes through **advising**.",
                "Another language is approved on three questions: **used in your path, covers the outcomes, types checked differently from your next course's language.**"
              ]
            }
          ]
        }
      ],
      "_source": "lessons/choose-a-track.md"
    },
    "start-diagnostic": {
      "id": "start-diagnostic",
      "title": "Where you are starting from",
      "kind": "quiz",
      "ungraded": true,
      "kicker": "Orientation · ungraded",
      "intro": "Eleven questions. The first is about you; the other ten are one for each of the ten outcomes this course teaches. Nothing here is graded — it shows you where you are starting. If you have never programmed, you will not know most of the ten, or any of them. That is the expected starting point for many students, and the course is built to teach every one from the beginning. If you have an idea, guess. If you have none, choose **I don't know**. Please do not look answers up: a lucky search tells you nothing about where to begin.",
      "result": "Nothing here is graded. Each bar below is one part of the course, and an empty bar means that part will be new to you. If most of them are empty, or all of them, you are where many students start, and the course starts there too: go on to [First programs](#/lesson/first-program), which assumes nothing.",
      "questions": [
        {
          "id": "background",
          "ungraded": true,
          "prompt": "Have you written a program before?",
          "answers": [
            {
              "key": "A",
              "text": "No — this is my first time",
              "note": "Then the course starts where you are. For the next ten, choose **I don't know** whenever you have no idea. That is an honest answer, not a failure.",
              "result": "You said this is your first time programming. Every lesson here starts from nothing, so begin with [First programs](#/lesson/first-program) and take the lessons in order."
            },
            {
              "key": "B",
              "text": "A little — a class, a tutorial, or some tinkering",
              "note": "Then some of the next ten may look familiar and some will not. Guess when you have an idea; choose **I don't know** when you do not.",
              "result": "You have programmed a little. Take the lessons in order from [First programs](#/lesson/first-program); where you already know something, a lesson's opening questions will show it."
            },
            {
              "key": "C",
              "text": "Yes, regularly — in JavaScript or another language",
              "note": "Then several of the next ten will be familiar. A few are there to catch experienced programmers out.",
              "result": "You have programmed before. The full bars below are what you already know. The course still goes in order — First programs is short, and it sets up the tools every later lesson uses."
            }
          ]
        },
        {
          "id": "q1",
          "skill": "Types, conversion, equality",
          "prompt": "What does this print?",
          "code": "console.log(\"5\" + 1);",
          "answers": [
            {
              "key": "A",
              "text": "`6`",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "`51`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "A `TypeError` — you cannot add text and a number",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`NaN`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. When either side of `+` is text, JavaScript converts the other side to text and joins them. This conversion is taught in \"Conversion — when a value does not fit\".",
            "wrong": "When either side of `+` is text, JavaScript turns the number into text and joins them: `\"51\"`. It neither adds nor refuses. \"Conversion — when a value does not fit\" is where this is taught."
          }
        },
        {
          "id": "q2",
          "skill": "References and mutability",
          "prompt": "What does this print?",
          "code": "const a = [1, 2];\nconst b = a;\nb.push(3);\nconsole.log(a.length);",
          "answers": [
            {
              "key": "A",
              "text": "`2`",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "A `TypeError` — `b` is `const`, so it cannot change",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`3`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`undefined`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `b = a` did not copy the array; both names point at the same one, so a change through `b` shows up through `a`. It is taught in \"Copying versus aliasing\".",
            "wrong": "`b = a` does not copy the array — both names now point at one array, so pushing through `b` changes what `a` sees. And `const` stops you pointing `b` somewhere else; it does not stop the array changing. \"Copying versus aliasing\" teaches this."
          }
        },
        {
          "id": "q3",
          "skill": "Control flow and iteration",
          "prompt": "This is meant to remove every even number. What does it print?",
          "code": "const xs = [4, 6, 7];\nfor (let i = 0; i < xs.length; i++) {\n  if (xs[i] % 2 === 0) xs.splice(i, 1);\n}\nconsole.log(xs);",
          "answers": [
            {
              "key": "A",
              "text": "`[6, 7]`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`[7]`",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "C",
              "text": "`[4, 6, 7]` — you cannot change an array while looping over it",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It never finishes",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Removing `4` shifts `6` into position 0, and the loop has already moved on to position 1, so `6` is never checked. No error — just a wrong answer. It is taught in \"Mutating while traversing\".",
            "wrong": "Removing `4` shifts every later item one place left, so `6` moves into the position the loop has just finished with. The loop skips it: `[6, 7]`, and no error. \"Mutating while traversing\" teaches why."
          }
        },
        {
          "id": "q4",
          "skill": "Scope and closures",
          "prompt": "What does this print?",
          "code": "function counter() {\n  let n = 0;\n  return () => ++n;\n}\nconst next = counter();\nnext();\nnext();\nconsole.log(next());",
          "answers": [
            {
              "key": "A",
              "text": "`1`",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "A `ReferenceError` — `n` was gone once `counter` returned",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`0`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`3`",
              "tag": "correct",
              "correct": true
            }
          ],
          "explain": {
            "right": "Right. The returned function keeps hold of the `n` it was created with, so every call adds to the same `n`. It is taught in \"Closures keep their surroundings\".",
            "wrong": "`counter` runs once and creates one `n`. The function it returns keeps that `n` alive and adds to it on every call: 1, 2, then 3. \"Closures keep their surroundings\" teaches this."
          }
        },
        {
          "id": "q5",
          "skill": "Functions as values",
          "prompt": "What does this print?",
          "code": "function compose(f, g) {\n  return function (x) { return f(g(x)); };\n}\nfunction double(n) { return n * 2; }\nfunction addOne(n) { return n + 1; }\n\nconst h = compose(double, addOne);\nconsole.log(h(5));",
          "answers": [
            {
              "key": "A",
              "text": "`11`",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "`12`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "A `TypeError` — `h` is a number, not a function",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`10`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `compose` returns a new function, and that function calls `g` first, then `f`: `double(addOne(5))`. It is taught in \"Building functions from functions\".",
            "wrong": "`compose` returns a function, not a result, so `h(5)` is fine. Read `f(g(x))` from the inside out: `addOne` runs first, then `double` — `(5 + 1) * 2`. \"Building functions from functions\" teaches this."
          }
        },
        {
          "id": "q6",
          "skill": "Object modeling",
          "prompt": "What does this print?",
          "code": "class Shape  { describe() { return \"a shape\"; } }\nclass Rect   extends Shape { describe() { return \"a rectangle\"; } }\nclass Square extends Rect {}\n\nconsole.log(new Square().describe());",
          "answers": [
            {
              "key": "A",
              "text": "`a shape`",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "A `TypeError` — `Square` defines no `describe`",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "C",
              "text": "`a rectangle`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`undefined`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `Square` has no `describe` of its own, so the lookup moves up one level and stops at the first one it finds — `Rect`'s. It is taught in \"How a method is found\".",
            "wrong": "When an object has no method of that name, JavaScript looks one level up, then the next, and stops at the first match. `Rect` has one, so the search never reaches `Shape`. \"How a method is found\" teaches how that lookup works."
          }
        },
        {
          "id": "q7",
          "skill": "Modules and dependencies",
          "prompt": "A project's `package.json` asks for `\"lodash\": \"^4.17.20\"`. Which versions may npm install?",
          "answers": [
            {
              "key": "A",
              "text": "Exactly `4.17.20`",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "`4.17.20` or later fixes to `4.17` only, such as `4.17.21`",
              "tag": "close",
              "correct": false
            },
            {
              "key": "C",
              "text": "`4.17.20` or any newer version at all, including `5.0.0`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`4.17.20` or anything newer with the same first number — up to, not including, `5.0.0`",
              "tag": "correct",
              "correct": true
            }
          ],
          "explain": {
            "right": "Right. `^` marks a range: any newer version that keeps the first number. Because of the range, the version written in `package.json` and the version npm installs can differ, which is why npm keeps a lockfile recording the exact version installed. It is taught in \"Adding and pinning a dependency\".",
            "wrong": "A version with `^` in front is a range, not a pin. It allows any newer version with the same first number, so `4.18.0` qualifies and `5.0.0` does not. \"Adding and pinning a dependency\" teaches this, and how to pin exactly."
          }
        },
        {
          "id": "q8",
          "skill": "Errors and failure",
          "prompt": "What happens?",
          "code": "function parse(text) {\n  try {\n    return JSON.parse(text);\n  } catch (e) {\n    return null;\n  }\n}\n\nconst config = parse(\"{ port: 8080 }\");\nconsole.log(config.port);",
          "answers": [
            {
              "key": "A",
              "text": "A `TypeError` on the last line: it cannot read `port` of `null`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "It prints `8080`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "A `SyntaxError` from `JSON.parse` stops the program",
              "tag": "close",
              "correct": false
            },
            {
              "key": "D",
              "text": "It prints `undefined`",
              "tag": "common guess",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The text is not valid JSON — its key is not quoted — so `JSON.parse` throws. The `catch` swallows that error and returns `null`, and the program fails a line later, with a message about `null` instead of about the input. Where to handle an error is taught in \"Where an error should be handled\".",
            "wrong": "`JSON.parse` does throw, because `port` is not in quotes — but the `catch` swallows it and returns `null`. The failure moves to the last line, as a `TypeError` about `null` that says nothing about the real problem. \"Where an error should be handled\" teaches why catching everything does this."
          }
        },
        {
          "id": "q9",
          "skill": "Async and runtime",
          "prompt": "In what order are the letters printed?",
          "code": "console.log(\"A\");\nsetTimeout(() => console.log(\"B\"), 0);\nPromise.resolve().then(() => console.log(\"C\"));\nconsole.log(\"D\");",
          "answers": [
            {
              "key": "A",
              "text": "A B C D",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "A D B C",
              "tag": "close",
              "correct": false
            },
            {
              "key": "C",
              "text": "A D C B",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "A C D B",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The code that runs straight through finishes first: A, then D. Then promise callbacks, then timers, even a timer of `0`. It is taught in \"The event loop, slowly\".",
            "wrong": "The code that runs straight through finishes before any callback runs, so D comes before both callbacks. Then the runtime runs promise callbacks before timers, even a timer of `0`: A D C B. \"The event loop, slowly\" teaches the order."
          }
        },
        {
          "id": "q10",
          "skill": "Diagnosis and AI evaluation",
          "prompt": "Running `app.js` stops with the error below. Which line caused it?",
          "code": "1  let name = \"Ada\";\n2  name = null;\n3  console.log(name.toUpperCase());\n\napp.js:3\nconsole.log(name.toUpperCase());\n                 ^\n\nTypeError: Cannot read properties of null (reading 'toUpperCase')",
          "answers": [
            {
              "key": "A",
              "text": "Line 3 — the error points there",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "Line 2 — it sets `name` to `null`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Line 1 — `\"Ada\"` should not be in quotes",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Line 3 — `toUpperCase` is misspelled",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Line 3 is where the program broke; line 2 is where it went wrong. The message says the value was `null`, and line 2 is the one that put `null` there. Telling where it broke from where it went wrong is taught in \"When it breaks: reading errors\".",
            "wrong": "The error points at where it broke, which is not always where it went wrong. The message says `name` was `null` when line 3 ran, so look back for the line that made it `null`: line 2. \"When it breaks: reading errors\" teaches reading an error this way."
          }
        }
      ],
      "_source": "checks/start-diagnostic.yml"
    },
    "first-program": {
      "id": "first-program",
      "title": "Your first program",
      "kind": "read",
      "shape": "procedural",
      "why": "CS 140 has no prerequisite (D-042), and every later lesson assumes a student can read a short program line by line, say what a call such as `Math.max(3, 7)` produces, and fix a line that will not parse. The zero-prerequisite audit found no lesson that taught any of the three (`basics.program`, `basics.syntax`, `basics.calling`). They are procedures a beginner follows, so the page shows each one first and checks it straight after (01-explanations rule 8).",
      "concepts": [
        "basics.program",
        "basics.syntax",
        "basics.calling"
      ],
      "lede": "A program is a list of instructions, run one at a time from the top. This page shows you how to read one line by line, how to use code that someone else has already written, and what to do when Node cannot read what you wrote. You ran your first file while setting up. This page explains what happened.",
      "margins": [
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Statement** — one instruction. **Value** — a piece of data, such as `42` or `\"hello\"`. **String** — text in quotes. **Call** — running a function by writing its name followed by parentheses, `()`. **Argument** — a value you hand to a call between its parentheses. **Result** — the value a call produces.",
          "entries": [
            "**Statement** — one instruction.",
            "**Value** — a piece of data, such as `42` or `\"hello\"`.",
            "**String** — text in quotes.",
            "**Call** — running a function by writing its name followed by parentheses, `()`.",
            "**Argument** — a value you hand to a call between its parentheses.",
            "**Result** — the value a call produces."
          ]
        },
        {
          "label": "SEE ALSO",
          "body": "Set up where your code runs · When it breaks: reading errors"
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions about setting up. Answer from memory. Scrolling back to check\nwould take away most of the benefit."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`hello.js` holds two lines: `console.log(\"hello\");` and then `console.log(6 * 7);`. You run `node hello.js`. What is the second line it prints? Type it.",
                    "accept": [
                      "42"
                    ],
                    "miss": "**If you typed `6 * 7`, look at the quotes.** There are no quotes around `6 * 7`, so Node works out `6 * 7` and shows the result.\n",
                    "why": "`42`. `console.log` shows the value between its parentheses, and `6 * 7` is worked out first.\n"
                  },
                  {
                    "question": "`node hello.js` prints `Error: Cannot find module`. What do you check first?",
                    "choices": [
                      "Which folder the terminal is in",
                      "The code inside hello.js"
                    ],
                    "accept": [
                      "Which folder the terminal is in"
                    ],
                    "miss": "**If you chose the code: Node never got as far as reading the code.** It could not find `hello.js` in the folder the terminal is in.\n",
                    "why": "The folder. Node looks for the file in the folder the terminal is in.\n"
                  },
                  {
                    "question": "You changed `hello.js`, and there is still a dot on its tab. You run it. Which version does Node run?",
                    "choices": [
                      "The one on screen",
                      "The last one saved"
                    ],
                    "accept": [
                      "The last one saved"
                    ],
                    "why": "The last one saved. Node reads the file from disk, and the dot means your changes are not on disk yet.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can predict the exact output of a short program, line by line, and change it to print something else",
                "I can call a built-in function or method with arguments and say what value the call produces",
                "I can say what parentheses, quotes, semicolons, braces and comments do",
                "I can fix a line that will not parse, using the error's line number and caret"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "A program runs top to bottom",
          "body": [
            {
              "p": "A **program** is a file of instructions for the computer. Each instruction is a\n**statement**. In this course, a simple statement sits on a line of its own\nand ends with a semicolon, `;`. (*The shape of a statement*, below, says why\nthis course always writes the semicolon.)"
            },
            {
              "p": "When you run the file, Node **runs** the statements one at a time. It starts at\nthe top line and works down to the bottom, and it finishes each one before it\nstarts the next. In every program on this page, Node never skips ahead or\ngoes back up. The Branching and Loops modules, later in the course, show how to make a program skip\nlines or repeat them."
            },
            {
              "p": "`console.log(…)` is the statement you met in *Set up where your code runs*. It shows whatever is\nbetween its **parentheses** — the round marks `(` and `)` — in the terminal, on a\nline of its own. What goes between the parentheses is a **value**, which is a piece of data. A value can be a number,\nlike `42`, or text in double quotes, like `\"hello\"`. Text in quotes is called a\n**string**."
            },
            {
              "p": "`//` starts a **comment**, a note for people who read the code. Node skips\neverything from the `//` to the end of that line. Most comments sit on a line of\ntheir own, so the whole line is skipped. A comment can also follow a statement\non the same line. Then the statement before the `//` still runs, and only the\ncomment is skipped. In the examples below, each\ncomment says what the lines under it are *for*."
            },
            {
              "p": "**Example 1.** Read each line in order, and write down what it prints."
            },
            {
              "code": "// Show a heading\nconsole.log(\"Shopping list\");\n\n// Show each item\nconsole.log(\"eggs\");\nconsole.log(\"bread\");\n\n// Show how many items there are\nconsole.log(2);",
              "lang": "js",
              "file": "list.js"
            },
            {
              "p": "Node skips line 1, because it is a comment. Line 2 prints `Shopping list`.\nLine 3 is blank, and a blank line prints nothing: it is there to space the file\nout for people reading it. Lines 5 and 6 print `eggs` and then `bread`, and\nline 9 prints `2`. Each `console.log` gets a line of its own in the terminal:"
            },
            {
              "code": "Shopping list\neggs\nbread\n2",
              "lang": "text",
              "file": "node list.js"
            },
            {
              "p": "**Example 2.** This program does a different job, and Node reads it the same\nway: top to bottom, one statement at a time."
            },
            {
              "code": "// Count down\nconsole.log(3);\nconsole.log(2);\nconsole.log(1);\n\n// Announce the launch\nconsole.log(\"Go!\");",
              "lang": "js",
              "file": "launch.js"
            },
            {
              "p": "It prints `3`, `2`, `1` and then `Go!`, because that is the order of the lines.\nTo get a different output, you change the statements. Swap lines 2 and 4, and\nthe program counts up. Put `//` in front of line 3, and the `2` disappears from\nthe output. Node only does\nwhat the lines say, in the order they are written."
            },
            {
              "p": "Now read a program yourself. Answer each question from the code alone,\nwithout running it."
            },
            {
              "code": "console.log(\"cold\");\n// console.log(\"warm\");\nconsole.log(\"hot\");",
              "lang": "js",
              "file": "weather.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "What does `node weather.js` print? Type each line of output on its own line.",
                    "lines": 3,
                    "accept": [
                      "cold\nhot"
                    ],
                    "miss": "**If you included `warm`, look at the start of line 2.** A line that begins with `//` is a comment, and Node skips it.\n",
                    "why": "`cold`, then `hot`, each on its own line. Line 2 is a comment, so it prints nothing.\n"
                  },
                  {
                    "question": "You delete the `//` from the start of line 2. What does it print now? One line of output per line.",
                    "lines": 4,
                    "accept": [
                      "cold\nwarm\nhot"
                    ],
                    "miss": "**If you put `warm` first or last: line 2 runs in its own place.** Deleting the `//` makes line 2 a statement again, and Node still runs line 1, then line 2, then line 3.\n",
                    "why": "`cold`, `warm`, `hot`. The output follows the lines, top to bottom.\n"
                  },
                  {
                    "question": "Start again from the original `weather.js`, with the `//` still in place. You put three blank lines between line 1 and line 2. How many lines of output does it print now? Type a number.",
                    "accept": [
                      "2",
                      "two"
                    ],
                    "miss": "**If you counted the blank lines — they print nothing.** Only a `console.log` puts a line in the terminal, and there are still two that run.\n",
                    "why": "Two, `cold` and `hot`, exactly as before. Blank lines space out the file for people; they change nothing about what it prints.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Calling a function: arguments in, a result out",
          "body": [
            {
              "p": "A **function** is a named piece of code that does one job. JavaScript comes with\nmany of them already written. To use one, you **call** it: write its name, then\nparentheses. The values you put between the parentheses are its **arguments**. They\nare the input you hand to the function, and a comma separates each one from the\nnext."
            },
            {
              "p": "A call **produces a value**, called its **result**. When the call runs, its\nresult takes the place of the call in the line."
            },
            {
              "table": {
                "head": [
                  "Call",
                  "Its result"
                ],
                "rows": [
                  [
                    "`Math.max(3, 7)`",
                    "`7`, the largest argument"
                  ],
                  [
                    "`Math.min(3, 7)`",
                    "`3`, the smallest argument"
                  ],
                  [
                    "`Number(\"5\")`",
                    "the number `5`, made from the string `\"5\"`"
                  ],
                  [
                    "`\"hello\".toUpperCase()`",
                    "`\"HELLO\"`"
                  ]
                ]
              }
            },
            {
              "p": "The last row, `\"hello\".toUpperCase()`, calls a **method**. A method is a\nfunction that belongs to a value, and you call it with a dot: `\"hello\".toUpperCase()` asks the string `\"hello\"` for a\ncopy of itself in capitals. `toUpperCase` takes no\narguments, but it still needs its parentheses, because the parentheses are what\ncall it. Without them, the method is never called."
            },
            {
              "p": "`console.log` is a method too, written with the same dot: `log` belongs to `console`, which the runtime gives you for showing output. Its argument is the value it shows. (`Math.max` is, strictly, a method of `Math` in the same way; people usually just call both functions.)"
            },
            {
              "p": "**A result that nothing uses is lost.** If a line reads `Math.max(3, 7);` and\nnothing else, Node works out `7` and then drops it, so nothing appears. To see a\nresult in the terminal, hand it to `console.log`. When one call sits inside another, read it\n**from the inside out**. The inner call runs first, and its result becomes the\nouter call's argument."
            },
            {
              "p": "**Example 1.**"
            },
            {
              "code": "// Work out the higher score, and show it\nconsole.log(Math.max(88, 92));\n\n// Work out the lower score, and show it\nconsole.log(Math.min(88, 92));",
              "lang": "js",
              "file": "scores.js"
            },
            {
              "p": "Start inside line 2. `Math.max(88, 92)` produces `92`, so the line becomes\n`console.log(92)`, which prints `92`. Line 5 works the same way and prints `88`."
            },
            {
              "p": "**Example 2.** The same steps, with strings."
            },
            {
              "code": "// Turn the text into capitals, and show it\nconsole.log(\"quiet please\".toUpperCase());\n\n// Turn the text into a number, and show it\nconsole.log(Number(\"40\"));\n\n// Work something out, and show nothing\nMath.max(1, 2);",
              "lang": "js",
              "file": "labels.js"
            },
            {
              "p": "Line 2 prints `QUIET PLEASE`. Line 5 prints `40`. Line 8 produces `2`, but\nnothing uses the result, so nothing is printed. Three statements run and two\nlines of output appear."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "What does `console.log(Math.min(12, 4, 9));` print? Type the value.",
                    "accept": [
                      "4"
                    ],
                    "miss": "**If you answered 12, `Math.min` gives the smallest argument, not the first.** It can take any number of arguments.\n",
                    "why": "`4`, the smallest of the three arguments.\n"
                  },
                  {
                    "question": "What does `console.log(\"ok\".toUpperCase());` print? Type exactly what appears.",
                    "accept": [
                      "OK"
                    ],
                    "why": "`OK`. The inner call produces `\"OK\"`, and `console.log` shows it without the quotes.\n"
                  },
                  {
                    "question": "A line reads `Math.max(5, 50);` and nothing else. What appears in the terminal?",
                    "choices": [
                      "50",
                      "Nothing"
                    ],
                    "accept": [
                      "Nothing"
                    ],
                    "miss": "**If you said 50, the call does produce 50, but nothing uses it.** Only `console.log` shows a value in the terminal.\n",
                    "why": "Nothing. The result is worked out and dropped. Write `console.log(Math.max(5, 50));` to see it.\n"
                  },
                  {
                    "question": "In `Math.max(5, 50)`, what are `5` and `50`?",
                    "choices": [
                      "Arguments",
                      "Results",
                      "Statements"
                    ],
                    "accept": [
                      "Arguments"
                    ],
                    "why": "Arguments, the values handed to the call. `50` also happens to be the result, but the result is what comes out of the call, not what goes in.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "The shape of a statement",
          "body": [
            {
              "p": "A few marks do almost all the work in the lines you have seen. Each one has a\nsingle job."
            },
            {
              "table": {
                "head": [
                  "Mark",
                  "Its job",
                  "Example"
                ],
                "rows": [
                  [
                    "`( )` parentheses",
                    "Holds a call's arguments. Every `(` needs a matching `)`",
                    "`Math.max(3, 7)`"
                  ],
                  [
                    "`\" \"` quotes",
                    "Marks a string. What sits between the quotes is text, used exactly as written",
                    "`\"6 * 7\"`"
                  ],
                  [
                    "`.` dot",
                    "Asks the value on its left for something it has, such as a method",
                    "`\"hi\".toUpperCase()`"
                  ],
                  [
                    "`;` semicolon",
                    "Ends a statement",
                    "`console.log(1);`"
                  ],
                  [
                    "`//` comment",
                    "Starts a comment. Node skips the rest of the line",
                    "`// show the total`"
                  ],
                  [
                    "`{ }` braces",
                    "Groups several statements into one block that belongs together",
                    "Your exercises have braces. You will write your own in the Branching module"
                  ]
                ]
              }
            },
            {
              "p": "**Quotes change what a line means.** Here are two lines that differ only in\ntheir quotes:"
            },
            {
              "code": "console.log(6 * 7);     // prints 42: Node works it out\nconsole.log(\"6 * 7\");   // prints 6 * 7: it is text, used as written",
              "lang": "js",
              "file": "quotes.js"
            },
            {
              "p": "In JavaScript, a word without quotes is treated as a **name**, and Node goes\nlooking for something with that name. For example, `console.log(hello);` looks for something\nnamed `hello` and stops with an error when there is nothing by that name.\n`console.log(\"hello\");` prints the text."
            },
            {
              "p": "**Parentheses and quotes that open have to close, and the one opened last\ncloses first.** In `console.log(Math.max(3, 7));`, the `(` after `max` opened\nlast, so its `)` comes first, before the `)` that closes `console.log(`. When you check a line, match every `(` with its `)`, and every opening quote\nwith its closing quote."
            },
            {
              "p": "(Leaving out a semicolon is legal in JavaScript. Node usually works out where\nthe statement ends and acts as if the semicolon were there. In a few cases it\nguesses wrong and joins two lines into one statement. Some programmers leave\nsemicolons out and learn those cases. This course always writes the semicolon,\nso you never have to work out whether a line is one of those cases.)"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "What does `console.log(\"2 + 2\");` print? Type exactly what appears.",
                    "accept": [
                      "2 + 2",
                      "2+2"
                    ],
                    "miss": "**If you typed 4, look at the quotes.** Everything inside them is text, and Node prints text as it is written.\n",
                    "why": "`2 + 2`. The quotes make it a string, so nothing is added up.\n"
                  },
                  {
                    "question": "`console.log(Number(\"8\")` is missing something at the end. How many `)` does it still need? Type a number.",
                    "accept": [
                      "1",
                      "one"
                    ],
                    "miss": "**Count the `(` and the `)` separately.** There are two `(`, one after `log` and one after `Number`, and only one `)` so far.\n",
                    "why": "One. The `)` after `\"8\"` closes `Number(`, and `console.log(` is still open.\n"
                  },
                  {
                    "question": "What does `console.log(\"hi\"); // console.log(\"bye\");` print? One line of output per line.",
                    "lines": 2,
                    "accept": [
                      "hi"
                    ],
                    "miss": "**If you included `bye` — everything after `//` is a comment,** even a whole statement. The `console.log(\"hi\");` before it still runs.\n",
                    "why": "`hi`. The statement before the `//` runs; the rest of the line is a note for people.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "When Node cannot read a line: SyntaxError",
          "body": [
            {
              "p": "Node reads the **whole file before it runs any of it**. If one statement is not\na shape it can read, for example because a quote or a parenthesis never closes, it\nstops with a **SyntaxError**. Nothing runs, not even line 1."
            },
            {
              "code": "console.log(\"Welcome\");\nconsole.log(\"Hello);\nconsole.log(\"Bye\");",
              "lang": "js",
              "file": "welcome.js"
            },
            {
              "code": "/workspaces/cs140/welcome.js:2\nconsole.log(\"Hello);\n            ^^^^^^^^\n\nSyntaxError: Invalid or unexpected token",
              "lang": "text",
              "file": "node welcome.js"
            },
            {
              "p": "Read the error message from the top down. On your computer, the folder at the\nstart of the path will be your own."
            },
            {
              "list": [
                "**Where.** `welcome.js:2` gives the file, then the line number.",
                "**The line itself**, copied out, with `^` marks under the place where Node gave up.",
                "**The kind.** `SyntaxError` means Node could not read the line, so nothing ran. `Welcome` was not printed either."
              ],
              "ordered": true
            },
            {
              "p": "The carets start at `\"Hello);`. The quote before `Hello` opens a string and\nnever closes it, so Node reads `Hello);` as part of the string and reaches the\nend of the line still inside it. The fix is to close the\nquote: `\"Hello\"`."
            },
            {
              "p": "**The caret can point just after the mistake**, because it marks where Node gave\nup, not where you made the mistake. Here is a second file:"
            },
            {
              "code": "console.log(\"Top score:\");\nconsole.log(Math.max(40, 55);",
              "lang": "js",
              "file": "scores.js"
            },
            {
              "code": "/workspaces/cs140/scores.js:2\nconsole.log(Math.max(40, 55);\n                           ^\n\nSyntaxError: missing ) after argument list",
              "lang": "text",
              "file": "node scores.js"
            },
            {
              "p": "The caret is under the `;`. The mistake is just before it: `Math.max(` is\nclosed, but `console.log(` is not. The two examples give one method for any\nSyntaxError: go to the line the error names, look at the caret and the\ncharacters just before it, and match every opening mark with its closing mark."
            },
            {
              "p": "Here is a third error, from a file called `total.js`. The questions below are\nabout it."
            },
            {
              "code": "/workspaces/cs140/total.js:3\nconsole.log(\"Total: \";\n                    ^\n\nSyntaxError: missing ) after argument list",
              "lang": "text",
              "file": "node total.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A file has six lines, and line 4 is missing a closing quote. You run it. How many lines of output appear before the error? Type a number.",
                    "accept": [
                      "0",
                      "zero",
                      "none"
                    ],
                    "miss": "**If you said 3, Node never started running.** It reads the whole file first, and a SyntaxError stops everything before line 1 runs.\n",
                    "why": "None. A SyntaxError means Node could not read the file, so none of it ran.\n"
                  },
                  {
                    "question": "In the `total.js` error, which line of `total.js` do you go to? Type its number.",
                    "accept": [
                      "3",
                      "line 3",
                      "total.js:3"
                    ],
                    "why": "Line 3. The file name and the line number come first in the error.\n"
                  },
                  {
                    "question": "In the `total.js` error, what is missing?",
                    "choices": [
                      "A closing )",
                      "A closing quote",
                      "A semicolon"
                    ],
                    "accept": [
                      "A closing )"
                    ],
                    "miss": "**Match the marks.** The quote opens before `Total` and closes after the space, so the string is complete. The `(` after `log` never closes.\n",
                    "why": "A closing `)`. The line should be `console.log(\"Total: \");`, and the caret sits on the `;` where Node expected the `)`.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "How exercises work: you write the return",
          "body": [
            {
              "p": "Practice exercises check your code by **calling a function** and looking at\nwhat comes back. Writing a whole function is taught in the Functions module.\nUntil then, every exercise gives you the function already written, like this:"
            },
            {
              "code": "function shout(word) {\n  // TODO: put your code here\n}",
              "lang": "js",
              "file": "shout.js"
            },
            {
              "list": [
                "`shout` is the name the tests call.",
                "`word` holds the value each test hands in, for example `\"hi\"`.",
                "The braces `{ }` hold the function's statements. Your code goes between them."
              ]
            },
            {
              "p": "Between the braces, write code that produces the answer, and put `return` in\nfront of that code. `return` sends the value out of the function so the tests\ncan check it. The Functions module explains `return` in full."
            },
            {
              "p": "**Example 1.** The exercise is: \"return the word in capitals\"."
            },
            {
              "code": "function shout(word) {\n  return word.toUpperCase();\n}",
              "lang": "js",
              "file": "shout.js"
            },
            {
              "p": "When a test calls `shout(\"hi\")`, `word` holds `\"hi\"`. `word.toUpperCase()`\nproduces `\"HI\"`, and `return` sends `\"HI\"` out to the test."
            },
            {
              "p": "**Example 2.** The exercise is: \"return the larger of the two numbers\"."
            },
            {
              "code": "function larger(a, b) {\n  return Math.max(a, b);\n}",
              "lang": "js",
              "file": "larger.js"
            },
            {
              "p": "When a test calls `larger(4, 9)`, `a` holds `4` and `b` holds `9`.\n`Math.max(a, b)` produces `9`, and `return` sends `9` out to the test."
            },
            {
              "p": "**`console.log` is not `return`.** If you write\n`console.log(word.toUpperCase());`, the capitals show up for *you* in the\noutput, but nothing is sent out, so the test gets `undefined` and fails. Keep\nusing `console.log` to check what a value is while you work. Use `return` for the\nanswer."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A test calls `shout(\"go\")`, and the function holds `return word.toUpperCase();`. What value does the test get? Type it.",
                    "accept": [
                      "GO",
                      "\"GO\""
                    ],
                    "why": "`GO`. `word` holds `\"go\"`, the call produces `\"GO\"`, and `return` sends it out.\n"
                  },
                  {
                    "question": "The function holds `console.log(word.toUpperCase());` and no `return`. Does the test pass?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes, the capitals do appear in the output, but the test is not reading the output.** It checks what comes back from the function, and nothing came back.\n",
                    "why": "No. The test gets `undefined`. `console.log` shows a value to you, and `return` sends it to the test.\n"
                  },
                  {
                    "question": "Do you write the line `function shout(word) {` yourself?",
                    "choices": [
                      "Yes",
                      "No — it is given"
                    ],
                    "accept": [
                      "No — it is given"
                    ],
                    "why": "No. It is given. Your code goes between the braces.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Node runs the programs on this page **one statement at a time, top to bottom**, and the output comes out in that order. The Branching and Loops modules show how to change that order.",
                "A **call** runs a function with **arguments** and **produces a result**. A result that nothing uses is dropped, and nested calls are read **inside out**.",
                "**Quotes** make text. **Parentheses and quotes must close.** When they do not, you get a **SyntaxError**, nothing runs, and the caret marks where Node gave up.",
                "In exercises, put **`return`** in front of the code that produces your answer. `console.log` shows a value to you, and `return` sends it to the test."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "console.log()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/console/log_static",
              "note": "What it prints, and what else it can take."
            },
            {
              "title": "Math.max()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/max",
              "note": "A reference page for one function. Every built-in has one laid out the same way."
            },
            {
              "title": "SyntaxError",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/SyntaxError",
              "note": "The error for code Node cannot read."
            }
          ]
        }
      ],
      "_source": "lessons/first-program.md",
      "practice": {
        "id": "first-program",
        "steps": [
          {
            "kind": "predict",
            "title": "Read it before you run it",
            "prompt": "Type exactly what each one asks for: the output, or a value. Pick where there are buttons.",
            "items": [
              {
                "id": "p1",
                "question": "A file holds three lines: `console.log(\"one\");`, then `// console.log(\"two\");`, then `console.log(\"three\");`. What does it print? Type the words in order, separated by spaces.",
                "accept": [
                  "one three",
                  "one, three",
                  "one,three",
                  "one then three"
                ],
                "miss": "**If you included `two`, look at the start of line 2.** `//` makes the rest of the line a comment, and Node skips it.\n",
                "why": "`one`, then `three`. Node runs the lines top to bottom and skips the comment."
              },
              {
                "id": "p2",
                "question": "What does `console.log(Math.max(3, 7));` print? Type the value.",
                "accept": [
                  "7"
                ],
                "why": "`7`. Read it inside out: `Math.max(3, 7)` produces `7`, and `console.log` shows it."
              },
              {
                "id": "p3",
                "question": "`console.log(\"hello);` will not run. What is missing?",
                "choices": [
                  "A closing quote",
                  "A closing )",
                  "A semicolon"
                ],
                "accept": [
                  "A closing quote"
                ],
                "miss": "**Match the marks one at a time.** The `(` has its `)`, and the `;` is there. The quote before `hello` never closes.\n",
                "why": "A closing quote. It should be `\"hello\"`. As written, Node reads `hello);` as part of the text and runs off the end of the line."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "What does `console.log(\"hi\".toUpperCase());` print? Type exactly what appears.",
                "accept": [
                  "HI"
                ],
                "why": "`HI`. The method produces `\"HI\"`, and `console.log` shows it."
              },
              {
                "id": "p5",
                "question": "A line reads `Math.min(2, 8);` and nothing else. What appears in the terminal?",
                "choices": [
                  "2",
                  "Nothing"
                ],
                "accept": [
                  "Nothing"
                ],
                "why": "Nothing. The call produces `2`, but nothing uses the result. To see `2` in the terminal, the line would have to hand it to `console.log`."
              },
              {
                "id": "p6",
                "question": "What does `console.log(\"3 * 3\");` print? Type exactly what appears.",
                "accept": [
                  "3 * 3",
                  "3*3"
                ],
                "why": "`3 * 3`. The quotes make it text, used as written."
              },
              {
                "id": "p7",
                "question": "A file's line 5 has a parenthesis that never closes. How many of its lines run? Type a number.",
                "accept": [
                  "0",
                  "zero",
                  "none"
                ],
                "why": "None. Node reads the whole file before running any of it, so a SyntaxError on line 5 stops the file before line 1 runs."
              }
            ],
            "hints": [
              {
                "when": "you included the commented line",
                "say": "A line that starts with `//` is a note for people. Read each line and ask whether Node runs it."
              },
              {
                "when": "you gave the arguments instead of the result",
                "say": "A call produces one value. Work out the inner call first, then ask what `console.log` is handed."
              },
              {
                "when": "you picked the wrong missing mark",
                "say": "Go along the line and pair each opening mark with its closing mark: `(` with `)`, and `\"` with `\"`. Which mark has no partner?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict, then run",
            "prompt": "Say what each line prints, or pick an answer. Then run it and compare.",
            "filename": "calls.js",
            "code": "console.log(\"Start\");\nconsole.log(Math.max(4, 11, 6));\nconsole.log(\"ready\".toUpperCase());\nMath.min(2, 8);\nconsole.log(Number(\"7\"));\nconsole.log(\"End\");\n",
            "items": [
              {
                "id": "r1",
                "question": "What does line 2 print? Type the value.",
                "accept": [
                  "11"
                ],
                "why": "`11`, the largest of the three arguments."
              },
              {
                "id": "r2",
                "question": "What does line 3 print? Type exactly what appears.",
                "accept": [
                  "READY"
                ],
                "why": "`READY`. `toUpperCase()` produces a copy of `\"ready\"` in capitals."
              },
              {
                "id": "r3",
                "question": "Does line 4 print anything?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you said yes, the call does produce `2`, but nothing uses it.** A result appears only when you hand it to `console.log`.\n",
                "why": "No. `Math.min(2, 8)` produces `2`, but nothing on line 4 uses the `2`, so it is dropped."
              },
              {
                "id": "r4",
                "question": "How many lines of output does the program print altogether? Type a number.",
                "accept": [
                  "5",
                  "five"
                ],
                "why": "Five: `Start`, `11`, `READY`, `7`, `End`. Six statements run, and one of them prints nothing."
              }
            ],
            "hints": [
              {
                "when": "you counted six lines of output",
                "say": "Count the lines that hand something to `console.log`. Every statement runs, but not every statement prints."
              },
              {
                "when": "you gave the first argument for Math.max",
                "say": "`Math.max` looks at every argument and produces the largest."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Put the statements in order",
            "prompt": "Build a program that prints exactly this, one per line: `Ready`, then `3`, then `DONE`. Put the lines in order. Two of the lines do not belong.\n",
            "lines": [
              "console.log(\"Ready\");",
              "console.log(Math.min(3, 8));",
              "console.log(\"done\".toUpperCase());"
            ],
            "distractors": [
              {
                "line": "console.log(Math.max(3, 8));",
                "why": "`Math.max` produces the larger argument, which is `8`, not `3`."
              },
              {
                "line": "Math.min(3, 8);",
                "why": "This produces `3` and then drops it. Nothing is printed unless the result goes to `console.log`."
              }
            ],
            "why": "Node runs the lines top to bottom, so the output comes out in the order of the lines. The line that prints `Ready` goes first, because `Ready` has to appear first. Each value you want to see in the terminal has to be handed to `console.log`.\n",
            "hints": [
              {
                "when": "the output order is wrong",
                "say": "Output comes out in the same order as the lines. Which line prints `Ready`? That one goes first."
              },
              {
                "when": "you used a line with no console.log",
                "say": "Ask of each line whether anything appears in the terminal when it runs."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review — three slips",
            "prompt": "A classmate wrote this to print `Welcome`, the higher of two scores, and `BYE`. It does not run. Review it, and for each slip, name the line it is on.\n",
            "filename": "review.js",
            "code": "// Welcome, the higher score, then goodbye\nconsole.log(\"Welcome);\nconsole.log(Math.max(40, 55);\nconsole.log(\"bye\".toUppercase());\n",
            "items": [
              {
                "id": "c1",
                "question": "As it is, how many lines of output appear before the error? Type a number.",
                "accept": [
                  "0",
                  "zero",
                  "none"
                ],
                "miss": "**If you said 1, Node never started running.** A SyntaxError means Node could not read the file, so line 1 never ran either. (Line 1 is a comment anyway.)\n",
                "why": "None. Node reads the whole file before running any of it."
              },
              {
                "id": "c2",
                "question": "Which line does the first SyntaxError name? Type its number.",
                "accept": [
                  "2",
                  "line 2"
                ],
                "why": "Line 2. The quote before `Welcome` never closes."
              },
              {
                "id": "c3",
                "question": "Line 2 is fixed. Node still cannot read the file, because of line 3. What is missing on line 3?",
                "choices": [
                  "A closing )",
                  "A closing quote",
                  "A comma"
                ],
                "accept": [
                  "A closing )"
                ],
                "why": "A closing `)`. `Math.max(` is closed, but `console.log(` is not. The caret would sit on the `;`, just after the mistake."
              },
              {
                "id": "c4",
                "question": "Line 4 has a name spelled differently from the method in the lesson. Type the name as it should be.",
                "accept": [
                  "toUpperCase",
                  "toUpperCase()",
                  ".toUpperCase()",
                  ".toUpperCase"
                ],
                "miss": "**Compare it letter by letter with the lesson.** JavaScript treats a capital letter and a small letter as different, so one wrong letter makes it a different name.\n",
                "why": "`toUpperCase`, with a capital `C`. `toUppercase` is a different name, and no string has a method by that name. *When it breaks: reading errors* shows the error this produces."
              }
            ],
            "hints": [
              {
                "when": "you found one slip and stopped",
                "say": "Node reports one SyntaxError at a time. Check every line for pairs that do not close and for names spelled differently from the ones you know."
              },
              {
                "when": "you thought line 1 printed",
                "say": "Line 1 printed nothing, for two reasons: it is a comment, and a SyntaxError stops the whole file before anything runs."
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Fill in the answer",
            "prompt": "`loud(word)` should return the word in capitals. `loud(\"wait\")` returns `\"WAIT\"`. Between the braces, write code that produces the answer, and put `return` in front of that code. `return` sends the value out of the function so the tests can check it. The Functions module explains `return` in full. Once your code passes: in one sentence, why does `console.log` in place of `return` fail the tests?\n",
            "exportName": "loud",
            "filename": "loud.js",
            "starter": "function loud(word) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "loud(\"wait\") returns \"WAIT\"",
                "body": "return subject('wait') === 'WAIT';"
              },
              {
                "label": "loud(\"go on\") returns \"GO ON\"",
                "body": "return subject('go on') === 'GO ON';"
              },
              {
                "label": "loud(\"Hi\") returns \"HI\"",
                "body": "return subject('Hi') === 'HI';"
              }
            ],
            "hints": [
              {
                "when": "every test fails and the output shows the right word",
                "say": "You showed the value with `console.log`, but the tests read what the function sends out. What goes in front of the answer?"
              },
              {
                "when": "the result is a function, not a string",
                "say": "A method runs only when you call it. Look at what follows `toUpperCase`."
              },
              {
                "when": "only the first test passes",
                "say": "The tests hand in different words. Use `word`, which holds whatever each test hands in, not one word typed out."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Change what it produces",
            "prompt": "`highest(first, second, third)` is meant to return the largest of its three numbers. Its line hands `Math.max` only two of the three. Change the line so `Math.max` gets all three. Keep `return` at the start of the line, because `return` sends the value out of the function so the tests can check it. The Functions module explains `return` in full. Once your code passes: in one sentence, what did you change about the call?\n",
            "exportName": "highest",
            "filename": "highest.js",
            "starter": "function highest(first, second, third) {\n  // TODO: put your code here — change the line below\n  return Math.max(first, second);\n}\n",
            "tests": [
              {
                "label": "highest(3, 9, 4) is 9",
                "body": "return subject(3, 9, 4) === 9;"
              },
              {
                "label": "highest(10, 2, 5) is 10",
                "body": "return subject(10, 2, 5) === 10;"
              },
              {
                "label": "highest(1, 2, 30) is 30 (the third one is largest)",
                "body": "return subject(1, 2, 30) === 30;"
              },
              {
                "label": "highest(7, 7, 7) is 7",
                "body": "return subject(7, 7, 7) === 7;"
              }
            ],
            "hints": [
              {
                "when": "a test where one particular number is the largest fails",
                "say": "The call only looks at the numbers you hand it. Which of the three is it never given?"
              },
              {
                "when": "the largest is never returned",
                "say": "Check which function the line calls. `Math.max` and `Math.min` differ: one gives the largest argument, the other the smallest."
              },
              {
                "when": "only one test passes",
                "say": "Use the names `first`, `second` and `third`, which hold whatever each test hands in. Numbers typed into the line only fit one test."
              }
            ]
          }
        ]
      }
    },
    "what-js-is": {
      "id": "what-js-is",
      "title": "What JavaScript is",
      "kind": "read",
      "outcome": 9,
      "shape": "conceptual",
      "why": "Students run code in two runtimes, a browser and Node, from the first day, and the most baffling early errors — `document is not defined` — come from the runtime, not the language. Every later runtime comparison (outcome 9) rests on the difference between the language and the runtime.",
      "concepts": [
        "runtime.host"
      ],
      "lede": "Much of what people call \"JavaScript\" is not the JavaScript language. The language itself is small. What surrounds it, the runtime that runs your code, is enormous. Keeping the language and the runtime apart will save you a lot of confusion this term.",
      "margins": [
        {
          "label": "MARGIN NOTE",
          "body": "The official name of the language is **ECMAScript**. \"JavaScript\" is a trademark that stuck. You will see `ES2015`, `ES6`, `ES2024` in the wild — those are versions of the language, not of the browser."
        },
        {
          "label": "SEE ALSO",
          "body": "Where your code runs · When it breaks: reading errors"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Runtime environment** (*runtime* for short) — the program that runs your JavaScript and decides what else it can reach. A browser tab is one. Node is another.",
          "entries": [
            "**Runtime environment** (*runtime* for short) — the program that runs your JavaScript and decides what else it can reach. A browser tab is one. Node is another."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four names. For each, pick **language** or **runtime** — is it part of\nJavaScript itself, or something the program running your code (the *runtime\nenvironment*) handed you? A guess is fine: these four questions are here to find out what you think\nbefore the lesson starts."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "Math.max",
                          "choices": [
                            "language",
                            "runtime"
                          ],
                          "accept": [
                            "language"
                          ],
                          "why": "Part of the language. It works in every JavaScript that has ever existed — browser, Node, anything.\n"
                        },
                        {
                          "code": "document.querySelector",
                          "choices": [
                            "language",
                            "runtime"
                          ],
                          "accept": [
                            "runtime"
                          ],
                          "miss": "**If you picked `language`: `document` exists only where a browser supplies it.** That difference decides whether a tutorial snippet works or throws `document is not defined` in Node. It is also why MDN has two large sections, one for the language and one for what the browser supplies, and knowing which section to open is most of finding an answer quickly.\n",
                          "why": "The browser handed you `document`. Paste this into Node and you get `document is not defined`.\n"
                        },
                        {
                          "code": "console.log",
                          "choices": [
                            "language",
                            "runtime"
                          ],
                          "accept": [
                            "runtime"
                          ],
                          "miss": "**If you picked `language` because every tutorial uses it: `console.log` is in every *runtime*, which is not the same as being in the language.** It appears in no version of the language specification. Each runtime writes its own, which is why `console.log` of an object prints differently in Node and in Chrome.\n",
                          "why": "This one surprises people. `console` is not in the language specification at all — every runtime provides its own, which is why its behaviour differs between Node and a browser.\n"
                        },
                        {
                          "code": "JSON.stringify",
                          "choices": [
                            "language",
                            "runtime"
                          ],
                          "accept": [
                            "language"
                          ],
                          "miss": "**If you picked `runtime` because JSON sounds like a web thing: `JSON` is a built-in object in the language, alongside `Math` and `Date`.** The name misleads.\n",
                          "why": "Part of the language, despite sounding like a web thing. `JSON` is a built-in object like `Math`.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "len()",
                          "choices": [
                            "language",
                            "platform"
                          ],
                          "accept": [
                            "language"
                          ],
                          "why": "A builtin — always available, no import, part of Python itself.\n"
                        },
                        {
                          "code": "requests.get()",
                          "choices": [
                            "language",
                            "platform"
                          ],
                          "accept": [
                            "platform"
                          ],
                          "why": "A third-party package, which you install and then import. Python's version of the split is builtins, which are always there, versus everything you import: the standard library and the packages you install.\n"
                        },
                        {
                          "code": "print()",
                          "choices": [
                            "language",
                            "platform"
                          ],
                          "accept": [
                            "language"
                          ],
                          "why": "A builtin. JavaScript's `console.log` is not part of its language, so this is a real difference between the two languages, and a good example of why the question is worth asking.\n"
                        },
                        {
                          "code": "os.path.join()",
                          "choices": [
                            "language",
                            "platform"
                          ],
                          "accept": [
                            "platform"
                          ],
                          "why": "Standard library, and it needs an import. Available everywhere Python is, but not part of the core language the way `len` is.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "int, string, bool",
                          "choices": [
                            "language",
                            "platform"
                          ],
                          "accept": [
                            "language"
                          ],
                          "why": "Language keywords, defined by the C# specification.\n"
                        },
                        {
                          "code": "Console.WriteLine",
                          "choices": [
                            "language",
                            "platform"
                          ],
                          "accept": [
                            "platform"
                          ],
                          "why": "The .NET base class library, not the language. C# the language and .NET the platform are separate things, exactly as JavaScript and the browser are.\n"
                        },
                        {
                          "code": "LINQ's .Where()",
                          "choices": [
                            "language",
                            "platform"
                          ],
                          "accept": [
                            "platform"
                          ],
                          "why": "Library — extension methods in `System.Linq`. The `from … select` *syntax* is language; the methods are not.\n"
                        },
                        {
                          "code": "async / await",
                          "choices": [
                            "language",
                            "platform"
                          ],
                          "accept": [
                            "language"
                          ],
                          "why": "Keywords the compiler understands. The `Task` type they work with is library. So the line between language and platform can run through the middle of a feature you think of as one thing.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can tell whether a given name comes from the language or from the runtime",
                "I can predict what happens when code meets a runtime that lacks something it uses",
                "I can say why the same code behaves differently in Node and in a browser"
              ]
            }
          ]
        },
        {
          "h": "The language is small. The runtime is big."
        },
        {
          "p": "Beginners often miss this distinction, and job postings often assume you have it."
        },
        {
          "p": "The **language** gives you values, operators, functions, objects, control flow, classes and modules. That is nearly all of it, and this course takes those one module at a time. The language has no idea what a web page is. On its own, it cannot show you anything, read a file, or talk to a network."
        },
        {
          "p": "The **runtime environment** — *runtime* for short — is the program that runs your JavaScript and decides what else it can reach: a browser tab, or Node. The runtime hands you everything else. In a browser tab that means `document`, `window`, `alert`, `fetch` and `localStorage`. In Node it means files, processes and the network. None of those are part of the language, even the ones both runtimes now have: runtimes copy useful names from each other, so Node has had its own `fetch` since version 18, and its own `localStorage` since version 25."
        },
        {
          "code": "// The language. Works in every JavaScript that has ever existed.\nMath.max(3, 7);\nNumber(\"5\");\n\"abc\".toUpperCase();\n\n// The runtime. Works in a browser tab. Not in Node.\ndocument.querySelector(\"h1\");      // finds the page's heading\nalert(\"Saved\");                    // pops up a message on the page\n\n// A different runtime. Works in Node. Not in a browser tab.\nprocess.platform;                  // the system Node is running on",
          "lang": "js",
          "file": "language-vs-runtime.js"
        },
        {
          "p": "You have already met the last line: in *Set up where your code runs*, `console.log(process.platform)`\ntold you which system your code runs on. Node supplied `process`. A browser tab\ndoes not have it."
        },
        {
          "block": "notice",
          "label": "The test",
          "body": [
            {
              "p": "The **language** is what the JavaScript standard defines, and it works in every runtime, even a bare JavaScript engine with nothing added. Everything else came from a runtime, including names that many runtimes share: `console.log`, `setTimeout` and `fetch` work in a browser tab and in Node, but each runtime supplies its own, and a bare engine has none of them. `Math` is the language. `document` is the browser. When you are not sure, look the name up on MDN: its *JavaScript* reference is the language, and its *Web APIs* section is the browser's."
            }
          ]
        },
        {
          "p": "This is not pedantry. Keeping the language and the runtime apart explains two\nthings that otherwise look mysterious:"
        },
        {
          "list": [
            "Why a snippet from a tutorial throws `document is not defined` when you paste it into Node.",
            "Why \"I know JavaScript\" and \"I can build a web page\" are genuinely different claims."
          ]
        },
        {
          "h": "What the runtime gives you"
        },
        {
          "p": "A runtime hands your code capabilities the language does not have. Each\ncapability comes with an agreed set of names your code uses to reach it, called\nan **API** (application programming interface)."
        },
        {
          "list": [
            "**A browser tab** gives you the page (`document`), the network (`fetch`), a little storage on this device (`localStorage`), and `console`.",
            "**Node** gives you files (`node:fs`), the running process (`process`), the network (including its own `fetch`), and its own `console`."
          ]
        },
        {
          "p": "Neither list is the language. Move your code to the other runtime, and a name\nfrom one list may not be there: `document` is missing in Node, and `process`\nis missing in a browser tab. `console` and `fetch` are in both, but each\nruntime supplies its own."
        },
        {
          "scaffold": {
            "label": "Deciding which half a name comes from",
            "body": [
              {
                "h": "Deciding which half a name comes from"
              },
              {
                "p": "Two questions are usually enough."
              },
              {
                "code": "// You are looking at a name and want to know where it came from.\n\n// 1. Would it work in EVERY runtime — a browser, Node, a worker, an\n//    embedded engine?\n//    yes -> the language. Done.\n//    no / not sure -> keep going.\n\n// 2. Is it about the page, the network, the filesystem, the device, or\n//    the user?\n//    yes -> the runtime. Something outside the language handed it to you.\n//    no  -> check the JavaScript reference on MDN; if it is there, it is\n//           the language.\n\n// The shortcut that is usually enough: if you can imagine it existing\n// before web browsers did, it is probably the language.",
                "lang": "js",
                "file": "which-half.js"
              },
              {
                "p": "Now use the two questions on four names. You do not need to know what the\nnames do yet: the two questions work on the name alone."
              },
              {
                "code": "// setTimeout       ______   (runs some code after a delay)\n// Math.round       ______   (rounds a number)\n// fetch            ______   (asks another computer for data)\n// Number           ______   (turns text into a number)",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`setTimeout` and `fetch` come from the runtime. Each runtime supplies its\nown, which is why their timing and restrictions differ between Node and a\nbrowser. `Math.round` and `Number` are the language. `setTimeout` feels like a\nlanguage feature and is not, and a name like that is exactly the case the two\nquestions are for."
              }
            ]
          }
        },
        {
          "h": "Two runtimes, one language"
        },
        {
          "p": "The same language runs in two very different jobs, and job postings are frequently vague about which they mean."
        },
        {
          "table": {
            "head": [
              "",
              "Client-side",
              "Server-side"
            ],
            "rows": [
              [
                "Runs on",
                "The visitor's device",
                "A machine you control"
              ],
              [
                "Reaches",
                "The page, that browser, the network",
                "Files, databases, other services"
              ],
              [
                "Runtime",
                "The browser tab",
                "Node, Deno, Bun"
              ]
            ]
          }
        },
        {
          "p": "Same language, two runtimes. What the code can reach, the **Reaches** row, is decided by the runtime, not the language."
        },
        {
          "p": "To see it for yourself you need one new word. `typeof` asks what *kind* of value\na name holds, and answers with a string such as `\"object\"` or `\"number\"`. Asked\nabout a name that does not exist, it answers `\"undefined\"` instead of stopping\nwith an error. (The next module uses `typeof` properly.) Here is the same line\nin each runtime, side by side:"
        },
        {
          "table": {
            "head": [
              "You type",
              "In a browser tab",
              "In Node"
            ],
            "rows": [
              [
                "`typeof document`",
                "`\"object\"`",
                "`\"undefined\"`"
              ],
              [
                "`typeof process`",
                "`\"undefined\"`",
                "`\"object\"`"
              ],
              [
                "`typeof Math`",
                "`\"object\"`",
                "`\"object\"`"
              ]
            ]
          }
        },
        {
          "p": "**`Math` is part of the language, so `typeof Math` gives the same answer in\nboth.** `document` belongs to the browser and `process` belongs to Node, so each\nis missing from the other runtime. Code that *uses* a name the runtime does not\nhave stops with an error at that name, such as `document is not defined`."
        },
        {
          "video": {
            "title": "Tour: the console, the language, and the runtime",
            "caption": "Opening the console in Chrome, Firefox and Safari, the same line on two different pages, and then the same lines in Node — where one of them fails.\n"
          }
        },
        {
          "block": "try",
          "label": "poke at the runtime",
          "body": [
            {
              "p": "Open any web page, then open your browser's **console** — part of its developer tools:"
            },
            {
              "table": {
                "head": [
                  "Browser",
                  "Windows or Chromebook",
                  "Mac"
                ],
                "rows": [
                  [
                    "Chrome, Edge",
                    "Ctrl+Shift+J",
                    "Cmd+Option+J"
                  ],
                  [
                    "Firefox",
                    "Ctrl+Shift+K",
                    "Cmd+Option+K"
                  ],
                  [
                    "Safari",
                    "—",
                    "Cmd+Option+C, after turning on **Settings › Advanced › Show features for web developers**"
                  ]
                ]
              }
            },
            {
              "p": "Type each of these and press Enter. Type them rather than pasting: Chrome blocks pasting into a new console until you type `allow pasting`."
            },
            {
              "list": [
                "`Math.max(3, 7)` — the language.",
                "`document.title` — the browser. Try it on a few different sites.",
                "`window.location.href` — also the browser.",
                "`typeof document` and `typeof Math` — both answer `\"object\"`, but `document` comes from the browser and `Math` from the language."
              ],
              "ordered": true
            },
            {
              "p": "You are now running JavaScript in a second runtime, the browser, with no file, no editor and no setup. The next lesson uses this console again."
            }
          ]
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **where does the line between the language and its platform fall?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "The language",
                  "The platform",
                  "Told apart by"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "ECMAScript — values, functions, objects",
                    "browser APIs, or Node's",
                    "does it work everywhere?"
                  ],
                  [
                    "**Python**",
                    "builtins and syntax",
                    "the standard library, packages",
                    "does it need an import?"
                  ],
                  [
                    "**C#**",
                    "the C# specification",
                    ".NET base class library",
                    "keyword, or a type you reference?"
                  ]
                ]
              }
            },
            {
              "p": "Each of these three languages has the split, and in each one people mix up the\ntwo halves. The useful habit is the same everywhere: when something is missing,\nask whether you are in the wrong *runtime* before you assume you wrote it\nwrong."
            }
          ]
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the four questions from the start of this lesson again, from memory. Then name one thing you used in the\nconsole exercise above that would fail in Node, and say what error you would\nexpect to see."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You paste a tutorial snippet into a Node script and get\n`ReferenceError: document is not defined`."
            },
            {
              "p": "Two responses:"
            },
            {
              "list": [
                "**Find a Node equivalent** — the snippet was written for a browser, so translate it.",
                "**Run it in a browser instead** — the snippet is fine; you were in the wrong place."
              ]
            },
            {
              "p": "Pick one and say why — and note that the answer depends entirely on what you are\ntrying to build, which is the point. Then say what in the snippet would have\ntold you, before running it, which runtime it was written for."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "The **language** is small: values, operators, functions, objects, control flow, classes and modules. On its own, it cannot show you anything.",
                "The **runtime** supplies everything else: `document` and `alert` in a browser; files and processes in Node; and some names, such as `console` and `fetch`, in both, each runtime supplying its own.",
                "**The test:** defined by the JavaScript standard, and present even in a bare engine → the language. Supplied by the runtime, even if many runtimes have it → the runtime. MDN's *JavaScript* reference lists the language.",
                "Code that meets a runtime without what it uses fails **at that name**: `document is not defined`.",
                "`console.log` belongs to the **runtime**. Every runtime supplies its own `console.log`, which is why it behaves differently in each."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "What is JavaScript?",
              "href": "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Scripting/What_is_JavaScript",
              "note": "The fuller tour, including a worked example that adds behaviour to a page."
            },
            {
              "title": "Web APIs — index",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API",
              "note": "Everything the browser hands you. Skim it once to see the size of it; do not try to learn it."
            },
            {
              "title": "JavaScript reference",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference",
              "note": "The language itself. Notice how much smaller this section is than the one above."
            }
          ]
        }
      ],
      "_source": "lessons/what-js-is.md",
      "practice": {
        "id": "what-js-is",
        "outcome": 9,
        "steps": [
          {
            "kind": "predict",
            "title": "Language or runtime",
            "prompt": "Each question asks for one of three things. For a name on its own, pick **language** or **runtime**: is it part of JavaScript, or supplied by the *runtime environment*, the program that runs your JavaScript and decides what else it can reach (a browser tab, or Node)? For a `typeof` question, type what `typeof` gives. For a snippet that fails, pick what to question first: your code, or the runtime.",
            "items": [
              {
                "id": "p1",
                "code": "console.error",
                "choices": [
                  "language",
                  "runtime"
                ],
                "accept": [
                  "runtime"
                ],
                "miss": "**If you picked `language` because every runtime has a `console`: every runtime *chose* to supply one.** The language specification defines no `console` at all.\n",
                "why": "The runtime. `console`, with `error` and `log` alike, is in no version of the language. Each runtime supplies its own `console`, which is why its output looks different in Node and in a browser."
              },
              {
                "id": "p2",
                "question": "Typed into Node: `typeof document`",
                "accept": [
                  "undefined",
                  "\"undefined\"",
                  "'undefined'"
                ],
                "miss": "**If you answered `\"object\"`: that is the answer in a browser tab.** `document` is the page, and Node has no page to hand you.\n",
                "why": "`\"undefined\"`. Node never supplied a `document`, so the name is not there. `typeof` is the one question you can ask about a missing name without an error."
              },
              {
                "id": "p3",
                "question": "You paste a tutorial snippet into a Node script and get `ReferenceError: window is not defined`. Question your code, or the runtime you ran it in?",
                "choices": [
                  "My code",
                  "The runtime"
                ],
                "accept": [
                  "The runtime"
                ],
                "why": "`window` is a name only a browser supplies. Before rewriting anything, ask which runtime the snippet was written for — the code may be fine and in the wrong place."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "JSON.parse",
                "choices": [
                  "language",
                  "runtime"
                ],
                "accept": [
                  "language"
                ],
                "why": "The language, despite the web-sounding name. `JSON` is a built-in object like `Math`, and it works in every runtime."
              },
              {
                "id": "p5",
                "code": "setTimeout",
                "choices": [
                  "language",
                  "runtime"
                ],
                "accept": [
                  "runtime"
                ],
                "why": "The runtime. It feels like part of the language because every runtime you have met supplies it — but the runtime keeps the clock, so the runtime supplies the timer."
              },
              {
                "id": "p6",
                "question": "Typed into a browser tab: `typeof process`",
                "accept": [
                  "undefined",
                  "\"undefined\"",
                  "'undefined'"
                ],
                "why": "`\"undefined\"`. `process` is Node's object for the running program, and a browser tab never supplies it. This is the `typeof document` test from the other side."
              },
              {
                "id": "p7",
                "question": "A snippet in a browser tab throws `ReferenceError: require is not defined`. Question your code, or the runtime?",
                "choices": [
                  "My code",
                  "The runtime"
                ],
                "accept": [
                  "The runtime"
                ],
                "why": "`require` is how Node loads a module, and a browser tab does not supply it. The snippet was written for Node."
              }
            ],
            "hints": [
              {
                "when": "you answered language for console.error",
                "say": "Apply the test from the lesson: is it in the language, or does every runtime just happen to supply one? Having seen it everywhere is not evidence either way."
              },
              {
                "when": "you answered \"object\" for typeof document",
                "say": "Ask which runtime you are in first. `document` is the page — which runtime has a page, and which does not?"
              },
              {
                "when": "you answered code for the third",
                "say": "`is not defined` on a name like `window`, `document` or `require` means the runtime never supplied it. Check where the snippet was meant to run before you change a line of it."
              }
            ]
          },
          {
            "kind": "run",
            "title": "A runtime you have not met",
            "prompt": "The practice boxes on this site do not run your code in Node, or in the page. They run it in a **web worker** — a part of the browser that runs JavaScript with **no page attached**. Predict what each line does in that runtime — type what it prints, or pick what happens — then run it.\n",
            "filename": "worker.js",
            "code": "console.log(typeof Math);\nconsole.log(typeof setTimeout);\nconsole.log(typeof document);\nconsole.log(document.title);\nconsole.log(\"finished\");\n",
            "items": [
              {
                "id": "r1",
                "question": "What does `console.log(typeof setTimeout)` print here?",
                "accept": [
                  "function",
                  "\"function\""
                ],
                "why": "`function`. `setTimeout` comes from the runtime, and this runtime supplies it. \"From the runtime\" does not mean \"missing\" — it means \"there only where a runtime chose to provide it\"."
              },
              {
                "id": "r2",
                "question": "What does `console.log(typeof document)` print here?",
                "accept": [
                  "undefined",
                  "\"undefined\""
                ],
                "why": "`undefined`. A worker is a separate runtime inside the browser, and it has no page, so nothing gives your code a `document`. What matters is the runtime your code is in, not the browser around it."
              },
              {
                "id": "r3",
                "question": "What happens at `console.log(document.title)`?",
                "choices": [
                  "It prints undefined",
                  "It throws an error"
                ],
                "accept": [
                  "It throws an error"
                ],
                "miss": "**If you picked `undefined`: asking `typeof` about a missing name is safe, but *using* the name is not.** The program stops at that line with an error.\n",
                "why": "`ReferenceError: document is not defined`. Code that meets a runtime without what it uses fails at that name."
              },
              {
                "id": "r4",
                "question": "Does \"finished\" print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The error stops the run at the line that used `document`; nothing after it happens."
              }
            ],
            "hints": [
              {
                "when": "you expected document to exist because this is a browser",
                "say": "A browser contains several runtimes. A tab has a page; a worker does not. Ask what *this* runtime supplies, not what browsers usually have."
              },
              {
                "when": "you expected the program to carry on after the error",
                "say": "A thrown error ends the run where it happened. The lines before it have already done their work; the lines after it never start."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review — written for the wrong runtime",
            "prompt": "You asked an AI tool for *a Node script that saves a short report to a file and says when it is done*. This is what came back. You do not need to know every name in it: for each one, ask which runtime supplies it. Answer with a line number where one is asked for.\n",
            "filename": "save-report.js",
            "code": "const fs = require(\"node:fs\");   // Node's tools for files, under the name fs\n\nconsole.log(\"Saving the report\");\nfs.writeFileSync(\"report.txt\", \"3 scores saved\");\nalert(\"Report saved\");\ndocument.title = \"Saved\";\n",
            "items": [
              {
                "id": "c1",
                "question": "Run with Node. Which line is the first to fail? Type its number.",
                "accept": [
                  "5",
                  "line 5"
                ],
                "miss": "**If you typed 1 or 4: `require` and `fs` are Node's own, so Node runs those lines.** Look for a name Node never supplies.\n",
                "why": "Line 5. `alert` is a browser tab's — it pops up a box on the page — and Node has no page. `ReferenceError: alert is not defined`."
              },
              {
                "id": "c2",
                "question": "When it fails, does `report.txt` exist?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "miss": "**If you answered no: the error does not undo what already ran.** Node ran lines 1 to 4, doing each thing as it reached it.\n",
                "why": "Yes. Line 4 wrote the file before line 5 failed. An error stops what comes after it, not what came before — so a half-finished run can leave real changes behind."
              },
              {
                "id": "c3",
                "question": "You change line 5 to use `console.log` and run again. Which line fails now? Type its number.",
                "accept": [
                  "6",
                  "line 6"
                ],
                "why": "Line 6. `document` is the browser's object for the page, and Node has no page either. A run stops at the *first* missing name, so a second missing name stays hidden until the first one is fixed."
              },
              {
                "id": "c4",
                "question": "The tool offers a browser version that starts with the same line 1. In a browser tab, which line fails first? Type its number.",
                "accept": [
                  "1",
                  "line 1"
                ],
                "why": "Line 1: `ReferenceError: require is not defined`. The same test from the other side — `require` and `node:fs` are Node's, and a tab supplies neither."
              }
            ],
            "hints": [
              {
                "when": "you picked line 1 or line 4 for Node",
                "say": "Run the lesson's test on each name: which runtime supplies `require`? Which supplies `alert`? The file is only wrong where it reaches for something *this* runtime lacks."
              },
              {
                "when": "you thought the failure undid the file write",
                "say": "Node runs the file once, top to bottom, doing each line as it reaches it. There is no going back when a later line fails."
              },
              {
                "when": "you did not know what alert is",
                "say": "Read the name, and ask what it needs: an alert is a box that pops up *on a page*. Which runtime has a page?"
              }
            ]
          }
        ]
      }
    },
    "where-code-runs": {
      "id": "where-code-runs",
      "title": "Where your code runs",
      "kind": "read",
      "outcome": 9,
      "shape": "conceptual",
      "why": "Beginners read a file as if all of it runs, all at once, whenever it is needed. The first bugs that make no sense — an element that is plainly in the HTML coming back `null` — come from the runtime's schedule, and asynchrony (outcome 9) is built on the same schedule later.",
      "concepts": [
        "runtime.host",
        "diag.read-errors"
      ],
      "lede": "You wrote the file, but you do not decide when it runs. The runtime environment does: the program that runs your JavaScript, such as a browser tab or Node. It runs your file once, top to bottom, and after that only when it calls a function you handed it. Many of the most baffling early bugs are code that ran at a moment its author did not expect.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What JavaScript is · When it breaks: reading errors"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Parse** — the browser reading your HTML and building the page from it, tag by tag, in order.",
          "entries": [
            "**Parse** — the browser reading your HTML and building the page from it, tag by tag, in order."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about *when* code runs. Pick an answer, or type a line number where one is asked for. A guess is fine; that is what this is for."
            },
            {
              "code": "console.log(\"one\");\nsetTimeout(() => console.log(\"two\"), 1000);\nconsole.log(\"three\");",
              "lang": "js",
              "file": "timer.js"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "In `timer.js`, which word is printed second?",
                          "choices": [
                            "two",
                            "three"
                          ],
                          "accept": [
                            "three"
                          ],
                          "miss": "**If you answered `two` — `setTimeout` is not a pause.** It hands the function to the runtime, asks for it to be called in a second, and the file carries straight on to the next line.\n",
                          "why": "`three`. The function holding `\"two\"` was handed over, not run. Node calls it a second later, after the file has finished.\n"
                        },
                        {
                          "question": "Run `timer.js` in Node. Does the program end right after it prints its second word?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes because that is the last line — Node is still holding a function it promised to call.** Node waits for it before it lets the program end.\n",
                          "why": "No. The file is finished, but a timer is pending. Node stays alive, prints `two`, and only then exits.\n"
                        },
                        {
                          "question": "A script in `<head>` runs `document.querySelector(\"h1\")`; the `<h1>` is further down the page. What does it return?",
                          "choices": [
                            "The h1 element",
                            "null",
                            "An error"
                          ],
                          "accept": [
                            "null"
                          ],
                          "miss": "**If you answered the element — it is in the file, but the browser had not built it yet.** The browser ran your script the moment it reached the tag, before it read the rest of the page.\n",
                          "why": "`null` — \"nothing matched\". Not an error, and not the element: at the moment the script ran, there was no `<h1>` to find.\n"
                        },
                        {
                          "question": "Line 1 of that script keeps what `querySelector` returned under the name `heading`. Line 2 sets `heading.textContent`. The console reports an error. At which line? Type its number.",
                          "accept": [
                            "2",
                            "line 2",
                            "two"
                          ],
                          "miss": "**If you answered 1 — line 1 is where the cause is, not where the error is reported.** Line 1 got `null` without complaint. The error is reported at line 2, where the `null` was *used*.\n",
                          "why": "Line 2. The error is `TypeError: Cannot set properties of null`. It names the symptom on line 2; the cause is on line 1, one line earlier.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "A script uses a name before the line that assigns it. What happens?",
                          "choices": [
                            "It gets None",
                            "It raises an error"
                          ],
                          "accept": [
                            "It raises an error"
                          ],
                          "why": "Python has no hoisting: until the line that assigns the name runs, the name does not exist, so using it raises a `NameError`.\n"
                        },
                        {
                          "question": "Does Python have an equivalent of \"the page is not built yet\"?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Not for a script — there is no document being parsed alongside your code. The nearest equivalent is a resource that is not open yet.\n"
                        },
                        {
                          "question": "Does import order matter in Python?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "It does. A module runs top to bottom the first time it is imported. That is the same \"once, in order\" rule this lesson describes, applied to modules.\n"
                        },
                        {
                          "question": "Two Python processes run the same script. Do they share variables?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Each process has its own memory, so they share no variables. A browser tab is isolated from other tabs in the same way.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Does the compiler catch \"used before assigned\"?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "The compiler's definite-assignment analysis rejects it before the program runs. Of the three tracks, C# catches this mistake earliest.\n"
                        },
                        {
                          "question": "Does the order of methods in a class matter?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "The order of methods in a class does not matter. Only the statements inside a method run in order.\n"
                        },
                        {
                          "question": "Where does a console app start? Name it.",
                          "accept": [
                            "main",
                            "Main",
                            "the Main method",
                            "top-level statements"
                          ],
                          "why": "C# has an explicit entry point: the `Main` method, or the top-level statements that stand in for it. A browser script has none; it starts wherever the browser reaches it in the page. That is a real structural difference.\n"
                        },
                        {
                          "question": "Two processes run the same program. Do they share variables?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Each process has its own memory, as in the other tracks. Threads *within* one process do share memory. Shared-memory threads are the concept JavaScript lacks, so this course meets them later, as an idea.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say when the runtime runs my code: once top to bottom, then only when it calls something I handed it",
                "I can predict what code finds when it looks for something the runtime has not made yet",
                "I can read an error that names a symptom and find the cause on an earlier line"
              ]
            }
          ]
        },
        {
          "h": "Once, top to bottom"
        },
        {
          "p": "When the runtime starts your file, it runs it **once, from the first line to the\nlast**, doing each thing as it reaches it. It does not go back and run the\nfile a second time, and it does not run a line before it reaches that line."
        },
        {
          "p": "That sounds too obvious to need saying. The next section is why it is worth\nsaying: some lines ask the runtime to run code later, after the file has finished."
        },
        {
          "h": "Then only when the runtime calls you back"
        },
        {
          "p": "Some lines do not do their work on the spot. They **hand the runtime a\nfunction** and ask it to call that function later. `setTimeout` is the\nsimplest: \"here is a function — call it in a second.\""
        },
        {
          "p": "You are handing the runtime a piece of code to run later — the *Functions as values* module explains how that works. For now, read `() => console.log(\"two\")` as *a\npackage holding the statement `console.log(\"two\")`*, and the `1000` after it as\nthe delay in milliseconds (1000 ms is one second)."
        },
        {
          "code": "console.log(\"one\");                              // runs now\nsetTimeout(() => console.log(\"two\"), 1000);      // hands a function over; does NOT run it\nconsole.log(\"three\");                            // runs now\n// Prints: one, three … and a second later, two",
          "lang": "js",
          "file": "timer.js"
        },
        {
          "p": "**The `setTimeout` line runs while the file runs. The function inside it\ndoes not.** The function waits with the runtime until the runtime decides to call\nit. In JavaScript, the runtime never calls it in the middle of your file: it\nwaits until the file has finished, even if the delay is already up."
        },
        {
          "p": "What happens after the last line depends on which runtime is running the file.\nNode and a browser tab do different things:"
        },
        {
          "table": {
            "head": [
              "After the last line…",
              "In Node",
              "In a browser tab"
            ],
            "rows": [
              [
                "Nothing handed over",
                "The program ends",
                "The page stays open"
              ],
              [
                "A timer still pending",
                "Node waits, calls it, **then** ends",
                "The page stays open; the browser calls it when the time comes"
              ]
            ]
          }
        },
        {
          "p": "In both runtimes, once the last line has run, your code runs again only when\nthe runtime calls a function you gave it. You did not write that schedule. The\nruntime keeps it."
        },
        {
          "block": "try",
          "label": "watch the schedule",
          "body": [
            {
              "p": "Open your browser's console and type the three lines of `timer.js` as one\nentry: press **Shift+Enter** at the end of each line to start a new one without\nrunning it, then **Enter** to run all three. Watch the order: `one`, `three`,\nthen — after a pause — `two`."
            },
            {
              "p": "The console also shows a grey `undefined` before `two`. Your code did not print\nit. After running what you typed, the console shows the value of the last line\nyou entered, and that line, `console.log(\"three\")`, prints a word but produces\nno value, so the console shows `undefined`."
            },
            {
              "p": "Now change `1000` to `5000` and run it again. Only the pause changes. The\n*order* does not depend on how long the timer is. It depends on the function\nwaiting until the browser calls it, and the browser calls it only after the\nlast line has run."
            }
          ]
        },
        {
          "h": "One small browser example"
        },
        {
          "p": "A browser tab is a runtime with one more thing on its schedule: it builds the\npage by reading your HTML **top to bottom**, and when it reaches an ordinary\n`<script>` tag, it stops and runs that script right there."
        },
        {
          "code": "<head>\n  <script src=\"main.js\"></script>   <!-- the browser runs main.js HERE -->\n</head>\n<body>\n  <h1>My page</h1>                  <!-- …and only then reads this -->\n</body>",
          "lang": "html",
          "file": "index.html"
        },
        {
          "code": "const heading = document.querySelector(\"h1\");\nheading.textContent = \"Changed!\";",
          "lang": "js",
          "file": "main.js"
        },
        {
          "p": "Read line 1 as *\"find the first `<h1>` on the page, and keep what you found\nunder the name `heading`\"*. Line 2 sets that heading's text. (Naming a value\nwith `const` is the first lesson of the next module; `.textContent` is part of\nwhat a browser page hands you.)"
        },
        {
          "p": "The HTML is fine. The JavaScript is fine. The *order* is wrong. When\n`main.js` ran, the browser had not reached the `<body>`, so there was no\n`<h1>` yet — `querySelector` looked, found nothing, and correctly returned\n`null`."
        },
        {
          "p": "One attribute changes the schedule: `<script defer src=\"main.js\">` asks the\nbrowser to run the script after it has built the whole page. How and when pages\nload is a topic of its own in web development. What this course needs from it\nis the idea underneath: **the browser, not your file, decided when that code\nran.**"
        },
        {
          "h": "Reading the error it produces"
        },
        {
          "p": "The console does not say \"the `<h1>` did not exist yet\". It says this:"
        },
        {
          "code": "Uncaught TypeError: Cannot set properties of null (setting 'textContent')\n    at main.js:2:21",
          "lang": "text",
          "file": "console"
        },
        {
          "p": "Read it in three parts, in this order:"
        },
        {
          "list": [
            "**Where** — `main.js:2:21`. The file, the line, and the column where things went wrong.",
            "**What** — `Cannot set properties of null`. Line 2 tried to use something that was `null`.",
            "**Why** — the error cannot tell you why. Ask where the `null` *came from*. Line 1 put it in `heading`, without complaint."
          ],
          "ordered": true
        },
        {
          "block": "notice",
          "label": "The error names the symptom",
          "body": [
            {
              "p": "Line 2 is where the `null` was used. Line 1 is where it came from, and the\ncause is earlier still: *when* line 1 ran. An error points at the line that\nnoticed the problem, which is often not the line that caused it."
            }
          ]
        },
        {
          "p": "Browsers word the error differently — Firefox says `heading is null` — but\nevery browser gives you the where and the what, and leaves the why to you."
        },
        {
          "scaffold": {
            "label": "Working out what existed when your line ran",
            "body": [
              {
                "h": "Working out what existed when your line ran"
              },
              {
                "p": "Three questions, in order, whenever a value is not what you expected."
              },
              {
                "code": "// 1. Where did it fail? Read the file and line from the error.\n\n// 2. What was wrong there? Usually a value — null, undefined — that the\n//    line tried to use.\n\n// 3. When did the line that PRODUCED that value run?\n//    - while the file ran top to bottom -> did the thing it looked for\n//      exist yet?\n//    - inside a function handed to the runtime -> has the runtime called it\n//      yet?",
                "lang": "js",
                "file": "what-existed.js"
              },
              {
                "p": "Now apply it. Say what each logs or finds."
              },
              {
                "code": "// A. <head><script src=\"a.js\"></script></head><body><h1>Hi</h1></body>\n//    a.js: document.querySelector(\"h1\")                  ______\n\n// B. <head><script defer src=\"b.js\"></script></head><body><h1>Hi</h1></body>\n//    b.js: document.querySelector(\"h1\")                  ______\n\n// C. console.log(\"first\");\n//    setTimeout(() => console.log(\"second\"), 0);\n//    console.log(\"third\");                  the order printed: ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "A is `null`, B is the element, C is `first`, `third`, `second`. C is the same\nkind of timing question as A, with no page involved: the code that prints\n`\"second\"` was handed to the runtime, and even with a delay of 0 the runtime\ndoes not call it until the last line has run."
              }
            ]
          }
        },
        {
          "video": {
            "title": "The runtime's schedule, in Node and a browser",
            "caption": "Node holding on until its timer fires, the same lines in a browser console, then the null-element bug in a real page — the error clicked through to its line, and fixed with one word.\n"
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the four questions at the top of this lesson again, from memory. Then\nsay, in one sentence, when the runtime runs a function you pass to `setTimeout`,\nand why it can never run in the middle of your file."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A teammate hits the null-element bug and fixes it by wrapping the lookup in a\ntimer, so the page has \"time to load\":"
            },
            {
              "code": "setTimeout(() => {\n  const heading = document.querySelector(\"h1\");\n  heading.textContent = \"Changed!\";\n}, 100);",
              "lang": "js",
              "file": "main.js"
            },
            {
              "p": "Two positions:"
            },
            {
              "list": [
                "**Fine** — it works on every machine they have tried, and it is one change.",
                "**Use `defer` instead** — ask the browser for the thing you actually depend on."
              ]
            },
            {
              "p": "Pick one and say why. Say what the timer version assumes about the browser's\nschedule, and what would have to be true of a visitor's phone for that\nassumption to fail. Then say which line the error would point at if the\nassumption did fail."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "The runtime runs your file **once, top to bottom**. After that, your code runs **only when the runtime calls a function you handed it**, and never while the file is still running.",
                "Handing a function to `setTimeout` is not running it. Node stays alive while it still holds a function it has not called; a browser tab stays open whether or not it holds one.",
                "A `<head>` script runs before the page below it exists, so `querySelector` returns **`null`**.",
                "An error names **where** and **what**; the **why** is often a line earlier. Ask where the bad value came from, and when that line ran."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "setTimeout()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/setTimeout",
              "note": "What the runtime does with the function you hand it."
            },
            {
              "title": "The <script> element",
              "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/script",
              "note": "The `defer` attribute, precisely defined."
            }
          ]
        }
      ],
      "_source": "lessons/where-code-runs.md",
      "practice": {
        "id": "where-code-runs",
        "outcome": 9,
        "steps": [
          {
            "kind": "predict",
            "title": "When does it run?",
            "prompt": "Two predictions about when the runtime runs your code, and the first move when an error appears. Type what each one asks for — the letters, a number of seconds, a line number — or pick an answer where there are buttons.",
            "items": [
              {
                "id": "p1",
                "code": "console.log(\"a\");\nsetTimeout(() => console.log(\"b\"), 0);\nconsole.log(\"c\");\n— the three letters, in the order printed?\n",
                "accept": [
                  "a c b",
                  "a, c, b",
                  "a,c,b",
                  "acb",
                  "a then c then b"
                ],
                "miss": "**If you answered `a b c` — a delay of 0 is not \"now\".** The `setTimeout` line hands the function to the runtime, and the runtime never calls it while the file is still running.\n",
                "why": "`a c b`. The function holding `\"b\"` waits with the runtime until the file has finished — even with a delay of 0."
              },
              {
                "id": "p2",
                "code": "setTimeout(() => console.log(\"late\"), 3000);\nconsole.log(\"end\");\n— run with Node: about how many seconds until the program ends?\n",
                "accept": [
                  "3",
                  "3 seconds",
                  "three",
                  "about 3",
                  "3s",
                  "about 3 seconds"
                ],
                "miss": "**If you answered 0 — the file is finished, but Node is still holding a function it promised to call.** It waits for it before it lets the program end.\n",
                "why": "About 3. `end` prints at once, Node waits for the pending timer, prints `late`, and only then exits."
              },
              {
                "id": "p3",
                "code": "Uncaught TypeError: Cannot read properties of null (reading 'value')\n    at form.js:4:27\n— which line of form.js do you open first?\n",
                "accept": [
                  "4",
                  "line 4",
                  "form.js:4"
                ],
                "why": "Line 4 — *where* comes first, and the error names it. Then ask where that `null` came from: the cause is often a line earlier."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "setTimeout(() => console.log(\"later\"), 0);\nconsole.log(\"now\");\n— the two words, in the order printed?\n",
                "accept": [
                  "now later",
                  "now, later",
                  "now,later",
                  "now then later"
                ],
                "why": "`now`, then `later`. The code holding `\"later\"` was handed to the runtime, and the runtime does not call it until the last line has run — even with a delay of 0."
              },
              {
                "id": "p5",
                "code": "setTimeout(() => console.log(\"hi\"), 1000);\n— in a browser tab: once \"hi\" prints, does the page close?\n",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. A tab stays open whether or not anything is pending. Node is the runtime that ends the program when nothing is left to call. The same code ends differently in the two runtimes."
              },
              {
                "id": "p6",
                "question": "An error points at line 9 of `app.js`. Must the mistake be on line 9?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. Line 9 is where a bad value was *used*. The error names the symptom; the cause is wherever the value came from."
              }
            ],
            "hints": [
              {
                "when": "you put b second",
                "say": "`setTimeout` is not a pause, and a delay of 0 is not \"now\". What does the line hand the runtime — and when does the runtime call it?"
              },
              {
                "when": "you said the Node program ends at once",
                "say": "After the last line, Node checks whether it still holds anything you handed it. Is there a timer pending?"
              },
              {
                "when": "you went looking for the cause before the line the error names",
                "say": "Read in order: where, then what, then why. Start with the file and line the error gives, then walk back from there."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Watch the schedule",
            "prompt": "Predict the order — type the word for each — then run it. The runner waits for the timers before it shows you the output.",
            "filename": "schedule.js",
            "code": "console.log(\"start\");\nsetTimeout(() => console.log(\"slow\"), 200);\nsetTimeout(() => console.log(\"quick\"), 0);\nconsole.log(\"end\");\n",
            "items": [
              {
                "id": "r1",
                "question": "Which word is printed second?",
                "accept": [
                  "end",
                  "\"end\""
                ],
                "miss": "**If you answered `quick` — a delay of 0 still waits for the file to finish.** Both timer lines only hand functions over.\n",
                "why": "`end`. Both `setTimeout` lines ran — and all they did was hand the runtime a function. The file carries on."
              },
              {
                "id": "r2",
                "question": "Which word is printed third?",
                "accept": [
                  "quick",
                  "\"quick\""
                ],
                "why": "`quick`. After the file has finished, the runtime calls each function when its time comes: 0 ms before 200 ms."
              },
              {
                "id": "r3",
                "question": "Which word is printed last?",
                "accept": [
                  "slow",
                  "\"slow\""
                ],
                "miss": "**If you answered `quick` because its line comes later — the runtime goes by each timer's time, not by the order of the lines.**\n",
                "why": "`slow`. It was handed over first but asked for the later time."
              }
            ],
            "hints": [
              {
                "when": "you put a timer's word before end",
                "say": "Nothing a timer holds runs until the file has finished. Which words are printed by the file itself?"
              },
              {
                "when": "you ordered the timers by their lines",
                "say": "The lines decide when each function is *handed over*. The delays decide when the runtime *calls* them."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace the runtime's schedule",
            "prompt": "One program, traced the way the runtime runs it. Line numbers are on the left. Type a line number, a number, or the words in order, as each question asks.",
            "items": [
              {
                "id": "t1",
                "code": "1  console.log(\"A\");\n2  setTimeout(() => {\n3    console.log(\"B\");\n4    console.log(\"C\");\n5  }, 1000);\n6  console.log(\"D\");\n7  setTimeout(() => console.log(\"E\"), 0);\n8  console.log(\"F\");\n— the runtime runs line 2. Which line does it run next?\n",
                "accept": [
                  "6",
                  "line 6"
                ],
                "miss": "**If you answered 3 — lines 3 and 4 are inside the code that was handed over.** Line 2 runs, the runtime takes the package, and the file carries on at line 6.\n",
                "why": "Line 6. Running line 2 hands lines 3 and 4 to the runtime; they wait until the runtime calls them."
              },
              {
                "id": "t2",
                "question": "Same program — how many words are printed before the file reaches its last line? Type a number.",
                "accept": [
                  "3",
                  "three"
                ],
                "why": "Three: `A`, `D` and `F`. Lines 1, 6 and 8 print while the file runs; everything else was handed over."
              },
              {
                "id": "t3",
                "question": "Same program — which word does the runtime print first after the file has finished? Type it.",
                "accept": [
                  "e",
                  "\"e\""
                ],
                "miss": "**If you answered `B` because its line comes first — the runtime goes by each timer's delay, not by the order of the lines.**\n",
                "why": "`E`. Its delay is 0 ms; the package holding lines 3 and 4 asked for 1000 ms."
              },
              {
                "id": "t4",
                "question": "Same program — all six words, in the order printed? Type them separated by spaces.",
                "accept": [
                  "a d f e b c",
                  "a, d, f, e, b, c",
                  "adfebc"
                ],
                "why": "`A D F E B C`. Running the file prints A, D, F. Then the runtime calls the 0 ms package (E), and a second later the 1000 ms one, which runs its two lines in order (B, C)."
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "1  setTimeout(() => console.log(\"x\"), 500);\n2  setTimeout(() => console.log(\"y\"), 100);\n3  console.log(\"z\");\n— the three letters, in the order printed?\n",
                "accept": [
                  "z y x",
                  "z, y, x",
                  "zyx"
                ],
                "why": "`z y x`. `z` prints while the file runs. After the file has finished, the runtime calls each package when its time comes: 100 ms before 500 ms."
              },
              {
                "id": "t6",
                "question": "`setTimeout` — the language, or the runtime?",
                "choices": [
                  "The language",
                  "The runtime"
                ],
                "accept": [
                  "The runtime"
                ],
                "why": "The runtime. The runtime keeps the schedule, so the runtime supplies `setTimeout`, the function that adds your code to that schedule. That is why what happens around a timer can differ from one runtime to the next."
              }
            ],
            "hints": [
              {
                "when": "you traced straight from line 2 into line 3",
                "say": "Line 2 runs — and all it does is hand code over. Where is the runtime's next line *outside* that package?"
              },
              {
                "when": "you ordered the timers by their lines",
                "say": "The lines decide when each package is *handed over*. The delays decide when the runtime *calls* them."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review — a timer is not a pause",
            "prompt": "A classmate wants this script to print `Saving…`, then — one second later — `Saved`, and only after that `Goodbye`. They say it \"waits a second on line 3\". Review it: pick an answer or type a line number.\n",
            "filename": "save.js",
            "code": "// Save, wait a second, confirm, then say goodbye\nconsole.log(\"Saving…\");\nsetTimeout(() => console.log(\"Saved\"), 1000);\nconsole.log(\"Goodbye\");\n",
            "items": [
              {
                "id": "c1",
                "question": "Which word is actually printed second?",
                "choices": [
                  "Saved",
                  "Goodbye"
                ],
                "accept": [
                  "Goodbye"
                ],
                "miss": "**If you answered `Saved` — line 3 does not wait.** It hands the runtime the code that prints `Saved`, and the file carries straight on.\n",
                "why": "`Goodbye`. Line 3 finishes at once; `Saved` prints a second later, after the file has finished."
              },
              {
                "id": "c2",
                "question": "Which line holds the mistaken belief? Type its number.",
                "accept": [
                  "3",
                  "line 3"
                ],
                "why": "Line 3. The comment and the classmate both treat it as a pause. It is a hand-over."
              },
              {
                "id": "c3",
                "question": "They try again with line 4 moved above line 3. Does `Saved` now print before `Goodbye`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. `Goodbye` still prints while the file runs, and `Saved` still comes a second after the file ends. Reordering the lines that run while the file runs cannot make any of them run after the handed-over code."
              },
              {
                "id": "c4",
                "question": "They change `1000` to `0`. Does `Saved` now print before `Goodbye`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**A delay of 0 is not \"now\".** The runtime still waits for the file to finish before it calls anything it was handed.\n",
                "why": "No. Only the pause changes. The order does not depend on the delay. The code that prints `Saved` waits until the runtime calls it, and the runtime calls it only after the file has finished."
              }
            ],
            "hints": [
              {
                "when": "you expected Saved second",
                "say": "Ask what line 3 does *while it runs*: it hands code over and finishes. What is the next line the file runs?"
              },
              {
                "when": "you thought reordering or a delay of 0 would fix it",
                "say": "Anything handed to the runtime runs after the file has finished. What is printed *while the file runs*, whatever its order?"
              }
            ]
          }
        ]
      }
    },
    "first-splash": {
      "id": "first-splash",
      "title": "Reading a program you did not write",
      "kind": "read",
      "outcome": 10,
      "shape": "procedural",
      "why": "Nearly every program a student reads — a tutorial, a teammate's file, an AI tool's answer — contains things they have not been taught, and every critique task in the course assumes they can find its shape anyway. This is the first pass at that reading procedure.",
      "concepts": [
        "diag.code-review"
      ],
      "lede": "You are about to read a complete, working program containing at least five things you have not been taught. That is deliberate. The skill this lesson builds is not writing code — it is reading code you do not fully understand and finding its shape anyway. You will do that for the rest of your career.",
      "margins": [
        {
          "label": "MARGIN NOTE",
          "body": "Being able to read past what you do not know is a learnable skill, and it is the one that separates people who can join an existing codebase from people who can only start new ones."
        },
        {
          "label": "SEE ALSO",
          "body": "Where your code runs · When it breaks: reading errors"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**State** — the values a program keeps between one event and the next. Here: the secret number, and how many guesses have been used.",
          "entries": [
            "**State** — the values a program keeps between one event and the next. Here: the secret number, and how many guesses have been used."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Three questions on the runtime environment: the program that runs your\nJavaScript, such as a browser tab or Node. Answer from memory. The program in\nthis lesson depends on the third question: how many times the file runs."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`console.log` — language or runtime?",
                    "choices": [
                      "language",
                      "runtime"
                    ],
                    "accept": [
                      "runtime"
                    ],
                    "miss": "**If you answered `language` because every tutorial uses it — the common JavaScript runtimes each supply a `console`, but that does not make it part of the language.** JavaScript the language defines no way to print anything.\n",
                    "why": "The runtime. A browser tab and Node each hand you their own `console`, which is why it behaves differently in each.\n"
                  },
                  {
                    "question": "Typed into Node, what does `typeof document` give?",
                    "accept": [
                      "undefined",
                      "\"undefined\"",
                      "'undefined'"
                    ],
                    "miss": "**If you answered `\"object\"` — that is the answer in a browser tab.** `document` is the page, and Node has no page.\n",
                    "why": "`\"undefined\"`. The browser hands you `document`; Node does not, so the name is simply not there.\n"
                  },
                  {
                    "question": "The runtime runs your file. How many times does it run it top to bottom? Type a number.",
                    "accept": [
                      "1",
                      "once",
                      "one"
                    ],
                    "miss": "**If you answered more than once — after its first run, the runtime never runs the file again.** It runs only the functions you handed it, when it decides to.\n",
                    "why": "Once. After that, your code runs only when the runtime calls something you handed it.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can read an unfamiliar expression from its names and from the inside out",
                "I can find a program's entry point and event handlers, its state, and every place the state changes",
                "I can find a defect in the order of a program's branches",
                "I can identify what I do not know precisely enough to look it up"
              ]
            }
          ]
        },
        {
          "block": "notice",
          "label": "Read this first",
          "body": [
            {
              "p": "This program uses a lot you have not been taught, and that is on purpose. Here is how to read each piece for now, and where it is taught properly:"
            },
            {
              "list": [
                "`let` and `const` — read them as *\"this name now holds that value\"*. (Values, types and operators.)",
                "`if`, `else if`, `else` — *\"if this is true, do this block; otherwise…\"*. (The Branching module.)",
                "`function checkGuess() { … }` — a named block of statements that runs when something calls it. (Functions.)",
                "`guessField.value`, `lastResult.textContent` — a part of something on the page: the text in the box, the text of a message. (Objects.)"
              ]
            },
            {
              "p": "You are here for the shape of the thing, not the grammar. The grammar will land better for having been seen in use."
            }
          ]
        },
        {
          "h": "The program"
        },
        {
          "p": "A number-guessing game. It picks a secret number from 1 to 100. You get ten guesses. After each one it tells you whether you were high or low, and when the game ends it offers you a fresh one."
        },
        {
          "p": "The program is small enough to hold in your head, and big enough to use many of the language's basic building blocks."
        },
        {
          "p": "Before reading any of the code, know what you are looking for. An interactive program of this size usually breaks into four pieces:"
        },
        {
          "table": {
            "head": [
              "Part",
              "Its job",
              "In this game"
            ],
            "rows": [
              [
                "**State**",
                "What the program remembers",
                "The secret number; how many guesses so far"
              ],
              [
                "**Entry point and event handlers**",
                "What starts things happening: the file's first run, then every reaction it sets up",
                "The file runs once; after that, each click on Submit calls `checkGuess`"
              ],
              [
                "**The step**",
                "What happens once per interaction",
                "Check one guess; report high, low, or right"
              ],
              [
                "**The end**",
                "How it finishes, and how it restarts",
                "Win or run out; then offer a new game"
              ]
            ]
          }
        },
        {
          "p": "Here is the whole program with every function body emptied out. The five steps below read it in a fixed order, and learning that order is the skill this lesson teaches. People who are good at reading code are not reading faster than you. They are reading in a different order."
        },
        {
          "code": "// ---- state -------------------------------------------------\nlet secret = /* a random number from 1 to 100 */;\nlet guessCount = 1;\n\n// ---- the step ----------------------------------------------\nfunction checkGuess() {\n  // read what they typed\n  // right?        -> say so, end the game\n  // out of turns? -> say so, end the game\n  // otherwise     -> say \"too high\" or \"too low\"\n  // count the guess, clear the box, put the cursor back\n}\n\n// ---- the end -----------------------------------------------\nfunction setGameOver() {\n  // switch off the input\n  // add a \"Start new game\" button; hand resetGame to the browser for its click\n}\n\nfunction resetGame() {\n  // clear the messages, switch the input back on\n  // pick a new secret number, reset the count\n}\n\n// ---- the click handler ---------------------------------------\nsubmitButton.addEventListener(\"click\", checkGuess);",
          "lang": "text",
          "file": "guess.js — the skeleton"
        },
        {
          "beat": "step",
          "label": "Read past what you do not know",
          "body": [
            {
              "p": "You will meet names you have not been taught on every line. Two moves get you past most of them."
            },
            {
              "p": "**Read the name.** `\"banana\".includes(\"nan\")` — you have not been taught `includes`, but the name says it: does `\"banana\"` include `\"nan\"`? A name that reads as a yes-or-no question usually answers `true` or `false`. Guess from the name, check the guess against what happens next, and keep reading."
            },
            {
              "p": "**Read nested calls from the inside out.** The secret number is picked by this:"
            },
            {
              "code": "Math.floor(Math.random() * 100) + 1",
              "lang": "js",
              "file": "secret.js"
            },
            {
              "p": "Start at the innermost call and work outwards, one layer at a time. At each layer, keep track of two things: the **smallest** value it can give, and the **largest**."
            },
            {
              "table": {
                "head": [
                  "Layer",
                  "What it does",
                  "Smallest",
                  "Largest"
                ],
                "rows": [
                  [
                    "`Math.random()`",
                    "a random number from 0 up to 1, **never reaching** 1",
                    "0",
                    "0.999…"
                  ],
                  [
                    "`… * 100`",
                    "stretches that range 100 times wider",
                    "0",
                    "99.999…"
                  ],
                  [
                    "`Math.floor(…)`",
                    "rounds **down** to a whole number",
                    "0",
                    "99"
                  ],
                  [
                    "`… + 1`",
                    "adds one to every value",
                    "1",
                    "100"
                  ]
                ]
              }
            },
            {
              "p": "The smallest and largest values are where mistakes hide. Read only the first three layers and you would think the game can pick 0 and can never pick 100. The last layer, `+ 1`, is what moves the whole range from 0–99 to 1–100."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "What does `\"Splash\".startsWith(\"Sp\")` give? Guess from the name, and type the value.",
                    "accept": [
                      "true"
                    ],
                    "miss": "**Read the name as a question: does `\"Splash\"` start with `\"Sp\"`?** A yes-or-no method answers `true` or `false`.\n",
                    "why": "`true`. You did not need to be taught `startsWith` to read it from its name.\n"
                  },
                  {
                    "question": "What range of numbers can `Math.floor(Math.random() * 6) + 1` give? Type it as lowest to highest, like `2 to 9`.",
                    "accept": [
                      "1 to 6",
                      "1-6",
                      "1..6",
                      "1 through 6",
                      "1 to 6 inclusive"
                    ],
                    "miss": "**If you answered 0 to 6, or 1 to 7, read it inside out again and check the ends.** `random()` never reaches 1, so `Math.random() * 6` never reaches 6, and the floored value is at most 5. The `+ 1` moves the range from 0–5 to 1–6.\n",
                    "why": "1 to 6 — a die. `random()` gives 0 up to 0.999…; times 6 and floored gives 0–5; plus one gives 1–6. Same shape as the secret number, different size.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Find the entry point and the event handlers",
          "body": [
            {
              "p": "Ask first: **what starts things happening?** Everything else is reached from there."
            },
            {
              "p": "A program's **entry point** is where it starts: here, the top of the file. When the browser runs this file, it does three things, once, top to bottom: it sets up two values, it defines three functions, and, on the last line, it hands one of those functions, `checkGuess`, to the browser:"
            },
            {
              "code": "submitButton.addEventListener(\"click\", checkGuess);",
              "lang": "js",
              "file": "game.js"
            },
            {
              "p": "Then the file is finished. Nothing else happens until the player clicks Submit, and then the browser calls `checkGuess`. A function handed over like this is an **event handler**: code the program runs in reaction to something happening. (MDN also calls it an event *listener*, which is where `addEventListener` gets its name.) The entry point and the handlers it hands over are everything that can make this program do anything."
            },
            {
              "p": "**Look at what follows the name `checkGuess`: nothing.** No `()`. In *Your first program*, the parentheses were what made a call happen. Here they are left off on purpose: you are handing the browser a piece of code to run later — the *Functions as values* module explains how that works. With the parentheses — `checkGuess()` — you would be calling `checkGuess` *now*, and handing the browser whatever it produced. Checking whether a function name has `()` after it is the most useful piece of syntax-reading in this lesson."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`button.addEventListener(\"click\", check);` — when does `check` run?",
                    "choices": [
                      "Now, as this line runs",
                      "Later, on each click"
                    ],
                    "accept": [
                      "Later, on each click"
                    ],
                    "miss": "**If you said now — look at what follows the name: nothing.** No `()` means the function is being handed over, not called.\n",
                    "why": "Later, each time the button is clicked. The browser calls it, not your file.\n"
                  },
                  {
                    "question": "`button.addEventListener(\"click\", check());` — when does `check` run?",
                    "choices": [
                      "Now, as this line runs",
                      "Later, on each click"
                    ],
                    "accept": [
                      "Now, as this line runs"
                    ],
                    "miss": "**If you said on click — look at the `()`.** They call `check` now, while this line runs, and hand the browser whatever it returned. Here that is `undefined`, so clicking does nothing at all.\n",
                    "why": "Now, once, as the file runs. Two characters changed and the program's whole timing changed with them.\n"
                  },
                  {
                    "question": "When this file first runs, how many functions does it hand to the browser? Type a number.",
                    "accept": [
                      "1",
                      "one"
                    ],
                    "miss": "**If you answered 2 — `resetGame` is handed over too, but inside `setGameOver`, which has not run yet.** It only runs when a game ends.\n",
                    "why": "One: `checkGuess`. The second hand-over happens later, when the game ends and `setGameOver` adds the new-game button.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Find the state, and where it changes",
          "body": [
            {
              "p": "Next: **what does the program remember between clicks?** State is usually declared at the top of the file and changed in only a few places. Many bugs live in those places, so find every one."
            },
            {
              "p": "Take `secret`. It is set on the first line of the file. Search the skeleton for `secret` and there is one more place: `resetGame` picks a new secret number. The new number has to be picked inside a function, because the top of the file runs only once. The browser never runs it again, so a new game needs a function the browser *can* call."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Not counting its declaration at the top, how many functions change `guessCount`? Type a number.",
                    "accept": [
                      "2",
                      "two"
                    ],
                    "miss": "**If you answered 1, you found the count going up and missed it going back down.** Search every function body, not just the one you expect.\n",
                    "why": "Two. `checkGuess` adds one after every guess; `resetGame` puts it back to 1 for a new game.\n"
                  },
                  {
                    "question": "A player finishes a game and clicks **Start new game**. How many times has the line `let secret = …` run? Type a number.",
                    "accept": [
                      "1",
                      "once",
                      "one"
                    ],
                    "miss": "**If you answered 2 — the new secret comes from `resetGame`, not from the top of the file.** The file ran once, and the browser does not run it again.\n",
                    "why": "Once. The second secret is picked inside `resetGame`, which is why that function has to exist.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Read one part in full, and check its order",
          "body": [
            {
              "p": "Only now read a function closely. Here is the step — the function that runs once per guess. Read it against the skeleton and watch the comments turn into code."
            },
            {
              "code": "function checkGuess() {\n  const guess = Number(guessField.value);\n\n  if (guess === secret) {\n    lastResult.textContent = \"Got it!\";\n    setGameOver();\n  } else if (guessCount === 10) {\n    lastResult.textContent = \"Out of turns — it was \" + secret;\n    setGameOver();\n  } else {\n    lastResult.textContent = \"Not yet\";\n    lowOrHi.textContent = guess < secret ? \"Too low\" : \"Too high\";\n  }\n\n  guessCount = guessCount + 1;\n  guessField.value = \"\";\n  guessField.focus();\n}",
              "lang": "text",
              "file": "guess.js — the step"
            },
            {
              "p": "Count the branches first: `if`, `else if`, `else` — three outcomes. Each condition in parentheses is a question with a yes-or-no answer: `guess === secret` asks *\"is the guess the same as the secret?\"*. The program asks the questions in order, from the top, and runs the block of the **first** one whose answer is yes; `else` catches everything left over. Only one block runs. **So the order of the questions is part of the design.** \"Did they win?\" has to come before \"are they out of turns?\", or a correct tenth guess reports a loss."
            },
            {
              "p": "Whether the branches are in the right order is a question about what the code *means*, not about its syntax, and no error message will raise it for you. Reviewing code — yours, a colleague's, an AI tool's — is mostly asking questions like that one. Here is the function after a colleague \"tidied\" it:"
            },
            {
              "code": "  if (guessCount === 10) {\n    lastResult.textContent = \"Out of turns — it was \" + secret;\n    setGameOver();\n  } else if (guess === secret) {\n    lastResult.textContent = \"Got it!\";\n    setGameOver();\n  } else {\n    // unchanged\n  }",
              "lang": "text",
              "file": "guess.js — after the tidy"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "if (…) { } else if (…) { } else { }  →  how many outcomes?",
                    "accept": [
                      "3",
                      "three"
                    ],
                    "why": "Three: the two conditions and the `else` that catches everything else. Counting branches gives you a program's shape before you understand any single line of it.\n"
                  },
                  {
                    "question": "The tidied version: the secret is 50 and the tenth guess is 50. Win or lose?",
                    "choices": [
                      "Win",
                      "Lose"
                    ],
                    "accept": [
                      "Lose"
                    ],
                    "miss": "**If you answered win — read the conditions in order, the way the program does.** On the tenth guess `guessCount === 10` is true, and that branch now comes first. The check for a correct guess is never reached.\n",
                    "why": "Lose — the player is told \"Out of turns — it was 50\" on a correct guess. It runs without an error, which is exactly why it is a reviewer's job to catch.\n"
                  },
                  {
                    "question": "In the original, change 10 to 11. How many guesses does the player get? Type a number.",
                    "accept": [
                      "11",
                      "eleven"
                    ],
                    "miss": "**If you answered 10 — look at where `guessCount` starts: 1, not 0.** The check runs before the count goes up, so on the tenth guess it reads 10.\n",
                    "why": "Eleven. The count starts at 1 and is checked before it goes up, so guess number *n* sees `guessCount === n`. Off-by-one questions are answered by checking the ends, as with `Math.random`.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Name what you do not know",
          "body": [
            {
              "p": "You will finish reading most programs with something you still do not understand. \"I don't get this line\" is not something you can look up. **Narrow it to the one piece you do not know, then ask about that piece.**"
            },
            {
              "p": "Take this line from the step:"
            },
            {
              "code": "lowOrHi.textContent = guess < secret ? \"Too low\" : \"Too high\";",
              "lang": "js",
              "file": "step.js"
            },
            {
              "p": "You can read most of it: something is being set to one of two messages, and `guess < secret` decides which. The part you have not met is the `? :`. So the question is not \"what does this line do?\" — it is **\"what does `? :` do in JavaScript?\"** That question has an answer on the first page you search: it is the **ternary operator** (officially the *conditional operator*), which the Branching module teaches in *The ternary operator*. Now that you know its name, you can search for it and ask about it by name. The precision of the question is the skill."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`guessField.focus();` — which one name would you look up? Type the name.",
                    "accept": [
                      "focus",
                      "focus()",
                      ".focus()",
                      ".focus"
                    ],
                    "why": "`focus`. `guessField` is a name this program made up, so searching for it finds nothing. `focus` is a method someone else defined, so it is documented.\n"
                  },
                  {
                    "question": "A later version adds `const gap = Math.abs(guess - secret);`. Which one name would you look up? Type the name.",
                    "accept": [
                      "Math.abs",
                      "abs",
                      "Math.abs()",
                      "abs()"
                    ],
                    "miss": "**If you picked `gap`, `guess` or `secret` — those are names this program made up.** Searching them finds nothing. Look up the names somebody else defined.\n",
                    "why": "`Math.abs`. The searchable question is \"what does `Math.abs` do?\" — it gives the distance from zero, so `-3` becomes `3`: how far the guess was from the secret, whichever side it fell on.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Most code you read contains **things you have not been taught**. Read past them: guess from the name, and read nested calls **inside out**.",
                "**Shape before detail**, in order: the entry point and the event handlers it hands over, the state and every place it changes, then one part in full.",
                "Passed to `addEventListener`, a function name **without `()`** is handed to the runtime to call later; with `()` the function runs now.",
                "**The order of branches is part of the design**, and no error message will tell you it is wrong.",
                "The useful question is not \"do I understand this?\" but **\"what exactly do I not understand?\"** — because that version is searchable."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "A first splash into JavaScript",
              "href": "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Scripting/A_first_splash",
              "note": "The complete program this lesson reads, with the page it runs in."
            },
            {
              "title": "Math.random()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/random",
              "note": "Read the \"Getting a random integer between two values\" example carefully. The lesson *Fix a bug that never throws* is about what goes wrong when a program gets that range wrong."
            },
            {
              "title": "Conditional (ternary) operator",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Conditional_operator",
              "note": "The answer to the `? :` question above."
            }
          ]
        }
      ],
      "_source": "lessons/first-splash.md",
      "practice": {
        "id": "first-splash",
        "outcome": 10,
        "steps": [
          {
            "kind": "predict",
            "title": "Read it before you run it",
            "prompt": "From the shape of the code alone. Type a range, a value or a count where there is a box; pick where there are buttons.",
            "items": [
              {
                "id": "p1",
                "question": "What range of whole numbers can `Math.floor(Math.random() * 10)` give? Type it as lowest to highest, like `2 to 9`.",
                "accept": [
                  "0 to 9",
                  "0-9",
                  "0..9",
                  "0 through 9",
                  "0 to 9 inclusive"
                ],
                "miss": "**If you answered 1 to 10 or 0 to 10 — read it inside out and check both ends.** `random()` never reaches 1, so times 10 never reaches 10, and there is no `+ 1` this time.\n",
                "why": "0 to 9. `random()` gives 0 up to 0.999…; times 10 gives up to 9.99…; `floor` drops the fraction. Checking the smallest and largest values is where reading inside out pays off."
              },
              {
                "id": "p2",
                "question": "`saveButton.addEventListener(\"click\", save());` — when does `save` run?",
                "choices": [
                  "Now, as this line runs",
                  "Later, on each click"
                ],
                "accept": [
                  "Now, as this line runs"
                ],
                "miss": "**If you answered on click — look at the `()`.** They call `save` now, while this line runs, and hand the browser whatever it returned.\n",
                "why": "Now, once, as the file runs. The parentheses call it; without them the function would be handed over for the browser to call on each click."
              },
              {
                "id": "p3",
                "question": "You open a program you have never seen. In the reading order this lesson teaches, what do you look for first? Name it.",
                "accept": [
                  "the entry point",
                  "entry point",
                  "the entry point and the event handlers",
                  "entry point and event handlers",
                  "the event handlers",
                  "event handlers",
                  "handlers",
                  "what starts things happening"
                ],
                "miss": "**If you answered the first line, or the state — that comes next.** Start with what makes anything happen at all; everything else is reached from there.\n",
                "why": "What starts things happening: the entry point, and the event handlers it hands over. Then the state and every place it changes, then one part in full."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "What does `\"Hello\".endsWith(\"lo\")` give? Guess from the name, and type the value.",
                "accept": [
                  "true"
                ],
                "why": "`true`. You have not been taught `endsWith`, but the name asks a yes-or-no question — does `\"Hello\"` end with `\"lo\"`? — and it does."
              },
              {
                "id": "p5",
                "code": "if (…) { } else if (…) { } else if (…) { } else { }   — how many outcomes?",
                "accept": [
                  "4",
                  "four"
                ],
                "why": "Four: three conditions and the `else`. Counting branches gives you the shape before you understand any line."
              },
              {
                "id": "p6",
                "question": "`form.addEventListener(\"submit\", send);` — when does `send` run?",
                "choices": [
                  "Now, as this line runs",
                  "Later, on each submit"
                ],
                "accept": [
                  "Later, on each submit"
                ],
                "why": "Later, each time the form is submitted. No `()` — the function is handed to the browser."
              }
            ],
            "hints": [
              {
                "when": "you answered 1 to 10 for the range",
                "say": "Look for a `+ 1`. Then check the top end: can `Math.random() * 10` ever reach 10?"
              },
              {
                "when": "you answered on click for save()",
                "say": "Compare with the line in the lesson that hands `checkGuess` to the browser. What follows the function name there — and what follows it here?"
              },
              {
                "when": "you started with the state or with line 1",
                "say": "State matters, but it only changes because something happened. What makes anything happen in this program?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Guess from the name, then check",
            "prompt": "Four methods you have not been taught. Guess each from its name — and check the ends — then type what each line prints, run it, and see which guesses held.\n",
            "filename": "names.js",
            "code": "console.log(\"Splash\".toLowerCase());\nconsole.log(\"Splash\".slice(0, 3));\nconsole.log(\"Splash\".includes(\"las\"));\nconsole.log(\"Splash\".indexOf(\"p\"));\n",
            "items": [
              {
                "id": "r1",
                "code": "\"Splash\".toLowerCase()",
                "accept": [
                  "splash",
                  "\"splash\""
                ],
                "why": "`splash` — exactly what the name says: the opposite of `toUpperCase`."
              },
              {
                "id": "r2",
                "code": "\"Splash\".slice(0, 3)",
                "accept": [
                  "Spl",
                  "\"Spl\""
                ],
                "miss": "**If you answered `Spla` — the name was right and the end was not.** `slice` stops *before* position 3, the way `Math.random()` stops before 1.\n",
                "why": "`Spl`: the letters at positions 0, 1 and 2. The name tells you it cuts a piece; only the ends needed checking."
              },
              {
                "id": "r3",
                "code": "\"Splash\".includes(\"las\")",
                "accept": [
                  "true"
                ],
                "why": "`true`. A name that reads as a yes-or-no question usually answers `true` or `false`."
              },
              {
                "id": "r4",
                "code": "\"Splash\".indexOf(\"p\")",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered 2 — positions count from 0.** `S` is at 0, so `p` is at 1.\n",
                "why": "`1`. \"Index of\" asks for a position, and positions start at 0 — another end worth checking."
              }
            ],
            "hints": [
              {
                "when": "a guess was right about what it does but wrong by one",
                "say": "That is the usual miss, and it is the lesson's point: the name tells you what a method does, and only the boundaries need checking. Does it include its end? Does counting start at 0?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Find the shape",
            "prompt": "A program you have not seen. Read it in the lesson's order — event handlers, then state — and answer from the code: a number, a value or a name, as each question asks.",
            "items": [
              {
                "id": "t1",
                "code": "1   let score = 0;\n2   let lives = 3;\n3\n4   function answer(correct) {\n5     if (correct) {\n6       score = score + 10;\n7     } else {\n8       lives = lives - 1;\n9     }\n10  }\n11\n12  function restart() {\n13    score = 0;\n14    lives = 3;\n15  }\n16\n17  quiz.addEventListener(\"answer\", answer);\n18  resetButton.addEventListener(\"click\", restart);\n— how many functions does this file hand to the runtime?\n",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "Two, on lines 17 and 18: `answer` and `restart`, each without `()`. Those two functions are the event handlers; after the first run of the file, everything the program does starts from one of them."
              },
              {
                "id": "t2",
                "question": "Same program — not counting line 2, how many lines change `lives`?",
                "accept": [
                  "2",
                  "two"
                ],
                "miss": "**If you answered 1 — you found it going down and missed it going back up.** Search every function body, not only the one you expect.\n",
                "why": "Two: line 8 takes one away, line 14 puts it back to 3."
              },
              {
                "id": "t3",
                "question": "Same program — when the runtime first runs the file, how many times does line 6 run?",
                "accept": [
                  "0",
                  "zero",
                  "none",
                  "never"
                ],
                "why": "None. The first run declares two values and two functions, and hands both functions to the runtime. Line 6 runs only when the runtime calls `answer`."
              },
              {
                "id": "t4",
                "question": "Same program — four wrong answers in a row. What is `lives`?",
                "accept": [
                  "-1",
                  "minus 1",
                  "minus one"
                ],
                "why": "`-1`. Nothing checks for zero, so the game carries on past it. That is something for a reviewer to note. Reading every place `lives` changes finds it; no error will report it."
              }
            ],
            "extra": [
              {
                "id": "t5",
                "question": "Same program — which function would you read in full first? Type its name.",
                "accept": [
                  "answer",
                  "answer()",
                  "answer function"
                ],
                "why": "`answer` — the step, the thing that happens once per interaction. `restart` is the end-and-restart part."
              },
              {
                "id": "t6",
                "question": "Same program — a player scores 30, then clicks reset. What is `score`?",
                "accept": [
                  "0",
                  "zero"
                ],
                "why": "`0`. `restart` resets both pieces of state. The top of the file does not run again, which is why `restart` has to exist."
              }
            ],
            "hints": [
              {
                "when": "you counted every function as an event handler",
                "say": "An event handler is a function the runtime is handed to call. Which lines hand something over?"
              },
              {
                "when": "you expected line 6 to run when the file loads",
                "say": "A function body runs when the function is called. While the file runs, who calls `answer`?"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a colleague's game",
            "prompt": "A colleague wrote this from a spec: *a secret number from 1 to 20, five guesses, and a correct guess always wins — including the fifth*. It runs without a single error. Review it: name the lines.\n",
            "filename": "guess.js",
            "code": "let secret = Math.floor(Math.random() * 20);\nlet turns = 0;\n\nfunction checkGuess() {\n  const guess = Number(guessField.value);\n  turns = turns + 1;\n  if (turns === 5) {\n    message.textContent = \"Out of turns\";\n  } else if (guess === secret) {\n    message.textContent = \"Correct!\";\n  } else {\n    message.textContent = guess < secret ? \"Too low\" : \"Too high\";\n  }\n}\n\nguessButton.addEventListener(\"click\", checkGuess());\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line picks a secret the spec does not allow? Type its number.",
                "accept": [
                  "1",
                  "line 1"
                ],
                "why": "Line 1: `floor(random() * 20)` gives 0 to 19. It can pick 0 and never 20 — the `+ 1` is missing."
              },
              {
                "id": "c2",
                "question": "The fifth guess is correct. What does the page say?",
                "choices": [
                  "Correct!",
                  "Out of turns"
                ],
                "accept": [
                  "Out of turns"
                ],
                "miss": "**Read the conditions in order, the way the program does.** On the fifth guess `turns` is 5 before any check runs.\n",
                "why": "\"Out of turns\" — on a correct guess. The branches are checked in order and the first match wins."
              },
              {
                "id": "c3",
                "question": "Which line holds the check that has to move below the correct-guess check? Type its number.",
                "accept": [
                  "7",
                  "line 7"
                ],
                "why": "Line 7. \"Did they win?\" has to come before \"are they out of turns?\" — the order is the design."
              },
              {
                "id": "c4",
                "question": "Line 16 — when does `checkGuess` run?",
                "choices": [
                  "Now, as this line runs",
                  "Later, on each click"
                ],
                "accept": [
                  "Now, as this line runs"
                ],
                "miss": "**If you answered on each click — look at the `()`.** They call it now and hand the browser `undefined`, so clicking does nothing.\n",
                "why": "Once, as the file runs, and never on a click: the browser was handed `undefined`. That makes three defects in this review, and not one of them produces an error message."
              }
            ],
            "hints": [
              {
                "when": "you found one defect and stopped",
                "say": "Read in the lesson's order: the event handler (line 16), then the state and where it changes (lines 1, 2, 6), then the step in full. Each read-through turns up a different kind of problem."
              },
              {
                "when": "you said the correct fifth guess wins",
                "say": "Put in the numbers: fifth click, so `turns` becomes 5 on line 6. Which condition is checked first?"
              }
            ]
          }
        ]
      }
    },
    "reading-errors": {
      "id": "reading-errors",
      "title": "When it breaks: reading errors",
      "kind": "read",
      "outcome": 10,
      "shape": "procedural",
      "why": "Every program a student writes will break, and what the runtime prints is the first evidence they get — or, when it prints nothing, the evidence they have to make. The debugger, logging and code review all start from this.",
      "concepts": [
        "diag.read-errors",
        "diag.silent-failure"
      ],
      "lede": "An error message is the best thing that can happen to you. It names the problem, the file and the line. The failures worth fearing are the silent ones — the code that runs perfectly and is simply wrong.",
      "margins": [
        {
          "label": "MARGIN NOTE",
          "body": "Error messages differ between runtime environments — the programs that run your JavaScript. Node, Chrome, Firefox and Safari can word the same fault differently. Search the *shape* of the message — its kind and the name it mentions — not the exact words."
        },
        {
          "label": "SEE ALSO",
          "body": "What JavaScript is · Fix a bug that never throws"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Stack trace** — the lines starting with `at` under an error message: the chain of calls that led to it. Reading it across several calls is taught in the Functions module.",
          "entries": [
            "**Stack trace** — the lines starting with `at` under an error message: the chain of calls that led to it. Reading it across several calls is taught in the Functions module."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on earlier lessons. Answer from memory: retrieving something\nis what makes it stick. The answer to the first question is where this lesson\nstarts."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "You type `document.title` into Node. What happens?",
                    "choices": [
                      "It prints undefined",
                      "It throws an error"
                    ],
                    "accept": [
                      "It throws an error"
                    ],
                    "miss": "**If you said `undefined` — that is what `typeof document` gives.** `typeof` asks what kind of value a name holds, and asking it about a missing name is safe. *Using* the missing name, as `document.title` does, throws an error, and the program stops on that line.\n",
                    "why": "It throws `ReferenceError: document is not defined`. Node is a runtime with no page, so it never handed your code a `document`.\n"
                  },
                  {
                    "question": "You type `typeof document` into Node. What does it give?",
                    "accept": [
                      "undefined",
                      "\"undefined\"",
                      "'undefined'"
                    ],
                    "why": "`\"undefined\"`. The name `document` does not exist in Node, and in JavaScript `typeof` can be applied to a missing name without causing an error.\n"
                  },
                  {
                    "question": "A five-line file has a quote that never closes on line 4. How many of its lines run? Type a number.",
                    "accept": [
                      "0",
                      "zero",
                      "none"
                    ],
                    "miss": "**If you said 3 — Node reads the whole file before it runs any of it.** When it cannot read one line, it runs none of them.\n",
                    "why": "None. A quote that never closes is a SyntaxError, from *Your first program*: nothing in the file runs, not even line 1.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can turn an error message into a file, a line and a cause",
                "I can tell a SyntaxError, a ReferenceError and a TypeError apart, and say what each narrows the search to",
                "I can find a defect that produced a wrong answer and no message"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Name the kind",
          "body": [
            {
              "p": "The answer to the first recall question was an error: `ReferenceError:\ndocument is not defined`. Code can go wrong in three ways, and only two of\nthem print a message to tell you."
            },
            {
              "table": {
                "head": [
                  "Kind",
                  "What happens",
                  "Does it tell you?"
                ],
                "rows": [
                  [
                    "**Syntax**",
                    "Will not run at all — Node cannot even read it",
                    "Yes, before anything runs"
                  ],
                  [
                    "**Runtime**",
                    "Runs, then hits something impossible and stops",
                    "Yes, at the moment it fails"
                  ],
                  [
                    "**Logic**",
                    "Runs start to finish and produces the wrong answer",
                    "**No.** Nothing at all"
                  ]
                ]
              }
            },
            {
              "p": "A runtime error is named for when it happens: while the program is running.\n(The *runtime* is the program that runs your code. In this lesson, that is\nNode.)"
            },
            {
              "p": "An error message starts with its *kind*, and the kind is the first thing to\nread, because it tells you what sort of mistake to look for:"
            },
            {
              "list": [
                "**`SyntaxError`** — Node could not read the code: a missing `)` or a quote that never closes, as in *Your first program*. Nothing ran. The caret (the `^` Node prints under the line) may point *after* the real mistake, because the caret marks where Node gave up reading, not where the mistake began.",
                "**`ReferenceError`** — you used a **name** that does not exist. A typo, like `consle`, or a name this runtime never supplied, like `document` in Node.",
                "**`TypeError`** — the name exists, but its **value** is not the kind of thing you treated it as. The commonest version: calling something that is not a function. `\"tea\".toUppercase()` asks the string `\"tea\"` for a method it does not have, so the message says `\"tea\".toUppercase is not a function`."
              ]
            },
            {
              "p": "ReferenceError and TypeError are the two people mix up. A ReferenceError\nsends you looking for a *bare name*, one with no dot in front of it, that was\nnever defined. A TypeError says the name was found, so the problem is what the\ncode did with its value: look at what comes after the dot, or at where the\nvalue came from. So `consle.log(\"hi\")` is a ReferenceError, because there is no\n`consle`. But `console.lg(\"hi\")` is a TypeError: `console` exists, and it has\nnothing called `lg` to call."
            },
            {
              "p": "Here are four lines of code. For each one, pick the kind of error it causes,\nor **silent** if nothing is reported."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "console.log(\"Total\";",
                          "choices": [
                            "SyntaxError",
                            "ReferenceError",
                            "TypeError",
                            "silent"
                          ],
                          "accept": [
                            "SyntaxError"
                          ],
                          "why": "A **SyntaxError**: `missing ) after argument list`. The `(` after `log` never closes. Node cannot read the line, so nothing runs at all.\n"
                        },
                        {
                          "code": "Math.max(3, 7).toUpperCase()",
                          "choices": [
                            "SyntaxError",
                            "ReferenceError",
                            "TypeError",
                            "silent"
                          ],
                          "accept": [
                            "TypeError"
                          ],
                          "miss": "**If you said ReferenceError — every name here exists.** Read it inside out: `Math.max(3, 7)` produces the number `7`, and `.toUpperCase()` then asks that number for a string method, which a number does not have.\n",
                          "why": "A **TypeError**: `Math.max(...).toUpperCase is not a function`. The value was a number, not the string the code assumed.\n"
                        },
                        {
                          "code": "consle.log(\"hi\")",
                          "choices": [
                            "SyntaxError",
                            "ReferenceError",
                            "TypeError",
                            "silent"
                          ],
                          "accept": [
                            "ReferenceError"
                          ],
                          "miss": "**If you said TypeError — the two errors point at different things.** A ReferenceError means the **name** does not exist. A TypeError means the name was found and its **value** was wrong for what the code did with it. Knowing which one you have halves the search.\n",
                          "why": "A **ReferenceError**: `consle is not defined`. A typo in a bare name.\n"
                        },
                        {
                          "code": "console.log((1 + 2 + 3) / 4);  // meant the average of three numbers",
                          "choices": [
                            "SyntaxError",
                            "ReferenceError",
                            "TypeError",
                            "silent"
                          ],
                          "accept": [
                            "silent"
                          ],
                          "miss": "**If you expected a warning — the line looks wrong to you only because you know what it was meant to do.** The comment says three numbers, but Node does not read comments, and nothing in the code says to divide by 3.\n",
                          "why": "Nothing at all. It runs, prints `1.5`, and is simply wrong. The last step of this lesson, *Find the one that says nothing*, shows how to find a mistake like this one.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "x = ",
                          "choices": [
                            "SyntaxError",
                            "NameError",
                            "AttributeError",
                            "silent"
                          ],
                          "accept": [
                            "SyntaxError"
                          ],
                          "why": "A SyntaxError, reported before anything runs.\n"
                        },
                        {
                          "code": "\"tea\".uper()",
                          "choices": [
                            "SyntaxError",
                            "NameError",
                            "AttributeError",
                            "silent"
                          ],
                          "accept": [
                            "AttributeError"
                          ],
                          "why": "An AttributeError is Python's version of JavaScript's TypeError here: the value `\"tea\"` was found, and it has nothing called `uper`.\n"
                        },
                        {
                          "code": "prnt(\"hi\")",
                          "choices": [
                            "SyntaxError",
                            "NameError",
                            "AttributeError",
                            "silent"
                          ],
                          "accept": [
                            "NameError"
                          ],
                          "why": "A NameError. Same meaning as JavaScript's ReferenceError, different word.\n"
                        },
                        {
                          "code": "print((1 + 2 + 3) / 4)   # meant the average of three",
                          "choices": [
                            "SyntaxError",
                            "NameError",
                            "AttributeError",
                            "silent"
                          ],
                          "accept": [
                            "silent"
                          ],
                          "why": "Silent. It prints `1.5`. The comment says what you meant, but Python does not read comments, and nothing in the code says to divide by 3.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "int x = ;",
                          "choices": [
                            "compile error",
                            "runtime exception",
                            "silent"
                          ],
                          "accept": [
                            "compile error"
                          ],
                          "why": "The compiler rejects it before the program runs. That is the earliest a mistake can be reported.\n"
                        },
                        {
                          "code": "Console.WriteLine(int.Parse(\"tea\"));",
                          "choices": [
                            "compile error",
                            "runtime exception",
                            "silent"
                          ],
                          "accept": [
                            "runtime exception"
                          ],
                          "why": "A `FormatException` while it runs: the value was not what the code assumed.\n"
                        },
                        {
                          "code": "Consle.WriteLine(\"hi\");",
                          "choices": [
                            "compile error",
                            "runtime exception",
                            "silent"
                          ],
                          "accept": [
                            "compile error"
                          ],
                          "why": "A compile error rather than a runtime one — the name is looked up before the program ever runs.\n"
                        },
                        {
                          "code": "Console.WriteLine((1 + 2 + 3) / 4.0);  // meant 3",
                          "choices": [
                            "compile error",
                            "runtime exception",
                            "silent"
                          ],
                          "accept": [
                            "silent"
                          ],
                          "why": "Silent. The compiler checks that each value is the right kind (its type). It cannot check that the program does what you meant.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Read the message",
          "body": [
            {
              "p": "An error message looks like noise until you know it has a fixed layout. Here is\na small file, `menu.js`, and exactly what Node prints when you run it."
            },
            {
              "code": "console.log(\"Menu\");\nconsole.log(\"tea\".toUpperCase());\nconsole.log(\"cake\".toUppercase());\nconsole.log(\"Done\");",
              "lang": "js",
              "file": "menu.js"
            },
            {
              "code": "Menu\nTEA\n/workspaces/cs140/menu.js:3\nconsole.log(\"cake\".toUppercase());\n                   ^\n\nTypeError: \"cake\".toUppercase is not a function\n    at Object.<anonymous> (/workspaces/cs140/menu.js:3:20)\n    at Module._compile (node:internal/modules/cjs/loader:1781:14)\n    ...",
              "lang": "text",
              "file": "node menu.js"
            },
            {
              "p": "On your computer, the path in the error will be wherever your own file is.\nFirst, notice what printed **before** the error: `Menu` and `TEA`. Node ran\nlines 1 and 2, top to bottom, then stopped at line 3. `Done` never printed.\nThen read the error in this order:"
            },
            {
              "list": [
                "**Where** — the first line of the error: `menu.js:3`. File, then line.",
                "**The line itself**, with a `^` under the spot. Here it is under `toUppercase`.",
                "**The kind** — `TypeError`. The value, `\"cake\"`, was found; what the code did with it was wrong.",
                "**What it was doing** — `\"cake\".toUppercase is not a function`. The code tried to call something called `toUppercase`, and strings have no method by that name. Compare it letter by letter with `toUpperCase` on line 2."
              ],
              "ordered": true
            },
            {
              "p": "Put together, the four parts make a sentence you can act on: *on line 3 of\n`menu.js`, `toUppercase` is misspelled.* A browser console shows the same parts, laid out differently —\n`Uncaught TypeError: …`, with a clickable `menu.js:3` beside it."
            },
            {
              "p": "The lines below the message that start with `at` are the **stack trace**.\nWhile your programs have no functions of your own, only the first `at` line\nmatters, and it repeats what the first line of the error already said:\n`menu.js:3`. The rest is Node's own code. Once your programs have\nfunctions that call each other, the trace shows which call led to which — the\nFunctions module teaches you to read it."
            },
            {
              "p": "Now a different file. Read what Node printed."
            },
            {
              "code": "console.log(\"Welcome\");\nconsole.log(Math.min(8, 3));\nconsle.log(\"Done\");",
              "lang": "js",
              "file": "welcome.js"
            },
            {
              "code": "Welcome\n3\n/workspaces/cs140/welcome.js:3\nconsle.log(\"Done\");\n^\n\nReferenceError: consle is not defined",
              "lang": "text",
              "file": "node welcome.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line did it break on? Type its number.",
                    "accept": [
                      "3",
                      "line 3",
                      "welcome.js:3"
                    ],
                    "why": "Line 3. The first line of the error, just below the output, says `welcome.js:3`.\n"
                  },
                  {
                    "question": "What kind of error is it?",
                    "choices": [
                      "SyntaxError",
                      "ReferenceError",
                      "TypeError"
                    ],
                    "accept": [
                      "ReferenceError"
                    ],
                    "why": "A **ReferenceError**. `consle` is a bare name, and nothing by that name exists.\n"
                  },
                  {
                    "question": "Which name does not exist? Type it exactly as the file spells it.",
                    "accept": [
                      "consle",
                      "`consle`"
                    ],
                    "miss": "**Type it the way the file has it, not the way it should be.** The error names the spelling Node could not find.\n",
                    "why": "`consle`. The fix is `console`.\n"
                  },
                  {
                    "question": "How many lines of output appeared before the error? Type a number.",
                    "accept": [
                      "2",
                      "two"
                    ],
                    "miss": "**Look above the error.** Node ran lines 1 and 2 before it reached line 3. A runtime error stops the program where it happens, not before.\n",
                    "why": "Two: `Welcome` and `3`. A runtime error stops the lines after it; the lines before it have already run. A SyntaxError is different: with a SyntaxError, nothing runs at all.\n"
                  }
                ]
              }
            },
            {
              "video": {
                "title": "Three errors, one file",
                "caption": "A new file with three mistakes, run and fixed live. The SyntaxError is on the last line, but it is reported first, and nothing prints until it is fixed. Then each runtime error appears when the program reaches its line.\n"
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Find the one that says nothing",
          "body": [
            {
              "p": "Sometimes nothing breaks. The program runs, prints an answer, and the answer is\nwrong. No message, no line number, no trace — because nothing *illegal*\nhappened. The computer did exactly what you wrote. It has no idea what you\nmeant."
            },
            {
              "p": "So you have to find the mistake yourself, by checking the values the program\nmakes, line by line:"
            },
            {
              "list": [
                "**Know the right answer first.** Work it out by hand for one input. Without that, a wrong answer looks like any other answer.",
                "**Run it and compare.** If what it prints differs from your answer, you have a silent failure.",
                "**Print the values in between**, one line at a time, and check each against what you expect.",
                "**The first wrong value marks the line.** The line that makes the first wrong value holds the mistake. The lines after it may be correct code working on a bad value, so check them again once that line is fixed."
              ],
              "ordered": true
            },
            {
              "p": "The programs below keep each value under a name with a `const` declaration,\nso that later lines, and your `console.log`, can use it. Read `const discount = …` as\n*\"`discount` now holds this value\"*. Naming values is the first lesson of the\nnext module."
            },
            {
              "p": "A price after a 20% discount:"
            },
            {
              "code": "const price = 50;\nconst discount = price * 20;\nconst final = price - discount;\nconsole.log(final);",
              "lang": "js",
              "file": "discount.js"
            },
            {
              "p": "By hand, 20% off 50 is **40**. Node prints **`-950`**. Put\n`console.log(\"discount:\", discount);` after line 2 and run it again. It prints\n`discount: 1000`, and by hand the discount should be **10**. So `discount` is\nthe first wrong value, and line 2, which makes it, holds the mistake: `20`\nwhere `0.2` was meant. Line 3 was never wrong; it subtracted correctly from a\nwrong discount."
            },
            {
              "p": "Your turn. This program is meant to report hours worked, given start and end times in\nminutes after midnight — `540` is 9:00, `1020` is 17:00."
            },
            {
              "code": "const start = 540;\nconst end = 1020;\nconst minutes = end - start;\nconst hours = minutes / 100;\nconsole.log(hours);",
              "lang": "js",
              "file": "hours.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "By hand: what should it print? Type the number.",
                    "accept": [
                      "8",
                      "8 hours",
                      "8.0"
                    ],
                    "why": "9:00 to 17:00 is **8** hours. Node prints `4.8`. The two differ, so this is a silent failure.\n"
                  },
                  {
                    "question": "You log `minutes` and it prints `480`. Right or wrong?",
                    "choices": [
                      "Right",
                      "Wrong"
                    ],
                    "accept": [
                      "Right"
                    ],
                    "miss": "**Check it by hand rather than by feel:** 1020 − 540 is 480, and 8 hours is 480 minutes. A value you have not checked is not evidence either way.\n",
                    "why": "Right — 8 hours is 480 minutes. So the mistake is on a later line.\n"
                  },
                  {
                    "question": "Which line holds the first wrong value? Type its number.",
                    "accept": [
                      "4",
                      "line 4"
                    ],
                    "miss": "**If you said 5 — `console.log(hours)` is correct code showing a bad value.** The first wrong value is `hours`; look at the line that makes it.\n",
                    "why": "Line 4. There are 60 minutes in an hour, not 100: `minutes / 60`.\n"
                  }
                ]
              }
            },
            {
              "video": {
                "title": "Finding the one that says nothing",
                "caption": "A new silent bug, hunted live. The right answer is worked out by hand first. Values are printed and checked, and a value that is right counts as evidence too. The first wrong value marks the line, and the extra `console.log` lines are taken out afterwards.\n"
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**SyntaxError** — nothing runs. **ReferenceError** — the *name* does not exist. **TypeError** — the name was found, and its *value* was wrong for what the code did with it.",
                "Read the top of the message in order: **where** (file and line), **the line** with its caret, **the kind**, then **what it was doing**. Any output printed above the error shows how far the program got.",
                "A **silent failure** has no message, so you make one: know the right answer, print the values in between, and the **first wrong value** marks the line.",
                "Runtimes phrase the same fault differently. Search the **shape** of the message, not the words."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "What went wrong? Troubleshooting JavaScript",
              "href": "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Scripting/What_went_wrong",
              "note": "Walks the same kinds of bug through a guessing game, with real console screenshots."
            },
            {
              "title": "JavaScript error reference",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors",
              "note": "Every built-in error message, explained. Paste a message you do not recognise into the search box — this is the page you want."
            },
            {
              "title": "console.log()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/console/log_static",
              "note": "the basic tool. Choosing *what* to log comes later, in *Logging to tell two explanations apart*."
            }
          ]
        }
      ],
      "_source": "lessons/reading-errors.md",
      "practice": {
        "id": "reading-errors",
        "outcome": 10,
        "steps": [
          {
            "kind": "predict",
            "title": "Name the failure",
            "prompt": "For the first and third, pick the kind of error the line causes, or **silent** if nothing is reported. The second is an error message, and reading the line number is the first step in reading one: type the line number you would go to.",
            "items": [
              {
                "id": "p1",
                "code": "console.lg(\"hi\")",
                "choices": [
                  "SyntaxError",
                  "ReferenceError",
                  "TypeError",
                  "silent"
                ],
                "accept": [
                  "TypeError"
                ],
                "miss": "**If you said ReferenceError — `console` exists.** The name before the dot was found; what it was asked for, `lg`, is not something it can call.\n",
                "why": "A TypeError: `console.lg is not a function`. The name `console` was found, and its *value* has nothing called `lg` to call."
              },
              {
                "id": "p2",
                "code": "/workspaces/cs140/label.js:4\nconsole.log(\"sale\".toUppercase());\n                   ^\n\nTypeError: \"sale\".toUppercase is not a function\n— which line of label.js do you go to first?\n",
                "accept": [
                  "4",
                  "line 4",
                  "label.js:4"
                ],
                "why": "Line 4 — the error names the file and the line first. Then look under the caret."
              },
              {
                "id": "p3",
                "code": "console.log(90 / 4);   // the bill is split three ways",
                "choices": [
                  "SyntaxError",
                  "ReferenceError",
                  "TypeError",
                  "silent"
                ],
                "accept": [
                  "silent"
                ],
                "why": "Silent. It runs, prints 22.5, and is wrong. Nothing in the program says there are three people."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "notDefinedAnywhere()",
                "choices": [
                  "SyntaxError",
                  "ReferenceError",
                  "TypeError",
                  "silent"
                ],
                "accept": [
                  "ReferenceError"
                ],
                "why": "A ReferenceError — nothing named `notDefinedAnywhere` exists. Usually that means a typo."
              },
              {
                "id": "p5",
                "code": "Number(\"5\").toUpperCase()",
                "choices": [
                  "SyntaxError",
                  "ReferenceError",
                  "TypeError",
                  "silent"
                ],
                "accept": [
                  "TypeError"
                ],
                "why": "A TypeError. Read it inside out: `Number(\"5\")` produces the number `5`, and a number has no `toUpperCase` to call."
              },
              {
                "id": "p6",
                "question": "Typed into Node: `document.title`",
                "choices": [
                  "SyntaxError",
                  "ReferenceError",
                  "TypeError",
                  "silent"
                ],
                "accept": [
                  "ReferenceError"
                ],
                "why": "A ReferenceError: `document is not defined`. Node never supplies `document`, so to Node it is a missing name, just as a typo would be."
              },
              {
                "id": "p7",
                "code": "console.log(Math.max(3, 7);",
                "choices": [
                  "SyntaxError",
                  "ReferenceError",
                  "TypeError",
                  "silent"
                ],
                "accept": [
                  "SyntaxError"
                ],
                "why": "A SyntaxError — `console.log(` never closes, so Node cannot read the line, and nothing runs at all."
              }
            ],
            "hints": [
              {
                "when": "you gave ReferenceError for console.lg",
                "say": "ReferenceError and TypeError send you to different places. A ReferenceError says a bare name does not exist. A TypeError says the name before the dot was found, and the problem is what came after the dot."
              },
              {
                "when": "you went looking somewhere other than the line the error names",
                "say": "Read the top first: the file and line come before anything else. Start there, then look under the caret."
              },
              {
                "when": "you expected the division to be reported",
                "say": "Ask what rule the line breaks. Dividing 90 by 4 is legal; only you know it should have been 3."
              }
            ]
          },
          {
            "kind": "run",
            "title": "What prints before it breaks",
            "prompt": "Predict each answer before you run the program: type a value, or pick a button where there are buttons. Then run it.",
            "filename": "order.js",
            "code": "console.log(\"Order\");\nconsole.log(\"tea\".toUpperCase());\nconsole.log(Math.max(2, 5));\nconsole.log(\"cake\".toUppercase());\nconsole.log(\"Done\");\n",
            "items": [
              {
                "id": "r1",
                "question": "What does line 3 print? Type the value.",
                "accept": [
                  "5"
                ],
                "why": "`5`. Lines 1 to 3 run normally, top to bottom."
              },
              {
                "id": "r2",
                "question": "Line 4 — what kind of error?",
                "choices": [
                  "SyntaxError",
                  "ReferenceError",
                  "TypeError"
                ],
                "accept": [
                  "TypeError"
                ],
                "miss": "**If you said ReferenceError — `\"cake\"` is a value that exists.** It is asked for a method it does not have, which is a TypeError.\n",
                "why": "A TypeError: `\"cake\".toUppercase is not a function`. The value was found; strings have no method spelled that way."
              },
              {
                "id": "r3",
                "question": "How many lines of output appear before the error message? Type a number.",
                "accept": [
                  "3",
                  "three"
                ],
                "miss": "**If you said 0 — nothing printing is what happens with a SyntaxError.** This one is a runtime error: Node could read every line, so it ran until it hit the impossible one.\n",
                "why": "Three: `Order`, `TEA`, `5`. A runtime error stops the run where it happens, not before."
              },
              {
                "id": "r4",
                "question": "Does \"Done\" print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. A thrown error ends the run at that line."
              }
            ],
            "hints": [
              {
                "when": "you expected nothing to print",
                "say": "Ask whether Node could *read* every line. If it could, it runs them top to bottom until one fails while running."
              },
              {
                "when": "you expected Done to print",
                "say": "An error stops the program at the line where it happens. Nothing after it runs."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Find the first wrong value",
            "prompt": "This program runs without an error and prints the wrong answer. Follow the procedure from the lesson: work out the right answer by hand, then check the values line by line. Type a number for each question.",
            "items": [
              {
                "id": "t1",
                "code": "1  const price = 12;\n2  const quantity = 3;\n3  const subtotal = price + quantity;\n4  const total = subtotal + 2;\n5  console.log(total);\n— meant: 3 tickets at 12 each, plus a 2 booking fee. By hand, what should it print?\n",
                "accept": [
                  "38"
                ],
                "why": "38: 3 × 12 is 36, plus 2. Now you have something to compare with."
              },
              {
                "id": "t2",
                "question": "Same program — what does line 5 actually print? Type the number.",
                "accept": [
                  "17"
                ],
                "why": "17. The two answers differ, so there is a silent failure somewhere above line 5."
              },
              {
                "id": "t3",
                "question": "Same program — what value does `subtotal` hold after line 3? Type the number.",
                "accept": [
                  "15"
                ],
                "miss": "**Work out the line as written, not as meant.** `price + quantity` is 12 + 3.\n",
                "why": "15 — and by hand it should be 36. That is the first wrong value."
              },
              {
                "id": "t4",
                "question": "Same program — which line holds the first wrong value? Type its number.",
                "accept": [
                  "3",
                  "line 3"
                ],
                "miss": "**If you said 4 or 5 — those lines are correct code working on a bad value.** Find the first line whose value differs from your hand working.\n",
                "why": "Line 3: `+` where `*` was meant. Line 4 adds the fee correctly to a wrong subtotal."
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "1  const litres = 40;\n2  const pricePerLitre = 2;\n3  const cost = litres * pricePerLitre;\n4  const perPerson = cost / 4;\n5  console.log(perPerson);\n— meant: fuel for a trip, split between 2 people. Which line holds the first wrong value?\n",
                "accept": [
                  "4",
                  "line 4"
                ],
                "why": "Line 4. `cost` is 80, correctly; split between 2 it should be 40, and `/ 4` gives 20."
              },
              {
                "id": "t6",
                "question": "A program prints the right answer for the one input you tried. Is it correct?",
                "choices": [
                  "Yes",
                  "Not necessarily"
                ],
                "accept": [
                  "Not necessarily"
                ],
                "why": "Not necessarily. One right answer shows the program works for that one input. Also try inputs where you know the answer and where a mistake would change the result."
              }
            ],
            "hints": [
              {
                "when": "you skipped working out the right answer",
                "say": "Without the right answer, 17 looks like any other number. First work out what the program should print, from what it was meant to do."
              },
              {
                "when": "you picked the line that prints",
                "say": "The line that prints only shows a value. Go up the lines and find the first value that disagrees with your hand working."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review — a bug that never throws",
            "prompt": "A classmate wrote this to split a bill of 84 between 3 people, adding a 10% tip to each share. It runs without an error. Review it: work out the right answer first, then find the first wrong value. Once that line is fixed, compare again. Type a number, or pick a button.\n",
            "filename": "bill.js",
            "code": "// Split the bill, then add a 10% tip to each share\nconst bill = 84;\nconst people = 3;\nconst share = bill / 4;\nconst tip = share * 10;\nconsole.log(share + tip);\n",
            "items": [
              {
                "id": "c1",
                "question": "By hand: what should it print? Type the number.",
                "accept": [
                  "30.8"
                ],
                "why": "30.8 — each share is 84 ÷ 3 = 28, and 10% of 28 is 2.8. Without that number, the answer the program prints would look like any other answer."
              },
              {
                "id": "c2",
                "question": "It prints 231. Which line holds the first wrong value? Type its number.",
                "accept": [
                  "4",
                  "line 4"
                ],
                "miss": "**If you said 5 or 6 — check `share` by hand first.** It should be 28.\n",
                "why": "Line 4. `bill / 4` gives 21: it divides by 4 where `people` — 3 — was meant."
              },
              {
                "id": "c3",
                "question": "Line 4 is fixed and it now prints 308. Which line holds the first wrong value now? Type its number.",
                "accept": [
                  "5",
                  "line 5"
                ],
                "why": "Line 5. `share` is 28, correctly; 10% of it is 2.8, and `* 10` gives 280. A percentage is a fraction: `* 0.1`."
              },
              {
                "id": "c4",
                "question": "Would any error message have pointed at either line?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. Both lines are legal and run perfectly. The only evidence is a right answer worked out by hand — and the second defect hid behind the first until it was fixed."
              }
            ],
            "hints": [
              {
                "when": "you found one defect and stopped",
                "say": "Fix the first, then compare again. A second wrong value can hide behind the first."
              },
              {
                "when": "you pointed at the line that prints",
                "say": "Line 6 adds two values correctly. Check each value above it against your hand working."
              }
            ]
          }
        ]
      }
    },
    "fix-the-random": {
      "id": "fix-the-random",
      "title": "Fix a bug that never throws",
      "kind": "trial",
      "kicker": "TRIAL 1.5",
      "filename": "pick.js",
      "exportName": "pickNumber",
      "brief": "Fix `pickNumber` so it returns a whole number from 1 to 100, inclusive. The line that starts with `return` runs without an error, and it is wrong every time it is called. Run the trials first and read what they tell you. Then change that line. Keep `return` at the start of it: `return` sends the value out of the function, so the tests can check it. The Functions module explains functions and `return` in full.",
      "hint": "Read the line inside out, as in *Reading a program you did not write*. `Math.random()` gives a number from 0 up to (but never reaching) 1. `Math.floor()` of any number from 0 up to 1 is 0. So something has to happen to the random number **between** the two calls, before `Math.floor()` rounds it down, to stretch its range.",
      "success": "That is the fix. Notice what the trials caught: not a crash, but a value that was 1 every time. No error message would ever have told you.",
      "starter": "// Should return a whole number from 1 to 100.\n// It runs without an error. It is also wrong every time.\nfunction pickNumber() {\n  // TODO: put your code here — change the line below\n  return Math.floor(Math.random()) + 1;\n}\n",
      "tests": [
        {
          "label": "returns a whole number",
          "body": "var v = subject(); return typeof v === \"number\" && Number.isInteger(v);"
        },
        {
          "label": "never below 1, never above 100",
          "body": "for (var i = 0; i < 500; i++) { var v = subject(); if (v < 1 || v > 100) return false; } return true;"
        },
        {
          "label": "actually varies — more than one value in 200 calls",
          "body": "var seen = {}; for (var i = 0; i < 200; i++) seen[subject()] = 1; return Object.keys(seen).length > 1;"
        },
        {
          "label": "covers the whole range, not just part of it",
          "body": "var lo = Infinity, hi = -Infinity; for (var i = 0; i < 3000; i++) { var v = subject(); if (v < lo) lo = v; if (v > hi) hi = v; } return lo <= 3 && hi >= 98;"
        }
      ],
      "_source": "trials/fix-the-random.yml"
    },
    "check-getting-started": {
      "id": "check-getting-started",
      "title": "First programs check",
      "kind": "quiz",
      "unlocks": "Values, types and operators",
      "questions": [
        {
          "id": "q1",
          "skill": "Language vs runtime",
          "prompt": "Which of these is **not** part of the JavaScript language?",
          "answers": [
            {
              "key": "A",
              "text": "`Math.floor`",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`document.querySelector`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`JSON.stringify`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`Number`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `document` comes from the browser, not the language — which is why the same line throws `document is not defined` in Node. The other three work in every JavaScript runtime there is.",
            "wrong": "Apply the test: does it work in *every* JavaScript runtime? `Math`, `JSON` and `Number` do. `document` is handed to you by the browser, so the answer is B."
          }
        },
        {
          "id": "q2",
          "skill": "When the runtime runs your code",
          "prompt": "A page has a `<script src=\"main.js\">` in the `<head>` with no other attributes. `main.js` selects an `<h1>` from the body and sets its text. What happens?",
          "answers": [
            {
              "key": "A",
              "text": "It works — the browser loads the whole page before running any script",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`querySelector` throws, because the element is missing",
              "tag": "close",
              "correct": false
            },
            {
              "key": "C",
              "text": "`querySelector` returns `null`, and the *next* line throws a TypeError",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "Nothing happens and no error appears",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `querySelector` looked, found nothing, and returned `null`. That is a normal answer, not a failure. The error comes one line later, when the code uses `null` as if it were an element. So the error points at the second line, but the cause is on the first.",
            "wrong": "`querySelector` does not throw when it finds nothing — it returns `null`. The TypeError happens on the *next* line, when that `null` gets treated as an element. The answer is C. The point of the question is the gap: the error appears on one line, and its cause is on the line before."
          }
        },
        {
          "id": "q3",
          "skill": "Kinds of error",
          "prompt": "This line is meant to give a whole number from 1 to 100, chosen at random. What kind of error is it?",
          "code": "Math.floor(Math.random()) + 1",
          "answers": [
            {
              "key": "A",
              "text": "Syntax error — it will not parse",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "Runtime error — it stops with an error message",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "Logic error — it runs cleanly and is always wrong",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "No error — this is correct",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Every operation is legal, so nothing is reported. `Math.random()` is from 0 up to 1, `Math.floor` of that is 0, and 0 + 1 is 1 — every single time. A logic error is the costly kind, because no message tells you it is there.",
            "wrong": "It parses, and it never stops with an error — so neither A nor B. It is a logic error: `Math.random()` gives a number from 0 up to 1, `Math.floor` of any such number is 0, so the line gives 1 every time. The answer is C."
          }
        },
        {
          "id": "q4",
          "skill": "Kinds of error",
          "prompt": "You see `TypeError: console.lg is not a function`. What is the fault?",
          "answers": [
            {
              "key": "A",
              "text": "`console` does not exist in this runtime",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "The method name is misspelled — it is `log`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "A parenthesis or a quote on the line never closes",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "The script ran before the page was built",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Read the message literally: it found `console` fine — otherwise it would be a ReferenceError, `console is not defined` — and `lg` is not something it can call. A parenthesis that never closes would be a SyntaxError, and nothing would have run.",
            "wrong": "Read the kind first. A `TypeError` means the name before the dot, `console`, was found, so it is not A; an unclosed parenthesis is a `SyntaxError`, so it is not C. What `console` was asked for, `lg`, is not a function: it is misspelled. The answer is B."
          }
        },
        {
          "id": "q5",
          "skill": "Reading a program",
          "prompt": "In the guessing game, why must \"did they guess correctly?\" be checked **before** \"are they out of turns?\"",
          "answers": [
            {
              "key": "A",
              "text": "It runs faster that way",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "JavaScript requires equality checks before comparisons",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "Otherwise a correct tenth guess is reported as a loss",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "The order makes no difference",
              "tag": "common guess",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `if / else if` takes the first branch whose condition is true. On a correct tenth guess both conditions are true, so whichever check comes first wins. Swap them and a player who guesses correctly on their last turn is told they lost — a logic error no message will ever catch.",
            "wrong": "The branches of an `if / else if` are checked in order and only the first match runs. Put \"out of turns\" first and a correct tenth guess is reported as a loss. The answer is C — and the order of your conditions is a design decision, not a formality."
          }
        },
        {
          "id": "q6",
          "skill": "A program runs top to bottom",
          "prompt": "What does this program print?",
          "code": "console.log(\"start\");\n// console.log(\"middle\");\nMath.max(4, 9);\nconsole.log(Math.min(4, 9));",
          "answers": [
            {
              "key": "A",
              "text": "`start`, then `4`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`start`, `middle`, `9`, then `4`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`start`, `9`, then `4`",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "D",
              "text": "`start`, then `9`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Line 2 is a comment, so Node skips it. Line 3 works out 9, but nothing uses the result, so nothing prints. Line 4 shows the smaller number, 4.",
            "wrong": "Go line by line. A line starting with `//` is a comment and does not run. A call on its own, like `Math.max(4, 9);`, produces a value that nothing shows — only `console.log` prints. `Math.min` gives the smaller argument. The answer is A."
          }
        },
        {
          "id": "q7",
          "skill": "SyntaxError",
          "prompt": "A five-line file prints a word on every line. Line 3 is `console.log(\"three\";`. You run it. What happens?",
          "answers": [
            {
              "key": "A",
              "text": "Lines 1 and 2 print, then a SyntaxError",
              "tag": "common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "A SyntaxError, and nothing prints at all",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Every line except line 3 prints",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "All five lines print; the semicolon closes the line",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Node reads the whole file before running any of it. It cannot read line 3 — the `(` after `log` never closes — so it runs none of the file, not even line 1.",
            "wrong": "A SyntaxError is found while Node *reads* the file, before anything runs, so nothing prints — not even lines 1 and 2. (A runtime error is different: the lines before it do run.) The answer is B."
          }
        }
      ],
      "_source": "checks/check-getting-started.yml"
    },
    "variables": {
      "id": "variables",
      "title": "Naming values: let and const",
      "kind": "read",
      "outcome": 1,
      "shape": "procedural",
      "why": "Every later lesson stores a value under a name, and until D-042 no lesson taught how — `let`, `const` and reassignment were assumed (zero-prerequisite audit, `values.names`). It opens Values, types and operators (D-043) and carries the first pass of hoisting: a `let` or `const` cannot be read before its line (D-045). The \"`const` by default, `let` when it changes\" rule moved here from `declarations`, because students write names from this module on. Procedural (01-explanations rule 8): declaring and assigning is notation, and a student who has never seen `let` has nothing to guess from.",
      "concepts": [
        "values.names"
      ],
      "lede": "A program that cannot remember anything can only print. This lesson gives values names, so a line further down can use what a line further up worked out — and shows the two words JavaScript uses to do it.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Operators · When it breaks: reading errors"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Value** — one piece of data: `42`, `\"Ada\"`, `true`. **Variable** — a name that refers to a value. **Declare** — create a name, with `let` or `const`. **Assign** — make a name refer to a value, with `=`. **Reassign** — point an existing `let` name at a new value.",
          "entries": [
            "**Value** — one piece of data: `42`, `\"Ada\"`, `true`.",
            "**Variable** — a name that refers to a value.",
            "**Declare** — create a name, with `let` or `const`.",
            "**Assign** — make a name refer to a value, with `=`.",
            "**Reassign** — point an existing `let` name at a new value."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on First programs. Answer from memory — pulling it back out is\nwhat makes it stick. If you have no idea, press **I don't know**."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A program has four lines, each a `console.log`. Line 2 throws an error. Does line 3 print anything?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes — a program runs top to bottom, and an error stops it where it happens.** Nothing after the failing line runs.\n",
                    "why": "No. The program stops at line 2. Line 1 already printed; lines 3 and 4 never run.\n"
                  },
                  {
                    "code": "console.log(Math.max(3, 7));",
                    "accept": [
                      "7"
                    ],
                    "why": "`7`. `Math.max` is a built-in function: you hand it values, and the call produces the largest one. `console.log` then prints what the call produced.\n"
                  },
                  {
                    "question": "An error says `ReferenceError: totl is not defined`. What is wrong?",
                    "choices": [
                      "A name that does not exist",
                      "A value of the wrong kind"
                    ],
                    "accept": [
                      "A name that does not exist"
                    ],
                    "miss": "**If you said a value of the wrong kind — that is a TypeError.** A ReferenceError is about the **name**: the program used one that was never created. Here, probably a typo for `total`.\n",
                    "why": "A name that does not exist. Look for a typo, or for a name that was never declared. This lesson shows a different message, for a name that is declared but used too early.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can declare a name with `const` or `let` and use it in place of its value",
                "I can reassign a `let`, and say what every name holds after a sequence of assignments",
                "I can say why reassigning a `const` fails, and choose between `const` and `let`",
                "I can say why a name cannot be used on a line above the one that declares it"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Give a value a name",
          "body": [
            {
              "p": "A **value** is one piece of data: the number `12`, the text `\"Walla Walla\"`.\nA **variable** is a name that refers to a value. You **declare** a name — create\nit — with the word `const`, then use the name anywhere you would have written\nthe value."
            },
            {
              "code": "const price = 12;",
              "lang": "js",
              "file": "price.js"
            },
            {
              "p": "Read that line in four parts:"
            },
            {
              "table": {
                "head": [
                  "Part",
                  "What it is"
                ],
                "rows": [
                  [
                    "`const`",
                    "the **keyword** that says \"make a new name\""
                  ],
                  [
                    "`price`",
                    "the name you chose"
                  ],
                  [
                    "`=`",
                    "the **assignment operator**: make the name refer to the value on the right"
                  ],
                  [
                    "`12;`",
                    "the value, and the **semicolon** `;` that ends the statement"
                  ]
                ]
              }
            },
            {
              "p": "**`=` does not mean \"is equal to\".** It is an instruction: *price gets 12*.\nChecking whether two things are equal uses a different operator, `===`, which\nthe lesson *Comparing and combining*, later in this module, covers."
            },
            {
              "p": "Two worked examples. The labels say what each group of lines is *for*."
            },
            {
              "code": "// name the value\nconst price = 12;\n\n// use the name where the value would go\nconsole.log(price);        // 12\nconsole.log(price + 3);    // 15",
              "lang": "js",
              "file": "price.js"
            },
            {
              "code": "// name the value\nconst town = \"Walla Walla\";\n\n// use the name where the value would go\nconsole.log(town);         // Walla Walla",
              "lang": "js",
              "file": "town.js"
            },
            {
              "p": "When the line `console.log(price + 3);` runs, JavaScript looks up `price`,\nfinds `12`, and works out `12 + 3`. The name stands in for its value. (`+`, `-`, `*` for\nmultiply and `/` for divide work as they do in arithmetic. The next lesson,\n*Operators*, covers them properly.)"
            },
            {
              "p": "A few rules for names. A name can hold letters, digits, `_` and `$`, and cannot\nstart with a digit or contain a space. **Capitals matter**: `price` and `Price`\nare two different names. A name made of several words cannot contain a space,\nso JavaScript programmers join the words and start each one after the first with\na capital: `ticketPrice`, `userName`. That convention is called **camelCase**.\n`ticket_price` is also legal; this course writes camelCase because JavaScript's\nown built-in names use it, so your names match the ones you call. Pick names\nthat say what the value *means*: `price`, not `p` or `x`. A reader cannot tell\nwhat `x` holds without tracing the program."
            },
            {
              "p": "For each, type what it prints — or pick, where there are buttons."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const width = 8;\nconsole.log(width + 2);\n",
                    "accept": [
                      "10"
                    ],
                    "why": "`10`. JavaScript looks up `width`, finds `8`, and works out `8 + 2`.\n"
                  },
                  {
                    "code": "const city = \"Pasco\";\nconsole.log(City);\n",
                    "choices": [
                      "Pasco",
                      "City",
                      "ReferenceError"
                    ],
                    "accept": [
                      "ReferenceError"
                    ],
                    "miss": "**If you picked `Pasco` — capitals matter.** `city` and `City` are two different names, and only one of them was declared.\n",
                    "why": "A ReferenceError: `City is not defined`. The declared name is `city`, all lowercase.\n"
                  },
                  {
                    "question": "How do you read `=` in `const total = 5;`?",
                    "choices": [
                      "total is equal to 5",
                      "total now refers to 5"
                    ],
                    "accept": [
                      "total now refers to 5"
                    ],
                    "miss": "**If you read it as \"is equal to\" — that is what `=` means in maths, not here.** In JavaScript `=` is an instruction that links the name to the value. It does not ask anything.\n",
                    "why": "*total now refers to 5.* `=` assigns; it never compares.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Change it: let, and reassigning",
          "body": [
            {
              "p": "Some values change while a program runs: a score, a count, a running total.\nFor those, declare the name with **`let`**. Then you can **reassign** it —\npoint the same name at a new value — by writing the name, `=`, and the new\nvalue. A reassignment has no `let` in front of it, because the name already\nexists."
            },
            {
              "code": "let score = 0;\nscore = 10;\nconsole.log(score);   // 10",
              "lang": "js",
              "file": "score.js"
            },
            {
              "p": "The line people stumble on is this one:"
            },
            {
              "code": "score = score + 5;",
              "lang": "js"
            },
            {
              "p": "Read as maths, it is impossible: no number equals itself plus 5. Read as an\ninstruction, it is ordinary, and JavaScript\ndoes it in two moves: **first** it works out the right-hand side using the value\n`score` has *now*, **then** it points `score` at the result. Read it as *score\ngets score plus 5*."
            },
            {
              "p": "To follow a program like this, keep a small table of every name and its current\nvalue, and update it one line at a time. Two worked examples:"
            },
            {
              "code": "let lives = 3;          // lives: 3\n\nlives = lives - 1;      // 3 - 1 is 2       lives: 2\nlives = lives - 1;      // 2 - 1 is 1       lives: 1\n\nconsole.log(lives);     // 1",
              "lang": "js",
              "file": "lives.js"
            },
            {
              "code": "let a = 5;              // a: 5\nlet b = a;              // b gets a's value NOW: 5      a: 5  b: 5\n\na = 9;                  // a: 9  b: 5\n\nconsole.log(b);         // 5",
              "lang": "js",
              "file": "copy.js"
            },
            {
              "p": "*copy.js* is the one to slow down on. `let b = a` gave `b` the value `a` had **at\nthat moment**, which was `5`. It did not tie `b` to `a`. When `a` later changes,\n`b` still holds `5`. In JavaScript, that is always true for numbers and text.\nLists and records behave differently, and the Values and references module\nshows how."
            },
            {
              "p": "You declare a name **once**. Writing `let` again for a name that already exists\nin the same program, as in the third question below, is an error, reported\nbefore any line runs: `SyntaxError: Identifier 'score' has already been\ndeclared`. (The Scope module shows where code can declare the same name twice.)"
            },
            {
              "p": "For each, type the number it prints — or pick, where there are buttons."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let n = 4;\nn = n * 2;\nn = n + 1;\nconsole.log(n);\n",
                    "accept": [
                      "9"
                    ],
                    "miss": "**If you answered `5` — each line uses the value `n` has *after* the line above.** `n * 2` is `8`, and then `8 + 1` is `9`.\n",
                    "why": "`9`. Table: `n` is 4, then 8, then 9.\n"
                  },
                  {
                    "code": "let x = 1;\nlet y = x;\nx = 7;\nconsole.log(y);\n",
                    "accept": [
                      "1"
                    ],
                    "miss": "**If you answered `7` — `y` got `x`'s value when line 2 ran, which was `1`.** Changing `x` afterwards does not reach back and change `y`.\n",
                    "why": "`1`. `y` holds a value, not a link to `x`.\n"
                  },
                  {
                    "code": "let total = 10;\nlet total = 20;\nconsole.log(total);\n",
                    "choices": [
                      "10",
                      "20",
                      "SyntaxError"
                    ],
                    "accept": [
                      "SyntaxError"
                    ],
                    "miss": "**If you answered `20` — the second line tries to *create* `total` again.** To change it, leave out the keyword: `total = 20;`.\n",
                    "why": "A SyntaxError: `Identifier 'total' has already been declared`. Nothing runs, not even line 1.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "const means this name will not be reassigned",
          "body": [
            {
              "p": "A name declared with `const` cannot be pointed at a different value. Try, and\nthe program stops:"
            },
            {
              "code": "const limit = 3;\nlimit = 4;    // TypeError: Assignment to constant variable.",
              "lang": "js",
              "file": "limit.js"
            },
            {
              "p": "Why would you want a name that cannot change? Because it is a promise to\nwhoever reads the code, you included. When you see `const taxRate = 0.08` at the\ntop of a long file, you know `taxRate` is `0.08` on every line below it. You do\nnot have to hunt for a line that changed it. There isn't one."
            },
            {
              "p": "This course follows a common convention: **`const` by default; `let` when the\nname must be reassigned.** Writing `let` for every name is legal, and the\nprogram runs the same. What you lose is the promise: a reader has to check\nevery line below a `let` to know whether its value changed. Some programmers\nwrite `let` everywhere and accept that cost. To decide between the two, ask one\nquestion: *will any later line say `name = …` for this name?* If yes, `let`.\nIf no, `const`."
            },
            {
              "p": "Two worked examples of deciding."
            },
            {
              "code": "// no name here is ever reassigned, so each one is a const\nconst itemPrice = 4;\nconst quantity = 3;\nconst subtotal = itemPrice * quantity;",
              "lang": "js",
              "file": "receipt.js"
            },
            {
              "code": "// goal is never reassigned, so it is a const\n// steps is reassigned on the last two lines, so it is a let\nconst goal = 10000;\nlet steps = 0;\n\nsteps = steps + 2500;\nsteps = steps + 4000;",
              "lang": "js",
              "file": "steps.js"
            },
            {
              "p": "Two more facts about declaring:"
            },
            {
              "list": [
                "A `const` must be given its value on the same line. `const goal;` is a SyntaxError: there would be no way to give it one later.",
                "A `let` can be declared with no value — `let answer;` — and holds `undefined`, JavaScript's \"nothing here yet\", until something is assigned. The lesson *When there is nothing there*, later in this module, is about that value."
              ]
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const max = 5;\nmax = max + 1;\nconsole.log(max);\n",
                    "choices": [
                      "5",
                      "6",
                      "TypeError"
                    ],
                    "accept": [
                      "TypeError"
                    ],
                    "miss": "**If you answered `6` — `const` refuses the reassignment.** The program stops on line 2 with `TypeError: Assignment to constant variable.`, and line 3 never runs.\n",
                    "why": "A TypeError. The fix is to decide whether `max` should change at all. If it should, declare it with `let`.\n"
                  },
                  {
                    "code": "___ attempts = 0;\nattempts = attempts + 1;\n",
                    "choices": [
                      "const",
                      "let"
                    ],
                    "accept": [
                      "let"
                    ],
                    "why": "`let`. Line 2 reassigns `attempts`, so it cannot be a `const`.\n"
                  },
                  {
                    "code": "___ greeting = \"Welcome back\";\nconsole.log(greeting);\nconsole.log(greeting);\n",
                    "choices": [
                      "const",
                      "let"
                    ],
                    "accept": [
                      "const"
                    ],
                    "miss": "**If you picked `let` \"in case it changes\" — nothing reassigns it.** `const` tells the next reader that, which is the point of it.\n",
                    "why": "`const`. The name is used twice and never reassigned.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "A name exists from its line down",
          "body": [
            {
              "p": "A program runs top to bottom, and a name declared with `let` or `const` is not\nready to use until the line that declares it has run. Use the name on a line\nabove its declaration, and the program stops:"
            },
            {
              "code": "console.log(total);   // ReferenceError: Cannot access 'total' before initialization\nlet total = 5;",
              "lang": "js",
              "file": "early.js"
            },
            {
              "p": "Compare that message with the one for a name that is declared **nowhere**:"
            },
            {
              "table": {
                "head": [
                  "Message",
                  "What it means",
                  "Fix"
                ],
                "rows": [
                  [
                    "`total is not defined`",
                    "there is no `total` anywhere",
                    "a typo, or a missing declaration"
                  ],
                  [
                    "`Cannot access 'total' before initialization`",
                    "`total` is declared, but **further down**",
                    "move the use below the declaration"
                  ]
                ]
              }
            },
            {
              "p": "The *before initialization* message raises a question: how does JavaScript\nknow about `total` before it reaches the line that declares it? Before running\nany of your code, JavaScript reads through it and notes every name it declares.\nFrom the start of the program down to the line that declares `total`, the name\nis known but not usable. That stretch has a name, the **temporal dead zone**:\n*temporal* because it is about *when* the declaring line runs. A `const` name\nhas a temporal dead zone too, exactly like a `let` name."
            },
            {
              "block": "notice",
              "label": "You will also see var",
              "body": [
                {
                  "p": "Older code declares names with **`var`**: `var count = 0;`. It works much like\n`let`, with two differences you will trip over when reading it. Used above its\nline, a `var` gives `undefined` instead of an error, so the mistake goes\nunreported. And it ignores some of the boundaries `let` respects — the Scope\nmodule explains which. Recognise `var` when you read it. This course writes\n`const` and `let`, because they report the mistakes that `var` lets through.\nFunctions have their own rule about being used above their line, and the\nFunctions module covers it."
                }
              ]
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "console.log(city);\nconst city = \"Paris\";\n",
                    "choices": [
                      "Paris",
                      "undefined",
                      "ReferenceError"
                    ],
                    "accept": [
                      "ReferenceError"
                    ],
                    "miss": "**If you answered `Paris` — line 1 runs before line 2.** When line 1 runs, `city` has no value yet.\n",
                    "why": "`ReferenceError: Cannot access 'city' before initialization`. Move the `console.log` below the declaration.\n"
                  },
                  {
                    "question": "An error says `Cannot access 'price' before initialization`. Where do you look for `price`?",
                    "choices": [
                      "Further down the file",
                      "Nowhere — it was never declared"
                    ],
                    "accept": [
                      "Further down the file"
                    ],
                    "miss": "**If you said nowhere — that is the other message, `is not defined`.** This one means JavaScript found the declaration; your code used the name too early.\n",
                    "why": "Further down. The name is declared below the line that used it.\n"
                  },
                  {
                    "code": "const a = 2;\nconst b = a + 1;\nconsole.log(b);\n",
                    "accept": [
                      "3"
                    ],
                    "why": "`3`. `a` is declared on line 1, so line 2 can use it. The declaration comes before the use, which is all the rule asks.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A small program for a coffee order, with the keywords left out:"
            },
            {
              "code": "___ cupPrice = 4;\n___ cups = 2;\n___ total = cupPrice * cups;\n___ tip = 0;\n___ message = \"Thanks for your order\";\n\ncups = cups + 1;\ntotal = cupPrice * cups;\ntip = 2;",
              "lang": "js",
              "file": "order.js"
            },
            {
              "p": "Choose `const` or `let` for each of the five names. For each name you made a\n`let`, give the number of the line that reassigns it. Then: someone moves\n`tip = 2;` to line 1, above every declaration. What happens when the program\nruns, and what does the error message tell them to do?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`const name = value;`** gives a value a name. `=` means *gets*, never *is equal to*.",
                "**This course writes `let` for a name that will be reassigned, and `const` for every other**, so a reader knows which values can change. A reassigned name gets the value worked out *at the moment the line runs*.",
                "**A name cannot be used above its declaration**: `Cannot access … before initialization` means the declaration is further down."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "let",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/let",
              "note": "declaring a name you will reassign, and the temporal dead zone."
            },
            {
              "title": "const",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/const",
              "note": "declaring a name that will not be reassigned."
            }
          ]
        }
      ],
      "_source": "lessons/variables.md",
      "practice": {
        "id": "variables",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "What does it print?",
            "prompt": "Type the number the last line prints — or, where there are buttons, pick the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "let points = 5;\npoints = points + 3;\nconsole.log(points);\n",
                "accept": [
                  "8"
                ],
                "why": "`8`. The right side is worked out first with the current value, `5`, and then `points` gets the result.\n"
              },
              {
                "id": "p2",
                "code": "let a = 4;\nlet b = a;\na = 10;\nconsole.log(b);\n",
                "accept": [
                  "4"
                ],
                "miss": "**If you answered `10` — `b` got `a`'s value on line 2, which was `4`.** It is not tied to `a` afterwards.\n",
                "why": "`4`. Reassigning `a` changes only `a`.\n"
              },
              {
                "id": "p3",
                "code": "const size = 3;\nsize = 4;\nconsole.log(size);\n",
                "choices": [
                  "3",
                  "4",
                  "TypeError"
                ],
                "accept": [
                  "TypeError"
                ],
                "miss": "**If you answered `4` — a `const` cannot be reassigned.** Line 2 stops the program, and line 3 never runs.\n",
                "why": "`TypeError: Assignment to constant variable.`\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "console.log(total);\nlet total = 2;\n",
                "choices": [
                  "2",
                  "undefined",
                  "ReferenceError"
                ],
                "accept": [
                  "ReferenceError"
                ],
                "why": "`Cannot access 'total' before initialization`. Line 1 runs before `total` has a value.\n"
              },
              {
                "id": "p5",
                "code": "let x = 2;\nx = x * x;\nx = x + 1;\nconsole.log(x);\n",
                "accept": [
                  "5"
                ],
                "why": "`5`. `x` is 2, then `2 * 2` is 4, then `4 + 1` is 5.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 3 for points",
                "say": "`points` does not stay 5. Work out the right-hand side of line 2 with the current value, then update your table.\n"
              },
              {
                "when": "you answered 10 for b",
                "say": "Keep a table with a row for `a` and a row for `b`. On line 2, what number goes in `b`'s row? Does line 3 touch that row?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict four lines, then run it",
            "prompt": "Predict each line the program prints — type the number — then run it and compare.\n",
            "filename": "temperature.js",
            "code": "let temp = 60;\nconsole.log(temp);\ntemp = temp + 15;\nconsole.log(temp);\nconst high = temp;\ntemp = temp - 30;\nconsole.log(high);\nconsole.log(Math.max(temp, 50));\n",
            "items": [
              {
                "id": "r1",
                "question": "Type the first number it prints.",
                "accept": [
                  "60"
                ],
                "why": "`60` — `temp`'s first value.\n"
              },
              {
                "id": "r2",
                "question": "Type the second number it prints.",
                "accept": [
                  "75"
                ],
                "why": "`75`. `temp` gets `60 + 15`.\n"
              },
              {
                "id": "r3",
                "question": "Type the third number it prints (the value of `high`).",
                "accept": [
                  "75"
                ],
                "miss": "**If you answered `45` — `high` got `temp`'s value on line 5, which was `75`.** Line 6 changes `temp`, not `high`.\n",
                "why": "`75`. `high` holds the value `temp` had when line 5 ran.\n"
              },
              {
                "id": "r4",
                "question": "Type the fourth number it prints.",
                "accept": [
                  "50"
                ],
                "miss": "**If you answered `45` — that is `temp`, but the line asks `Math.max` which is bigger, `45` or `50`.**\n",
                "why": "`50`. `temp` is `45` by now, and `Math.max(45, 50)` produces the larger value.\n"
              }
            ],
            "hints": [
              {
                "when": "your third answer was 45",
                "say": "Find the line that gives `high` its value. What was `temp` on that line? Nothing after it reassigns `high` — it is a `const`.\n"
              },
              {
                "when": "your fourth answer was 75",
                "say": "`temp` was reassigned on line 6. Update your table for `temp` before working out the last line.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Keep the table",
            "prompt": "One program, growing a line at a time. For each, type the number the name holds after the last line shown.\n",
            "items": [
              {
                "id": "t1",
                "code": "let stock = 12;\nstock = stock - 5;\n// stock now holds?\n",
                "accept": [
                  "7"
                ],
                "why": "`7`. `12 - 5`.\n"
              },
              {
                "id": "t2",
                "code": "let stock = 12;\nstock = stock - 5;\nconst sold = 12 - stock;\n// sold now holds?\n",
                "accept": [
                  "5"
                ],
                "why": "`5`. `stock` is `7` by now, so `12 - 7`.\n"
              },
              {
                "id": "t3",
                "code": "let stock = 12;\nstock = stock - 5;\nconst sold = 12 - stock;\nstock = stock + sold;\n// stock now holds?\n",
                "accept": [
                  "12"
                ],
                "miss": "**If you answered `17` — `stock` was `7`, not `12`, when line 4 ran.** Use the value from your table, not the one on line 1.\n",
                "why": "`12`. `7 + 5`: the shelf is restocked with what was sold.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "let hours = 8;\nlet pay = hours * 20;\nhours = 10;\n// pay now holds?\n",
                "accept": [
                  "160"
                ],
                "miss": "**If you answered `200` — `pay` was worked out on line 2, when `hours` was `8`.** Changing `hours` later does not redo line 2.\n",
                "why": "`160`. A name holds the value it was given; it is not a formula that updates itself.\n"
              },
              {
                "id": "t5",
                "code": "let a = 1;\nlet b = 2;\na = b;\nb = a;\n// b now holds?\n",
                "accept": [
                  "2"
                ],
                "why": "`2`. Line 3 makes `a` 2; line 4 gives `b` that 2. The original `1` is gone — this is why swapping two names needs a third one.\n"
              }
            ],
            "hints": [
              {
                "when": "you used a value from line 1 on a later line",
                "say": "Each line uses the values in your table at that moment. Update the table after every `=`, then read from the table, not from the top of the program.\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Reassign, then send it out",
            "prompt": "`afterRefill` should return how much fuel is in the tank after 20 more litres go in. `tank` already holds the starting amount. Add a line that reassigns `tank` to 20 more than it holds now. Then send `tank` out with a second line, `return tank;`. `return` sends a value out of the function so the tests can check it; the Functions module explains the rest. Then, in one sentence: why is `tank` declared with `let` and not `const`?\n",
            "exportName": "afterRefill",
            "filename": "refill.js",
            "starter": "function afterRefill(fuel) {\n  let tank = fuel;\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "afterRefill(5) → 25",
                "body": "return subject(5) === 25;"
              },
              {
                "label": "afterRefill(0) → 20",
                "body": "return subject(0) === 20;"
              },
              {
                "label": "afterRefill(31) → 51",
                "body": "return subject(31) === 51;"
              }
            ],
            "hints": [
              {
                "when": "the error says tank has already been declared",
                "say": "`tank` already exists — line 2 created it. To change it, write the name without a keyword: `tank = …`.\n"
              },
              {
                "when": "every test says undefined",
                "say": "The function worked out the answer but never sent it out. Add `return tank;` after the line that reassigns `tank`.\n"
              },
              {
                "when": "afterRefill(5) gives 5",
                "say": "A line like `tank + 20;` works out 25 and throws the result away. The result lands in `tank` only if you assign it: `tank = …`.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Add a fee, and meet the const",
            "prompt": "`finalPrice` takes 2 off for a coupon. The shop now adds a 1-dollar fee *after* the coupon. Where the TODO is, add the fee on its own line: `total = total + 1;`. Run it and read the error, then change one word so the function works. The line `return total;` is already there: `return` sends a value out of the function so the tests can check it, and the Functions module explains the rest. Then, in one sentence: what did the error tell you about how `total` was declared?\n",
            "exportName": "finalPrice",
            "filename": "final-price.js",
            "starter": "function finalPrice(price) {\n  const total = price - 2;   // a 2-dollar coupon\n  // TODO: put your code here\n  return total;\n}\n",
            "tests": [
              {
                "label": "finalPrice(10) → 9",
                "body": "return subject(10) === 9;"
              },
              {
                "label": "finalPrice(5) → 4",
                "body": "return subject(5) === 4;"
              },
              {
                "label": "finalPrice(2) → 1",
                "body": "return subject(2) === 1;"
              }
            ],
            "hints": [
              {
                "when": "the error says Assignment to constant variable",
                "say": "That is the point of this step. `total` is a `const`, and your new line reassigns it. Which keyword lets a name be reassigned?\n"
              },
              {
                "when": "finalPrice(10) gives 8",
                "say": "The fee is not in the answer yet. Is your new line above the `return`? Lines after a `return` never run.\n"
              },
              {
                "when": "the error says total has already been declared",
                "say": "Your new line starts with `let` or `const`, which tries to create `total` a second time. Reassign it without a keyword, and change the keyword on line 2 instead.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a classmate's program",
            "prompt": "A classmate wrote this to work out an average sale, and asked why it will not run. Read it, then answer each question.\n",
            "filename": "average.js",
            "code": "let total = 0;\nconst count = 0;\n\ntotal = total + 40;\ncount = count + 1;\ntotal = total + 25;\ncount = count + 1;\n\nconsole.log(average);\nconst average = total / count;\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line stops the program first? Type the line number.",
                "accept": [
                  "5",
                  "line 5",
                  "l5"
                ],
                "miss": "**If you said 9 — the program never gets that far.** It runs top to bottom, and an earlier line fails first.\n",
                "why": "Line 5. `count` is a `const`, and line 5 reassigns it.\n"
              },
              {
                "id": "c2",
                "question": "What kind of error does that line throw?",
                "choices": [
                  "SyntaxError",
                  "ReferenceError",
                  "TypeError"
                ],
                "accept": [
                  "TypeError"
                ],
                "why": "`TypeError: Assignment to constant variable.` The fix is `let` on line 2.\n"
              },
              {
                "id": "c3",
                "question": "With line 2 fixed, a different line throws. Type its line number.",
                "accept": [
                  "9",
                  "line 9",
                  "l9"
                ],
                "why": "Line 9 uses `average`, which is declared on line 10.\n"
              },
              {
                "id": "c4",
                "question": "Which fix is right for line 9?",
                "choices": [
                  "Move the console.log below the line that declares average",
                  "Change const to let on line 10"
                ],
                "accept": [
                  "Move the console.log below the line that declares average"
                ],
                "miss": "**If you picked `let` — the keyword is not the problem.** A `let` also cannot be used above its line. The order is the problem.\n",
                "why": "Move it down. `Cannot access 'average' before initialization` is about order, and `let` has the same rule.\n"
              }
            ],
            "hints": [
              {
                "when": "you named line 9 first",
                "say": "Read from the top, keeping your table. Does every line from 1 to 8 run without a problem? Check which keyword each name was declared with.\n"
              }
            ]
          }
        ]
      }
    },
    "operators": {
      "id": "operators",
      "title": "Arithmetic operators",
      "kind": "read",
      "outcome": 1,
      "shape": "procedural",
      "why": "Every lesson after this one computes with numbers — `%` in the Functions examples, `+=` and `++` in every loop; `fix-the-random`, in Getting started, already leans on `Math.floor(Math.random() * n)` — and until D-042 none taught them (zero-prerequisite audit: `ops.arithmetic`, `values.math`). Split from comparing and combining by D-029: arithmetic and `Math` are met together — rounding and clamping are arithmetic on a number — while comparison and logic are their own lesson, `comparisons`. Procedural (01-explanations rule 8): operators are notation and a rule of order, and the precedence rule is a procedure a student applies step by step.",
      "concepts": [
        "ops.arithmetic",
        "values.math"
      ],
      "lede": "An operator takes values and produces a new one: `+` takes two numbers and produces their sum. This lesson covers the operators for arithmetic, the order JavaScript works them out in, and the built-in tools for rounding and checking a number. Comparing values comes later in this module, in *Comparing and combining*.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Naming values: let and const · Comparing and combining · Conversion"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Operator** — a symbol that takes values and produces a new value: `+`, `*`, `%`. **Operand** — a value an operator works on. **Expression** — any piece of code that produces a value: `2 + 3`, `price`, `price * 2`. **Precedence** — the order in which operators are worked out.",
          "entries": [
            "**Operator** — a symbol that takes values and produces a new value: `+`, `*`, `%`.",
            "**Operand** — a value an operator works on.",
            "**Expression** — any piece of code that produces a value: `2 + 3`, `price`, `price * 2`.",
            "**Precedence** — the order in which operators are worked out."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on the lesson before this one, *Naming values: let and const*.\nAnswer from memory; press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let score = 10;\nscore = score + 5;\nconsole.log(score);\n",
                    "accept": [
                      "15"
                    ],
                    "why": "`15`. The right side is worked out with the value `score` has now, then `score` gets the result.\n"
                  },
                  {
                    "code": "const limit = 3;\nlimit = 4;\n",
                    "choices": [
                      "limit is now 4",
                      "TypeError",
                      "ReferenceError"
                    ],
                    "accept": [
                      "TypeError"
                    ],
                    "miss": "**If you said `limit` is now 4 — a `const` cannot be reassigned.** That is what `const` promises.\n",
                    "why": "`TypeError: Assignment to constant variable.`\n"
                  },
                  {
                    "question": "A name will be reassigned later in the program. Which keyword declares it?",
                    "choices": [
                      "const",
                      "let"
                    ],
                    "accept": [
                      "let"
                    ],
                    "why": "`let`. This course writes `const` by default, and `let` when some later line says `name = …`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can work out an expression in the order JavaScript does, and add parentheses to make it mean something else",
                "I can predict `%` (with a negative number too), `**`, `+=`, prefix and postfix `++`, `0.1 + 0.2`, `1 / 0` and `NaN`",
                "I can round, clamp and check a number with `Math` and `Number.isNaN`"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Arithmetic",
          "body": [
            {
              "p": "An **operator** is a symbol that takes values and produces a new one. The values\nit works on are its **operands**. Any piece of code that produces a value is an\n**expression**: `7`, `price`, and `price * 2` are all expressions."
            },
            {
              "p": "Working out an expression is called **evaluating** it, and the value it\nproduces is what it **evaluates to**: `7 + 2` evaluates to `9`."
            },
            {
              "table": {
                "head": [
                  "Operator",
                  "Name",
                  "Does",
                  "Example",
                  "Produces"
                ],
                "rows": [
                  [
                    "`+`",
                    "addition",
                    "add",
                    "`7 + 2`",
                    "`9`"
                  ],
                  [
                    "`-`",
                    "subtraction",
                    "subtract",
                    "`7 - 2`",
                    "`5`"
                  ],
                  [
                    "`*`",
                    "multiplication",
                    "multiply",
                    "`7 * 2`",
                    "`14`"
                  ],
                  [
                    "`/`",
                    "division",
                    "divide",
                    "`7 / 2`",
                    "`3.5`"
                  ],
                  [
                    "`%`",
                    "remainder (often called *modulus* or *mod*)",
                    "what is **left over** after dividing",
                    "`7 % 2`",
                    "`1`"
                  ],
                  [
                    "`**`",
                    "exponentiation",
                    "to the power of",
                    "`7 ** 2`",
                    "`49`"
                  ]
                ]
              }
            },
            {
              "p": "Use the names when you search or ask about one: \"JavaScript remainder\noperator\" finds `%`, where searching for the symbol often finds nothing."
            },
            {
              "p": "Two of these need a second look."
            },
            {
              "p": "**`/` keeps the fraction.** `7 / 2` is `3.5`, not `3`. JavaScript's ordinary number\ntype holds decimals, and every number in this course is that type. (There is\na second, `BigInt`, for whole numbers too large for the ordinary type; you\nwill not need it here.)"
            },
            {
              "p": "**`%` is the remainder** — what is left over after dividing as many whole times\nas fit. Two worked examples:"
            },
            {
              "code": "// 5 fits into 17 three times, which uses 15\n// 17 - 15 is 2\n17 % 5     // 2",
              "lang": "js",
              "file": "remainder.js"
            },
            {
              "code": "// 60 fits into 135 twice, which uses 120\n// 135 - 120 is 15\n135 % 60   // 15 — 135 minutes is 2 hours and 15 minutes",
              "lang": "js",
              "file": "minutes.js"
            },
            {
              "p": "A remainder of `0` means the division came out exact, with nothing left over.\nThat is how you test whether a number is even: `n % 2` is `0` for every even\nnumber."
            },
            {
              "p": "**Remainder, modulus, mod.** You will often hear `%` called the *modulus* (or\n*modulo*) operator, and see maths written as *x mod n*: `17 mod 5` is 2, the\nsame as `17 % 5`. JavaScript's own name for `%` is the *remainder* operator,\nand the difference shows only with negative numbers. JavaScript's remainder\nkeeps the sign of the number being divided, so `-7 % 3` is `-1`; in maths,\n`-7 mod 3` is `2`, and so is `-7 % 3` in Python, where `%` really is modulo.\nThe counts, times and positions in this course's examples are never negative,\nso there the two answers agree. When you do need the maths answer for a negative number, write\n`((x % n) + n) % n`."
            },
            {
              "p": "Type the number each evaluates to."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "9 / 2",
                    "accept": [
                      "4.5"
                    ],
                    "why": "`4.5`. Division keeps the fraction.\n"
                  },
                  {
                    "code": "10 % 3",
                    "accept": [
                      "1"
                    ],
                    "miss": "**If you answered `3` or `3.33…` — that is how many times it fits, not what is left.** 3 fits three times, using 9, and `10 - 9` is `1`.\n",
                    "why": "`1`. The remainder.\n"
                  },
                  {
                    "code": "8 % 2",
                    "accept": [
                      "0"
                    ],
                    "why": "`0`. 2 fits into 8 exactly, so nothing is left: 8 is even.\n"
                  },
                  {
                    "code": "2 ** 4",
                    "accept": [
                      "16"
                    ],
                    "why": "`16`. `2 * 2 * 2 * 2`.\n"
                  },
                  {
                    "code": "-7 % 3",
                    "accept": [
                      "-1"
                    ],
                    "miss": "**If you answered `2` — that is `-7 mod 3` in maths, and Python's answer.** JavaScript's `%` is a remainder: it keeps the sign of `-7`. 3 fits into `-7` twice going towards zero, using `-6`, and `-7 - (-6)` is `-1`.\n",
                    "why": "`-1`. JavaScript's remainder takes the sign of the number being divided.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Which operator goes first",
          "body": [
            {
              "p": "When an expression has more than one operator, JavaScript does not simply go\nleft to right. It follows an order, the same one you may know from maths:"
            },
            {
              "list": [
                "anything in **parentheses** `( )`, first",
                "then `**`",
                "then `*`, `/` and `%`, left to right",
                "then `+` and `-`, left to right"
              ],
              "ordered": true
            },
            {
              "p": "This order is called **precedence**. To work an expression out, repeat one move\nuntil a single value is left: *find the operator that goes first, and replace it\nand its two operands with its result.* Three worked examples — the third leaves\nthe last step to you."
            },
            {
              "code": "// 2 + 3 * 4\n// find the operator that goes first\n//   * goes before +, so 3 * 4 first\n// replace it with its result\n//   2 + 12\n// repeat until one value is left\n//   14",
              "lang": "js"
            },
            {
              "code": "// 20 - 8 / 2 * 3\n// find the operator that goes first\n//   / and * are the same level: take the leftmost, 8 / 2\n// replace it with its result\n//   20 - 4 * 3\n// repeat until one value is left\n//   20 - 12   (* goes before -)\n//   8",
              "lang": "js"
            },
            {
              "code": "// (20 - 8) / 2 * 3\n// find the operator that goes first\n//   parentheses: 20 - 8\n// replace it with its result\n//   12 / 2 * 3\n// repeat until one value is left\n//   6 * 3\n//   ?",
              "lang": "js"
            },
            {
              "p": "The third example evaluates to `18`. It has the same numbers as the second\nexample, plus one pair of parentheses, and it gives a different answer. **When\nyou want a different order, add parentheses.** Parentheses are also worth adding\nwhen the order is already right but a reader might not be sure of it. A common\nmistake that parentheses fix is an average:"
            },
            {
              "code": "const a = 4;\nconst b = 6;\nconsole.log(a + b / 2);     // 7 — divides only b\nconsole.log((a + b) / 2);   // 5 — the average",
              "lang": "js",
              "file": "average.js"
            },
            {
              "p": "Type the number each evaluates to."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "10 - 2 * 3",
                    "accept": [
                      "4"
                    ],
                    "miss": "**If you answered `24` — you went left to right.** `*` goes before `-`, so `2 * 3` is evaluated first: `10 - 6`.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "code": "(10 - 2) * 3",
                    "accept": [
                      "24"
                    ],
                    "why": "`24`. The parentheses make `10 - 2` go first.\n"
                  },
                  {
                    "code": "2 * 3 ** 2",
                    "accept": [
                      "18"
                    ],
                    "miss": "**If you answered `36` — `**` goes before `*`.** `3 ** 2` is `9`, then `2 * 9`.\n",
                    "why": "`18`.\n"
                  },
                  {
                    "question": "Someone writes the average of `8` and `4` as `8 + 4 / 2`. Type the number it evaluates to.",
                    "accept": [
                      "10"
                    ],
                    "why": "`10`, not `6`. Only the `4` is divided. It needs `(8 + 4) / 2`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Updating a name in place",
          "body": [
            {
              "p": "The lesson *Naming values: let and const* reassigned names like this:\n`score = score + 5`. That pattern is so common that JavaScript has shorter\nspellings for it. Each short form is a reassignment, so it works only on a name\ndeclared with `let`; on a `const` it throws the same TypeError as `=`."
            },
            {
              "table": {
                "head": [
                  "Short form",
                  "Name",
                  "Means"
                ],
                "rows": [
                  [
                    "`score += 5`",
                    "addition assignment",
                    "`score = score + 5`"
                  ],
                  [
                    "`score -= 5`",
                    "subtraction assignment",
                    "`score = score - 5`"
                  ],
                  [
                    "`score *= 2`",
                    "multiplication assignment",
                    "`score = score * 2`"
                  ],
                  [
                    "`score /= 2`",
                    "division assignment",
                    "`score = score / 2`"
                  ],
                  [
                    "`count++`",
                    "increment",
                    "`count = count + 1`"
                  ],
                  [
                    "`count--`",
                    "decrement",
                    "`count = count - 1`"
                  ]
                ]
              }
            },
            {
              "p": "The four that end in `=` are called the *compound assignment* operators."
            },
            {
              "p": "`++` and `--` count up and down by one. Most of the `for` loops in the\nLoops module use one. Each comes in two forms, and the difference\nmatters:"
            },
            {
              "list": [
                "**Postfix increment**, `count++`: adds one, and as an expression evaluates to the value from *before* the change.",
                "**Prefix increment**, `++count`: adds one, and evaluates to the value *after* it."
              ]
            },
            {
              "p": "On a line of their own, `count++;` and `++count;` do exactly the same thing:\nnothing uses the value they produce. The difference appears as soon as one sits\ninside a bigger expression:"
            },
            {
              "code": "let a = 5;\nconst before = a++;   // before: 5    a: 6\nlet b = 5;\nconst after = ++b;    // after: 6     b: 6",
              "lang": "js",
              "file": "counting.js"
            },
            {
              "p": "`--` works the same way: *postfix decrement* `count--` and *prefix decrement*\n`--count`. This course writes `++` and `--` only on a line of their own, where\nthe two forms cannot differ, so a reader never has to work out which value was\nproduced. Other people's code often puts them inside bigger expressions, so\nwhen you read one there, look at which side of the name the `++` is on."
            },
            {
              "p": "Two worked examples, keeping a table of each name's value as in *Naming\nvalues*:"
            },
            {
              "code": "let total = 0;       // total: 0\ntotal += 12;         // total: 12\ntotal += 8;          // total: 20\ntotal *= 2;          // total: 40",
              "lang": "js",
              "file": "cart.js"
            },
            {
              "code": "let visitors = 5;    // visitors: 5\nvisitors++;          // visitors: 6\nvisitors++;          // visitors: 7\nvisitors--;          // visitors: 6",
              "lang": "js",
              "file": "visitors.js"
            },
            {
              "p": "Type the number each name holds at the end."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let t = 10;\nt += 5;\nt -= 3;\n",
                    "accept": [
                      "12"
                    ],
                    "why": "`12`. 10, then 15, then 12.\n"
                  },
                  {
                    "code": "let c = 0;\nc++;\nc++;\nc++;\n",
                    "accept": [
                      "3"
                    ],
                    "why": "`3`. Each `++` adds one.\n"
                  },
                  {
                    "code": "let p = 3;\np *= p;\n",
                    "accept": [
                      "9"
                    ],
                    "why": "`9`. `p = p * p`, evaluated with `p` at 3.\n"
                  },
                  {
                    "code": "let x = 3;\nconst y = x++;\ny\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**If you answered `4` — postfix `x++` evaluates to the value from before the change.** `x` becomes 4, but `y` was given the old value, 3.\n",
                    "why": "`3`. `x` is now 4; `y` got the value from before.\n"
                  },
                  {
                    "code": "let x = 3;\nconst y = ++x;\ny\n",
                    "accept": [
                      "4"
                    ],
                    "miss": "**If you answered `3` — prefix `++x` adds one first, then evaluates to the new value.**\n",
                    "why": "`4`. Prefix: add one, then produce the new value.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Numbers that surprise you, and Math",
          "body": [
            {
              "p": "Three results look like bugs the first time you meet them. They are not."
            },
            {
              "code": "console.log(0.1 + 0.2);   // 0.30000000000000004\nconsole.log(1 / 0);       // Infinity\nconsole.log(0 / 0);       // NaN",
              "lang": "js",
              "file": "surprises.js"
            },
            {
              "list": [
                "**`0.1 + 0.2` is not exactly `0.3`.** Numbers are stored in binary, and most decimal fractions cannot be written exactly in binary — the way ⅓ cannot be written exactly as a decimal. The error is tiny, and it is not JavaScript's alone: every language that stores decimals in binary, which is most of them, has it. It matters when you compare decimals for equality, and when you show a result to a person.",
                "**In JavaScript, dividing by zero does not stop the program.** It gives `Infinity`, which is a number. Some other languages stop with an error.",
                "**`NaN` means \"not a number\"**, and it is — confusingly — a number value. It is what you get from arithmetic that has no answer. It spreads: `NaN + 1` is `NaN`, so one bad value turns everything computed from it into `NaN`, with no error."
              ]
            },
            {
              "p": "The built-in `Math` gives you tools for tidying numbers. Each `Math.…` entry\nbelow is a function you call, the way you called `Math.max` in First programs:"
            },
            {
              "table": {
                "head": [
                  "Call",
                  "Produces"
                ],
                "rows": [
                  [
                    "`Math.round(2.5)`",
                    "`3` — nearest whole number"
                  ],
                  [
                    "`Math.floor(4.9)`",
                    "`4` — the whole number at or below"
                  ],
                  [
                    "`Math.max(3, 9, 4)`",
                    "`9` — the largest"
                  ],
                  [
                    "`Math.min(3, 9, 4)`",
                    "`3` — the smallest"
                  ],
                  [
                    "`Math.random()`",
                    "a decimal from `0` up to, but not including, `1`"
                  ],
                  [
                    "`(0.1 + 0.2).toFixed(2)`",
                    "`\"0.30\"` — **text**, rounded to 2 places, for showing"
                  ],
                  [
                    "`Number.isNaN(0 / 0)`",
                    "`true` — asks whether a value is `NaN`"
                  ]
                ]
              }
            },
            {
              "p": "`Number.isNaN` answers a yes-or-no question, so it produces `true` or `false`\nrather than a number. `true` and `false` are the subject of a later lesson in\nthis module, *Comparing and combining*."
            },
            {
              "p": "Two worked examples that combine them. Read nested calls from the inside out."
            },
            {
              "code": "// Math.random()     a decimal from 0 up to 1, e.g. 0.7302\n// * 6               stretched to 0 up to 6,   e.g. 4.3812\n// Math.floor(...)   fraction cut off, 0 to 5:  4\n// + 1               shifted to 1 to 6:         5\nconst roll = Math.floor(Math.random() * 6) + 1;",
              "lang": "js",
              "file": "dice.js"
            },
            {
              "code": "const raw = 130;\n// Math.max(raw, 0) raises anything below 0 to 0\n// Math.min(..., 100) lowers anything above 100 to 100\nconst volume = Math.min(Math.max(raw, 0), 100);   // 100",
              "lang": "js",
              "file": "volume.js"
            },
            {
              "p": "Keeping a value inside a range, as *volume.js* does, is called **clamping**.\nNote that `toFixed` produces text, not a number, so use it last, when you show\nthe result to a person. The\nlesson *Conversion* is about what happens when text and numbers mix."
            },
            {
              "p": "Type the value each evaluates to."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "Math.floor(7.8)",
                    "accept": [
                      "7"
                    ],
                    "miss": "**If you answered `8` — that is `Math.round`.** `Math.floor` always goes down to the whole number below.\n",
                    "why": "`7`.\n"
                  },
                  {
                    "code": "Math.max(12, 40, 7)",
                    "accept": [
                      "40"
                    ],
                    "why": "`40`, the largest.\n"
                  },
                  {
                    "code": "NaN + 5",
                    "accept": [
                      "NaN"
                    ],
                    "why": "`NaN`. Arithmetic with `NaN` gives `NaN`, silently.\n"
                  },
                  {
                    "code": "Math.min(Math.max(-20, 0), 100)",
                    "accept": [
                      "0"
                    ],
                    "why": "`0`. Inside first: `Math.max(-20, 0)` is `0`, and `Math.min(0, 100)` is `0`. The value was below the range, so it was raised to the bottom.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "Eight slices of pizza are shared equally among `people`, and nobody gets part\nof a slice. Two programmers each write an expression for the number of slices\neach person gets:"
            },
            {
              "code": "const eachA = Math.round(8 / people);\nconst eachB = Math.floor(8 / people);",
              "lang": "js",
              "file": "pizza.js"
            },
            {
              "p": "Choose the expression that never hands out more slices than there are, and give a\nvalue of `people` for which the two disagree. Then write the expression for how\nmany slices are **left over**, and say why `Math.floor` and `%` belong together\nhere."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Evaluate an expression one operator at a time**: parentheses, `**`, then `* / %`, then `+ -`. Add parentheses whenever you mean another order.",
                "**`%` is the remainder** (often called *modulus* or *mod*; it differs from maths' *mod* only for negative numbers). `+=`, `-=`, `++` and `--` update a `let` in place; postfix `x++` evaluates to the old value, prefix `++x` the new.",
                "**Decimals are approximate and `NaN` spreads silently.** `Math.round`, `floor`, `max` and `min` tidy numbers; `Number.isNaN` checks for the broken one."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Expressions and operators",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Expressions_and_operators",
              "note": "every operator, grouped by kind."
            },
            {
              "title": "Operator precedence",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Operator_precedence",
              "note": "the full order, when you need it."
            },
            {
              "title": "Math",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math",
              "note": "every rounding and random function."
            }
          ]
        }
      ],
      "_source": "lessons/operators.md",
      "practice": {
        "id": "operators",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "What does it produce?",
            "prompt": "Type the value each line produces.\n",
            "items": [
              {
                "id": "p1",
                "code": "10 - 2 * 3",
                "accept": [
                  "4"
                ],
                "miss": "**If you answered `24` — you went left to right.** `*` goes before `-`.\n",
                "why": "`4`. `2 * 3` first, then `10 - 6`.\n"
              },
              {
                "id": "p2",
                "code": "17 % 5",
                "accept": [
                  "2"
                ],
                "miss": "**If you answered `3` or `3.4` — `%` is what is left over, not how many times it fits.** 5 fits three times, using 15.\n",
                "why": "`2`. `17 - 15`.\n"
              },
              {
                "id": "p3",
                "code": "Math.floor(7.8)",
                "accept": [
                  "7"
                ],
                "miss": "**If you answered `8` — that is `Math.round`.** `Math.floor` always goes down to the whole number below.\n",
                "why": "`7`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "(10 - 2) * 3",
                "accept": [
                  "24"
                ],
                "why": "`24`. The parentheses go first.\n"
              },
              {
                "id": "p5",
                "code": "NaN * 0",
                "accept": [
                  "NaN"
                ],
                "miss": "**If you answered `0` — `NaN` spreads.** Any arithmetic with `NaN` gives `NaN`, even multiplying by zero.\n",
                "why": "`NaN`.\n"
              },
              {
                "id": "p6",
                "code": "0.1 + 0.2",
                "accept": [
                  "0.30000000000000004"
                ],
                "miss": "**If you answered `0.3` — decimals are stored approximately.** The result is a tiny bit more than 0.3.\n",
                "why": "`0.30000000000000004`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 24 for 10 - 2 * 3",
                "say": "Find the operator that goes first. `*` and `/` come before `+` and `-`, whatever their position.\n"
              },
              {
                "when": "you answered 3 for 17 % 5",
                "say": "3 is how many times 5 fits into 17. `%` asks what is left over after that.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict six lines, then run it",
            "prompt": "Predict each line it prints — type the value — then run it and compare.\n",
            "filename": "numbers.js",
            "code": "console.log(7 / 2);\nconsole.log(7 % 2);\nconsole.log(2 ** 10);\nconsole.log(1 / 0);\nconsole.log(0 / 0);\nconsole.log(Math.round(7 / 2));\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(7 / 2);",
                "accept": [
                  "3.5"
                ],
                "miss": "**If you answered `3` — division keeps the fraction.** JavaScript has one ordinary number type, and it holds decimals.\n",
                "why": "`3.5`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(7 % 2);",
                "accept": [
                  "1"
                ],
                "why": "`1`. 7 is odd.\n"
              },
              {
                "id": "r3",
                "code": "console.log(2 ** 10);",
                "accept": [
                  "1024"
                ],
                "why": "`1024`. Ten 2s multiplied together.\n"
              },
              {
                "id": "r4",
                "code": "console.log(1 / 0);",
                "accept": [
                  "Infinity"
                ],
                "miss": "**If you expected an error — dividing by zero does not stop a JavaScript program.** It gives a number, `Infinity`.\n",
                "why": "`Infinity`.\n"
              },
              {
                "id": "r5",
                "code": "console.log(0 / 0);",
                "accept": [
                  "NaN"
                ],
                "why": "`NaN`. Arithmetic with no answer.\n"
              },
              {
                "id": "r6",
                "code": "console.log(Math.round(7 / 2));",
                "accept": [
                  "4"
                ],
                "why": "`4`. Inside first: `3.5`, and `Math.round` takes a half up.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected 1 / 0 to be an error",
                "say": "Some languages stop the program there. JavaScript produces a number instead. Which number is bigger than any other?\n"
              },
              {
                "when": "you answered 3 for Math.round(7 / 2)",
                "say": "That is `Math.floor`. `Math.round` goes to the nearest whole number, and a half goes up.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One operator at a time",
            "prompt": "The first three questions work out `20 - 6 / 2 * 3` one operator at a time: type the expression after each step, then the final value. The rest give a short program or expression: type the value it ends with.\n",
            "items": [
              {
                "id": "t1",
                "question": "In `20 - 6 / 2 * 3`, the first operator worked out is `/`. Type the expression after that step.",
                "accept": [
                  "20 - 3 * 3",
                  "20-3*3"
                ],
                "why": "`20 - 3 * 3`. `/` and `*` are the same level, so the leftmost goes first.\n"
              },
              {
                "id": "t2",
                "question": "Type the expression after the next step.",
                "accept": [
                  "20 - 9",
                  "20-9"
                ],
                "miss": "**If you typed `17 * 3` — `*` goes before `-`.** Only `3 * 3` is ready to be worked out.\n",
                "why": "`20 - 9`.\n"
              },
              {
                "id": "t3",
                "question": "Type the final value.",
                "accept": [
                  "11"
                ],
                "why": "`11`.\n"
              },
              {
                "id": "t4",
                "code": "let lives = 3;\nlives -= 1;\nlives *= 4;\nlives++;\n",
                "accept": [
                  "9"
                ],
                "miss": "**If you answered `8` — the last line adds one more.** `lives++` is `lives = lives + 1`.\n",
                "why": "`9`. 3, then 2, then 8, then 9.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "(2 + 4) * 3 ** 2",
                "accept": [
                  "54"
                ],
                "why": "`54`. Parentheses: `6 * 3 ** 2`. Then `**`: `6 * 9`.\n"
              },
              {
                "id": "t6",
                "code": "let n = 10;\nn %= 4;\n",
                "accept": [
                  "2"
                ],
                "why": "`2`. `n = n % 4`, and 4 fits twice into 10, using 8.\n"
              }
            ],
            "hints": [
              {
                "when": "you worked 20 - 6 first",
                "say": "`-` is the last level. Look for `*`, `/` or `%` first, and take the leftmost of those.\n"
              },
              {
                "when": "your lives answer was 12 or 13",
                "say": "`lives -= 1` changes `lives` before the next line runs. Keep a table and update it after every line.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Keep a value inside a range",
            "prompt": "A volume setting must stay from 0 to 100, whatever number arrives. Put the lines in order so `volume` is `raw` clamped to that range. One line is a mistake — leave it out.\n",
            "lines": [
              "const raw = 140;",
              "const notTooLow = Math.max(raw, 0);",
              "const volume = Math.min(notTooLow, 100);",
              "console.log(volume);"
            ],
            "distractors": [
              {
                "line": "const volume = Math.max(notTooLow, 100);",
                "why": "`Math.max` picks the larger, so every volume comes out at least 100. To cap a value at the top, you take the *smaller* of it and the cap.\n"
              }
            ],
            "why": "Raise it to the bottom of the range with `Math.max(raw, 0)`, then lower it to the top with `Math.min(…, 100)`. It reads backwards at first: `max` sets the *minimum* allowed, and `min` sets the *maximum*.\n",
            "hints": [
              {
                "when": "you used the Math.max line for volume",
                "say": "Try it with `raw` at 50. `Math.max(50, 100)` is 100 — a quiet setting turned all the way up. Which function gives the smaller of two numbers?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "What is left over",
            "prompt": "`minutesLeftOver` takes a number of minutes and should return the minutes left after taking out whole hours: 135 minutes is 2 hours and **15** minutes, so it returns `15`. Write one line between the braces: `return`, then the expression that works out the minutes left. `return` sends a value out of the function so the tests can check it; the Functions module explains the rest. Then, in one sentence: why does `/` not give you this answer?\n",
            "exportName": "minutesLeftOver",
            "filename": "minutes.js",
            "starter": "function minutesLeftOver(totalMinutes) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "minutesLeftOver(135) → 15",
                "body": "return subject(135) === 15;"
              },
              {
                "label": "minutesLeftOver(60) → 0",
                "body": "return subject(60) === 0;"
              },
              {
                "label": "minutesLeftOver(59) → 59",
                "body": "return subject(59) === 59;"
              },
              {
                "label": "minutesLeftOver(200) → 20",
                "body": "return subject(200) === 20;"
              }
            ],
            "hints": [
              {
                "when": "135 gives 2.25",
                "say": "`/` tells you how many hours, with the fraction. You want what is left over after the whole hours. Which operator gives a remainder?\n"
              },
              {
                "when": "135 gives 75",
                "say": "Taking away 60 once only removes one hour. 200 minutes holds three. One operator takes out every whole hour at once.\n"
              },
              {
                "when": "every test says undefined",
                "say": "Put `return` in front of the expression, so its value is sent out.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Round the average",
            "prompt": "`roundedAverage` works out the average of three scores, but returns things like `6.666666666666667`. Change it to return the average rounded to the nearest whole number: for 5, 7 and 8 that is `7`. Change the expression on the `return` line. `return` sends a value out of the function so the tests can check it; the Functions module explains the rest. Then, in one sentence: where did your parentheses have to go, and why?\n",
            "exportName": "roundedAverage",
            "filename": "average.js",
            "starter": "function roundedAverage(a, b, c) {\n  // TODO: put your code here\n  return (a + b + c) / 3;\n}\n",
            "tests": [
              {
                "label": "roundedAverage(5, 7, 8) → 7",
                "body": "return subject(5, 7, 8) === 7;"
              },
              {
                "label": "roundedAverage(1, 2, 3) → 2",
                "body": "return subject(1, 2, 3) === 2;"
              },
              {
                "label": "roundedAverage(1, 1, 2) → 1",
                "body": "return subject(1, 1, 2) === 1;"
              },
              {
                "label": "roundedAverage(90, 85, 81) → 85",
                "body": "return subject(90, 85, 81) === 85;"
              }
            ],
            "hints": [
              {
                "when": "5, 7, 8 gives 6",
                "say": "6.67 is nearer 7 than 6. `Math.floor` always goes down; which function goes to the nearest?\n"
              },
              {
                "when": "5, 7, 8 gives 6.666666666666667",
                "say": "You rounded before dividing, so the division brought the fraction back. Round the whole average: put all of `(a + b + c) / 3` inside `Math.round( … )`.\n"
              },
              {
                "when": "5, 7, 8 gives 15 or more",
                "say": "Without parentheses around `a + b + c`, only `c` is divided. Work the expression out one operator at a time.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "A score as a whole percentage",
            "prompt": "`percentage` takes a score and the most it could have been, and should return the score as a percentage rounded to the nearest whole number: 45 out of 50 is `90`, and 2 out of 3 is `67`. Write one line between the braces: `return`, then the expression that works out the percentage. `return` sends a value out of the function so the tests can check it; the Functions module explains the rest. Then, in one sentence: does it matter whether you divide first or multiply by 100 first?\n",
            "exportName": "percentage",
            "filename": "percent.js",
            "starter": "function percentage(score, outOf) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "percentage(45, 50) → 90",
                "body": "return subject(45, 50) === 90;"
              },
              {
                "label": "percentage(2, 3) → 67",
                "body": "return subject(2, 3) === 67;"
              },
              {
                "label": "percentage(1, 3) → 33",
                "body": "return subject(1, 3) === 33;"
              },
              {
                "label": "percentage(0, 20) → 0",
                "body": "return subject(0, 20) === 0;"
              }
            ],
            "hints": [
              {
                "when": "2 out of 3 gives 66",
                "say": "66.67 is nearer 67. `Math.floor` always goes down; which function goes to the nearest?\n"
              },
              {
                "when": "45 out of 50 gives 100",
                "say": "You rounded before multiplying by 100, so `45 / 50` became `1` and the fraction was lost. Round last: the whole percentage goes inside the rounding.\n"
              },
              {
                "when": "45 out of 50 gives 0",
                "say": "`outOf * 100` was worked out first, because of your parentheses. Divide the score by `outOf`, then multiply.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a grade calculator",
            "prompt": "A code assistant wrote this to average two test scores (each out of 50) and report the average as a percentage, rounded to the nearest whole number. Read it, then answer each question.\n",
            "filename": "grade.js",
            "code": "const first = 40;\nconst second = 45;\nconst average = first + second / 2;\nconst percent = average / 50 * 100;\nconst shown = Math.floor(percent);\nconsole.log(shown);\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line computes the wrong average? Type the line number.",
                "accept": [
                  "3",
                  "line 3",
                  "l3"
                ],
                "why": "Line 3. Only `second` is divided.\n"
              },
              {
                "id": "c2",
                "question": "Type the number `average` holds.",
                "accept": [
                  "62.5"
                ],
                "miss": "**If you answered `42.5` — that is what it should hold.** Work it out one operator at a time: `/` goes first.\n",
                "why": "`62.5`. `45 / 2` is `22.5`, then `40 + 22.5`.\n"
              },
              {
                "id": "c3",
                "question": "Which rewrite of line 3 is right?",
                "choices": [
                  "(first + second) / 2",
                  "first + (second / 2)",
                  "first + second / (2)"
                ],
                "accept": [
                  "(first + second) / 2"
                ],
                "why": "`(first + second) / 2`. The other two put parentheses around `second / 2`, or around `2` alone, and both leave the order line 3 already had: only `second` is divided.\n"
              },
              {
                "id": "c4",
                "question": "Line 5 has a second bug. If `percent` were `84.9`, type the number `shown` would hold.",
                "accept": [
                  "84"
                ],
                "miss": "**If you answered `85` — that is what the report should say.** `Math.floor` always goes down, so 84.9 becomes 84. Nearest needs `Math.round`.\n",
                "why": "`84`. The spec says nearest whole number; line 5 needs `Math.round(percent)`.\n"
              }
            ],
            "hints": [
              {
                "when": "you picked first + (second / 2)",
                "say": "That is the order the line already runs in. Which parentheses would make the addition happen before the division?\n"
              },
              {
                "when": "you thought line 5 was fine",
                "say": "Try a percent just under a whole number, like 84.9, and ask which way `Math.floor` goes.\n"
              }
            ]
          }
        ]
      }
    },
    "function-answers": {
      "id": "function-answers",
      "title": "Writing a function's answer",
      "kind": "read",
      "outcome": 1,
      "shape": "procedural",
      "why": "Every exercise from *Your first program* on, and every homework in this module, hands the student a function and asks them to write the `return` (D-044) — but every example before this lesson goes code → value, and nothing modelled the other direction: a sentence into an expression. Students stalled on `primitives-game` in both ways this lesson names: not knowing what to type, and answering with the function's own name (`return not(input)`), which never finishes. It is the first pass of `fn.define-call` (D-045): what a parameter holds, what `return` sends out, and how to write the answer from a description. Writing a whole function stays in the Functions module. It sits after *Arithmetic operators* so its examples have operators to use, and before *Comparing and combining*, which recalls it. Nick asked for it as its own lesson, so a student looking for \"how do I write the answer\" can find it (2026-10-08). Its content is derived from 01-explanations rules 1, 3 and 5, pending Nick's review. Procedural (01-explanations rule 8): the four subgoals are a procedure.",
      "concepts": [
        "fn.define-call"
      ],
      "lede": "Exercises and homework hand you a function with an empty body and a sentence saying what it should return. This lesson is about getting from that sentence to the line you write: where the function's values come from, four steps that turn a description into code, and one kind of answer that looks right but never gets an answer.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Your first program · Arithmetic operators · Comparing and combining"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Parameter** — a name in a function's parentheses, like `temp` in `isFreezing(temp)`, that holds the value each call hands in. **Return** — send a value out of the function, to whatever called it. **Call stack** — the list of calls that are still waiting for an answer.",
          "entries": [
            "**Parameter** — a name in a function's parentheses, like `temp` in `isFreezing(temp)`, that holds the value each call hands in.",
            "**Return** — send a value out of the function, to whatever called it.",
            "**Call stack** — the list of calls that are still waiting for an answer."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on what this lesson builds on, from memory: how a call hands\nvalues to a function and what comes back, from *Your first program*, and the\norder operators are worked out in, from *Arithmetic operators*. Type the value,\nwith quotes if it is text. Press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "10 - 2 * 3",
                    "accept": [
                      "4"
                    ],
                    "miss": "**If you answered `24` — `*` goes before `-`.** `2 * 3` is worked out first, then `10 - 6`.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "question": "A function holds `return word.toUpperCase();`. A test calls `shout(\"go\")`. Type the value the call returns.",
                    "accept": [
                      "\"GO\"",
                      "GO",
                      "'GO'"
                    ],
                    "miss": "**In the call `shout(\"go\")`, `word` holds `\"go\"`**, as in *Your first program*'s exercises. `\"go\".toUpperCase()` is `\"GO\"`, and `return` sends it back.\n",
                    "why": "`\"GO\"`.\n"
                  },
                  {
                    "question": "What does the call `Math.max(4, 11, 7)` return? Type the number.",
                    "accept": [
                      "11"
                    ],
                    "miss": "**`Math.max` returns the largest of the values in its parentheses**, its arguments, from *Your first program*.\n",
                    "why": "`11`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what value a parameter holds when a test calls the function",
                "I can turn a sentence describing a result into an expression and return it",
                "I can use a failed test's input to find what is wrong with my answer",
                "I can say why a function that answers by calling itself never gets an answer"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Where the values come from",
          "body": [
            {
              "p": "Every example in the lessons so far has gone one way: here is some code, what\ndoes it produce? Exercises and homework go the other way: here is a sentence\ndescribing what you want, now write the code that says it. Writing code from a\nsentence is a separate skill from reading code, and it is normal to find it\nharder. This lesson shows how to do it."
            },
            {
              "p": "An exercise gives you a function like this one:"
            },
            {
              "code": "function average(a, b) {\n  // TODO: put your code here\n}",
              "lang": "js",
              "file": "average.js"
            },
            {
              "p": "`a` and `b` are names, like the names you make with `const`. The difference is\nwhere their values come from: you do not write them; each call hands them in.\nWhen a test calls `average(4, 6)`, `a` holds `4` and `b`\nholds `6` for that call, just as if the first lines inside the braces were:"
            },
            {
              "code": "const a = 4;\nconst b = 6;",
              "lang": "js",
              "file": "average.js"
            },
            {
              "p": "The next test calls `average(10, 20)`, and this time `a` holds `10` and `b`\nholds `20`. The values are matched up in order: the first value in the call\ngoes to the first name, the second to the second. A name like this, between a\nfunction's parentheses, is a **parameter**."
            },
            {
              "p": "So the values never appear in your code. You write the answer once, using the\nparameter names, and it works for whichever values a call hands in. Whatever\nyou put after `return` is what the call returns to the test."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A test calls `average(10, 20)`. What value does `b` hold during that call? Type it.",
                    "accept": [
                      "20"
                    ],
                    "why": "`20`. It is the second value in the call, and `b` is the second name.\n"
                  },
                  {
                    "question": "Another test calls `average(7, 3)`. What value does `a` hold? Type it.",
                    "accept": [
                      "7"
                    ],
                    "why": "`7`. Each call hands in its own values.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "From a sentence to code",
          "body": [
            {
              "p": "This course writes a function's answer in four moves. They work for every\nexercise in this module:"
            },
            {
              "list": [
                "**Name the values you have.** They are the parameters, the names in the parentheses. Say what kind of value each holds: a number, a string, a boolean.",
                "**Find the question in the sentence.** Words like *half*, *each*, *left over*, *both* and *not* each name an operation, and in code each one becomes an operator.",
                "**Write it with operators**, using the names.",
                "**Send the answer out with `return`.**"
              ],
              "ordered": true
            },
            {
              "p": "**Example 1.** \"`average` takes two numbers and returns the number halfway\nbetween them.\""
            },
            {
              "code": "function average(a, b) {\n  // name the values you have: a and b, both numbers\n  // find the question in the sentence: \"halfway between\",\n  //   so add them, then halve the total\n  // write it with operators: (a + b) / 2\n  // send the answer out\n  return (a + b) / 2;\n}",
              "lang": "js",
              "file": "average.js"
            },
            {
              "p": "That is the finished version. Here is a common first try, which leaves out the\nparentheses:"
            },
            {
              "code": "function average(a, b) {\n  return a + b / 2;\n}",
              "lang": "js",
              "file": "average.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✗ average(4, 6) → 5\n✓ average(0, 0) → 0\n✗ average(10, 20) → 15",
              "lang": "text"
            },
            {
              "p": "The first test that failed is `average(4, 6)`. Work the line through by hand,\nwith `a` holding `4` and `b` holding `6`, one operator at a time, as in\n*Arithmetic operators*. `/` goes before `+`, so `b / 2` comes first: `6 / 2` is\n`3`. Then `a + 3` is `4 + 3`, which is `7`, not `5`. Only `b` was halved.\nParentheses around `a + b` make the addition happen first. Working a failed\ntest's input through your code by hand, like this, is usually the quickest way\nto find what is wrong."
            },
            {
              "p": "Notice too that this wrong version passed `average(0, 0)`. A test that passes\ndoes not prove the code is right; it only shows that the code gives the right\nanswer for that one input."
            },
            {
              "p": "**Example 2.** \"`total` takes a price and a quantity and returns the price\ntimes the quantity.\""
            },
            {
              "p": "A common first try writes the operation the way the sentence says it:"
            },
            {
              "code": "function total(price, quantity) {\n  return price times quantity;\n}",
              "lang": "js",
              "file": "total.js"
            },
            {
              "code": "  return price times quantity;\n               ^^^^^\nSyntaxError: Unexpected identifier 'times'",
              "lang": "text"
            },
            {
              "p": "JavaScript does not understand English words as operations. *Times* is the\nidea; `*` is the operator that writes it. The same goes for every word in a\nsentence that names an operation: *plus* is `+`, *left over* is `%`, and in the\nnext lesson, *Comparing and combining*, *and*, *or* and *not* are `&&`, `||`\nand `!`."
            },
            {
              "code": "function total(price, quantity) {\n  // name the values you have: price and quantity, both numbers\n  // find the question in the sentence: \"the price times the quantity\",\n  //   and times is written *\n  // write it with operators: price * quantity\n  // send the answer out\n  return price * quantity;\n}",
              "lang": "js",
              "file": "total.js"
            },
            {
              "p": "**Example 3: you choose the line.** \"`perPerson` takes the cost of a bill and a\nnumber of people, and returns what each person pays.\" The comments do the\nfirst two moves for you."
            },
            {
              "code": "function perPerson(bill, people) {\n  // name the values you have: bill and people, both numbers\n  // find the question in the sentence: \"what each person pays\",\n  //   the bill split evenly, so bill divided by people\n  // TODO: write it with operators, and send the answer out\n}",
              "lang": "js",
              "file": "share.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes where the TODO is?",
                    "choices": [
                      "return bill divided by people;",
                      "return perPerson(bill, people);",
                      "return bill / people;"
                    ],
                    "accept": [
                      "return bill / people;"
                    ],
                    "miss": "**If you chose `divided by` — that is English, and JavaScript stops with a `SyntaxError`, as `times` did in Example 2.** Division is written `/`. **If you chose `perPerson(bill, people)` — that line calls the function you are writing.** It looks like an answer, but it never gets one; the next step shows why.\n",
                    "why": "`return bill / people;`. For a bill of 60 and 4 people it returns `15`.\n"
                  },
                  {
                    "question": "\"`minutesIn` takes a number of hours and returns how many minutes that is.\" Which line?",
                    "choices": [
                      "return hours / 60;",
                      "return hours * 60;",
                      "return minutesIn(hours) * 60;"
                    ],
                    "accept": [
                      "return hours * 60;"
                    ],
                    "miss": "**If you chose `/` — try it with 2 hours.** `2 / 60` is a tiny fraction, but 2 hours is 120 minutes. Each hour holds 60 minutes, so multiply. **If you chose `minutesIn(hours) * 60` — it calls the function you are writing**, and the next step shows why that never gets an answer.\n",
                    "why": "`return hours * 60;`. For 2 hours it returns `120`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "A function cannot answer with its own name",
          "body": [
            {
              "p": "Look again at one of the choices in Example 3, `return perPerson(bill, people);`:"
            },
            {
              "code": "function perPerson(bill, people) {\n  return perPerson(bill, people);\n}",
              "lang": "js",
              "file": "share.js"
            },
            {
              "p": "The line reads like the sentence, but it never gets an answer. Follow a call through.\nA test calls `perPerson(60, 4)`. To work out its answer, the function calls\n`perPerson(60, 4)`: the same function, with the same values. That second call\ndoes the same thing and starts a third, and the third starts a fourth. Each call\nwaits for the call it started, and none of them ever gets as far as an answer."
            },
            {
              "p": "Node keeps a list of the calls still waiting for an answer, called the **call\nstack**. After several thousand calls are waiting, the call stack runs out of\nroom and Node stops the program:"
            },
            {
              "code": "RangeError: Maximum call stack size exceeded",
              "lang": "text"
            },
            {
              "p": "That is the message in Node and in Chrome. The practice exercises run in your\nbrowser, so Firefox shows the same mistake as `InternalError: too much\nrecursion`. Different JavaScript engines word the same error differently; the\ncause is the same."
            },
            {
              "p": "In this module, when you see this error, look for a function that calls\nitself."
            },
            {
              "p": "A function that calls itself is not always a mistake. When each call works on\na smaller part of the problem, and the calls stop at a case simple enough to\nanswer outright, it is called **recursion**. Recursion is taught in the\nFunctions module, in *Recursion: a function that calls itself*. No function in\nthis module needs it."
            },
            {
              "p": "This catches people most when a function is **named after an operator**, as it\nsometimes is in homework: a function called `remainder` that should do what `%`\ndoes, or one called `not` that should do what `!` does. Writing\n`return remainder(a, b);` inside `remainder` asks the same question again,\nforever. The function's name says what it is *for*. In this module, its answer\nis built from its parameters and an operator."
            },
            {
              "p": "In *Your first program*, the answers were calls to *other* functions, such as\n`Math.max(a, b)`. That works when another function already does the job. In\nthis module, the job is done by an operator."
            },
            {
              "p": "Here is the same mistake in a function called `triple`:"
            },
            {
              "code": "function triple(n) {\n  return triple(n);\n}",
              "lang": "js",
              "file": "triple.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A test calls `triple(4)` on the function above. What happens?",
                    "choices": [
                      "It returns 12",
                      "It returns undefined",
                      "It stops with a RangeError"
                    ],
                    "accept": [
                      "It stops with a RangeError"
                    ],
                    "miss": "**The function calls itself with the same value, and that call does the same.** No call ever reaches an answer, so there is nothing to return.\n",
                    "why": "It stops with `RangeError: Maximum call stack size exceeded`.\n"
                  },
                  {
                    "question": "\"`triple` returns three times the number it takes.\" Which line?",
                    "choices": [
                      "return triple(n * 3);",
                      "return n * 3;",
                      "return triple(n) * 3;"
                    ],
                    "accept": [
                      "return n * 3;"
                    ],
                    "miss": "**Both other lines call `triple` from inside `triple`**, so neither ever finishes. The answer is built from the parameter and an operator.\n",
                    "why": "`return n * 3;`. `triple(4)` returns `12`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**A parameter holds whatever the call hands in**, matched in order. You write the answer once, using the names.",
                "**Four moves:** name the values you have, find the question in the sentence, write it with operators, `return` it. To find a bug, work a failed test's input through your code by hand.",
                "**In this module, never answer by calling the function you are writing.** The call asks the same question forever and stops with `Maximum call stack size exceeded`."
              ]
            }
          ]
        }
      ],
      "_source": "lessons/function-answers.md",
      "practice": {
        "id": "function-answers",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "What does the call return?",
            "prompt": "Each question shows a function and a call to it. Type the number the call returns. Where a question has buttons, press the one that says what happens.\n",
            "items": [
              {
                "id": "p1",
                "code": "function half(n) {\n  return n / 2;\n}\nhalf(9)\n",
                "accept": [
                  "4.5"
                ],
                "miss": "**`n` holds `9`, the value in the call.** Then `9 / 2`, and division keeps the fraction.\n",
                "why": "`4.5`.\n"
              },
              {
                "id": "p2",
                "code": "function area(width, height) {\n  return width * height;\n}\narea(3, 5)\n",
                "accept": [
                  "15"
                ],
                "why": "`15`. `width` holds `3`, `height` holds `5`.\n"
              },
              {
                "id": "p3",
                "code": "function double(n) {\n  return double(n);\n}\ndouble(4)\n",
                "choices": [
                  "8",
                  "undefined",
                  "It stops with a RangeError"
                ],
                "accept": [
                  "It stops with a RangeError"
                ],
                "miss": "**`double` calls `double` with the same value, and so does every call after it.** No call ever reaches an answer.\n",
                "why": "It stops with `RangeError: Maximum call stack size exceeded`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "function minus(a, b) {\n  return a - b;\n}\nminus(2, 10)\n",
                "accept": [
                  "-8"
                ],
                "miss": "**If you answered `8` — the values are matched in order.** `a` holds `2` and `b` holds `10`, so it is `2 - 10`.\n",
                "why": "`-8`.\n"
              },
              {
                "id": "p5",
                "code": "function leftOver(total, size) {\n  return total % size;\n}\nleftOver(20, 6)\n",
                "accept": [
                  "2"
                ],
                "why": "`2`. 6 fits into 20 three times, using 18.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 8 for double(4)",
                "say": "Read the line after `return` exactly. It does not multiply anything; it calls `double` again.\n"
              },
              {
                "when": "you answered with the wrong one of a and b",
                "say": "The first value in the call goes to the first name in the parentheses, the second to the second.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict four calls, then run it",
            "prompt": "Predict each line it prints — type the number — then run it and compare.\n",
            "filename": "calls.js",
            "code": "function perPerson(bill, people) {\n  return bill / people;\n}\nfunction average(a, b) {\n  return a + b / 2;\n}\nconsole.log(perPerson(60, 4));\nconsole.log(perPerson(4, 60) * 60);\nconsole.log(average(4, 6));\nconsole.log(average(0, 0));\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(perPerson(60, 4));",
                "accept": [
                  "15"
                ],
                "why": "`15`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(perPerson(4, 60) * 60);",
                "accept": [
                  "4"
                ],
                "miss": "**The order of the values decides which name holds which.** Here `bill` holds `4` and `people` holds `60`, so the call returns `4 / 60`, a small fraction. Multiplying that fraction by 60 gives `4` again.\n",
                "why": "`4`.\n"
              },
              {
                "id": "r3",
                "code": "console.log(average(4, 6));",
                "accept": [
                  "7"
                ],
                "miss": "**If you predicted `5` — that is what an average of 4 and 6 should be, but this `average` has a bug.** With no parentheses, `/` goes first: `6 / 2` is `3`, then `4 + 3`.\n",
                "why": "`7`. This `average` has a bug.\n"
              },
              {
                "id": "r4",
                "code": "console.log(average(0, 0));",
                "accept": [
                  "0"
                ],
                "why": "`0`, which is correct, by luck. A test that passes shows only that the code gives the right answer for that one input.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 5 for average(4, 6)",
                "say": "Work the line after `return` one operator at a time with `a` as 4 and `b` as 6. Which operator goes first?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One call, one name at a time",
            "prompt": "`function secondsIn(minutes, seconds)` holds one line: `return minutes * 60 + seconds;`. A test calls `secondsIn(2, 30)`. Type each value.\n",
            "items": [
              {
                "id": "t1",
                "question": "In the call `secondsIn(2, 30)`, what does `minutes` hold? Type the number.",
                "accept": [
                  "2"
                ],
                "why": "`2`, the first value in the call.\n"
              },
              {
                "id": "t2",
                "question": "What does `minutes * 60` produce in that call? Type the number.",
                "accept": [
                  "120"
                ],
                "why": "`120`.\n"
              },
              {
                "id": "t3",
                "question": "What does the call return? Type the number.",
                "accept": [
                  "150"
                ],
                "miss": "**`*` goes first, then `+` adds `seconds`, which holds `30`.** `120 + 30`.\n",
                "why": "`150`. Two and a half minutes is 150 seconds.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "Now a test calls `secondsIn(0, 45)`. What does it return? Type the number.",
                "accept": [
                  "45"
                ],
                "why": "`45`. `0 * 60` is `0`, plus `45`.\n"
              },
              {
                "id": "t5",
                "question": "A test calls `secondsIn(1, 0)`. What does it return? Type the number.",
                "accept": [
                  "60"
                ],
                "why": "`60`.\n"
              }
            ],
            "hints": [
              {
                "when": "you gave 2 for the call's answer",
                "say": "`minutes` holds 2, but the call returns what comes after `return`, worked out with those values.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Put the function together",
            "prompt": "\"`totalCost` takes a price and a quantity, and returns the price times the quantity, plus 5 for delivery.\" Put the lines in order. One line is a mistake — leave it out.\n",
            "lines": [
              "function totalCost(price, quantity) {",
              "  const items = price * quantity;",
              "  return items + 5;",
              "}"
            ],
            "distractors": [
              {
                "line": "  return totalCost(price, quantity) + 5;",
                "why": "It calls `totalCost` from inside `totalCost`, with the same values, so it never gets an answer. The answer has to be built from the parameters and operators.\n"
              }
            ],
            "why": "Work out what the items cost and store it in `items`; then `return` that cost plus 5 for delivery.\n",
            "hints": [
              {
                "when": "you used the totalCost(price, quantity) line",
                "say": "Follow a call to `totalCost(10, 2)`. To return its answer, what does that line call first?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Finish the area of a frame",
            "prompt": "\"`frameArea` takes the width and height of a picture in cm, and returns the area of a frame 2 cm wider and 2 cm taller.\" The comments do the first two of the four moves, and the line for `frameWidth` is written for you. Write the rest of the answer, with `return` in front of it. Then, in one sentence: why do the parentheses matter in your line?\n",
            "exportName": "frameArea",
            "filename": "frame.js",
            "starter": "function frameArea(width, height) {\n  // name the values you have: width and height, both numbers\n  // find the question in the sentence: the frame is width + 2\n  //   wide and height + 2 tall, and its area is how wide times\n  //   how tall\n  const frameWidth = width + 2;\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "frameArea(10, 20) → 264",
                "body": "return subject(10, 20) === 264;"
              },
              {
                "label": "frameArea(1, 1) → 9",
                "body": "return subject(1, 1) === 9;"
              },
              {
                "label": "frameArea(0, 5) → 14",
                "body": "return subject(0, 5) === 14;"
              }
            ],
            "hints": [
              {
                "when": "frameArea(10, 20) gives 242",
                "say": "`frameWidth * height + 2` multiplies first, then adds 2. The height needs its 2 added before the multiplying: parentheses.\n"
              },
              {
                "when": "every test says undefined",
                "say": "Put `return` in front of the expression, so its value is sent out.\n"
              },
              {
                "when": "the error says Maximum call stack size exceeded",
                "say": "Your answer calls `frameArea` from inside `frameArea`. Build it from `frameWidth`, `height` and operators instead.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Whole hours only",
            "prompt": "`hoursIn` takes a number of minutes and should return how many **whole** hours it holds: 135 minutes holds 2. Right now it returns things like `2.25`. Change the line so it returns whole hours. Then, in one sentence: why does `Math.round` give the wrong answer for 170?\n",
            "exportName": "hoursIn",
            "filename": "hours.js",
            "starter": "function hoursIn(minutes) {\n  // TODO: put your code here\n  return minutes / 60;\n}\n",
            "tests": [
              {
                "label": "hoursIn(135) → 2",
                "body": "return subject(135) === 2;"
              },
              {
                "label": "hoursIn(59) → 0",
                "body": "return subject(59) === 0;"
              },
              {
                "label": "hoursIn(120) → 2",
                "body": "return subject(120) === 2;"
              },
              {
                "label": "hoursIn(170) → 2",
                "body": "return subject(170) === 2;"
              }
            ],
            "hints": [
              {
                "when": "hoursIn(170) gives 3",
                "say": "`Math.round` goes to the nearest whole number, and 170 minutes is 2.83 hours. A third hour has not finished. Which function always goes down?\n"
              },
              {
                "when": "hoursIn(59) gives 1",
                "say": "59 minutes is not a whole hour yet. Rounding to the nearest takes it up; you need the whole hours below.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Count the legs",
            "prompt": "\"`totalLegs` takes how many chickens and how many cows a farm has, and returns how many legs they have between them.\" Use the four moves, and put `return` in front of your answer. Then, in one sentence: which test would catch a version that forgot the cows?\n",
            "exportName": "totalLegs",
            "filename": "legs.js",
            "starter": "function totalLegs(chickens, cows) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "totalLegs(3, 2) → 14",
                "body": "return subject(3, 2) === 14;"
              },
              {
                "label": "totalLegs(0, 5) → 20",
                "body": "return subject(0, 5) === 20;"
              },
              {
                "label": "totalLegs(4, 0) → 8",
                "body": "return subject(4, 0) === 8;"
              },
              {
                "label": "totalLegs(0, 0) → 0",
                "body": "return subject(0, 0) === 0;"
              }
            ],
            "hints": [
              {
                "when": "totalLegs(3, 2) gives 5",
                "say": "You counted the animals. A chicken has 2 legs and a cow has 4: each kind needs multiplying first.\n"
              },
              {
                "when": "totalLegs(0, 5) gives 0 or 10",
                "say": "Check what multiplies `cows`. A cow has 4 legs.\n"
              },
              {
                "when": "the error says Maximum call stack size exceeded",
                "say": "Your answer calls `totalLegs` from inside `totalLegs`. Build it from `chickens`, `cows` and operators.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review two helpers",
            "prompt": "A code assistant wrote these two functions. `square` should return a number times itself, and `mean3` the average of three numbers. Read them, then answer each question.\n",
            "filename": "helpers.js",
            "code": "function square(n) {\n  return square(n) * n;\n}\n\nfunction mean3(a, b, c) {\n  return a + b + c / 3;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `square(4)`. What happens?",
                "choices": [
                  "It returns 16",
                  "It returns NaN",
                  "It stops with a RangeError"
                ],
                "accept": [
                  "It stops with a RangeError"
                ],
                "miss": "**Before it can multiply, line 2 calls `square(4)` again,** and that call does the same. The multiplying is never reached.\n",
                "why": "It stops with `RangeError: Maximum call stack size exceeded`. The line should be `return n * n;`.\n"
              },
              {
                "id": "c2",
                "question": "A test calls `mean3(3, 3, 3)`. Type the number it returns.",
                "accept": [
                  "7"
                ],
                "miss": "**If you answered `3` — that is what it should return.** `/` goes first: only `c` is divided, `3 / 3` is `1`, then `3 + 3 + 1`.\n",
                "why": "`7`.\n"
              },
              {
                "id": "c3",
                "question": "Which rewrite of line 6 is right?",
                "choices": [
                  "return (a + b + c) / 3;",
                  "return a + b + (c / 3);",
                  "return mean3(a, b, c) / 3;"
                ],
                "accept": [
                  "return (a + b + c) / 3;"
                ],
                "why": "`(a + b + c) / 3`. The second puts parentheses around `c / 3`, the order the buggy line already used, so it gives the same wrong answer. The third calls `mean3` from inside `mean3`, so it never gets an answer.\n"
              }
            ],
            "hints": [
              {
                "when": "you said square(4) returns 16",
                "say": "Read line 2 from left to right. What is the first thing it has to work out?\n"
              }
            ]
          }
        ]
      }
    },
    "comparisons": {
      "id": "comparisons",
      "title": "Comparing and combining",
      "kind": "read",
      "outcome": 1,
      "shape": "procedural",
      "why": "Every lesson after this one asks yes-or-no questions of values — `<` in every loop condition, `===` in every branch, `&&` in every guard — and until D-042 none taught them (zero-prerequisite audit: `ops.comparison`, `ops.logical`). The two are one lesson because a comparison produces the boolean that `&&`, `||` and `!` combine, and a combination is only ever met over comparisons (D-029); arithmetic is its own lesson, `operators`, just before. `ops.logical` is pass 1 here, booleans only; what `&&` and `||` return as operands is `flow.short-circuit`, in `conditions` (D-045). Procedural (01-explanations rule 8): each operator is notation, and working out a combined condition is a procedure applied one comparison at a time. Writing an answer from a sentence is its own lesson, *Writing a function's answer*, just before (Nick, 2026-10-08). This one recalls it, and step 3 carries it over to `&&`, `||` and `!` — where the homework's `not`, `and` and `or` invite the self-calling answer. Pending Nick's review.",
      "concepts": [
        "ops.comparison",
        "ops.logical"
      ],
      "lede": "A comparison asks a question about two values — is this less than that? are these the same? — and answers `true` or `false`. This lesson covers the comparison operators, for numbers and for text, and the three operators that combine the answers.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Writing a function's answer · Equality: == versus === · Truthiness"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Boolean** — a value that is either `true` or `false`. **Comparison** — an operator that asks a question about two values and produces a boolean: `<`, `===`. **Condition** — an expression that produces a boolean, used to decide something: `age >= 18`.",
          "entries": [
            "**Boolean** — a value that is either `true` or `false`.",
            "**Comparison** — an operator that asks a question about two values and produces a boolean: `<`, `===`.",
            "**Condition** — an expression that produces a boolean, used to decide something: `age >= 18`."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on the last two lessons, *Arithmetic operators* and *Writing a\nfunction's answer*, from memory. Press **I don't know** if\nyou have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A function holds `return bill / people;`. A test calls `perPerson(60, 4)`. Type the value the call returns.",
                    "accept": [
                      "15"
                    ],
                    "miss": "**`bill` holds the first value in the call, `60`, and `people` the second, `4`.** Then `60 / 4`.\n",
                    "why": "`15`.\n"
                  },
                  {
                    "question": "A function holds `return a + b / 2;`. A test calls `average(2, 8)`. Type the value the call returns.",
                    "accept": [
                      "6"
                    ],
                    "miss": "**If you answered `5` — that is what it should return.** Work it one operator at a time: `/` goes first, so only `b` is halved: `8 / 2` is `4`, then `2 + 4`.\n",
                    "why": "`6`. The missing parentheses are the bug.\n"
                  },
                  {
                    "question": "A function called `double` holds `return double(n);`. What happens when a test calls `double(5)`?",
                    "choices": [
                      "It returns 10",
                      "It returns undefined",
                      "It stops with a RangeError"
                    ],
                    "accept": [
                      "It stops with a RangeError"
                    ],
                    "miss": "**`double` calls `double` with the same value, and so does every call after it.** No call ever gets an answer: `Maximum call stack size exceeded`.\n",
                    "why": "It stops with a `RangeError`. The answer has to be built from `n` and an operator: `n * 2`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can predict the `true` or `false` a comparison of two numbers produces",
                "I can predict a comparison between two pieces of text, capitals included",
                "I can combine comparisons with `&&`, `||` and `!` and predict the result",
                "I can write a function's answer that combines comparisons from a sentence"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Comparing numbers",
          "body": [
            {
              "p": "A comparison asks a question and produces a **boolean**: a value that is either\n`true` or `false`. Those are two ordinary values, like `7` or `\"Ada\"`, and you\ncan store them in names."
            },
            {
              "table": {
                "head": [
                  "Operator",
                  "Name",
                  "Asks",
                  "Example",
                  "Produces"
                ],
                "rows": [
                  [
                    "`<`",
                    "less than",
                    "less than?",
                    "`3 < 5`",
                    "`true`"
                  ],
                  [
                    "`>`",
                    "greater than",
                    "greater than?",
                    "`3 > 5`",
                    "`false`"
                  ],
                  [
                    "`<=`",
                    "less than or equal",
                    "less than or equal?",
                    "`5 <= 5`",
                    "`true`"
                  ],
                  [
                    "`>=`",
                    "greater than or equal",
                    "greater than or equal?",
                    "`4 >= 5`",
                    "`false`"
                  ],
                  [
                    "`===`",
                    "strict equality",
                    "the same value?",
                    "`5 === 5`",
                    "`true`"
                  ],
                  [
                    "`!==`",
                    "strict inequality",
                    "a different value?",
                    "`5 !== 5`",
                    "`false`"
                  ]
                ]
              }
            },
            {
              "p": "Arithmetic operators go before comparison operators, so `price * 2 > 10`\nmultiplies first and then compares."
            },
            {
              "p": "**`=` and `===` are different operators.** `=` is the assignment operator: it\nstores a value in a name. `===` is the strict equality operator: it compares,\nchanges nothing, and produces `true` or `false`. You will also see `==`, the\nloose equality operator, with two equals signs. It sometimes converts values\nbefore comparing them,\nand those conversions surprise even experienced programmers; the lesson\n*Equality: == versus ===* shows when. This course writes `===`, because it never\nconverts: it compares exactly the two values you can see."
            },
            {
              "p": "Two worked examples."
            },
            {
              "code": "const age = 17;\n// no arithmetic, so straight to the comparison: 17 >= 18\nconst canVote = age >= 18;         // false",
              "lang": "js",
              "file": "vote.js"
            },
            {
              "code": "const n = 14;\n// arithmetic first: n % 2 is 0\n// then the comparison: 0 === 0\nconst isEven = n % 2 === 0;        // true",
              "lang": "js",
              "file": "even.js"
            },
            {
              "p": "Press `true` or `false`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "7 >= 7",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "why": "`true`. \"Greater than **or equal**\", and they are equal.\n"
                  },
                  {
                    "code": "7 > 7",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "why": "`false`. 7 is not greater than itself.\n"
                  },
                  {
                    "code": "3 * 4 !== 12",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**If you said `true` — `!==` asks \"are they different?\"** `3 * 4` is `12`, so they are the same, and the answer is no.\n",
                    "why": "`false`.\n"
                  },
                  {
                    "code": "let total = 10;\ntotal === 12\n",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**If you said `true` — you may have read `===` as assignment.** It only asks. `total` is still `10`.\n",
                    "why": "`false`. `===` compares and changes nothing.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Comparing text",
          "body": [
            {
              "p": "Comparisons work on text too. `===` asks whether two pieces of text are exactly\nthe same, **capitals included**: `\"cat\"` and `\"Cat\"` are different."
            },
            {
              "p": "`<` and `>` compare text in alphabetical order, one character at a time: look at\nthe first characters; if they are the same, move on to the next pair. For\nletters there is one catch. **In this comparison, every capital letter comes\nbefore every lowercase letter**, so `\"Zebra\"` comes before `\"apple\"`."
            },
            {
              "code": "// p and p are the same, so move to the next pair\n// e comes before l\n\"pear\" < \"plum\"      // true",
              "lang": "js",
              "file": "fruit.js"
            },
            {
              "code": "// Z and a differ, so this pair decides\n// Z is a capital, and capitals come first\n\"Zebra\" < \"apple\"    // true",
              "lang": "js",
              "file": "animals.js"
            },
            {
              "p": "Press `true` or `false`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "\"Walla\" === \"walla\"",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**If you said `true` — `===` compares text exactly.** A capital `W` and a lowercase `w` are different characters.\n",
                    "why": "`false`. Capitals matter.\n"
                  },
                  {
                    "code": "\"apple\" < \"banana\"",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "why": "`true`. `a` comes before `b`.\n"
                  },
                  {
                    "code": "\"banana\" < \"Cherry\"",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**If you said `true` — the capital `C` comes before every lowercase letter.** So `\"Cherry\"` sorts first, and `\"banana\"` is not less.\n",
                    "why": "`false`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Combining yes-or-no answers",
          "body": [
            {
              "p": "Real questions often have two parts: *old enough **and** has a ticket*; *a\nweekend **or** a holiday*. Three operators combine booleans:"
            },
            {
              "table": {
                "head": [
                  "Operator",
                  "Name",
                  "Produces `true` when"
                ],
                "rows": [
                  [
                    "`&&`",
                    "logical AND",
                    "**both** sides are `true`"
                  ],
                  [
                    "`||`",
                    "logical OR",
                    "**at least one** side is `true`"
                  ],
                  [
                    "`!`",
                    "logical NOT",
                    "the value is `false` — it flips it"
                  ]
                ]
              }
            },
            {
              "p": "Comparisons go before `&&` and `||`, so `age >= 13 && age <= 19` compares\nfirst and then combines the two answers. `&&` goes before `||`. When this course\nmixes `&&` and `||` in one condition, it adds parentheses, so a reader does not\nhave to remember which goes first. Leaving them out is legal and gives the same\nresult."
            },
            {
              "code": "const age = 16;\n// each comparison first: age >= 13 is true, age <= 19 is true\n// then && combines them: true && true\nconst isTeen = age >= 13 && age <= 19;   // true",
              "lang": "js",
              "file": "teen.js"
            },
            {
              "code": "const hasTicket = false;\nconst isStaff = true;\n// already booleans, so just combine: false || true\nconst mayEnter = hasTicket || isStaff;   // true\nconst turnedAway = !mayEnter;            // false",
              "lang": "js",
              "file": "entry.js"
            },
            {
              "p": "`!` is different from the other two: it takes **one** value, not two, and goes\nin front of it. Read `!mayEnter` as \"not may-enter\". `!mayEnter` is the\nopposite boolean: `true` when `mayEnter` is `false`, and `false` when it is\n`true`."
            },
            {
              "p": "In this lesson, both sides of `&&` and `||` are always `true` or `false`. Given\nother values, `&&` and `||` can produce something other than a boolean; the\nlesson *What &&, || and ?? return*, in the Branching module, covers that."
            },
            {
              "p": "Press the result."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const temp = 25;\ntemp > 20 && temp < 30\n",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "why": "`true`. Both comparisons are `true`.\n"
                  },
                  {
                    "code": "const raining = false;\nconst cold = true;\nraining || cold\n",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "why": "`true`. `||` needs only one side to be `true`.\n"
                  },
                  {
                    "code": "!(5 > 3)",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "why": "`false`. `5 > 3` is `true`, and `!` flips it.\n"
                  },
                  {
                    "code": "const score = 45;\nscore < 0 || score > 100\n",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**If you said `true` — neither side is true.** 45 is not below 0 and not above 100, so it is inside the range.\n",
                    "why": "`false`. This is how you ask \"is it *outside* the range?\"\n"
                  },
                  {
                    "code": "const isOpen = true;\n!isOpen\n",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "why": "`false`. `isOpen` holds `true`, and `!` flips it to `false`.\n"
                  }
                ]
              }
            },
            {
              "p": "**In a function.** Exercises often ask for a combined condition as a function's\nanswer, and the four moves from *Writing a function's answer* work the same way. \"`canBoard` takes\ntwo booleans, whether the passenger has a ticket and whether they have ID, and\nreturns `true` only when they have both.\""
            },
            {
              "code": "function canBoard(hasTicket, hasID) {\n  // name the values you have: hasTicket and hasID, both booleans\n  // find the question in the sentence: \"only when they have both\",\n  //   so both must be true\n  // write it with operators: hasTicket && hasID; they are already\n  //   booleans, so no comparison is needed\n  // send the answer out\n  return hasTicket && hasID;\n}",
              "lang": "js",
              "file": "board.js"
            },
            {
              "p": "The sentence says *and*, but writing `hasTicket and hasID` stops with\n`SyntaxError: Unexpected identifier 'and'`. In JavaScript, *and*, *or* and *not*\nare written `&&`, `||` and `!`. A homework function may be named after one of\nthem, such as `not`, `and` or `or`. In this module, its answer is still built\nfrom its parameters and the operator. Writing `return not(input);` inside `not`\nasks the same question forever and stops with `Maximum call stack size\nexceeded`."
            },
            {
              "p": "\"`isClosed` takes whether a shop is open, and returns the opposite.\""
            },
            {
              "code": "function isClosed(isOpen) {\n  // TODO\n}",
              "lang": "js",
              "file": "closed.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes where the TODO is?",
                    "choices": [
                      "return isClosed(isOpen);",
                      "return not isOpen;",
                      "return !isOpen;"
                    ],
                    "accept": [
                      "return !isOpen;"
                    ],
                    "miss": "**If you chose `not isOpen` — *not* is the English word, and JavaScript stops with a `SyntaxError`.** It is written `!`. **If you chose `isClosed(isOpen)` — it calls the function you are writing,** with the same value, forever.\n",
                    "why": "`return !isOpen;`. A shop that is open is not closed.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A game gives a bonus when a player's score is a multiple of 10 **and** they\nfinished in under 60 seconds. Two people write the check:"
            },
            {
              "code": "const bonusA = score % 10 === 0 && seconds < 60;\nconst bonusB = score % 10 === 0 || seconds < 60;",
              "lang": "js",
              "file": "bonus.js"
            },
            {
              "p": "Choose the one that matches the rule, and give a `score` and `seconds` for which\nthe two disagree. Then: work out `score % 10 === 0` for `score = 70` step by step\n— which operator goes first, and how do you know?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`=` assigns; `===` asks.** A comparison produces a boolean, `true` or `false`, and arithmetic is worked out before it.",
                "**Text compares exactly and alphabetically**, one character at a time — and every capital comes before every lowercase letter.",
                "**`&&` needs both, `||` needs one, `!` flips.** Work out each comparison first, then combine. In JavaScript, *and*, *or* and *not* are written `&&`, `||` and `!`. The English words stop with a `SyntaxError`, and calling the function you are writing never gets an answer."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Comparison operators",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Expressions_and_operators#comparison_operators",
              "note": "every comparison, with examples."
            },
            {
              "title": "Logical operators",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Expressions_and_operators#logical_operators",
              "note": "`&&`, `||` and `!`."
            }
          ]
        }
      ],
      "_source": "lessons/comparisons.md",
      "practice": {
        "id": "comparisons",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "True or false?",
            "prompt": "Press the value each produces.\n",
            "items": [
              {
                "id": "p1",
                "code": "const age = 16;\nage >= 13 && age <= 19\n",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. Both comparisons are `true`, and `&&` needs both.\n"
              },
              {
                "id": "p2",
                "code": "\"Walla\" === \"walla\"",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you said `true` — `===` compares text exactly.** A capital `W` and a lowercase `w` are different characters.\n",
                "why": "`false`.\n"
              },
              {
                "id": "p3",
                "code": "10 % 4 > 2",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you said `true` — `10 % 4` is `2`, and `2 > 2` is `false`.** Arithmetic goes first; then the comparison. `>` does not include the number itself.\n",
                "why": "`false`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "\"Zebra\" < \"apple\"",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you said `false` — every capital letter comes before every lowercase one.** `Z` is a capital, so `\"Zebra\"` sorts first.\n",
                "why": "`true`.\n"
              },
              {
                "id": "p5",
                "code": "!(3 === 3) || 5 !== 5",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. `!true` is `false`, `5 !== 5` is `false`, and `||` needs at least one `true`.\n"
              }
            ],
            "hints": [
              {
                "when": "you said true for the second",
                "say": "`===` compares character by character. Is a capital `W` the same character as a lowercase `w`?\n"
              },
              {
                "when": "you said true for the third",
                "say": "Work out `10 % 4` first. Then ask: is that number greater than 2, or equal to it?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict six lines, then run it",
            "prompt": "Predict each line it prints — press `true` or `false` — then run it and compare.\n",
            "filename": "compare.js",
            "code": "console.log(5 > 3);\nconsole.log(8 <= 7);\nconsole.log(\"cat\" === \"Cat\");\nconsole.log(\"apple\" < \"banana\");\nconsole.log(true && false);\nconsole.log(!false || false);\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(5 > 3);",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(8 <= 7);",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. 8 is neither less than 7 nor equal to it.\n"
              },
              {
                "id": "r3",
                "code": "console.log(\"cat\" === \"Cat\");",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you said `true` — capitals count.** `c` and `C` are different characters.\n",
                "why": "`false`.\n"
              },
              {
                "id": "r4",
                "code": "console.log(\"apple\" < \"banana\");",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. `a` comes before `b`.\n"
              },
              {
                "id": "r5",
                "code": "console.log(true && false);",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. `&&` needs both sides `true`.\n"
              },
              {
                "id": "r6",
                "code": "console.log(!false || false);",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you said `false` — `!` goes first.** `!false` is `true`, and `true || false` is `true`.\n",
                "why": "`true`.\n"
              }
            ],
            "hints": [
              {
                "when": "you said true for \"cat\" === \"Cat\"",
                "say": "`===` asks whether the two are exactly the same, character by character. Look at the first character of each.\n"
              },
              {
                "when": "you said false for the last",
                "say": "Flip `false` with `!` first, then combine the two sides with `||`.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One comparison at a time",
            "prompt": "`age` is `20`. Work out `age >= 13 && age <= 19` one piece at a time: press the value of each comparison, then the value of the whole expression. The last question is a new expression; work it out the same way.\n",
            "items": [
              {
                "id": "t1",
                "question": "With `age` at 20, what does `age >= 13` produce?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. 20 is at least 13.\n"
              },
              {
                "id": "t2",
                "question": "What does `age <= 19` produce?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. 20 is more than 19.\n"
              },
              {
                "id": "t3",
                "question": "What does the whole `age >= 13 && age <= 19` produce?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you said `true` — `&&` needs both sides.** The right side was `false`, so 20 is not a teen.\n",
                "why": "`false`.\n"
              },
              {
                "id": "t4",
                "code": "const price = 12;\nconst quantity = 3;\nprice * quantity > 30 && !(quantity > 5)\n",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. `36 > 30` is `true`; `3 > 5` is `false` and `!` flips it to `true`; `true && true`.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "const day = \"Sunday\";\nday === \"Saturday\" || day === \"Sunday\"\n",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. The left side is `false`, the right side is `true`, and `||` needs only one.\n"
              },
              {
                "id": "t6",
                "code": "const score = 100;\nscore > 0 && score < 100\n",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you said `true` — `<` does not include 100 itself.** `100 < 100` is `false`.\n",
                "why": "`false`.\n"
              }
            ],
            "hints": [
              {
                "when": "you said true for the whole expression",
                "say": "Write down the two answers you gave for the parts. `&&` gives `true` only when both of them are `true`.\n"
              },
              {
                "when": "you said false for the price line",
                "say": "Work the arithmetic first: `12 * 3`. Then the part in parentheses, then `!`, then `&&`.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Tall enough, and not too tall",
            "prompt": "A ride allows riders from 120 to 200 cm tall, both included. Put the lines in order so `canRide` says whether `height` is allowed. One line is a mistake — leave it out.\n",
            "lines": [
              "const height = 130;",
              "const tallEnough = height >= 120;",
              "const notTooTall = height <= 200;",
              "const canRide = tallEnough && notTooTall;",
              "console.log(canRide);"
            ],
            "distractors": [
              {
                "line": "const canRide = tallEnough || notTooTall;",
                "why": "`||` is `true` when either side is. A rider 90 cm tall is not tall enough, but is not too tall either, so `||` lets them on. Both conditions must hold: `&&`.\n"
              }
            ],
            "why": "Work out each comparison into a name, then combine them with `&&`, because the rule needs both.\n",
            "hints": [
              {
                "when": "you used the || line",
                "say": "Try it with `height` at 90. Work out each side. Should a 90 cm rider get on?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Finish the teen check",
            "prompt": "`isTeen` takes an age and should return `true` for 13 to 19, both included, and `false` otherwise. Half of it is done: `oldEnough` already holds the first comparison. Write the rest between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it, as in *Writing a function's answer*. Writing a whole function comes in the Functions module.\n",
            "exportName": "isTeen",
            "filename": "teen.js",
            "starter": "function isTeen(age) {\n  const oldEnough = age >= 13;\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "isTeen(16) → true",
                "body": "return subject(16) === true;"
              },
              {
                "label": "isTeen(13) → true",
                "body": "return subject(13) === true;"
              },
              {
                "label": "isTeen(19) → true",
                "body": "return subject(19) === true;"
              },
              {
                "label": "isTeen(20) → false",
                "body": "return subject(20) === false;"
              },
              {
                "label": "isTeen(12) → false",
                "body": "return subject(12) === false;"
              }
            ],
            "hints": [
              {
                "when": "isTeen(19) gives false",
                "say": "19 counts. Which comparison includes the number itself?\n"
              },
              {
                "when": "isTeen(20) gives true",
                "say": "You returned `oldEnough` alone, or combined with `||`. There is a top to the range too, and both parts must hold.\n"
              },
              {
                "when": "every test says undefined",
                "say": "Put `return` in front of the expression, so its value is sent out.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "The other weekend day",
            "prompt": "`isWeekend` takes the name of a day, like `\"Saturday\"`, and should return `true` for Saturday **or** Sunday. Right now it checks only for Saturday. Change it so Sunday counts too. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it, as in *Writing a function's answer*. Then, in one sentence: why does `isWeekend(\"sunday\")` give `false`?\n",
            "exportName": "isWeekend",
            "filename": "weekend.js",
            "starter": "function isWeekend(day) {\n  // TODO: put your code here\n  return day === \"Saturday\";\n}\n",
            "tests": [
              {
                "label": "isWeekend(\"Saturday\") → true",
                "body": "return subject('Saturday') === true;"
              },
              {
                "label": "isWeekend(\"Sunday\") → true",
                "body": "return subject('Sunday') === true;"
              },
              {
                "label": "isWeekend(\"Monday\") → false",
                "body": "return subject('Monday') === false;"
              },
              {
                "label": "isWeekend(\"sunday\") → false (the names are written with a capital)",
                "body": "return subject('sunday') === false;"
              }
            ],
            "hints": [
              {
                "when": "every test except Sunday passes",
                "say": "Your code still checks only for Saturday. Ask a second question with `===`, and combine the two questions.\n"
              },
              {
                "when": "Saturday and Sunday both give false",
                "say": "You combined with `&&`. Can one day be Saturday **and** Sunday at the same time? Which operator needs only one side?\n"
              },
              {
                "when": "Sunday gives \"Sunday\" instead of true",
                "say": "Each side of `||` has to be a whole comparison: `day === \"Sunday\"`, not just `\"Sunday\"`.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Old enough, and registered",
            "prompt": "`canVote` takes an age and a boolean saying whether the person is registered. It should return `true` only when they are **18 or over** and registered, and `false` otherwise. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it, as in *Writing a function's answer*. Then, in one sentence: what input would tell `>` and `>=` apart?\n",
            "exportName": "canVote",
            "filename": "vote.js",
            "starter": "function canVote(age, registered) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "canVote(30, true) → true",
                "body": "return subject(30, true) === true;"
              },
              {
                "label": "canVote(18, true) → true",
                "body": "return subject(18, true) === true;"
              },
              {
                "label": "canVote(17, true) → false",
                "body": "return subject(17, true) === false;"
              },
              {
                "label": "canVote(30, false) → false",
                "body": "return subject(30, false) === false;"
              }
            ],
            "hints": [
              {
                "when": "canVote(18, true) gives false",
                "say": "18 counts. Which comparison includes the number itself?\n"
              },
              {
                "when": "canVote(30, false) gives true",
                "say": "Your answer ignores `registered`, or combines with `||`. Both parts have to be true.\n"
              },
              {
                "when": "canVote(17, true) gives true",
                "say": "`||` is true when either side is. The rule needs both.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a pass check",
            "prompt": "A code assistant wrote this to say whether a percentage is a pass (from 60 to 100, both included) and whether a letter grade is the top one, `\"A\"`. Read it, then answer each question.\n",
            "filename": "pass.js",
            "code": "const percent = 30;\nconst letter = \"a\";\nconst passed = percent >= 60 || percent <= 100;\nconst topGrade = letter === \"A\";\nconsole.log(passed);\nconsole.log(topGrade);\n",
            "items": [
              {
                "id": "c1",
                "question": "With `percent` at 30, what does line 3 give `passed`?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you said `false` — check the right side.** `30 <= 100` is `true`, and `||` needs only one.\n",
                "why": "`true` — for a failing score.\n"
              },
              {
                "id": "c2",
                "question": "Which rewrite of line 3 is right?",
                "choices": [
                  "percent >= 60 && percent <= 100",
                  "percent > 60 && percent < 100",
                  "percent >= 60 || percent < 100"
                ],
                "accept": [
                  "percent >= 60 && percent <= 100"
                ],
                "why": "`&&`, with both ends included. Every number is either at least 60 or at most 100, so the `||` version is always `true`; the `>`/`<` version shuts out 60 and 100 themselves.\n"
              },
              {
                "id": "c3",
                "question": "The letter was typed in lowercase. What does line 4 give `topGrade`?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you said `true` — `===` compares text exactly.** `\"a\"` and `\"A\"` are different characters.\n",
                "why": "`false`. Whoever calls this has to give the letter as a capital, or the code has to convert it first — the lesson *Working with strings* shows how.\n"
              }
            ],
            "hints": [
              {
                "when": "you thought line 3 was fine",
                "say": "Try a failing score, like 30, and work out each side of `||` separately.\n"
              },
              {
                "when": "you picked the > and < rewrite",
                "say": "Is a score of exactly 60 a pass? Work out `60 > 60`.\n"
              }
            ]
          }
        ]
      }
    },
    "strings": {
      "id": "strings",
      "title": "Working with strings",
      "kind": "read",
      "outcome": 1,
      "shape": "procedural",
      "why": "Text is the other value every program handles, and later lessons use string work untaught — `name[0].toUpperCase()`, `line.startsWith(…)`, `.trim()`, `password.length` — while template literals were taught nowhere (zero-prerequisite audit, `values.strings`). It sits in Values, types and operators (D-043), after Operators, because building text uses `+` and indexing uses `length - 1`. Procedural (01-explanations rule 8): each piece is notation or a tool; the one idea underneath — a string is never changed, a method returns a new one — is taught as its own step with a check.",
      "concepts": [
        "values.strings"
      ],
      "lede": "Names, messages, file names, anything a person typed: most of what a program handles is text. This lesson shows how to build text from values, read it one character at a time, and reshape it. It ends with the rule that catches many beginners: in JavaScript a string, once made, never changes, and a method that seems to change one gives you a new string instead.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Operators · Conversion — when a value does not fit"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**String** — a value that is text, written in quotes: `\"Ada\"`. **Character** — one letter, digit, space or symbol in a string. **Index** — a character's position, counted from `0`. **Method** — a function that belongs to a value, called with a dot: `name.trim()`.",
          "entries": [
            "**String** — a value that is text, written in quotes: `\"Ada\"`.",
            "**Character** — one letter, digit, space or symbol in a string.",
            "**Index** — a character's position, counted from `0`.",
            "**Method** — a function that belongs to a value, called with a dot: `name.trim()`."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on *Arithmetic operators* and *Comparing and combining*.\nAnswer from memory; press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "2 + 3 * 4",
                    "accept": [
                      "14"
                    ],
                    "miss": "**If you answered `20` — `*` goes before `+`.** Work out `3 * 4` first.\n",
                    "why": "`14`.\n"
                  },
                  {
                    "code": "let count = 5;\ncount++;\ncount += 10;\n",
                    "accept": [
                      "16"
                    ],
                    "why": "`16`. 5, then 6, then 16.\n"
                  },
                  {
                    "code": "\"cat\" === \"Cat\"",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "why": "`false`. `===` compares text exactly, capitals included.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can build a string from values with a template literal or with `+`",
                "I can read a character by its index, and use `.length` to find the last one",
                "I can use `toUpperCase`, `trim`, `slice`, `includes` and `startsWith`",
                "I can say why calling a method on a string does not change it, and keep the result"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Build text from values",
          "body": [
            {
              "p": "A **string** is a value that is text. You write one in quotes: `\"Ada\"` or\n`'Ada'`. Both kinds of quotes make the same string. Mixing them in one program\nis legal, but the code is easier to read if you pick one kind and use it\nthroughout. The quotes are not part of the text; they only mark where it starts\nand stops. `console.log(\"Ada\")` prints `Ada`."
            },
            {
              "p": "There are two ways to build a string out of other values."
            },
            {
              "p": "**`+` joins strings.** Between two strings, `+` does not add: it sticks the\nsecond string on the end of the first. Joining strings this way is called\n**concatenation**. `+` puts nothing between the two strings, so if you want a\nspace, you have to put the space in one of the strings yourself."
            },
            {
              "p": "**A template literal** is a string written between backticks instead of\nquotes. On a US keyboard, the backtick key is usually just left of `1`. In\nthis example, the first line uses ordinary double quotes and the second line\nuses backticks:"
            },
            {
              "code": "const plain = \"Hi\";\nconst template = `Hi`;",
              "lang": "js"
            },
            {
              "p": "Inside a template literal, you can write `${ }` with any expression between\nthe braces. This is called a **placeholder**. JavaScript works out the\nexpression and puts its value into the text where the placeholder was. Two\nworked examples follow. *greeting.js* builds the same message twice, once with\n`+` and once with a template literal. *receipt.js* puts a calculation inside a\nplaceholder."
            },
            {
              "code": "const name = \"Ada\";\nconst unread = 3;\n\nconst withPlus = \"Hi \" + name + \", you have \" + unread + \" messages\";\nconst withTemplate = `Hi ${name}, you have ${unread} messages`;\n\nconsole.log(withTemplate);   // Hi Ada, you have 3 messages",
              "lang": "js",
              "file": "greeting.js"
            },
            {
              "code": "const item = \"tea\";\nconst price = 4;\nconst qty = 3;\n\nconst line = `${qty} x ${item}: $${price * qty}`;\nconsole.log(line);           // 3 x tea: $12",
              "lang": "js",
              "file": "receipt.js"
            },
            {
              "p": "In *receipt.js* the placeholder holds `price * qty`, so the multiplying\nhappens first and its result, `12`, goes into the text. The `$` just before\nthe placeholder is an ordinary dollar sign."
            },
            {
              "p": "Both ways build the same string. This course uses template literals when it\nmixes text and values, because you can see the spaces in the finished text,\nand a missing space is easier to spot. Joining with `+` is just as correct, and\nyou will read it in a lot of code."
            },
            {
              "p": "When `+` joins a number to a string, the number becomes part of the text: in\n*greeting.js*, `unread` held the number `3`, and `3` appeared in the message.\nThe lesson *Conversion — when a value does not fit* explains that rule and\nwhere it causes surprises."
            },
            {
              "p": "Type what each prints, without quotes."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const first = \"Grace\";\nconst last = \"Hopper\";\nconsole.log(first + last);\n",
                    "accept": [
                      "GraceHopper"
                    ],
                    "miss": "**If you answered `Grace Hopper` — `+` adds nothing between them.** A space has to be in one of the strings: `first + \" \" + last`.\n",
                    "why": "`GraceHopper`, with no space.\n"
                  },
                  {
                    "code": "const city = \"Walla Walla\";\nconsole.log(`Welcome to ${city}!`);\n",
                    "accept": [
                      "Welcome to Walla Walla!"
                    ],
                    "why": "`Welcome to Walla Walla!` The value of `city` goes where `${city}` was.\n"
                  },
                  {
                    "code": "const a = 2;\nconst b = 5;\nconsole.log(`${a} + ${b} = ${a + b}`);\n",
                    "accept": [
                      "2 + 5 = 7"
                    ],
                    "miss": "**If you answered `2 + 5 = 2 + 5` — only what is inside `${ }` is worked out.** The rest is plain text, `+` and `=` included.\n",
                    "why": "`2 + 5 = 7`. The last `${ }` holds an expression, and its value is `7`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Length, and one character at a time",
          "body": [
            {
              "p": "Every string has a **length**: how many characters it holds. A **character** is\none letter, digit, space or symbol. Spaces count."
            },
            {
              "code": "\"Ada\".length          // 3\n\"Walla Walla\".length  // 11 — the space is a character\n\"\".length             // 0 — the empty string",
              "lang": "js"
            },
            {
              "p": "Each character has a position, its **index**. In JavaScript, as in most\nlanguages, **indexes start at 0**: the first character is at index 0. You read\none character by writing its index in square brackets after the string. This is\ncalled **bracket notation**:"
            },
            {
              "indexes": {
                "variable": null,
                "items": [
                  "\"H\"",
                  "\"o\"",
                  "\"p\"",
                  "\"p\"",
                  "\"e\"",
                  "\"r\""
                ],
                "kind": "character",
                "caption": "The length is 6."
              }
            },
            {
              "code": "const last = \"Hopper\";\nlast[0]     // \"H\"\nlast[1]     // \"o\"",
              "lang": "js"
            },
            {
              "p": "Because counting starts at 0, the last character of a string is at the index\n**one less than the length**. (The empty string, `\"\"`, has no characters, so it\nhas no last index.) Two worked examples of finding the last character:"
            },
            {
              "code": "const last = \"Hopper\";\n// the length is 6, so the last index is 6 - 1, which is 5\nlast[last.length - 1]    // \"r\"",
              "lang": "js"
            },
            {
              "code": "const word = \"cat\";\n// the length is 3, so the last index is 3 - 1, which is 2\nword[word.length - 1]    // \"t\"",
              "lang": "js"
            },
            {
              "p": "Asking for an index past the end is not an error. It gives `undefined`,\nJavaScript's \"nothing here\", and the program carries on. `\"cat\"[3]` is\n`undefined`."
            },
            {
              "p": "Type the value, without quotes."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "\"hello\".length",
                    "accept": [
                      "5"
                    ],
                    "why": "`5`.\n"
                  },
                  {
                    "code": "\"hello\"[1]",
                    "accept": [
                      "e"
                    ],
                    "miss": "**If you answered `h` — indexes start at 0.** `h` is index 0, so index 1 is the second character.\n",
                    "why": "`e`.\n"
                  },
                  {
                    "code": "const word = \"code\";\nword[word.length - 1]\n",
                    "accept": [
                      "e"
                    ],
                    "why": "`e`. The length is 4, so the last index is 3.\n"
                  },
                  {
                    "code": "\"hi\"[5]",
                    "accept": [
                      "undefined"
                    ],
                    "why": "`undefined`. There is no index 5, and reading past the end is not an error.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Methods: asking and reshaping",
          "body": [
            {
              "p": "A **method** is a function that belongs to a value. You call it by writing a\ndot after the value, then the method's name and parentheses: `name.trim()`.\nLike any call, it produces a value. Some string methods answer a question about\nthe string, such as whether it starts with some text. Others produce a\nreshaped copy of the string, such as a copy in capitals."
            },
            {
              "table": {
                "head": [
                  "Method",
                  "Produces",
                  "Example",
                  "Result"
                ],
                "rows": [
                  [
                    "`toUpperCase()`",
                    "the text in capitals",
                    "`\"ada\".toUpperCase()`",
                    "`\"ADA\"`"
                  ],
                  [
                    "`toLowerCase()`",
                    "the text in lowercase",
                    "`\"ADA\".toLowerCase()`",
                    "`\"ada\"`"
                  ],
                  [
                    "`trim()`",
                    "the text with spaces removed from both ends",
                    "`\"  ada \".trim()`",
                    "`\"ada\"`"
                  ],
                  [
                    "`slice(start, end)`",
                    "the characters from `start` up to, **not including**, `end`",
                    "`\"JavaScript\".slice(0, 4)`",
                    "`\"Java\"`"
                  ],
                  [
                    "`slice(start)`",
                    "from `start` to the end",
                    "`\"JavaScript\".slice(4)`",
                    "`\"Script\"`"
                  ],
                  [
                    "`includes(text)`",
                    "`true` if `text` appears anywhere",
                    "`\"report.pdf\".includes(\".pdf\")`",
                    "`true`"
                  ],
                  [
                    "`startsWith(text)`",
                    "`true` if it begins with `text`",
                    "`\"Hello\".startsWith(\"He\")`",
                    "`true`"
                  ]
                ]
              }
            },
            {
              "p": "`includes` and `startsWith` compare exactly, capitals included, the same way\n`===` does."
            },
            {
              "p": "`slice` takes indexes. It includes the character at the start index and\nleaves out the character at the end index. To work out a slice, count\npositions from 0, as in the step *Length, and one character at a time*. Two\nworked examples:"
            },
            {
              "code": "// \"JavaScript\".slice(0, 4)\n// start 0; end 4, left out\n// indexes 0, 1, 2, 3 -> J a v a\n\"Java\"",
              "lang": "js"
            },
            {
              "code": "// \"2026-09-28\".slice(5, 7)\n// start 5; end 7, left out\n// indexes 5, 6 -> 0 9\n\"09\"",
              "lang": "js"
            },
            {
              "p": "Methods can be **chained**: you write one method call after another, and\neach method works on the string the previous method produced.\n`\"  Ada  \".trim().toUpperCase()` trims first, giving `\"Ada\"`, and then\n`toUpperCase()` capitalises `\"Ada\"`, giving `\"ADA\"`."
            },
            {
              "p": "Type the value — text without quotes, or press `true` or `false`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "\"  hi there  \".trim()",
                    "accept": [
                      "hi there"
                    ],
                    "why": "`hi there`. Only the ends are trimmed; the space in the middle stays.\n"
                  },
                  {
                    "code": "\"JavaScript\".slice(4, 6)",
                    "accept": [
                      "Sc"
                    ],
                    "miss": "**If you answered `Scr` — the end index is left out.** Indexes 4 and 5 only.\n",
                    "why": "`Sc`.\n"
                  },
                  {
                    "code": "\"Hello\".startsWith(\"h\")",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**If you said `true` — the comparison is exact.** `\"h\"` and `\"H\"` are different characters.\n",
                    "why": "`false`. Capitals matter.\n"
                  },
                  {
                    "code": "\"  report  \".trim().length",
                    "accept": [
                      "6"
                    ],
                    "why": "`6`. `trim()` gives `\"report\"`, and its length is 6.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "A string never changes",
          "body": [
            {
              "p": "Here is the rule that catches many beginners. In JavaScript, a string, once\nmade, can never be changed. A value that can never be changed is called\n**immutable**. So **no string method changes the string you call it on.**\n`toUpperCase()` does not capitalise your string: it produces a **new** string,\nin capitals, and leaves the original string exactly as it was."
            },
            {
              "p": "So if you do not store the new string somewhere, it is lost:"
            },
            {
              "code": "const name = \"ada\";\nname.toUpperCase();      // makes \"ADA\" — and throws it away\nconsole.log(name);       // ada",
              "lang": "js"
            },
            {
              "p": "To keep the new string, store it in a variable. You can declare a new\nvariable for it. Or, if the original variable was declared with `let`, you can\nreassign that variable to the new string:"
            },
            {
              "code": "const name = \"ada\";\nconst shout = name.toUpperCase();\nconsole.log(shout);      // ADA\n\nlet input = \"  yes  \";\ninput = input.trim();\nconsole.log(input);      // yes",
              "lang": "js"
            },
            {
              "p": "Assigning to one character does not change a string either. `name[0] = \"A\"`\nthrows a TypeError, and `name` still holds `\"ada\"`. That is what happens in\n**strict mode**, a stricter set of rules that most modern JavaScript runs\nunder. Your homework runs in strict mode, and this course writes code as if it\nis on. Without strict mode, the same assignment silently does nothing: no\nerror, and no change. An optional lesson in the Modules module explains what\nstrict mode is and how it is turned on."
            },
            {
              "p": "The two worked examples below do the same job: capitalise the first letter of\na word. *tidy.js* also trims the spaces off first. Each example stores every\nnew string it needs in a variable:"
            },
            {
              "code": "const raw = \"grace\";\n\nconst first = raw[0].toUpperCase();     // \"G\"\nconst rest = raw.slice(1);              // \"race\"\nconst name = first + rest;              // \"Grace\"",
              "lang": "js",
              "file": "capitalise.js"
            },
            {
              "code": "const raw = \"  london  \";\n\nconst clean = raw.trim();               // \"london\" — tidy it first\nconst first = clean[0].toUpperCase();   // \"L\"\nconst rest = clean.slice(1);            // \"ondon\"\nconst city = first + rest;              // \"London\"",
              "lang": "js",
              "file": "tidy.js"
            },
            {
              "p": "In both programs, `raw` holds the same string at the end as at the start.\nEach step made a new string and stored it in a new variable."
            },
            {
              "p": "Type what each prints, without quotes."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const word = \"quiet\";\nword.toUpperCase();\nconsole.log(word);\n",
                    "accept": [
                      "quiet"
                    ],
                    "miss": "**If you answered `QUIET` — `toUpperCase()` made a new string, and nothing on line 2 stored it.** `word` itself never changed.\n",
                    "why": "`quiet`. Keep the result: `const loud = word.toUpperCase();`.\n"
                  },
                  {
                    "code": "let code = \"  ab12 \";\ncode = code.trim();\nconsole.log(code.length);\n",
                    "accept": [
                      "4"
                    ],
                    "why": "`4`. This time the trimmed string, `\"ab12\"`, was kept: line 2 reassigned `code` to it.\n"
                  },
                  {
                    "question": "After `const s = \"hat\"; const t = s.slice(1);` — what does `s` hold? Type it without quotes.",
                    "accept": [
                      "hat"
                    ],
                    "miss": "**If you answered `at` — that is `t`.** `slice` made a new string and left `s` alone.\n",
                    "why": "`hat`. `t` holds `at`; `s` is unchanged.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A sign-up form gives you `first` and `last` exactly as the person typed them —\nperhaps with spaces around them, perhaps in any mix of capitals. You need a\nusername: the first name in lowercase, a dot, then the last name in lowercase,\nlike `grace.hopper`."
            },
            {
              "p": "Write the lines, keeping every result you need. Choose between a template\nliteral and `+` for the final step, and say why. Then say which line would\nquietly do nothing if you forgot to keep its result, and what the username would\nlook like if you did."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Build text with a template literal** — backticks, with `${name}` where a value goes — or with `+`, which adds no spaces for you.",
                "**Indexes start at 0**, so the last character is at `length - 1`. Past the end is `undefined`, not an error.",
                "**In JavaScript, a string never changes.** Every string method produces a new string; store it in a variable, or it is lost."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "String",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String",
              "note": "every string method, each with examples."
            },
            {
              "title": "Template literals",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals",
              "note": "backticks and `${ }`."
            }
          ]
        }
      ],
      "_source": "lessons/strings.md",
      "practice": {
        "id": "strings",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "What does it produce?",
            "prompt": "Type the value — text without quotes — or, where there are buttons, pick it.\n",
            "items": [
              {
                "id": "p1",
                "code": "\"code\".length",
                "accept": [
                  "4"
                ],
                "why": "`4`.\n"
              },
              {
                "id": "p2",
                "code": "const w = \"hello\";\nw[1]\n",
                "accept": [
                  "e"
                ],
                "miss": "**If you answered `h` — indexes start at 0.** `h` is index 0.\n",
                "why": "`e`.\n"
              },
              {
                "id": "p3",
                "code": "const name = \"ada\";\nname.toUpperCase();\nconsole.log(name);\n",
                "accept": [
                  "ada"
                ],
                "miss": "**If you answered `ADA` — the method made a new string, and nothing kept it.** `name` never changed.\n",
                "why": "`ada`. In JavaScript a string never changes; to keep the capitals, store the new string in a variable.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "\"JavaScript\".slice(0, 4)",
                "accept": [
                  "Java"
                ],
                "miss": "**If you answered `JavaS` — the end index is left out.** Indexes 0 to 3.\n",
                "why": "`Java`.\n"
              },
              {
                "id": "p5",
                "code": "const n = 3;\nconsole.log(`${n * 2} items`);\n",
                "accept": [
                  "6 items"
                ],
                "why": "`6 items`. The expression inside `${ }` is worked out first.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered h for the second",
                "say": "Count positions from 0: `h` is 0, `e` is 1.\n"
              },
              {
                "when": "you answered ADA for the third",
                "say": "Look for a line that keeps what `toUpperCase()` produced — a name and an `=`. There is none, so the capitals were thrown away.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict six lines, then run it",
            "prompt": "Predict each line it prints — type it without quotes — then run it and compare.\n",
            "filename": "names.js",
            "code": "const first = \"Grace\";\nconst last = \"Hopper\";\nconsole.log(first + last);\nconsole.log(first + \" \" + last);\nconsole.log(`${last}, ${first}`);\nconsole.log(last.length);\nconsole.log(last[last.length - 1]);\nconsole.log(last.slice(0, 3).toUpperCase());\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(first + last);",
                "accept": [
                  "GraceHopper"
                ],
                "miss": "**If you put a space in — `+` puts nothing between the two strings.**\n",
                "why": "`GraceHopper`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(first + \" \" + last);",
                "accept": [
                  "Grace Hopper"
                ],
                "why": "`Grace Hopper`. The space is a string of its own.\n"
              },
              {
                "id": "r3",
                "code": "console.log(`${last}, ${first}`);",
                "accept": [
                  "Hopper, Grace"
                ],
                "why": "`Hopper, Grace`. The comma and space are plain text in the template.\n"
              },
              {
                "id": "r4",
                "code": "console.log(last.length);",
                "accept": [
                  "6"
                ],
                "why": "`6`.\n"
              },
              {
                "id": "r5",
                "code": "console.log(last[last.length - 1]);",
                "accept": [
                  "r"
                ],
                "why": "`r`. The length is 6, so the last index is 5.\n"
              },
              {
                "id": "r6",
                "code": "console.log(last.slice(0, 3).toUpperCase());",
                "accept": [
                  "HOP"
                ],
                "why": "`HOP`. `slice(0, 3)` gives `Hop`, then `toUpperCase()` works on that.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered e for the fifth",
                "say": "`last.length` is 6, and `6 - 1` is 5. Count to index 5 from 0.\n"
              },
              {
                "when": "you answered HOPP for the last",
                "say": "`slice(0, 3)` stops *before* index 3. Which characters are at 0, 1 and 2?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One method at a time",
            "prompt": "In a chain, each method works on the value the previous method produced. For each line, type the value the whole line produces — text without quotes, or press a button.\n",
            "items": [
              {
                "id": "t1",
                "code": "\"  Walla Walla  \".trim()",
                "accept": [
                  "Walla Walla"
                ],
                "why": "`Walla Walla`. The ends are trimmed; the middle space stays.\n"
              },
              {
                "id": "t2",
                "code": "\"  Walla Walla  \".trim().toLowerCase()",
                "accept": [
                  "walla walla"
                ],
                "why": "`walla walla`.\n"
              },
              {
                "id": "t3",
                "code": "\"  Walla Walla  \".trim().toLowerCase().startsWith(\"walla\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you said `false` — by this point the text is already lowercase.** `startsWith` checks what the chain produced, not the original.\n",
                "why": "`true`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "\"report.PDF\".toLowerCase().includes(\".pdf\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. Lowercasing first means the check no longer depends on how the name was typed.\n"
              },
              {
                "id": "t5",
                "code": "\"  ok  \".length - \"  ok  \".trim().length",
                "accept": [
                  "4"
                ],
                "why": "`4`. 6 characters before trimming, 2 after.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered false for the third",
                "say": "Work left to right and write down the value after each method call. Each method works only on the value the method before it produced, not on the original text.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Capitalise a name",
            "prompt": "Someone typed their name as `\"  ada  \"`. Put the lines in order so the program prints `Ada`. Two lines are mistakes — leave them out.\n",
            "lines": [
              "const raw = \"  ada  \";",
              "const name = raw.trim();",
              "const fixed = name[0].toUpperCase() + name.slice(1);",
              "console.log(fixed);"
            ],
            "distractors": [
              {
                "line": "raw.trim();",
                "why": "It makes a trimmed string and throws it away. `raw` still has its spaces.\n"
              },
              {
                "line": "const fixed = name[1].toUpperCase() + name.slice(1);",
                "why": "Indexes start at 0. `name[1]` is `d`, so this gives `Dda`.\n"
              }
            ],
            "why": "Trim first, and store the trimmed string in `name`. Then build a new string from the first character of `name` in capitals and the rest of `name` from index 1 on. `raw` is unchanged at the end, because each step made a new string and stored it in a new variable.\n",
            "hints": [
              {
                "when": "you used raw.trim(); on its own",
                "say": "Does that line store what `trim()` produced? A new string that is not stored in a variable is lost as soon as the line ends.\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Keep the result",
            "prompt": "`badge` should return a name trimmed and in capitals: `\"  ada \"` gives `\"ADA\"`. The trimmed name is already in `clean`. Finish it. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why does `clean.toUpperCase();` on a line of its own not work?\n",
            "exportName": "badge",
            "filename": "badge.js",
            "starter": "function badge(name) {\n  const clean = name.trim();\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "badge(\"  ada \") → \"ADA\"",
                "body": "return subject('  ada ') === 'ADA';"
              },
              {
                "label": "badge(\"Grace\") → \"GRACE\"",
                "body": "return subject('Grace') === 'GRACE';"
              },
              {
                "label": "badge(\"   lin\") → \"LIN\"",
                "body": "return subject('   lin') === 'LIN';"
              }
            ],
            "hints": [
              {
                "when": "badge(\"  ada \") gives \"ada\"",
                "say": "In JavaScript a string never changes. `clean.toUpperCase()` made a capitalised copy — did anything store that copy or return it?\n"
              },
              {
                "when": "badge(\"  ada \") gives \"  ADA \"",
                "say": "You capitalised `name`, which still has its spaces. The trimmed version is in `clean`.\n"
              },
              {
                "when": "every test says undefined",
                "say": "Put `return` in front of the value you want sent out.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Tidy the code in the message",
            "prompt": "`readyMessage` puts an order code into a message. Codes arrive as typed — `\" ab12 \"` — and the message should show them trimmed and in capitals: `\"Order AB12 is ready\"`. Change it. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: in what order did you call the two methods, and does the order matter here?\n",
            "exportName": "readyMessage",
            "filename": "ready.js",
            "starter": "function readyMessage(code) {\n  // TODO: put your code here\n  return `Order ${code} is ready`;\n}\n",
            "tests": [
              {
                "label": "readyMessage(\" ab12 \") → \"Order AB12 is ready\"",
                "body": "return subject(' ab12 ') === 'Order AB12 is ready';"
              },
              {
                "label": "readyMessage(\"xy9\") → \"Order XY9 is ready\"",
                "body": "return subject('xy9') === 'Order XY9 is ready';"
              },
              {
                "label": "readyMessage(\"  Q7\") → \"Order Q7 is ready\"",
                "body": "return subject('  Q7') === 'Order Q7 is ready';"
              }
            ],
            "hints": [
              {
                "when": "the message has two spaces in a row",
                "say": "The code was capitalised but not trimmed. Chain a second method.\n"
              },
              {
                "when": "the code is still lowercase",
                "say": "If you called the methods on a line of their own, their results were thrown away. Call them inside the `${ }`, or keep the result in a name and use that.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Show the last four",
            "prompt": "`lastFour` takes a card number as text, of any length, and returns four stars followed by its last four characters: `\"1234567812345678\"` gives `\"****5678\"`. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why does `slice(12)` pass the first test and fail another?\n",
            "exportName": "lastFour",
            "filename": "card.js",
            "starter": "function lastFour(cardNumber) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "lastFour(\"1234567812345678\") → \"****5678\"",
                "body": "return subject('1234567812345678') === '****5678';"
              },
              {
                "label": "lastFour(\"123456789012\") → \"****9012\"",
                "body": "return subject('123456789012') === '****9012';"
              },
              {
                "label": "lastFour(\"9876\") → \"****9876\"",
                "body": "return subject('9876') === '****9876';"
              }
            ],
            "hints": [
              {
                "when": "the first test passes and the second fails",
                "say": "You counted the start for a 16-character number. Work the start out from the length, so it is right for any number.\n"
              },
              {
                "when": "you get the first four instead",
                "say": "`slice(0, 4)` takes from the start. Where do the last four characters begin, in terms of `length`?\n"
              },
              {
                "when": "you get five characters or three",
                "say": "`slice(start)` includes `start`. Check it on `\"9876\"`: the start must be index 0, and the length is 4.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a username builder",
            "prompt": "A code assistant wrote this to build a username — the first name in lowercase, then the year — from a name as a person typed it. Read it, then answer each question.\n",
            "filename": "username.js",
            "code": "const first = \"Maya \";\nconst year = 2026;\nfirst.toLowerCase();\nconst username = first + year;\nconsole.log(username);\n",
            "items": [
              {
                "id": "c1",
                "question": "Type exactly what it prints.",
                "accept": [
                  "Maya 2026"
                ],
                "miss": "**If you answered `maya2026` — that is what the code was meant to print.** Check whether any line stored the lowercase version, and what `\"Maya \"` ends with.\n",
                "why": "`Maya 2026` — a capital M, and a space in the middle.\n"
              },
              {
                "id": "c2",
                "question": "Which line does nothing? Type the line number.",
                "accept": [
                  "3",
                  "line 3",
                  "l3"
                ],
                "why": "Line 3. It makes the new string `\"maya \"`, and nothing stores it.\n"
              },
              {
                "id": "c3",
                "question": "Why does line 3 do nothing?",
                "choices": [
                  "The string never changes; the result was not kept",
                  "toLowerCase only works on a let",
                  "Methods do not work on const names"
                ],
                "accept": [
                  "The string never changes; the result was not kept"
                ],
                "miss": "**If you blamed `const` — `const` only stops the name being reassigned.** No method changes a string, whatever keyword named it.\n",
                "why": "`toLowerCase()` produces a new string and leaves `first` as it was. Nothing stored the new string.\n"
              },
              {
                "id": "c4",
                "question": "Keeping the lowercase result still leaves one thing wrong. Which method fixes it?",
                "choices": [
                  "trim()",
                  "slice(1)",
                  "toUpperCase()"
                ],
                "accept": [
                  "trim()"
                ],
                "why": "`trim()`. The typed name ends with a space, and it ends up inside the username.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered maya2026",
                "say": "Keep a table of what each variable holds. After line 3, what does `first` hold? Did line 3 use `=` to store anything?\n"
              }
            ]
          }
        ]
      }
    },
    "where-type-lives": {
      "id": "where-type-lives",
      "title": "Where the type actually lives",
      "kind": "read",
      "outcome": 1,
      "shape": "conceptual",
      "why": "Outcome 1 opens with how a language treats types. Where the type is attached decides when a type error surfaces, the question the type-discipline axis turns on (`program/language-tracks.md`); D-029 keeps the two together as one idea seen from two sides. `values.primitives` — the five primitive types and `typeof` — is absorbed here (`concepts.md` §0, D-042): a student starting from zero needs to know what a type is before asking where it lives.",
      "concepts": [
        "values.primitives",
        "types.location",
        "types.checking-time"
      ],
      "lede": "Every value in a program is one kind of thing — a number, a piece of text, a yes or no. That kind is its type. This lesson asks a question that sounds like hair-splitting and turns out to explain most of this module: does the type belong to the variable, or to the value?\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Naming values: let and const · Conversion — when a value does not fit · The type-discipline axis"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Value** — one piece of data: `5`, `\"hello\"`, `true`. **Type** — what kind of value it is. **Primitive** — one of the simple built-in types this module covers. **Variable** — a name you gave a value with `let` or `const`.",
          "entries": [
            "**Value** — one piece of data: `5`, `\"hello\"`, `true`.",
            "**Type** — what kind of value it is.",
            "**Primitive** — one of the simple built-in types this module covers.",
            "**Variable** — a name you gave a value with `let` or `const`."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about types. Some of this has not been explained yet — guess if\nyou have an idea, and press **I don't know** if you have none."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "After `let count = 5;` and then `count = \"hello\";` — does JavaScript object?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you expected an error, you are thinking of the variable as a box with a type written on it.** In JavaScript it is only a name. You pointed it at a different value, and nothing ever promised what kind of value that would be.\n",
                          "why": "Nothing objects. The variable is a name, and it now points at a value of a different type.\n"
                        },
                        {
                          "question": "JavaScript has an operator, `typeof`, that tells you a type. What does `typeof \"5\"` give?",
                          "choices": [
                            "\"number\"",
                            "\"string\""
                          ],
                          "accept": [
                            "\"string\""
                          ],
                          "miss": "**If you picked `\"number\"` — look at the quotes.** `\"5\"` is text that happens to contain a digit. The quotes make it a string, whatever is inside them.\n",
                          "why": "`\"string\"`. Quotes make a string. `typeof 5`, with no quotes, would give `\"number\"`.\n"
                        },
                        {
                          "question": "After `let count = 5;` you ask `typeof count`. Then `count = \"hello\";` and you ask again. What are the two answers?",
                          "choices": [
                            "\"number\" both times",
                            "\"number\", then \"string\""
                          ],
                          "accept": [
                            "\"number\", then \"string\""
                          ],
                          "miss": "**If you picked `\"number\"` both times, you asked about the variable.** `typeof` asks about the *value* the name points at now, and that value changed.\n",
                          "why": "`\"number\"`, then `\"string\"`. `typeof` reports the type of the value, so the answer changes when the value does.\n"
                        },
                        {
                          "question": "In JavaScript, when is a type mistake — say, asking a number for `toUpperCase()` — found?",
                          "choices": [
                            "Before the program runs",
                            "When that line runs"
                          ],
                          "accept": [
                            "When that line runs"
                          ],
                          "miss": "**If you said before, you may be picturing a language where the variable carries the type.** In JavaScript a name has no type of its own; only its value does. The values do not exist until the program runs, so JavaScript has nothing to check ahead of time.\n",
                          "why": "When that line runs. The lines before it run normally; the mistake is only found when JavaScript reaches it.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "After `count = 5` and then `count = \"hello\"` — does Python object?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Nothing. Python works the same way — the name is a label, not a box.\n"
                        },
                        {
                          "question": "What does `type(\"5\")` give?",
                          "choices": [
                            "<class 'int'>",
                            "<class 'str'>"
                          ],
                          "accept": [
                            "<class 'str'>"
                          ],
                          "why": "A string. The quotes decide it, whatever is inside them.\n"
                        },
                        {
                          "question": "After `count = 5`, then `count = \"hello\"`, what does `type(count)` give each time?",
                          "choices": [
                            "int both times",
                            "int, then str"
                          ],
                          "accept": [
                            "int, then str"
                          ],
                          "why": "The value's type, so it changes with the value.\n"
                        },
                        {
                          "question": "You write `count: int = 5` and later `count = \"hi\"`. Does Python refuse when it runs?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "No. The `: int` is a **type hint**: a note for readers and for separate checking tools. Python itself ignores it when it runs.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "After `int count = 5;` and then `count = \"hello\";` — does anything object?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "The compiler refuses. The **variable** has a type, and it only ever accepts values of that type.\n"
                        },
                        {
                          "question": "What type is `\"5\"`?",
                          "choices": [
                            "int",
                            "string"
                          ],
                          "accept": [
                            "string"
                          ],
                          "why": "A string. The quotes decide it.\n"
                        },
                        {
                          "question": "Write `var count = 5; count = \"hello\";` instead. Does `var` stop the compiler objecting?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "`var` works out the type once, from the first value, and fixes it. It is shorthand for writing `int`.\n"
                        },
                        {
                          "question": "When is a type mistake like `count = \"hello\"` found in C#?",
                          "choices": [
                            "Before the program runs",
                            "When that line runs"
                          ],
                          "accept": [
                            "Before the program runs"
                          ],
                          "why": "Before it runs, by the compiler. That is the direct consequence of the variable carrying the type.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can name the type of any value — number, string, boolean, `null`, `undefined` — and check it with `typeof`",
                "I can say whether a language attaches the type to the variable or to the value, from a code sample alone",
                "I can say when a type error surfaces in my language, and why"
              ]
            }
          ]
        },
        {
          "h": "Five kinds of value"
        },
        {
          "p": "A **value** is one piece of data your program works with. Every value has a\n**type** — what kind of thing it is. JavaScript has a handful of simple,\nbuilt-in types called **primitives**. You will use five of them all the time:"
        },
        {
          "table": {
            "head": [
              "Type",
              "What it is",
              "Examples"
            ],
            "rows": [
              [
                "number",
                "any number, whole or not",
                "`42`, `-3`, `2.5`"
              ],
              [
                "string",
                "text, written in quotes",
                "`\"hello\"`, `'Ada'`, `\"42\"`"
              ],
              [
                "boolean",
                "yes or no",
                "`true`, `false`"
              ],
              [
                "undefined",
                "\"nothing was ever put here\"",
                "`undefined`"
              ],
              [
                "null",
                "\"someone said: there is none\"",
                "`null`"
              ]
            ]
          }
        },
        {
          "p": "`undefined` and `null` get their own lesson later in this module, *When there\nis nothing there*. For now, know that they are values too."
        },
        {
          "p": "To ask a value its type, put **`typeof`** in front of it. `typeof` is an\n**operator**, like `+`: it takes a value and produces a new one. What it\nproduces is the type's name, as a string."
        },
        {
          "code": "typeof 42          // \"number\"\ntypeof \"42\"        // \"string\" — the quotes make it text\ntypeof true        // \"boolean\"\ntypeof undefined   // \"undefined\"",
          "lang": "js",
          "file": "types.js"
        },
        {
          "p": "Look at the second line of *types.js*, `typeof \"42\"`. `\"42\"` looks like a\nnumber, but it is text: the characters `4` and `2`. The quotes are the only\nsign, and they decide the type."
        },
        {
          "block": "notice",
          "label": "One wrong answer to know about",
          "body": [
            {
              "p": "`typeof null` gives `\"object\"`. That is a mistake in the language, from its\nfirst version, and it cannot be fixed without breaking old programs that\ndepend on it. The extra page *Equality — going further* tells the story. For\nnow, know that `typeof` cannot tell you whether a value is `null`."
            }
          ]
        },
        {
          "p": "JavaScript has two more primitive types, `bigint` and `symbol`, which you will\nrarely meet. Lists, records and functions are not primitives. You will meet\nthem in the Arrays, Objects and Functions modules, and `typeof` has less to say\nabout them."
        },
        {
          "p": "What does each give? Pick the answer."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "typeof false",
                "choices": [
                  "\"boolean\"",
                  "\"string\"",
                  "\"undefined\""
                ],
                "accept": [
                  "\"boolean\""
                ],
                "why": "`\"boolean\"`. `false` without quotes is one of the two boolean values.\n"
              },
              {
                "code": "typeof \"false\"",
                "choices": [
                  "\"boolean\"",
                  "\"string\"",
                  "\"undefined\""
                ],
                "accept": [
                  "\"string\""
                ],
                "miss": "**If you picked `\"boolean\"` — look for the quotes first.** With quotes it is five characters of text that spell a word.\n",
                "why": "`\"string\"`. The quotes make it text, whatever the text says.\n"
              },
              {
                "code": "typeof 3.5",
                "choices": [
                  "\"number\"",
                  "\"string\"",
                  "\"decimal\""
                ],
                "accept": [
                  "\"number\""
                ],
                "why": "`\"number\"`. JavaScript has one type for all numbers, whole or not.\n"
              }
            ]
          }
        },
        {
          "h": "Two models: a box, or a sticky note"
        },
        {
          "p": "In some languages, the **variable** has a type. You declare that `count` holds\na whole number, and from then on it is a labelled box that accepts only whole\nnumbers. Put a string in it and something objects — usually before your\nprogram ever runs."
        },
        {
          "p": "In other languages, the **value** carries its own type, and the variable is\njust a name pointing at it. `count` is not a box; it is a sticky note. Today it\nis stuck to the number `5`. Later you can move it to the string `\"hello\"`, and\nthe language does not mind, because nothing ever promised otherwise."
        },
        {
          "p": "JavaScript is the second kind."
        },
        {
          "code": "let count = 5;\ntypeof count;      // \"number\" — asking about the value, not the name\ncount = \"hello\";\ntypeof count;      // \"string\" — the variable never objected",
          "lang": "js",
          "file": "sticky-note.js"
        },
        {
          "p": "`typeof count` looks like a question about the variable. It is not. JavaScript\nlooks up the value `count` points at, and reports that value's type."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "After `let price = \"9\";` and then `price = 9;` — what does `typeof price` give?",
                "choices": [
                  "\"string\"",
                  "\"number\""
                ],
                "accept": [
                  "\"number\""
                ],
                "miss": "**If you picked `\"string\"` — that was true a line ago.** `typeof` only ever sees the value the name points at now.\n",
                "why": "`\"number\"`. The name moved to a new value, and the new value is a number.\n"
              }
            ]
          }
        },
        {
          "h": "When you find out"
        },
        {
          "p": "Where the type lives decides **when you find out you made a type mistake**."
        },
        {
          "p": "If the variable has a type, the language can check every line before the\nprogram runs: it knows what each name is allowed to hold. If only the value has\na type, there is nothing to check until the value exists. JavaScript checks each\noperation **at the moment that line runs**."
        },
        {
          "code": "const price = 5;\nconsole.log(\"starting\");            // prints\nconsole.log(price + 1);             // prints 6\nconsole.log(price.toUpperCase());   // TypeError: price.toUpperCase is not a function\nconsole.log(\"done\");                // never runs",
          "lang": "js",
          "file": "late.js"
        },
        {
          "p": "`toUpperCase` is a method strings have and numbers do not. JavaScript does\nnot notice the mistake until it reaches line 4. Lines 1 to 3 run normally, and\ntwo of them print. Then the program stops with a **TypeError** — an error that means \"this operation\ndoes not work on a value of this type\" — and line 5 never runs."
        },
        {
          "p": "In the Branching module you will write lines that run only sometimes. In\nJavaScript, a type mistake on a line like that stays hidden until the day the\nline runs — which may be in front of a user."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In `late.js`, how many lines print before the error? Type a number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "Two: `starting` and `6`. JavaScript only checks line 4 when it gets there.\n"
              },
              {
                "question": "Does anything in `late.js` warn you about line 4 before the program starts?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The type belongs to the value, and the check happens when the line runs.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Working out where the type lives",
            "body": [
              {
                "h": "Working out where the type lives"
              },
              {
                "p": "One experiment, in three steps, tells you where the type lives in most\nlanguages you will meet. The two worked examples below run it in JavaScript\nand in Go."
              },
              {
                "p": "**Worked example 1 — JavaScript.**"
              },
              {
                "code": "// point one name at a number, then at a string\nlet x = 5;\nx = \"hello\";\n// → accepted. Nothing objected.\n\n// ask the language for the type, before and after\n// typeof x was \"number\"; now it is \"string\". The answer CHANGED.\n\n// decide where the type lives\n// Accepted, and the answer followed the value → the type lives on the VALUE.",
                "lang": "js",
                "file": "where.js"
              },
              {
                "p": "**Worked example 2 — a language you have not seen.** Here is Go, a language\nthis course does not use. You only need to read what happened."
              },
              {
                "code": "// point one name at a number, then at a string\nvar x int = 5\nx = \"hello\"\n// → refused before the program ran: cannot use \"hello\" as int value\n\n// ask the language for the type, before and after\n// Not needed — it refused before anything ran.\n\n// decide where the type lives\n// Something knew what x was allowed to hold before any value arrived.\n// Only the variable existed then → the type lives on the VARIABLE.",
                "lang": "go",
                "file": "where.go"
              },
              {
                "p": "**Now you.** Here is the same experiment run in Python. Fill in each blank,\nthen check your answers against the paragraph below the code."
              },
              {
                "code": "x = 5\nx = \"hello\"          # accepted\ntype(x)              # <class 'str'>  — it was <class 'int'> a line ago\n\n# point one name at a number, then at a string      → ______\n# ask the language for the type, before and after   → ______\n# decide where the type lives                       → ______",
                "lang": "python",
                "file": "where.py"
              },
              {
                "p": "Accepted; the answer changed with the value; so the type lives on the value —\nthe same model as JavaScript."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what does the type belong to?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Type belongs to",
                  "Checked",
                  "`x = 5` then `x = \"hi\"`"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "the value",
                    "while running",
                    "fine"
                  ],
                  [
                    "**Python**",
                    "the value",
                    "while running",
                    "fine"
                  ],
                  [
                    "**C#**",
                    "the **variable**",
                    "before running",
                    "refused"
                  ]
                ]
              }
            },
            {
              "p": "C# has a keyword, `dynamic`, that lets one variable behave the JavaScript way.\nThe `dynamic` keyword is the most useful fact beside this table: it shows the\ntwo models are a *choice a language makes*, not a law of programming. When you meet a new\nlanguage, \"where does the type live?\" has a real answer, and it tells you when\nyou will hear about your mistakes."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "In JavaScript, every value has a type, and no variable does. `typeof` asks\nthe value."
              },
              {
                "code": "typeof 5;          // \"number\"\ntypeof \"hi\";       // \"string\"\ntypeof true;       // \"boolean\"\ntypeof undefined;  // \"undefined\"\ntypeof null;       // \"object\" — a known mistake; test for null directly",
                "lang": "js"
              }
            ],
            "python": [
              {
                "p": "Same model, different words: names are bound to values, and values have types."
              },
              {
                "code": "type(5)        # <class 'int'>  — Python splits whole numbers...\ntype(2.5)      # <class 'float'> — ...from the rest, unlike JavaScript\ntype(\"hi\")     # <class 'str'>",
                "lang": "python"
              },
              {
                "p": "Type hints like `count: int` are ignored when the program runs. Separate tools\nsuch as `mypy` can read them and check before running — a third position\nbetween JavaScript and C#."
              }
            ],
            "csharp": [
              {
                "p": "The variable is declared with a type and keeps it. `var` works the type out\nonce; it does not remove it."
              },
              {
                "code": "int count = 5;          // the variable is an int, permanently\nvar name = \"ada\";       // worked out as string, permanently\ndynamic loose = 5;      // opts this one variable out of the check",
                "lang": "csharp"
              },
              {
                "p": "`dynamic` is rarely the right choice, because it turns off the compiler's\ncheck for that variable. But knowing it exists makes the point:\nwhere the type lives is a design choice, and some languages let you set it per\nvariable."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the four opening questions again from memory. Then say, in one sentence, what\n\"the variable is a sticky note\" means for a value that arrives from outside\nyour program — text a user typed, say — and what your code can therefore\nassume about its type."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "Your program asks the user for a price, and receives what they typed. Forty\nlines later it adds tax to that price. One day a user types `12.50` and the\noutput shows `12.501.5` instead of a total."
            },
            {
              "p": "Two places you could have found out that the price was text:"
            },
            {
              "list": [
                "**Where it arrives** — right after reading it, look at `typeof price` and deal with it there.",
                "**Where it is used** — at the line that adds the tax."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: how far from the cause is the\nline where you notice, and how many other lines would have to remember that\nthe price might be text?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Every value has a **type**. The five you will use now: number, string, boolean, `undefined`, `null`. `typeof` names it — except `typeof null`, which wrongly says `\"object\"`.",
                "Some languages attach the type to the **variable**; JavaScript attaches it to the **value**. A variable is a name pointing at a value.",
                "So JavaScript finds a type mistake **when that line runs** — never before."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Data structures and types",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Data_structures",
              "note": "the primitives, and that values carry types."
            },
            {
              "title": "typeof",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/typeof",
              "note": "the full table of what it reports."
            },
            {
              "title": "Dynamic typing",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Dynamic_typing",
              "note": "the one-paragraph glossary version of this lesson."
            }
          ]
        }
      ],
      "_source": "lessons/where-type-lives.md",
      "practice": {
        "id": "where-type-lives",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict, then place",
            "prompt": "Three quick ones. Type what `typeof` gives — the word it produces, with or without quotes — or pick an answer where there are buttons.\n",
            "items": [
              {
                "id": "p1",
                "code": "let c = 5; c = \"hello\"; typeof c",
                "accept": [
                  "\"string\"",
                  "string",
                  "'string'"
                ],
                "miss": "**If you typed `number` — that was the type of `c`'s first value.** `typeof` asks about the value the name points at now.\n",
                "why": "`typeof` asks the **value**, and the value is now a string. The name `c` never had a type of its own to ask about.\n"
              },
              {
                "id": "p2",
                "question": "An unfamiliar language: point a name at a number, then the same name at a string. It is refused before the program runs. Where does the type live?",
                "choices": [
                  "The variable",
                  "The value"
                ],
                "accept": [
                  "The variable"
                ],
                "why": "Something knew what the name was allowed to hold before any value arrived, and only the variable exists at that point.\n"
              },
              {
                "id": "p3",
                "code": "typeof \"42\"",
                "choices": [
                  "\"number\"",
                  "\"string\""
                ],
                "accept": [
                  "\"string\""
                ],
                "why": "The quotes make it text. The digits inside do not change its type.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "typeof true",
                "choices": [
                  "\"boolean\"",
                  "\"string\"",
                  "\"true\""
                ],
                "accept": [
                  "\"boolean\""
                ],
                "why": "`true` without quotes is a boolean value. `typeof` gives the type's name, not the value itself.\n"
              },
              {
                "id": "p5",
                "question": "Python: `count = 5` then `count = \"hi\"` is accepted. Where does the type live?",
                "choices": [
                  "The variable",
                  "The value"
                ],
                "accept": [
                  "The value"
                ],
                "why": "The change was accepted, so no type was attached to the name: the type belongs to the value. This is the same model as JavaScript.\n"
              },
              {
                "id": "p6",
                "code": "typeof null",
                "choices": [
                  "\"null\"",
                  "\"object\"",
                  "\"undefined\""
                ],
                "accept": [
                  "\"object\""
                ],
                "why": "`\"object\"` — a mistake in the language that cannot be fixed without breaking old programs. Among the primitive types, it is the one answer from `typeof` you cannot trust.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered \"number\" for the first",
                "say": "You asked about the name. Ask what `c` points at *now* — `typeof` only ever sees the value.\n"
              },
              {
                "when": "you answered \"number\" for \"42\"",
                "say": "Look at the quotes before you look at what is inside them. Which type is always written in quotes?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Watch when the error arrives",
            "prompt": "One line has a type mistake — numbers cannot do `toUpperCase`. Answer the questions first, then run it.\n",
            "filename": "receipt.js",
            "code": "const item = \"tea\";\nconst price = 3;\nconsole.log(item.toUpperCase());\nconsole.log(price + 2);\nconsole.log(price.toUpperCase());\nconsole.log(\"done\");\n",
            "items": [
              {
                "id": "r1",
                "question": "How many lines print before anything goes wrong? Type a number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "Two: `TEA` and `5`. Lines 1 to 4 are fine, and JavaScript does not look at line 5 until it gets there.\n"
              },
              {
                "id": "r2",
                "question": "What kind of error stops it? Type its name.",
                "accept": [
                  "typeerror",
                  "type error",
                  "a typeerror"
                ],
                "why": "A `TypeError` — an operation tried on a value of the wrong type. The name is the first word of the message.\n"
              },
              {
                "id": "r3",
                "question": "Does `\"done\"` print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. In a program like this one, an error stops the program at the line where it happens, and every line after it is skipped.\n"
              },
              {
                "id": "r4",
                "question": "Did JavaScript warn you about line 5 before the program started?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The type belongs to the value, so the check waits until the line runs.\n"
              }
            ],
            "hints": [
              {
                "when": "you said nothing prints",
                "say": "That would be true if types were checked before running. Count the lines JavaScript runs before it reaches the bad one.\n"
              },
              {
                "when": "you said \"done\" prints",
                "say": "Ask what happens to the rest of a program when a line throws an error. Does JavaScript carry on to the next line?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Ask the value, line by line",
            "prompt": "One name, several values. Each item carries on from the one before it. Type the word `typeof` reports — or, where there are buttons, pick the answer.\n",
            "items": [
              {
                "id": "t1",
                "code": "let item = 5; item = \"5\"; typeof item",
                "accept": [
                  "\"string\"",
                  "string",
                  "'string'"
                ],
                "why": "The quotes make it text. The name pointed at a number a moment earlier, but that tells you nothing about the value it points at now.\n"
              },
              {
                "id": "t2",
                "code": "… then item = item.length; typeof item",
                "accept": [
                  "\"number\"",
                  "number",
                  "'number'"
                ],
                "why": "`item.length` is worked out first, while `item` is still `\"5\"` — it is `1`, a number. Then the name moves to that number.\n"
              },
              {
                "id": "t3",
                "code": "… then item = item > 0; typeof item",
                "accept": [
                  "\"boolean\"",
                  "boolean",
                  "'boolean'"
                ],
                "why": "`1 > 0` is a comparison, and a comparison produces `true` or `false`. Same name, third type.\n"
              },
              {
                "id": "t4",
                "code": "let other; typeof other",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "`other` was declared and never given a value, so it holds `undefined` — and `typeof undefined` is `\"undefined\"`.\n"
              },
              {
                "id": "t5",
                "question": "Go: `var n int = 5` then `n = \"hi\"` is refused before running. Where does the type live?",
                "choices": [
                  "The variable",
                  "The value"
                ],
                "accept": [
                  "The variable"
                ],
                "why": "`int` is written on the declaration, and the refusal comes before anything runs. A language you have never used, placed from two lines.\n"
              }
            ],
            "extra": [
              {
                "id": "t6",
                "question": "C#: `var n = 5; n = \"hi\";` is refused by the compiler. Does `var` put the type on the value?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "`var` works the variable's type out once and fixes it. The refusal is the evidence: the variable has a type.\n"
              },
              {
                "id": "t7",
                "code": "let score = \"7\"; score = score * 2; typeof score",
                "accept": [
                  "\"number\"",
                  "number",
                  "'number'"
                ],
                "why": "`\"7\" * 2` works out to the number `14` (the next lesson, *Conversion — when a value does not fit*, says why), and the name moves to `14`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered \"string\" for t2",
                "say": "The right-hand side runs first, while `item` is still `\"5\"`. What is `\"5\".length`, and what type is that?\n"
              },
              {
                "when": "you answered \"number\" for t3",
                "say": "What does a comparison like `1 > 0` produce? It is not a number.\n"
              },
              {
                "when": "you said the value for t5",
                "say": "Something refused before the program ran. Ask what had to have a type for that to be possible.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Label a value with its type",
            "prompt": "`labelOf` receives one value and should return a sentence naming its type: given `5` it returns `\"5 is a number\"`; given `\"hi\"` it returns `\"hi is a string\"`. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why do `5` and `\"5\"` get different sentences when they print the same?\n",
            "exportName": "labelOf",
            "filename": "label-of.js",
            "starter": "function labelOf(value) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "labelOf(5) → \"5 is a number\"",
                "body": "return subject(5) === \"5 is a number\";"
              },
              {
                "label": "labelOf(\"5\") → \"5 is a string\"",
                "body": "return subject(\"5\") === \"5 is a string\";"
              },
              {
                "label": "labelOf(true) → \"true is a boolean\"",
                "body": "return subject(true) === \"true is a boolean\";"
              },
              {
                "label": "labelOf(\"true\") → \"true is a string\"",
                "body": "return subject(\"true\") === \"true is a string\";"
              }
            ],
            "hints": [
              {
                "when": "every test fails and nothing seems to come back",
                "say": "Check that your line starts with `return`. Without it the function works the sentence out and then returns `undefined`.\n"
              },
              {
                "when": "you get \"5 is a 5\"",
                "say": "You put the value in twice. The second part should be the value's type — which operator asks a value for that?\n"
              },
              {
                "when": "you get \"number\" on its own",
                "say": "`typeof value` is only the end of the sentence. Build the whole thing: the value, then `\" is a \"`, then its type. A template literal or `+` both work.\n"
              }
            ]
          }
        ]
      }
    },
    "conversion": {
      "id": "conversion",
      "title": "Conversion — when a value does not fit",
      "kind": "read",
      "outcome": 1,
      "shape": "conceptual",
      "why": "Outcome 1 names conversions. JavaScript's coercion is the plainest case of why type discipline matters (`program/language-flexibility.md`, \"coercion pathology\"): `\"5\" - 1` gives a wrong answer with no error at all, and the definedness question is what lets a student predict it rather than memorise it.",
      "lede": "An operator expects certain types. Hand it something else and the language has to decide what to do. Languages disagree about it, but in ways you can predict once you know which two questions to ask.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Where the type actually lives · Truthiness · Equality: == versus ==="
        }
      ],
      "concepts": [
        "types.definedness",
        "types.conversion",
        "types.checking-time"
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Here are four expressions. Type what each one produces: text in quotes, a\nnumber without quotes, or `error` if you think the language refuses to run it. Guess if you have an idea; press **I don't\nknow** if you have none."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "\"5\" + 1",
                          "accept": [
                            "\"51\"",
                            "51",
                            "'51'"
                          ],
                          "miss": "**You probably read `\"5\"` as the number five.** It is text that looks like a number, and what decides the result is what `+` is *defined* to do when one side is text.\n",
                          "why": "In JavaScript, `+` is the one arithmetic operator that also has a meaning for text: joining. Given text on one side and a number on the other, it turns the number into text and joins the two.\n"
                        },
                        {
                          "code": "\"5\" - 1",
                          "accept": [
                            "4"
                          ],
                          "miss": "**If you answered `\"4\"`, you applied the rule for `+` to `-`.** `+` has a meaning for text, so it joins. `-` has no meaning for text, so JavaScript converts `\"5\"` to the number 5 and subtracts.\n",
                          "why": "`-` has no meaning for text. JavaScript converts `\"5\"` to the number 5, then subtracts 1.\n"
                        },
                        {
                          "code": "\"5\" + 1 - 1",
                          "accept": [
                            "50"
                          ],
                          "miss": "**If you answered `\"5\"`, reasoning that adding one and taking one away cancel out: this is the costly mistake, because nothing warns you.** In algebra they cancel. Here the result is `50`, with no error and no warning.\n",
                          "why": "Both rules apply, one after the other. `+` runs first and joins `\"5\"` and `1` into the text `\"51\"`. Then `-` converts `\"51\"` to the number 51 and subtracts 1. You added one and took one away, and ended up at `50` instead of `5`.\n"
                        },
                        {
                          "code": "Number(\"5\") + 1",
                          "accept": [
                            "6"
                          ],
                          "miss": "**Check what `Number(\"5\")` produced before the `+` ran.** It gives the number 5. Then `+` has a number on each side, so it adds: 5 plus 1 is 6.\n",
                          "why": "`Number(\"5\")` converts the text to a number before `+` runs, so `+` sees two numbers and adds them. When you want arithmetic on a value that is text, converting it first like this is the way to say so.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "\"5\" + 1",
                          "accept": [
                            "error",
                            "typeerror",
                            "type error"
                          ],
                          "why": "Python refuses with a `TypeError`. In Python, `+` is defined for two strings and for two numbers, but not for a string and a number.\n"
                        },
                        {
                          "code": "\"5\" * 2",
                          "accept": [
                            "\"55\"",
                            "55",
                            "'55'"
                          ],
                          "why": "In Python, `*` *is* defined for a string and a number: it repeats the string that many times. Whether an operation is defined decides the result, not whether it seems sensible.\n"
                        },
                        {
                          "code": "\"5\" + \"1\"",
                          "accept": [
                            "\"51\"",
                            "51",
                            "'51'"
                          ],
                          "why": "Both sides are text, so `+` joins them. It is the same operator in the same language as the first line; the answer differs because the types differ.\n"
                        },
                        {
                          "code": "int(\"5\") + 1",
                          "accept": [
                            "6"
                          ],
                          "why": "`int(\"5\")` converts the text to the number 5 before `+` runs, so `+` adds two numbers. When you want arithmetic on a value that is text, converting it first like this is the way to say so.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "\"5\" + 1",
                          "accept": [
                            "\"51\"",
                            "51",
                            "'51'"
                          ],
                          "why": "In C#, `+` is defined for a string and an int: it turns the int into text and joins the two. This compiles and runs.\n"
                        },
                        {
                          "code": "\"5\" - 1",
                          "accept": [
                            "error",
                            "compile error",
                            "does not compile"
                          ],
                          "why": "In C#, `-` is not defined for a string and an int, and the compiler reports an error before the program runs.\n"
                        },
                        {
                          "code": "\"5\" + \"1\"",
                          "accept": [
                            "\"51\"",
                            "51",
                            "'51'"
                          ],
                          "why": "Both sides are text, so `+` joins them.\n"
                        },
                        {
                          "code": "int.Parse(\"5\") + 1",
                          "accept": [
                            "6"
                          ],
                          "why": "`int.Parse(\"5\")` converts the text to the number 5 before `+` runs, so `+` adds two numbers. When you want arithmetic on a value that is text, converting it first like this is the way to say so.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can predict an operator's result by asking whether it is *defined* for those two types, rather than remembering a rule for each operator",
                "I can point at a conversion in code and say who asked for it — the author, or the operator",
                "Given unfamiliar code, I can say whether a type error would surface before it runs or while it runs"
              ]
            }
          ]
        },
        {
          "h": "Two words, and the difference between them"
        },
        {
          "p": "You met **operators** in the Operators lesson: `+`, `-`, `*`, `/`, `===` and\nthe rest. The values on either side of an operator are what it works on. Every\noperator expects certain types. This lesson is about one question: what does\nan operator do when the values on either side are *not* the types it\nexpected?"
        },
        {
          "p": "First, two words. Both mean \"changing a value from one type to another\". They\ndiffer in **who asked** for the change."
        },
        {
          "p": "**Conversion** is a change of type that *you* ask for, by writing it in your\ncode.\n`Number(...)` is a built-in function that takes a value and returns a\nnumber; `String(...)` returns text."
        },
        {
          "code": "Number(\"5\")    // 5 — you wrote this, on purpose\nString(5)      // \"5\"\nNumber(\"abc\")  // NaN — \"not a number\": the text did not look like one\nNumber(\"\")     // 0 — careful: empty text becomes zero, not NaN",
          "lang": "js"
        },
        {
          "p": "**Coercion** is the language converting *for* you, because an operator needed\nit to, without being asked:"
        },
        {
          "code": "\"5\" - 1        // 4 — you did not ask for a conversion. You got one.",
          "lang": "js"
        },
        {
          "p": "The change of type is the same in both; who asked for it differs. A\nconversion is written in your code, so you can find it later by reading. A\ncoercion happens inside an operator, and nothing in the code shows it."
        },
        {
          "p": "For each line below, who asked for the change of type: the author of the code,\nor the operator?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "Number(\"8\") + 2",
                "choices": [
                  "The author",
                  "The operator"
                ],
                "accept": [
                  "The author"
                ],
                "why": "`Number(...)` is written in the code. By the time `+` runs, both sides are already numbers.\n"
              },
              {
                "code": "\"8\" * 2",
                "choices": [
                  "The author",
                  "The operator"
                ],
                "accept": [
                  "The operator"
                ],
                "miss": "**If you picked the author, look for `Number(...)` or `String(...)` on the line.** There is none. Nobody wrote a conversion, but `*` did one anyway.\n",
                "why": "This is coercion. `*` has no meaning for text, so JavaScript converted `\"8\"` to the number 8 without being asked.\n"
              }
            ]
          }
        },
        {
          "h": "Two questions, and languages answer them separately"
        },
        {
          "p": "It is tempting to sort languages into two groups, strict and loose. That\nsorting will mislead you. Two separate questions decide what happens when an\noperator gets types it did not expect, and a language answers each question\non its own. Telling the two questions apart is most of what this lesson\nteaches."
        },
        {
          "h": "Is the operation defined for these types?"
        },
        {
          "p": "A language defines which operators work on which combinations of types. When\nan operator is defined for the types it gets, it does what it is defined to\ndo. In JavaScript, `+` is defined for text and a number, and its definition is\nto join them. That is an ordinary definition, not a mistake."
        },
        {
          "p": "When an operator is not defined for the types it gets, the language has two\noptions. It can **refuse** with an error, which is what Python does. Or it can\n**convert** the values to types the operator is defined for, which is what\nJavaScript does. That is why `\"5\" - 1` is `4` in JavaScript."
        },
        {
          "p": "**Whether an operation is defined is decided operator by operator, not once\nfor the whole language.** In the same program, Python refuses `\"5\" + 1` and\nevaluates `\"5\" * 2` to `\"55\"`, because `*` is defined for a string and a\nnumber and `+` is not."
        },
        {
          "h": "When do you find out it was not defined?"
        },
        {
          "table": {
            "head": [
              "",
              "Checked",
              "Examples"
            ],
            "rows": [
              [
                "**Before it runs**",
                "A **compiler** — a program that reads all your code before any of it runs — checks the types",
                "C#"
              ],
              [
                "**While it runs**",
                "The check happens when that line runs. If the line never runs, you never find out; if it first runs while someone is using the program, they see the error",
                "JavaScript, Python"
              ]
            ]
          }
        },
        {
          "block": "notice",
          "label": "What this does not tell you",
          "body": [
            {
              "p": "When a language checks types does not tell you whether it converts. A\nlanguage that checks before running still converts silently whenever the\noperation is *defined*: `\"5\" + 1` compiles in C# and produces `\"51\"`. The two\nquestions are independent; the answer to one does not predict the answer to\nthe other."
            }
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "Python refuses `\"5\" + 1` but gives `\"55\"` for `\"5\" * 2`. In the same program. What decides which one works?",
                "choices": [
                  "Whether Python is strict",
                  "Whether that operator is defined for those two types"
                ],
                "accept": [
                  "Whether that operator is defined for those two types"
                ],
                "why": "Whether each operator is defined for those two types. If Python were simply \"strict\", it would refuse both.\n"
              },
              {
                "question": "C# checks types before the program runs. Does that stop `\"5\" + 1` from giving `\"51\"`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you said yes: checking early and converting less are two different things.** In C#, `+` is defined for text and a number, so the check finds nothing to refuse.\n",
                "why": "No. C# checks before the program runs, and the check passes, because `+` is defined for a string and an int.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Reading it one operation at a time",
            "body": [
              {
                "h": "Reading it one operation at a time"
              },
              {
                "p": "To predict what an expression with mixed types gives, take its operators one\nat a time and ask the same four questions about each. The comments below write\nthe four questions as subgoals. The operators run in the order of\n**precedence** from the Operators lesson: `*` and `/` before `+` and `-`, and\noperators at the same level from left to right."
              },
              {
                "p": "**Worked example 1.**"
              },
              {
                "code": "\"5\" + 1 - 1\n\n// find the operator that goes first\n//   the leftmost: \"5\" + 1\n// name the types either side\n//   text, and a number\n// ask whether that combination is defined\n//   yes — + joins when either side is text\n// say what comes out, and its type\n//   \"51\", text\n\n// and again on the result:\n\"51\" - 1\n// find the operator that goes first        -\n// name the types either side               text, and a number\n// ask whether that combination is defined  no — so the text converts\n// say what comes out, and its type         50, a number",
                "lang": "js",
                "file": "counter-example.js"
              },
              {
                "p": "**Worked example 2.**"
              },
              {
                "code": "const typed = \"3\";           // what a user typed: always text\nconst total = typed * 4 + 1;\n\n// find the operator that goes first\n//   * goes before +\n// name the types either side\n//   text, and a number\n// ask whether that combination is defined\n//   no — * has no meaning for text, so \"3\" converts\n// say what comes out, and its type\n//   12, a number\n\n// and again on the result: 12 + 1 is two numbers, so 13",
                "lang": "js",
                "file": "quantity.js"
              },
              {
                "p": "**Now you.** The first three subgoals are filled in. Fill in the last one."
              },
              {
                "code": "\"10\" * \"2\"\n\n// find the operator that goes first        *\n// name the types either side               text, and text\n// ask whether that combination is defined  no — so both sides convert\n// say what comes out, and its type         ______",
                "lang": "js",
                "file": "third-example.js"
              },
              {
                "p": "You never had to remember a rule about `*`. You asked whether `*` is defined\nfor two pieces of text, and the answer told you what happens."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **the two questions, answered separately.** The table shows the\nsame two expressions in three languages. Each row answers both questions for\none language."
            },
            {
              "table": {
                "head": [
                  "",
                  "`\"5\" + 1`",
                  "`\"5\" - 1`",
                  "You find out"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "`\"51\"` — defined, joins",
                    "`4` — not defined, converts",
                    "while running"
                  ],
                  [
                    "**Python**",
                    "`TypeError` — not defined",
                    "`TypeError` — not defined",
                    "while running"
                  ],
                  [
                    "**C#**",
                    "`\"51\"` — defined, joins",
                    "won't compile",
                    "before running"
                  ]
                ]
              }
            },
            {
              "p": "JavaScript and C# agree about `+`: both join. They differ about `-`:\nJavaScript converts and subtracts, and C# refuses before the program runs.\nJavaScript and Python both check while the program runs, and differ on what\nis *defined*: JavaScript defines `+` for text and a number, and Python does\nnot. Each language answers the two questions independently."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "JavaScript rarely stops with an error here: when an operator is not defined\nfor the types it gets, JavaScript converts them. `+` joins when either side is\ntext; the other arithmetic operators convert text to a number."
              },
              {
                "code": "\"5\" + 1          // \"51\"\n\"5\" - 1          // 4\n\"5\" * \"2\"        // 10\nNumber(\"5\") + 1  // 6",
                "lang": "js"
              },
              {
                "p": "`Number()` is the explicit conversion. A common practice is to convert once,\nwhere the value enters your program; after that, the rest of your code works\nwith a number and can do arithmetic without converting again. The task at the\nend of this lesson asks you to weigh that against converting at each use."
              }
            ],
            "python": [
              {
                "p": "Python defines fewer operations on mixed types, and it does not convert for\nyou, so it stops with an error more often. The error message names the two\ntypes it refused to combine."
              },
              {
                "code": "\"5\" + 1       # TypeError: can only concatenate str (not \"int\") to str\n\"5\" * 2       # \"55\"  — defined, and it repeats\nint(\"5\") + 1  # 6",
                "lang": "python"
              },
              {
                "p": "`int()` and `float()` are the explicit conversions. A common practice is to\nconvert once, at the edge of the program, where the value arrives."
              }
            ],
            "csharp": [
              {
                "p": "C# checks types before the program runs, so an operation that is not defined\nis reported by the compiler and never reaches a user. Operations that are\ndefined still convert silently."
              },
              {
                "code": "\"5\" + 1              // \"51\"  — compiles; + is defined for string and int\n\"5\" - 1              // error CS0019 — before the program runs\nint.Parse(\"5\") + 1   // 6",
                "lang": "csharp"
              },
              {
                "p": "`int.Parse` stops the program with an error on bad input; `int.TryParse` hands\nyou a boolean instead. Which you want depends on whether bad input is expected."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the four expressions from the start of the lesson again, from memory.\nThen compare with what you typed the first time. An answer that changed is\nsomething you learned."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "`\"5\" + 1 - 1` produced `50` and raised no error. Each operator did exactly\nwhat JavaScript's rules say it does."
            },
            {
              "p": "Your program reads a quantity from a text box on a web page, so the quantity\narrives as text, and adds one to it. There are two places to put the\nconversion:"
            },
            {
              "list": [
                "**At the edge** — convert the moment the value arrives, once.",
                "**At each use** — convert wherever you do arithmetic on it."
              ]
            },
            {
              "p": "Pick one and say why. Then, for each of the two places, say what goes wrong\nwhen the text box is empty or contains `abc`. Either choice is defensible;\nyour reasoning is the answer."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Ask whether the operator is **defined** for those two types. In JavaScript, `+` is defined for text and joins; `-`, `*` and `/` are not, so JavaScript converts the text to a number.",
                "**Conversion** is a change of type you wrote (`Number(x)`); **coercion** is one an operator did for you. A common practice is to convert once, where the value arrives.",
                "Checking before running or while running changes **when you are told**, not what is defined."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Type coercion",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Type_coercion",
              "note": "the one-paragraph version, worth reading before the long one."
            },
            {
              "title": "Addition (+)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Addition",
              "note": "exactly which combinations are defined, spelled out."
            },
            {
              "title": "Number()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/Number",
              "note": "and what it does with `\"\"` and `\"abc\"`, which the practice asks about."
            }
          ]
        }
      ],
      "_source": "lessons/conversion.md",
      "extra": {
        "id": "conversion",
        "title": "Conversion — going further",
        "assessed": false,
        "body": [
          {
            "block": "notice",
            "label": "Not assessed",
            "body": [
              {
                "p": "Nothing on this page is on a check, and none of it is needed for outcome 1. It\nis here because people ask, and because the answers are genuinely interesting —\nnot because you should read it before you can move on. **If you are still\nshaky on the main page, go back to it instead.**"
              }
            ]
          },
          {
            "h": "Why `+` joins instead of adding"
          },
          {
            "p": "It is not an accident, and it is not a bug. When a language designer overloads\none symbol for two operations, they have to pick a winner for the mixed case.\nJoining wins because the failure is visible and recoverable — you see `\"51\"` and\nknow something is off. If `+` had converted to a number instead, `\"5\" + 1` would\nbe `6`, and a form field containing a phone number would quietly become\narithmetic. Both choices are defensible; one fails louder."
          },
          {
            "h": "What \"dynamically typed\" does not tell you"
          },
          {
            "p": "The lesson's table is one question asked of several languages: **how much\nconversion will a language do on your behalf without being asked?** Keep that\nseparate from *when* you find out it went wrong, because the two are independent\n— and \"dynamically typed\" only answers the second one."
          },
          {
            "p": "JavaScript and Python are both dynamically typed. Both check types while the\nprogram runs. They still sit at opposite ends of the guessing axis: JavaScript\nconverts eagerly and hands you `\"51\"`, Python refuses and raises. So knowing a\nlanguage is dynamic tells you *when* the check happens. It tells you nothing\nabout how generous the conversion rules are. Those are separate design\ndecisions, and languages mix them freely."
          },
          {
            "p": "That is the part worth carrying out of this lesson. When you meet another\nlanguage, the useful question is not \"is it like JavaScript?\" but \"where does it\nsit on each of those two axes?\""
          },
          {
            "p": "Two further markers, if you want them. **Ruby** is dynamic and refuses, like\nPython, but its error names the conversion it declined to guess: `no implicit\nconversion of Integer into String` — the word *implicit* is deliberate, the\nlanguage saying it could have guessed and chose not to. **Rust** refuses before\nrunning and goes further than C#: no implicit numeric widening either, so\n`let x: i64 = 5i32;` will not compile. That is the far end of the axis, and\nwhere CS 240 starts."
          },
          {
            "h": "Where this goes next"
          },
          {
            "p": "Conversion is half of a pair. The other half is **equality** — what happens when\ntwo values of different types are *compared* rather than combined. The rules are\nrelated but not identical, and JavaScript has two operators precisely because of\nit. That is the equality lesson."
          }
        ]
      },
      "practice": {
        "id": "conversion",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the value",
            "prompt": "These work like the four expressions at the start of the lesson. Type the exact value, or, where there are buttons, pick the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "\"7\" + 2",
                "accept": [
                  "\"72\"",
                  "72",
                  "'72'"
                ],
                "why": "`+` is defined for text and a number: it joins them rather than adding, so the result is the text `\"72\"`.\n"
              },
              {
                "id": "p2",
                "code": "\"7\" - 2",
                "accept": [
                  "5"
                ],
                "why": "`-` has no meaning for text, so JavaScript converts `\"7\"` to the number 7 and subtracts 2.\n"
              },
              {
                "id": "p3",
                "question": "First question for `\"8\" / \"2\"`: is `/` defined for two pieces of text?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No, and that one answer decides the rest. Because `/` is not defined for text, JavaScript converts both sides to numbers, and `8 / 2` is `4`. You never needed a rule about `/`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "2 + 3 + \"7\"",
                "accept": [
                  "\"57\"",
                  "57",
                  "'57'"
                ],
                "why": "Work left to right. The first `+` has two numbers, so `2 + 3` adds to `5`. The second `+` has the number `5` and the text `\"7\"`, so it joins them into `\"57\"`.\n"
              },
              {
                "id": "p5",
                "code": "\"7\" * \"2\"",
                "accept": [
                  "14"
                ],
                "why": "Neither side is a number, and `*` is not defined for text, so JavaScript converts both sides to numbers: `7 * 2` is `14`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 9 for `\"7\" + 2`",
                "say": "Ask what `+` does when one side is text. `+` is the only arithmetic operator with a second meaning, joining. Re-read the lesson's first question, `\"5\" + 1`.\n"
              },
              {
                "when": "you answered \"5\" for `\"7\" - 2`",
                "say": "You applied the rule for `+` to `-`. Ask whether `-` itself is defined for text: does subtracting mean anything for text?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace the intermediate value",
            "prompt": "`\"5\" + 1 - 1` runs as two operations. Give the value after each one, then say what type the final result is.\n",
            "items": [
              {
                "id": "t1",
                "code": "after \"5\" + 1",
                "accept": [
                  "\"51\"",
                  "51",
                  "'51'"
                ],
                "why": "`+` and `-` are at the same level of precedence, so the leftmost runs first. `+` with text on one side joins, giving the text `\"51\"`. This is the step where the expression stops doing arithmetic.\n"
              },
              {
                "id": "t2",
                "code": "after \"51\" - 1",
                "accept": [
                  "50"
                ],
                "why": "`-` is not defined for text, so `\"51\"` converts to the number `51`, and `51 - 1` is `50`. The value changed type twice and ended somewhere nobody intended, with no error.\n"
              },
              {
                "id": "t3",
                "code": "typeof (\"5\" + 1 - 1)",
                "accept": [
                  "\"number\"",
                  "number",
                  "'number'"
                ],
                "why": "The last operation was `-`, and `-` on text or numbers always produces a number. Here it is the wrong number, and nothing says it is wrong.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "after \"4\" + \"5\"",
                "accept": [
                  "\"45\"",
                  "45",
                  "'45'"
                ],
                "why": "Both sides are text, so `+` joins them, giving `\"45\"`.\n"
              },
              {
                "id": "t5",
                "code": "\"3\" - \"1\" + \"1\"",
                "accept": [
                  "\"21\"",
                  "21",
                  "'21'"
                ],
                "why": "The leftmost operation, `\"3\" - \"1\"`, converts both sides and gives the number `2`. Then `+` has the number `2` and the text `\"1\"`, so it joins them: `\"21\"`.\n"
              }
            ],
            "hints": [
              {
                "when": "you gave the same value after both steps",
                "say": "The two values should differ. The first step uses `+`, which joins when one side is text. The second uses `-`, which converts text to a number. Work each step with its own operator.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Who asked for it?",
            "prompt": "Each line converts a value. Pick who asked for the conversion: the **author**, who wrote it on purpose, or the **operator**, which did it because it needed to.\n",
            "items": [
              {
                "id": "w1",
                "code": "Number(typed) + 1",
                "choices": [
                  "The author",
                  "The operator"
                ],
                "accept": [
                  "The author"
                ],
                "why": "`Number(...)` is written in the code: a conversion you can find later by reading. By the time `+` runs, both sides are numbers, so there is nothing left to convert.\n"
              },
              {
                "id": "w2",
                "code": "typed * 2    (typed is \"4\")",
                "choices": [
                  "The author",
                  "The operator"
                ],
                "accept": [
                  "The operator"
                ],
                "why": "Nobody wrote a conversion. `*` is not defined for text, so JavaScript converted `\"4\"` to a number by itself. That is coercion.\n"
              },
              {
                "id": "w3",
                "code": "\"Total: \" + 4",
                "choices": [
                  "The author",
                  "The operator"
                ],
                "accept": [
                  "The operator"
                ],
                "why": "`+` turned the number `4` into the text `\"4\"` so it could join. Coercion goes both ways, text to number and number to text, and this direction is just as silent.\n"
              },
              {
                "id": "w4",
                "code": "String(n) + \"px\"",
                "choices": [
                  "The author",
                  "The operator"
                ],
                "accept": [
                  "The author"
                ],
                "why": "The author converted with `String(n)`, so `+` receives two pieces of text and has nothing to decide.\n"
              }
            ],
            "extra": [
              {
                "id": "w5",
                "code": "Number(typed) * 2",
                "choices": [
                  "The author",
                  "The operator"
                ],
                "accept": [
                  "The author"
                ],
                "why": "`Number(...)` is an explicit conversion. `*` then receives a number and has nothing to convert.\n"
              },
              {
                "id": "w6",
                "code": "score - \"10\"",
                "choices": [
                  "The author",
                  "The operator"
                ],
                "accept": [
                  "The operator"
                ],
                "why": "No line of code converts `\"10\"`. `-` converted it to a number, because `-` is not defined for text.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered author for w2 or w3",
                "say": "Look for the conversion *in the code*. If there is no `Number(...)`, `String(...)` or similar on the line, nobody wrote one — so who did it?\n"
              },
              {
                "when": "you answered operator for w1 or w4",
                "say": "There is still an operator on the line, but check what it received. If both sides were already the right type, it had nothing to convert.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Change one thing, and watch the coercion stop helping",
            "prompt": "A shop page reads two text boxes the user typed in, so both values arrive as text: `qty` and `unit` (the price of one item). `orderCost` works: `\"3\" * \"2.50\" + 1` is `8.5`, because `*` converts both pieces of text to numbers, and `+` then adds two numbers. Now change the fee: it is **1.00 per unit** rather than once per order, so `\"3\"` units at `\"2.50\"` cost `(2.50 + 1) × 3 = 10.5`. Change only what comes after `return` — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: the old line had no `Number()` in it and still worked — why does yours need one?\n",
            "exportName": "orderCost",
            "filename": "order-cost.js",
            "starter": "// qty and unit arrive as text, e.g. \"3\" and \"2.50\".\n// Cost: quantity times unit price, plus a flat 1.00 fee.\nfunction orderCost(qty, unit) {\n  return qty * unit + 1;\n}\n",
            "tests": [
              {
                "label": "orderCost(\"3\", \"2.50\") → 10.5",
                "body": "return subject('3', '2.50') === 10.5;"
              },
              {
                "label": "orderCost(\"2\", \"4\") → 10",
                "body": "return subject('2', '4') === 10;"
              },
              {
                "label": "orderCost(\"1\", \"0\") → 1",
                "body": "return subject('1', '0') === 1;"
              },
              {
                "label": "the result is a number, not text",
                "body": "return typeof subject('2', '1.5') === 'number';"
              }
            ],
            "hints": [
              {
                "when": "\"3\" and \"2.50\" gave 7.503",
                "say": "Trace `(unit + 1)` on its own. Which operator is it, what types are either side, and is it defined for text? In the old line, `+` only ever saw the result of the multiplication, which was already a number.\n"
              },
              {
                "when": "\"2\" and \"4\" gave 82",
                "say": "The same join with different text: `\"4\" + 1`. Convert the unit price yourself before `+` sees it.\n"
              },
              {
                "when": "the result is still 8.5 or 9",
                "say": "That is the old behaviour: the fee is still added once. The fee now belongs to each unit, so it goes inside the multiplication, in parentheses.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Convert before you add",
            "prompt": "`addToCart` receives `inCart`, the number of items already in the cart, and `typed`, the text the user just typed into the quantity box. Return the new number of items, as a number. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why does `Number(inCart + typed)` give the wrong answer even though it has a `Number()` in it?\n",
            "exportName": "addToCart",
            "filename": "add-to-cart.js",
            "starter": "function addToCart(inCart, typed) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "addToCart(2, \"3\") → 5",
                "body": "return subject(2, '3') === 5;"
              },
              {
                "label": "addToCart(0, \"1\") → 1",
                "body": "return subject(0, '1') === 1;"
              },
              {
                "label": "spaces around the number are fine: addToCart(4, \" 2 \") → 6",
                "body": "return subject(4, ' 2 ') === 6;"
              },
              {
                "label": "the result is a number, not text",
                "body": "return typeof subject(1, '1') === 'number';"
              }
            ],
            "hints": [
              {
                "when": "addToCart(2, \"3\") gave \"23\"",
                "say": "`+` with text on one side joins. Which side is text? Convert that one before `+` sees it.\n"
              },
              {
                "when": "addToCart(2, \"3\") gave 23",
                "say": "You converted, but only after `+` had already joined `2` and `\"3\"` into `\"23\"`. Convert the text first, then add.\n"
              },
              {
                "when": "every test fails and nothing seems to come back",
                "say": "Check that your line starts with `return`. Without it the function works out the total and returns `undefined`.\n"
              }
            ]
          }
        ]
      }
    },
    "equality": {
      "id": "equality",
      "title": "Equality: == versus ===",
      "kind": "read",
      "outcome": 1,
      "shape": "conceptual",
      "why": "Outcome 1 names equality, and `==` against `===` is, with truthiness, the material the program singles out for showing why type discipline matters (`program/language-flexibility.md`, \"coercion pathology\"). This is pass 1 of `types.equality` (D-045): primitives, `==` against `===`, and one warning that two equal-looking arrays compare `false`. Identity and equivalence — the why — are pass 2, in Values and references.",
      "lede": "\"Are these the same?\" feels like one question. It is really three. Two of the three are about plain values (numbers, text, true and false), and JavaScript gives each of those two its own operator. They are this lesson. The third question waits until you have lists and records to ask it about.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Operators · Conversion — when a value does not fit · Truthiness"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Strict equality** (`===`) — same type and same value, no conversion. **Loose equality** (`==`) — convert first, then compare.",
          "entries": [
            "**Strict equality** (`===`) — same type and same value, no conversion.",
            "**Loose equality** (`==`) — convert first, then compare."
          ]
        }
      ],
      "concepts": [
        "types.equality"
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Here are four comparisons. Pick the result of each one, without running it. Guess if you have\nan idea; press **I don't know** if you have none."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "1 == \"1\"",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you picked `false` because a number is not text: that is what `===` would say.** `==` converts one side first, and after that the two sides match.\n",
                          "why": "`==` converts before it compares. The text `\"1\"` becomes the number `1`, and the numbers match.\n"
                        },
                        {
                          "code": "1 === \"1\"",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "false"
                          ],
                          "miss": "**If you picked `true`: `===` never converts.** A number and a piece of text are two different types, so they are never strictly equal, however alike they look.\n",
                          "why": "`===` compares without converting. The two sides are different types, so the result is `false`.\n"
                        },
                        {
                          "code": "null == undefined",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you picked `false`: `==` has one special rule for these two.** `null` and `undefined` are loosely equal to each other and to nothing else.\n",
                          "why": "This is a special case written into `==`: `null` and `undefined`, the two \"nothing\" values, count as equal to each other.\n"
                        },
                        {
                          "code": "NaN === NaN",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "false"
                          ],
                          "miss": "**If you picked `true` because everything equals itself: `NaN` is the one exception, and the exception is deliberate.** `NaN` means \"a calculation that did not produce a number\", and two failures are not the same number.\n",
                          "why": "In JavaScript, `NaN` is the one value not equal to itself. To test whether `x` is `NaN`, use `Number.isNaN(x)`.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "1 == \"1\"",
                          "choices": [
                            "True",
                            "False",
                            "error"
                          ],
                          "accept": [
                            "False"
                          ],
                          "why": "Python's `==` does not convert between numbers and text. A number and a piece of text are not equal, and `==` says so without an error.\n"
                        },
                        {
                          "code": "1 == 1.0",
                          "choices": [
                            "True",
                            "False",
                            "error"
                          ],
                          "accept": [
                            "True"
                          ],
                          "why": "Both are numbers, so Python compares their values: one and one.\n"
                        },
                        {
                          "code": "None == 0",
                          "choices": [
                            "True",
                            "False",
                            "error"
                          ],
                          "accept": [
                            "False"
                          ],
                          "why": "`None` — Python's \"nothing\" — is equal only to itself.\n"
                        },
                        {
                          "code": "float('nan') == float('nan')",
                          "choices": [
                            "True",
                            "False",
                            "error"
                          ],
                          "accept": [
                            "False"
                          ],
                          "why": "NaN is not equal to itself in Python either. The rule comes from IEEE 754, the standard that almost every language uses to store decimals.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "1 == \"1\"",
                          "choices": [
                            "true",
                            "false",
                            "error"
                          ],
                          "accept": [
                            "error"
                          ],
                          "why": "The compiler refuses the comparison, because in C# `==` is not defined for an `int` and a `string`. You find out before the program runs.\n"
                        },
                        {
                          "code": "\"ab\" == \"ab\"",
                          "choices": [
                            "true",
                            "false",
                            "error"
                          ],
                          "accept": [
                            "true"
                          ],
                          "why": "Two strings with the same characters are equal.\n"
                        },
                        {
                          "code": "1 == 1.0",
                          "choices": [
                            "true",
                            "false",
                            "error"
                          ],
                          "accept": [
                            "true"
                          ],
                          "why": "Both sides are numbers. C# converts the whole number `1` to the `double` `1.0`, and the two match.\n"
                        },
                        {
                          "code": "double.NaN == double.NaN",
                          "choices": [
                            "true",
                            "false",
                            "error"
                          ],
                          "accept": [
                            "false"
                          ],
                          "why": "NaN is not equal to itself in C# either: the rule comes from IEEE 754, the standard that almost every language uses to store decimals.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say whether a comparison converts before it compares (`==`) or not (`===`), and predict its result for numbers, strings, booleans, `null` and `undefined`",
                "I can say which question a comparison operator answers in my language, and which one I meant",
                "I can say what `[1, 2] === [1, 2]` gives, and that I should not trust either operator on two lists"
              ]
            }
          ]
        },
        {
          "h": "Two questions now, and one for later"
        },
        {
          "p": "When you point at two things and ask \"are these the same?\", you could mean\none of three questions:"
        },
        {
          "list": [
            "**Coerced equality** — would these be equal *if* I converted one to the other's type first? (**Coercion**, from the conversion lesson, is a conversion the language does for you.)",
            "**Strict equality** — are these the same type *and* the same value, with no converting?",
            "**Identity** — are these *the same thing*, not just alike? This one only matters for lists and records, which you have not met yet. It appears at the end of this lesson, as a warning, and is taught properly in the Values and references module."
          ]
        },
        {
          "p": "For numbers, strings, booleans, `null` and `undefined`, only the first two\nquestions arise: coerced equality and strict equality. JavaScript gives each of\nthem its own operator."
        },
        {
          "h": "Two operators, two questions"
        },
        {
          "p": "You met `===` in the Operators lesson. It is **strict equality**: `true` only\nwhen both sides are the same type *and* the same value. It never converts."
        },
        {
          "p": "`==` is **loose equality**. If the two sides are different types, it converts\nthem first — usually both to numbers, the same way `\"5\" - 1` did — and then\ncompares."
        },
        {
          "p": "Here they are side by side, on the same values:"
        },
        {
          "table": {
            "head": [
              "Comparison",
              "`===` (no converting)",
              "`==` (converts first)"
            ],
            "rows": [
              [
                "`1` and `\"1\"`",
                "`false` — number and text",
                "`true` — `\"1\"` becomes `1`"
              ],
              [
                "`0` and `\"\"`",
                "`false`",
                "`true` — `\"\"` becomes `0`"
              ],
              [
                "`0` and `false`",
                "`false`",
                "`true` — `false` becomes `0`"
              ],
              [
                "`\"abc\"` and `\"abc\"`",
                "`true`",
                "`true`"
              ],
              [
                "`null` and `undefined`",
                "`false`",
                "`true` — a special rule"
              ]
            ]
          }
        },
        {
          "p": "`!==` and `!=` are the \"not equal\" versions: `!==` is the opposite of `===`,\nand `!=` is the opposite of `==`."
        },
        {
          "p": "Look at the second row. `0 == \"\"` is `true`, because empty text converts to\nzero. If you are checking whether a user typed `0` into a text box, `==` also\nsays yes when they typed nothing at all. Mistakes like that are why most\nJavaScript style guides, and this course, **use `===` by default**. `==` is\nlegal and has its uses (the next section shows the most common one), but its\nconversions are invisible in the code. When you need to compare two values of\ndifferent types, the habit this course follows is to convert one yourself, as\nin `Number(typed) === 0`, so the conversion is written where you can see it."
        },
        {
          "p": "What does each comparison give? Pick one."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "\"7\" === 7",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "One side is text and the other is a number. `===` does not convert, so they are not equal.\n"
              },
              {
                "code": "\"7\" == 7",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`==` converts `\"7\"` to `7`, and then they match.\n"
              },
              {
                "code": "Number(\"7\") === 7",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you picked `false`, look at what `Number(\"7\")` produces before `===` runs.** It is the number `7`, so both sides are numbers.\n",
                "why": "You did the conversion yourself, so `===` compares two numbers.\n"
              }
            ]
          }
        },
        {
          "h": "Two values with special rules"
        },
        {
          "p": "**`null` and `undefined`.** These are JavaScript's two \"nothing here\" values;\nthe lesson after next is about them. `==` treats them as equal to each other,\nand to **nothing else**: `null == 0` is `false`, and so is `null == \"\"`.\nBecause of that rule, `x == null` is a handy test for \"is `x` either kind of\nnothing?\". It is the one place where most JavaScript programmers use `==` on\npurpose."
        },
        {
          "p": "**`NaN`.** `NaN` stands for \"not a number\". It is what you get from\n`Number(\"abc\")` or `0 / 0`. It is the one value that is not equal to itself,\nwith either operator. So comparing a value with `NaN` can never find it, and\nJavaScript gives you a test built for the job:"
        },
        {
          "code": "const n = Number(\"abc\");   // NaN\nn === NaN                  // false — even though n is NaN\nNumber.isNaN(n)            // true — the test that works",
          "lang": "js",
          "file": "nan.js"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "null == 0",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you picked `true` because both mean \"nothing\": `==` does not convert `null` to a number.** `null` is loosely equal to `undefined` and to nothing else.\n",
                "why": "`null` equals only `undefined` under `==`.\n"
              },
              {
                "code": "Number(\"ten\") === NaN",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`Number(\"ten\")` is `NaN`, and `NaN` is not equal to anything, itself included. `Number.isNaN(Number(\"ten\"))` is the test that says `true`.\n"
              }
            ]
          }
        },
        {
          "h": "A warning, for later"
        },
        {
          "p": "You will meet **arrays** — lists of values like `[1, 2]` — in the Arrays\nmodule. When you do, remember this line:"
        },
        {
          "code": "[1, 2] === [1, 2]    // false\n[1, 2] == [1, 2]     // false, too",
          "lang": "js",
          "file": "warning.js"
        },
        {
          "p": "Two lists that hold exactly the same things compare `false`, with both\noperators. Nothing went wrong. Each `[1, 2]` makes a separate list. Given two\nlists, both operators ask the third question, *are these the same list?*,\nrather than *do they hold the same things?* Why they work that way, and how to\nask whether two lists hold the same things, comes in the Values and references\nmodule. Until then, **do not use `==` or `===` to check whether two lists hold\nthe same things**: the answer will be `false` for any two separate lists."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "[3] === [3]",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you picked `true` because they hold the same thing: that is the trap this section warns about.** Two separately made lists are never `===`, whatever is in them.\n",
                "why": "Each `[3]` makes a separate list. Given two lists, `===` asks whether they are the same list, and they are not. The reason is taught in Values and references.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Deciding which comparison you mean",
            "body": [
              {
                "h": "Deciding which comparison you mean"
              },
              {
                "p": "Before you pick a comparison operator, work through three steps. The comments\nin the examples below name each step and work it through."
              },
              {
                "p": "**Worked example 1.** A quiz page asks \"What is 6 × 7?\". The user's answer\narrives as text; the right answer is the number `42`."
              },
              {
                "code": "const typed = \"42\";\nconst answer = 42;\n\n// name the types on each side\n//   typed is text; answer is a number. Different types.\n// decide whether any conversion is wanted\n//   yes: compare them as numbers, and show the conversion in the code\n// write the conversion, then compare strictly\nconst correct = Number(typed) === answer;   // true",
                "lang": "js",
                "file": "quiz.js"
              },
              {
                "p": "`typed == answer` would also give `true` here, and for `\" 42 \"` and `\"042\"` it\ngives the same answers as `Number(typed) === answer`, because `==` does the\nsame conversion. The difference is what a reader can see: nothing in\n`typed == answer` shows that a conversion happens, and nobody can tell whether\nit was meant. `Number(typed) === answer` shows the conversion in the code."
              },
              {
                "p": "**Worked example 2.** A sign-up page checks that the user typed their email\nthe same way twice."
              },
              {
                "code": "const first = \"ada@example.com\";\nconst second = \"Ada@example.com\";\n\n// name the types on each side\n//   both text. Same type.\n// decide whether any conversion is wanted\n//   no change of type, but capital letters should not matter in an email\n// write the conversion, then compare strictly\nconst same = first.toLowerCase() === second.toLowerCase();   // true",
                "lang": "js",
                "file": "confirm.js"
              },
              {
                "p": "The \"conversion\" in this example is not a change of type. Both values are\nalready text; `toLowerCase()` makes them comparable by removing the difference\nin capital letters. The step is the same as in example 1: do the conversion\nyourself, where the code shows it, then compare with `===`."
              },
              {
                "p": "**Now you.** A game stores a player's level as a number. A settings box gives\nthe level the player chose, as text."
              },
              {
                "code": "const current = 3;\nconst chosen = \"3\";\n\n// name the types on each side               ______\n// decide whether any conversion is wanted   ______\n// write the conversion, then compare strictly\nconst unchanged = ______;",
                "lang": "js",
                "file": "level.js"
              },
              {
                "p": "One answer: the types are a number and text; yes, you want to compare them as\nnumbers; so `const unchanged = current === Number(chosen);`."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "Languages differ on one thing here: **which question their plain `==`\nanswers.** The answer tells you whether `==`, the operator most people reach\nfor first, converts in that language."
            },
            {
              "table": {
                "head": [
                  "",
                  "`1 == \"1\"`",
                  "Converts before comparing?",
                  "The strict version"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "`true`",
                    "yes",
                    "`===`"
                  ],
                  [
                    "**Python**",
                    "`False`",
                    "no",
                    "`==` already is"
                  ],
                  [
                    "**C#**",
                    "won't compile",
                    "no — refuses to compare",
                    "`==` already is"
                  ]
                ]
              }
            },
            {
              "p": "Of these three languages, JavaScript is the odd one out: its shorter operator,\n`==`, converts, and the strict one needs an extra `=`. So when you meet a new\nlanguage, the useful question is not \"does it have `===`?\" It is **\"does its\n`==` convert first?\"**"
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "This course uses `===` by default, for the reason in *Two operators, two\nquestions*: `==` converts without showing it. `==` has one widely accepted use:\n`x == null` catches both `null` and `undefined` in one test. If you use `==`\nanywhere else, a comment saying why tells the next reader the conversion was\nintended."
              },
              {
                "code": "value === other          // your default\nNumber(typed) === 42     // convert yourself, then compare strictly\nvalue == null            // deliberate: null or undefined\nNumber.isNaN(value)      // the test built for NaN",
                "lang": "js"
              }
            ],
            "python": [
              {
                "p": "`==` is your default, and it does not convert between numbers and text."
              },
              {
                "code": "value == other           # your default — no converting\nint(typed) == 42         # convert yourself when the types differ\nvalue is None            # the usual way to test for None",
                "lang": "python"
              },
              {
                "p": "Python also has `is`, which asks a different question: whether two things\nare the same object. You will need that idea when you meet lists. For numbers\nand text, use `==`."
              }
            ],
            "csharp": [
              {
                "p": "`==` refuses to compare an `int` with a `string`, so the question \"would\nthese be equal after converting?\" never comes up. Convert first:"
              },
              {
                "code": "value == other               // same types only\nint.Parse(typed) == 42       // convert yourself, then compare\ndouble.IsNaN(value)          // the test built for NaN",
                "lang": "csharp"
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the four comparisons from the start of the lesson again, from memory.\nFor each one, say whether the operator converted before comparing. A\ncomparison you can now explain, and could not before, is something you\nlearned."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A page asks \"How many guests are coming with you?\". What the user typed\narrives as text in `typed`. A colleague wants a line that is `true` only when\nthe user answered zero, and suggests three:"
            },
            {
              "list": [
                "`typed == 0`",
                "`typed === \"0\"`",
                "`Number(typed) === 0`"
              ]
            },
            {
              "p": "Work out what each gives when the user typed `\"0\"`, when they typed nothing\n(`\"\"`), and when they typed `\" 0 \"` with spaces. Then pick one and say why —\nincluding what you want to happen for the empty box. The reasoning is the\nanswer, not the operator."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "`===` asks **same type and same value**, and never converts. `==` **converts first**, which is why `0 == \"\"` is `true`. This course uses `===` by default, and converts by hand when the types differ, so the conversion is visible.",
                "`null == undefined` is `true`, and neither is `==` to anything else. `NaN` is equal to nothing, itself included — test it with `Number.isNaN`.",
                "`[1, 2] === [1, 2]` is `false`. Until Values and references, do not use either operator to check whether two lists hold the same things; that module explains why."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Strict equality (===)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Strict_equality",
              "note": "the operator to use by default."
            },
            {
              "title": "Equality (==)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Equality",
              "note": "exactly what it converts, and when."
            },
            {
              "title": "Number.isNaN()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isNaN",
              "note": "the test for the one value `===` cannot find."
            }
          ]
        }
      ],
      "_source": "lessons/equality.md",
      "extra": {
        "id": "equality",
        "title": "Equality — going further",
        "assessed": false,
        "body": [
          {
            "block": "notice",
            "label": "Not assessed",
            "body": [
              {
                "p": "Nothing here is on a check and none of it is needed for outcome 1. It is here\nbecause these two come up every time the topic does. **If the three questions on\nthe main page are not yet solid, go back to them instead.**"
              }
            ]
          },
          {
            "h": "Why `NaN` is not equal to itself"
          },
          {
            "p": "This is not a JavaScript decision. `NaN` means \"a number-shaped answer that is\nnot a number\" — the result of `0/0`, or of parsing text that was never numeric.\nTwo computations can both fail that way for unrelated reasons, and IEEE 754, the\nfloating-point standard almost every language implements, says the sensible\nanswer to \"are these two failures the same number?\" is **no**."
          },
          {
            "p": "So every language you will use agrees here: Python, C#, Ruby, Java. When you\nmeet an inequality that holds in every language at once, it is usually the\nhardware standard talking rather than the language."
          },
          {
            "p": "The practical consequence is that you cannot test for it by comparison. Each\nlanguage gives you a predicate instead — `Number.isNaN` in JavaScript,\n`math.isnan` in Python, `double.IsNaN` in C# — and reaching for one is the\nsignal that you understood why `===` could not do it."
          },
          {
            "h": "Why `typeof null` is `\"object\"`"
          },
          {
            "p": "It is wrong, everyone agrees it is wrong, and it cannot be changed. In the first\nimplementation a value's type was read from a tag in its low bits; the object\ntag was `0`, and the null pointer was all zeroes, so `null` read as an object.\nA straightforward bug."
          },
          {
            "p": "The interesting part is why it is still here thirty years later. By the time it\nwas noticed, code existed that depended on the answer. Fixing it would have been\na correctness improvement that broke working programs — and a language with\nusers has to weigh those against each other, not just declare the bug fixed."
          },
          {
            "p": "That trade-off is the transferable part, and it is not peculiar to JavaScript.\nEvery language that reaches wide deployment accumulates decisions it would take\nback: things that are wrong, known to be wrong, and load-bearing. It is worth\nrecognising the shape, because it explains a lot of what looks like carelessness\nin mature languages — and it is a good argument for taking early design\ndecisions seriously in your own code, where the cost of a bad name or a wrong\ndefault compounds the same way on a smaller scale."
          },
          {
            "p": "The practical rule is one line: `typeof` cannot tell you whether something is\n`null`. Ask directly."
          },
          {
            "code": "value === null            // the only reliable test\ntypeof value === 'object' // true for null, and for every object",
            "lang": "js"
          }
        ]
      },
      "practice": {
        "id": "equality",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the comparison",
            "prompt": "Three comparisons. Pick the result of each.\n",
            "items": [
              {
                "id": "p1",
                "code": "\"2\" == 2",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`==` converts before comparing, so `\"2\"` becomes `2` and they match.\n"
              },
              {
                "id": "p2",
                "code": "\"2\" === 2",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`===` never converts, and text and a number are different types, so the result is `false`.\n"
              },
              {
                "id": "p3",
                "code": "0 == \"\"",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you picked `false`, ask what `==` turns `\"\"` into.** Empty text converts to the number `0`.\n",
                "why": "`\"\"` converts to `0`, and `0 == 0` is `true`. This is the conversion that lets an empty text box pass for a zero.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "null == undefined",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`==` treats these two as equal to each other and to nothing else.\n"
              },
              {
                "id": "p5",
                "code": "null == 0",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`null` is loosely equal only to `undefined`. It is never converted to a number by `==`.\n"
              },
              {
                "id": "p6",
                "code": "NaN == NaN",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`NaN` is equal to nothing, itself included, with either operator.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered false for `\"2\" == 2`",
                "say": "That is the answer `===` would give. Ask what `==` does first when the two sides are different types.\n"
              },
              {
                "when": "you answered true for `\"2\" === 2`",
                "say": "`===` asks two things: same type, and same value. Is text the same type as a number?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "What does == turn it into?",
            "prompt": "In each `==` comparison below, `==` converts any side that is not a number into a number, then compares. For the first two items, type the number the text becomes. For the rest, pick the result.\n",
            "items": [
              {
                "id": "t1",
                "question": "In `\"10\" == 10`, what number does `\"10\"` become before comparing? Type it.",
                "accept": [
                  "10"
                ],
                "why": "`\"10\"` becomes `10`. Then `10 == 10` is `true`.\n"
              },
              {
                "id": "t2",
                "question": "In `\" 10 \" == 10` — with spaces — what number does `\" 10 \"` become? Type it, or type `NaN`.",
                "accept": [
                  "10"
                ],
                "miss": "**If you typed `NaN`: the spaces at the ends are ignored.** Converting text to a number removes them first, just as `Number(\" 10 \")` does.\n",
                "why": "`\" 10 \"` becomes `10`, because spaces at either end are dropped. So `\" 10 \" == 10` is `true`.\n"
              },
              {
                "id": "t3",
                "code": "\"ten\" == 10",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`\"ten\"` converts to `NaN`, and `NaN` is not equal to anything, so the result is `false`.\n"
              },
              {
                "id": "t4",
                "code": "false == 0",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`==` converts `false` to `0` (and would convert `true` to `1`). Then `0 == 0` is `true`.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "\"1\" == true",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`\"1\"` converts to `1`, and `true` converts to `1`, so `1 == 1` is `true`. `==` is not checking whether `\"1\"` means yes; it is comparing two numbers.\n"
              },
              {
                "id": "t6",
                "code": "\"010\" == 10",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`==` converts `\"010\"` to the number `10`; the leading zero disappears in the conversion. Then `10 == 10` is `true`.\n"
              },
              {
                "id": "t7",
                "code": "\"010\" === \"10\"",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "Both sides are text, with different characters. `===` does not convert, so the leading zero stays, and the two are not equal.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered NaN for `\" 10 \" == 10`",
                "say": "Try `Number(\" 10 \")` in your head, or in the console. What happens to spaces at the ends?\n"
              },
              {
                "when": "you answered true for \"ten\" == 10",
                "say": "What does `Number(\"ten\")` give? Now ask whether that value is equal to anything.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "A test that can never be true",
            "prompt": "`isNotANumber` should return `true` when its value is `NaN` and `false` otherwise — including for text, which is not `NaN` even when it does not look like a number. Right now it returns `false` for everything. Change only what comes after `return` — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why can `value === NaN` never be `true`?\n",
            "exportName": "isNotANumber",
            "filename": "is-not-a-number.js",
            "starter": "function isNotANumber(value) {\n  return value === NaN;\n}\n",
            "tests": [
              {
                "label": "isNotANumber(NaN) → true",
                "body": "return subject(NaN) === true;"
              },
              {
                "label": "isNotANumber(Number(\"abc\")) → true",
                "body": "return subject(Number('abc')) === true;"
              },
              {
                "label": "isNotANumber(5) → false",
                "body": "return subject(5) === false;"
              },
              {
                "label": "isNotANumber(0) → false",
                "body": "return subject(0) === false;"
              },
              {
                "label": "isNotANumber(\"abc\") → false — it is text, not NaN",
                "body": "return subject('abc') === false;"
              }
            ],
            "hints": [
              {
                "when": "NaN still gives false",
                "say": "`NaN` is not equal to anything, itself included, so comparing a value with `NaN` using `===` or `==` can never find it. The lesson section *Two values with special rules* names the test built for this job.\n"
              },
              {
                "when": "\"abc\" gives true",
                "say": "You may have used the older `isNaN(...)`, which converts its value to a number first, and `\"abc\"` converts to `NaN`. The test from the lesson has `Number.` in front and does not convert.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "When == says yes too easily",
            "prompt": "A door keypad stores its code as a number, `pin`. What the user typed arrives as text, `typed`. Return `true` only when they typed exactly the code — the same digits, nothing added. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why does `typed == pin` let `\"01234\"` in?\n",
            "exportName": "matchesPin",
            "filename": "matches-pin.js",
            "starter": "function matchesPin(typed, pin) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "matchesPin(\"1234\", 1234) → true",
                "body": "return subject('1234', 1234) === true;"
              },
              {
                "label": "matchesPin(\"4321\", 1234) → false",
                "body": "return subject('4321', 1234) === false;"
              },
              {
                "label": "an extra leading zero is not the code: matchesPin(\"01234\", 1234) → false",
                "body": "return subject('01234', 1234) === false;"
              },
              {
                "label": "a space is not the code: matchesPin(\" 1234\", 1234) → false",
                "body": "return subject(' 1234', 1234) === false;"
              },
              {
                "label": "nothing typed is not the code: matchesPin(\"\", 0) → false",
                "body": "return subject('', 0) === false;"
              }
            ],
            "hints": [
              {
                "when": "\"01234\" or \" 1234\" gets in",
                "say": "Converting the typed text to a number throws away leading zeros and spaces, and both `==` and `Number()` do that conversion. One way that keeps them is to convert in the other direction: turn the number into text, and compare text with text.\n"
              },
              {
                "when": "the empty box gets in when the code is 0",
                "say": "`\"\"` converts to `0`. Any comparison that turns the typed text into a number will let an empty box match a zero.\n"
              },
              {
                "when": "\"1234\" is rejected",
                "say": "`typed === pin` compares text with a number, and `===` does not convert, so for text and a number it is always `false`. Convert one side yourself — which one keeps every character the user typed?\n"
              }
            ]
          }
        ]
      }
    },
    "truthiness": {
      "id": "truthiness",
      "title": "Truthiness",
      "kind": "read",
      "outcome": 1,
      "shape": "conceptual",
      "why": "Outcome 1 names truthiness, and every `if` a student writes in Branching consults it. Its answers differ by language, so outcome 1's comparison is shown in a few lines (`program/programming-sequence.md`). Predicting which values pass an `if` moved here from `flow.conditions` (D-018). It sits in Values, types and operators, before `if` exists (D-043), so it is taught through `Boolean(x)` and `!x`; arrays and objects get one line and are met in their own modules (D-045).",
      "lede": "Sometimes JavaScript needs a yes or no, and what it has is a number, or some text. It then has to decide whether that value counts as true. The rule JavaScript uses for that decision is short. It is not the rule `==` uses when you compare the same value to `false`.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Operators · Equality: == versus === · Conversion — when a value does not fit"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Truthy** — counts as true when a yes or no is needed. **Falsy** — counts as false. **`Boolean(x)`** — asks which one `x` is, and returns `true` or `false`.",
          "entries": [
            "**Truthy** — counts as true when a yes or no is needed.",
            "**Falsy** — counts as false.",
            "**`Boolean(x)`** — asks which one `x` is, and returns `true` or `false`."
          ]
        }
      ],
      "concepts": [
        "types.truthiness"
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four lines. `Boolean(x)` is a built-in function: give it any value and it\nanswers `true` or `false` — whether that value counts as true. Pick what each\nline produces. Guess if you have an idea; press **I don't know** if you have\nnone."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "Boolean(\"0\")",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you picked `false` because `\"0\"` looks like zero: `\"0\"` is a *string*, and the only string that counts as false is the empty string, `\"\"`.** The characters inside a string are never looked at. `\"0\"`, `\"false\"` and `\"no\"` all count as true.\n",
                          "why": "Every string except the empty string counts as true. It does not matter what the characters are.\n"
                        },
                        {
                          "code": "\"0\" == false",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you reasoned \"`\"0\"` counts as true, so this must be `false`\": that is a reasonable assumption, and it is wrong.** \"Does `\"0\"` count as true?\" and \"Is `\"0\"` equal to `false` under `==`?\" are two different questions, answered by two different rules. This line is where you can watch the two rules disagree.\n",
                          "why": "`true` as well, but by a different rule. `==` converted both sides to numbers, and `0` equals `0`. So `\"0\"` counts as true *and* is loosely equal to `false`.\n"
                        },
                        {
                          "code": "Boolean(null)",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "false"
                          ],
                          "why": "`null` is on the short list of values that count as false.\n"
                        },
                        {
                          "code": "null == false",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "false"
                          ],
                          "miss": "**If you picked `true` because `null` counts as false: this is the same trap as `\"0\" == false`, the other way round.** `==` treats `null` as equal to `undefined` and nothing else. It never converts `null` to a number the way it converts `\"0\"`.\n",
                          "why": "`false`. `null` counts as false, but it is not loosely equal to `false`. The first pair was the opposite: `\"0\"` counts as true, but it is loosely equal to `false`.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "bool(\"0\")",
                          "choices": [
                            "True",
                            "False",
                            "error"
                          ],
                          "accept": [
                            "True"
                          ],
                          "why": "Any non-empty string is true, whatever it contains. This much matches JavaScript.\n"
                        },
                        {
                          "code": "bool(0.0)",
                          "choices": [
                            "True",
                            "False",
                            "error"
                          ],
                          "accept": [
                            "False"
                          ],
                          "why": "Zero is false, whole or decimal.\n"
                        },
                        {
                          "code": "bool(\"\")",
                          "choices": [
                            "True",
                            "False",
                            "error"
                          ],
                          "accept": [
                            "False"
                          ],
                          "why": "The empty string is false, as it is in almost every language that lets a non-boolean value count as true or false.\n"
                        },
                        {
                          "code": "\"0\" == False",
                          "choices": [
                            "True",
                            "False",
                            "error"
                          ],
                          "accept": [
                            "False"
                          ],
                          "why": "Python's `==` does not convert text, so a string is never equal to a boolean. The JavaScript surprise, a string that counts as true and is still `== false`, cannot happen in Python.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "if (0) { }",
                          "choices": [
                            "true",
                            "false",
                            "error"
                          ],
                          "accept": [
                            "error"
                          ],
                          "why": "C# has no truthiness at all. A condition must be a `bool` and nothing else, so `if (0)` is rejected before the program runs.\n"
                        },
                        {
                          "code": "if (\"\") { }",
                          "choices": [
                            "true",
                            "false",
                            "error"
                          ],
                          "accept": [
                            "error"
                          ],
                          "why": "Same reason: `\"\"` is a string, not a `bool`. In C#, the only value a condition treats as false is `false` itself.\n"
                        },
                        {
                          "code": "Convert.ToBoolean(0)",
                          "choices": [
                            "true",
                            "false",
                            "error"
                          ],
                          "accept": [
                            "false"
                          ],
                          "why": "You can ask for the conversion yourself, with `Convert.ToBoolean`. What C# refuses is to convert a value to a `bool` without being asked.\n"
                        },
                        {
                          "code": "\"\" == false",
                          "choices": [
                            "true",
                            "false",
                            "error"
                          ],
                          "accept": [
                            "error"
                          ],
                          "why": "C# does not define `==` between a `string` and a `bool`, so the line is rejected before the program runs, and the comparison never happens.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can list the values JavaScript treats as false, and check rather than guess for the ones I am unsure of",
                "I can predict whether a value counts as true, including the surprising ones",
                "I can explain why a value can count as true and still be `== false`"
              ]
            }
          ]
        },
        {
          "h": "When a value has to be yes or no"
        },
        {
          "p": "You met the two **boolean** values, `true` and `false`, in *Comparing and\ncombining*, along with `!`, the **logical NOT** operator, which turns `true`\ninto `false` and `false` into `true`."
        },
        {
          "p": "Sometimes JavaScript needs a boolean and is handed something else — a number,\nor a piece of text. Put `!` in front of `\"hello\"`, and JavaScript has to\ndecide: does `\"hello\"` count as true? The `if` statement, which you will meet\nin the Branching module, runs some code only when a value counts as true,\nso it makes the same decision every time it runs."
        },
        {
          "p": "The words for this: a value that counts as true is **truthy**; a value that\ncounts as false is **falsy**. Here are two ways to ask JavaScript for its\ndecision:"
        },
        {
          "code": "Boolean(\"hello\")   // true  — \"hello\" is truthy\n!\"hello\"           // false — the opposite of truthy\n!!\"hello\"          // true  — flipped twice: the same answer as Boolean()",
          "lang": "js",
          "file": "ask.js"
        },
        {
          "p": "`Boolean(x)` is the clearest way to ask. In code you will see `!x` more\noften. `!x` means \"is `x` falsy?\"."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "!0",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you picked `false`: `!` gives the *opposite* of whether the value is truthy.** `0` counts as false, so `!0` is `true`.\n",
                "why": "`0` is falsy, and `!` flips that to `true`.\n"
              },
              {
                "code": "Boolean(-1)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`-1` is not on the falsy list. Being negative does not make a number falsy.\n"
              }
            ]
          }
        },
        {
          "h": "The list is short, and it is arbitrary"
        },
        {
          "p": "Here is JavaScript's whole list. These values are falsy; **every other value\nin JavaScript is truthy.**"
        },
        {
          "code": "false\n0, -0, 0n          // the zeroes (0n is a rarely used kind of whole number)\n\"\"                 // the empty string only\nnull\nundefined\nNaN",
          "lang": "js",
          "file": "falsy.js"
        },
        {
          "p": "That list is worth memorising, precisely because it is short. Every value not\non it is truthy, including these four, which catch people out:"
        },
        {
          "code": "Boolean(\" \")       // true  — a space is a character\nBoolean(\"false\")   // true  — five characters, so not empty\nBoolean(\"0\")       // true  — one character\nBoolean(-1)        // true  — -1 is not on the list",
          "lang": "js",
          "file": "truthy.js"
        },
        {
          "p": "Lists and records — **arrays and objects**, which you will meet later — are\nalways truthy in JavaScript, even when they are empty."
        },
        {
          "p": "No principle connects the values on this list, so you cannot reason your way\nto whether a value is on it. The list is a set of decisions the designers of\nJavaScript made. Learn the list, and check it when you are unsure."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "Boolean(\"\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "The empty string is the one falsy string.\n"
              },
              {
                "code": "Boolean(\"null\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you picked `false` — look for the quotes.** `\"null\"` is four characters of text, not the value `null`.\n",
                "why": "A non-empty string. What it spells does not matter.\n"
              },
              {
                "code": "Boolean(NaN)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`NaN` is on the list.\n"
              }
            ]
          }
        },
        {
          "h": "Two questions that look like one"
        },
        {
          "p": "\"Is this value false?\" can be asked two ways in JavaScript, and they use\ndifferent rules."
        },
        {
          "list": [
            "**Is it falsy?** Asked by `Boolean(x)`, by `!x`, and by `if`. The rule is the **list**: no converting, no comparing, just \"is it one of these values?\"",
            "**Is it loosely equal to `false`?** Asked by `x == false`. The rule is the **conversion** `==` does, from *Equality: == versus ===*: when one side is `true` or `false`, `==` turns it into a number (`1` or `0`) and compares again. Text on the other side is then turned into a number too; `null` and `undefined` are never converted, and equal only each other."
          ]
        },
        {
          "p": "The two rules agree most of the time. Here are the two pairs from the opening\nquestions, where they disagree:"
        },
        {
          "code": "Boolean(\"0\")    // true  — \"0\" is a non-empty string, not on the list\n\"0\" == false    // true  — both sides became 0, and 0 equals 0\n\nBoolean(null)   // false — null is on the list\nnull == false   // false — == never converts null to a number",
          "lang": "js",
          "file": "two-rules.js"
        },
        {
          "block": "notice",
          "label": "The rule that keeps you out of trouble",
          "body": [
            {
              "p": "`x == false` and `x == true` are legal JavaScript, but this course does not\nwrite them, because they use the conversion rule while looking as if they ask\nwhether `x` is truthy. To know whether a value is truthy, ask `Boolean(x)` or\n`!x`. To know whether a value is exactly `false`, write `x === false`."
            }
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`\" \" == false` is `true`. Which rule produced that answer?",
                "choices": [
                  "The falsy list",
                  "The conversion == does"
                ],
                "accept": [
                  "The conversion == does"
                ],
                "miss": "**If you picked the list: the list says `\" \"` is truthy,** which would make `\" \" == false` come out `false`. `==` converted the space-only text to `0`.\n",
                "why": "Conversion. Text with only spaces becomes `0`, and `false` becomes `0`. The list would have said the opposite.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Deciding whether a value counts as true",
            "body": [
              {
                "h": "Deciding whether a value counts as true"
              },
              {
                "p": "Both worked examples, and the one you finish, use the same three steps."
              },
              {
                "p": "**Worked example 1.** A form stores what the user typed in a \"quantity\" box.\nThey typed a zero."
              },
              {
                "code": "const typed = \"0\";\n\n// name the value's type\n//   a string: it came from a box, so it is text.\n// check it against the falsy list\n//   the only falsy string is \"\". \"0\" is not \"\".\n// say what that means here\n//   Boolean(typed) is true: the box is not empty.",
                "lang": "js",
                "file": "quantity.js"
              },
              {
                "p": "**Worked example 2.** A game stores the number of lives left as a number. The\nplayer has none."
              },
              {
                "code": "const lives = 0;\nconst livesMissing = !lives;\n\n// name the value's type\n//   a number.\n// check it against the falsy list\n//   0 is on it. So lives is falsy, and !lives is true.\n// say what that means here\n//   livesMissing is true — but lives is not missing. It is zero.",
                "lang": "js",
                "file": "lives.js"
              },
              {
                "p": "Worked example 2 shows a common bug. The author wanted to ask \"is there no\nvalue?\", but `!lives` asks \"is `lives` falsy?\", and zero is falsy. The next\nlesson, *When there is nothing there*, shows how to ask \"is there no value?\"\ndirectly."
              },
              {
                "p": "**Now you.** A sign-up form stores a nickname. The user typed a single space."
              },
              {
                "code": "const nickname = \" \";\n\n// name the value's type                ______\n// check it against the falsy list      ______\n// say what that means here             Boolean(nickname) is ______",
                "lang": "js",
                "file": "nickname.js"
              },
              {
                "p": "A string; not the empty string; so `Boolean(nickname)` is `true`, even though\nnobody would call a single space a nickname."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis here is not \"which values are falsy\". It is one level up: **does the\nlanguage have truthiness at all?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Does a condition take a non-boolean?",
                  "Empty string",
                  "Zero",
                  "Not-a-number"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "yes",
                    "false",
                    "false",
                    "**false**"
                  ],
                  [
                    "**Python**",
                    "yes",
                    "false",
                    "false",
                    "**true**"
                  ],
                  [
                    "**C#**",
                    "**no — it will not compile**",
                    "—",
                    "—",
                    "—"
                  ]
                ]
              }
            },
            {
              "p": "Truthiness is a **feature a language chooses**, not a fact about programming.\nC# declines it entirely: a condition must be `true` or `false`, and `if (0)` is\nan error before the program runs. And the languages that do have truthiness do\nnot share one list — `NaN` is falsy in JavaScript and truthy in Python. So a\nlist of falsy values learned in one language does not carry over to another.\nWhat carries over is knowing to ask whether a language has truthiness, and\nlooking up its list."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "`Boolean(x)` and `!x` ask the falsy list. When you mean something more\nspecific, write the specific test:"
              },
              {
                "code": "Boolean(typed)             // \"is the box non-empty?\" — \" \" and \"0\" pass\ntyped.trim() !== \"\"        // \"did they type anything but spaces?\"\ncount !== undefined        // \"was there a value?\" — 0 passes",
                "lang": "js"
              }
            ],
            "python": [
              {
                "p": "`bool(x)` asks Python's list. Most of it matches JavaScript's — `0`, `\"\"` and\n`None` are false — but not all: `bool(float(\"nan\"))` is `True`."
              },
              {
                "code": "bool(\"0\")        # True\nbool(\"\")         # False\nvalue is not None  # \"was there a value?\" — not the same question",
                "lang": "python"
              }
            ],
            "csharp": [
              {
                "p": "C# has no truthiness to learn. A condition must be a `bool`, so each test has\nto state what it checks:"
              },
              {
                "code": "typed != \"\"               // explicit, and the only option\n!string.IsNullOrEmpty(name)",
                "lang": "csharp"
              },
              {
                "p": "The cost is more typing; the benefit is that a number can never silently stand\nin for a yes or no."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say, for each, **which rule**\nproduced the answer — the falsy list, or the conversion `==` does. Naming the\nrule behind each answer is what this lesson set out to teach."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A sign-up page stores three answers:"
            },
            {
              "list": [
                "`nickname` — text; the user may leave it empty on purpose.",
                "`age` — the text they typed, such as `\"19\"`.",
                "`guests` — a number; `0` is a real answer."
              ]
            },
            {
              "p": "A colleague writes `const hasNickname = Boolean(nickname);`,\n`const hasAge = Boolean(age);` and `const hasGuests = Boolean(guests);`."
            },
            {
              "p": "For each, decide whether `Boolean(...)` asks the question its author meant.\nSay which values decide it — the falsy ones, and the surprising truthy ones\nsuch as `\" \"` and `\"0\"`. Then the colleague suggests `guests == false` as a way\nto spot \"no guests\". Say what it does with `0`, and whether you would accept\nit. Your reasoning is the answer, not the operator you choose."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A few values are **falsy**: `false`, `0`, `\"\"`, `null`, `undefined`, `NaN` (and `-0`, `0n`). **Everything else is truthy** — `\"0\"`, `\" \"` and `-1` included.",
                "`Boolean(x)` and `!x` ask the **list**. `x == false` asks a **different rule** — conversion — which is why `\"0\"` is truthy and `\"0\" == false`.",
                "This course does not write `== false`: it is legal, but it uses conversion, not the list. Write `!x` for \"is it falsy?\" and `=== false` for \"is it exactly `false`?\"."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Falsy",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Falsy",
              "note": "the complete list, and shorter than this lesson. If you read one thing, read this."
            },
            {
              "title": "Truthy",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Truthy",
              "note": "the other half, which is \"everything else\"."
            }
          ]
        }
      ],
      "_source": "lessons/truthiness.md",
      "practice": {
        "id": "truthiness",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the answer",
            "prompt": "Three quick ones. Pick an answer for each.\n",
            "items": [
              {
                "id": "p1",
                "code": "Boolean(\"false\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`\"false\"` is a non-empty string, so it is not on the falsy list. The characters are never looked at: `\"false\"` is exactly as truthy as `\"hello\"`.\n"
              },
              {
                "id": "p2",
                "code": "\" \" == false",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`, even though `\" \"` is *truthy*. `==` does not ask the falsy list: it converted the space-only text to `0`, and `false` to `0`. A different rule gives the opposite answer.\n"
              },
              {
                "id": "p3",
                "question": "To decide what `Boolean(x)` gives, what happens to `x`?",
                "choices": [
                  "It is converted to a number",
                  "It is compared with false",
                  "It is looked up in a list"
                ],
                "accept": [
                  "It is looked up in a list"
                ],
                "why": "Looked up. `Boolean(x)` checks whether `x` is one of the falsy values, with no converting and no comparing. That check is the only step.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "Boolean(\" \")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "Only the *empty* string is falsy. A string holding one space has a character in it, so it is truthy.\n"
              },
              {
                "id": "p5",
                "code": "Boolean(NaN)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`NaN` is on the list. It is also the only value not equal to itself.\n"
              },
              {
                "id": "p6",
                "code": "!\"0\"",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`\"0\"` is truthy, so `!` flips it to `false`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered false for the first",
                "say": "You read what the characters say. JavaScript's truthiness rule never looks at them: it asks only whether the value is on the falsy list, and no non-empty string is.\n"
              },
              {
                "when": "you answered false for the second",
                "say": "You answered the truthiness question, and `\" \"` is truthy. But `==` does not ask that question — it converts both sides first. Re-read \"Two questions that look like one\".\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Put seven values through Boolean",
            "prompt": "Answer the questions about this program first, then run it and compare.\n",
            "filename": "which.js",
            "code": "console.log(Boolean(0));\nconsole.log(Boolean(\"0\"));\nconsole.log(Boolean(\" \"));\nconsole.log(Boolean(-1));\nconsole.log(Boolean(NaN));\nconsole.log(Boolean(\"\"));\nconsole.log(!\"hello\");\n",
            "items": [
              {
                "id": "r1",
                "question": "Line 2, `Boolean(\"0\")` — what does it print?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`\"0\"` is a string with a character in it. Not on the list — the digit inside is never looked at.\n"
              },
              {
                "id": "r2",
                "question": "Line 4, `Boolean(-1)` — what does it print?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`-1` is not on the falsy list. Being negative does not make a number falsy.\n"
              },
              {
                "id": "r3",
                "question": "How many of the seven lines print `false`? Type a number.",
                "accept": [
                  "4",
                  "four"
                ],
                "why": "Four: `0`, `NaN`, `\"\"`, and `!\"hello\"`. `!\"hello\"` prints `false` because `\"hello\"` is truthy and `!` flips that to `false`.\n"
              }
            ],
            "hints": [
              {
                "when": "you counted 3",
                "say": "Did you count the last line, `!\"hello\"`? `!` gives `true` for a falsy value and `false` for a truthy one.\n"
              },
              {
                "when": "you said \"0\" prints false",
                "say": "Ask what kind of value `\"0\"` is before you think about what it says. Which strings are on the list?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Two rules, one value",
            "prompt": "`Boolean(x)` asks the list; `==` converts. Pick each result, and for the last one pick the rule.\n",
            "items": [
              {
                "id": "t1",
                "code": "Boolean(\"0\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`\"0\"` is a one-character string. It is not on the falsy list, so it is truthy.\n"
              },
              {
                "id": "t2",
                "code": "\"0\" == false",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`==` converts both sides to numbers: `\"0\"` becomes `0`, `false` becomes `0`. Truthy *and* loosely equal to `false`.\n"
              },
              {
                "id": "t3",
                "code": "undefined == false",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`undefined` is falsy, but `==` never converts `undefined` to a number: `undefined` is loosely equal to `null` and nothing else.\n"
              },
              {
                "id": "t4",
                "question": "Which rule produced the answer to `\"0\" == false`?",
                "choices": [
                  "The list",
                  "The conversion == does"
                ],
                "accept": [
                  "The conversion == does"
                ],
                "why": "Conversion. The falsy list says `\"0\"` is truthy, which is the opposite verdict. That is why this course does not use `== false` to ask \"is this falsy?\"\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "\"1\" == true",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "Conversion again: `\"1\"` becomes `1`, `true` becomes `1`. The falsy list plays no part.\n"
              },
              {
                "id": "t6",
                "code": "\"2\" == true",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`true` becomes `1`, `\"2\"` becomes `2`, and `2` is not `1`. Yet `Boolean(\"2\")` is `true`. Here the two rules disagree again.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered false for t2",
                "say": "You asked the falsy list. `==` asks something else: it converts both sides to numbers, then compares them. What number is `\"0\"`?\n"
              },
              {
                "when": "you answered true for t3",
                "say": "`undefined` is falsy — but `==` is not asking about falsiness. It has a special rule for `null` and `undefined`. What are they loosely equal to?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Say what you mean, not what the list says",
            "prompt": "A thermometer sends readings. `0` is a real reading; `null` and `undefined` mean the sensor was offline. `wasTaken` should be `true` for any real reading and `false` when the sensor was offline. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why would `return Boolean(reading);` be wrong here?\n",
            "exportName": "wasTaken",
            "filename": "readings.js",
            "starter": "// true for any reading actually taken — including 0.\n// false when the sensor was offline: null or undefined.\nfunction wasTaken(reading) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "wasTaken(3) → true",
                "body": "return subject(3) === true;"
              },
              {
                "label": "0 is a reading: wasTaken(0) → true",
                "body": "return subject(0) === true;"
              },
              {
                "label": "wasTaken(null) → false",
                "body": "return subject(null) === false;"
              },
              {
                "label": "undefined is offline too: wasTaken(undefined) → false",
                "body": "return subject(undefined) === false;"
              },
              {
                "label": "negative readings count: wasTaken(-4) → true",
                "body": "return subject(-4) === true;"
              }
            ],
            "hints": [
              {
                "when": "0 comes back false",
                "say": "Your expression asks the falsy list, and `0` is on it. You need an expression that names the two values that mean \"offline\", and nothing else. `!==` and `&&` from *Comparing and combining* are enough.\n"
              },
              {
                "when": "undefined comes back true",
                "say": "Your expression checks for `null` only. Two values mean \"offline\" here.\n"
              },
              {
                "when": "every test fails",
                "say": "Check that your line starts with `return`. Without it the function works out the answer and returns `undefined`.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "One more value to turn away",
            "prompt": "The sensor now sends `NaN` when a reading fails. Change `wasTaken` so `NaN` gives `false` as well — and `0` still gives `true`. Change only what comes after `return` — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why can `reading !== NaN` not do this?\n",
            "exportName": "wasTaken",
            "filename": "readings.js",
            "starter": "// true for any reading actually taken — including 0.\n// false when the sensor was offline: null or undefined.\nfunction wasTaken(reading) {\n  return reading !== null && reading !== undefined;\n}\n",
            "tests": [
              {
                "label": "NaN is a failed reading: wasTaken(NaN) → false",
                "body": "return subject(NaN) === false;"
              },
              {
                "label": "0 is still a reading: wasTaken(0) → true",
                "body": "return subject(0) === true;"
              },
              {
                "label": "null and undefined still give false",
                "body": "return subject(null) === false && subject(undefined) === false;"
              },
              {
                "label": "wasTaken(12) → true",
                "body": "return subject(12) === true;"
              }
            ],
            "hints": [
              {
                "when": "NaN still gives true",
                "say": "If you wrote `reading !== NaN`: `NaN` is the one value not equal to itself, so `reading !== NaN` is `true` for every value, `NaN` included. The equality lesson named a test made for checking whether a value is `NaN`.\n"
              },
              {
                "when": "you checked typeof and NaN got through",
                "say": "`typeof NaN` is `\"number\"`: despite its name, \"not a number\" is a value of the number type. Test for `NaN` itself.\n"
              },
              {
                "when": "0 now gives false",
                "say": "`Boolean(reading)` would turn away `NaN` — and `0` with it, because both are on the falsy list. Keep the two tests you already have, for `null` and `undefined`, and add a third that catches `NaN` alone.\n"
              }
            ]
          }
        ]
      }
    },
    "absence": {
      "id": "absence",
      "title": "When there is nothing there",
      "kind": "read",
      "outcome": 1,
      "shape": "conceptual",
      "why": "Outcome 1's checklist asks a student to say how their language represents \"nothing\" and to guard a value that might be missing. `types.nullability` stayed a written CS 140 concept when tracking absence in the type moved to CS 240 (D-019). This is pass 1 (D-045): `null` and `undefined` as values, met through variables and primitives. Missing properties and `?.` are pass 2, in the Objects module.",
      "concepts": [
        "types.nullability"
      ],
      "lede": "Sometimes a value you expected is not there — a variable nobody filled in, an answer the user skipped. JavaScript has two values that say so. Holding one of them in a variable is never an error; using it as if a real value were there usually is. JavaScript does not check this for you, so guarding against it is your job.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Truthiness · Equality: == versus === · When it breaks: reading errors"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Absent** — a value that is `null` or `undefined`: there is nothing there. **Guard** — a check that runs before a value is used, and decides what happens when it is absent.",
          "entries": [
            "**Absent** — a value that is `null` or `undefined`: there is nothing there.",
            "**Guard** — a check that runs before a value is used, and decides what happens when it is absent."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four lines. In each, the code after the last semicolon is the part to judge.\nType what that part produces — a value, or `error` if you think it stops the\nprogram — or pick an answer where there are buttons. Guess if you have an\nidea; press **I don't know** if you have none."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "let email;   email",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you answered `error` — a variable with no value is allowed.** It holds `undefined`, JavaScript's \"nothing was ever put here\", and the program carries on.\n",
                          "why": "`undefined`. `email` was declared and never given a value.\n"
                        },
                        {
                          "code": "let email;   email.length",
                          "accept": [
                            "error",
                            "typeerror",
                            "type error",
                            "throws",
                            "it throws",
                            "an error"
                          ],
                          "miss": "**If you answered `undefined` or `0` — holding nothing is fine; using it is not.** `.length` asks `undefined` how long it is, and `undefined` has no length to give.\n",
                          "why": "A TypeError: `Cannot read properties of undefined (reading 'length')`. `let email;` was harmless; using what `email` held was not.\n"
                        },
                        {
                          "code": "null === undefined",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "false"
                          ],
                          "miss": "**If you picked `true` because both mean \"nothing\" — they mean it as two different values.** `===` does not convert, and they are not the same value.\n",
                          "why": "`false`. JavaScript has **two** absences, and they are different values.\n"
                        },
                        {
                          "code": "const count = 0;   !count",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you picked `false` because the count is there — `!` does not ask \"is it there?\".** It asks \"is it falsy?\", and `0` is on the falsy list alongside `null` and `undefined`.\n",
                          "why": "`true`. If `!count` was meant to mean \"the count is missing\", it just called a real zero missing.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "email = None;   email",
                          "accept": [
                            "none"
                          ],
                          "why": "`None` — Python's one way to say \"nothing here\". In Python a variable must be given a value before it is used, so this `None` is one someone wrote.\n"
                        },
                        {
                          "code": "email = None;   len(email)",
                          "accept": [
                            "error",
                            "typeerror",
                            "type error",
                            "throws",
                            "an error"
                          ],
                          "why": "A TypeError: `object of type 'NoneType' has no len()`. Holding `None` is fine; using it is not.\n"
                        },
                        {
                          "code": "None == 0",
                          "choices": [
                            "True",
                            "False"
                          ],
                          "accept": [
                            "False"
                          ],
                          "why": "`None` is equal only to itself.\n"
                        },
                        {
                          "code": "count = 0;   not count",
                          "choices": [
                            "True",
                            "False"
                          ],
                          "accept": [
                            "True"
                          ],
                          "why": "`0` is falsy in Python too, so `not count` calls a real zero missing.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say how my language represents \"nothing\", and guard a value that might be missing before I use it",
                "I can say which of my language's absences a value is, and where it came from",
                "I can write a guard that catches a missing value and lets `0` and `\"\"` through"
              ]
            }
          ]
        },
        {
          "h": "Two ways to say nothing"
        },
        {
          "p": "JavaScript has two values that mean \"there is nothing here\". They are not two\nspellings of one idea; each has its own job."
        },
        {
          "list": [
            "**`undefined`** is what JavaScript gives you when nothing was ever put there. You never have to write it — it arrives on its own.",
            "**`null`** is a value somebody **wrote**, on purpose, to say \"there is none\". The language never fills a gap with `null` the way it fills one with `undefined`: `null` comes from code that returns it on purpose, either code you wrote or a function the runtime supplies (`prompt()` returns `null` when the person cancels)."
          ]
        },
        {
          "p": "Here is where each one comes from, using only the parts of JavaScript this\ncourse has covered so far."
        },
        {
          "table": {
            "head": [
              "Where it came from",
              "What you get"
            ],
            "rows": [
              [
                "A variable declared with no value — `let email;`",
                "`undefined`"
              ],
              [
                "A character past the end of a string — `\"cat\"[5]`",
                "`undefined`"
              ],
              [
                "A value someone wrote to mean \"none\" — `let middleName = null;`",
                "`null`"
              ],
              [
                "A value set back to \"none\" after it had one — `nickname = null;`",
                "`null`"
              ],
              [
                "`\"cat\".indexOf(\"z\")` — \"where does `z` appear?\"",
                "`-1` — **not an absence at all**"
              ]
            ]
          }
        },
        {
          "p": "The last row, `indexOf`, is the odd one out. `indexOf` says \"not found\" with a\nnumber, `-1`, so none of the guards in this lesson will catch it: `-1` is not\n`null`, not `undefined`, and not even falsy. How a built-in function says\n\"nothing\" is a choice its authors made, and the only way to know which value it\nuses is to look the function up."
        },
        {
          "p": "`undefined` turns up in many more places once you have more of the language: a\nmissing property of an object, a function that returns nothing, an argument\nnobody passed. The module that teaches each of those shows where `undefined`\ncomes from there. Missing properties, and the `?.` operator that guards them,\ncome in the Objects module."
        },
        {
          "p": "What does each produce? Type the value."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "let score;   score",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined` — declared, never given a value.\n"
              },
              {
                "code": "let nickname = \"Al\";   nickname = null;   nickname",
                "accept": [
                  "null"
                ],
                "miss": "**If you answered `undefined` — this one was written on purpose.** The nickname had a value, and the line `nickname = null` cleared it deliberately.\n",
                "why": "`null`. Someone decided the nickname is now \"none\", and wrote it.\n"
              },
              {
                "code": "typeof undefined",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "`\"undefined\"`. (And `typeof null` is `\"object\"` — the known mistake in JavaScript from *Where the type actually lives*.)\n"
              }
            ]
          }
        },
        {
          "h": "Holding nothing is fine; using it is not"
        },
        {
          "p": "In JavaScript, having an absent value in a variable never fails. The trouble\ncomes when code **uses** that value as if something were there."
        },
        {
          "table": {
            "head": [
              "What you do with an absent value",
              "What happens"
            ],
            "rows": [
              [
                "Ask it for something — `x.length`, `x.toUpperCase()`",
                "**TypeError** — `Cannot read properties of undefined`"
              ],
              [
                "Do arithmetic — `x + 1`",
                "`NaN`, silently"
              ],
              [
                "Join it to text — `\"Hi \" + x`",
                "`\"Hi undefined\"`, silently"
              ]
            ]
          }
        },
        {
          "p": "Asking an absent value for something stops the program with a TypeError. The\nerror points at the line that **used** the absence, not the line that\n**produced** it. You met this in *When it breaks: reading errors*: the question\nto ask is where the value went missing, and that can be many lines earlier.\nArithmetic and joining text do not stop anything. They put `NaN` or the word\n`undefined` into your output, and the program carries on as if nothing\nhappened."
        },
        {
          "p": "So a missing value matters in two places. The line where it came from is where\nyou decide whether a missing value is allowed. The line where it is used is\nwhere it breaks."
        },
        {
          "p": "What does each produce? Type the value, or `error`."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "let bonus;   10 + bonus",
                "accept": [
                  "nan"
                ],
                "miss": "**If you answered `10` — `undefined` is not zero.** Arithmetic with it gives `NaN`, with no error.\n",
                "why": "`NaN`. Nothing stops; a wrong number carries on.\n"
              },
              {
                "code": "const name = null;   `Hi, ${name}!`",
                "accept": [
                  "\"hi, null!\"",
                  "hi, null!",
                  "'hi, null!'",
                  "`hi, null!`"
                ],
                "why": "`\"Hi, null!\"`. The absence became text, and nobody was told.\n"
              },
              {
                "code": "const name = null;   name.toUpperCase()",
                "accept": [
                  "error",
                  "typeerror",
                  "type error",
                  "throws",
                  "it throws",
                  "an error"
                ],
                "why": "A TypeError: `Cannot read properties of null (reading 'toUpperCase')`.\n"
              }
            ]
          }
        },
        {
          "h": "Guarding: one question, asked two ways"
        },
        {
          "p": "A **guard** is a check that runs before a value is used. Every guard in this\nlesson asks the same question — **is this `null` or `undefined`?** — and nothing\nelse. `0`, `\"\"`, `false` and `NaN` are all values that are *there*, and a guard\nfor absence must let them through."
        },
        {
          "p": "**Ask it outright.** Each of these two expressions gives a boolean."
        },
        {
          "code": "x === null || x === undefined    // true if x is absent\nx == null                        // the same test, shorter",
          "lang": "js",
          "file": "ask.js"
        },
        {
          "p": "`x == null` is the one use of `==` the equality lesson called defensible: `==`\ntreats `null` and `undefined` as equal to each other and to nothing else. In the\nBranching module you will put tests like this inside an `if`, to choose what\ncode runs."
        },
        {
          "p": "**Supply a fallback.** The **nullish coalescing operator**, `??`, produces\nits left side if the left side is there, and its right side if the left side is\n`null` or `undefined`."
        },
        {
          "code": "count ?? 10     // 10 if count is null or undefined; otherwise count, whatever it is",
          "lang": "js",
          "file": "fallback.js"
        },
        {
          "p": "**The guard that looks right and is not:** `!x`. `!x` asks whether `x` is\nfalsy, and the falsy list holds more than the two absent values. Here are five\nvalues put through `!x` and through `x == null`:"
        },
        {
          "table": {
            "head": [
              "`x`",
              "`!x` calls it missing",
              "`x == null` calls it missing"
            ],
            "rows": [
              [
                "`undefined`",
                "yes",
                "yes"
              ],
              [
                "`null`",
                "yes",
                "yes"
              ],
              [
                "`0`",
                "**yes**",
                "no"
              ],
              [
                "`\"\"`",
                "**yes**",
                "no"
              ],
              [
                "`\"Ada\"`",
                "no",
                "no"
              ]
            ]
          }
        },
        {
          "p": "The two tests agree on every row except `0` and `\"\"`: values that are there\nbut happen to be falsy. If `0` or `\"\"` can be a real answer — a score, a price,\na count, a middle name left blank on purpose — `!x` treats that real answer as\nmissing."
        },
        {
          "p": "Logical OR (`||`) is also used for defaults, and logical AND (`&&`) to check a\nvalue before using it. Both follow the falsy list, so they go wrong on the same\ntwo rows. What `&&` and `||` evaluate to, and when each is the right choice, is\n*What &&, || and ?? return*, in the Branching module."
        },
        {
          "p": "What does each produce? Type the value."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const score = 0;   score ?? 100",
                "accept": [
                  "0"
                ],
                "miss": "**If you answered `100` because `0` is falsy — `??` does not ask the falsy list.** It asks only \"is this `null` or `undefined`?\", and `0` is neither.\n",
                "why": "`0`. The score is there; it is just zero. `??` keeps it.\n"
              },
              {
                "code": "let city;   city ?? \"unknown\"",
                "accept": [
                  "\"unknown\"",
                  "unknown",
                  "'unknown'"
                ],
                "why": "`\"unknown\"`. `city` is `undefined`, so the expression evaluates to its right side.\n"
              },
              {
                "code": "const middle = \"\";   middle == null",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. An empty string is there — it is just empty.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Guarding a value, worked",
            "body": [
              {
                "h": "Guarding a value, worked"
              },
              {
                "p": "Both worked examples, and the one you finish, use the same three steps: name\nwhat may be absent, guard it before its first use, and say what happens\ninstead."
              },
              {
                "p": "**Worked example 1.** A page greets the user by nickname. `nickname` is `null`\nwhen they did not choose one."
              },
              {
                "code": "const nickname = null;\n\n// name what may be absent\n//   nickname may be null. Nothing else here can be.\n// guard it before its first use, and say what happens instead\nconst shown = nickname ?? \"friend\";\n\nconsole.log(`Hi, ${shown}!`);    // Hi, friend!",
                "lang": "js",
                "file": "greet.js"
              },
              {
                "p": "Here one operator is both the guard and the fallback: `??` asks whether\n`nickname` is absent and, if it is, gives `\"friend\"` instead. One operator is\nenough because what happens instead is just a value. Without the `??`, the page\nwould say `Hi, null!`."
              },
              {
                "p": "**Worked example 2.** A game adds a bonus to a score. The bonus is `undefined`\nuntil the player earns one, and `0` is a real bonus."
              },
              {
                "code": "const score = 40;\nlet bonus;                        // not earned yet\n\n// name what may be absent\n//   bonus may be undefined. score is always a number.\n// guard it before its first use, and say what happens instead\nconst total = score + (bonus ?? 0);\n\nconsole.log(total);               // 40 — not NaN",
                "lang": "js",
                "file": "bonus.js"
              },
              {
                "p": "The parentheses matter. Without them, `score + bonus ?? 0` runs the `+` first,\nwhich gives `NaN`, and only then asks whether `NaN` is absent. `NaN` is not\nabsent, so `total` would be `NaN`."
              },
              {
                "p": "**Now you.** A form stores a middle name. It is `null` when the user skipped\nthe box, and `\"\"` when they cleared it on purpose. Show `\"(none)\"` only when it\nis absent."
              },
              {
                "code": "const middle = null;\n\n// name what may be absent          ______\n// guard it before its first use, and say what happens instead\nconst shownMiddle = ______;",
                "lang": "js",
                "file": "middle.js"
              },
              {
                "p": "`middle` may be `null`, so `shownMiddle` is `middle ?? \"(none)\"`. A cleared\nbox, `\"\"`, is kept. That is the difference from `!middle`, which would call\n`\"\"` missing too."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **how many ways to say nothing, and what happens when you use one?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Ways to say nothing",
                  "`\"Hi \" + nothing`",
                  "`nothing + 1`"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "two: `undefined` and `null`",
                    "`\"Hi undefined\"`, silently",
                    "`NaN`, silently"
                  ],
                  [
                    "**Python**",
                    "one: `None`",
                    "**TypeError**",
                    "**TypeError**"
                  ]
                ]
              }
            },
            {
              "p": "JavaScript lets you carry an absence a long way — into text, into arithmetic —\nbefore anything complains, and sometimes nothing ever does. Python refuses at\nthe first use. Neither is wrong, but in JavaScript the line where you notice is\noften far from the line where the value went missing, which is why the guard\nbelongs where the value arrives."
            },
            {
              "p": "Some statically typed languages go further and record in a variable's type\nwhether it may be absent, then refuse to run a use with no guard in front of\nit. That is a later course. In JavaScript nothing checks it for you."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "The toolkit so far, and what each one decides:"
              },
              {
                "code": "x == null          // test: absent or not (null and undefined only)\nx ?? fallback      // default: fallback only when x is absent\n!x                 // NOT an absence test — also catches 0, \"\", false, NaN",
                "lang": "js"
              },
              {
                "p": "The Objects module adds `?.`, for reading a property of something that may be\nabsent."
              }
            ],
            "python": [
              {
                "p": "One absence, `None`, and one test for it:"
              },
              {
                "code": "x is None              # the test: absent or not\nnot x                  # NOT an absence test — also true for 0 and \"\"",
                "lang": "python"
              },
              {
                "p": "Python has no `??`. The Branching module shows how a default is usually\nwritten instead."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the fourth line,\n`const count = 0;   !count`, write a test that is `true` when `count` is\nmissing and `false` when it is `0`."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A page asks the user three things with `prompt`. For each, Cancel gives `null`,\nand pressing OK with the box empty gives `\"\"`."
            },
            {
              "list": [
                "`name` — they must give one; `\"\"` is as good as no answer.",
                "`middle` — optional; `\"\"` means \"I have none\", which is a real answer.",
                "`tickets` — how many; they may type `0`."
              ]
            },
            {
              "p": "A colleague's draft:"
            },
            {
              "code": "const shownName    = name ?? \"guest\";\nconst shownMiddle  = middle ?? \"(skipped)\";\nconst noTickets    = !tickets;",
              "lang": "js"
            },
            {
              "p": "For each line, decide: keep it, or change the guard. Say what a missing value\n*means* in each case, and whether `\"\"` or `\"0\"` should count as an answer. There\nis more than one defensible choice; the reasoning is the answer."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "JavaScript has **two absences**: `undefined` (nothing was ever there) and `null` (someone wrote \"none\"). Other \"not found\" values, like `-1`, are not absences at all.",
                "**Holding** an absent value is not an error. **Using** one either stops the program with a TypeError or quietly becomes `NaN` or `\"undefined\"`.",
                "Guard for absence with `x == null` or `x ?? fallback`: they ask only about `null` and `undefined`. `!x` asks the falsy list and also catches `0` and `\"\"`."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "null",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/null",
              "note": "and its comparison with `undefined`."
            },
            {
              "title": "undefined",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/undefined",
              "note": "where it comes from."
            },
            {
              "title": "Nullish coalescing (??)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Nullish_coalescing",
              "note": "the fallback operator, and what it treats as absent."
            }
          ]
        }
      ],
      "_source": "lessons/absence.md",
      "practice": {
        "id": "absence",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the nothing",
            "prompt": "Three quick ones. In each, judge the code after the last semicolon. Type what it produces — a value, or `error` if it stops the program — or pick an answer where there are buttons.\n",
            "items": [
              {
                "id": "p1",
                "code": "let total;   total + 5",
                "accept": [
                  "nan"
                ],
                "miss": "**If you answered `5` — `undefined` is not zero.** Arithmetic with it gives `NaN`, and nothing stops.\n",
                "why": "`NaN`. `total` was never given a value, and `undefined + 5` is not a number.\n"
              },
              {
                "id": "p2",
                "code": "let city;   city.toUpperCase()",
                "accept": [
                  "error",
                  "typeerror",
                  "type error",
                  "throws",
                  "it throws",
                  "an error"
                ],
                "miss": "**If you answered `undefined` — holding nothing is fine; using it is not.** `city` holds `undefined`, and asking `undefined` to do something is a TypeError.\n",
                "why": "A TypeError: `Cannot read properties of undefined (reading 'toUpperCase')`.\n"
              },
              {
                "id": "p3",
                "code": "const score = 0;   score ?? 100",
                "accept": [
                  "0"
                ],
                "miss": "**If you answered `100` — `??` does not ask the falsy list.** It asks only \"is this `null` or `undefined`?\", and `0` is neither.\n",
                "why": "`0`. The score is there; it is zero. `??` keeps it.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const phone = null;   phone ?? \"no phone\"",
                "accept": [
                  "\"no phone\"",
                  "no phone",
                  "'no phone'"
                ],
                "why": "`\"no phone\"`. `null` is one of the two values `??` treats as absent.\n"
              },
              {
                "id": "p5",
                "code": "null == undefined",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`==` treats the two absences as equal to each other, and to nothing else. That is why `x == null` catches both.\n"
              },
              {
                "id": "p6",
                "code": "const middle = \"\";   middle ?? \"(none)\"",
                "accept": [
                  "\"\"",
                  "''",
                  "empty string",
                  "the empty string",
                  "an empty string"
                ],
                "miss": "**If you answered `\"(none)\"` — `\"\"` is falsy, but it is not absent.** `??` falls back only for `null` and `undefined`.\n",
                "why": "`\"\"`. An empty string is a value that is there. `??` keeps it.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 5 for the first",
                "say": "What is in `total`? It was declared and never given a value. Now ask what arithmetic does with that.\n"
              },
              {
                "when": "you answered 100 for the third",
                "say": "`??` does not consult the falsy list. Is `0` `null` or `undefined`?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Four absences and a crash",
            "prompt": "Answer the questions about this program first, then run it and compare.\n",
            "filename": "profile.js",
            "code": "let nickname;\nconst middle = null;\nconsole.log(nickname);\nconsole.log(middle);\nconsole.log(\"Hi \" + nickname);\nconsole.log(middle ?? \"(none)\");\nconsole.log(nickname.length);\nconsole.log(\"done\");\n",
            "items": [
              {
                "id": "r1",
                "question": "What does line 3, `console.log(nickname)`, print? Type it.",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined` — nobody ever put a value in `nickname`.\n"
              },
              {
                "id": "r2",
                "question": "What does line 4, `console.log(middle)`, print? Type it.",
                "accept": [
                  "null"
                ],
                "miss": "**If you answered `undefined` — somebody wrote `null` into `middle` on purpose.** The two absences print differently because they are different values.\n",
                "why": "`null` — the value someone wrote to say \"none\".\n"
              },
              {
                "id": "r3",
                "question": "What does line 5 print? Type it exactly.",
                "accept": [
                  "hi undefined",
                  "\"hi undefined\""
                ],
                "why": "`Hi undefined`. The absent value in `nickname` was turned into the word `undefined` and joined on, with no error.\n"
              },
              {
                "id": "r4",
                "question": "Does `\"done\"` print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you answered yes — line 7 stops the program.** `nickname` is `undefined`, and asking it for `.length` is a TypeError.\n",
                "why": "No. Line 7 throws a TypeError, and nothing after it runs.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected line 3 to be an error",
                "say": "Holding nothing is allowed. Only using it can fail. Which line *uses* `nickname` as if it were text?\n"
              },
              {
                "when": "you expected null and undefined to print the same",
                "say": "They are two different values. Which one did somebody write, and which one did nobody write?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Walk it back, then guard it",
            "prompt": "The first item shows an error message: type the name of the variable that was absent. For the rest, pick the result.\n",
            "items": [
              {
                "id": "t1",
                "question": "`const clean = typed.trim();` fails with `TypeError: Cannot read properties of undefined (reading 'trim')`. Type the name that was undefined.",
                "accept": [
                  "typed",
                  "`typed`"
                ],
                "miss": "**If you answered `trim` — `trim` is what the code was *trying* to use.** The error says it could not read `trim` *from* something. That something is just before the dot.\n",
                "why": "`typed`. The message names what the code was trying to read, `trim`. The absent value is whatever sits just before the dot in `.trim`.\n"
              },
              {
                "id": "t2",
                "code": "const qty = 0;   !qty",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. `!qty` asks the falsy list, and `0` is on it, so a real quantity is called missing.\n"
              },
              {
                "id": "t3",
                "code": "const qty = 0;   qty == null",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. `== null` asks only about `null` and `undefined`, so the zero gets through.\n"
              },
              {
                "id": "t4",
                "code": "let qty;   qty == null",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. `qty` is `undefined`, and `== null` catches both absences.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "question": "`const shout = reply.toUpperCase();` fails with `TypeError: Cannot read properties of null (reading 'toUpperCase')`. Type the name that was null.",
                "accept": [
                  "reply",
                  "`reply`"
                ],
                "why": "`reply`. The message says `null`, not `undefined`, so somebody's code put that `null` there on purpose — perhaps a `prompt` the user cancelled.\n"
              },
              {
                "id": "t6",
                "code": "const qty = null;   qty === undefined",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. `===` does not treat the two absences as the same, so a test that names only `undefined` lets `null` through.\n"
              }
            ],
            "hints": [
              {
                "when": "you named the thing in quotes in the message",
                "say": "`(reading 'trim')` is what it was trying to get. Look one step to the left of `.trim` in the line — that is what it was trying to get it *from*.\n"
              },
              {
                "when": "you gave the same answer for !qty and qty == null",
                "say": "`!qty` and `qty == null` are different questions. `!` consults the whole falsy list; `== null` matches exactly two values, `null` and `undefined`. Is `0` one of those two?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Fall back only when it is absent",
            "prompt": "`shownNickname` receives what a user chose as a nickname. It is `null` or `undefined` when they chose none — show `\"friend\"` then. An empty string means they cleared it on purpose, and should be kept as it is. Write your answer between the braces, and put `return` in front of it — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: why is `\"\"` kept when `null` is not?\n",
            "exportName": "shownNickname",
            "filename": "nickname.js",
            "starter": "function shownNickname(nickname) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "shownNickname(\"Ace\") → \"Ace\"",
                "body": "return subject(\"Ace\") === \"Ace\";"
              },
              {
                "label": "shownNickname(null) → \"friend\"",
                "body": "return subject(null) === \"friend\";"
              },
              {
                "label": "shownNickname(undefined) → \"friend\"",
                "body": "return subject(undefined) === \"friend\";"
              },
              {
                "label": "cleared on purpose: shownNickname(\"\") → \"\"",
                "body": "return subject(\"\") === \"\";"
              }
            ],
            "hints": [
              {
                "when": "\"\" comes back as \"friend\"",
                "say": "Your fallback asks the falsy list, and `\"\"` is on it. The fallback operator from the lesson asks only about `null` and `undefined`.\n"
              },
              {
                "when": "null comes back as null",
                "say": "You returned the nickname without a guard. Which operator produces a fallback when its left side is absent?\n"
              },
              {
                "when": "every test fails",
                "say": "Check that your line starts with `return`. Without it the function works out the answer and returns `undefined`.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "A missing bonus is not a number",
            "prompt": "`withBonus` adds a bonus to a score. The bonus is `undefined` or `null` until one is earned, and right now that makes the total `NaN`. Change it so a missing bonus counts as `0` — and a real bonus, including `0`, is still added. Change only what comes after `return` — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest. Then, in one sentence: where does the `NaN` come from?\n",
            "exportName": "withBonus",
            "filename": "with-bonus.js",
            "starter": "function withBonus(score, bonus) {\n  return score + bonus;\n}\n",
            "tests": [
              {
                "label": "withBonus(10, 5) → 15",
                "body": "return subject(10, 5) === 15;"
              },
              {
                "label": "withBonus(10, undefined) → 10",
                "body": "return subject(10, undefined) === 10;"
              },
              {
                "label": "withBonus(10, null) → 10",
                "body": "return subject(10, null) === 10;"
              },
              {
                "label": "withBonus(10, 0) → 10",
                "body": "return subject(10, 0) === 10;"
              }
            ],
            "hints": [
              {
                "when": "undefined still gives NaN",
                "say": "If you wrote `score + bonus ?? 0`: the `+` runs first and makes `NaN`, and `NaN` is not absent, so `??` keeps it. Parentheses decide what runs first.\n"
              },
              {
                "when": "null gives 10 but undefined gives NaN",
                "say": "`null + 10` happens to give `10`, because arithmetic turns `null` into `0`. Arithmetic turns `undefined` into `NaN`. Guard against both absent values.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a greeting line",
            "prompt": "A code assistant wrote this for the top of a page. `nickname` is text, or `null` when the user skipped it. `visits` is a number of earlier visits, and `0` is real. The spec: `greetingLine(\"Ada\", 4)` returns `\"Welcome back, Ada! Visit number 5\"`, and a skipped nickname shows `friend`. Two things are wrong. Find them, then fix the line after `return` — `return` sends the value out of the function so the tests can check it. The Functions module explains the rest.\n",
            "filename": "greeting.js",
            "code": "// nickname: text, or null if skipped. visits: a number.\nfunction greetingLine(nickname, visits) {\n  return \"Welcome back, \" + nickname + \"! Visit number \" + visits + 1;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "What does `greetingLine(null, 4)` return? Type it exactly, without quotes.",
                "accept": [
                  "welcome back, null! visit number 41",
                  "\"welcome back, null! visit number 41\""
                ],
                "why": "`Welcome back, null! Visit number 41`. The one line has two defects: the absent nickname became the word `null`, and `+ 1` joined `1` onto the text instead of adding it to `visits`.\n"
              },
              {
                "id": "c2",
                "question": "Why does it say `41` instead of `5`?",
                "choices": [
                  "`+` works left to right, so by then it is joining text",
                  "`visits` was text all along",
                  "`1` is text"
                ],
                "accept": [
                  "`+` works left to right, so by then it is joining text"
                ],
                "why": "Everything to the left of `+ visits` is already text, so `+ visits` joins `4` onto it, and `+ 1` then joins `1`. `visits` and `1` are never added together.\n"
              },
              {
                "id": "c3",
                "question": "Which operator guards the nickname so `null` becomes `friend` but `\"\"` would be kept?",
                "choices": [
                  "`!`",
                  "`??`",
                  "`==`"
                ],
                "accept": [
                  "`??`"
                ],
                "why": "`??` falls back only for `null` and `undefined`.\n"
              }
            ],
            "starter": "// nickname: text, or null if skipped. visits: a number.\nfunction greetingLine(nickname, visits) {\n  return \"Welcome back, \" + nickname + \"! Visit number \" + visits + 1;\n}\n",
            "exportName": "greetingLine",
            "tests": [
              {
                "label": "greetingLine(\"Ada\", 4) → \"Welcome back, Ada! Visit number 5\"",
                "body": "return subject(\"Ada\", 4) === \"Welcome back, Ada! Visit number 5\";"
              },
              {
                "label": "greetingLine(null, 4) → \"Welcome back, friend! Visit number 5\"",
                "body": "return subject(null, 4) === \"Welcome back, friend! Visit number 5\";"
              },
              {
                "label": "greetingLine(undefined, 0) → \"Welcome back, friend! Visit number 1\"",
                "body": "return subject(undefined, 0) === \"Welcome back, friend! Visit number 1\";"
              }
            ],
            "hints": [
              {
                "when": "the visit number is still 41 or 01",
                "say": "Add the numbers before `+` meets any text. Parentheses around `visits + 1` make that part run first.\n"
              },
              {
                "when": "it says \"null\" or \"undefined\" instead of \"friend\"",
                "say": "The nickname is used without a guard. Give it a fallback before it is joined into the text.\n"
              }
            ]
          }
        ]
      }
    },
    "type-discipline": {
      "id": "type-discipline",
      "title": "The type-discipline axis",
      "kind": "read",
      "outcome": 1,
      "shape": "conceptual",
      "why": "Outcome 1 asks students to compare their language's type rules to another's, and \"strict\" or \"loose\" cannot carry that comparison: JavaScript and Python are both dynamic and convert opposite amounts. Recognising when a language checks and how much it converts is what CS 140 keeps once static checking moved to CS 240 (D-019).",
      "concepts": [
        "types.discipline-axis"
      ],
      "lede": "\"Strongly typed\" and \"weakly typed\" are used constantly and mean almost nothing, because different people use them for different things. Two separate questions say what those terms try to say: when does a language check types, and how much does it convert for you? This lesson puts both questions on one picture, and the type lessons before it fit onto that picture.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Where the type actually lives · Conversion — when a value does not fit"
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "A few questions about where languages stand on types. Pick an answer, or\ntype one where there is a box. Guess if you have an idea; press **I don't\nknow** if you have none."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "JavaScript and Python are both dynamically typed. Do they convert the same amount?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes, you are reading \"dynamically typed\" as the answer to two questions at once:** when types are checked, and how much the language converts. `\"5\" - 1` is `4` in JavaScript and a TypeError in Python. Both languages check types while the program runs, and they convert opposite amounts, so the two questions have separate answers.\n",
                          "why": "No. `\"5\" - 1` is `4` in JavaScript and a TypeError in Python. \"Dynamically typed\" says *when* types are checked. It says nothing about *how much* the language converts.\n"
                        },
                        {
                          "question": "C# checks types before running. Does that stop `\"5\" + 1` from producing `\"51\"`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes, you are treating \"static\" and \"strict\" as the same thing.** They answer the two different questions. \"Static\" means types are checked before the program runs. \"Strict\" means the language converts little. C# checks early and still joins `\"5\" + 1`, because `+` is *defined* for a string and an int. Python checks late and refuses `\"5\" + 1`.\n",
                          "why": "No. It compiles and gives `\"51\"`, because `+` is *defined* for string and int. Checking early does not mean converting less.\n"
                        },
                        {
                          "question": "So how many independent questions are there? Type a number.",
                          "accept": [
                            "2",
                            "two"
                          ],
                          "why": "**When are types checked**, and **how much does the language convert**. They vary independently, which is why one \"strict to loose\" line misleads.\n"
                        },
                        {
                          "question": "Which of the two does \"strongly typed\" refer to?",
                          "choices": [
                            "When types are checked",
                            "How much the language converts",
                            "Either — people use it for both"
                          ],
                          "accept": [
                            "Either — people use it for both"
                          ],
                          "miss": "**Both of the first two answers are in use.** Some people mean when types are checked, and others mean how much the language converts, so the term alone does not tell you which. The two questions do. Ask when a language checks types, and ask how much it converts, and you can place any language you meet.\n",
                          "why": "Different people use \"strongly typed\" for each of the two questions. That is why this course drops the term and names the question instead.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Python and JavaScript are both dynamic. What does `\"5\" - 1` give in each — Python first, then JavaScript? A value or `error` for each.",
                          "accept": [
                            "error and 4",
                            "TypeError and 4",
                            "error, 4",
                            "typeerror, 4",
                            "error then 4",
                            "typeerror then 4",
                            "error 4",
                            "typeerror 4"
                          ],
                          "why": "Python refuses, JavaScript converts. Same checking time, opposite conversion policy.\n"
                        },
                        {
                          "question": "Does `mypy` change when Python checks types?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "`mypy` is a separate tool that checks types before the program runs, in the code it is able to check. You choose whether to run it, so it gives Python a third position on the checking axis, between late and early.\n"
                        },
                        {
                          "question": "How many independent questions are there? Type a number.",
                          "accept": [
                            "2",
                            "two"
                          ],
                          "why": "When types are checked, and how much the language converts. Python checks types late, and converts very little.\n"
                        },
                        {
                          "question": "Is Python \"strongly typed\"?",
                          "choices": [
                            "Yes",
                            "No",
                            "It depends which question you mean"
                          ],
                          "accept": [
                            "It depends which question you mean"
                          ],
                          "why": "If \"strongly typed\" means converts little, yes: Python refuses most conversions. If it means checks types early, no: Python checks while the program runs. The phrase carries both meanings, so on its own it settles nothing.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "C# checks before running. Does `\"5\" + 1` compile?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "It does, giving `\"51\"` — `+` is defined for string and int. Early checking is not the same as refusing conversions.\n"
                        },
                        {
                          "question": "Does `\"5\" - 1` compile?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "No. `-` is not defined for a string and an int, so the compiler refuses it before the program runs. JavaScript handles the *same* expression by converting `\"5\"` to the number 5.\n"
                        },
                        {
                          "question": "What does `dynamic` do to when types are checked?",
                          "choices": [
                            "Moves the check to run time",
                            "Nothing — C# still checks before running"
                          ],
                          "accept": [
                            "Moves the check to run time"
                          ],
                          "why": "Declaring a variable `dynamic` moves the checks on that variable to run time; every other variable is still checked before running. So C#'s position on the checking axis is not fixed: the language lets you change it, one variable at a time.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can place a language on two independent axes rather than on one line"
              ]
            }
          ]
        },
        {
          "h": "The two questions, on one picture"
        },
        {
          "p": "Two words first. A language is **dynamically typed** when it checks types\nwhile the program runs — JavaScript and Python are. It is **statically typed**\nwhen a compiler checks them before the program runs — C# is. That is one\nquestion. The other, from *Conversion — when a value does not fit*, is how\nmuch the language converts for you when an operator is handed the wrong type.\nDraw the two questions as two axes at right angles, and any language can be\nplaced somewhere on the picture:"
        },
        {
          "code": "                      CONVERTS A LOT\n                            ^\n                            |\n         JavaScript ●       |       ● C# (for defined cases)\n                            |\n   checked late  <----------+----------> checked early\n                            |\n             Python ●       |       ● Rust\n                            |\n                      CONVERTS LITTLE"
        },
        {
          "p": "Each quarter of the picture holds a real language. All four combinations of\nthe two answers exist:"
        },
        {
          "list": [
            "**JavaScript** — late and generous. When an operator is not defined for the types it is given, JavaScript converts the values until it is, so it almost never stops.",
            "**Python** — late and strict. Few operations are defined across types; it stops with an error instead of guessing.",
            "**C#** — early and moderately generous. `\"5\" + 1` compiles and joins; `\"5\" - 1` does not compile at all.",
            "**Rust** — early and strict. It will not even turn one size of whole number into another; you write every conversion."
          ]
        },
        {
          "p": "The type lessons before this one each fill in part of the picture. *Where the\ntype actually lives* is about the horizontal axis, when types are checked.\n*Conversion — when a value does not fit* is about the vertical axis, how much\nthe language converts. *Equality: == versus ===* and *Truthiness* ask the same\ntwo questions about particular operations: comparing with `==`, and using a\nvalue as a condition."
        },
        {
          "block": "notice",
          "label": "The sentence worth keeping",
          "body": [
            {
              "p": "**\"Dynamically typed\" tells you when the check happens. It tells you nothing\nabout how generous the conversion rules are.** JavaScript and Python share the\nfirst and sit at opposite ends of the second."
            }
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "Python checks types while running, and refuses `\"5\" - 1` with a TypeError. Where is it on the picture?",
                "choices": [
                  "Checked late, converts little",
                  "Checked late, converts a lot",
                  "Checked early, converts little"
                ],
                "accept": [
                  "Checked late, converts little"
                ],
                "why": "Late, because the check happens while running. Little, because it refuses rather than converts.\n"
              },
              {
                "question": "JavaScript and C# both give `\"51\"` for `\"5\" + 1`. Are they in the same place on the picture?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you said yes: `\"5\" + 1` tests only one thing, what `+` does with text and a number.** JavaScript and C# agree about that. They differ on *when* they check types: C# refuses `\"5\" - 1` before the program runs, and JavaScript converts it to `4`.\n",
                "why": "No. They give the same answer for `\"5\" + 1`, but C# checks types before running and JavaScript checks while running, and they give different answers for `\"5\" - 1`.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Placing a language you have never used",
            "body": [
              {
                "h": "Placing a language you have never used"
              },
              {
                "p": "To place a language, run three probes: give a variable a number and then a\nstring, evaluate `\"5\" + 1`, and evaluate `\"5\" - 1`. The worked examples below\nfollow the same subgoals for each language. You can run the probes in the\nlanguage's console, if it has one, or read the answers from a code sample."
              },
              {
                "p": "**Worked example 1 — Python.**"
              },
              {
                "code": "# find out when types are checked\n#   x = 5, then x = \"hi\"   → accepted, no complaint\n#   → nothing checked before running: LATE\n# find out how much it converts\n#   \"5\" + 1   → TypeError\n#   \"5\" - 1   → TypeError\n#   → both refuse: converts LITTLE\n# place it on the picture\n#   \"Checked late, converts little.\"",
                "lang": "python",
                "file": "place-python.txt"
              },
              {
                "p": "**Worked example 2 — C#.**"
              },
              {
                "code": "// find out when types are checked\n//   int x = 5; then x = \"hi\";   → refused before the program ran\n//   → EARLY\n// find out how much it converts\n//   \"5\" + 1   → \"51\": + is defined for text and a number\n//   \"5\" - 1   → refused: - is not\n//   → one works, one refuses: converts SOME\n// place it on the picture\n//   \"Checked early, converts some.\"",
                "lang": "csharp",
                "file": "place-csharp.txt"
              },
              {
                "p": "Worked example 2 shows why you run both `\"5\" + 1` and `\"5\" - 1`. With only\n`\"5\" + 1`, C# would look as generous as JavaScript; `\"5\" - 1` shows that it\nrefuses where an operator is not defined."
              },
              {
                "p": "**Now you — Ruby.** It accepts `x = 5` then `x = \"hi\"`. It gives `\"5\" + 1` a\n`TypeError: no implicit conversion of Integer into String`, and refuses\n`\"5\" - 1` too."
              },
              {
                "code": "find out when types are checked   ______\nfind out how much it converts     ______\nplace it on the picture           ______",
                "lang": "text",
                "file": "place-ruby.txt"
              },
              {
                "p": "Ruby checks late, because it accepted `x = \"hi\"` without complaint. It converts\nlittle, because it refused both expressions. On the picture it is \"Checked\nlate, converts little\", which is Python's quadrant. Ruby's error message states the\npolicy in its own words: *no implicit conversion*."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "This whole lesson **is** a comparison between languages, so here it is as a\ntable."
            },
            {
              "table": {
                "head": [
                  "",
                  "Checked",
                  "Converts",
                  "`\"5\" + 1`",
                  "`\"5\" - 1`"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "while running",
                    "a lot",
                    "`\"51\"`",
                    "`4`"
                  ],
                  [
                    "**Python**",
                    "while running",
                    "very little",
                    "`TypeError`",
                    "`TypeError`"
                  ],
                  [
                    "**C#**",
                    "before running",
                    "some",
                    "`\"51\"`",
                    "won't compile"
                  ]
                ]
              }
            },
            {
              "p": "The two columns on the right, `\"5\" + 1` and `\"5\" - 1`, do most of the\nplacing. Whether each one gives a value or refuses tells you how much the\nlanguage converts, and \"won't compile\" also tells you the language checks\nbefore running. JavaScript and C# give the same answer for `\"5\" + 1` and\ndifferent answers for `\"5\" - 1`, which is why one expression, and one axis,\nis not enough."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "JavaScript checks late and converts generously. You get flexibility, and no\nwarning before the program runs, so checking that a value has the type you\nexpect is up to you."
              },
              {
                "code": "const n = Number(typed);\nNumber.isNaN(n)        // true means the text was not a number —\n                       // the check nothing else will make for you",
                "lang": "js"
              },
              {
                "p": "Convert a value where it enters your program, such as text a user typed,\ncheck that the conversion worked, and the rest of your code can assume it has\na number. *Conversion — when a value does not fit* gave the same advice. In a\nlanguage that checks late, that check is your job, because nothing else will\nmake it."
              }
            ],
            "python": [
              {
                "p": "Python checks late and converts little. Of the two dynamic languages in this\nlesson, Python catches more mistakes, because it stops with an error instead of\nconverting."
              },
              {
                "code": "n = int(typed)      # stops with an error on anything that is not a number",
                "lang": "python"
              },
              {
                "p": "If you add type annotations and run `mypy` in CI (automatically, on every\nchange), some checks move to before the program runs. For the code `mypy`\nchecks, Python with `mypy` comes close to a language that checks before\nrunning."
              }
            ],
            "csharp": [
              {
                "p": "C# checks early and converts a moderate amount. The compiler checks every\noperation before the program runs. Where an operator is defined for the types\ninvolved, as `+` is for a string and an int, the program still converts."
              },
              {
                "code": "\"5\" + 1                    // \"51\" — compiles; + is defined for string and int\n\"5\" - 1                    // does not compile — found before running\nint n = int.Parse(typed);  // the conversion you write yourself",
                "lang": "csharp"
              },
              {
                "p": "Checking early decides *when* you hear about `\"5\" - 1`: before the program\nruns. It does not stop `\"5\" + 1` from joining into `\"51\"`. Whether `+` joins is\nthe other question, how much the language converts."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening questions again from memory. Then, without looking, say the\nthree probes you would run to place the next language you meet, and what answer\nto each would put it in JavaScript's quadrant."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A colleague proposes moving a small Python service to JavaScript: \"Both are\ndynamically typed, so the code will behave the same. It's a straight\ntranslation.\""
            },
            {
              "p": "Decide whether you agree. Place both languages on both axes, then say which axis\nthe claim is about and which one it left out. Finish with one line from a\nservice like this — adding a quantity, which arrives as text, to a running\ntotal that is a number — and say what it does in each language, and what the\ntranslated version would need so it fails the way the original did."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Two independent questions**, not one line: *when are types checked* and *how much does the language convert*.",
                "\"Strongly typed\" is used for both and settles neither. Name the axis instead.",
                "JavaScript and Python are both **dynamic** and sit at **opposite ends** of conversion."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Type coercion",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Type_coercion",
              "note": "the vertical axis, in one paragraph."
            },
            {
              "title": "Dynamic typing",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Dynamic_typing",
              "note": "the horizontal axis."
            }
          ]
        }
      ],
      "_source": "lessons/type-discipline.md",
      "practice": {
        "id": "type-discipline",
        "outcome": 1,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the placement",
            "prompt": "Three questions that keep the two axes apart: when types are checked, and how much the language converts. Pick an answer, or type one where there is a box.\n",
            "items": [
              {
                "id": "p1",
                "question": "JavaScript and Python are both dynamic. Do they convert the same amount?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. `\"5\" - 1` is `4` in JavaScript and a TypeError in Python. \"Dynamic\" says *when* types are checked, not *how much* the language converts.\n"
              },
              {
                "id": "p2",
                "question": "C# checks before running. Does `\"5\" + 1` still give `\"51\"`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "It compiles and joins, because `+` is defined for string and int. Checking early is not the same as converting less.\n"
              },
              {
                "id": "p3",
                "question": "First probe on a new language: `x = 5`, then `x = \"hi\"`. It is accepted. Are its types checked early or late?",
                "choices": [
                  "Early",
                  "Late"
                ],
                "accept": [
                  "Late"
                ],
                "why": "Late. Nothing objected before the program ran, so nothing checked the types then. This probe answers the checking question only; it says nothing yet about how much the language converts.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "How many independent questions are there? Type a number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "When types are checked, and how much the language converts. They vary independently, which is why a single strict-to-loose line misleads.\n"
              },
              {
                "id": "p5",
                "question": "Which of the two does \"strongly typed\" refer to?",
                "choices": [
                  "When types are checked",
                  "How much the language converts",
                  "Either — people use it for both"
                ],
                "accept": [
                  "Either — people use it for both"
                ],
                "why": "Some people mean when types are checked, and others mean how much the language converts. Naming the question you mean is more useful than the label.\n"
              }
            ],
            "hints": [
              {
                "when": "you said JavaScript and Python convert the same amount",
                "say": "Run `\"5\" - 1` in both. Both check types while running, and they give opposite answers, so when a language checks and how much it converts are separate properties.\n"
              },
              {
                "when": "you said C# does not give `\"51\"`",
                "say": "You may be reading \"checked early\" as \"converts little\". Ask instead whether `+` is defined for text and a number in C#.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Place a language from the evidence",
            "prompt": "Ruby: `x = 5` then `x = \"hi\"` is accepted; `\"5\" + 1` raises `TypeError: no implicit conversion of Integer into String`, and `\"5\" - 1` raises an error too.\n",
            "items": [
              {
                "id": "t1",
                "question": "When are its types checked?",
                "choices": [
                  "Early",
                  "Late"
                ],
                "accept": [
                  "Late"
                ],
                "why": "Giving `x` a string after a number was accepted, so nothing checked the types before the program ran.\n"
              },
              {
                "id": "t2",
                "question": "How much does it convert?",
                "choices": [
                  "A lot",
                  "Some",
                  "Little"
                ],
                "accept": [
                  "Little"
                ],
                "why": "It refuses instead of converting, and the error message states the policy in its own words: *no implicit conversion*.\n"
              },
              {
                "id": "t3",
                "question": "Which language from the lesson shares its quadrant?",
                "choices": [
                  "JavaScript",
                  "Python",
                  "C#",
                  "Rust"
                ],
                "accept": [
                  "Python"
                ],
                "why": "Late and converts little: Python's quadrant. Three probes placed a language you may never have used.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "Go refuses `x = 5` then `x = \"hi\"` before running, and refuses both `\"5\" + 1` and `\"5\" - 1` before running. Which language from the lesson shares its quadrant?",
                "choices": [
                  "JavaScript",
                  "Python",
                  "C#",
                  "Rust"
                ],
                "accept": [
                  "Rust"
                ],
                "why": "Early and converts little: Rust's quadrant. Go will not even add an `int` to a `float64` unless you convert one of them yourself.\n"
              },
              {
                "id": "t5",
                "question": "Java refuses `x = 5` then `x = \"hi\"` before running; `\"5\" + 1` compiles to `\"51\"`; `\"5\" - 1` does not compile. Which language from the lesson shares its quadrant?",
                "choices": [
                  "JavaScript",
                  "Python",
                  "C#",
                  "Rust"
                ],
                "accept": [
                  "C#"
                ],
                "why": "Early, and converts only where an operator is defined: `+` joins text and a number, and `-` refuses. That is the same place as C#.\n"
              }
            ],
            "hints": [
              {
                "when": "you called it strongly typed and stopped",
                "say": "This lesson drops that phrase, because people use it for both questions. Give the two answers instead: checked late or early, and converts a lot or little.\n"
              },
              {
                "when": "you placed it with JavaScript",
                "say": "You answered the checking question and stopped. JavaScript and Ruby agree about *when*; now look at what each does with `\"5\" - 1`.\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "The case one probe gets wrong",
            "prompt": "Two probes decide the conversion axis: does `\"5\" + 1` produce a value (`plusWorks`), and does `\"5\" - 1` (`minusWorks`)? The lesson's middle answer, **converts some**, means exactly one of the two works, as in C#. `convertsSome` should return `true` when exactly one works, and `false` when both work or both refuse. Write your answer between the braces, and put `return` in front of it: `return` sends the value out of the function so the tests can check it. The Functions module explains functions in full. Then, in one sentence: why does one probe not settle the conversion axis?\n",
            "exportName": "convertsSome",
            "filename": "converts-some.js",
            "starter": "// plusWorks:  true if \"5\" + 1 produced a value\n// minusWorks: true if \"5\" - 1 produced a value\nfunction convertsSome(plusWorks, minusWorks) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "C#: + works, - refuses → true",
                "body": "return subject(true, false) === true;"
              },
              {
                "label": "JavaScript: both work → false (converts a lot)",
                "body": "return subject(true, true) === false;"
              },
              {
                "label": "Python: both refuse → false (converts little)",
                "body": "return subject(false, false) === false;"
              },
              {
                "label": "the other mixed case: + refuses, - works → true",
                "body": "return subject(false, true) === true;"
              }
            ],
            "hints": [
              {
                "when": "JavaScript comes back true",
                "say": "You answered \"did either work?\". That is true for JavaScript too, which converts a lot. You need \"did exactly one work?\".\n"
              },
              {
                "when": "the \"+ refuses, - works\" test fails",
                "say": "You covered `+` working and `-` refusing, but not the other way round. The two probes can disagree in either direction.\n"
              },
              {
                "when": "every test fails",
                "say": "Check that your line starts with `return`, and that it produces `true` or `false`. Two booleans are \"different\" exactly when `!==` says so.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a comment",
            "prompt": "A colleague left the comment below, and a code assistant repeated the same claim when asked. `price` came from a box on a web page, so it is text. Answer each question.\n",
            "filename": "discounted.js",
            "code": "// price comes from a form field, so it is text.\n// JavaScript is dynamically typed, so the \"-\" below throws a\n// TypeError if price is not a number. No need to check it.\nconst price = \"20\";\nconst sale = price - 5;\nconsole.log(sale);\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line makes the false claim? Type the line number.",
                "accept": [
                  "2",
                  "line 2",
                  "l2",
                  "2-3",
                  "lines 2-3",
                  "2 and 3",
                  "lines 2 and 3",
                  "3",
                  "line 3"
                ],
                "why": "Lines 2–3. In JavaScript, `-` does not throw on text; it converts the text to a number. The comment takes \"dynamic\", which answers when types are checked, as if it answered how much the language converts.\n"
              },
              {
                "id": "c2",
                "question": "What does line 6 print? Type it.",
                "accept": [
                  "15"
                ],
                "why": "`\"20\" - 5` converts `\"20\"` to a number and gives `15`. There is no error, and here `15` is the right answer, which is why nobody noticed the comment was wrong.\n"
              },
              {
                "id": "c3",
                "question": "If the user had typed `abc`, so `price` is `\"abc\"`, what would line 6 print? Type it.",
                "accept": [
                  "nan"
                ],
                "why": "`NaN`, with no error. The TypeError the comment promised never comes; a wrong value travels on instead.\n"
              },
              {
                "id": "c4",
                "question": "Which axis did the comment get wrong?",
                "choices": [
                  "When types are checked",
                  "How much the language converts"
                ],
                "accept": [
                  "How much the language converts"
                ],
                "why": "How much it converts. \"Dynamic\" is true and says *when* a check would happen; it says nothing about whether `-` refuses text. In JavaScript, `-` converts text instead of refusing it.\n"
              }
            ],
            "hints": [
              {
                "when": "you said line 6 is an error",
                "say": "Ask whether `-` is defined for text in JavaScript. It is not. What does JavaScript do then: refuse, or convert?\n"
              },
              {
                "when": "you picked the checking axis",
                "say": "The comment is right that JavaScript checks late. What it got wrong is what `-` does when it gets text. Which axis is that?\n"
              }
            ]
          }
        ]
      }
    },
    "check-types": {
      "id": "check-types",
      "title": "Types and values check",
      "kind": "quiz",
      "unlocks": "Branching",
      "questions": [
        {
          "id": "q1",
          "skill": "Where the type lives",
          "prompt": "What does this log?",
          "code": "let total = \"5\";\ntotal = total * 2;\nconsole.log(typeof total, total);",
          "answers": [
            {
              "key": "A",
              "text": "string 10 — `total` was declared holding text",
              "tag": "the variable as a box",
              "correct": false
            },
            {
              "key": "B",
              "text": "number 10",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "string 55",
              "tag": "* read as joining",
              "correct": false
            },
            {
              "key": "D",
              "text": "A TypeError — you cannot multiply text",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `*` is not defined for text, so `\"5\"` converts to a number and the result is the number `10`. `total` then points at that number, and `typeof` reports the type of the value. The variable itself never had a type.",
            "wrong": "`*` is not defined for text, so JavaScript converts `\"5\"` to a number and multiplies: `10`, a number. `total` now points at that number, and `typeof` reports the type of the value, not of the variable. The answer is B."
          }
        },
        {
          "id": "q2",
          "skill": "Type coercion",
          "prompt": "What is the value of `\"5\" + 1 - 1`?",
          "answers": [
            {
              "key": "A",
              "text": "`50`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`5`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`\"51\"`",
              "tag": "stopped one step early",
              "correct": false
            },
            {
              "key": "D",
              "text": "`NaN`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `+` concatenates `\"5\"` and `1` into `\"51\"`. Then `-` is not defined for a string, so JavaScript converts `\"51\"` to the number 51: 51 − 1 = 50.",
            "wrong": "`+` runs first and concatenates, giving `\"51\"`. Then `-` is not defined for a string, so JavaScript converts `\"51\"` to the number 51: 51 − 1 = **50**."
          }
        },
        {
          "id": "q3",
          "skill": "Equality",
          "prompt": "Which comparison is `true`?",
          "answers": [
            {
              "key": "A",
              "text": "`NaN === NaN`",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`null == 0`",
              "tag": "null is loosely equal only to undefined",
              "correct": false
            },
            {
              "key": "C",
              "text": "`0 == \"0\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`null === undefined`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `==` converts the string to a number, so `0 == \"0\"` is true. All three of the others are false — including `NaN === NaN`.",
            "wrong": "`==` converts, so `0 == \"0\"` is true. `NaN` is never equal to itself, `null` is loosely equal only to `undefined`, and `null === undefined` is false because `===` never converts."
          }
        },
        {
          "id": "q4",
          "skill": "Truthiness",
          "prompt": "Which of these is **truthy** in JavaScript?",
          "answers": [
            {
              "key": "A",
              "text": "`0`",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"\"`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`\"0\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`NaN`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `\"0\"` is a one-character string, and the only falsy string is the empty one. So `\"0\"` is truthy, even though `\"0\" == false` is true. Those two facts together are the clearest example that `==` and truthiness follow different rules.",
            "wrong": "`\"0\"` is a non-empty string, and the only falsy string is the empty one, so `\"0\"` is truthy. `0`, `\"\"` and `NaN` are all on the falsy list."
          }
        },
        {
          "id": "q5",
          "skill": "Absence",
          "prompt": "`count` may be `0`, `null` or `undefined`. Which test is `true` for a missing count and `false` for `0`?",
          "answers": [
            {
              "key": "A",
              "text": "`!count`",
              "tag": "asks the falsy list — 0 is on it",
              "correct": false
            },
            {
              "key": "B",
              "text": "`count == null`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`count === null`",
              "tag": "misses undefined",
              "correct": false
            },
            {
              "key": "D",
              "text": "`count == false`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `== null` matches exactly `null` and `undefined`, so the test is `false` for `0`, which is a real count.",
            "wrong": "`!count` asks the falsy list, which includes `0`. `count === null` misses `undefined`. `count == false` is `true` for `0` and `false` for both absences — backwards. `count == null` matches exactly the two absences. The answer is B."
          }
        }
      ],
      "_source": "checks/check-types.yml"
    },
    "branching": {
      "id": "branching",
      "title": "The if statement",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.branching` is the first concept of Control flow, and every lesson after it puts code inside an `if`. No lesson taught it until the zero-prerequisite audit marked it new (its `flow.branching` row: the conditional operator was \"look it up\" in *Reading a program you did not write*). It sits after the Values module because a condition is a comparison (`comparisons`) and `if (x)` consults the falsy list (`truthiness`); the `if (x)` framing of truthiness and `toQuantity`, which returns `null` and needs a branch, were moved here from that module (rebuild-plan, \"Material moved out\"). D-061 sets the rest: explain first; both return forms, `return` in each branch and a `let` set by the branches, with neither enforced; a missing `else` that gives `undefined` taught as a failure. The writing direction, from a sentence to branches, is 01-explanations rule 5a, carried on from *Writing a function's answer*. Long chains and `switch` are `flow.multiway`, in *Choosing among many paths*; what `&&`, `||` and `??` evaluate to is `flow.short-circuit`, in *What &&, || and ?? evaluate to*. The examples and the order of the steps are derived from those rules, pending Nick's review. Procedural (01-explanations rule 8): reading and writing a branch are procedures. The ternary operator was this lesson's last step until D-062 gave it its own lesson and concept, `flow.ternary`, in *The ternary operator*, next; this lesson names it and leaves it there.",
      "concepts": [
        "flow.branching"
      ],
      "lede": "So far every program has run each of its lines, top to bottom. Branching is running some lines only when a condition holds. JavaScript has three ways to branch: the `if` statement, with `else` and `else if`; the ternary operator, which chooses between two values; and the `switch` statement, which chooses one path out of many. This lesson covers the `if` statement. The next lesson, *The ternary operator*, covers the ternary, and *Choosing among many paths* covers `switch`. It also covers the mistake that catches most people once a function contains an `if`: a path that reaches the end of the function without a `return`, so the function returns `undefined`.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Writing a function's answer · Comparing and combining · Truthiness · When there is nothing there · The ternary operator · Choosing among many paths"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Block** — the statements between a `{` and its `}`, which run together or not at all. **Branch** — one of the paths an `if` can take. **Edge case** — a value where one case turns into the next, such as 0 for frozen and liquid water; the best values to test with. **Expression** — code that produces a value, such as `a + b` or `temp < 0`. **Unreachable code** (or *dead code*) — code that no input can ever make run, such as a branch whose values are all caught by a condition above it.",
          "entries": [
            "**Block** — the statements between a `{` and its `}`, which run together or not at all.",
            "**Branch** — one of the paths an `if` can take.",
            "**Edge case** — a value where one case turns into the next, such as 0 for frozen and liquid water; the best values to test with.",
            "**Expression** — code that produces a value, such as `a + b` or `temp < 0`.",
            "**Unreachable code** (or *dead code*) — code that no input can ever make run, such as a branch whose values are all caught by a condition above it."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on the Values module, from memory. Press **I don't know** if\nyou have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const age = 17;\nage >= 18\n",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "why": "`false`. 17 is not greater than or equal to 18.\n"
                  },
                  {
                    "code": "Boolean(\"0\")",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "miss": "**If you said `false` — `\"0\"` is text, one character long.** The only falsy string is the empty one, `\"\"`.\n",
                    "why": "`true`. In JavaScript, every string except `\"\"` is truthy.\n"
                  },
                  {
                    "code": "let city;\ncity\n",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**A name declared with no value holds `undefined`,** JavaScript's \"nothing was ever put here\".\n",
                    "why": "`undefined`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can write an `if` / `else` that picks a path, and say which branch runs for a given input",
                "I can check that every path through a function that returns an answer reaches a `return`",
                "I can write the answer as a `return` in each branch or as a `let` the branches set, and say which reads better",
                "I can predict whether `if (x)` runs its block when `x` is not a boolean"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Running some code only sometimes: the if statement",
          "body": [
            {
              "p": "A program runs its **statements**, the instructions that end in `;`, one after\nanother from the top. An `if` lets a group of statements run only when a\n**condition** is `true`. The condition is the expression in the parentheses,\nusually a comparison like the ones in *Comparing and combining*."
            },
            {
              "code": "const temp = -3;\n\nif (temp < 0) {\n  console.log(\"Frost tonight\");\n}\nconsole.log(\"Forecast done\");",
              "lang": "js",
              "file": "frost.js"
            },
            {
              "p": "The statements between `{` and `}` are a **block**. The `if` decides whether\nits block runs: all of it, or none of it. Whatever comes after the closing `}`\nruns either way."
            },
            {
              "p": "Reading an `if` is the same three moves every time. Here they are with `temp`\nat `-3`, then at `12`."
            },
            {
              "code": "temp is -3\n  work out the condition     -3 < 0  -> true\n  choose the path            true, so the block runs: Frost tonight\n  carry on after the block   Forecast done\n\ntemp is 12\n  work out the condition     12 < 0  -> false\n  choose the path            false, so the block is skipped\n  carry on after the block   Forecast done",
              "lang": "text"
            },
            {
              "p": "So with `-3` the program prints two lines, and with `12` it prints one."
            },
            {
              "p": "**Indent what is inside a block.** The lines inside a block are moved in one\nlevel so you can see what the `if` controls. In JavaScript the indentation is\nfor people: JavaScript ignores it, and only the braces decide what is inside."
            },
            {
              "p": "`coat.js` shows JavaScript ignoring indentation. Its last line is indented\n**wrongly, on purpose**. The line is moved in as though it belonged to the `if`,\nbut it comes after the closing `}`, so it is outside the block. With `temp` at `5` the condition is\n`false`: `Frost tonight` is skipped, and `Wear a coat` prints anyway. The\nindentation tells the reader one thing and JavaScript does another."
            },
            {
              "code": "const temp = 5;\n\nif (temp < 0) {\n  console.log(\"Frost tonight\");\n}\n  console.log(\"Wear a coat\");   // indented, but outside the block",
              "lang": "js",
              "file": "coat.js"
            },
            {
              "p": "Not every language ignores indentation. In Python there are no braces, and the\nindentation *is* the block: move a line in or out and you change what the\nprogram does. So indent as though it mattered, in any language. Indentation is\nhow every reader sees the structure, and in some languages it is the structure."
            },
            {
              "p": "One level of indentation can be a tab or a run of spaces. With spaces, two and\nfour are the most common widths; this course's examples use two. Which one you\npick matters less than using the same one throughout a file, and your editor can\nkeep it consistent for you."
            },
            {
              "p": "**Braces around a single statement are optional.** Without braces, an `if`\ncontrols exactly one statement: the next one. These two lines do the same\nthing:"
            },
            {
              "code": "if (temp < 0) { console.log(\"Frost tonight\"); }\nif (temp < 0) console.log(\"Frost tonight\");",
              "lang": "js",
              "file": "frost.js"
            },
            {
              "p": "Some people leave the braces out on purpose, where every branch is one short\nstatement and putting each on its own line reads cleanly:"
            },
            {
              "code": "function sign(n) {\n  if (n > 0) return \"positive\";\n  else if (n < 0) return \"negative\";\n  else return \"zero\";\n}",
              "lang": "js",
              "file": "sign.js"
            },
            {
              "p": "The cost shows up when someone adds a line later. Without braces, only the one\nstatement right after the `if` belongs to it, whatever the indentation suggests:"
            },
            {
              "code": "const temp = 5;\n\nif (temp < 0)\n  console.log(\"Frost tonight\");\n  console.log(\"Wear gloves\");   // not in the if: it always runs",
              "lang": "js",
              "file": "gloves.js"
            },
            {
              "p": "Adding a line under a brace-less `if` and expecting it to be inside has caused\nreal bugs. A well-known security flaw in Apple's\nsoftware in 2014 came from one extra line under a brace-less `if`. Braces mark\nwhere a block ends, so a new line cannot quietly fall outside it. That is why\nmany teams require them, and why this course's examples always use them. Leaving\nthem out trades that safety for brevity. Whether the trade is worth it depends\non the code: leaving braces out can suit a chain where every branch is one short\nstatement, and suits little else."
            },
            {
              "p": "**A semicolon straight after the condition.** The same rule, that an `if`\ncontrols the one statement after it, explains a slip that is easy to make,\nbecause most lines end in `;`. The next piece of code is wrong on purpose:"
            },
            {
              "code": "const temp = 5;\n\nif (temp < 0); {\n  console.log(\"Frost tonight\");\n}",
              "lang": "js",
              "file": "frost.js"
            },
            {
              "p": "This prints `Frost tonight`, though 5 is not below 0. A semicolon on its own\nis an **empty statement**: a statement that does nothing. JavaScript allows one\nanywhere a statement can go, and here it is the statement right after the\ncondition, so it is the `if`'s whole body. The `if` decides whether to do\nnothing. The braces below it are a block of their own, which JavaScript also\nallows, and that block runs every time."
            },
            {
              "p": "Nothing reports an error. The program runs and does the wrong thing, so the\nmistake is hard to find by looking at the output. Look at the code instead: in\nthis course's style, the `)` that closes an `if`'s condition is followed by `{`.\nA `;` right after that `)` is almost always this slip."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "In `frost.js`, `temp` is changed to `0`. How many lines does the program print? Type the number.",
                    "accept": [
                      "1",
                      "one"
                    ],
                    "miss": "**Work out the condition first: is `0 < 0`?** No: `0` is not less than itself. So the block is skipped, and only `Forecast done` prints.\n",
                    "why": "`1`. Only `Forecast done`.\n"
                  },
                  {
                    "question": "In `coat.js`, `temp` is changed to `-2`. How many lines does the program print? Type the number.",
                    "accept": [
                      "2",
                      "two"
                    ],
                    "miss": "**Now the condition is `true`, so the block runs: `Frost tonight`.** Then `Wear a coat`, which is outside the block whatever its indentation, runs too.\n",
                    "why": "`2`. `Frost tonight` from the block, then `Wear a coat`, which always runs.\n"
                  },
                  {
                    "question": "In `gloves.js`, `temp` is `5`. Does `Wear gloves` print?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "miss": "**With no braces, the `if` controls only the next statement,** which is `console.log(\"Frost tonight\")`. `Wear gloves` is indented to look inside, but it is the statement after, so it runs whatever the condition says.\n",
                    "why": "Yes. Only `Frost tonight` belongs to the `if`.\n"
                  },
                  {
                    "code": "let bonus = 0;\nconst score = 4;\nif (score > 10); {\n  bonus = 5;\n}\n// What does bonus hold now? Type the number.\n",
                    "accept": [
                      "5"
                    ],
                    "miss": "**Look at what follows the condition's `)`: a `;`.** That empty statement is the `if`'s whole body. The block in braces is not part of the `if`, so it runs whatever `score` is.\n",
                    "why": "`5`, though 4 is not more than 10.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Two paths, or three: else and else if",
          "body": [
            {
              "p": "`else` gives the `if` a second block, which runs when the condition is\n`false`. Exactly one of the two blocks runs, never both and never neither."
            },
            {
              "code": "const temp = 5;\n\nif (temp < 0) {\n  console.log(\"Frost tonight\");\n} else {\n  console.log(\"No frost\");\n}",
              "lang": "js",
              "file": "frost.js"
            },
            {
              "p": "`5 < 0` is `false`, so this prints `No frost`."
            },
            {
              "p": "For three paths, put another `if` after the `else`. That is an **`else if`\nchain**:"
            },
            {
              "code": "const water = 85;   // degrees C\n\nif (water >= 100) {\n  console.log(\"Boiling\");\n} else if (water >= 70) {\n  console.log(\"Hot enough for tea\");\n} else {\n  console.log(\"Heat it more\");\n}",
              "lang": "js",
              "file": "tea.js"
            },
            {
              "p": "The rule for a chain: **check the conditions from the top. The first one that\nis `true` runs its block, and every block below it is skipped.** The final\n`else` has no condition. Its block runs when every condition above it is\n`false`."
            },
            {
              "code": "water is 85\n  work out the first condition    85 >= 100 -> false, move on\n  work out the next condition     85 >= 70  -> true\n  run that block, skip the rest   Hot enough for tea\n\nwater is 100\n  work out the first condition    100 >= 100 -> true\n  run that block, skip the rest   Boiling",
              "lang": "text"
            },
            {
              "p": "With `water` at `100`, the second condition, `100 >= 70`, is also `true`. But\nit is never checked: the chain already stopped at `100 >= 100`. That is why\n**the order of the conditions matters**."
            },
            {
              "p": "Here is the same chain with its first two conditions swapped. **It is wrong\non purpose**, to show what the order does:"
            },
            {
              "code": "const water = 100;   // degrees C\n\nif (water >= 70) {\n  console.log(\"Hot enough for tea\");\n} else if (water >= 100) {\n  console.log(\"Boiling\");          // can never run\n} else {\n  console.log(\"Heat it more\");\n}",
              "lang": "js",
              "file": "tea-swapped.js"
            },
            {
              "code": "water is 100\n  work out the first condition    100 >= 70 -> true\n  run that block, skip the rest   Hot enough for tea",
              "lang": "text"
            },
            {
              "p": "With `water` at `100`, it prints `Hot enough for tea`, because `100 >= 70` is\nnow the first condition that is `true`. But the problem is bigger than one\nwrong answer. **`Boiling` can now never print, whatever `water` holds.** For\nthe second condition to be checked, the first, `water >= 70`, must have been\n`false`, so `water` is below 70; and then `water >= 100` cannot be `true`.\nEvery value the `Boiling` branch was meant for is caught by the condition\nabove it. A branch that no input can ever reach is called **unreachable\ncode** (or *dead code*). JavaScript runs it without complaint and gives no\nwarning, so the only signs are wrong answers and a branch that never\nhappens."
            },
            {
              "p": "The fix is to check the more demanding condition first: `water >= 100`\nbefore `water >= 70`, as `tea.js` does. *Choosing among many paths* gives a\nquick way to check a longer chain's order by eye."
            },
            {
              "p": "A chain can have more than three paths, and JavaScript has a second way to choose\none path out of many, `switch`; *Choosing among many paths* covers both."
            },
            {
              "p": "Press your answer for each question."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "In `tea.js`, `water` is `40`. Which line prints?",
                    "choices": [
                      "Boiling",
                      "Hot enough for tea",
                      "Heat it more"
                    ],
                    "accept": [
                      "Heat it more"
                    ],
                    "why": "`Heat it more`. Both conditions are `false`, so the `else` runs.\n"
                  },
                  {
                    "question": "In `tea.js`, `water` is `70`. Which line prints?",
                    "choices": [
                      "Boiling",
                      "Hot enough for tea",
                      "Heat it more"
                    ],
                    "accept": [
                      "Hot enough for tea"
                    ],
                    "miss": "**`>=` includes the number itself.** `70 >= 100` is `false`; `70 >= 70` is `true`.\n",
                    "why": "`Hot enough for tea`.\n"
                  },
                  {
                    "question": "In `tea-swapped.js`, which line can never print, whatever number `water` holds?",
                    "choices": [
                      "Boiling",
                      "Hot enough for tea",
                      "Heat it more"
                    ],
                    "accept": [
                      "Boiling"
                    ],
                    "miss": "**Try a value for each line.** `100` prints `Hot enough for tea` and `40` prints `Heat it more`. For `Boiling`, `water >= 70` would have to be `false` and `water >= 100` `true` at the same time, and no number is both.\n",
                    "why": "`Boiling`. Every value of 100 or more is caught by `water >= 70` first, so that branch is unreachable.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "A function that returns an answer, with branches",
          "body": [
            {
              "p": "Each function in this section has one job: to return an answer, such as an\nentry fee. Which answer depends on the input: a child pays one fee and an adult\nanother. An `if` inside the function sends different inputs down different\npaths, so there is one thing to check that a function without branches never\nneeded. **Follow each path to its end. Does it reach a `return`?** A path that\nreaches the function's closing `}` without one makes the function return `undefined` for every\ninput that takes it."
            },
            {
              "p": "That check applies to functions like these, whose job is to return an\nanswer. It does not mean every `if` needs a `return` inside it. An `if` can\ninstead change a variable, with a single `return` after it; the next section,\n*Two ways to send the answer out*, shows that form. And a function whose job is\nsomething else, such as printing a message, may have no `return` at all. This\nsection writes a `return` in each branch."
            },
            {
              "p": "**Example 1.** \"`entryFee` takes a visitor's age and returns their entry\nfee: children under 5 get in free, and everyone else pays 12.\""
            },
            {
              "p": "Here is a common first try. It handles children under 5, and nothing else:"
            },
            {
              "code": "function entryFee(age) {\n  if (age < 5) {\n    return 0;\n  }\n}",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✓ entryFee(3) → 0\n✗ entryFee(30) → 12\n✗ entryFee(5) → 12",
              "lang": "text"
            },
            {
              "p": "The first test that failed is `entryFee(30)`. Work it through by hand, with\n`age` holding `30`. The condition `30 < 5` is `false`, so the block is skipped.\nAfter the block there is nothing else in the function, so it reaches its\nclosing `}` without ever running a `return`. **A function that finishes\nwithout a `return` returns `undefined`.** You can see it by printing the\nresult: `console.log(entryFee(30));` prints:"
            },
            {
              "code": "undefined",
              "lang": "text"
            },
            {
              "p": "The description has two cases, children under 5 and everyone else, but the\ncode only handles the first. The fix is an `else` for everyone else:"
            },
            {
              "code": "function entryFee(age) {\n  // name the values you have: age, a number\n  // find the question in the sentence: under 5 -> 0, everyone else -> 12\n  // write it with operators: age < 5\n  // send the answer out, from every path\n  if (age < 5) {\n    return 0;\n  } else {\n    return 12;\n  }\n}",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "With the `else`, all three tests pass. There is a second way to write the\nsame function, and you will see it often: leave out the `else`, and put\n`return 12;` on its own after the `if` block."
            },
            {
              "code": "function entryFee(age) {\n  if (age < 5) {\n    return 0;\n  }\n  return 12;\n}",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "It gives the same answers, because **`return` ends the function\nimmediately**. With `age` at `3`, the condition is `true`, `return 0` runs,\nand the function stops there; the line `return 12;` is never reached. With\n`age` at `30`, the condition is `false`, the block is skipped, and the next\nline is `return 12;`. Either way, every path ends in a `return`."
            },
            {
              "p": "**Example 2.** \"`waterState` takes a temperature in °C and returns `\"frozen\"`\nbelow 0, `\"steam\"` at 100 or above, and `\"water\"` otherwise.\""
            },
            {
              "code": "function waterState(temp) {\n  if (temp < 0) {\n    return \"frozen\";\n  } else if (temp >= 100) {\n    return \"steam\";\n  } else {\n    return \"water\";\n  }\n}",
              "lang": "js",
              "file": "state.js"
            },
            {
              "p": "Try it with `0`, the freezing point of water. `0 < 0` is\n`false`, and `0 >= 100` is `false`, so the `else` runs and the answer is\n`\"water\"`, which is right. Values like 0 and 100, where one case turns into the\nnext, are called **edge cases**. They are the best values to test with,\nbecause they are where a `<` that should have been `<=` gives the wrong\nanswer."
            },
            {
              "p": "**Example 3: you choose the line.** \"`sizeFor` takes a chest measurement in cm\nand returns a shirt size: `\"S\"` under 90, `\"M\"` under 100, and `\"L\"` for\nanything bigger.\""
            },
            {
              "code": "function sizeFor(chest) {\n  if (chest < 90) {\n    return \"S\";\n  } else if (chest < 100) {\n    return \"M\";\n  } else {\n    // TODO\n  }\n}",
              "lang": "js",
              "file": "size.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes where the TODO is?",
                    "choices": [
                      "\"L\";",
                      "return \"L\";",
                      "return sizeFor(chest);"
                    ],
                    "accept": [
                      "return \"L\";"
                    ],
                    "miss": "**If you chose `\"L\";` — the value is worked out and then dropped.** Without `return`, nothing is sent out, so `sizeFor(104)` returns `undefined`. **If you chose `sizeFor(chest)` — it calls the function you are writing**, with the same value, and stops with `Maximum call stack size exceeded`, as in *Writing a function's answer*.\n",
                    "why": "`return \"L\";`. Every path now returns.\n"
                  },
                  {
                    "question": "Someone writes the chain the other way round: `if (chest < 100)` returns `\"M\"` first, then `else if (chest < 90)` returns `\"S\"`. What does `sizeFor(85)` return?",
                    "choices": [
                      "\"S\"",
                      "\"M\"",
                      "\"L\""
                    ],
                    "accept": [
                      "\"M\""
                    ],
                    "miss": "**The first condition that is `true` wins.** `85 < 100` is checked first and is `true`, so `\"M\"` is returned before `chest < 90` is looked at.\n",
                    "why": "`\"M\"`. In that order, nobody is ever size S.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Two ways to send the answer out",
          "body": [
            {
              "p": "Example 1 put a `return` in each branch. There is a second way: make a\nvariable with `let` before the `if`, have each branch set it, and return it\nonce at the end."
            },
            {
              "code": "function entryFee(age) {\n  let fee;               // made before the if\n  if (age < 5) {         // set on every path\n    fee = 0;\n  } else {\n    fee = 12;\n  }\n  return fee;            // sent out once, at the end\n}",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "There is a shorter version of that second way. Most visitors pay 12; only\nchildren under 5 pay something different. So `fee` can start at 12, the answer\nfor most people, and the `if` only has to handle the exception: when the visitor\nis under 5, change `fee` to 0. A visitor the `if` skips keeps the 12 that `fee`\nstarted with. A starting value like this, used unless something changes it, is\ncalled a **default value**."
            },
            {
              "code": "function entryFee(age) {\n  let fee = 12;          // what most visitors pay\n  if (age < 5) {\n    fee = 0;             // the one exception\n  }\n  return fee;\n}",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "Every working version of `entryFee` in this lesson returns the same values,\nand the tests cannot tell them apart. Use whichever reads more clearly to you."
            },
            {
              "list": [
                "**A `return` in each branch** keeps each answer beside its condition, and the function finishes as soon as it has its answer. But `return` only works inside a function. Until the Functions module, exercises give you the function. In other code, you would first have to write a function, with its parameters, to hold the `if`, and a reader would have to jump to that function and back to follow what happens.",
                "**A variable the branches set** works anywhere, inside a function or not, and the version with a default value reads as \"12, unless the visitor is under 5\". The cost is that you must check the variable gets a value on every path. If the function starts with `let fee;` and has no `else` to set it, the function returns `undefined` for any input that skips the `if`: the same bug as the missing `return` in Example 1, in a new place."
              ]
            },
            {
              "p": "Make the variable **before** the `if`, not inside a branch. A variable made\nwith `let` inside a block only exists until that block's `}`. So in this version, `fee`\nno longer exists by the line `return fee;`, and the program stops there with\n`ReferenceError: fee is not defined`:"
            },
            {
              "code": "function entryFee(age) {\n  if (age < 5) {\n    let fee = 0;\n  } else {\n    let fee = 12;\n  }\n  return fee;\n}",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "The Scope module explains why variables work that way."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "function parkingFee(hours) {\n  let fee;\n  if (hours > 2) {\n    fee = 5;\n  }\n  return fee;\n}\nparkingFee(1)\n",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**`1 > 2` is `false`, so nothing ever sets `fee`.** It still holds what `let fee;` gave it: `undefined`.\n",
                    "why": "`undefined`. Starting it at `0`, `let fee = 0;`, would fix it.\n"
                  },
                  {
                    "code": "function shelfLabel(count) {\n  let label = \"in stock\";\n  if (count === 0) {\n    label = \"sold out\";\n  }\n  return label;\n}\nshelfLabel(4)\n",
                    "accept": [
                      "\"in stock\"",
                      "in stock",
                      "'in stock'"
                    ],
                    "why": "`\"in stock\"`. `4 === 0` is `false`, so `label` keeps its starting value.\n"
                  },
                  {
                    "question": "An exercise asks for a function with two cases. Which way of sending the answer out do the tests require?",
                    "choices": [
                      "A return in each branch",
                      "A let the branches set",
                      "Either — the tests check only the value"
                    ],
                    "accept": [
                      "Either — the tests check only the value"
                    ],
                    "why": "Either. A test calls the function and checks what comes back, not how the code got there.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "When the condition is not a boolean",
          "body": [
            {
              "p": "A condition does not have to be a comparison. In JavaScript, `if (x)` works with any value,\nand asks the same question as `Boolean(x)` in *Truthiness*: **is `x` on the\nfalsy list?** If it is not, the block runs."
            },
            {
              "table": {
                "head": [
                  "Falsy: the block is skipped",
                  "Everything else runs the block, including"
                ],
                "rows": [
                  [
                    "`false`, `0`, `\"\"`, `null`, `undefined`, `NaN`",
                    "`\"0\"`, `\" \"`, `\"false\"`, `-1`"
                  ]
                ]
              }
            },
            {
              "code": "const nickname = \"\";\n\nif (nickname) {\n  console.log(`Hi, ${nickname}`);\n} else {\n  console.log(\"Hi, guest\");\n}",
              "lang": "js",
              "file": "greet.js"
            },
            {
              "p": "`\"\"` is on the list, so this prints `Hi, guest`. Here that is the right\nanswer: an empty nickname means there is no nickname. `if (x)` is fine when\n**every** falsy value means \"nothing there\". When `0` or `\"\"` can be a real\nanswer, `if (x)` asks the wrong question."
            },
            {
              "p": "**Example: a quantity box.** \"`toQuantity` takes the text typed into a quantity\nbox and returns the number it holds, or `null` when it holds no number. An\nempty box is not a quantity of zero.\" `Number(text)` turns the text into a\nnumber, and gives `NaN` when the text does not look like one (*Conversion*)."
            },
            {
              "p": "Here is a common first try. It uses `if (n)` to ask \"is it a number?\":"
            },
            {
              "code": "function toQuantity(text) {\n  const n = Number(text);\n  if (n) {\n    return n;\n  } else {\n    return null;\n  }\n}",
              "lang": "js",
              "file": "quantity.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✓ toQuantity(\"5\") → 5\n✓ toQuantity(\"12.5\") → 12.5\n✓ toQuantity(\"abc\") → null\n✗ toQuantity(\"0\") → 0\n✓ toQuantity(\"\") → null",
              "lang": "text"
            },
            {
              "p": "The test that failed is `toQuantity(\"0\")`. Work it through by hand, with\n`text` holding `\"0\"`. `Number(\"0\")` is `0`, so `n` holds `0`. The condition\n`if (n)` asks whether `0` is on the falsy list. It is, so the `else` runs and\nthe function returns `null`. The code asked \"is `n` truthy?\" when it meant\n\"is `n` a number?\". `NaN` is falsy, so `if (n)` does turn away text that is not\na number. But a real `0` is falsy too, so it turns that away as well."
            },
            {
              "p": "The exact question is \"is `n` `NaN`?\", and `Number.isNaN(n)` asks it. Using it\nas the condition fixes the `\"0\"` test and breaks another:"
            },
            {
              "code": "✓ toQuantity(\"5\") → 5\n✓ toQuantity(\"12.5\") → 12.5\n✓ toQuantity(\"abc\") → null\n✓ toQuantity(\"0\") → 0\n✗ toQuantity(\"\") → null",
              "lang": "text"
            },
            {
              "p": "`Number(\"\")` is `0`, not `NaN`, so the function now returns `0` for an empty box. The\n`if (n)` version passed this test only by luck: it turned away the empty box's\n`0` because `0` is falsy, the same reason it wrongly turned away `\"0\"`. The\ndescription has two ways for the box to hold no number: the box is empty, or\nits text is not a number. So the code needs two questions:"
            },
            {
              "code": "function toQuantity(text) {\n  const n = Number(text);\n  if (text.trim() === \"\") {        // empty, or only spaces\n    return null;\n  } else if (Number.isNaN(n)) {    // not a number\n    return null;\n  } else {\n    return n;\n  }\n}",
              "lang": "js",
              "file": "quantity.js"
            },
            {
              "p": "All five tests pass. When a falsy value can be a real answer, write the exact\nquestion:\n`=== \"\"`, `=== null`, `Number.isNaN(n)`. The ternary operator's condition\nworks the same way, in *The ternary operator*, and *What &&, || and ?? evaluate to*\nshows the same choice for default values."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const lives = 0;\nif (lives) {\n  console.log(\"Keep playing\");\n} else {\n  console.log(\"Game over\");\n}\n",
                    "choices": [
                      "Keep playing",
                      "Game over"
                    ],
                    "accept": [
                      "Game over"
                    ],
                    "why": "`Game over`. `0` is falsy, so the `else` runs. Here that is the right answer: zero lives means the game is over.\n"
                  },
                  {
                    "code": "const answer = \"0\";\nif (answer) {\n  console.log(\"Answered\");\n} else {\n  console.log(\"Skipped\");\n}\n",
                    "choices": [
                      "Answered",
                      "Skipped"
                    ],
                    "accept": [
                      "Answered"
                    ],
                    "miss": "**`\"0\"` is text, one character long, and not on the falsy list.** Only the empty string `\"\"` is falsy.\n",
                    "why": "`Answered`.\n"
                  },
                  {
                    "question": "A score is `null` until a game is played, and `0` is a real score. Which condition is `true` only when there is no score yet?",
                    "choices": [
                      "!score",
                      "score === null",
                      "score === 0"
                    ],
                    "accept": [
                      "score === null"
                    ],
                    "miss": "**`!score` asks the falsy list,** and `0` is on it, so a real score of zero would count as \"no score\".\n",
                    "why": "`score === null`. It asks exactly \"is there no score yet?\", and a real score of `0` does not match it.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A café gives a free drink when a loyalty card has 10 or more stamps.\n`stamps` is a number, and it is `null` for a customer with no card. A function\n`freeDrink(stamps)` should return `\"free drink\"`, `\"keep collecting\"` or\n`\"no card\"`."
            },
            {
              "p": "Write it twice: once with a `return` in each branch, and once with a `let`\nthat starts at one of the three answers. Say which you would keep, and why.\nThen: what does each of your versions return for `0`, for `null` and for\n`10`? Would `if (stamps)` have been a safe first condition?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**An `if` runs its block only when the condition is `true`; `else` runs when it is not.** In an `else if` chain the first `true` condition wins and the rest are skipped, so order matters.",
                "**In a function that returns an answer, every path must send one out**, by a `return` in each branch or a `let` that every branch sets. A path that sends no answer out makes the call return `undefined`.",
                "**`if (x)` asks the falsy list.** When `0` or `\"\"` can be a real answer, write the exact question: `=== null`, `=== \"\"`, `Number.isNaN(n)`."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "if...else",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/if...else",
              "note": "the statement, with `else if`."
            }
          ]
        }
      ],
      "_source": "lessons/branching.md",
      "practice": {
        "id": "branching",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Which path, and what comes back?",
            "prompt": "Type the value each call returns — a number as digits, `undefined` as the word — or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "function tipFor(rating) {\n  if (rating >= 4) {\n    return 5;\n  } else {\n    return 2;\n  }\n}\ntipFor(4)\n",
                "accept": [
                  "5"
                ],
                "miss": "**`>=` includes the number itself.** `4 >= 4` is `true`, so the first block runs.\n",
                "why": "`5`.\n"
              },
              {
                "id": "p2",
                "question": "To find out which block of `if (a > b) { … } else { … }` runs, what happens first?",
                "choices": [
                  "a > b is worked out",
                  "The first block runs",
                  "Both blocks run, and one result is kept"
                ],
                "accept": [
                  "a > b is worked out"
                ],
                "why": "The condition is worked out first. Its `true` or `false` decides which one block runs; the other never runs at all.\n"
              },
              {
                "id": "p3",
                "code": "function bonus(points) {\n  if (points > 100) {\n    return 10;\n  }\n}\nbonus(50)\n",
                "accept": [
                  "undefined"
                ],
                "miss": "**`50 > 100` is `false`, so the block is skipped** — and after it there is no `return`. A function that finishes without one returns `undefined`.\n",
                "why": "`undefined`. The path for `50` has no `return`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "function result(mark) {\n  if (mark >= 50) {\n    return \"pass\";\n  } else if (mark >= 80) {\n    return \"merit\";\n  } else {\n    return \"fail\";\n  }\n}\nresult(90)\n",
                "choices": [
                  "\"pass\"",
                  "\"merit\"",
                  "\"fail\""
                ],
                "accept": [
                  "\"pass\""
                ],
                "miss": "**The first condition that is `true` wins.** `90 >= 50` is checked first and is `true`, so `mark >= 80` is never looked at.\n",
                "why": "`\"pass\"`. In this order, `\"merit\"` can never come back.\n"
              },
              {
                "id": "p5",
                "code": "function lightOn(hour) {\n  if (hour < 7) {\n    return true;\n  }\n  return false;\n}\nlightOn(9)\n",
                "choices": [
                  "true",
                  "false",
                  "undefined"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. `9 < 7` is `false`, so the block is skipped and the `return false` after it runs.\n"
              },
              {
                "id": "p6",
                "code": "function warning(temp) {\n  if (temp < 0); {\n    return \"Frost\";\n  }\n  return \"Clear\";\n}\nwarning(12)\n",
                "choices": [
                  "\"Frost\"",
                  "\"Clear\"",
                  "undefined"
                ],
                "accept": [
                  "\"Frost\""
                ],
                "miss": "**Look at what follows the condition's `)`: a `;`.** That empty statement is the `if`'s whole body. The block in braces is not part of the `if`, so its `return \"Frost\"` runs whatever `temp` is.\n",
                "why": "`\"Frost\"`, though 12 is not below 0. `return \"Clear\"` is never reached.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 2 for tipFor(4)",
                "say": "Work out `4 >= 4` on its own first. Does \"greater than or equal\" include equal?\n"
              },
              {
                "when": "you chose \"Clear\" for warning(12)",
                "say": "Read the `if` line to its end. What is the `if`'s body when a `;` comes straight after the condition?\n"
              },
              {
                "when": "you answered 0 or 10 for bonus(50)",
                "say": "Follow the call with `points` as 50. Which `return` does it reach? Is there one on that path at all?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict four lines, then run it",
            "prompt": "Predict each line it prints — type it exactly as it would appear, text without quotes — then run it and compare.\n",
            "filename": "seats.js",
            "code": "function seatsNote(seats) {\n  let note = \"plenty\";\n  if (seats === 0) {\n    note = \"full\";\n  } else if (seats < 5) {\n    note = \"few left\";\n  }\n  return note;\n}\nconst promo = \"\";\nconsole.log(seatsNote(0));\nconsole.log(seatsNote(5));\nconsole.log(seatsNote(-1));\nif (promo) {\n  console.log(\"code entered\");\n} else {\n  console.log(\"no code\");\n}\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(seatsNote(0));",
                "accept": [
                  "full"
                ],
                "why": "`full`. `0 === 0`, so the first branch sets the note.\n"
              },
              {
                "id": "r2",
                "code": "console.log(seatsNote(5));",
                "accept": [
                  "plenty"
                ],
                "miss": "**`5 < 5` is `false`.** Neither branch runs, so `note` keeps the value it started with.\n",
                "why": "`plenty`. The default is kept.\n"
              },
              {
                "id": "r3",
                "code": "console.log(seatsNote(-1));",
                "accept": [
                  "few left"
                ],
                "miss": "**`-1 === 0` is `false`, but `-1 < 5` is `true`.** The function never checks for a number below zero, so a mistaken `-1` comes out as \"few left\".\n",
                "why": "`few left`. A bad input took a path meant for real seat counts. Tests for a function like this should include a `-1` case.\n"
              },
              {
                "id": "r4",
                "code": "if (promo) { … } else { … }",
                "accept": [
                  "no code"
                ],
                "miss": "**`\"\"` is on the falsy list,** so the `else` block runs.\n",
                "why": "`no code`.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted few left for seatsNote(5)",
                "say": "Work out `5 < 5` on its own. Is a number less than itself?\n"
              },
              {
                "when": "you predicted code entered",
                "say": "`promo` holds the empty string. Is `\"\"` truthy or falsy?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Which branch runs, and how many questions it takes",
            "prompt": "`windLabel(kmh)` asks, in order: `if (kmh < 20)` returns `\"calm\"`; `else if (kmh < 60)` returns `\"breezy\"`; `else` returns `\"gale\"`. Press the label, or type the number.\n",
            "items": [
              {
                "id": "t1",
                "question": "What does `windLabel(20)` return?",
                "choices": [
                  "\"calm\"",
                  "\"breezy\"",
                  "\"gale\""
                ],
                "accept": [
                  "\"breezy\""
                ],
                "miss": "**`20 < 20` is `false`** — 20 is not less than itself — so the chain moves on to `20 < 60`, which is `true`.\n",
                "why": "`\"breezy\"`.\n"
              },
              {
                "id": "t2",
                "question": "For `windLabel(5)`, how many conditions are worked out? Type the number.",
                "accept": [
                  "1",
                  "one"
                ],
                "miss": "**The chain stops at the first `true`.** `5 < 20` is `true` on the first check, so `kmh < 60` is never asked.\n",
                "why": "`1`.\n"
              },
              {
                "id": "t3",
                "question": "For `windLabel(75)`, how many conditions are worked out? Type the number.",
                "accept": [
                  "2",
                  "two"
                ],
                "miss": "**`else` has no condition to work out.** Two are asked and both are `false`; then the `else` block runs.\n",
                "why": "`2`, and the answer is `\"gale\"`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "What does `windLabel(60)` return?",
                "choices": [
                  "\"calm\"",
                  "\"breezy\"",
                  "\"gale\""
                ],
                "accept": [
                  "\"gale\""
                ],
                "why": "`\"gale\"`. `60 < 20` and `60 < 60` are both `false`.\n"
              },
              {
                "id": "t5",
                "question": "The second condition is changed to `kmh <= 60`. What does `windLabel(60)` return now?",
                "choices": [
                  "\"calm\"",
                  "\"breezy\"",
                  "\"gale\""
                ],
                "accept": [
                  "\"breezy\""
                ],
                "why": "`\"breezy\"`. `60 <= 60` is `true`. Whether 60 itself counts as breezy or as a gale is decided by the choice of `<` or `<=`.\n"
              }
            ],
            "hints": [
              {
                "when": "you said 2 for windLabel(5)",
                "say": "After a condition is `true` and its block runs, does the chain go on asking the rest?\n"
              },
              {
                "when": "you said 3 for windLabel(75)",
                "say": "Count only the parts with a condition in parentheses. What does the `else` ask?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Put the ticket prices together",
            "prompt": "\"`ticketPrice` takes an age and returns the ticket price: under 12 pays 6, 65 or over pays 8, and everyone else pays 11.\" Put the lines in order. Two lines are mistakes — leave them out.\n",
            "lines": [
              "function ticketPrice(age) {",
              "  if (age < 12) {",
              "    return 6;",
              "  } else if (age >= 65) {",
              "    return 8;",
              "  } else {",
              "    return 11;",
              "  }",
              "}"
            ],
            "distractors": [
              {
                "line": "  } else if (age > 65) {",
                "why": "`>` leaves out 65 itself. The sentence says \"65 or over\", so a 65-year-old would pay 11.\n"
              },
              {
                "line": "    11;",
                "why": "Without `return`, the value is worked out and dropped, and the call returns `undefined` for adults.\n"
              }
            ],
            "why": "One condition per case, in the sentence's order, an `else` for everyone else, and a `return` on every path.\n",
            "hints": [
              {
                "when": "you used the age > 65 line",
                "say": "Work it through with an age of exactly 65. Which price does the sentence give, and which does that line give?\n"
              },
              {
                "when": "you used the 11; line",
                "say": "What sends a value out of the function?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "A late fee with a default",
            "prompt": "\"`lateFee` takes how many days late a library book is, and returns the fee: 0.25 for each day late, or 0 if it is not late (0 days or fewer).\" The starter sets `fee` to 0 and returns it at the end. In place of the TODO, write the `if` that changes it — *The if statement* shows how. Then, in one sentence: which test would catch `if (daysLate)`?\n",
            "exportName": "lateFee",
            "filename": "late.js",
            "starter": "function lateFee(daysLate) {\n  let fee = 0;\n  // TODO: put your code here\n  return fee;\n}\n",
            "tests": [
              {
                "label": "lateFee(4) → 1",
                "body": "return subject(4) === 1;"
              },
              {
                "label": "lateFee(1) → 0.25",
                "body": "return subject(1) === 0.25;"
              },
              {
                "label": "lateFee(0) → 0",
                "body": "return subject(0) === 0;"
              },
              {
                "label": "returned early: lateFee(-2) → 0",
                "body": "return subject(-2) === 0;"
              }
            ],
            "hints": [
              {
                "when": "lateFee(-2) gives -0.5",
                "say": "A negative number of days means the book came back early. Either the `if` is missing, or it asks the falsy list — and `-2` is not on it. Ask the exact question: is it more than 0 days?\n"
              },
              {
                "when": "lateFee(1) gives 0",
                "say": "One day late is late. Check whether your condition includes 1.\n"
              },
              {
                "when": "every test gives 0",
                "say": "`fee` must be changed inside the `if` — `fee = …` — or the default is what comes back every time.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "A third fee",
            "prompt": "`parkingFee` takes how many hours a car was parked and returns the fee: free for up to 1 hour, and 4 for anything longer. The rule has changed: more than 4 hours now costs 10. Change the function so it returns all three fees. Then, in one sentence: why would a new `if` added after the existing `if` / `else` never run?\n",
            "exportName": "parkingFee",
            "filename": "parking.js",
            "starter": "function parkingFee(hours) {\n  // TODO: put your code here\n  if (hours <= 1) {\n    return 0;\n  } else {\n    return 4;\n  }\n}\n",
            "tests": [
              {
                "label": "parkingFee(1) → 0",
                "body": "return subject(1) === 0;"
              },
              {
                "label": "parkingFee(2) → 4",
                "body": "return subject(2) === 4;"
              },
              {
                "label": "parkingFee(4) → 4",
                "body": "return subject(4) === 4;"
              },
              {
                "label": "parkingFee(4.5) → 10",
                "body": "return subject(4.5) === 10;"
              },
              {
                "label": "parkingFee(9) → 10",
                "body": "return subject(9) === 10;"
              }
            ],
            "hints": [
              {
                "when": "parkingFee(9) gives 4",
                "say": "Follow 9 through your code. Is the new condition ever reached? Every path of the old `if` / `else` already ends in a `return`, so a line after it never runs. The new case has to be part of the chain.\n"
              },
              {
                "when": "parkingFee(4) gives 10",
                "say": "\"More than 4 hours\" does not include 4. Check `>` against `>=`.\n"
              },
              {
                "when": "parkingFee(2) gives 10",
                "say": "Which condition is checked first? 2 hours is not more than 4: look at the condition that returns 10.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "The battery note",
            "prompt": "\"`batteryNote` takes a battery's charge in percent and whether it is plugged in, a boolean. It returns `\"charging\"` whenever it is plugged in, `\"low\"` below 20 percent, and `\"ok\"` otherwise.\" Write your answer between the braces, with a `return` on every path — *The if statement* shows how. Then, in one sentence: which test catches a version that checks the percent first?\n",
            "exportName": "batteryNote",
            "filename": "battery.js",
            "starter": "function batteryNote(percent, charging) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "batteryNote(80, false) → \"ok\"",
                "body": "return subject(80, false) === 'ok';"
              },
              {
                "label": "batteryNote(10, false) → \"low\"",
                "body": "return subject(10, false) === 'low';"
              },
              {
                "label": "batteryNote(20, false) → \"ok\"",
                "body": "return subject(20, false) === 'ok';"
              },
              {
                "label": "batteryNote(10, true) → \"charging\"",
                "body": "return subject(10, true) === 'charging';"
              },
              {
                "label": "batteryNote(100, true) → \"charging\"",
                "body": "return subject(100, true) === 'charging';"
              }
            ],
            "hints": [
              {
                "when": "batteryNote(10, true) gives \"low\"",
                "say": "\"Whenever it is plugged in\" comes before everything else. Which condition does your chain ask first?\n"
              },
              {
                "when": "batteryNote(20, false) gives \"low\"",
                "say": "\"Below 20\" does not include 20. Check `<` against `<=`.\n"
              },
              {
                "when": "batteryNote(80, false) gives undefined",
                "say": "Follow 80 and `false` through your code. Which path do they take, and does it reach a `return`?\n"
              },
              {
                "when": "the error says Maximum call stack size exceeded",
                "say": "Your answer calls `batteryNote` from inside `batteryNote`. Each branch's answer is a value: `\"charging\"`, `\"low\"` or `\"ok\"`.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review two labels",
            "prompt": "A code assistant wrote these. `stockLabel` should return `\"plenty\"` above 10, `\"low\"` from 1 to 10, and `\"none\"` at 0. `scoreText` takes a score that is `null` until a game is played — and `0` is a real score. Read them, then answer each question.\n",
            "filename": "labels.js",
            "code": "function stockLabel(count) {\n  if (count > 10) {\n    return \"plenty\";\n  } else if (count > 0) {\n    return \"low\";\n  }\n}\n\nfunction scoreText(score) {\n  if (score) {\n    return `Score: ${score}`;\n  } else {\n    return \"No score yet\";\n  }\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "What does `stockLabel(0)` return? Type it.",
                "accept": [
                  "undefined"
                ],
                "miss": "**`0 > 10` and `0 > 0` are both `false`,** and there is no `else`. The function reaches its closing brace with no `return`.\n",
                "why": "`undefined`. The description's third case, `\"none\"` at 0, was never written.\n"
              },
              {
                "id": "c2",
                "question": "What does `scoreText(0)` return?",
                "choices": [
                  "\"Score: 0\"",
                  "\"No score yet\""
                ],
                "accept": [
                  "\"No score yet\""
                ],
                "miss": "**`if (score)` asks the falsy list,** and `0` is on it, so a real score of zero takes the `else`.\n",
                "why": "`\"No score yet\"`, for a game that was played.\n"
              },
              {
                "id": "c3",
                "question": "Which rewrite of line 10 is right?",
                "choices": [
                  "if (score !== null) {",
                  "if (score > 0) {",
                  "if (!score) {"
                ],
                "accept": [
                  "if (score !== null) {"
                ],
                "why": "`score !== null` asks exactly \"has a game been played?\", and a real score of `0` passes it. `score > 0` still treats a score of `0` as no score. `!score` gives the two answers the wrong way round, and still treats `0` as no score.\n"
              },
              {
                "id": "c4",
                "question": "Which change makes `stockLabel` right?",
                "choices": [
                  "Replace line 6 with } else { return \"none\"; }",
                  "Add return \"low\"; after line 6",
                  "Replace line 6 with } else if (count < 0) { return \"none\"; }"
                ],
                "accept": [
                  "Replace line 6 with } else { return \"none\"; }"
                ],
                "miss": "**Work `0` through each change.** Adding `return \"low\";` gives `\"low\"` for 0. In the `count < 0` version, `0 < 0` is `false`, so 0 still reaches the closing brace with no `return`.\n",
                "why": "An `else` with `return \"none\"`: now every path returns.\n"
              }
            ],
            "hints": [
              {
                "when": "you said \"low\" for stockLabel(0)",
                "say": "Work out `0 > 0` on its own. Is zero greater than itself?\n"
              },
              {
                "when": "you chose the Score line for scoreText(0)",
                "say": "What does `if` do with a value that is not a boolean? Is `0` on the falsy list?\n"
              }
            ]
          }
        ]
      }
    },
    "ternary": {
      "id": "ternary",
      "title": "The ternary operator",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.ternary` became its own concept and lesson by D-062, between *The if statement* and *What &&, || and ?? evaluate to*: an `if` chooses what runs, a ternary chooses between two values, and `&&`, `||` and `??` choose a value from their operands. Until D-062 it was the last step of the `if` lesson, its smallest step with a handful of items (Nick: \"I worry ternaries might not get a fair amount of content, examples, practice\"); that step's content starts this lesson. The name and the official name at first use are 01-explanations rule 6a; the reasons for choosing an `if` or a ternary, rather than a rule, are rule 6b. The writing direction, from a sentence to a ternary with a first try that fails, is rule 5a, carried on from *Writing a function's answer* and *The if statement*. `flow-calendar` asks for a ternary (D-061). The examples and the order of the steps are derived from those rules, pending Nick's review. Procedural, explain first (D-061 item 1).",
      "concepts": [
        "flow.ternary"
      ],
      "lede": "*The if statement* chose which lines run. Often all an `if` chooses is a value: 0 or 12, `\"icy\"` or `\"clear\"`. The ternary operator, JavaScript's second way to branch, makes that choice in one expression. This lesson shows how it is worked out, where it can go, how to write one from a sentence, how to read one inside another, and when an `if` reads better.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The if statement · Writing a function's answer · Arithmetic operators · Truthiness · What &&, || and ?? evaluate to"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Operand** — a value an operator works on: in `a + b`, `a` and `b`. **Ternary operator** — `condition ? a : b`, which produces one of two values. Its official name is the *conditional operator*; \"ternary\" means it has three operands. **Expression** — code that produces a value, such as `a + b`, `temp < 0` or a whole ternary. **Statement** — an instruction that does something and produces no value, such as `const x = 5;` or an `if`.",
          "entries": [
            "**Operand** — a value an operator works on: in `a + b`, `a` and `b`.",
            "**Ternary operator** — `condition ? a : b`, which produces one of two values. Its official name is the *conditional operator*; \"ternary\" means it has three operands.",
            "**Expression** — code that produces a value, such as `a + b`, `temp < 0` or a whole ternary.",
            "**Statement** — an instruction that does something and produces no value, such as `const x = 5;` or an `if`."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on *The if statement*, from memory. Press **I don't know** if\nyou have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let fee = 12;\nconst age = 3;\nif (age < 5) {\n  fee = 0;\n}\nfee\n",
                    "accept": [
                      "0"
                    ],
                    "miss": "**`3 < 5` is `true`, so the block runs and sets `fee` to `0`.** The starting value, `12`, is replaced.\n",
                    "why": "`0`.\n"
                  },
                  {
                    "code": "function grade(mark) {\n  if (mark >= 80) {\n    return \"A\";\n  } else if (mark >= 60) {\n    return \"B\";\n  }\n}\ngrade(45)\n",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**Both conditions are `false` for `45`, and there is no `else`.** The function reaches its closing `}` without a `return`, so it returns `undefined`.\n",
                    "why": "`undefined`.\n"
                  },
                  {
                    "code": "const code = \"\";\nif (code) {\n  console.log(\"Code applied\");\n} else {\n  console.log(\"No code\");\n}\n",
                    "choices": [
                      "Code applied",
                      "No code"
                    ],
                    "accept": [
                      "No code"
                    ],
                    "miss": "**`\"\"` is on the falsy list,** so the `else` block runs.\n",
                    "why": "`No code`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what a ternary produces for a given input, working out the condition first",
                "I can use a ternary where a value goes: after `return`, after `=`, in a template literal, as an argument",
                "I can say why `const x = if …` is an error and a ternary in the same place is not",
                "I can write a ternary from a sentence, with the two values on the right sides",
                "I can read a ternary inside another, and say when an `if` reads better"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Choosing between two values: the ternary operator",
          "body": [
            {
              "p": "A museum charges 12 for entry, and children under 5 get in free. Here is that\nchoice written with an `if`: a variable made with `let`, set in each branch."
            },
            {
              "code": "const age = 30;\n\nlet fee;\nif (age < 5) {\n  fee = 0;\n} else {\n  fee = 12;\n}\nconsole.log(fee);   // 12",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "The six lines from `let fee;` to the closing `}` do one thing: choose between\nthe values 0 and 12. JavaScript has an operator for exactly that choice, the\n**ternary operator**. Here is the same program written with it:"
            },
            {
              "code": "const age = 30;\n\nconst fee = age < 5 ? 0 : 12;\nconsole.log(fee);   // 12",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "**Why it is called the ternary operator.** An operator takes values, called\nits **operands**, and produces a new value. Operators are often described by\nhow many operands they take:"
            },
            {
              "table": {
                "head": [
                  "Operands",
                  "Described as",
                  "Example"
                ],
                "rows": [
                  [
                    "one",
                    "unary",
                    "`!done`"
                  ],
                  [
                    "two",
                    "binary",
                    "`a + b`, `temp < 0`"
                  ],
                  [
                    "three",
                    "ternary",
                    "`age < 5 ? 0 : 12`"
                  ]
                ]
              }
            },
            {
              "p": "The ternary operator is the only JavaScript operator that takes three\noperands: a condition and two values. That is why \"ternary\" became its\neveryday name. The JavaScript standard calls it the **conditional operator**.\nBoth names find it when you search; the symbols `?` and `:` on their own do not\nsearch well."
            },
            {
              "p": "**Why it counts as an operator.** It is written with two symbols, `?` and `:`,\nwith its operands between them, so it can look more like a piece of syntax,\nsuch as the parentheses of an `if`. What makes it an operator is what it does:\nlike `+`, it takes values and produces a value. An `if` produces no value; it\nonly decides which lines run. The next step shows why that difference\nmatters."
            },
            {
              "p": "**Its three parts.**"
            },
            {
              "code": "condition ? valueIfTrue : valueIfFalse",
              "lang": "text"
            },
            {
              "p": "The condition comes first, before the `?`. The value after the `?` is the\nanswer when the condition is `true`. The value after the `:` is the answer when\nthe condition is `false`. Read aloud, `age < 5 ? 0 : 12` is \"is `age` under 5?\nthen 0; otherwise 12\"."
            },
            {
              "p": "**How it is worked out.** Two moves, every time. Here they are with `age` at\n`3`, then at `30`:"
            },
            {
              "code": "age is 3\n  work out the condition     3 < 5   -> true\n  take the value after ? if true, after : if false\n    true takes the value after ?     -> 0\n\nage is 30\n  work out the condition     30 < 5  -> false\n  take the value after ? if true, after : if false\n    false takes the value after :    -> 12",
              "lang": "text"
            },
            {
              "p": "Only the value on the chosen side is worked out. The value on the other side is\nskipped, as an `if` skips the block it does not choose."
            },
            {
              "p": "A second example, with text instead of numbers:"
            },
            {
              "code": "const temp = 4;\nconst roads = temp < 0 ? \"icy\" : \"clear\";   // 4 < 0 is false: \"clear\"",
              "lang": "js",
              "file": "roads.js"
            },
            {
              "p": "Type the value each produces, with its quotes if it is text."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const hour = 9;\nhour < 12 ? \"morning\" : \"afternoon\"\n",
                    "accept": [
                      "\"morning\"",
                      "morning",
                      "'morning'"
                    ],
                    "why": "`\"morning\"`. `9 < 12` is `true`, so the value after `?` is taken.\n"
                  },
                  {
                    "code": "const age = 12;\nage < 12 ? \"child\" : \"adult\"\n",
                    "accept": [
                      "\"adult\"",
                      "adult",
                      "'adult'"
                    ],
                    "miss": "**`12 < 12` is `false`:** 12 is not less than itself. `false` takes the value after `:`.\n",
                    "why": "`\"adult\"`.\n"
                  },
                  {
                    "question": "In `x > 10 ? \"big\" : \"small\"`, which part is worked out first?",
                    "choices": [
                      "x > 10",
                      "\"big\"",
                      "\"small\""
                    ],
                    "accept": [
                      "x > 10"
                    ],
                    "why": "`x > 10`, the condition. Its answer decides which one of the two values is used; the other is never worked out.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "An expression, not a statement",
          "body": [
            {
              "p": "An **expression** is code that produces a value: `3 + 4`, `temp < 0`,\n`\"icy\"`, a call like `Math.max(2, 9)`. A **statement** is an instruction that\ndoes something: `const roads = \"icy\";`, `return 12;`, an `if`. A statement\nproduces no value. *Your first program* called a statement \"one instruction\";\nthis is the same idea, set beside the expression."
            },
            {
              "p": "The whole of `age < 5 ? 0 : 12` is an expression: it produces `0` or `12`. An\n`if` is a statement: it decides which statements run, and produces nothing.\nThat difference decides where each one can go."
            },
            {
              "p": "The next example is **wrong on purpose**. It tries to use an `if` as a value,\nto the right of `=`:"
            },
            {
              "code": "const temp = -3;\nconst roads = if (temp < 0) { \"icy\" } else { \"clear\" };",
              "lang": "js",
              "file": "roads.js"
            },
            {
              "p": "Node refuses it:"
            },
            {
              "code": "const roads = if (temp < 0) { \"icy\" } else { \"clear\" };\n              ^^\n\nSyntaxError: Unexpected token 'if'",
              "lang": "text"
            },
            {
              "p": "A `SyntaxError` means JavaScript could not read the program at all, so none of\nit ran, not even the first line. After `=`, JavaScript expects an expression,\nand `if` cannot start one."
            },
            {
              "p": "The reverse goes wrong too. The two values in a ternary must be expressions, so\na statement such as `return` cannot go inside one. This version is also\n**wrong on purpose**:"
            },
            {
              "code": "function entryFee(age) {\n  age < 5 ? return 0 : return 12;\n}",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "code": "  age < 5 ? return 0 : return 12;\n            ^^^^^^\n\nSyntaxError: Unexpected token 'return'",
              "lang": "text"
            },
            {
              "p": "Put one `return` in front of the whole ternary instead. The ternary produces\nthe value, and `return` sends it out:"
            },
            {
              "code": "function entryFee(age) {\n  return age < 5 ? 0 : 12;\n}",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "One more thing follows from the ternary being an expression. Compare the two\nways of choosing the fee:"
            },
            {
              "code": "let fee;                        // made first, with no value yet\nif (age < 5) {\n  fee = 0;                      // given its value later, in a branch\n} else {\n  fee = 12;\n}\n\nconst fee2 = age < 5 ? 0 : 12;  // given its value on the line that makes it",
              "lang": "js",
              "file": "fee.js"
            },
            {
              "p": "The `if` version has to use `let`, because `fee` gets its value after the line\nthat makes it. The ternary version gives the variable its value on the line\nthat makes it, so it can be a `const`. A `const` tells anyone reading the code\nthat the variable keeps that value from then on."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line is valid JavaScript?",
                    "choices": [
                      "const size = n > 9 ? \"big\" : \"small\";",
                      "const size = if (n > 9) \"big\" else \"small\";",
                      "n > 9 ? return \"big\" : return \"small\";"
                    ],
                    "accept": [
                      "const size = n > 9 ? \"big\" : \"small\";"
                    ],
                    "miss": "**An `if` produces no value, so it cannot go after `=`.** And `return` is a statement, so it cannot go inside a ternary; it goes in front of the whole ternary.\n",
                    "why": "`const size = n > 9 ? \"big\" : \"small\";`.\n"
                  },
                  {
                    "question": "Is `temp < 0` an expression or a statement?",
                    "choices": [
                      "An expression",
                      "A statement"
                    ],
                    "accept": [
                      "An expression"
                    ],
                    "why": "An expression. It produces a value, `true` or `false`.\n"
                  },
                  {
                    "question": "A program has three `console.log` lines, then a fourth line, `const word = if (ok) { \"yes\" } else { \"no\" };`. How many lines does it print before the error? Type the number.",
                    "accept": [
                      "0",
                      "zero",
                      "none"
                    ],
                    "miss": "**A `SyntaxError` stops the program before any of it runs.** JavaScript reads the whole file first, and cannot read this one.\n",
                    "why": "`0`. Nothing runs.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Where a ternary can go",
          "body": [
            {
              "p": "Because a ternary produces a value, it can go anywhere a value can go. Four\nplaces come up all the time."
            },
            {
              "p": "**After `return`**, to send the chosen value out of a function:"
            },
            {
              "code": "function parity(n) {\n  return n % 2 === 0 ? \"even\" : \"odd\";\n}",
              "lang": "js",
              "file": "parity.js"
            },
            {
              "p": "**After `=`**, to give a variable its value:"
            },
            {
              "code": "const speed = 72;\nconst status = speed > 65 ? \"too fast\" : \"within the limit\";   // \"too fast\"",
              "lang": "js",
              "file": "speed.js"
            },
            {
              "p": "**Inside a template literal**, between `${` and `}`:"
            },
            {
              "code": "const isOpen = false;\nconsole.log(`Door ${isOpen ? \"open\" : \"closed\"}`);   // Door closed",
              "lang": "js",
              "file": "door.js"
            },
            {
              "p": "**As an argument**, the value handed to a function call:"
            },
            {
              "code": "const isMember = true;\nconsole.log(isMember ? \"Member price\" : \"Full price\");   // Member price",
              "lang": "js",
              "file": "price.js"
            },
            {
              "p": "**Inside a bigger expression, put it in parentheses.** The order operators are\nworked out in is their **precedence**, from *Arithmetic operators*. The ternary\ncomes almost last: arithmetic, comparisons, `===`, `&&` and `||` all go before\nit. Of the operators in this course so far, only `=` and its relatives, such\nas `+=`, come after it. So in a calculation,\nthe ternary has to be wrapped in parentheses to be worked out as one piece."
            },
            {
              "p": "Say a room costs 80 a night, plus a cleaning fee of 20 for stays under 3\nnights:"
            },
            {
              "code": "const nights = 2;\nconst cost = 80 * nights + (nights < 3 ? 20 : 0);   // 180",
              "lang": "js",
              "file": "stay.js"
            },
            {
              "p": "The next line is **wrong on purpose**: it leaves the parentheses out."
            },
            {
              "code": "const cost = 80 * nights + nights < 3 ? 20 : 0;     // 0",
              "lang": "js",
              "file": "stay.js"
            },
            {
              "p": "Without parentheses, `*` and `+` go first: `80 * 2 + 2` is `162`. Then `<`:\n`162 < 3` is `false`. Only then the ternary: `false` takes the value after `:`,\nso `cost` is `0`. The ternary's condition swallowed the whole calculation."
            },
            {
              "p": "Inside `${ }` in a template literal, no parentheses are needed: `${` and `}`\nalready hold one whole expression."
            },
            {
              "p": "Type the value each produces, with its quotes if it is text."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const n = 4;\n`${n} is ${n % 2 === 0 ? \"even\" : \"odd\"}`\n",
                    "accept": [
                      "\"4 is even\"",
                      "4 is even",
                      "'4 is even'",
                      "`4 is even`"
                    ],
                    "why": "`\"4 is even\"`. `4 % 2 === 0` is `true`.\n"
                  },
                  {
                    "code": "const base = 10;\nconst large = false;\nbase + (large ? 5 : 0)\n",
                    "accept": [
                      "10"
                    ],
                    "why": "`10`. The parentheses make the ternary one piece: `large` is `false`, so it gives `0`, and `10 + 0` is `10`.\n"
                  },
                  {
                    "code": "const base = 10;\nconst items = 2;\nbase + items > 5 ? 1 : 0\n",
                    "accept": [
                      "1"
                    ],
                    "miss": "**With no parentheses, `+` and `>` go before the ternary.** `10 + 2` is `12`, `12 > 5` is `true`, and `true` takes the value after `?`.\n",
                    "why": "`1`. Not `11`: the ternary's condition is the whole of `base + items > 5`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "When the condition is not a boolean",
          "body": [
            {
              "p": "A ternary's condition works like an `if`'s. It does not have to be `true` or\n`false`: any value is checked against the falsy list from *Truthiness*."
            },
            {
              "table": {
                "head": [
                  "Falsy: the value after `:` is taken",
                  "Everything else takes the value after `?`, including"
                ],
                "rows": [
                  [
                    "`false`, `0`, `\"\"`, `null`, `undefined`, `NaN`",
                    "`\"0\"`, `\" \"`, `\"false\"`, `-1`"
                  ]
                ]
              }
            },
            {
              "code": "const nickname = \"\";\nconst shown = nickname ? nickname : \"guest\";   // \"guest\"",
              "lang": "js",
              "file": "greet.js"
            },
            {
              "p": "`\"\"` is on the falsy list, so `shown` is `\"guest\"`. Here that is the right\nanswer: an empty nickname means there is no nickname."
            },
            {
              "p": "The same trap as in *The if statement* is waiting when `0` or `\"\"` can be a\nreal answer. A score is `null` until a game is played, and `0` is a real\nscore:"
            },
            {
              "code": "const score = 0;\nconst text = score ? `Score: ${score}` : \"No score yet\";   // \"No score yet\"",
              "lang": "js",
              "file": "score.js"
            },
            {
              "p": "The game was played and scored 0, but `0` is falsy, so the ternary takes the\nvalue after `:`. The condition asked \"is `score` truthy?\" when the question\nwas \"has a game been played?\". Asking that exactly fixes it:"
            },
            {
              "code": "const text = score !== null ? `Score: ${score}` : \"No score yet\";   // \"Score: 0\"",
              "lang": "js",
              "file": "score.js"
            },
            {
              "p": "A ternary of the shape `x ? x : fallback`, like `shown` above, is so common\nthat JavaScript has shorter ways to write it. The next lesson, *What &&, || and\n?? evaluate to*, shows them, and the same choice between the falsy list and the\nexact question."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const promo = \"\";\npromo ? \"code entered\" : \"no code\"\n",
                    "accept": [
                      "\"no code\"",
                      "no code",
                      "'no code'"
                    ],
                    "miss": "**`\"\"` is on the falsy list,** so the ternary takes the value after `:`.\n",
                    "why": "`\"no code\"`.\n"
                  },
                  {
                    "code": "const answer = \"0\";\nanswer ? \"answered\" : \"skipped\"\n",
                    "accept": [
                      "\"answered\"",
                      "answered",
                      "'answered'"
                    ],
                    "miss": "**`\"0\"` is text, one character long, and not on the falsy list.** Only the empty string `\"\"` is falsy.\n",
                    "why": "`\"answered\"`.\n"
                  },
                  {
                    "question": "`secondsLeft` is `null` when no timer is running, and `0` when a timer has just run out. Which ternary gives `\"No timer\"` only when no timer is running?",
                    "choices": [
                      "secondsLeft ? \"Running\" : \"No timer\"",
                      "secondsLeft === null ? \"No timer\" : \"Running\"",
                      "secondsLeft > 0 ? \"Running\" : \"No timer\""
                    ],
                    "accept": [
                      "secondsLeft === null ? \"No timer\" : \"Running\""
                    ],
                    "miss": "**Work `0` through each one.** The first asks the falsy list, and `0` is on it. In the third, `0 > 0` is `false`. Both say \"No timer\" for a timer that has just run out.\n",
                    "why": "`secondsLeft === null ? \"No timer\" : \"Running\"`. It asks exactly \"is no timer running?\".\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "From a sentence to a ternary",
          "body": [
            {
              "p": "The four moves from *Writing a function's answer* still apply. For a ternary,\nthe second move finds two things in the sentence: the question, which becomes\nthe condition, and the two answers. The third move has one extra decision:\n**which answer goes after the `?`**. The value after `?` is for when the\ncondition is `true`, whatever order the sentence names the two answers in."
            },
            {
              "p": "**Example 1.** \"`busFare` takes a rider's age and returns the bus fare: 2.5,\nor 1 for riders under 18.\""
            },
            {
              "p": "Here is a common first try. It follows the sentence's order: 2.5 is named\nfirst, so it went first."
            },
            {
              "code": "function busFare(age) {\n  return age < 18 ? 2.5 : 1;\n}",
              "lang": "js",
              "file": "fare.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✗ busFare(30) → 2.5\n✗ busFare(12) → 1\n✗ busFare(18) → 2.5",
              "lang": "text"
            },
            {
              "p": "The first test that failed is `busFare(30)`. Work it through by hand, with\n`age` holding `30`. The condition `30 < 18` is `false`, and `false` takes the\nvalue after `:`, which is `1`. So `console.log(busFare(30));` prints:"
            },
            {
              "code": "1",
              "lang": "text"
            },
            {
              "p": "The sentence says riders under 18 pay 1, so 1 belongs with a `true` answer to\n`age < 18`: after the `?`. The two values are on the wrong sides. There are two\nways to fix it, and both are right. Swap the values, or keep the values where\nthey are and ask the opposite question, `age >= 18 ? 2.5 : 1`. Pick the\ncondition that reads more naturally to you."
            },
            {
              "code": "function busFare(age) {\n  // under 18? -> 1; everyone else -> 2.5\n  // the answer for true goes after the ?\n  return age < 18 ? 1 : 2.5;\n}",
              "lang": "js",
              "file": "fare.js"
            },
            {
              "p": "All three tests pass."
            },
            {
              "p": "**Example 2.** \"`orderTotal` takes an order's subtotal and how many items are in\nit, and returns the subtotal plus shipping. Shipping is free for more than\n10 items, and 4 otherwise.\""
            },
            {
              "p": "Here is a common first try. It writes the sentence left to right, \"the\nsubtotal plus\" and then the shipping:"
            },
            {
              "code": "function orderTotal(subtotal, items) {\n  return subtotal + items > 10 ? 0 : 4;\n}",
              "lang": "js",
              "file": "order.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✗ orderTotal(30, 2) → 34\n✗ orderTotal(30, 12) → 30\n✓ orderTotal(0, 1) → 4",
              "lang": "text"
            },
            {
              "p": "The first test that failed is `orderTotal(30, 2)`. Work it through by hand,\nwith `subtotal` holding `30` and `items` holding `2`, in precedence order.\n`+` goes first: `30 + 2` is `32`. Then `>`: `32 > 10` is `true`. Then the\nternary: `true` takes the value after `?`, which is `0`. The function returns\n`0`, the shipping alone, and the subtotal is lost. As in the room example\nin *Where a ternary can go*, the ternary's condition swallowed the\ncalculation."
            },
            {
              "p": "The test that passed, `orderTotal(0, 1)`, passed by luck: `0 + 1 > 10` is\n`false`, which gives `4`, and the right answer, `0 + 4`, is also `4`. A test\nthat passes shows only that the code is right for that one input."
            },
            {
              "p": "The fix puts the ternary in parentheses, so it is worked out as one piece and\nits value added to `subtotal`:"
            },
            {
              "code": "function orderTotal(subtotal, items) {\n  // shipping: more than 10 items? -> 0; otherwise -> 4\n  // in parentheses, so its value is added to subtotal\n  return subtotal + (items > 10 ? 0 : 4);\n}",
              "lang": "js",
              "file": "order.js"
            },
            {
              "p": "All three tests pass. `items > 10 ? subtotal : subtotal + 4` passes them too:\nit puts the addition inside the two values instead."
            },
            {
              "p": "**Example 3: you choose the line.** \"`roomFor` takes a number of guests and\nreturns `\"suite\"` for more than 4 guests, and `\"double\"` otherwise.\""
            },
            {
              "code": "function roomFor(guests) {\n  // TODO\n}",
              "lang": "js",
              "file": "room.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes where the TODO is?",
                    "choices": [
                      "return guests > 4 ? \"double\" : \"suite\";",
                      "return guests > 4 ? \"suite\" : \"double\";",
                      "guests > 4 ? return \"suite\" : return \"double\";",
                      "return guests > 4 ? \"suite\" : roomFor(guests);"
                    ],
                    "accept": [
                      "return guests > 4 ? \"suite\" : \"double\";"
                    ],
                    "miss": "**The value after `?` is for a `true` condition.** More than 4 guests makes `guests > 4` `true`, so `\"suite\"` goes after the `?`. `return` goes in front of the whole ternary, not inside it. And the version that calls `roomFor` calls the function you are writing, which stops with `Maximum call stack size exceeded`, as in *Writing a function's answer*.\n",
                    "why": "`return guests > 4 ? \"suite\" : \"double\";`.\n"
                  },
                  {
                    "question": "\"`signText` takes whether a shop is open, a boolean, and returns `\"Open\"` or `\"Closed\"`.\" Which line?",
                    "choices": [
                      "return isOpen ? \"Closed\" : \"Open\";",
                      "return isOpen ? \"Open\" : \"Closed\";",
                      "return if (isOpen) \"Open\" else \"Closed\";"
                    ],
                    "accept": [
                      "return isOpen ? \"Open\" : \"Closed\";"
                    ],
                    "miss": "**The value after `?` is for `true`, the one after `:` for `false`.** And `if` produces no value, so `return if ...` is a `SyntaxError`.\n",
                    "why": "`return isOpen ? \"Open\" : \"Closed\";`. `isOpen` is already a boolean, so it can be the whole condition.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "step",
          "label": "A ternary inside a ternary",
          "body": [
            {
              "p": "Each of a ternary's two values can be any expression, including another\nternary. That lets one expression choose among three values. Here is `sizeFor`\nfrom *The if statement* written that way:"
            },
            {
              "code": "function sizeFor(chest) {\n  return chest < 90 ? \"S\" : chest < 100 ? \"M\" : \"L\";\n}",
              "lang": "js",
              "file": "size.js"
            },
            {
              "p": "**How to read it.** The second ternary is the value after the first `:`.\nJavaScript reads the line as if it had these parentheses:"
            },
            {
              "code": "chest < 90 ? \"S\" : (chest < 100 ? \"M\" : \"L\")",
              "lang": "text"
            },
            {
              "p": "With `chest` at `95`:"
            },
            {
              "code": "work out the first condition    95 < 90  -> false\ntake the value after :          another ternary\nwork out its condition          95 < 100 -> true\ntake the value after ?          \"M\"",
              "lang": "text"
            },
            {
              "p": "That is the same as an `else if` chain: the first condition that is `true`\nwins, and the last value is used when every condition is `false`."
            },
            {
              "p": "**Some code formats a chain like this on purpose,** one condition per line, so\nit reads like a table:"
            },
            {
              "code": "function sizeFor(chest) {\n  return chest < 90 ? \"S\"\n    : chest < 100 ? \"M\"\n    : \"L\";\n}",
              "lang": "js",
              "file": "size.js"
            },
            {
              "p": "Each line reads \"this condition, this value\", and the last line is the value\nfor everything else."
            },
            {
              "p": "Another common layout gives every part its own line. The condition comes\nfirst; the value after `?` starts the next line, indented; the `:` starts the\nline after that. A ternary nested inside another moves one indent further in,\nso the indentation shows which `?` each `:` belongs to:"
            },
            {
              "code": "function sizeFor(chest) {\n  return chest < 90\n    ? \"S\"\n    : chest < 100\n      ? \"M\"\n      : \"L\";\n}",
              "lang": "js",
              "file": "size.js"
            },
            {
              "p": "Both layouts are the same code to JavaScript, which ignores the line breaks\nand spaces. They differ only in how easy they are for a person to read."
            },
            {
              "p": "**A ternary inside the `?` side is harder.** The next example is legal and\ngives the right answers. It is shown because it is hard to read. A member of\n5 years or more is `\"gold\"`, any other member is `\"silver\"`, and someone who is\nnot a member is `\"guest\"`:"
            },
            {
              "code": "const level = isMember ? years >= 5 ? \"gold\" : \"silver\" : \"guest\";",
              "lang": "js",
              "file": "level.js"
            },
            {
              "p": "To read it, you have to pair each `:` with its `?`. Each `:` belongs to the\nnearest `?` before it that has no `:` yet. So `: \"silver\"` pairs with the\nsecond `?`, and `: \"guest\"` pairs with the first. Readers have to do that\npairing slowly, every time. That is why many people find nested ternaries hard\nto read, and why some teams use a **linter**, a tool that reads code and\nflags patterns the team has agreed to avoid, to reject them. ESLint is the most\ncommon linter for JavaScript.\nOthers allow the one-per-line chain and nothing else. Parentheses help:\n`isMember ? (years >= 5 ? \"gold\" : \"silver\") : \"guest\"`. So does the layout\nwith every part on its own line, because the indentation does the pairing for\nyou:"
            },
            {
              "code": "const level = isMember\n  ? years >= 5\n    ? \"gold\"\n    : \"silver\"\n  : \"guest\";",
              "lang": "js",
              "file": "level.js"
            },
            {
              "p": "`\"gold\"` and `\"silver\"` sit one indent deeper, under the condition they belong\nto, and `: \"guest\"` lines up with the first `?`."
            },
            {
              "p": "Even laid out like this, `level` is harder to read than `sizeFor`, and the\ndifference is where the nested ternary sits. In `sizeFor`, each new condition\ncomes after a `:`, so you read straight down: a condition, its value, and if\nnot, the next condition. In `level`, the nested ternary comes after the `?`,\nso to see what happens when `isMember` is `false` you have to look past the\nwhole inner ternary and back out to the `: \"guest\"` line. That jump back is\nwhy nested ternaries, especially on the `?` side, are often avoided. An `if`\nwith an `if` inside its block shows the same choice with indentation, and\nreads top to bottom."
            },
            {
              "p": "Type the value each produces, with its quotes."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "With the `sizeFor` above, what does `sizeFor(90)` return?",
                    "accept": [
                      "\"M\"",
                      "M",
                      "'M'"
                    ],
                    "miss": "**`90 < 90` is `false`,** so the first ternary takes the value after its `:`, the second ternary. `90 < 100` is `true`.\n",
                    "why": "`\"M\"`.\n"
                  },
                  {
                    "code": "const isMember = false;\nconst years = 10;\nisMember ? years >= 5 ? \"gold\" : \"silver\" : \"guest\"\n",
                    "accept": [
                      "\"guest\"",
                      "guest",
                      "'guest'"
                    ],
                    "miss": "**The first condition is `isMember`, and it is `false`.** `false` takes the value after the `:` that pairs with the first `?`, the last one: `\"guest\"`. `years` is never looked at.\n",
                    "why": "`\"guest\"`.\n"
                  },
                  {
                    "code": "const isMember = true;\nconst years = 2;\nisMember ? years >= 5 ? \"gold\" : \"silver\" : \"guest\"\n",
                    "accept": [
                      "\"silver\"",
                      "silver",
                      "'silver'"
                    ],
                    "why": "`\"silver\"`. `isMember` is `true`, so the inner ternary is worked out, and `2 >= 5` is `false`.\n"
                  }
                ]
              }
            }
          ],
          "n": 6
        },
        {
          "beat": "step",
          "label": "An if or a ternary?",
          "body": [
            {
              "p": "Both are correct for any choice between two values, and a test cannot tell\nthem apart: it checks only the value that comes back. The reverse does not\nhold. A ternary can only produce a value, while an `if` can run any statements.\nSo the choice is about what reads more clearly. Here is how people usually\ndecide, with the reason for each."
            },
            {
              "list": [
                "**A ternary suits a choice between two values that goes somewhere:** to a name, after `return`, into a template literal, into a call. The choice stays on one line, beside where the value is used, and the name can be a `const`.",
                "**An `if` suits a path that *does* something:** prints, changes a name, runs more than one statement. A ternary can be made to do this. The next line is legal and works, and it is shown because it reads oddly:  ```js door.js isOpen ? console.log(\"Open\") : console.log(\"Closed\"); ```  It looks like a choice of value, but the value is thrown away; the work is all in what the two calls print. `if (isOpen) { … } else { … }` says plainly that it chooses what runs. Many teams' linters, such as ESLint, flag the ternary version.",
                "**An `if` suits a long condition or long values.** A ternary's advantage is that the choice reads in one glance. Once the line runs on past the edge of the editor, that advantage is gone, and the `if` with one value per line is easier to read.",
                "**For more than two values,** a ternary chain that reads straight down, like `sizeFor`, works for a few conditions. A common rule of thumb is to stop at two or three ternaries in one chain; past that, an `else if` chain reads more easily, and it is easier to add to and to put a `console.log` inside while you look for a bug. A ternary nested in the `?` side, such as `isMember ? years >= 5 ? \"gold\" : \"silver\" : \"guest\"`, almost always reads better as an `if`."
              ]
            },
            {
              "p": "This course's examples use a ternary for a short choice between two values,\nand an `if` for anything else. That is a choice, made for the reasons above,\nand other code you read will draw the line in a different place."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A function should return `\"AM\"` or `\"PM\"` for an hour from 0 to 23. Which reads better?",
                    "choices": [
                      "A ternary",
                      "An if"
                    ],
                    "accept": [
                      "A ternary"
                    ],
                    "why": "A ternary: two short values, sent out by `return`. `return hour < 12 ? \"AM\" : \"PM\";` reads in one glance.\n"
                  },
                  {
                    "question": "When a save fails, a program should print an error message and also set `retry` to `true`. Which reads better?",
                    "choices": [
                      "A ternary",
                      "An if"
                    ],
                    "accept": [
                      "An if"
                    ],
                    "miss": "**The path does two things and produces no value.** A ternary chooses a value; an `if` chooses what runs.\n",
                    "why": "An `if`. Two statements run on one path.\n"
                  },
                  {
                    "question": "Is `ready ? console.log(\"Go\") : console.log(\"Wait\");` legal JavaScript?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "miss": "**Each side is a call, and a call is an expression,** so the line is legal and prints one of the two words.\n",
                    "why": "Yes. It is legal and it works; it reads oddly, because it uses a choice of value to choose what runs.\n"
                  }
                ]
              }
            }
          ],
          "n": 7
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A thermostat's display shows `\"Heating\"` when the room is below the target\ntemperature and `\"Idle\"` otherwise. A function `display(temp, target)` should\nreturn that text."
            },
            {
              "p": "Write it with a ternary. Then the rule changes: more than 2 degrees above the\ntarget, the display shows `\"Cooling\"`. Write the new version twice: once as a\nternary chain formatted one per line, and once as an `if` chain. Which would\nyou keep, and why? Which values of `temp` would you test with, given a\n`target` of 20?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**A ternary, `condition ? a : b`, is an expression:** it works out the condition, then produces the value after `?` when the condition is truthy, and the value after `:` when it is falsy.",
                "**It goes wherever a value goes;** inside a bigger expression, wrap it in parentheses, because almost every operator goes before it. The value for `true` comes first, whatever order a sentence names the answers in.",
                "**An `if` chooses what runs; a ternary chooses a value.** A ternary suits a short choice between two values; an `if` suits actions, long lines and many paths."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Conditional (ternary) operator",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Conditional_operator",
              "note": "`condition ? a : b`, and chains of them."
            },
            {
              "title": "Operator precedence",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Operator_precedence",
              "note": "where the ternary sits in the order, near the bottom."
            }
          ]
        }
      ],
      "_source": "lessons/ternary.md",
      "practice": {
        "id": "ternary",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Which value comes out?",
            "prompt": "Type the value each one produces, with its quotes if it is text, or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "const hour = 13;\nhour < 12 ? \"morning\" : \"afternoon\"\n",
                "accept": [
                  "\"afternoon\"",
                  "afternoon",
                  "'afternoon'"
                ],
                "miss": "**`13 < 12` is `false`,** and `false` takes the value after `:`.\n",
                "why": "`\"afternoon\"`.\n"
              },
              {
                "id": "p2",
                "question": "To work out `isOpen ? \"Open\" : \"Closed\"`, what happens first?",
                "choices": [
                  "isOpen is checked",
                  "\"Open\" is worked out",
                  "Both values are worked out, and one is kept"
                ],
                "accept": [
                  "isOpen is checked"
                ],
                "why": "The condition, `isOpen`, is checked first. Its answer decides which value is used; the other value is never worked out.\n"
              },
              {
                "id": "p3",
                "code": "const name = \"\";\nname ? name : \"guest\"\n",
                "accept": [
                  "\"guest\"",
                  "guest",
                  "'guest'"
                ],
                "miss": "**`\"\"` is on the falsy list,** so the value after `:` is taken.\n",
                "why": "`\"guest\"`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const subtotal = 40;\nconst items = 3;\nsubtotal + items > 10 ? 0 : 5\n",
                "accept": [
                  "0"
                ],
                "miss": "**With no parentheses, `+` and `>` go before the ternary.** `40 + 3` is `43`, `43 > 10` is `true`, and `true` takes the value after `?`.\n",
                "why": "`0`. Not `45`: the condition is the whole of `subtotal + items > 10`.\n"
              },
              {
                "id": "p5",
                "code": "const t = 15;\nt < 10 ? \"cold\" : t < 20 ? \"mild\" : \"warm\"\n",
                "accept": [
                  "\"mild\"",
                  "mild",
                  "'mild'"
                ],
                "miss": "**`15 < 10` is `false`, so the value after the first `:` is used:** the second ternary. `15 < 20` is `true`.\n",
                "why": "`\"mild\"`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered \"morning\" for hour 13",
                "say": "Work out `13 < 12` on its own first. Which side does that answer take?\n"
              },
              {
                "when": "you answered \"\" for the empty name",
                "say": "Is `\"\"` truthy or falsy? A falsy condition takes the value after `:`.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict four lines, then run it",
            "prompt": "Predict each line it prints — type it exactly as it would appear, text without quotes — then run it and compare.\n",
            "filename": "cart.js",
            "code": "const promo = \"\";\nconst volume = 0;\nconst express = true;\nconsole.log(promo ? \"code entered\" : \"no code\");\nconsole.log(`Volume: ${volume === 0 ? \"muted\" : volume}`);\nconsole.log(5 + (express ? 10 : 2));\nconsole.log(\"Shipping: \" + express ? \"fast\" : \"slow\");\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(promo ? \"code entered\" : \"no code\");",
                "accept": [
                  "no code"
                ],
                "miss": "**`\"\"` is on the falsy list,** so the ternary takes the value after `:`.\n",
                "why": "`no code`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(`Volume: ${volume === 0 ? \"muted\" : volume}`);",
                "accept": [
                  "Volume: muted"
                ],
                "why": "`Volume: muted`. `0 === 0` is `true`.\n"
              },
              {
                "id": "r3",
                "code": "console.log(5 + (express ? 10 : 2));",
                "accept": [
                  "15"
                ],
                "why": "`15`. The parentheses make the ternary one piece: `10`, then `5 + 10`.\n"
              },
              {
                "id": "r4",
                "code": "console.log(\"Shipping: \" + express ? \"fast\" : \"slow\");",
                "accept": [
                  "fast"
                ],
                "miss": "**With no parentheses, `+` goes before the ternary.** It joins the text and `true` into `\"Shipping: true\"`. That is the condition, and a non-empty string is truthy, so the ternary gives `\"fast\"` alone.\n",
                "why": "`fast`. The words `Shipping: ` are lost. Parentheses around the ternary would print `Shipping: fast`.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted code entered",
                "say": "`promo` holds the empty string. Is `\"\"` truthy or falsy?\n"
              },
              {
                "when": "you predicted Shipping: fast",
                "say": "Without parentheses, which goes first: `+` or the ternary? What is the condition, then?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One condition at a time",
            "prompt": "`fanSpeed(temp)` holds one line: `return temp < 20 ? \"off\" : temp < 28 ? \"low\" : \"high\";`. Press the value, or type the number.\n",
            "items": [
              {
                "id": "t1",
                "question": "What does `fanSpeed(20)` return?",
                "choices": [
                  "\"off\"",
                  "\"low\"",
                  "\"high\""
                ],
                "accept": [
                  "\"low\""
                ],
                "miss": "**`20 < 20` is `false`** — 20 is not less than itself — so the value after the first `:` is used: the second ternary. `20 < 28` is `true`.\n",
                "why": "`\"low\"`.\n"
              },
              {
                "id": "t2",
                "question": "For `fanSpeed(15)`, how many conditions are worked out? Type the number.",
                "accept": [
                  "1",
                  "one"
                ],
                "miss": "**`15 < 20` is `true`, so the value after the first `?` is taken.** The second ternary is on the other side, and the other side is never worked out.\n",
                "why": "`1`, and the answer is `\"off\"`.\n"
              },
              {
                "id": "t3",
                "question": "For `fanSpeed(30)`, how many conditions are worked out? Type the number.",
                "accept": [
                  "2",
                  "two"
                ],
                "miss": "**The last value, `\"high\"`, has no condition of its own.** Two are asked and both are `false`.\n",
                "why": "`2`, and the answer is `\"high\"`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "What does `fanSpeed(28)` return?",
                "choices": [
                  "\"off\"",
                  "\"low\"",
                  "\"high\""
                ],
                "accept": [
                  "\"high\""
                ],
                "why": "`\"high\"`. `28 < 20` and `28 < 28` are both `false`.\n"
              },
              {
                "id": "t5",
                "question": "Someone writes the conditions the other way round: `temp < 28 ? \"low\" : temp < 20 ? \"off\" : \"high\"`. What does it return for `10`?",
                "choices": [
                  "\"off\"",
                  "\"low\"",
                  "\"high\""
                ],
                "accept": [
                  "\"low\""
                ],
                "why": "`\"low\"`. `10 < 28` is checked first and is `true`. In that order, `\"off\"` can never come back, as in an `else if` chain.\n"
              }
            ],
            "hints": [
              {
                "when": "you said 2 for fanSpeed(15)",
                "say": "The first condition is `true`, so the value after its `?` is taken. Is the second ternary after that `?`, or after its `:`?\n"
              },
              {
                "when": "you said \"off\" for fanSpeed(20)",
                "say": "Work out `20 < 20` on its own. Is a number less than itself?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "A stock line",
            "prompt": "\"`stockLine` takes an item's name and how many are in stock, and returns a line such as `\"Lamp: in stock\"`, or `\"Lamp: sold out\"` when the count is 0.\" The starter builds the line from `status`, which is always `\"\"`. Replace the `\"\"` with a ternary that gives `\"in stock\"` when the count is above 0 and `\"sold out\"` otherwise — *The ternary operator* shows how. Then, in one sentence: why can `status` be a `const` here?\n",
            "exportName": "stockLine",
            "filename": "stock.js",
            "starter": "function stockLine(item, count) {\n  // TODO: put your code here\n  const status = \"\";\n  return `${item}: ${status}`;\n}\n",
            "tests": [
              {
                "label": "stockLine(\"Lamp\", 3) → \"Lamp: in stock\"",
                "body": "return subject('Lamp', 3) === 'Lamp: in stock';"
              },
              {
                "label": "stockLine(\"Lamp\", 0) → \"Lamp: sold out\"",
                "body": "return subject('Lamp', 0) === 'Lamp: sold out';"
              },
              {
                "label": "stockLine(\"Desk\", 1) → \"Desk: in stock\"",
                "body": "return subject('Desk', 1) === 'Desk: in stock';"
              }
            ],
            "hints": [
              {
                "when": "stockLine(\"Lamp\", 3) gives \"Lamp: sold out\"",
                "say": "The two values are on the wrong sides. With a count of 3, is your condition `true` or `false`, and which value does that take?\n"
              },
              {
                "when": "stockLine(\"Desk\", 1) gives \"Desk: sold out\"",
                "say": "One in stock is still in stock. Work your condition out with a count of 1.\n"
              },
              {
                "when": "every test gives \"Lamp: \" with nothing after it",
                "say": "`status` is still `\"\"`. The ternary goes where the `\"\"` is.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "A third result",
            "prompt": "`passLabel` takes a score and returns `\"pass\"` at 50 or more, and `\"retry\"` below. The rule has changed: 85 or more now returns `\"distinction\"`. Change the function so it returns all three. A ternary chain and an `if` chain both pass. Then, in one sentence: which did you choose, and why does it read better to you here?\n",
            "exportName": "passLabel",
            "filename": "pass.js",
            "starter": "function passLabel(score) {\n  // TODO: put your code here\n  return score >= 50 ? \"pass\" : \"retry\";\n}\n",
            "tests": [
              {
                "label": "passLabel(90) → \"distinction\"",
                "body": "return subject(90) === 'distinction';"
              },
              {
                "label": "passLabel(85) → \"distinction\"",
                "body": "return subject(85) === 'distinction';"
              },
              {
                "label": "passLabel(84) → \"pass\"",
                "body": "return subject(84) === 'pass';"
              },
              {
                "label": "passLabel(50) → \"pass\"",
                "body": "return subject(50) === 'pass';"
              },
              {
                "label": "passLabel(49) → \"retry\"",
                "body": "return subject(49) === 'retry';"
              }
            ],
            "hints": [
              {
                "when": "passLabel(90) gives \"pass\"",
                "say": "Which condition is checked first? 90 is also 50 or more, and the first `true` condition wins. Ask the narrower question first.\n"
              },
              {
                "when": "passLabel(85) gives \"pass\"",
                "say": "\"85 or more\" includes 85. Check `>` against `>=`.\n"
              },
              {
                "when": "a test gives undefined",
                "say": "Follow that test's score through your code. Which path does it take, and does that path have a `return`?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "The service charge",
            "prompt": "\"`withService` takes a restaurant bill and the number of guests, and returns the bill plus a service charge: 10 for 6 or more guests, and nothing otherwise.\" Write it with a ternary, between the braces, with `return` in front — *The ternary operator* shows how. Then, in one sentence: which test would catch a version with no parentheses?\n",
            "exportName": "withService",
            "filename": "service.js",
            "starter": "function withService(bill, guests) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "withService(50, 6) → 60",
                "body": "return subject(50, 6) === 60;"
              },
              {
                "label": "withService(50, 2) → 50",
                "body": "return subject(50, 2) === 50;"
              },
              {
                "label": "withService(120, 10) → 130",
                "body": "return subject(120, 10) === 130;"
              },
              {
                "label": "withService(0, 1) → 0",
                "body": "return subject(0, 1) === 0;"
              }
            ],
            "hints": [
              {
                "when": "withService(50, 6) gives 10 and withService(50, 2) gives 10",
                "say": "Without parentheses, `+` goes before the ternary, so the condition is the whole of `bill + guests >= 6`. Wrap the ternary in parentheses so it is worked out as one piece.\n"
              },
              {
                "when": "withService(50, 6) gives 50",
                "say": "\"6 or more\" includes 6. Check `>` against `>=`, and check which value is after the `?`.\n"
              },
              {
                "when": "every test gives undefined",
                "say": "Put `return` in front of your answer, so its value is sent out.\n"
              },
              {
                "when": "the error says Maximum call stack size exceeded",
                "say": "Your answer calls `withService` from inside `withService`. Build it from `bill`, `guests` and operators.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review two helpers",
            "prompt": "A code assistant wrote these. `discounted` should return the price with 5 off for customers of 3 years or more, and the full price for everyone else. `tempText` takes a temperature that is `null` when the sensor has no reading — and `0` is a real temperature. Read them, then answer each question.\n",
            "filename": "helpers.js",
            "code": "function discounted(price, years) {\n  return price - years >= 3 ? 5 : 0;\n}\n\nfunction tempText(temp) {\n  return temp ? `${temp}°C` : \"No reading\";\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `discounted(40, 4)`. Type the number it returns.",
                "accept": [
                  "5"
                ],
                "miss": "**With no parentheses, `-` and `>=` go before the ternary.** `40 - 4` is `36`, `36 >= 3` is `true`, and `true` takes the value after `?`.\n",
                "why": "`5`, the discount alone. It should be `35`.\n"
              },
              {
                "id": "c2",
                "question": "Which rewrite of line 2 is right?",
                "choices": [
                  "return price - (years >= 3 ? 5 : 0);",
                  "return (price - years) >= 3 ? 5 : 0;",
                  "return price - years >= 3 ? 0 : 5;"
                ],
                "accept": [
                  "return price - (years >= 3 ? 5 : 0);"
                ],
                "why": "`price - (years >= 3 ? 5 : 0)`. The second only writes the buggy order out with parentheses, so it gives the same wrong answer. The third swaps the values and still subtracts `years` from the price.\n"
              },
              {
                "id": "c3",
                "question": "What does `tempText(0)` return?",
                "choices": [
                  "\"0°C\"",
                  "\"No reading\""
                ],
                "accept": [
                  "\"No reading\""
                ],
                "miss": "**The condition asks the falsy list,** and `0` is on it, so a real temperature of zero takes the value after `:`.\n",
                "why": "`\"No reading\"`, for a sensor that read 0°C.\n"
              },
              {
                "id": "c4",
                "question": "Which rewrite of line 6 is right?",
                "choices": [
                  "return temp !== null ? `${temp}°C` : \"No reading\";",
                  "return temp > 0 ? `${temp}°C` : \"No reading\";",
                  "return !temp ? `${temp}°C` : \"No reading\";"
                ],
                "accept": [
                  "return temp !== null ? `${temp}°C` : \"No reading\";"
                ],
                "why": "`temp !== null` asks exactly \"is there a reading?\", and a real `0` passes it. `temp > 0` turns away `0` and every temperature below freezing. `!temp` gives the two answers the wrong way round.\n"
              }
            ],
            "hints": [
              {
                "when": "you said 35 for discounted(40, 4)",
                "say": "Without parentheses, which goes first: `-`, `>=` or the ternary? What is the condition, then?\n"
              },
              {
                "when": "you chose \"0°C\" for tempText(0)",
                "say": "A ternary's condition works like an `if`'s. Is `0` on the falsy list?\n"
              }
            ]
          }
        ]
      }
    },
    "conditions": {
      "id": "conditions",
      "title": "What &&, || and ?? evaluate to",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`&&`, `||` and `??` are how real JavaScript writes defaults, and they evaluate to an operand, not a boolean — so `count || 10` silently throws away a real `0`. D-018 recast `flow.conditions` as `flow.short-circuit` because this is what the lesson actually teaches, and outcome 3 is about how a program chooses. It is pass 2 of `ops.logical` (D-045): pass 1, in *Comparing and combining*, gave `&&` and `||` booleans only and said this lesson would cover the rest. `??` was met first in *When there is nothing there*, as the absence fallback; here it is set beside `||`. Primitives only (zero-prereq audit): `?.` and the object examples moved to the Objects module. Procedural, explain first (D-061 item 1). The default-value function that fails first is 01-explanations rule 5a, because the practice asks students to write one. Rebuilt 2026-10-08, pending Nick's review.",
      "concepts": [
        "flow.short-circuit"
      ],
      "lede": "In *Comparing and combining*, `&&` and `||` always had `true` or `false` on both sides. In JavaScript, put other values on either side and the operator evaluates to one of those two values, not a `true` or `false`. It also skips its right side when the left side already decides the answer. This lesson shows what each evaluates to, and how to choose between `||` and `??` when you want a default.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Comparing and combining · Truthiness · When there is nothing there · The if statement · The ternary operator"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Operand** — a value on either side of an operator: in `a || b`, `a` and `b`. **Short-circuit** — an operator stopping as soon as it knows its answer, so the other side is never worked out. **Default** — the value to use when no real value was given.",
          "entries": [
            "**Operand** — a value on either side of an operator: in `a || b`, `a` and `b`.",
            "**Short-circuit** — an operator stopping as soon as it knows its answer, so the other side is never worked out.",
            "**Default** — the value to use when no real value was given."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory: `&&` and `||` with\n`true` and `false`, the falsy list, and `??`. Type the value, or press the\nanswer. Press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const age = 16;\nconst hasTicket = false;\nage >= 13 && hasTicket\n",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**Logical AND (`&&`) is `true` only when both sides are `true`**, from *Comparing and combining*. `16 >= 13` is `true`, but `hasTicket` is `false`.\n",
                    "why": "`false`. One side is `false`, so `&&` gives `false`.\n"
                  },
                  {
                    "code": "const age = 16;\nconst hasTicket = false;\nage >= 18 || !hasTicket\n",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "miss": "**Logical OR (`||`) is `true` when at least one side is `true`.** `16 >= 18` is `false`, but `!hasTicket` is `!false`, which is `true`.\n",
                    "why": "`true`. The right side is `true`, and `||` needs only one.\n"
                  },
                  {
                    "code": "Boolean(0)",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**`0` is on the falsy list** from *Truthiness*: `false`, `0`, `\"\"`, `null`, `undefined` and `NaN`.\n",
                    "why": "`false`. `0` and `NaN` are the only falsy numbers.\n"
                  },
                  {
                    "code": "const score = 0;\nscore ?? 100\n",
                    "accept": [
                      "0"
                    ],
                    "miss": "**If you answered `100` because `0` is falsy — `??` does not ask the falsy list.** It asks only \"is this `null` or `undefined`?\", as in *When there is nothing there*.\n",
                    "why": "`0`. A score of zero is a real score, not `null` or `undefined`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say which value `&&` and `||` evaluate to when their operands are not `true` or `false`",
                "I can say when the right side of `&&`, `||` or `??` is never worked out",
                "I can choose `||` or `??` for a default, and say which values make them differ",
                "I can write a function's answer that supplies a default"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "What || and && evaluate to",
          "body": [
            {
              "p": "In *Comparing and combining*, `&&` and `||` always had `true` or `false` on\neach side, and their answer was `true` or `false` too. But JavaScript lets you\nput any value on either side: a number, a string, `null`. Then the answer is\nsomething you might not expect:"
            },
            {
              "code": "const name = \"\";\nconst shown = name || \"guest\";\nconsole.log(shown);   // guest",
              "lang": "js",
              "file": "shown.js"
            },
            {
              "p": "`shown` is not `true` or `false`. It is `\"guest\"`, the value on the right. That\nis how logical OR (`||`) works. It looks at the value on its left. If that\nvalue is truthy, `||` produces it. If it is falsy, `||` produces the value\non its right instead. The falsy values are the six from *Truthiness*: `false`,\n`0`, `\"\"`, `null`, `undefined` and `NaN`. Here `name` is `\"\"`, which is falsy,\nso `name || \"guest\"` evaluates to `\"guest\"`."
            },
            {
              "p": "Logical AND (`&&`) asks the same question about its left side, and does the\nopposite with the answer. If the left side is falsy, `&&` produces it. If it\nis truthy, `&&` produces the value on its right:"
            },
            {
              "code": "const count = 3;\nconst label = count && \"in stock\";\nconsole.log(label);   // in stock",
              "lang": "js",
              "file": "stock.js"
            },
            {
              "p": "`count` is `3`, which is truthy, so `count && \"in stock\"` evaluates to `\"in stock\"`."
            },
            {
              "p": "Either way, the answer is one of the two values you gave the operator, its\noperands, unchanged. The two operators side by side:"
            },
            {
              "table": {
                "head": [
                  "The left side is…",
                  "`a || b` evaluates to",
                  "`a && b` evaluates to"
                ],
                "rows": [
                  [
                    "truthy",
                    "`a`, the left side",
                    "`b`, the right side"
                  ],
                  [
                    "falsy",
                    "`b`, the right side",
                    "`a`, the left side"
                  ]
                ]
              }
            },
            {
              "p": "These are not new rules that replace *Comparing and combining*; they are the\nsame operators, seen in full. In `true || false`, the left side is truthy, so\nthe expression evaluates to it: `true`. When both operands are booleans, the answer is a\nboolean, which is why it looked as if `&&` and `||` always produced `true` or\n`false`."
            },
            {
              "p": "Type the value each expression evaluates to, with quotes if it is a string."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "0 || \"default\"",
                    "accept": [
                      "\"default\"",
                      "default",
                      "'default'"
                    ],
                    "miss": "**If you answered `true` — `||` produces an operand, not a boolean.** `0` is falsy, so you get the right side, the string.\n",
                    "why": "`\"default\"`.\n"
                  },
                  {
                    "code": "\"ready\" && \"go\"",
                    "accept": [
                      "\"go\"",
                      "go",
                      "'go'"
                    ],
                    "miss": "**If you answered `true` — `&&` produces an operand too.** `\"ready\"` is truthy, so the expression evaluates to its right side.\n",
                    "why": "`\"go\"`.\n"
                  },
                  {
                    "code": "null || 0",
                    "accept": [
                      "0"
                    ],
                    "miss": "**If you answered `null` or `false` — `null` is falsy, so the expression evaluates to the right side,** whatever it is. Here the right side is `0`, and the expression evaluates to it even though `0` is falsy too.\n",
                    "why": "`0`.\n"
                  },
                  {
                    "code": "\"\" && \"go\"",
                    "accept": [
                      "\"\"",
                      "''",
                      "empty string",
                      "(empty string)",
                      "the empty string",
                      "an empty string"
                    ],
                    "miss": "**If you answered `false` — `&&` produces the falsy operand itself.** `\"\"` is falsy, so `\"\"` is the answer.\n",
                    "why": "`\"\"`, the empty string.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "The other side may never be worked out",
          "body": [
            {
              "p": "To **short-circuit** is to stop as soon as the answer is known. If the left\nside of `||` is truthy, the answer is the left side, so the right side is\n**never worked out at all**. Anything written there does not happen. `&&` does\nthe same when its left side is falsy: the answer is the left side, and the\nright side is never worked out."
            },
            {
              "p": "`console.log` shows this, because it prints only when it runs. `console.log`\nalso returns `undefined`, which is why `b`, below, ends up holding\n`undefined`."
            },
            {
              "code": "const a = 3 || console.log(\"ran\");\nconsole.log(a);\n\nconst b = 0 || console.log(\"ran\");\nconsole.log(b);",
              "lang": "js",
              "file": "skipped.js"
            },
            {
              "code": "3\nran\nundefined",
              "lang": "text"
            },
            {
              "p": "The program has two calls that would print `ran`, but `ran` printed once. In\nthe line that makes `a`, `3` is truthy, so `||` had its answer and never called\n`console.log`. In the line that makes `b`, `0` is on the left, so `||` needed\nits right side: it called `console.log`, which printed `ran` and returned\n`undefined`."
            },
            {
              "p": "Skipping the right side is what makes `&&` useful as a **guard**: a check that comes before a\nvalue is used. Reading `.length` of `null` stops the program:"
            },
            {
              "code": "const word = null;\nword.length",
              "lang": "js",
              "file": "word.js"
            },
            {
              "code": "TypeError: Cannot read properties of null (reading 'length')",
              "lang": "text"
            },
            {
              "p": "With `&&` in front, `word.length` is only worked out when `word` is truthy:"
            },
            {
              "code": "const word = null;\nword && word.length     // null — .length was never read",
              "lang": "js",
              "file": "word.js"
            },
            {
              "p": "If `word` holds `\"hey\"`, the same line evaluates to `3`. If `word` holds `\"\"`, it\nevaluates to `\"\"`, not a length, because `\"\"` is falsy and `&&` produces it. Watch\nfor that when the result is shown to someone."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "This line runs: `3 || console.log(\"ran\")`. Does anything print?",
                    "choices": [
                      "Nothing prints",
                      "ran prints"
                    ],
                    "accept": [
                      "Nothing prints"
                    ],
                    "miss": "**`3` is truthy, so `||` already has its answer.** The right side, the call to `console.log`, is never worked out.\n",
                    "why": "Nothing prints.\n"
                  },
                  {
                    "question": "This line runs: `\"\" || console.log(\"ran\")`. Does anything print?",
                    "choices": [
                      "Nothing prints",
                      "ran prints"
                    ],
                    "accept": [
                      "ran prints"
                    ],
                    "miss": "**`\"\"` is falsy, so `||` needs its right side** and calls `console.log`.\n",
                    "why": "`ran` prints.\n"
                  },
                  {
                    "question": "This line runs: `0 && console.log(\"ran\")`. Does anything print?",
                    "choices": [
                      "Nothing prints",
                      "ran prints"
                    ],
                    "accept": [
                      "Nothing prints"
                    ],
                    "miss": "**`&&` stops at a falsy left side,** and `0` is falsy. The expression evaluates to `0` without working out the right side.\n",
                    "why": "Nothing prints.\n"
                  },
                  {
                    "code": "const word = null;\nword && word.length\n",
                    "accept": [
                      "null"
                    ],
                    "miss": "**If you expected a TypeError — `word.length` was never read.** `null` is falsy, so `&&` produced it and stopped.\n",
                    "why": "`null`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "?? gives a default only for null and undefined",
          "body": [
            {
              "p": "`??` appeared in *When there is nothing there*:"
            },
            {
              "code": "a ?? b   // if a is null or undefined, you get b. Otherwise you get a.",
              "lang": "js",
              "file": "rules.js"
            },
            {
              "p": "It short-circuits too: when `a` is not `null` or `undefined`, `b` is never\nworked out. What makes `??` different from `||` is the question it asks. `||`\nasks \"is the left side **falsy**?\", and six values make the answer yes. `??`\nasks \"is the left side **`null` or `undefined`**?\", and only two values do."
            },
            {
              "p": "Each operator can be written out as a ternary, from *The ternary operator*. The ternary shows exactly what question\nthe operator asks:"
            },
            {
              "code": "count || 10   // count ? count : 10\ncount ?? 10   // count === null || count === undefined ? 10 : count",
              "lang": "js",
              "file": "written-out.js"
            },
            {
              "p": "Here are both operators, each tried with five values of `count`:"
            },
            {
              "table": {
                "head": [
                  "`count`",
                  "`count || 10`",
                  "`count ?? 10`"
                ],
                "rows": [
                  [
                    "`5`",
                    "`5`",
                    "`5`"
                  ],
                  [
                    "`undefined`",
                    "`10`",
                    "`10`"
                  ],
                  [
                    "`null`",
                    "`10`",
                    "`10`"
                  ],
                  [
                    "**`0`**",
                    "**`10`**",
                    "**`0`**"
                  ],
                  [
                    "**`\"\"`**",
                    "**`10`**",
                    "**`\"\"`**"
                  ]
                ]
              }
            },
            {
              "p": "The two operators give different answers only in the bold rows, where `count`\nholds a **real value that is falsy**. `||` treats a real `0` or `\"\"` as\n\"nothing given\" and replaces it. `??` keeps it. (`false` and `NaN` would differ\nin the same way as the bold rows.)"
            },
            {
              "tracks": {
                "js": [
                  {
                    "p": "`??` and `||` are both JavaScript's. The rest of this lesson is about choosing\nbetween them."
                  }
                ],
                "python": [
                  {
                    "p": "Python's `or` and `and` produce an operand exactly as `||` and `&&` do:\n`0 or 10` is `10`, and `\"ready\" and \"go\"` is `\"go\"`. Python has no `??`. When\n`0` or `\"\"` is a real value, write the check out:"
                  },
                  {
                    "code": "size = count if count is not None else 10",
                    "lang": "python"
                  }
                ],
                "csharp": [
                  {
                    "p": "In C#, `&&` and `||` take only `true` or `false` and produce a `bool`, so\nthey cannot supply a default. Defaults come from `??`, which works as it does\nin JavaScript: `count ?? 10`."
                  }
                ]
              }
            },
            {
              "p": "Type the value each expression evaluates to, with quotes if it is a string."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "0 ?? \"default\"",
                    "accept": [
                      "0"
                    ],
                    "miss": "**If you answered `\"default\"` because `0` is falsy — `??` never looks at the falsy list.** `0` is not `null` or `undefined`, so it is kept.\n",
                    "why": "`0`.\n"
                  },
                  {
                    "code": "false ?? true",
                    "accept": [
                      "false"
                    ],
                    "miss": "**If you answered `true` — `false` is a real value, not `null` or `undefined`.** `??` produces the right side only for `null` and `undefined`.\n",
                    "why": "`false`.\n"
                  },
                  {
                    "code": "null ?? 0",
                    "accept": [
                      "0"
                    ],
                    "why": "`0`. `null` is absent, so you get the right side, and `??` does not care that the right side is falsy.\n"
                  },
                  {
                    "code": "\"\" || \"none\"",
                    "accept": [
                      "\"none\"",
                      "none",
                      "'none'"
                    ],
                    "miss": "**`||` asks the falsy list, and `\"\"` is on it,** so the expression evaluates to the right side. With `??` you would get `\"\"`.\n",
                    "why": "`\"none\"`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Choosing the operator for a default",
          "body": [
            {
              "p": "A **default** is the value to use when no real value was given. Choosing\nbetween `||` and `??` comes down to one question about the value: **are `0`,\n`\"\"` and `false` real answers here, or do they mean \"nothing given\"?** This lesson's exercises\nask you to write a default as a function's answer. The four moves from *Writing\na function's answer* work as before. The second move, finding the question in\nthe sentence, now includes this question about `0`, `\"\"` and `false`."
            },
            {
              "p": "**Example 1.** \"`tipPercent` takes the tip a customer chose, as a percentage, or\n`null` if they skipped the question. It returns the tip to use: what they\nchose, or 15 if they skipped. A tip of 0 is a choice.\""
            },
            {
              "p": "Here is a common first try. It uses `||`, the way defaults are often written:"
            },
            {
              "code": "function tipPercent(chosen) {\n  return chosen || 15;\n}",
              "lang": "js",
              "file": "tip.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✓ tipPercent(20) → 20\n✓ tipPercent(null) → 15\n✗ tipPercent(0) → 0\n✓ tipPercent(undefined) → 15",
              "lang": "text"
            },
            {
              "p": "The test that failed is `tipPercent(0)`. Work it through by hand, with\n`chosen` holding `0`. Is `0` truthy? No, it is on the falsy list. So `chosen || 15` evaluates\nto its right side, `15`, and a customer who chose no tip is charged 15\npercent. The description says only a skipped question gets the default, and a\nskipped question is `null`. \"Is it `null` or `undefined`?\" is the question `??`\nasks, so the fix is `??`:"
            },
            {
              "code": "function tipPercent(chosen) {\n  // 0 is a real choice; only a skipped question (null) gets 15\n  return chosen ?? 15;\n}",
              "lang": "js",
              "file": "tip.js"
            },
            {
              "p": "Notice that three of the four tests passed with `||`. Only `tipPercent(0)`\nshowed the bug. That is why a default needs a test for each falsy value that\ncan really be given."
            },
            {
              "p": "**Example 2.** \"`teamName` takes the name a player typed for their team: `\"\"` if\nthey left the box empty, or `null` if they were never asked. It returns the\nname, or `\"Team 1\"` when there is none.\""
            },
            {
              "code": "function teamName(typed) {\n  // \"\" and null both mean no name, and \"\" is falsy, so ||\n  return typed || \"Team 1\";\n}",
              "lang": "js",
              "file": "team.js"
            },
            {
              "p": "With `??`, `teamName(\"\")` would return `\"\"`, and the team would show with\nno name at all. Example 2 has the same shape as Example 1 but uses the opposite\noperator, and both are right. What decides the operator is what a falsy value\n*means* for that input: in `tipPercent`, `0` is a real choice; in `teamName`,\n`\"\"` means no name was given."
            },
            {
              "p": "`&&` is rarely the right tool for a default. When its left side is falsy it\nproduces that side, so `unread && unread + \" new\"` is `0` when `unread` is\n`0`, and a `0` ends up on the screen. When a stray `0` would matter, write the choice out\nwith a ternary: `unread > 0 ? unread + \" new\" : \"\"`."
            },
            {
              "p": "**Example 3: you choose the line.** \"`volumeLevel` takes a saved volume, from 0\nto 10, or `undefined` if nothing was saved. It returns the saved volume, or\n7 if nothing was saved. 0 means muted.\""
            },
            {
              "code": "function volumeLevel(saved) {\n  // 0 means muted, a real setting\n  // TODO\n}",
              "lang": "js",
              "file": "volume.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes where the TODO is?",
                    "choices": [
                      "return saved || 7;",
                      "return saved ?? 7;",
                      "return volumeLevel(saved) ?? 7;"
                    ],
                    "accept": [
                      "return saved ?? 7;"
                    ],
                    "miss": "**If you chose `||` — work `volumeLevel(0)` through by hand.** `0` is falsy, so `saved || 7` evaluates to `7`, and a muted player is turned back up. **If you chose `volumeLevel(saved) ?? 7` — that line calls the function you are writing,** with the same value, forever: *Writing a function's answer* shows why it stops with `Maximum call stack size exceeded`.\n",
                    "why": "`return saved ?? 7;`. `volumeLevel(0)` returns `0`; `volumeLevel(undefined)` returns `7`.\n"
                  },
                  {
                    "question": "\"`signOff` takes the name to sign an email with, or `undefined`. An empty string `\"\"` also means no name. It returns the name, or `\"The team\"`.\" Its parameter is `typed`. Which line?",
                    "choices": [
                      "return typed ?? \"The team\";",
                      "return typed || \"The team\";"
                    ],
                    "accept": [
                      "return typed || \"The team\";"
                    ],
                    "miss": "**Work `signOff(\"\")` through by hand.** `\"\"` is not `null` or `undefined`, so `??` keeps it, and the email is signed with nothing.\n",
                    "why": "`return typed || \"The team\";`. Both `\"\"` and `undefined` are falsy, so both fall back.\n"
                  },
                  {
                    "code": "const unread = 0;\nunread && unread + \" new\"\n",
                    "accept": [
                      "0"
                    ],
                    "miss": "**`&&` stops at a falsy left side and produces it.** That is the number `0`, not `\"\"` and not `false`.\n",
                    "why": "`0`.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A game reads four settings from a save file. Each one may be `undefined` in a\nsave from an older version of the game."
            },
            {
              "list": [
                "`difficulty`: a number from 1 to 5; missing means 3.",
                "`playerName`: a string; missing, or cleared to `\"\"`, means `\"Player 1\"`.",
                "`music`: `true` or `false`; missing means `true`.",
                "`lastLevel`: a level number, where `0` is the tutorial; missing means `0`."
              ]
            },
            {
              "p": "Choose `||` or `??` for each, and say why. Where `||` would give a wrong answer,\nname the saved value it gets wrong and what the player would see. Then a\ncolleague suggests using `??` everywhere \"to be safe\": say which setting that\nbreaks, and what the game would show. Two of the four give the same result\nwith either operator, for every value they can hold: which two, and why?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`a || b` and `a && b` evaluate to one of their operands**, not a new `true` or `false`: `||` gives `a` when it is truthy, `&&` gives `a` when it is falsy, and otherwise each gives `b`.",
                "**They short-circuit:** when the left side settles the answer, the right side is never worked out. `??` does the same.",
                "**For a default, ask whether `0`, `\"\"` or `false` is a real answer.** If it is, use `??`, which replaces only `null` and `undefined`. If it means \"nothing given\", use `||`."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Logical OR (||)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_OR",
              "note": "the first paragraph says it produces an operand, not a boolean."
            },
            {
              "title": "Nullish coalescing (??)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Nullish_coalescing",
              "note": "its table against `||` is the clearest version of the difference."
            }
          ]
        }
      ],
      "_source": "lessons/conditions.md",
      "practice": {
        "id": "conditions",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the value, and the first move",
            "prompt": "Three expressions, then one question about where you would start. Type each value, with quotes if it is a string, then pick the operator.\n",
            "items": [
              {
                "id": "p1",
                "code": "0 || \"default\"",
                "accept": [
                  "\"default\"",
                  "default",
                  "'default'"
                ],
                "miss": "**If you answered `true` or `0` — `||` produces an operand,** and `0` is falsy, so the expression evaluates to the right side.\n",
                "why": "`\"default\"`. `0` is on the falsy list, so the default wins even though zero may have been a real answer.\n"
              },
              {
                "id": "p2",
                "code": "0 ?? \"default\"",
                "accept": [
                  "0"
                ],
                "miss": "**If you answered `\"default\"` — `??` asks only about `null` and `undefined`.** `0` is neither.\n",
                "why": "`0`. `0 || \"default\"` gave `\"default\"`; with `??` in place of `||`, the result is the opposite.\n"
              },
              {
                "id": "p3",
                "code": "\"ready\" && \"go\"",
                "accept": [
                  "\"go\"",
                  "go",
                  "'go'"
                ],
                "miss": "**If you answered `true` — `&&` produces an operand.** `\"ready\"` is truthy, so the expression evaluates to the right side.\n",
                "why": "`\"go\"`.\n"
              },
              {
                "id": "p4",
                "question": "A page size: `const shown = size ___ 20;`. A size of `0` that someone typed must be kept. Which operator goes in the gap?",
                "choices": [
                  "||",
                  "??"
                ],
                "accept": [
                  "??"
                ],
                "miss": "**With `||`, `0` is falsy and gets replaced by `20`.** You want the operator that falls back only when `size` is `null` or `undefined`.\n",
                "why": "`??`.\n"
              }
            ],
            "extra": [
              {
                "id": "p5",
                "code": "\"\" || \"anon\"",
                "accept": [
                  "\"anon\"",
                  "anon",
                  "'anon'"
                ],
                "why": "`\"anon\"`. The same trap with a different falsy value.\n"
              },
              {
                "id": "p6",
                "code": "null ?? 0",
                "accept": [
                  "0"
                ],
                "why": "`0`. `null` is absent, so you get the right side, and the expression evaluates to it even though `0` is falsy.\n"
              },
              {
                "id": "p7",
                "code": "false || null",
                "accept": [
                  "null"
                ],
                "why": "`null`. The left side is falsy, so the expression evaluates to the right side, whatever it is.\n"
              }
            ],
            "hints": [
              {
                "when": "your answers to the first two were the same",
                "say": "The two operators ask different questions. One asks \"is it falsy?\"; the other asks \"is it `null` or `undefined`?\". Which question does `0` answer yes to?\n"
              },
              {
                "when": "you answered true or false for an operator",
                "say": "None of these builds a boolean. Ask which side the operator produces — that operand is the answer.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "See which side ran",
            "prompt": "`console.log` prints only when it is actually called. Predict what each line below prints, then run the program and compare.\n",
            "filename": "short-circuit.js",
            "code": "const saved = 0;\nconsole.log(saved || 25);\nconsole.log(saved ?? 25);\nconsole.log(null && \"go\");\n\n3 || console.log(\"ran: 3 ||\");\n0 ?? console.log(\"ran: 0 ??\");\nundefined ?? console.log(\"ran: undefined ??\");\n\"\" && console.log(\"ran: empty &&\");\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(saved || 25);  // prints",
                "accept": [
                  "25"
                ],
                "why": "`25`. `0` is falsy, so `saved || 25` evaluates to its right side: the number itself, not `true`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(saved ?? 25);  // prints",
                "accept": [
                  "0"
                ],
                "why": "`0`. `0` is not `null` or `undefined`, so `??` kept the left side. The value is the same as in the line above; the question the operator asks is different.\n"
              },
              {
                "id": "r3",
                "code": "console.log(null && \"go\");  // prints",
                "accept": [
                  "null"
                ],
                "why": "`null`. `&&` stopped at the falsy left side and produced it.\n"
              },
              {
                "id": "r4",
                "question": "How many lines that start with `ran:` does the program print? Type the number.",
                "accept": [
                  "1",
                  "one"
                ],
                "miss": "**Count only the calls that actually happen.** Each operator works out its right side only when its left side does not settle the answer.\n",
                "why": "`1`: only `undefined ??` needed its right side. `3` is truthy, so `||` stopped; `0` is not `null` or `undefined`, so `??` stopped; `\"\"` is falsy, so `&&` stopped.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 4 lines starting with ran",
                "say": "Take the four lines that could print `ran:`, one at a time. Does the left side already decide what the expression evaluates to? If it does, the `console.log` on the right never runs.\n"
              },
              {
                "when": "you predicted false for null && \"go\"",
                "say": "`&&` produces an operand. Which operand did it stop on?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace the operand",
            "prompt": "Type the value each expression evaluates to — the operand the operator stopped on. Use quotes for a string.\n",
            "items": [
              {
                "id": "t1",
                "code": "\"a\" || \"b\"",
                "accept": [
                  "\"a\"",
                  "a",
                  "'a'"
                ],
                "why": "`\"a\"`. `\"a\"` is truthy, so `||` produces it and never works out `\"b\"`.\n"
              },
              {
                "id": "t2",
                "code": "0 && \"go\"",
                "accept": [
                  "0"
                ],
                "why": "`0`. The left side is falsy, so `&&` produces it immediately.\n"
              },
              {
                "id": "t3",
                "code": "\" \" || \"none\"",
                "accept": [
                  "\" \"",
                  "' '",
                  "a space",
                  "space",
                  "(a space)",
                  "one space"
                ],
                "miss": "**A string holding one space is not empty.** Only `\"\"` is falsy among strings, so `\" \"` is truthy and `||` produces it.\n",
                "why": "`\" \"`, the string with one space in it.\n"
              },
              {
                "id": "t4",
                "code": "const unread = 0;\nunread && unread + \" new\"\n",
                "accept": [
                  "0"
                ],
                "why": "`0`. `&&` stopped at the falsy `0` and produced it. On a screen, that would show a `0`, not nothing.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "0 || \"\" || \"x\"",
                "accept": [
                  "\"x\"",
                  "x",
                  "'x'"
                ],
                "why": "`\"x\"`. Read left to right: `0 || \"\"` is `\"\"`, and `\"\" || \"x\"` is `\"x\"`.\n"
              },
              {
                "id": "t6",
                "code": "undefined ?? null ?? \"last\"",
                "accept": [
                  "\"last\"",
                  "last",
                  "'last'"
                ],
                "why": "`\"last\"`. `undefined ?? null` is `null`, and `null ?? \"last\"` is `\"last\"`.\n"
              },
              {
                "id": "t7",
                "code": "const n = 0;\n(n || 5) + (n ?? 5)\n",
                "accept": [
                  "5"
                ],
                "why": "`5`. `n || 5` is `5` because `0` is falsy; `n ?? 5` is `0` because `0` is not `null` or `undefined`. `5 + 0` is `5`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered true or false",
                "say": "Neither operator produces a boolean. Ask which operand it produces.\n"
              },
              {
                "when": "you answered \"none\" for the string with a space",
                "say": "Check the falsy list from *Truthiness*. Which string is on it? Is a string holding one space the same string?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Keep a zero that was meant",
            "prompt": "`retries` takes the number of times to retry a failed upload, or `null` or `undefined` if there is no setting, and returns the number to use. It works for its old spec. The spec has changed: a setting of `0` now means \"do not retry\" and must come back as `0`. No setting still means 3. Change the one thing that stops `retries(0)` returning `0`. Then, in one sentence: which inputs does your change affect, and which does it leave alone?\n",
            "exportName": "retries",
            "filename": "retries.js",
            "starter": "// A setting of 0 means do not retry: return 0.\n// No setting (null or undefined) means 3.\nfunction retries(setting) {\n  // TODO: change this line\n  return setting || 3;\n}\n",
            "tests": [
              {
                "label": "retries(5) → 5",
                "body": "return subject(5) === 5;"
              },
              {
                "label": "retries(0) → 0",
                "body": "return subject(0) === 0;"
              },
              {
                "label": "retries(undefined) → 3",
                "body": "return subject(undefined) === 3;"
              },
              {
                "label": "retries(null) → 3",
                "body": "return subject(null) === 3;"
              }
            ],
            "hints": [
              {
                "when": "the retries(0) test fails",
                "say": "Work `retries(0)` through by hand. Is `0` falsy? Then what does `setting || 3` evaluate to? You want the operator that falls back only when there is no setting.\n"
              },
              {
                "when": "the retries(null) test fails",
                "say": "A check for `undefined` alone misses `null`. \"No setting\" here is both, and one operator asks exactly that.\n"
              },
              {
                "when": "the error says Maximum call stack size exceeded",
                "say": "Your answer calls `retries` from inside `retries`. Build it from `setting`, an operator and `3`.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "One line, two defaults",
            "prompt": "\"`scoreLine` takes a player's name and their score, and returns a line for a scoreboard, such as `\"Ada: 12\"`. A name that is `\"\"`, `null` or `undefined` shows as `Anonymous`. A score of `0` is a real score; a score that is `null` or `undefined` (the game was not finished) shows as `-`.\" Write your answer between the braces, and put `return` in front of it — *Writing a function's answer* shows how. Then, in one sentence: why can the name and the score not use the same operator?\n",
            "exportName": "scoreLine",
            "filename": "scoreboard.js",
            "starter": "// scoreLine(\"Ada\", 12)          -> \"Ada: 12\"\n// scoreLine(\"\", 7)              -> \"Anonymous: 7\"\n// scoreLine(\"Bo\", null)         -> \"Bo: -\"\nfunction scoreLine(name, score) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "scoreLine(\"Ada\", 12) → \"Ada: 12\"",
                "body": "return subject('Ada', 12) === 'Ada: 12';"
              },
              {
                "label": "scoreLine(\"Ada\", 0) → \"Ada: 0\"",
                "body": "return subject('Ada', 0) === 'Ada: 0';"
              },
              {
                "label": "scoreLine(\"\", 7) → \"Anonymous: 7\"",
                "body": "return subject('', 7) === 'Anonymous: 7';"
              },
              {
                "label": "scoreLine(null, 7) → \"Anonymous: 7\"",
                "body": "return subject(null, 7) === 'Anonymous: 7';"
              },
              {
                "label": "scoreLine(\"Bo\", null) → \"Bo: -\"",
                "body": "return subject('Bo', null) === 'Bo: -';"
              },
              {
                "label": "scoreLine(undefined, undefined) → \"Anonymous: -\"",
                "body": "return subject(undefined, undefined) === 'Anonymous: -';"
              }
            ],
            "hints": [
              {
                "when": "scoreLine(\"Ada\", 0) gives \"Ada: -\"",
                "say": "Work it through by hand: `score` holds `0`. Which operator treats `0` as missing? The score wants the one that asks only about `null` and `undefined`.\n"
              },
              {
                "when": "scoreLine(\"\", 7) gives \": 7\"",
                "say": "`??` keeps `\"\"`, because an empty string is not absent. For the name, an empty string should fall back.\n"
              },
              {
                "when": "scoreLine(\"Ada\", 12) gives just \"Ada\"",
                "say": "`+` is worked out before `??` and `||`, so the whole `+` chain after the operator became its right side. Put each default in parentheses, or inside `${ }` in a template literal.\n"
              },
              {
                "when": "the error says Unexpected token '??'",
                "say": "JavaScript refuses `||` and `??` side by side without parentheses. Put each default in its own parentheses.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review two helpers",
            "prompt": "A code assistant wrote these two functions from the comments above them. Read them, then answer each question.\n",
            "filename": "helpers.js",
            "code": "// copies: how many to print. 0 means print none.\n// undefined means the person did not say: print 1.\nfunction copiesToPrint(copies) {\n  return copies || 1;\n}\n\n// city: the city a person typed. \"\" or null means\n// they left it blank: show \"Unknown\".\nfunction cityShown(city) {\n  return city ?? \"Unknown\";\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `copiesToPrint(0)`. Type the number it returns.",
                "accept": [
                  "1"
                ],
                "miss": "**Work it through by hand.** `0` is falsy, so `copies || 1` evaluates to its right side.\n",
                "why": "`1` — one copy printed for someone who asked for none.\n"
              },
              {
                "id": "c2",
                "question": "A test calls `cityShown(\"\")`. What does it return?",
                "choices": [
                  "\"Unknown\"",
                  "an empty string, \"\"",
                  "null"
                ],
                "accept": [
                  "an empty string, \"\""
                ],
                "miss": "**`??` asks only about `null` and `undefined`.** `\"\"` is neither, so it is kept.\n",
                "why": "`\"\"`. The page shows a blank where the comment said `Unknown`.\n"
              },
              {
                "id": "c3",
                "question": "Which pair of fixed lines matches both comments?",
                "choices": [
                  "return copies ?? 1;  and  return city || \"Unknown\";",
                  "return copies ?? 1;  and  return city ?? \"Unknown\";",
                  "return copiesToPrint(copies) ?? 1;  and  return city || \"Unknown\";"
                ],
                "accept": [
                  "return copies ?? 1;  and  return city || \"Unknown\";"
                ],
                "miss": "**Each function needs the operator the other function used.** For `copies`, `0` is real, so `??`; for `city`, `\"\"` means blank, so `||`. The third choice calls `copiesToPrint` from inside itself and never gets an answer.\n",
                "why": "`copies ?? 1` keeps a real `0`; `city || \"Unknown\"` replaces a blank `\"\"`.\n"
              }
            ],
            "hints": [
              {
                "when": "you said copiesToPrint(0) returns 0",
                "say": "Which operator is on line 4, and is `0` on the falsy list?\n"
              }
            ]
          }
        ]
      }
    },
    "multiway": {
      "id": "multiway",
      "title": "Choosing among many paths",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "Multiway branching is ordinary JavaScript that no lesson taught until `flow.multiway` joined the inventory (D-020). It follows *The if statement* and *The ternary operator*; the first teaches `if`/`else` and a short `else if`, and *What &&, || and ?? evaluate to*, which the `case \"sat\" || \"sun\"` mistake draws on. What is new here is the longer chain whose order is its meaning, the `switch` with its fall-through, and the default — nothing checks that every case is handled, so a deliberate default is the guard (concepts.md `flow.multiway`). Procedural, explain first (D-061 item 1): the student starts from zero (D-042), and a `switch` is notation applied by a procedure. Both return forms are shown and neither enforced (D-061 item 2). The writing direction is modelled with a forgotten `break` and a misordered chain as the fail-first examples (01-explanations rule 5a). Lookup tables move to Objects and later, and the throwing default to Errors (zero-prereq audit, Module 3 rows; §3 row 4). Rock-paper-scissors is this lesson's practice write rung (D-061 item 4). The lesson's wording and examples are derived from those rules, pending Nick's review.",
      "concepts": [
        "flow.multiway"
      ],
      "lede": "Some decisions have many possible answers, not two or three. This lesson covers two ways to write such a decision: a longer `else if` chain, which can test anything, and a `switch`, which matches one value against exact values. It also covers the default: the branch that catches every value no other branch named.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The if statement · The ternary operator · What &&, || and ?? evaluate to · Writing a function's answer · Leaving a loop early: break and continue"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Branch** — one of the paths a decision can take. **Case** — in a `switch`, one exact value to match, and the lines to run when it does. **Default** — the branch for every value no other branch named. **Fall-through** — a `switch` running on past the case it matched, into the lines of the next case.",
          "entries": [
            "**Branch** — one of the paths a decision can take.",
            "**Case** — in a `switch`, one exact value to match, and the lines to run when it does.",
            "**Default** — the branch for every value no other branch named.",
            "**Fall-through** — a `switch` running on past the case it matched, into the lines of the next case."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on what this lesson builds on, from memory: an `else if`\nchain, how `===` matches text, and where a range starts. Type the value, with\nquotes if it is text, or press the answer. Press **I don't know** if you have\nno idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "function band(n) {\n  if (n >= 10) {\n    return \"high\";\n  } else if (n >= 5) {\n    return \"mid\";\n  } else {\n    return \"low\";\n  }\n}\nband(5)\n",
                    "accept": [
                      "\"mid\"",
                      "mid",
                      "'mid'"
                    ],
                    "miss": "**The chain checks its conditions from the top, and the first `true` one wins.** `5 >= 10` is `false`; `5 >= 5` is `true`, so `\"mid\"`.\n",
                    "why": "`\"mid\"`.\n"
                  },
                  {
                    "code": "\"Red\" === \"red\"",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**`===` compares text exactly, capitals included**, from *Comparing and combining*. `R` and `r` are different characters.\n",
                    "why": "`false`. This matters in this lesson: a `switch` matches with `===`.\n"
                  },
                  {
                    "code": "5 >= 5",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "miss": "**`>=` includes the number itself.** `5` is equal to `5`, so \"greater than or equal\" is `true`.\n",
                    "why": "`true`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can write an `else if` chain that tests ranges, ordered so each branch gets only the values meant for it",
                "I can write a `switch` with `case`, `break` and `default`, and predict what runs when a `break` is missing",
                "I can choose between a chain and a `switch`, and say what my default catches",
                "I can write a function's answer that handles every case, including the values no case names"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "A longer chain: the first true branch wins",
          "body": [
            {
              "p": "In *The if statement*, a decision had two or three paths. Many\ndecisions have more: a score becomes one of five letter grades; a parcel's\nweight puts it in one of four price bands. One way to write that is a longer\n`else if` chain."
            },
            {
              "p": "A chain tries its conditions **from the top down, and takes the first one that\nis `true`**. The lines in that branch run, and every condition below it is\nskipped — never even worked out. The plain `else` at the end catches every\nvalue that no condition above it caught. This final `else` is called the\nchain's **default**: the branch for everything else."
            },
            {
              "p": "Here are two worked examples. They give their answer in the two ways\n*Branching* showed: a `return` in each branch, or a `let` that a branch\nreassigns."
            },
            {
              "p": "**Example 1: `return` in each branch.** A score from 0 to 100 becomes a letter:\n90 or more is an A, 80 or more a B, 70 or more a C, 60 or more a D, and\nanything lower an F."
            },
            {
              "code": "function letterGrade(score) {\n  // highest threshold first\n  if (score >= 90) {\n    return \"A\";\n  } else if (score >= 80) {\n    return \"B\";\n  } else if (score >= 70) {\n    return \"C\";\n  } else if (score >= 60) {\n    return \"D\";\n  } else {\n    // the default: everything else\n    return \"F\";\n  }\n}",
              "lang": "js",
              "file": "grade.js"
            },
            {
              "p": "A score of 95 is also `>= 80`, `>= 70` and `>= 60`. It gets an A only because\n`>= 90` is tested first. **When conditions overlap, order is part of the\nmeaning.** If `score >= 60` were tested first, every score of 60 or more\nwould stop at that test and get a D. Each test would still be correct on its\nown; only the order would be wrong, and that is enough to make the function\nwrong."
            },
            {
              "p": "**Example 2: a `let` with a default, reassigned in one branch.** Postage costs\n4 up to 1 kg, 9 up to 5 kg, 18 up to 20 kg, and 40 for anything heavier."
            },
            {
              "code": "function postage(kg) {\n  let cost = 40;          // the default: over 20 kg\n  // lightest first\n  if (kg <= 1) {\n    cost = 4;\n  } else if (kg <= 5) {\n    cost = 9;\n  } else if (kg <= 20) {\n    cost = 18;\n  }\n  return cost;\n}",
              "lang": "js",
              "file": "parcel.js"
            },
            {
              "p": "In `postage`, the default comes first: `40` is the starting value of `cost`.\nA branch replaces that value only when the branch's own test is true. The\nthresholds rise from 1 to 5 to 20, so the narrowest test, `kg <= 1`, goes\nfirst: every weight up to 1 kg is also up to 5 kg, so testing `kg <= 5` first\nwould catch those weights too. A way to check a chain's order by eye: the most\ndemanding test goes first. With `<` or `<=`, that means the smallest threshold\nfirst, rising; with `>` or `>=`, the largest first, falling. Thresholds that\nrise in a `>=` chain, like `>= 60` then `>= 90`, are in the wrong order."
            },
            {
              "p": "The two forms give the same answers for the same input. `return` in each branch is short, and each\nanswer sits next to its test. The `let` form keeps the answer in one name and\nsends it out once, at the bottom. Use whichever reads more clearly to you."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "function rating(score) {\n  if (score >= 60) {\n    return \"pass\";\n  } else if (score >= 90) {\n    return \"distinction\";\n  } else {\n    return \"fail\";\n  }\n}\nrating(95)\n",
                    "accept": [
                      "\"pass\"",
                      "pass",
                      "'pass'"
                    ],
                    "miss": "**If you answered `\"distinction\"` — a chain takes the first true branch, not the best fit.** `95 >= 60` is `true`, so the first branch runs and the `>= 90` test is never looked at.\n",
                    "why": "`\"pass\"`.\n"
                  },
                  {
                    "question": "In that `rating` function, which branch can never run, whatever the score?",
                    "choices": [
                      "pass",
                      "distinction",
                      "fail"
                    ],
                    "accept": [
                      "distinction"
                    ],
                    "why": "`distinction`. Every score that is `>= 90` is also `>= 60`, so the `pass` branch, which is tested first, catches it. The fix is order: test `>= 90` before `>= 60`.\n"
                  },
                  {
                    "question": "Using `postage` above, what does `postage(5)` return? Type the number.",
                    "accept": [
                      "9"
                    ],
                    "miss": "**Work the chain from the top.** `5 <= 1` is `false`; `5 <= 5` is `true`, so `cost` becomes `9` and the rest is skipped.\n",
                    "why": "`9`. `<=` is also true when the two numbers are equal, so `5 <= 5` is `true`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "A switch: one value against exact matches",
          "body": [
            {
              "p": "When every branch asks the same question about the same value — **is this\nvalue exactly equal to one particular value?** — there is a second way to write\nthe decision: the `switch`. It is built from four pieces of syntax:"
            },
            {
              "list": [
                "**`switch (value)`** names the one value being matched. It is worked out once.",
                "**`case \"green\":`** is one exact value to match, followed by the lines to run when it matches.",
                "**`break`** ends the `switch`. The program carries on after its closing brace.",
                "**`default:`** is where the `switch` goes when no case matched."
              ]
            },
            {
              "p": "**Example 1: `return` in each case.** A traffic light's colour says what a\ndriver does."
            },
            {
              "code": "function lightAction(colour) {\n  switch (colour) {      // the one value being matched\n    case \"green\":\n      return \"go\";\n    case \"yellow\":\n      return \"slow down\";\n    case \"red\":\n      return \"stop\";\n    default:             // when no case matched\n      return \"unknown\";\n  }\n}",
              "lang": "js",
              "file": "light.js"
            },
            {
              "p": "How a call like `lightAction(\"yellow\")` runs:"
            },
            {
              "list": [
                "`colour` holds `\"yellow\"`.",
                "The `switch` compares it with each `case`, top to bottom, using `===`: `\"yellow\" === \"green\"` is `false`; `\"yellow\" === \"yellow\"` is `true`.",
                "The `switch` jumps to `case \"yellow\"` and runs the lines below it: `return \"slow down\"`. `return` sends the answer out and leaves the function, so nothing else runs.",
                "If no case had matched, the `switch` would have jumped to `default`."
              ],
              "ordered": true
            },
            {
              "p": "**Example 2: a `let` assigned in each case, then `break`.** A ticket type gives\nthe discount it gets."
            },
            {
              "code": "function discountLabel(type) {\n  let label;\n  switch (type) {\n    case \"adult\":\n      label = \"full price\";\n      break;\n    case \"child\":\n      label = \"half price\";\n      break;\n    case \"senior\":\n      label = \"a third off\";\n      break;\n    default:\n      label = \"unknown\";\n  }\n  return label;\n}",
              "lang": "js",
              "file": "ticket.js"
            },
            {
              "p": "In `discountLabel`, no case has a `return`, so nothing leaves the function\ninside the `switch`. Each case ends with `break` instead. Without that\n`break`, the program would carry on into the next case's lines; the next step\nshows what that does. `break` is the same keyword that leaves a loop\nearly, which *Leaving a loop early: break and continue* covers."
            },
            {
              "p": "**Which form?** A `return` in each case is shorter, and a case written that\nway cannot run on into the next case, because `return` leaves the function.\nBut `return` only works inside a function, and with it every case ends the\nfunction. The `let` form works anywhere — not only in a function — and puts\nthe answer in one place, but every case needs its `break`, and a forgotten\n`break` is one of the most common `switch` mistakes. Neither form is the right one;\nreal code uses both, often."
            },
            {
              "p": "**A `switch` compares with `===`.** `===` asks the strict question from\n*Equality: == versus ===*: same type and same value, nothing converted. So the text `\"2\"`\nnever matches `case 2`, and `\"Red\"` never matches `case \"red\"`. Text a person\ntyped is a string, even when it looks like a number. Either write the cases as\nstrings, or convert the value once, before the `switch`, with `Number(…)`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Using `lightAction` from Example 1, what does `lightAction(\"Red\")` return? Type the value.",
                    "accept": [
                      "\"unknown\"",
                      "unknown",
                      "'unknown'"
                    ],
                    "miss": "**If you answered `\"stop\"` — `===` compares text exactly, capitals included.** `\"Red\" === \"red\"` is `false`, so no case matches and the `default` runs.\n",
                    "why": "`\"unknown\"`.\n"
                  },
                  {
                    "code": "const floor = \"3\";\nlet label = \"?\";\nswitch (floor) {\n  case 3:\n    label = \"top\";\n    break;\n  default:\n    label = \"other\";\n}\nlabel\n",
                    "accept": [
                      "\"other\"",
                      "other",
                      "'other'"
                    ],
                    "miss": "**If you answered `\"top\"` — a `switch` does not convert.** `floor` holds the text `\"3\"`, the case is the number `3`, and `\"3\" === 3` is `false`.\n",
                    "why": "`\"other\"`. The `default` catches it.\n"
                  },
                  {
                    "question": "In Example 2, after `label = \"half price\";` runs, which line runs next?",
                    "choices": [
                      "label = \"a third off\";",
                      "break;",
                      "return label;"
                    ],
                    "accept": [
                      "break;"
                    ],
                    "why": "`break;` — and then the program jumps past the `switch`'s closing brace to `return label;`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Fall-through, and grouping cases",
          "body": [
            {
              "p": "A `case` is a place to **start**, not a box with walls. The `switch` jumps to\nthe matching case and runs every line below it — through the next `case` and the\none after — until it meets a `break`, a `return` or the closing brace of the\n`switch`. Running on into the next case's lines is called **fall-through**."
            },
            {
              "p": "Here it is with no `break` anywhere:"
            },
            {
              "code": "const size = \"m\";\nlet price = 0;\nswitch (size) {\n  case \"s\":\n    price = 5;\n  case \"m\":\n    price = 7;\n  case \"l\":\n    price = 9;\n}",
              "lang": "js",
              "file": "size.js"
            },
            {
              "p": "The `switch` enters at `case \"m\"` and sets `price` to `7`. Nothing stops it, so\nit carries on into `case \"l\"`'s line and sets `price` to `9`. Afterwards\n`price` is `9`, not `7`."
            },
            {
              "p": "Fall-through is a bug when it happens because you forgot a `break`, and a\ntool when you mean it. The kind of\nfall-through most often written on purpose is **cases stacked with nothing between\nthem**, so several values share one branch:"
            },
            {
              "code": "function dayType(day) {\n  switch (day) {\n    // two values share this branch\n    case \"sat\":\n    case \"sun\":\n      return \"weekend\";\n    // five values share this one\n    case \"mon\":\n    case \"tue\":\n    case \"wed\":\n    case \"thu\":\n    case \"fri\":\n      return \"weekday\";\n    default:\n      return \"unknown\";\n  }\n}",
              "lang": "js",
              "file": "day.js"
            },
            {
              "p": "`dayType(\"sat\")` enters at `case \"sat\"`, which has no lines of its own, falls\nstraight into `case \"sun\"`'s line, and returns `\"weekend\"`."
            },
            {
              "p": "A tempting shortcut does not work: `case \"sat\" || \"sun\":`. As *What &&, ||\nand ?? evaluate to* showed, `||` produces its first truthy value, so\n`\"sat\" || \"sun\"` is just `\"sat\"`. That case matches `\"sat\"` and never\n`\"sun\"`. Stack the cases instead."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let points = 0;\nswitch (2) {\n  case 1:\n    points += 1;\n  case 2:\n    points += 10;\n  case 3:\n    points += 100;\n    break;\n  case 4:\n    points += 1000;\n}\npoints\n",
                    "accept": [
                      "110"
                    ],
                    "miss": "**If you answered `10` — `case 2` has no `break`, so the program runs on into `case 3`.** If you answered `1110` — `case 3` ends with `break`, which stops it before `case 4`.\n",
                    "why": "`110`. The `switch` enters at `case 2` and adds 10, runs on through `case 3` and adds 100, and leaves at the `break` in `case 3`.\n"
                  },
                  {
                    "code": "function dayType(day) {\n  switch (day) {\n    case \"sat\" || \"sun\":\n      return \"weekend\";\n    default:\n      return \"unknown\";\n  }\n}\ndayType(\"sun\")\n",
                    "accept": [
                      "\"unknown\"",
                      "unknown",
                      "'unknown'"
                    ],
                    "miss": "**`\"sat\" || \"sun\"` is worked out first, and it is `\"sat\"`.** The case is really `case \"sat\":`, so `\"sun\"` matches nothing and the `default` runs.\n",
                    "why": "`\"unknown\"`. Two values that share a branch need two stacked `case` lines.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "The default, and choosing a form",
          "body": [
            {
              "p": "**Nothing in JavaScript checks that every case is handled.** Suppose a fourth\ncolour of light is added, or a `\"student\"` ticket. Every chain and `switch`\nthat decides on lights or tickets keeps running, with no error and no warning.\nThe new value gets whatever happens when nothing matches:"
            },
            {
              "table": {
                "head": [
                  "Nothing matches, and there is no default",
                  "What you get"
                ],
                "rows": [
                  [
                    "a chain or `switch` that `return`s in each branch",
                    "the function reaches its end and returns `undefined`"
                  ],
                  [
                    "the `let` form",
                    "the name keeps whatever it held before, often `undefined`"
                  ]
                ]
              }
            },
            {
              "p": "Either way the missing case turns into `undefined` quietly, and the trouble\nshows up later, somewhere else. So write the default **on purpose**. It does one\nof two jobs, and choosing which job is the real decision:"
            },
            {
              "list": [
                "**Everything else has a real answer.** Parcels over 20 kg cost 40; a score under 60 is an F. The default *is* a case, the biggest one.",
                "**Everything else was not planned for.** A ticket type nobody listed is a typo or a new type someone forgot to add. The default returns a value that says so plainly, such as `\"unknown\"`, so a test or a person reading the output can see it. The Errors module shows how to stop the program with a message instead."
              ]
            },
            {
              "p": "The default to avoid is a plausible guess, such as `default: label = \"full\nprice\"` on a list that was meant to be complete. With that default, a typo\nin a ticket type gets `\"full price\"`: an answer that looks right. A wrong\nanswer that looks right is harder to find than one that looks wrong."
            },
            {
              "p": "Some languages check that every case is handled for you: when the possible\nvalues are part of a declared type, the compiler can refuse a `switch` that\nleaves one out. JavaScript has no such type to check against, so in JavaScript\nyour default does that job."
            },
            {
              "p": "**Chain or `switch`?** Ask what each branch tests."
            },
            {
              "table": {
                "head": [
                  "",
                  "A chain",
                  "A `switch`"
                ],
                "rows": [
                  [
                    "Each branch can test",
                    "anything: a range (`<`, `>=`), two names at once, a different name per branch",
                    "one value, against exact values, with `===`"
                  ],
                  [
                    "\"90 or more\"",
                    "`score >= 90`",
                    "cannot be written as a case"
                  ],
                  [
                    "Nothing matched",
                    "the final `else`",
                    "`default`"
                  ]
                ]
              }
            },
            {
              "p": "So: ranges, and anything that asks more than \"is it exactly this?\", need a\nchain. One value checked against a list of exact values often reads better as a\n`switch`: the value is named once, and each case is one line. When each branch\nonly returns a value, there is a third way — a table of values, looked up by\nname — which the Objects module teaches."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A cinema charges by age: under 12 pays 6, 65 and over pays 8, everyone else 12. Chain or switch?",
                    "choices": [
                      "Chain",
                      "Switch"
                    ],
                    "accept": [
                      "Chain"
                    ],
                    "miss": "**\"Under 12\" is a range.** A `case` is compared with `===`, so it can only name one exact value.\n",
                    "why": "A chain.\n"
                  },
                  {
                    "question": "A coffee order code is `\"s\"`, `\"m\"` or `\"l\"`, and each gives a cup size. Chain or switch?",
                    "choices": [
                      "Chain",
                      "Switch"
                    ],
                    "accept": [
                      "Switch"
                    ],
                    "why": "A `switch` reads best: one value, matched against exact ones. A chain would work too, repeating `code ===` in every test.\n"
                  },
                  {
                    "code": "function boardingGroup(seat) {\n  switch (seat) {\n    case \"first\":\n      return 1;\n    case \"economy\":\n      return 3;\n  }\n}\nboardingGroup(\"premium\")\n",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**No case matches `\"premium\"`, and there is no `default`.** The program leaves the `switch` and reaches the function's closing brace without a `return`.\n",
                    "why": "`undefined` — quietly, with no message. A `default` would have said what an unlisted seat gets.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Writing a multiway decision from a sentence",
          "body": [
            {
              "p": "The four moves from *Writing a function's answer* still work. The second move\nchanges shape: instead of one question, you find **every case** in the sentence,\nand what everything else gives."
            },
            {
              "list": [
                "**Name the values you have** — the parameters.",
                "**Find the cases in the sentence**, and the default. Exact values point to a `switch`; ranges point to a chain.",
                "**Write one branch per case**, then the default.",
                "**Send the answer out**, from every path."
              ],
              "ordered": true
            },
            {
              "p": "**Example 1: a forgotten `break`.** \"`coffeeSize` takes a one-letter code and\nreturns the cup size: `\"s\"` is `\"small\"`, `\"m\"` is `\"medium\"`, `\"l\"` is\n`\"large\"`. Any other code gives `\"unknown\"`.\""
            },
            {
              "p": "Here is a first try, written in the `let` form:"
            },
            {
              "code": "function coffeeSize(code) {\n  let size;\n  switch (code) {\n    case \"s\":\n      size = \"small\";\n      break;\n    case \"m\":\n      size = \"medium\";\n    case \"l\":\n      size = \"large\";\n      break;\n    default:\n      size = \"unknown\";\n  }\n  return size;\n}",
              "lang": "js",
              "file": "coffee.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✓ coffeeSize(\"s\") → \"small\"\n✗ coffeeSize(\"m\") → \"medium\"\n✓ coffeeSize(\"l\") → \"large\"\n✓ coffeeSize(\"x\") → \"unknown\"",
              "lang": "text"
            },
            {
              "p": "Three tests passed, so most of the function is right. The test that failed\nis `coffeeSize(\"m\")`. Work it through by hand, with `code` holding `\"m\"`.\n`\"m\" === \"s\"` is `false`; `\"m\" === \"m\"` is `true`, so the `switch` jumps to\n`case \"m\"` and sets `size` to `\"medium\"`. The next line is `case \"l\":` — and\nthere is no `break` before it, so the program falls through and sets `size` to\n`\"large\"`. Then it meets the `break` in `case \"l\"`, leaves the `switch`, and\n`return size;` sends out `\"large\"`. The fix is one line: `break;` after\n`size = \"medium\";`."
            },
            {
              "code": "function coffeeSize(code) {\n  // name the values you have: code, a one-letter string\n  // find the cases in the sentence: three exact values, so a switch\n  let size;\n  switch (code) {\n    // write one branch per case, then the default\n    case \"s\":\n      size = \"small\";\n      break;\n    case \"m\":\n      size = \"medium\";\n      break;\n    case \"l\":\n      size = \"large\";\n      break;\n    default:\n      size = \"unknown\";\n  }\n  // send the answer out\n  return size;\n}",
              "lang": "js",
              "file": "coffee.js"
            },
            {
              "p": "If `coffeeSize` were written with a `return` in each case, a forgotten\n`break` could not cause this at all, because each `return` leaves the\nfunction."
            },
            {
              "p": "**Example 2: a chain in the wrong order.** \"`windWarning` takes a wind speed in\nkm/h. Under 40 is `\"none\"`; 40 or more is `\"yellow\"`; 70 or more is `\"amber\"`;\n100 or more is `\"red\"`.\""
            },
            {
              "p": "Here is a first try. It tests the thresholds in the order the sentence\ngives them:"
            },
            {
              "code": "function windWarning(kmh) {\n  if (kmh >= 40) {\n    return \"yellow\";\n  } else if (kmh >= 70) {\n    return \"amber\";\n  } else if (kmh >= 100) {\n    return \"red\";\n  } else {\n    return \"none\";\n  }\n}",
              "lang": "js",
              "file": "wind.js"
            },
            {
              "code": "✓ windWarning(10) → \"none\"\n✓ windWarning(50) → \"yellow\"\n✗ windWarning(80) → \"amber\"\n✗ windWarning(120) → \"red\"",
              "lang": "text"
            },
            {
              "p": "The first test that failed is `windWarning(80)`. Work it through with `kmh`\nholding `80`: `80 >= 40` is `true`, so the first branch returns `\"yellow\"`, and\nthe `kmh >= 70` test is never looked at. The sentence lists the thresholds from\nlow to high, but every speed of 70 or more is also 40 or more, so `kmh >= 40`\ncatches those speeds before the later tests can. The narrowest test has to come\nfirst: `>= 100`, then `>= 70`, then `>= 40`."
            },
            {
              "code": "function windWarning(kmh) {\n  // ranges, so a chain: highest threshold first\n  if (kmh >= 100) {\n    return \"red\";\n  } else if (kmh >= 70) {\n    return \"amber\";\n  } else if (kmh >= 40) {\n    return \"yellow\";\n  } else {\n    return \"none\";\n  }\n}",
              "lang": "js",
              "file": "wind.js"
            },
            {
              "p": "**Example 3: you choose the line.** \"`pizzaSlices` takes a size: `\"small\"` has\n6 slices, `\"medium\"` and `\"large\"` both have 8, `\"party\"` has 12. The shop\nmakes no other size, so anything else has 0 slices.\""
            },
            {
              "code": "function pizzaSlices(size) {\n  switch (size) {\n    case \"small\":\n      return 6;\n    // TODO (A): medium and large share this branch\n      return 8;\n    case \"party\":\n      return 12;\n    // TODO (B): everything else\n  }\n}",
              "lang": "js",
              "file": "pizza.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes where TODO (A) is?",
                    "choices": [
                      "case \"medium\" || \"large\":",
                      "case \"medium\": case \"large\":",
                      "case \"medium\", \"large\":"
                    ],
                    "accept": [
                      "case \"medium\": case \"large\":"
                    ],
                    "miss": "**`\"medium\" || \"large\"` is just `\"medium\"`**, so `\"large\"` would match nothing. **`\"medium\", \"large\"` is worked out to just `\"large\"`**, so `\"medium\"` would match nothing. Two values that share a branch need two `case` labels, stacked.\n",
                    "why": "`case \"medium\": case \"large\":` — two cases with nothing between them, written here on one line.\n"
                  },
                  {
                    "question": "Which line goes where TODO (B) is?",
                    "choices": [
                      "default: return 0;",
                      "default: return pizzaSlices(size);",
                      "default: return;"
                    ],
                    "accept": [
                      "default: return 0;"
                    ],
                    "miss": "**`return pizzaSlices(size)` calls the function you are writing, with the same value**, and never gets an answer: it stops with a `RangeError`, as *Writing a function's answer* shows. **`return;` sends out nothing**, so the call returns `undefined`, not the `0` the sentence asks for.\n",
                    "why": "`default: return 0;`. The sentence gives everything else a real answer.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A help desk sends each ticket to a queue. A ticket has a priority — `\"low\"`,\n`\"normal\"`, `\"high\"` or `\"urgent\"` — and a true-or-false `isVip`. Low goes to\n`\"backlog\"`, normal to `\"standard\"`, high and urgent to `\"fast\"`. **Any ticket\nfrom a VIP goes to `\"fast\"`, whatever its priority.** A fifth priority,\n`\"critical\"`, is coming next year."
            },
            {
              "p": "Sketch how you would write it: where the VIP rule goes, and whether the\npriorities are a chain or a `switch` — and why. Then say what your default does\nwith a priority it does not know, and what happens on the day the first\n`\"critical\"` ticket arrives: does anyone find out, or does it quietly land in a\nqueue?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**A chain takes the first true branch**, top to bottom. When conditions overlap, order is part of the meaning: put the narrowest test first. Ranges need a chain.",
                "**A `switch` matches one value with `===`** and runs from the matching case down until a `break` or `return`. Stack cases with nothing between them to share a branch.",
                "**Nothing in JavaScript checks that every case is handled.** Write the default on purpose: a real answer for everything else, or a value like `\"unknown\"` that says nothing matched."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "switch",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/switch",
              "note": "the section on `break` and fall-through."
            },
            {
              "title": "if...else",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/if...else",
              "note": "including the note that `else if` is an `if` inside an `else`."
            }
          ]
        }
      ],
      "_source": "lessons/multiway.md",
      "practice": {
        "id": "multiway",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Which branch runs?",
            "prompt": "Two programs to predict, and two questions about where you would start. Type the value — with quotes if it is text — or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "let tier = \"\";\nswitch (\"silver\") {\n  case \"gold\":\n    tier = \"G\";\n  case \"silver\":\n    tier = \"S\";\n  case \"bronze\":\n    tier = \"B\";\n}\ntier\n",
                "accept": [
                  "\"B\"",
                  "B",
                  "'B'"
                ],
                "miss": "**If you answered `\"S\"` — the `switch` did not stop at the next `case`.** A case is where the program starts, not a box around it. With no `break`, it ran on into `case \"bronze\"`.\n",
                "why": "`\"B\"`. The `switch` enters at `case \"silver\"` and sets `tier` to `\"S\"`, then runs on into `case \"bronze\"` and sets it to `\"B\"`.\n"
              },
              {
                "id": "p2",
                "code": "const choice = \"1\";\nlet action = \"none\";\nswitch (choice) {\n  case 1:\n    action = \"add\";\n    break;\n  default:\n    action = \"invalid\";\n}\naction\n",
                "accept": [
                  "\"invalid\"",
                  "invalid",
                  "'invalid'"
                ],
                "miss": "**If you answered `\"add\"` — `switch` compares with `===`, and does not convert.** `\"1\" === 1` is `false`.\n",
                "why": "`\"invalid\"`. The text never equals the number case, so the `default` catches it.\n"
              },
              {
                "id": "p3",
                "question": "A thermostat gives `\"heat\"` below 18 degrees, `\"cool\"` above 24, and `\"off\"` otherwise. Chain or switch?",
                "choices": [
                  "Chain",
                  "Switch"
                ],
                "accept": [
                  "Chain"
                ],
                "miss": "**\"Below 18\" is a range.** A `case` is compared with `===`, so it can only name one exact value.\n",
                "why": "A chain. The tests are ranges.\n"
              },
              {
                "id": "p4",
                "question": "A `switch` has cases for `\"cat\"` and `\"dog\"`, each with a `return`, and no `default`. It sits in a function. What does the function return for `\"fish\"`? Type the value.",
                "accept": [
                  "undefined"
                ],
                "miss": "**No case matches and there is no `default`**, so nothing in the `switch` runs and the function ends without a `return`.\n",
                "why": "`undefined`, quietly.\n"
              }
            ],
            "extra": [
              {
                "id": "p5",
                "code": "let n = 0;\nswitch (\"b\") {\n  case \"a\":\n    n += 1;\n  case \"b\":\n    n += 2;\n  case \"c\":\n    n += 4;\n    break;\n  default:\n    n += 8;\n}\nn\n",
                "accept": [
                  "6"
                ],
                "why": "`6`. The `switch` enters at `case \"b\"` and adds 2, runs on through `case \"c\"` and adds 4, and leaves at the `break` in `case \"c\"`. The `default` never runs.\n"
              },
              {
                "id": "p6",
                "code": "const day = \"Sun\";\nlet kind;\nswitch (day.toLowerCase()) {\n  case \"sat\":\n  case \"sun\":\n    kind = \"weekend\";\n    break;\n  default:\n    kind = \"weekday\";\n}\nkind\n",
                "accept": [
                  "\"weekend\"",
                  "weekend",
                  "'weekend'"
                ],
                "why": "`\"weekend\"`. `day.toLowerCase()` is worked out once, to `\"sun\"`, before any case is compared.\n"
              },
              {
                "id": "p7",
                "question": "A shop gives each of its four store codes, `\"N\"`, `\"S\"`, `\"E\"` and `\"W\"`, a city name. Chain or switch?",
                "choices": [
                  "Chain",
                  "Switch"
                ],
                "accept": [
                  "Switch"
                ],
                "why": "A `switch`: one value, matched against exact ones.\n"
              }
            ],
            "hints": [
              {
                "when": "you stopped at the matching case",
                "say": "Find the `break`s. The `switch` runs every line after the match until a `break`, a `return` or its closing brace.\n"
              },
              {
                "when": "you expected text to match a number case",
                "say": "`switch` asks the strict question from *Equality: == versus ===*. Are the two values the same type?\n"
              },
              {
                "when": "you picked switch for a range",
                "say": "Could you write \"below 18\" as a single `case` value? A `case` is compared with `===`.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "See what nothing-matched does",
            "prompt": "Two functions, each called with values it was and was not written for. Predict each printed line — type the value as it prints, without quotes — then run it.\n",
            "filename": "shelves.js",
            "code": "function shelf(genre) {\n  let place;\n  switch (genre) {\n    case \"poetry\":\n      place = \"A\";\n    case \"novel\":\n      place = \"B\";\n      break;\n    case \"history\":\n      place = \"C\";\n      break;\n  }\n  return place;\n}\n\nfunction shipping(country) {\n  switch (country) {\n    case \"US\":\n      return 5;\n    case \"CA\":\n      return 8;\n  }\n}\n\nconsole.log(shelf(\"poetry\"));\nconsole.log(shelf(\"comics\"));\nconsole.log(shipping(\"ca\"));\nconsole.log(shipping(\"CA\") ?? 20);\nconsole.log(shipping(\"MX\") ?? 20);\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(shelf(\"poetry\"));",
                "accept": [
                  "B",
                  "\"B\""
                ],
                "miss": "**If you answered `A` — look for a `break` after `case \"poetry\"`.** There is none, so it falls into `case \"novel\"`.\n",
                "why": "`B`. `shelf(\"poetry\")` sets `place` to `\"A\"`, then falls through into `case \"novel\"`, which sets it to `\"B\"` and reaches a `break`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(shelf(\"comics\"));",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined`. No case matched, there is no `default`, and `place` was never given a value. `return place;` still runs and sends out `undefined`.\n"
              },
              {
                "id": "r3",
                "code": "console.log(shipping(\"ca\"));",
                "accept": [
                  "undefined"
                ],
                "miss": "**`\"ca\" === \"CA\"` is `false`** — `===` compares text exactly, capitals included.\n",
                "why": "`undefined`. No case matched, and the function ended without a `return`.\n"
              },
              {
                "id": "r4",
                "code": "console.log(shipping(\"CA\") ?? 20);",
                "accept": [
                  "8"
                ],
                "why": "`8`. The call returns `8`, which is not `null` or `undefined`, so `??` keeps it.\n"
              },
              {
                "id": "r5",
                "code": "console.log(shipping(\"MX\") ?? 20);",
                "accept": [
                  "20"
                ],
                "why": "`20`. The call returns `undefined`, and `??` falls back to `20`. The `?? 20` is a default added by the caller, outside `shipping`. It works, but anyone reading `shipping` on its own cannot see it.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected shelf(\"poetry\") to be A",
                "say": "Read down from `case \"poetry\"` and stop only at a `break`. Which lines run?\n"
              },
              {
                "when": "you expected an error for a value no case names",
                "say": "A `switch` with no `default` does not complain about a value it does not know. What does a function return when it ends without a `return`?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Which branch does the chain take?",
            "prompt": "The first item shows `speedLabel`. For each call, type what it returns (with quotes), then answer the question about its order.\n",
            "items": [
              {
                "id": "t1",
                "code": "function speedLabel(kmh) {\n  let label = \"stopped\";\n  if (kmh > 50) {\n    label = \"fast\";\n  } else if (kmh > 120) {\n    label = \"reckless\";\n  } else if (kmh > 0) {\n    label = \"moving\";\n  }\n  return label;\n}\nspeedLabel(150)\n",
                "accept": [
                  "\"fast\"",
                  "fast",
                  "'fast'"
                ],
                "miss": "**If you answered `\"reckless\"` — the chain takes the first true branch, not the best fit.** `150 > 50` is already `true`.\n",
                "why": "`\"fast\"`. `150 > 50` is the first condition, it is `true`, and the chain stops there.\n"
              },
              {
                "id": "t2",
                "code": "speedLabel(30)",
                "accept": [
                  "\"moving\"",
                  "moving",
                  "'moving'"
                ],
                "why": "`\"moving\"`. `30` is not above 50 and not above 120, but it is above 0.\n"
              },
              {
                "id": "t3",
                "question": "Which branch of `speedLabel` can never run, whatever the speed?",
                "choices": [
                  "fast",
                  "reckless",
                  "moving"
                ],
                "accept": [
                  "reckless"
                ],
                "why": "`reckless`. Every speed above 120 is also above 50, so the `fast` branch, which is tested first, catches it. The fix is order: test `> 120` before `> 50`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "speedLabel(0)",
                "accept": [
                  "\"stopped\"",
                  "stopped",
                  "'stopped'"
                ],
                "why": "`\"stopped\"`. `0 > 0` is `false`, so no branch runs and `label` keeps its starting value — the default.\n"
              },
              {
                "id": "t5",
                "code": "speedLabel(-5)",
                "accept": [
                  "\"stopped\"",
                  "stopped",
                  "'stopped'"
                ],
                "why": "`\"stopped\"`. The default catches every value no branch caught, including ones nobody meant it for, such as a negative speed.\n"
              },
              {
                "id": "t6",
                "code": "speedLabel(NaN)",
                "accept": [
                  "\"stopped\"",
                  "stopped",
                  "'stopped'"
                ],
                "why": "`\"stopped\"`. `NaN` fails every comparison, so it reaches the default too.\n"
              }
            ],
            "hints": [
              {
                "when": "you picked the branch that fits best",
                "say": "Test each condition from the top and stop at the first `true`. The chain never looks further.\n"
              },
              {
                "when": "you could not find the branch that never runs",
                "say": "For each branch, ask: is there any speed that fails every condition above it and passes this one?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Put the switch together",
            "prompt": "\"`wheels` takes a vehicle: a `\"bike\"` has 2 wheels, a `\"car\"` and a `\"taxi\"` both have 4, and anything else gives 0.\" Put the lines in order, with the cases in the order the sentence names them. Two lines are mistakes — leave them out.\n",
            "lines": [
              "function wheels(vehicle) {",
              "  switch (vehicle) {",
              "    case \"bike\":",
              "      return 2;",
              "    case \"car\":",
              "    case \"taxi\":",
              "      return 4;",
              "    default:",
              "      return 0;",
              "  }",
              "}"
            ],
            "distractors": [
              {
                "line": "    case \"car\" || \"taxi\":",
                "why": "`\"car\" || \"taxi\"` is worked out first, to `\"car\"`, so this case never matches `\"taxi\"`. Two values that share a branch need two stacked `case` lines.\n"
              },
              {
                "line": "      return wheels(vehicle);",
                "why": "This line calls `wheels` from inside `wheels` with the same value, so the call never gets an answer: it stops with a `RangeError`. The default returns a value: `0`.\n"
              }
            ],
            "why": "Name the value once in `switch`, then one case per value, with the two four-wheeled vehicles stacked on one `return`, and the `default` last for everything else.\n",
            "hints": [
              {
                "when": "you used the car || taxi line",
                "say": "Work out `\"car\" || \"taxi\"` on its own first, as in *What &&, || and ?? evaluate to*. Which single value does the case end up naming?\n"
              },
              {
                "when": "you left out default",
                "say": "The sentence gives \"anything else\" an answer. Which word catches every value no case named?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Add a shared bin, and a default",
            "prompt": "`binColour` works for paper and glass. Change it so that `\"cans\"` and `\"plastic\"` both give `\"yellow\"`, and anything else gives `\"unknown\"` instead of `undefined`. Keep the `switch` and its `let`. Then, in one sentence: what did the old version return for `\"batteries\"`, and why is that harder to notice than `\"unknown\"`?\n",
            "exportName": "binColour",
            "filename": "bins.js",
            "starter": "function binColour(waste) {\n  let bin;\n  switch (waste) {\n    case \"paper\":\n      bin = \"blue\";\n      break;\n    case \"glass\":\n      bin = \"green\";\n      break;\n  }\n  return bin;\n}\n",
            "tests": [
              {
                "label": "binColour(\"paper\") → \"blue\", binColour(\"glass\") → \"green\"",
                "body": "return subject('paper') === 'blue' && subject('glass') === 'green';"
              },
              {
                "label": "binColour(\"cans\") → \"yellow\"",
                "body": "return subject('cans') === 'yellow';"
              },
              {
                "label": "binColour(\"plastic\") → \"yellow\"",
                "body": "return subject('plastic') === 'yellow';"
              },
              {
                "label": "binColour(\"batteries\") → \"unknown\"",
                "body": "return subject('batteries') === 'unknown';"
              },
              {
                "label": "binColour(\"\") → \"unknown\"",
                "body": "return subject('') === 'unknown';"
              }
            ],
            "hints": [
              {
                "when": "cans gives blue or green",
                "say": "Your new case ran and then kept going into the next case. What ends a case — and is there one after your new line?\n"
              },
              {
                "when": "plastic gives unknown or undefined but cans is right",
                "say": "Two values, one branch: stack two `case` lines with nothing between them. `case \"cans\" || \"plastic\"` names only `\"cans\"`.\n"
              },
              {
                "when": "batteries gives undefined",
                "say": "No case names the values you did not list. Which word catches everything else — and what should it put in `bin`?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Rock, paper, scissors",
            "prompt": "\"`rpsResult` takes the player's choice and the computer's choice, each a number: 0 is rock, 1 is paper, 2 is scissors. Rock beats scissors, scissors beat paper, and paper beats rock. It returns `\"Player wins\"`, `\"Computer wins\"` or `\"It's a draw\"`.\" Write your answer between the braces, with `return` on every path — *Writing a function's answer* shows how. Then, in one sentence: how many different pairs of choices are there, and which of your branches handles the ones that are draws?\n",
            "exportName": "rpsResult",
            "filename": "rps.js",
            "starter": "// 0 is rock, 1 is paper, 2 is scissors.\nfunction rpsResult(player, computer) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "rpsResult(0, 0) → \"It's a draw\"",
                "body": "return subject(0, 0) === \"It's a draw\";"
              },
              {
                "label": "rpsResult(1, 1) → \"It's a draw\"",
                "body": "return subject(1, 1) === \"It's a draw\";"
              },
              {
                "label": "rpsResult(2, 2) → \"It's a draw\"",
                "body": "return subject(2, 2) === \"It's a draw\";"
              },
              {
                "label": "rpsResult(0, 2) → \"Player wins\" (rock beats scissors)",
                "body": "return subject(0, 2) === 'Player wins';"
              },
              {
                "label": "rpsResult(2, 1) → \"Player wins\" (scissors beat paper)",
                "body": "return subject(2, 1) === 'Player wins';"
              },
              {
                "label": "rpsResult(1, 0) → \"Player wins\" (paper beats rock)",
                "body": "return subject(1, 0) === 'Player wins';"
              },
              {
                "label": "rpsResult(2, 0) → \"Computer wins\" (rock beats scissors)",
                "body": "return subject(2, 0) === 'Computer wins';"
              },
              {
                "label": "rpsResult(1, 2) → \"Computer wins\" (scissors beat paper)",
                "body": "return subject(1, 2) === 'Computer wins';"
              },
              {
                "label": "rpsResult(0, 1) → \"Computer wins\" (paper beats rock)",
                "body": "return subject(0, 1) === 'Computer wins';"
              }
            ],
            "hints": [
              {
                "when": "the draws give Computer wins or Player wins",
                "say": "Three of the nine pairs are the same choice twice. Which test catches all three at once — and should it come before the others?\n"
              },
              {
                "when": "rpsResult(0, 2) gives Computer wins",
                "say": "A bigger number does not always win: rock (0) beats scissors (2). Write out who wins for each pair rather than comparing the numbers.\n"
              },
              {
                "when": "some calls give undefined",
                "say": "A path reaches the end of the function without a `return`. Which pairs have no branch — and does your chain end in a plain `else`?\n"
              },
              {
                "when": "the error says Maximum call stack size exceeded",
                "say": "Your answer calls `rpsResult` from inside `rpsResult`. Build it from `player`, `computer`, comparisons and `&&`, `||`.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a menu an assistant wrote",
            "prompt": "A code assistant wrote this to act on a menu choice a person typed. The spec is in the comment. It gives the wrong answer for every input. Answer the questions, then fix it.\n",
            "filename": "menu.js",
            "code": "// choice is what the person typed: \"1\" add, \"2\" list, \"3\" quit.\n// Anything else gives \"invalid\".\nfunction menuAction(choice) {\n  let action;\n  switch (choice) {\n    case 1:\n      action = \"add\";\n    case 2:\n      action = \"list\";\n      break;\n    case 3:\n      action = \"quit\";\n      break;\n  }\n  return action;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "As written, what does `menuAction(\"1\")` return? Type the value.",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined`. `choice` is the text `\"1\"`, the cases are numbers, and `\"1\" === 1` is `false`. No case can ever match typed text.\n"
              },
              {
                "id": "c2",
                "question": "Suppose the cases were fixed to match. Which line needs a `break` after it? Type the line number.",
                "accept": [
                  "7",
                  "line 7"
                ],
                "why": "Line 7. Without a `break` after it, the program runs on into `case 2`, and `action` changes from `\"add\"` to `\"list\"`.\n"
              },
              {
                "id": "c3",
                "question": "The spec says `\"9\"` gives `\"invalid\"`. Which keyword is missing that would give it?",
                "choices": [
                  "break",
                  "default",
                  "else"
                ],
                "accept": [
                  "default"
                ],
                "why": "`default`. With none, the call returns `undefined` for an unplanned choice, and whoever called the function has to find that out.\n"
              }
            ],
            "exportName": "menuAction",
            "starter": "// choice is what the person typed: \"1\" add, \"2\" list, \"3\" quit.\n// Anything else gives \"invalid\".\nfunction menuAction(choice) {\n  let action;\n  switch (choice) {\n    case 1:\n      action = \"add\";\n    case 2:\n      action = \"list\";\n      break;\n    case 3:\n      action = \"quit\";\n      break;\n  }\n  return action;\n}\n",
            "tests": [
              {
                "label": "menuAction(\"1\") → \"add\"",
                "body": "return subject('1') === 'add';"
              },
              {
                "label": "menuAction(\"2\") → \"list\", menuAction(\"3\") → \"quit\"",
                "body": "return subject('2') === 'list' && subject('3') === 'quit';"
              },
              {
                "label": "menuAction(\"9\"), (\"\") and (\"add\") → \"invalid\"",
                "body": "return subject('9') === 'invalid' && subject('') === 'invalid' && subject('add') === 'invalid';"
              }
            ],
            "hints": [
              {
                "when": "\"1\" still gives undefined",
                "say": "What type is `choice`? Either write the cases as that type, or convert once before the `switch` with `Number(choice)` — not both.\n"
              },
              {
                "when": "\"1\" gives list",
                "say": "The case matched, then kept running. Look at what ends the other two cases.\n"
              },
              {
                "when": "anything else gives undefined",
                "say": "No case names the values the spec did not list. The spec gives them an answer, `\"invalid\"`, so the branch for everything else should set `action` to it.\n"
              }
            ]
          }
        ]
      }
    },
    "check-flow": {
      "id": "check-flow",
      "title": "Branching check",
      "kind": "quiz",
      "unlocks": "Loops",
      "questions": [
        {
          "id": "q1",
          "skill": "Every path returns",
          "prompt": "What does this print?",
          "code": "function shippingNote(total) {\n  if (total >= 50) {\n    return \"free shipping\";\n  } else if (total > 0) {\n    return \"add \" + (50 - total) + \" for free shipping\";\n  }\n}\nconsole.log(shippingNote(0));",
          "answers": [
            {
              "key": "A",
              "text": "`add 50 for free shipping`",
              "tag": "0 > 0 read as true",
              "correct": false
            },
            {
              "key": "B",
              "text": "`undefined`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`free shipping`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Nothing — the function stops with an error",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `0 >= 50` is `false` and `0 > 0` is `false`, so neither branch runs, and there is no `else`. The function reaches its closing brace without a `return`, and the call returns `undefined` — quietly, with no error.",
            "wrong": "Work `0` through by hand. `0 >= 50` is `false`; `0 > 0` is `false` too, because 0 is not more than itself. No branch runs and there is no `else`, so the function ends without a `return` and returns `undefined`. The answer is B. An `else` for the last case means every path sends an answer out."
          }
        },
        {
          "id": "q2",
          "skill": "Which branch runs",
          "prompt": "What does `grade` hold?",
          "code": "const score = 85;\nlet grade;\nif (score >= 60) {\n  grade = \"pass\";\n} else if (score >= 80) {\n  grade = \"merit\";\n} else {\n  grade = \"fail\";\n}",
          "answers": [
            {
              "key": "A",
              "text": "`\"merit\"` — 85 is 80 or more",
              "tag": "the best match, not the first",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"pass\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`\"fail\"`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`undefined`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. An `if` / `else if` chain tests its conditions from the top and runs only the first branch whose condition is `true`. `85 >= 60` is `true`, so `grade` becomes `\"pass\"` and the chain is over: `score >= 80` is never tested.",
            "wrong": "An `if` / `else if` chain runs at most one branch: the first one whose condition is `true`, testing from the top. The first condition, `85 >= 60`, is already `true`, so `grade` is set to `\"pass\"` and the `else if` and `else` are skipped. With `score >= 80` tested first, the chain would give `\"merit\"`. The answer is B."
          }
        },
        {
          "id": "q3",
          "skill": "Truthiness",
          "prompt": "What does `label` hold?",
          "code": "const reply = \"no\";\nconst label = reply ? \"answered\" : \"skipped\";",
          "answers": [
            {
              "key": "A",
              "text": "`\"skipped\"` — the reply was no",
              "tag": "the word read as its meaning",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"answered\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`false`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`\"no\"`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. A ternary asks the falsy list, and the only falsy string is `\"\"`. `\"no\"` is two characters of text, so it is truthy and the value after `?` is taken.",
            "wrong": "A condition does not read the words in a string. It asks whether the value is on the falsy list — `false`, `0`, `\"\"`, `null`, `undefined`, `NaN` — and `\"no\"` is not. So the value after `?` is taken: `\"answered\"`. The answer is B."
          }
        },
        {
          "id": "q4",
          "skill": "The ternary operator",
          "prompt": "What does this print?",
          "code": "const count = 0;\nconsole.log(count + \" \" + (count > 1 ? \"items\" : \"item\"));",
          "answers": [
            {
              "key": "A",
              "text": "`0 items`",
              "tag": "what it was meant to do",
              "correct": false
            },
            {
              "key": "B",
              "text": "`0 item`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`0 false`",
              "tag": "the condition read as the result",
              "correct": false
            },
            {
              "key": "D",
              "text": "`item`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The ternary operator works out its condition first: `0 > 1` is `false`, so it gives the value after the `:`, `\"item\"`, and the line prints `0 item`. In English zero takes the plural, so `count === 1 ? \"item\" : \"items\"` is the condition this line needed.",
            "wrong": "The ternary operator, `condition ? valueIfTrue : valueIfFalse`, works out its condition and gives one of its two values. `0 > 1` is `false`, so it gives the value after the `:`, `\"item\"`, and the line prints `0 item`. The answer is B. The code was meant to print `0 items`; testing `count === 1` would fix it."
          }
        },
        {
          "id": "q5",
          "skill": "Ternary or if",
          "prompt": "By the common guideline, in which of these does the ternary operator fit better than an `if`?",
          "answers": [
            {
              "key": "A",
              "text": "Choosing `\"AM\"` or `\"PM\"` from the hour, to go inside a time string",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "Printing a warning and adding one to an error count when a password is too short",
              "tag": "a path that does things, not a value",
              "correct": false
            },
            {
              "key": "C",
              "text": "Choosing a shirt size from a chest measurement — S, M, L or XL",
              "tag": "more than two choices",
              "correct": false
            },
            {
              "key": "D",
              "text": "None — an `if` can go anywhere the ternary operator can",
              "tag": "an if produces no value",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The time string needs one of two values, and the ternary operator is an expression: it produces a value, so it can go straight inside the string. The password case does two things on one path, and the shirt size has four choices; by the same guideline, both read better as an `if` or an `if` / `else if` chain.",
            "wrong": "Ask whether the code chooses between two values or chooses what runs. Only `\"AM\"` or `\"PM\"` is a choice between two values. The ternary operator produces a value, so it can go inside the string; an `if` is a statement, produces no value, and cannot go there. The password case does two things, and the shirt size has four choices: both read better as an `if` chain. The answer is A."
          }
        },
        {
          "id": "q6",
          "skill": "Defaults — || versus ??",
          "prompt": "`seats` holds the number of free seats on a flight, or `null` when the airline has not said. `0` means the flight is full. Which line shows `\"unknown\"` only when the number is not known, and shows `0` for a full flight?",
          "answers": [
            {
              "key": "A",
              "text": "`seats || \"unknown\"`",
              "tag": "0 is on the falsy list",
              "correct": false
            },
            {
              "key": "B",
              "text": "`seats && \"unknown\"`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`seats ?? \"unknown\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`seats === undefined ? \"unknown\" : seats`",
              "tag": "misses null",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `??` falls back only for `null` and `undefined`, so a full flight's `0` is kept. Here `0` is a real answer, and that is the question that picks `??` over `||`.",
            "wrong": "Work `0` through each line. `||` asks the falsy list, and `0` is on it, so a full flight shows `\"unknown\"`. The `&&` line evaluates to `0` for a full flight but `\"unknown\"` for every flight with seats — backwards. The `=== undefined` test never matches `null`, so it shows `null`. `??` replaces only `null` and `undefined`. The answer is C."
          }
        },
        {
          "id": "q7",
          "skill": "What && returns",
          "prompt": "What does `note` hold?",
          "code": "const note = 5 > 2 && \"ok\";",
          "answers": [
            {
              "key": "A",
              "text": "`true`",
              "tag": "&& read as always giving a boolean",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"ok\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`false`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`5`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Logical AND (`&&`) produces one of its two operands, not a new boolean. The left operand, `5 > 2`, is `true`, which is truthy, so `&&` produces the right operand: `\"ok\"`.",
            "wrong": "Logical AND (`&&`) produces one of its two operands. When the left operand is falsy, `&&` produces the left operand; when it is truthy, `&&` produces the right operand. `5 > 2` is `true`, so `note` holds the right operand, `\"ok\"`. The answer is B. `&&` produces `true` only when its right operand is itself `true`."
          }
        },
        {
          "id": "q8",
          "skill": "Comparing text",
          "prompt": "Which shelf does this choose?",
          "code": "const name = \"adam\";\nlet shelf;\nif (name < \"M\") {\n  shelf = \"A to L\";\n} else {\n  shelf = \"M to Z\";\n}",
          "answers": [
            {
              "key": "A",
              "text": "`\"A to L\"` — a comes before M",
              "tag": "capitals forgotten",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"M to Z\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`undefined` — `<` does not work on text",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`undefined` — no branch sets `shelf`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Text compares one character at a time, and every capital A to Z comes before every lowercase a to z. `\"a\"` is lowercase, so it comes after `\"M\"`, `\"adam\" < \"M\"` is `false`, and the `else` runs. Lowercasing or capitalising both sides first would fix it.",
            "wrong": "`<` compares text in alphabetical order, one character at a time — with one catch: every capital A to Z comes before every lowercase a to z. `\"a\"` comes after `\"M\"`, so the condition is `false` and the `else` sets `\"M to Z\"`. The answer is B."
          }
        },
        {
          "id": "q9",
          "skill": "Fall-through",
          "prompt": "What does `perks` hold afterwards?",
          "code": "const plan = \"silver\";\nlet perks = \"\";\nswitch (plan) {\n  case \"gold\":\n    perks += \"lounge \";\n  case \"silver\":\n    perks += \"priority \";\n  case \"bronze\":\n    perks += \"snacks\";\n    break;\n  default:\n    perks = \"none\";\n}",
          "answers": [
            {
              "key": "A",
              "text": "`\"priority \"`",
              "tag": "a case read as a box with walls",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"lounge priority snacks\"`",
              "tag": "started at the top, not at the match",
              "correct": false
            },
            {
              "key": "C",
              "text": "`\"priority snacks\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`\"none\"`",
              "tag": "ran on into the default",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The `switch` jumps to `case \"silver\"` and runs every line below it until a `break`: it adds `\"priority \"`, falls through into `case \"bronze\"` and adds `\"snacks\"`, then stops at that `break` before the `default`.",
            "wrong": "A `case` is a place to start, not a box. The `switch` jumps to the case that matches, `\"silver\"` — so `\"lounge \"` is never added — and runs on through the next case, because there is no `break` between them. The `break` after `\"snacks\"` stops it before the `default`. The answer is C."
          }
        },
        {
          "id": "q10",
          "skill": "Chain or switch",
          "prompt": "Which of these decisions can be written as a `switch`?",
          "answers": [
            {
              "key": "A",
              "text": "A parcel's postage from its weight — up to 2 kg, up to 10 kg, over 10 kg",
              "tag": "a range is not one exact value",
              "correct": false
            },
            {
              "key": "B",
              "text": "A menu's action from the key pressed — `\"n\"`, `\"o\"`, `\"s\"` or `\"q\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "A warning when the temperature is over 30 and the humidity over 80",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "All three — a `switch` can do anything a chain can",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. A `switch` matches one value against exact values with `===`. The key is one value with four exact answers. Weights are ranges, and the warning tests two names at once: both need an `else if` chain.",
            "wrong": "Ask what each branch tests. A `case` is compared with `===`, so it can name one exact value only — never \"up to 2 kg\", and never two names at once. Ranges and combined conditions need a chain. The menu key is one value against exact strings, so it is the only one that fits a `switch`. The answer is B."
          }
        }
      ],
      "_source": "checks/check-flow.yml"
    },
    "while-loop": {
      "id": "while-loop",
      "title": "The while loop",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.while` (concepts.md): write a `while` loop that builds up a result, say how many iterations it runs and why it stops, and fix one that never stops. D-066 split `flow.repetition` into `flow.while`, `flow.for` and `flow.do-while`, one lesson each, because *Repeating work: while and for* was too much for one page; this lesson keeps that page's `while` material and `flow.repetition`'s first-loop job (zero-prerequisite audit, `flow.repetition` row: \"new lesson before `loop-forms`\"; D-042). Building up a result is taught here and recalled in every loop lesson after it (D-066). Pass 1 of loops, over numbers and strings; walking arrays is the Arrays module's (D-045). Procedural and explanation-first (D-061 item 1; 01-explanations rule 8): a loop's parts interact, and these students have no foothold to attempt one from. It models the writing direction (01-explanations rule 5a) because the `flow-loops` homework asks for a `while` with an unknown number of iterations (D-061 item 4). The loop that never ends is this form's characteristic mistake (D-066), so it has its own step and the practice's critique. The five loop subgoals extend *Writing a function's answer*'s four moves; the extension, the \"until\" step and the examples are derived from those rules, pending Nick's review.",
      "concepts": [
        "flow.while"
      ],
      "lede": "A loop runs the same lines again and again while a condition holds. This lesson covers the simplest loop, the `while` loop: how it decides to stop, how to build up a result one iteration at a time, how to tell how many iterations it runs, and how to find and fix a loop that never stops.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Writing a function's answer · The if statement · The for loop · The do…while loop · Choosing a loop"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Loop** — code that runs a block of lines again and again while a condition is `true`. **Body** — the lines between the loop's braces. **Iteration** — one run of the body. **Trace** — follow code by hand, one row of a table per step. **Accumulator** — a variable that builds up a result, one iteration at a time. **Infinite loop** — a loop whose condition never becomes `false`, so it never finishes.",
          "entries": [
            "**Loop** — code that runs a block of lines again and again while a condition is `true`.",
            "**Body** — the lines between the loop's braces.",
            "**Iteration** — one run of the body.",
            "**Trace** — follow code by hand, one row of a table per step.",
            "**Accumulator** — a variable that builds up a result, one iteration at a time.",
            "**Infinite loop** — a loop whose condition never becomes `false`, so it never finishes."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what loops are built from, from memory: updating a variable,\nadding to a string, and testing for an even number. Type the value, with quotes\nif it is text, or press the answer. Press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let total = 0;\ntotal += 4;\ntotal += 6;\ntotal\n",
                    "accept": [
                      "10"
                    ],
                    "miss": "**`total += 4` means `total = total + 4`**, from *Arithmetic operators*: 0, then 4, then 10.\n",
                    "why": "`10`.\n"
                  },
                  {
                    "code": "let count = 3;\ncount++;\ncount++;\ncount\n",
                    "accept": [
                      "5"
                    ],
                    "miss": "**Each `count++` adds one.** 3, then 4, then 5.\n",
                    "why": "`5`.\n"
                  },
                  {
                    "code": "let word = \"ab\";\nword += \"c\";\nword\n",
                    "accept": [
                      "\"abc\"",
                      "abc",
                      "'abc'"
                    ],
                    "miss": "**With text, `+` and `+=` join**, from *Working with strings*.\n",
                    "why": "`\"abc\"`.\n"
                  },
                  {
                    "code": "17 % 2 === 0",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**`17 % 2` is the remainder after dividing by 2**, from *Arithmetic operators*: 17 is 8 twos and 1 left over. A remainder of 0 means even.\n",
                    "why": "`false`. `17 % 2` is `1`, so 17 is odd.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say how many iterations a `while` loop runs, and why it stops where it does",
                "I can write a `while` loop inside a function that builds up a total or a string and returns it",
                "I can trace a loop one iteration at a time in a table",
                "I can find why a loop never stops, and fix it"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "What a loop does",
          "body": [
            {
              "p": "Every program so far has run each line at most once, top to bottom: an `if`\ncan skip lines, but nothing goes back. Some work has to be done many times.\nHere is a program that adds up the numbers from 1 to 5, written with only what\nyou have met so far:"
            },
            {
              "code": "let total = 0;\ntotal += 1;\ntotal += 2;\ntotal += 3;\ntotal += 4;\ntotal += 5;\nconsole.log(total);",
              "lang": "js",
              "file": "sum-by-hand.js"
            },
            {
              "p": "It prints `15`, and it works. But five of its lines are nearly identical: each\none adds the next number to `total`. Writing near-identical lines of code over\nand over causes two problems:"
            },
            {
              "list": [
                "**It does not grow.** Adding up the numbers from 1 to 100 this way takes 100 near-identical lines, and a typo in any one of them is easy to make and hard to spot.",
                "**Sometimes there is no right number of lines to write.** Suppose a function takes a number `n` and adds up the numbers from 1 to `n`. Called with 5, it needs five of those lines; called with 100, it needs a hundred. You write the function once, before anyone calls it, so you cannot know how many to write."
              ]
            },
            {
              "p": "A **loop** solves both. You write the repeated line once, and the loop runs it\nagain and again, as many times as the work needs, even when that number is\nonly known once the program is running."
            },
            {
              "p": "JavaScript has four loops you will write in this module: the `while` loop, the\n`for` loop, the `do…while` loop and the `for…of` loop. This lesson covers the\n`while` loop, the simplest of them. The next three lessons cover the others,\none each, and *Choosing a loop* compares all four. Other ways to repeat work\nover a list or an object come in the Arrays and Objects modules."
            },
            {
              "p": "Here is a `while` loop:"
            },
            {
              "code": "let n = 3;\nwhile (n > 0) {\n  console.log(n);\n  n = n - 1;\n}\nconsole.log(\"Go\");",
              "lang": "js",
              "file": "countdown.js"
            },
            {
              "p": "A `while` loop (officially, the `while` statement) works like an `if` that\nkeeps coming back:"
            },
            {
              "list": [
                "**Check the condition.** `n > 0` — is it `true`?",
                "**If it is `true`, run the body** (the lines between the braces), then go back to step 1.",
                "**If it is `false`, skip the body** and carry on after the closing brace."
              ],
              "ordered": true
            },
            {
              "p": "One run of the body is an **iteration**. To follow a loop, write a table with one row for each check of the\ncondition. Writing that table is called **tracing** the loop:"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`n`",
                  "`n > 0`",
                  "What happens"
                ],
                "rows": [
                  [
                    "1",
                    "`3`",
                    "`true`",
                    "prints `3`; `n` becomes `2`"
                  ],
                  [
                    "2",
                    "`2`",
                    "`true`",
                    "prints `2`; `n` becomes `1`"
                  ],
                  [
                    "3",
                    "`1`",
                    "`true`",
                    "prints `1`; `n` becomes `0`"
                  ],
                  [
                    "4",
                    "`0`",
                    "`false`",
                    "the loop ends; prints `Go`"
                  ]
                ]
              }
            },
            {
              "p": "The loop runs three iterations but checks its condition four times. The fourth check is `false`, and\nthat is what stops the loop."
            },
            {
              "p": "Because the condition is checked **before** each iteration, a loop can run no\niterations at all. Start with `let n = 0;` and the first check is `false`: the body\nnever runs, and the program prints only `Go`."
            },
            {
              "p": "Type each answer as a number, or press the answer."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let n = 5;\nlet iterations = 0;\nwhile (n > 2) {\n  n = n - 1;\n  iterations++;\n}\niterations\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**Trace it.** `n` is 5, 4, 3 at the checks that say `true`; at the fourth check `n` is 2, and `2 > 2` is `false`.\n",
                    "why": "`3`.\n"
                  },
                  {
                    "question": "In the same loop, what does `n` hold after the loop ends? Type the number.",
                    "accept": [
                      "2"
                    ],
                    "why": "`2`. The loop stops at the first check that is `false`, which is when `n` is 2.\n"
                  },
                  {
                    "question": "A `while` loop is reached, and its condition is already `false`. How many times does its body run? Type the number.",
                    "accept": [
                      "0",
                      "zero",
                      "none"
                    ],
                    "why": "`0`. The condition is checked before every iteration, the first one included.\n"
                  },
                  {
                    "question": "When does a `while` loop check its condition?",
                    "choices": [
                      "Before every iteration",
                      "After every iteration",
                      "Only once, at the start"
                    ],
                    "accept": [
                      "Before every iteration"
                    ],
                    "why": "Before every iteration. That is why a `while` loop can run zero iterations, and why it stops as soon as the condition turns `false`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Building up a result",
          "body": [
            {
              "p": "Most loops you write will work something out and return it from a function.\nThe loop lessons in this module write them with one pattern: make a name for\nthe result **before** the loop, change it a little on each iteration, and `return`\nit **after** the loop."
            },
            {
              "p": "The pattern has five subgoals. They extend the four moves of *Writing a\nfunction's answer* with the parts a loop adds:"
            },
            {
              "list": [
                "**Start the result** — and the counter, if the loop needs one.",
                "**Say when to stop** — the condition.",
                "**Do one iteration's work** — the part that repeats.",
                "**Change something each iteration** — so the condition can become `false`.",
                "**Send the result out** — `return`, after the loop."
              ]
            },
            {
              "p": "**Example 1.** This is the function from the start of the lesson, the one\nthat could not be written as a fixed number of lines. \"`sumTo` takes a whole\nnumber `n` and returns 1 + 2 + … + n.\""
            },
            {
              "p": "The repeated line, `total += …`, is written once. The number it adds changes\nevery iteration, so it is kept in a name, `i`, which starts at 1 and goes up by one\neach iteration until it goes past `n`."
            },
            {
              "code": "function sumTo(n) {\n  // start the result\n  let total = 0;\n  let i = 1;\n  // say when to stop\n  while (i <= n) {\n    // do one iteration's work\n    total += i;\n    // change something each iteration\n    i++;\n  }\n  // send the result out\n  return total;\n}",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "p": "A trace of `sumTo(4)`, one row per check:"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`i`",
                  "`i <= 4`",
                  "`total`"
                ],
                "rows": [
                  [
                    "1",
                    "`1`",
                    "`true`",
                    "`1`"
                  ],
                  [
                    "2",
                    "`2`",
                    "`true`",
                    "`3`"
                  ],
                  [
                    "3",
                    "`3`",
                    "`true`",
                    "`6`"
                  ],
                  [
                    "4",
                    "`4`",
                    "`true`",
                    "`10`"
                  ],
                  [
                    "5",
                    "`5`",
                    "`false`",
                    "`10`"
                  ]
                ]
              }
            },
            {
              "p": "`sumTo(4)` returns `10`. `total` is a **running total**: each iteration adds one\nmore number to it. A variable that builds up a result like this is called an\n**accumulator**. `total` is made with `let`, before the loop, because it has to\nkeep its value from one iteration to the next. A `let total = 0;` written inside the\nbraces would run on every iteration and start the total again at 0."
            },
            {
              "p": "**Example 2.** \"`liftoff` takes a number and returns a countdown from it,\nending in liftoff: `liftoff(3)` is `\"3 2 1 liftoff\"`.\" The same five\nsubgoals build text instead of a number. A text result starts empty, as `\"\"`,\nand `+=` adds to the end of it."
            },
            {
              "code": "function liftoff(n) {\n  let text = \"\";\n  let count = n;\n  while (count > 0) {\n    text += count + \" \";   // a number + \" \" is text: \"3 \"\n    count--;\n  }\n  return text + \"liftoff\";\n}",
              "lang": "js",
              "file": "liftoff.js"
            },
            {
              "p": "`count--` is the *decrement operator*: it takes one away from `count`, as\n`count++`, the *increment operator*, adds one. In `liftoff(3)`, `text` is `\"3 \"`\nafter the first iteration, then `\"3 2 \"`, then `\"3 2 1 \"`. After the loop, the\n`return` adds `\"liftoff\"` to the end. `liftoff(0)` runs no iterations and returns\n`\"liftoff\"`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A test calls `sumTo(3)`. What does it return? Type the number.",
                    "accept": [
                      "6"
                    ],
                    "why": "`6`. `total` is 1, then 3, then 6.\n"
                  },
                  {
                    "question": "How many iterations does `sumTo(3)` run? Type the number.",
                    "accept": [
                      "3",
                      "three"
                    ],
                    "why": "`3`, one for each of 1, 2 and 3. The fourth check, with `i` at 4, is `false`.\n"
                  },
                  {
                    "question": "What does `liftoff(2)` return? Type the text, without quotes.",
                    "accept": [
                      "2 1 liftoff",
                      "\"2 1 liftoff\""
                    ],
                    "why": "`\"2 1 liftoff\"`.\n"
                  },
                  {
                    "question": "Where does `return total;` go in `sumTo`?",
                    "choices": [
                      "Inside the loop, after total += i",
                      "After the closing brace of the loop",
                      "Before the loop"
                    ],
                    "accept": [
                      "After the closing brace of the loop"
                    ],
                    "miss": "**`return` ends the whole function the moment it runs.** A `return` inside the loop would send `total` out after the first iteration. Before the loop, `total` is still 0.\n",
                    "why": "After the loop, once every iteration has added its number.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Where a loop stops",
          "body": [
            {
              "p": "A `while` loop checks its condition only at the top, before each iteration. It\nnever stops in the middle of an iteration, even when a value goes past the point you\nhad in mind."
            },
            {
              "p": "**Example 1.** Keep doubling a size while it is less than 100:"
            },
            {
              "code": "let size = 1;\nlet doublings = 0;\nwhile (size < 100) {\n  size = size * 2;\n  doublings++;\n}",
              "lang": "js",
              "file": "doubling.js"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`size`",
                  "`size < 100`",
                  "What happens"
                ],
                "rows": [
                  [
                    "1",
                    "`1`",
                    "`true`",
                    "`size` becomes `2`"
                  ],
                  [
                    "2",
                    "`2`",
                    "`true`",
                    "`size` becomes `4`"
                  ],
                  [
                    "3",
                    "`4`",
                    "`true`",
                    "`size` becomes `8`"
                  ],
                  [
                    "4",
                    "`8`",
                    "`true`",
                    "`size` becomes `16`"
                  ],
                  [
                    "5",
                    "`16`",
                    "`true`",
                    "`size` becomes `32`"
                  ],
                  [
                    "6",
                    "`32`",
                    "`true`",
                    "`size` becomes `64`"
                  ],
                  [
                    "7",
                    "`64`",
                    "`true`",
                    "`size` becomes `128`"
                  ],
                  [
                    "8",
                    "`128`",
                    "`false`",
                    "the loop ends"
                  ]
                ]
              }
            },
            {
              "p": "`size` ends at 128, not 100. At the seventh check, `size` is 64, and\n`64 < 100` is `true`, so the iteration runs and doubles it to 128. Only then is the\ncondition checked again. The loop ran 7 iterations, so `doublings` is 7."
            },
            {
              "p": "**Example 2.** The same thing happens with text. Keep adding `\"ab\"` while the\ntext is shorter than 5 characters:"
            },
            {
              "code": "let stripes = \"\";\nwhile (stripes.length < 5) {\n  stripes += \"ab\";\n}",
              "lang": "js",
              "file": "stripes.js"
            },
            {
              "p": "`stripes.length` is 0, then 2, then 4: each check is `true`. The third iteration\nmakes `stripes` `\"ababab\"`, and `6 < 5` is `false`. The loop ran 3 iterations, and\n`stripes` ends 6 characters long, one more than 5."
            },
            {
              "p": "Both examples show the same two things:"
            },
            {
              "list": [
                "**When a `while` loop ends, its condition is `false`.** That is the one thing you know for certain after the loop. After `doubling.js`, `size < 100` is `false`, so `size` is 100 or more. It is not necessarily exactly 100.",
                "**You cannot read the number of iterations off the `while` line.** It depends on the values, so you find it by tracing: count the checks that are `true`."
              ]
            },
            {
              "p": "Type each answer as a number, or press the answer."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let n = 10;\nwhile (n > 0) {\n  n = n - 4;\n}\nn\n",
                    "accept": [
                      "-2"
                    ],
                    "miss": "**The check comes before each iteration, not in the middle of one.** `n` is 10, 6, then 2; `2 > 0` is `true`, so the iteration runs and `n` becomes `-2`.\n",
                    "why": "`-2`. Then `-2 > 0` is `false`.\n"
                  },
                  {
                    "question": "How many iterations did that loop run? Type the number.",
                    "accept": [
                      "3",
                      "three"
                    ],
                    "why": "`3`: with `n` at 10, 6 and 2.\n"
                  },
                  {
                    "code": "let x = 1;\nwhile (x < 50) {\n  x = x * 5;\n}\nx\n",
                    "accept": [
                      "125"
                    ],
                    "miss": "**When `x` is 25, `25 < 50` is `true`**, so one more iteration runs.\n",
                    "why": "`125`. 1, 5, 25, 125.\n"
                  },
                  {
                    "question": "After `doubling.js` ends, which of these is certain?",
                    "choices": [
                      "size is exactly 100",
                      "size is 100 or more",
                      "size is less than 100"
                    ],
                    "accept": [
                      "size is 100 or more"
                    ],
                    "miss": "**The loop ends when `size < 100` is `false`.** That says `size` is 100 or more; it does not say by how much.\n",
                    "why": "`size` is 100 or more. It is 128.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "A loop that never stops",
          "body": [
            {
              "p": "A `while` loop stops when its condition becomes `false`. The condition can only\nchange if a line in the body changes the value the condition checks. That line\nis the **change something each iteration** subgoal. Leave it out, or get it wrong,\nand the condition stays `true` forever: an **infinite loop**. It is the mistake\n`while` loops are best known for."
            },
            {
              "p": "**Example 1.** \"`halvings` takes a number and returns how many times you can\nhalve it before it is 1 or less: 8, 4, 2, 1 is three halvings.\" Here is a\nfirst attempt. It has a mistake, shown on purpose: the line meant to halve\n`value` does not change `value`."
            },
            {
              "code": "function halvings(n) {\n  let value = n;\n  let count = 0;\n  while (value > 1) {\n    value / 2;\n    count++;\n  }\n  return count;\n}",
              "lang": "js",
              "file": "halve.js"
            },
            {
              "p": "The practice editor ran its three tests and, after a few seconds, showed this\nand nothing else:"
            },
            {
              "code": "Timed out after 5000ms — check for a loop that never ends.",
              "lang": "text"
            },
            {
              "p": "The editor waits a few seconds and then gives up; how long it waits depends on\nhow many tests there are. No test gets a ✓ or a ✗, because none of them\nfinished."
            },
            {
              "block": "notice",
              "label": "Outside this editor, nothing warns you",
              "body": [
                {
                  "p": "The practice editor stops a program that runs too long and tells you so. A\nterminal does not. Run a program like this with `node` and there is no error\nand no message: the terminal just sits there, as if the program were still\nworking, and it will keep running until you stop it. If a program you\nexpected to finish at once is still going, suspect an infinite loop."
                },
                {
                  "p": "To stop it, click in the terminal and press **Ctrl+C**. On a Mac it is the\nsame keys, Control and C, not Command. The program ends and the terminal is\nready for your next command."
                }
              ]
            },
            {
              "p": "To find the problem, work one test through by hand. Here is `halvings(8)`, with\n`n` holding 8:"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`value`",
                  "`value > 1`",
                  "What the body does"
                ],
                "rows": [
                  [
                    "1",
                    "`8`",
                    "`true`",
                    "works out `8 / 2`, which is `4`, and throws it away; `count` is 1"
                  ],
                  [
                    "2",
                    "`8`",
                    "`true`",
                    "works out `4` again, throws it away; `count` is 2"
                  ],
                  [
                    "3",
                    "`8`",
                    "`true`",
                    "… and so on, forever"
                  ]
                ]
              }
            },
            {
              "p": "`value / 2` works out a value, but nothing stores it, so `value` never\nchanges. The line has to assign the result back to `value`:"
            },
            {
              "code": "    value = value / 2;",
              "lang": "js",
              "file": "halve.js"
            },
            {
              "p": "With that fix, `value` is 8, then 4, then 2, then 1. The check `1 > 1` is\n`false`, so the loop stops, and `halvings(8)` returns `3`."
            },
            {
              "p": "**Example 2.** A loop can also change its value and still never stop, when the\nvalue steps over the point where the condition would turn `false`. Here is\n`stripes.js` from the last step again, with one change: the condition is\nwritten with `!==`, the *strict inequality* operator, as \"keep going while the\nlength is not 5\"."
            },
            {
              "code": "let stripes = \"\";\nwhile (stripes.length !== 5) {\n  stripes += \"ab\";\n}",
              "lang": "js",
              "file": "stripes.js"
            },
            {
              "p": "The length goes 0, 2, 4, 6, 8, and on. It steps over 5 and never equals it, so\n`stripes.length !== 5` is `true` at every check, and the loop never ends. With\n`<`, the same loop stopped at 6."
            },
            {
              "p": "`!==` is a legal condition, and it works when the value is certain to land\nexactly on the stopping value, as a count that goes up by 1 from 0 does. `<`\nand `>` also stop when the value jumps past that point, or starts past it. So\nwhen a value moves in one direction, this course writes `<` or `>` (or `<=`,\n`>=`) in the loop's condition: it costs nothing, and the loop cannot run\nforever because of a step you did not foresee."
            },
            {
              "p": "**Example 3.** Most lines end in `;`, so it is easy to type one after a\nloop's condition too. The next piece of code is wrong on purpose:"
            },
            {
              "code": "let n = 3;\nwhile (n > 0); {\n  n--;\n}",
              "lang": "js",
              "file": "countdown.js"
            },
            {
              "p": "This never finishes. A semicolon on its own is an **empty statement**: a\nstatement that does nothing. JavaScript allows one anywhere a statement can go,\nand a loop's body is one statement, so the `;` right after `while (n > 0)` is\nthe loop's whole body. The braces below it are a separate block, after the\nloop. So the loop checks `3 > 0`, does nothing, and checks again, forever:\nnothing in the loop changes `n`. If `n` started at 0, the loop would end at the\nfirst check, and the block would then run once, leaving `n` at -1."
            },
            {
              "p": "Either way nothing reports an error: the program hangs, or it runs and gets\nthe wrong answer. Look for a `;` right after the `)` that closes a loop's\ncondition. A `while` loop's body comes after its condition, so a `;` there\nends the loop before its body. (*The do…while loop* shows the one loop whose\n`while (…)` does end in `;`, because there the condition comes last.)"
            },
            {
              "p": "There are four ways, then, for the condition to stay `true` forever:"
            },
            {
              "table": {
                "head": [
                  "What goes wrong",
                  "Example",
                  "What happens to the value"
                ],
                "rows": [
                  [
                    "Nothing changes it",
                    "`value / 2;`",
                    "works out a new value and throws it away"
                  ],
                  [
                    "It changes the wrong way",
                    "`count++` in a loop that waits for `count` to fall to 0",
                    "moves away from the stopping point"
                  ],
                  [
                    "It steps over the stopping point",
                    "`!==` with a step of 2",
                    "never equals the one value that would stop it"
                  ],
                  [
                    "The body is empty",
                    "`while (n > 0);`",
                    "the line that changes it is outside the loop"
                  ]
                ]
              }
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let k = 5;\nwhile (k > 0) {\n  console.log(k);\n}\n",
                    "choices": [
                      "It prints 5 4 3 2 1",
                      "It prints 5 once",
                      "It never finishes"
                    ],
                    "accept": [
                      "It never finishes"
                    ],
                    "miss": "**Nothing in the body changes `k`.** It is 5 at every check, so `k > 0` is `true` at every check.\n",
                    "why": "It prints `5` again and again and never finishes.\n"
                  },
                  {
                    "code": "let k = 5;\nwhile (k > 0) {\n  k++;\n}\n",
                    "choices": [
                      "It runs 5 iterations",
                      "It never finishes",
                      "It runs 0 iterations"
                    ],
                    "accept": [
                      "It never finishes"
                    ],
                    "miss": "**`k` changes, but in the wrong direction.** 6, 7, 8… each is still more than 0.\n",
                    "why": "It never finishes. The change has to move the value *towards* the point where the condition is `false`.\n"
                  },
                  {
                    "question": "Which line, added to the body of the loop that prints `k`, makes it print 5, 4, 3, 2, 1 and stop?",
                    "choices": [
                      "k - 1;",
                      "k--;",
                      "k++;"
                    ],
                    "accept": [
                      "k--;"
                    ],
                    "miss": "**If you chose `k - 1;` — it works out a value and throws it away,** as `value / 2;` did in `halvings`. `k--` changes `k` itself.\n",
                    "why": "`k--;`, the same as `k = k - 1;`.\n"
                  },
                  {
                    "code": "let n = 9;\nwhile (n !== 0) {\n  n = n - 2;\n}\n",
                    "choices": [
                      "It runs 4 iterations",
                      "It runs 5 iterations",
                      "It never finishes"
                    ],
                    "accept": [
                      "It never finishes"
                    ],
                    "miss": "**Write out the values of `n`:** 9, 7, 5, 3, 1, -1, -3… It goes from 1 to -1 without ever being 0.\n",
                    "why": "It never finishes. `while (n > 0)` would stop it after 5 iterations, with `n` at -1.\n"
                  },
                  {
                    "code": "let n = 0;\nwhile (n > 0); {\n  n--;\n}\n// What does n hold now? Type the number.\n",
                    "accept": [
                      "-1"
                    ],
                    "miss": "**The `;` after the condition is the loop's whole body.** `0 > 0` is `false`, so that empty loop ends at once. The block in braces is not part of the loop: it runs once, after it.\n",
                    "why": "`-1`. With `n` starting at 3, the same code never finishes.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "From a sentence to a while loop",
          "body": [
            {
              "p": "Exercises and homework give you a sentence and an empty function. For a loop,\nturn the sentence into the five subgoals before writing any code:"
            },
            {
              "list": [
                "**Start the result.** What is it before the loop starts: 0 for a total, `\"\"` for text, or the value you were given?",
                "**Say when to stop.** Sentences often say *until*: \"until it reaches 1\". A `while` loop's condition says when to **keep going**, which is the opposite. \"Until it reaches 1\" becomes \"keep going **while** it is not there yet\".",
                "**Do one iteration's work.** What happens to the result on each iteration?",
                "**Change something each iteration.** What moves the loop on, towards the point where the condition is `false`?",
                "**Send the result out.** `return` it after the loop."
              ],
              "ordered": true
            },
            {
              "p": "**Example 1.** \"`stepsToOne` takes a whole number of 1 or more and counts the\nsteps it takes to reach 1 by two rules: if the number is even, halve it; if it\nis odd, multiply it by 3 and add 1. `stepsToOne(6)` is 8: 6, 3, 10, 5, 16, 8,\n4, 2, 1.\" (These numbers are called the *Collatz sequence*. Nobody has found a\nway to work out the number of steps without taking them, which makes it a job\nfor a `while` loop.)"
            },
            {
              "p": "Here is a first attempt. It has a mistake, shown on purpose: its condition is\nthe sentence's *until*, copied in as it stands."
            },
            {
              "code": "function stepsToOne(n) {\n  let value = n;\n  let steps = 0;\n  while (value === 1) {\n    steps++;\n    if (value % 2 === 0) {\n      value = value / 2;\n    } else {\n      value = 3 * value + 1;\n    }\n  }\n  return steps;\n}",
              "lang": "js",
              "file": "collatz.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✗ stepsToOne(6) → 8\n✗ stepsToOne(1) → 0\n✗ stepsToOne(16) → 4\n✗ stepsToOne(7) → 16",
              "lang": "text"
            },
            {
              "p": "The first test that failed is `stepsToOne(6)`. Trace it, with `n` holding 6:"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`value`",
                  "`value === 1`",
                  "What happens"
                ],
                "rows": [
                  [
                    "1",
                    "`6`",
                    "`false`",
                    "the loop ends; `steps` is still 0"
                  ]
                ]
              }
            },
            {
              "p": "The loop runs no iterations at all, and `stepsToOne(6)` returns `0`. The condition\nsays when to **stop**, but a `while` loop runs while its condition is `true`.\nThe loop has to keep going while `value` has **not** reached 1. `value` starts\nat 1 or more, so \"not reached 1 yet\" is `value > 1`:"
            },
            {
              "code": "function stepsToOne(n) {\n  // start the result\n  let value = n;\n  let steps = 0;\n  // say when to stop: \"until it reaches 1\" becomes \"while value > 1\"\n  while (value > 1) {\n    // do one iteration's work\n    steps++;\n    // change something each iteration\n    if (value % 2 === 0) {\n      value = value / 2;\n    } else {\n      value = 3 * value + 1;\n    }\n  }\n  // send the result out\n  return steps;\n}",
              "lang": "js",
              "file": "collatz.js"
            },
            {
              "code": "✓ stepsToOne(6) → 8\n✓ stepsToOne(1) → 0\n✓ stepsToOne(16) → 4\n✓ stepsToOne(7) → 16",
              "lang": "text"
            },
            {
              "p": "`value !== 1` would pass these four tests too: the value always lands exactly\non 1. But a caller who passed 0 by mistake would wait forever, because 0 is\neven, and half of 0 is 0. With `value > 1`, `stepsToOne(0)` runs no iterations and\nreturns 0."
            },
            {
              "p": "**Example 2: you choose the lines.** \"`fillTo` takes a piece of text and a\nwidth, and adds dots to the end of the text until it is that many characters\nlong: `fillTo(\"Name\", 8)` is `\"Name....\"`. Text that is already that long, or\nlonger, comes back unchanged.\""
            },
            {
              "code": "function fillTo(text, width) {\n  let line = text;\n  // TODO (a): \"until it is width characters long\"\n    // TODO (b): add one dot to the end\n  }\n  return line;\n}",
              "lang": "js",
              "file": "fill.js"
            },
            {
              "p": "Here one line is both the iteration's work and the change the condition depends on.\nThat is common: when the result is the value the condition checks, changing\nthe result is what moves the loop on."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes at TODO (a)?",
                    "choices": [
                      "while (line.length >= width) {",
                      "while (line.length < width) {",
                      "while (line.length !== width) {"
                    ],
                    "accept": [
                      "while (line.length < width) {"
                    ],
                    "miss": "**If you chose `>=` — that is the *until*, copied in.** `\"Name\"` is shorter than 8, so the first check is `false` and no dots are added; and `\"Contents\"`, already 8 long, would get dots forever. **If you chose `!==` — try `fillTo(\"Contents\", 5)`.** The length starts at 8, grows, and never equals 5.\n",
                    "why": "`while (line.length < width) {`: keep going while the text is still too short.\n"
                  },
                  {
                    "question": "Which line goes at TODO (b)?",
                    "choices": [
                      "line + \".\";",
                      "line += \".\";",
                      "text += \".\";"
                    ],
                    "accept": [
                      "line += \".\";"
                    ],
                    "miss": "**If you chose `line + \".\";` — it works out the longer text and throws it away,** as `value / 2;` did in `halvings`, and the loop never ends. **If you chose `text += \".\";` — the condition checks `line`, and `line` never changes,** so that loop never ends either.\n",
                    "why": "`line += \".\";`. `fillTo(\"Ch\", 4)` runs two iterations and returns `\"Ch..\"`.\n"
                  },
                  {
                    "question": "Before the fix, `stepsToOne(6)` returned 0. How many iterations did its loop run? Type the number.",
                    "accept": [
                      "0",
                      "zero",
                      "none"
                    ],
                    "why": "`0`. `6 === 1` is `false` at the first check, so the body never ran.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "Two loops add to a value until it reaches 1:"
            },
            {
              "code": "let a = 0;\nwhile (a !== 1) {\n  a = a + 0.25;\n}\n\nlet b = 0;\nwhile (b !== 1) {\n  b = b + 0.1;\n}",
              "lang": "js",
              "file": "tenths.js"
            },
            {
              "p": "The loop on `a` stops after four iterations. The loop on `b` never stops: after ten\niterations `b` is `0.9999999999999999`, because, as `0.1 + 0.2` showed in\n*Arithmetic operators*, most decimals are not stored exactly. Changing the\nsecond condition to `b < 1` makes the loop stop, but after **eleven** iterations,\nwith `b` at `1.0999999999999999`."
            },
            {
              "p": "You want a loop that adds 0.1 exactly ten times. Choose what its condition\nshould check, and defend the choice: why can a tiny error in `b` not change\nhow many iterations your loop runs?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**A `while` loop checks its condition before every iteration** and stops at the first check that is `false`, so it can run zero iterations. When it ends, its condition is `false`; the value may have gone past the point you had in mind.",
                "**Build a result in five subgoals:** start it before the loop, say when to stop (an *until* becomes its opposite), do one iteration's work, change something each iteration, `return` it after the loop.",
                "**A loop never stops when nothing moves its value towards `false`:** a change that is thrown away, a change in the wrong direction, a `!==` that is stepped over, or a stray `;` after the condition that empties the body. Find it by tracing a few checks by hand."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "while",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/while",
              "note": "the condition, the body, and a loop that never runs."
            },
            {
              "title": "Loops and iteration",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Loops_and_iteration",
              "note": "every kind of loop; this lesson is its `while` section."
            }
          ]
        }
      ],
      "_source": "lessons/while-loop.md",
      "practice": {
        "id": "while-loop",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "How many iterations, and what is left?",
            "prompt": "Type the value each one ends with, or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "let total = 0;\nlet i = 1;\nwhile (i <= 3) {\n  total += i;\n  i++;\n}\ntotal\n",
                "accept": [
                  "6"
                ],
                "miss": "**Trace it one iteration at a time.** `i` is 1, 2, 3 at the checks that are `true`; `total` becomes 1, then 3, then 6.\n",
                "why": "`6`.\n"
              },
              {
                "id": "p2",
                "question": "A program reaches `while (count > 0) {`, with `count` holding 3. What happens first?",
                "choices": [
                  "count > 0 is checked",
                  "The body runs once",
                  "count changes"
                ],
                "accept": [
                  "count > 0 is checked"
                ],
                "miss": "**A `while` loop checks its condition before every iteration, the first one included.** Only if the check is `true` does the body run.\n",
                "why": "`count > 0` is checked first. It is `true`, so the first iteration runs.\n"
              },
              {
                "id": "p3",
                "code": "let n = 1;\nwhile (n < 20) {\n  n = n * 3;\n}\nn\n",
                "accept": [
                  "27"
                ],
                "miss": "**The check happens before each iteration, not in the middle of one.** When `n` is 9, `9 < 20` is `true`, so the iteration runs and `n` becomes 27; then `27 < 20` is `false`.\n",
                "why": "`27`. 1, 3, 9, 27.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "let count = 0;\nlet x = 10;\nwhile (x < 5) {\n  count++;\n  x++;\n}\ncount\n",
                "accept": [
                  "0"
                ],
                "why": "`0`. `10 < 5` is `false` at the first check, so the body never runs.\n"
              },
              {
                "id": "p5",
                "code": "let s = \"\";\nlet n = 3;\nwhile (n > 0) {\n  s += n;\n  n--;\n}\ns\n",
                "accept": [
                  "\"321\"",
                  "321",
                  "'321'"
                ],
                "miss": "**`s` is text, so `+=` joins.** `\"\" + 3` is `\"3\"`, then `\"32\"`, then `\"321\"`; with `n` at 0 the check is `false`.\n",
                "why": "`\"321\"`.\n"
              },
              {
                "id": "p6",
                "code": "let x = 40;\nwhile (x >= 5) {\n  x = x / 2;\n}\nx\n",
                "accept": [
                  "2.5"
                ],
                "miss": "**`>=` lets 5 in.** `x` is 40, 20, 10, 5; `5 >= 5` is `true`, so the iteration runs once more and halves it.\n",
                "why": "`2.5`. Then `2.5 >= 5` is `false`.\n"
              },
              {
                "id": "p7",
                "code": "let k = 2;\nlet iterations = 0;\nwhile (k > 5); {\n  iterations++;\n}\niterations\n",
                "accept": [
                  "1"
                ],
                "miss": "**Look at what follows the condition's `)`: a `;`.** That empty statement is the loop's whole body, and `2 > 5` is `false`, so the empty loop ends at once. The block in braces is not part of the loop: it runs once, after it.\n",
                "why": "`1`. With `k` at 9, the same code would never finish.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 9 for the loop that multiplies by 3",
                "say": "The loop does not stop the moment `n` goes past 20. It checks only at the top. What is `n` at the check after the one where it is 9?\n"
              },
              {
                "when": "you answered 0 for the loop with a ; after its condition",
                "say": "Is the block in braces part of the loop? What is the loop's body when a `;` comes straight after the condition?\n"
              },
              {
                "when": "you chose \"The body runs once\"",
                "say": "That is another kind of loop, which comes in *The do…while loop*. When does a `while` loop look at its condition?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict four calls, then run it",
            "prompt": "`firstOver` is meant to return the first power of 2 that is bigger than `limit`: 1, 2, 4, 8, 16, and so on. `countdown` counts down by threes. Predict each line the program prints — type the number or the text, without quotes — then run it and compare.\n",
            "filename": "powers.js",
            "code": "function firstOver(limit) {\n  let n = 1;\n  while (n <= limit) {\n    n = n * 2;\n  }\n  return n;\n}\nfunction countdown(from) {\n  let text = \"\";\n  let n = from;\n  while (n > 0) {\n    text += n;\n    n = n - 3;\n  }\n  return text;\n}\nconsole.log(firstOver(10));\nconsole.log(firstOver(16));\nconsole.log(firstOver(0));\nconsole.log(countdown(7));\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(firstOver(10));",
                "accept": [
                  "16"
                ],
                "miss": "**Trace `n`:** 1, 2, 4 and 8 each make `n <= 10` true; the iteration with `n` at 8 doubles it to 16, and `16 <= 10` is `false`.\n",
                "why": "`16`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(firstOver(16));",
                "accept": [
                  "32"
                ],
                "miss": "**If you predicted `16` — `<=` lets 16 in.** `16 <= 16` is `true`, so one more iteration doubles it. That is what the sentence asks for: the first power *bigger* than 16.\n",
                "why": "`32`.\n"
              },
              {
                "id": "r3",
                "code": "console.log(firstOver(0));",
                "accept": [
                  "1"
                ],
                "miss": "**`1 <= 0` is `false` at the first check**, so there are no iterations, and `n` is returned as it started.\n",
                "why": "`1`.\n"
              },
              {
                "id": "r4",
                "code": "console.log(countdown(7));",
                "accept": [
                  "741",
                  "\"741\""
                ],
                "miss": "**`text` is text, so each number is joined on, with nothing between.** `n` is 7, 4, 1; then -2, and `-2 > 0` is `false`.\n",
                "why": "`741`.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 16 for firstOver(16)",
                "say": "Is `16 <= 16` true or false? If it is true, the body runs once more.\n"
              },
              {
                "when": "you predicted 7410 or 74 for countdown(7)",
                "say": "Write out the values of `n`: 7, then 7 − 3, then that − 3. Which of them make `n > 0` true?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One iteration at a time",
            "prompt": "The first box shows `digitSum`, which adds up the digits of a whole number. Trace a call to `digitSum(472)` one iteration at a time, and type each value as a number.\n",
            "items": [
              {
                "id": "t1",
                "code": "function digitSum(n) {\n  let total = 0;\n  let rest = n;\n  while (rest > 0) {\n    total += rest % 10;\n    rest = Math.floor(rest / 10);\n  }\n  return total;\n}\n// digitSum(472): what is total after the first iteration?\n",
                "accept": [
                  "2"
                ],
                "miss": "**`472 % 10` is what is left over after dividing by 10:** the last digit, 2.\n",
                "why": "`2`.\n"
              },
              {
                "id": "t2",
                "question": "After the first iteration of `digitSum(472)`, what does `rest` hold? Type the number.",
                "accept": [
                  "47"
                ],
                "miss": "**`472 / 10` is `47.2`, and `Math.floor` takes it down to `47`.** The last digit has been dropped.\n",
                "why": "`47`.\n"
              },
              {
                "id": "t3",
                "question": "How many iterations does `digitSum(472)` run? Type the number.",
                "accept": [
                  "3",
                  "three"
                ],
                "miss": "**`rest` is 472, 47, 4, then 0.** The check with 0 is `false`, so there are three iterations, one per digit.\n",
                "why": "`3`.\n"
              },
              {
                "id": "t4",
                "question": "What does `digitSum(472)` return? Type the number.",
                "accept": [
                  "13"
                ],
                "why": "`13`. `2 + 7 + 4`.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "question": "What does `digitSum(5)` return? Type the number.",
                "accept": [
                  "5"
                ],
                "why": "`5`. One iteration: `total` becomes 5 and `rest` becomes 0.\n"
              },
              {
                "id": "t6",
                "question": "How many iterations does `digitSum(30)` run? Type the number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "`2`. `rest` is 30, then 3, then 0. The first iteration adds 0, the last digit of 30.\n"
              }
            ],
            "hints": [
              {
                "when": "you gave 4 for the total after the first iteration",
                "say": "`%` gives what is left over after dividing. What is left over when 472 is divided by 10?\n"
              },
              {
                "when": "you counted 4 iterations",
                "say": "Count only the checks that are `true`. Is `0 > 0` true?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "The first square bigger than a number",
            "prompt": "\"`firstSquareOver` takes a whole number `limit` and returns the first square number (1, 4, 9, 16, …) that is bigger than `limit`: `firstSquareOver(10)` is 16, and `firstSquareOver(9)` is 16 too.\" Put the lines in order. Two lines are mistakes — leave them out.\n",
            "lines": [
              "function firstSquareOver(limit) {",
              "  let n = 1;",
              "  while (n * n <= limit) {",
              "    n++;",
              "  }",
              "  return n * n;",
              "}"
            ],
            "distractors": [
              {
                "line": "  while (n * n < limit) {",
                "why": "When `limit` is itself a square, this stops on it: with `limit` at 9, `3 * 3 < 9` is `false`, so it returns 9, which is not bigger than 9.\n"
              },
              {
                "line": "    n + 1;",
                "why": "It works out `n + 1` and throws it away. `n` never changes, so the loop never ends.\n"
              }
            ],
            "why": "Start `n` at 1 before the loop, keep going while `n * n` is not yet bigger than `limit`, move `n` on by one each iteration, and return the square after the loop's closing brace. When the loop ends, `n * n <= limit` is `false`, so `n * n` is bigger than `limit`.\n",
            "hints": [
              {
                "when": "you used the n + 1 line",
                "say": "Trace two checks. What is `n` at the first check, and what is it at the second?\n"
              },
              {
                "when": "you used the n * n < limit line",
                "say": "Try `firstSquareOver(9)`. When `n` is 3, is `3 * 3 < 9` true? What does the function return then?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Strip the zeros off the front",
            "prompt": "\"`stripZeros` takes a code made of digits, as text, and removes the zeros from its start: `stripZeros(\"0042\")` is `\"42\"`. A code of only zeros becomes the empty string, `\"\"`.\" Everything but the loop's condition is written. Replace `false` with the condition, where the TODO is. Then, in one sentence: why does your loop stop for `\"000\"`?\n",
            "exportName": "stripZeros",
            "filename": "zeros.js",
            "starter": "function stripZeros(code) {\n  let rest = code;\n  // say when to stop: until rest no longer starts with \"0\"\n  // TODO: replace false with the condition\n  while (false) {\n    // drop the first character\n    rest = rest.slice(1);\n  }\n  return rest;\n}\n",
            "tests": [
              {
                "label": "stripZeros(\"0042\") → \"42\"",
                "body": "return subject('0042') === '42';"
              },
              {
                "label": "stripZeros(\"700\") → \"700\"",
                "body": "return subject('700') === '700';"
              },
              {
                "label": "stripZeros(\"000\") → \"\"",
                "body": "return subject('000') === '';"
              },
              {
                "label": "stripZeros(\"05\") → \"5\"",
                "body": "return subject('05') === '5';"
              }
            ],
            "hints": [
              {
                "when": "\"0042\" comes back unchanged",
                "say": "Your loop runs no iterations. Work out your condition by hand with `rest` holding `\"0042\"`: is it `true`? A `while` condition says when to keep going, not when to stop.\n"
              },
              {
                "when": "you compared rest[0] with 0",
                "say": "`rest[0]` is a character, which is text. Is the text `\"0\"` `===` the number `0`?\n"
              },
              {
                "when": "\"700\" comes back as \"00\"",
                "say": "Your loop ran while the code did **not** start with `\"0\"`: the *until*, copied in. Keep going only while it does.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Days to finish a book",
            "prompt": "\"`daysToRead` takes a number of pages and how many pages a reader gets through in a day, and returns how many days it takes to read them all. A part-day still counts as a day: `daysToRead(100, 30)` is 4 (30, 30, 30, then the last 10).\" Use a `while` loop, with the five subgoals: start the result, say when to stop, do one iteration's work, change something each iteration, send the result out. Write it between the braces — *Writing a function's answer* shows how. Then, in one sentence: what does your loop's condition say about the pages left once it ends?\n",
            "exportName": "daysToRead",
            "filename": "reading.js",
            "starter": "function daysToRead(pages, perDay) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "daysToRead(100, 30) → 4",
                "body": "return subject(100, 30) === 4;"
              },
              {
                "label": "daysToRead(90, 30) → 3",
                "body": "return subject(90, 30) === 3;"
              },
              {
                "label": "daysToRead(10, 25) → 1",
                "body": "return subject(10, 25) === 1;"
              },
              {
                "label": "daysToRead(0, 20) → 0",
                "body": "return subject(0, 20) === 0;"
              }
            ],
            "hints": [
              {
                "when": "the editor says Timed out",
                "say": "Your loop never ends. Trace `daysToRead(100, 30)`: the pages left go 100, 70, 40, 10, -20. Does your condition ever become `false` for those values? A condition with `!==` waits for one exact value.\n"
              },
              {
                "when": "daysToRead(90, 30) gives 4 and daysToRead(0, 20) gives 1",
                "say": "Trace `daysToRead(90, 30)`: 90, 60, 30, then 0 pages left. Is the book finished when 0 pages are left? Check whether your condition lets 0 in.\n"
              },
              {
                "when": "daysToRead(100, 30) gives 3",
                "say": "Your loop stops while there are still pages left. Keep going while **any** pages are left, not while a whole day's worth is.\n"
              },
              {
                "when": "every answer is 1, and daysToRead(0, 20) gives undefined",
                "say": "Your `return` is inside the loop, so the function ends after the first iteration. Move it below the loop's closing brace.\n"
              },
              {
                "when": "it gave undefined",
                "say": "Put `return` and the name of your count after the loop's closing brace, so the count is sent out.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a loop that counts digits",
            "prompt": "A code assistant wrote `countDigits`, which should return how many digits a whole number of 1 or more has: 4096 has 4. Read it, then answer each question.\n",
            "filename": "digits.js",
            "code": "function countDigits(n) {\n  let count = 0;\n  while (n > 0) {\n    count++;\n  }\n  return count;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `countDigits(4096)`. What happens?",
                "choices": [
                  "It returns 4",
                  "It returns 0",
                  "It never finishes"
                ],
                "accept": [
                  "It never finishes"
                ],
                "miss": "**Nothing in the loop's body changes `n`.** `4096 > 0` is `true` at every check.\n",
                "why": "It never finishes. In the practice editor it stops with `Timed out` and the advice to check for a loop that never ends.\n"
              },
              {
                "id": "c2",
                "question": "Which line, added to the body of the `while` on line 3, fixes `countDigits`?",
                "choices": [
                  "n = Math.floor(n / 10);",
                  "Math.floor(n / 10);",
                  "n--;"
                ],
                "accept": [
                  "n = Math.floor(n / 10);"
                ],
                "miss": "**`Math.floor(n / 10);` works out a value and throws it away,** so `n` still never changes. **`n--` does finish — after 4096 iterations,** returning 4096. Each iteration has to drop one digit.\n",
                "why": "`n = Math.floor(n / 10);`. `n` is 4096, 409, 40, 4, then 0: four iterations.\n"
              },
              {
                "id": "c3",
                "question": "With that fix, a test calls `countDigits(0)`. Type the number it returns.",
                "accept": [
                  "0"
                ],
                "miss": "**Check the condition before the first iteration.** `0 > 0` is `false`, so the body never runs and `count` stays 0.\n",
                "why": "`0`, though 0 is written with one digit. That is why the description says *a whole number of 1 or more*. *The do…while loop* comes back to this function.\n"
              }
            ],
            "hints": [
              {
                "when": "you said countDigits(4096) returns 4",
                "say": "Trace two checks. What is `n` at the first check, and what is it at the second?\n"
              },
              {
                "when": "you chose n--",
                "say": "Count the iterations `n--` would take to bring 4096 down to 0. Is that the number of digits?\n"
              }
            ]
          }
        ]
      }
    },
    "for-loop": {
      "id": "for-loop",
      "title": "The for loop",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.for` (concepts.md): write a `for` loop for a known number of iterations, counting up, down or by a step; say from its header how many iterations it runs, and spot an off-by-one. D-066 split `flow.repetition` into `flow.while`, `flow.for` and `flow.do-while`, one lesson each; this lesson keeps the `for` material of *Repeating work: while and for* (the header, the order its parts run in, counting iterations, the off-by-one, the sentence-to-loop examples) and adds what that page only mentioned: counting down, stepping, and a `for` over a string's positions, which *The for…of loop* then shortens. It follows *The while loop*, which teaches building up a result, and recalls it: the `for` loop is introduced as that lesson's `sumTo`, rewritten. Pass 1 of loops, over numbers and strings; walking arrays is the Arrays module's (D-045). Procedural and explanation-first (D-061 item 1; 01-explanations rule 8). It models the writing direction (01-explanations rule 5a), because the `flow-loops` homework asks for a `for` with a step. The off-by-one is this form's characteristic mistake (D-066), so it runs through every step and the practice. When a `for` reads better than a `while` is one paragraph in the first step; Nick cut the step that compared them (2026-10-18), since choosing among all the forms is *Choosing a loop*'s. The new examples and the order of the steps are derived from those rules, pending Nick's review.",
      "concepts": [
        "flow.for"
      ],
      "lede": "A `for` loop gathers the three parts that control a loop into one line: the initialization, the condition and the update. This lesson covers the order those parts run in, how to read the number of iterations off that line, counting down and counting by steps, and the mistake `for` loops are best known for: one iteration too many, or one too few.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The while loop · Working with strings · Writing a function's answer · The for…of loop · Choosing a loop · Loops inside loops"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Header** — the first line of a `for` loop, in its parentheses. **Initialization** — the header's first part, which runs once, before anything else. **Condition** — the second part, checked before every iteration. **Update** — the third part, run after every iteration; MDN calls it the *afterthought*. **Loop variable** — the variable that holds the loop's current value on each iteration: `i` in a `for` loop, `ch` in `for (const ch of word)`. In a `for` loop the initialization makes it and the update changes it. When it counts, as most of the ones in this lesson do, it is also called a **counter**. **Off-by-one** — a loop that runs one iteration too many or one too few.",
          "entries": [
            "**Header** — the first line of a `for` loop, in its parentheses.",
            "**Initialization** — the header's first part, which runs once, before anything else.",
            "**Condition** — the second part, checked before every iteration.",
            "**Update** — the third part, run after every iteration; MDN calls it the *afterthought*.",
            "**Loop variable** — the variable that holds the loop's current value on each iteration: `i` in a `for` loop, `ch` in `for (const ch of word)`. In a `for` loop the initialization makes it and the update changes it. When it counts, as most of the ones in this lesson do, it is also called a **counter**.",
            "**Off-by-one** — a loop that runs one iteration too many or one too few."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory: the `while` loop\nfrom *The while loop*, `<` against `<=`, and reading a string's last character\nfrom *Working with strings*. Type the value, with quotes if it is text, or\npress the answer. Press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let total = 0;\nlet i = 1;\nwhile (i <= 4) {\n  total += i;\n  i++;\n}\ntotal\n",
                    "accept": [
                      "10"
                    ],
                    "miss": "**Trace it one check at a time.** `i` is 1, 2, 3, 4 at the checks that are `true`, and `total` becomes 1, 3, 6, 10. With `i` at 5, `5 <= 4` is `false`.\n",
                    "why": "`10`.\n"
                  },
                  {
                    "question": "A `while` loop is reached, and its condition is already `false`. How many iterations does it run? Type the number.",
                    "accept": [
                      "0",
                      "zero",
                      "none"
                    ],
                    "why": "`0`. The condition is checked before every iteration, the first one included.\n"
                  },
                  {
                    "code": "6 < 6",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**`<` does not include the number itself.** 6 is not less than 6; `6 <= 6` would be `true`.\n",
                    "why": "`false`.\n"
                  },
                  {
                    "code": "const city = \"Paris\";\ncity[city.length - 1]\n",
                    "accept": [
                      "\"s\"",
                      "s",
                      "'s'"
                    ],
                    "miss": "**Positions start at 0**, so `\"Paris\"`, 5 characters long, has positions 0 to 4. `city.length - 1` is 4, the last one.\n",
                    "why": "`\"s\"`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what each part of a `for` loop's header does, and the order the parts run in",
                "I can say from the header how many iterations a `for` loop runs, counting up, down or by a step",
                "I can write a `for` loop inside a function that builds up a result and returns it",
                "I can find an off-by-one by working a failing test's input through by hand"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Three parts in one line",
          "body": [
            {
              "p": "Here is `sumTo` from *The while loop*. It takes a whole number `n` and returns\n1 + 2 + … + n:"
            },
            {
              "code": "function sumTo(n) {\n  let total = 0;\n  let i = 1;          // initialization\n  while (i <= n) {    // condition\n    total += i;\n    i++;              // update\n  }\n  return total;\n}",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "p": "Look at the three lines that control the loop. All three are about one\nvariable, `i`, the **loop variable**:"
            },
            {
              "list": [
                "the **initialization**, `let i = 1`, before the loop, gives it its first value;",
                "the **condition**, `i <= n`, at the top, decides whether another iteration runs;",
                "the **update**, `i++`, at the bottom of the body, changes it each iteration."
              ]
            },
            {
              "p": "They are three lines, in three places."
            },
            {
              "p": "A **`for` loop** (officially, the `for` statement) gathers those three parts\ninto one line, separated by semicolons:"
            },
            {
              "code": "function sumTo(n) {\n  let total = 0;\n  for (let i = 1; i <= n; i++) {   // initialization; condition; update\n    total += i;\n  }\n  return total;\n}",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "p": "The line `for (let i = 1; i <= n; i++)` is the loop's **header**. This `for`\nversion of `sumTo` does exactly what the `while` version does. The header's\nthree parts run in this order:"
            },
            {
              "list": [
                "**Initialization** — `let i = 1` runs once, before anything else.",
                "**Condition** — `i <= n` is checked before every iteration. If it is `false`, the loop ends.",
                "**Body** — `total += i` runs.",
                "**Update** — `i++` runs after the body; then back to the condition."
              ],
              "ordered": true
            },
            {
              "p": "The trace of `sumTo(4)` is the same as for the `while` version: `i` is 1, 2, 3,\n4, and the check with `i` at 5 ends the loop. The `i` made by `let i = 1`\nbelongs to the loop: `i` does not exist after the loop's closing brace (the\nScope module explains why)."
            },
            {
              "p": "Nothing a `for` loop does is out of reach of a `while`, and the reverse is true\ntoo. What the `for` loop buys is that everything that controls the loop sits in\none line, where you can read it at a glance, and the update cannot get lost at\nthe bottom of a long body. That suits a loop whose count is known before it\nstarts. When a loop stops on a value it works out as it goes, a `while` loop\nusually reads better. *Choosing a loop* compares the forms."
            },
            {
              "p": "**A second example.** \"`dashes` takes a width and returns a line of that many\ndashes: `dashes(4)` is `\"----\"`.\""
            },
            {
              "code": "function dashes(width) {\n  let line = \"\";\n  for (let i = 0; i < width; i++) {\n    line += \"-\";\n  }\n  return line;\n}",
              "lang": "js",
              "file": "dashes.js"
            },
            {
              "p": "Here the counter starts at 0 and stops before `width`: with a width of 4, `i`\nis 0, 1, 2, 3, which is four iterations. The body never uses `i`; the counter is\nthere only to count. `dashes(0)` runs no iterations and returns `\"\"`."
            },
            {
              "p": "**A semicolon after the header.** The two semicolons *inside* the header\nseparate its three parts. A third one, typed after the closing `)` out of\nhabit, does something else. The next piece of code is wrong on purpose:"
            },
            {
              "code": "let count = 0;\nfor (let i = 0; i < 3; i++); {\n  count++;\n}",
              "lang": "js",
              "file": "count.js"
            },
            {
              "p": "`count` ends at 1, not 3. A semicolon on its own is an **empty statement**: a\nstatement that does nothing. JavaScript allows one anywhere a statement can go,\nand a loop's body is one statement, so the `;` right after the header becomes\nthe loop's whole body. The block in braces is no longer the loop's body: it is\na separate statement that comes after the loop."
            },
            {
              "p": "Here is the same code laid out the way JavaScript reads it. The line breaks\nchange nothing; JavaScript would read it this way however it was laid out. The\nsemicolon is what split the loop from the block. The layout only lets you see\nthe split:"
            },
            {
              "code": "let count = 0;\nfor (let i = 0; i < 3; i++)\n  ;          // the loop's body: an empty statement\n{            // a separate block, after the loop\n  count++;\n}",
              "lang": "js",
              "file": "count.js"
            },
            {
              "p": "Trace it, one row per check of the condition:"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`i`",
                  "`i < 3`",
                  "What happens"
                ],
                "rows": [
                  [
                    "1",
                    "`0`",
                    "`true`",
                    "the body does nothing; the update makes `i` 1"
                  ],
                  [
                    "2",
                    "`1`",
                    "`true`",
                    "the body does nothing; `i` becomes 2"
                  ],
                  [
                    "3",
                    "`2`",
                    "`true`",
                    "the body does nothing; `i` becomes 3"
                  ],
                  [
                    "4",
                    "`3`",
                    "`false`",
                    "the loop ends"
                  ]
                ]
              }
            },
            {
              "p": "Then the block runs, once, and `count` becomes 1."
            },
            {
              "p": "**Why this loop still ends.** The update, `i++`, is in the header, not in the\nbody. The semicolon emptied the body, but the update still runs after every\niteration, so `i` still reaches 3 and the condition still becomes `false`. A\n`while` loop has no update of its own: the line that changes its loop variable\nis inside the body, so the same semicolon cuts that line off. *The while loop*\nshowed it:"
            },
            {
              "code": "let n = 3;\nwhile (n > 0); {\n  n--;\n}",
              "lang": "js",
              "file": "countdown.js"
            },
            {
              "p": "There, the empty statement is the whole body, `n--` sits in the separate block\nafter the loop, nothing in the loop changes `n`, and the loop never ends."
            },
            {
              "p": "So the same slip does different damage. The `for` loop still runs every\niteration: it checks the condition, runs the empty body and runs the update,\nthree times. What goes wrong is the block you meant as its body: it is no\nlonger in the loop, so it runs once, after the loop has finished. The `while`\nloop never finishes at all."
            },
            {
              "p": "If that block uses the loop variable, it fails in a way that points somewhere else:"
            },
            {
              "code": "let total = 0;\nfor (let i = 1; i <= 3; i++); {\n  total += i;\n}",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "code": "ReferenceError: i is not defined",
              "lang": "text"
            },
            {
              "p": "The `i` made in the header belongs to the loop, and the block is outside the\nloop, so there is no `i` there. The message names `i`, not the semicolon, and\nwithout `i` in the block there is no message at all, only a wrong count. That\nis why this slip is hard to find by its symptoms. Look at the code instead: a\n`;` right after the `)` that closes a `for` header ends the loop before its\nbody. (*The do…while loop* shows the one loop whose `while (…)` does end in\n`;`, because there the condition comes last.)"
            },
            {
              "p": "Type the value each program ends with, a number as it is, text with its quotes,\nor press the answer."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let s = \"\";\nfor (let i = 0; i < 3; i++) {\n  s += i;\n}\ns\n",
                    "accept": [
                      "\"012\"",
                      "012",
                      "'012'"
                    ],
                    "miss": "**`s` is text, so `+=` joins rather than adds.** `\"\" + 0` is `\"0\"`, then `\"01\"`, then `\"012\"`.\n",
                    "why": "`\"012\"`.\n"
                  },
                  {
                    "question": "In `for (let i = 1; i <= 8; i++)`, which part runs only once?",
                    "choices": [
                      "let i = 1",
                      "i <= 8",
                      "i++"
                    ],
                    "accept": [
                      "let i = 1"
                    ],
                    "why": "`let i = 1`, the initialization, before the first check. The condition runs before every iteration, the update after every iteration.\n"
                  },
                  {
                    "question": "In a `for` loop, which runs straight after the body, on every iteration?",
                    "choices": [
                      "The initialization",
                      "The condition",
                      "The update"
                    ],
                    "accept": [
                      "The update"
                    ],
                    "miss": "**The update is written last in the header, and runs after the body.** Then the condition is checked, to decide whether there is another iteration.\n",
                    "why": "The update, `i++` here. Then the condition is checked again.\n"
                  },
                  {
                    "question": "`let k = 0; while (k < 3) { … k++; }` — which `for` header does the same counting?",
                    "choices": [
                      "for (let k = 0; k < 3; k++)",
                      "for (k < 3; let k = 0; k++)",
                      "for (let k = 0; k++; k < 3)"
                    ],
                    "accept": [
                      "for (let k = 0; k < 3; k++)"
                    ],
                    "miss": "**The order is fixed: initialization, condition, update.** JavaScript reads the first part as the start, the second as the condition and the third as the update, whatever you write in them.\n",
                    "why": "`for (let k = 0; k < 3; k++)`.\n"
                  },
                  {
                    "code": "let total = 0;\nfor (let i = 1; i <= 4; i++); {\n  total += 10;\n}\ntotal\n",
                    "accept": [
                      "10"
                    ],
                    "miss": "**Look at what follows the header's `)`: a `;`.** That empty statement is the loop's whole body. The loop runs four iterations that do nothing; the block in braces runs once, after it.\n",
                    "why": "`10`, not `40`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Counting iterations from the header",
          "body": [
            {
              "p": "Most loops have a number of iterations they must run: `dashes(4)` needs four,\none for each dash. A loop that runs one iteration too many or one too few has\nan **off-by-one** error. It is the most common loop mistake there is. `<` and\n`<=` look alike, and so do 0 and 1, so one is easy to write for the other."
            },
            {
              "p": "You can check the count from the header, without running the loop, the same\nway you trace one: follow the loop variable from its first value to the value\nthat makes the condition `false`. Each `true` check is one iteration; the\n`false` one stops the loop and runs nothing. In `sumTo(4)`, `i` started at 1,\nthe condition was `true` for 1, 2, 3 and 4, and `false` at 5: four iterations."
            },
            {
              "p": "**Starting at 0.** The `for` loop you will write most often is this one:"
            },
            {
              "code": "for (let i = 0; i < n; i++)",
              "lang": "js"
            },
            {
              "p": "The body runs with `i` at 0, 1, 2 and so on up to `n - 1`, and the loop stops\nwhen `i` reaches `n`. That is `n` iterations, and the count is right there in\nthe condition. `dashes` used it: with a width of 4, the body ran with `i` at\n0, 1, 2 and 3, and the loop stopped at 4."
            },
            {
              "p": "**Starting somewhere else.** A loop variable starts at the first value the\nwork needs. Most often that is a position, and positions start at 0. Sometimes\nthe work needs a different run of values. `sumTo` adds 1 + 2 + … + `n`, so its\nloop variable starts at 1, and its condition, `i <= n`, lets `n` in. It still\nruns `n` iterations; the body just gets the values 1 to `n` instead of 0 to\n`n - 1`."
            },
            {
              "p": "You will also meet loops that start at 1 out of habit, often written by people\nwho learned a language whose positions start at 1, such as Lua or MATLAB. That\nis a style, not a mistake, as long as the condition matches the start."
            },
            {
              "p": "From 0 with `<`, or from 1 with `<=`: both run `n` iterations. Mix the two and\nthe loop is off by one:"
            },
            {
              "table": {
                "head": [
                  "Header",
                  "`i` in the body",
                  "Stops at",
                  "Iterations"
                ],
                "rows": [
                  [
                    "`for (let i = 0; i < n; i++)`",
                    "0 to `n - 1`",
                    "`n`",
                    "`n`"
                  ],
                  [
                    "`for (let i = 1; i <= n; i++)`",
                    "1 to `n`",
                    "`n + 1`",
                    "`n`"
                  ],
                  [
                    "`for (let i = 1; i < n; i++)`",
                    "1 to `n - 1`",
                    "`n`",
                    "`n - 1`, one short"
                  ],
                  [
                    "`for (let i = 0; i <= n; i++)`",
                    "0 to `n`",
                    "`n + 1`",
                    "`n + 1`, one over"
                  ]
                ]
              }
            },
            {
              "p": "For any other header, count the way you trace: follow the loop variable from\nits first value to the value that stops the loop."
            },
            {
              "p": "Two questions catch most off-by-one errors:"
            },
            {
              "list": [
                "**The first iteration:** is the loop variable's first value the one you want?",
                "**The last iteration:** which value stops the loop? The value just before it is the last one the body runs with. Is that the last one you want?"
              ]
            },
            {
              "p": "**Walking a string's positions.** A string of length `n` has positions 0 to\n`n - 1`: exactly the values `i` takes in the first header above, with\n`word.length` as `n`. \"`doubled` takes a word and returns it with every letter\nwritten twice: `doubled(\"abc\")` is `\"aabbcc\"`.\""
            },
            {
              "code": "function doubled(word) {\n  let result = \"\";\n  // positions 0 to word.length - 1\n  for (let i = 0; i < word.length; i++) {\n    result += word[i] + word[i];\n  }\n  return result;\n}",
              "lang": "js",
              "file": "doubled.js"
            },
            {
              "p": "For `\"abc\"`, the body runs with `i` at 0, 1 and 2, and `word[i]` is `\"a\"`,\n`\"b\"`, `\"c\"`; the loop stops at 3, the length. Three iterations, one per\ncharacter. The next lesson, *The for…of loop*, shows a shorter\nloop for when you need each character and not its position. The Arrays module\nwalks a list the same way, position by position."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "How many iterations does `for (let i = 1; i < 10; i++)` run? Type the number.",
                    "accept": [
                      "9",
                      "nine"
                    ],
                    "miss": "**Find where it stops.** `i < 10` stops the loop at 10, so the body runs with `i` at 1 to 9.\n",
                    "why": "`9`.\n"
                  },
                  {
                    "question": "How many iterations does `for (let i = 0; i <= 3; i++)` run? Type the number.",
                    "accept": [
                      "4",
                      "four"
                    ],
                    "miss": "**Count 0 as well.** `i` is 0, 1, 2, 3.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "question": "A loop should run exactly `n` iterations. Which header does that?",
                    "choices": [
                      "for (let i = 0; i <= n; i++)",
                      "for (let i = 0; i < n; i++)",
                      "for (let i = 1; i < n; i++)"
                    ],
                    "accept": [
                      "for (let i = 0; i < n; i++)"
                    ],
                    "miss": "**Try it with `n` at 3 and find where each loop stops.** Starting at 0 with `i <= n`, the body runs with `i` at 0 to 3, four values. Starting at 1 with `i < n`, it runs with 1 and 2, two values.\n",
                    "why": "The body runs with `i` at 0 to `n - 1`, which is `n` values, and the loop stops at `n`. `for (let i = 1; i <= n; i++)` would do it too.\n"
                  },
                  {
                    "question": "Which header visits every position of `word`, and no more?",
                    "choices": [
                      "for (let i = 0; i <= word.length; i++)",
                      "for (let i = 0; i < word.length; i++)",
                      "for (let i = 1; i <= word.length; i++)"
                    ],
                    "accept": [
                      "for (let i = 0; i < word.length; i++)"
                    ],
                    "miss": "**Ask the two questions.** The first position is 0, so starting at 1 skips the first letter. The last position is `word.length - 1`, so `<=` lets in one position past the end, where `word[i]` is `undefined`.\n",
                    "why": "`for (let i = 0; i < word.length; i++)`: positions 0 to `word.length - 1`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Counting down, and counting by steps",
          "body": [
            {
              "p": "The update does not have to be `i++`. It can count down, with `i--` (the\n*decrement operator*), or move by more than one, with a compound assignment\nsuch as `i += 2` or `i -= 5`. Count the iterations the same way: follow the\nloop variable to the value that stops the loop."
            },
            {
              "table": {
                "head": [
                  "Header",
                  "`i` in the body",
                  "Stops at",
                  "Iterations"
                ],
                "rows": [
                  [
                    "`for (let i = 3; i >= 1; i--)`",
                    "3 2 1",
                    "0",
                    "3"
                  ],
                  [
                    "`for (let i = 20; i > 0; i -= 5)`",
                    "20 15 10 5",
                    "0",
                    "4"
                  ],
                  [
                    "`for (let i = 0; i <= 10; i += 3)`",
                    "0 3 6 9",
                    "12",
                    "4"
                  ]
                ]
              }
            },
            {
              "p": "In the last row the loop variable never equals 10: it steps from 9 to 12, and\n`12 <= 10` is `false`. A step can jump over the limit, and `<=` still stops the\nloop, because it asks whether `i` has gone past, not whether it is exactly there."
            },
            {
              "p": "A loop that counts down has the same two off-by-one questions, with the ends\nswapped: the first iteration is at the top, and the last at the bottom."
            },
            {
              "p": "**Example.** \"`backwards` takes a word and returns it written backwards:\n`backwards(\"loop\")` is `\"pool\"`.\" The positions have to be visited from the\nlast to the first. Here is a first attempt. It has a mistake, shown on purpose:\nit starts the counter at the word's length."
            },
            {
              "code": "function backwards(word) {\n  let result = \"\";\n  for (let i = word.length; i >= 0; i--) {\n    result += word[i];\n  }\n  return result;\n}",
              "lang": "js",
              "file": "backwards.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✗ backwards(\"abc\") → \"cba\"\n✗ backwards(\"a\") → \"a\"\n✗ backwards(\"\") → \"\"",
              "lang": "text"
            },
            {
              "p": "The first test that failed is `backwards(\"abc\")`. Trace it, with `word`\nholding `\"abc\"`, which is 3 characters long:"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`i`",
                  "`i >= 0`",
                  "`word[i]`",
                  "`result`"
                ],
                "rows": [
                  [
                    "1",
                    "`3`",
                    "`true`",
                    "`undefined`",
                    "`\"undefined\"`"
                  ],
                  [
                    "2",
                    "`2`",
                    "`true`",
                    "`\"c\"`",
                    "`\"undefinedc\"`"
                  ],
                  [
                    "3",
                    "`1`",
                    "`true`",
                    "`\"b\"`",
                    "`\"undefinedcb\"`"
                  ],
                  [
                    "4",
                    "`0`",
                    "`true`",
                    "`\"a\"`",
                    "`\"undefinedcba\"`"
                  ],
                  [
                    "5",
                    "`-1`",
                    "`false`",
                    "",
                    "`\"undefinedcba\"`"
                  ]
                ]
              }
            },
            {
              "p": "The first iteration is the problem. `\"abc\"` has positions 0, 1 and 2; position 3 is\npast the end, and reading past the end gives `undefined`. Joined onto a string,\n`undefined` becomes the text `\"undefined\"`. The last position is\n`word.length - 1`, so that is where the counter starts:"
            },
            {
              "code": "function backwards(word) {\n  let result = \"\";\n  // from the last position, word.length - 1, down to 0\n  for (let i = word.length - 1; i >= 0; i--) {\n    result += word[i];\n  }\n  return result;\n}",
              "lang": "js",
              "file": "backwards.js"
            },
            {
              "code": "✓ backwards(\"abc\") → \"cba\"\n✓ backwards(\"a\") → \"a\"\n✓ backwards(\"\") → \"\"",
              "lang": "text"
            },
            {
              "p": "Now ask the last-iteration question too. `i >= 0` lets in position 0, the first\nletter. Writing `i > 0` would stop one iteration early: `backwards(\"abc\")` would\nreturn `\"cb\"`."
            },
            {
              "p": "Type each answer as a number."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let total = 0;\nfor (let i = 10; i >= 8; i--) {\n  total += i;\n}\ntotal\n",
                    "accept": [
                      "27"
                    ],
                    "miss": "**This one counts down.** `i` is 10, 9, 8, and the check with `i` at 7 is `false`. `10 + 9 + 8`.\n",
                    "why": "`27`.\n"
                  },
                  {
                    "question": "How many iterations does `for (let i = 2; i <= 10; i += 2)` run? Type the number.",
                    "accept": [
                      "5",
                      "five"
                    ],
                    "why": "`5`: `i` is 2, 4, 6, 8, 10.\n"
                  },
                  {
                    "question": "How many iterations does `for (let i = 5; i >= 0; i--)` run? Type the number.",
                    "accept": [
                      "6",
                      "six"
                    ],
                    "miss": "**Count 0 as well.** `i` is 5, 4, 3, 2, 1, 0: `>=` lets 0 in.\n",
                    "why": "`6`.\n"
                  },
                  {
                    "question": "With the first attempt at `backwards`, what does `backwards(\"a\")` return? Type the text, without quotes.",
                    "accept": [
                      "undefineda",
                      "\"undefineda\""
                    ],
                    "miss": "**The counter starts at `word.length`, which is 1.** Position 1 is past the end of `\"a\"`, so the first iteration adds `undefined`; the second adds `\"a\"`.\n",
                    "why": "`undefineda`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "From a sentence to a for loop",
          "body": [
            {
              "p": "Exercises and homework give you a sentence and an empty function. For a `for`\nloop, the five subgoals from *The while loop* become four, because three of\nthem share the header:"
            },
            {
              "list": [
                "**Start the result.** What is it before the loop starts: 0 for a total, `\"\"` for text?",
                "**Write the header.** What are the first and last values the loop works on, and how does the loop variable move between them? Ask the first-iteration and last-iteration questions here.",
                "**Do one iteration's work.** What happens to the result on each iteration?",
                "**Send the result out.** `return` it after the loop."
              ],
              "ordered": true
            },
            {
              "p": "**Example 1.** \"`sumBetween` takes two whole numbers, `low` and `high`, and\nreturns `low` + … + `high`, both ends included.\""
            },
            {
              "p": "Here is a first attempt. It has a mistake, shown on purpose: the condition\nstops one iteration early."
            },
            {
              "code": "function sumBetween(low, high) {\n  let total = 0;\n  for (let i = low; i < high; i++) {\n    total += i;\n  }\n  return total;\n}",
              "lang": "js",
              "file": "between.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✗ sumBetween(3, 5) → 12\n✗ sumBetween(1, 1) → 1\n✓ sumBetween(5, 2) → 0\n✗ sumBetween(1, 10) → 55",
              "lang": "text"
            },
            {
              "p": "The first test that failed is `sumBetween(3, 5)`. Trace it, with `low` holding\n3 and `high` holding 5:"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`i`",
                  "`i < 5`",
                  "`total`"
                ],
                "rows": [
                  [
                    "1",
                    "`3`",
                    "`true`",
                    "`3`"
                  ],
                  [
                    "2",
                    "`4`",
                    "`true`",
                    "`7`"
                  ],
                  [
                    "3",
                    "`5`",
                    "`false`",
                    "`7`"
                  ]
                ]
              }
            },
            {
              "p": "`sumBetween(3, 5)` returns `7`, not `12`. The iteration for 5 is missing:\n`5 < 5` is `false`, so 5 is never added. The sentence says *both ends\nincluded*, so the condition has to let `high` in:"
            },
            {
              "code": "function sumBetween(low, high) {\n  // start the result\n  let total = 0;\n  // write the header: first value low, last value high, both included\n  for (let i = low; i <= high; i++) {\n    // do one iteration's work\n    total += i;\n  }\n  // send the result out\n  return total;\n}",
              "lang": "js",
              "file": "between.js"
            },
            {
              "p": "The test `sumBetween(5, 2)` passed with the wrong loop, and it passes with the\nfixed one too. With `low` at 5 and `high` at 2, the first check is `false`, so\nthere are no iterations and `total` stays 0. A passing test does not prove the loop\nis right; it says only that one input came out right."
            },
            {
              "p": "**Example 2.** \"`repeatWord` takes a word and a number, and returns the word\nthat many times in a row: `repeatWord(\"go\", 3)` is `\"gogogo\"`.\""
            },
            {
              "p": "Here is a first attempt. It has a different mistake, shown on purpose: it puts\n`return result;` inside the loop's braces, next to the line that does the work."
            },
            {
              "code": "function repeatWord(word, times) {\n  let result = \"\";\n  for (let i = 0; i < times; i++) {\n    result += word;\n    return result;\n  }\n}",
              "lang": "js",
              "file": "repeat.js"
            },
            {
              "code": "✗ repeatWord(\"go\", 3) → \"gogogo\"\n✓ repeatWord(\"ha\", 1) → \"ha\"\n✗ repeatWord(\"x\", 0) → \"\"",
              "lang": "text"
            },
            {
              "p": "Work `repeatWord(\"go\", 3)` through. The first check, `0 < 3`, is `true`;\n`result` becomes `\"go\"`; then `return` sends `\"go\"` out. `return` ends the\nwhole function, so there is never a second iteration. And `repeatWord(\"x\", 0)` runs\nno iterations, never reaches the `return` at all, and returns `undefined`. That is\nthe same failure as a missing `else` in *The if statement*: the function ends\nwithout reaching a `return`. Moving `return result;` below the loop's closing\nbrace fixes both failing tests."
            },
            {
              "code": "function repeatWord(word, times) {\n  // start the result\n  let result = \"\";\n  // write the header: 0 to times - 1, which is times iterations\n  for (let i = 0; i < times; i++) {\n    // do one iteration's work\n    result += word;\n  }\n  // send the result out\n  return result;\n}",
              "lang": "js",
              "file": "repeat.js"
            },
            {
              "p": "**Example 3: you choose the lines.** \"`factorial` takes a whole number `n` and\nreturns 1 × 2 × … × n: `factorial(4)` is 24.\""
            },
            {
              "code": "function factorial(n) {\n  // TODO (a): start the result\n  // TODO (b): write the header, 1 to n\n    result *= i;\n  }\n  return result;\n}",
              "lang": "js",
              "file": "factorial.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes at TODO (a)?",
                    "choices": [
                      "let result = 0;",
                      "let result = 1;",
                      "let result = n;"
                    ],
                    "accept": [
                      "let result = 1;"
                    ],
                    "miss": "**If you chose `0` — every iteration multiplies, and 0 times anything is 0.** The result would be 0 for every `n`. A total starts at 0 because adding 0 changes nothing; a product starts at 1 because multiplying by 1 changes nothing. **If you chose `n` — `n` gets multiplied in again on the last iteration,** so `factorial(4)` would be 96.\n",
                    "why": "`let result = 1;`. Then `result` is 1, 2, 6, 24 after each iteration.\n"
                  },
                  {
                    "question": "Which line goes at TODO (b)?",
                    "choices": [
                      "for (let i = 1; i < n; i++) {",
                      "for (let i = 1; i <= n; i++) {",
                      "for (let i = 0; i <= n; i++) {"
                    ],
                    "accept": [
                      "for (let i = 1; i <= n; i++) {"
                    ],
                    "miss": "**Ask the two off-by-one questions.** With `i < n`, the last iteration is missing and `n` is never multiplied in. Starting at 0, the first iteration multiplies by 0.\n",
                    "why": "`i` runs from 1 to `n`, both included.\n"
                  },
                  {
                    "question": "Before the fix, `sumBetween(3, 5)` returned 7. Which value never got added? Type the number.",
                    "accept": [
                      "5"
                    ],
                    "why": "`5`. `5 < 5` is `false`, so the iteration for 5 never ran.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "Two loops are meant to return 1 + 2 + … + 10:"
            },
            {
              "code": "let a = 0;\nfor (let i = 0; i < 10; i++) {\n  a += i;\n}\n\nlet b = 0;\nfor (let i = 1; i <= 10; i++) {\n  b += i;\n}",
              "lang": "js",
              "file": "ten.js"
            },
            {
              "p": "Choose the one that is right. For each, say how many iterations it runs and what\nvalue it ends with, and name the number the wrong loop leaves out. Then say what\nwould change if `b` started at 1 instead of 0, and why a loop that multiplies\nneeds a different starting value from one that adds."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**A `for` loop's header holds three parts:** the initialization runs once, the condition is checked before every iteration, and the update runs after every iteration. The loop does exactly what the same `while` loop does.",
                "**Count the iterations from the header:** follow the loop variable from its first value to the value that stops the loop, counting up, down or by a step. An off-by-one is a `<` that should be `<=`, or a start that should be 0, 1 or `length - 1`. Find it by tracing a failing test's input by hand.",
                "**A `for` loop reads best when the count is known before the loop starts.** When a loop stops on a value it works out as it goes, a `while` loop usually reads better. Either can be written as the other; the choice is about which one says what the loop is doing."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "for",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for",
              "note": "the three parts of the header, and the order they run in."
            },
            {
              "title": "Decrement (--)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Decrement",
              "note": "counting down by one."
            }
          ]
        }
      ],
      "_source": "lessons/for-loop.md",
      "practice": {
        "id": "for-loop",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "How many iterations, and in what order?",
            "prompt": "Type the value each one ends with, or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "let iterations = 0;\nfor (let i = 1; i < 5; i++) {\n  iterations++;\n}\niterations\n",
                "accept": [
                  "4"
                ],
                "miss": "**If you answered `5` — `i < 5` stops before 5.** `i` is 1, 2, 3, 4: four iterations.\n",
                "why": "`4`.\n"
              },
              {
                "id": "p2",
                "question": "A loop starts: `for (let i = 0; i < 3; i++)`. What happens first?",
                "choices": [
                  "i < 3 is checked",
                  "let i = 0 runs",
                  "The body runs",
                  "i++ runs"
                ],
                "accept": [
                  "let i = 0 runs"
                ],
                "miss": "**The initialization, `let i = 0`, runs once, before anything else.** Then the condition, then the body, then the update, and back to the condition.\n",
                "why": "`let i = 0` runs first, and only once.\n"
              },
              {
                "id": "p3",
                "code": "let s = \"\";\nfor (let i = 0; i < 3; i++) {\n  s += \"ab\";\n}\ns\n",
                "accept": [
                  "\"ababab\"",
                  "ababab",
                  "'ababab'"
                ],
                "why": "`\"ababab\"`. Three iterations, each adding `\"ab\"` to the end.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "let total = 0;\nfor (let i = 10; i > 0; i -= 3) {\n  total += i;\n}\ntotal\n",
                "accept": [
                  "22"
                ],
                "miss": "**Write out the values of `i`:** 10, 7, 4, 1. Then -2, and `-2 > 0` is `false`.\n",
                "why": "`22`. `10 + 7 + 4 + 1`.\n"
              },
              {
                "id": "p5",
                "code": "let count = 0;\nfor (let i = 0; i <= 6; i += 2) {\n  count++;\n}\ncount\n",
                "accept": [
                  "4"
                ],
                "miss": "**Count 0 as well, and let 6 in.** `i` is 0, 2, 4, 6.\n",
                "why": "`4`.\n"
              },
              {
                "id": "p6",
                "code": "let s = \"\";\nfor (let i = 3; i >= 1; i--) {\n  s += i;\n}\ns\n",
                "accept": [
                  "\"321\"",
                  "321",
                  "'321'"
                ],
                "why": "`\"321\"`. `i` is 3, 2, 1, and each is joined on as text.\n"
              },
              {
                "id": "p7",
                "code": "let n = 0;\nfor (let i = 0; i < 5; i++); {\n  n += 2;\n}\nn\n",
                "accept": [
                  "2"
                ],
                "miss": "**Look at what follows the header's `)`: a `;`.** That empty statement is the loop's whole body: five iterations that do nothing. The block in braces is not part of the loop, so it runs once, after it.\n",
                "why": "`2`, not `10`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 5 for the loop that counts iterations",
                "say": "Write out the values of `i` that get through the check `i < 5`. Does 5 itself get through?\n"
              },
              {
                "when": "you answered 10 for the loop with a ; after its header",
                "say": "Is the block in braces part of the loop? What is the loop's body when a `;` comes straight after the header's `)`?\n"
              },
              {
                "when": "you chose \"i < 3 is checked\"",
                "say": "The check needs `i` to exist already. Which part of the header makes `i`?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict four calls, then run it",
            "prompt": "`squares` is meant to return 1×1 + 2×2 + … + n×n. Predict each line it prints — type the number or the text, without quotes — then run it and compare.\n",
            "filename": "loops.js",
            "code": "function squares(n) {\n  let total = 0;\n  for (let i = 1; i < n; i++) {\n    total += i * i;\n  }\n  return total;\n}\nfunction everyOther(n) {\n  let s = \"\";\n  for (let i = 0; i < n; i += 2) {\n    s += i;\n  }\n  return s;\n}\nconsole.log(squares(3));\nconsole.log(squares(1));\nconsole.log(everyOther(7));\nconsole.log(everyOther(6));\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(squares(3));",
                "accept": [
                  "5"
                ],
                "miss": "**If you predicted `14` — 14 is what `squares(3)` should return.** Trace it: `i` is 1, then 2, and `3 < 3` is `false`. `1 + 4` is 5; the iteration for 3 never runs.\n",
                "why": "`5`. `squares` is off by one: its condition should be `i <= n`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(squares(1));",
                "accept": [
                  "0"
                ],
                "why": "`0`. `1 < 1` is `false` at the first check, so there are no iterations. The right answer is 1.\n"
              },
              {
                "id": "r3",
                "code": "console.log(everyOther(7));",
                "accept": [
                  "0246",
                  "\"0246\""
                ],
                "miss": "**`s` is text, so `+=` joins.** `i` is 0, 2, 4, 6, and the check with 8 is `false`.\n",
                "why": "`0246`.\n"
              },
              {
                "id": "r4",
                "code": "console.log(everyOther(6));",
                "accept": [
                  "024",
                  "\"024\""
                ],
                "miss": "**`6 < 6` is `false`,** so the iteration for 6 never runs and 6 is not added.\n",
                "why": "`024`.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 14 for squares(3)",
                "say": "Write out the values of `i` that get through `i < 3`. Which square is never added?\n"
              },
              {
                "when": "you predicted a number like 12 for everyOther",
                "say": "`s` starts as `\"\"`. What does `\"\" + 0` give: a number or text?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Every third letter",
            "prompt": "The first box shows `everyThird`, which takes every third character of a piece of text, starting with the first. Trace a call to `everyThird(\"abcdefgh\")` one iteration at a time.\n",
            "items": [
              {
                "id": "t1",
                "code": "function everyThird(text) {\n  let out = \"\";\n  for (let i = 0; i < text.length; i += 3) {\n    out += text[i];\n  }\n  return out;\n}\n// everyThird(\"abcdefgh\"): what is i on the\n// second iteration? Type the number.\n",
                "accept": [
                  "3"
                ],
                "miss": "**The update is `i += 3`.** The first iteration has `i` at 0; the update after it makes `i` 3.\n",
                "why": "`3`.\n"
              },
              {
                "id": "t2",
                "question": "How many iterations does `everyThird(\"abcdefgh\")` run? Type the number.",
                "accept": [
                  "3",
                  "three"
                ],
                "miss": "**`\"abcdefgh\"` is 8 characters long.** `i` is 0, 3, 6; then 9, and `9 < 8` is `false`.\n",
                "why": "`3`.\n"
              },
              {
                "id": "t3",
                "question": "What does `i` hold at the check that ends the loop? Type the number.",
                "accept": [
                  "9"
                ],
                "miss": "**The update runs after the last iteration too.** After the iteration with `i` at 6, `i += 3` makes it 9, and only then is the condition checked.\n",
                "why": "`9`. It steps over the length, 8, and `<` stops it.\n"
              },
              {
                "id": "t4",
                "question": "What does `everyThird(\"abcdefgh\")` return? Type the text, without quotes.",
                "accept": [
                  "adg",
                  "\"adg\""
                ],
                "why": "`\"adg\"`: the characters at positions 0, 3 and 6.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "question": "What does `everyThird(\"abc\")` return? Type the text, without quotes.",
                "accept": [
                  "a",
                  "\"a\""
                ],
                "why": "`\"a\"`. One iteration, with `i` at 0; then `3 < 3` is `false`.\n"
              },
              {
                "id": "t6",
                "question": "How many iterations does `everyThird(\"\")` run? Type the number.",
                "accept": [
                  "0",
                  "zero",
                  "none"
                ],
                "why": "`0`. The length is 0, and `0 < 0` is `false` at the first check.\n"
              }
            ],
            "hints": [
              {
                "when": "you said i is 1 on the second iteration",
                "say": "Look at the third part of the header. By how much does it change `i` after each iteration?\n"
              },
              {
                "when": "you counted 4 iterations",
                "say": "Write out the values of `i` and the length, 8. Does `9 < 8` let a fourth iteration in?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Add up the even numbers",
            "prompt": "\"`sumOfEvens` takes a whole number `n` and returns 2 + 4 + 6 + … up to `n`, with `n` included if it is even: `sumOfEvens(6)` is 12.\" Put the lines in order. Two lines are mistakes — leave them out.\n",
            "lines": [
              "function sumOfEvens(n) {",
              "  let total = 0;",
              "  for (let i = 2; i <= n; i += 2) {",
              "    total += i;",
              "  }",
              "  return total;",
              "}"
            ],
            "distractors": [
              {
                "line": "  for (let i = 2; i < n; i += 2) {",
                "why": "Off by one. When `n` is even, `n < n` is `false`, so the iteration for `n` itself is missing: `sumOfEvens(6)` would be 2 + 4, which is 6.\n"
              },
              {
                "line": "  for (let i = 2; i <= n; i++) {",
                "why": "`i++` steps by one, so the odd numbers are added too: `sumOfEvens(6)` would be 2 + 3 + 4 + 5 + 6.\n"
              }
            ],
            "why": "Start the total at 0 before the loop, step the counter by 2 from 2 up to `n` included, add each one, and return the total after the loop's closing brace.\n",
            "hints": [
              {
                "when": "you used the i < n line",
                "say": "Try `sumOfEvens(6)`. Write out the values of `i` your loop makes. Is 6 among them?\n"
              },
              {
                "when": "you put return total inside the loop",
                "say": "`return` ends the function the moment it runs. How many iterations would run before it?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Multiply, again and again",
            "prompt": "\"`power` takes a base and a number of times, and returns the base multiplied by itself that many times: `power(2, 3)` is 2 × 2 × 2, which is 8.\" Everything but one line is written. Write the line that does one iteration's work — the loop has to do the multiplying, so do not use `**`. The line goes inside the loop's braces, where the TODO is. Then, in one sentence: why does `result` start at 1 and not 0?\n",
            "exportName": "power",
            "filename": "power.js",
            "starter": "function power(base, times) {\n  let result = 1;\n  for (let i = 0; i < times; i++) {\n    // TODO: put your code here\n  }\n  return result;\n}\n",
            "tests": [
              {
                "label": "power(2, 3) → 8",
                "body": "return subject(2, 3) === 8;"
              },
              {
                "label": "power(5, 1) → 5",
                "body": "return subject(5, 1) === 5;"
              },
              {
                "label": "power(3, 4) → 81",
                "body": "return subject(3, 4) === 81;"
              },
              {
                "label": "power(7, 0) → 1",
                "body": "return subject(7, 0) === 1;"
              }
            ],
            "hints": [
              {
                "when": "power(2, 3) gives 7",
                "say": "You added `base` on each iteration. The sentence says *multiplied*.\n"
              },
              {
                "when": "power(2, 3) gives 4 or 0",
                "say": "Each iteration should multiply the result so far by `base`, and store it back in `result`. Does your line use `result` on both sides?\n"
              },
              {
                "when": "every test except power(7, 0) fails, giving 1",
                "say": "Your line works out a value but does not store it, so the value is thrown away. Assign it back to `result`.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Count down instead",
            "prompt": "\"`countDown` takes a whole number `n` from 1 to 9 and returns the digits from `n` down to 1, as text: `countDown(4)` is `\"4321\"`.\" Right now it counts up, so `countDown(4)` returns `\"1234\"`. Change only the loop's header, where the TODO is. Then, in one sentence: which of the header's three parts did you change, and what does each one now say?\n",
            "exportName": "countDown",
            "filename": "countdown.js",
            "starter": "function countDown(n) {\n  let digits = \"\";\n  // TODO: change this header so it counts down\n  for (let i = 1; i <= n; i++) {\n    digits += i;\n  }\n  return digits;\n}\n",
            "tests": [
              {
                "label": "countDown(4) → \"4321\"",
                "body": "return subject(4) === '4321';"
              },
              {
                "label": "countDown(1) → \"1\"",
                "body": "return subject(1) === '1';"
              },
              {
                "label": "countDown(9) → \"987654321\"",
                "body": "return subject(9) === '987654321';"
              },
              {
                "label": "countDown(2) → \"21\"",
                "body": "return subject(2) === '21';"
              }
            ],
            "hints": [
              {
                "when": "countDown(4) gives \"\"",
                "say": "Your loop runs no iterations. Work out your condition with `i` at its first value, 4: is it `true`? Counting down, the loop keeps going while `i` has not yet gone below the last digit you want.\n"
              },
              {
                "when": "countDown(4) gives \"432\" and countDown(1) gives \"\"",
                "say": "Ask the last-iteration question: does your condition let in 1, the last digit you want?\n"
              },
              {
                "when": "countDown(4) gives \"43210\"",
                "say": "Ask the last-iteration question: does your condition stop before 0?\n"
              },
              {
                "when": "countDown(4) gives \"321\"",
                "say": "Ask the first-iteration question: is your counter's first value `n`?\n"
              },
              {
                "when": "the editor says Timed out, or Invalid string length",
                "say": "Your loop runs away: the counter moves away from the point where the condition is `false`, and the text grows until JavaScript refuses to make it longer. Check that the update and the condition agree on the direction: counting down needs `i--`, and a condition that is `true` while `i` is still at or above 1.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Plus, minus, plus",
            "prompt": "\"`signs` takes a whole number `n` and returns a string of `n` characters that alternate `+` and `-`, starting with `+`: `signs(5)` is `\"+-+-+\"`.\" Use a `for` loop and the subgoals: start the result; write the header; do one iteration's work; send the result out. Write it between the braces — *Writing a function's answer* shows how. Then, in one sentence: which test would catch a loop that runs one iteration too many?\n",
            "exportName": "signs",
            "filename": "signs.js",
            "starter": "function signs(n) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "signs(5) → \"+-+-+\"",
                "body": "return subject(5) === '+-+-+';"
              },
              {
                "label": "signs(1) → \"+\"",
                "body": "return subject(1) === '+';"
              },
              {
                "label": "signs(4) → \"+-+-\"",
                "body": "return subject(4) === '+-+-';"
              },
              {
                "label": "signs(0) → \"\"",
                "body": "return subject(0) === '';"
              }
            ],
            "hints": [
              {
                "when": "signs(5) gives \"+\" but signs(1) passes",
                "say": "Your `return` is inside the loop, so the function ends after the first iteration. Move it below the loop's closing brace.\n"
              },
              {
                "when": "signs(0) gives \"+\" and the others pass",
                "say": "Your result starts as `\"+\"` before the loop, so even a loop that runs no iterations returns a `+`. Start it empty, and let the loop add every character.\n"
              },
              {
                "when": "every string has one character too many",
                "say": "Count the values your counter takes. Starting at 0, which condition runs exactly `n` iterations?\n"
              },
              {
                "when": "signs(5) gives \"-+-+-\"",
                "say": "The first character must be `+`. On the first iteration, what is your counter, and what does your `if` or ternary decide for that value?\n"
              },
              {
                "when": "the result starts with \"undefined\"",
                "say": "A string you build with `+=` has to start as `\"\"`.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a loop that counts multiples",
            "prompt": "A code assistant wrote `countThrees`, which should return how many multiples of 3 there are from 1 to `n`: from 1 to 10 there are three, 3, 6 and 9. Read it, answer each question, then fix it in the editor.\n",
            "filename": "threes.js",
            "code": "function countThrees(n) {\n  let count = 0;\n  for (let i = 0; i < n; i += 3) {\n    count++;\n  }\n  return count;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `countThrees(10)`. Type the number it returns.",
                "accept": [
                  "4"
                ],
                "miss": "**Write out the values of `i`:** 0, 3, 6, 9, and `12 < 10` is `false`. That is four iterations, and the iteration for 0 should not count: 0 is not between 1 and 10.\n",
                "why": "`4`. It should be 3.\n"
              },
              {
                "id": "c2",
                "question": "`countThrees(9)` returns 3, which is right. Why?",
                "choices": [
                  "The loop is correct for 9",
                  "It counts 0 and leaves out 9, so the two mistakes cancel",
                  "The loop stops early"
                ],
                "accept": [
                  "It counts 0 and leaves out 9, so the two mistakes cancel"
                ],
                "why": "`i` is 0, 3, 6: the iteration for 0 should not happen, and `9 < 9` keeps out the iteration for 9. One iteration too many at the start, one too few at the end. A test that passes says only that one input came out right.\n"
              },
              {
                "id": "c3",
                "question": "`countThrees(2)` returns 1, though there are no multiples of 3 from 1 to 2. Which iteration is the extra one?",
                "choices": [
                  "The iteration with i at 0",
                  "The iteration with i at 2",
                  "The iteration with i at 3"
                ],
                "accept": [
                  "The iteration with i at 0"
                ],
                "miss": "**Ask the first-iteration question.** The initialization makes `i` 0, and `0 < 2` is `true`, so the body counts it.\n",
                "why": "The iteration with `i` at 0. The first multiple of 3 from 1 up is 3, so that is where the counter should start.\n"
              }
            ],
            "exportName": "countThrees",
            "starter": "function countThrees(n) {\n  let count = 0;\n  // TODO: fix the loop's header\n  for (let i = 0; i < n; i += 3) {\n    count++;\n  }\n  return count;\n}\n",
            "tests": [
              {
                "label": "countThrees(10) → 3",
                "body": "return subject(10) === 3;"
              },
              {
                "label": "countThrees(2) → 0",
                "body": "return subject(2) === 0;"
              },
              {
                "label": "countThrees(9) → 3",
                "body": "return subject(9) === 3;"
              },
              {
                "label": "countThrees(3) → 1",
                "body": "return subject(3) === 1;"
              }
            ],
            "hints": [
              {
                "when": "you answered 3 for countThrees(10)",
                "say": "3 is what it should return. Write out the values of `i` the header makes, starting from its initialization.\n"
              },
              {
                "when": "countThrees(9) and countThrees(3) fail after your fix",
                "say": "Ask the last-iteration question: does your condition let `n` itself in when `n` is a multiple of 3?\n"
              },
              {
                "when": "countThrees(10) gives 4 after your fix",
                "say": "Ask the first-iteration question: is 0 one of the multiples from 1 to `n`? What should the first value of `i` be?\n"
              }
            ]
          }
        ]
      }
    },
    "do-while": {
      "id": "do-while",
      "title": "The do…while loop",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.do-while` (concepts.md; D-066 item 3): say when a loop's body must run at least once, write a do…while for it, and say what it does differently from a `while` when the condition is false from the start. Before D-066 the form was one aside in *Choosing a loop, and leaving early* (\"rarely needed\"), with no example, and listed in no concept (D-066 item 2). The anchor is a bug students write: the `while` lesson's critique specified `countDigits` for \"a whole number of 1 or more\", which quietly avoided 0, the input a `while (n > 0)` gets wrong. The second example, the next floor in a building that skips floor numbers with a 4, is a \"next after\" search, where the body must run once because \"next\" is never the starting value. Procedural, explain first (D-061 item 1); the writing direction with a fail-first example and a faded one is 01-explanations rule 5a. The Python contrast is D-010's first reason: Python has no do…while, so the idea, a check after the body, is shown to be separate from the syntax. Why the form is less common is given as a reason (rule 6b): most loops must be able to run zero iterations. Examples and steps derived from those rules, pending Nick's review.",
      "concepts": [
        "flow.do-while"
      ],
      "lede": "A `while` loop checks its condition before every iteration, so its body may never run at all. Usually that is right: when there is nothing to do, the loop should do nothing. But some work has to happen at least once. The do…while loop checks its condition after each iteration instead, so its body always runs at least once. This lesson shows how it runs, when a problem needs it, and why most loops do not.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The while loop · The for loop · Choosing a loop · Leaving a loop early: break and continue"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**do…while loop** — a loop that runs its body, then checks its condition, and runs the body again while the condition is `true`. Also written *do-while*. **Iteration** — one run of the body. **Condition** — the expression in parentheses after `while`, checked to decide whether to run another iteration. **Empty statement** — a semicolon on its own, which does nothing; after `while (…)` it becomes the loop's whole body.",
          "entries": [
            "**do…while loop** — a loop that runs its body, then checks its condition, and runs the body again while the condition is `true`. Also written *do-while*.",
            "**Iteration** — one run of the body.",
            "**Condition** — the expression in parentheses after `while`, checked to decide whether to run another iteration.",
            "**Empty statement** — a semicolon on its own, which does nothing; after `while (…)` it becomes the loop's whole body."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory. Type the value as a\nnumber, or press the answer. Press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let n = 0;\nlet iterations = 0;\nwhile (n > 0) {\n  iterations++;\n  n--;\n}\niterations\n",
                    "accept": [
                      "0"
                    ],
                    "miss": "**A `while` checks its condition before every iteration, the first one included.** `0 > 0` is `false`, so the body never runs.\n",
                    "why": "`0`.\n"
                  },
                  {
                    "question": "`for (let i = 1; i <= 4; i++)` — how many iterations does this loop run? Type the number.",
                    "accept": [
                      "4",
                      "four"
                    ],
                    "miss": "**Write out the values of `i`.** `<=` lets 4 in: `i` is 1, 2, 3, 4, and the check with 5 is `false`.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "code": "Math.floor(4096 / 10)",
                    "accept": [
                      "409"
                    ],
                    "miss": "**`4096 / 10` is `409.6`, and `Math.floor` rounds down to `409`.** Dividing by 10 and rounding down drops the last digit.\n",
                    "why": "`409`.\n"
                  },
                  {
                    "code": "String(43).includes(\"4\")",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "miss": "**`String(43)` is the text `\"43\"`,** and `includes` is `true` when the text contains `\"4\"` anywhere.\n",
                    "why": "`true`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say when a loop's body has to run at least once",
                "I can write a do…while loop for that case",
                "I can say what a do…while and a `while` each do when the condition is `false` from the start"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "A loop that never starts",
          "body": [
            {
              "p": "\"`countDigits` takes a whole number of 0 or more and returns how many digits\nit has: 4096 has 4 digits, and 0 has 1.\""
            },
            {
              "p": "Here is a first attempt, with a `while` loop. Each iteration counts one digit\nand drops it from the end of `n`: `Math.floor(4096 / 10)` is 409."
            },
            {
              "code": "function countDigits(n) {\n  let count = 0;\n  while (n > 0) {\n    count++;\n    n = Math.floor(n / 10);\n  }\n  return count;\n}",
              "lang": "js",
              "file": "digits.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✓ countDigits(4096) → 4\n✓ countDigits(7) → 1\n✗ countDigits(0) → 1\n✓ countDigits(10) → 2",
              "lang": "text"
            },
            {
              "p": "Three tests pass. For 4096, `n` is 4096, then 409, then 40, then 4, then 0:\nfour iterations, one per digit. The test that failed is `countDigits(0)`. Work it\nthrough by hand, with `n` holding 0:"
            },
            {
              "table": {
                "head": [
                  "Check",
                  "`n`",
                  "`n > 0`",
                  "What happens"
                ],
                "rows": [
                  [
                    "1",
                    "`0`",
                    "`false`",
                    "the loop ends before any iteration; `count` is still `0`"
                  ]
                ]
              }
            },
            {
              "p": "`countDigits(0)` returns `0`. The `while` checks `n > 0` before the first\niteration, and for 0 that check is already `false`, so the counting line never runs. But 0\nis written with one digit, the same as 7 is. Every whole number has at least\none digit, so the counting has to happen at least once, whatever `n` is.\nA `while` loop cannot promise that: its condition can stop it before it\nstarts."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "With this `while` version, how many iterations does `countDigits(0)` run? Type the number.",
                    "accept": [
                      "0",
                      "zero",
                      "none"
                    ],
                    "why": "`0`. The condition is `false` at the first check.\n"
                  },
                  {
                    "question": "With this `while` version, what does `countDigits(507)` return? Type the number.",
                    "accept": [
                      "3"
                    ],
                    "miss": "**Trace it.** `n` is 507, 50, 5, then 0: three checks that are `true`, and a 0 in the middle of a number does not stop the loop, because `50` is still more than 0.\n",
                    "why": "`3`, which is right.\n"
                  },
                  {
                    "question": "Which numbers does this `while` version get wrong?",
                    "choices": [
                      "Every number",
                      "Only 0",
                      "Every number that ends in 0"
                    ],
                    "accept": [
                      "Only 0"
                    ],
                    "miss": "**If you chose numbers that end in 0 — `countDigits(10)` passed.** The loop stops when the whole of `n` is 0, not when a digit is. Only the number 0 is `0` before the first iteration.\n",
                    "why": "Only 0, the one number for which `n > 0` is `false` before the first iteration.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "The do…while loop",
          "body": [
            {
              "p": "JavaScript has a loop that runs its body first and checks afterwards: the\n**do…while loop**, also written *do-while*. Here is `countDigits` with one:"
            },
            {
              "code": "function countDigits(n) {\n  // start the result\n  let count = 0;\n  do {\n    // do one iteration's work\n    count++;\n    // change something each iteration: drop the last digit, 4096 becomes 409\n    n = Math.floor(n / 10);\n  // say when to stop\n  } while (n > 0);\n  // send the result out\n  return count;\n}",
              "lang": "js",
              "file": "digits.js"
            },
            {
              "p": "The loop has four parts:"
            },
            {
              "table": {
                "head": [
                  "Part",
                  "In `countDigits`",
                  "What it is"
                ],
                "rows": [
                  [
                    "the `do` keyword",
                    "`do`",
                    "starts the loop"
                  ],
                  [
                    "the body",
                    "the lines between the braces",
                    "runs first, then again each time the condition is `true`"
                  ],
                  [
                    "the `while` keyword and the condition",
                    "`while (n > 0)`",
                    "checked after every iteration"
                  ],
                  [
                    "the semicolon",
                    "`;`",
                    "ends the statement"
                  ]
                ]
              }
            },
            {
              "p": "The subgoals are the five a `while` loop uses: start the result, do one\niteration's work, change something each iteration, say when to stop, and send the result out.\nOne has moved. **Say when to stop** was at the top of the `while` loop; in the\ndo…while it is at the bottom."
            },
            {
              "p": "The parts run in this order:"
            },
            {
              "list": [
                "**Run the body.**",
                "**Check the condition.** `n > 0` — is it `true`?",
                "**If it is `true`, go back to step 1.** If it is `false`, carry on after the semicolon."
              ],
              "ordered": true
            },
            {
              "p": "Here is `countDigits(0)` again, one row per iteration:"
            },
            {
              "table": {
                "head": [
                  "Iteration",
                  "`count` after the iteration",
                  "`n` after the iteration",
                  "`n > 0`",
                  "What happens"
                ],
                "rows": [
                  [
                    "1",
                    "`1`",
                    "`0`",
                    "`false`",
                    "the loop ends"
                  ]
                ]
              }
            },
            {
              "p": "`countDigits(0)` now returns `1`. And for 4096:"
            },
            {
              "table": {
                "head": [
                  "Iteration",
                  "`count` after the iteration",
                  "`n` after the iteration",
                  "`n > 0`",
                  "What happens"
                ],
                "rows": [
                  [
                    "1",
                    "`1`",
                    "`409`",
                    "`true`",
                    "another iteration"
                  ],
                  [
                    "2",
                    "`2`",
                    "`40`",
                    "`true`",
                    "another iteration"
                  ],
                  [
                    "3",
                    "`3`",
                    "`4`",
                    "`true`",
                    "another iteration"
                  ],
                  [
                    "4",
                    "`4`",
                    "`0`",
                    "`false`",
                    "the loop ends"
                  ]
                ]
              }
            },
            {
              "p": "That is the same four iterations the `while` loop ran. When the condition is\n`true` at the start, the two loops run the same iterations. They differ only when\nthe condition is `false` from the start:"
            },
            {
              "table": {
                "head": [
                  "Loop",
                  "`countDigits(0)` iterations",
                  "returns"
                ],
                "rows": [
                  [
                    "`while (n > 0) { … }`",
                    "0",
                    "`0`"
                  ],
                  [
                    "`do { … } while (n > 0);`",
                    "1",
                    "`1`"
                  ]
                ]
              }
            },
            {
              "p": "In short: **a do…while checks its condition after every iteration, so\nits body always runs at least once. A `while` checks before every iteration,\nso it can run none.**"
            },
            {
              "p": "**The semicolon.** A do…while ends in `while (n > 0);`, with a semicolon after\nthe condition. In a `while` loop the body comes after the condition, so the\nstatement does not end there. In a do…while the condition is the last thing in\nthe statement, so the semicolon goes after it, as at the end of `let count =\n0;`. This course writes it, as it writes every semicolon, so that you never\nhave to work out where JavaScript would put one for you (*Your first program*\nexplains that). C# and Java, which also have a do…while loop, require it."
            },
            {
              "p": "That semicolon belongs after a do…while's condition, never after a plain\n`while (…)`. There it becomes the loop's whole body, an empty statement, and a\nloop whose condition starts out `true` never finishes: the trap *The while\nloop* showed."
            },
            {
              "p": "Type each answer as a number, or press the answer."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let n = 10;\nlet iterations = 0;\ndo {\n  iterations++;\n  n = n - 1;\n} while (n > 20);\niterations\n",
                    "accept": [
                      "1"
                    ],
                    "miss": "**The body runs before the condition is checked.** `iterations` becomes 1 and `n` becomes 9; then `9 > 20` is `false`, and the loop ends.\n",
                    "why": "`1`.\n"
                  },
                  {
                    "question": "The same loop, written as `while (n > 20) { iterations++; n = n - 1; }`, with `n` starting at 10. How many iterations does it run? Type the number.",
                    "accept": [
                      "0",
                      "zero",
                      "none"
                    ],
                    "miss": "**A `while` checks first.** `10 > 20` is `false` before the first iteration.\n",
                    "why": "`0`. With the condition `false` from the start, the do…while runs one iteration and the `while` runs none.\n"
                  },
                  {
                    "question": "What does the do…while version of `countDigits(90)` return? Type the number.",
                    "accept": [
                      "2"
                    ],
                    "miss": "**Trace it.** After the first iteration, `count` is 1 and `n` is 9, and `9 > 0` is `true`. After the second, `count` is 2 and `n` is 0.\n",
                    "why": "`2`.\n"
                  },
                  {
                    "question": "In a plain `while` loop, what does a semicolon straight after the condition do, as in `while (i <= n);`?",
                    "choices": [
                      "It ends the loop there, so the loop body is empty",
                      "Nothing: JavaScript ignores it",
                      "It turns the loop into a do…while"
                    ],
                    "accept": [
                      "It ends the loop there, so the loop body is empty"
                    ],
                    "miss": "**A semicolon on its own is a statement that does nothing, and it becomes the loop's body.** The lines in braces below are a separate block that runs once, after the loop, if the loop ever ends.\n",
                    "why": "It ends the loop there with an empty body. If the condition is `true`, nothing in that empty body can change it, so the loop never ends.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "When the body has to run first",
          "body": [
            {
              "p": "\"A building leaves out every floor number that has a 4 in it: there is no\nfloor 4, no 14, and no 40 to 49. `nextFloor` takes the number of a floor and\nreturns the number of the floor above it: `nextFloor(3)` is 5, `nextFloor(12)`\nis 13 and `nextFloor(39)` is 50.\""
            },
            {
              "p": "Two tools from earlier lessons do the checking. `String(floor)` turns the\nnumber into text: `String(43)` is `\"43\"`. And `.includes(\"4\")` is `true` when\nthat text contains the character `\"4\"`: `\"43\".includes(\"4\")` is `true`."
            },
            {
              "p": "Here is a first attempt, with a `while` loop. Read as a sentence, it sounds\nright: go up while the floor number has a 4 in it."
            },
            {
              "code": "function nextFloor(floor) {\n  while (String(floor).includes(\"4\")) {\n    floor++;\n  }\n  return floor;\n}",
              "lang": "js",
              "file": "floors.js"
            },
            {
              "code": "✗ nextFloor(3) → 5\n✗ nextFloor(12) → 13\n✗ nextFloor(39) → 50",
              "lang": "text"
            },
            {
              "p": "Every test failed. Work `nextFloor(3)` through, with `floor` holding 3. The\nfirst check is `String(3).includes(\"4\")`, which is `\"3\".includes(\"4\")`, which\nis `false`. So the body never runs, and the function returns 3: the floor it\nstarted on."
            },
            {
              "p": "That happens for every test, because no floor anyone starts on has a 4 in it.\nThe `while` loop only moves past floors with a 4; it never moves off the\nstarting floor. Going up one floor is work that has to happen at least once,\nbefore there is anything worth checking. That is the sign of a do…while."
            },
            {
              "p": "Here is the do…while version, with two lines left for you to choose.\n`floor` is the result: it starts as the floor you are on, and the function\nreturns it. In this loop one line does an iteration's work and changes\nsomething each iteration, because going up a floor is both."
            },
            {
              "code": "function nextFloor(floor) {\n  do {\n    // TODO (a)\n  // TODO (b): go round again while the number has a 4 in it\n  return floor;\n}",
              "lang": "js",
              "file": "floors.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes at TODO (a)?",
                    "choices": [
                      "floor++;",
                      "floor + 1;",
                      "return floor + 1;"
                    ],
                    "accept": [
                      "floor++;"
                    ],
                    "miss": "**If you chose `floor + 1;` — it works out a value and throws it away,** so `floor` never changes. **If you chose `return floor + 1;` — `return` ends the function on the first iteration,** so `nextFloor(3)` would be 4, a floor that does not exist.\n",
                    "why": "`floor++;`, which adds one to `floor` itself.\n"
                  },
                  {
                    "question": "Which line goes at TODO (b)?",
                    "choices": [
                      "} while (String(floor).includes(\"4\"));",
                      "} while (!String(floor).includes(\"4\"));",
                      "} while (floor === 4);"
                    ],
                    "accept": [
                      "} while (String(floor).includes(\"4\"));"
                    ],
                    "miss": "**Say the condition as \"go round again while …\".** With the logical NOT, `!`, the loop goes round while the number has *no* 4, so from 3 it stops at 4, the one floor it must skip. With `floor === 4`, it skips 4 but not 14 or 40: `nextFloor(39)` would be 40.\n",
                    "why": "`} while (String(floor).includes(\"4\"));`: go round again while the floor number has a 4 in it. The semicolon ends the statement.\n"
                  },
                  {
                    "question": "With the do…while version, what does `nextFloor(39)` return? Type the number.",
                    "accept": [
                      "50"
                    ],
                    "miss": "**Every number from 40 to 49 has a 4 in it,** so the loop goes round again after each of them. The first floor after 39 without a 4 is 50.\n",
                    "why": "`50`.\n"
                  },
                  {
                    "question": "How many iterations does `nextFloor(39)` run? Type the number.",
                    "accept": [
                      "11",
                      "eleven"
                    ],
                    "miss": "**One iteration for each floor number the loop moves to.** `floor` becomes 40, 41, … 49, then 50: that is 10 iterations that land on a 4, and one more that lands on 50.\n",
                    "why": "`11`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Why most loops can run zero iterations",
          "body": [
            {
              "p": "`countDigits` and `nextFloor` both need their body to run at least once. Most\nloops do not. Here is `sumTo`, which returns 1 + 2 + … + n, written with each\nloop:"
            },
            {
              "code": "function sumTo(n) {\n  let total = 0;\n  let i = 1;\n  while (i <= n) {\n    total += i;\n    i++;\n  }\n  return total;\n}",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "code": "function sumTo(n) {\n  let total = 0;\n  let i = 1;\n  do {\n    total += i;\n    i++;\n  } while (i <= n);\n  return total;\n}",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "p": "For 4, both return 10. For 0, the `while` version's body never runs, and it\nreturns `0`: there are no numbers to add. The do…while version adds 1 before it checks\n`i <= n`, and returns `1`, which is wrong."
            },
            {
              "p": "To choose between them, ask one question: **when there is nothing to do, should\nthe work still happen once?**"
            },
            {
              "table": {
                "head": [
                  "Function",
                  "With nothing to do",
                  "Loop"
                ],
                "rows": [
                  [
                    "`sumTo(0)`",
                    "nothing to add: 0",
                    "`while` or `for`"
                  ],
                  [
                    "a line of 0 dashes",
                    "no dashes: `\"\"`",
                    "`while` or `for`"
                  ],
                  [
                    "`countDigits(0)`",
                    "0 still has one digit: 1",
                    "do…while"
                  ],
                  [
                    "`nextFloor(3)`",
                    "\"next\" always goes up at least one floor: 5",
                    "do…while"
                  ]
                ]
              }
            },
            {
              "p": "Most loops work through something, such as the numbers up to a limit or the\ncharacters of a text, and that something can be empty. An empty text has no\ncharacters to count, and a count of 0 is nothing to repeat. So most loops must\nbe able to run zero iterations, and only `while` and `for` can. That is why you\nwill meet the do…while far less often than the other two. It is not a worse\nloop; it fits fewer problems."
            },
            {
              "p": "There is a second reason. Any do…while can be written as a `while` that does\nthe first iteration's work some other way. Here is `countDigits` with a `while`\nthat starts `count` at 1, for the digit every number has, and drops a digit\nonly while there is more than one:"
            },
            {
              "code": "function countDigits(n) {\n  let count = 1;\n  while (n >= 10) {\n    n = Math.floor(n / 10);\n    count++;\n  }\n  return count;\n}",
              "lang": "js",
              "file": "digits.js"
            },
            {
              "p": "It passes all four tests, and some programmers prefer it. Both versions are\ncorrect. The do…while says \"at least once\" in the shape of the loop, where a\nreader sees it; this `while` version says it in its starting values, `count =\n1` and `n >= 10`, where a reader has to work it out."
            },
            {
              "p": "**In other languages.** C# and Java have a do…while loop written as\nJavaScript's is. Python has none. A Python programmer whose loop must run at\nleast once writes a loop that always starts, `while True:`, and leaves it from\ninside the body, at the end of the iteration, with `break`. `break` is taught later\nin this module, in *Leaving a loop early: break and continue*. So \"check after the body\"\nis an idea every one of these languages can express; the do…while is\nJavaScript's shorthand for it, and some languages do without the shorthand."
            },
            {
              "p": "Press the loop that fits."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Count the spaces in a text. The text can be empty.",
                    "choices": [
                      "while or for",
                      "do…while"
                    ],
                    "accept": [
                      "while or for"
                    ],
                    "why": "A `while` or a `for` loop. An empty text has no spaces, so the loop must be able to run zero iterations.\n"
                  },
                  {
                    "question": "Return the next leap year after `year`: after 2024 comes 2028, not 2024.",
                    "choices": [
                      "while or for",
                      "do…while"
                    ],
                    "accept": [
                      "do…while"
                    ],
                    "miss": "**\"Next\" never means the year you start from.** A `while` that checks \"is this a leap year?\" first would return 2024 for 2024, as `nextFloor` returned 3 for 3.\n",
                    "why": "A do…while: go forward one year, then check, and go round again while the year is not a leap year.\n"
                  },
                  {
                    "question": "Return how many years a savings balance needs to reach a goal. A balance already at the goal needs 0 years.",
                    "choices": [
                      "while or for",
                      "do…while"
                    ],
                    "accept": [
                      "while or for"
                    ],
                    "miss": "**0 years is a real answer here.** A do…while would always count at least one year.\n",
                    "why": "A `while` loop: a balance already at the goal needs no iterations.\n"
                  },
                  {
                    "question": "The `while` version of `countDigits` above starts `count` at 1 and loops while `n >= 10`. What does it return for 0? Type the number.",
                    "accept": [
                      "1"
                    ],
                    "why": "`1`. `0 >= 10` is `false`, so the body never runs, and `count` was already 1.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "\"`halvings` takes a number and returns how many times you can halve it before\nit is 1 or less: `halvings(8)` is 3, because 8, 4, 2, 1 is three halvings, and\n`halvings(1)` is 0.\""
            },
            {
              "p": "Choose a `while` or a do…while loop for `halvings`, and defend the choice with\nthe question from the last step: when there is nothing to do, should the work\nstill happen once? Then say what the loop you did not choose would return for\n`halvings(1)`, and why."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**A do…while runs its body, then checks its condition,** so the body always runs at least once. It ends with `} while (condition);`, semicolon included.",
                "**With a condition that is `false` from the start, a `while` runs no iterations and a do…while runs one.** With a condition that is `true` at the start, the two run the same iterations.",
                "**Ask: when there is nothing to do, should the work still happen once?** Usually not, so most loops are `while` or `for`; a number's digits and \"the next one after\" are cases where it should."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "do...while",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/do...while",
              "note": "the body runs before the condition is checked, so at least once."
            },
            {
              "title": "Empty statement",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/Empty",
              "note": "a semicolon on its own, and the loop it can empty by accident."
            }
          ]
        }
      ],
      "_source": "lessons/do-while.md",
      "practice": {
        "id": "do-while",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Before each iteration, or after it?",
            "prompt": "The first two loops have the same condition, `count > 5`, and it is `false` from the start. Type the value each one ends with, as a number, or press the answer.\n",
            "items": [
              {
                "id": "d1",
                "code": "let count = 0;\ndo {\n  count++;\n} while (count > 5);\ncount\n",
                "accept": [
                  "1"
                ],
                "miss": "**The body comes before the condition, and runs before it is checked.** `count` becomes 1; then `1 > 5` is `false`, and the loop ends after one iteration.\n",
                "why": "`1`. A do…while always runs at least one iteration.\n"
              },
              {
                "id": "d2",
                "code": "let count = 0;\nwhile (count > 5) {\n  count++;\n}\ncount\n",
                "accept": [
                  "0"
                ],
                "miss": "**A `while` checks its condition before every iteration, the first one included.** `0 > 5` is `false`, so the body never runs.\n",
                "why": "`0`. With the condition `false` from the start, the `while` runs no iterations and the do…while runs one.\n"
              },
              {
                "id": "d3",
                "question": "In a do…while loop, when is the condition checked?",
                "choices": [
                  "Before every iteration",
                  "After every iteration",
                  "Only once, after the first iteration"
                ],
                "accept": [
                  "After every iteration"
                ],
                "miss": "**The condition is written after the body, and it is checked there, after every iteration.** If it is `true`, the body runs again, and the condition is checked again after that iteration too.\n",
                "why": "After every iteration. That is why the body always runs at least once.\n"
              }
            ],
            "extra": [
              {
                "id": "e1",
                "code": "let n = 1;\ndo {\n  n = n * 3;\n} while (n < 20);\nn\n",
                "accept": [
                  "27"
                ],
                "miss": "**The check comes after each iteration.** `n` is 3, then 9, then 27. After the iteration that makes 9, `9 < 20` is `true`, so the body runs again; after 27, `27 < 20` is `false`.\n",
                "why": "`27`. A `while` with the same condition ends with 27 too: when the condition is `true` at the start, the two loops run the same iterations.\n"
              },
              {
                "id": "e2",
                "code": "let s = \"go\";\ndo {\n  s += \"!\";\n} while (s.length < 2);\ns\n",
                "accept": [
                  "\"go!\"",
                  "go!",
                  "'go!'"
                ],
                "miss": "**`\"go\".length` is 2, and `2 < 2` is `false`, but nothing checks that until after the first iteration.** The body adds `\"!\"` first.\n",
                "why": "`\"go!\"`. One iteration, then the check `3 < 2` is `false`.\n"
              },
              {
                "id": "e3",
                "code": "let k = 3;\nlet iterations = 0;\ndo {\n  k--;\n  iterations++;\n} while (k > 0);\niterations\n",
                "accept": [
                  "3"
                ],
                "miss": "**Trace it: after each iteration, write down `k`, then check.** `k` is 2, then 1, then 0, and `0 > 0` is `false`.\n",
                "why": "`3`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 0 for the do…while loop",
                "say": "Read the do…while from the top. What is the first line that runs after `do {`, and where is the condition written?\n"
              },
              {
                "when": "you answered 1 for the while loop",
                "say": "In a `while`, the condition is the first thing that runs. Is `0 > 5` true?\n"
              },
              {
                "when": "you answered 2 for the do…while that adds \"!\"",
                "say": "Each iteration adds one character. How many iterations run before the first check?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Two countdowns, one difference",
            "prompt": "`countdownA` and `countdownB` differ in one thing: A uses a `while` loop and B uses a do…while loop with the same condition. Predict each line the program prints — type it without quotes — then run it and compare.\n",
            "filename": "countdowns.js",
            "code": "function countdownA(n) {\n  let text = \"\";\n  while (n > 0) {\n    text += n + \" \";\n    n--;\n  }\n  return text + \"go\";\n}\nfunction countdownB(n) {\n  let text = \"\";\n  do {\n    text += n + \" \";\n    n--;\n  } while (n > 0);\n  return text + \"go\";\n}\nconsole.log(countdownA(3));\nconsole.log(countdownB(3));\nconsole.log(countdownA(0));\nconsole.log(countdownB(0));\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(countdownA(3));",
                "accept": [
                  "3 2 1 go",
                  "\"3 2 1 go\""
                ],
                "miss": "**`n` is 3, 2, 1 at the checks that are `true`.** Each iteration adds the number and a space; after the loop, `\"go\"` goes on the end.\n",
                "why": "`3 2 1 go`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(countdownB(3));",
                "accept": [
                  "3 2 1 go",
                  "\"3 2 1 go\""
                ],
                "miss": "**When the condition is `true` at the start, both loops run the same iterations.** The do…while checks `n > 0` after each iteration: with `n` at 2, at 1, then at 0, which ends it.\n",
                "why": "`3 2 1 go`, the same as `countdownA(3)`.\n"
              },
              {
                "id": "r3",
                "code": "console.log(countdownA(0));",
                "accept": [
                  "go",
                  "\"go\""
                ],
                "miss": "**`0 > 0` is `false` at the first check,** so the body of the `while` never runs and `text` is still `\"\"`.\n",
                "why": "`go`.\n"
              },
              {
                "id": "r4",
                "code": "console.log(countdownB(0));",
                "accept": [
                  "0 go",
                  "\"0 go\""
                ],
                "miss": "**The do…while runs its body before it checks.** The body adds `\"0 \"` and makes `n` −1; then `-1 > 0` is `false`.\n",
                "why": "`0 go`. A countdown from 0 should have nothing to count, so this is the do…while running an iteration it should not: the `while` is the right loop here.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted go for countdownB(0)",
                "say": "Find the condition in `countdownB`. Which line runs before the program ever reaches it?\n"
              },
              {
                "when": "you predicted different lines for countdownA(3) and countdownB(3)",
                "say": "With `n` at 3, is `n > 0` true at the start? When the condition is true at the start, does it matter whether it is checked before or after the first iteration?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "The next working day",
            "prompt": "Days are numbered 0 for Sunday, 1 for Monday, up to 6 for Saturday. `nextWorkday` takes a day and returns the next day that is not Saturday or Sunday. Trace `nextWorkday(5)`, Friday, one iteration at a time.\n",
            "items": [
              {
                "id": "t1",
                "code": "function nextWorkday(day) {\n  do {\n    day = (day + 1) % 7;\n  } while (day === 0 || day === 6);\n  return day;\n}\n// nextWorkday(5): what is day after the first iteration?\n",
                "accept": [
                  "6"
                ],
                "miss": "**`(5 + 1) % 7` is `6 % 7`**, and 6 divided by 7 is 0 with 6 left over.\n",
                "why": "`6`. Saturday.\n"
              },
              {
                "id": "t2",
                "question": "After that first iteration, is the condition `day === 0 || day === 6` true or false?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**`day` is 6, so `day === 6` is `true`,** and with logical OR, one `true` side is enough.\n",
                "why": "`true`, so the loop runs another iteration.\n"
              },
              {
                "id": "t3",
                "question": "What is `day` after the second iteration? Type the number.",
                "accept": [
                  "0"
                ],
                "miss": "**`(6 + 1) % 7` is `7 % 7`,** and 7 divides by 7 with nothing left over. The remainder is what wraps Saturday round to Sunday.\n",
                "why": "`0`. Sunday, so the condition is `true` again.\n"
              },
              {
                "id": "t4",
                "question": "How many iterations does `nextWorkday(5)` run? Type the number.",
                "accept": [
                  "3",
                  "three"
                ],
                "miss": "**`day` is 6, then 0, then 1.** After the third iteration, `1 === 0` and `1 === 6` are both `false`, so the loop ends.\n",
                "why": "`3`.\n"
              },
              {
                "id": "t5",
                "question": "What does `nextWorkday(5)` return? Type the number.",
                "accept": [
                  "1"
                ],
                "why": "`1`: Friday is followed by Monday.\n"
              }
            ],
            "extra": [
              {
                "id": "t6",
                "question": "What does `nextWorkday(2)` return? Type the number.",
                "accept": [
                  "3"
                ],
                "why": "`3`. One iteration makes `day` 3, and the condition is `false`. Tuesday is followed by Wednesday.\n"
              },
              {
                "id": "t7",
                "question": "How many iterations does `nextWorkday(6)` run? Type the number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "`2`. `day` is 0, then 1.\n"
              },
              {
                "id": "t8",
                "question": "With the loop rewritten as `while (day === 0 || day === 6) { … }`, what would `nextWorkday(2)` return? Type the number.",
                "accept": [
                  "2"
                ],
                "miss": "**A `while` checks first.** `2 === 0 || 2 === 6` is `false`, so the body never runs and `day` is returned unchanged.\n",
                "why": "`2`: Tuesday, the same day, not the next one. \"The next working day\" always moves at least one day, which is why this loop is a do…while.\n"
              }
            ],
            "hints": [
              {
                "when": "you gave 7 for day after the second iteration",
                "say": "The line is `(day + 1) % 7`, not `day + 1`. What is left over when 7 is divided by 7?\n"
              },
              {
                "when": "you counted 2 iterations for nextWorkday(5)",
                "say": "Write `day` after each iteration, then check the condition with that value. Is the condition true when `day` is 0?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Multiply the digits",
            "prompt": "\"`digitProduct` takes a whole number of 0 or more and returns its digits multiplied together: `digitProduct(234)` is 2 × 3 × 4, which is 24, and `digitProduct(0)` is 0, because the number 0 has one digit, 0.\" Put the lines in order. Two lines are mistakes — leave them out.\n",
            "lines": [
              "function digitProduct(n) {",
              "  let product = 1;",
              "  do {",
              "    product *= n % 10;",
              "    n = Math.floor(n / 10);",
              "  } while (n > 0);",
              "  return product;",
              "}"
            ],
            "distractors": [
              {
                "line": "  let product = 0;",
                "why": "Every iteration multiplies, and 0 times anything is 0, so the function would return 0 for every number. A product starts at 1.\n"
              },
              {
                "line": "  } while (n >= 10);",
                "why": "It stops one digit early. For 234, after two iterations `n` is 2, and `2 >= 10` is `false`, so the 2 is never multiplied in: `digitProduct(234)` would be 12.\n"
              }
            ],
            "why": "Start the product at 1, then in each iteration multiply in the last digit, `n % 10`, and drop it with `Math.floor(n / 10)`. Check `n > 0` after the iteration: it is `false` once no digits are left. Because the check comes after the iteration, the loop runs one iteration for 0 and returns 0. A `while (n > 0)` loop would run no iterations for 0 and return 1.\n",
            "hints": [
              {
                "when": "you used let product = 0",
                "say": "What is 0 times 4? Try `digitProduct(234)` with the product starting at 0.\n"
              },
              {
                "when": "you used while (n >= 10)",
                "say": "Trace `digitProduct(234)`. After the second iteration, what is `n`, and has its digit been multiplied in yet?\n"
              },
              {
                "when": "you put n = Math.floor(n / 10) above the product line",
                "say": "The product line reads the last digit with `n % 10`. If the last digit is dropped first, which digit does it read?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Digits, back to front",
            "prompt": "\"`reverseDigits` takes a whole number of 0 or more and returns its digits in reverse order, as text: `reverseDigits(1203)` is `\"3021\"`, and `reverseDigits(0)` is `\"0\"`.\" Use a do…while loop: start the result, do one iteration's work, change something each iteration, say when to stop, send the result out. Write it between the braces — *Writing a function's answer* shows how. Then, in one sentence: which test would a `while (n > 0)` loop fail, and why?\n",
            "exportName": "reverseDigits",
            "filename": "reverse.js",
            "starter": "function reverseDigits(n) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "reverseDigits(1203) → \"3021\"",
                "body": "return subject(1203) === '3021';"
              },
              {
                "label": "reverseDigits(7) → \"7\"",
                "body": "return subject(7) === '7';"
              },
              {
                "label": "reverseDigits(450) → \"054\"",
                "body": "return subject(450) === '054';"
              },
              {
                "label": "reverseDigits(0) → \"0\"",
                "body": "return subject(0) === '0';"
              }
            ],
            "hints": [
              {
                "when": "reverseDigits(0) gives \"\" and the others pass",
                "say": "With 0, does your loop run any iterations? 0 has one digit. Which loop always runs at least one iteration?\n"
              },
              {
                "when": "reverseDigits(1203) gives \"302\"",
                "say": "The last digit, 1, is missing. After the third iteration, what is `n`, and does your condition let the loop run an iteration for it?\n"
              },
              {
                "when": "reverseDigits(1203) gives \"1203\"",
                "say": "Your digits come out in their original order. Each new digit should go on the end of the text, after the ones already there.\n"
              },
              {
                "when": "reverseDigits(1203) gives 6 or another small number",
                "say": "`n % 10` is a number, and number + number adds. Start the result as `\"\"` so that `+=` joins text.\n"
              },
              {
                "when": "reverseDigits(1203) gives \"3\"",
                "say": "Your `return` is inside the loop, so the function ends after the first iteration. Move it below the line with `while`.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review two loops",
            "prompt": "A code assistant wrote these. `stars` should return a line of `count` stars: `stars(3)` is `\"***\"`, and `stars(0)` is `\"\"`, no stars. `factorial` should return 1 × 2 × … × n: `factorial(4)` is 24. Read them, then answer each question. Then fix `stars` in the editor.\n",
            "filename": "review.js",
            "code": "function stars(count) {\n  let line = \"\";\n  do {\n    line += \"*\";\n    count--;\n  } while (count > 0);\n  return line;\n}\n\nfunction factorial(n) {\n  let result = 1;\n  let i = 1;\n  while (i <= n); {\n    result *= i;\n    i++;\n  }\n  return result;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "What does `stars(0)` return? Type the text, with its quotes.",
                "accept": [
                  "\"*\"",
                  "'*'",
                  "*"
                ],
                "miss": "**The do…while runs its body before it checks.** The first iteration adds a star; then `count` is −1, and `-1 > 0` is `false`.\n",
                "why": "`\"*\"`, one star where there should be none.\n"
              },
              {
                "id": "c2",
                "question": "Which loop fits `stars`?",
                "choices": [
                  "A while or for loop",
                  "A do…while loop"
                ],
                "accept": [
                  "A while or for loop"
                ],
                "miss": "**Ask what should happen when there is nothing to do.** With a count of 0, the right answer is no stars: zero iterations. Only a `while` or a `for` can run zero iterations.\n",
                "why": "A `while` or a `for` loop: a line of stars can have no stars in it.\n"
              },
              {
                "id": "c3",
                "question": "A test calls `factorial(4)`. What happens?",
                "choices": [
                  "It returns 24",
                  "It returns 1",
                  "It never finishes"
                ],
                "accept": [
                  "It never finishes"
                ],
                "miss": "**Look at the end of the `while` line.** The semicolon after `(i <= n)` ends the `while` statement there, with an empty body. The lines in braces below it are not part of the loop, so nothing in the loop changes `i`, and `1 <= 4` is `true` at every check.\n",
                "why": "It never finishes. In the practice editor it stops with `Timed out`.\n"
              },
              {
                "id": "c4",
                "question": "`factorial(0)` returns 1, which is right. Why?",
                "choices": [
                  "The loop is correct for 0",
                  "1 <= 0 is false, so the empty loop ends at once, and the block below runs once and multiplies by 1",
                  "The semicolon is ignored when n is 0"
                ],
                "accept": [
                  "1 <= 0 is false, so the empty loop ends at once, and the block below runs once and multiplies by 1"
                ],
                "why": "The empty loop runs no iterations, then the braces run once as an ordinary block: `result` is 1 × 1. A test that passes says only that one input came out right. Removing the semicolon makes the braces the loop's body again.\n"
              }
            ],
            "exportName": "stars",
            "starter": "function stars(count) {\n  let line = \"\";\n  // TODO: fix the loop\n  do {\n    line += \"*\";\n    count--;\n  } while (count > 0);\n  return line;\n}\n",
            "tests": [
              {
                "label": "stars(3) → \"***\"",
                "body": "return subject(3) === '***';"
              },
              {
                "label": "stars(1) → \"*\"",
                "body": "return subject(1) === '*';"
              },
              {
                "label": "stars(0) → \"\"",
                "body": "return subject(0) === '';"
              },
              {
                "label": "stars(5) → \"*****\"",
                "body": "return subject(5) === '*****';"
              }
            ],
            "hints": [
              {
                "when": "you said stars(0) returns \"\"",
                "say": "In a do…while, which runs first: the line that adds a star, or the condition?\n"
              },
              {
                "when": "you said factorial(4) returns 24",
                "say": "Find where the `while` statement ends. What comes straight after `(i <= n)`?\n"
              },
              {
                "when": "stars(3) gives \"****\" after your fix",
                "say": "One iteration too many. Write out `count` at each check. Which condition stops the loop when `count` reaches 0?\n"
              },
              {
                "when": "stars(0) still gives \"*\" and the other tests pass",
                "say": "A do…while runs its first iteration before any check, so whatever its condition says, it adds one star. For a count of 0 the condition has to be checked before the first iteration. Which loops check first?\n"
              }
            ]
          }
        ]
      }
    },
    "for-of": {
      "id": "for-of",
      "title": "The for…of loop",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.for-of` (concepts.md): walk a string one character at a time with `for…of`, and say when an iteration needs the position, so a `for` loop fits instead. D-066 made it its own concept and lesson, split from *Choosing a loop, and leaving early*, because each loop form has its own characteristic mistake, and this one's is needing a position `for…of` does not give. Pass 1, over strings; pass 2 walks arrays in the Arrays module (`arr.traversal`, D-045, D-066). It follows *The while loop*, *The for loop* and *The do…while loop*, and comes before *Choosing a loop*, which chooses among all four. Procedural, explain first (D-061 item 1). The writing direction is modelled with a fail-first example (01-explanations rule 5a) on the concept's own mistake, carried over from the `for` loop: the character used as a position, `text[ch]` (Nick, 2026-10-18: no loop that is not yet taught is shown, even as a mistake). The passage and examples moved from the old lesson; the position step, the fail-first example and the practice are new, derived from those rules, pending Nick's review.",
      "concepts": [
        "flow.for-of"
      ],
      "lede": "Many loops over a string want each character in turn and nothing else. The `for…of` loop hands you exactly that: the next character on every iteration, with no index to set up and no end to get wrong. This lesson shows how it works, how to write a function with it, and how to tell when an iteration needs the character's position, so a `for` loop fits instead.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Working with strings · The for loop · Writing a function's answer · Choosing a loop"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Iteration** — one run of the loop body. **Loop variable** — the variable that holds the loop's current value on each iteration: `i` in a `for` loop, `ch` in `for (const ch of word)`. **Index** (or **position**) — where a character sits in a string, counting from 0. **Neighbour** — the character just before or just after the one the loop is looking at.",
          "entries": [
            "**Iteration** — one run of the loop body.",
            "**Loop variable** — the variable that holds the loop's current value on each iteration: `i` in a `for` loop, `ch` in `for (const ch of word)`.",
            "**Index** (or **position**) — where a character sits in a string, counting from 0.",
            "**Neighbour** — the character just before or just after the one the loop is looking at."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory: reading a string\nby index, its length, a `for` loop's last iteration, and building text. Type the\nvalue, with quotes if it is text, or a number as it is. Press **I don't know**\nif you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const word = \"loop\";\nword[0]\n",
                    "accept": [
                      "\"l\"",
                      "l",
                      "'l'"
                    ],
                    "miss": "**Indexes start at 0**, from *Working with strings*: `word[0]` is the first character.\n",
                    "why": "`\"l\"`.\n"
                  },
                  {
                    "code": "\"red pen\".length",
                    "accept": [
                      "7"
                    ],
                    "miss": "**If you answered `6` — the space is a character too.** `r`, `e`, `d`, the space, `p`, `e`, `n`.\n",
                    "why": "`7`.\n"
                  },
                  {
                    "question": "`word` holds `\"loop\"`. In `for (let i = 0; i < word.length; i++)`, what is the value of `i` on the last iteration? Type the number.",
                    "accept": [
                      "3"
                    ],
                    "miss": "**`word.length` is 4, so the loop stops when `i` reaches 4.** The body runs with `i` at 0, 1, 2 and 3: the four indexes of `\"loop\"`.\n",
                    "why": "`3`, the index of the last character.\n"
                  },
                  {
                    "code": "let s = \"b\";\ns = \"a\" + s;\ns\n",
                    "accept": [
                      "\"ab\"",
                      "ab",
                      "'ab'"
                    ],
                    "miss": "**`\"a\" + s` puts `\"a\"` in front of what `s` holds**, and the result is stored back in `s`.\n",
                    "why": "`\"ab\"`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can walk a string one character at a time with `for…of`, and say what the loop variable holds on each iteration",
                "I can write a function that builds a count or a string from each character with `for…of`",
                "I can say when an iteration needs the position, so a `for` loop fits instead"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "One character at a time",
          "body": [
            {
              "p": "A string is a row of characters, each at its own index. A `for` loop can walk\none: start at index 0, stop when `i` reaches `.length`, and read the character\nat `i` on each iteration. This loop counts the dashes in a phone number:"
            },
            {
              "code": "const phone = \"555-867-5309\";\nlet dashes = 0;\nfor (let i = 0; i < phone.length; i++) {\n  if (phone[i] === \"-\") {\n    dashes++;\n  }\n}\nconsole.log(dashes);     // 2",
              "lang": "js",
              "file": "dashes.js"
            },
            {
              "p": "`phone` has 12 characters, at indexes 0 to 11. `i` takes each of those values\nin turn, and `phone[i]` is the character at that index. Look at what `i` is\nused for: only to reach `phone[i]`. The count of dashes never needs to know\n*where* a dash was, only that it was one."
            },
            {
              "p": "When each iteration needs the character and not its position, JavaScript has a\nloop that hands you the character directly: the **`for…of` loop**.\nDocumentation writes it `for...of`, with three dots, so search for it that\nway."
            },
            {
              "code": "const phone = \"555-867-5309\";\nlet dashes = 0;\nfor (const ch of phone) {\n  if (ch === \"-\") {\n    dashes++;\n  }\n}\nconsole.log(dashes);     // 2",
              "lang": "js",
              "file": "dashes.js"
            },
            {
              "p": "Read `for (const ch of phone)` as \"for each character `ch` of `phone`\". `ch` is\nthe **loop variable**: a name the loop sets at the start of each iteration.\nYou choose the name; `ch` is short for *character*. Here is the loop running:"
            },
            {
              "table": {
                "head": [
                  "Iteration",
                  "`ch`",
                  "`ch === \"-\"`?",
                  "`dashes` afterwards"
                ],
                "rows": [
                  [
                    "1",
                    "`\"5\"`",
                    "`false`",
                    "0"
                  ],
                  [
                    "2",
                    "`\"5\"`",
                    "`false`",
                    "0"
                  ],
                  [
                    "3",
                    "`\"5\"`",
                    "`false`",
                    "0"
                  ],
                  [
                    "4",
                    "`\"-\"`",
                    "`true`",
                    "1"
                  ],
                  [
                    "5 to 7",
                    "`\"8\"`, `\"6\"`, `\"7\"`",
                    "`false`",
                    "1"
                  ],
                  [
                    "8",
                    "`\"-\"`",
                    "`true`",
                    "2"
                  ],
                  [
                    "9 to 12",
                    "`\"5\"`, `\"3\"`, `\"0\"`, `\"9\"`",
                    "`false`",
                    "2"
                  ]
                ]
              }
            },
            {
              "p": "Twelve iterations, one for each character. Compare the two headers. The `for` loop\nneeds three parts, `let i = 0`, `i < phone.length` and `i++`, and each of them\nhas to be right: start at 1 and the first character is skipped; write `<=`\nand the last iteration reads past the end. `for…of` has none of those parts. It\nstarts at the first character, and it ends when the characters run out, so it\ncannot start late, stop one short or run one iteration too many."
            },
            {
              "p": "The loop variable is declared with `const`. That works because each iteration\ngets a new `ch`, and nothing in the body changes it. `let ch` is legal too. This\ncourse writes `const`, because it says that the body only reads `ch`. A `for`\nloop's loop variable has to be `let`, because `i++` changes it."
            },
            {
              "p": "**A second example.** Turn a word round:"
            },
            {
              "code": "const word = \"stressed\";\nlet reversed = \"\";\nfor (const ch of word) {\n  reversed = ch + reversed;    // put each one in front\n}\nconsole.log(reversed);         // desserts",
              "lang": "js",
              "file": "reverse.js"
            },
            {
              "p": "Each iteration puts its character **in front** of what has been built so far.\nAfter the first iteration `reversed` is `\"s\"`, after the second `\"ts\"`, after the third\n`\"rts\"`, and so on until the eighth character."
            },
            {
              "p": "A string with no characters, `\"\"`, gives a `for…of` loop no iterations at all:\nthe body never runs, and the result keeps the value it started with."
            },
            {
              "p": "`for…of` is not only for strings. In the Arrays module it walks a list, one\nitem per iteration, in exactly the same way."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let out = \"\";\nfor (const ch of \"abc\") {\n  out = out + ch + ch;\n}\nconsole.log(out);\n",
                    "accept": [
                      "aabbcc",
                      "\"aabbcc\""
                    ],
                    "miss": "**Each iteration adds its character twice, after what is already there.** `\"aa\"`, then `\"aabb\"`, then `\"aabbcc\"`.\n",
                    "why": "`aabbcc`.\n"
                  },
                  {
                    "question": "`for (const ch of \"hi there\")` — how many iterations does the loop run? Type the number.",
                    "accept": [
                      "8",
                      "eight"
                    ],
                    "miss": "**If you answered `7` — the space is a character too.** `\"hi there\"` has a length of 8, so the loop runs 8 iterations.\n",
                    "why": "`8`. One iteration per character, the space included.\n"
                  },
                  {
                    "question": "In `for (const ch of \"cat\")`, what does `ch` hold on the first iteration?",
                    "choices": [
                      "\"c\"",
                      "0",
                      "\"cat\""
                    ],
                    "accept": [
                      "\"c\""
                    ],
                    "miss": "**`for…of` hands you characters, not positions.** `0` is the index of the first character; a `for` loop would give you that. And `\"cat\"` is the whole string, which the loop walks one character at a time.\n",
                    "why": "`\"c\"`, then `\"a\"`, then `\"t\"`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "From a sentence to a for…of loop",
          "body": [
            {
              "p": "So far you have read finished loops. Exercises and the homework start from\nthe other end: a sentence describing what a function returns, and the\nfunction to fill in. Writing it takes four subgoals, in order:"
            },
            {
              "list": [
                "**Start the result.** What does it hold before any character is seen? `0` for a count, `\"\"` for text.",
                "**Take each character in turn.** Each iteration needs one character and not its position, so this is `for (const ch of …)`.",
                "**Do one iteration's work.** What does one character add to the result?",
                "**Send the result out** with `return`, as in *Writing a function's answer*."
              ],
              "ordered": true
            },
            {
              "p": "**Example 1.** \"`withoutSpaces` takes a name typed with spaces in it, such as\n`\"Mary Ann\"`, and returns the name with the spaces left out: `\"MaryAnn\"`.\""
            },
            {
              "p": "Here is a first try, written by someone who has just learned the `for` loop.\nIn a `for` loop, `text[i]` reads the character at index `i`, and the habit\ncarries over: the first try reads `name[ch]`. It is shown so that you can\nsee what that mistake does."
            },
            {
              "code": "function withoutSpaces(name) {\n  let result = \"\";\n  for (const ch of name) {\n    if (name[ch] !== \" \") {\n      result += name[ch];\n    }\n  }\n  return result;\n}",
              "lang": "js",
              "file": "name.js"
            },
            {
              "p": "The code runs without an error. The tests reported:"
            },
            {
              "code": "✗ withoutSpaces(\"Mary Ann\") → \"MaryAnn\"\n✗ withoutSpaces(\"a b\") → \"ab\"\n✓ withoutSpaces(\"\") → \"\"",
              "lang": "text"
            },
            {
              "p": "`withoutSpaces(\"a b\")` returned `\"undefinedundefinedundefined\"`: the word\n`undefined`, three times. Trace it, with `name` holding `\"a b\"`:"
            },
            {
              "table": {
                "head": [
                  "Iteration",
                  "`ch`",
                  "`name[ch]`",
                  "`result` afterwards"
                ],
                "rows": [
                  [
                    "1",
                    "`\"a\"`",
                    "`undefined`",
                    "`\"undefined\"`"
                  ],
                  [
                    "2",
                    "`\" \"`",
                    "`undefined`",
                    "`\"undefinedundefined\"`"
                  ],
                  [
                    "3",
                    "`\"b\"`",
                    "`undefined`",
                    "`\"undefinedundefinedundefined\"`"
                  ]
                ]
              }
            },
            {
              "p": "`ch` holds a character, so `name[ch]` asks for the character at a position\ncalled `\"a\"`, then `\" \"`, then `\"b\"`. A string's positions are 0, 1 and 2, so\neach of those is `undefined`. `undefined` is never equal to a space, so the\n`if` lets every one through, and adding `undefined` to a string adds the word.\nThe space is never left out."
            },
            {
              "p": "`for…of` has already read the character for you: it is `ch`. Reading it\nagain with `name[ch]` treats the character as a position. The test with `\"\"`\npassed only because an empty string gives the loop no iterations. Here is the\nfunction using `ch` itself:"
            },
            {
              "code": "function withoutSpaces(name) {\n  // start the result\n  let result = \"\";\n  // take each character in turn\n  for (const ch of name) {\n    // do one iteration's work\n    if (ch !== \" \") {\n      result += ch;\n    }\n  }\n  // send the result out\n  return result;\n}",
              "lang": "js",
              "file": "name.js"
            },
            {
              "code": "✓ withoutSpaces(\"Mary Ann\") → \"MaryAnn\"\n✓ withoutSpaces(\"a b\") → \"ab\"\n✓ withoutSpaces(\"\") → \"\"",
              "lang": "text"
            },
            {
              "p": "**Example 2: you choose the line.** \"`masked` takes a password and returns one\n`*` for each of its characters: `masked(\"cat5\")` is `\"****\"`.\" One line is\nmissing, marked `TODO`."
            },
            {
              "code": "function masked(password) {\n  let hidden = \"\";\n  for (const ch of password) {\n    // TODO\n  }\n  return hidden;\n}",
              "lang": "js",
              "file": "masked.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes at the TODO?",
                    "choices": [
                      "hidden += ch;",
                      "hidden += \"*\";",
                      "hidden = \"*\";"
                    ],
                    "accept": [
                      "hidden += \"*\";"
                    ],
                    "miss": "**If you chose `hidden += ch;` — that adds the password's own character,** so `masked(\"cat5\")` would return `\"cat5\"`. **If you chose `hidden = \"*\";` — `=` replaces what `hidden` held,** so every iteration leaves it at a single `\"*\"`.\n",
                    "why": "`hidden += \"*\";`. Each character adds one `*`, and the character itself is never used.\n"
                  },
                  {
                    "question": "`word` holds `\"hey\"`. In `for (const ch of word)`, what is `word[ch]` on the first iteration?",
                    "choices": [
                      "\"h\"",
                      "undefined",
                      "\"e\""
                    ],
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**On the first iteration `ch` is `\"h\"`, the character, not a position.** `word[\"h\"]` asks for the character at position `\"h\"`, and a string has no such position.\n",
                    "why": "`undefined`. The character is `ch` itself; there is no need to look it up in `word`.\n"
                  },
                  {
                    "question": "What does `masked(\"\")` return? Type it, with its quotes.",
                    "accept": [
                      "\"\"",
                      "''",
                      "an empty string",
                      "empty string"
                    ],
                    "miss": "**An empty string gives `for…of` no iterations.** The body never runs, so `hidden` keeps the value it started with.\n",
                    "why": "`\"\"`, an empty string.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "When an iteration needs the position",
          "body": [
            {
              "p": "`for…of` hands you the character, never its position. For counting, copying\nand turning round, that is all an iteration needs. Some tasks need more."
            },
            {
              "p": "**Example 1.** \"`doubledLetters` takes a word and counts the places where a\nletter is the same as the letter before it: `doubledLetters(\"bookkeeper\")` is\n3, for the `oo`, the `kk` and the `ee`.\""
            },
            {
              "p": "An iteration that looks at one character cannot answer this. It has to compare that\ncharacter with its **neighbour**, the one before it, and to reach the one\nbefore it, the iteration needs to know where it is. A `for` loop has the position\nin `i`, so the character before is `word[i - 1]`:"
            },
            {
              "code": "function doubledLetters(word) {\n  let count = 0;\n  // each position that has a letter before it\n  for (let i = 1; i < word.length; i++) {\n    if (word[i] === word[i - 1]) {\n      count++;\n    }\n  }\n  return count;\n}",
              "lang": "js",
              "file": "doubled.js"
            },
            {
              "p": "`i` starts at 1, not 0, because the first letter has no letter before it to\ncompare with. With `word` holding `\"moon\"`, the iterations compare `o` with `m`,\n`o` with `o` (a match), and `n` with `o`, so `doubledLetters(\"moon\")` is 1."
            },
            {
              "p": "**Example 2.** \"`everySecond` takes a code and returns every second character,\nstarting with the first: `everySecond(\"a1b2c3\")` is `\"abc\"`.\""
            },
            {
              "code": "function everySecond(code) {\n  let result = \"\";\n  // every second position\n  for (let i = 0; i < code.length; i += 2) {\n    result += code[i];\n  }\n  return result;\n}",
              "lang": "js",
              "file": "every-second.js"
            },
            {
              "p": "`for…of` gives you every character, one after another, and cannot skip ahead.\nA `for` loop controls its own counter, so `i += 2` moves it two places at a\ntime: `i` is 0, 2 and 4."
            },
            {
              "p": "Both examples needed something `for…of` does not have. In general,\n**an iteration needs the position when it looks at a neighbour, when it skips\ncharacters, or when what it does depends on where it is**, such as treating\nthe first or last character differently. Then a `for` loop fits. A task whose\nanswer *is* a position, such as \"where is the first space?\", needs it too:\n*Leaving a loop early: break and continue* writes that kind of loop."
            },
            {
              "p": "You will also see a counter kept beside a `for…of` loop:"
            },
            {
              "code": "let i = 0;\nfor (const ch of \"cat\") {\n  console.log(i, ch);    // 0 c, then 1 a, then 2 t\n  i++;\n}",
              "lang": "js",
              "file": "position-beside.js"
            },
            {
              "p": "That works, and it is legal. It brings back the counter `for…of` took away,\nbut spread over three places: the `let` above the loop, the use in the body,\nand the `i++` at the bottom, which is easy to forget or to put in the wrong\nplace. A `for` header keeps all three on one line. This course writes a `for`\nloop when an iteration needs the position, because the header shows at a glance\nwhere the loop starts, where it stops and how it moves."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which task needs the position of each character?",
                    "choices": [
                      "Count the capital letters in a name",
                      "Take every third character of a code",
                      "Turn a word round"
                    ],
                    "accept": [
                      "Take every third character of a code"
                    ],
                    "miss": "**Counting capitals and turning a word round need each character, and nothing else**, so `for…of` does both. Taking every third character means skipping two after each one taken, and a `for` loop's update skips directly: `i += 3`.\n",
                    "why": "Every third character: `for (let i = 0; i < code.length; i += 3)`.\n"
                  },
                  {
                    "question": "In `doubledLetters`, why does `i` start at 1?",
                    "choices": [
                      "The first letter has no letter before it to compare with",
                      "Indexes start at 1",
                      "The first letter never counts as a doubled letter, so it is skipped to save an iteration"
                    ],
                    "accept": [
                      "The first letter has no letter before it to compare with"
                    ],
                    "miss": "**Indexes start at 0**, so starting at 1 is a choice here, not the rule. Each iteration compares `word[i]` with `word[i - 1]`; with `i` at 0 that would be `word[-1]`, a character that does not exist.\n",
                    "why": "Each iteration compares a letter with the one before it, and the first letter has none.\n"
                  },
                  {
                    "code": "const code = \"a1b2c3\";\nlet out = \"\";\nfor (let i = 1; i < code.length; i += 2) {\n  out += code[i];\n}\nconsole.log(out);\n",
                    "accept": [
                      "123",
                      "\"123\""
                    ],
                    "miss": "**This loop starts at index 1, not 0.** `i` is 1, 3 and 5, and those indexes hold the digits.\n",
                    "why": "`123`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`for…of` hands you each character in turn,** from the first to the last, with no index to start, check or move, so it cannot start late, stop short or run one iteration too many.",
                "**`ch` already is the character.** Don't look it up in the string: `text[ch]` treats the character as a position, and gives `undefined`.",
                "**Build a result from a string in four subgoals:** start the result, take each character in turn, do one iteration's work, send the result out.",
                "**When an iteration needs the position** (a neighbour, a skip, or doing something different at one place), use a `for` loop: `for…of` never gives you one."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "for...of",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for...of",
              "note": "its example \"Iterating over a string\"; the arrays come in the Arrays module."
            }
          ]
        }
      ],
      "_source": "lessons/for-of.md",
      "practice": {
        "id": "for-of",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "What each iteration hands you",
            "prompt": "Type what each loop prints, or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "let out = \"\";\nfor (const ch of \"tea\") {\n  out = out + ch + \"-\";\n}\nconsole.log(out);\n",
                "accept": [
                  "t-e-a-",
                  "\"t-e-a-\""
                ],
                "miss": "**Each iteration adds its character and then a dash, after what is already there.** `\"t-\"`, then `\"t-e-\"`, then `\"t-e-a-\"`.\n",
                "why": "`t-e-a-`.\n"
              },
              {
                "id": "p2",
                "question": "In `for (const ch of \"dog\")`, what does `ch` hold on the second iteration?",
                "choices": [
                  "\"o\"",
                  "1",
                  "\"do\""
                ],
                "accept": [
                  "\"o\""
                ],
                "miss": "**`for…of` hands you the character, not its position.** `1` is the index of the second character. And `ch` holds one character at a time, never the characters so far.\n",
                "why": "`\"o\"`.\n"
              },
              {
                "id": "p3",
                "question": "A function counts the spaces in `text` with `for (const ch of text)`. Which `if` goes inside the loop?",
                "choices": [
                  "if (ch === \" \") {",
                  "if (text[ch] === \" \") {",
                  "if (text[i] === \" \") {"
                ],
                "accept": [
                  "if (ch === \" \") {"
                ],
                "miss": "**If you chose `text[ch]` — `ch` is already the character.** `text[ch]` uses it as a position, such as `text[\"a\"]`, which is `undefined`, so no space is ever counted. **If you chose `text[i]` — a `for…of` loop has no `i`**, and the call stops with `ReferenceError: i is not defined`.\n",
                "why": "`if (ch === \" \") {`. Each iteration already holds its character in `ch`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "`for (const ch of \"a b c\")` — how many iterations does the loop run? Type the number.",
                "accept": [
                  "5",
                  "five"
                ],
                "miss": "**The spaces are characters too.** `a`, space, `b`, space, `c`.\n",
                "why": "`5`.\n"
              },
              {
                "id": "p5",
                "code": "let total = 0;\nfor (const ch of \"123\") {\n  total += ch;\n}\nconsole.log(total);\n",
                "accept": [
                  "0123",
                  "\"0123\""
                ],
                "miss": "**`ch` holds a character, and a character is a string.** On the first iteration `ch` is `\"1\"`, not the number 1, and `0 + \"1\"` joins them: `\"01\"`. Then `\"012\"`, then `\"0123\"`.\n",
                "why": "`0123`, a string. `total` never holds 6.\n"
              },
              {
                "id": "p6",
                "code": "let s = \"x\";\nfor (const ch of \"\") {\n  s += \"!\";\n}\nconsole.log(s);\n",
                "accept": [
                  "x",
                  "\"x\""
                ],
                "miss": "**An empty string has no characters, so the loop runs no iterations.** `s` is never changed.\n",
                "why": "`x`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered with a number where a character was asked for",
                "say": "A `for` loop's `i` is a number. What does the word after `const` in a `for…of` header hold?\n"
              },
              {
                "when": "you counted one iteration too few",
                "say": "Count every character in the string between the quotes, spaces and punctuation included.\n"
              },
              {
                "when": "you chose text[ch]",
                "say": "On the first iteration of `for (const ch of \"a b\")`, what does `ch` hold? Is that a position in the string?\n"
              },
              {
                "when": "you answered 6 for the \"123\" loop",
                "say": "What type is `ch`: a number or a string? What does `+` do when one side is a string?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Three loops, run",
            "prompt": "Predict each line this program prints, then run it and compare.\n",
            "filename": "walk.js",
            "code": "let capitals = 0;\nfor (const ch of \"Hello, World\") {\n  if (ch >= \"A\" && ch <= \"Z\") {\n    capitals++;\n  }\n}\nconsole.log(capitals);\n\nlet backwards = \"\";\nfor (const ch of \"level up\") {\n  backwards = ch + backwards;\n}\nconsole.log(backwards);\n\nconst word = \"abc\";\nlet pairs = \"\";\nlet i = 0;\nfor (const ch of word) {\n  pairs += ch + word[i + 1] + \",\";\n  i++;\n}\nconsole.log(pairs);\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print (`capitals`)? Type the number.",
                "accept": [
                  "2"
                ],
                "miss": "**Only `H` and `W` are from `\"A\"` to `\"Z\"`.** The comma and the space are characters, but not capitals.\n",
                "why": "`2`.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print (`backwards`)? Type it.",
                "accept": [
                  "pu level",
                  "\"pu level\""
                ],
                "miss": "**Each character goes in front of what is already there**, the space included. The last character, `p`, ends up first.\n",
                "why": "`pu level`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print (`pairs`)? Type it.",
                "accept": [
                  "ab,bc,cundefined,",
                  "\"ab,bc,cundefined,\""
                ],
                "miss": "**On the last iteration `ch` is `\"c\"` and `i` is 2, so `word[i + 1]` is `word[3]`.** `\"abc\"` has positions 0 to 2, so `word[3]` is `undefined`, and joining it onto a string adds the word.\n",
                "why": "`ab,bc,cundefined,`. A counter kept beside `for…of` runs to the last character, and the next one is past the end. A `for` loop that stops at `word.length - 1` would not.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected 0 or 3 capitals",
                "say": "Go through `\"Hello, World\"` one character at a time and ask, for each, whether it is from `\"A\"` to `\"Z\"`.\n"
              },
              {
                "when": "you expected \"pulevel\" or \"level up\"",
                "say": "Write `backwards` after each of the first three iterations. Where does each new character go, and is the space a character?\n"
              },
              {
                "when": "you expected \"ab,bc,\" with nothing for the c",
                "say": "`for…of` runs one iteration for every character, the last one too. On that iteration, what is `i + 1`, and is there a character there?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Character by character, position by position",
            "prompt": "Work each one through iteration by iteration, writing down what the result holds after each iteration, and type what it prints.\n",
            "items": [
              {
                "id": "t1",
                "code": "let out = \"\";\nfor (const ch of \"to be\") {\n  out += ch === \" \" ? \"_\" : ch;\n}\nconsole.log(out);\n",
                "accept": [
                  "to_be",
                  "\"to_be\""
                ],
                "miss": "**The ternary operator chooses one of two values on each iteration:** `\"_\"` when `ch` is a space, `ch` itself when it is not.\n",
                "why": "`to_be`.\n"
              },
              {
                "id": "t2",
                "code": "const word = \"abcdef\";\nlet out = \"\";\nfor (let i = 1; i < word.length; i += 2) {\n  out += word[i];\n}\nconsole.log(out);\n",
                "accept": [
                  "bdf",
                  "\"bdf\""
                ],
                "miss": "**`i` starts at 1 and moves by 2:** 1, 3, 5. Those indexes hold `b`, `d` and `f`; at 7 the condition is `false`.\n",
                "why": "`bdf`.\n"
              },
              {
                "id": "t3",
                "code": "const s = \"aabccc\";\nlet n = 0;\nfor (let i = 1; i < s.length; i++) {\n  if (s[i] === s[i - 1]) {\n    n++;\n  }\n}\nconsole.log(n);\n",
                "accept": [
                  "3"
                ],
                "miss": "**Each iteration compares a character with the one before it.** `a`/`a` match, `b`/`a` and `c`/`b` do not, and `c`/`c` match twice.\n",
                "why": "`3`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "let i = 0;\nlet out = \"\";\nfor (const ch of \"cat\") {\n  out += ch + i;\n  i++;\n}\nconsole.log(out);\n",
                "accept": [
                  "c0a1t2",
                  "\"c0a1t2\""
                ],
                "miss": "**`i` is a counter kept beside the loop.** It is 0 on the first iteration, and `i++` at the bottom moves it on for the next.\n",
                "why": "`c0a1t2`.\n"
              },
              {
                "id": "t5",
                "code": "const w = \"abc\";\nlet out = \"\";\nfor (let i = w.length - 1; i >= 0; i--) {\n  out += w[i];\n}\nconsole.log(out);\n",
                "accept": [
                  "cba",
                  "\"cba\""
                ],
                "miss": "**This loop counts down, from the last index to 0:** 2, 1, 0.\n",
                "why": "`cba`. A `for` loop can walk a string backwards; `for…of` always goes from the first character to the last.\n"
              }
            ],
            "hints": [
              {
                "when": "your answer to the to_be loop kept the space",
                "say": "The ternary operator tests `ch === \" \"`. On the iteration where that is `true`, which value does it choose?\n"
              },
              {
                "when": "you started the abcdef loop at \"a\"",
                "say": "Read where `i` starts. Which character is at that index?\n"
              },
              {
                "when": "your count for \"aabccc\" was 2 or 4",
                "say": "Write each iteration on its own line: `i`, `s[i]`, `s[i - 1]`, and whether they match. There are five iterations.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Count the digits",
            "prompt": "`countDigits` should return how many characters of `text` are digits, from `\"0\"` to `\"9\"`: `countDigits(\"Flat 4B, 221 Baker St\")` is 4. Build the lines that go between its braces, in order. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "  let count = 0;",
              "  for (const ch of text) {",
              "    if (ch >= \"0\" && ch <= \"9\") {",
              "      count++;",
              "    }",
              "  }",
              "  return count;"
            ],
            "distractors": [
              {
                "line": "    if (text[ch] >= \"0\" && text[ch] <= \"9\") {",
                "why": "`ch` is already the character. `text[ch]` uses it as a position: for the `F` of `\"Flat\"` it is `text[\"F\"]`, which is `undefined`.\n"
              },
              {
                "line": "      count += ch;",
                "why": "`ch` is a character, a string such as `\"4\"`, not the number 4. `count += ch` joins it onto `count` as text, and the function returns `\"04221\"` instead of 4.\n"
              }
            ],
            "why": "Start the count at 0, take each character in turn, and count it when it is from `\"0\"` to `\"9\"`. Send the count out after the loop.\n",
            "hints": [
              {
                "when": "you are unsure whether to use count++ or count += ch",
                "say": "The answer is how many digits there are. When the loop finds the `4`, should the count grow by 1 or by `\"4\"`?\n"
              },
              {
                "when": "you are unsure which if line is right",
                "say": "Under `for…of`, `ch` is the character itself. Does it need to be looked up in `text` again?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Show the first and last",
            "prompt": "`masked` returns one `*` for each character of a password. Change it so the first and last characters show and only the middle is hidden: `masked(\"secret\")` should be `\"s****t\"`, and a password of one or two characters is shown whole. Write your code between the braces, and keep `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: why did the loop have to change?\n",
            "exportName": "masked",
            "filename": "masked.js",
            "starter": "function masked(password) {\n  // TODO: put your code here\n  let hidden = \"\";\n  for (const ch of password) {\n    hidden += \"*\";\n  }\n  return hidden;\n}\n",
            "tests": [
              {
                "label": "masked(\"secret\") → \"s****t\"",
                "body": "return subject('secret') === 's****t';"
              },
              {
                "label": "masked(\"sense\") → \"s***e\"",
                "body": "return subject('sense') === 's***e';"
              },
              {
                "label": "masked(\"ab\") → \"ab\"",
                "body": "return subject('ab') === 'ab';"
              },
              {
                "label": "masked(\"x\") → \"x\"",
                "body": "return subject('x') === 'x';"
              },
              {
                "label": "masked(\"\") → \"\"",
                "body": "return subject('') === '';"
              }
            ],
            "hints": [
              {
                "when": "masked(\"secret\") gives \"s*****\"",
                "say": "The first character shows; the last does not. What is the index of the last character of a string, in terms of its `.length`?\n"
              },
              {
                "when": "masked(\"sense\") gives \"se*se\" or another mix of letters",
                "say": "Your loop compares each character with the first and last *characters*, so every `s` and every `e` shows. The task is about *where* a character is, not what it is. Which loop tells each iteration where it is?\n"
              },
              {
                "when": "masked(\"secret\") gives \"******\" or \"secret\"",
                "say": "The loop has to treat two places differently from the rest: index 0, and the last index. A `for` loop has the index in `i`.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Initials",
            "prompt": "`initials` takes a name and returns its capital letters, in order: `initials(\"Ada King Lovelace\")` is `\"AKL\"`. A capital letter is a character from `\"A\"` to `\"Z\"`. Write your code between the braces, and put `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: why does `for…of` fit this task?\n",
            "exportName": "initials",
            "filename": "initials.js",
            "starter": "function initials(name) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "initials(\"Ada King Lovelace\") → \"AKL\"",
                "body": "return subject('Ada King Lovelace') === 'AKL';"
              },
              {
                "label": "initials(\"Zora Neale Hurston\") → \"ZNH\"",
                "body": "return subject('Zora Neale Hurston') === 'ZNH';"
              },
              {
                "label": "initials(\"grace hopper\") → \"\"",
                "body": "return subject('grace hopper') === '';"
              },
              {
                "label": "initials(\"\") → \"\"",
                "body": "return subject('') === '';"
              }
            ],
            "hints": [
              {
                "when": "initials(\"Ada King Lovelace\") gives \"A K L\"",
                "say": "The spaces came through. A space is not lowercase, but it is not a capital either. Test that the character is from `\"A\"` to `\"Z\"`.\n"
              },
              {
                "when": "initials(\"Zora Neale Hurston\") gives \"NH\"",
                "say": "`Z` was left out. Does your test let in the ends of the range, `\"A\"` and `\"Z\"` themselves?\n"
              },
              {
                "when": "initials(\"Ada King Lovelace\") gives \"\" or undefined",
                "say": "Check three things: the test uses `ch` itself, not `name[ch]`; the result is added to inside the loop; and the result is returned after the loop.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a character count",
            "prompt": "A code assistant wrote `countChar`, which should return how many times the character `target` appears in `text`. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "count.js",
            "code": "// How many times target appears in text.\nfunction countChar(text, target) {\n  let count = 0;\n  for (const ch of text) {\n    if (text[ch] === target) {\n      count + 1;\n    }\n  }\n  return count;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `countChar(\"banana\", \"a\")`. Type the number it returns.",
                "accept": [
                  "0"
                ],
                "miss": "**The `if` never compares `ch` itself.** On the first iteration `ch` is `\"b\"`, and `text[\"b\"]` is `undefined`, never `\"a\"`. Every iteration is the same, so `count` stays 0.\n",
                "why": "`0`.\n"
              },
              {
                "id": "c2",
                "question": "Which line treats the character as a position? Type the line number.",
                "accept": [
                  "5",
                  "line 5"
                ],
                "why": "Line 5. `ch` is already the character, so it should be `if (ch === target) {`.\n"
              },
              {
                "id": "c3",
                "question": "With line 5 fixed, `countChar(\"banana\", \"a\")` still returns `0`. Which line is wrong now? Type the line number.",
                "accept": [
                  "6",
                  "line 6"
                ],
                "miss": "**The `if` is now `true` three times.** Look at what the line inside it does with `count`.\n",
                "why": "Line 6. `count + 1` works out a value and throws it away; `count` never changes. It should be `count++;` or `count += 1;`.\n"
              }
            ],
            "exportName": "countChar",
            "starter": "// How many times target appears in text.\nfunction countChar(text, target) {\n  let count = 0;\n  for (const ch of text) {\n    if (text[ch] === target) {\n      count + 1;\n    }\n  }\n  return count;\n}\n",
            "tests": [
              {
                "label": "countChar(\"banana\", \"a\") → 3",
                "body": "return subject('banana', 'a') === 3;"
              },
              {
                "label": "countChar(\"a-b-c\", \"-\") → 2",
                "body": "return subject('a-b-c', '-') === 2;"
              },
              {
                "label": "countChar(\"Mississippi\", \"s\") → 4",
                "body": "return subject('Mississippi', 's') === 4;"
              },
              {
                "label": "countChar(\"\", \"x\") → 0",
                "body": "return subject('', 'x') === 0;"
              }
            ],
            "hints": [
              {
                "when": "every count is 0",
                "say": "Fix one line at a time. What does `ch` hold on each iteration, and is the `if` comparing that? Then: does the line inside the `if` change `count`?\n"
              },
              {
                "when": "countChar(\"banana\", \"a\") gives 1",
                "say": "`count = 1` sets `count` to 1 on every match. The count should grow by one each time: add one to what `count` already holds.\n"
              }
            ]
          }
        ]
      }
    },
    "loop-forms": {
      "id": "loop-forms",
      "title": "Choosing a loop",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.loop-forms`, narrowed by D-066 to choosing among the language's loop forms by what each iteration needs, and defending the choice. It follows the four lessons that each teach one form, *The while loop*, *The for loop*, *The do…while loop* and *The for…of loop*, and comes before *Leaving a loop early: break and continue*, which took `break`, `continue` and the search (`flow.break-continue`, D-066, partly reversing D-015). Its concepts.md row lists every loop form and where each is taught; this lesson names the ones taught later in one sentence (D-066 item 2). Pass 1: primitives and strings (D-045). `do…while` is now a full lesson before this one, so it is the fourth choice here, not an aside. Procedural, explain first (D-061 item 1). The writing direction is modelled with a fail-first example (01-explanations rule 5a): a `for…of` chosen for a task that stops when a condition changes. The chooser method, table and examples moved from *Choosing a loop, and leaving early*; the comparison of one task in three forms, the fail-first example and the practice are new, derived from those rules, pending Nick's review.",
      "concepts": [
        "flow.loop-forms"
      ],
      "lede": "You can now write four loops: `while`, `for`, `do…while` and `for…of`. Any of them can be made to do the job of the others, but each fits one kind of job best, and the one that fits leaves you less to get wrong. This lesson shows how to choose by what each iteration needs, and how to defend the choice in one sentence.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The while loop · The for loop · The do…while loop · The for…of loop · Leaving a loop early: break and continue"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Iteration** — one run of the loop body. **Loop form** — one of the ways a language writes a loop: in this lesson, `while`, `for`, `do…while` and `for…of`. **Defending a choice** — saying what each iteration needs, and why the loop you picked hands over exactly that.",
          "entries": [
            "**Iteration** — one run of the loop body.",
            "**Loop form** — one of the ways a language writes a loop: in this lesson, `while`, `for`, `do…while` and `for…of`.",
            "**Defending a choice** — saying what each iteration needs, and why the loop you picked hands over exactly that."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions, one on each loop, from memory. Type the number, or press the\nanswer. Press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let n = 100;\nlet steps = 0;\nwhile (n >= 10) {\n  n = n / 2;\n  steps++;\n}\nsteps\n",
                    "accept": [
                      "4"
                    ],
                    "miss": "**The condition is checked before every iteration.** `n` is 100, 50, 25 and 12.5, each at least 10, so the body runs for each. After the fourth iteration, `n` is 6.25 and the loop ends.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "question": "How many iterations does `for (let i = 10; i > 0; i -= 3)` run? Type the number.",
                    "accept": [
                      "4",
                      "four"
                    ],
                    "miss": "**Write out the values of `i`.** 10, 7, 4 and 1 are all above 0; the next, -2, is not.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "code": "let x = 5;\nlet iterations = 0;\ndo {\n  iterations++;\n} while (x < 0);\niterations\n",
                    "accept": [
                      "1",
                      "one"
                    ],
                    "miss": "**If you answered `0` — a `do…while` checks after the body, not before.** The body runs once; then `5 < 0` is `false`, and the loop ends.\n",
                    "why": "`1`.\n"
                  },
                  {
                    "question": "In `for (const ch of \"go, go\")`, what does `ch` hold on the third iteration?",
                    "choices": [
                      "\",\"",
                      "2",
                      "\"g\""
                    ],
                    "accept": [
                      "\",\""
                    ],
                    "miss": "**`for…of` hands you characters, not positions.** The characters are `g`, `o`, then the comma.\n",
                    "why": "`\",\"`, the comma.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can choose among `while`, `for`, `do…while` and `for…of` by what each iteration needs",
                "I can defend the choice in one sentence: what each iteration needs, and why the loop I chose hands over exactly that",
                "I can spot a loop whose form does not fit its task, and name the input that shows it"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "One task, three loops",
          "body": [
            {
              "p": "Here is one task written three ways: count the spaces in `\"a cup of tea\"`."
            },
            {
              "code": "const sentence = \"a cup of tea\";\nlet spaces = 0;\nfor (const ch of sentence) {\n  if (ch === \" \") {\n    spaces++;\n  }\n}\n// spaces is 3",
              "lang": "js",
              "file": "spaces-of.js"
            },
            {
              "code": "const sentence = \"a cup of tea\";\nlet spaces = 0;\nfor (let i = 0; i < sentence.length; i++) {\n  if (sentence[i] === \" \") {\n    spaces++;\n  }\n}\n// spaces is 3",
              "lang": "js",
              "file": "spaces-for.js"
            },
            {
              "code": "const sentence = \"a cup of tea\";\nlet spaces = 0;\nlet i = 0;\nwhile (i < sentence.length) {\n  if (sentence[i] === \" \") {\n    spaces++;\n  }\n  i++;\n}\n// spaces is 3",
              "lang": "js",
              "file": "spaces-while.js"
            },
            {
              "p": "All three are correct, and all three find 3. What differs is how much each\none asks you to write, and so how much each one lets you get wrong:"
            },
            {
              "list": [
                "**`for…of`** walks the string for you. There is no index, so there is no start, end or step to get wrong.",
                "**`for`** needs an index, `i`, and three parts in its header: where `i` starts, the check that stops it and the update that moves it. Start at 1, or write `<=`, and the count is off by one.",
                "**`while`** needs the same three parts, in three different places: `let i = 0` above the loop, the check at the top and `i++` at the bottom of the body. Leave out `i++` and `i` stays 0, so the condition never turns `false` and the loop never ends."
              ]
            },
            {
              "p": "Every iteration of this task needs one character, and nothing else. `for…of`\nhands over exactly that, which is why it fits best. A `do…while` could be\nmade to count the spaces too, but it runs its body once before it checks\nanything, and this task has nothing to do when the sentence is empty."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "In `spaces-while.js`, which line, if it were left out, would make the loop never end?",
                    "choices": [
                      "i++;",
                      "spaces++;",
                      "let spaces = 0;"
                    ],
                    "accept": [
                      "i++;"
                    ],
                    "miss": "**The loop ends when `i < sentence.length` turns `false`.** Only one line changes `i`. Without `spaces++` the count would be wrong, but the loop would still end.\n",
                    "why": "`i++;`. Without it, `i` is 0 on every iteration.\n"
                  },
                  {
                    "question": "Which of the three versions has no way to read past the end of the string, however its body is written?",
                    "choices": [
                      "spaces-of.js",
                      "spaces-for.js",
                      "spaces-while.js"
                    ],
                    "accept": [
                      "spaces-of.js"
                    ],
                    "miss": "**The `for` and `while` versions stop because of a check you write.** Write `i <= sentence.length` in either, and the last iteration reads `sentence[12]`, which is `undefined`.\n",
                    "why": "`spaces-of.js`. `for…of` ends when the characters run out.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Choosing by what each iteration needs",
          "body": [
            {
              "p": "To choose a loop, answer four questions, in this order. The comments at the\ntop of each example below answer them."
            },
            {
              "list": [
                "**Name what you have.** A string? A number? A count?",
                "**Name what each iteration needs.** A character? A position? A number that changes as the loop goes?",
                "**Ask whether the body must run before the check makes sense.** Usually it does not. When there is nothing to check until the body has run once, it does.",
                "**Pick the loop that hands each iteration exactly that.**"
              ],
              "ordered": true
            },
            {
              "p": "**Example 1.** \"Count the question marks in a message.\""
            },
            {
              "code": "// name what you have: a string\n// name what each iteration needs: one character, not its position\n// must the body run first? no: an empty message has nothing to count\n// pick the loop: for…of\nconst message = \"Did you? Really? Yes.\";\nlet marks = 0;\nfor (const ch of message) {\n  if (ch === \"?\") {\n    marks++;\n  }\n}\n// marks is 2",
              "lang": "js",
              "file": "questions.js"
            },
            {
              "p": "**Example 2.** \"Add up the odd numbers from 1 to 99.\""
            },
            {
              "code": "// name what you have: two numbers, 1 and 99\n// name what each iteration needs: the next odd number\n// must the body run first? no\n// pick the loop: for, because the start, end and step are known\nlet total = 0;\nfor (let n = 1; n <= 99; n += 2) {\n  total += n;\n}\n// total is 2500",
              "lang": "js",
              "file": "odd-sum.js"
            },
            {
              "p": "Before the loop starts, you know where the numbers start, where they stop\nand how they move. That is a `for` loop's header."
            },
            {
              "p": "**Example 3.** \"A ball bounces back to half the height it fell from. Dropped\nfrom 100 cm, how many bounces until a bounce rises less than 1 cm?\""
            },
            {
              "code": "// name what you have: a height, 100\n// name what each iteration needs: the height so far\n// must the body run first? no: the height can be checked first\n// pick the loop: while, because the number of bounces is not known in advance\nlet height = 100;\nlet bounces = 0;\nwhile (height >= 1) {\n  height = height / 2;\n  bounces++;\n}\n// bounces is 7",
              "lang": "js",
              "file": "bounce.js"
            },
            {
              "p": "How many iterations this loop runs depends on the numbers, and you find out\nonly by running it. A number of iterations you cannot know in advance, with a\ncondition that says when to stop, is the sign of a `while`. And if the ball\nstarts below 1 cm, the loop runs no iterations at all, which is right: no bounce\nwas counted."
            },
            {
              "p": "**Example 4.** \"Roll a die. While it shows a 6, roll again. How many rolls\nwere made?\""
            },
            {
              "code": "// name what you have: nothing yet; each iteration makes a roll\n// name what each iteration needs: a new roll\n// must the body run first? yes: there is no roll to check until one is made\n// pick the loop: do…while\nlet rolls = 0;\nlet roll;\ndo {\n  roll = Math.floor(Math.random() * 6) + 1;\n  rolls++;\n} while (roll === 6);\n// rolls is 1, unless the first roll was a 6",
              "lang": "js",
              "file": "dice.js"
            },
            {
              "p": "The condition, `roll === 6`, asks about a roll, and before the first iteration\nthere is none. A `while` would need a made-up first value, such as\n`let roll = 6;`, only to get into the loop. A `do…while` runs the body first,\nso the first roll is a real one."
            },
            {
              "p": "Here are the four examples, as a table:"
            },
            {
              "table": {
                "head": [
                  "The loop",
                  "Use it when",
                  "Each iteration gets",
                  "Iterations if there is nothing to do"
                ],
                "rows": [
                  [
                    "`for…of`",
                    "you want each character, and not its position",
                    "the next character",
                    "none"
                  ],
                  [
                    "`for` loop",
                    "you know the start, end and step, or you need the position",
                    "a number you control, `i`",
                    "none"
                  ],
                  [
                    "`while`",
                    "you stop when a condition changes, and cannot know the count in advance",
                    "nothing; you manage it",
                    "none"
                  ],
                  [
                    "`do…while`",
                    "as a `while`, but the body must run once before the check makes sense",
                    "nothing; you manage it",
                    "one"
                  ]
                ]
              }
            },
            {
              "p": "**Defending a choice** means saying what each iteration needs, and why the loop\nyou picked hands over exactly that: \"a `while`, because the number of\nbounces depends on the height, and the loop stops when the height drops below\n1\"."
            },
            {
              "p": "These four are the loops this module teaches. JavaScript has more ways to\nrepeat: `for…of` walks a list in the Arrays module, the `for…in` loop walks\nthe keys of an object in the Objects module, and array methods such as\n`forEach`, `map` and `filter` come in the *Functions as values* module."
            },
            {
              "p": "Press the loop that fits."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Add up the whole numbers from 1 to 100.",
                    "choices": [
                      "for",
                      "for…of",
                      "while",
                      "do…while"
                    ],
                    "accept": [
                      "for"
                    ],
                    "why": "A `for` loop. You know the start, the end and the step before you begin: 100 iterations.\n"
                  },
                  {
                    "question": "Count how many times the letter `s` appears in a word.",
                    "choices": [
                      "for",
                      "for…of",
                      "while",
                      "do…while"
                    ],
                    "accept": [
                      "for…of"
                    ],
                    "miss": "**Each iteration needs one character, and the position does not matter.** A `for` loop works, but it asks you to manage an index you never use.\n",
                    "why": "`for…of`.\n"
                  },
                  {
                    "question": "Halve a number until it is below 1, and count the halvings. A number already below 1 needs none.",
                    "choices": [
                      "for",
                      "for…of",
                      "while",
                      "do…while"
                    ],
                    "accept": [
                      "while"
                    ],
                    "miss": "**If you chose `do…while` — it would halve a number that is already below 1**, and count one halving too many. The condition can be checked before any halving, so a `while` checks first.\n",
                    "why": "`while`. There is no string to walk, and you cannot know the number of iterations until the condition says stop.\n"
                  },
                  {
                    "question": "Find the position of the first `@` in an email address.",
                    "choices": [
                      "for",
                      "for…of",
                      "while",
                      "do…while"
                    ],
                    "accept": [
                      "for"
                    ],
                    "miss": "**The answer is a position**, and `for…of` never tells you one. A `for` loop has it in `i`.\n",
                    "why": "A `for` loop. *Leaving a loop early: break and continue* writes this kind of loop.\n"
                  },
                  {
                    "question": "Count the digits of a whole number, which may be 0.",
                    "choices": [
                      "for",
                      "for…of",
                      "while",
                      "do…while"
                    ],
                    "accept": [
                      "do…while"
                    ],
                    "miss": "**A number is not a string, so `for…of` cannot walk it** (`for…of` over a number stops with `TypeError: n is not iterable`). Counting the digits means taking one off with `Math.floor(n / 10)` until none are left, and even 0 has one digit, so the body must run at least once.\n",
                    "why": "`do…while`. A `while` checking `n > 0` would run no iterations for 0, and say it has no digits.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "When the form does not fit",
          "body": [
            {
              "p": "A loop in the wrong form often works on the first example you try, and fails\non another. This step shows one, and how working the failing input through\nby hand points to the loop that fits."
            },
            {
              "p": "**Example 1.** \"`leadingSpaces` takes a piece of text and returns how many\nspaces it starts with: `leadingSpaces(\"   indented\")` is 3, and\n`leadingSpaces(\"flush\")` is 0.\""
            },
            {
              "p": "Here is a common first try. The task is about spaces in a string, and\n`for…of` counts characters, so it reaches for `for…of`:"
            },
            {
              "code": "function leadingSpaces(text) {\n  let count = 0;\n  for (const ch of text) {\n    if (ch === \" \") {\n      count++;\n    }\n  }\n  return count;\n}",
              "lang": "js",
              "file": "leading.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✓ leadingSpaces(\"   indented\") → 3\n✓ leadingSpaces(\"flush\") → 0\n✗ leadingSpaces(\"  two words\") → 2",
              "lang": "text"
            },
            {
              "p": "The test that failed is `leadingSpaces(\"  two words\")`, and it returned 3.\nWork it through by hand, with `text` holding `\"  two words\"`. The first two\niterations see spaces, and `count` becomes 2. Then come `t`, `w` and `o`, which\nare not spaces. Then the space between the two words, and `count` becomes 3.\nThe loop carries on to the last character, because a `for…of` loop always\nwalks the whole string."
            },
            {
              "p": "The first two tests passed because in `\"   indented\"` and `\"flush\"` every\nspace is at the start. Go back to the second question of the four: what does\neach iteration need? It needs the character, and the loop must **stop at the first\ncharacter that is not a space**. A loop that stops when a condition changes\nis a `while`:"
            },
            {
              "code": "function leadingSpaces(text) {\n  // name what you have: text, a string\n  // name what each iteration needs: the next character, while it is a space\n  // must the body run first? no: \"flush\" needs no iterations\n  // pick the loop: while, which stops at the first character that is not a space\n  let count = 0;\n  while (count < text.length && text[count] === \" \") {\n    count++;\n  }\n  return count;\n}",
              "lang": "js",
              "file": "leading.js"
            },
            {
              "code": "✓ leadingSpaces(\"   indented\") → 3\n✓ leadingSpaces(\"flush\") → 0\n✓ leadingSpaces(\"  two words\") → 2",
              "lang": "text"
            },
            {
              "p": "`count` does two jobs here: it is the number of spaces found so far, and the\nindex of the next character to look at. The condition first checks that\n`count` is still inside the string, and then that the character there is a\nspace. At the `t` of `\"  two words\"` the condition is `false`, and the loop\nends with `count` at 2."
            },
            {
              "p": "The defence: \"a `while`, because the loop must stop at the first character\nthat is not a space, and a `while` stops when its condition turns `false`.\"\n*Leaving a loop early: break and continue*, next, shows another way: a\n`for…of` loop that stops itself partway."
            },
            {
              "p": "**Example 2: you choose the line.** \"`digitsAtStart` takes a code and returns\nhow many digits it starts with: `digitsAtStart(\"42B\")` is 2, and\n`digitsAtStart(\"B42\")` is 0.\" The loop's first line is missing, marked `TODO`."
            },
            {
              "code": "function digitsAtStart(code) {\n  // name what you have: code, a string\n  // name what each iteration needs: the next character, while it is a digit\n  let i = 0;\n  // TODO: pick the loop, and write its first line\n    i++;\n  }\n  return i;\n}",
              "lang": "js",
              "file": "digits.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes at the TODO?",
                    "choices": [
                      "while (i < code.length && code[i] >= \"0\" && code[i] <= \"9\") {",
                      "for (const ch of code) {",
                      "if (code[i] >= \"0\" && code[i] <= \"9\") {"
                    ],
                    "accept": [
                      "while (i < code.length && code[i] >= \"0\" && code[i] <= \"9\") {"
                    ],
                    "miss": "**If you chose the `for…of` — it walks the whole code and never looks at a character,** so `i` ends at the length: `digitsAtStart(\"42B\")` would be 3. **If you chose the `if` — an `if` runs its body at most once,** so the answer could never be more than 1.\n",
                    "why": "The `while`. It stops at the first character that is not a digit, or at the end of the code.\n"
                  },
                  {
                    "question": "Which of the three test inputs shows that the `for…of` version of `leadingSpaces` does not fit?",
                    "choices": [
                      "\"   indented\"",
                      "\"flush\"",
                      "\"  two words\""
                    ],
                    "accept": [
                      "\"  two words\""
                    ],
                    "miss": "**In `\"   indented\"` and `\"flush\"`, every space is at the start**, so counting all the spaces and counting the leading ones give the same answer.\n",
                    "why": "`\"  two words\"`. It has a space that is not at the start.\n"
                  },
                  {
                    "question": "Which sentence defends `for…of` for counting the question marks in a message?",
                    "choices": [
                      "Each iteration needs one character and not its position, and for…of hands over exactly that",
                      "for…of is shorter than the other loops",
                      "The message is a string, and strings need for…of"
                    ],
                    "accept": [
                      "Each iteration needs one character and not its position, and for…of hands over exactly that"
                    ],
                    "miss": "**A defence names what each iteration needs.** Being short is a result of the fit, not the reason. And a string does not always need `for…of`: `leadingSpaces` walks a string with a `while`.\n",
                    "why": "It says what each iteration needs, and why the loop hands over exactly that.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A sign-up form shows how many digits a phone number has. Three people write\nthe loop:"
            },
            {
              "list": [
                "**A** — a `for…of` that counts each character from `\"0\"` to `\"9\"`.",
                "**B** — a `for` loop over the indexes that does the same.",
                "**C** — a `while` with its own index that does the same."
              ]
            },
            {
              "p": "All three give the right answer. Choose one and defend it by what each iteration\nneeds. Then the form is changed: it must show the number with every digit\nhidden except the last four. Which designs have to change, and why? Last, a\nfourth person suggests a `do…while`. What would it do with an empty phone\nnumber, and does this task need that?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Any loop can be made to do the job; choose the one that hands each iteration exactly what it needs:** each character, `for…of`; a known start, end and step, or a position, `for`; a condition that says stop, `while`; a body that must run once before the check, `do…while`.",
                "**Defend a choice in one sentence:** what each iteration needs, and why the loop hands over exactly that.",
                "**A loop in the wrong form often passes the first test.** Try the input that would break it: a space that is not at the start, an empty string, a 0."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Loops and iteration",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Loops_and_iteration",
              "note": "every kind of loop, with examples."
            },
            {
              "title": "do...while",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/do...while",
              "note": "the loop that checks after its body."
            },
            {
              "title": "for...of",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for...of",
              "note": "its example \"Iterating over a string\"."
            }
          ]
        }
      ],
      "_source": "lessons/loop-forms.md",
      "practice": {
        "id": "loop-forms",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Which loop fits?",
            "prompt": "Press the loop that fits each task best, or type what the code prints.\n",
            "items": [
              {
                "id": "p1",
                "question": "Count the commas in a line of text.",
                "choices": [
                  "for",
                  "for…of",
                  "while",
                  "do…while"
                ],
                "accept": [
                  "for…of"
                ],
                "miss": "**Each iteration needs one character, and not its position.** A `for` loop or a `while` would work, but they make you manage an index the task never uses.\n",
                "why": "`for…of`.\n"
              },
              {
                "id": "p2",
                "question": "Count how many times 7 can be taken away from a number before it is below 7. A number already below 7 gives 0.",
                "choices": [
                  "for",
                  "for…of",
                  "while",
                  "do…while"
                ],
                "accept": [
                  "while"
                ],
                "miss": "**If you chose `do…while` — it would take 7 away once even from a number already below 7**, and count 1 instead of 0. **If you chose `for` — you do not know the number of iterations before the loop starts**; the condition finds it.\n",
                "why": "`while`. It stops when a condition changes, and can run no iterations.\n"
              },
              {
                "id": "p3",
                "question": "Return every third character of a code, starting with the first.",
                "choices": [
                  "for",
                  "for…of",
                  "while",
                  "do…while"
                ],
                "accept": [
                  "for"
                ],
                "miss": "**Each iteration skips two characters.** `for…of` hands over every character and cannot skip; a `for` loop's update can be `i += 3`.\n",
                "why": "A `for` loop.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "Build a whole number as text, one digit at a time from the right. 0 must come out as \"0\".",
                "choices": [
                  "for",
                  "for…of",
                  "while",
                  "do…while"
                ],
                "accept": [
                  "do…while"
                ],
                "miss": "**Every whole number has at least one digit, 0 included.** A `while` checking `n > 0` would run no iterations for 0 and build `\"\"`.\n",
                "why": "`do…while`. The body must run once before the check makes sense.\n"
              },
              {
                "id": "p5",
                "question": "Count the capital letters in a sentence.",
                "choices": [
                  "for",
                  "for…of",
                  "while",
                  "do…while"
                ],
                "accept": [
                  "for…of"
                ],
                "why": "`for…of`. Each iteration needs one character, and not its position.\n"
              },
              {
                "id": "p6",
                "code": "let n = 0;\nlet count = 0;\nwhile (n > 0) {\n  n = Math.floor(n / 10);\n  count++;\n}\nconsole.log(count);\n",
                "accept": [
                  "0"
                ],
                "miss": "**A `while` checks before the first iteration.** `0 > 0` is `false`, so the body never runs.\n",
                "why": "`0`. This is why counting digits wants a `do…while`: 0 has one digit.\n"
              }
            ],
            "hints": [
              {
                "when": "you chose for…of for a task that skips or needs positions",
                "say": "What does `for…of` hand each iteration? Does this task need anything more than the character?\n"
              },
              {
                "when": "you chose do…while where nothing has to happen first",
                "say": "Try the task with the input where there is nothing to do. How many iterations should the loop run then?\n"
              },
              {
                "when": "you chose for where the number of iterations is unknown",
                "say": "Could you write the last value of the counter in the header before the loop starts?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Four loops with nothing to do",
            "prompt": "Each loop below meets a case where there is nothing to do. Predict each line this program prints, then run it and compare.\n",
            "filename": "nothing.js",
            "code": "let n = 0;\nlet whileIterations = 0;\nwhile (n > 0) {\n  n = Math.floor(n / 10);\n  whileIterations++;\n}\nconsole.log(whileIterations);\n\nn = 0;\nlet doIterations = 0;\ndo {\n  n = Math.floor(n / 10);\n  doIterations++;\n} while (n > 0);\nconsole.log(doIterations);\n\nlet ofIterations = 0;\nfor (const ch of \"\") {\n  ofIterations++;\n}\nconsole.log(ofIterations);\n\nlet forIterations = 0;\nfor (let i = 5; i < 5; i++) {\n  forIterations++;\n}\nconsole.log(forIterations);\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print (`whileIterations`)? Type the number.",
                "accept": [
                  "0"
                ],
                "miss": "**A `while` checks before every iteration, the first included.** `0 > 0` is `false`.\n",
                "why": "`0`.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print (`doIterations`)? Type the number.",
                "accept": [
                  "1"
                ],
                "miss": "**A `do…while` runs its body, then checks.** One iteration, then `0 > 0` is `false`.\n",
                "why": "`1`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print (`ofIterations`)? Type the number.",
                "accept": [
                  "0"
                ],
                "miss": "**`\"\"` has no characters**, so `for…of` has nothing to hand over.\n",
                "why": "`0`.\n"
              },
              {
                "id": "r4",
                "question": "What does the fourth line print (`forIterations`)? Type the number.",
                "accept": [
                  "0"
                ],
                "miss": "**The check runs before the first iteration.** `i` starts at 5, and `5 < 5` is `false`.\n",
                "why": "`0`. Only the `do…while` ran its body when there was nothing to do.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected 1 from the while or the for",
                "say": "Where is each loop's check: before the body, or after it? Run the check once, with the starting values, before anything else.\n"
              },
              {
                "when": "you expected 0 from the do…while",
                "say": "Read the `do…while` from the top. What comes first, the body or `while (n > 0)`?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One loop in each form",
            "prompt": "Work each one through, one iteration at a time, and type what it prints.\n",
            "items": [
              {
                "id": "t1",
                "code": "let i = 0;\nlet out = \"\";\nfor (const ch of \"abcdef\") {\n  if (i % 2 === 0) {\n    out += ch;\n  }\n  i++;\n}\nconsole.log(out);\n",
                "accept": [
                  "ace",
                  "\"ace\""
                ],
                "miss": "**`i` is a counter kept beside the `for…of`.** It is 0 for `a`, 1 for `b`, and so on; the even ones are kept.\n",
                "why": "`ace`. It works, but a `for` loop with `i += 2` says the same thing in its header.\n"
              },
              {
                "id": "t2",
                "code": "const s = \"a,b,,c\";\nlet i = 0;\nlet commas = 0;\nwhile (i < s.length) {\n  if (s[i] === \",\") {\n    commas++;\n  }\n  i++;\n}\nconsole.log(commas);\n",
                "accept": [
                  "3"
                ],
                "miss": "**Two commas sit side by side,** and each is a character of its own.\n",
                "why": "`3`.\n"
              },
              {
                "id": "t3",
                "code": "const s = \"--a--\";\nlet i = 0;\nwhile (i < s.length && s[i] === \"-\") {\n  i++;\n}\nconsole.log(i);\n",
                "accept": [
                  "2"
                ],
                "miss": "**The loop stops at the first character that is not a dash.** At the `a`, `s[i] === \"-\"` is `false`, and the dashes after it are never reached.\n",
                "why": "`2`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "let count = 0;\nfor (let i = 10; i > 0; i -= 4) {\n  count++;\n}\nconsole.log(count);\n",
                "accept": [
                  "3"
                ],
                "miss": "**Write out the values of `i`:** 10, 6 and 2 make `i > 0` true; -2 does not.\n",
                "why": "`3`.\n"
              },
              {
                "id": "t5",
                "code": "let n = 50;\ndo {\n  n = n * 2;\n} while (n < 10);\nconsole.log(n);\n",
                "accept": [
                  "100"
                ],
                "miss": "**The body runs before the check.** `n` becomes 100; then `100 < 10` is `false`.\n",
                "why": "`100`. A `while (n < 10)` would have left `n` at 50.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 1 for \"--a--\" or counted all four dashes",
                "say": "The condition has two parts joined by logical AND. On which iteration does the second part turn `false`, and what happens to the loop then?\n"
              },
              {
                "when": "you answered \"abc\" or \"bdf\" for the first loop",
                "say": "Write `i` next to each character before its iteration. `i++` is at the bottom of the body, so `a` sees `i` at 0.\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "The first power of 2 above a limit",
            "prompt": "The powers of 2 are 1, 2, 4, 8, 16 and so on, each double the one before. `firstPowerOver` takes a limit and returns the first power of 2 that is greater than it: `firstPowerOver(100)` is 128, and `firstPowerOver(0)` is 1. The loop's body is written in the comment. Choose the loop and write it around that body, between the braces; the `return` is already there. Then, in one sentence: defend your choice of loop.\n",
            "exportName": "firstPowerOver",
            "filename": "powers.js",
            "starter": "function firstPowerOver(limit) {\n  let power = 1;\n  // TODO: put your loop here. Its body is\n  //   power = power * 2;\n  return power;\n}\n",
            "tests": [
              {
                "label": "firstPowerOver(100) → 128",
                "body": "return subject(100) === 128;"
              },
              {
                "label": "firstPowerOver(0) → 1",
                "body": "return subject(0) === 1;"
              },
              {
                "label": "firstPowerOver(1) → 2",
                "body": "return subject(1) === 2;"
              },
              {
                "label": "firstPowerOver(128) → 256",
                "body": "return subject(128) === 256;"
              },
              {
                "label": "firstPowerOver(-5) → 1",
                "body": "return subject(-5) === 1;"
              }
            ],
            "hints": [
              {
                "when": "firstPowerOver(0) gives 2",
                "say": "1 is already greater than 0, so the answer is 1 and no doubling is needed. Your loop doubled once before it checked anything. Which loop checks first?\n"
              },
              {
                "when": "firstPowerOver(128) gives 128, or firstPowerOver(1) gives 1",
                "say": "The answer must be *greater than* the limit. When `power` equals the limit, should the loop go round again?\n"
              },
              {
                "when": "the error says power is not iterable, or limit is not iterable",
                "say": "`for…of` walks the characters of a string. There is no string here: the loop stops when a condition about `power` changes.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Dots at the end",
            "prompt": "`trailingDots` takes a piece of text and returns how many dots it ends with: `trailingDots(\"wait...\")` is 3, `trailingDots(\"a.b.\")` is 1, and `trailingDots(\"done\")` is 0. Choose the loop. Write your code between the braces, and put `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: defend your choice of loop, and name the test that would catch a loop that counts every dot.\n",
            "exportName": "trailingDots",
            "filename": "dots.js",
            "starter": "function trailingDots(text) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "trailingDots(\"wait...\") → 3",
                "body": "return subject('wait...') === 3;"
              },
              {
                "label": "trailingDots(\"a.b.\") → 1",
                "body": "return subject('a.b.') === 1;"
              },
              {
                "label": "trailingDots(\"done\") → 0",
                "body": "return subject('done') === 0;"
              },
              {
                "label": "trailingDots(\"...\") → 3",
                "body": "return subject('...') === 3;"
              },
              {
                "label": "trailingDots(\"\") → 0",
                "body": "return subject('') === 0;"
              }
            ],
            "hints": [
              {
                "when": "trailingDots(\"a.b.\") gives 2",
                "say": "Your loop counted the dot after `a` as well. Only the dots at the end count: the loop has to stop at the first character, counting from the end, that is not a dot.\n"
              },
              {
                "when": "trailingDots(\"wait...\") gives 0",
                "say": "Your loop starts at the beginning of the text and stops at once, at the `w`. The dots are at the other end. Where should the index start, and which way should it move?\n"
              },
              {
                "when": "trailingDots(\"...\") gives 2",
                "say": "The loop stopped before it checked index 0. Should the condition let `i` reach 0?\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a space count",
            "prompt": "A code assistant wrote `countSpaces`, which should return how many spaces are in a piece of text. Read it, answer the questions, then fix it so the tests pass. Any loop that passes is a fix.\n",
            "filename": "spaces.js",
            "code": "// How many spaces are in text.\nfunction countSpaces(text) {\n  let count = 0;\n  let i = 1;\n  while (i < text.length) {\n    if (text[i] === \" \") {\n      count++;\n    }\n    i++;\n  }\n  return count;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `countSpaces(\" a b\")`. Type the number it returns.",
                "accept": [
                  "1"
                ],
                "miss": "**The loop starts at index 1**, so the space at index 0 is never looked at.\n",
                "why": "`1`, though the text has two spaces.\n"
              },
              {
                "id": "c2",
                "question": "Which line causes that? Type the line number.",
                "accept": [
                  "4",
                  "line 4"
                ],
                "why": "Line 4. The first index of a string is 0, so it should be `let i = 0;`.\n"
              },
              {
                "id": "c3",
                "question": "Which loop would make this mistake impossible, because it has no index to start?",
                "choices": [
                  "for…of",
                  "for",
                  "do…while"
                ],
                "accept": [
                  "for…of"
                ],
                "miss": "**A `for` loop and a `do…while` with an index both have a start you write**, and both can start at 1 by mistake.\n",
                "why": "`for…of`. Each iteration needs one character and not its position, so `for (const ch of text)` fits, and it always starts at the first character.\n"
              }
            ],
            "exportName": "countSpaces",
            "starter": "// How many spaces are in text.\nfunction countSpaces(text) {\n  let count = 0;\n  let i = 1;\n  while (i < text.length) {\n    if (text[i] === \" \") {\n      count++;\n    }\n    i++;\n  }\n  return count;\n}\n",
            "tests": [
              {
                "label": "countSpaces(\" a b\") → 2",
                "body": "return subject(' a b') === 2;"
              },
              {
                "label": "countSpaces(\"a b c\") → 2",
                "body": "return subject('a b c') === 2;"
              },
              {
                "label": "countSpaces(\"   \") → 3",
                "body": "return subject('   ') === 3;"
              },
              {
                "label": "countSpaces(\"\") → 0",
                "body": "return subject('') === 0;"
              }
            ],
            "hints": [
              {
                "when": "countSpaces(\" a b\") gives 1",
                "say": "The space at the very start is missed. Which index does the loop look at first?\n"
              },
              {
                "when": "the run never finishes",
                "say": "If you rewrote the `while`, check that `i++` is still in the body and runs on every iteration, not only inside the `if`.\n"
              }
            ]
          }
        ]
      }
    },
    "break-continue": {
      "id": "break-continue",
      "title": "Leaving a loop early: break and continue",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.break-continue` (concepts.md): leave a loop with `break`, skip the rest of one iteration with `continue`, say what each skips, and write a search that stops at its first match and has a value for not found. D-066 made it its own concept and lesson, partly reversing D-015, which had folded `break` and `continue` into `flow.loop-forms`; the old id `flow.early-exit` is not revived, because early `return` stays in `flow.flattening`. It follows *Choosing a loop*, and its characteristic mistake is reading `continue` as leaving the loop. Inside a `switch`, `break` leaves only the `switch` (concepts.md row), which builds on *Choosing among many paths*. Labelled `break` and `continue` belong to this concept (Nick, 2026-10-18): named here, shown at work in *Loops inside loops*, where nesting makes them matter. The `flow-loops` homework's `for` over a string with `break` rests on this lesson (D-061 item 4), so the search is taught in the writing direction (01-explanations rule 5a), failing first on a missing not-found path (D-061 item 2), with both return forms shown and neither enforced. Procedural (D-061 item 1). The steps, examples, questions and practice moved from *Choosing a loop, and leaving early*; the side-by-side trace, the fixed forms of the stuck `while`, and the new questions are derived from those rules, pending Nick's review.",
      "concepts": [
        "flow.break-continue"
      ],
      "lede": "Every loop so far has run until its condition turned false or its characters ran out. Two statements, written inside the loop body, change that: `break` ends the loop, and `continue` ends only the current iteration. This lesson shows what each one skips, two places where they surprise people, and the loop they matter most in: a search that stops at the first match, and says so when there is none.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The for…of loop · Choosing a loop · Choosing among many paths · Writing a function's answer · Loops inside loops"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Iteration** — one run of the loop body. **`break`** — a statement that ends the nearest loop or `switch` around it. **`continue`** — a statement that ends the current iteration of a loop; the loop goes on to its next iteration. **Search** — a loop that looks for the first value that matches and stops. **Not-found value** — what a search returns when nothing matches, such as `-1` or `null`. **Label** — a name and a colon before a loop, such as `outer:`, so that `break outer;` or `continue outer;` can name that loop.",
          "entries": [
            "**Iteration** — one run of the loop body.",
            "**`break`** — a statement that ends the nearest loop or `switch` around it.",
            "**`continue`** — a statement that ends the current iteration of a loop; the loop goes on to its next iteration.",
            "**Search** — a loop that looks for the first value that matches and stops.",
            "**Not-found value** — what a search returns when nothing matches, such as `-1` or `null`.",
            "**Label** — a name and a colon before a loop, such as `outer:`, so that `break outer;` or `continue outer;` can name that loop."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory. Type the value, or\npress the answer. Press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let out = \"\";\nfor (const ch of \"2+3\") {\n  out = ch + out;\n}\nconsole.log(out);\n",
                    "accept": [
                      "3+2",
                      "\"3+2\""
                    ],
                    "miss": "**Each iteration puts its character in front of what is there.** `\"2\"`, then `\"+2\"`, then `\"3+2\"`.\n",
                    "why": "`3+2`.\n"
                  },
                  {
                    "question": "In a `switch`, what does `break` at the end of a case do?",
                    "choices": [
                      "Ends the switch, so the next case does not run",
                      "Ends the whole program",
                      "Jumps to the default case"
                    ],
                    "accept": [
                      "Ends the switch, so the next case does not run"
                    ],
                    "why": "It ends the `switch`. Without `break`, the code falls through into the next case. This lesson uses the same `break` in a loop.\n"
                  },
                  {
                    "code": "function sign(n) {\n  if (n > 0) {\n    return \"positive\";\n  }\n}\nsign(-3)\n",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**The `if` is `false`, so its `return` never runs.** A function that reaches its closing brace without a `return` returns `undefined`.\n",
                    "why": "`undefined`.\n"
                  },
                  {
                    "question": "In `for (let i = 0; i < 5; i++)`, when does `i++` run?",
                    "choices": [
                      "After every iteration, before the next check",
                      "Before every iteration",
                      "Once, before the first iteration"
                    ],
                    "accept": [
                      "After every iteration, before the next check"
                    ],
                    "miss": "**`let i = 0` runs once, before the first iteration.** `i < 5` is checked before every iteration, and `i++` runs at the end of every iteration.\n",
                    "why": "After every iteration. Then the check runs again.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can leave a loop early with `break`, or skip the rest of one iteration with `continue`, and say what each one skips",
                "I can say what `break` does inside a `switch` inside a loop, and why `continue` can leave a `while` stuck",
                "I can write a search that stops at the first match and returns a not-found value when nothing matches"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Leaving early: break and continue",
          "body": [
            {
              "p": "Here are two loops over the same string. They differ in one word: `skip.js`\nuses `continue`, and `stop.js` uses `break`."
            },
            {
              "code": "for (const ch of \"a-b-c\") {\n  if (ch === \"-\") {\n    continue;      // skip the dash; go on to the next character\n  }\n  console.log(ch);\n}\n// a\n// b\n// c",
              "lang": "js",
              "file": "skip.js"
            },
            {
              "code": "for (const ch of \"a-b-c\") {\n  if (ch === \"-\") {\n    break;         // stop at the first dash; nothing after it is read\n  }\n  console.log(ch);\n}\n// a",
              "lang": "js",
              "file": "stop.js"
            },
            {
              "p": "Here they are running, one iteration at a time:"
            },
            {
              "table": {
                "head": [
                  "Iteration",
                  "`ch`",
                  "`skip.js`",
                  "`stop.js`"
                ],
                "rows": [
                  [
                    "1",
                    "`\"a\"`",
                    "prints `a`",
                    "prints `a`"
                  ],
                  [
                    "2",
                    "`\"-\"`",
                    "`continue`: the rest of this iteration is skipped",
                    "`break`: the loop ends"
                  ],
                  [
                    "3",
                    "`\"b\"`",
                    "prints `b`",
                    "—"
                  ],
                  [
                    "4",
                    "`\"-\"`",
                    "`continue`: the rest of this iteration is skipped",
                    "—"
                  ],
                  [
                    "5",
                    "`\"c\"`",
                    "prints `c`",
                    "—"
                  ]
                ]
              }
            },
            {
              "p": "The **`break` statement** ends **the loop**. When `break` runs, the rest of\nthe current iteration is skipped, and so is every iteration still to come. The program\ncarries on after the loop's closing brace. You met `break` in *Choosing among\nmany paths*, where it ends a `switch`; inside a loop it does the same job and\nends the loop."
            },
            {
              "p": "The **`continue` statement** ends **the current iteration** only. When `continue`\nruns, the rest of the body is skipped, and the loop goes on to its next iteration\nas usual."
            },
            {
              "p": "**Both skip the rest of the iteration. Only `break` skips the iterations\nstill to come.** The common mistake is to read `continue` as \"leave the loop\". The word\nmeans the opposite: the loop *continues*, with its next iteration."
            },
            {
              "p": "Many loops need both. \"Build a username from what someone typed: leave out\nthe spaces, and stop at the `@`.\""
            },
            {
              "code": "const typed = \"ada lovelace@mail\";\nlet name = \"\";\nfor (const ch of typed) {\n  // the name ends at the @: leave the loop\n  if (ch === \"@\") {\n    break;\n  }\n  // a space is not kept: go on to the next character\n  if (ch === \" \") {\n    continue;\n  }\n  name = name + ch;\n}\n// name is \"adalovelace\"",
              "lang": "js",
              "file": "username.js"
            },
            {
              "p": "The space after `ada` runs `continue`, so that iteration ends before\n`name = name + ch` and the space is not kept; the loop goes on to the `l`. The\n`@` runs `break`, so the loop ends there, and `mail` is never read."
            },
            {
              "p": "Type what each one prints, or press the answer."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let s = \"\";\nfor (const ch of \"2+3=5\") {\n  if (ch === \"=\") {\n    break;\n  }\n  s = s + ch;\n}\nconsole.log(s);\n",
                    "accept": [
                      "2+3",
                      "\"2+3\""
                    ],
                    "miss": "**`break` at the `=` ends the loop,** so the `5` is never read.\n",
                    "why": "`2+3`.\n"
                  },
                  {
                    "code": "let n = 0;\nfor (const ch of \"a b c\") {\n  if (ch === \" \") {\n    continue;\n  }\n  n++;\n}\nconsole.log(n);\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**If you answered `1` — you read `continue` as leaving the loop.** It only skips `n++` for each space; the loop goes on to the next letter.\n",
                    "why": "`3`. The two spaces were skipped; the three letters were counted.\n"
                  },
                  {
                    "question": "In `username.js`, `typed` is changed to `\"a b@c d\"`. Type what `name` ends as.",
                    "accept": [
                      "ab",
                      "\"ab\""
                    ],
                    "miss": "**The space after `a` is skipped with `continue`**, and the loop goes on to the `b`. **The `@` ends the loop with `break`**, so `c d` is never read.\n",
                    "why": "`ab`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Where they surprise people",
          "body": [
            {
              "p": "**`continue` in a `while`.** A `for` loop's update, `i++`, runs at the end of\nevery iteration, even an iteration that `continue` cut short. A `while` has no update of\nits own: the line that moves it on is just a line in the body. `continue`\njumps straight back to the condition, so if that line comes after the\n`continue`, it is skipped. The next example is meant to print 0 to 4,\nskipping 2, and it is wrong in exactly that way:"
            },
            {
              "code": "let i = 0;\nwhile (i < 5) {\n  if (i === 2) {\n    continue;      // back to the check with i still 2 — forever\n  }\n  console.log(i);\n  i++;\n}",
              "lang": "js",
              "file": "stuck.js"
            },
            {
              "p": "It prints `0` and `1`. Then `i` is 2, `continue` runs, `i++` is skipped, the\ncheck `2 < 5` is `true` again, and the same iteration starts over, for ever. A\n`do…while` behaves the same way: `continue` jumps to its check at the bottom,\npast any update written after it."
            },
            {
              "p": "Two fixes. A `for` loop, because its update is in the header and runs at the\nend of every iteration:"
            },
            {
              "code": "for (let i = 0; i < 5; i++) {\n  if (i === 2) {\n    continue;      // i++ still runs\n  }\n  console.log(i);\n}\n// 0 1 3 4, one on each line",
              "lang": "js",
              "file": "skip-two.js"
            },
            {
              "p": "Or keep the `while`, and move `i` on before the `continue` as well:"
            },
            {
              "code": "let i = 0;\nwhile (i < 5) {\n  if (i === 2) {\n    i++;\n    continue;\n  }\n  console.log(i);\n  i++;\n}\n// 0 1 3 4, one on each line",
              "lang": "js",
              "file": "skip-two.js"
            },
            {
              "p": "Both are correct. The `for` version writes the update once, where it cannot\nbe skipped; the `while` version has to write it twice. That is one more\nreason a known count fits a `for` loop."
            },
            {
              "p": "**`break` inside a `switch` inside a loop.** `break` ends the **nearest** loop\nor `switch` around it. So a `break` written inside a `switch` ends the\n`switch`, even when the `switch` is inside a loop. The next example is meant\nto stop a robot at the `S`, and does not:"
            },
            {
              "code": "// F means forward; S was meant to stop the robot\nlet moves = 0;\nfor (const cmd of \"FFSF\") {\n  switch (cmd) {\n    case \"F\":\n      moves++;\n      break;\n    case \"S\":\n      break;       // ends the switch, not the loop\n  }\n}\n// moves is 3: the loop carried on past the S",
              "lang": "js",
              "file": "robot.js"
            },
            {
              "p": "To stop the loop, test for it before the `switch`:"
            },
            {
              "code": "let moves = 0;\nfor (const cmd of \"FFSF\") {\n  if (cmd === \"S\") {\n    break;         // not inside the switch: ends the loop\n  }\n  switch (cmd) {\n    case \"F\":\n      moves++;\n      break;\n  }\n}\n// moves is 2",
              "lang": "js",
              "file": "robot.js"
            },
            {
              "p": "**A loop inside a loop.** A loop's body can hold another loop. You have not\nmet that yet: *Loops inside loops*, the next lesson, teaches it, and fills in\nwhat this paragraph only sketches. One thing about `break` is worth knowing\nahead of it. A plain `break` ends only the innermost loop around it; the outer\nloop goes on with its next iteration. To end an outer loop from inside an\ninner one, JavaScript has **labels**: a name and a colon written just before a\nloop, such as `outer:`. Then `break outer;` ends the loop with that name, and\nevery loop inside it. Documentation calls this a *labeled statement*. Here is\nthe shape of one:"
            },
            {
              "code": "outer: for (let row = 1; row <= 3; row++) {   // the outer loop\n  for (let col = 1; col <= 3; col++) {        // the inner loop\n    if (row * col === 4) {\n      console.log(row, col);                  // 2 2\n      break outer;                            // ends both loops\n    }\n  }\n}",
              "lang": "js",
              "file": "labels.js"
            },
            {
              "p": "`continue outer;` works the same way: it ends the current iteration of the\nouter loop and goes on to its next one. *Loops inside loops* traces a loop like\nthis one step by step, shows a search where a plain `break` gives the wrong\nanswer and `break outer;` the right one, and shows the plainer way most code\ndoes the same job."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let n = 0;\nfor (const ch of \"aXa\") {\n  switch (ch) {\n    case \"X\":\n      break;\n    default:\n      n++;\n  }\n}\nconsole.log(n);\n",
                    "accept": [
                      "2"
                    ],
                    "miss": "**If you answered `1` — that `break` is inside the `switch`.** It ends the `switch` for the `X`, and the loop goes on to the last `a`.\n",
                    "why": "`2`.\n"
                  },
                  {
                    "code": "let n = 0;\nfor (let i = 0; i < 4; i++) {\n  if (i === 2) {\n    continue;\n  }\n  n++;\n}\nconsole.log(n);\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**`continue` skips `n++` once, when `i` is 2,** and the update `i++` still runs. `i` is 0, 1, 2 and 3; three of those iterations reach `n++`.\n",
                    "why": "`3`.\n"
                  },
                  {
                    "question": "In `stuck.js`, which line does `continue` skip that the loop needs?",
                    "choices": [
                      "i++;",
                      "console.log(i);",
                      "while (i < 5) {"
                    ],
                    "accept": [
                      "i++;"
                    ],
                    "miss": "**Skipping `console.log(i)` is the point: 2 is not printed.** The condition is not skipped either; `continue` jumps straight to it. The line the loop needs is the one that moves `i` on.\n",
                    "why": "`i++;`. Without it, `i` stays 2 and the loop never ends.\n"
                  },
                  {
                    "question": "A plain `break` runs inside a loop that sits inside another loop. Which loop does it end?",
                    "choices": [
                      "Only the inner loop",
                      "Both loops",
                      "Only the outer loop"
                    ],
                    "accept": [
                      "Only the inner loop"
                    ],
                    "miss": "**`break` ends the nearest loop or `switch` around it**, and the nearest is the inner loop. The outer loop goes on. Ending the outer loop takes a label: `break outer;`.\n",
                    "why": "Only the inner loop.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Searching: stop at the first match",
          "body": [
            {
              "p": "A **search** looks for the first thing that matches, and stops. Every search\ncan end two ways: it finds a match, or it runs out of things to look at. It\nneeds an answer for both, so a search returns a **not-found value** that\ncannot be mistaken for a real answer. When the answer would be a position, the\nnot-found value is usually `-1`: positions start at 0, so `-1` is never a\nposition. When the answer would be a character, the not-found value is usually\n`null`."
            },
            {
              "p": "**Example 1.** \"`firstDigitAt` takes a piece of text and returns the position\nof its first digit, or `-1` if it has none.\""
            },
            {
              "p": "The answer is a position, so this is a `for` loop. A digit is a character\nfrom `\"0\"` to `\"9\"`, which *Comparing and combining* lets you test:\n`text[i] >= \"0\" && text[i] <= \"9\"`. Here is a common first try. It returns the\nposition when it finds a digit, and nothing else:"
            },
            {
              "code": "function firstDigitAt(text) {\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] >= \"0\" && text[i] <= \"9\") {\n      return i;\n    }\n  }\n}",
              "lang": "js",
              "file": "digit.js"
            },
            {
              "p": "`return` inside a loop ends the loop and the function together, so the search\nstops at the first digit with no `break`. The tests reported:"
            },
            {
              "code": "✓ firstDigitAt(\"Room 12\") → 5\n✓ firstDigitAt(\"7 days\") → 0\n✗ firstDigitAt(\"no digits\") → -1",
              "lang": "text"
            },
            {
              "p": "The test that failed is `firstDigitAt(\"no digits\")`, and it returned\n`undefined`. Work it through by hand, with `text` holding `\"no digits\"`. The\ntext has 9 characters, so the loop runs 9 iterations. On each one the\n`if` is false. The loop ends, and the function reaches its closing brace without\nmeeting a `return`. A function that ends without `return` returns\n`undefined`, just as a missing `else` did in *The if statement*.\nThe first try never wrote the not-found path."
            },
            {
              "p": "The tempting fix puts `return -1;` in an `else`. It is shown because it is\nwhat many people write next, and it is wrong:"
            },
            {
              "code": "function firstDigitAt(text) {\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] >= \"0\" && text[i] <= \"9\") {\n      return i;\n    } else {\n      return -1;\n    }\n  }\n}",
              "lang": "js",
              "file": "digit.js"
            },
            {
              "code": "✗ firstDigitAt(\"Room 12\") → 5\n✓ firstDigitAt(\"7 days\") → 0\n✓ firstDigitAt(\"no digits\") → -1",
              "lang": "text"
            },
            {
              "p": "Now `firstDigitAt(\"Room 12\")` fails. Work it through: `i` is 0, and `text[0]`\nis `\"R\"`, which is not a digit, so the `else` returns `-1` on the very first\niteration. But one character that does not match says nothing about the characters\nafter it. You only know that no character matched once **every** character has\nbeen checked, and that is after the loop ends. So `return -1;` goes after the\nloop:"
            },
            {
              "code": "function firstDigitAt(text) {\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] >= \"0\" && text[i] <= \"9\") {\n      // found\n      return i;\n    }\n  }\n  // not found: every position was checked\n  return -1;\n}",
              "lang": "js",
              "file": "digit.js"
            },
            {
              "p": "With `return -1;` after the loop, all three tests pass. This version also\nreturns `-1` for the empty string `\"\"`, where the loop runs no iterations at\nall; the `else` version returned `undefined` for `\"\"`."
            },
            {
              "p": "**A second form: `break` and a variable.** The same search can keep its answer\nin a variable, `found`, and return it once, after the loop:"
            },
            {
              "code": "function firstDigitAt(text) {\n  let found = -1;            // not found, unless the loop finds one\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] >= \"0\" && text[i] <= \"9\") {\n      found = i;\n      break;\n    }\n  }\n  return found;\n}",
              "lang": "js",
              "file": "digit.js"
            },
            {
              "p": "Both forms give the same answers. Returning from inside the loop is shorter,\nand it stops the moment it finds a digit. But `return` only works inside a\nfunction, and the `return -1;` after the loop is easy to forget: the first try\nforgot it. The variable form sets `found` to the not-found value before the\nloop starts, so that value cannot be forgotten, and it also works in a loop that\nis not inside a function. It costs a variable and a `break`. Either form is\nright. Use the one you can read more easily."
            },
            {
              "p": "**Example 2.** \"`firstCapital` returns the first capital letter in a piece\nof text, or `null` if there is none.\" This answer is the character itself, not\nits position, so `for…of` is enough:"
            },
            {
              "code": "function firstCapital(text) {\n  for (const ch of text) {\n    if (ch >= \"A\" && ch <= \"Z\") {\n      // found\n      return ch;\n    }\n  }\n  // not found: every character was checked\n  return null;\n}\n// firstCapital(\"hello World\") is \"W\"; firstCapital(\"quiet\") is null",
              "lang": "js",
              "file": "capital.js"
            },
            {
              "p": "**Example 3: you choose the line.** \"`positionOf` takes a piece of text and a\ncharacter, and returns the position where that character first appears, or\n`-1`.\" The loop is written; the line after the loop is not."
            },
            {
              "code": "function positionOf(text, target) {\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] === target) {\n      return i;\n    }\n  }\n  // TODO\n}",
              "lang": "js",
              "file": "position.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes where the TODO is?",
                    "choices": [
                      "return i;",
                      "return -1;",
                      "return positionOf(text, target);"
                    ],
                    "accept": [
                      "return -1;"
                    ],
                    "miss": "**If you chose `return i;` — `i` belongs to the loop.** After the loop there is no `i`, and the call stops with `ReferenceError: i is not defined`. **If you chose `positionOf(text, target)` — it calls the function you are writing,** with the same values, forever; *Writing a function's answer* shows why.\n",
                    "why": "`return -1;`. The loop has checked every position and none matched.\n"
                  },
                  {
                    "question": "Take the `break` out of the `found` version of `firstDigitAt`. What does it return for `\"Room 12\"`? Type the number.",
                    "accept": [
                      "6"
                    ],
                    "miss": "**Without `break`, the loop keeps going after the first digit.** When `i` is 5, the `1`, it sets `found` to 5. When `i` is 6, the `2`, it sets `found` to 6.\n",
                    "why": "`6`: the position of the **last** digit. Without `break`, a search finds the last match, not the first.\n"
                  },
                  {
                    "question": "The `else { return -1; }` version of `firstDigitAt` is called with `\"a1\"`. Type what it returns.",
                    "accept": [
                      "-1"
                    ],
                    "miss": "**The `else` runs on the first iteration**, because `\"a\"` is not a digit. The loop never reaches the `1`.\n",
                    "why": "`-1`, though the text has a digit at position 1.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A sign-up form must say whether a password contains a space. Three people\nwrite the loop:"
            },
            {
              "list": [
                "**A** — a `for…of` that sets `hasSpace = true` and `break`s at the first space.",
                "**B** — a `for` loop that does the same.",
                "**C** — a `for…of` with no `break` that sets `hasSpace = true` at every space."
              ]
            },
            {
              "p": "All three give the right answer. Choose one and defend it by what each iteration\nneeds. Then: the form is changed to say *where* the first space is. Which design\nhas to change, and why? Last, a fourth person writes `password.includes(\" \")`,\nfrom *Working with strings*. What does that tell you about when to write a\nloop at all?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`break` ends the loop; `continue` ends one iteration, and the loop goes on.** Inside a `switch`, `break` ends only the `switch`; inside a loop inside a loop, only the inner loop, unless a label names the outer one.",
                "**In a `while`, `continue` jumps straight to the check,** past any update after it: move the loop on before the `continue`, or use a `for` loop.",
                "**A search stops at its first match, and returns a not-found value** (`-1`, `null`) only after the loop has checked everything. Two forms work: `return` from inside the loop, or `break` and a variable that is returned after the loop."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "break",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/break",
              "note": "and its note that it ends the nearest loop *or* `switch`."
            },
            {
              "title": "Labeled statement",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/label",
              "note": "`break` and `continue` with a label, for a loop inside a loop."
            },
            {
              "title": "continue",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/continue",
              "note": "including how it behaves in a `while`."
            }
          ]
        }
      ],
      "_source": "lessons/break-continue.md",
      "practice": {
        "id": "break-continue",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Skip, stop, and where \"not found\" goes",
            "prompt": "Type what each loop prints, or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "let kept = \"\";\nfor (const ch of \"a1b2\") {\n  if (ch >= \"0\" && ch <= \"9\") {\n    continue;\n  }\n  kept = kept + ch;\n}\nconsole.log(kept);\n",
                "accept": [
                  "ab",
                  "\"ab\""
                ],
                "miss": "**If you answered `a` — you read `continue` as leaving the loop.** It ends only the iteration for each digit; the loop goes on to `b`.\n",
                "why": "`ab`. The digits were skipped; the letters were kept.\n"
              },
              {
                "id": "p2",
                "code": "let count = 0;\nfor (const ch of \"go-now\") {\n  if (ch === \"-\") {\n    break;\n  }\n  count++;\n}\nconsole.log(count);\n",
                "accept": [
                  "2"
                ],
                "miss": "**If you answered `5` — you read `break` as skipping one iteration.** It ends the loop at the dash, so `now` is never counted.\n",
                "why": "`2`: the `g` and the `o`.\n"
              },
              {
                "id": "p3",
                "question": "A function searches a piece of text for its first `?`, and should return `-1` when there is none. Where does `return -1;` go?",
                "choices": [
                  "Before the loop",
                  "In an else inside the loop",
                  "After the loop"
                ],
                "accept": [
                  "After the loop"
                ],
                "miss": "**An `else` inside the loop returns on the first character that is not a `?`.** You only know there is none once every character has been checked.\n",
                "why": "After the loop. If the loop finishes without returning, nothing matched.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const s = \"a1b2\";\nlet found = -1;\nfor (let i = 0; i < s.length; i++) {\n  if (s[i] >= \"0\" && s[i] <= \"9\") {\n    found = i;\n  }\n}\nconsole.log(found);\n",
                "accept": [
                  "3"
                ],
                "miss": "**If you answered `1` — there is no `break`.** The loop sets `found` to 1 at the `1`, and then to 3 at the `2`.\n",
                "why": "`3`, the last digit's position. A search without `break` finds the last match.\n"
              },
              {
                "id": "p5",
                "code": "let n = 0;\nfor (const ch of \"go!go\") {\n  switch (ch) {\n    case \"!\":\n      break;\n    default:\n      n++;\n  }\n}\nconsole.log(n);\n",
                "accept": [
                  "4"
                ],
                "miss": "**If you answered `2` — that `break` ends the `switch`, not the loop.** The loop carries on to the second `go`.\n",
                "why": "`4`.\n"
              },
              {
                "id": "p6",
                "code": "let i = 0;\nwhile (i < 4) {\n  if (i === 2) {\n    continue;\n  }\n  console.log(i);\n  i++;\n}\n",
                "choices": [
                  "It prints 0, 1 and 3",
                  "It prints 0 and 1, then never finishes",
                  "It prints 0, 1, 2 and 3"
                ],
                "accept": [
                  "It prints 0 and 1, then never finishes"
                ],
                "miss": "**`continue` in a `while` jumps straight back to the check.** When `i` is 2, `i++` comes after the `continue`, so it is skipped: `i` stays 2, and `2 < 4` is `true` for ever.\n",
                "why": "It prints 0 and 1, then never finishes. A `for` loop's `i++` would still run; in a `while`, the update has to come before the `continue`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered as if continue left the loop",
                "say": "Both `break` and `continue` skip the rest of the iteration. Only one of them also skips every iteration still to come.\n"
              },
              {
                "when": "you expected the while loop to print 0, 1 and 3",
                "say": "Follow the iteration where `i` is 2, line by line. Which line moves `i` on, and does that iteration reach it?\n"
              },
              {
                "when": "you put return -1 in an else",
                "say": "Try the text `\"a?\"` in your head. What does the first iteration do when it meets the `a`?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Three loops, run",
            "prompt": "Predict each line this program prints, then run it and compare.\n",
            "filename": "loops.js",
            "code": "let kept = \"\";\nfor (const ch of \"ab cd!ef\") {\n  if (ch === \" \") {\n    continue;\n  }\n  if (ch === \"!\") {\n    break;\n  }\n  kept = kept + ch;\n}\nconsole.log(kept);\n\nlet moves = 0;\nfor (const cmd of \"LLSR\") {\n  switch (cmd) {\n    case \"S\":\n      break;\n    default:\n      moves++;\n  }\n}\nconsole.log(moves);\n\nlet n = 9000;\nlet halvings = 0;\nwhile (n >= 1000) {\n  n = n / 2;\n  halvings++;\n}\nconsole.log(halvings);\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print (`kept`)? Type it.",
                "accept": [
                  "abcd",
                  "\"abcd\""
                ],
                "miss": "**The space is skipped with `continue`, and the `!` ends the loop with `break`,** so `ef` is never read.\n",
                "why": "`abcd`.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print (`moves`)? Type the number.",
                "accept": [
                  "3"
                ],
                "miss": "**If you answered `2` — the `break` ended the `switch`, not the loop.** The loop went on to the `R`.\n",
                "why": "`3`: `L`, `L` and `R`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print (`halvings`)? Type the number.",
                "accept": [
                  "4"
                ],
                "miss": "**Check the condition before every iteration.** 9000, 4500, 2250, 1125 are all at least 1000, so the body runs for each; after the fourth iteration, `n` is 562.5 and the loop ends.\n",
                "why": "`4`.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected \"ab\" or \"abcdef\"",
                "say": "Which keyword does the space trigger, and which does the `!` trigger? One keyword ends only the current iteration, and the other ends the loop.\n"
              },
              {
                "when": "you expected 2 moves",
                "say": "`break` ends the nearest loop or `switch` around it. What is nearest to that `break`?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Work a search through",
            "prompt": "Work each one through, one iteration at a time, and type the value it returns or prints.\n",
            "items": [
              {
                "id": "t1",
                "code": "function firstSpaceAt(text) {\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] === \" \") {\n      return i;\n    }\n  }\n  return -1;\n}\nfirstSpaceAt(\"to be\")\n",
                "accept": [
                  "2"
                ],
                "why": "`2`. `t` and `o` are not spaces. When `i` is 2 the `if` is true, and `return` ends the loop and the function.\n"
              },
              {
                "id": "t2",
                "code": "function firstSpaceAt(text) {\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] === \" \") {\n      return i;\n    }\n  }\n  return -1;\n}\nfirstSpaceAt(\"hello\")\n",
                "accept": [
                  "-1"
                ],
                "miss": "**The loop runs five iterations and none returns,** so the function carries on past the loop to `return -1`.\n",
                "why": "`-1`, the not-found value.\n"
              },
              {
                "id": "t3",
                "code": "let i = 0;\nlet count = 0;\nwhile (i < 6) {\n  i++;\n  if (i % 2 === 0) {\n    continue;\n  }\n  count++;\n}\nconsole.log(count);\n",
                "accept": [
                  "3"
                ],
                "why": "`3`. `i` goes 1 to 6; the even ones hit `continue`, so only 1, 3 and 5 are counted. `i++` comes before `continue`, so the loop always moves on.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "function firstSpaceAt(text) {\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] === \" \") {\n      return i;\n    } else {\n      return -1;\n    }\n  }\n}\nfirstSpaceAt(\"to be\")\n",
                "accept": [
                  "-1"
                ],
                "miss": "**The `else` runs on the very first iteration,** because `t` is not a space. The loop never reaches position 2.\n",
                "why": "`-1`, though there is a space.\n"
              },
              {
                "id": "t5",
                "code": "let n = 0;\nfor (let i = 0; i < 10; i++) {\n  if (i === 3) {\n    continue;\n  }\n  if (i === 6) {\n    break;\n  }\n  n++;\n}\nconsole.log(n);\n",
                "accept": [
                  "5"
                ],
                "why": "`5`: 0, 1, 2, 4 and 5 are counted. 3 is skipped, and at 6 the loop ends.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 2 for \"hello\"",
                "say": "`\"hello\"` has no space. Follow the loop to its last iteration, then read the line after the loop.\n"
              },
              {
                "when": "your count for the while loop was 6 or 0",
                "say": "Write down `i` at the start of each iteration, then whether that iteration reached `count++` or stopped at `continue`.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Build a search",
            "prompt": "`indexOfDot` should return the position of the first `.` in a piece of text, or `-1` if there is none. Build the lines that go between its braces, in order. Two of the lines are mistakes.\n",
            "lines": [
              "  let found = -1;",
              "  for (let i = 0; i < text.length; i++) {",
              "    if (text[i] === \".\") {",
              "      found = i;",
              "      break;",
              "    }",
              "  }",
              "  return found;"
            ],
            "distractors": [
              {
                "line": "      continue;",
                "why": "`continue` ends this iteration and goes on searching, so a later `.` replaces the first one. The search would find the last dot.\n"
              },
              {
                "line": "  let found;",
                "why": "With no starting value, `found` is `undefined` when the text has no `.`, and that is what comes back instead of `-1`.\n"
              }
            ],
            "why": "Set the not-found value first, look at each position, and at the first `.` keep its position and leave with `break`. Whatever `found` holds after the loop is the answer.\n",
            "hints": [
              {
                "when": "you are unsure which keyword ends the search",
                "say": "After the first `.`, should the loop go on looking? Pick the keyword that skips the iterations still to come.\n"
              },
              {
                "when": "you are unsure which `let` line is right",
                "say": "Try the text `\"abc\"` in your head. What does `found` hold when the loop ends?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Stop at the comment, skip the spaces",
            "prompt": "In a settings file, `#` starts a comment, and spaces do not count. `countChars` counts every character in a line. Change it, one rule at a time: first, nothing from the `#` on is counted; then, spaces are not counted. Write your code between the braces, and put `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: what does each of your two new lines skip?\n",
            "exportName": "countChars",
            "filename": "settings.js",
            "starter": "function countChars(line) {\n  // TODO: put your code here\n  let count = 0;\n  for (const ch of line) {\n    count++;\n  }\n  return count;\n}\n",
            "tests": [
              {
                "label": "countChars(\"size=10\") → 7",
                "body": "return subject('size=10') === 7;"
              },
              {
                "label": "countChars(\"on # yes\") → 2",
                "body": "return subject('on # yes') === 2;"
              },
              {
                "label": "countChars(\"# all comment\") → 0",
                "body": "return subject('# all comment') === 0;"
              },
              {
                "label": "countChars(\"x = 1\") → 3",
                "body": "return subject('x = 1') === 3;"
              },
              {
                "label": "countChars(\"a b#c d\") → 2",
                "body": "return subject('a b#c d') === 2;"
              },
              {
                "label": "countChars(\"\") → 0",
                "body": "return subject('') === 0;"
              }
            ],
            "hints": [
              {
                "when": "countChars(\"on # yes\") gives 5 or 7",
                "say": "The `#` was skipped, and the loop went on to count `yes`. The `#` means there is nothing more to count: end the loop, not the iteration.\n"
              },
              {
                "when": "countChars(\"x = 1\") gives 1",
                "say": "A space ended the whole loop. A space should end only its own iteration; the characters after it still count.\n"
              },
              {
                "when": "countChars(\"on # yes\") gives 3, or countChars(\"x = 1\") gives 5",
                "say": "The spaces were counted. Is the space check written yet, and does it come before `count++`?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "The first word",
            "prompt": "`firstWord` takes a piece of text and returns everything before its first space. If there is no space, the whole text is one word: return all of it. Write your code between the braces, and put `return` in front of the answer — *Writing a function's answer* shows how. Either return form works. Then, in one sentence: which test would catch a version with no not-found path?\n",
            "exportName": "firstWord",
            "filename": "word.js",
            "starter": "function firstWord(text) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "firstWord(\"hello world\") → \"hello\"",
                "body": "return subject('hello world') === 'hello';"
              },
              {
                "label": "firstWord(\"a b c\") → \"a\"",
                "body": "return subject('a b c') === 'a';"
              },
              {
                "label": "firstWord(\"single\") → \"single\"",
                "body": "return subject('single') === 'single';"
              },
              {
                "label": "firstWord(\"\") → \"\"",
                "body": "return subject('') === '';"
              }
            ],
            "hints": [
              {
                "when": "firstWord(\"single\") gives undefined",
                "say": "When there is no space, the loop ends without returning, and so does the function. What should come back then, and where does that line go?\n"
              },
              {
                "when": "firstWord(\"hello world\") gives \"helloworld\"",
                "say": "At the space, the loop skipped one iteration and kept going. The first word is finished at the first space: end the loop.\n"
              },
              {
                "when": "firstWord(\"hello world\") gives \"hello world\"",
                "say": "Check what happens on the first iteration. If an `else` returns the whole text when the character is not a space, it does so at the `h`.\n"
              },
              {
                "when": "the error says Maximum call stack size exceeded",
                "say": "Your answer calls `firstWord` from inside `firstWord`. Build it from `text`, a loop and `slice` or `+`.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a search",
            "prompt": "A code assistant wrote `firstCapitalAt`, which should return the position of the first capital letter in a piece of text, or `-1` if there is none. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "capital.js",
            "code": "// Position of the first capital letter, or -1.\nfunction firstCapitalAt(text) {\n  let found;\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] >= \"A\" && text[i] <= \"Z\") {\n      found = i;\n      continue;\n    }\n  }\n  return found;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `firstCapitalAt(\"hello World Wide\")`. Type the number it returns.",
                "accept": [
                  "12"
                ],
                "miss": "**`continue` does not stop the search.** `found` is set at the `W` of `World`, position 6, and set again at the `W` of `Wide`.\n",
                "why": "`12`, the last capital, not the first.\n"
              },
              {
                "id": "c2",
                "question": "Which line makes it find the last capital instead of the first? Type the line number.",
                "accept": [
                  "7",
                  "line 7"
                ],
                "why": "Line 7. It should be `break`, which ends the loop at the first capital.\n"
              },
              {
                "id": "c3",
                "question": "`firstCapitalAt(\"quiet\")` returns `undefined`. Which line causes that? Type the line number.",
                "accept": [
                  "3",
                  "line 3"
                ],
                "why": "Line 3. `found` starts with no value, and nothing sets it when there is no capital. It should be `let found = -1;`.\n"
              }
            ],
            "exportName": "firstCapitalAt",
            "starter": "// Position of the first capital letter, or -1.\nfunction firstCapitalAt(text) {\n  let found;\n  for (let i = 0; i < text.length; i++) {\n    if (text[i] >= \"A\" && text[i] <= \"Z\") {\n      found = i;\n      continue;\n    }\n  }\n  return found;\n}\n",
            "tests": [
              {
                "label": "firstCapitalAt(\"hello World Wide\") → 6",
                "body": "return subject('hello World Wide') === 6;"
              },
              {
                "label": "firstCapitalAt(\"Ada\") → 0",
                "body": "return subject('Ada') === 0;"
              },
              {
                "label": "firstCapitalAt(\"quiet\") → -1",
                "body": "return subject('quiet') === -1;"
              },
              {
                "label": "firstCapitalAt(\"\") → -1",
                "body": "return subject('') === -1;"
              }
            ],
            "hints": [
              {
                "when": "firstCapitalAt(\"hello World Wide\") gives 12",
                "say": "The search found a capital and kept going. Which keyword ends the loop rather than the iteration?\n"
              },
              {
                "when": "firstCapitalAt(\"quiet\") gives undefined",
                "say": "When no capital is found, `found` is never set. What should it hold before the loop starts?\n"
              }
            ]
          }
        ]
      }
    },
    "nesting": {
      "id": "nesting",
      "title": "Loops inside loops",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "Nested loops had no concept in the inventory, and the F25 patterns homework nearly went with them; Nick: students struggle with nesting and improve because of it (D-061, which adds `flow.nesting` and puts this lesson last in Loops, after *Leaving a loop early: break and continue* (D-066), so the `flow-patterns` homework follows the lessons it rests on). This is pass 1, strings and counting; pass 2 is in Arrays, over rows and columns (D-045). Procedural and explain-first (D-061 item 1). The run-time picture is the core of it: a trace table with both counters, row by row (01-explanations rule 10, rule 3 of 00-core). The writing direction is modelled with a fail-first worked example (01-explanations rule 5a), its test message showing spaces as `·` and line ends as `⏎` as the homework's tests do (D-061 item 5). Examples avoid the number and right-aligned triangles, so the homework meets those shapes fresh. The step on leaving both loops shows labelled `break` and `continue`, which belong to `flow.break-continue` (Nick, 2026-10-18) and matter only with a loop inside a loop, with the `return` form beside it. Content derived from the rules cited, pending Nick's review.",
      "concepts": [
        "flow.nesting"
      ],
      "lede": "A loop can sit inside another loop. On every iteration of the outer loop, the inner loop runs all the way through. This lesson traces a nested loop as it runs, counts how many times the inner loop's body runs, and uses nested loops to build text in rows: grids, tables and boards.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The while loop · The for loop · Leaving a loop early: break and continue · Writing a function's answer"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Nested loop** — a loop written inside the body of another loop. **Outer loop** — the loop on the outside. **Inner loop** — the loop in its body. **Iteration** — one run through a loop's body. **Separator** — something that goes *between* pieces, such as a space or a line break, and not after the last one. **Newline character** — `\\n` inside a string: a single character, a line break. **Escape sequence** — a backslash and a letter that together write one character you cannot type straight into a string. **Label** — a name and a colon before a loop, such as `outer:`, so that `break outer;` can end that loop from inside a loop within it.",
          "entries": [
            "**Nested loop** — a loop written inside the body of another loop.",
            "**Outer loop** — the loop on the outside.",
            "**Inner loop** — the loop in its body.",
            "**Iteration** — one run through a loop's body.",
            "**Separator** — something that goes *between* pieces, such as a space or a line break, and not after the last one.",
            "**Newline character** — `\\n` inside a string: a single character, a line break.",
            "**Escape sequence** — a backslash and a letter that together write one character you cannot type straight into a string.",
            "**Label** — a name and a colon before a loop, such as `outer:`, so that `break outer;` can end that loop from inside a loop within it."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on the two lessons before this one, from memory. Press **I don't know** if\nyou have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`for (let i = 1; i < 5; i++)` — how many iterations does this loop run? Type the number.",
                    "accept": [
                      "4"
                    ],
                    "miss": "**If you answered `5` — the loop stops at 5.** The body runs with `i` at 1, 2, 3 and 4; then `5 < 5` is `false` and the loop ends.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "code": "let word = \"\";\nfor (let i = 0; i < 3; i++) {\n  word += \"ha\";\n}\nconsole.log(word);\n",
                    "accept": [
                      "hahaha"
                    ],
                    "why": "`hahaha`. `word` starts empty, and each iteration adds `\"ha\"` to the end.\n"
                  },
                  {
                    "code": "let out = \"\";\nfor (const ch of \"red,blue\") {\n  if (ch === \",\") {\n    break;\n  }\n  out += ch;\n}\nconsole.log(out);\n",
                    "accept": [
                      "red"
                    ],
                    "miss": "**`break` ends the loop at once.** At the comma, nothing more is added, and no later character is looked at.\n",
                    "why": "`red`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say which counter changes when in a loop inside a loop, and fill in a trace table with both",
                "I can say how many times the inner body runs, including when the inner limit depends on the outer counter",
                "I can write a loop inside a loop that builds rows of text, with no space or line break left over at the end"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "One loop inside another",
          "body": [
            {
              "p": "A loop's body can hold any statements, and that includes another loop. A loop\nwritten inside the body of another loop is a **nested loop**. The loop on the\noutside is the **outer loop**; the one in its body is the **inner loop**."
            },
            {
              "p": "One rule explains how a nested loop behaves:"
            },
            {
              "p": "**The inner loop is one statement in the outer loop's body. So every iteration of\nthe outer loop runs the whole inner loop, from its start to its end.**"
            },
            {
              "code": "for (let row = 1; row <= 2; row++) {\n  for (let seat = 1; seat <= 3; seat++) {\n    console.log(\"row \" + row + \", seat \" + seat);\n  }\n}",
              "lang": "js",
              "file": "seats.js"
            },
            {
              "code": "row 1, seat 1\nrow 1, seat 2\nrow 1, seat 3\nrow 2, seat 1\nrow 2, seat 2\nrow 2, seat 3",
              "lang": "text"
            },
            {
              "p": "Here is `seats.js` running. The table has a line for each time the inner\ncondition, `seat <= 3`, is checked, and a column for **each** counter. A table\nlike this is a **trace table**. Use one whenever you trace a nested loop by hand: one line for\neach check, with both counters written down."
            },
            {
              "table": {
                "head": [
                  "`row`",
                  "`seat`",
                  "`seat <= 3`?",
                  "what happens"
                ],
                "rows": [
                  [
                    "1",
                    "1",
                    "`true`",
                    "prints `row 1, seat 1`"
                  ],
                  [
                    "1",
                    "2",
                    "`true`",
                    "prints `row 1, seat 2`"
                  ],
                  [
                    "1",
                    "3",
                    "`true`",
                    "prints `row 1, seat 3`"
                  ],
                  [
                    "1",
                    "4",
                    "**`false`**",
                    "inner loop ends; `row++`"
                  ],
                  [
                    "2",
                    "1",
                    "`true`",
                    "prints `row 2, seat 1`"
                  ],
                  [
                    "2",
                    "2",
                    "`true`",
                    "prints `row 2, seat 2`"
                  ],
                  [
                    "2",
                    "3",
                    "`true`",
                    "prints `row 2, seat 3`"
                  ],
                  [
                    "2",
                    "4",
                    "**`false`**",
                    "inner loop ends; `row++`, and the outer loop ends too"
                  ]
                ]
              }
            },
            {
              "p": "Three things to read off the table:"
            },
            {
              "list": [
                "**The inner counter changes fastest.** `seat` changes on almost every line; `row` changes only when the inner loop has finished. It works like a clock: the minute hand goes all the way round before the hour hand moves on by one.",
                "**The inner counter starts again on every iteration of the outer loop.** `let seat = 1` is part of the inner `for`. The outer loop's body runs that whole `for` again, so `seat` is made again, starting at 1: that is why the line after `row 1, seat 3` is `row 2, seat 1`.",
                "**The inner loop ends one past its limit.** The limit is 3, and the inner loop ends when `seat` reaches 4, just as a loop on its own does. Only then does the outer loop run its update, `row++`, and check its own condition. After row 2, `row` is 3, `3 <= 2` is `false`, and the outer loop ends too."
              ]
            },
            {
              "p": "This lesson uses nested loops to count and to build text. In the Arrays module\nyou will meet them again, walking the rows and columns of a list."
            },
            {
              "p": "For each, type a number or press an answer."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "for (let row = 1; row <= 3; row++) {\n  for (let seat = 1; seat <= 4; seat++) {\n    console.log(row, seat);\n  }\n}\n// How many lines does this print?\n",
                    "accept": [
                      "12"
                    ],
                    "miss": "**Each of the outer loop's 3 iterations runs the whole inner loop**, which prints 4 lines. 3 iterations of 4 lines each.\n",
                    "why": "`12`.\n"
                  },
                  {
                    "question": "In `seats.js`, which line is printed right after `row 1, seat 3`?",
                    "choices": [
                      "row 1, seat 4",
                      "row 2, seat 1",
                      "row 2, seat 3"
                    ],
                    "accept": [
                      "row 2, seat 1"
                    ],
                    "miss": "**If you chose `row 1, seat 4` — `4 <= 3` is `false`,** so nothing is printed for seat 4: the inner loop ends there. **If you chose `row 2, seat 3` — `seat` does not carry on from where it was.** The inner loop starts again, with `seat` at 1.\n",
                    "why": "`row 2, seat 1`. The inner loop has ended, so `row++` makes `row` 2, and the inner loop starts again from `seat` 1.\n"
                  },
                  {
                    "question": "In `seats.js`, just after `row` becomes 2, the inner loop starts. What does `seat` hold on its first iteration?",
                    "choices": [
                      "1",
                      "3",
                      "4"
                    ],
                    "accept": [
                      "1"
                    ],
                    "miss": "**`let seat = 1` runs again on every iteration of the outer loop.** The inner loop is started fresh each time, not resumed.\n",
                    "why": "`1`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Counting how many times the inner body runs",
          "body": [
            {
              "p": "When the inner loop runs the same number of iterations every time, the inner\nloop's body runs **outer iterations × inner iterations** times. Three rows of four\nseats print 3 × 4 = 12 lines."
            },
            {
              "p": "Sometimes the inner loop's limit uses the outer counter. Then the inner loop\nruns a different number of iterations on each iteration of the outer loop, and\nyou count them one outer iteration at a time. A music\nstudent practises one scale on day 1, two on day 2, three on day 3, and so on:"
            },
            {
              "code": "let total = 0;\nfor (let day = 1; day <= 4; day++) {\n  for (let scale = 1; scale <= day; scale++) {\n    total++;\n  }\n}\nconsole.log(total);   // 10",
              "lang": "js",
              "file": "scales.js"
            },
            {
              "p": "The trace table for the first two days, line by line:"
            },
            {
              "table": {
                "head": [
                  "`day`",
                  "`scale`",
                  "`scale <= day`?",
                  "`total` afterwards"
                ],
                "rows": [
                  [
                    "1",
                    "1",
                    "`true`",
                    "1"
                  ],
                  [
                    "1",
                    "2",
                    "**`false`**: the inner loop ends",
                    "1"
                  ],
                  [
                    "2",
                    "1",
                    "`true`",
                    "2"
                  ],
                  [
                    "2",
                    "2",
                    "`true`",
                    "3"
                  ],
                  [
                    "2",
                    "3",
                    "**`false`**: the inner loop ends",
                    "3"
                  ]
                ]
              }
            },
            {
              "p": "Days 3 and 4 carry on the same way. Here is the whole run, shortened to one\nline for each iteration of the outer loop:"
            },
            {
              "table": {
                "head": [
                  "`day`",
                  "inner body runs",
                  "`total` after the iteration"
                ],
                "rows": [
                  [
                    "1",
                    "1 time",
                    "1"
                  ],
                  [
                    "2",
                    "2 times",
                    "3"
                  ],
                  [
                    "3",
                    "3 times",
                    "6"
                  ],
                  [
                    "4",
                    "4 times",
                    "10"
                  ]
                ]
              }
            },
            {
              "p": "So the total is 1 + 2 + 3 + 4 = 10. When the inner loop runs one more\niteration each time round the outer loop, the counts add up: 1 + 2 + … + *n*."
            },
            {
              "p": "Here are both counts, the seats and the scales, worked out the same way:"
            },
            {
              "code": "// Seats: for (row 1 to 3) { for (seat 1 to 4) }\n// 1. iterations of the outer loop: row is 1, 2, 3, so 3\n// 2. iterations of the inner loop on each one: always 4\n// 3. add them up: 4 + 4 + 4 = 12, which is 3 × 4\n\n// Scales: for (day 1 to 4) { for (scale 1 to day) }\n// 1. iterations of the outer loop: day is 1, 2, 3, 4, so 4\n// 2. iterations of the inner loop on each one: as many as day\n// 3. add them up: 1 + 2 + 3 + 4 = 10",
              "lang": "js",
              "file": "count.js"
            },
            {
              "p": "For each, type the number `n` holds at the end."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let n = 0;\nfor (let r = 0; r < 5; r++) {\n  for (let c = 0; c < 3; c++) {\n    n++;\n  }\n}\n",
                    "accept": [
                      "15"
                    ],
                    "why": "`15`. 5 iterations of the outer loop, 3 of the inner loop on each.\n"
                  },
                  {
                    "code": "let n = 0;\nfor (let i = 0; i <= 3; i++) {\n  for (let j = 0; j < 2; j++) {\n    n++;\n  }\n}\n",
                    "accept": [
                      "8"
                    ],
                    "miss": "**Count the outer loop's iterations with care: `i` is 0, 1, 2 and 3.** `<=` lets 3 in, and starting at 0 adds one more. That is 4 iterations of the outer loop, with 2 of the inner loop on each.\n",
                    "why": "`8`.\n"
                  },
                  {
                    "code": "// Each guest shakes hands with every guest who came before.\nlet n = 0;\nfor (let guest = 1; guest <= 5; guest++) {\n  for (let other = 1; other < guest; other++) {\n    n++;\n  }\n}\n",
                    "accept": [
                      "10"
                    ],
                    "miss": "**Count one guest at a time.** Guest 1 shakes no hands (`1 < 1` is `false` at once), guest 2 shakes 1, guest 3 shakes 2, and so on: 0 + 1 + 2 + 3 + 4.\n",
                    "why": "`10`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Building rows of text",
          "body": [
            {
              "p": "Nested loops are how you build text in rows: a grid, a table, a board. First,\none new piece of notation. Inside a string, **`\\n` is a single character,\nthe *newline character*: a line break.** The backslash starts an *escape\nsequence*, two characters in your code that stand for one character in the\nstring. Printing `\"ab\\ncd\"` shows two lines, `ab` and `cd`, and\n`\"ab\\ncd\".length` is `5`."
            },
            {
              "p": "The code in this lesson that builds a grid does its work in four places, the\nsame four each time:"
            },
            {
              "list": [
                "**Before the outer loop**, the result starts empty.",
                "**At the top of the outer loop's body**, the row starts empty. This is called **resetting** the row: every row is built from nothing.",
                "**Inside the inner loop**, one piece is added to the row.",
                "**After the inner loop, still inside the outer one**, the row is added to the result, followed by a line break if more rows are still to come."
              ],
              "ordered": true
            },
            {
              "p": "\"`block` takes a width and a height, and returns a block of `#` that many\ncharacters wide and that many rows tall.\""
            },
            {
              "code": "function block(width, height) {\n  let result = \"\";\n  for (let r = 0; r < height; r++) {\n    let line = \"\";\n    for (let c = 0; c < width; c++) {\n      line += \"#\";\n    }\n    result += line;\n    if (r < height - 1) {\n      result += \"\\n\";\n    }\n  }\n  return result;\n}",
              "lang": "js",
              "file": "block.js"
            },
            {
              "p": "Both loops count from 0, as most loops do: `r` and `c` only count rows and\npieces, and their values are never shown. The last row is `height - 1`, so\n`r < height - 1` is `true` for every row but the last."
            },
            {
              "p": "`console.log(block(3, 2))` prints:"
            },
            {
              "code": "###\n###",
              "lang": "text"
            },
            {
              "p": "Here is `block(3, 2)` running, one line per piece added. A line break is drawn\nas `⏎` so you can see it."
            },
            {
              "table": {
                "head": [
                  "`r`",
                  "`c`",
                  "`line` afterwards",
                  "`result` afterwards"
                ],
                "rows": [
                  [
                    "0",
                    "",
                    "`\"\"` (reset)",
                    "`\"\"`"
                  ],
                  [
                    "0",
                    "0",
                    "`\"#\"`",
                    "`\"\"`"
                  ],
                  [
                    "0",
                    "1",
                    "`\"##\"`",
                    "`\"\"`"
                  ],
                  [
                    "0",
                    "2",
                    "`\"###\"`",
                    "`\"\"`"
                  ],
                  [
                    "0",
                    "inner loop over",
                    "`\"###\"`",
                    "`\"###⏎\"` (`0 < 1`: a break)"
                  ],
                  [
                    "1",
                    "",
                    "`\"\"` (reset)",
                    "`\"###⏎\"`"
                  ],
                  [
                    "1",
                    "0, 1, 2",
                    "`\"###\"`",
                    "`\"###⏎\"`"
                  ],
                  [
                    "1",
                    "inner loop over",
                    "`\"###\"`",
                    "`\"###⏎###\"` (`1 < 1` is `false`: no break)"
                  ]
                ]
              }
            },
            {
              "p": "Why only *between* rows? A line break here is a **separator**: it separates\none row from the next, and nothing comes after the last row. A break after the\nlast row would leave an empty line at the end. That is easy to miss when you\nlook at the printed output, where it shows only as one blank line, but\n`\"###\\n###\\n\"` and `\"###\\n###\"` are different strings, and a test compares the\nstrings."
            },
            {
              "p": "A separator can also go inside a row. \"`dotGrid` returns rows of `.`,\nwith one space between the dots in a row.\" The only new part is in the inner\nloop: a space after every dot except the last one in the row."
            },
            {
              "code": "function dotGrid(width, height) {\n  let result = \"\";\n  for (let r = 0; r < height; r++) {\n    let line = \"\";\n    for (let c = 0; c < width; c++) {\n      line += \".\";\n      // a space after every dot but the last in the row\n      if (c < width - 1) {\n        line += \" \";\n      }\n    }\n    result += line;\n    if (r < height - 1) {\n      result += \"\\n\";\n    }\n  }\n  return result;\n}",
              "lang": "js",
              "file": "dots.js"
            },
            {
              "p": "`dotGrid(4, 2)` returns `\". . . .\\n. . . .\"`, which prints:"
            },
            {
              "code": ". . . .\n. . . .",
              "lang": "text"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`block(2, 3)` is printed with `console.log`. Type what it prints, one line per line.",
                    "lines": 3,
                    "accept": [
                      "##\n##\n##"
                    ],
                    "miss": "**The width is how many characters go in each row; the height is how many rows.** 3 rows of 2.\n",
                    "why": "Three rows of `##`.\n"
                  },
                  {
                    "question": "In `block`, which line runs once for every row, and not once for every `#`?",
                    "choices": [
                      "line += \"#\";",
                      "let line = \"\";",
                      "let result = \"\";"
                    ],
                    "accept": [
                      "let line = \"\";"
                    ],
                    "miss": "**`line += \"#\"` is in the inner loop**, so it runs for every `#`. **`let result = \"\"` is before both loops**, so it runs once in all.\n",
                    "why": "`let line = \"\";`. It is in the outer loop's body, above the inner loop: one reset per row.\n"
                  },
                  {
                    "question": "How many line breaks are in the string `block(5, 4)` returns? Type the number.",
                    "accept": [
                      "3"
                    ],
                    "miss": "**Breaks go between rows, not after each one.** 4 rows have 3 gaps between them.\n",
                    "why": "`3`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "From a sentence to two loops",
          "body": [
            {
              "p": "So far you have read finished code and worked out the text it builds.\nExercises and the homework start from the other end: they give you a sentence\ndescribing some text, and a function to fill in. The four places a grid is\nbuilt in, from the step before, turn into four questions to ask, in this\norder:"
            },
            {
              "list": [
                "**What is one row?** Say it in words, with an example.",
                "**How many rows?** That is the outer loop.",
                "**What goes in a row, and how many pieces?** That is the inner loop and its body. Note any separator between the pieces.",
                "**Where is the row reset, and where is it added?** Reset at the top of the outer body; added after the inner loop, with a break between rows."
              ],
              "ordered": true
            },
            {
              "p": "Then send the answer out with `return`, as in *Writing a function's answer*."
            },
            {
              "p": "**Example 1.** \"`timesTable` takes a number `n` and returns the\nmultiplication table from 1 to `n` as text: one row for each number, the\nproducts in a row separated by single spaces, and the rows separated by line\nbreaks.\" So `timesTable(3)` returns three lines: `1 2 3`, `2 4 6` and\n`3 6 9`."
            },
            {
              "p": "Here is a common first try. It adds a space after every number and a line\nbreak after every row:"
            },
            {
              "code": "function timesTable(n) {\n  let table = \"\";\n  for (let r = 1; r <= n; r++) {\n    let line = \"\";\n    for (let c = 1; c <= n; c++) {\n      line += r * c + \" \";\n    }\n    table += line + \"\\n\";\n  }\n  return table;\n}",
              "lang": "js",
              "file": "table.js"
            },
            {
              "p": "When it is printed, the table looks right. The tests report a failure:"
            },
            {
              "code": "✖ timesTable(3)\n  AssertionError [ERR_ASSERTION]: timesTable(3) does not match. (· is a space, ⏎ is the end of a row.)\n    Expected:\n      1·2·3⏎\n      2·4·6⏎\n      3·6·9\n    Yours:\n      1·2·3·⏎\n      2·4·6·⏎\n      3·6·9·⏎\n    First difference: row 1, character 6. Expected nothing (the row ends), yours has \"·\" (a space).\n    Row 1 ends with a space, which you cannot see when it prints. One space goes BETWEEN two numbers, so a row of 3 numbers has 2 spaces, not 3.",
              "lang": "text"
            },
            {
              "p": "`timesTable(2)` and `timesTable(1)` failed the same way. Spaces and line\nbreaks cannot be seen, so these tests draw every space as `·` and every line\nbreak as `⏎`; the homework's tests do the same. With them drawn, the\ndifference shows: `Yours` has a `·` before every `⏎`, and a `⏎` after the last\nrow."
            },
            {
              "p": "`timesTable(1)` builds only one row, so the smallest failing test with two\nrows is `timesTable(2)`. Work it through by hand, with `n` holding `2`:"
            },
            {
              "table": {
                "head": [
                  "`r`",
                  "`c`",
                  "`line` afterwards",
                  "`table` afterwards"
                ],
                "rows": [
                  [
                    "1",
                    "",
                    "`\"\"` (reset)",
                    "`\"\"`"
                  ],
                  [
                    "1",
                    "1",
                    "`\"1·\"`",
                    "`\"\"`"
                  ],
                  [
                    "1",
                    "2",
                    "`\"1·2·\"`",
                    "`\"\"`"
                  ],
                  [
                    "1",
                    "inner loop over",
                    "`\"1·2·\"`",
                    "`\"1·2·⏎\"`"
                  ],
                  [
                    "2",
                    "",
                    "`\"\"` (reset)",
                    "`\"1·2·⏎\"`"
                  ],
                  [
                    "2",
                    "1",
                    "`\"2·\"`",
                    "`\"1·2·⏎\"`"
                  ],
                  [
                    "2",
                    "2",
                    "`\"2·4·\"`",
                    "`\"1·2·⏎\"`"
                  ],
                  [
                    "2",
                    "inner loop over",
                    "`\"2·4·\"`",
                    "`\"1·2·⏎2·4·⏎\"`"
                  ]
                ]
              }
            },
            {
              "p": "The table shows where the extras come from. `line += r * c + \" \"` adds a\nspace after the last number of each row as well, and `table += line + \"\\n\"`\nadds a break after the last row as well. Spaces and breaks are both\nseparators, so both belong only *between* pieces. Add a space only when\n`c < n`, because then another number follows in the row. Add a break only when\n`r < n`, because then another row follows. Here is the function with both\nseparators fixed, and the tests it now passes:"
            },
            {
              "code": "function timesTable(n) {\n  let table = \"\";\n  // how many rows: n\n  for (let r = 1; r <= n; r++) {\n    // reset the row\n    let line = \"\";\n    // what goes in a row: r × 1 up to r × n, a space between numbers\n    for (let c = 1; c <= n; c++) {\n      line += r * c;\n      if (c < n) {\n        line += \" \";\n      }\n    }\n    // add the row, with a break between rows\n    table += line;\n    if (r < n) {\n      table += \"\\n\";\n    }\n  }\n  return table;\n}",
              "lang": "js",
              "file": "table.js"
            },
            {
              "code": "✔ timesTable(3)\n✔ timesTable(2)\n✔ timesTable(1)",
              "lang": "text"
            },
            {
              "p": "`line += r * c` adds a number to a string. JavaScript turns the number into\ntext and joins it onto the end of `line`, as `+` does with a string and a\nnumber."
            },
            {
              "p": "**Example 2: you choose the lines.** \"`checkerboard` takes a size and returns\na square board that many characters each way: `#` where the row number\nplus the column number is even, `.` where it is odd.\" `checkerboard(3)` prints\nthe board below. In `board.js`, two lines are missing, marked `TODO 1` and\n`TODO 2`: you choose them."
            },
            {
              "code": "#.#\n.#.\n#.#",
              "lang": "text"
            },
            {
              "code": "function checkerboard(size) {\n  let board = \"\";\n  for (let r = 0; r < size; r++) {\n    // TODO 1: reset the row\n    for (let c = 0; c < size; c++) {\n      if ((r + c) % 2 === 0) {\n        line += \"#\";\n      } else {\n        line += \".\";\n      }\n    }\n    board += line;\n    if (r < size - 1) {\n      // TODO 2: the break between rows\n    }\n  }\n  return board;\n}",
              "lang": "js",
              "file": "board.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes at TODO 1?",
                    "choices": [
                      "let line = \"\";",
                      "board = \"\";",
                      "line += \"\";"
                    ],
                    "accept": [
                      "let line = \"\";"
                    ],
                    "miss": "**Neither of the other two makes `line`,** so the first `line += \"#\"` stops with `ReferenceError: line is not defined`. And `board = \"\"` would throw away every row built so far.\n",
                    "why": "`let line = \"\";`. Each row starts from nothing.\n"
                  },
                  {
                    "question": "Which line goes at TODO 2?",
                    "choices": [
                      "line += \"\\n\";",
                      "board += \"\\n\";",
                      "board += \" \";"
                    ],
                    "accept": [
                      "board += \"\\n\";"
                    ],
                    "miss": "**If you chose `line += \"\\n\"` — the row has already been added to `board`,** so a break added to `line` at that point never reaches `board`. **If you chose `board += \" \"` — rows are separated by a line break**, not a space.\n",
                    "why": "`board += \"\\n\";`. The break goes on the board, between rows.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Four mistakes, and what they look like",
          "body": [
            {
              "p": "Each of these four mistakes runs without an error message. The only sign\nof the mistake is the wrong text the code returns, so a trace table is a\ngood way to find it."
            },
            {
              "p": "**The row is reset in the wrong place.** Here `let line = \"\";` has moved up,\nabove the outer loop:"
            },
            {
              "code": "let result = \"\";\nlet line = \"\";\nfor (let r = 0; r < 3; r++) {\n  for (let c = 0; c < 2; c++) {\n    line += \"#\";\n  }\n  result += line;\n  if (r < 2) {\n    result += \"\\n\";\n  }\n}\nconsole.log(result);",
              "lang": "js",
              "file": "block.js"
            },
            {
              "code": "##\n####\n######",
              "lang": "text"
            },
            {
              "p": "`let line = \"\";` now runs only once, so `line` is never emptied. Each row\nstarts with every `#` from the rows before it. Reset the row **inside** the\nouter loop, **above** the inner one."
            },
            {
              "p": "**Both loops use the same counter name.** Each `let i` makes its own `i`. Inside\nthe inner loop, `i` means the inner counter, and the outer one cannot be\nreached at all:"
            },
            {
              "code": "let table = \"\";\nfor (let i = 1; i <= 3; i++) {\n  let row = \"\";\n  for (let i = 1; i <= 3; i++) {\n    row += i * i;\n    if (i < 3) {\n      row += \" \";\n    }\n  }\n  table += row;\n  if (i < 3) {\n    table += \"\\n\";\n  }\n}\nconsole.log(table);",
              "lang": "js",
              "file": "squares.js"
            },
            {
              "code": "1 4 9\n1 4 9\n1 4 9",
              "lang": "text"
            },
            {
              "p": "The code was meant to build a times table. But inside the inner loop, `i` is\nthe inner counter, so `i * i` multiplies the inner counter by itself, on every\nrow. Give each loop its own name, such as `r` for the row and\n`c` for the column, and the mistake cannot happen."
            },
            {
              "p": "**The inner loop updates the outer loop's variable.** With two names in\nplay, it is easy to type the wrong one in the update:\n`for (let c = 0; c < width; r++)`. The inner loop changes `r`, not `c`, so `c`\nstays 0, `c < width` stays `true`, and the program never finishes."
            },
            {
              "p": "**The inner limit is off by one.** `for (let c = 0; c <= 3; c++)` runs four\niterations, not three: the body runs with `c` at 0, 1, 2 and 3. Start at 0\nand stop with `<`, as most loops do: `c < 3` runs three. Start at 1 and use\n`<=` only when the values 1 to `n` are what the body needs, as in\n`timesTable`. When you are unsure, trace it to the value that stops the\nloop."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A `block` with its row reset above the outer loop is called with width 1 and height 3. Type what it prints, one line per line.",
                    "lines": 3,
                    "accept": [
                      "#\n##\n###"
                    ],
                    "miss": "**`line` is never emptied.** The second row is the first row plus one more `#`, and the third row is the second plus one more.\n",
                    "why": "`#`, then `##`, then `###`. Each row starts with every `#` from the rows before it.\n"
                  },
                  {
                    "code": "for (let r = 1; r <= 2; r++) {\n  for (let c = 1; c <= 3; r++) {\n    console.log(r, c);\n  }\n}\n",
                    "choices": [
                      "It prints 6 lines",
                      "It prints 3 lines",
                      "It never finishes"
                    ],
                    "accept": [
                      "It never finishes"
                    ],
                    "miss": "**Read the inner `for` closely: it increases `r`, not `c`.** `c` stays at 1, so `c <= 3` is never `false`.\n",
                    "why": "It never finishes. The inner loop has to change its own counter.\n"
                  },
                  {
                    "code": "let line = \"\";\nfor (let c = 0; c <= 4; c++) {\n  line += \"*\";\n}\n// How many * does line hold?\n",
                    "accept": [
                      "5"
                    ],
                    "miss": "**Starting at 0 and using `<=` counts one more than the limit.** `c` is 0, 1, 2, 3 and 4.\n",
                    "why": "`5`.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "step",
          "label": "Leaving both loops at once",
          "body": [
            {
              "p": "A search can run over two loops too. \"In a times table from 1 × 1 to 3 × 3,\nfind the first row and column whose product is 6.\" Rows are searched in\norder, and so are the columns within each row. Here is a first try with a\nplain `break`, shown because it looks right and is not:"
            },
            {
              "code": "let found = \"none\";\nfor (let r = 1; r <= 3; r++) {\n  for (let c = 1; c <= 3; c++) {\n    if (r * c === 6) {\n      found = r + \" \" + c;\n      break;               // ends only the inner loop\n    }\n  }\n}\nconsole.log(found);        // 3 2",
              "lang": "js",
              "file": "find.js"
            },
            {
              "p": "It prints `3 2`, not `2 3`. `break` ends the nearest loop around it, as in\n*Leaving a loop early: break and continue*, and here that is the inner loop.\nIn row 2 the search finds `2 3` and ends the inner loop. The outer loop does\nnot know anything was found: it goes on to row 3, finds `3 2` there, and\nwrites over `found`. Like a search without `break`, it ends with the last\nmatch instead of the first."
            },
            {
              "p": "To end both loops, give the outer loop a **label**, a name and a colon\nwritten just before it, and name it in the `break`:"
            },
            {
              "code": "let found = \"none\";\nouter: for (let r = 1; r <= 3; r++) {\n  for (let c = 1; c <= 3; c++) {\n    if (r * c === 6) {\n      found = r + \" \" + c;\n      break outer;         // ends the loop labelled outer\n    }\n  }\n}\nconsole.log(found);        // 2 3",
              "lang": "js",
              "file": "find.js"
            },
            {
              "p": "`break outer;` ends the loop labelled `outer`, and the inner loop with it,\nso the search stops at row 2, column 3. `continue outer;` works the same\nway for `continue`: it abandons the inner loop and goes on to the next iteration\nof the outer one. Documentation calls this a *labeled statement*."
            },
            {
              "p": "Labels are legal JavaScript, and they work. They are also rare, so many\npeople reading `break outer;` have to stop and look up what it does, and\nmany style guides discourage them (the ESLint checker has a rule for it,\n`no-labels`). The plainer way to end both loops is to put them in a\nfunction and `return` the answer: `return` ends every loop and the function\ntogether."
            },
            {
              "code": "function firstPair(target) {\n  for (let r = 1; r <= 3; r++) {\n    for (let c = 1; c <= 3; c++) {\n      if (r * c === target) {\n        return r + \" \" + c;    // ends both loops and the function\n      }\n    }\n  }\n  return \"none\";\n}\nconsole.log(firstPair(6));     // 2 3",
              "lang": "js",
              "file": "find.js"
            },
            {
              "p": "This course writes the `return` form, because the exercises already give you\na function, and a reader sees at once that the search is over. A label is\nthe choice when the loops are not inside a function of their own."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let found = \"none\";\nfor (let r = 1; r <= 3; r++) {\n  for (let c = 1; c <= 3; c++) {\n    if (r * c === 2) {\n      found = r + \" \" + c;\n      break;\n    }\n  }\n}\nconsole.log(found);\n",
                    "accept": [
                      "2 1",
                      "\"2 1\""
                    ],
                    "miss": "**The plain `break` ends only the inner loop.** Row 1 finds `1 2`, but the outer loop goes on, and row 2 finds `2 1` and writes over it. Row 3 has no product of 2.\n",
                    "why": "`2 1`, the last match. With `break outer;` and a label, it would be `1 2`.\n"
                  },
                  {
                    "question": "In the labelled version of `find.js`, how many times is `r * c === 6` checked? Type the number.",
                    "accept": [
                      "6",
                      "six"
                    ],
                    "miss": "**Count row by row.** Row 1 checks columns 1, 2 and 3. Row 2 checks columns 1, 2 and 3, and the third is the match, so `break outer;` ends both loops. Row 3 is never reached.\n",
                    "why": "`6`: three in row 1, three in row 2.\n"
                  },
                  {
                    "question": "Inside the inner loop, what does `continue outer;` do?",
                    "choices": [
                      "Ends the inner loop and goes on to the next iteration of the outer loop",
                      "Ends both loops",
                      "Skips the rest of one iteration of the inner loop"
                    ],
                    "accept": [
                      "Ends the inner loop and goes on to the next iteration of the outer loop"
                    ],
                    "miss": "**`continue` never ends a loop for good; it moves on to a next iteration.** The label says whose next iteration: the outer loop's. A plain `continue` would skip one iteration of the inner loop.\n",
                    "why": "It abandons the inner loop and goes on to the outer loop's next iteration.\n"
                  }
                ]
              }
            }
          ],
          "n": 6
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "\"`frame` takes a width and a height and returns a rectangle of `#` around\nthe edge with `.` inside.\" `frame(5, 3)` prints:"
            },
            {
              "code": "#####\n#...#\n#####",
              "lang": "text"
            },
            {
              "p": "Answer the four questions for it: what is one row (there are two\nkinds), how many rows, what goes in a row, and where the row is reset and\nadded. Then say which condition decides between `#` and `.` for one spot, and\nwhich two counters it needs."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**The inner loop runs all the way through on every iteration of the outer one.** The inner counter changes fastest and starts again each time; a trace table with both counters shows it line by line.",
                "**Count how many times the inner body runs:** rows × columns when the inner limit is fixed, 1 + 2 + … + *n* when it grows with the outer counter.",
                "**Building rows:** reset the row at the top of the outer body, add pieces in the inner loop, add the row after the inner loop — and put separators *between* pieces, never after the last one."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Loops and iteration",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Loops_and_iteration",
              "note": "every kind of loop, with examples."
            },
            {
              "title": "for",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for",
              "note": "the three parts of a `for`, and when each runs."
            },
            {
              "title": "Labeled statement",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/label",
              "note": "`break` and `continue` with a label."
            }
          ]
        }
      ],
      "_source": "lessons/nesting.md",
      "practice": {
        "id": "nesting",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Which counter, and how many times?",
            "prompt": "Type the number, or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "let n = 0;\nfor (let r = 0; r < 3; r++) {\n  for (let c = 0; c < 4; c++) {\n    n++;\n  }\n}\n// What does n hold now?\n",
                "accept": [
                  "12"
                ],
                "miss": "**Each of the outer loop's 3 iterations runs the whole inner loop,** which adds 4 each time.\n",
                "why": "`12`. 3 iterations × 4.\n"
              },
              {
                "id": "p2",
                "code": "for (let a = 1; a <= 2; a++) {\n  for (let b = 1; b <= 3; b++) {\n    console.log(a, b);\n  }\n}\n// Which line prints right after \"1 3\"?\n",
                "choices": [
                  "1 4",
                  "2 1",
                  "2 3"
                ],
                "accept": [
                  "2 1"
                ],
                "miss": "**The inner loop ends at `b` = 4 without printing,** and when the outer loop moves on, the inner loop starts again with `b` at 1.\n",
                "why": "`2 1`.\n"
              },
              {
                "id": "p3",
                "question": "You are building a grid of text, one row on each iteration of the outer loop. Where does `let line = \"\";` go?",
                "choices": [
                  "Before the outer loop",
                  "At the top of the outer loop's body, before the inner loop",
                  "Inside the inner loop"
                ],
                "accept": [
                  "At the top of the outer loop's body, before the inner loop"
                ],
                "miss": "**Before the outer loop, `line` is never emptied,** so each row carries the ones before it. **Inside the inner loop, it is emptied before every piece,** so a row never holds more than one.\n",
                "why": "At the top of the outer loop's body: every row starts from nothing.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "let n = 0;\nfor (let i = 1; i <= 3; i++) {\n  for (let j = 1; j <= i; j++) {\n    n++;\n  }\n}\n// What does n hold now?\n",
                "accept": [
                  "6"
                ],
                "miss": "**The inner limit is `i`, so it changes on each iteration of the outer loop:** the inner body runs once, then twice, then three times.\n",
                "why": "`6`. 1 + 2 + 3.\n"
              },
              {
                "id": "p5",
                "code": "let out = \"\";\nfor (const a of \"ab\") {\n  for (const b of \"xyz\") {\n    out += a + b;\n  }\n}\n// What does out hold? Type it without quotes.\n",
                "accept": [
                  "axayazbxbybz"
                ],
                "why": "`axayazbxbybz`. `a` stays on `\"a\"` while `b` goes through all of `\"xyz\"`; then `a` moves to `\"b\"`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 7 for the first",
                "say": "7 is 3 + 4: it adds the iterations of the two loops together. Each iteration of the outer loop runs the whole inner loop: how many times does `n++` run in one iteration of the outer loop?\n"
              },
              {
                "when": "you chose 1 4 or 2 3",
                "say": "Check `b <= 3` with `b` at 4. Then ask what `let b = 1` does when the outer loop starts its next iteration.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict three lines, then run it",
            "prompt": "Predict each line it prints — type it exactly — then run it and compare.\n",
            "filename": "counts.js",
            "code": "let calls = 0;\nfor (let a = 1; a <= 3; a++) {\n  for (let b = 1; b <= 4; b++) {\n    calls++;\n  }\n}\nconsole.log(calls);\n\nlet pairs = \"\";\nfor (const x of \"ab\") {\n  for (const y of \"12\") {\n    pairs += x + y + \",\";\n  }\n}\nconsole.log(pairs);\n\nlet games = 0;\nfor (let home = 1; home <= 4; home++) {\n  for (let away = 1; away < home; away++) {\n    games++;\n  }\n}\nconsole.log(games);\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(calls);",
                "accept": [
                  "12"
                ],
                "why": "`12`. 3 × 4.\n"
              },
              {
                "id": "r2",
                "code": "console.log(pairs);",
                "accept": [
                  "a1,a2,b1,b2,"
                ],
                "miss": "**The comma is added after every pair — the last one too.** So the text ends with a comma.\n",
                "why": "`a1,a2,b1,b2,`. That last comma is a separator left over at the end: the lesson shows how to keep it off.\n"
              },
              {
                "id": "r3",
                "code": "console.log(games);",
                "accept": [
                  "6"
                ],
                "miss": "**The inner limit is `away < home`, so count one value of `home` at a time:** home 1 plays 0 games, home 2 plays 1, home 3 plays 2, home 4 plays 3.\n",
                "why": "`6`. 0 + 1 + 2 + 3: every pair of four teams plays once.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 7 for calls",
                "say": "The inner loop runs all 4 of its iterations on each of the outer loop's 3 iterations. Add 4 three times.\n"
              },
              {
                "when": "you predicted 10 for games",
                "say": "`<` stops before `home`. Write how many iterations the inner loop runs when `home` is 1, then 2, 3 and 4.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Both counters, line by line",
            "prompt": "Trace the program in the first question with a table: columns for `r`, `c` and `out`, one line each time `out += r * c` runs. Then answer each question from your table. Type text without quotes.\n",
            "items": [
              {
                "id": "t1",
                "code": "let out = \"\";\nfor (let r = 1; r <= 2; r++) {\n  for (let c = 1; c <= 3; c++) {\n    out += r * c;\n  }\n  out += \"|\";\n}\n// What does out hold after the outer loop's\n// first iteration, once \"|\" is added?\n",
                "accept": [
                  "123|"
                ],
                "why": "`123|`. `r` is 1 while `c` goes 1, 2, 3, then the `|` goes on.\n"
              },
              {
                "id": "t2",
                "question": "The 4th time `out += r * c` runs, what does `r` hold? Type the number.",
                "accept": [
                  "2"
                ],
                "miss": "**The outer loop's first iteration runs the inner body 3 times,** so the 4th is the first one of the outer loop's second iteration.\n",
                "why": "`2`.\n"
              },
              {
                "id": "t3",
                "question": "And what does `c` hold that 4th time? Type the number.",
                "accept": [
                  "1"
                ],
                "miss": "**The inner loop starts again on each iteration of the outer loop,** so `c` is back at 1.\n",
                "why": "`1`. That 4th time, `out += r * c` adds `2 * 1`.\n"
              },
              {
                "id": "t4",
                "question": "When the inner loop stops for the first time, what does `c` hold? Type the number.",
                "accept": [
                  "4"
                ],
                "miss": "**The loop stops when `c <= 3` is `false`,** and that first happens at 4.\n",
                "why": "`4`. One past the limit, as with any `for`.\n"
              },
              {
                "id": "t5",
                "question": "What does `out` hold when the program ends? Type it without quotes.",
                "accept": [
                  "123|246|"
                ],
                "why": "`123|246|`.\n"
              }
            ],
            "extra": [
              {
                "id": "t6",
                "question": "In that program, how many times does `out += r * c` run in all? Type the number.",
                "accept": [
                  "6"
                ],
                "why": "`6`. 2 iterations of the outer loop × 3.\n"
              },
              {
                "id": "t7",
                "question": "And how many times does `out += \"|\"` run? Type the number.",
                "accept": [
                  "2"
                ],
                "miss": "**`out += \"|\"` is in the outer loop's body, after the inner loop,** so it runs once per iteration of the outer loop.\n",
                "why": "`2`.\n"
              },
              {
                "id": "t8",
                "code": "let count = 0;\nouter: for (let r = 1; r <= 3; r++) {\n  for (let c = 1; c <= 3; c++) {\n    if (c === 2) {\n      continue outer;\n    }\n    count++;\n  }\n}\n// What does count hold at the end?\n",
                "accept": [
                  "3"
                ],
                "miss": "**`continue outer;` abandons the inner loop and goes on to the outer loop's next iteration.** In each row, `c` is 1 and `count++` runs; at `c` 2 the row is over. 3 rows, one count each.\n",
                "why": "`3`. A plain `continue` would skip only `c` 2, and count 6.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 6 for the labelled continue",
                "say": "`continue outer;` names the outer loop. After it runs, does the inner loop ever reach `c` 3 in that row?\n"
              },
              {
                "when": "you answered 1 for the labelled continue",
                "say": "`continue` never ends a loop for good. Which loop goes on to its next iteration, and how many iterations does it have?\n"
              },
              {
                "when": "you answered 1 for r the 4th time",
                "say": "Count down your table: the first three lines are `r` = 1 with `c` = 1, 2, 3. What comes on the fourth line?\n"
              },
              {
                "when": "you answered 3 or 4 for c the 4th time",
                "say": "`let c = 1` is part of the inner `for`, and the outer body runs it again on every iteration of the outer loop.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "A wall of bricks",
            "prompt": "`rows` and `bricks` hold numbers. Put the lines in order so `wall` ends up as `rows` rows of `[]`, `bricks` in each row, with a line break between rows and none at the end. Two lines are mistakes — leave them out.\n",
            "lines": [
              "let wall = \"\";",
              "for (let r = 1; r <= rows; r++) {",
              "  let line = \"\";",
              "  for (let b = 1; b <= bricks; b++) {",
              "    line += \"[]\";",
              "  }",
              "  wall += line;",
              "  if (r < rows) { wall += \"\\n\"; }",
              "}"
            ],
            "distractors": [
              {
                "line": "  for (let b = 1; b < bricks; b++) {",
                "why": "Starting at 1 with `<` stops one short: with `bricks` at 3, each row gets only 2.\n"
              },
              {
                "line": "  wall += line + \"\\n\";",
                "why": "This line adds a line break after every row, the last one too. A break is a separator, so it goes only between rows.\n"
              }
            ],
            "why": "The result starts empty; each iteration of the outer loop resets `line`, fills it in the inner loop, then adds it to `wall` with a break if more rows follow.\n",
            "hints": [
              {
                "when": "you put let line = \"\" before the outer loop",
                "say": "Then `line` is never emptied, and the second row carries the first. Where does a row start from nothing?\n"
              },
              {
                "when": "you used wall += line + \"\\n\"",
                "say": "Follow the last row. Does another row come after it, for a break to separate it from?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "A seating map",
            "prompt": "\"`seatMap` takes a number of rows and a number of seats in each row, and returns a map of seat names: rows lettered A, B, C…, seats numbered from 1, one space between seats, a line break between rows.\" `seatMap(2, 3)` returns `A1 A2 A3` and `B1 B2 B3` on two lines. The outer loop, the row's letter, the reset and the `return` are already written. Write the inner loop that adds each seat to `line`. Then, in one sentence: why is the space added only when the seat number is below `seats`?\n",
            "exportName": "seatMap",
            "filename": "seats.js",
            "starter": "function seatMap(rows, seats) {\n  let map = \"\";\n  for (let r = 1; r <= rows; r++) {\n    const letter = \"ABCDEFGH\"[r - 1];\n    let line = \"\";\n    // TODO: put your code here\n    map += line;\n    if (r < rows) {\n      map += \"\\n\";\n    }\n  }\n  return map;\n}\n",
            "tests": [
              {
                "label": "seatMap(2, 3) → \"A1·A2·A3⏎B1·B2·B3\"",
                "body": "const got = subject(2, 3);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"A1 A2 A3\\nB1 B2 B3\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "seatMap(1, 1) → \"A1\"",
                "body": "const got = subject(1, 1);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"A1\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "seatMap(3, 2) → \"A1·A2⏎B1·B2⏎C1·C2\"",
                "body": "const got = subject(3, 2);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"A1 A2\\nB1 B2\\nC1 C2\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              }
            ],
            "hints": [
              {
                "when": "every row ends in · before the ⏎",
                "say": "A space goes after every seat, the last one too. The space is a separator: add it only when more seats follow in that row.\n"
              },
              {
                "when": "each row is one seat short",
                "say": "Count the iterations of your inner loop for `seats` at 3. Starting at 1, which comparison lets 3 in?\n"
              },
              {
                "when": "every seat in a row has the same number",
                "say": "The seat number is the inner counter. Which name changes on every iteration of the inner loop?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "From a block to a frame",
            "prompt": "`frame` returns a solid block of `#` right now. Change it so only the edge is `#` and the inside is `.`: `frame(5, 3)` should return `#####`, `#...#` and `#####` on three lines. Change only the inner loop's body. Then, in one sentence: suppose your code put `#` only in the first and last rows, and `.` everywhere else. Which test would still pass?\n",
            "exportName": "frame",
            "filename": "frame.js",
            "starter": "function frame(width, height) {\n  let result = \"\";\n  for (let r = 1; r <= height; r++) {\n    let line = \"\";\n    for (let c = 1; c <= width; c++) {\n      // TODO: put your code here\n      line += \"#\";\n    }\n    result += line;\n    if (r < height) {\n      result += \"\\n\";\n    }\n  }\n  return result;\n}\n",
            "tests": [
              {
                "label": "frame(5, 3) → \"#####⏎#...#⏎#####\"",
                "body": "const got = subject(5, 3);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"#####\\n#...#\\n#####\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "frame(4, 4) → \"####⏎#..#⏎#..#⏎####\"",
                "body": "const got = subject(4, 4);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"####\\n#..#\\n#..#\\n####\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "frame(3, 2) → \"###⏎###\"",
                "body": "const got = subject(3, 2);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"###\\n###\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              }
            ],
            "hints": [
              {
                "when": "the middle rows come out as all dots",
                "say": "You check the row (top or bottom) but not the column. A spot is on the edge when it is in the first or last row **or** the first or last column.\n"
              },
              {
                "when": "everything comes out as dots",
                "say": "`&&` needs every part true at once. An edge spot needs only one of them: which operator is that?\n"
              },
              {
                "when": "the right-hand edge is missing or one spot in",
                "say": "The last column is `c === width`. Check what your comparison uses.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "A grid of coordinates",
            "prompt": "\"`coordinates` takes a number of rows and columns and returns every position as `(row,col)`: one space between positions in a row, a line break between rows.\" `coordinates(2, 3)` returns `(1,1) (1,2) (1,3)` and `(2,1) (2,2) (2,3)` on two lines. Write it between the braces with the four subgoals, and end with `return` — *Writing a function's answer* shows how. Then, in one sentence: if `let` for the row were placed above the outer loop, which test would catch it?\n",
            "exportName": "coordinates",
            "filename": "coords.js",
            "starter": "function coordinates(rows, cols) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "coordinates(2, 3) → \"(1,1)·(1,2)·(1,3)⏎(2,1)·(2,2)·(2,3)\"",
                "body": "const got = subject(2, 3);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"(1,1) (1,2) (1,3)\\n(2,1) (2,2) (2,3)\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "coordinates(1, 1) → \"(1,1)\"",
                "body": "const got = subject(1, 1);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"(1,1)\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "coordinates(3, 1) → \"(1,1)⏎(2,1)⏎(3,1)\"",
                "body": "const got = subject(3, 1);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"(1,1)\\n(2,1)\\n(3,1)\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "coordinates(1, 4) → \"(1,1)·(1,2)·(1,3)·(1,4)\"",
                "body": "const got = subject(1, 4);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"(1,1) (1,2) (1,3) (1,4)\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              }
            ],
            "hints": [
              {
                "when": "it gave undefined",
                "say": "Put `return` and the name of your result after the outer loop, so the text you built is sent out.\n"
              },
              {
                "when": "positions come out as (1,2) where (2,1) should be",
                "say": "The row is the outer counter and the column the inner one. Check which name goes first inside the brackets.\n"
              },
              {
                "when": "a · or a ⏎ sits at the very end",
                "say": "A separator goes only between pieces. Add it when another piece follows: compare the counter with its limit.\n"
              },
              {
                "when": "the second row starts with the first row's positions",
                "say": "Your row is never emptied. Reset it at the top of the outer loop's body, above the inner loop.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a grid of x",
            "prompt": "A code assistant wrote `xGrid`, which should return `rows` rows of `x`, `cols` in each, one space between them and a line break between rows: `xGrid(2, 3)` should return `x x x` and `x x x` on two lines. Read it, answer each question, then fix it.\n",
            "filename": "xgrid.js",
            "code": "function xGrid(rows, cols) {\n  let grid = \"\";\n  let line = \"\";\n  for (let r = 1; r <= rows; r++) {\n    for (let c = 1; c <= cols; c++) {\n      line += \"x \";\n    }\n    grid += line;\n    if (r < rows) {\n      grid += \"\\n\";\n    }\n  }\n  return grid;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "What does `xGrid(2, 2)` return? Spaces are drawn as `·`, line breaks as `⏎`.",
                "choices": [
                  "\"x·x⏎x·x\"",
                  "\"x·x·⏎x·x·x·x·\"",
                  "\"x·x·⏎x·x·\""
                ],
                "accept": [
                  "\"x·x·⏎x·x·x·x·\""
                ],
                "miss": "**Trace `line` with both counters.** It is made once, on line 3, and never emptied, so the second row adds to what the first left there. And every `x` brings a space with it.\n",
                "why": "`\"x·x·⏎x·x·x·x·\"`. Two defects: the second row carries the first, and every row ends in a space.\n"
              },
              {
                "id": "c2",
                "question": "Which line is in the wrong place?",
                "choices": [
                  "line 2",
                  "line 3",
                  "line 8"
                ],
                "accept": [
                  "line 3"
                ],
                "miss": "**`grid` should be made once, before the loops — line 2 is right.** It is `line`, the row, that must start empty on every iteration of the outer loop.\n",
                "why": "Line 3. `let line = \"\";` belongs at the top of the outer loop's body, above the inner loop.\n"
              },
              {
                "id": "c3",
                "question": "Line 6 adds `\"x \"` every time. What is wrong with that?",
                "choices": [
                  "It leaves a space after the last x in each row",
                  "It puts a line break after each x",
                  "Nothing: the test ignores spaces"
                ],
                "accept": [
                  "It leaves a space after the last x in each row"
                ],
                "why": "The space is a separator, so it goes only between the `x`s: add `x`, then a space when `c < cols`.\n"
              }
            ],
            "exportName": "xGrid",
            "starter": "function xGrid(rows, cols) {\n  let grid = \"\";\n  let line = \"\";\n  for (let r = 1; r <= rows; r++) {\n    for (let c = 1; c <= cols; c++) {\n      line += \"x \";\n    }\n    grid += line;\n    if (r < rows) {\n      grid += \"\\n\";\n    }\n  }\n  return grid;\n}\n",
            "tests": [
              {
                "label": "xGrid(2, 3) → \"x·x·x⏎x·x·x\"",
                "body": "const got = subject(2, 3);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"x x x\\nx x x\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "xGrid(1, 1) → \"x\"",
                "body": "const got = subject(1, 1);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"x\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              },
              {
                "label": "xGrid(3, 2) → \"x·x⏎x·x⏎x·x\"",
                "body": "const got = subject(3, 2);\nconst see = v => typeof v === \"string\" ? '\"' + v.replaceAll(\" \", \"·\").replaceAll(\"\\n\", \"⏎\") + '\"' : String(v);\nif (got === \"x x\\nx x\\nx x\") return true;\nthrow new Error(\"it gave \" + see(got));\n"
              }
            ],
            "hints": [
              {
                "when": "you chose \"x·x⏎x·x\"",
                "say": "`\"x·x⏎x·x\"` is what `xGrid` should return, not what it does. Trace `line` through the outer loop's second iteration: does anything empty it first?\n"
              },
              {
                "when": "rows are right but end in ·",
                "say": "With the reset moved, each row starts empty. Now keep the space off the end of the row: add it only when another `x` follows.\n"
              }
            ]
          }
        ]
      }
    },
    "check-loops": {
      "id": "check-loops",
      "title": "Loops check",
      "kind": "quiz",
      "unlocks": "Arrays",
      "questions": [
        {
          "id": "q1",
          "skill": "Tracing a while loop",
          "prompt": "What does this print?",
          "code": "let n = 100;\nlet steps = 0;\nwhile (n > 10) {\n  n = n - 25;\n  steps++;\n}\nconsole.log(steps, n);",
          "answers": [
            {
              "key": "A",
              "text": "`3 25`",
              "tag": "stopped one iteration early",
              "correct": false
            },
            {
              "key": "B",
              "text": "`4 0`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`4 -25`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`5 -25`",
              "tag": "one iteration too many",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `n` is 100, 75, 50 and 25 at the four checks that say `true`, and each iteration takes 25 off. At the fifth check `n` is 0, `0 > 10` is `false`, and the loop ends: four iterations, and `n` is 0.",
            "wrong": "Trace it, one row per check: 100, 75, 50, 25 all make `n > 10` true, and each iteration subtracts 25 and counts one. At 25, `25 > 10` is still `true`, so there is a fourth iteration, which makes `n` 0. The next check, `0 > 10`, is `false`. The answer is B."
          }
        },
        {
          "id": "q2",
          "skill": "Off-by-one",
          "prompt": "This loop is meant to add up the even numbers from 2 to 10, both included. What does `total` hold when it ends?",
          "code": "let total = 0;\nfor (let i = 2; i < 10; i += 2) {\n  total += i;\n}",
          "answers": [
            {
              "key": "A",
              "text": "`30`",
              "tag": "what it was meant to do",
              "correct": false
            },
            {
              "key": "B",
              "text": "`20`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`18`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`45`",
              "tag": "+= 2 read as += 1",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `i` is 2, 4, 6 and 8; `10 < 10` is `false`, so the last iteration never runs. 2 + 4 + 6 + 8 is 20. The fix is the last-iteration question: `i <= 10` lets 10 in.",
            "wrong": "Write out the values of `i`: it starts at 2 and goes up by 2 while `i < 10` — 2, 4, 6, 8. At 10 the check is `false`, so 10 is never added: 2 + 4 + 6 + 8 is 20, not the 30 the sentence asked for. The answer is B."
          }
        },
        {
          "id": "q9",
          "skill": "The do…while loop",
          "prompt": "What does this print?",
          "code": "let level = 10;\nlet rounds = 0;\ndo {\n  level -= 3;\n  rounds++;\n} while (level > 10);\nconsole.log(rounds, level);",
          "answers": [
            {
              "key": "A",
              "text": "`0 10`",
              "tag": "read as a while, checked before the first iteration",
              "correct": false
            },
            {
              "key": "B",
              "text": "`1 7`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`1 10`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`0 7`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. A `do…while` runs its body before the first check, so `level` becomes 7 and `rounds` 1. Then `7 > 10` is `false`, and the loop ends after one iteration.",
            "wrong": "A `do…while` checks its condition after each iteration, not before, so its body always runs at least once. The first iteration makes `level` 7 and `rounds` 1; only then is `level > 10` checked, and `7 > 10` is `false`. A `while` with the same condition would check `10 > 10` first and run no iterations. The answer is B."
          }
        },
        {
          "id": "q3",
          "skill": "Type coercion",
          "prompt": "What does `total` hold when the loop ends?",
          "code": "let total = \"0\";\nfor (let i = 1; i <= 3; i++) {\n  total += i;\n}",
          "answers": [
            {
              "key": "A",
              "text": "`6`",
              "tag": "the quotes around 0 missed",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"0123\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`\"6\"`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`\"0\"`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `total` starts as the string `\"0\"`. When one side of `+` is a string, `+` joins text instead of adding, so each iteration adds one character: `\"01\"`, `\"012\"`, `\"0123\"`.",
            "wrong": "Look at the first line: `total` starts as `\"0\"`, a string, not the number 0. `total += i` uses `+`, and when one side of `+` is a string, `+` joins text: `\"0\" + 1` is `\"01\"`. Three iterations give `\"0123\"`. Starting with `let total = 0;` would give the number 6. The answer is B."
          }
        },
        {
          "id": "q10",
          "skill": "The for…of loop",
          "prompt": "What does this print?",
          "code": "let result = \"\";\nfor (const ch of \"abc\") {\n  result = ch + result;\n}\nconsole.log(result);",
          "answers": [
            {
              "key": "A",
              "text": "`cba`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`abc`",
              "tag": "each character added to the end",
              "correct": false
            },
            {
              "key": "C",
              "text": "`210`",
              "tag": "ch read as a position",
              "correct": false
            },
            {
              "key": "D",
              "text": "`c`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `ch` is each character in turn: `\"a\"`, then `\"b\"`, then `\"c\"`. Each iteration puts the new character in front of what is there, so the string builds up as `\"a\"`, `\"ba\"`, `\"cba\"`.",
            "wrong": "`for…of` hands each iteration the next character, never its position, so `ch` is `\"a\"`, `\"b\"` and `\"c\"`, not 0, 1 and 2. `ch + result` puts the new character in front: `\"a\"`, `\"ba\"`, `\"cba\"`. The answer is A."
          }
        },
        {
          "id": "q4",
          "skill": "break and continue",
          "prompt": "What does `total` hold when the loop ends?",
          "code": "let total = 0;\nfor (let i = 1; i <= 6; i++) {\n  if (i % 3 === 0) {\n    continue;\n  }\n  total += i;\n}",
          "answers": [
            {
              "key": "A",
              "text": "`3`",
              "tag": "continue read as break",
              "correct": false
            },
            {
              "key": "B",
              "text": "`21`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`12`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`9`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `continue` ends only the current iteration. For 3 and 6, `i % 3` is 0, so `total += i` is skipped and the loop goes on: 1 + 2 + 4 + 5 is 12.",
            "wrong": "`continue` skips the rest of one iteration; `break` would end the loop. The iterations for 3 and 6 skip `total += i`, and every other iteration adds its number: 1 + 2 + 4 + 5 is 12. With `break` instead, the loop would end at 3 with `total` at 3. The answer is C."
          }
        },
        {
          "id": "q5",
          "skill": "Choosing a loop",
          "prompt": "A message has to be copied with every `!` left out. Which loop fits, and why?",
          "answers": [
            {
              "key": "A",
              "text": "`for…of` — each iteration needs one character, not its position",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "A `for` loop — going through a string needs an index",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`while` — you cannot know how many `!` there are",
              "tag": "counts the matches, not the iterations",
              "correct": false
            },
            {
              "key": "D",
              "text": "Two loops, one inside the other — one for the message, one for the `!`s",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Each iteration looks at one character and keeps it unless it is `!`. Nothing needs a position, and `for…of` hands over exactly the character, one per iteration, stopping when the characters run out.",
            "wrong": "Ask what each iteration needs: one character, to keep or leave out. Its position is never used, so `for…of` fits, and it cannot run one iteration too many. The number of iterations is known — one per character — even if the number of `!` is not, so this is not a `while`. The answer is A."
          }
        },
        {
          "id": "q6",
          "skill": "The ternary operator",
          "prompt": "What does `line` hold when the loop ends?",
          "code": "let line = \"\";\nfor (let i = 1; i <= 4; i++) {\n  line += i % 2 === 0 ? \"-\" : i;\n}",
          "answers": [
            {
              "key": "A",
              "text": "`\"1-3-\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`\"-2-4\"`",
              "tag": "the two values swapped",
              "correct": false
            },
            {
              "key": "C",
              "text": "`\"1234\"`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`\"1-3\"`",
              "tag": "one iteration too few",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. On each iteration the ternary operator gives one value to add: `\"-\"` when `i % 2 === 0`, that is when `i` is even, and `i` itself when `i` is odd. The four iterations add 1, `\"-\"`, 3 and `\"-\"`.",
            "wrong": "Take one iteration at a time: `i` is 1, 2, 3, then 4. When `i % 2 === 0` is `true` (`i` is even), the ternary operator gives the value after the `?`, `\"-\"`; otherwise it gives the value after the `:`, the number `i`. Adding to a string joins, so `line` becomes `\"1-3-\"`. The answer is A."
          }
        },
        {
          "id": "q7",
          "skill": "Counting the inner loop's iterations",
          "prompt": "How long is `stars` when the loops end?",
          "code": "let stars = \"\";\nfor (let r = 1; r <= 3; r++) {\n  for (let c = r; c <= 3; c++) {\n    stars += \"*\";\n  }\n}",
          "answers": [
            {
              "key": "A",
              "text": "`9`",
              "tag": "rows × columns, but the inner start moves",
              "correct": false
            },
            {
              "key": "B",
              "text": "`3`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`6`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`7`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The inner loop starts at `r`, so it runs fewer iterations each time: `c` runs 1 to 3 (three), then 2 to 3 (two), then 3 to 3 (one). 3 + 2 + 1 is 6.",
            "wrong": "Count one value of `r` at a time. The inner counter starts at `r`, not at 1. When `r` is 1, `c` is 1, 2, 3; when `r` is 2, `c` is 2, 3; when `r` is 3, just 3. That is 3 + 2 + 1 = 6 stars — 3 × 3 only when the inner loop always runs the same number of iterations. The answer is C."
          }
        },
        {
          "id": "q8",
          "skill": "Which counter changes when",
          "prompt": "What does `out` hold when the loops end?",
          "code": "let out = \"\";\nfor (let a = 1; a <= 2; a++) {\n  for (let b = 1; b <= 3; b++) {\n    out += a;\n  }\n  out += \"|\";\n}",
          "answers": [
            {
              "key": "A",
              "text": "`\"123|123|\"`",
              "tag": "read the inner counter",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"111|222|\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`\"12|12|12|\"`",
              "tag": "the loops swapped",
              "correct": false
            },
            {
              "key": "D",
              "text": "`\"111222||\"`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Each iteration of the outer loop runs the whole inner loop, three iterations, and each of those adds `a`, which does not change until the inner loop is over. Then the `|` is added, once per iteration of the outer loop.",
            "wrong": "Trace both counters. While `a` is 1, the inner loop runs three iterations, each adding `a`, so `\"111\"`; after the inner loop, still inside the outer one, `\"|\"` is added. Then `a` becomes 2 and the same happens again. The line adds `a`, not `b`, and the `|` comes after each row. The answer is B."
          }
        }
      ],
      "_source": "checks/check-loops.yml"
    },
    "arrays-basics": {
      "id": "arrays-basics",
      "title": "Lists of values",
      "kind": "read",
      "outcome": 2,
      "shape": "procedural",
      "why": "`arr.basics` (concepts.md): create an array, read and replace an element by index, use `.length` and the last index, say what an index past the end gives, and say why an empty array is still truthy. D-042 found that no lesson taught creating an array, because arrays were assumed known; D-043 placed Arrays after Loops, and D-069 gave the module six concepts, this one first. It builds on a string's index and `.length` (*Working with strings*) and carries truthiness's second pass (the empty array, moved here when Values was rebuilt). Procedural, explain first (D-061 item 1); the writing direction is modelled fail-first (01-explanations rule 5a) on the concept's own mistake, the last index taken as `.length`. Derived from those rules, pending Nick's review.",
      "concepts": [
        "arr.basics"
      ],
      "lede": "A program that keeps track of three temperatures can give each one its own name. A program that keeps track of a month of them cannot. An array holds any number of values, in order, under one name. This lesson shows how to make one, read and replace what is in it, and find its last element, and why an empty array still counts as true.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Working with strings · Truthiness · Adding and removing elements"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Array** — an ordered list of values held under one name. **Element** — one value in an array. **Index** — an element's position, counting from 0. **Array literal** — an array written out in square brackets: `[3, 1, 4]`. **Empty array** — an array with no elements, `[]`. **Composite type** — a type whose values are built from other values, as an array is from its elements; not a primitive.",
          "entries": [
            "**Array** — an ordered list of values held under one name.",
            "**Element** — one value in an array.",
            "**Index** — an element's position, counting from 0.",
            "**Array literal** — an array written out in square brackets: `[3, 1, 4]`.",
            "**Empty array** — an array with no elements, `[]`.",
            "**Composite type** — a type whose values are built from other values, as an array is from its elements; not a primitive."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory: reading a string by\nits index, its length and its last character, and which values are falsy. Type\nthe value, with quotes if it is text. Press **I don't know** if you have no\nidea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const word = \"plant\";\nword[1]\n",
                    "accept": [
                      "\"l\"",
                      "l",
                      "'l'"
                    ],
                    "miss": "**Indexes start at 0**, from *Working with strings*: `word[0]` is `\"p\"`, so `word[1]` is the second character.\n",
                    "why": "`\"l\"`.\n"
                  },
                  {
                    "code": "const word = \"plant\";\nword[word.length - 1]\n",
                    "accept": [
                      "\"t\"",
                      "t",
                      "'t'"
                    ],
                    "miss": "**`word.length` is 5, so `word.length - 1` is 4**, the index of the last character.\n",
                    "why": "`\"t\"`. The last index is one less than the length.\n"
                  },
                  {
                    "code": "\"cat\"[3]",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**Reading past the end is not an error.** `\"cat\"` has indexes 0, 1 and 2; there is no index 3, so the value is `undefined`.\n",
                    "why": "`undefined`.\n"
                  },
                  {
                    "code": "Boolean(\"\")",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**The empty string is on the falsy list**, from *Truthiness*. It is the one string that counts as false.\n",
                    "why": "`false`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can create an array, and read an element by its index",
                "I can replace an element, and use `.length` to find the last one",
                "I can say what reading past the end of an array gives, and why an empty array is still truthy"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Many values under one name",
          "body": [
            {
              "p": "Here is a program that keeps three temperature readings, one variable each:"
            },
            {
              "code": "const reading1 = 12;\nconst reading2 = 15;\nconst reading3 = 9;\nconst sum = reading1 + reading2 + reading3;\nconst average = sum / 3;\nconsole.log(average);   // 12",
              "lang": "js",
              "file": "three-names.js"
            },
            {
              "p": "That works for three. For thirty readings it needs thirty variables, and the\nline that adds them up has to name all thirty."
            },
            {
              "p": "A program's variables are the ones written in its code, decided before it\nruns. A running program cannot create a new variable. A loop cannot help\neither: its loop variable counts 1, 2, 3, but a number is not a variable's\nname, so the loop cannot reach `reading1`, `reading2` and `reading3` one after\nanother."
            },
            {
              "p": "An **array** is a container of values. It holds zero or more of them, in\norder, and one variable can hold the array:"
            },
            {
              "code": "const readings = [12, 15, 9];",
              "lang": "js",
              "file": "readings.js"
            },
            {
              "p": "The square brackets with values inside, separated by commas, are an **array\nliteral**: an array written out in full. Each value in it is an **element**.\nThis array has three elements, and the variable `readings` holds the array."
            },
            {
              "p": "An array is not one of the primitive types from *Where the type actually\nlives*, where each value is a single piece of data. Arrays are a **composite\ntype**: a type whose values are built from other values."
            },
            {
              "p": "An array is read like a string. Each element has an **index**, its position,\nand indexes start at 0. **Bracket notation**, the index in square brackets\nafter the array, reads one element:"
            },
            {
              "indexes": {
                "variable": "readings",
                "items": [
                  "12",
                  "15",
                  "9"
                ],
                "kind": "element"
              }
            },
            {
              "code": "const readings = [12, 15, 9];\nreadings[0]     // 12\nreadings[1]     // 15\nreadings[2]     // 9",
              "lang": "js",
              "file": "readings.js"
            },
            {
              "p": "`readings[0]` evaluates to the element at index 0, the number `12`. A string's\nbracket notation reads a character; an array's reads whatever value is stored\nat that index."
            },
            {
              "p": "An array can hold any kind of value. These are arrays of strings and of\nbooleans:"
            },
            {
              "code": "const days = [\"Mon\", \"Tue\", \"Wed\"];\nconst answers = [true, false, true];\ndays[2]        // \"Wed\"\nanswers[0]     // true",
              "lang": "js",
              "file": "elements.js"
            },
            {
              "p": "`[]`, with nothing between the brackets, is an **empty array**: an array with no\nelements."
            },
            {
              "p": "JavaScript also lets one array mix kinds of value: `[12, \"Mon\", true]` is\nlegal. Many languages forbid it. In C#, an array is declared with the type of\nits elements, and a string cannot go into an array of numbers. This course keeps\none kind of value in each array, because the code that reads an array is\nwritten for one kind: a line that adds up `readings` would join a `\"Mon\"` onto\nthe total as text instead of adding it."
            },
            {
              "p": "When `console.log` prints an array, Node shows it with spaces inside the\nbrackets, `[ 12, 15, 9 ]`. The editor on this site shows `[12,15,9]`. Both\nare the same array."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const colours = [\"red\", \"green\", \"blue\"];\ncolours[1]\n",
                    "accept": [
                      "\"green\"",
                      "green",
                      "'green'"
                    ],
                    "miss": "**Indexes start at 0.** `colours[0]` is `\"red\"`, so `colours[1]` is the second element.\n",
                    "why": "`\"green\"`.\n"
                  },
                  {
                    "code": "const sizes = [8, 10, 12, 14];\nsizes[0] + sizes[3]\n",
                    "accept": [
                      "22"
                    ],
                    "miss": "**`sizes[0]` is the first element, 8, and `sizes[3]` is the fourth, 14.** Each one is a number, so `+` adds them.\n",
                    "why": "`22`.\n"
                  },
                  {
                    "question": "How many elements does `[\"a\", \"b\", \"c\", \"d\"]` have? Type the number.",
                    "accept": [
                      "4",
                      "four"
                    ],
                    "why": "`4`. Its indexes are 0, 1, 2 and 3.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Length, the last element, and past the end",
          "body": [
            {
              "p": "An array has a **length**, the number of elements it holds, read with\n`.length` as on a string:"
            },
            {
              "code": "[12, 15, 9].length                // 3\n[\"Mon\", \"Tue\", \"Wed\", \"Thu\"].length   // 4\n[].length                         // 0",
              "lang": "js",
              "file": "length.js"
            },
            {
              "p": "Because indexes start at 0, the last element is at the index **one less than\nthe length**, as with a string. Two worked examples:"
            },
            {
              "code": "const readings = [12, 15, 9];\n// the length is 3, so the last index is 3 - 1, which is 2\nreadings[readings.length - 1]     // 9",
              "lang": "js",
              "file": "last.js"
            },
            {
              "code": "const days = [\"Mon\", \"Tue\", \"Wed\", \"Thu\"];\n// the length is 4, so the last index is 4 - 1, which is 3\ndays[days.length - 1]             // \"Thu\"",
              "lang": "js",
              "file": "last.js"
            },
            {
              "p": "`readings[2]` would also read the `9` here, but only because this array has\nthree elements. `readings[readings.length - 1]` reads the last element of an\narray of any length."
            },
            {
              "p": "Reading an index past the end is not an error. It evaluates to `undefined`,\nJavaScript's \"nothing here\", and the program carries on:"
            },
            {
              "code": "const readings = [12, 15, 9];\nreadings[3]       // undefined\nreadings[50]      // undefined",
              "lang": "js",
              "file": "past-the-end.js"
            },
            {
              "p": "A **negative index** gives `undefined` too: `readings[-1]` is `undefined`. This\nis one place where languages differ. In Python, `readings[-1]` is the last\nelement, counting back from the end. In JavaScript, bracket notation only counts\nforward from 0, so `-1` is not an index of the array at all. JavaScript has a\nseparate method, `readings.at(-1)`, that counts back from the end the way Python\ndoes. Both are legal. This course writes `readings[readings.length - 1]`,\nbecause the idea under it, that the last index is one less than the length,\nholds in every language with arrays, and the lessons after this one build on\nit."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const scores = [40, 75, 62, 90, 58];\nscores[scores.length - 1]\n",
                    "accept": [
                      "58"
                    ],
                    "miss": "**`scores.length` is 5, so the last index is 4**, and `scores[4]` is `58`.\n",
                    "why": "`58`.\n"
                  },
                  {
                    "code": "const scores = [40, 75, 62];\nscores[scores.length]\n",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**`scores.length` is 3, and the indexes are 0, 1 and 2.** Index 3 is past the end, so the value is `undefined`. No error is reported.\n",
                    "why": "`undefined`.\n"
                  },
                  {
                    "code": "const names = [\"Ada\", \"Grace\"];\nnames[-1]\n",
                    "choices": [
                      "\"Grace\"",
                      "undefined",
                      "an error"
                    ],
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**If you chose `\"Grace\"` — that is what Python does.** In JavaScript, bracket notation only counts forward from 0, so `-1` is no index of the array, and reading it gives `undefined`.\n",
                    "why": "`undefined`, with no error.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Replacing an element",
          "body": [
            {
              "p": "Bracket notation on the left of `=` replaces the element at that index:"
            },
            {
              "code": "const readings = [12, 15, 9];\nreadings[1] = 16;\nconsole.log(readings);       // [12, 16, 9]",
              "lang": "js",
              "file": "replace.js"
            },
            {
              "p": "`readings[1] = 16` puts `16` at index 1, where `15` was. The other elements\nstay where they are, and the length is still 3."
            },
            {
              "p": "The value on the right can be any expression, including another element:"
            },
            {
              "code": "const days = [\"Mon\", \"Tue\", \"Wed\"];\ndays[0] = days[2];\nconsole.log(days);           // [\"Wed\", \"Tue\", \"Wed\"]",
              "lang": "js",
              "file": "replace.js"
            },
            {
              "p": "JavaScript works out the right side first: `days[2]` is `\"Wed\"`. Then it stores\n`\"Wed\"` at index 0. The `\"Mon\"` that was there is gone; nothing keeps a copy\nof it."
            },
            {
              "p": "**Arrays can be changed; strings cannot.** *Working with strings* showed that\na string is immutable: once made, it never changes. The same assignment on a\nstring stops with an error in strict mode, which this course assumes is on:"
            },
            {
              "code": "const word = \"java\";\nword[0] = \"J\";   // TypeError",
              "lang": "js",
              "file": "replace.js"
            },
            {
              "p": "Line 2 stops with `TypeError: Cannot assign to read only property '0' of\nstring 'java'`. Without strict mode, the assignment silently does nothing and `word` still\nholds `\"java\"`, as *Working with strings* explained. An array has no such\nrule: the element at any index can be replaced."
            },
            {
              "p": "**A `const` array can still have its elements replaced.** `readings` was\ndeclared with `const` above, and `readings[1] = 16` worked. `const` stops the\n*name* `readings` from being given a different value: `readings = [1, 2]`\nstops with `TypeError: Assignment to constant variable.` It does not stop\nthe array from changing. Why the two are different is the subject of\n*Copying versus aliasing*, in the Values and references module. For now:\n`const` fixes which array the name holds, and the array's elements can still\nbe replaced."
            },
            {
              "p": "What happens when you assign to an index past the end, `readings[10] = 4`, is\nthe next lesson's subject, *Adding and removing elements*, with the methods\nbuilt for making an array longer and shorter."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const marks = [5, 3, 8];\nmarks[2] = 1;\nmarks\n",
                    "accept": [
                      "[5, 3, 1]",
                      "[5,3,1]",
                      "[ 5, 3, 1 ]"
                    ],
                    "miss": "**`marks[2] = 1` replaces the element at index 2**, the third one, which was `8`.\n",
                    "why": "`[5, 3, 1]`. Type it with its square brackets.\n"
                  },
                  {
                    "code": "const a = [4, 7];\na[0] = a[1] + 1;\na\n",
                    "accept": [
                      "[8, 7]",
                      "[8,7]",
                      "[ 8, 7 ]"
                    ],
                    "miss": "**The right side is worked out first:** `a[1] + 1` is `7 + 1`, which is 8. Then 8 is stored at index 0.\n",
                    "why": "`[8, 7]`.\n"
                  },
                  {
                    "question": "`const list = [1, 2, 3];` — which line stops with a TypeError?",
                    "choices": [
                      "list[0] = 9;",
                      "list = [9, 2, 3];",
                      "Neither"
                    ],
                    "accept": [
                      "list = [9, 2, 3];"
                    ],
                    "miss": "**`const` fixes which array the name holds, not what is in it.** `list[0] = 9` replaces an element of the same array, so it is allowed. `list = [9, 2, 3]` gives the name a different array, which `const` forbids.\n",
                    "why": "`list = [9, 2, 3];` stops with `TypeError: Assignment to constant variable.` Replacing an element is allowed.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "An empty array is still truthy",
          "body": [
            {
              "p": "*Working with strings* and *Truthiness* showed that the empty string, `\"\"`, is\nfalsy. A beginner expects the same of the empty array, and JavaScript does not\nwork that way:"
            },
            {
              "code": "Boolean(\"\")       // false\nBoolean([])       // true",
              "lang": "js",
              "file": "empty.js"
            },
            {
              "p": "`[]` is not on the falsy list, and *Truthiness* gave the rule: every value not\non the list is truthy. **Every array is truthy, empty or not.** So an `if`\nthat tests an array runs its block whatever the array holds:"
            },
            {
              "code": "const waiting = [];\nif (waiting) {\n  console.log(\"someone is waiting\");    // printed, though no one is\n}",
              "lang": "js",
              "file": "empty.js"
            },
            {
              "p": "To ask whether an array has any elements, ask about its length:"
            },
            {
              "code": "const waiting = [];\nif (waiting.length > 0) {\n  console.log(\"someone is waiting\");    // not printed\n}",
              "lang": "js",
              "file": "empty.js"
            },
            {
              "p": "`waiting.length` is `0`, and `0 > 0` is `false`. `waiting.length === 0` asks\nthe opposite question, \"is it empty?\"."
            },
            {
              "p": "This is another place where languages differ. In Python, an empty list is\nfalsy, so Python programmers write `if waiting:` to mean \"if anyone is\nwaiting\". The same habit in JavaScript tests nothing, because the condition is\nalways true."
            },
            {
              "p": "The loose equality operator makes it more confusing still: `[] == false` is\n`true`, even though `Boolean([])` is `true` too. *Equality: == versus ===*\nshowed that `==` converts before it compares. It converts one side at a time,\nand it keeps going until both sides are the same type. Here that takes three\nconversions, two of them on the left:"
            },
            {
              "coercion": {
                "left": [
                  "[]",
                  "\"\"",
                  "0"
                ],
                "right": [
                  "false",
                  "0"
                ],
                "op": "==",
                "steps": {
                  "left": [
                    2,
                    3
                  ],
                  "right": [
                    1
                  ]
                }
              }
            },
            {
              "list": [
                "**A boolean converts first, to a number.** `false` becomes `0`, as in *Equality*. That leaves `[] == 0`: an array and a number, still two different types.",
                "**An array converts to a string.** `==` cannot compare an array with a number, so the array becomes a string of its elements. `[]` has no elements, so it becomes the empty string: `\"\" == 0`.",
                "**A string compared with a number converts to a number.** `\"\"` becomes `0`, as in *Equality*: `0 == 0`."
              ],
              "ordered": true
            },
            {
              "p": "Both sides are now the number `0`, so `[] == false` is `true`. That is one\nmore reason this course writes `===`, which compares without converting:\n`[] === false` is `false`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "Boolean([0])",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "miss": "**Every array is truthy, whatever it holds.** `[0]` is an array with one element; that the element is falsy does not matter.\n",
                    "why": "`true`.\n"
                  },
                  {
                    "question": "`const items = [];` — which condition is `true` only when `items` has at least one element?",
                    "choices": [
                      "items",
                      "items.length > 0",
                      "items !== \"\""
                    ],
                    "accept": [
                      "items.length > 0"
                    ],
                    "miss": "**`items` on its own is always truthy**, empty or not. **`items !== \"\"` is always `true` too:** an array is never the same value as a string, and `!==` does not convert.\n",
                    "why": "`items.length > 0`. The length is 0 exactly when the array is empty.\n"
                  },
                  {
                    "code": "const queue = [];\nlet message = \"empty\";\nif (queue) {\n  message = \"has items\";\n}\nmessage\n",
                    "accept": [
                      "\"has items\"",
                      "has items",
                      "'has items'"
                    ],
                    "miss": "**An empty array is truthy**, so `if (queue)` runs its block and `message` is reassigned.\n",
                    "why": "`\"has items\"`, although `queue` has none. The condition should be `queue.length > 0`.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "From a sentence to a function",
          "body": [
            {
              "p": "So far you have read code that uses arrays. Exercises start from the other\nend: a sentence saying what a function returns, and the function to fill in.\nThe four subgoals from *Writing a function's answer* still apply:"
            },
            {
              "list": [
                "**Name the values you have.** Here, one of them is an array.",
                "**Find the question in the sentence.** Which element, or what about the array, does it ask for?",
                "**Write it with the operators**, here bracket notation and `.length`.",
                "**Send the answer out** with `return`."
              ],
              "ordered": true
            },
            {
              "p": "**Example 1.** \"`latest` takes an array of readings, oldest first, and returns\nthe newest one: `latest([12, 15, 9])` is `9`.\""
            },
            {
              "p": "The newest reading is the last element, and the last element is at the end of\nthe array, so a first try reads the element at the array's length:"
            },
            {
              "code": "function latest(readings) {\n  return readings[readings.length];\n}",
              "lang": "js",
              "file": "latest.js"
            },
            {
              "p": "It runs without an error. The tests reported:"
            },
            {
              "code": "✗ latest([12, 15, 9]) → 9\n✗ latest([7]) → 7\n✓ latest([]) → undefined",
              "lang": "text"
            },
            {
              "p": "`latest([12, 15, 9])` returned `undefined`. Work it through by hand with\n`readings` holding `[12, 15, 9]`. `readings.length` is `3`, so\n`readings[readings.length]` is `readings[3]`:"
            },
            {
              "table": {
                "head": [
                  "Expression",
                  "Value"
                ],
                "rows": [
                  [
                    "`readings.length`",
                    "`3`"
                  ],
                  [
                    "`readings[3]`",
                    "`undefined`"
                  ]
                ]
              }
            },
            {
              "p": "The array has indexes 0, 1 and 2. The length counts the elements, 3 of them,\nbut the indexes start at 0, so the length is always one more than the last\nindex. The test with `[]` passed only because an empty array has no last\nelement, and `undefined` was the right answer for it."
            },
            {
              "p": "The last index is the length minus one:"
            },
            {
              "code": "function latest(readings) {\n  return readings[readings.length - 1];\n}",
              "lang": "js",
              "file": "latest.js"
            },
            {
              "code": "✓ latest([12, 15, 9]) → 9\n✓ latest([7]) → 7\n✓ latest([]) → undefined",
              "lang": "text"
            },
            {
              "p": "**Example 2: you choose the line.** \"`firstOrNone` takes an array of names and\nreturns the first name, or `\"none\"` when the array is empty:\n`firstOrNone([\"Ada\", \"Lin\"])` is `\"Ada\"`, and `firstOrNone([])` is `\"none\"`.\"\nThe condition is missing, marked `TODO`."
            },
            {
              "code": "function firstOrNone(names) {\n  if (/* TODO */) {\n    return \"none\";\n  }\n  return names[0];\n}",
              "lang": "js",
              "file": "first.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which condition goes at the TODO?",
                    "choices": [
                      "names.length === 0",
                      "!names",
                      "names.length === 1"
                    ],
                    "accept": [
                      "names.length === 0"
                    ],
                    "miss": "**If you chose `!names` — an array is always truthy,** so `!names` is always `false`, and `firstOrNone([])` would return `names[0]`, which is `undefined`. **If you chose `names.length === 1` — that is true for an array with one name,** which should return that name, not `\"none\"`.\n",
                    "why": "`names.length === 0`. The length is 0 exactly when the array is empty.\n"
                  },
                  {
                    "question": "Without the `if`, what would `firstOrNone([])` return? Type the value.",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**`names[0]` on an empty array reads past the end**, and reading past the end gives `undefined`.\n",
                    "why": "`undefined`.\n"
                  },
                  {
                    "question": "`latest` returns `readings[readings.length - 1]`. What does `latest([4, 4, 2, 5])` return? Type the number.",
                    "accept": [
                      "5"
                    ],
                    "miss": "**The length is 4, so the last index is 3**, and `readings[3]` is `5`.\n",
                    "why": "`5`.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**An array literal, `[12, 15, 9]`, holds values in order under one name.** Bracket notation reads an element by its index, counting from 0.",
                "**The last element is at `.length - 1`.** Reading past the end, or at a negative index, gives `undefined`, with no error.",
                "**`list[i] = value` replaces an element**, even in a `const` array; a string cannot be changed that way.",
                "**Every array is truthy, even `[]`.** Test `list.length > 0` to ask whether it has elements."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array",
              "note": "the sections \"Description\" and \"Accessing array elements\"; the methods come in the next two lessons."
            }
          ]
        }
      ],
      "_source": "lessons/arrays-basics.md",
      "practice": {
        "id": "arrays-basics",
        "outcome": 2,
        "steps": [
          {
            "kind": "predict",
            "title": "Reading by index",
            "prompt": "Type the value each one evaluates to, or press the answer. Write text with its quotes.\n",
            "items": [
              {
                "id": "p1",
                "code": "const xs = [5, 10, 15, 20];\nxs[2]\n",
                "accept": [
                  "15"
                ],
                "miss": "**Indexes start at 0.** `xs[0]` is 5, `xs[1]` is 10, so `xs[2]` is the third element.\n",
                "why": "`15`.\n"
              },
              {
                "id": "p2",
                "code": "const xs = [5, 10, 15];\nxs[xs.length]\n",
                "accept": [
                  "undefined"
                ],
                "miss": "**`xs.length` is 3, and the indexes are 0, 1 and 2.** Index 3 is past the end, and reading past the end gives `undefined`, with no error.\n",
                "why": "`undefined`.\n"
              },
              {
                "id": "p3",
                "question": "`lastName` should return the last element of the array `names`. Which expression does that?",
                "choices": [
                  "names[names.length - 1]",
                  "names[names.length]",
                  "names[-1]"
                ],
                "accept": [
                  "names[names.length - 1]"
                ],
                "miss": "**If you chose `names[names.length]` — the length is one more than the last index,** so that reads past the end. **If you chose `names[-1]` — that is Python's rule.** In JavaScript a negative index is no index of the array, and gives `undefined`.\n",
                "why": "`names[names.length - 1]`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "Boolean([])",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**Every array is truthy, even an empty one.** `[]` is not on the falsy list. The empty *string*, `\"\"`, is.\n",
                "why": "`true`.\n"
              },
              {
                "id": "p5",
                "code": "[\"up\", \"down\"].length + [].length",
                "accept": [
                  "2"
                ],
                "miss": "**`.length` counts elements.** `[\"up\", \"down\"]` has two, and `[]` has none.\n",
                "why": "`2`.\n"
              },
              {
                "id": "p6",
                "code": "const pair = [\"left\", \"right\"];\npair[1] = \"up\";\npair[1]\n",
                "accept": [
                  "\"up\"",
                  "up",
                  "'up'"
                ],
                "miss": "**`pair[1] = \"up\"` replaces the element at index 1**, and a `const` array's elements can be replaced. Only the name `pair` cannot be given a different array.\n",
                "why": "`\"up\"`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered as if indexes started at 1",
                "say": "What is the index of the first element? Count the positions from that number.\n"
              },
              {
                "when": "you expected an error for an index past the end",
                "say": "What did *Working with strings* say `\"cat\"[3]` evaluates to? An array behaves the same way.\n"
              },
              {
                "when": "you chose names[-1]",
                "say": "Which way does bracket notation count in JavaScript: forward from 0, or back from the end too?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "A queue, run",
            "prompt": "Predict each line this program prints, then run it and compare.\n",
            "filename": "queue.js",
            "code": "const queue = [\"Kai\", \"Rosa\", \"Ines\"];\nconsole.log(queue.length);\nconsole.log(queue[queue.length - 1]);\n\nqueue[0] = \"Tomas\";\nconsole.log(queue[0] + \" and \" + queue[1]);\nconsole.log(queue[3]);\n\nconst waitingList = [];\nconsole.log(waitingList ? \"busy\" : \"quiet\");\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print? Type the number.",
                "accept": [
                  "3"
                ],
                "why": "`3`. Three names.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print? Type it.",
                "accept": [
                  "Ines",
                  "\"Ines\""
                ],
                "miss": "**The length is 3, so the last index is 2**, and `queue[2]` is `\"Ines\"`.\n",
                "why": "`Ines`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print? Type it.",
                "accept": [
                  "Tomas and Rosa",
                  "\"Tomas and Rosa\""
                ],
                "miss": "**`queue[0] = \"Tomas\"` replaced `\"Kai\"`.** Index 1 was not touched, so it still holds `\"Rosa\"`.\n",
                "why": "`Tomas and Rosa`.\n"
              },
              {
                "id": "r4",
                "question": "What does the fourth line print? Type it.",
                "accept": [
                  "undefined"
                ],
                "miss": "**Replacing an element does not change the length.** `queue` still has indexes 0 to 2, so index 3 is past the end.\n",
                "why": "`undefined`.\n"
              },
              {
                "id": "r5",
                "question": "What does the last line print? Type it.",
                "accept": [
                  "busy",
                  "\"busy\""
                ],
                "miss": "**An empty array is truthy**, so the ternary operator chooses the value after `?`.\n",
                "why": "`busy`, though no one is waiting. `waitingList.length > 0` is the condition that means \"anyone waiting?\".\n"
              }
            ],
            "hints": [
              {
                "when": "you expected Kai and Rosa",
                "say": "Which line runs before the third `console.log`? What does it do to index 0?\n"
              },
              {
                "when": "you expected quiet",
                "say": "Is `[]` on the falsy list from *Truthiness*? What does the ternary operator choose when its condition is truthy?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One replacement at a time",
            "prompt": "Work each one through line by line, writing down the array after each line, and type the value of the last line.\n",
            "items": [
              {
                "id": "t1",
                "code": "const a = [3, 6, 9];\na[0] = a[1];\na[1] = a[2];\na[0] + a[1]\n",
                "accept": [
                  "15"
                ],
                "miss": "**After line 2, `a` is `[6, 6, 9]`; after line 3, `[6, 9, 9]`.** So `a[0] + a[1]` is `6 + 9`.\n",
                "why": "`15`.\n"
              },
              {
                "id": "t2",
                "code": "const p = [\"x\", \"y\", \"z\"];\nconst i = p.length - 2;\np[i] = p[i - 1] + p[i + 1];\np[1]\n",
                "accept": [
                  "\"xz\"",
                  "xz",
                  "'xz'"
                ],
                "miss": "**`p.length - 2` is 1, so `i` is 1.** Line 3 stores `p[0] + p[2]`, which is `\"x\" + \"z\"`, at index 1.\n",
                "why": "`\"xz\"`.\n"
              },
              {
                "id": "t3",
                "code": "const temps = [14, 22, 19];\nconst last = temps[temps.length - 1];\nlast > temps[0] ? \"warmer\" : \"cooler\"\n",
                "accept": [
                  "\"warmer\"",
                  "warmer",
                  "'warmer'"
                ],
                "miss": "**`last` is the last element, 19, and `temps[0]` is 14.** `19 > 14` is `true`, so the ternary operator chooses `\"warmer\"`.\n",
                "why": "`\"warmer\"`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "const q = [1, 2];\nq[q.length - 1] = q.length;\nq[1]\n",
                "accept": [
                  "2"
                ],
                "miss": "**`q.length - 1` is 1, and `q.length` is 2,** so line 2 stores 2 at index 1, where 2 already was.\n",
                "why": "`2`. The array is unchanged.\n"
              },
              {
                "id": "t5",
                "code": "const w = [\"ab\", \"cd\"];\nw[0] = w[0] + w[1];\nw[0].length\n",
                "accept": [
                  "4"
                ],
                "miss": "**`w[0]` is now `\"abcd\"`, a string of four characters.** `.length` on an element that is a string counts its characters.\n",
                "why": "`4`.\n"
              }
            ],
            "hints": [
              {
                "when": "your answer to the first one used the original values",
                "say": "Line 3 reads `a[2]`, but line 2 has already changed `a[0]`. Write the array out after each line before you add.\n"
              },
              {
                "when": "you got i wrong in the second one",
                "say": "What is `p.length`? Take 2 away from it.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Swap the ends",
            "prompt": "`swapEnds` should swap the first and last elements of `list` and return the array: `swapEnds([1, 2, 3, 4])` is `[4, 2, 3, 1]`. `list` always has at least one element. Build the lines that go between its braces, in order. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "  const first = list[0];",
              "  list[0] = list[list.length - 1];",
              "  list[list.length - 1] = first;",
              "  return list;"
            ],
            "distractors": [
              {
                "line": "  list[list.length - 1] = list[0];",
                "why": "By the time this line runs, `list[0]` already holds the last element. It copies that element back where it came from, and the first element is lost: `[1, 2, 3, 4]` becomes `[4, 2, 3, 4]`.\n"
              },
              {
                "line": "  list[0] = list[list.length];",
                "why": "`list[list.length]` is past the end, so this puts `undefined` at index 0.\n"
              }
            ],
            "why": "Replacing `list[0]` throws away what was there, so the first element is saved in `first` before that line runs. Then the last element goes to index 0, and the saved first element goes to the last index.\n",
            "hints": [
              {
                "when": "you are unsure where const first goes",
                "say": "After `list[0] = …` runs, where is the old first element? Save it before that line, not after.\n"
              },
              {
                "when": "you are unsure which line puts the first element at the end",
                "say": "When the last line before `return` runs, what does `list[0]` hold? Is it still the first element?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Counting from the end",
            "prompt": "`fromEnd` returns the last element of `list`. Change it so it returns the element `n` places from the end, where `n` of 1 means the last element: `fromEnd([\"a\", \"b\", \"c\", \"d\"], 2)` should be `\"c\"`. Write your code between the braces, and keep `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: why is the last element `n` of 1 and not 0?\n",
            "exportName": "fromEnd",
            "filename": "from-end.js",
            "starter": "function fromEnd(list, n) {\n  // TODO: put your code here\n  return list[list.length - 1];\n}\n",
            "tests": [
              {
                "label": "fromEnd([\"a\", \"b\", \"c\", \"d\"], 1) → \"d\"",
                "body": "return subject(['a', 'b', 'c', 'd'], 1) === 'd';"
              },
              {
                "label": "fromEnd([\"a\", \"b\", \"c\", \"d\"], 2) → \"c\"",
                "body": "return subject(['a', 'b', 'c', 'd'], 2) === 'c';"
              },
              {
                "label": "fromEnd([\"a\", \"b\", \"c\", \"d\"], 4) → \"a\"",
                "body": "return subject(['a', 'b', 'c', 'd'], 4) === 'a';"
              },
              {
                "label": "fromEnd([7, 8], 1) → 8",
                "body": "return subject([7, 8], 1) === 8;"
              }
            ],
            "hints": [
              {
                "when": "fromEnd([\"a\", \"b\", \"c\", \"d\"], 2) gives \"b\"",
                "say": "You counted one place too far. With `n` of 1 the answer is the last element, at `list.length - 1`. What expression gives `list.length - 1` when `n` is 1?\n"
              },
              {
                "when": "fromEnd([\"a\", \"b\", \"c\", \"d\"], 1) gives undefined",
                "say": "With `n` of 1 your index works out to `list.length`, which is past the end. Work it out by hand for a list of length 4: the last element is at index 3.\n"
              },
              {
                "when": "fromEnd([\"a\", \"b\", \"c\", \"d\"], 1) gives \"b\"",
                "say": "You counted from the front: `list[n]`. Counting from the end starts at `list.length`. What do you take away from it?\n"
              },
              {
                "when": "every test gives the last element",
                "say": "The starter's line does not use `n`. Where in the index does `n` belong?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "The middle element",
            "prompt": "`middle` takes an array and returns its middle element: `middle([3, 8, 5])` is `8`. When the array has an even number of elements there are two in the middle; return the later one: `middle([\"a\", \"b\", \"c\", \"d\"])` is `\"c\"`. Write your code between the braces, and put `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: why does the index need `Math.floor`?\n",
            "exportName": "middle",
            "filename": "middle.js",
            "starter": "function middle(list) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "middle([3, 8, 5]) → 8",
                "body": "return subject([3, 8, 5]) === 8;"
              },
              {
                "label": "middle([1, 2, 3, 4, 5]) → 3",
                "body": "return subject([1, 2, 3, 4, 5]) === 3;"
              },
              {
                "label": "middle([\"a\", \"b\", \"c\", \"d\"]) → \"c\"",
                "body": "return subject(['a', 'b', 'c', 'd']) === 'c';"
              },
              {
                "label": "middle([\"solo\"]) → \"solo\"",
                "body": "return subject(['solo']) === 'solo';"
              }
            ],
            "hints": [
              {
                "when": "odd lengths give undefined",
                "say": "For `[3, 8, 5]`, what is `list.length / 2`? Is that a whole number, and does an array have an element at that index?\n"
              },
              {
                "when": "middle([1, 2, 3, 4, 5]) gives 4",
                "say": "`Math.round(2.5)` is 3, which is one place too far. Which function from *Arithmetic operators* always rounds down?\n"
              },
              {
                "when": "middle([\"a\", \"b\", \"c\", \"d\"]) gives \"b\"",
                "say": "With an even length you picked the earlier of the two. Work out your index by hand for a length of 4: the answer is at index 2.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a first-and-last label",
            "prompt": "A code assistant wrote `ends`, which should return the first and last words of an array joined by `\" to \"`, or `\"(none)\"` when the array is empty. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "ends.js",
            "code": "// \"first to last\" for a list of words, or \"(none)\" when empty.\nfunction ends(words) {\n  if (!words) {\n    return \"(none)\";\n  }\n  return words[1] + \" to \" + words[words.length];\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `ends([\"north\", \"east\", \"south\"])`. Type what it returns, without quotes.",
                "accept": [
                  "east to undefined",
                  "\"east to undefined\""
                ],
                "miss": "**`words[1]` is the second word, and `words[words.length]` is past the end.** `words.length` is 3, and the indexes are 0, 1 and 2.\n",
                "why": "`east to undefined`.\n"
              },
              {
                "id": "c2",
                "question": "What does `ends([])` return? Type it, without quotes.",
                "accept": [
                  "undefined to undefined",
                  "\"undefined to undefined\""
                ],
                "miss": "**`!words` is `false` for every array, because every array is truthy.** So the `if` never runs, and both reads on line 6 are past the end of an empty array.\n",
                "why": "`undefined to undefined`.\n"
              },
              {
                "id": "c3",
                "question": "Line 3 checks for an empty array the way Python does. What should its condition be?",
                "choices": [
                  "words.length === 0",
                  "words === undefined",
                  "words === \"\""
                ],
                "accept": [
                  "words.length === 0"
                ],
                "miss": "**An empty array is still an array, so it is never `undefined`, and never the same value as a string.** `===` does not convert. The length is 0 exactly when the array is empty.\n",
                "why": "`if (words.length === 0) {`. Line 6 needs two fixes too: index 0 for the first word, and `words.length - 1` for the last.\n"
              }
            ],
            "exportName": "ends",
            "starter": "// \"first to last\" for a list of words, or \"(none)\" when empty.\nfunction ends(words) {\n  if (!words) {\n    return \"(none)\";\n  }\n  return words[1] + \" to \" + words[words.length];\n}\n",
            "tests": [
              {
                "label": "ends([\"north\", \"east\", \"south\"]) → \"north to south\"",
                "body": "return subject(['north', 'east', 'south']) === 'north to south';"
              },
              {
                "label": "ends([\"up\", \"down\"]) → \"up to down\"",
                "body": "return subject(['up', 'down']) === 'up to down';"
              },
              {
                "label": "ends([\"here\"]) → \"here to here\"",
                "body": "return subject(['here']) === 'here to here';"
              },
              {
                "label": "ends([]) → \"(none)\"",
                "body": "return subject([]) === '(none)';"
              }
            ],
            "hints": [
              {
                "when": "ends([]) gives \"undefined to undefined\"",
                "say": "Is an empty array falsy? What does `!words` evaluate to when `words` is `[]`?\n"
              },
              {
                "when": "the last word comes out undefined",
                "say": "The length is one more than the last index. Which index holds the last word?\n"
              },
              {
                "when": "the first word is wrong",
                "say": "Which index holds the first element of an array?\n"
              }
            ]
          }
        ]
      }
    },
    "changing-arrays": {
      "id": "changing-arrays",
      "title": "Adding and removing elements",
      "kind": "read",
      "outcome": 2,
      "shape": "procedural",
      "why": "`arr.change` (concepts.md): add and remove elements at either end and in the middle with `push`, `pop`, `shift`, `unshift` and `splice`, say what each returns, and predict the array and its `.length` after a write past the end or an assignment to `.length`. D-069 split the old `arr.change` so that the methods that leave an array as it was are the next lesson's (`arr.query`). It builds on *Lists of values* (index, `.length`, replacing an element) and on *Working with strings*, whose rule that no string method changes its string is the contrast this lesson opens on. The fixed-size contrast with C#, Java and C is concepts.md's, under D-010: it shows what is unusual about JavaScript's own arrays. Strict mode is assumed (D-073); nothing here behaves differently without it. Procedural, explain first (D-061 item 1); the writing direction is modelled fail-first (01-explanations rule 5a) on the concept's own mistake, returning what `push` returns. Derived from those rules, pending Nick's review.",
      "concepts": [
        "arr.change"
      ],
      "lede": "*Lists of values* replaced elements, but every array kept its length. This lesson changes the length: adding and removing at the end, at the front and in the middle, what each method returns, and what happens when you write past the end or set the length yourself.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Lists of values · Working with strings · Finding, slicing and joining"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Stack** — values added and removed at the same end: the last in is the first out. **Queue** — values added at one end and removed from the other: the first in is the first out. **Empty slot** — an index below an array's length that holds no element; reading it gives `undefined`. **Sparse array** — an array with empty slots.",
          "entries": [
            "**Stack** — values added and removed at the same end: the last in is the first out.",
            "**Queue** — values added at one end and removed from the other: the first in is the first out.",
            "**Empty slot** — an index below an array's length that holds no element; reading it gives `undefined`.",
            "**Sparse array** — an array with empty slots."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on what this lesson builds on, from memory: the last element\nand replacing one, from *Lists of values*, and what a string method does to\nits string, from *Working with strings*. Type the value, with quotes if it is\ntext. Press **I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const xs = [7, 3, 9];\nxs[xs.length - 1]\n",
                    "accept": [
                      "9"
                    ],
                    "miss": "**The length is 3, so the last index is 2**, and `xs[2]` is `9`.\n",
                    "why": "`9`.\n"
                  },
                  {
                    "code": "const xs = [7, 3, 9];\nxs[1] = 4;\nxs.length\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**Replacing an element does not change how many there are.** `xs` is now `[7, 4, 9]`, still three elements.\n",
                    "why": "`3`.\n"
                  },
                  {
                    "code": "const word = \"hi\";\nword.toUpperCase();\nword\n",
                    "accept": [
                      "\"hi\"",
                      "hi",
                      "'hi'"
                    ],
                    "miss": "**No string method changes the string you call it on.** `word.toUpperCase()` produced `\"HI\"`, but nothing kept it, and `word` still holds `\"hi\"`.\n",
                    "why": "`\"hi\"`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can add and remove elements at either end of an array, and say what each method returns",
                "I can remove and insert elements in the middle of an array with `splice`",
                "I can predict an array's length after a write past the end or an assignment to `.length`"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "At the end: push and pop",
          "body": [
            {
              "p": "*Working with strings* said no string method changes the string you call it\non. The methods in this lesson are the opposite: each one changes the array\nyou call it on, and the array stays changed."
            },
            {
              "p": "**`push`** adds one or more elements to the end of an array:"
            },
            {
              "code": "const readings = [12, 15];\nreadings.push(9);\nconsole.log(readings);       // [12, 15, 9]\nreadings.push(20, 18);\nconsole.log(readings);       // [12, 15, 9, 20, 18]",
              "lang": "js",
              "file": "push.js"
            },
            {
              "p": "`readings` was declared with `const`, and `push` still changes it, as\nreplacing an element did in *Lists of values*: `const` fixes which array the\nname holds, not what is in it."
            },
            {
              "p": "**`pop`** removes the last element and returns it:"
            },
            {
              "code": "const readings = [12, 15, 9];\nconst last = readings.pop();\nconsole.log(last);           // 9\nconsole.log(readings);       // [12, 15]",
              "lang": "js",
              "file": "pop.js"
            },
            {
              "p": "The two methods do not return the same kind of thing. `pop` returns the\nelement it removed. `push` returns the array's new length, not the array:"
            },
            {
              "code": "const readings = [12, 15];\nconst result = readings.push(9);\nconsole.log(result);         // 3",
              "lang": "js",
              "file": "push.js"
            },
            {
              "p": "So a function that ends with `return list.push(item);` returns a number. The\nlast step in this lesson works through that mistake."
            },
            {
              "p": "`pop` on an empty array has nothing to remove. It returns `undefined` and\nleaves the array empty, with no error."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const letters = [\"a\", \"b\", \"c\"];\nletters.pop();\nletters.length\n",
                    "accept": [
                      "2"
                    ],
                    "miss": "**`pop` removes the last element**, so `letters` is now `[\"a\", \"b\"]`.\n",
                    "why": "`2`.\n"
                  },
                  {
                    "code": "const xs = [10];\nxs.push(20, 30)\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**`push` returns the new length, not the array.** It added two elements, so `xs` has three.\n",
                    "why": "`3`. `xs` is now `[10, 20, 30]`.\n"
                  },
                  {
                    "code": "const ids = [4, 8];\nids.push(5);\nids\n",
                    "choices": [
                      "[4, 8, 5]",
                      "[5, 4, 8]",
                      "3"
                    ],
                    "accept": [
                      "[4, 8, 5]"
                    ],
                    "miss": "**If you chose `[5, 4, 8]` — `push` adds at the end, not the front.** **If you chose `3` — that is what `push` returns**, but the question asks what `ids` holds. The array itself changed.\n",
                    "why": "`[4, 8, 5]`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "At the front: unshift and shift",
          "body": [
            {
              "p": "**`unshift`** adds elements to the front of an array, and **`shift`** removes\nthe first element and returns it. They do at the front what `push` and `pop`\ndo at the end, and they return the same things: `unshift` returns the new\nlength, and `shift` returns the element it removed. (`unshift` is named as\nthe opposite of `shift`.)"
            },
            {
              "code": "const line = [\"Rosa\", \"Ines\"];\nline.unshift(\"Kai\");\nconsole.log(line);           // [\"Kai\", \"Rosa\", \"Ines\"]\nconst first = line.shift();\nconsole.log(first);          // Kai\nconsole.log(line);           // [\"Rosa\", \"Ines\"]",
              "lang": "js",
              "file": "front.js"
            },
            {
              "p": "Working at the front moves every other element to a new index. Before\n`unshift(\"Kai\")`, `\"Rosa\"` is at index 0:"
            },
            {
              "indexes": {
                "variable": "line",
                "items": [
                  "\"Rosa\"",
                  "\"Ines\""
                ],
                "kind": "element"
              }
            },
            {
              "p": "After it, `\"Kai\"` is at index 0 and everything else has moved up one:"
            },
            {
              "indexes": {
                "variable": "line",
                "items": [
                  "\"Kai\"",
                  "\"Rosa\"",
                  "\"Ines\""
                ],
                "kind": "element"
              }
            },
            {
              "p": "`shift` moves everything back down one. An index saved before either change\nnow points at a different element."
            },
            {
              "p": "Two ways of using these methods have names you will meet again. Adding with\n`push` and removing with `pop` takes elements off the same end they went on,\nso the last in is the first out: an array used this way is a **stack**.\nAdding with `push` and removing with `shift` hands elements back in the order\nthey arrived, first in, first out: a **queue**."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const q = [\"x\", \"y\"];\nq.unshift(\"w\");\nq[1]\n",
                    "accept": [
                      "\"x\"",
                      "x",
                      "'x'"
                    ],
                    "miss": "**`unshift` puts `\"w\"` at index 0**, and every other element moves up one, so `\"x\"` is now at index 1.\n",
                    "why": "`\"x\"`.\n"
                  },
                  {
                    "code": "const q = [\"x\", \"y\", \"z\"];\nq.shift()\n",
                    "accept": [
                      "\"x\"",
                      "x",
                      "'x'"
                    ],
                    "miss": "**`shift` removes the first element and returns it.**\n",
                    "why": "`\"x\"`. `q` is now `[\"y\", \"z\"]`.\n"
                  },
                  {
                    "question": "A help desk adds each new ticket to an array with `push`. Which method takes the ticket that has waited longest?",
                    "choices": [
                      "shift",
                      "pop",
                      "unshift"
                    ],
                    "accept": [
                      "shift"
                    ],
                    "miss": "**If you chose `pop` — that takes the newest ticket**, the last one pushed. **If you chose `unshift` — that adds**, it does not take. The oldest ticket is at the front, and `shift` takes from the front.\n",
                    "why": "`shift`. Adding with `push` and taking with `shift` makes the array a queue.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "In the middle: splice",
          "body": [
            {
              "p": "**`splice`** removes and inserts elements anywhere in an array. It takes the\nindex to start at, how many elements to remove, and then any elements to\ninsert there. It returns the elements it removed, as an array."
            },
            {
              "p": "**Removing.** Two arguments: where to start, and how many to remove."
            },
            {
              "code": "const days = [\"Mon\", \"Tue\", \"Wed\", \"Thu\"];\nconst removed = days.splice(1, 2);\nconsole.log(removed);        // [\"Tue\", \"Wed\"]\nconsole.log(days);           // [\"Mon\", \"Thu\"]",
              "lang": "js",
              "file": "splice.js"
            },
            {
              "p": "**Inserting.** Remove 0 elements, then list what to insert. The new elements\ngo in at the start index, and the elements from there on move up:"
            },
            {
              "code": "const days = [\"Mon\", \"Thu\"];\ndays.splice(1, 0, \"Tue\", \"Wed\");\nconsole.log(days);           // [\"Mon\", \"Tue\", \"Wed\", \"Thu\"]",
              "lang": "js",
              "file": "splice.js"
            },
            {
              "p": "**Replacing.** Remove some and insert others, in one call:"
            },
            {
              "code": "const days = [\"Mon\", \"Tue\", \"Wed\"];\ndays.splice(1, 1, \"Holiday\");\nconsole.log(days);           // [\"Mon\", \"Holiday\", \"Wed\"]",
              "lang": "js",
              "file": "splice.js"
            },
            {
              "p": "With only the start index, `splice` removes everything from there to the end."
            },
            {
              "p": "`splice` always returns an array, even when it removes one element or none.\n`days.splice(1, 1)` returns `[\"Tue\"]`, not `\"Tue\"`. To get the one element it\nremoved, read index 0 of what it returns:"
            },
            {
              "code": "const days = [\"Mon\", \"Tue\", \"Wed\"];\nconst removed = days.splice(1, 1);\nconsole.log(removed[0]);     // Tue",
              "lang": "js",
              "file": "splice.js"
            },
            {
              "p": "The next lesson, *Finding, slicing and joining*, teaches `slice`, one letter\naway from `splice`. `slice` copies part of an array and leaves the array as\nit was; `splice` changes it."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const xs = [1, 2, 3, 4, 5];\nxs.splice(2, 2);\nxs\n",
                    "accept": [
                      "[1, 2, 5]",
                      "[1,2,5]",
                      "[ 1, 2, 5 ]"
                    ],
                    "miss": "**`splice(2, 2)` starts at index 2 and removes two elements**, `3` and `4`.\n",
                    "why": "`[1, 2, 5]`.\n"
                  },
                  {
                    "code": "const xs = [\"a\", \"c\"];\nxs.splice(1, 0, \"b\");\nxs.length\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**A count of 0 removes nothing**, so `\"b\"` is inserted at index 1 and `\"c\"` moves up to index 2.\n",
                    "why": "`3`. `xs` is now `[\"a\", \"b\", \"c\"]`.\n"
                  },
                  {
                    "code": "const xs = [7, 8, 9];\nconst out = xs.splice(1, 1);\nout\n",
                    "accept": [
                      "[8]",
                      "[ 8 ]"
                    ],
                    "miss": "**`splice` returns the removed elements as an array**, even when there is only one.\n",
                    "why": "`[8]`, an array. `out[0]` is `8`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "No fixed size: past the end, and setting the length",
          "body": [
            {
              "p": "*Lists of values* showed that reading past the end gives `undefined`. Writing\npast the end is allowed too, and it makes the array longer:"
            },
            {
              "code": "const slots = [\"a\", \"b\"];\nslots[4] = \"e\";\nconsole.log(slots.length);   // 5\nconsole.log(slots[2]);       // undefined",
              "lang": "js",
              "file": "grow.js"
            },
            {
              "p": "The length is now 5, but nothing was ever stored at index 2 or 3. Those are\n**empty slots**, sometimes called holes, and reading one gives `undefined`.\nAn array with empty slots is a **sparse array**. Node shows the empty slots\nwhen it prints the array: `[ 'a', 'b', <2 empty items>, 'e' ]`."
            },
            {
              "p": "Writing at exactly `list.length` adds one element at the end with no empty\nslot, which is what `push` does. One index higher, and the index in between\nis left empty."
            },
            {
              "p": "`.length` can be assigned, too. Setting it lower removes elements from the\nend; setting it higher adds empty slots:"
            },
            {
              "code": "const xs = [1, 2, 3, 4];\nxs.length = 2;\nconsole.log(xs);             // [1, 2]\nxs.length = 4;\nconsole.log(xs[3]);          // undefined",
              "lang": "js",
              "file": "length.js"
            },
            {
              "p": "The two elements cut off are gone. Setting the length back to 4 brings back\ntwo empty slots, not the `3` and `4` that were there."
            },
            {
              "p": "Empty slots are rarely what a program wants, and a common cause is an index\nthat is one too high. Code that grows an array with `push` and shrinks it with\n`pop`, `shift` or `splice` never makes one. Setting `.length` on purpose does\nhave one common use: `list.length = 0` empties the array itself, without\nmaking a new one."
            },
            {
              "p": "This is where JavaScript differs from many other languages. In C#, Java and\nC, an array's size is fixed when the array is created: an array of 3 always\nhas 3 elements. Writing at index 5 of it is an error in C# and Java. C does\nnot check, so what happens is undefined: the write may overwrite other data,\nor crash the program. In C# and Java, a list that grows is a separate type,\nsuch as C#'s `List` and Java's `ArrayList`. JavaScript has one kind of array,\nand it grows and shrinks."
            },
            {
              "p": "Python's lists grow too, but a write past the end stops with\n`IndexError: list assignment index out of range`, so a Python list never has\nempty slots."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const a = [1, 2, 3];\na[5] = 6;\na.length\n",
                    "accept": [
                      "6"
                    ],
                    "miss": "**The highest index is now 5, so the length is 6.** Indexes 3 and 4 are empty slots.\n",
                    "why": "`6`.\n"
                  },
                  {
                    "code": "const a = [1, 2, 3, 4, 5];\na.length = 3;\na[3]\n",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**Setting the length to 3 removed the elements at indexes 3 and 4.** Index 3 is now past the end.\n",
                    "why": "`undefined`.\n"
                  },
                  {
                    "question": "Which line leaves an empty slot in the array `list`?",
                    "choices": [
                      "list[list.length + 1] = \"x\";",
                      "list[list.length] = \"x\";",
                      "list.push(\"x\");"
                    ],
                    "accept": [
                      "list[list.length + 1] = \"x\";"
                    ],
                    "miss": "**`list[list.length]` is the index just past the last element**, so writing there adds one element with nothing skipped, as `push` does. One index higher skips an index, and that index is left empty.\n",
                    "why": "`list[list.length + 1] = \"x\";`.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "From a sentence to a function",
          "body": [
            {
              "p": "The four subgoals from *Writing a function's answer* still apply. What is new\nis that the method you call can change the array and return something else,\nso the fourth subgoal, sending the answer out with `return`, has to send out\nthe right value."
            },
            {
              "p": "**Example 1.** \"`addReading` takes an array of readings and a new reading,\nadds the new reading to the end, and returns the array:\n`addReading([12, 15], 9)` is `[12, 15, 9]`.\""
            },
            {
              "p": "`push` adds to the end, so a first try returns what `push` returns:"
            },
            {
              "code": "function addReading(readings, reading) {\n  return readings.push(reading);\n}",
              "lang": "js",
              "file": "add-reading.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✗ addReading([12, 15], 9) → [12, 15, 9]\n✗ addReading([], 4) → [4]",
              "lang": "text"
            },
            {
              "p": "`addReading([12, 15], 9)` returned `3`. Work it through by hand. `push` added\n`9`, so the array is right: `[12, 15, 9]`. But the function returns what\n`push` returns, and `push` returns the new length, `3`."
            },
            {
              "p": "The fix keeps the two jobs on two lines: one changes the array, and one sends\nit out."
            },
            {
              "code": "function addReading(readings, reading) {\n  readings.push(reading);\n  return readings;\n}",
              "lang": "js",
              "file": "add-reading.js"
            },
            {
              "code": "✓ addReading([12, 15], 9) → [12, 15, 9]\n✓ addReading([], 4) → [4]",
              "lang": "text"
            },
            {
              "p": "**Example 2: you choose the line.** \"`addToFront` takes a queue and a name,\nputs the name at the front, and returns the queue:\n`addToFront([\"Rosa\"], \"Kai\")` is `[\"Kai\", \"Rosa\"]`.\""
            },
            {
              "code": "function addToFront(queue, name) {\n  // TODO: put your code here\n}",
              "lang": "js",
              "file": "add-to-front.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which lines go at the TODO?",
                    "choices": [
                      "queue.unshift(name); return queue;",
                      "return queue.unshift(name);",
                      "queue.push(name); return queue;"
                    ],
                    "accept": [
                      "queue.unshift(name); return queue;"
                    ],
                    "miss": "**If you chose `return queue.unshift(name);` — `unshift` returns the new length, as `push` does,** so the function would return `2`. **If you chose `push` — that adds at the end**, giving `[\"Rosa\", \"Kai\"]`.\n",
                    "why": "`queue.unshift(name);` then `return queue;`: the same two jobs on two lines as the fixed `addReading`.\n"
                  },
                  {
                    "question": "What does `addToFront([], \"Kai\")` return? Type it.",
                    "accept": [
                      "[\"Kai\"]",
                      "['Kai']",
                      "[ \"Kai\" ]",
                      "[ 'Kai' ]"
                    ],
                    "miss": "**`unshift` on an empty array adds the one name**, and the function returns the array.\n",
                    "why": "`[\"Kai\"]`.\n"
                  },
                  {
                    "question": "Using the fixed `addReading`, what does `addReading([1], 2).length` evaluate to? Type the number.",
                    "accept": [
                      "2"
                    ],
                    "miss": "**The fixed version returns the array**, `[1, 2]`, and its length is 2.\n",
                    "why": "`2`. With the first try, `addReading([1], 2)` would return the number `2`, and `.length` of a number is `undefined`.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`push` and `pop` work at the end; `unshift` and `shift` at the front.** Each changes the array it is called on, even a `const` one.",
                "**`pop` and `shift` return the element removed; `push` and `unshift` return the new length**, not the array.",
                "**`splice` removes and inserts anywhere**: a start index, how many to remove, then elements to insert. It returns the removed elements as an array.",
                "**A JavaScript array has no fixed size.** A write past the end leaves empty slots, and assigning `.length` cuts the array short or pads it."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array.prototype.push()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/push",
              "note": "and its neighbours `pop`, `shift` and `unshift`, linked from the same page's sidebar."
            },
            {
              "title": "Array.prototype.splice()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/splice",
              "note": "every form, with examples."
            },
            {
              "title": "Sparse arrays",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Indexed_collections#sparse_arrays",
              "note": "empty slots, and how different methods treat them."
            }
          ]
        }
      ],
      "_source": "lessons/changing-arrays.md",
      "practice": {
        "id": "changing-arrays",
        "outcome": 2,
        "steps": [
          {
            "kind": "predict",
            "title": "What each method returns",
            "prompt": "Type the value each one evaluates to, or press the answer. Write text with its quotes, and an array with its square brackets.\n",
            "items": [
              {
                "id": "p1",
                "code": "const xs = [4, 8];\nxs.push(6)\n",
                "accept": [
                  "3"
                ],
                "miss": "**`push` returns the new length, not the array.** `xs` is now `[4, 8, 6]`, three elements.\n",
                "why": "`3`.\n"
              },
              {
                "id": "p2",
                "code": "const xs = [4, 8, 6];\nxs.pop()\n",
                "accept": [
                  "6"
                ],
                "miss": "**`pop` removes the last element and returns it.**\n",
                "why": "`6`.\n"
              },
              {
                "id": "p3",
                "code": "const xs = [\"b\", \"c\"];\nxs.unshift(\"a\");\nxs[0]\n",
                "accept": [
                  "\"a\"",
                  "a",
                  "'a'"
                ],
                "miss": "**`unshift` adds at the front**, so `\"a\"` is now at index 0.\n",
                "why": "`\"a\"`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const xs = [1, 2, 3];\nxs.shift();\nxs.length\n",
                "accept": [
                  "2"
                ],
                "miss": "**`shift` removed the first element**, so two are left.\n",
                "why": "`2`.\n"
              },
              {
                "id": "p5",
                "code": "const xs = [1, 2, 3, 4];\nxs.splice(1, 2)\n",
                "accept": [
                  "[2, 3]",
                  "[2,3]",
                  "[ 2, 3 ]"
                ],
                "miss": "**`splice(1, 2)` starts at index 1 and removes two elements, and it returns them as an array.**\n",
                "why": "`[2, 3]`. `xs` is now `[1, 4]`.\n"
              },
              {
                "id": "p6",
                "code": "[].pop()",
                "accept": [
                  "undefined"
                ],
                "miss": "**`pop` on an empty array has nothing to remove**, so it returns `undefined`, with no error.\n",
                "why": "`undefined`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered the first one with the array",
                "say": "Which value does `push` return: the array, the element it added, or the array's new length?\n"
              },
              {
                "when": "you expected the first element from pop",
                "say": "Which end does `pop` take from? Which method takes from the front?\n"
              },
              {
                "when": "you expected a single number from splice",
                "say": "`splice` can remove many elements at once. What kind of value holds many elements?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "A playlist, run",
            "prompt": "Predict each line this program prints, then run it and compare.\n",
            "filename": "playlist.js",
            "code": "const playlist = [\"intro\", \"verse\"];\nconsole.log(playlist.push(\"chorus\"));\nconsole.log(playlist.shift());\n\nplaylist.unshift(\"count-in\");\nconsole.log(playlist.length);\nconsole.log(playlist.pop());\nconsole.log(playlist);\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print? Type it.",
                "accept": [
                  "3"
                ],
                "miss": "**`console.log` prints what `push` returns**, and `push` returns the new length.\n",
                "why": "`3`.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print? Type it.",
                "accept": [
                  "intro",
                  "\"intro\""
                ],
                "miss": "**`shift` removes the first element and returns it**, and the first element is still `\"intro\"`: `push` added at the other end.\n",
                "why": "`intro`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print? Type it.",
                "accept": [
                  "3"
                ],
                "miss": "**After the `shift`, the playlist was `[\"verse\", \"chorus\"]`.** `unshift` added `\"count-in\"` at the front, making three.\n",
                "why": "`3`.\n"
              },
              {
                "id": "r4",
                "question": "What does the fourth line print? Type it.",
                "accept": [
                  "chorus",
                  "\"chorus\""
                ],
                "miss": "**`pop` takes the last element**, and the last is `\"chorus\"`.\n",
                "why": "`chorus`.\n"
              },
              {
                "id": "r5",
                "question": "What does the last line print? Type the array.",
                "accept": [
                  "[\"count-in\", \"verse\"]",
                  "[\"count-in\",\"verse\"]",
                  "['count-in', 'verse']",
                  "[ 'count-in', 'verse' ]"
                ],
                "miss": "**Follow the array line by line:** `[\"intro\", \"verse\", \"chorus\"]`, then `[\"verse\", \"chorus\"]`, then `[\"count-in\", \"verse\", \"chorus\"]`, then `pop` takes `\"chorus\"`.\n",
                "why": "`[\"count-in\", \"verse\"]`.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected the first line to print the array",
                "say": "What does `push` return? `console.log` prints exactly that value.\n"
              },
              {
                "when": "you expected the second line to print chorus",
                "say": "Which end does `shift` take from? Where did `push` put `\"chorus\"`?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One array, several changes",
            "prompt": "Work each one through line by line, writing down the array after each line, and type the value of the last line.\n",
            "items": [
              {
                "id": "t1",
                "code": "const a = [1, 2, 3];\nconst n = a.push(10);\na.unshift(n);\na\n",
                "accept": [
                  "[4, 1, 2, 3, 10]",
                  "[4,1,2,3,10]",
                  "[ 4, 1, 2, 3, 10 ]"
                ],
                "miss": "**`a.push(10)` returns the new length, 4, so `n` is 4.** Then `unshift(n)` puts that 4 at the front.\n",
                "why": "`[4, 1, 2, 3, 10]`.\n"
              },
              {
                "id": "t2",
                "code": "const s = [\"a\", \"d\"];\ns.splice(1, 0, \"b\", \"c\");\ns.length\n",
                "accept": [
                  "4"
                ],
                "miss": "**A count of 0 removes nothing**, and both `\"b\"` and `\"c\"` go in at index 1, so `s` is `[\"a\", \"b\", \"c\", \"d\"]`.\n",
                "why": "`4`.\n"
              },
              {
                "id": "t3",
                "code": "const h = [1, 2];\nh[4] = 5;\nh.length\n",
                "accept": [
                  "5"
                ],
                "miss": "**Writing at index 4 makes the array long enough to hold it.** Indexes 2 and 3 are empty slots, and the length is 5.\n",
                "why": "`5`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "const h = [1, 2, 3, 4];\nh.length = 2;\nh[3]\n",
                "accept": [
                  "undefined"
                ],
                "miss": "**Setting the length to 2 removed the elements at indexes 2 and 3.** Index 3 is past the end now.\n",
                "why": "`undefined`.\n"
              },
              {
                "id": "t5",
                "code": "const r = [\"x\", \"y\", \"z\"];\nconst removed = r.splice(0, 1);\nremoved.length + r.length\n",
                "accept": [
                  "3"
                ],
                "miss": "**`removed` is `[\"x\"]`, one element, and `r` is `[\"y\", \"z\"]`, two.**\n",
                "why": "`3`. Nothing was lost: one element moved from `r` into `removed`.\n"
              }
            ],
            "hints": [
              {
                "when": "your answer to the first one has 10 at the front",
                "say": "What does `push` return: the element it added, or the new length? Count `a` after line 2.\n"
              },
              {
                "when": "you expected 3 for the third one",
                "say": "What is the highest index now? Here the length is one more than that.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "To the back of the line",
            "prompt": "`toBack` should move the first element of `line` to the end and return the array: `toBack([\"Ana\", \"Ben\", \"Cy\"])` is `[\"Ben\", \"Cy\", \"Ana\"]`. `line` always has at least one element. Build the lines that go between its braces, in order. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "  const first = line.shift();",
              "  line.push(first);",
              "  return line;"
            ],
            "distractors": [
              {
                "line": "  return line.push(first);",
                "why": "`push` returns the new length, so `toBack` would return `3`, not the array.\n"
              },
              {
                "line": "  const first = line.pop();",
                "why": "`pop` takes the last element, not the first. Pushing it back puts it where it was, and the array does not change.\n"
              }
            ],
            "why": "`shift` removes the first element and returns it, so `first` holds it. `push` adds it at the end. Then the array itself is returned, on its own line, because `push` returns a length.\n",
            "hints": [
              {
                "when": "you are unsure which method takes the first element",
                "say": "`pop` and `shift` both remove and return an element. Which one works at the front?\n"
              },
              {
                "when": "you are unsure how to return the array",
                "say": "What does `push` return? If that is not the array, which line sends the array out?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Read it, then take it",
            "prompt": "`takeTop` should return the last element of `stack` and remove it from the stack: `takeTop([\"a\", \"b\", \"c\"])` should return `\"c\"` and leave the array as `[\"a\", \"b\"]`. The starter only reads the last element, and leaves it there. Write your code between the braces, and keep `return` in front of the answer. Then, in one sentence: what does your version do that the starter's bracket notation does not?\n",
            "exportName": "takeTop",
            "filename": "take-top.js",
            "starter": "function takeTop(stack) {\n  // TODO: put your code here\n  return stack[stack.length - 1];\n}\n",
            "tests": [
              {
                "label": "takeTop([\"a\", \"b\", \"c\"]) → \"c\"",
                "body": "return subject(['a', 'b', 'c']) === 'c';"
              },
              {
                "label": "after takeTop([\"a\", \"b\", \"c\"]), the array is [\"a\", \"b\"]",
                "body": "const s = ['a', 'b', 'c']; subject(s); return s.length === 2 && s[0] === 'a' && s[1] === 'b';"
              },
              {
                "label": "takeTop([7]) → 7, and leaves []",
                "body": "const s = [7]; return subject(s) === 7 && s.length === 0;"
              },
              {
                "label": "takeTop([]) → undefined",
                "body": "return subject([]) === undefined;"
              }
            ],
            "hints": [
              {
                "when": "the value is right but the array still has three elements",
                "say": "Bracket notation reads an element and leaves it where it is. Which method removes the last element and returns it?\n"
              },
              {
                "when": "takeTop([\"a\", \"b\", \"c\"]) gives \"a\"",
                "say": "You took from the front. Which method takes from the end?\n"
              },
              {
                "when": "takeTop returns an array",
                "say": "`splice` returns an array of what it removed. Is there a method that removes the last element and returns the element itself?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Keep the first few",
            "prompt": "`keepFirst` takes an array and a number `n`, shortens the array so only its first `n` elements are left, and returns the same array: `keepFirst([5, 6, 7, 8], 2)` is `[5, 6]`. When the array already has `n` elements or fewer, return it unchanged; do not make it longer. Write your code between the braces, and put `return` in front of the answer. Then, in one sentence: what would happen if `keepFirst` set the length when `n` is larger?\n",
            "exportName": "keepFirst",
            "filename": "keep-first.js",
            "starter": "function keepFirst(list, n) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "keepFirst([5, 6, 7, 8], 2) → [5, 6]",
                "body": "const r = subject([5, 6, 7, 8], 2); return Array.isArray(r) && r.length === 2 && r[0] === 5 && r[1] === 6;"
              },
              {
                "label": "it returns the same array it was given, shortened",
                "body": "const a = [1, 2, 3]; const r = subject(a, 1); return r === a && a.length === 1;"
              },
              {
                "label": "keepFirst([\"x\", \"y\"], 5) → [\"x\", \"y\"], not longer",
                "body": "const a = ['x', 'y']; const r = subject(a, 5); return r === a && a.length === 2;"
              },
              {
                "label": "keepFirst([4, 2], 0) → []",
                "body": "const r = subject([4, 2], 0); return Array.isArray(r) && r.length === 0;"
              }
            ],
            "hints": [
              {
                "when": "keepFirst([\"x\", \"y\"], 5) makes the array longer",
                "say": "Setting `.length` higher than the array's length adds empty slots. When should your code leave the length alone?\n"
              },
              {
                "when": "your function returns a number",
                "say": "Some array methods return a length or the removed elements. After changing the array, what should `return` send out?\n"
              },
              {
                "when": "you used slice or built a new array",
                "say": "The second test checks that the array you were given is the one returned, shortened. Which way of shortening changes that array?\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a queue's next customer",
            "prompt": "A code assistant wrote `serveNext`, which should remove the first customer from the queue and return their name, or return `\"nobody\"` when no one is waiting. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "serve-next.js",
            "code": "// Serve the first customer in line: remove them and return their name,\n// or \"nobody\" when no one is waiting.\nfunction serveNext(queue) {\n  if (queue.length === 0) {\n    return \"nobody\";\n  }\n  return queue.splice(queue.length - 1, 1);\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `serveNext([\"Kai\", \"Rosa\"])`. Type what it returns.",
                "accept": [
                  "[\"Rosa\"]",
                  "['Rosa']",
                  "[ \"Rosa\" ]",
                  "[ 'Rosa' ]"
                ],
                "miss": "**`splice` returns the removed elements as an array.** It started at `queue.length - 1`, the last index, and removed one.\n",
                "why": "`[\"Rosa\"]`: an array, and the wrong customer.\n"
              },
              {
                "id": "c2",
                "question": "Which customer does line 7 remove from the queue?",
                "choices": [
                  "the last one in line",
                  "the first one in line",
                  "nobody"
                ],
                "accept": [
                  "the last one in line"
                ],
                "miss": "**`queue.length - 1` is the last index**, so `splice` starts at the last customer. The first in line is at index 0.\n",
                "why": "The last one in line: the customer who arrived most recently.\n"
              },
              {
                "id": "c3",
                "question": "Which line should replace line 7?",
                "choices": [
                  "return queue.shift();",
                  "return queue.pop();",
                  "return queue.splice(0, 1);"
                ],
                "accept": [
                  "return queue.shift();"
                ],
                "miss": "**`pop` takes the last customer, the same mistake.** **`splice(0, 1)` takes the right customer but still returns an array**, `[\"Kai\"]`. `shift` removes the first element and returns the element itself.\n",
                "why": "`return queue.shift();`.\n"
              }
            ],
            "exportName": "serveNext",
            "starter": "// Serve the first customer in line: remove them and return their name,\n// or \"nobody\" when no one is waiting.\nfunction serveNext(queue) {\n  if (queue.length === 0) {\n    return \"nobody\";\n  }\n  return queue.splice(queue.length - 1, 1);\n}\n",
            "tests": [
              {
                "label": "serveNext([\"Kai\", \"Rosa\"]) → \"Kai\"",
                "body": "return subject(['Kai', 'Rosa']) === 'Kai';"
              },
              {
                "label": "after serveNext([\"Kai\", \"Rosa\"]), the queue is [\"Rosa\"]",
                "body": "const q = ['Kai', 'Rosa']; subject(q); return q.length === 1 && q[0] === 'Rosa';"
              },
              {
                "label": "serveNext([\"Ines\"]) → \"Ines\"",
                "body": "return subject(['Ines']) === 'Ines';"
              },
              {
                "label": "serveNext([]) → \"nobody\"",
                "body": "return subject([]) === 'nobody';"
              }
            ],
            "hints": [
              {
                "when": "it returns [\"Kai\"]",
                "say": "`splice` always returns an array. Which method removes the first element and returns that element, not an array?\n"
              },
              {
                "when": "it returns the last customer",
                "say": "Which index is the first in line? Which method works at that end?\n"
              }
            ]
          }
        ]
      }
    },
    "finding-slicing": {
      "id": "finding-slicing",
      "title": "Finding, slicing and joining",
      "kind": "read",
      "outcome": 2,
      "shape": "procedural",
      "why": "`arr.query` (concepts.md): find an element with `includes` and `indexOf`, copy part of an array with `slice`, combine two with `concat`, turn an array into a string with `join` and a string into an array with `split`, and say for any method whether it changed the array or returned a new value. D-069 split the old `arr.change` so that the methods that change an array are the previous lesson's (*Adding and removing elements*) and the ones that leave it as it was are this one's; the changed-or-returned question is this concept's own. `slice` and `includes` mean what they mean on strings (*Working with strings*), so the lesson points back rather than reteaching. The Python contrasts (`+` between lists, `split()` with no argument) are habits that fail silently in JavaScript, under D-010. Strict mode is assumed (D-073); nothing here behaves differently without it. Procedural, explain first (D-061 item 1); the writing direction is modelled fail-first (01-explanations rule 5a) on the concept's own mistake, a non-mutating method's result thrown away, written by following the previous lesson's `push` fix. Derived from those rules, pending Nick's review.",
      "concepts": [
        "arr.query"
      ],
      "lede": "The methods in the previous lesson, *Adding and removing elements*, change the array they are called on. The methods in this lesson leave an array as it was and return something new: whether a value is in the array and where, a copy of part of it, two arrays combined, and the array turned into a string and back. It ends with the question to ask of every method: did it change the array, or return a new value?",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Working with strings · Adding and removing elements · Walking an array"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Separator** — the string `join` puts between elements, and the string `split` cuts at: `\", \"` in `\"Mon, Tue\"`. **Mutating method** — a method that changes the array it is called on, such as `push`. **Non-mutating method** — a method that leaves the array as it was and returns a new value, such as `slice`.",
          "entries": [
            "**Separator** — the string `join` puts between elements, and the string `split` cuts at: `\", \"` in `\"Mon, Tue\"`.",
            "**Mutating method** — a method that changes the array it is called on, such as `push`.",
            "**Non-mutating method** — a method that leaves the array as it was and returns a new value, such as `slice`."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory: `slice` and\n`includes` on a string and what a string method does to its string, from\n*Working with strings*, and what `splice` does to an array, from *Adding and\nremoving elements*. Type the value, with quotes if it is text. Press **I don't\nknow** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "\"spreadsheet\".slice(0, 6)",
                    "accept": [
                      "\"spread\"",
                      "spread",
                      "'spread'"
                    ],
                    "miss": "**The end index is left out.** `slice(0, 6)` takes indexes 0 to 5, six characters.\n",
                    "why": "`\"spread\"`.\n"
                  },
                  {
                    "code": "\"hello world\".includes(\"lo w\")",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "true"
                    ],
                    "miss": "**`includes` on a string looks for the text anywhere inside it**, spaces included. `\"lo w\"` is there, across the middle.\n",
                    "why": "`true`.\n"
                  },
                  {
                    "code": "const code = \"AB-12\";\ncode.slice(3);\ncode\n",
                    "accept": [
                      "\"AB-12\"",
                      "AB-12",
                      "'AB-12'"
                    ],
                    "miss": "**No string method changes the string you call it on.** `code.slice(3)` made `\"12\"`, but nothing kept it.\n",
                    "why": "`\"AB-12\"`.\n"
                  },
                  {
                    "code": "const xs = [5, 6, 7];\nxs.splice(1, 1);\nxs\n",
                    "accept": [
                      "[5, 7]",
                      "[5,7]",
                      "[ 5, 7 ]"
                    ],
                    "miss": "**`splice` changes the array it is called on.** `splice(1, 1)` starts at index 1 and removes one element, the `6`.\n",
                    "why": "`[5, 7]`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can find an element with `includes` and `indexOf`, and copy part of an array with `slice`",
                "I can combine two arrays with `concat`, and turn an array into a string and back with `join` and `split`",
                "I can say for any array method whether it changes the array or returns a new value"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Is it there, and where: includes and indexOf",
          "body": [
            {
              "p": "*Adding and removing elements* taught methods that change the array they are\ncalled on. None of the methods this lesson teaches do. Each one leaves the array as\nit was and returns a new value, the way every string method does."
            },
            {
              "p": "**`includes`** asks whether an array holds a value. It returns `true` if an\nelement is equal to the value you pass it, and `false` if none is:"
            },
            {
              "code": "const days = [\"Mon\", \"Tue\", \"Wed\"];\ndays.includes(\"Tue\")      // true\ndays.includes(\"Sun\")      // false",
              "lang": "js",
              "file": "includes.js"
            },
            {
              "p": "*Working with strings* used `includes` on a string, where it finds text\nanywhere inside: `\"Monday\".includes(\"Mon\")` is `true`. On an array it compares\nwhole elements, so part of an element is not a match:"
            },
            {
              "code": "const days = [\"Monday\", \"Tuesday\"];\ndays.includes(\"Mon\")      // false",
              "lang": "js",
              "file": "includes.js"
            },
            {
              "p": "It compares without converting, the way `===` does. `[1, 2, 3].includes(\"2\")`\nis `false`, because the string `\"2\"` is not the number `2`."
            },
            {
              "p": "**`indexOf`** asks where. It returns the index of the first element equal to\nthe value, or `-1` when no element is:"
            },
            {
              "code": "const flavours = [\"mint\", \"lemon\", \"plum\", \"lemon\"];\nflavours.indexOf(\"lemon\")     // 1\nflavours.indexOf(\"mint\")      // 0\nflavours.indexOf(\"lime\")      // -1",
              "lang": "js",
              "file": "index-of.js"
            },
            {
              "p": "`\"lemon\"` is at index 1 and at index 3, and `indexOf` stops at the first.\n`-1` means \"not found\" because it can never be a position: indexes start at 0,\nso every element's index is 0 or more. A related method, **`lastIndexOf`**,\nsearches from the end and finds the last match instead. It is named here, not\ntaught."
            },
            {
              "p": "**`indexOf` in a condition is a common mistake.** Here is code that means to\nask \"do we have mint?\":"
            },
            {
              "code": "const flavours = [\"mint\", \"lemon\", \"plum\"];\nif (flavours.indexOf(\"mint\")) {\n  console.log(\"we have mint\");      // not printed\n}",
              "lang": "js",
              "file": "index-of.js"
            },
            {
              "p": "`flavours.indexOf(\"mint\")` is `0`, because mint is the first element. *Truthiness*\nput `0` on the falsy list, so the `if` skips its block, although mint is\nthere. The other way round, `flavours.indexOf(\"lime\")` is `-1`, and `-1` is\ntruthy, so the same test for lime would run its block. The condition is wrong\nfor the first element and for every value that is missing, and right for the\nrest, so a quick test with `\"lemon\"` passes."
            },
            {
              "p": "For a yes-or-no question, `includes` returns the boolean itself:"
            },
            {
              "code": "const flavours = [\"mint\", \"lemon\", \"plum\"];\nif (flavours.includes(\"mint\")) {\n  console.log(\"we have mint\");      // printed\n}",
              "lang": "js",
              "file": "includes.js"
            },
            {
              "p": "Comparing with `-1` is also correct: `flavours.indexOf(\"mint\") !== -1` is\n`true`. `includes` was added to JavaScript in 2016, and older code asks the\nquestion that way, so you will read both. This course writes `includes` when\nthe question is yes or no, because its name says what it asks, and keeps\n`indexOf` for when the position is the answer."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "[\"ant\", \"bee\", \"cat\"].indexOf(\"cat\")",
                    "accept": [
                      "2"
                    ],
                    "miss": "**Indexes start at 0.** `\"ant\"` is at 0, `\"bee\"` at 1, `\"cat\"` at 2.\n",
                    "why": "`2`.\n"
                  },
                  {
                    "code": "[\"10\", \"20\"].includes(10)",
                    "choices": [
                      "true",
                      "false"
                    ],
                    "accept": [
                      "false"
                    ],
                    "miss": "**`includes` does not convert.** The elements are the strings `\"10\"` and `\"20\"`, and the number `10` is equal to neither.\n",
                    "why": "`false`.\n"
                  },
                  {
                    "code": "const pets = [\"cat\", \"dog\"];\nlet found = \"no\";\nif (pets.indexOf(\"cat\")) {\n  found = \"yes\";\n}\nfound\n",
                    "accept": [
                      "\"no\"",
                      "no",
                      "'no'"
                    ],
                    "miss": "**`pets.indexOf(\"cat\")` is `0`, and `0` is falsy**, so the `if` skips its block, although `\"cat\"` is there.\n",
                    "why": "`\"no\"`. The condition should be `pets.includes(\"cat\")`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Copying part of an array: slice",
          "body": [
            {
              "p": "`slice` on an array works as it does on a string, from *Working with\nstrings*. `slice(start, end)` takes the elements from `start` up to, but not\nincluding, `end`. `slice(start)` takes them from `start` to the end. On a\nstring it returns a new string; on an array it returns a new array."
            },
            {
              "indexes": {
                "variable": "week",
                "items": [
                  "\"Mon\"",
                  "\"Tue\"",
                  "\"Wed\"",
                  "\"Thu\"",
                  "\"Fri\""
                ],
                "kind": "element"
              }
            },
            {
              "code": "const week = [\"Mon\", \"Tue\", \"Wed\", \"Thu\", \"Fri\"];\nconst midweek = week.slice(1, 4);\nconsole.log(midweek);     // [\"Tue\", \"Wed\", \"Thu\"]\nconst lastTwo = week.slice(3);\nconsole.log(lastTwo);     // [\"Thu\", \"Fri\"]\nconsole.log(week);        // [\"Mon\", \"Tue\", \"Wed\", \"Thu\", \"Fri\"]",
              "lang": "js",
              "file": "slice.js"
            },
            {
              "p": "`week.slice(1, 4)` starts at index 1, `\"Tue\"`, and stops before index 4,\n`\"Fri\"`, which is left out. That is indexes 1, 2 and 3: `4 - 1` is 3\nelements. `week.slice(3)` has no end, so it runs from `\"Thu\"` to the last\nelement. The last line shows `week` as it was: `slice` copied the elements,\nand moved none of them."
            },
            {
              "p": "`slice()` with no arguments copies the whole array. Why a program would want\na copy is the subject of *Copying versus aliasing*, in the Values and\nreferences module. `slice` also accepts a negative index, counted back from\nthe end: `week.slice(-2)` is the last two days. This course writes\n`week.slice(week.length - 2)`, for the reason *Lists of values* gave for\nwriting `readings[readings.length - 1]` rather than `readings.at(-1)`: the\nidea under it, that the last index is one less than the length, holds in every\nlanguage with arrays."
            },
            {
              "p": "*Adding and removing elements* said that `slice` is one letter away from\n`splice`. Here they are on the same array, with the same two numbers:"
            },
            {
              "code": "const a = [10, 20, 30, 40];\nconsole.log(a.slice(1, 3));    // [20, 30]\nconsole.log(a);                // [10, 20, 30, 40]\n\nconst b = [10, 20, 30, 40];\nconsole.log(b.splice(1, 3));   // [20, 30, 40]\nconsole.log(b);                // [10]",
              "lang": "js",
              "file": "slice-splice.js"
            },
            {
              "p": "They differ in two ways. The second number means something different: for `slice`\nit is the end index, which is left out, and for `splice` it is how many\nelements to remove. And `slice` leaves `a` as it was, while `splice` took\nthree elements out of `b`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "[\"a\", \"b\", \"c\", \"d\", \"e\"].slice(2, 4)",
                    "accept": [
                      "[\"c\", \"d\"]",
                      "[\"c\",\"d\"]",
                      "['c', 'd']",
                      "[ 'c', 'd' ]"
                    ],
                    "miss": "**The start index is included and the end index is left out**, so `slice(2, 4)` takes indexes 2 and 3.\n",
                    "why": "`[\"c\", \"d\"]`. Type it with its square brackets.\n"
                  },
                  {
                    "code": "const xs = [5, 6, 7, 8];\nconst ys = xs.slice(1);\nxs.length\n",
                    "accept": [
                      "4"
                    ],
                    "miss": "**`slice` leaves the array as it was.** `ys` is the new array, `[6, 7, 8]`; `xs` still has four elements.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "question": "`const nums = [1, 2, 3, 4];` — which call leaves `nums` with two elements?",
                    "choices": [
                      "nums.slice(1, 3)",
                      "nums.splice(1, 2)",
                      "nums.slice(2)"
                    ],
                    "accept": [
                      "nums.splice(1, 2)"
                    ],
                    "miss": "**Both `slice` calls return a copy and leave `nums` as it was**, with four elements. `splice(1, 2)` removes two of them.\n",
                    "why": "`nums.splice(1, 2)`. `nums` is then `[1, 4]`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Combining arrays: concat",
          "body": [
            {
              "p": "**`concat`** returns a new array: the elements of the array it is called on,\nfollowed by the elements of the array you pass it. Both arrays are left as\nthey were. The name is short for *concatenation*, the word *Working with\nstrings* used for joining strings with `+`."
            },
            {
              "code": "const week1 = [12, 15, 9];\nconst week2 = [11, 14];\nconst both = week1.concat(week2);\nconsole.log(both);        // [12, 15, 9, 11, 14]\nconsole.log(week1);       // [12, 15, 9]",
              "lang": "js",
              "file": "concat.js"
            },
            {
              "p": "`concat` can take more than one array, `a.concat(b, c)`, and puts each one's\nelements on the end, in order."
            },
            {
              "p": "**`+` does not combine arrays.** In Python, `+` between two lists makes one\nlist. In JavaScript, `+` turns each array into a string first, its elements\nwith commas between them, and then joins the two strings:"
            },
            {
              "code": "[1, 2] + [3]      // \"1,23\"",
              "lang": "js",
              "file": "plus.js"
            },
            {
              "p": "`[1, 2]` becomes `\"1,2\"`, `[3]` becomes `\"3\"`, and `\"1,2\" + \"3\"` is\n`\"1,23\"`, a string. No error is reported."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "[4, 5].concat([6, 7])",
                    "accept": [
                      "[4, 5, 6, 7]",
                      "[4,5,6,7]",
                      "[ 4, 5, 6, 7 ]"
                    ],
                    "miss": "**`concat` puts the elements of the array you pass it after the elements of the first array.**\n",
                    "why": "`[4, 5, 6, 7]`.\n"
                  },
                  {
                    "code": "const a = [\"x\"];\nconst b = a.concat([\"y\"]);\na.length\n",
                    "accept": [
                      "1"
                    ],
                    "miss": "**`concat` leaves the array it is called on as it was.** `b` is the new array, `[\"x\", \"y\"]`.\n",
                    "why": "`1`.\n"
                  },
                  {
                    "code": "[1] + [2]",
                    "accept": [
                      "\"12\"",
                      "'12'",
                      "12"
                    ],
                    "miss": "**`+` turns each array into a string first**, `\"1\"` and `\"2\"`, and then joins the strings.\n",
                    "why": "`\"12\"`, a string, not an array.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "From an array to a string and back: join and split",
          "body": [
            {
              "p": "**`join`** returns one string made of an array's elements, with a\n**separator** between each pair of elements. You pass the separator as a\nstring:"
            },
            {
              "code": "const days = [\"Mon\", \"Tue\", \"Wed\"];\ndays.join(\", \")      // \"Mon, Tue, Wed\"\ndays.join(\"\")        // \"MonTueWed\"\ndays.join()          // \"Mon,Tue,Wed\"",
              "lang": "js",
              "file": "join.js"
            },
            {
              "p": "The separator goes only between elements, not after the last one. `join(\"\")`\nuses the empty string, so the elements run together. With no argument at all,\nthe separator is a comma with no space. Elements that are not strings become\ntext: `[2026, 10, 26].join(\"-\")` is `\"2026-10-26\"`."
            },
            {
              "p": "**`split`** is a string method, and it goes the other way. It cuts a string\nat each separator and returns an array of the pieces. The separators\nthemselves are dropped:"
            },
            {
              "code": "\"Mon,Tue,Wed\".split(\",\")     // [\"Mon\", \"Tue\", \"Wed\"]\n\"a b c\".split(\" \")           // [\"a\", \"b\", \"c\"]\n\"cat\".split(\"\")              // [\"c\", \"a\", \"t\"]\n\"Mon\".split(\",\")             // [\"Mon\"]",
              "lang": "js",
              "file": "split.js"
            },
            {
              "p": "`split(\"\")` cuts between every pair of characters. When the separator is not\nin the string, there is nothing to cut, and the array holds the whole string\nas its one element. The same happens with no separator at all:\n`\"a b c\".split()` is `[\"a b c\"]`. Calling `split()` with no argument is a\nPython habit that does not carry over: Python's `\"a b c\".split()` cuts at the\nspaces."
            },
            {
              "p": "**The pieces are always strings.** `\"4,5,6\".split(\",\")` is `[\"4\", \"5\", \"6\"]`,\nthree strings, not three numbers, so `+` on two of them joins text.\n`Number`, from *Conversion — when a value does not fit*, turns a piece into a\nnumber: `Number(\"4\")` is `4`. `split` has two more forms, a second argument that limits how\nmany pieces it returns and a pattern called a *regular expression* in place of\nthe separator; this course uses neither."
            },
            {
              "p": "`split` leaves its string as it was, as every string method does, and `join`\nleaves its array as it was. Because `split` returns an array, an array method\ncan be chained straight after it, as *Working with strings* chained string\nmethods:"
            },
            {
              "code": "\"2026-10-26\".split(\"-\").join(\"/\")     // \"2026/10/26\"",
              "lang": "js",
              "file": "chain.js"
            },
            {
              "p": "`split(\"-\")` evaluates to `[\"2026\", \"10\", \"26\"]`, and `join(\"/\")` puts the\npieces back together with a different separator."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "[\"x\", \"y\", \"z\"].join(\"-\")",
                    "accept": [
                      "\"x-y-z\"",
                      "x-y-z",
                      "'x-y-z'"
                    ],
                    "miss": "**The separator goes between each pair of elements**, not after the last one.\n",
                    "why": "`\"x-y-z\"`.\n"
                  },
                  {
                    "code": "\"red green blue\".split(\" \").length",
                    "accept": [
                      "3"
                    ],
                    "miss": "**`split(\" \")` cuts at each space**: `[\"red\", \"green\", \"blue\"]`.\n",
                    "why": "`3`.\n"
                  },
                  {
                    "code": "\"7,8\".split(\",\")[0] + 1",
                    "accept": [
                      "\"71\"",
                      "71",
                      "'71'"
                    ],
                    "miss": "**The pieces `split` returns are strings.** The element at index 0 is `\"7\"`, and `+` between a string and a number joins them as text.\n",
                    "why": "`\"71\"`. `Number(\"7\") + 1` would be `8`.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Does it change the array?",
          "body": [
            {
              "p": "Between this lesson and *Adding and removing elements* you have met ten array\nmethods, and `split` on strings. Two questions decide how you use each one:\ndoes it change the array it is called on, and what does it return? A method\nthat changes the array is **mutating**, and one that leaves it as it was is\n**non-mutating**."
            },
            {
              "p": "**Mutating: these change the array.**"
            },
            {
              "list": [
                "`push` and `unshift` add elements, and return the new length.",
                "`pop` and `shift` remove one element, and return it.",
                "`splice` removes and inserts, and returns the removed elements, as an array."
              ]
            },
            {
              "p": "**Non-mutating: these leave the array as it was.**"
            },
            {
              "list": [
                "`includes` returns `true` or `false`.",
                "`indexOf` returns an index, or `-1`.",
                "`slice` and `concat` return a new array.",
                "`join` returns a string."
              ]
            },
            {
              "p": "`split` belongs with the second group, because no string method changes its\nstring. `reverse`, which turns an array back to front, belongs with the first:\nit reverses the array it is called on. It is named here, not taught."
            },
            {
              "p": "The two groups lead to opposite mistakes. With a mutating method, the mistake\nis to use what it returns as if it were the array: `return list.push(item);`\nreturns a length, as *Adding and removing elements* showed. With a\nnon-mutating method, the mistake is to call it and not keep what it returns:"
            },
            {
              "code": "const queue = [\"Ada\", \"Lin\", \"Kai\"];\nqueue.slice(1);\nconsole.log(queue);       // [\"Ada\", \"Lin\", \"Kai\"]",
              "lang": "js",
              "file": "keep.js"
            },
            {
              "p": "`queue.slice(1)` made a new array, `[\"Lin\", \"Kai\"]`, and nothing stored it.\n`queue` is unchanged, because `slice` never changes the array. It is the same\nmistake as `name.toUpperCase();` on a line of its own. To keep the result,\nstore it:"
            },
            {
              "code": "const rest = queue.slice(1);",
              "lang": "js",
              "file": "keep.js"
            },
            {
              "p": "A variable declared with `let` can also be reassigned to the result, as\n*Working with strings* did with `input = input.trim()`; one declared with\n`const` cannot, as *Lists of values* showed."
            },
            {
              "p": "For a method you have not met, MDN's page for it says whether it changes the\narray, and MDN's page on arrays lists the mutating methods together. You can\nalso find out by running it: call the method, then print the array."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const xs = [3, 1, 2];\nxs.slice(0, 1);\nxs.length\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**`slice` is non-mutating.** It returned `[3]`, nothing kept it, and `xs` still has three elements.\n",
                    "why": "`3`.\n"
                  },
                  {
                    "code": "const xs = [3, 1, 2];\nxs.splice(0, 1);\nxs.length\n",
                    "accept": [
                      "2"
                    ],
                    "miss": "**`splice` is mutating.** It removed the element at index 0 from `xs` itself.\n",
                    "why": "`2`. `xs` is now `[1, 2]`.\n"
                  },
                  {
                    "question": "Which line changes the array `list`?",
                    "choices": [
                      "list.join(\"-\");",
                      "list.unshift(0);",
                      "list.concat([0]);"
                    ],
                    "accept": [
                      "list.unshift(0);"
                    ],
                    "miss": "**`join` and `concat` are non-mutating:** each returns a new value, a string and an array, and leaves `list` as it was. `unshift` adds to the front of `list` itself.\n",
                    "why": "`list.unshift(0);`.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "step",
          "label": "From a sentence to a function",
          "body": [
            {
              "p": "The four subgoals from *Writing a function's answer* still apply. The one that\nchanges with these methods is the fourth, sending the answer out with\n`return`. A non-mutating method's answer is the value it returns, so that\nvalue is what `return` has to send out."
            },
            {
              "p": "**Example 1.** \"`fullTeam` takes an array of players and an array of\nsubstitutes, and returns one array holding both, players first, without\nchanging either: `fullTeam([\"Ada\", \"Lin\"], [\"Kai\"])` is\n`[\"Ada\", \"Lin\", \"Kai\"]`.\""
            },
            {
              "p": "*Adding and removing elements* fixed a function by putting two jobs on two\nlines, one that changes the array and one that returns it:"
            },
            {
              "code": "readings.push(reading);\nreturn readings;",
              "lang": "js",
              "file": "add-reading.js"
            },
            {
              "p": "A first try uses the same two lines with `concat`:"
            },
            {
              "code": "function fullTeam(players, substitutes) {\n  players.concat(substitutes);\n  return players;\n}",
              "lang": "js",
              "file": "full-team.js"
            },
            {
              "p": "It runs without an error. The tests reported:"
            },
            {
              "code": "✗ fullTeam([\"Ada\", \"Lin\"], [\"Kai\"]) → [\"Ada\", \"Lin\", \"Kai\"]\n✓ fullTeam([\"Ada\"], []) → [\"Ada\"]\n✓ fullTeam leaves players as it was",
              "lang": "text"
            },
            {
              "p": "`fullTeam([\"Ada\", \"Lin\"], [\"Kai\"])` returned `[\"Ada\", \"Lin\"]`. Work it\nthrough by hand. Line 2, `players.concat(substitutes)`, evaluates to a new\narray, `[\"Ada\", \"Lin\", \"Kai\"]`, and nothing stores it. `concat` never changes\nthe array it is called on, so `players` still holds `[\"Ada\", \"Lin\"]`, and\nline 3 returns that. The test with `[]` passed only because adding no\nsubstitutes gives the same players back."
            },
            {
              "p": "The two-line fix worked for `push` because `push` changes the array. `concat`\ndoes not: its answer is what it returns. So the fix returns that value:"
            },
            {
              "code": "function fullTeam(players, substitutes) {\n  return players.concat(substitutes);\n}",
              "lang": "js",
              "file": "full-team.js"
            },
            {
              "code": "✓ fullTeam([\"Ada\", \"Lin\"], [\"Kai\"]) → [\"Ada\", \"Lin\", \"Kai\"]\n✓ fullTeam([\"Ada\"], []) → [\"Ada\"]\n✓ fullTeam leaves players as it was",
              "lang": "text"
            },
            {
              "p": "**Example 2: you choose the line.** \"`asSentence` takes an array of words and\nreturns them as one string, with a space between each pair:\n`asSentence([\"fresh\", \"bread\", \"daily\"])` is `\"fresh bread daily\"`.\""
            },
            {
              "code": "function asSentence(words) {\n  // TODO: put your code here\n}",
              "lang": "js",
              "file": "as-sentence.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "What goes at the TODO?",
                    "choices": [
                      "return words.join(\" \");",
                      "words.join(\" \"); return words;",
                      "return words.split(\" \");"
                    ],
                    "accept": [
                      "return words.join(\" \");"
                    ],
                    "miss": "**If you chose `words.join(\" \"); return words;` — `join` is non-mutating,** so the string it makes is lost, and the function returns the array unchanged. **If you chose `split` — `split` is a string method,** and `words` is an array: the call stops with `TypeError: words.split is not a function`.\n",
                    "why": "`return words.join(\" \");`. The answer is the string `join` returns.\n"
                  },
                  {
                    "question": "What does `asSentence([\"solo\"])` return? Type it.",
                    "accept": [
                      "\"solo\"",
                      "solo",
                      "'solo'"
                    ],
                    "miss": "**The separator goes only between elements.** One element has no pair to separate, so `join` returns it on its own, as a string.\n",
                    "why": "`\"solo\"`.\n"
                  },
                  {
                    "code": "function fullTeam(players, substitutes) {\n  players.concat(substitutes);\n  return players;\n}\nfullTeam([], [\"Kai\"])\n",
                    "accept": [
                      "[]",
                      "[ ]"
                    ],
                    "miss": "**If you typed `[\"Kai\"]` — that would mean `concat` changed `players`.** It does not: the new array it made, `[\"Kai\"]`, was not kept, and the first try returns `players` as it came in, empty.\n",
                    "why": "`[]`. The fixed version returns `[\"Kai\"]`.\n"
                  }
                ]
              }
            }
          ],
          "n": 6
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`includes` says whether a value is in an array; `indexOf` says where**, or `-1`. In a condition, `indexOf`'s `0` is falsy and its `-1` truthy, so a yes-or-no question reads better with `includes`.",
                "**`slice(start, end)` copies part of an array**, the end left out, and `concat` returns two arrays combined. `+` does not combine arrays.",
                "**`join` makes a string from an array**, with a separator between elements; **`split` cuts a string into an array** of strings.",
                "**Mutating methods change the array** (`push`, `pop`, `shift`, `unshift`, `splice`). The others this lesson teaches leave it as it was and return a new value, which is lost unless you keep it."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array: copying methods and mutating methods",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array#copying_methods_and_mutating_methods",
              "note": "which methods change the array, side by side."
            },
            {
              "title": "Array.prototype.slice()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/slice",
              "note": "and its neighbours `includes`, `indexOf`, `concat` and `join`, linked from the same page's sidebar."
            },
            {
              "title": "String.prototype.split()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/split",
              "note": "every form, including the ones this course does not use."
            }
          ]
        }
      ],
      "_source": "lessons/finding-slicing.md",
      "practice": {
        "id": "finding-slicing",
        "outcome": 2,
        "steps": [
          {
            "kind": "predict",
            "title": "Finding and copying",
            "prompt": "Type the value each one evaluates to, or press the answer. Write text with its quotes, and an array with its square brackets.\n",
            "items": [
              {
                "id": "p1",
                "code": "const pets = [\"cat\", \"dog\", \"fish\"];\npets.indexOf(\"fish\")\n",
                "accept": [
                  "2"
                ],
                "miss": "**Indexes start at 0.** `\"cat\"` is at 0, `\"dog\"` at 1, so `\"fish\"` is at 2.\n",
                "why": "`2`.\n"
              },
              {
                "id": "p2",
                "code": "const xs = [1, 2, 3, 4];\nconst ys = xs.slice(1, 3);\nxs\n",
                "accept": [
                  "[1, 2, 3, 4]",
                  "[1,2,3,4]",
                  "[ 1, 2, 3, 4 ]"
                ],
                "miss": "**`slice` leaves the array as it was and returns a copy of part of it.** The copy, `[2, 3]`, is in `ys`; the question asks about `xs`.\n",
                "why": "`[1, 2, 3, 4]`.\n"
              },
              {
                "id": "p3",
                "code": "[\"Ann\", \"Bo\"].includes(\"An\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**On an array, `includes` compares whole elements.** `\"An\"` is part of `\"Ann\"`, but no element is `\"An\"` itself. Finding text inside a string is what `includes` does on a string.\n",
                "why": "`false`.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "[3, 6, 9].indexOf(4)",
                "accept": [
                  "-1"
                ],
                "miss": "**`indexOf` returns `-1` when no element is equal to the value.** It is not an error, and it is not `undefined`.\n",
                "why": "`-1`.\n"
              },
              {
                "id": "p5",
                "code": "\"a-b-c\".split(\"-\")",
                "accept": [
                  "[\"a\", \"b\", \"c\"]",
                  "[\"a\",\"b\",\"c\"]",
                  "['a', 'b', 'c']",
                  "[ 'a', 'b', 'c' ]"
                ],
                "miss": "**`split` cuts the string at each separator and drops the separators.** The pieces are the three letters.\n",
                "why": "`[\"a\", \"b\", \"c\"]`.\n"
              },
              {
                "id": "p6",
                "code": "[2, 4].concat([6]).join(\"+\")",
                "accept": [
                  "\"2+4+6\"",
                  "2+4+6",
                  "'2+4+6'"
                ],
                "miss": "**`concat` makes `[2, 4, 6]`, and `join(\"+\")` puts `\"+\"` between each pair.** `join` makes a string; nothing adds the numbers.\n",
                "why": "`\"2+4+6\"`, a string.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered the first one with 3",
                "say": "What is the index of the first element? Count the positions from that number.\n"
              },
              {
                "when": "you expected xs to be [2, 3]",
                "say": "Which variable holds what `slice` returned? Does `slice` change the array it is called on?\n"
              },
              {
                "when": "you chose true for the third one",
                "say": "Is `\"An\"` one of the elements, or part of one? What does `includes` compare on an array?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "A tag list, run",
            "prompt": "Predict each line this program prints, then run it and compare.\n",
            "filename": "tags.js",
            "code": "const tags = [\"news\", \"sport\", \"weather\"];\nconst more = tags.concat([\"travel\"]);\nconsole.log(more.length);\nconsole.log(tags.length);\n\nconsole.log(more.indexOf(\"travel\"));\nconsole.log(more.slice(1, 3));\nconsole.log(tags.join(\" | \"));\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print? Type the number.",
                "accept": [
                  "4"
                ],
                "miss": "**`more` is the new array `concat` returned**: the three tags, then `\"travel\"`.\n",
                "why": "`4`.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print? Type the number.",
                "accept": [
                  "3"
                ],
                "miss": "**`concat` leaves the array it is called on as it was.** Only `more` has `\"travel\"`.\n",
                "why": "`3`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print? Type the number.",
                "accept": [
                  "3"
                ],
                "miss": "**`\"travel\"` is the fourth element of `more`, and indexes start at 0.**\n",
                "why": "`3`.\n"
              },
              {
                "id": "r4",
                "question": "What does the fourth line print? Type the array.",
                "accept": [
                  "[\"sport\", \"weather\"]",
                  "[\"sport\",\"weather\"]",
                  "['sport', 'weather']",
                  "[ 'sport', 'weather' ]"
                ],
                "miss": "**`slice(1, 3)` takes indexes 1 and 2**; index 3, `\"travel\"`, is the end and is left out.\n",
                "why": "`[\"sport\",\"weather\"]`, as the editor prints it.\n"
              },
              {
                "id": "r5",
                "question": "What does the last line print? Type it.",
                "accept": [
                  "news | sport | weather",
                  "\"news | sport | weather\""
                ],
                "miss": "**The separator `\" | \"` goes between each pair of elements**, and `tags` still has only its three.\n",
                "why": "`news | sport | weather`.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected the second line to print 4",
                "say": "Which array did `concat` return, and which array was it called on? Did either change?\n"
              },
              {
                "when": "you expected the fourth line to print three tags",
                "say": "Is the element at the end index of `slice` included or left out?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One value, several methods",
            "prompt": "Work each one through line by line, writing down what each name holds after each line, and type the value of the last line.\n",
            "items": [
              {
                "id": "t1",
                "code": "const parts = \"10:45:30\".split(\":\");\nconst kept = parts.slice(0, 2);\nkept.join(\"h\")\n",
                "accept": [
                  "\"10h45\"",
                  "10h45",
                  "'10h45'"
                ],
                "miss": "**`parts` is `[\"10\", \"45\", \"30\"]`, and `slice(0, 2)` keeps indexes 0 and 1.** `join(\"h\")` puts `\"h\"` between them.\n",
                "why": "`\"10h45\"`.\n"
              },
              {
                "id": "t2",
                "code": "const a = [4, 8];\nconst b = a.concat([8, 2]);\nb.indexOf(8) + b.length\n",
                "accept": [
                  "5"
                ],
                "miss": "**`b` is `[4, 8, 8, 2]`.** `indexOf` finds the first `8`, at index 1, and `b` has four elements.\n",
                "why": "`5`.\n"
              },
              {
                "id": "t3",
                "code": "const q = [\"a\", \"b\", \"c\"];\nq.slice(1);\nq.splice(0, 1);\nq.join(\"\")\n",
                "accept": [
                  "\"bc\"",
                  "bc",
                  "'bc'"
                ],
                "miss": "**Line 2 changes nothing: `slice` returned `[\"b\", \"c\"]` and nothing kept it.** Line 3's `splice` removes `\"a\"` from `q` itself.\n",
                "why": "`\"bc\"`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "const nums = \"4,5\".split(\",\");\nnums[0] + nums[1]\n",
                "accept": [
                  "\"45\"",
                  "45",
                  "'45'"
                ],
                "miss": "**The pieces `split` returns are strings**, `\"4\"` and `\"5\"`, so `+` joins them as text.\n",
                "why": "`\"45\"`, not `9`.\n"
              },
              {
                "id": "t5",
                "code": "const w = [\"x\", \"y\"];\nw.concat([\"z\"]);\nw.push(\"z\");\nw.length\n",
                "accept": [
                  "3"
                ],
                "miss": "**Line 2's `concat` returned a new array that nothing kept. Line 3's `push` changed `w` itself.**\n",
                "why": "`3`. `w` is `[\"x\", \"y\", \"z\"]`.\n"
              }
            ],
            "hints": [
              {
                "when": "your answer to the third one was \"c\"",
                "say": "Does line 2 change `q`? What did `slice` return, and where did it go?\n"
              },
              {
                "when": "you answered 9 for the fourth one",
                "say": "What type is each piece that `split` returns? What does `+` do between two of those?\n"
              },
              {
                "when": "you answered 4 for the last one",
                "say": "Which of the two methods on lines 2 and 3 changes the array it is called on?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Drop the extension",
            "prompt": "`withoutExtension` should return a file name without its last dot and what follows it: `withoutExtension(\"report.final.pdf\")` is `\"report.final\"`. `fileName` always has at least one dot. Build the lines that go between its braces, in order. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "  const parts = fileName.split(\".\");",
              "  const kept = parts.slice(0, parts.length - 1);",
              "  return kept.join(\".\");"
            ],
            "distractors": [
              {
                "line": "  return parts.join(\".\");",
                "why": "`slice` made a new array, `kept`, and left `parts` as it was, so `parts` still ends in `\"pdf\"`. Joining it gives back `\"report.final.pdf\"`.\n"
              },
              {
                "line": "  const kept = parts.slice(0, parts.length - 2);",
                "why": "The end index is already left out, so `parts.length - 1` drops the last piece. `- 2` drops one more, and the result is `\"report\"`.\n"
              }
            ],
            "why": "`split(\".\")` cuts the name into `[\"report\", \"final\", \"pdf\"]`. `slice` copies every piece but the last, because its end index, `parts.length - 1`, is left out. `join(\".\")` puts back the dots between the pieces that are left.\n",
            "hints": [
              {
                "when": "you are unsure which array to join",
                "say": "Does `slice` change `parts`, or return a new array? Which name holds the pieces without `\"pdf\"`?\n"
              },
              {
                "when": "you are unsure of the end index",
                "say": "For `[\"report\", \"final\", \"pdf\"]`, the length is 3. Which end index keeps indexes 0 and 1, remembering the end is left out?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "A list that ends in \"and\"",
            "prompt": "`listLabel` takes an array of at least two items and returns them with a comma and a space between each pair. Change it so the last two are joined by `\" and \"` instead: `listLabel([\"tea\", \"milk\", \"bread\"])` should be `\"tea, milk and bread\"`, and `listLabel([\"tea\", \"milk\"])` should be `\"tea and milk\"`. Leave the array as it was. Write your code between the braces, and keep `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: which method would have changed the array, and why does your version not?\n",
            "exportName": "listLabel",
            "filename": "list-label.js",
            "starter": "function listLabel(items) {\n  // TODO: put your code here\n  return items.join(\", \");\n}\n",
            "tests": [
              {
                "label": "listLabel([\"tea\", \"milk\", \"bread\"]) → \"tea, milk and bread\"",
                "body": "return subject(['tea', 'milk', 'bread']) === 'tea, milk and bread';"
              },
              {
                "label": "listLabel([\"tea\", \"milk\"]) → \"tea and milk\"",
                "body": "return subject(['tea', 'milk']) === 'tea and milk';"
              },
              {
                "label": "listLabel([\"a\", \"b\", \"c\", \"d\"]) → \"a, b, c and d\"",
                "body": "return subject(['a', 'b', 'c', 'd']) === 'a, b, c and d';"
              },
              {
                "label": "listLabel leaves the array as it was",
                "body": "const a = ['tea', 'milk', 'bread']; subject(a); return a.length === 3 && a[0] === 'tea' && a[2] === 'bread';"
              }
            ],
            "hints": [
              {
                "when": "the labels are right but the array lost its last item",
                "say": "`pop` removes the last element from the array itself. Which way of reading the last element leaves it where it is?\n"
              },
              {
                "when": "the last item appears twice",
                "say": "`items.join(\", \")` already includes the last item. Which method copies every item but the last?\n"
              },
              {
                "when": "listLabel([\"tea\", \"milk\", \"bread\"]) gives \"tea and bread\"",
                "say": "The end index of `slice` is left out. Work out your end index by hand for a length of 3: you want indexes 0 and 1.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "The next stop",
            "prompt": "`nextStop` takes a route, a string of stop names with `\" > \"` between them, and the name of one stop. It returns the name of the stop after that one: `nextStop(\"Main > Elm > Oak\", \"Elm\")` is `\"Oak\"`. When the stop is the last on the route, or is not on the route at all, it returns `\"none\"`. Write your code between the braces, and put `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: what would `nextStop(\"Main > Elm > Oak\", \"Ash\")` return without a check for `-1`, and why?\n",
            "exportName": "nextStop",
            "filename": "next-stop.js",
            "starter": "function nextStop(route, stop) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "nextStop(\"Main > Elm > Oak\", \"Elm\") → \"Oak\"",
                "body": "return subject('Main > Elm > Oak', 'Elm') === 'Oak';"
              },
              {
                "label": "nextStop(\"Main > Elm > Oak\", \"Main\") → \"Elm\"",
                "body": "return subject('Main > Elm > Oak', 'Main') === 'Elm';"
              },
              {
                "label": "nextStop(\"Main > Elm > Oak\", \"Oak\") → \"none\"",
                "body": "return subject('Main > Elm > Oak', 'Oak') === 'none';"
              },
              {
                "label": "nextStop(\"Main > Elm > Oak\", \"Ash\") → \"none\"",
                "body": "return subject('Main > Elm > Oak', 'Ash') === 'none';"
              },
              {
                "label": "nextStop(\"Pier\", \"Pier\") → \"none\"",
                "body": "return subject('Pier', 'Pier') === 'none';"
              }
            ],
            "hints": [
              {
                "when": "nextStop(\"Main > Elm > Oak\", \"Ash\") gives \"Main\"",
                "say": "What does `indexOf` return for a stop that is not there? Add 1 to it: which index is that?\n"
              },
              {
                "when": "nextStop(\"Main > Elm > Oak\", \"Main\") gives \"none\"",
                "say": "`\"Main\"` is at index 0. What does your condition do with `0`? Is `0` truthy or falsy?\n"
              },
              {
                "when": "nextStop(\"Main > Elm > Oak\", \"Oak\") gives undefined",
                "say": "`\"Oak\"` is the last stop, so the index after it is past the end. What does reading past the end give, and what should the function return instead?\n"
              },
              {
                "when": "every test gives \"none\"",
                "say": "Print what your `split` returns. Does the separator you passed match `\" > \"`, spaces included?\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review an allowed-types check",
            "prompt": "A code assistant wrote `isAllowed`, which should return `true` when a file type is one of the types in a setting such as `\"jpg,png,gif\"`, and `false` when it is not. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "is-allowed.js",
            "code": "// true when type is one of the file types in setting, a list such as\n// \"jpg,png,gif\" with a comma between each pair.\nfunction isAllowed(setting, type) {\n  const allowed = setting.split(\", \");\n  return allowed.indexOf(type) > 0;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "When `setting` is `\"jpg,png,gif\"`, what does line 4 store in `allowed`? Type the array.",
                "accept": [
                  "[\"jpg,png,gif\"]",
                  "['jpg,png,gif']",
                  "[ \"jpg,png,gif\" ]",
                  "[ 'jpg,png,gif' ]"
                ],
                "miss": "**The separator `\", \"` has a space, and the setting has none.** When the separator is not in the string, `split` returns the whole string as one element.\n",
                "why": "`[\"jpg,png,gif\"]`: one element. Line 4 should split at `\",\"`.\n"
              },
              {
                "id": "c2",
                "question": "With line 4 fixed to split at `\",\"`, what does `isAllowed(\"jpg,png,gif\", \"jpg\")` return?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**`\"jpg\"` is the first type, at index 0, and `0 > 0` is `false`.** The comparison keeps `-1` out, but it keeps index 0 out too.\n",
                "why": "`false`, for the first type in the setting.\n"
              },
              {
                "id": "c3",
                "question": "Which comparison should line 5 make?",
                "choices": [
                  "allowed.indexOf(type) >= 0",
                  "allowed.indexOf(type) !== 0",
                  "allowed.indexOf(type) >= 1"
                ],
                "accept": [
                  "allowed.indexOf(type) >= 0"
                ],
                "miss": "**`!== 0` is `true` for `-1`**, so a missing type would be allowed. **`>= 1` is the same as `> 0`**: it still leaves out index 0.\n",
                "why": "`allowed.indexOf(type) >= 0`: every real index is 0 or more, and `-1` is not. `allowed.includes(type)` would also be correct, and reads as the yes-or-no question it is.\n"
              }
            ],
            "exportName": "isAllowed",
            "starter": "// true when type is one of the file types in setting, a list such as\n// \"jpg,png,gif\" with a comma between each pair.\nfunction isAllowed(setting, type) {\n  const allowed = setting.split(\", \");\n  return allowed.indexOf(type) > 0;\n}\n",
            "tests": [
              {
                "label": "isAllowed(\"jpg,png,gif\", \"jpg\") → true",
                "body": "return subject('jpg,png,gif', 'jpg') === true;"
              },
              {
                "label": "isAllowed(\"jpg,png,gif\", \"gif\") → true",
                "body": "return subject('jpg,png,gif', 'gif') === true;"
              },
              {
                "label": "isAllowed(\"jpg,png,gif\", \"exe\") → false",
                "body": "return subject('jpg,png,gif', 'exe') === false;"
              },
              {
                "label": "isAllowed(\"pdf\", \"pdf\") → true",
                "body": "return subject('pdf', 'pdf') === true;"
              },
              {
                "label": "isAllowed(\"tiff,png\", \"tif\") → false",
                "body": "return subject('tiff,png', 'tif') === false;"
              }
            ],
            "hints": [
              {
                "when": "\"jpg\" and \"gif\" both give false",
                "say": "Print `allowed`. How many elements does it have? Does the separator you split at appear in the setting?\n"
              },
              {
                "when": "\"jpg\" gives false, but \"gif\" gives true",
                "say": "`\"jpg\"` is at index 0. Is `0 > 0` true? Which comparison lets in index 0 but not `-1`?\n"
              },
              {
                "when": "isAllowed(\"jpg,png,gif\", \"exe\") gives true",
                "say": "What does `indexOf` return for a type that is not in the array? Does your comparison let that value through?\n"
              },
              {
                "when": "isAllowed(\"tiff,png\", \"tif\") gives true",
                "say": "Are you searching the setting, a string, or the array of types? `includes` on a string finds text anywhere in the setting: `\"tif\"` is inside `\"tiff\"`.\n"
              }
            ]
          }
        ]
      }
    },
    "walking-arrays": {
      "id": "walking-arrays",
      "title": "Walking an array",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`arr.traversal` (concepts.md): walk an array with `for…of` or an indexed `for`, choosing by whether an iteration needs the position, and write sum, count, maximum, find-first and keep-only-some by hand. It is `for…of`'s second pass (D-045, D-066): *The for…of loop* walked strings, and this lesson points back to it rather than reteaching it, as it does to *The for loop*, *The while loop* (the accumulator) and *Leaving a loop early: break and continue* (the search and its not-found value). The five patterns are this lesson's worked examples and practice, not lessons of their own (Nick, D-069). Keep-only-some builds a new array with `push`, from *Adding and removing elements*; changing the array being walked is the next lesson's, *Mutating while traversing*, and is only pointed at. The forms not taught here are named with where they are taught (D-066 item 2): `for…in` in Objects, the methods that take a function in *Functions as values*. Strict mode is assumed (D-073); nothing here behaves differently without it. Procedural, explain first (D-061 item 1); the writing direction is modelled fail-first (01-explanations rule 5a) on the pattern's own mistake, a maximum started at 0. Derived from those rules, pending Nick's review.",
      "concepts": [
        "arr.traversal"
      ],
      "lede": "*The for…of loop* walked a string one character at a time. An array is walked the same way, one element at a time. This lesson shows how to tell whether an iteration needs only the element or also its position, and how to write five common jobs: add up, count, find the highest, find the first match, and keep only the elements that match.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The for…of loop · The for loop · Leaving a loop early: break and continue · Adding and removing elements · Mutating while traversing"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Iteration** — one run of the loop body. **Loop variable** — the variable that holds the loop's current value on each iteration: `reading` in `for (const reading of readings)`, `i` in a `for` loop. **Accumulator** — a variable that builds up a result, one iteration at a time. **Search** — a loop that looks for the first element that matches, and stops. **Not-found value** — what a search returns when nothing matches, such as `null` or `-1`. **Filtering** — keeping only the elements that match, in a new array.",
          "entries": [
            "**Iteration** — one run of the loop body.",
            "**Loop variable** — the variable that holds the loop's current value on each iteration: `reading` in `for (const reading of readings)`, `i` in a `for` loop.",
            "**Accumulator** — a variable that builds up a result, one iteration at a time.",
            "**Search** — a loop that looks for the first element that matches, and stops.",
            "**Not-found value** — what a search returns when nothing matches, such as `null` or `-1`.",
            "**Filtering** — keeping only the elements that match, in a new array."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory: the `for…of` and\n`for` loops over a string, `push`, and where a search returns its not-found\nvalue. Type the value, with quotes if it is text, or press the answer. Press\n**I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let letters = 0;\nfor (const ch of \"a-b-c\") {\n  if (ch !== \"-\") {\n    letters++;\n  }\n}\nletters\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**`for…of` hands each iteration one character**, from *The for…of loop*. Five iterations; the three that are not a dash add one.\n",
                    "why": "`3`.\n"
                  },
                  {
                    "code": "const word = \"cat\";\nlet out = \"\";\nfor (let i = 0; i < word.length; i++) {\n  out += i;\n}\nout\n",
                    "accept": [
                      "\"012\"",
                      "012",
                      "'012'"
                    ],
                    "miss": "**`i` takes each position of the string**, from *The for loop*: 0, 1 and 2. The loop stops when `i` reaches 3, the length. `out` is text, so `+=` joins each number on.\n",
                    "why": "`\"012\"`.\n"
                  },
                  {
                    "code": "const xs = [4];\nxs.push(9);\nxs\n",
                    "accept": [
                      "[4, 9]",
                      "[4,9]",
                      "[ 4, 9 ]"
                    ],
                    "miss": "**`push` adds to the end of the array**, from *Adding and removing elements*, and the array stays changed.\n",
                    "why": "`[4, 9]`.\n"
                  },
                  {
                    "question": "A search loop looks for the first digit in a piece of text. Where does it return its not-found value, `-1`?",
                    "choices": [
                      "After the loop",
                      "In an else, inside the loop",
                      "Before the loop"
                    ],
                    "accept": [
                      "After the loop"
                    ],
                    "miss": "**One character that does not match says nothing about the ones after it**, from *Leaving a loop early*. An `else` inside the loop would return `-1` at the first character that is not a digit.\n",
                    "why": "After the loop, once every character has been checked.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can walk an array with `for…of` or an indexed `for` loop, and choose by whether an iteration needs the element's position",
                "I can add up, count and find the highest element of an array with a loop",
                "I can write a search for the first element that matches, and build a new array of only the elements that match"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "One element at a time",
          "body": [
            {
              "p": "*Lists of values* kept temperature readings in an array. Adding them up by\nindex looks like this:"
            },
            {
              "code": "const readings = [12, 15, 9];\nconst total = readings[0] + readings[1] + readings[2];\nconsole.log(total);    // 36",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "p": "That line names every index, so it is right only for an array of exactly\nthree readings. An array of any length needs a loop."
            },
            {
              "p": "`for…of` walks an array the way *The for…of loop* showed it walking a\nstring, handing each iteration the next element:"
            },
            {
              "code": "const readings = [12, 15, 9];\nlet total = 0;\nfor (const reading of readings) {\n  total += reading;\n}\nconsole.log(total);    // 36",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "p": "`reading` is the loop variable, and on each iteration it holds one element.\n`total` is an **accumulator**, as in *The while loop*: it starts at 0, before\nthe loop, and each iteration adds one element to it."
            },
            {
              "table": {
                "head": [
                  "Iteration",
                  "`reading`",
                  "`total` after"
                ],
                "rows": [
                  [
                    "1",
                    "`12`",
                    "`12`"
                  ],
                  [
                    "2",
                    "`15`",
                    "`27`"
                  ],
                  [
                    "3",
                    "`9`",
                    "`36`"
                  ]
                ]
              }
            },
            {
              "p": "Three iterations, one for each element. There is no index to start, check or\nmove, so while the body leaves the array alone, the loop cannot skip the first\nelement or read past the last. An empty array, `[]`, gives the loop no\niterations at all, and `total` stays 0."
            },
            {
              "p": "The loop variable can have any name. This course names the array in the\nplural and the loop variable in the singular, `readings` and `reading`,\n`names` and `name`, so that the header reads as a sentence and the body says\nwhat each value is. Short loops often use one letter instead, as `ch` did for\na character. The loop variable is declared with `const`, as in *The for…of\nloop*."
            },
            {
              "p": "**The element is not an index.** *The for…of loop* showed that `text[ch]`\nuses a character as a position and gives `undefined`. Over an array of\nnumbers the same slip is harder to see, because a number *can* be an index.\nThis loop is wrong on purpose:"
            },
            {
              "code": "const readings = [2, 0, 5];\nlet total = 0;\nfor (const reading of readings) {\n  total += readings[reading];\n}\nconsole.log(total);    // NaN",
              "lang": "js",
              "file": "sum.js"
            },
            {
              "p": "Each row is one iteration, with `total` as it stands after that iteration:"
            },
            {
              "table": {
                "head": [
                  "`reading`",
                  "`readings[reading]`",
                  "`total`"
                ],
                "rows": [
                  [
                    "`2`",
                    "`5`",
                    "`5`"
                  ],
                  [
                    "`0`",
                    "`2`",
                    "`7`"
                  ],
                  [
                    "`5`",
                    "`undefined`",
                    "`NaN`"
                  ]
                ]
              }
            },
            {
              "p": "The first two iterations read real elements, just not the ones the loop is\non. The third reads index 5, past the end, and a number plus `undefined` is\n`NaN`, as in *When there is nothing there*. `reading` already is the element:\nthe line is `total += reading;`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let out = \"\";\nfor (const day of [\"Mon\", \"Tue\", \"Wed\"]) {\n  out += day[0];\n}\nout\n",
                    "accept": [
                      "\"MTW\"",
                      "MTW",
                      "'MTW'"
                    ],
                    "miss": "**`day` holds one element, a whole string such as `\"Mon\"`,** and `day[0]` is that string's first character.\n",
                    "why": "`\"MTW\"`.\n"
                  },
                  {
                    "question": "In `for (const n of [10, 20, 30])`, what does `n` hold on the second iteration?",
                    "choices": [
                      "20",
                      "1",
                      "[10, 20]"
                    ],
                    "accept": [
                      "20"
                    ],
                    "miss": "**`for…of` hands you the element, not its index.** `1` is the index of the second element; a `for` loop's `i` would hold that. And `n` holds one element at a time, never the elements so far.\n",
                    "why": "`20`.\n"
                  },
                  {
                    "code": "const xs = [1, 2];\nlet total = 0;\nfor (const x of xs) {\n  total += xs[x];\n}\ntotal\n",
                    "accept": [
                      "NaN"
                    ],
                    "miss": "**`xs[x]` uses the element as an index.** On the first iteration `x` is 1, and `xs[1]` is 2. On the second `x` is 2, and `xs[2]` is past the end: `undefined`. `2 + undefined` is `NaN`.\n",
                    "why": "`NaN`. The line should be `total += x;`, and then `total` is 3.\n"
                  },
                  {
                    "code": "let count = 0;\nfor (const x of []) {\n  count++;\n}\ncount\n",
                    "accept": [
                      "0"
                    ],
                    "miss": "**An empty array has no elements, so the loop runs no iterations.** `count++` never runs.\n",
                    "why": "`0`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "When an iteration needs the position",
          "body": [
            {
              "p": "`for…of` hands each iteration the element and nothing else. Some tasks also\nneed to know where the element is. The test is the one *The for…of loop*\ngave for strings: **an iteration needs the position when it looks at a\nneighbour, when it skips elements, or when what it does depends on where it\nis.** A task whose answer is a position needs it too. Then a `for` loop fits,\nbecause its loop variable `i` is the position."
            },
            {
              "p": "**Example.** Print a website's visitors for each day, with the day's\nnumber in front:"
            },
            {
              "code": "const visitors = [420, 610, 380];\nfor (let i = 0; i < visitors.length; i++) {\n  const day = i + 1;\n  console.log(\"Day \" + day + \": \" + visitors[i]);\n}\n// Day 1: 420\n// Day 2: 610\n// Day 3: 380",
              "lang": "js",
              "file": "visitors.js"
            },
            {
              "p": "The loop starts at 0, because `i` is a position and positions start at 0.\nThe day numbers people read start at 1, so each iteration works out\n`i + 1` for the label."
            },
            {
              "p": "**Which one to write.** An indexed `for` loop can walk every element too:\n`for (let i = 0; i < readings.length; i++)`, with `readings[i]` in the body\nwhere `for…of` has `reading`. That is legal, and common in older code,\nbecause `for…of` only arrived in JavaScript in 2015. This course writes\n`for…of` when an iteration needs only the element, because there is no index\nto get wrong, and a `for` loop when it needs the position, because the header\nshows at a glance where the loop starts, where it stops and how it moves. *The for…of loop* showed why a counter kept beside a\n`for…of` loop is the weaker way to get a position."
            },
            {
              "p": "JavaScript has other ways to walk an array. A `while` loop can do it, as\n*Choosing a loop* showed for strings. The `for…in` loop looks similar to\n`for…of` but is a different loop: the Objects module teaches it, and shows\nwhy it is not used for arrays. Arrays also have methods that walk them for\nyou, such as `forEach`, `map`, `filter`, `reduce` and `find`. Each one takes\na function as its argument, so they come in the *Functions as values* module.\nUnderneath, each runs a loop like the ones in this lesson."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which task needs the position of each element?",
                    "choices": [
                      "Add up a list of prices",
                      "Number a list of names: \"1. Ada\", \"2. Lin\"",
                      "Count the names that start with \"A\""
                    ],
                    "accept": [
                      "Number a list of names: \"1. Ada\", \"2. Lin\""
                    ],
                    "miss": "**Adding up and counting need each element, and nothing else**, so `for…of` does both. The numbers in front of the names come from where each name is: its position, plus one.\n",
                    "why": "Numbering the names: each label is `i + 1`.\n"
                  },
                  {
                    "code": "const xs = [3, 3, 5, 5, 5];\nlet same = 0;\nfor (let i = 1; i < xs.length; i++) {\n  if (xs[i] === xs[i - 1]) {\n    same++;\n  }\n}\nsame\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**Each iteration compares an element with the one before it**, as `doubledLetters` did for a string in *The for…of loop*. `i` is 1 to 4: 3 with 3 matches, 5 with 3 does not, and 5 with 5 matches twice.\n",
                    "why": "`3`.\n"
                  },
                  {
                    "question": "A function should return *where* the first reading below 0 is: its index. Which loop fits?",
                    "choices": [
                      "A for loop",
                      "for…of"
                    ],
                    "accept": [
                      "A for loop"
                    ],
                    "miss": "**The answer is a position, and `for…of` never gives you one.** A `for` loop has it in `i`.\n",
                    "why": "A `for` loop, which returns `i` when it finds the reading.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Counting, and finding the first",
          "body": [
            {
              "p": "**Counting.** A count has the shape of a sum with an `if` in the body. A sum\nadds each element to its accumulator; a count adds 1, and only for the\nelements that match:"
            },
            {
              "code": "const scores = [72, 48, 90, 55, 61];\nlet passed = 0;\nfor (const score of scores) {\n  if (score >= 60) {\n    passed++;\n  }\n}\nconsole.log(passed);   // 3",
              "lang": "js",
              "file": "passed.js"
            },
            {
              "p": "Both accumulators start at 0, and both are made before the loop, with `let`,\nso that they keep their value from one iteration to the next."
            },
            {
              "p": "**Finding the first.** *Leaving a loop early* wrote a **search** over a\nstring: a loop that stops at the first match, and returns a **not-found\nvalue** when nothing matches. Over an array it is the same loop. \"`firstFrost`\ntakes an array of readings and returns the first one below 0, or `null` when\nthere is none.\""
            },
            {
              "code": "function firstFrost(readings) {\n  for (const reading of readings) {\n    if (reading < 0) {\n      // found\n      return reading;\n    }\n  }\n  // not found: every reading was checked\n  return null;\n}\n// firstFrost([4, -2, 1, -6]) is -2; firstFrost([4, 1]) is null",
              "lang": "js",
              "file": "frost.js"
            },
            {
              "p": "The search works as it did over a string in *Leaving a loop early: break\nand continue*: `return` ends the loop and the function at the first match,\nand `return null;` comes after the loop, once every reading has been checked.\nThat lesson's other form, a variable and `break`, works over an array too.\nThe not-found value is `null`, not `-1`, because `-1` could be a real reading."
            },
            {
              "p": "*Finding, slicing and joining* taught `includes` and `indexOf`, which search\nan array for an element *equal to* a value you give them. A loop can search\nfor an element that meets any condition, such as \"below 0\"."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const ages = [17, 21, 16, 30];\nlet adults = 0;\nfor (const age of ages) {\n  if (age >= 18) {\n    adults++;\n  }\n}\nadults\n",
                    "accept": [
                      "2"
                    ],
                    "miss": "**The count grows by 1 only when the `if` is `true`:** for 21 and 30.\n",
                    "why": "`2`.\n"
                  },
                  {
                    "question": "In `passed.js`, where does `let passed = 0;` have to go?",
                    "choices": [
                      "Before the loop",
                      "Inside the loop, above the if",
                      "After the loop"
                    ],
                    "accept": [
                      "Before the loop"
                    ],
                    "miss": "**If you chose inside the loop — that line would run on every iteration,** and start the count again at 0 each time. **If you chose after the loop — it stops with a ReferenceError:** the loop reads `passed` before the line that makes it has run.\n",
                    "why": "Before the loop, so it runs once and the count carries from one iteration to the next.\n"
                  },
                  {
                    "question": "What does `firstFrost([3, -1, -4])` return? Type the number.",
                    "accept": [
                      "-1"
                    ],
                    "miss": "**The search stops at the first reading below 0.** That is `-1`; the loop never reaches `-4`.\n",
                    "why": "`-1`, a real reading. That is why the not-found value is `null`.\n"
                  },
                  {
                    "question": "What does `firstFrost([])` return? Type it.",
                    "accept": [
                      "null"
                    ],
                    "miss": "**An empty array gives the loop no iterations**, so the `return` inside it never runs, and the function reaches `return null;` after the loop.\n",
                    "why": "`null`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Keeping only some",
          "body": [
            {
              "p": "Some tasks want the matching elements themselves, not how many there are.\nThe result is then a new array: start it empty, and `push` each element that\nmatches. \"Keep the prices under 10.\""
            },
            {
              "code": "const prices = [12, 4, 30, 8];\nconst cheap = [];\nfor (const price of prices) {\n  if (price < 10) {\n    cheap.push(price);\n  }\n}\nconsole.log(cheap);    // [4, 8]\nconsole.log(prices);   // [12, 4, 30, 8]",
              "lang": "js",
              "file": "cheap.js"
            },
            {
              "p": "Each row is one iteration, with `cheap` as it stands after that iteration:"
            },
            {
              "table": {
                "head": [
                  "`price`",
                  "`price < 10`",
                  "`cheap`"
                ],
                "rows": [
                  [
                    "`12`",
                    "`false`",
                    "`[]`"
                  ],
                  [
                    "`4`",
                    "`true`",
                    "`[4]`"
                  ],
                  [
                    "`30`",
                    "`false`",
                    "`[4]`"
                  ],
                  [
                    "`8`",
                    "`true`",
                    "`[4, 8]`"
                  ]
                ]
              }
            },
            {
              "p": "`push` adds at the end, so the kept elements stay in the order they had in\n`prices`. `cheap` is declared with `const`, because the name holds the same\narray the whole time; `push` changes what is in it, and *Lists of values*\nshowed that `const` allows that. `prices` is not changed: the loop only reads\nit."
            },
            {
              "p": "Keeping only the elements that match is often called **filtering**. The\n`filter` method in *Functions as values* does the same with a function."
            },
            {
              "p": "Why build a new array, instead of removing the prices of 10 or more from\n`prices` itself with `splice`? Removing elements from an array while a loop\nwalks it skips elements, with no error: *Mutating while traversing* shows\nwhy. Building a new array leaves the one being walked alone."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const words = [\"sky\", \"a\", \"tree\", \"is\"];\nconst kept = [];\nfor (const w of words) {\n  if (w.length > 2) {\n    kept.push(w);\n  }\n}\nkept\n",
                    "accept": [
                      "[\"sky\", \"tree\"]",
                      "[\"sky\",\"tree\"]",
                      "['sky', 'tree']",
                      "[ 'sky', 'tree' ]"
                    ],
                    "miss": "**Only the words longer than 2 letters are pushed:** `\"sky\"` and `\"tree\"`, in the order they came.\n",
                    "why": "`[\"sky\", \"tree\"]`.\n"
                  },
                  {
                    "code": "const marks = [5, 9, 7];\nconst high = [];\nfor (const m of marks) {\n  if (m > 10) {\n    high.push(m);\n  }\n}\nhigh.length\n",
                    "accept": [
                      "0"
                    ],
                    "miss": "**No mark is above 10**, so nothing is pushed, and `high` is still the empty array it started as.\n",
                    "why": "`0`.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "From a sentence to a function",
          "body": [
            {
              "p": "The four subgoals from *The for…of loop* work for an array, with \"character\"\nread as \"element\":"
            },
            {
              "list": [
                "**Start the result.** What does it hold before any element is seen?",
                "**Take each element in turn.** Does an iteration need only the element, or its position too? That decides between `for…of` and a `for` loop.",
                "**Do one iteration's work.** What does one element do to the result?",
                "**Send the result out** with `return`, after the loop."
              ],
              "ordered": true
            },
            {
              "p": "**Example 1.** \"`highest` takes an array of temperature readings and returns\nthe highest one: `highest([12, 15, 9])` is 15. For an empty array there is no\nhighest reading, so it returns `undefined`.\""
            },
            {
              "p": "Each iteration needs a reading and not its position, so the loop is\n`for…of`. Each iteration compares its reading with the highest so far, and\nkeeps the higher one. A sum and a count start at 0, so a first try starts\nthe highest so far at 0 too:"
            },
            {
              "code": "function highest(readings) {\n  let top = 0;\n  for (const reading of readings) {\n    if (reading > top) {\n      top = reading;\n    }\n  }\n  return top;\n}",
              "lang": "js",
              "file": "highest.js"
            },
            {
              "p": "The tests reported:"
            },
            {
              "code": "✓ highest([12, 15, 9]) → 15\n✓ highest([7]) → 7\n✗ highest([-3, -8, -1]) → -1\n✗ highest([]) → undefined",
              "lang": "text"
            },
            {
              "p": "`highest([-3, -8, -1])` returned `0`. Work it through by hand, with\n`readings` holding `[-3, -8, -1]`, a cold week:"
            },
            {
              "p": "The first row is before the loop; each row after it is one iteration, with\n`top` as it stands after that iteration:"
            },
            {
              "table": {
                "head": [
                  "`reading`",
                  "`reading > top`",
                  "`top`"
                ],
                "rows": [
                  [
                    "before",
                    "",
                    "`0`"
                  ],
                  [
                    "`-3`",
                    "`false`",
                    "`0`"
                  ],
                  [
                    "`-8`",
                    "`false`",
                    "`0`"
                  ],
                  [
                    "`-1`",
                    "`false`",
                    "`0`"
                  ]
                ]
              }
            },
            {
              "p": "No reading is above 0, so `top` never changes, and the function returns `0`:\na reading that is not in the array. `highest([])` returned `0` too. The first\ntwo tests passed only because their readings were all above 0."
            },
            {
              "p": "Starting at 0 is right for a sum, because adding 0 changes nothing. For a\nhighest, the start must not be above the real answer, and 0 is above the\nanswer whenever every reading is below 0. A start that is sure to work is a\nreading from the array: the first."
            },
            {
              "code": "function highest(readings) {\n  // start the result: the first reading\n  let top = readings[0];\n  // take each reading in turn\n  for (const reading of readings) {\n    // do one iteration's work: keep the higher\n    if (reading > top) {\n      top = reading;\n    }\n  }\n  // send the result out\n  return top;\n}",
              "lang": "js",
              "file": "highest.js"
            },
            {
              "code": "✓ highest([12, 15, 9]) → 15\n✓ highest([7]) → 7\n✓ highest([-3, -8, -1]) → -1\n✓ highest([]) → undefined",
              "lang": "text"
            },
            {
              "p": "The first iteration compares the first reading with itself, which changes\nnothing. For `[]`, `readings[0]` is `undefined`, the loop runs no iterations,\nand `undefined` is returned, as the sentence asked."
            },
            {
              "p": "You will see two other ways. A `for` loop that starts at index 1 skips that\nfirst comparison, at the cost of an index. And some code starts at\n`-Infinity`, which every other number beats; then `highest([])` returns\n`-Infinity` instead of `undefined`. Both are legal. This course starts with\nthe first element, because it is a real value from the array, and keeps\n`for…of`, because no iteration needs the position."
            },
            {
              "p": "**Example 2: you choose the line.** \"`shortest` takes an array of words and\nreturns the shortest one: `shortest([\"pear\", \"fig\", \"banana\"])` is `\"fig\"`.\nFor an empty array it returns `undefined`.\" The line that starts the result\nis missing, marked `TODO`."
            },
            {
              "code": "function shortest(words) {\n  // TODO\n  for (const word of words) {\n    if (word.length < result.length) {\n      result = word;\n    }\n  }\n  return result;\n}",
              "lang": "js",
              "file": "shortest.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which line goes at the TODO?",
                    "choices": [
                      "let result = words[0];",
                      "let result = \"\";",
                      "let result = 0;"
                    ],
                    "accept": [
                      "let result = words[0];"
                    ],
                    "miss": "**If you chose `\"\"` — the empty string has a length of 0, and no word is shorter,** so `result` never changes and the function returns `\"\"`. **If you chose `0` — a number has no `.length`;** `result.length` is `undefined`, every comparison with it is `false`, and the function returns `0`.\n",
                    "why": "`let result = words[0];`: a real word from the array, and `undefined` when the array is empty.\n"
                  },
                  {
                    "question": "With that line, what does `shortest([\"kiwi\", \"fig\", \"yam\"])` return? Type it, with quotes.",
                    "accept": [
                      "\"fig\"",
                      "'fig'",
                      "fig"
                    ],
                    "miss": "**`\"fig\"` and `\"yam\"` are both 3 letters.** When the loop reaches `\"yam\"`, `3 < 3` is `false`, so `result` stays `\"fig\"`.\n",
                    "why": "`\"fig\"`: with `<`, the first of the shortest words wins a tie.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`for…of` hands each iteration the next element** of an array, as it did each character of a string. The element is not an index: `xs[x]` reads the wrong element, or none.",
                "**Use a `for` loop when an iteration needs the position:** a neighbour, a skip, a number shown with the element, or an answer that is a position.",
                "**A sum and a count start at 0; a highest starts at the first element.** A search returns its not-found value after the loop.",
                "**To keep only some elements, `push` them into a new array**, and leave the array you are walking alone."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "for...of",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for...of",
              "note": "its example \"Iterating over an Array\"."
            },
            {
              "title": "Indexed collections",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Indexed_collections#iterating_over_arrays",
              "note": "the section \"Iterating over arrays\"; its `forEach` comes in *Functions as values*."
            }
          ]
        }
      ],
      "_source": "lessons/walking-arrays.md",
      "practice": {
        "id": "walking-arrays",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "What each iteration holds",
            "prompt": "Type the value each one evaluates to, or press the answer. Write text with its quotes, and an array with its square brackets.\n",
            "items": [
              {
                "id": "p1",
                "code": "let total = 0;\nfor (const n of [4, 1, 6]) {\n  total += n * 2;\n}\ntotal\n",
                "accept": [
                  "22"
                ],
                "miss": "**`n` holds each element in turn: 4, then 1, then 6.** Each iteration adds twice the element: 8, then 2, then 12.\n",
                "why": "`22`.\n"
              },
              {
                "id": "p2",
                "question": "A function adds up the array `prices` with `for (const price of prices)`. Which line goes in the loop body?",
                "choices": [
                  "total += price;",
                  "total += prices[price];",
                  "total += prices[i];"
                ],
                "accept": [
                  "total += price;"
                ],
                "miss": "**If you chose `prices[price]` — `price` is already the element.** `prices[price]` uses it as an index, and reads some other element, or `undefined`. **If you chose `prices[i]` — a `for…of` loop has no `i`**, and the call stops with `ReferenceError: i is not defined`.\n",
                "why": "`total += price;`.\n"
              },
              {
                "id": "p3",
                "code": "const xs = [5, 10];\nlet total = 0;\nfor (let i = 0; i <= xs.length; i++) {\n  total += xs[i];\n}\ntotal\n",
                "accept": [
                  "NaN"
                ],
                "miss": "**`<=` lets `i` reach 2, the length.** `xs` has indexes 0 and 1, so on the last iteration `xs[2]` is `undefined`, and `15 + undefined` is `NaN`.\n",
                "why": "`NaN`. With `<`, the loop stops at 2, and `total` is 15.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const xs = [3, 8, 2, 9];\nlet big = 0;\nfor (const x of xs) {\n  if (x > 5) {\n    big++;\n  }\n}\nbig\n",
                "accept": [
                  "2"
                ],
                "miss": "**The count grows only when the `if` is `true`:** for 8 and 9.\n",
                "why": "`2`.\n"
              },
              {
                "id": "p5",
                "code": "const xs = [5, 6, 7, 8];\nconst out = [];\nfor (const x of xs) {\n  if (x % 2 === 1) {\n    out.push(x * 10);\n  }\n}\nout\n",
                "accept": [
                  "[50, 70]",
                  "[50,70]",
                  "[ 50, 70 ]"
                ],
                "miss": "**`x` holds each element, not its index.** Only the odd elements pass the `if`, 5 and 7, and each pushes ten times itself. If you answered `[10, 30]`, you used the odd indexes, 1 and 3.\n",
                "why": "`[50, 70]`.\n"
              },
              {
                "id": "p6",
                "code": "const names = [\"Ada\", \"Lin\"];\nlet out = \"\";\nfor (const name of names) {\n  out += names[name];\n}\nout\n",
                "accept": [
                  "\"undefinedundefined\"",
                  "undefinedundefined",
                  "'undefinedundefined'"
                ],
                "miss": "**`names[name]` uses a name as an index:** `names[\"Ada\"]`, then `names[\"Lin\"]`. An array has no element at those, so each is `undefined`, and joining `undefined` onto a string adds the word.\n",
                "why": "`\"undefinedundefined\"`. The line should be `out += name;`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered as if the loop variable held an index",
                "say": "What does the name after `const` in a `for…of` header hold: a position, or the element at that position?\n"
              },
              {
                "when": "you expected 15 for the loop with <=",
                "say": "Write out the values `i` takes. What is the last one, and is there an element at that index?\n"
              },
              {
                "when": "you chose prices[price]",
                "say": "If `prices` is `[8, 3]`, what does `price` hold on the first iteration? Is there an element at `prices[8]`?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Star ratings, run",
            "prompt": "Predict each line this program prints, then run it and compare.\n",
            "filename": "ratings.js",
            "code": "const ratings = [5, 3, 4, 5, 2];\n\nlet fives = 0;\nfor (const r of ratings) {\n  if (r === 5) {\n    fives++;\n  }\n}\nconsole.log(fives);\n\nconst low = [];\nfor (const r of ratings) {\n  if (r < 4) {\n    low.push(r);\n  }\n}\nconsole.log(low);\n\nlet firstLow = -1;\nfor (let i = 0; i < ratings.length; i++) {\n  if (ratings[i] < 4) {\n    firstLow = i;\n    break;\n  }\n}\nconsole.log(firstLow);\n\nlet lowest = 0;\nfor (const r of ratings) {\n  if (r < lowest) {\n    lowest = r;\n  }\n}\nconsole.log(lowest);\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print? Type the number.",
                "accept": [
                  "2"
                ],
                "why": "`2`. Two of the ratings are 5.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print? Type the array.",
                "accept": [
                  "[3, 2]",
                  "[3,2]",
                  "[ 3, 2 ]"
                ],
                "miss": "**Only the ratings below 4 are pushed**, 3 and 2, in the order they came.\n",
                "why": "`[3, 2]`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print? Type the number.",
                "accept": [
                  "1"
                ],
                "miss": "**`firstLow` is set to `i`, the position, not the rating.** The first rating below 4 is the `3`, at index 1, and `break` stops the loop there.\n",
                "why": "`1`: the index of the rating 3.\n"
              },
              {
                "id": "r4",
                "question": "What does the last line print? Type the number.",
                "accept": [
                  "0"
                ],
                "miss": "**No rating is below 0**, so `lowest` never changes from its start.\n",
                "why": "`0`, which is not one of the ratings. A lowest has to start at a real element, `ratings[0]`, and then it ends as 2.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected the third line to print 3, the rating",
                "say": "What is stored in `firstLow`: `ratings[i]` or `i`?\n"
              },
              {
                "when": "you expected the last line to print 2",
                "say": "What does `lowest` start as? Is any rating less than that?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "One iteration at a time",
            "prompt": "Work each one through iteration by iteration, writing down what each variable holds after each iteration, and type the value of the last line.\n",
            "items": [
              {
                "id": "t1",
                "code": "const xs = [6, 2, 9, 4, 11];\nlet top = xs[0];\nlet changes = 0;\nfor (const x of xs) {\n  if (x > top) {\n    top = x;\n    changes++;\n  }\n}\nchanges\n",
                "accept": [
                  "2"
                ],
                "miss": "**`top` starts as 6.** It changes only when an element beats it: 9 beats 6, and 11 beats 9. 2 and 4 do not.\n",
                "why": "`2`. `top` ends as 11.\n"
              },
              {
                "id": "t2",
                "code": "const xs = [10, 7, 7, 12];\nconst out = [];\nfor (let i = 1; i < xs.length; i++) {\n  out.push(xs[i] - xs[i - 1]);\n}\nout\n",
                "accept": [
                  "[-3, 0, 5]",
                  "[-3,0,5]",
                  "[ -3, 0, 5 ]"
                ],
                "miss": "**Each iteration takes an element minus the one before it.** `i` is 1, 2 and 3: `7 - 10`, `7 - 7`, `12 - 7`.\n",
                "why": "`[-3, 0, 5]`: one fewer than the elements, because the first has no element before it.\n"
              },
              {
                "id": "t3",
                "code": "const words = [\"ox\", \"bee\", \"cat\", \"ant\"];\nlet found = null;\nfor (const w of words) {\n  if (w.length === 3) {\n    found = w;\n  }\n}\nfound\n",
                "accept": [
                  "\"ant\"",
                  "ant",
                  "'ant'"
                ],
                "miss": "**There is no `break`, so the loop goes on after the first match.** `found` is set to `\"bee\"`, then `\"cat\"`, then `\"ant\"`.\n",
                "why": "`\"ant\"`, the last match. A search for the first needs `break`, or a `return` inside the loop.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "const xs = [2, 3, 4];\nlet total = 1;\nfor (const x of xs) {\n  total = total * x;\n}\ntotal\n",
                "accept": [
                  "24"
                ],
                "miss": "**A product starts at 1**, because multiplying by 1 changes nothing. `total` is 2, then 6, then 24.\n",
                "why": "`24`. Started at 0, it would stay 0.\n"
              },
              {
                "id": "t5",
                "code": "const xs = [\"a\", \"b\", \"c\", \"d\", \"e\"];\nconst out = [];\nfor (let i = 0; i < xs.length; i += 2) {\n  out.push(xs[i]);\n}\nout.length\n",
                "accept": [
                  "3"
                ],
                "miss": "**`i` moves by 2:** 0, 2 and 4. At 6 the condition is `false`.\n",
                "why": "`3`. `out` is `[\"a\", \"c\", \"e\"]`.\n"
              }
            ],
            "hints": [
              {
                "when": "your answer to the first one was 3 or 5",
                "say": "What does `top` hold before the loop? On which iterations is `x` greater than what `top` holds at that moment?\n"
              },
              {
                "when": "you answered \"bee\" for the third one",
                "say": "Does anything stop the loop when `found` is set? Write `found` after each iteration.\n"
              },
              {
                "when": "your array for the second one has four numbers",
                "say": "Where does `i` start? How many iterations does the loop run for an array of length 4?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Keep the even numbers",
            "prompt": "`evens` should return a new array of the even numbers in `numbers`, in order: `evens([3, 8, 5, 2])` is `[8, 2]`. Build the lines that go between its braces, in order. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "  const kept = [];",
              "  for (const n of numbers) {",
              "    if (n % 2 === 0) {",
              "      kept.push(n);",
              "    }",
              "  }",
              "  return kept;"
            ],
            "distractors": [
              {
                "line": "    if (numbers[n] % 2 === 0) {",
                "why": "`n` is already the element. `numbers[n]` uses it as an index: for `[3, 8, 5, 2]` the first iteration reads `numbers[3]`, which is 2, and keeps the 3.\n"
              },
              {
                "line": "      return kept.push(n);",
                "why": "`push` returns the new length, and `return` ends the function at the first even number, so `evens([3, 8, 5, 2])` would return `1`.\n"
              }
            ],
            "why": "Start the result as an empty array, before the loop. Take each number in turn; when it is even, push it onto `kept`. Send `kept` out after the loop.\n",
            "hints": [
              {
                "when": "you are unsure which if line is right",
                "say": "Under `for…of`, `n` is the element itself. Does it need to be looked up in `numbers` again?\n"
              },
              {
                "when": "you are unsure where the return goes",
                "say": "When is the new array finished: after one even number, or after every number has been seen?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Where, not what",
            "prompt": "`firstOver` returns the first amount in `amounts` that is over `limit`, or `null` when none is. Change it so it returns the index of that amount instead, or `-1` when no amount is over the limit: `firstOver([20, 150, 90, 300], 100)` should be `1`. Write your code between the braces, and keep `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: why did the loop have to change, and why is the not-found value now `-1`?\n",
            "exportName": "firstOver",
            "filename": "first-over.js",
            "starter": "function firstOver(amounts, limit) {\n  // TODO: put your code here\n  for (const amount of amounts) {\n    if (amount > limit) {\n      return amount;\n    }\n  }\n  return null;\n}\n",
            "tests": [
              {
                "label": "firstOver([20, 150, 90, 300], 100) → 1",
                "body": "return subject([20, 150, 90, 300], 100) === 1;"
              },
              {
                "label": "firstOver([500], 100) → 0",
                "body": "return subject([500], 100) === 0;"
              },
              {
                "label": "firstOver([100, 101], 100) → 1",
                "body": "return subject([100, 101], 100) === 1;"
              },
              {
                "label": "firstOver([20, 30], 100) → -1",
                "body": "return subject([20, 30], 100) === -1;"
              },
              {
                "label": "firstOver([], 100) → -1",
                "body": "return subject([], 100) === -1;"
              }
            ],
            "hints": [
              {
                "when": "firstOver([20, 150, 90, 300], 100) gives 150",
                "say": "That is the amount, not where it is. Which loop gives each iteration the position, and what do you return from it?\n"
              },
              {
                "when": "firstOver([20, 150, 90, 300], 100) gives 2",
                "say": "You counted the amounts from 1. Indexes start at 0: what is the index of the second amount?\n"
              },
              {
                "when": "firstOver([20, 150, 90, 300], 100) gives -1",
                "say": "Your loop returned `-1` on the first amount that was not over the limit. When do you know that no amount is over it: inside the loop, or after it?\n"
              },
              {
                "when": "firstOver([20, 30], 100) gives null",
                "say": "The loop is right; the line after it still returns the old not-found value.\n"
              },
              {
                "when": "firstOver([500], 100) gives -1",
                "say": "The only amount is at index 0. Does your loop look at index 0, or does it start later?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "The readings actually taken",
            "prompt": "A thermometer logs one reading an hour. `null` means the sensor was offline that hour; `0` is a real reading. `taken` takes the log and returns a new array of the real readings, in order: `taken([3, null, 0, -2, null])` is `[3, 0, -2]`. Leave the log itself as it was. Write your code between the braces, and put `return` in front of the answer — *Writing a function's answer* shows how. Then, in one sentence: why would `if (reading)` be wrong here?\n",
            "exportName": "taken",
            "filename": "taken.js",
            "starter": "function taken(log) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "taken([3, null, 0, -2, null]) → [3, 0, -2]",
                "body": "const r = subject([3, null, 0, -2, null]); return Array.isArray(r) && r.length === 3 && r[0] === 3 && r[1] === 0 && r[2] === -2;"
              },
              {
                "label": "taken([0]) → [0]",
                "body": "const r = subject([0]); return Array.isArray(r) && r.length === 1 && r[0] === 0;"
              },
              {
                "label": "taken([null, null]) → []",
                "body": "const r = subject([null, null]); return Array.isArray(r) && r.length === 0;"
              },
              {
                "label": "taken([]) → []",
                "body": "const r = subject([]); return Array.isArray(r) && r.length === 0;"
              },
              {
                "label": "the log is left as it was",
                "body": "const log = [5, null, 6]; subject(log); return log.length === 3 && log[0] === 5 && log[1] === null && log[2] === 6;"
              }
            ],
            "hints": [
              {
                "when": "taken([0]) gives []",
                "say": "`if (reading)` asks whether the reading is truthy, and `0` is on the falsy list from *Truthiness*. Which one value means \"offline\"? Test for that value and nothing else.\n"
              },
              {
                "when": "your function returns a number",
                "say": "`push` returns the new length, not the array. Push on one line, and return the array on its own line, after the loop.\n"
              },
              {
                "when": "the result has only the first reading",
                "say": "Is your `return` inside the loop's braces? It ends the function on the first iteration. Where does it go?\n"
              },
              {
                "when": "the result ends with undefined",
                "say": "Your loop ran one iteration past the last index, where `log[i]` is `undefined`, and `undefined !== null` is `true`. Which comparison stops `i` before the length? Or does each iteration need `i` at all?\n"
              },
              {
                "when": "the log is changed",
                "say": "The task asks for a new array. Start an empty one before the loop, and push the real readings onto it; the loop only reads `log`.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a word count",
            "prompt": "A code assistant wrote `countLonger`, which should return how many words in `words` have more than `n` letters. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "count-longer.js",
            "code": "// How many words have more than n letters.\nfunction countLonger(words, n) {\n  let count = 0;\n  for (let i = 0; i <= words.length; i++) {\n    if (words[i].length > n) {\n      count++;\n    }\n  }\n  return count;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `countLonger([\"hi\", \"there\"], 3)`. What happens?",
                "choices": [
                  "It returns 1",
                  "It stops with a TypeError",
                  "It returns 2"
                ],
                "accept": [
                  "It stops with a TypeError"
                ],
                "miss": "**On the last iteration `i` is 2, the length.** `words[2]` is past the end, so it is `undefined`, and `undefined.length` stops the call, as in *When there is nothing there*.\n",
                "why": "It stops with `TypeError: Cannot read properties of undefined (reading 'length')`.\n"
              },
              {
                "id": "c2",
                "question": "Which line causes it? Type the line number.",
                "accept": [
                  "4",
                  "line 4"
                ],
                "miss": "**Line 5 is where it stops, but line 4 lets `i` get there.** `<=` runs one iteration past the last index.\n",
                "why": "Line 4. With `<`, the loop stops at the length.\n"
              },
              {
                "id": "c3",
                "question": "Each iteration uses `i` only to reach `words[i]`. Which header fits the task better?",
                "choices": [
                  "for (const word of words) {",
                  "for (let i = 1; i <= words.length; i++) {",
                  "for (let i = 0; i < words.length - 1; i++) {"
                ],
                "accept": [
                  "for (const word of words) {"
                ],
                "miss": "**Starting at 1 skips the first word and still reads past the end.** **Stopping before `words.length - 1` skips the last word.** Each iteration needs a word and not its position.\n",
                "why": "`for (const word of words) {`, with `if (word.length > n) {` in the body. With no index, there is no end to get wrong.\n"
              }
            ],
            "exportName": "countLonger",
            "starter": "// How many words have more than n letters.\nfunction countLonger(words, n) {\n  let count = 0;\n  for (let i = 0; i <= words.length; i++) {\n    if (words[i].length > n) {\n      count++;\n    }\n  }\n  return count;\n}\n",
            "tests": [
              {
                "label": "countLonger([\"hi\", \"there\"], 3) → 1",
                "body": "return subject(['hi', 'there'], 3) === 1;"
              },
              {
                "label": "countLonger([\"ccc\", \"a\", \"bb\"], 1) → 2",
                "body": "return subject(['ccc', 'a', 'bb'], 1) === 2;"
              },
              {
                "label": "countLonger([\"same\"], 4) → 0",
                "body": "return subject(['same'], 4) === 0;"
              },
              {
                "label": "countLonger([], 5) → 0",
                "body": "return subject([], 5) === 0;"
              }
            ],
            "hints": [
              {
                "when": "every test stops with a TypeError",
                "say": "Which value of `i` does the loop reach last? Is there a word at that index?\n"
              },
              {
                "when": "countLonger([\"same\"], 4) gives 1",
                "say": "\"More than 4 letters\" leaves out a word of exactly 4. Which comparison operator leaves the 4 out?\n"
              },
              {
                "when": "countLonger([\"ccc\", \"a\", \"bb\"], 1) gives 1",
                "say": "One word was missed. Does your loop start at the first word and stop after the last?\n"
              }
            ]
          }
        ]
      }
    },
    "mutate-traverse": {
      "id": "mutate-traverse",
      "title": "Mutating while traversing",
      "kind": "read",
      "outcome": 3,
      "shape": "conceptual",
      "why": "`flow.mutation-during-traversal` (concepts.md): predict what happens when a collection is changed while a loop walks it, and say why. D-069 placed it after *Adding and removing elements* (`splice`, `push`) and *Walking an array* (indexed `for`, `for…of`, building a new array with `push`), and this lesson uses nothing else. Conceptual, problem first (01-explanations rule 8): what is learned is a model, where the elements go when one is removed and what the loop's index does meanwhile, not a procedure. Rule 8 asks for explain first on highly interacting material, and an index and shifting elements come close; problem first still fits because only two parts interact, each already taught, the hook asks for a prediction that the trace then explains, and the reversal evidence is about learners with no foothold (rule 8's scope; D-061 item 1's reason for Control flow and before). It is the only problem-first lesson in Arrays; the shape choice is pending Nick's review. The hook's `miss` notes carry the anticipated wrong answers. The four fixes, walking backwards, building a new array, walking a copy and saving the length, are worked on the hook's own loops. The contrast with Python and Java (D-010) shows that a language can check for this and that JavaScript does not; Python's lists skip as JavaScript's arrays do. `filter` is named, not shown (D-066 item 2). Strict mode is assumed (D-073); nothing here behaves differently without it. Derived from those rules, pending Nick's review.",
      "concepts": [
        "flow.mutation-during-traversal"
      ],
      "lede": "*Walking an array* read an array, or built a new one. This lesson changes the array while the loop is still walking it, and shows what the loop does then, and why.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Adding and removing elements · Finding, slicing and joining · Walking an array"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**In place** — changing the array itself, rather than building a new one. **Copy** — a new array holding the same elements as another; changing one leaves the other as it was.",
          "entries": [
            "**In place** — changing the array itself, rather than building a new one.",
            "**Copy** — a new array holding the same elements as another; changing one leaves the other as it was."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four loops, each changing the array it walks. Type the value the last line\nevaluates to, writing an array with its square brackets, or press the answer.\nGuess if you have an idea; press **I don't know** if you have none."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const scores = [72, 40, 88, 35];\nfor (let i = 0; i < scores.length; i++) {\n  if (scores[i] < 50) {\n    scores.splice(i, 1);\n  }\n}\nscores\n",
                    "accept": [
                      "[72, 88]",
                      "[72,88]",
                      "[ 72, 88 ]"
                    ],
                    "miss": "**This one leaves `[72, 88]`.** Whether every element was read is the next question.\n",
                    "why": "`[72, 88]`. It looks correct, and for this array it is. The next one is the same loop on the same scores in a different order.\n"
                  },
                  {
                    "code": "const scores = [72, 40, 35, 88];\nfor (let i = 0; i < scores.length; i++) {\n  if (scores[i] < 50) {\n    scores.splice(i, 1);\n  }\n}\nscores\n",
                    "accept": [
                      "[72, 35, 88]",
                      "[72,35,88]",
                      "[ 72, 35, 88 ]"
                    ],
                    "miss": "**If you answered `[72, 88]`: that is what the loop was written to do,** and the loop above did it. Here the same loop leaves one score below 50 behind, with no error.\n",
                    "why": "`[72, 35, 88]`. The `35` is never read. The first section below shows where it went.\n"
                  },
                  {
                    "code": "const tasks = [\"wash\", \"dry\"];\nlet count = 0;\nfor (const task of tasks) {\n  count++;\n  if (task === \"wash\") {\n    tasks.push(\"fold\");\n  }\n}\ncount\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**If you answered `2` because the array had two elements when the loop began:** `for…of` does not count the elements once at the start. It checks the array as it is now, on every iteration, and by the third check `\"fold\"` is there.\n",
                    "why": "`3`. The loop reached the element it added.\n"
                  },
                  {
                    "code": "const songs = [\"Rain\", \"Tide\"];\nfor (const song of songs) {\n  songs.push(song);\n}\n// does this loop end?\n",
                    "choices": [
                      "Yes, with songs as [\"Rain\", \"Tide\", \"Rain\", \"Tide\"]",
                      "No, it never ends",
                      "It stops with an error straight away"
                    ],
                    "accept": [
                      "No, it never ends"
                    ],
                    "miss": "**If you expected the array to double:** that is what the code was meant to do. But every element it adds is one more element for the loop to reach, and reaching it adds another.\n",
                    "why": "It never ends. It runs until it is stopped, or until the program runs out of memory.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can predict what a loop does when it changes the array it is walking, and say why",
                "I can say why removing while walking forward skips an element, and avoid it by walking backwards or building a new array",
                "I can say why adding while walking makes a loop run longer or forever, and avoid it by walking a copy or saving the length"
              ]
            }
          ]
        },
        {
          "h": "Removing shifts what comes after"
        },
        {
          "p": "Here is the second opening loop again. It should remove every score below 50:"
        },
        {
          "code": "const scores = [72, 40, 35, 88];\nfor (let i = 0; i < scores.length; i++) {\n  if (scores[i] < 50) {\n    scores.splice(i, 1);\n  }\n}\nconsole.log(scores);   // [72, 35, 88]",
          "lang": "js",
          "file": "remove-low.js"
        },
        {
          "p": "Trace it, one row per check of `i < scores.length`, written with that check's\nvalues; Element is `scores[i]`:"
        },
        {
          "table": {
            "head": [
              "Condition",
              "Element",
              "`scores` after"
            ],
            "rows": [
              [
                "`0 < 4`",
                "`72`",
                "`[72, 40, 35, 88]`"
              ],
              [
                "`1 < 4`",
                "`40`",
                "`[72, 35, 88]`"
              ],
              [
                "`2 < 3`",
                "`88`",
                "`[72, 35, 88]`"
              ],
              [
                "`3 < 3`",
                "",
                "`[72, 35, 88]`"
              ]
            ]
          }
        },
        {
          "p": "The length drops from 4 to 3 when `40` is removed, and at the fourth check\n`3 < 3` is `false`, so the loop ends after three iterations."
        },
        {
          "p": "At `i` 1, `40` is below 50, and `splice(1, 1)` removes it. Before that\n`splice`, `35` is at index 2:"
        },
        {
          "indexes": {
            "variable": "scores",
            "items": [
              "72",
              "40",
              "35",
              "88"
            ],
            "kind": "element"
          }
        },
        {
          "p": "After it, every element after the removed one has moved down one index, so\n`35` is at index 1:"
        },
        {
          "indexes": {
            "variable": "scores",
            "items": [
              "72",
              "35",
              "88"
            ],
            "kind": "element"
          }
        },
        {
          "p": "Then the update, `i++`, makes `i` 2, and `scores[2]` is now `88`. `35` moved\ninto index 1 after the loop had read index 1, so the loop never reads `35`."
        },
        {
          "p": "Two moves work against each other: removing moves the next element down one index,\nand the update moves `i` up one. **The element just after each removed one is\nskipped.** Nothing reports an error, because every line did exactly what it\nsays."
        },
        {
          "p": "The first opening loop, on `[72, 40, 88, 35]`, skipped an element too. When\n`40` was removed, `88` moved to index 1, and the loop went on to index 2,\nwhich by then held `35`. `88` was never read, and that cost nothing, because\n`88` is not below 50. The skip only shows when the element after a removed\none should also be removed. That is why a loop like this can pass a test and\nstill be wrong."
        },
        {
          "p": "A `for…of` loop skips in the same way. It keeps a position too, out of sight,\nand moves it on after every iteration, so an element that moves down into a\nposition the loop has passed is never reached."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const scores = [10, 20, 30];\nfor (let i = 0; i < scores.length; i++) {\n  if (scores[i] < 50) {\n    scores.splice(i, 1);\n  }\n}\nscores\n",
                "accept": [
                  "[20]",
                  "[ 20 ]"
                ],
                "miss": "**Removing `10` moves `20` down to index 0**, and the loop goes on to index 1, which by then holds `30`. `20` is never read.\n",
                "why": "`[20]`, though every score is below 50.\n"
              },
              {
                "question": "The same loop removes every score below 50. For which array does it leave a score below 50 behind?",
                "choices": [
                  "[40, 35, 90]",
                  "[40, 90, 35]",
                  "[90, 40, 80]"
                ],
                "accept": [
                  "[40, 35, 90]"
                ],
                "miss": "**Look for two scores below 50 next to each other.** In `[40, 35, 90]`, removing `40` moves `35` to index 0, and the loop has moved on to index 1.\n",
                "why": "`[40, 35, 90]`: the loop leaves `[35, 90]`.\n"
              }
            ]
          }
        },
        {
          "h": "Walking backwards"
        },
        {
          "p": "The skip comes from elements moving into positions the loop has already\npassed. `splice` only moves the elements *after* the index it removes at.\nWalk the array from the last index down to 0, and those are the elements the\nloop has already read. The ones still to read, at lower indexes, do not move."
        },
        {
          "p": "*The for loop* walked a string backwards the same way, from the last index,\n`length - 1`, down to 0:"
        },
        {
          "code": "const scores = [72, 40, 35, 88];\nfor (let i = scores.length - 1; i >= 0; i--) {\n  if (scores[i] < 50) {\n    scores.splice(i, 1);\n  }\n}\nconsole.log(scores);   // [72, 88]",
          "lang": "js",
          "file": "remove-low.js"
        },
        {
          "table": {
            "head": [
              "Condition",
              "Element",
              "`scores` after"
            ],
            "rows": [
              [
                "`3 >= 0`",
                "`88`",
                "`[72, 40, 35, 88]`"
              ],
              [
                "`2 >= 0`",
                "`35`",
                "`[72, 40, 88]`"
              ],
              [
                "`1 >= 0`",
                "`40`",
                "`[72, 88]`"
              ],
              [
                "`0 >= 0`",
                "`72`",
                "`[72, 88]`"
              ],
              [
                "`-1 >= 0`",
                "",
                "`[72, 88]`"
              ]
            ]
          }
        },
        {
          "p": "`-1 >= 0` is `false`, and the loop ends after four iterations, one for each\nelement. When `35` is removed at index 2, `88` moves down from index 3 to\nindex 2, and the loop has already read it. The update makes `i` 1, and\n`scores[1]` is still `40`, as it was before the removal. Every element is read\nonce."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const scores = [10, 20, 30];\nfor (let i = scores.length - 1; i >= 0; i--) {\n  if (scores[i] < 50) {\n    scores.splice(i, 1);\n  }\n}\nscores\n",
                "accept": [
                  "[]",
                  "[ ]"
                ],
                "miss": "**Walking backwards, each removal moves only elements the loop has already read**, so `30`, then `20`, then `10` are each read and removed.\n",
                "why": "`[]`.\n"
              },
              {
                "question": "Which header walks `scores` from its last element down to its first?",
                "choices": [
                  "for (let i = scores.length - 1; i >= 0; i--)",
                  "for (let i = scores.length; i >= 0; i--)",
                  "for (let i = scores.length - 1; i > 0; i--)"
                ],
                "accept": [
                  "for (let i = scores.length - 1; i >= 0; i--)"
                ],
                "miss": "**If you chose the one that starts at `scores.length` — that index is one past the end.** **If you chose `i > 0` — that stops before index 0**, so the first element is never read.\n",
                "why": "`for (let i = scores.length - 1; i >= 0; i--)`: from the last index, down to and including 0.\n"
              }
            ]
          }
        },
        {
          "h": "Or build a new array"
        },
        {
          "p": "*Walking an array* kept only some of an array's elements, *filtering* them,\nby building a new array with `push`. That avoids the problem altogether,\nbecause the loop walks one array and changes another:"
        },
        {
          "code": "const scores = [72, 40, 35, 88];\nconst passing = [];\nfor (const score of scores) {\n  if (score >= 50) {\n    passing.push(score);\n  }\n}\nconsole.log(passing);   // [72, 88]\nconsole.log(scores);    // [72, 40, 35, 88]",
          "lang": "js",
          "file": "passing.js"
        },
        {
          "p": "Nothing moves while the loop walks `scores`, so nothing is skipped. The\ncondition is turned around: the loop *removed* scores below 50, and this one\n*keeps* scores of 50 and over."
        },
        {
          "p": "Which to choose depends on what the program needs afterwards. Building a new\narray leaves the original as it was, which is what you want when something\nstill needs the original. Changing the array in place, walking backwards, is\nwhat you want when the job is to change the array you were given. *Copying\nversus aliasing*, in the Values and references module, shows why that\ndifference matters when two parts of a program hold the same array."
        },
        {
          "p": "The `filter` method builds the new array for you; it takes a function, so\n*Functions as values* teaches it."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "After the `passing` loop runs, what is `scores.length`? Type the number.",
                "accept": [
                  "4",
                  "four"
                ],
                "miss": "**The loop only reads `scores`.** It pushes onto `passing`, a different array.\n",
                "why": "`4`. `scores` is unchanged.\n"
              }
            ]
          }
        },
        {
          "h": "Adding moves the end"
        },
        {
          "p": "The third opening loop added an element while walking:"
        },
        {
          "code": "const tasks = [\"wash\", \"dry\"];\nfor (const task of tasks) {\n  console.log(task);\n  if (task === \"wash\") {\n    tasks.push(\"fold\");\n  }\n}\n// wash\n// dry\n// fold",
          "lang": "js",
          "file": "tasks.js"
        },
        {
          "p": "A `for…of` loop does not count the elements when it starts. Before each\niteration it checks whether its position is still inside the array, as the\narray is now. `\"fold\"` was pushed during the first iteration, so the array\nhad three elements by the time the loop looked for a third. An indexed `for`\nloop with `i < tasks.length` does the same: it reads `tasks.length` again at\nevery check. The loop ends once an iteration adds nothing."
        },
        {
          "p": "When every iteration adds an element, the loop never ends. This was the\nfourth opening loop:"
        },
        {
          "code": "const songs = [\"Rain\", \"Tide\"];\nfor (const song of songs) {\n  songs.push(song);\n}",
          "lang": "js",
          "file": "double.js"
        },
        {
          "p": "It was meant to repeat the list once, giving\n`[\"Rain\", \"Tide\", \"Rain\", \"Tide\"]`. But each iteration adds one element, so\nthe end moves on one each time the loop does, and the loop never catches up.\nDo not run this one: it runs until it is stopped, or until the program runs\nout of memory."
        },
        {
          "p": "**Walk a copy.** *Finding, slicing and joining* showed that `slice` with no\narguments copies the whole array, and leaves the array as it was. Here the\nloop walks the copy, `songs.slice()`, which never changes, and `push` adds to\nthe original:"
        },
        {
          "code": "const songs = [\"Rain\", \"Tide\"];\nfor (const song of songs.slice()) {\n  songs.push(song);\n}\nconsole.log(songs);   // [\"Rain\", \"Tide\", \"Rain\", \"Tide\"]",
          "lang": "js",
          "file": "double.js"
        },
        {
          "p": "The copy has two elements, so the loop runs twice."
        },
        {
          "p": "**Save the length.** An indexed loop can stop after the elements that were\nthere at the start by saving the length before it begins:"
        },
        {
          "code": "const songs = [\"Rain\", \"Tide\"];\nconst n = songs.length;\nfor (let i = 0; i < n; i++) {\n  songs.push(songs[i]);\n}\nconsole.log(songs);   // [\"Rain\", \"Tide\", \"Rain\", \"Tide\"]",
          "lang": "js",
          "file": "double.js"
        },
        {
          "p": "`n` stays 2 while `songs` grows, so this loop runs twice too."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const ids = [1, 2];\nfor (const id of ids.slice()) {\n  ids.push(id + 10);\n}\nids\n",
                "accept": [
                  "[1, 2, 11, 12]",
                  "[1,2,11,12]",
                  "[ 1, 2, 11, 12 ]"
                ],
                "miss": "**The loop walks the copy, `[1, 2]`**, so it runs twice, and each iteration pushes onto `ids`.\n",
                "why": "`[1, 2, 11, 12]`.\n"
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **does the language notice that a loop's collection changed?**"
            },
            {
              "p": "JavaScript never checks. A Python list behaves just as a JavaScript array\ndoes: removing in a `for` loop skips the next element, silently. Python does\ncheck two other kinds of collection, dictionaries (values looked up by a key)\nand sets (values with no order and no repeats): a loop over one that grows\nor shrinks stops with a `RuntimeError`."
            },
            {
              "p": "In Java, an `ArrayList` checks in a for-each loop, Java's counterpart of\n`for…of`, and stops it with a `ConcurrentModificationException`. Java's\ndocumentation calls that check \"best-effort\", meant for finding bugs and not\nfor a program to rely on. The check is in the for-each loop, not in an\nindexed one: an indexed loop that removes with `remove(i)` skips just as\nJavaScript's does."
            },
            {
              "p": "Whether a language checks is a design choice, and the four fixes work either\nway."
            }
          ]
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Two loops like the opening ones. Answer from what you now know about where\nthe elements go."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const temps = [30, 5, 3, 22];\nfor (let i = 0; i < temps.length; i++) {\n  if (temps[i] < 10) {\n    temps.splice(i, 1);\n  }\n}\ntemps\n",
                    "accept": [
                      "[30, 3, 22]",
                      "[30,3,22]",
                      "[ 30, 3, 22 ]"
                    ],
                    "miss": "**Removing `5` at index 1 moves `3` down to index 1**, and the update moves `i` to 2.\n",
                    "why": "`[30, 3, 22]`.\n"
                  },
                  {
                    "question": "Why is `3` left in `temps`?",
                    "choices": [
                      "After 5 was removed, 3 moved to an index the loop had already passed",
                      "splice removes only one element per loop",
                      "The loop stops one index early"
                    ],
                    "accept": [
                      "After 5 was removed, 3 moved to an index the loop had already passed"
                    ],
                    "miss": "**`splice(i, 1)` removes one element each time it is called**, and the loop does reach the last index. What moves is `3`: from index 2 to index 1, after the loop had read index 1.\n",
                    "why": "`3` moved down into index 1, and the loop went on to index 2.\n"
                  },
                  {
                    "code": "const queue = [\"a\", \"b\"];\nlet runs = 0;\nfor (let i = 0; i < queue.length; i++) {\n  runs++;\n  if (queue[i] === \"a\") {\n    queue.push(\"c\");\n  }\n}\nruns\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**`i < queue.length` reads the length at every check**, and `\"c\"` was pushed during the first iteration.\n",
                    "why": "`3`.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "Two jobs, each on an array the program already has."
            },
            {
              "list": [
                "**Coupons.** `daysLeft` holds how many days each coupon has left, such as `[3, 0, -1, 7]`. Remove every coupon with no days left, 0 or fewer, from `daysLeft`. Other code reads `daysLeft` afterwards and must not see them.",
                "**Guests.** `guests` holds names, and `plusOnes` holds the names of the guests who are bringing someone. For every name in `guests` that is also in `plusOnes`, add `\"guest of \"` and that name to the end of `guests`."
              ]
            },
            {
              "p": "For each, choose how to walk the array: forwards, backwards, building a new\narray, walking a copy, or saving the length. Say what a plain forward loop would do, with an\nexample array that shows it. Then say why the way you chose avoids that.\nThere is more than one good answer; your reasoning is the answer."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Removing while walking forward skips the next element.** Everything after the removed one moves down an index while the loop moves up one, with no error.",
                "**Adding while walking makes the loop longer**, because the loop checks the array's current length; adding on every iteration makes it endless.",
                "**To remove in place, walk backwards or build a new array. To add, walk a copy (`songs.slice()`) or save the length.** JavaScript never warns you."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array.prototype.splice()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/splice",
              "note": "it changes the array in place and moves the elements after the ones it removes."
            },
            {
              "title": "Array.prototype.slice()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/slice",
              "note": "with no arguments, a copy of the whole array."
            },
            {
              "title": "for...of",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/for...of",
              "note": "the loop, over arrays and strings."
            }
          ]
        }
      ],
      "_source": "lessons/mutate-traverse.md",
      "practice": {
        "id": "mutate-traverse",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "What the loop leaves",
            "prompt": "Type the value the last line evaluates to, writing text with its quotes and an array with its square brackets, or press the answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "const words = [\"to\", \"be\", \"or\", \"not\"];\nfor (let i = 0; i < words.length; i++) {\n  if (words[i].length < 3) {\n    words.splice(i, 1);\n  }\n}\nwords\n",
                "accept": [
                  "[\"be\", \"not\"]",
                  "[\"be\",\"not\"]",
                  "['be', 'not']",
                  "[ 'be', 'not' ]",
                  "[ \"be\", \"not\" ]"
                ],
                "miss": "**Removing `\"to\"` moves `\"be\"` down to index 0**, and the loop goes on to index 1, which by then holds `\"or\"`. `\"be\"` is never read.\n",
                "why": "`[\"be\", \"not\"]`. `\"be\"` is shorter than 3 and is still there.\n"
              },
              {
                "id": "p2",
                "code": "const stops = [\"A\", \"B\"];\nlet visits = 0;\nfor (const stop of stops) {\n  visits++;\n  if (stop === \"B\") {\n    stops.push(\"C\");\n  }\n}\nvisits\n",
                "accept": [
                  "3"
                ],
                "miss": "**`for…of` checks the array as it is on every iteration**, and `\"C\"` was pushed before the loop looked for a third element.\n",
                "why": "`3`.\n"
              },
              {
                "id": "p3",
                "question": "A loop removes every mark below 5 from `marks` with `marks.splice(i, 1)`. Which header lets it remove them all, with none skipped?",
                "choices": [
                  "for (let i = marks.length - 1; i >= 0; i--)",
                  "for (let i = 0; i < marks.length; i++)",
                  "Either one"
                ],
                "accept": [
                  "for (let i = marks.length - 1; i >= 0; i--)"
                ],
                "miss": "**Walking forward, each removal moves the next element down into an index the loop has already passed.** Walking backwards, the elements that move are ones the loop has already read.\n",
                "why": "The backward header, from `marks.length - 1` down to 0.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const words = [\"to\", \"be\", \"or\", \"not\"];\nfor (let i = words.length - 1; i >= 0; i--) {\n  if (words[i].length < 3) {\n    words.splice(i, 1);\n  }\n}\nwords\n",
                "accept": [
                  "[\"not\"]",
                  "['not']",
                  "[ \"not\" ]",
                  "[ 'not' ]"
                ],
                "miss": "**Walking backwards, nothing the loop has still to read moves**, so every short word is read and removed.\n",
                "why": "`[\"not\"]`.\n"
              },
              {
                "id": "p5",
                "code": "const sizes = [3, 4];\nfor (const size of sizes) {\n  sizes.push(size * 2);\n}\n// does this loop end?\n",
                "choices": [
                  "Yes, with sizes as [3, 4, 6, 8]",
                  "No, it never ends"
                ],
                "accept": [
                  "No, it never ends"
                ],
                "miss": "**Every iteration adds one element**, so the end moves on one each time the loop does, and the loop never catches up.\n",
                "why": "It never ends.\n"
              },
              {
                "id": "p6",
                "code": "const sizes = [3, 4];\nfor (const size of sizes.slice()) {\n  sizes.push(size * 2);\n}\nsizes\n",
                "accept": [
                  "[3, 4, 6, 8]",
                  "[3,4,6,8]",
                  "[ 3, 4, 6, 8 ]"
                ],
                "miss": "**The loop walks the copy**, which still holds `[3, 4]`, so it runs twice. `push` adds to `sizes`.\n",
                "why": "`[3, 4, 6, 8]`.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected every short word to be gone",
                "say": "Write the array down after the first removal. Which element is now at index 0, and which index does the loop read next?\n"
              },
              {
                "when": "you expected 2 visits",
                "say": "Does `for…of` count the elements once, at the start, or look at the array again before each iteration?\n"
              },
              {
                "when": "you chose either header",
                "say": "After `splice(i, 1)`, which elements move: the ones before `i`, or the ones after it? Which of those has the loop still to read?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Readings over 100, run",
            "prompt": "This loop should remove every reading over 100, and it prints each reading as it reads it. Predict what it prints, then run it and compare.\n",
            "filename": "readings.js",
            "code": "const readings = [90, 120, 130, 80];\nfor (let i = 0; i < readings.length; i++) {\n  console.log(readings[i]);\n  if (readings[i] > 100) {\n    readings.splice(i, 1);\n  }\n}\nconsole.log(readings);\n",
            "items": [
              {
                "id": "r1",
                "question": "How many readings does the loop print, before the last line? Type the number.",
                "accept": [
                  "3",
                  "three"
                ],
                "miss": "**After `120` is removed, the array has three elements**, and the loop has already read two of them.\n",
                "why": "`3`: `90`, `120` and `80`.\n"
              },
              {
                "id": "r2",
                "question": "Which reading is never printed? Type the number.",
                "accept": [
                  "130"
                ],
                "miss": "**Removing `120` at index 1 moves `130` down to index 1**, and the update moves `i` on to 2.\n",
                "why": "`130`.\n"
              },
              {
                "id": "r3",
                "question": "What does the last line print? Type the array.",
                "accept": [
                  "[90, 130, 80]",
                  "[90,130,80]",
                  "[ 90, 130, 80 ]"
                ],
                "miss": "**`130` was never read, so it was never removed.**\n",
                "why": "`[90, 130, 80]`: one reading over 100 is left.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected all four readings to be printed",
                "say": "Write the array down after `120` is removed. What is at index 2 now?\n"
              },
              {
                "when": "you expected the last line to be [90, 80]",
                "say": "Can the loop remove a reading it never read?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "The array after each iteration",
            "prompt": "Work each one through, writing down the array after every iteration, and type the value of the last line.\n",
            "items": [
              {
                "id": "t1",
                "code": "const nums = [2, 4, 5, 6];\nfor (let i = 0; i < nums.length; i++) {\n  if (nums[i] % 2 === 0) {\n    nums.splice(i, 1);\n  }\n}\nnums\n",
                "accept": [
                  "[4, 5]",
                  "[4,5]",
                  "[ 4, 5 ]"
                ],
                "miss": "**At `i` 0, `2` is removed and `4` moves to index 0.** At `i` 1 the loop reads `5`, and at `i` 2 it reads `6` and removes it.\n",
                "why": "`[4, 5]`. `4` is even and was skipped.\n"
              },
              {
                "id": "t2",
                "code": "const nums = [3, 4, 5, 7, 8];\nfor (let i = nums.length - 1; i >= 0; i--) {\n  if (nums[i] % 2 === 1) {\n    nums.splice(i, 1);\n  }\n}\nnums\n",
                "accept": [
                  "[4, 8]",
                  "[4,8]",
                  "[ 4, 8 ]"
                ],
                "miss": "**Walking backwards, every element is read once.** `7`, `5` and `3` have a remainder of 1 when divided by 2, so each is removed.\n",
                "why": "`[4, 8]`.\n"
              },
              {
                "id": "t3",
                "code": "const xs = [4, 6];\nconst n = xs.length;\nfor (let i = 0; i < n; i++) {\n  xs.push(xs[i] * 2);\n}\nxs\n",
                "accept": [
                  "[4, 6, 8, 12]",
                  "[4,6,8,12]",
                  "[ 4, 6, 8, 12 ]"
                ],
                "miss": "**`n` was saved before the loop**, so it stays 2 while `xs` grows, and the loop runs twice.\n",
                "why": "`[4, 6, 8, 12]`.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "const nums = [2, 4, 5];\nconst odd = [];\nfor (const n of nums) {\n  if (n % 2 === 1) {\n    odd.push(n);\n  }\n}\nnums.length\n",
                "accept": [
                  "3",
                  "three"
                ],
                "miss": "**The loop only reads `nums`.** Every `push` goes to `odd`.\n",
                "why": "`3`. `nums` is unchanged, and `odd` is `[5]`.\n"
              },
              {
                "id": "t5",
                "code": "const nums = [2, 4, 5, 6];\nfor (let i = nums.length - 1; i >= 0; i--) {\n  if (nums[i] % 2 === 0) {\n    nums.splice(i, 1);\n  }\n}\nnums\n",
                "accept": [
                  "[5]",
                  "[ 5 ]"
                ],
                "miss": "**Walking backwards, no even number is skipped**: `6`, `4` and `2` are each read and removed.\n",
                "why": "`[5]`. The same removal walking forward left `[4, 5]`.\n"
              }
            ],
            "hints": [
              {
                "when": "your answer to the first one is [5]",
                "say": "Write the array down after `2` is removed. Which element is at index 0 now, and which index does the loop read next?\n"
              },
              {
                "when": "you expected the third one never to end",
                "say": "Where does the loop's condition get its limit: from `xs.length` at every check, or from `n`, saved before the loop?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Remove the short words",
            "prompt": "`removeShort` should remove every word shorter than 3 characters from `words` itself and return `words`: `removeShort([\"an\", \"ox\", \"elk\"])` is `[\"elk\"]`. Build the lines that go between its braces, in order. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "  for (let i = words.length - 1; i >= 0; i--) {",
              "    if (words[i].length < 3) {",
              "      words.splice(i, 1);",
              "    }",
              "  }",
              "  return words;"
            ],
            "distractors": [
              {
                "line": "  for (let i = 0; i < words.length; i++) {",
                "why": "Walking forward, removing `\"an\"` moves `\"ox\"` down to index 0 while the loop moves on to index 1, so `\"ox\"` is skipped and left in.\n"
              },
              {
                "line": "  for (let i = words.length; i >= 0; i--) {",
                "why": "`words[words.length]` is one past the end, `undefined`, and `undefined.length` stops with a TypeError on the first iteration.\n"
              }
            ],
            "why": "The loop starts at the last index, `words.length - 1`, and counts down to 0. `splice` moves only the elements after `i`, which the loop has already read, so no word is skipped. The array itself is returned after the loop.\n",
            "hints": [
              {
                "when": "you are unsure which header to use",
                "say": "After `splice(i, 1)`, which elements move? Which direction leaves the elements still to be read where they were?\n"
              },
              {
                "when": "you are unsure where the loop starts",
                "say": "What is the last index of an array whose length is `words.length`?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Change it in place",
            "prompt": "`removeOver` should remove every price over `limit` from `prices` itself, and return `prices`: after `removeOver([5, 30, 40, 8], 20)`, the array given is `[5, 8]`, and that same array is returned. The starter builds a new array instead, and leaves `prices` as it was. Change it so it removes from `prices`. Write your code between the braces, and keep `return` in front of the answer. Then, in one sentence: why does your loop not skip a price?\n",
            "exportName": "removeOver",
            "filename": "remove-over.js",
            "starter": "function removeOver(prices, limit) {\n  // TODO: put your code here\n  const kept = [];\n  for (const price of prices) {\n    if (price <= limit) {\n      kept.push(price);\n    }\n  }\n  return kept;\n}\n",
            "tests": [
              {
                "label": "removeOver([5, 30, 40, 8], 20) → [5, 8]",
                "body": "const r = subject([5, 30, 40, 8], 20); return Array.isArray(r) && r.length === 2 && r[0] === 5 && r[1] === 8;"
              },
              {
                "label": "it returns the same array it was given, changed",
                "body": "const a = [5, 30, 40, 8]; const r = subject(a, 20); return r === a && a.length === 2 && a[0] === 5 && a[1] === 8;"
              },
              {
                "label": "removeOver([20, 25], 20) → [20]: a price equal to the limit stays",
                "body": "const a = [20, 25]; const r = subject(a, 20); return r === a && a.length === 1 && a[0] === 20;"
              },
              {
                "label": "removeOver([50, 60], 20) → []",
                "body": "const a = [50, 60]; const r = subject(a, 20); return r === a && a.length === 0;"
              },
              {
                "label": "removeOver([1, 2], 20) → [1, 2], unchanged",
                "body": "const a = [1, 2]; const r = subject(a, 20); return r === a && a.length === 2 && a[0] === 1 && a[1] === 2;"
              }
            ],
            "hints": [
              {
                "when": "the right prices come back, but in a different array",
                "say": "The tests check that the array you were given is the one returned. Which method removes an element from `prices` itself?\n"
              },
              {
                "when": "removeOver([5, 30, 40, 8], 20) leaves 40 in",
                "say": "Write the array down after `30` is removed. Where is `40` now, and which index does your loop read next? Which direction avoids that?\n"
              },
              {
                "when": "removeOver([20, 25], 20) removes the 20",
                "say": "The task removes prices *over* the limit. Is 20 over 20?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Add halves",
            "prompt": "`addHalves` takes an array of amounts and, for every amount over 10 in it, adds half of that amount to the end of the same array. It returns the array: `addHalves([40, 6])` is `[40, 6, 20]`. Only the amounts in the array when the function was called count; an added half is not halved again. Write your code between the braces, and put `return` in front of the answer. Then, in one sentence: what would a loop that walks `amounts` itself, adding as it goes, return for `addHalves([40, 6])`, and why?\n",
            "exportName": "addHalves",
            "filename": "add-halves.js",
            "starter": "function addHalves(amounts) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "addHalves([40, 6]) → [40, 6, 20]",
                "body": "const r = subject([40, 6]); return Array.isArray(r) && r.length === 3 && r[0] === 40 && r[1] === 6 && r[2] === 20;"
              },
              {
                "label": "addHalves([24, 30]) → [24, 30, 12, 15]",
                "body": "const r = subject([24, 30]); return Array.isArray(r) && r.length === 4 && r[2] === 12 && r[3] === 15;"
              },
              {
                "label": "it returns the same array it was given",
                "body": "const a = [40, 6]; const r = subject(a); return r === a && a.length === 3;"
              },
              {
                "label": "addHalves([10]) → [10]: 10 is not over 10",
                "body": "const r = subject([10]); return Array.isArray(r) && r.length === 1 && r[0] === 10;"
              },
              {
                "label": "addHalves([]) → []",
                "body": "const r = subject([]); return Array.isArray(r) && r.length === 0;"
              }
            ],
            "hints": [
              {
                "when": "addHalves([40, 6]) gives [40, 6, 20, 10]",
                "say": "Your loop reached the `20` it added, and 20 is over 10. How can the loop walk only the amounts that were there at the start?\n"
              },
              {
                "when": "the halves come back, but not in the array given",
                "say": "The test checks that the array you were given is the one returned. Where should `push` add the halves?\n"
              },
              {
                "when": "addHalves([10]) gives [10, 5]",
                "say": "The task adds a half for amounts *over* 10. Is 10 over 10?\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a cleanup function",
            "prompt": "A code assistant wrote `dropNegatives`, which should remove every negative number from `nums` and return `nums`. It tested it with `dropNegatives([3, -1, 5])`, which returned `[3, 5]`. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "drop-negatives.js",
            "code": "// Remove every negative number from nums, and return nums.\nfunction dropNegatives(nums) {\n  for (let i = 0; i < nums.length; i++) {\n    if (nums[i] < 0) {\n      nums.splice(i, 1);\n    }\n  }\n  return nums;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `dropNegatives([4, -1, -2, 7])`. Type what it returns.",
                "accept": [
                  "[4, -2, 7]",
                  "[4,-2,7]",
                  "[ 4, -2, 7 ]"
                ],
                "miss": "**Removing `-1` at index 1 moves `-2` down to index 1**, and the update moves `i` on to 2, which holds `7`.\n",
                "why": "`[4, -2, 7]`: a negative number is left in.\n"
              },
              {
                "id": "c2",
                "question": "Why did the assistant's test, `[3, -1, 5]`, not catch the bug?",
                "choices": [
                  "No two negative numbers in it are next to each other",
                  "It has only one negative number, and splice works only once",
                  "The loop does not run on short arrays"
                ],
                "accept": [
                  "No two negative numbers in it are next to each other"
                ],
                "miss": "**The loop skipped `5` there too**, after `-1` was removed. `5` is not negative, so skipping it changed nothing. `splice` works every time it is called.\n",
                "why": "The skipped element is the one after each removal, and in `[3, -1, 5]` that is `5`, which should stay anyway.\n"
              },
              {
                "id": "c3",
                "question": "Which line should replace line 3?",
                "choices": [
                  "for (let i = nums.length - 1; i >= 0; i--) {",
                  "for (let i = 0; i <= nums.length; i++) {",
                  "for (let i = 1; i < nums.length; i++) {"
                ],
                "accept": [
                  "for (let i = nums.length - 1; i >= 0; i--) {"
                ],
                "miss": "**`i <= nums.length` reads one more index, past the end, and still skips.** **Starting at 1 skips the first number as well.** Walking backwards moves only numbers the loop has already read.\n",
                "why": "`for (let i = nums.length - 1; i >= 0; i--) {`.\n"
              }
            ],
            "exportName": "dropNegatives",
            "starter": "// Remove every negative number from nums, and return nums.\nfunction dropNegatives(nums) {\n  for (let i = 0; i < nums.length; i++) {\n    if (nums[i] < 0) {\n      nums.splice(i, 1);\n    }\n  }\n  return nums;\n}\n",
            "tests": [
              {
                "label": "dropNegatives([4, -1, -2, 7]) → [4, 7]",
                "body": "const r = subject([4, -1, -2, 7]); return Array.isArray(r) && r.length === 2 && r[0] === 4 && r[1] === 7;"
              },
              {
                "label": "it returns the same array it was given, changed",
                "body": "const a = [4, -1, -2, 7]; const r = subject(a); return r === a && a.length === 2;"
              },
              {
                "label": "dropNegatives([-3, -3]) → []",
                "body": "const r = subject([-3, -3]); return Array.isArray(r) && r.length === 0;"
              },
              {
                "label": "dropNegatives([0, 2]) → [0, 2]: 0 is not negative",
                "body": "const r = subject([0, 2]); return Array.isArray(r) && r.length === 2 && r[0] === 0 && r[1] === 2;"
              },
              {
                "label": "dropNegatives([]) → []",
                "body": "const r = subject([]); return Array.isArray(r) && r.length === 0;"
              }
            ],
            "hints": [
              {
                "when": "[4, -1, -2, 7] still leaves -2",
                "say": "After `splice(i, 1)`, which elements move? Which way can the loop walk so that the ones it has still to read stay where they are?\n"
              },
              {
                "when": "the right numbers come back, but in a different array",
                "say": "The comment says to remove from `nums` and return `nums`. Does your version change the array it was given?\n"
              }
            ]
          }
        ]
      }
    },
    "arrays-of-arrays": {
      "id": "arrays-of-arrays",
      "title": "Arrays of arrays",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`arr.nested` (concepts.md), and `flow.nesting`'s second pass (D-045, D-069 item 5): *Loops inside loops* built rows of text; here the rows and columns are data, so the lesson points back to it rather than reteaching it. It builds on *Lists of values*, *Adding and removing elements* (`push`, a write past the end) and *Walking an array* (`for…of` or an indexed `for`, chosen by whether an iteration needs the position). A jagged array is named and handled with each row's own `.length`; `flat` is named, not taught (D-066 item 2). The C# contrast is under D-010: JavaScript has only the array of arrays. Strict mode is assumed (D-073). Outcome 3, with `flow.nesting`; the module also carries 2. Procedural, explain first (D-061 item 1), with a fail-first writing example (01-explanations rule 5a) on the concept's own mistake, the number of rows used as a row's length. The homework's column sums and matrix addition are not worked here. Derived from those rules, pending Nick's review.",
      "concepts": [
        "arr.nested"
      ],
      "lede": "Some data comes in rows and columns: readings taken three times a day, a seating chart, the scores of several game nights. An array can hold other arrays, one for each row. This lesson shows how to read and replace one element by its row and column, which `.length` counts what, and how to walk one row or one column with a loop, and every element with a loop inside a loop.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Loops inside loops · Lists of values · Walking an array"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Array of arrays** — an array whose elements are arrays. Also called a **two-dimensional array** (2-D array) or a nested array. **Row** — one inner array. **Column** — the elements at the same index in every row. **Matrix** — a rectangle of numbers; in code, an array of arrays of numbers whose rows all have the same length. **Jagged array** — an array of arrays whose rows have different lengths.",
          "entries": [
            "**Array of arrays** — an array whose elements are arrays. Also called a **two-dimensional array** (2-D array) or a nested array.",
            "**Row** — one inner array.",
            "**Column** — the elements at the same index in every row.",
            "**Matrix** — a rectangle of numbers; in code, an array of arrays of numbers whose rows all have the same length.",
            "**Jagged array** — an array of arrays whose rows have different lengths."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on what this lesson builds on, from memory: the last index,\nfrom *Lists of values*; `push`, from *Adding and removing elements*; a count\nover an array, from *Walking an array*; and a loop inside a loop, from *Loops\ninside loops*. Type the value, with square brackets if it is an array. Press\n**I don't know** if you have no idea."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const xs = [4, 7, 5];\nxs[xs.length - 1]\n",
                    "accept": [
                      "5"
                    ],
                    "miss": "**The length is 3, so the last index is 2**, and `xs[2]` is `5`.\n",
                    "why": "`5`.\n"
                  },
                  {
                    "code": "const xs = [1, 2];\nxs.push(3);\nxs\n",
                    "accept": [
                      "[1, 2, 3]",
                      "[1,2,3]",
                      "[ 1, 2, 3 ]"
                    ],
                    "miss": "**`push` adds to the end of the array and changes it.** It returns the new length, 3, but the question asks what `xs` holds.\n",
                    "why": "`[1, 2, 3]`.\n"
                  },
                  {
                    "code": "let count = 0;\nfor (const x of [5, 0, 8, 0]) {\n  if (x === 0) {\n    count++;\n  }\n}\ncount\n",
                    "accept": [
                      "2"
                    ],
                    "miss": "**`for…of` gives each element in turn**, and `count` goes up only for the two zeros.\n",
                    "why": "`2`.\n"
                  },
                  {
                    "code": "let n = 0;\nfor (let r = 0; r < 2; r++) {\n  for (let c = 0; c < 3; c++) {\n    n++;\n  }\n}\nn\n",
                    "accept": [
                      "6"
                    ],
                    "miss": "**Each of the outer loop's 2 iterations runs the whole inner loop**, which adds 3 each time.\n",
                    "why": "`6`. 2 iterations × 3.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can read and replace an element of an array of arrays by its row and column",
                "I can say which `.length` counts the rows and which counts the elements in a row",
                "I can walk one row or one column with a loop, and every element with a loop inside a loop, even when the rows have different lengths"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "An array of arrays",
          "body": [
            {
              "p": "A weather station takes a temperature reading three times a day, in the\nmorning, at noon and in the evening. Here are four days of readings in one\narray:"
            },
            {
              "code": "const readings = [12, 18, 14, 10, 15, 11, 9, 16, 13, 11, 20, 15];",
              "lang": "js",
              "file": "readings.js"
            },
            {
              "p": "That holds every reading, and some programs do store a grid this way. But\nnothing in the array marks where one day ends and the next begins. To find\nthe third day's noon reading, you work out its index: two days of three\nreadings come before it, then that day's morning, so it is at index 7."
            },
            {
              "p": "An array's elements can be any value, and that includes other arrays. Here\neach day is an array of three readings, and the four days are the elements\nof one outer array:"
            },
            {
              "code": "const temps = [\n  [12, 18, 14],\n  [10, 15, 11],\n  [9, 16, 13],\n  [11, 20, 15]\n];",
              "lang": "js",
              "file": "temps.js"
            },
            {
              "p": "The literal is written over several lines, one inner array to a line.\nJavaScript ignores line breaks between elements, and the layout shows the\ngrid. `temps` is an **array of arrays**: the outer array has four elements,\nand each of them is an array of three numbers. Each inner array is a\n**row**, here one day. The elements at the same index in every row make a\n**column**: column 0 holds the morning readings, column 1 the noon ones, and\ncolumn 2 the evening ones."
            },
            {
              "table": {
                "head": [
                  "",
                  "col 0",
                  "col 1",
                  "col 2"
                ],
                "rows": [
                  [
                    "**row 0**",
                    "12",
                    "18",
                    "14"
                  ],
                  [
                    "**row 1**",
                    "10",
                    "15",
                    "11"
                  ],
                  [
                    "**row 2**",
                    "9",
                    "16",
                    "13"
                  ],
                  [
                    "**row 3**",
                    "11",
                    "20",
                    "15"
                  ]
                ]
              }
            },
            {
              "p": "An array of arrays is also called a **two-dimensional array**, or *2-D\narray*, and sometimes a *nested array*. When it holds numbers and every row\nhas the same length, it is a **matrix**, the mathematical name for a\nrectangle of numbers."
            },
            {
              "p": "**A row, then an element.** `temps[1]` reads the element at index 1 of the\nouter array, and that element is a whole row, `[10, 15, 11]`. A row is an\narray, so it can be read by index too. One way is to keep the row in a\nvariable first:"
            },
            {
              "code": "const day = temps[1];    // [10, 15, 11]\nday[2]                   // 11",
              "lang": "js",
              "file": "temps.js"
            },
            {
              "p": "`temps[1][2]` does the same in one expression. JavaScript reads the brackets\nfrom left to right: `temps[1]` evaluates to the row `[10, 15, 11]`, and `[2]`\nthen reads index 2 of that row."
            },
            {
              "code": "temps[1][2]     // 11: row 1, then index 2 in that row\ntemps[2][1]     // 16: row 2, then index 1 in that row",
              "lang": "js",
              "file": "temps.js"
            },
            {
              "p": "`temps[2][1]` is the third day's noon reading, the one at index 7 of\n`readings`, and finding it took no arithmetic."
            },
            {
              "p": "So the first bracket picks the row and the second picks the column. For any\narray of arrays, here called `grid`, with `r` for the row and `c` for the\ncolumn, that is `grid[r][c]`. That order comes from how `temps` was written,\nwith each inner array holding one row. Nothing in JavaScript makes the inner arrays\nrows: a program could store each column as an inner array, and then the\nfirst bracket would pick a column. Storing rows is the usual layout, and the\none this course uses, because a literal written one row to a line then looks\nlike the grid it holds."
            },
            {
              "p": "`temps[1][2]` and `temps[2][1]` are different elements. Swapping the two\nindexes reads a different reading, and nothing warns you."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const g = [[5, 1], [7, 3], [6, 8]];\ng[2][0]\n",
                    "accept": [
                      "6"
                    ],
                    "miss": "**The first bracket picks the row.** `g[2]` is the row `[6, 8]`, and index 0 of that row is `6`.\n",
                    "why": "`6`.\n"
                  },
                  {
                    "code": "const g = [[5, 1], [7, 3], [6, 8]];\ng[1]\n",
                    "choices": [
                      "[7, 3]",
                      "7",
                      "[1, 3, 8]"
                    ],
                    "accept": [
                      "[7, 3]"
                    ],
                    "miss": "**If you chose `7` — one bracket reads one element of the outer array, and each of its elements is a row.** **If you chose `[1, 3, 8]` — that is column 1,** one element from each row; it is not an element of `g`.\n",
                    "why": "`[7, 3]`, the whole of row 1.\n"
                  },
                  {
                    "question": "`const g = [[5, 1], [7, 3], [6, 8]];` — which expression evaluates to the `8`?",
                    "choices": [
                      "g[2][1]",
                      "g[1][2]",
                      "g[1][1]"
                    ],
                    "accept": [
                      "g[2][1]"
                    ],
                    "miss": "**If you chose `g[1][2]` — the indexes are swapped.** `g[1]` is `[7, 3]`, which has no index 2, so `g[1][2]` is `undefined`. **If you chose `g[1][1]` — that is row 1, `[7, 3]`, so it is `3`.**\n",
                    "why": "`g[2][1]`: row 2 is `[6, 8]`, and the `8` is at index 1 in it.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Two lengths, and replacing an element",
          "body": [
            {
              "p": "Two `.length`s matter here. `temps.length` counts the elements of the outer\narray, which are the rows. `temps[0].length` counts the elements in\nrow 0:"
            },
            {
              "code": "const temps = [\n  [12, 18, 14],\n  [10, 15, 11],\n  [9, 16, 13],\n  [11, 20, 15]\n];\ntemps.length       // 4: the rows\ntemps[0].length    // 3: the elements in row 0",
              "lang": "js",
              "file": "lengths.js"
            },
            {
              "p": "When every row has the same length, the length of a row is the number of\ncolumns. Rows of different lengths come later in this lesson."
            },
            {
              "p": "The index of the last row, and of the last element in a row, is one less\nthan a length, as in *Lists of values*. Each `.length` belongs to the array it is read from:"
            },
            {
              "code": "const lastRow = temps[temps.length - 1];    // [11, 20, 15]\nlastRow[lastRow.length - 1]                 // 15",
              "lang": "js",
              "file": "lengths.js"
            },
            {
              "p": "`temps.length - 1` is 3, the index of the last row. `lastRow.length - 1` is\n2, the last index in that row."
            },
            {
              "p": "**Replacing an element.** Say row 1's evening reading was misread, and\nshould be 12. Two brackets on the left of `=` replace one element:"
            },
            {
              "code": "temps[1][2] = 12;\nconsole.log(temps[1]);    // [10, 15, 12]",
              "lang": "js",
              "file": "lengths.js"
            },
            {
              "p": "`temps[1]` finds row 1, and `[2] = 12` replaces index 2 in that row, as\nreplacing an element did in *Lists of values*. The other rows are not\ntouched."
            },
            {
              "p": "**Past the end.** Reading past the end of a row gives `undefined`, as\nreading past the end of any array does. A read past the end of the outer\narray gives `undefined` too: `temps[4]` is `undefined`, because there is no\nrow 4. The trouble is the second bracket. It asks `undefined` for its\nelement 0, and `undefined` has no elements, so the program stops:"
            },
            {
              "code": "temps[0][3]     // undefined: row 0 has indexes 0, 1 and 2\ntemps[4][0]     // TypeError",
              "lang": "js",
              "file": "lengths.js"
            },
            {
              "p": "`temps[4][0]` stops with `TypeError: Cannot read properties of undefined\n(reading '0')`. The message says what went wrong: it tried to read `'0'`\nfrom a value that was `undefined`. That value is `temps[4]`, the row that\ndoes not exist. When you see this message on a grid, check the row index\nfirst."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const g = [[1, 2, 3], [4, 5, 6]];\ng.length\n",
                    "accept": [
                      "2"
                    ],
                    "miss": "**`g.length` counts the elements of the outer array**, and each of them is a row. There are two rows.\n",
                    "why": "`2`.\n"
                  },
                  {
                    "code": "const g = [[1, 2, 3], [4, 5, 6]];\ng[1].length\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**`g[1]` is the row `[4, 5, 6]`**, and `.length` counts its elements.\n",
                    "why": "`3`.\n"
                  },
                  {
                    "code": "const g = [[1, 2, 3], [4, 5, 6]];\ng[0][1] = 9;\ng[0]\n",
                    "accept": [
                      "[1, 9, 3]",
                      "[1,9,3]",
                      "[ 1, 9, 3 ]"
                    ],
                    "miss": "**`g[0]` is row 0, and `[1] = 9` replaces index 1 in that row**, the `2`.\n",
                    "why": "`[1, 9, 3]`.\n"
                  },
                  {
                    "code": "const g = [[1, 2, 3], [4, 5, 6]];\ng[2][0]\n",
                    "choices": [
                      "undefined",
                      "a TypeError",
                      "4"
                    ],
                    "accept": [
                      "a TypeError"
                    ],
                    "miss": "**`g` has rows 0 and 1, so `g[2]` is `undefined`.** Reading index 0 of `undefined` stops the program. A read past the end of one row would give `undefined`; this one is past the end of the outer array.\n",
                    "why": "A TypeError: `Cannot read properties of undefined (reading '0')`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "One row, or one column",
          "body": [
            {
              "p": "A row is an array, so it can be walked like any array, with the loops from\n*Walking an array*. Here is the total of row 1:"
            },
            {
              "code": "const temps = [[12, 18, 14], [10, 15, 11], [9, 16, 13], [11, 20, 15]];\nlet total = 0;\nfor (const t of temps[1]) {\n  total += t;\n}\nconsole.log(total);    // 36",
              "lang": "js",
              "file": "row.js"
            },
            {
              "p": "`temps[1]` is `[10, 15, 11]`, so the loop runs three iterations, and `total`\nends at 10 + 15 + 11, which is 36. Written as an indexed `for`, the loop's\ncondition would be `c < temps[1].length`: the length of the row being\nwalked."
            },
            {
              "p": "A column is not an array of its own. Column 1, the noon readings, is one\nelement from each row: `temps[0][1]`, `temps[1][1]`, `temps[2][1]` and\n`temps[3][1]`. To walk it, loop over the rows and read the same column index\nin each one."
            },
            {
              "p": "Which day had the hottest noon? The answer is a row's index, a position, so\nthe loop is an indexed `for`, as *Walking an array* chose for a search whose\nanswer is a position. The loop keeps the highest noon reading so far, the\nway *Walking an array* found the highest reading, and the row it came from:"
            },
            {
              "code": "const temps = [[12, 18, 14], [10, 15, 11], [9, 16, 13], [11, 20, 15]];\nlet top = temps[0][1];\nlet topRow = 0;\nfor (let r = 0; r < temps.length; r++) {\n  if (temps[r][1] > top) {\n    top = temps[r][1];\n    topRow = r;\n  }\n}\nconsole.log(topRow);    // 3",
              "lang": "js",
              "file": "column.js"
            },
            {
              "p": "`top` is the highest noon reading so far, and `topRow` the row it came\nfrom. Both start from row 0: its noon reading, and its index. The loop\nruns one iteration per row, so its condition uses `temps.length`, the number\nof rows. The column index, `1`, is the same on every iteration. In the\ntable, the noon column is `temps[r][1]`."
            },
            {
              "table": {
                "head": [
                  "`r`",
                  "noon",
                  "`top`",
                  "`topRow`"
                ],
                "rows": [
                  [
                    "before",
                    "",
                    "18",
                    "0"
                  ],
                  [
                    "0",
                    "18",
                    "18",
                    "0"
                  ],
                  [
                    "1",
                    "15",
                    "18",
                    "0"
                  ],
                  [
                    "2",
                    "16",
                    "18",
                    "0"
                  ],
                  [
                    "3",
                    "20",
                    "20",
                    "3"
                  ]
                ]
              }
            },
            {
              "p": "The first iteration compares row 0's reading with itself, which changes\nnothing. Then `r` is 4, `4 < 4` is `false`, and the loop ends: the hottest\nnoon reading, 20, was in row 3."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const g = [[3, 8], [6, 1], [4, 9]];\nfor (let r = 0; r < g.length; r++) {\n  console.log(g[r][1]);\n}\n// What does it print? One line per line.\n",
                    "lines": 3,
                    "accept": [
                      "8\n1\n9"
                    ],
                    "miss": "**The loop goes down the rows, and reads index 1 in each:** `g[0][1]`, `g[1][1]`, then `g[2][1]`. That is column 1.\n",
                    "why": "`8`, `1`, `9`: column 1, from the top.\n"
                  },
                  {
                    "question": "`grid` has 5 rows, each of 2 elements. A loop walks column 0, reading `grid[r][0]`. What is its condition?",
                    "choices": [
                      "r < grid.length",
                      "r < grid[0].length",
                      "r <= grid.length"
                    ],
                    "accept": [
                      "r < grid.length"
                    ],
                    "miss": "**If you chose `r < grid[0].length` — that is the length of a row, 2,** so the loop would read only rows 0 and 1. **If you chose `<=` — the last iteration would have `r` at 5,** and `grid[5]` is `undefined`, so `grid[5][0]` stops with a TypeError.\n",
                    "why": "`r < grid.length`: one iteration for each of the 5 rows.\n"
                  },
                  {
                    "question": "`grid` has 5 rows, each of 2 elements. How many iterations does `for (const x of grid[3])` run? Type the number.",
                    "accept": [
                      "2",
                      "two"
                    ],
                    "miss": "**`grid[3]` is one row**, and the loop walks its elements.\n",
                    "why": "`2`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Every element: a loop inside a loop",
          "body": [
            {
              "p": "To reach every element, walk every row, and in each row walk every element.\nThat is a loop inside a loop. As in *Loops inside loops*, the outer loop\npicks a row, and on each of its iterations the inner loop runs all the way\nthrough. There the loops built rows of text; here the rows are already in\nthe array."
            },
            {
              "p": "This seating chart holds `true` for a seat that is taken and `false` for one\nthat is free. The program prints where each taken seat is:"
            },
            {
              "code": "const seats = [\n  [true, false, true],\n  [false, true, true]\n];\nfor (let r = 0; r < seats.length; r++) {\n  for (let c = 0; c < seats[r].length; c++) {\n    if (seats[r][c]) {\n      console.log(r + \" \" + c);\n    }\n  }\n}",
              "lang": "js",
              "file": "seats.js"
            },
            {
              "code": "0 0\n0 2\n1 1\n1 2",
              "lang": "text"
            },
            {
              "p": "Each line is a row index, then a column index, counted from 0. A chart for\npeople to read would add 1 to each, as *Walking an array* did for positions\npeople read."
            },
            {
              "p": "The outer loop's condition, `r < seats.length`, counts the rows. The inner\nloop's condition, `c < seats[r].length`, counts the elements in the row the\nouter loop is on. The loop variables are named `r` for the row and `c` for\nthe column, as *Loops inside loops* suggested, so `seats[r][c]` reads row\nfirst, then column."
            },
            {
              "p": "A trace table with both loop variables, as in *Loops inside loops*, shows\nthe order: `r` stays at 0 while `c` goes 0, 1, 2; then `c < 3` is `false`,\n`r` becomes 1, and `c` starts again at 0."
            },
            {
              "p": "Printing where a seat is needs `r` and `c`. Counting the taken seats does\nnot, and then both loops can be `for…of`, over the same `seats`:"
            },
            {
              "code": "let taken = 0;\nfor (const row of seats) {\n  for (const seat of row) {\n    if (seat) {\n      taken++;\n    }\n  }\n}\nconsole.log(taken);    // 4",
              "lang": "js",
              "file": "count.js"
            },
            {
              "p": "The outer `for…of` gives each row, which is an array, and the inner one\ngives each seat in that row. *Walking an array* chose between `for…of` and\nan indexed `for` by whether an iteration needs the position, and each of the\ntwo loops here is chosen the same way."
            },
            {
              "p": "**Rows of different lengths.** Nothing in JavaScript makes the rows of an\narray of arrays the same length. Here are the scores from three game nights:\ntwo games were played on the first night, three on the second, and one on\nthe third."
            },
            {
              "code": "const games = [\n  [3, 1],\n  [2, 4, 6],\n  [5]\n];",
              "lang": "js",
              "file": "games.js"
            },
            {
              "p": "An array of arrays whose rows have different lengths is a **jagged array**.\n`games.length` is 3, and each row has its own length: `games[0].length` is\n2, `games[1].length` is 3, and `games[2].length` is 1."
            },
            {
              "p": "Because each row has its own length, the inner loop's condition reads the\nlength of the row the outer loop is on, `c < games[r].length`, and each row\nis walked to its own end. This program gives every game one bonus point:"
            },
            {
              "code": "const games = [[3, 1], [2, 4, 6], [5]];\nfor (let r = 0; r < games.length; r++) {\n  for (let c = 0; c < games[r].length; c++) {\n    games[r][c] += 1;\n  }\n}\nconsole.log(games);    // [[4, 2], [3, 5, 7], [6]]",
              "lang": "js",
              "file": "bonus.js"
            },
            {
              "p": "A common slip is to write the first row's length, `games[0].length`, as\nthe inner limit. When every row has the same length, `grid[0].length` and\n`grid[r].length` are the same number, and either one works. In `games` they\nare not, and the first row's length would skip elements of the longer rows.\n`grid[r].length` works whether the rows differ or not, and whoever reads the\ncode does not have to check whether they can, so this course writes\n`grid[r].length`."
            },
            {
              "p": "This is one place where JavaScript differs from some other languages. In\nC#, a grid can be a *multidimensional array* (also called rectangular),\ndeclared `int[,]`: one array with two indexes, read as `grid[1, 2]`, whose\nrows all have the same length because it is made that way. C# also has the\narray of arrays, `int[][]`, and calls it a jagged array, whatever its rows'\nlengths. JavaScript has only the array of arrays. So nothing keeps its rows\nthe same length, and when they must match, the code that builds the grid is\nwhat makes them match."
            },
            {
              "p": "JavaScript also has a method that puts every element of an array of arrays\ninto one new array: `games.flat()` returns `[3, 1, 2, 4, 6, 5]`. It is named\nhere and not taught; this course walks the rows with loops."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const g = [[2, 5, 1], [4, 3, 6]];\nfor (let r = 0; r < g.length; r++) {\n  for (let c = 0; c < g[r].length; c++) {\n    if (g[r][c] > 4) {\n      console.log(r + \" \" + c);\n    }\n  }\n}\n// What does it print? One line per line.\n",
                    "lines": 2,
                    "accept": [
                      "0 1\n1 2"
                    ],
                    "miss": "**The loops reach all six elements, row by row.** Two of them are greater than 4: the `5` at row 0, column 1, and the `6` at row 1, column 2.\n",
                    "why": "`0 1`, then `1 2`.\n"
                  },
                  {
                    "question": "In that program, the inner loop's body runs for the fourth time. What do `r` and `c` hold?",
                    "choices": [
                      "r is 1, c is 0",
                      "r is 0, c is 3",
                      "r is 1, c is 1"
                    ],
                    "accept": [
                      "r is 1, c is 0"
                    ],
                    "miss": "**Row 0 has three elements, so the body runs three times with `r` at 0.** `c` at 3 ends the inner loop without running the body. On the outer loop's next iteration, the inner loop starts again with `c` at 0.\n",
                    "why": "`r` is 1 and `c` is 0: the first element of row 1, the `4`.\n"
                  },
                  {
                    "code": "const j = [[7], [1, 2, 3, 4], [4, 5]];\nj[1].length\n",
                    "accept": [
                      "4"
                    ],
                    "miss": "**`j[1]` is the row `[1, 2, 3, 4]`**, and `.length` counts its elements, not the rows.\n",
                    "why": "`4`.\n"
                  },
                  {
                    "code": "const j = [[7], [1, 2, 3, 4], [4, 5]];\nlet n = 0;\nfor (let r = 0; r < j.length; r++) {\n  for (let c = 0; c < j[0].length; c++) {\n    n++;\n  }\n}\n// What does n hold now?\n",
                    "accept": [
                      "3"
                    ],
                    "miss": "**The inner limit is `j[0].length`, the length of row 0, which is 1**, on every row. So the inner loop runs once in each of the 3 rows.\n",
                    "why": "`3`. With `c < j[r].length` it would be 7, one for each element.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Building a grid row by row",
          "body": [
            {
              "p": "A loop inside a loop can also build an array of arrays. It does its work in\nthe same four places as the text grids of *Loops inside loops*:"
            },
            {
              "list": [
                "**Before the outer loop**, the grid starts as an empty array.",
                "**At the top of the outer loop's body**, a new, empty row is made.",
                "**Inside the inner loop**, one element is pushed onto the row.",
                "**After the inner loop, still inside the outer one**, the row is pushed onto the grid."
              ],
              "ordered": true
            },
            {
              "p": "Here is a cinema's seating chart, `rowCount` rows of `perRow` seats, with every\nseat free:"
            },
            {
              "code": "const rowCount = 2;\nconst perRow = 3;\nconst chart = [];\nfor (let r = 0; r < rowCount; r++) {\n  const row = [];\n  for (let c = 0; c < perRow; c++) {\n    row.push(false);\n  }\n  chart.push(row);\n}\nconsole.log(chart);    // [[false, false, false], [false, false, false]]",
              "lang": "js",
              "file": "chart.js"
            },
            {
              "p": "`chart.push(row)` adds the whole row as one element of `chart`. After the\nloops, `chart.length` is 2 and `chart[0].length` is 3."
            },
            {
              "p": "Two ways of writing this go wrong. The first writes each element straight\ninto the grid, `chart[r][c] = false;`, in place of the two pushes. That stops with `TypeError: Cannot set properties of\nundefined (setting '0')`: `chart` starts empty, so `chart[0]` is `undefined`,\nand there is no row yet to put an element in. A row has to exist before an\nelement can be written into it."
            },
            {
              "p": "The second writes `const row = [];` once, above the outer loop. Then there\nis only one row array, and every iteration pushes onto it, so `chart` holds\nthat one array twice, with six `false` in it. Why is the subject of *Copying\nversus aliasing*, in the Values and references module. Make the row at the\ntop of the outer loop's body, where *Loops inside loops* reset its row."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "const grid = [];\nfor (let r = 0; r < 3; r++) {\n  const row = [];\n  for (let c = 0; c < 2; c++) {\n    row.push(r);\n  }\n  grid.push(row);\n}\ngrid\n",
                    "accept": [
                      "[[0, 0], [1, 1], [2, 2]]",
                      "[[0,0],[1,1],[2,2]]",
                      "[ [ 0, 0 ], [ 1, 1 ], [ 2, 2 ] ]"
                    ],
                    "miss": "**Each row gets 2 elements, and each element is the row's own `r`.** The outer loop runs 3 iterations, so there are 3 rows.\n",
                    "why": "`[[0, 0], [1, 1], [2, 2]]`.\n"
                  },
                  {
                    "question": "In `chart.js`, why is `const row = [];` inside the outer loop, above the inner one?",
                    "choices": [
                      "So that each row is a new array",
                      "So that the inner loop can push onto chart",
                      "Because a const cannot be declared before a loop"
                    ],
                    "accept": [
                      "So that each row is a new array"
                    ],
                    "miss": "**The inner loop pushes onto `row`, not onto `chart`.** And a `const` can be declared anywhere. Made once, above the outer loop, the row would be one array that every iteration added to.\n",
                    "why": "So that each row is a new array, made on each iteration of the outer loop.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "step",
          "label": "From a sentence to a function",
          "body": [
            {
              "p": "The four subgoals from *Writing a function's answer* still apply:"
            },
            {
              "list": [
                "**Name the values you have.** Here, one of them is an array of arrays.",
                "**Find the question in the sentence.** Which elements does it ask about: one, one row, one column, or every one?",
                "**Write it**: two brackets for one element, a loop for a row or a column, a loop inside a loop for every element.",
                "**Send the answer out** with `return`."
              ],
              "ordered": true
            },
            {
              "p": "**Example 1.** \"`capAt` takes a grid of numbers and a limit, replaces every\nelement above the limit with the limit, and returns the grid:\n`capAt([[4, 9, 2], [7, 1, 8]], 5)` is `[[4, 5, 2], [5, 1, 5]]`.\""
            },
            {
              "p": "Every element may change, so both loops are indexed. A first try:"
            },
            {
              "code": "function capAt(grid, limit) {\n  for (let r = 0; r < grid.length; r++) {\n    for (let c = 0; c < grid.length; c++) {\n      if (grid[r][c] > limit) {\n        grid[r][c] = limit;\n      }\n    }\n  }\n  return grid;\n}",
              "lang": "js",
              "file": "cap.js"
            },
            {
              "p": "It runs without an error. The tests reported:"
            },
            {
              "code": "✗ capAt([[4, 9, 2], [7, 1, 8]], 5) → [[4, 5, 2], [5, 1, 5]]\n✓ capAt([[6, 3], [2, 9]], 5) → [[5, 3], [2, 5]]\n✗ capAt([[1, 2, 3, 4]], 2) → [[1, 2, 2, 2]]",
              "lang": "text"
            },
            {
              "p": "`capAt([[4, 9, 2], [7, 1, 8]], 5)` returned `[[4, 5, 2], [5, 1, 8]]`: the\n`8` was not capped. Work it through by hand with that grid. `grid.length` is\n2, the rows, and `grid[0].length` is 3, the elements in a row. The inner\ncondition, `c < grid.length`, is `c < 2` in every row, so the loop reads\ncolumns 0 and 1 and never column 2. Column 2 holds the `2`, which needs no\nchange, and the `8`, which does."
            },
            {
              "p": "The square grid passed because it has 2 rows and every row has 2 elements,\nso the two lengths are the same number. A square grid hides this mistake,\nwhich is why these tests, and the ones in the exercises, include grids that\nare not square."
            },
            {
              "p": "The inner loop has to count the elements of the row it is on. The fix is\nits header, and nothing else:"
            },
            {
              "code": "    for (let c = 0; c < grid[r].length; c++) {",
              "lang": "js",
              "file": "cap.js"
            },
            {
              "code": "✓ capAt([[4, 9, 2], [7, 1, 8]], 5) → [[4, 5, 2], [5, 1, 5]]\n✓ capAt([[6, 3], [2, 9]], 5) → [[5, 3], [2, 5]]\n✓ capAt([[1, 2, 3, 4]], 2) → [[1, 2, 2, 2]]",
              "lang": "text"
            },
            {
              "p": "**Example 2: you choose the line.** \"`fillRow` takes a grid, a row index `r`\nand a value, replaces every element of row `r` with the value, and returns\nthe grid: `fillRow([[1, 2], [3, 4]], 0, 9)` is `[[9, 9], [3, 4]]`.\" Only one\nrow changes, so there is one loop. Its header is missing, marked `TODO`."
            },
            {
              "code": "function fillRow(grid, r, value) {\n  for (/* TODO */) {\n    grid[r][c] = value;\n  }\n  return grid;\n}",
              "lang": "js",
              "file": "fill.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "What goes inside the parentheses at the TODO?",
                    "choices": [
                      "let c = 0; c < grid[r].length; c++",
                      "let c = 0; c < grid.length; c++",
                      "let c = 0; c <= grid[r].length; c++"
                    ],
                    "accept": [
                      "let c = 0; c < grid[r].length; c++"
                    ],
                    "miss": "**If you chose `c < grid.length` — that counts the rows.** On `[[1, 2, 3]]` it would replace only the first element. **If you chose `<=` — the loop never ends.** When `c` equals the row's length, the write is one past the last index, and a write past the end adds an element, as in *Adding and removing elements*. The row is now one longer, so `c <= grid[r].length` is still `true`, and every iteration after that does the same.\n",
                    "why": "`let c = 0; c < grid[r].length; c++`: one iteration for each element of row `r`.\n"
                  },
                  {
                    "question": "What does `fillRow([[1, 2], [3, 4], [5, 6]], 2, 0)` return? Type it.",
                    "accept": [
                      "[[1, 2], [3, 4], [0, 0]]",
                      "[[1,2],[3,4],[0,0]]",
                      "[ [ 1, 2 ], [ 3, 4 ], [ 0, 0 ] ]"
                    ],
                    "miss": "**`r` is 2, so only row 2, `[5, 6]`, changes.** Both of its elements become `0`.\n",
                    "why": "`[[1, 2], [3, 4], [0, 0]]`.\n"
                  }
                ]
              }
            }
          ],
          "n": 6
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**An array of arrays holds a grid, one inner array for each row.** `grid[r]` is a row; `grid[r][c]` reads row first, then column; and `grid[r][c] = value` replaces one element.",
                "**Two lengths:** `grid.length` counts the rows, and `grid[r].length` counts the elements in row `r`.",
                "**Walk a row like any array; walk a column by looping over the rows** with the column index fixed. Every element takes a loop inside a loop.",
                "**The inner condition is `c < grid[r].length`**, which also walks a jagged array correctly. Build a grid by pushing a new row on each iteration of the outer loop."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array#creating_a_two-dimensional_array",
              "note": "the example \"Creating a two-dimensional array\", a chess board as an array of arrays."
            },
            {
              "title": "Multi-dimensional arrays",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Indexed_collections#multi-dimensional_arrays",
              "note": "in the guide to indexed collections. It makes each row with `new Array(4)`, which this course names but does not teach; the lessons build rows with `push`."
            },
            {
              "title": "Array.prototype.flat()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat",
              "note": "named in this lesson, not taught."
            }
          ]
        }
      ],
      "_source": "lessons/arrays-of-arrays.md",
      "practice": {
        "id": "arrays-of-arrays",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Row, then column",
            "prompt": "Type the value each one evaluates to, or press the answer. Write an array with its square brackets.\n",
            "items": [
              {
                "id": "p1",
                "code": "const m = [[2, 7, 1], [8, 3, 5]];\nm[1][2]\n",
                "accept": [
                  "5"
                ],
                "miss": "**The first bracket picks the row.** `m[1]` is `[8, 3, 5]`, and index 2 of that row is `5`.\n",
                "why": "`5`.\n"
              },
              {
                "id": "p2",
                "code": "const m = [[2, 7, 1], [8, 3, 5]];\nm.length\n",
                "accept": [
                  "2"
                ],
                "miss": "**`m.length` counts the elements of the outer array, and each of them is a row.** It does not count the numbers inside the rows.\n",
                "why": "`2`. Each row's own length is 3.\n"
              },
              {
                "id": "p3",
                "question": "`board` has 3 rows of 4 elements. Which expression reads the element in the last row, first column?",
                "choices": [
                  "board[2][0]",
                  "board[0][2]",
                  "board[3][0]"
                ],
                "accept": [
                  "board[2][0]"
                ],
                "miss": "**If you chose `board[0][2]` — the indexes are swapped:** that is row 0, column 2. **If you chose `board[3][0]` — 3 rows have indexes 0, 1 and 2,** so `board[3]` is past the end, and `board[3][0]` stops with a TypeError.\n",
                "why": "`board[2][0]`: the last row is at index 2, and the first column is index 0.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const m = [[2, 7, 1], [8, 3, 5]];\nm[0][3]\n",
                "accept": [
                  "undefined"
                ],
                "miss": "**`m[0]` is the row `[2, 7, 1]`, with indexes 0, 1 and 2.** Index 3 is past the end of that row.\n",
                "why": "`undefined`.\n"
              },
              {
                "id": "p5",
                "code": "const m = [[2, 7, 1], [8, 3, 5]];\nm[1]\n",
                "accept": [
                  "[8, 3, 5]",
                  "[8,3,5]",
                  "[ 8, 3, 5 ]"
                ],
                "miss": "**One bracket reads one element of the outer array, and that element is a whole row.**\n",
                "why": "`[8, 3, 5]`.\n"
              },
              {
                "id": "p6",
                "code": "[[1], [2, 3], []][2].length",
                "accept": [
                  "0"
                ],
                "miss": "**Index 2 of the outer array is the row `[]`**, an empty array.\n",
                "why": "`0`.\n"
              },
              {
                "id": "p7",
                "code": "const m = [[2, 7, 1], [8, 3, 5]];\nm[1].includes(7)\n",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**`m[1]` is only row 1, `[8, 3, 5]`.** The `7` is in row 0.\n",
                "why": "`false`. A row is an array, so `includes` searches that row and nothing else.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered the first one as if the first bracket were the column",
                "say": "Read the brackets from left to right. What does `m[1]` evaluate to on its own? Then read index 2 of that.\n"
              },
              {
                "when": "you answered 6 for m.length",
                "say": "What are the elements of the outer array: numbers, or rows?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "A season of matches, run",
            "prompt": "`goals` holds the goals a team scored in each half of three matches, one row per match. Predict each line this program prints, then run it and compare.\n",
            "filename": "goals.js",
            "code": "const goals = [\n  [1, 2],\n  [0, 3],\n  [2, 2]\n];\nconsole.log(goals.length);\nconsole.log(goals[1]);\nconsole.log(goals[2][0] + goals[2][1]);\n\ngoals[0][1] = 4;\nconsole.log(goals[0]);\nconsole.log(goals[goals.length - 1].join(\" - \"));\nconsole.log(goals[1][2]);\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print? Type the number.",
                "accept": [
                  "3"
                ],
                "miss": "**`goals.length` counts the rows**, one per match.\n",
                "why": "`3`.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print? Type the array.",
                "accept": [
                  "[0,3]",
                  "[0, 3]",
                  "[ 0, 3 ]"
                ],
                "miss": "**`goals[1]` is the whole of row 1**, the second match.\n",
                "why": "`[0,3]`. The site prints an array without spaces; Node prints `[ 0, 3 ]`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print? Type the number.",
                "accept": [
                  "4"
                ],
                "miss": "**`goals[2]` is `[2, 2]`**, so the line adds its two elements.\n",
                "why": "`4`.\n"
              },
              {
                "id": "r4",
                "question": "What does the fourth line print? Type the array.",
                "accept": [
                  "[1,4]",
                  "[1, 4]",
                  "[ 1, 4 ]"
                ],
                "miss": "**`goals[0][1] = 4` replaced index 1 of row 0**, the `2`.\n",
                "why": "`[1,4]`.\n"
              },
              {
                "id": "r5",
                "question": "What does the fifth line print? Type it.",
                "accept": [
                  "2 - 2",
                  "\"2 - 2\""
                ],
                "miss": "**`goals.length - 1` is 2, so this is the last row, `[2, 2]`.** `join` puts `\" - \"` between its elements.\n",
                "why": "`2 - 2`.\n"
              },
              {
                "id": "r6",
                "question": "What does the last line print? Type it.",
                "accept": [
                  "undefined"
                ],
                "miss": "**Row 1 is `[0, 3]`, with indexes 0 and 1.** Index 2 is past the end of the row, which gives `undefined`, not an error.\n",
                "why": "`undefined`.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected the second line to print 0",
                "say": "How many brackets does `goals[1]` have? What is the element at index 1 of the outer array?\n"
              },
              {
                "when": "you expected the fourth line to print [1, 2]",
                "say": "Which line runs just before it? Which row and index does it replace?\n"
              },
              {
                "when": "you expected an error on the last line",
                "say": "`goals[1]` exists. Is reading past the end of an array that exists an error?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Both loop variables, element by element",
            "prompt": "Trace the program in the first question with a table: columns for `r`, `c`, `g[r][c]` and `total`, one line each time the inner loop's body runs. Then answer each question from your table.\n",
            "items": [
              {
                "id": "t1",
                "code": "const g = [[5, 2, 8], [1, 6, 3]];\nlet total = 0;\nfor (let r = 0; r < g.length; r++) {\n  for (let c = 0; c < g[r].length; c++) {\n    if (c === r) {\n      total += g[r][c];\n    }\n  }\n}\n// What does total hold at the end?\n",
                "accept": [
                  "11"
                ],
                "miss": "**`total` grows only when `c` equals `r`:** at row 0, column 0, the `5`, and at row 1, column 1, the `6`.\n",
                "why": "`11`. Those two elements run along the diagonal, from the top left.\n"
              },
              {
                "id": "t2",
                "question": "The 4th time the inner loop's body runs, what does `r` hold? Type the number.",
                "accept": [
                  "1"
                ],
                "miss": "**Row 0 has three elements, so the first three times have `r` at 0.** The 4th is the first of row 1.\n",
                "why": "`1`.\n"
              },
              {
                "id": "t3",
                "question": "And what does `c` hold that 4th time? Type the number.",
                "accept": [
                  "0"
                ],
                "miss": "**The inner loop starts again on each iteration of the outer loop**, so `c` is back at 0.\n",
                "why": "`0`.\n"
              },
              {
                "id": "t4",
                "question": "And what is `g[r][c]` that 4th time? Type the number.",
                "accept": [
                  "1"
                ],
                "miss": "**`g[1]` is `[1, 6, 3]`**, and index 0 of it is `1`.\n",
                "why": "`1`. `c` is not equal to `r` there, so `total` does not change.\n"
              },
              {
                "id": "t5",
                "question": "How many times does the inner loop's body run in all? Type the number.",
                "accept": [
                  "6",
                  "six"
                ],
                "why": "`6`: 2 rows of 3 elements.\n"
              }
            ],
            "extra": [
              {
                "id": "t6",
                "code": "const g = [[3, 8], [6, 1], [2, 7]];\nlet out = \"\";\nfor (let r = 0; r < g[0].length; r++) {\n  out += g[r][1];\n}\n// What does out hold? Type it without quotes.\n",
                "accept": [
                  "81"
                ],
                "miss": "**The condition uses `g[0].length`, the length of a row, which is 2.** So the loop reads only rows 0 and 1, and never row 2.\n",
                "why": "`81`. Column 1 is `8`, `1` and `7`; walking a column needs `r < g.length`.\n"
              },
              {
                "id": "t7",
                "code": "const g = [[3, 8], [6, 1], [2, 7]];\nlet out = \"\";\nfor (let r = 0; r < g.length; r++) {\n  out += g[0][r];\n}\n// What does out hold? Type it without quotes.\n",
                "accept": [
                  "38undefined"
                ],
                "miss": "**`g[0][r]` stays in row 0 and moves along it,** while `r` counts the 3 rows. Row 0 has only indexes 0 and 1, so `g[0][2]` is `undefined`, and `+` joins it on as text.\n",
                "why": "`38undefined`.\n"
              },
              {
                "id": "t8",
                "code": "const j = [[8, 8, 8], [8], [8, 8]];\nfor (let r = 0; r < j.length; r++) {\n  for (let c = 0; c < j[r].length; c++) {\n    j[r][c] = c;\n  }\n}\nj\n",
                "accept": [
                  "[[0, 1, 2], [0], [0, 1]]",
                  "[[0,1,2],[0],[0,1]]",
                  "[ [ 0, 1, 2 ], [ 0 ], [ 0, 1 ] ]"
                ],
                "miss": "**The inner limit is each row's own length**, so each row of this jagged array keeps its length, and every element is replaced by its own column index.\n",
                "why": "`[[0, 1, 2], [0], [0, 1]]`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 0 for r the 4th time",
                "say": "Count down your table: how many lines does row 0 take before the inner loop ends?\n"
              },
              {
                "when": "you answered 3 for c the 4th time",
                "say": "`c` at 3 ends the inner loop without running its body. What does `let c = 0` do when the outer loop moves on?\n"
              },
              {
                "when": "you answered 817 for the column walked with g[0].length",
                "say": "What is `g[0].length`? How many iterations does `r < 2` allow?\n"
              },
              {
                "when": "you answered 25 for total",
                "say": "`total` changes only inside `if (c === r)`. On which lines of your table are `c` and `r` equal?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "A triangle of counts",
            "prompt": "`triangle` should return `n` rows, where row 1 counts from 1 to 1, row 2 from 1 to 2, and so on: `triangle(3)` is `[[1], [1, 2], [1, 2, 3]]`. Build the lines that go between its braces, in order. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "  const grid = [];",
              "  for (let r = 1; r <= n; r++) {",
              "    const row = [];",
              "    for (let c = 1; c <= r; c++) {",
              "      row.push(c);",
              "    }",
              "    grid.push(row);",
              "  }",
              "  return grid;"
            ],
            "distractors": [
              {
                "line": "    for (let c = 1; c <= n; c++) {",
                "why": "This inner limit is the same on every row, so every row would count to `n`: `triangle(3)` would be `[[1, 2, 3], [1, 2, 3], [1, 2, 3]]`, a square.\n"
              },
              {
                "line": "    for (let c = 1; c < r; c++) {",
                "why": "`<` stops one short of `r`, so row 1 would be empty and every row one element short: `triangle(3)` would be `[[], [1], [1, 2]]`.\n"
              }
            ],
            "why": "The grid starts empty. Each iteration of the outer loop makes a new row, the inner loop counts from 1 up to the row's own number, `r`, and then the whole row is pushed onto the grid. The inner limit uses the outer loop variable, so each row is one longer than the row before: a jagged array. Both loops start at 1, because the values 1 to `n` are what go in the rows.\n",
            "hints": [
              {
                "when": "you are unsure which inner loop header to use",
                "say": "How many elements does row 2 have? Which name holds 2 while that row is built?\n"
              },
              {
                "when": "you are unsure where grid.push(row) goes",
                "say": "The row is finished only when the inner loop has ended. Is that line inside the inner loop, or after it?\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "From a row to a column",
            "prompt": "`columnHas` should return `true` when column `c` of `grid` holds `target`, and `false` when it does not: with `grid` as `[[1, 2, 3], [4, 5, 6]]`, `columnHas(grid, 1, 5)` is `true` and `columnHas(grid, 0, 2)` is `false`. Right now it searches row `c` instead. Change it so it searches column `c`. Write your code between the braces, and keep `return` in front of each answer — *Writing a function's answer* shows how. Then, in one sentence: why does a search of one column go through every row?\n",
            "exportName": "columnHas",
            "filename": "column-has.js",
            "starter": "function columnHas(grid, c, target) {\n  // TODO: put your code here\n  for (const x of grid[c]) {\n    if (x === target) {\n      return true;\n    }\n  }\n  return false;\n}\n",
            "tests": [
              {
                "label": "columnHas([[1, 2, 3], [4, 5, 6]], 1, 5) → true",
                "body": "return subject([[1, 2, 3], [4, 5, 6]], 1, 5) === true;"
              },
              {
                "label": "columnHas([[1, 2, 3], [4, 5, 6]], 2, 3) → true",
                "body": "return subject([[1, 2, 3], [4, 5, 6]], 2, 3) === true;"
              },
              {
                "label": "columnHas([[1, 2, 3], [4, 5, 6]], 0, 2) → false",
                "body": "return subject([[1, 2, 3], [4, 5, 6]], 0, 2) === false;"
              },
              {
                "label": "columnHas([[7, 8], [9, 7], [6, 5]], 1, 5) → true",
                "body": "return subject([[7, 8], [9, 7], [6, 5]], 1, 5) === true;"
              },
              {
                "label": "columnHas([[7, 8], [9, 7], [6, 5]], 0, 8) → false",
                "body": "return subject([[7, 8], [9, 7], [6, 5]], 0, 8) === false;"
              }
            ],
            "hints": [
              {
                "when": "columnHas([[1, 2, 3], [4, 5, 6]], 2, 3) stops with a TypeError",
                "say": "`grid[c]` with `c` at 2 is a row that does not exist. A column is not an element of `grid`: which index goes in the first bracket, and which in the second?\n"
              },
              {
                "when": "columnHas([[7, 8], [9, 7], [6, 5]], 1, 5) gives false",
                "say": "The `5` is in the last row. How many iterations does your loop run? It should run one for each row: what counts the rows?\n"
              },
              {
                "when": "columnHas([[1, 2, 3], [4, 5, 6]], 0, 2) gives true",
                "say": "`2` is in row 0, not in column 0. Is your loop still walking a row?\n"
              },
              {
                "when": "a match in a later row gives false",
                "say": "Does your loop return `false` on the first element that does not match? Only after every row has been checked is the answer `false`.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Where is it?",
            "prompt": "`findCell` takes a grid and a target, and returns where the target first appears as an array of two numbers, `[row, col]`, searching row by row from the top and each row from the left. When the target is nowhere in the grid, it returns `[-1, -1]`, as `indexOf` returns `-1`. `findCell([[5, 3, 8], [1, 9, 4]], 9)` is `[1, 1]`. The rows may have different lengths. Write your code between the braces, and put `return` in front of each answer — *Writing a function's answer* shows how. Then, in one sentence: why does a `return` inside the inner loop find the first match, not the last?\n",
            "exportName": "findCell",
            "filename": "find-cell.js",
            "starter": "function findCell(grid, target) {\n  // TODO: put your code here\n}\n",
            "tests": [
              {
                "label": "findCell([[5, 3, 8], [1, 9, 4]], 9) → [1, 1]",
                "body": "const r = subject([[5, 3, 8], [1, 9, 4]], 9); return Array.isArray(r) && r.length === 2 && r[0] === 1 && r[1] === 1;"
              },
              {
                "label": "findCell([[5, 3, 8], [1, 9, 4]], 4) → [1, 2]",
                "body": "const r = subject([[5, 3, 8], [1, 9, 4]], 4); return Array.isArray(r) && r.length === 2 && r[0] === 1 && r[1] === 2;"
              },
              {
                "label": "findCell([[6, 2], [2, 6]], 2) → [0, 1], the first one",
                "body": "const r = subject([[6, 2], [2, 6]], 2); return Array.isArray(r) && r.length === 2 && r[0] === 0 && r[1] === 1;"
              },
              {
                "label": "findCell([[1], [2, 3, 7]], 7) → [1, 2]",
                "body": "const r = subject([[1], [2, 3, 7]], 7); return Array.isArray(r) && r.length === 2 && r[0] === 1 && r[1] === 2;"
              },
              {
                "label": "findCell([[1, 2], [3, 4]], 5) → [-1, -1]",
                "body": "const r = subject([[1, 2], [3, 4]], 5); return Array.isArray(r) && r.length === 2 && r[0] === -1 && r[1] === -1;"
              }
            ],
            "hints": [
              {
                "when": "it gave undefined when the target is missing",
                "say": "After both loops have ended, nothing has been found. Put `return` and the not-found answer there, after the outer loop.\n"
              },
              {
                "when": "findCell([[5, 3, 8], [1, 9, 4]], 4) gives [2, 1]",
                "say": "The answer is row first, then column. Which loop variable counts the rows?\n"
              },
              {
                "when": "findCell([[5, 3, 8], [1, 9, 4]], 4) gives [-1, -1]",
                "say": "The `4` is in column 2. Does your inner loop ever reach `c` at 2? Check what its condition compares `c` with.\n"
              },
              {
                "when": "findCell([[1], [2, 3, 7]], 7) gives [-1, -1]",
                "say": "Row 1 is longer than row 0. Does your inner condition use the length of the row the outer loop is on?\n"
              },
              {
                "when": "findCell([[6, 2], [2, 6]], 2) gives [1, 0]",
                "say": "That is the last `2`, not the first. Does your code stop at the first match, or keep looking and keep the latest?\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a largest number",
            "prompt": "A code assistant wrote `gridMax`, which should return the largest number anywhere in a grid. Every row has at least one number. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "grid-max.js",
            "code": "// The largest number in a grid. Every row has at least one number.\nfunction gridMax(grid) {\n  let best = 0;\n  for (let r = 0; r < grid.length; r++) {\n    for (let c = 0; c < grid[0].length; c++) {\n      if (grid[r][c] > best) {\n        best = grid[r][c];\n      }\n    }\n  }\n  return best;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test calls `gridMax([[4, 1], [6, 2, 9]])`. Type what it returns.",
                "accept": [
                  "6"
                ],
                "miss": "**`grid[0].length` is 2, so the inner loop reads only columns 0 and 1 of every row.** Row 1's `9` is at index 2, and is never read.\n",
                "why": "`6`.\n"
              },
              {
                "id": "c2",
                "question": "What does `gridMax([[-5, -2], [-8, -1]])` return? Type it.",
                "accept": [
                  "0"
                ],
                "miss": "**`best` starts at 0, and no number in the grid is greater than 0**, so `best` is never replaced.\n",
                "why": "`0`, a number that is not in the grid.\n"
              },
              {
                "id": "c3",
                "question": "Which line should replace line 5?",
                "choices": [
                  "for (let c = 0; c < grid[r].length; c++) {",
                  "for (let c = 0; c < grid.length; c++) {",
                  "for (let c = 0; c <= grid[0].length; c++) {"
                ],
                "accept": [
                  "for (let c = 0; c < grid[r].length; c++) {"
                ],
                "miss": "**`grid.length` counts the rows, not the elements in a row.** **`<= grid[0].length` still uses row 0's length for every row**, and reads one past its end. Each row has its own length.\n",
                "why": "`c < grid[r].length`: the length of the row the outer loop is on. No iteration here needs a position, so `for…of` loops would also do, with no length to get wrong.\n"
              },
              {
                "id": "c4",
                "question": "What should `best` start as, on line 3?",
                "choices": [
                  "grid[0][0]",
                  "-1",
                  "grid[0]"
                ],
                "accept": [
                  "grid[0][0]"
                ],
                "miss": "**`-1` fails the same way 0 does**, on a grid of numbers below `-1`. **`grid[0]` is a whole row, an array**, not a number.\n",
                "why": "`grid[0][0]`: a number that is in the grid, as *Walking an array* started the highest so far from the first element.\n"
              }
            ],
            "exportName": "gridMax",
            "starter": "// The largest number in a grid. Every row has at least one number.\nfunction gridMax(grid) {\n  let best = 0;\n  for (let r = 0; r < grid.length; r++) {\n    for (let c = 0; c < grid[0].length; c++) {\n      if (grid[r][c] > best) {\n        best = grid[r][c];\n      }\n    }\n  }\n  return best;\n}\n",
            "tests": [
              {
                "label": "gridMax([[4, 1], [6, 2, 9]]) → 9",
                "body": "return subject([[4, 1], [6, 2, 9]]) === 9;"
              },
              {
                "label": "gridMax([[-5, -2], [-8, -1]]) → -1",
                "body": "return subject([[-5, -2], [-8, -1]]) === -1;"
              },
              {
                "label": "gridMax([[7]]) → 7",
                "body": "return subject([[7]]) === 7;"
              },
              {
                "label": "gridMax([[2, 3, 1], [5]]) → 5",
                "body": "return subject([[2, 3, 1], [5]]) === 5;"
              },
              {
                "label": "gridMax([[-7, -3], [-4]]) → -3",
                "body": "return subject([[-7, -3], [-4]]) === -3;"
              }
            ],
            "hints": [
              {
                "when": "gridMax([[4, 1], [6, 2, 9]]) gives 6",
                "say": "Row 1 has three elements. How many does the inner loop read in it? Which array's length should its condition use?\n"
              },
              {
                "when": "gridMax([[-5, -2], [-8, -1]]) gives 0",
                "say": "Is 0 in that grid? Start `best` at a number that is.\n"
              }
            ]
          }
        ]
      }
    },
    "check-arrays": {
      "id": "check-arrays",
      "title": "Arrays check",
      "kind": "quiz",
      "unlocks": "Objects",
      "questions": [
        {
          "id": "q1",
          "skill": "The last index",
          "prompt": "What does this print?",
          "code": "const stops = [\"Elm\", \"Oak\", \"Pine\", \"Ash\"];\nconsole.log(stops[stops.length - 1]);\nconsole.log(stops[stops.length]);",
          "answers": [
            {
              "key": "A",
              "text": "`Pine`, then `Ash`",
              "tag": "the last index taken as .length",
              "correct": false
            },
            {
              "key": "B",
              "text": "`Ash`, then `undefined`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`Ash`, then the program stops with an error",
              "tag": "reading past the end taken as an error",
              "correct": false
            },
            {
              "key": "D",
              "text": "`Ash`, then `Ash`",
              "tag": "an index past the end taken to stop at the last element",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `stops.length` is 4, and the indexes are 0 to 3. `stops.length - 1` is 3, the last index, which holds `\"Ash\"`. `stops[4]` is one past the end, and reading past the end evaluates to `undefined`, with no error.",
            "wrong": "The length counts the elements, 4 of them, but indexes start at 0, so the last index is one less than the length: `stops[3]` is `\"Ash\"`. `stops[stops.length]` is `stops[4]`, past the end. Reading there is not an error and does not stop at the last element: it evaluates to `undefined`. The answer is B."
          }
        },
        {
          "id": "q2",
          "skill": "An empty array is truthy",
          "prompt": "What does this print?",
          "code": "const waitlist = [];\nconst next = \"Next: \" + waitlist[0];\nconst message = waitlist ? next : \"No one waiting\";\nconsole.log(message);",
          "answers": [
            {
              "key": "A",
              "text": "`No one waiting`",
              "tag": "an empty array taken as falsy",
              "correct": false
            },
            {
              "key": "B",
              "text": "`Next: `",
              "tag": "undefined taken to join as nothing",
              "correct": false
            },
            {
              "key": "C",
              "text": "`Next: undefined`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "Nothing; `waitlist[0]` stops the program with an error",
              "tag": "reading an empty array taken as an error",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Every array is truthy, even an empty one, so the ternary operator picks the value after the `?`, `next`. `waitlist[0]` reads past the end of an empty array, which evaluates to `undefined`, and `+` joins it as the text `\"undefined\"`. The test for an empty array is `waitlist.length > 0`.",
            "wrong": "Start with the condition. `[]` is not on the falsy list, so it is truthy: an empty array still counts as true, unlike the empty string. The ternary operator picks `next`. On the line before, index 0 of an empty array is past the end, so it is `undefined`, with no error, and joining it to a string writes the word `undefined`. The answer is C."
          }
        },
        {
          "id": "q3",
          "skill": "What push and pop return",
          "prompt": "What do `added` and `taken` hold, and what is `stack.length`?",
          "code": "const stack = [4, 9];\nconst added = stack.push(6);\nconst taken = stack.pop();",
          "answers": [
            {
              "key": "A",
              "text": "`added` is `[4, 9, 6]`, `taken` is `6`, `stack.length` is 2",
              "tag": "push taken to return the array",
              "correct": false
            },
            {
              "key": "B",
              "text": "`added` is `3`, `taken` is `4`, `stack.length` is 2",
              "tag": "pop taken to remove the first element",
              "correct": false
            },
            {
              "key": "C",
              "text": "Line 2 stops with a TypeError, because `stack` is a `const`",
              "tag": "const taken to freeze the array",
              "correct": false
            },
            {
              "key": "D",
              "text": "`added` is `3`, `taken` is `6`, `stack.length` is 2",
              "tag": "correct",
              "correct": true
            }
          ],
          "explain": {
            "right": "Right. `push(6)` adds `6` to the end and returns the new length, 3, not the array. `pop()` removes the last element, the `6` just added, and returns it. That leaves `[4, 9]`, two elements.",
            "wrong": "Two methods, two different kinds of return value. `push` changes the array and returns its new length, so `added` is 3. `pop` removes the last element and returns that element, so `taken` is 6; `shift` is the one that takes the first. `const` does not stop either: it fixes which array `stack` names, not what is in it. The answer is D."
          }
        },
        {
          "id": "q4",
          "skill": "What splice returns",
          "prompt": "What do `removed` and `songs.length` hold?",
          "code": "const songs = [\"Ivy\", \"Juno\", \"Kite\", \"Lark\"];\nconst removed = songs.splice(2, 1);",
          "answers": [
            {
              "key": "A",
              "text": "`removed` is `\"Kite\"`, and `songs.length` is 3",
              "tag": "splice taken to return the element, not an array",
              "correct": false
            },
            {
              "key": "B",
              "text": "`removed` is `[\"Kite\"]`, and `songs.length` is 3",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`removed` is `[\"Ivy\", \"Juno\", \"Lark\"]`, and `songs.length` is 3",
              "tag": "splice taken to return the changed array",
              "correct": false
            },
            {
              "key": "D",
              "text": "`removed` is `[]`, and `songs.length` is 4",
              "tag": "the 1 read as an end index, as slice reads it",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `splice(2, 1)` starts at index 2 and removes one element, `\"Kite\"`, from `songs` itself. It always returns the removed elements as an array, even when there is only one, so `removed` is `[\"Kite\"]` and `removed[0]` is `\"Kite\"`.",
            "wrong": "`splice` takes the index to start at, then how many elements to remove: here, one element at index 2, `\"Kite\"`. The second number is a count, not an end index as it is for `slice`. `splice` returns what it removed, always as an array, and `songs` keeps the other three. The answer is B."
          }
        },
        {
          "id": "q5",
          "skill": "Writing past the end, and setting the length",
          "prompt": "What does this print?",
          "code": "const marks = [6, 2];\nmarks[4] = 9;\nconst grown = marks.length;\nmarks.length = 1;\nconsole.log(grown, marks[1]);",
          "answers": [
            {
              "key": "A",
              "text": "`3 undefined`",
              "tag": "the write taken to add at the next index, as push does",
              "correct": false
            },
            {
              "key": "B",
              "text": "`4 undefined`",
              "tag": "the highest index taken as the length",
              "correct": false
            },
            {
              "key": "C",
              "text": "`5 undefined`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "Nothing; line 2 stops with an error",
              "tag": "a fixed size, as in Python, C# or Java",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Writing at index 4 makes the array long enough to have an index 4, so the length is 5, with empty slots at indexes 2 and 3. Setting `.length` to 1 then cuts off everything after index 0, so index 1 is past the end: `undefined`.",
            "wrong": "A JavaScript array has no fixed size. `marks[4] = 9` stores `9` at index 4 itself, not at the next free index, and the length is one more than the highest index: 5. Indexes 2 and 3 are left as empty slots. `marks.length = 1` removes every element from index 1 on, so `marks[1]` reads past the end. The answer is C."
          }
        },
        {
          "id": "q6",
          "skill": "indexOf in a condition",
          "prompt": "What does `note` hold at the end?",
          "code": "const allergies = [\"nuts\", \"milk\", \"eggs\"];\nlet note = \"\";\nif (allergies.indexOf(\"nuts\")) {\n  note += \"N\";\n}\nif (allergies.indexOf(\"soy\")) {\n  note += \"S\";\n}\nif (allergies.includes(\"milk\")) {\n  note += \"M\";\n}",
          "answers": [
            {
              "key": "A",
              "text": "`\"NM\"`",
              "tag": "indexOf read as a yes-or-no answer",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"SM\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`\"M\"`",
              "tag": "-1 taken as falsy",
              "correct": false
            },
            {
              "key": "D",
              "text": "`\"NSM\"`",
              "tag": "every index taken as truthy",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `indexOf` returns a position, not a yes or no. `\"nuts\"` is at index 0, and `0` is falsy, so the first `if` skips its block although nuts are on the list. `\"soy\"` is missing, so `indexOf` returns `-1`, which is truthy. Only `includes` returns the `true` or `false` the question means.",
            "wrong": "Work out what each condition evaluates to. `allergies.indexOf(\"nuts\")` is `0`, the first index, and `0` is on the falsy list: no `\"N\"`. `allergies.indexOf(\"soy\")` is `-1`, \"not found\", and every number but 0 and `NaN` is truthy: `\"S\"` is added. `includes(\"milk\")` is `true`: `\"M\"`. The answer is B."
          }
        },
        {
          "id": "q7",
          "skill": "Does it change the array?",
          "prompt": "What do `trees.length` and `line` hold at the end?",
          "code": "const trees = [\"oak\", \"ash\", \"elm\", \"yew\"];\ntrees.slice(0, 2);\ntrees.concat([\"fir\"]);\nconst line = trees.join(\" \");",
          "answers": [
            {
              "key": "A",
              "text": "`4` and `\"oak ash elm yew\"`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`2` and `\"oak ash\"`",
              "tag": "slice taken to change the array",
              "correct": false
            },
            {
              "key": "C",
              "text": "`5` and `\"oak ash elm yew fir\"`",
              "tag": "concat taken to change the array",
              "correct": false
            },
            {
              "key": "D",
              "text": "`3` and `\"oak ash fir\"`",
              "tag": "both taken to change the array",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `slice`, `concat` and `join` are all non-mutating: each returns a new value and leaves `trees` as it was. Lines 2 and 3 made a new array each, and nothing kept them. Only `join`'s string was stored, and it is built from the four original trees.",
            "wrong": "Ask of each method whether it changes the array. `slice` returns a copy of part of the array, and `concat` returns a new, combined array; neither touches `trees`. Lines 2 and 3 do not store what they return, so their work is lost. `trees` still has four elements when `join` builds `line`. The answer is A."
          }
        },
        {
          "id": "q8",
          "skill": "Finding the first match",
          "prompt": "What does this print?",
          "code": "const temps = [14, 22, 9, 31, 18];\nlet hotDay = -1;\nfor (let i = 0; i < temps.length; i++) {\n  if (temps[i] > 20) {\n    hotDay = i;\n    break;\n  }\n}\nconsole.log(hotDay);",
          "answers": [
            {
              "key": "A",
              "text": "`22`",
              "tag": "the element taken for its position",
              "correct": false
            },
            {
              "key": "B",
              "text": "`3`",
              "tag": "break overlooked, so the last match is kept",
              "correct": false
            },
            {
              "key": "C",
              "text": "`1`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`2`",
              "tag": "positions counted from 1",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `hotDay` takes `i`, the position, not `temps[i]`, the reading. The first reading above 20 is `22`, at index 1, and `break` ends the loop there, so the `31` at index 3 is never reached.",
            "wrong": "Trace `i`: at 0 the reading is 14, not above 20; at 1 it is 22, so `hotDay` becomes `i`, which is 1. The line stores the position, not the reading, and positions count from 0. Then `break` ends the loop, so the later match, `31` at index 3, never replaces it. The answer is C."
          }
        },
        {
          "id": "q9",
          "skill": "Type coercion",
          "prompt": "What does `total` hold when the loop ends?",
          "code": "const parts = \"3,8,1\".split(\",\");\nlet total = 0;\nfor (const part of parts) {\n  total += part;\n}",
          "answers": [
            {
              "key": "A",
              "text": "`12`",
              "tag": "the pieces taken as numbers",
              "correct": false
            },
            {
              "key": "B",
              "text": "`\"381\"`",
              "tag": "the starting 0 taken to vanish",
              "correct": false
            },
            {
              "key": "C",
              "text": "`NaN`",
              "tag": "text added to a number taken as not a number",
              "correct": false
            },
            {
              "key": "D",
              "text": "`\"0381\"`",
              "tag": "correct",
              "correct": true
            }
          ],
          "explain": {
            "right": "Right. `split` always returns strings: `[\"3\", \"8\", \"1\"]`. On the first iteration, `0 + \"3\"` has a string on one side, so `+` joins text and gives `\"03\"`. From then on `total` is a string, and each piece is joined on.",
            "wrong": "The pieces `split` returns are strings, `\"3\"`, `\"8\"` and `\"1\"`, not numbers. When one side of `+` is a string, `+` joins text, so the first iteration turns the number 0 into the text `\"03\"`, and the next two give `\"038\"` and `\"0381\"`. `total += Number(part);` would add, and give 12. The answer is D."
          }
        },
        {
          "id": "q10",
          "skill": "Removing while walking forward",
          "prompt": "This loop is meant to remove every 0. What does `counts` hold when it ends, and how many iterations ran?",
          "code": "const counts = [0, 0, 5, 0, 7];\nfor (let i = 0; i < counts.length; i++) {\n  if (counts[i] === 0) {\n    counts.splice(i, 1);\n  }\n}",
          "answers": [
            {
              "key": "A",
              "text": "`[5, 7]`, after 5 iterations",
              "tag": "every element taken to be visited",
              "correct": false
            },
            {
              "key": "B",
              "text": "`[0, 5, 7]`, after 3 iterations",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`[0, 5, 7]`, after 5 iterations",
              "tag": "the length taken to be read once, at the start",
              "correct": false
            },
            {
              "key": "D",
              "text": "It stops with an error when the array gets shorter",
              "tag": "a check JavaScript does not make",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Removing the 0 at index 0 moves every later element down one, so the second 0 moves into index 0, which the loop has just passed, and is never read. The loop reads `i < counts.length` again at every check, and after two removals `3 < 3` is `false`.",
            "wrong": "Trace it. At `i` 0, the first 0 is removed, and the second 0 moves down into index 0. The update makes `i` 1, which now holds `5`, so the second 0 is skipped. At `i` 2, the 0 there is removed, leaving `[0, 5, 7]`. The condition reads the length as it is now, 3, so `3 < 3` ends the loop after three iterations. JavaScript reports nothing. Walking backwards, from `counts.length - 1` down to 0, removes every 0. The answer is B."
          }
        },
        {
          "id": "q11",
          "skill": "Row, then column",
          "prompt": "What does this print?",
          "code": "const shelves = [\n  [5, 8],\n  [2, 6],\n  [9, 4]\n];\nconsole.log(shelves[2][0], shelves.length, shelves[0].length);",
          "answers": [
            {
              "key": "A",
              "text": "`undefined 3 2`",
              "tag": "the column read first",
              "correct": false
            },
            {
              "key": "B",
              "text": "`9 2 3`",
              "tag": "the two lengths swapped",
              "correct": false
            },
            {
              "key": "C",
              "text": "`9 6 2`",
              "tag": "the outer length taken to count every element",
              "correct": false
            },
            {
              "key": "D",
              "text": "`9 3 2`",
              "tag": "correct",
              "correct": true
            }
          ],
          "explain": {
            "right": "Right. The first bracket picks the row: `shelves[2]` is `[9, 4]`, and index 0 of it is `9`. `shelves.length` counts the elements of the outer array, which are the three rows. `shelves[0].length` counts the elements of row 0: 2.",
            "wrong": "Read the brackets left to right. `shelves[2]` is the row `[9, 4]`, and `[0]` then reads index 0 of that row: `9`. Read column first, it would be `shelves[0][2]`, past the end of a two-element row. `shelves.length` counts the rows, 3, not the six numbers; `shelves[0].length` counts the elements in row 0, 2. The answer is D."
          }
        },
        {
          "id": "q12",
          "skill": "Which length counts what",
          "prompt": "This program is meant to count the elements that are not 0. What does it print?",
          "code": "const bookings = [\n  [4, 0],\n  [0, 7],\n  [2, 9]\n];\nlet used = 0;\nfor (let r = 0; r < bookings.length; r++) {\n  for (let c = 0; c < bookings.length; c++) {\n    if (bookings[r][c] !== 0) {\n      used++;\n    }\n  }\n}\nconsole.log(used);",
          "answers": [
            {
              "key": "A",
              "text": "`4`",
              "tag": "what it was meant to do",
              "correct": false
            },
            {
              "key": "B",
              "text": "`7`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "It stops with a TypeError",
              "tag": "past the end of a row taken as an error",
              "correct": false
            },
            {
              "key": "D",
              "text": "`16`",
              "tag": "the outer length taken to count every element",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The inner condition uses `bookings.length`, the number of rows, 3, but each row has only 2 elements. So every row is read at `c` 2 as well, one past its end, which gives `undefined`, and `undefined !== 0` is `true`. Three extra counts make the 4 real ones 7. The inner limit should be `bookings[r].length`.",
            "wrong": "Look at the inner condition: `c < bookings.length` compares `c` with the number of rows, 3, so `c` is 0, 1 and 2 in every row. Each row has two elements, so `bookings[r][2]` is past the end of the row: `undefined`, with no error, since the row itself exists. `undefined !== 0` is `true`, so each row counts one element too many: 4 + 3 is 7. The answer is B."
          }
        }
      ],
      "_source": "checks/check-arrays.yml"
    },
    "functions-1": {
      "id": "functions-1",
      "title": "Functions, pass 1 — declare, call, return",
      "kind": "read",
      "shape": "conceptual",
      "why": "Every later lesson writes and calls functions, so the syntax is re-entered here first. And a function that logs its result instead of returning it is the first silent `undefined` students write — answering versus acting names that mistake, and what a function can change through its arguments pays it off later.",
      "concepts": [
        "fn.side-effects"
      ],
      "lede": "A function does one of two jobs. It answers a question — returns a value to whoever called it — or it acts, and changes something outside itself. Seeing an answer in the console is not the same as the call returning one, and most early function bugs live in that gap.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "When it breaks: reading errors · What a function can change"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Return value** — the value a call produces for the code that made it. **Side effect** — anything a call changes outside itself: the console, the page, a variable the function did not create.",
          "entries": [
            "**Return value** — the value a call produces for the code that made it.",
            "**Side effect** — anything a call changes outside itself: the console, the page, a variable the function did not create."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four short programs. Say what the last line produces — type it, or pick where\nthere are buttons. A guess is fine; that is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "function twice(n) { console.log(n * 2); }   const x = twice(5);   x",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you answered `10` — you would see `10` in the console, and that is the trap.** `console.log` put `10` on the screen. It did not return anything to the line that called `twice`, so `x` got nothing: `undefined`.\n",
                          "why": "The function *acted*: it changed the console. It never *answered*, so the call produces `undefined`.\n"
                        },
                        {
                          "code": "function twice(n) { return n * 2; }   const x = twice(5);   x",
                          "accept": [
                            "10"
                          ],
                          "why": "`return` hands the value back to the caller. This is a function that answers — and nothing appears in the console, because nothing asked it to.\n"
                        },
                        {
                          "code": "function twice(n) { n * 2; }   const x = twice(5);   x",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you answered `10` — the body does multiply, and then discards the result.** A body is a list of instructions; the function returns a value only at a `return`.\n",
                          "why": "It worked out `10` and threw it away. No `return`, no answer: `undefined`. It does not act either — this one does nothing you can see.\n"
                        },
                        {
                          "code": "let count = 0;   function bump() { count = count + 1; }   bump();   bump();   count",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `0` — `bump` changes `count` itself, not a copy of it.** Each call adds one to the variable outside the function.\n",
                          "why": "`bump` returns nothing, yet something changed: `count`. That is a function that acts, and the useful thing to say about it is *what* it changes.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "def twice(n):\\n    n * 2\\n\\ntwice(5)",
                          "accept": [
                            "None"
                          ],
                          "why": "Same mistake, different spelling. Without `return`, the call returns `None`.\n"
                        },
                        {
                          "code": "def twice(n):\\n    return n * 2\\n\\ntwice(5)",
                          "accept": [
                            "10"
                          ],
                          "why": "`return` sends the value back.\n"
                        },
                        {
                          "code": "def greet(name):\\n    return \"hi \" + name\\n\\ngreet()",
                          "choices": [
                            "\"hi None\"",
                            "An error"
                          ],
                          "accept": [
                            "An error"
                          ],
                          "why": "Python **refuses** the call — `missing 1 required positional argument`. A real difference from JavaScript, which would have built `\"hi undefined\"` and carried on.\n"
                        },
                        {
                          "code": "def f():\\n    return 1\\n    print(\"after\")   — does a call print \"after\"?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Unreachable, same as JavaScript. Some linters will warn you about it.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "int Twice(int n) { n * 2; }  — does this compile?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Two errors: the expression has no effect, and not all code paths return a value. The compiler catches the mistake JavaScript lets you ship.\n"
                        },
                        {
                          "code": "int Twice(int n) { return n * 2; }  Twice(5)",
                          "accept": [
                            "10"
                          ],
                          "why": "The same idea, with the return type declared up front.\n"
                        },
                        {
                          "code": "string Greet(string name) => \"hi \" + name;  Greet()",
                          "choices": [
                            "\"hi \"",
                            "A compile error"
                          ],
                          "accept": [
                            "A compile error"
                          ],
                          "why": "No overload takes zero arguments. Checked before running, unlike both dynamic tracks.\n"
                        },
                        {
                          "code": "void Log(string m) { }  — what does it return?",
                          "choices": [
                            "null",
                            "Nothing"
                          ],
                          "accept": [
                            "Nothing"
                          ],
                          "why": "`void` states that there is no answer, so the compiler stops you using one. JavaScript expresses the same idea by returning `undefined` implicitly.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can write a function that takes values and returns an answer",
                "I can tell a function that answers from one that acts, and name what it changes",
                "I can say what a call produces when the function logs its result instead of returning it"
              ]
            }
          ]
        },
        {
          "h": "Three parts"
        },
        {
          "code": "function totalWithTax(price, rate) {   // 1. a name, and what it needs\n  const tax = price * rate;            // 2. the work\n  return price + tax;                  // 3. the answer\n}\n\ntotalWithTax(100, 0.065);   // 106.5",
          "lang": "js"
        },
        {
          "list": [
            "**The name** says what it does, from the caller's point of view. `totalWithTax` is better than `calc` because a reader of the call site learns something.",
            "**The parameters** — `price` and `rate` — are what it needs from the caller. The values you supply at the call, `100` and `0.065`, are the **arguments**.",
            "**The return** is the answer. It is the only way a value leaves the function."
          ]
        },
        {
          "h": "Answers and acts"
        },
        {
          "p": "Here is the same work, three ways, called the same way. Only the body differs."
        },
        {
          "code": "function twice(n) { return n * 2; }        // answers\nfunction twice(n) { console.log(n * 2); }  // acts — changes the console\nfunction twice(n) { n * 2; }               // neither",
          "lang": "js",
          "file": "three-bodies.js"
        },
        {
          "table": {
            "head": [
              "Body",
              "The console shows",
              "`twice(5)` produces",
              "`twice(5) + 1`"
            ],
            "rows": [
              [
                "`return n * 2;`",
                "nothing",
                "`10`",
                "`11`"
              ],
              [
                "`console.log(n * 2);`",
                "`10`",
                "`undefined`",
                "`NaN`"
              ],
              [
                "`n * 2;`",
                "nothing",
                "`undefined`",
                "`NaN`"
              ]
            ]
          }
        },
        {
          "p": "**The middle row is the one that fools people.** You can see `10`, so it feels\nas if the function produced `10`. It did not. It *printed* `10` — it changed\nthe console, which is an action — and then returned `undefined`, because\nnothing was returned. Code that tries to use the result gets `undefined`, and\nthe arithmetic quietly turns into `NaN` somewhere later. That is a silent\nfailure: nothing throws."
        },
        {
          "p": "Type `twice(5)` into the browser console with the logging version and you see\nboth halves at once: `10` from the `console.log`, then `undefined` — the\nconsole showing you what the call itself produced."
        },
        {
          "block": "notice",
          "label": "The test",
          "body": [
            {
              "p": "Ask what the call **returns**. A value → the function answers. `undefined`\n→ it answers nothing, so if it is worth calling, it must *change* something.\nSay what: the console, the page, a variable outside it."
            }
          ]
        },
        {
          "h": "Two kinds, two kinds of name"
        },
        {
          "p": "The split decides what a good name looks like."
        },
        {
          "code": "// ANSWERS a question — returns a value, changes nothing\nfunction totalWithTax(price, rate) { return price + price * rate; }\nfunction isEven(n) { return n % 2 === 0; }\n\n// ACTS — changes something, returns nothing\nlet count = 0;\nfunction showScore(score) { document.querySelector(\"#score\").textContent = score; }\nfunction bump() { count = count + 1; }",
          "lang": "js"
        },
        {
          "p": "Name one that answers for **what it gives you** — `totalWithTax`, `isEven`,\n`formatDate`. Name one that acts for **what it does** — `showScore`,\n`saveOrder`, `sendEmail`. And for one that acts, say what it changes: `showScore`\nchanges the page; `bump` changes `count`."
        },
        {
          "p": "A function that does both is usually two functions. That guideline gets sharper\nin the lesson on what gets shared, when \"changes something\" turns out to include\nthe data you passed in."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "function isAdult(age) { return age >= 18; }   — answers or acts?",
                "choices": [
                  "answers",
                  "acts"
                ],
                "accept": [
                  "answers"
                ],
                "why": "It returns `true` or `false` and changes nothing.\n"
              },
              {
                "code": "function logTotal(a, b) { console.log(a + b); }   — answers or acts?",
                "choices": [
                  "answers",
                  "acts"
                ],
                "accept": [
                  "acts"
                ],
                "miss": "**If you answered `answers` — it shows the total, but it does not return it.** `const t = logTotal(2, 3)` leaves `t` as `undefined`.\n",
                "why": "It changes the console and returns nothing. The name even says so: *log*.\n"
              },
              {
                "code": "function showScore(s) { document.querySelector(\"#score\").textContent = s; }   — what does it change? Name it.",
                "accept": [
                  "the page",
                  "page",
                  "the document",
                  "document",
                  "the score",
                  "the #score element",
                  "the element"
                ],
                "why": "The page — the text of the `#score` element. It returns nothing; its whole job is that change.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Writing a function from a requirement",
            "body": [
              {
                "h": "Writing a function from a requirement"
              },
              {
                "code": "// \"Work out the total including tax.\"\n\n// 1. What does the function return? Name it for that.\n//    a number, the total -> totalWithTax\n\n// 2. What does it need that it cannot work out itself?\n//    the price, and the rate -> two parameters\n\n// 3. What is the answer, and have you RETURNED it?\n//    return price + price * rate\n\n// 4. Does it also change something outside itself?\n//    yes -> that is a second function. Split it.",
                "lang": "js",
                "file": "how.js"
              },
              {
                "p": "Now do one. Fill in each line."
              },
              {
                "code": "// \"Tell me whether a password is long enough — at least 8 characters.\"\n\n// 1. name:        ______\n// 2. parameters:  ______\n// 3. return:      ______\n// 4. changes:     ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`isLongEnough` (or `isValidLength`), one parameter `password`,\n`return password.length >= 8`, and it changes nothing — it answers. Note the\nname begins with `is` — a strong convention for a function answering a yes/no\nquestion, and one that makes `if (isLongEnough(p))` read as a sentence."
              }
            ]
          }
        },
        {
          "video": {
            "title": "One function, written from scratch",
            "caption": "The tax requirement turned into a function line by line, then the same function with `console.log` in place of `return`, and what the caller sees each time. Watch it if the four opening answers were not all obvious.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **does anything tell you a function acts, before it runs?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "A function with no `return` returns",
                  "Using that result"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "`undefined`",
                    "allowed — it travels until something breaks"
                  ],
                  [
                    "**Python**",
                    "`None`",
                    "allowed — fails when the line that uses it runs"
                  ],
                  [
                    "**C#**",
                    "nothing: the signature says `void`",
                    "**compile error** — refused before it runs"
                  ]
                ]
              }
            },
            {
              "p": "In a dynamically typed language like JavaScript, whether a function answers or\nacts is written down nowhere but its name and its body. A statically typed\nlanguage writes it into the declaration — `void` means \"acts, answers nothing\"\n— so the compiler can stop you using an answer that does not exist. In\nJavaScript that check is yours: read the body for a `return`."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "`function` declarations for named work; arrow functions for short ones."
              },
              {
                "code": "function totalWithTax(price, rate) { return price + price * rate; }\nconst double = n => n * 2;              // one expression: returned automatically\nconst double2 = n => { n * 2; };        // braces: back to needing `return` — undefined\nconst double3 = n => { return n * 2; }; // braces and return: 10 again",
                "lang": "js"
              },
              {
                "p": "The middle line is the one-word bug again, wearing different syntax."
              }
            ],
            "python": [
              {
                "p": "`def` for everything; `lambda` exists but is limited to one expression and rarely\nthe right choice."
              },
              {
                "code": "def total_with_tax(price, rate):\n    return price * (1 + rate)",
                "lang": "python"
              },
              {
                "p": "Python checks arity at the call, so a missing argument raises immediately rather\nthan becoming `None` and travelling. That is a genuine advantage of the model."
              }
            ],
            "csharp": [
              {
                "p": "Return type declared up front, and the compiler enforces that every path returns."
              },
              {
                "code": "decimal TotalWithTax(decimal price, decimal rate) => price * (1 + rate);\nvoid SaveOrder(Order o) { }    // void states there is no answer",
                "lang": "csharp"
              },
              {
                "p": "`void` versus a return type makes the answers/acts distinction part of the\nsignature rather than a naming convention."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the first one, say what\nwould have to change for `x` to hold `10` — and what the console would show\nafter that change."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You are writing `checkout(items)`. It needs to total the items, apply tax, save\nthe order, and email a receipt."
            },
            {
              "p": "Two designs:"
            },
            {
              "list": [
                "**One function** that does all four.",
                "**Four functions**, with `checkout` calling them in order."
              ]
            },
            {
              "p": "Pick one and say why. Then, for the second, say which of the four *answer* a\nquestion and which *act* — for each one that acts, name what it changes — and\nname each accordingly. Finally, say which of the four you could test without a\ndatabase or an email server, and why that is an argument for the split rather\nthan a coincidence."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A function **answers** — returns a value — or **acts**: changes something outside itself. For one that acts, name what it changes.",
                "**A function returns a value only through `return`.** Without it the caller gets `undefined`.",
                "**Logging is acting, not answering.** You see the value in the console; the caller still gets `undefined`, and it fails silently later.",
                "Name a function that answers for what it gives you; name one that acts for what it does. Doing both usually means two functions."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Functions",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Functions",
              "note": "declaring, calling and returning."
            },
            {
              "title": "return",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/return",
              "note": "the statement that returns a value, and exits the function as it does."
            },
            {
              "title": "Arrow functions",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Arrow_functions",
              "note": "implicit return, and when braces change it."
            }
          ]
        }
      ],
      "_source": "lessons/functions-1.md",
      "practice": {
        "id": "functions-1",
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the return",
            "prompt": "Type what the last expression produces. The third is the first step in writing one.",
            "items": [
              {
                "id": "p1",
                "code": "function half(n) { console.log(n / 2); }   const h = half(8);   h",
                "accept": [
                  "undefined"
                ],
                "miss": "**If you answered `4` — you would see `4` in the console, and that is the trap.** `console.log` put it on the screen; it did not return it to the line that called `half`.\n",
                "why": "`undefined`. The function *acted* — it changed the console — and never *answered*."
              },
              {
                "id": "p2",
                "code": "function half(n) { return n / 2; }   half(8) + 1",
                "accept": [
                  "5"
                ],
                "why": "`5`. `half(8)` returns `4` to the caller, so the call can be used in an expression."
              },
              {
                "id": "p3",
                "question": "You need `area(w, h)` so that `area(2, 3) * 2` works. Which keyword must its body use? Type the keyword.",
                "accept": [
                  "return",
                  "`return`",
                  "return statement"
                ],
                "miss": "**If you answered `console.log` — that shows the area; it does not return it.** `area(2, 3) * 2` would be `undefined * 2`.\n",
                "why": "`return` — the only way a value leaves a function for its caller."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "function twice(n) { n * 2; }   twice(5)",
                "accept": [
                  "undefined"
                ],
                "why": "The body worked out 10 and threw it away. No `return`, no answer."
              },
              {
                "id": "p5",
                "code": "const double = n => { n * 2; };   double(5)",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined`. Braces turn an arrow back into a body, and a body needs `return`. Without braces — `n => n * 2` — it would be 10."
              },
              {
                "id": "p6",
                "code": "let seen = 0;   function see() { seen = seen + 1; }   see(); see(); see();   seen",
                "accept": [
                  "3"
                ],
                "why": "`3`. `see` returns nothing, yet something changed: `seen`. That is a function that acts, and the useful thing to say about it is *what* it changes."
              }
            ],
            "hints": [
              {
                "when": "you answered with the number the function logs",
                "say": "The console shows it, but the console is not the caller. Look for a `return` — without one, the call itself produces `undefined`."
              },
              {
                "when": "you answered console.log for area",
                "say": "Ask what the call *returns*. Printing changes the console; it hands nothing to `* 2`."
              }
            ]
          },
          {
            "kind": "run",
            "title": "What you see, and what you get",
            "prompt": "Predict each line's output — type what it prints, or pick what happens — then run it.",
            "filename": "average.js",
            "code": "function average(a, b) {\n  console.log((a + b) / 2);\n}\n\nconst avg = average(6, 10);\nconsole.log(\"avg is \" + avg);\nconsole.log(\"plus one: \" + (avg + 1));\nconsole.log(avg.toFixed(1));\n",
            "items": [
              {
                "id": "r1",
                "question": "What is printed first?",
                "accept": [
                  "8"
                ],
                "why": "`8` — printed from *inside* `average`. That is the function acting."
              },
              {
                "id": "r2",
                "code": "console.log(\"avg is \" + avg)",
                "accept": [
                  "avg is undefined"
                ],
                "miss": "**If you answered `avg is 8` — you saw 8 on the line above.** Seeing it is not getting it: `average` returned nothing.\n",
                "why": "`avg is undefined`. The 8 went to the console, not to `avg`."
              },
              {
                "id": "r3",
                "code": "console.log(\"plus one: \" + (avg + 1))",
                "accept": [
                  "plus one: nan",
                  "plus one:nan"
                ],
                "why": "`plus one: NaN`. `undefined + 1` is `NaN` — no error, just a wrong value travelling on. A silent failure."
              },
              {
                "id": "r4",
                "question": "What happens at `console.log(avg.toFixed(1))`?",
                "choices": [
                  "It prints undefined",
                  "It prints NaN",
                  "It throws an error"
                ],
                "accept": [
                  "It throws an error"
                ],
                "why": "A TypeError — reading a property of `undefined`. The error appears three lines after the cause, which is in `average` itself: it logs instead of returning."
              }
            ],
            "hints": [
              {
                "when": "you expected avg to be 8",
                "say": "Find the `return` in `average`. There is none — so what does the call return?"
              },
              {
                "when": "you expected the NaN line to throw",
                "say": "Arithmetic on `undefined` is allowed; it gives `NaN`. Only asking `undefined` for a property throws."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Answers or acts",
            "prompt": "Where there are buttons, pick **answers** or **acts**. Where there is a box, type the name of what the function changes.",
            "items": [
              {
                "id": "t1",
                "code": "function totalWithTax(price, rate) { return price + price * rate; }",
                "choices": [
                  "answers",
                  "acts"
                ],
                "accept": [
                  "answers"
                ],
                "why": "It returns a value and changes nothing. Name it for what it gives you."
              },
              {
                "id": "t2",
                "code": "function saveOrder(order) { database.write(order); }",
                "choices": [
                  "answers",
                  "acts"
                ],
                "accept": [
                  "acts"
                ],
                "why": "It changes the database and returns nothing. Name it for what it does, and say what it changes."
              },
              {
                "id": "t3",
                "code": "let lives = 3;   function loseLife() { lives = lives - 1; }   — what does loseLife change?",
                "accept": [
                  "lives",
                  "the lives variable",
                  "`lives`",
                  "the variable lives"
                ],
                "why": "`lives` — a variable it did not create. It returns nothing; that change is its whole job."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "function welcome(name) { return \"Hi, \" + name; console.log(\"welcomed\"); }   — answers or acts?",
                "choices": [
                  "answers",
                  "acts"
                ],
                "accept": [
                  "answers"
                ],
                "why": "Answers. `return` returns the value *and ends the function*, so the `console.log` after it never runs."
              },
              {
                "id": "t5",
                "code": "function setTitle(text) { document.title = text; }   — what does it change?",
                "accept": [
                  "the page",
                  "page",
                  "the title",
                  "the page title",
                  "document.title",
                  "the document",
                  "the tab title"
                ],
                "why": "The page — its title. It returns nothing."
              },
              {
                "id": "t6",
                "question": "A function saves an order **and** returns the total. What should you do?",
                "choices": [
                  "Keep it as one function",
                  "Split it into two"
                ],
                "accept": [
                  "Split it into two"
                ],
                "why": "Doing both is usually two functions. The split also makes the answering half testable without a database."
              }
            ],
            "hints": [
              {
                "when": "you called saveOrder an answer",
                "say": "Check what it returns. Nothing — so a caller cannot use it in an expression, only run it for its effect."
              },
              {
                "when": "you said loseLife changes nothing",
                "say": "It returns nothing, which is a different claim. Look for a name on the left of an `=` that the function did not create."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "From acting to answering",
            "prompt": "`ticketPrice` works — it prints the price of `qty` tickets at `each` dollars. Change it to **answer** instead: return the price and print nothing, so `ticketPrice(2, 15) + 5` is `35`. Once it passes: in one sentence, what did the caller get from the old version?\n",
            "exportName": "ticketPrice",
            "filename": "tickets.js",
            "starter": "// Works today: prints the price.\nfunction ticketPrice(qty, each) {\n  console.log(qty * each);\n}\n",
            "tests": [
              {
                "label": "returns the price",
                "body": "const realLog = console.log; console.log = function () {}; let r; try { r = subject(2, 15); } finally { console.log = realLog; } return r === 30;"
              },
              {
                "label": "the answer can be used in an expression",
                "body": "const realLog = console.log; console.log = function () {}; let r; try { r = subject(2, 15) + 5; } finally { console.log = realLog; } return r === 35;"
              },
              {
                "label": "no tickets costs 0 — still an answer",
                "body": "const realLog = console.log; console.log = function () {}; let r; try { r = subject(0, 15); } finally { console.log = realLog; } return r === 0;"
              },
              {
                "label": "prints nothing — it answers, it does not act",
                "body": "const realLog = console.log; let printed = 0; console.log = function () { printed++; }; try { subject(2, 15); } finally { console.log = realLog; } return printed === 0;"
              }
            ],
            "hints": [
              {
                "when": "returns the price fails",
                "say": "Seeing 30 in the console is not returning it. What is the one keyword that makes a function return a value?"
              },
              {
                "when": "prints nothing fails",
                "say": "The old line is still there. A function that answers should not also print — the caller decides what to do with the answer."
              },
              {
                "when": "you wrote return console.log(...)",
                "say": "`console.log` itself answers nothing: it returns `undefined`. Return the price, not what printing it returned."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Write four small functions",
            "prompt": "Write `checkout` exporting four functions — `subtotal(items)`, `withTax(amount, rate)`, `isValid(items)` and `describe(items, rate)`. Three answer questions; `describe` uses the others to build a string. Once it passes: in one sentence, why is `describe` easy to test without a console?\n",
            "exportName": "checkout",
            "filename": "checkout.js",
            "starter": "// subtotal(items) -> sum of item.price\n// withTax(amount, rate) -> amount plus amount * rate\n// isValid(items) -> true when there is at least one item\n// describe(items, rate) -> \"N items, total T\" using the others\nconst checkout = {\n\n};\n",
            "tests": [
              {
                "label": "subtotal adds the prices",
                "body": "return subject.subtotal([{price: 2}, {price: 3}]) === 5;"
              },
              {
                "label": "an empty list has a subtotal of zero",
                "body": "return subject.subtotal([]) === 0;"
              },
              {
                "label": "withTax applies the rate",
                "body": "return Math.abs(subject.withTax(100, 0.1) - 110) < 1e-9;"
              },
              {
                "label": "a zero rate changes nothing",
                "body": "return subject.withTax(100, 0) === 100;"
              },
              {
                "label": "isValid is false for an empty list",
                "body": "return subject.isValid([]) === false;"
              },
              {
                "label": "isValid is true when there is an item",
                "body": "return subject.isValid([{price: 1}]) === true;"
              },
              {
                "label": "describe builds the sentence",
                "body": "const m = /^2 items, total ([0-9.]+)$/.exec(subject.describe([{price: 50}, {price: 50}], 0.1)); return !!m && Math.abs(Number(m[1]) - 110) < 1e-9;"
              },
              {
                "label": "every one of the four returns something",
                "body": "return subject.subtotal([]) !== undefined && subject.withTax(1, 0) !== undefined &&\n       subject.isValid([]) !== undefined && subject.describe([], 0) !== undefined;"
              }
            ],
            "hints": [
              {
                "when": "a function returns undefined",
                "say": "Check every body for a `return`. A body that computes a value — or logs it — without returning it returns `undefined`, and nothing warns you."
              },
              {
                "when": "isValid returns the length instead of a boolean",
                "say": "The test uses `===` against `true` and `false`, so a truthy number is not enough. Compare, do not just return the count."
              },
              {
                "when": "describe recalculates the total itself",
                "say": "It should call the other two. That is the point of splitting them — one place to change when the tax rule changes."
              },
              {
                "when": "describe says \"total NaN\"",
                "say": "One of the functions `describe` calls is returning `undefined`, and arithmetic on it gives `NaN`. Call each one on its own and see which."
              }
            ]
          }
        ]
      }
    },
    "params-arity": {
      "id": "params-arity",
      "title": "Parameters, arity and defaults",
      "kind": "read",
      "outcome": 4,
      "shape": "conceptual",
      "why": "Parameters are the names a call creates, so outcome 4's question — what a name refers to and when it exists — has to be answered for them too. And JavaScript never checks a call against its declaration: a missing argument arrives as an ordinary `undefined` and fails somewhere else, far from its cause. That `undefined` is also exactly when a default fires, so defaults are taught here, once, and recalled wherever a later syntax declares one (D-032).",
      "concepts": [
        "scope.parameters",
        "scope.arity"
      ],
      "lede": "A parameter is a binding the call creates. What happens when the caller supplies too few, too many, or the wrong kind is decided entirely by the language — and JavaScript's answer is more permissive than almost any other.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What a declaration creates · Rest parameters and spread"
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "One function, four calls. Type what it returns each time."
            },
            {
              "code": "function greet(name, greeting = \"hello\") {\n  return greeting + \", \" + name;\n}",
              "lang": "js"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "greet(\"ada\")",
                          "accept": [
                            "\"hello, ada\"",
                            "hello, ada",
                            "'hello, ada'"
                          ],
                          "why": "The default fired because `greeting` was missing — which is to say, it was `undefined`.\n"
                        },
                        {
                          "code": "greet(\"ada\", undefined)",
                          "accept": [
                            "\"hello, ada\"",
                            "hello, ada",
                            "'hello, ada'"
                          ],
                          "miss": "**If this differed from your answer for `greet(\"ada\")`, note that it should not.** A default fires on `undefined`, whether the caller omitted the argument or passed `undefined` on purpose.\n",
                          "why": "Identical. A default fires on `undefined`, whether the caller omitted the argument or passed `undefined` explicitly.\n"
                        },
                        {
                          "code": "greet(\"ada\", null)",
                          "accept": [
                            "\"null, ada\"",
                            "null, ada",
                            "'null, ada'"
                          ],
                          "miss": "**If you expected the default, you said there was `null` — a value meaning \"deliberately empty\".** Only `undefined` means \"not supplied\". This is the same distinction `??` draws, and it is worth learning once for both.\n",
                          "why": "`null` is a **value**, so the default does not fire. This is the same `null`/`undefined` split as `??`, and it catches people the same way.\n"
                        },
                        {
                          "code": "greet(\"ada\", \"hi\", \"extra\")",
                          "accept": [
                            "\"hi, ada\"",
                            "hi, ada",
                            "'hi, ada'"
                          ],
                          "miss": "**If you expected an error — in Python and C#, yes.** In JavaScript extra arguments are collected and discarded, and you get no signal at all. Arity exists here; nothing enforces it.\n",
                          "why": "The extra argument is silently discarded. No error, no warning, ever.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "greet(\"ada\")",
                          "accept": [
                            "\"hello, ada\"",
                            "hello, ada",
                            "'hello, ada'"
                          ],
                          "why": "The default applies when the argument is omitted.\n"
                        },
                        {
                          "code": "greet(\"ada\", None)",
                          "accept": [
                            "\"None, ada\"",
                            "None, ada",
                            "'None, ada'"
                          ],
                          "why": "`None` is a value, so it is used. Python has no `undefined`, so \"omitted\" and \"explicitly nothing\" are genuinely different things here.\n"
                        },
                        {
                          "code": "greet(\"ada\", \"hi\", \"extra\")",
                          "accept": [
                            "error",
                            "typeerror",
                            "TypeError"
                          ],
                          "why": "`TypeError: greet() takes from 1 to 2 positional arguments but 3 were given`. Python checks arity and refuses. JavaScript does not.\n"
                        },
                        {
                          "code": "greet(greeting=\"hi\", name=\"ada\")",
                          "accept": [
                            "\"hi, ada\"",
                            "hi, ada",
                            "'hi, ada'"
                          ],
                          "why": "Keyword arguments, in any order. JavaScript has no equivalent — an options object is how the same readability is bought.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "Greet(\"ada\")",
                          "accept": [
                            "\"hello, ada\"",
                            "hello, ada",
                            "'hello, ada'"
                          ],
                          "why": "The optional parameter's default applies.\n"
                        },
                        {
                          "code": "Greet(\"ada\", null)",
                          "accept": [
                            "\", ada\"",
                            ", ada"
                          ],
                          "why": "`null` is passed through — the default does not fire — and C# joins a `null` string as if it were empty.\n"
                        },
                        {
                          "code": "Greet(\"ada\", \"hi\", \"extra\")",
                          "accept": [
                            "error",
                            "compile error",
                            "does not compile"
                          ],
                          "why": "No overload takes three arguments, and the compiler says so before the program runs — one step earlier than Python's runtime check.\n"
                        },
                        {
                          "code": "Greet(greeting: \"hi\", name: \"ada\")",
                          "accept": [
                            "\"hi, ada\"",
                            "hi, ada",
                            "'hi, ada'"
                          ],
                          "why": "Named arguments, like Python's. Both languages decided readability at the call site was worth a syntax.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say when a parameter starts existing and what it is bound to",
                "I can predict what a function sees when given too few or too many arguments",
                "I can give a missing argument a default, and say when that default fires and when it does not"
              ]
            }
          ]
        },
        {
          "h": "A parameter is a binding the call creates"
        },
        {
          "p": "Each call makes a **fresh** set of bindings, initialised from the arguments. They\nlive in the function body's scope and disappear when the call ends — unless a\nclosure keeps them."
        },
        {
          "code": "function scale(factor) {\n  factor = factor * 2;   // a local binding; the caller sees nothing\n  return factor;\n}\n\nconst f = 3;\nscale(f);   // 6\nf;          // 3 — reassigning a parameter never reaches the caller",
          "lang": "js"
        },
        {
          "p": "That is the same rule as for shared data: a function can change **what you pointed at**,\nnever **where you point**. A parameter is exactly \"where the function points.\""
        },
        {
          "h": "Too few, too many"
        },
        {
          "p": "JavaScript checks neither."
        },
        {
          "code": "function three(a, b, c) { return [a, b, c]; }\n\nthree(1);              // [1, undefined, undefined]\nthree(1, 2, 3, 4);     // [1, 2, 3] — the fourth is discarded\nthree.length;          // 3",
          "lang": "js"
        },
        {
          "p": "Missing parameters are `undefined`. That is not an error state — it is an\nordinary value, which then flows onward and fails somewhere else entirely. A\n`NaN` three functions away usually began as a missing argument here."
        },
        {
          "block": "notice",
          "label": "What `fn.length` counts",
          "body": [
            {
              "p": "Parameters **before** the first one with a default or a rest. So\n`function f(a, b = 1, c) {}` has a `length` of `1`, not `3`. It is the count of\n*required positional* parameters, and libraries use it — some test runners decide\nwhether your callback is async by whether it declared a `done` parameter."
            }
          ]
        },
        {
          "h": "Defaults fire on `undefined` only"
        },
        {
          "code": "function page(size = 10) { return size; }\n\npage();             // 10\npage(undefined);    // 10  — same thing\npage(null);         // null\npage(0);            // 0   — a real value, kept",
          "lang": "js"
        },
        {
          "p": "A default is evaluated **at call time**, not once at definition, and it can use\nthe parameters to its left — they are already bound:"
        },
        {
          "code": "function span(start, end = start + 1) { return [start, end]; }\nspan(4);      // [4, 5] — end defaulted, using start\nspan(4, 9);   // [4, 9]",
          "lang": "js"
        },
        {
          "p": "Evaluating at call time is also why\nJavaScript does not have Python's mutable-default trap:"
        },
        {
          "code": "function addTo(item, list = []) { list.push(item); return list; }\naddTo(1);   // [1]\naddTo(2);   // [2]  — a fresh array each call",
          "lang": "js"
        },
        {
          "p": "In Python the same shape accumulates, because the default object is created once\nwhen the function is defined. Worth knowing precisely because the two languages\nlook identical here and behave differently."
        },
        {
          "scaffold": {
            "label": "Working out what a call binds",
            "body": [
              {
                "h": "Working out what a call binds"
              },
              {
                "code": "// For each parameter, left to right:\n\n// 1. Was an argument supplied at this position?\n//    no  -> the value is `undefined`. Go to 3.\n//    yes -> the value is that argument. Go to 2.\n\n// 2. Is that value exactly `undefined`?\n//    yes -> treat as not supplied. Go to 3.\n//    no  -> bind it. Done. (null, 0, \"\" and false all land here.)\n\n// 3. Does this parameter declare a default?\n//    yes -> evaluate the default NOW and bind it.\n//    no  -> bind `undefined`.",
                "lang": "js",
                "file": "binding.js"
              },
              {
                "p": "Now apply it. Fill in the last column."
              },
              {
                "code": "function slot(a, b = \"B\", c) { return [a, b, c]; }\n\nslot(1);                 // [1, \"B\", undefined]\nslot(1, undefined, 3);   // ______\nslot(1, null, 3);        // ______\nslot.length;             // ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "Answers: `[1, \"B\", 3]`, `[1, null, 3]`, and `1`. The last one is the surprise —\n`length` stops counting at the first default, even though `c` has none."
              }
            ]
          }
        },
        {
          "video": {
            "title": "One call, four bindings",
            "caption": "The binding procedure run against each of the opening four calls, with the parameter slots drawn as they fill. Watch it if the undefined-versus-null split did not land.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **does the language check that the call matches the declaration?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Too few",
                  "Too many",
                  "Named arguments"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "`undefined`, silently",
                    "discarded, silently",
                    "no — use an options object"
                  ],
                  [
                    "**Python**",
                    "`TypeError` at the call",
                    "`TypeError` at the call",
                    "yes"
                  ],
                  [
                    "**C#**",
                    "compile error",
                    "compile error",
                    "yes"
                  ]
                ]
              }
            },
            {
              "p": "Three positions on one axis, and the same axis as type checking: **when do you\nfind out?** C# before running, Python when the line executes, JavaScript never."
            },
            {
              "p": "The consequence for JavaScript is that arity mistakes surface far from their\ncause, as an `undefined` that travels. That is an argument for small functions\nwith few parameters, and for an options object once there are more than about\nthree — a named property is checkable in a way a position is not."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Default what can be defaulted, and take an object once the list grows."
              },
              {
                "code": "function send(to, { retries = 3, timeout = 1000 } = {}) { }\n\nsend(\"ada\");                        // both defaults\nsend(\"ada\", { timeout: 50 });       // named, order-free, self-documenting",
                "lang": "js"
              },
              {
                "p": "The `= {}` on the destructured parameter matters — without it, calling `send(\"ada\")`\nthrows, because you cannot destructure `undefined`."
              }
            ],
            "python": [
              {
                "p": "Keyword arguments do this natively, and `*args` / `**kwargs` collect the rest."
              },
              {
                "code": "def send(to, *, retries=3, timeout=1000): ...\nsend(\"ada\", timeout=50)",
                "lang": "python"
              },
              {
                "p": "The bare `*` forces everything after it to be keyword-only — a readability tool\nJavaScript has no equivalent for. And remember the mutable-default trap: write\n`def f(x, items=None)` and build the list inside."
              }
            ],
            "csharp": [
              {
                "p": "Optional parameters and named arguments, both checked at compile time."
              },
              {
                "code": "void Send(string to, int retries = 3, int timeout = 1000) { }\nSend(\"ada\", timeout: 50);",
                "lang": "csharp"
              },
              {
                "p": "Overloading is the other route, and it is the one to prefer when the parameter\nlists differ in *meaning* rather than just in how many were supplied."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say what `greet.length` is for\nthat function, and why the answer is not `2`."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A function in your codebase takes six positional parameters and three of them are\nbooleans. Call sites read `render(user, true, false, true, null, 2)`."
            },
            {
              "p": "Two directions:"
            },
            {
              "list": [
                "**An options object** — one parameter, named properties, defaults inside.",
                "**Split the function** — if three booleans select behaviour, there may be three functions hiding in there."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what the first does to every\nexisting call site and whether the compiler-free checking bothers you, and what\nthe second does if the booleans are not independent. Name which you would reach\nfor if the booleans turned out to be mutually exclusive."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A parameter is a **fresh binding per call**. Reassigning one never reaches the caller.",
                "Too few arguments gives `undefined`; too many are **discarded**. JavaScript reports neither.",
                "A default fires on **`undefined` only** — not on `null`, `0`, `\"\"` or `false`.",
                "Defaults are evaluated **at call time**, which is why JavaScript has no mutable-default trap and Python does.",
                "`fn.length` counts parameters **before the first default or rest**."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Default parameters",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Default_parameters",
              "note": "note that they are evaluated at call time, which is the Python contrast."
            },
            {
              "title": "Function.prototype.length",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/length",
              "note": "exactly what it stops counting at."
            },
            {
              "title": "arguments",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/arguments",
              "note": "the old way of seeing extra arguments, and why rest parameters replaced it."
            }
          ]
        }
      ],
      "_source": "lessons/params-arity.md",
      "practice": {
        "id": "params-arity",
        "outcome": 4,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict what the function sees",
            "prompt": "Type what each produces. The third asks for a single value.\n",
            "items": [
              {
                "id": "p1",
                "code": "function three(a, b, c) { return [a, b, c]; }  three(1, 2)",
                "accept": [
                  "[1, 2, undefined]",
                  "[1,2,undefined]",
                  "1, 2, undefined",
                  "1,2,undefined"
                ],
                "why": "Too few arguments is not an error. The parameter with nothing at its position is bound to `undefined`, and the call goes ahead."
              },
              {
                "id": "p2",
                "code": "function bump(n) { n = n + 1; return n; }  let k = 1; bump(k);  →  k",
                "accept": [
                  "1"
                ],
                "why": "The call made a fresh binding `n`, initialised from `k`'s value. Reassigning `n` changes that binding only; the caller's `k` is untouched."
              },
              {
                "id": "p0",
                "question": "Working out what a call binds: an argument *was* supplied at a position. Which one value still counts as \"not supplied\"?",
                "accept": [
                  "undefined",
                  "`undefined`"
                ],
                "why": "That is the second question of the procedure. Only `undefined` counts as missing — `null`, `0`, `\"\"` and `false` are values the caller chose, and they are bound as they are."
              }
            ],
            "extra": [
              {
                "id": "p3",
                "code": "function greet(name, greeting = \"hello\") { return greeting + \", \" + name; }  greet(\"ada\", null)",
                "accept": [
                  "\"null, ada\"",
                  "null, ada",
                  "'null, ada'"
                ],
                "why": "`null` is a value, so the default does not fire. Same split as `??`."
              },
              {
                "id": "p4",
                "code": "greet(\"ada\", \"hi\", \"extra\")",
                "accept": [
                  "\"hi, ada\"",
                  "hi, ada",
                  "'hi, ada'"
                ],
                "why": "The extra argument is discarded silently. JavaScript never checks arity."
              },
              {
                "id": "p5",
                "code": "greet.length",
                "accept": [
                  "1"
                ],
                "why": "`length` counts parameters before the first default, so `greeting` is not counted."
              }
            ],
            "hints": [
              {
                "when": "you expected an error for the first",
                "say": "That is Python and C#. JavaScript binds `undefined` and continues, which is why the failure shows up somewhere else."
              },
              {
                "when": "you answered 2 for the second",
                "say": "A parameter is a new binding the call creates. Ask which binding `n = n + 1` changes — the function's, or the caller's?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict five lines, then run them",
            "prompt": "One function, called three ways. Predict every line it prints — type each line exactly as it would appear — then run it.\n",
            "code": "function stamp(label, count) {\n  count = count + 1;\n  console.log(label + \" \" + count);\n}\n\nlet n = 5;\nstamp(\"first\", n);\nconsole.log(n);\nstamp(\"second\");\nstamp(\"third\", 1, 99);\nconsole.log(stamp.length);\n",
            "items": [
              {
                "id": "r1",
                "question": "Line 1 printed",
                "accept": [
                  "first 6",
                  "\"first 6\""
                ],
                "why": "`count` is bound to `5` and the function adds one to its own binding."
              },
              {
                "id": "r2",
                "question": "Line 2 printed",
                "accept": [
                  "5"
                ],
                "why": "Reassigning the parameter never reaches the caller — `n` and `count` are two bindings."
              },
              {
                "id": "r3",
                "question": "Line 3 printed",
                "accept": [
                  "second NaN",
                  "\"second NaN\""
                ],
                "why": "`count` was missing, so it is `undefined`; `undefined + 1` is `NaN`. No error — the `NaN` just travels on."
              },
              {
                "id": "r4",
                "question": "Line 4 printed",
                "accept": [
                  "third 2",
                  "\"third 2\""
                ],
                "why": "The `99` had no parameter to go to and was discarded, silently."
              },
              {
                "id": "r5",
                "question": "Line 5 printed",
                "accept": [
                  "2"
                ],
                "why": "`length` counts the declared parameters — two — whatever any call supplied."
              }
            ],
            "hints": [
              {
                "when": "you expected line 3 to throw",
                "say": "Nothing checks the count. Work out what `count` holds, then what `+ 1` does to that value."
              },
              {
                "when": "you answered 6 for line 2",
                "say": "Two names, two bindings: `n` in the caller, `count` in the call. Which one did the function reassign?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace what the call binds",
            "prompt": "`function slot(a, b = \"B\", c) { return [a, b, c]; }`. Give what each produces.",
            "items": [
              {
                "id": "t1",
                "code": "slot(1)",
                "accept": [
                  "[1, \"B\", undefined]",
                  "[1,\"B\",undefined]",
                  "1, B, undefined"
                ],
                "why": "`a` bound, `b` defaulted, `c` missing and therefore `undefined` — an ordinary value that will travel."
              },
              {
                "id": "t2",
                "code": "slot(1, null, 3)",
                "accept": [
                  "[1, null, 3]",
                  "[1,null,3]",
                  "1, null, 3"
                ],
                "why": "`null` was supplied, so the default was skipped and `null` bound."
              }
            ],
            "extra": [
              {
                "id": "t3",
                "code": "slot.length",
                "accept": [
                  "1"
                ],
                "why": "Counting stops at the first default, even though `c` declares none after it."
              },
              {
                "id": "t4",
                "code": "function clear(list) { list = []; }  const xs = [1, 2]; clear(xs);  →  xs.length",
                "accept": [
                  "2"
                ],
                "why": "`list = []` rebinds the parameter to a new array. The caller's `xs` still points at the old one."
              },
              {
                "id": "t5",
                "code": "function add(list) { list.push(0); }  const ys = [1]; add(ys);  →  ys.length",
                "accept": [
                  "2"
                ],
                "why": "This time nothing was rebound: `list` and `ys` point at the same array, and `push` changed that array. A function can change what you pointed at, never where you point."
              }
            ],
            "hints": [
              {
                "when": "you expected a TypeError for the missing argument",
                "say": "That is Python and C#. JavaScript binds `undefined` and continues, which is why the failure shows up somewhere else."
              },
              {
                "when": "the two extras about lists got the same answer from you for different reasons",
                "say": "Ask of each line: does it make the parameter point somewhere new, or change the thing it already points at?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Fill in what a missing argument means",
            "prompt": "`area(width, height)` should treat a call with one argument as a square. Fill the gap in the parameter list so a missing `height` takes the width. A height of `0` is a real height. Then, in one sentence: what would `height` be bound to without your default, when the caller leaves it out?\n",
            "exportName": "area",
            "filename": "area.js",
            "starter": "// area(3, 4) is 12. area(3) is a square: 9.\nfunction area(width, height /* ___ a missing height takes the width ___ */) {\n  return width * height;\n}\n",
            "tests": [
              {
                "label": "two arguments: a rectangle",
                "body": "return subject(3, 4) === 12;"
              },
              {
                "label": "one argument: a square",
                "body": "return subject(3) === 9;"
              },
              {
                "label": "an explicit undefined is the same as leaving it out",
                "body": "return subject(5, undefined) === 25;"
              },
              {
                "label": "a height of 0 is kept",
                "body": "return subject(3, 0) === 0;"
              }
            ],
            "hints": [
              {
                "when": "area(3) comes back NaN",
                "say": "The gap is still empty, so `height` is `undefined` and `3 * undefined` is `NaN`. A default in the parameter list can use a parameter to its left."
              },
              {
                "when": "the height-of-0 test fails",
                "say": "Your check treats every falsy value as missing, and `0` is falsy. Test for the one value that means \"not supplied\"."
              },
              {
                "when": "the explicit-undefined test fails",
                "say": "You counted the arguments. An explicit `undefined` is supplied but still means \"not supplied\" — test the value, not the count."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Make the signature honest",
            "prompt": "`connect` takes five positional arguments and call sites are unreadable. Rewrite it to take a host plus an options object with defaults, keeping the same behaviour. Then, in one sentence: why would `||` be the wrong way to supply these defaults?\n",
            "exportName": "connect",
            "filename": "connect.js",
            "starter": "// Return \"host:port retries=N secure=B timeout=T\".\n// Defaults: port 443, retries 3, secure true, timeout 1000.\n// Rewrite so callers pass (host, options) instead of five positionals.\nfunction connect(host, port, retries, secure, timeout) {\n  return host + \":\" + port + \" retries=\" + retries +\n         \" secure=\" + secure + \" timeout=\" + timeout;\n}\n",
            "tests": [
              {
                "label": "host alone uses every default",
                "body": "return subject('a.com') === 'a.com:443 retries=3 secure=true timeout=1000';"
              },
              {
                "label": "one named option, the rest defaulted",
                "body": "return subject('a.com', { port: 80 }) === 'a.com:80 retries=3 secure=true timeout=1000';"
              },
              {
                "label": "options can be given in any order",
                "body": "return subject('a.com', { timeout: 50, port: 80 }) === 'a.com:80 retries=3 secure=true timeout=50';"
              },
              {
                "label": "secure: false is kept, not defaulted away",
                "body": "return subject('a.com', { secure: false }) === 'a.com:443 retries=3 secure=false timeout=1000';"
              },
              {
                "label": "retries: 0 is kept, not defaulted away",
                "body": "return subject('a.com', { retries: 0 }) === 'a.com:443 retries=0 secure=true timeout=1000';"
              }
            ],
            "hints": [
              {
                "when": "secure: false or retries: 0 came back as the default",
                "say": "You used `||` inside the function. Destructuring defaults fire on `undefined` only, which is exactly what you want here — put the default in the parameter list."
              },
              {
                "when": "calling with only a host throws",
                "say": "You destructured a parameter that was not supplied. Give the options parameter its own `= {}` default."
              }
            ]
          }
        ]
      }
    },
    "rest-params": {
      "id": "rest-params",
      "title": "Rest parameters and spread",
      "kind": "read",
      "outcome": 4,
      "shape": "procedural",
      "why": "JavaScript never checks a call against its declaration, so a rest parameter is how a function says it accepts any number of arguments — `scope.rest-spread`'s mastery. And `...` is everywhere in real code — `Math.max`, `console.log`, every forwarding wrapper — pointing in two directions that are told apart only by where it is written. Defaults are `params-arity`'s and are only recalled here (D-032).",
      "concepts": [
        "scope.rest-spread"
      ],
      "lede": "Two uses of the same three dots, for calls that do not match the declaration: `...rest` in a parameter list collects however many arguments are left, and `...` in a call spreads an array back out into separate arguments.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Parameters, arity and defaults · One level deep, or all the way · How a loop gets its next value"
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on things you have already met. Type what each produces."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "function f(a, b) { return b; }  f(1)",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you expected an error — JavaScript never checks the count.** A parameter with no argument at its position is bound to `undefined`, and the call goes ahead. Look back at what a call does with too few arguments.\n",
                          "why": "Too few arguments is not an error. The missing parameter is bound to `undefined`.\n"
                        },
                        {
                          "code": "function greet(name, greeting = \"hello\") { return greeting; }  greet(\"ada\", null)",
                          "accept": [
                            "null"
                          ],
                          "miss": "**If you answered `\"hello\"`, you treated `null` as missing.** A default fires on `undefined` only. `null` is a value the caller chose to pass, so it is kept.\n",
                          "why": "A default fires on `undefined` only — an omitted argument, or an explicit `undefined`. `null` is kept. The next steps rely on it.\n"
                        },
                        {
                          "code": "const a = [1, 2]; const b = [...a]; b.push(3);  a.length",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `3`, you read `[...a]` as a second name for `a`.** It builds a new array and copies `a`'s elements into it, so pushing to `b` never touches `a`. Look back at how a shallow copy is made.\n",
                          "why": "`[...a]` is a new array holding `a`'s elements — a shallow copy. `b.push` changes the copy only.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can write a function that takes a variable number of arguments",
                "I can spread a collection into a call, and say which direction a given `...` is going from where it is written"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Collect the rest into an array",
          "body": [
            {
              "p": "A call with more arguments than parameters loses the extras — the function\nnever sees them. To keep them, write **`...name` as the last parameter**. It\ncollects every argument the named parameters did not take into a real array."
            },
            {
              "tracks": {
                "js": [
                  {
                    "code": "function tag(label, ...values) {\n  return label + \": \" + values.join(\", \");\n}\n\ntag(\"sizes\", 1, 2, 3);   // \"sizes: 1, 2, 3\"  — label is \"sizes\", values is [1, 2, 3]\ntag(\"sizes\");            // \"sizes: \"         — values is [], not undefined",
                    "lang": "js"
                  },
                  {
                    "p": "Three rules:"
                  },
                  {
                    "list": [
                      "**It must be last.** The named parameters fill first, left to right; the rest parameter takes whatever is left.",
                      "**There can be only one.** Two would have no way to split the leftovers.",
                      "**It is always an array** — empty when nothing is left over, never `undefined`. So you can call `.length`, `.map` or `.join` on it without checking first."
                    ]
                  },
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "function f(a, ...more) { return more; }  f(1, 2, 3)",
                          "accept": [
                            "[2,3]",
                            "[2, 3]",
                            "2,3"
                          ],
                          "miss": "**If you included the `1` — the named parameters fill first.** `a` took the first argument, so `more` collects from the second onward.\n",
                          "why": "`a` is `1`; `...more` collects the two that are left into `[2, 3]`.\n"
                        },
                        {
                          "code": "function f(a, ...more) { return more; }  f(1)",
                          "accept": [
                            "[]",
                            "empty array",
                            "an empty array"
                          ],
                          "miss": "**If you answered `undefined` — a rest parameter is always an array.** With nothing left over, it is an empty one.\n",
                          "why": "Nothing was left after `a`, so `more` is `[]`. Never `undefined`.\n"
                        },
                        {
                          "code": "function f(...more, last) { }",
                          "accept": [
                            "SyntaxError",
                            "syntax error",
                            "error"
                          ],
                          "miss": "**If you expected `more` to take all but the last argument — JavaScript does not do that.** The rest parameter must be the last one, and anything else is rejected before the code runs.\n",
                          "why": "`SyntaxError: Rest parameter must be last formal parameter`. The file does not run at all.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "A rest parameter after a default",
          "body": [
            {
              "p": "You know what a default does from the recall: it fires when its slot gets\n`undefined`, and only then. Nothing about that changes when a rest parameter\nfollows it in the same list — which is how a function says, in its signature,\n\"this one is optional, and after it take any number.\""
            },
            {
              "tracks": {
                "js": [
                  {
                    "code": "function tag(label = \"item\", ...values) {\n  return label + \": \" + values.join(\", \");\n}\n\ntag(\"size\", 1, 2, 3);    // \"size: 1, 2, 3\"\ntag();                   // \"item: \"          — label defaulted, values is []\ntag(undefined, \"red\");   // \"item: red\"       — undefined in the slot fires the default",
                    "lang": "js"
                  },
                  {
                    "p": "What is new is how the two share the arguments. **Arguments still fill by\nposition**, and the rest parameter only gets what is left. A default does not\nmake `label` skip itself: the first argument goes to `label` whatever it is. To\nreach `values` while keeping the default, the caller passes `undefined` in\n`label`'s slot."
                  },
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "tag(\"colour\", \"red\", \"blue\")",
                          "accept": [
                            "\"colour: red, blue\"",
                            "colour: red, blue",
                            "'colour: red, blue'"
                          ],
                          "why": "`label` takes `\"colour\"`; `values` collects the other two.\n"
                        },
                        {
                          "code": "tag(\"red\")",
                          "accept": [
                            "\"red: \"",
                            "red: ",
                            "'red: '",
                            "red:"
                          ],
                          "miss": "**If you expected `\"item: red\"`, you let the default skip its slot.** The first argument always goes to the first parameter. `\"red\"` is not `undefined`, so it becomes the label and `values` is empty.\n",
                          "why": "`\"red\"` fills `label`, and nothing is left for `values`. A default only decides what happens when the slot gets `undefined`.\n"
                        },
                        {
                          "code": "tag(null, \"red\")",
                          "accept": [
                            "\"null: red\"",
                            "null: red",
                            "'null: red'"
                          ],
                          "miss": "**If you answered `\"item: red\"` — `null` is a value, not a missing argument.** The default fires on `undefined` only, so `label` is `null`.\n",
                          "why": "`null` is kept, as in the recall. String concatenation turns it into the text `null`.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Spread an array into a call",
          "body": [
            {
              "p": "The same three dots, written **in a call**, go the other way: they take an array\nand hand its elements over as separate arguments. It is the move you already\nmade with `[...a]` in an array literal, now in an argument list."
            },
            {
              "tracks": {
                "js": [
                  {
                    "code": "const nums = [3, 1, 2];\n\nMath.max(...nums);   // 3    — as if you had typed Math.max(3, 1, 2)\nMath.max(nums);      // NaN  — one argument, an array, which max cannot compare",
                    "lang": "js"
                  },
                  {
                    "block": "notice",
                    "label": "Which direction is this `...`?",
                    "body": [
                      {
                        "p": "**In a parameter list, it collects.** In a call or an array or object literal,\nit spreads. The token is the same; where it is written decides."
                      }
                    ]
                  },
                  {
                    "p": "The two directions meet whenever one function passes its arguments on to\nanother. The wrapper collects what it was given, then spreads it into the call:"
                  },
                  {
                    "code": "function warn(...parts) {         // collect: parts is an array\n  return tag(\"warning\", ...parts); // spread: each element becomes an argument\n}\n\nwarn(\"disk\", \"full\");   // \"warning: disk, full\"",
                    "lang": "js"
                  },
                  {
                    "p": "Spread at a call walks its source the way a `for...of` loop does, so it accepts\nanything a `for...of` loop accepts — an array, a string, a `Set` — and throws on\na plain object, which a `for...of` loop cannot walk either."
                  },
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "Math.max(...[3, 1, 2])",
                          "accept": [
                            "3"
                          ],
                          "miss": "**If you answered `[3, 1, 2]`, you have the direction backwards.** Three dots in a call spread: the array becomes three separate arguments.\n",
                          "why": "`Math.max` receives `3`, `1` and `2` as three arguments, and returns the largest.\n"
                        },
                        {
                          "code": "function count(...lines) { return lines.length; }  count([\"one\", \"two\"])",
                          "accept": [
                            "1",
                            "one"
                          ],
                          "miss": "**If you answered `2`, count the arguments at the call, not the elements in the array.** Without spread, the whole array is one argument, and `lines` collects that one argument.\n",
                          "why": "One argument was passed — an array — so `lines` is `[[\"one\", \"two\"]]`, with length 1.\n"
                        },
                        {
                          "code": "function count(...lines) { return lines.length; }  count(...[\"one\", \"two\"])",
                          "accept": [
                            "2",
                            "two"
                          ],
                          "why": "Spread turned the array into two arguments, and the rest parameter collected them back into an array of two.\n"
                        },
                        {
                          "code": "Math.max(...{ a: 3 })",
                          "accept": [
                            "TypeError",
                            "type error",
                            "error"
                          ],
                          "miss": "**If you answered `3` — spread at a call reads its source the way a `for...of` loop does, and a plain object cannot be walked that way.** `{ ...obj }` inside an object literal is the one place an object spreads, and it copies properties, not arguments.\n",
                          "why": "`TypeError`: a plain object is not iterable, so there is nothing for spread to hand over as arguments.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "`...name` **last** in a parameter list collects the leftover arguments into an array — always an array, empty when nothing is left.",
                "After a default, a rest parameter gets only what is left: arguments fill **by position**, so pass `undefined` to keep the default and reach the rest.",
                "`...` in a **call** spreads an array into separate arguments. Position decides the direction: parameter list collects, call or literal spreads."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Rest parameters",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/rest_parameters",
              "note": "including why it must be last."
            },
            {
              "title": "Spread syntax",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax",
              "note": "the three places it can be written, and what each accepts."
            }
          ]
        }
      ],
      "_source": "lessons/rest-params.md",
      "practice": {
        "id": "rest-params",
        "outcome": 4,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict what the dots do",
            "prompt": "Same three dots, two positions. Type the value the first two produce; for the third, pick one.\n",
            "items": [
              {
                "id": "p1",
                "code": "function f(a, ...more){ return more; }  f(1, 2, 3)",
                "accept": [
                  "[2,3]",
                  "[2, 3]",
                  "2,3"
                ],
                "why": "In a parameter list, `...` **collects** the remaining arguments into a real array."
              },
              {
                "id": "p2",
                "code": "Math.max(...[3, 1, 2])",
                "accept": [
                  "3"
                ],
                "why": "In a call, `...` **spreads** — the array becomes three separate arguments."
              },
              {
                "id": "p0",
                "question": "`...items` is written inside a call's parentheses. Does it collect or spread?",
                "choices": [
                  "collect",
                  "spread"
                ],
                "accept": [
                  "spread"
                ],
                "why": "The first thing to settle about any `...` is where it is written. In a parameter list it collects; in a call or a literal it spreads."
              }
            ],
            "extra": [
              {
                "id": "p3",
                "code": "function f(a, ...more){ return more; }  f(1)",
                "accept": [
                  "[]",
                  "empty array",
                  "an empty array"
                ],
                "why": "Always an array, never `undefined`. That guarantee is why rest replaced `arguments`."
              },
              {
                "id": "p4",
                "code": "Math.max([3, 1, 2])",
                "accept": [
                  "NaN",
                  "nan"
                ],
                "why": "Without the dots it is one argument — an array — which `max` cannot compare, so `NaN`."
              },
              {
                "id": "p5",
                "code": "Math.max(...\"315\")",
                "accept": [
                  "5"
                ],
                "why": "Spread walks its source the way `for...of` does, and a string walks character by character — so `max` receives `\"3\"`, `\"1\"`, `\"5\"` and compares them as numbers."
              }
            ],
            "hints": [
              {
                "when": "you gave an array for the second",
                "say": "Spread un-makes an array at a call site. Ask how many arguments `Math.max` actually received."
              },
              {
                "when": "you picked \"collect\" for the third",
                "say": "Collecting happens only in a function's *parameter list*, where the leftovers need somewhere to go. Inside a call, the array is being handed out."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace the direction",
            "prompt": "`report(title, ...lines)` exists and you hold `lines = [\"one\", \"two\"]`. How many lines does `report` see? Type a number.",
            "items": [
              {
                "id": "t1",
                "code": "report(\"Daily\", lines)",
                "accept": [
                  "1",
                  "one"
                ],
                "why": "One argument was passed — an array — so `lines` collects a single element that happens to be an array."
              },
              {
                "id": "t2",
                "code": "report(\"Daily\", ...lines)",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "Spread unpacked it into two arguments, which rest then collected as two lines."
              }
            ],
            "extra": [
              {
                "id": "t3",
                "code": "report(\"Daily\", lines, ...lines)",
                "accept": [
                  "3",
                  "three"
                ],
                "why": "Count the arguments at the call: the array itself, then its two elements spread out. Rest collects all three."
              },
              {
                "id": "t4",
                "code": "report(...lines)",
                "accept": [
                  "1",
                  "one"
                ],
                "why": "Arguments fill by position: `\"one\"` went to `title`, and only `\"two\"` was left for the rest parameter."
              }
            ],
            "hints": [
              {
                "when": "both answers were the same",
                "say": "One call passes a container; the other passes its contents. Count the arguments at the call site, not the values in the array."
              },
              {
                "when": "you answered 2 for the last extra",
                "say": "The named parameter fills first, whatever the argument is. Which element did `title` take?"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Build a function that takes any number of scores",
            "prompt": "Put the lines in order to make `highest(...scores)`: the largest score, or `0` when it is called with none. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "function highest(...scores) {",
              "  if (scores.length === 0) return 0;",
              "  return Math.max(...scores);",
              "}"
            ],
            "distractors": [
              {
                "line": "  return Math.max(scores);",
                "why": "This passes the whole array as one argument, and `Math.max` cannot compare an array — `NaN`. `scores` has to be spread back out at the call."
              },
              {
                "line": "  if (scores === undefined) return 0;",
                "why": "A rest parameter is never `undefined` — with no arguments it is `[]`. This check never fires, and `Math.max()` with nothing to compare returns `-Infinity`."
              }
            ],
            "why": "The same dots twice, pointing opposite ways: in the parameter list they collect whatever was passed into an array, and in the call to `Math.max` they spread that array back into separate arguments. The empty check reads `length` because the collected value is always an array.\n",
            "hints": [
              {
                "when": "you are unsure whether to keep the `undefined` check",
                "say": "Ask what a rest parameter holds when nothing is left over for it."
              },
              {
                "when": "you placed `Math.max(scores)`",
                "say": "Count the arguments `Math.max` receives on that line. How many numbers does it get to compare?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Forward the values",
            "prompt": "`report` collects any number of values and hands them to `average`. Fill the gap in the call so `report(\"temps\", 2, 4)` is `\"temps: 3\"`. Then, in one sentence: which `...` in this file collects, and which spreads?\n",
            "exportName": "report",
            "filename": "report.js",
            "starter": "function average(...nums) {\n  if (nums.length === 0) return 0;\n  let sum = 0;\n  for (const n of nums) sum += n;\n  return sum / nums.length;\n}\n\n// report(\"temps\", 2, 4) is \"temps: 3\".\nfunction report(label, ...values) {\n  return label + \": \" + average(/* ___ */);\n}\n",
            "tests": [
              {
                "label": "two values",
                "body": "return subject('temps', 2, 4) === 'temps: 3';"
              },
              {
                "label": "one value",
                "body": "return subject('x', 5) === 'x: 5';"
              },
              {
                "label": "four values",
                "body": "return subject('t', 1, 2, 3, 4) === 't: 2.5';"
              },
              {
                "label": "no values averages to 0",
                "body": "return subject('none') === 'none: 0';"
              },
              {
                "label": "the label is not averaged in",
                "body": "return subject('10', 2) === '10: 2';"
              }
            ],
            "hints": [
              {
                "when": "the result is NaN or a string of digits",
                "say": "`average` received one argument — the whole array — so `nums` is an array holding an array. Hand over the elements, not the container."
              },
              {
                "when": "the label-is-not-averaged test fails",
                "say": "You forwarded every argument `report` was given, including the label. Forward only what the rest parameter collected."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Default one, collect the rest",
            "prompt": "Write `logLine(level, ...parts)`. `level` defaults to `\"info\"`. It returns the level in square brackets followed by each part, all separated by single spaces: `logLine(\"warn\", \"disk\", \"full\")` is `\"[warn] disk full\"`, and `logLine()` is `\"[info]\"`. Then, in one sentence: how does a caller keep the default level and still pass parts?\n",
            "exportName": "logLine",
            "filename": "log-line.js",
            "starter": "// level defaults to \"info\"; any number of parts may follow.\nfunction logLine() {\n\n}\n",
            "tests": [
              {
                "label": "a level and several parts",
                "body": "return subject('warn', 'disk', 'full') === '[warn] disk full';"
              },
              {
                "label": "no arguments at all uses the default level",
                "body": "return subject() === '[info]';"
              },
              {
                "label": "a level with no parts has no trailing space",
                "body": "return subject('warn') === '[warn]';"
              },
              {
                "label": "undefined in the level slot fires the default",
                "body": "return subject(undefined, 'ready') === '[info] ready';"
              },
              {
                "label": "an empty-string level is kept, not defaulted",
                "body": "return subject('', 'x') === '[] x';"
              },
              {
                "label": "works with many parts",
                "body": "return subject('debug', 1, 2, 3, 4, 5) === '[debug] 1 2 3 4 5';"
              }
            ],
            "hints": [
              {
                "when": "the many-parts test fails",
                "say": "Your function names a fixed number of parameters, and the extras are discarded. A rest parameter, written last, collects however many there are."
              },
              {
                "when": "the empty-string test fails",
                "say": "You are defaulting with `||`, which also replaces `\"\"`. A default written in the parameter list fires on `undefined` only."
              },
              {
                "when": "the no-parts test fails",
                "say": "You add a space before the parts even when there are none. Put the bracketed level and the parts in one array and join that."
              }
            ]
          }
        ]
      }
    },
    "guard-clauses": {
      "id": "guard-clauses",
      "title": "Flattening with guard clauses",
      "kind": "read",
      "outcome": 3,
      "shape": "procedural",
      "why": "`flow.flattening` is outcome 3's other way conditions go wrong: every branch correct and the function still unreadable. Early return as a guard clause is this concept's (D-015). It is a mechanical rewrite with a fixed sequence of moves, so it is taught explanation-first (`01-explanations` rule 8). Split from `conditions` (D-029).",
      "concepts": [
        "flow.flattening"
      ],
      "lede": "A function whose conditions nest five deep can be right in every branch and still be hard to read. Guard clauses turn it inside out: the special cases leave at the top, one line each, and the work the function exists for is last and unindented. The rewrite is mechanical, and it changes the shape only.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What &&, || and ?? return · Choosing among many paths · When there is nothing there · Functions, pass 1 — declare, call, return"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Guard clause** — a condition at the top of a function that handles a case and returns, so the rest of the body can assume it is gone. **Early return** — a `return` before the end of the function body.",
          "entries": [
            "**Guard clause** — a condition at the top of a function that handles a case and returns, so the rest of the body can assume it is gone.",
            "**Early return** — a `return` before the end of the function body."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on things you have already met. Type or pick what each\nproduces."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "What does calling `f()` print, given `function f() { return \"done\"; console.log(\"after\"); }`? Type what appears, or *nothing*.",
                          "accept": [
                            "nothing",
                            "none",
                            "no output",
                            "it prints nothing"
                          ],
                          "miss": "**If you answered `after` — `return` ends the function.** The value goes back to the caller and nothing below the `return` runs. Look back at what `return` does in the first functions lesson.\n",
                          "why": "Nothing. The call returns `\"done\"` at that line and ends; the `console.log` is never reached.\n"
                        },
                        {
                          "code": "const qty = 0;   qty == null",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "false"
                          ],
                          "miss": "**If you answered `true` — `== null` asks the absence question only.** `0` is falsy, but it is there. Look back at the guard table in *When there is nothing there*.\n",
                          "why": "`false`. `x == null` is true only for `null` and `undefined`. A guard written this way lets `0` through; `if (!qty)` would not.\n"
                        },
                        {
                          "code": "const t = 25;   let s;   if (t > 10) s = \"warm\"; else if (t > 20) s = \"hot\"; else s = \"cold\";   s",
                          "accept": [
                            "\"warm\"",
                            "warm",
                            "'warm'"
                          ],
                          "miss": "**If you answered `\"hot\"` — a chain takes the first true branch, not the best one.** Look back at *A chain is checked in order*.\n",
                          "why": "`\"warm\"`. `25 > 10` is true, so that branch runs and the rest are skipped. Order decided the answer.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can rewrite deeply nested conditions so they read top to bottom"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "A return is a way out",
          "body": [
            {
              "p": "`return` does two things at once: it hands a value back **and it ends the\ncall**. So an `if` whose branch returns is a way out of the function — and\nevery line after it runs only for the inputs that did **not** take that way\nout."
            },
            {
              "p": "That is a **guard clause**: a check at the top of a function that handles one\ncase and leaves."
            },
            {
              "code": "function initial(name) {\n  // the special case: handle it and leave\n  if (name == null) return \"\";\n\n  // the main work: name is known to be there\n  return name[0].toUpperCase();\n}\n\ninitial(\"ada\");    // \"A\"\ninitial(null);     // \"\"  — name[0] is never read",
              "lang": "js",
              "file": "initial.js"
            },
            {
              "p": "Below the guard, `name` cannot be absent: any call where it was has already\nleft. You do not need an `else`, because there is nothing for the `else` to\nseparate. **After a guard, the rest of the function is the `else`.**"
            },
            {
              "p": "Type what each call returns, then answer the last."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "function size(list) { if (list.length === 0) return \"empty\"; return list.length + \" items\"; }   size([])",
                    "accept": [
                      "\"empty\"",
                      "empty",
                      "'empty'"
                    ],
                    "why": "`\"empty\"`. The guard matched and returned; the last line never ran.\n"
                  },
                  {
                    "code": "function size(list) { if (list.length === 0) return \"empty\"; return list.length + \" items\"; }   size([4, 5])",
                    "accept": [
                      "\"2 items\"",
                      "2 items",
                      "'2 items'"
                    ],
                    "why": "`\"2 items\"`. The guard did not match, so execution went on to the main line.\n"
                  },
                  {
                    "question": "In `if (!user) return null; else return user.name;` — is the `else` needed?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you answered yes — the `return` already left.** The only way to reach the next line is for the guard's condition to be false, so the `else` adds nothing but a level of indentation.\n",
                    "why": "No. `return user.name;` on its own line does the same thing.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Invert, return, un-indent",
          "body": [
            {
              "p": "Here is the shape that grows in every codebase. Every condition is correct; the\nfunction is still hard to read, and it gets harder with each new rule."
            },
            {
              "code": "function priceFor(order) {\n  if (order) {\n    if (order.items.length > 0) {\n      if (order.customer) {\n        if (order.customer.active) {\n          return total(order) * discountFor(order.customer);\n        } else {\n          return null;\n        }\n      } else {\n        return null;\n      }\n    } else {\n      return 0;\n    }\n  } else {\n    return null;\n  }\n}",
              "lang": "js",
              "file": "nested.js"
            },
            {
              "p": "The real work is one line, on the fifth level of indentation, and you cannot see\nit without reading every branch that is not it. The rewrite takes three moves,\nrepeated on the outermost `if` until none is left:"
            },
            {
              "list": [
                "**Does one branch just return (or throw), and the other contain the rest?** If not, this `if` is a genuine two-way choice — leave it and move inward.",
                "**Invert the condition and return the short branch immediately.** `if (x) { …rest } else { return null; }` becomes `if (!x) return null;` followed by `…rest`.",
                "**Un-indent the rest by one level.** Repeat on what is now outermost."
              ],
              "ordered": true
            },
            {
              "p": "Applied four times to `priceFor`:"
            },
            {
              "code": "function priceFor(order) {\n  // the special cases, each handled and left\n  if (!order) return null;\n  if (order.items.length === 0) return 0;\n  if (!order.customer) return null;\n  if (!order.customer.active) return null;\n\n  // the main path\n  return total(order) * discountFor(order.customer);\n}",
              "lang": "js",
              "file": "guarded.js"
            },
            {
              "p": "Same rules, same results, no nesting. **What did not change: the conditions,\nthe return values, or the order they are checked in.** Only the shape. The\nspecial cases now read as a list of preconditions, the main path is\nunindented and last, and adding a rule is one line rather than one more level."
            },
            {
              "p": "The same three moves on a different function:"
            },
            {
              "code": "function seatLabel(booking) {\n  if (booking.confirmed) {\n    if (booking.seat) {\n      return \"Seat \" + booking.seat;\n    } else {\n      return \"Seat to be assigned\";\n    }\n  } else {\n    return \"Not confirmed\";\n  }\n}",
              "lang": "js",
              "file": "nested-seat.js"
            },
            {
              "code": "function seatLabel(booking) {\n  if (!booking.confirmed) return \"Not confirmed\";\n  if (!booking.seat) return \"Seat to be assigned\";\n\n  return \"Seat \" + booking.seat;\n}",
              "lang": "js",
              "file": "guarded-seat.js"
            },
            {
              "p": "Move 1 matters. An `if` whose two branches both do real work — two different\ncalculations, say — is not a special case and a main path; it is a choice, and\nforcing it into a guard only hides one half of it."
            },
            {
              "video": {
                "title": "The same function, nested and guarded",
                "caption": "The `priceFor` example converted one level at a time, with each input traced through both versions to show they agree. Watch it if the mechanical step is not obvious yet; skip it if it is.\n"
              }
            },
            {
              "tracks": {
                "js": [
                  {
                    "code": "if (!order) return null;                // a guard\nif (order.items.length === 0) return 0; // another; the rest can assume both",
                    "lang": "js"
                  }
                ],
                "python": [
                  {
                    "p": "Guard clauses are the same move."
                  },
                  {
                    "code": "if not order: return None",
                    "lang": "python"
                  }
                ],
                "csharp": [
                  {
                    "p": "Guard clauses are idiomatic."
                  },
                  {
                    "code": "if (order is null) return null;\nif (order.Items.Count == 0) return 0;",
                    "lang": "csharp"
                  }
                ]
              }
            },
            {
              "p": "Convert this one in your head, then answer. The nested version:"
            },
            {
              "code": "function labelFor(user) {\n  if (user) {\n    if (user.name) {\n      return user.name;\n    } else {\n      return \"anonymous\";\n    }\n  } else {\n    return \"nobody\";\n  }\n}",
              "lang": "js",
              "file": "exercise.js"
            },
            {
              "p": "Fill in each blank with the code that goes there."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "The first guard in the flattened `labelFor`: `if (______) return \"nobody\";`",
                    "accept": [
                      "!user",
                      "(!user)",
                      "user == null"
                    ],
                    "miss": "**If you wrote `user` — the condition was not inverted.** The guard handles the case the outer `else` handled, which is when `user` is *not* there.\n",
                    "why": "`!user`. The outer `else` returned `\"nobody\"`, so the guard tests the opposite of the outer `if`.\n"
                  },
                  {
                    "question": "The second guard: `if (______) return \"anonymous\";`",
                    "accept": [
                      "!user.name",
                      "(!user.name)"
                    ],
                    "why": "`!user.name`. Same move one level in: invert the inner `if`, return its short branch.\n"
                  },
                  {
                    "question": "And the last line of the flattened `labelFor` is: `return ______;`",
                    "accept": [
                      "user.name",
                      "user.name;"
                    ],
                    "why": "`user.name` — the main path, unindented and last.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Order the guards by what they make safe",
          "body": [
            {
              "p": "Guards run top to bottom, and the first one that matches leaves. That gives\nthe order two jobs, and the flattening keeps both because it keeps the order."
            },
            {
              "p": "**A guard can only use what the guards above it have made safe.** In\n`priceFor`, `order.items.length` is read on the second line. That is safe only\nbecause the first line already sent every `null` order away. Swap them and a\n`null` order throws a TypeError before it reaches the guard written for it."
            },
            {
              "p": "**When two guards could both match, the first one decides.** That is the rule\nyou met in an `if`/`else if` chain: the first true branch wins, not the best.\nAn order with no items *and* no customer matches two guards; it gets `0`,\nbecause the empty-items guard comes first — exactly what the nested version\ngave, since it checked in the same order."
            },
            {
              "code": "priceFor({ items: [], customer: null })   // 0 — the items guard is reached first",
              "lang": "js"
            },
            {
              "p": "So when you flatten, keep the order the nesting had. When you add a guard\nlater, put it below everything it relies on."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "What does `priceFor({ items: [1], customer: { active: false } })` return? Type the value.",
                    "accept": [
                      "null"
                    ],
                    "why": "`null`. Past the first three guards, stopped by the fourth. The main path only runs when every guard has been passed.\n"
                  },
                  {
                    "question": "A new rule: a customer marked `banned` pays nothing and gets `null`. The guard reads `order.customer.banned`. Can it go first, above `if (!order)`?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you answered yes — it reads through `order` and `order.customer`.** On a `null` order it would throw before any guard had caught it.\n",
                    "why": "No. It reads `order.customer.banned`, so it must come after the guards that rule out a missing order and a missing customer.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Decide what each guard returns",
          "body": [
            {
              "p": "A guard's return value is a decision, the same one a `default` made in\n*Choosing among many paths*: is this case **a real answer**, or **a mistake**?"
            },
            {
              "list": [
                "**A real answer** — an order with no items costs `0`. The guard returns it, and the caller can use it like any other price.",
                "**A mistake** — a case that should never reach this function. The guard **throws**, with the value in the message, so it fails loudly at the line that knows what went wrong."
              ]
            },
            {
              "p": "`priceFor` returns `null` from three different guards: no order, no customer,\ninactive customer. The caller gets the same `null` for all three and cannot\ntell them apart — or tell any of them from a price that is genuinely missing.\nWhether that is acceptable depends on what the caller needs to do next; how to\ndesign a failure a caller can act on is a later lesson, on errors. The rule\nhere is narrower: **write each guard's return on purpose**, not because the\nnested version happened to say `null`."
            },
            {
              "p": "Flattening does not change a return value. If one is wrong, change it as a\nseparate step, after the shape is right and every input still gives what it\ngave before."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A shipping function receives an order with no delivery address, and every order is meant to have one. Should its guard return a value or throw?",
                    "choices": [
                      "Return a value",
                      "Throw"
                    ],
                    "accept": [
                      "Throw"
                    ],
                    "why": "Throw. The case \"cannot happen\", so if it does, a loud failure naming the order is the useful answer — a quiet `null` would travel on.\n"
                  },
                  {
                    "question": "The same function receives an order with no items. The price of nothing is `0`. Return a value or throw?",
                    "choices": [
                      "Return a value",
                      "Throw"
                    ],
                    "accept": [
                      "Return a value"
                    ],
                    "why": "Return `0`. An empty order is a real case with a real answer.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "`priceFor` has three guards that return `null`: no order, no customer, an\ninactive customer. A colleague argues that one `null` for three different\nreasons throws away information the caller needs."
            },
            {
              "p": "Two directions:"
            },
            {
              "list": [
                "**Keep returning `null`** and let the caller work out what it meant.",
                "**Return something that says which case it was** — or throw for any case that should never happen."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: how a caller of the first\nversion tells \"no customer\" from \"inactive customer\", and what the second does\nto every place that already calls `priceFor`."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "`return` ends the call, so an `if` that returns is a way out: a **guard clause**. Below it, the case it handled is gone — no `else` needed.",
                "Flatten by **invert, return, un-indent**, outermost first. Leave a genuine two-way choice alone.",
                "Keep the order: a guard may use only what the guards above it made safe, and the first match wins. The rewrite changes the shape, never the answers.",
                "Decide each guard's return on purpose: a real answer, or a `throw`."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "return",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/return",
              "note": "the statement that returns a value and ends the function as it does."
            },
            {
              "title": "if...else",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/if...else",
              "note": "the statement the rewrite takes apart."
            }
          ]
        }
      ],
      "_source": "lessons/guard-clauses.md",
      "practice": {
        "id": "guard-clauses",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the return, and the first move",
            "prompt": "One guarded function to run in your head, then two questions about where a flattening starts. Type the value or the condition, or pick an answer.\n",
            "items": [
              {
                "id": "p1",
                "code": "function tier(points) { if (points == null) return \"none\"; if (points > 100) return \"gold\"; return \"basic\"; }   tier(0)",
                "accept": [
                  "\"basic\"",
                  "basic",
                  "'basic'"
                ],
                "miss": "**If you answered `\"none\"` — `== null` catches only `null` and `undefined`.** `0` gets past the first guard, fails the second, and reaches the main line.\n",
                "why": "`\"basic\"`. Neither guard matched `0`, so the last line ran.\n"
              },
              {
                "id": "p2",
                "question": "Flatten `if (order) { …everything else… } else { return null; }`. Type the guard clause’s condition.",
                "accept": [
                  "!order",
                  "(!order)",
                  "if (!order)",
                  "if(!order)",
                  "order == null"
                ],
                "why": "Invert the condition and return the short branch first: `if (!order) return null;`. The rest then un-indents by one level.\n"
              },
              {
                "id": "p3",
                "question": "In `if (ready) { …ten lines… } else { return \"wait\"; }`, which branch becomes the guard?",
                "choices": [
                  "The if branch",
                  "The else branch"
                ],
                "accept": [
                  "The else branch"
                ],
                "why": "The `else`. It is the short branch that just returns; the ten lines are the main path, and they un-indent below the guard.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "Flatten `if (user) { …everything else… } else { return \"nobody\"; }`. Type the guard clause’s condition.",
                "accept": [
                  "!user",
                  "(!user)",
                  "if (!user)",
                  "if(!user)"
                ],
                "why": "The branch that just returns becomes the guard, and its condition is the original one inverted.\n"
              },
              {
                "id": "p5",
                "question": "With the same `tier` function, what does `tier(null)` return? Type the value.",
                "accept": [
                  "\"none\"",
                  "none",
                  "'none'"
                ],
                "why": "`null == null` is true, so the first guard returns `\"none\"` and nothing below it runs.\n"
              },
              {
                "id": "p6",
                "code": "function count(list) { if (list.length === 0) return 0; if (!list) return null; return list.length; }   count(null)",
                "accept": [
                  "error",
                  "typeerror",
                  "type error",
                  "throws",
                  "it throws"
                ],
                "why": "It throws. The first guard reads `list.length` before the second has ruled out a missing list — the guards are in the wrong order.\n"
              }
            ],
            "hints": [
              {
                "when": "you wrote the guard's condition without a `!`",
                "say": "The guard handles the case the `else` handled. Which condition sends you to the `else`? That is the one to test at the top.\n"
              },
              {
                "when": "you chose the if branch as the guard",
                "say": "A guard is short: it handles one case and leaves. Which branch is only a `return`?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Which guard fires?",
            "prompt": "Use the guarded functions from the lesson. Say what each call returns — the first guard that matches decides.\n",
            "items": [
              {
                "id": "t1",
                "code": "priceFor({ items: [], customer: null })   →   returns",
                "accept": [
                  "0"
                ],
                "why": "The guards run top to bottom and the first one that matches leaves. The empty-items guard comes before the customer guard, so this is `0`, not `null` — the same answer the nested version gave.\n"
              },
              {
                "id": "t2",
                "code": "priceFor({ items: [1], customer: { active: false } })   →   returns",
                "accept": [
                  "null"
                ],
                "why": "Past the first three guards, stopped by the fourth. The main path only runs when every guard has been passed.\n"
              },
              {
                "id": "t3",
                "code": "seatLabel({ confirmed: true })   →   returns",
                "accept": [
                  "\"Seat to be assigned\"",
                  "Seat to be assigned",
                  "'Seat to be assigned'"
                ],
                "why": "The booking is confirmed, so the first guard lets it past; it has no seat, so the second catches it.\n"
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "seatLabel({ confirmed: false, seat: \"12A\" })   →   returns",
                "accept": [
                  "\"Not confirmed\"",
                  "Not confirmed",
                  "'Not confirmed'"
                ],
                "why": "The first guard matches and leaves. That the booking has a seat is never looked at.\n"
              },
              {
                "id": "t5",
                "code": "priceFor(null)   →   returns",
                "accept": [
                  "null"
                ],
                "why": "The first guard. Nothing below it is reached, which is why the second guard may read `order.items` safely.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered null for the empty-items order",
                "say": "Read the guards in order and stop at the first that matches. Which comes first — the check on `items`, or the check on `customer`?\n"
              },
              {
                "when": "you answered \"Seat 12A\" for the unconfirmed booking",
                "say": "The first guard that matches leaves the function. Does anything after it get a say?\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Rebuild it with guards",
            "prompt": "The nested `shippingFor` says: no order → `null`; an order with no items → `0`; an express order → `15`; anything else → `5`. Build the guarded version from these lines. Two of them are mistakes people make doing exactly this.\n",
            "lines": [
              "function shippingFor(order) {",
              "  if (!order) return null;",
              "  if (order.items.length === 0) return 0;",
              "  if (order.express) return 15;",
              "  return 5;",
              "}"
            ],
            "distractors": [
              {
                "line": "  if (order) return null;",
                "why": "The condition was not inverted. A guard handles the case the `else` handled, so it tests the opposite of the original `if` — this one returns `null` for every real order.\n"
              },
              {
                "line": "  if (!order.items) return 0;",
                "why": "`order.items` is an array, and an empty array is truthy, so this guard never fires on `[]`. The question is about its length.\n"
              }
            ],
            "why": "Each guard handles one exceptional case and leaves, in the order the nested version checked them — which matters: `order.items` would throw if the missing-order guard were not first. The main case is unindented and last.\n",
            "hints": [
              {
                "when": "you are not sure which guard goes first",
                "say": "A guard can only use what the guards above it have made safe. Which line would throw if `order` were `null`?\n"
              },
              {
                "when": "your version returns null for a real order",
                "say": "Check each guard's condition against the case it is meant to catch. A guard tests the opposite of the `if` it replaced.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Add a guard where it is safe",
            "prompt": "`boardingGroup` works. Add one rule: a passenger with `assist: true` boards in group `0`, before first class — but a missing passenger or a passenger with no ticket still gets `null`. Add it as one guard, in the one place it belongs. Then, in one sentence: why can it not be the first line?\n",
            "exportName": "boardingGroup",
            "filename": "boarding.js",
            "starter": "// Which group a passenger boards with.\nfunction boardingGroup(passenger) {\n  if (!passenger) return null;\n  if (!passenger.ticket) return null;\n  if (passenger.ticket.cabin === \"first\") return 1;\n  if (passenger.ticket.row < 20) return 2;\n  return 3;\n}\n",
            "tests": [
              {
                "label": "a passenger needing assistance boards first",
                "body": "return subject({ assist: true, ticket: { cabin: 'main', row: 30 } }) === 0;"
              },
              {
                "label": "even ahead of first class",
                "body": "return subject({ assist: true, ticket: { cabin: 'first', row: 2 } }) === 0;"
              },
              {
                "label": "no passenger is still null",
                "body": "return subject(null) === null;"
              },
              {
                "label": "no ticket is still null, assistance or not",
                "body": "return subject({ assist: true }) === null;"
              },
              {
                "label": "the other groups are unchanged",
                "body": "return subject({ ticket: { cabin: 'first', row: 2 } }) === 1 &&\n       subject({ ticket: { cabin: 'main', row: 10 } }) === 2 &&\n       subject({ assist: false, ticket: { cabin: 'main', row: 30 } }) === 3;"
              }
            ],
            "hints": [
              {
                "when": "the no-passenger test fails with a TypeError",
                "say": "Your guard reads `passenger.assist` before anything has ruled out a missing passenger. A guard can use only what the guards above it made safe.\n"
              },
              {
                "when": "the no-ticket test fails",
                "say": "The spec says a passenger with no ticket gets `null` whatever else is true. Which existing guard has to run before yours?\n"
              },
              {
                "when": "the ahead-of-first-class test fails",
                "say": "The first guard that matches decides. Is your guard reached before the first-class guard, or after it?\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a flattening that went wrong",
            "prompt": "A colleague flattened a nested `accountStatus` and says the answers did not change. They did. Find the two defects, then fix it.\n",
            "filename": "account-status.js",
            "code": "// Same answers as the nested version: no account -> \"sign in\";\n// locked -> \"locked\"; no plan -> \"choose a plan\"; else the plan's name.\nfunction accountStatus(account) {\n  if (account.locked) return \"locked\";\n  if (!account) return \"sign in\";\n  if (account.plan) return \"choose a plan\";\n  return account.plan.name;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line throws when `account` is `null`? Type the line number.",
                "accept": [
                  "4",
                  "line 4"
                ],
                "why": "Line 4 reads `account.locked` before line 5 has ruled out a missing account. The guards were reordered, and the order was part of what made each one safe.\n"
              },
              {
                "id": "c2",
                "question": "Which line’s condition was not inverted? Type the line number.",
                "accept": [
                  "6",
                  "line 6"
                ],
                "why": "Line 6 returns \"choose a plan\" when there *is* a plan. The nested version's `if (account.plan)` wrapped the main path; the guard has to test `!account.plan`.\n"
              }
            ],
            "exportName": "accountStatus",
            "starter": "// Same answers as the nested version: no account -> \"sign in\";\n// locked -> \"locked\"; no plan -> \"choose a plan\"; else the plan's name.\nfunction accountStatus(account) {\n  if (account.locked) return \"locked\";\n  if (!account) return \"sign in\";\n  if (account.plan) return \"choose a plan\";\n  return account.plan.name;\n}\n",
            "tests": [
              {
                "label": "no account",
                "body": "return subject(null) === 'sign in' && subject(undefined) === 'sign in';"
              },
              {
                "label": "a locked account",
                "body": "return subject({ locked: true, plan: { name: 'pro' } }) === 'locked' && subject({ locked: true }) === 'locked';"
              },
              {
                "label": "an account with no plan",
                "body": "return subject({ locked: false }) === 'choose a plan';"
              },
              {
                "label": "an account with a plan",
                "body": "return subject({ locked: false, plan: { name: 'pro' } }) === 'pro';"
              }
            ],
            "hints": [
              {
                "when": "the no-account test fails with a TypeError",
                "say": "Which guard reads a property of `account`, and has anything above it ruled out a missing account?\n"
              },
              {
                "when": "the plan tests swap their answers",
                "say": "A guard tests the case the `else` handled. When does an account need to \"choose a plan\" — when it has one, or when it does not?\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Flatten it, and keep every answer",
            "prompt": "Rewrite `settingsLine` with guard clauses instead of nesting, and fix the bug while you are there — a user whose name is an empty string should still be reported as anonymous, but a display count of `0` must survive. Then, in one sentence: why do the two fields need different operators?\n",
            "exportName": "settingsLine",
            "filename": "label.js",
            "starter": "// Return \"<name> (<count> shown)\".\n// A missing or empty name is \"anonymous\".\n// A count that was genuinely supplied — including 0 — must be kept.\n// A missing count defaults to 10.\nfunction settingsLine(user) {\n  if (user) {\n    if (user.name) {\n      return user.name + \" (\" + (user.count || 10) + \" shown)\";\n    } else {\n      return \"anonymous (\" + (user.count || 10) + \" shown)\";\n    }\n  } else {\n    return \"nobody (0 shown)\";\n  }\n}\n",
            "tests": [
              {
                "label": "a normal user",
                "body": "return subject({ name: 'ada', count: 5 }) === 'ada (5 shown)';"
              },
              {
                "label": "no user at all",
                "body": "return subject(null) === 'nobody (0 shown)';"
              },
              {
                "label": "a missing name is anonymous",
                "body": "return subject({ count: 5 }) === 'anonymous (5 shown)';"
              },
              {
                "label": "an empty name is anonymous too",
                "body": "return subject({ name: '', count: 5 }) === 'anonymous (5 shown)';"
              },
              {
                "label": "a count of 0 was supplied and must survive",
                "body": "return subject({ name: 'ada', count: 0 }) === 'ada (0 shown)';"
              },
              {
                "label": "a missing count defaults to 10",
                "body": "return subject({ name: 'ada' }) === 'ada (10 shown)';"
              }
            ],
            "hints": [
              {
                "when": "the count-of-zero test fails",
                "say": "`user.count || 10` asks the truthiness question, and `0` is on that list — so a deliberately supplied zero is discarded. You want the operator that checks only for missing.\n"
              },
              {
                "when": "the empty-name test fails",
                "say": "Here you *do* want truthiness: an empty name should fall back. Note that the two fields want different operators, which is the point — \"missing\" and \"falsy\" are not the same question.\n"
              },
              {
                "when": "your version still nests",
                "say": "Invert each outer condition and return immediately. Start with the case the outermost `else` handles, and un-indent what is left.\n"
              }
            ]
          }
        ]
      }
    },
    "hoisting": {
      "id": "hoisting",
      "title": "Hoisting and the dead zone",
      "kind": "read",
      "outcome": 4,
      "shape": "conceptual",
      "why": "Outcome 4 asks when every name exists, not only what it refers to, and a name-and-assign model has no answer for a name read before its line. JavaScript gives three answers depending on the keyword, and one of them is a silent `undefined` rather than an error.",
      "concepts": [
        "scope.lifetime"
      ],
      "lede": "A binding is created before the line that declares it runs. What it holds in the gap — and whether touching it is even legal — depends on which keyword you used.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What a declaration creates · Which binding a name means"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**TDZ** — temporal dead zone. The span between a `let`/`const` binding being created and being initialised. Reading it throws.",
          "entries": [
            "**TDZ** — temporal dead zone. The span between a `let`/`const` binding being created and being initialised. Reading it throws."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four programs, each touching a name before its declaration line. Type the value it\nproduces, or `error` if it throws. Where a question offers buttons, pick one."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "console.log(x); var x = 1;",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you expected an error here too — this is the old behaviour the dead zone was added to replace.** `var` creates the binding *and* initialises it to `undefined` immediately, so a typo silently reads `undefined` rather than failing.\n",
                          "why": "The binding existed and held `undefined`. `var` creates and initialises to `undefined` up front, then assigns when the line runs.\n"
                        },
                        {
                          "code": "console.log(y); let y = 1;",
                          "accept": [
                            "error",
                            "referenceerror",
                            "ReferenceError"
                          ],
                          "miss": "**If you answered `undefined`, you may believe `let` is not hoisted.** It *is* — the binding is created at the top of the block. What differs is that it is not *initialised* until its line runs. The proof is the message: `Cannot access 'y' before initialization`, not `y is not defined`.\n",
                          "why": "`ReferenceError: Cannot access 'y' before initialization`. The binding exists — that is why the message is not \"y is not defined\" — but reading it is illegal until the declaration runs.\n"
                        },
                        {
                          "code": "hoisted(); function hoisted() { return 1; }",
                          "accept": [
                            "1",
                            "works",
                            "runs"
                          ],
                          "why": "A function **declaration** is fully available before its line. This is the one hoisting behaviour people rely on deliberately.\n"
                        },
                        {
                          "code": "notYet(); const notYet = () => 1;",
                          "accept": [
                            "error",
                            "referenceerror",
                            "ReferenceError"
                          ],
                          "miss": "**If you expected this to work because functions are hoisted — function *declarations* are.** This is a `const` declaration whose value happens to be a function, so `const` rules apply and the dead zone catches it.\n",
                          "why": "An arrow assigned to a `const` is not a function declaration — it is a `const` holding a function, so the dead zone applies as normal.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "print(x)\\nx = 1",
                          "accept": [
                            "error",
                            "nameerror",
                            "NameError"
                          ],
                          "why": "Python has no hoisting: the name does not exist until the assignment runs.\n"
                        },
                        {
                          "code": "def f():\\n    print(x)\\n    x = 1\\n\\nf()",
                          "accept": [
                            "error",
                            "unboundlocalerror",
                            "UnboundLocalError"
                          ],
                          "why": "Closest thing to a dead zone. The assignment makes `x` local for the *whole* function, so the print sees a local that has no value yet.\n"
                        },
                        {
                          "code": "f()\\ndef f(): return 1",
                          "accept": [
                            "error",
                            "nameerror",
                            "NameError"
                          ],
                          "why": "`def` is an ordinary statement that runs top to bottom. No function is callable before its `def` executes.\n"
                        },
                        {
                          "code": "def f(): return g()\\ndef g(): return 1\\nf()",
                          "accept": [
                            "1"
                          ],
                          "why": "Fine — `g` is looked up when `f` *runs*, not when it is defined. Mutual recursion works for the same reason.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Does this compile? `Console.WriteLine(x); int x = 1;`",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "\"Use of unassigned local variable\" — rejected before running. The strictest of the three, and the earliest.\n"
                        },
                        {
                          "question": "Does this compile? `void F() { G(); } void G() { }`",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "Methods are members, not statements, so declaration order in a class does not matter at all.\n"
                        },
                        {
                          "question": "Does this compile? `int y; if (c) y = 1; Console.WriteLine(y);`",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Definite-assignment analysis: the compiler proves a path exists where `y` was never assigned, and refuses. JavaScript would print `undefined`.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say when a name starts and stops being usable",
                "I can explain why `let` before its declaration throws while `var` gives `undefined`",
                "I can say which forms of function are available before their line"
              ]
            }
          ]
        },
        {
          "h": "Two steps, and hoisting is the gap between them"
        },
        {
          "p": "Creating a binding and giving it a value are separate events. Every keyword does\nboth; they differ in **when**."
        },
        {
          "table": {
            "head": [
              "",
              "Binding created",
              "Initialised to",
              "Reading before the line"
            ],
            "rows": [
              [
                "`var`",
                "top of the **function**",
                "`undefined`, immediately",
                "gives `undefined`"
              ],
              [
                "`let` / `const`",
                "top of the **block**",
                "only when the line runs",
                "**throws**"
              ],
              [
                "`function` declaration",
                "top of the block",
                "the whole function, immediately",
                "works"
              ],
              [
                "`class`",
                "top of the block",
                "only when the line runs",
                "**throws**"
              ]
            ]
          }
        },
        {
          "p": "\"Hoisting\" names the fact that the first column happens early. It is not that\ncode moves — nothing is rearranged. The scope is set up before any of its\nstatements run, and the declarations in it are already known."
        },
        {
          "h": "The temporal dead zone"
        },
        {
          "p": "The span from the top of the block to the declaration line is the **TDZ** for\nthat binding. It is *temporal*, not spatial — it is about when execution passes\nthe line, not where the line sits."
        },
        {
          "code": "{\n  // TDZ for `value` starts here\n  typeof value;      // ReferenceError — even typeof, which is normally safe\n  const value = 1;   // TDZ ends\n  typeof value;      // \"number\"\n}",
          "lang": "js",
          "file": "tdz.js"
        },
        {
          "p": "`typeof` throwing is the detail worth remembering: on an entirely undeclared name\n`typeof` returns `\"undefined\"` safely, which is how people feature-test. Inside a\nTDZ it throws, because the binding does exist."
        },
        {
          "block": "notice",
          "label": "Why the dead zone is a feature",
          "body": [
            {
              "code": "var count = 1;\nfunction f() {\n  console.log(count);   // undefined — not 1\n  var count = 2;\n}",
              "lang": "js"
            },
            {
              "p": "The inner `var` hoisted a binding that shadowed the outer one for the whole\nfunction, so the log reads the local before it was assigned. With `let`, the same\ncode throws and tells you exactly where. The dead zone converts a silent wrong\nvalue into an error."
            }
          ]
        },
        {
          "h": "Function declarations are the exception"
        },
        {
          "code": "greet();                       // works\nfunction greet() { return 1; }\n\ngreet2();                      // TypeError: greet2 is not a function\nvar greet2 = function () { };  // binding hoisted, value not",
          "lang": "js"
        },
        {
          "p": "The second is the shape that confuses people. `var greet2` is hoisted and holds\n`undefined`; calling `undefined` is a `TypeError`, not a `ReferenceError`. The\nerror names the symptom rather than the cause, which is worth recognising."
        },
        {
          "scaffold": {
            "label": "Deciding what a name holds at a line",
            "body": [
              {
                "h": "Deciding what a name holds at a line"
              },
              {
                "code": "// You are at a line that uses a name declared later in the same scope.\n\n// 1. What declares it?\n//    function declaration -> the function. Usable. Done.\n//    var                  -> undefined.     Usable, probably a bug.\n//    let / const / class  -> TDZ.           ReferenceError.\n\n// 2. Not declared in this scope at all?\n//    -> step outward and ask again (the scope chain from `name-resolution`).\n\n// 3. Nowhere?\n//    -> ReferenceError: x is not defined. Note the DIFFERENT message —\n//       \"is not defined\" means no binding; \"cannot access\" means TDZ.",
                "lang": "js",
                "file": "at-this-line.js"
              },
              {
                "p": "Now apply it. Say what each line does."
              },
              {
                "code": "function report() {\n  console.log(a);          // ______\n  console.log(b);          // ______\n  console.log(c());        // ______\n  var a = 1;\n  let b = 2;\n  function c() { return 3; }\n}",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`undefined`, a ReferenceError, and `3`. The third works even though `c` is\ndeclared last, because a function declaration is initialised with the scope."
              }
            ]
          }
        },
        {
          "video": {
            "title": "The scope set up before the first line runs",
            "caption": "The `report` example with the scope drawn before execution starts, showing which slots already hold values and which are in the dead zone. Watch it if \"hoisted but not initialised\" is still a contradiction.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **when does a name start existing, and what happens if you touch it\nearly?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Before its line",
                  "Detected"
                ],
                "rows": [
                  [
                    "**JavaScript `var`**",
                    "`undefined`",
                    "never"
                  ],
                  [
                    "**JavaScript `let`**",
                    "ReferenceError",
                    "at run time, at that line"
                  ],
                  [
                    "**Python**",
                    "NameError / UnboundLocalError",
                    "at run time"
                  ],
                  [
                    "**C#**",
                    "compile error",
                    "before running"
                  ]
                ]
              }
            },
            {
              "p": "The same axis you have met before: *when do you find out?* JavaScript is the only one of\nthe three that has a form (`var`) which never tells you — and that is the whole\ncase for not using it."
            },
            {
              "p": "Python's `UnboundLocalError` is the closest analogue to the dead zone, and it\narises from the same design decision: the scope is analysed before the body runs,\nso a name assigned anywhere in a function is local throughout it."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "`const` by default, `let` when reassigned, and declare near first use. The dead\nzone then does nothing, because you never read before the line."
              },
              {
                "code": "const rate = 0.2;              // declared where it is first needed\nfunction total(items) { }      // hoisted; order in the file does not matter",
                "lang": "js"
              },
              {
                "p": "Function declarations being hoisted is genuinely useful: helpers can sit below\nthe function that uses them, so a file reads top-down in order of importance."
              }
            ],
            "python": [
              {
                "p": "No hoisting. Names exist from their assignment onward, and `def` runs like any\nother statement."
              },
              {
                "code": "def main():\n    return helper()      # resolved when main RUNS, not when it is defined\n\ndef helper():\n    return 1",
                "lang": "python"
              },
              {
                "p": "This is why a module's `def`s can call each other in any order while a top-level\n*call* still has to come last."
              }
            ],
            "csharp": [
              {
                "p": "Definite-assignment analysis means the compiler refuses to let you read a local\nthat might not have been assigned — it finds out earlier than either dynamic\nlanguage."
              },
              {
                "code": "int x;\nif (c) x = 1;\nConsole.WriteLine(x);   // error: use of unassigned local variable",
                "lang": "csharp"
              },
              {
                "p": "Members are order-independent, so a method can call one declared below it."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say what distinguishes the two\n`ReferenceError` messages — \"cannot access\" versus \"is not defined\" — and what\neach one tells you about whether a binding exists."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A file has 30 function declarations and a colleague wants a lint rule requiring\nevery function to be declared before it is used."
            },
            {
              "p": "Two positions:"
            },
            {
              "list": [
                "**Adopt it** — reading order should match execution order, and the rule makes the file honest.",
                "**Do not** — hoisting exists so a file can lead with what matters and put helpers below."
              ]
            },
            {
              "p": "Pick one and say why. Then say what the rule would do to a pair of mutually\nrecursive functions, and whether the same argument applies to `const`-assigned\narrow functions, which do not hoist their value at all."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Hoisting is the **binding being created early**, not code moving.",
                "`var` initialises to `undefined` immediately; `let`/`const`/`class` do not initialise until their line, and reading before that **throws**.",
                "That span is the **temporal dead zone**, and even `typeof` throws inside it.",
                "**Function declarations** are hoisted complete and callable. `var f = function` is not — it fails as a `TypeError`, not a `ReferenceError`.",
                "\"Cannot access X\" means the binding exists and is in the TDZ. \"X is not defined\" means there is no binding at all."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Hoisting",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Hoisting",
              "note": "the glossary entry, and its caution that \"hoisting\" is not a term in the specification."
            },
            {
              "title": "let — temporal dead zone",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/let",
              "note": "including the `typeof` behaviour, which is the detail worth keeping."
            },
            {
              "title": "Function declaration",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function",
              "note": "and how it differs from a function expression assigned to a variable."
            }
          ]
        }
      ],
      "_source": "lessons/hoisting.md",
      "practice": {
        "id": "hoisting",
        "outcome": 4,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict, then name the first step",
            "prompt": "The first two touch a name before its declaration — type the value, or `error`. For the third, pick one.\n",
            "items": [
              {
                "id": "p1",
                "code": "console.log(x); var x = 1;",
                "accept": [
                  "undefined"
                ],
                "why": "The binding was created and initialised to `undefined` up front, so reading it is legal and useless."
              },
              {
                "id": "p2",
                "code": "console.log(y); let y = 1;",
                "accept": [
                  "error",
                  "referenceerror",
                  "ReferenceError"
                ],
                "why": "The binding exists but is uninitialised — the temporal dead zone. Note the message says \"cannot access\", not \"is not defined\"."
              },
              {
                "id": "p0",
                "question": "A line reads `rate`, which is declared further down the same scope. What do you look at first?",
                "choices": [
                  "The keyword that declares it",
                  "The value it is given",
                  "Where it is called from"
                ],
                "accept": [
                  "The keyword that declares it"
                ],
                "why": "What a name holds before its line depends entirely on what declared it: a function declaration is ready, `var` holds `undefined`, and `let`/`const`/`class` are in the dead zone."
              }
            ],
            "extra": [
              {
                "id": "p3",
                "code": "hoisted(); function hoisted(){ return 1; }",
                "accept": [
                  "1",
                  "works",
                  "runs"
                ],
                "why": "A function declaration is hoisted complete. This is the one hoisting behaviour people rely on deliberately."
              },
              {
                "id": "p4",
                "code": "later(); var later = function(){};",
                "accept": [
                  "error",
                  "typeerror",
                  "TypeError"
                ],
                "why": "A **TypeError**, not a ReferenceError — `later` exists and holds `undefined`, and calling `undefined` is a type error."
              },
              {
                "id": "p5",
                "code": "soon(); const soon = () => 1;",
                "accept": [
                  "error",
                  "referenceerror",
                  "ReferenceError"
                ],
                "why": "A `const` whose value happens to be a function is still a `const`: dead zone until its line."
              }
            ],
            "hints": [
              {
                "when": "you answered undefined for the second",
                "say": "That was the old behaviour and it hid typos. `let` creates the binding early but refuses to be read until its line runs."
              },
              {
                "when": "you expected every function to be callable early",
                "say": "Only a function *declaration* is initialised with the scope. A function stored in a `var` or `const` follows that keyword's rules."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict each line, then run it",
            "prompt": "One function, four reads — three of them before or around a declaration. Predict what each `console.log` does, then run it and compare. The last one is worth reading the message for.\n",
            "code": "function report() {\n  console.log(\"total is \" + total);\n  console.log(double(4));\n  var total = 10;\n  console.log(\"total is \" + total);\n  function double(n) { return n * 2; }\n  console.log(label);\n  let label = \"done\";\n}\n\nreport();\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "total is undefined",
                  "\"total is undefined\""
                ],
                "why": "`var total` was created and set to `undefined` before the body ran; the `10` arrives two lines later."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "8"
                ],
                "why": "A function declaration is ready for the whole scope, so `double` works three lines before it is written."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "total is 10",
                  "\"total is 10\""
                ],
                "why": "The assignment on the `var` line has now run."
              },
              {
                "id": "r4",
                "question": "What the fourth `console.log` does — what it prints, or `error`",
                "accept": [
                  "error",
                  "referenceerror",
                  "ReferenceError",
                  "throws",
                  "throws a referenceerror",
                  "referenceerror: cannot access",
                  "cannot access 'label' before initialization",
                  "ReferenceError: Cannot access 'label' before initialization"
                ],
                "why": "`label` is in its dead zone. The message is \"Cannot access 'label' before initialization\" — *cannot access*, which tells you the binding exists and its line has not run yet."
              }
            ],
            "hints": [
              {
                "when": "you expected an error on the first line",
                "say": "That is what `let` would do. Ask what `var` does between creating the binding and reaching its line."
              },
              {
                "when": "you expected the fourth to print undefined",
                "say": "Which keyword declared `label`? Only one of the three keywords initialises early."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Say when each name is usable",
            "prompt": "Give what each expression produces — a value, or the kind of error.",
            "items": [
              {
                "id": "t1",
                "code": "function go(){ if (!ready) return \"wait\"; var ready = true; return \"go\"; }  →  go()",
                "accept": [
                  "\"wait\"",
                  "wait",
                  "'wait'"
                ],
                "why": "`ready` is hoisted and holds `undefined` at the `if`, and `undefined` is falsy — so the guard fires every time. Silent and wrong, which is the case against `var`."
              },
              {
                "id": "t2",
                "code": "{ let seat = \"12A\"; }  seat",
                "accept": [
                  "error",
                  "referenceerror",
                  "ReferenceError"
                ],
                "why": "A `let` binding stops being usable at the end of its block. The message here is \"seat is not defined\" — no binding at all, which is different from the dead zone."
              }
            ],
            "extra": [
              {
                "id": "t3",
                "code": "typeof b;  // let b = 2 appears later in the same block",
                "accept": [
                  "error",
                  "referenceerror",
                  "ReferenceError"
                ],
                "why": "`typeof` throws inside a dead zone — the one case where it is not safe. On a name that was never declared at all it would return `\"undefined\"`."
              },
              {
                "id": "t4",
                "code": "typeof neverDeclared",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "No binding anywhere, so `typeof` answers `\"undefined\"` instead of throwing. The difference from the previous one is whether a binding exists."
              },
              {
                "id": "t5",
                "code": "function f(){ return g(); } function g(){ return 2; }  →  f()",
                "accept": [
                  "2"
                ],
                "why": "`g` is looked up when `f` *runs*, and by then every declaration in the scope is in place. Declaration order in the file does not matter here."
              }
            ],
            "hints": [
              {
                "when": "you answered \"go\" for the first",
                "say": "Work out what `ready` holds at the `if`, before its own line has run. Then ask whether that value counts as true."
              },
              {
                "when": "you expected typeof to be safe inside the dead zone",
                "say": "`typeof` is safe on a name with no binding anywhere. Inside a TDZ the binding exists, so the read is illegal and it throws."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Convert a helper without breaking the order",
            "prompt": "`summary` works today, with two helpers declared below its `return`. The team's lint rule now requires helpers to be `const` arrow functions. Convert `count` to one — keep it inside `summary` — so that `summary` still returns the same strings. Then, in one sentence: why could `heading` stay where it is?\n",
            "exportName": "summary",
            "filename": "summary.js",
            "starter": "// Returns \"Items: N\" for a list of N items.\nfunction summary(items) {\n  return heading() + \" \" + count(items);\n\n  function heading() { return \"Items:\"; }\n  function count(list) { return list.length; }\n}\n",
            "tests": [
              {
                "label": "count is now a const inside summary",
                "body": "return /const\\s+count\\s*=/.test(String(subject));"
              },
              {
                "label": "two items",
                "body": "return subject(['a', 'b']) === 'Items: 2';"
              },
              {
                "label": "no items",
                "body": "return subject([]) === 'Items: 0';"
              },
              {
                "label": "one item",
                "body": "return subject(['x']) === 'Items: 1';"
              }
            ],
            "hints": [
              {
                "when": "it throws \"Cannot access 'count' before initialization\"",
                "say": "You converted it in place, below the `return`. A `const` is in its dead zone until its line runs — and that line is never reached. Where must it be written for the `return` to read it?"
              },
              {
                "when": "the const test fails",
                "say": "The check looks for `const count =` inside `summary`. A function declaration named `count` is still a declaration, however it is written."
              }
            ]
          }
        ]
      }
    },
    "declarations": {
      "id": "declarations",
      "title": "What a declaration creates",
      "kind": "read",
      "outcome": 4,
      "shape": "conceptual",
      "why": "Outcome 4 asks what every name in a program refers to, and that question is answered in terms of bindings and the scopes that hold them, so `scope.kinds` sets the vocabulary every later idea about names uses. And JavaScript has two keywords that look interchangeable and place a binding in different scopes — `var` cannot see a block — which is where a mechanical `var`-to-`let` change alters behaviour without an error.",
      "concepts": [
        "scope.kinds"
      ],
      "lede": "Declaring a name does more than give it a value. It creates a binding, and it puts that binding in one particular region of the program — and which region depends on the keyword you used.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Which binding a name means · Hoisting and the dead zone"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Binding** — the link between a name and a value in one scope. Not the value, and not the name. **Scope** — the region of code in which a binding exists.",
          "entries": [
            "**Binding** — the link between a name and a value in one scope. Not the value, and not the name.",
            "**Scope** — the region of code in which a binding exists."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four short programs. Type the final value — or `error` if you think it throws.\nWhere a question offers buttons, pick one.\nA guess is fine; that is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "var a = 1; { var a = 2; }  →  a",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1` because the braces made a new scope — they made a block, and `var` cannot see blocks.** It belongs to the enclosing *function* (or the whole file, at the top level), so the second line found the existing binding and assigned to it.\n",
                          "why": "`var` does not see blocks at all. The inner line re-declared the *same* binding, so there was only ever one `a`.\n"
                        },
                        {
                          "code": "let b = 1; { let b = 2; }  →  b",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If this matched your first answer, note that `let` and `var` do not differ in whether you can reassign — that is `const`.** They differ in **which region the binding belongs to**, and that is the difference that produces bugs.\n",
                          "why": "`let` belongs to the block, so the braces made a second, separate binding. It ended at the closing brace; the outer `b` was never touched.\n"
                        },
                        {
                          "code": "if (true) { const msg = \"hi\"; }  →  msg",
                          "accept": [
                            "error",
                            "referenceerror",
                            "reference error",
                            "throws",
                            "an error",
                            "it throws"
                          ],
                          "miss": "**If you answered `\"hi\"` because the `if` ran — it did, and the binding existed while it ran.** The `if`'s braces are a block, `const` belongs to that block, and the binding ended at the closing brace.\n",
                          "why": "`ReferenceError: msg is not defined`. Out here there is no `msg` at all.\n"
                        },
                        {
                          "code": "function f() { if (true) { var x = 1; } return x; }  →  f()",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If you answered `error`, you gave `var` the block's scope.** `var` belongs to the enclosing function, so `x` exists for all of `f` — inside the `if` and after it.\n",
                          "why": "`1`. The `if`'s braces are invisible to `var`; the binding belongs to `f`.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "x = 1\\nif True:\\n    x = 2\\nprint(x)",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1`, you gave the `if` a scope of its own.** Python's `if` and `for` create none.\n",
                          "why": "Python has no block scope — an `if` does not create one. This matches JavaScript's `var`, not its `let`.\n"
                        },
                        {
                          "code": "def f():\\n    y = 2\\nf()\\nprint(y)",
                          "accept": [
                            "error",
                            "nameerror",
                            "NameError"
                          ],
                          "why": "Functions *do* create a scope. Python's unit of scope is the function and the module, not the block.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Can `i` be used after the loop? `for (int i=0;i<3;i++) { }  i;`",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes — `i` was declared in the loop's header, and the loop is its block.** It does not exist after the loop.\n",
                          "why": "`i` belongs to the loop's block and does not exist after it — block scoping, enforced at compile time.\n"
                        },
                        {
                          "question": "Is this allowed? `int a = 1; { int a = 2; }`",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "A compile error: the inner block is a scope of its own, but C# refuses to declare a name in it that is already in scope outside.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can name which scope a declaration creates its binding in — block, function, module or global — from the declaration and where it is written"
              ]
            }
          ]
        },
        {
          "h": "Three things a declaration does"
        },
        {
          "p": "`let count = 0` is doing more than it looks like."
        },
        {
          "list": [
            "**Create a binding** for `count` — a slot that links the name to a value.",
            "**Place that binding in a scope** — a region of the program. The binding exists inside that region and nowhere else.",
            "**Initialise it** to the value on the right."
          ],
          "ordered": true
        },
        {
          "p": "Keep the three apart. Step 2 is this lesson. Which *keyword* you used decides\nwhich region, and the region decides where the name can be used at all: outside\nit, as far as that binding is concerned, the name does not exist."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`let total = 0` is written inside a function, inside an `if`. Which of the three steps does the keyword `let` decide?",
                "choices": [
                  "Create the binding",
                  "Place it in a scope",
                  "Initialise it"
                ],
                "accept": [
                  "Place it in a scope"
                ],
                "miss": "**If you picked initialising it — `= 0` decides the value, and it would be the same with `var`.** What changes with the keyword is which region the binding is placed in.\n",
                "why": "The second: which scope the binding goes in. The name and the value would be the same with any keyword.\n"
              }
            ]
          }
        },
        {
          "h": "The scopes JavaScript has"
        },
        {
          "table": {
            "head": [
              "Created by",
              "The binding belongs to"
            ],
            "rows": [
              [
                "`let`, `const`, `class`",
                "the enclosing **block** — any `{ }`: an `if`, a loop body, a bare pair of braces"
              ],
              [
                "`let` or `const` in a `for` header",
                "the **loop** — it does not exist after it"
              ],
              [
                "`var`",
                "the enclosing **function** — blocks are invisible to it. At the top level, the whole file"
              ],
              [
                "a `function` declaration",
                "the enclosing block in modules and strict code; older, non-strict code lets it escape to the function"
              ],
              [
                "a parameter",
                "the function's body"
              ],
              [
                "any declaration at the top level of a module file",
                "the **module** — not visible to other files unless exported"
              ],
              [
                "assigning to a name that was never declared",
                "**global** — an accident, and one that `\"use strict\"` and modules forbid"
              ]
            ]
          }
        },
        {
          "p": "Picture each scope as a box drawn around a region of code. A binding sits in\nexactly one box. `let` looks for the nearest box of any kind; `var` looks past\nevery box that is not a function."
        },
        {
          "p": "The practical rule is one line: **`const` by default, `let` when it must be\nreassigned, `var` never in new code.** `var` is not deprecated and will not be\nremoved — you will meet it in older code, and you need to read it — it is\nsimply placed in a larger region than you almost ever want."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "function f() { for (let k = 0; k < 2; k++) {} return k; }  →  f()",
                "accept": [
                  "error",
                  "referenceerror",
                  "reference error",
                  "throws",
                  "an error",
                  "it throws"
                ],
                "miss": "**If you answered `2` — that would be `var`.** A `let` in a `for` header belongs to the loop, so after the loop there is no `k`.\n",
                "why": "`ReferenceError: k is not defined`. The binding belonged to the loop.\n"
              },
              {
                "code": "function g() { { var z = 3; } return z; }  →  g()",
                "accept": [
                  "3"
                ],
                "why": "`3`. A bare block is still a block, and `var` still cannot see it.\n"
              }
            ]
          }
        },
        {
          "h": "Naming the scope, worked"
        },
        {
          "p": "Every time, the same three steps. The question is never \"where is the code\nrunning?\" — it is \"where is the declaration **written**, and what does its\nkeyword look for?\""
        },
        {
          "p": "A shop gives a loyalty discount. Which scope holds `rate`, and which holds\n`label`?"
        },
        {
          "code": "function discount(order) {\n  if (order.loyal) {\n    var rate = 0.1;\n    let label = \"loyalty\";\n  }\n  return rate;\n}",
          "lang": "js",
          "file": "discount.js"
        },
        {
          "code": "// find the keyword\n//   rate: var.   label: let.\n// find the nearest region that keyword looks for\n//   var looks for a function   -> the nearest is discount.\n//   let looks for any block    -> the nearest is the if's braces.\n// name the scope\n//   rate belongs to discount, so `return rate` can read it.\n//   label belongs to the if block, and ends at its closing brace."
        },
        {
          "p": "A different shape: two `for` loops, one keyword apart."
        },
        {
          "code": "for (var i = 0; i < 3; i++) { }\ni;   // 3\n\nfor (let j = 0; j < 3; j++) { }\nj;   // ReferenceError",
          "lang": "js",
          "file": "loops.js"
        },
        {
          "code": "// find the keyword\n//   i: var.   j: let, in a for header.\n// find the nearest region that keyword looks for\n//   var looks for a function   -> there is none, so the whole file.\n//   let in a for header        -> the loop itself.\n// name the scope\n//   i outlives its loop and still holds 3.\n//   j existed only while the loop ran."
        },
        {
          "p": "`var` leaked the counter into the surrounding code. `let` kept it inside the\nloop, which is the only place it meant anything."
        },
        {
          "p": "Now one with the last step left to you. A settings file:"
        },
        {
          "code": "const theme = \"dark\";\n\nexport function applyTheme(page) {\n  const accent = \"teal\";\n  page.style = theme + \" \" + accent;\n}",
          "lang": "js",
          "file": "settings.js"
        },
        {
          "code": "// find the keyword\n//   theme: const.   accent: const.\n// find the nearest region that keyword looks for\n//   theme: no block around it -> the top level of a module file.\n//   accent: the function body's braces are a block.\n// name the scope\n//   theme:  ______        accent:  ______"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`theme` in settings.js — which scope?",
                "choices": [
                  "block",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "module"
                ],
                "miss": "**If you said global — a module file's top level is its own scope.** Another file cannot see `theme` unless this one exports it.\n",
                "why": "The module. Everything in this file can read it; other files cannot, unless it is exported.\n"
              },
              {
                "question": "`accent` in settings.js — which scope?",
                "choices": [
                  "block",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "block",
                  "function"
                ],
                "why": "The block that is `applyTheme`'s body — for `const`, the nearest braces. It is created fresh each time the function runs.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Naming the scope by hand",
            "body": [
              {
                "h": "Naming the scope by hand"
              },
              {
                "code": "// You are looking at a declaration and want to know where its binding lives.\n\n// 1. Which keyword?  let / const / class, var, function, a parameter —\n//    or none at all (an assignment to a name nobody declared).\n// 2. Walk outward from the declaration to the nearest region that keyword\n//    looks for:\n//      let, const, class -> the nearest { } of any kind (a for header counts)\n//      var               -> the nearest function; at the top level, the file\n//      parameter         -> its function's body\n//      no keyword        -> nothing: in sloppy code it becomes a global\n// 3. That region is the scope. Outside it, this binding does not exist.",
                "lang": "js",
                "file": "which-scope.js"
              },
              {
                "p": "Now do one. Name the scope of each binding."
              },
              {
                "code": "function report(rows) {\n  let count = 0;                 // count:  ______\n  for (const row of rows) {      // row:    ______\n    if (row.ok) {\n      var last = row.id;         // last:   ______\n      count++;\n    }\n  }\n  missed = rows.length - count;  // missed: ______\n  return last;\n}",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`count` belongs to `report`'s body. `row` belongs to the loop. `last` belongs to\n`report` — `var` walked past both blocks — which is why `return last` works.\n`missed` was never declared, so outside strict mode it becomes a global that\nevery call to `report` overwrites."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what is the smallest region that can own a name?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Block `{ }`",
                  "Function",
                  "Module file"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "`let`/`const` yes, `var` no",
                    "yes",
                    "yes"
                  ],
                  [
                    "**Python**",
                    "**no** — `if` and `for` create none",
                    "yes",
                    "yes"
                  ],
                  [
                    "**C#**",
                    "yes",
                    "yes",
                    "— types and namespaces instead"
                  ]
                ]
              }
            },
            {
              "p": "JavaScript sits in the middle because it has both answers. With `var` the\nsmallest owner is the function, which is all Python offers; with `let` and\n`const` it is the block, as in C#. A language whose smallest scope is the\nfunction lets a loop counter or a temporary outlive the code that needed it.\nKnowing where your language sits tells you whether a name declared inside an\n`if` is still there after it."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "`const` by default, `let` when reassignment is needed, `var` never in new code."
              },
              {
                "code": "const rate = 0.2;        // default\nlet total = 0;           // reassigned in a loop\nfor (const item of items) total += item.price * rate;",
                "lang": "js"
              },
              {
                "p": "`const` fixes the *binding*, not the value: a `const` object can still be\nmutated. That is the copying-versus-sharing distinction again, and it is why\n`const` is a safe default rather than a strong guarantee."
              }
            ],
            "python": [
              {
                "p": "The unit of scope is the function. Assigning a name inside one makes it local\nfor the *whole* function, even before the assignment line."
              },
              {
                "code": "def total(rows):\n    for row in rows:\n        last = row       # no block scope: `last` belongs to total\n    return last          # still there after the loop",
                "lang": "python"
              }
            ],
            "csharp": [
              {
                "p": "Block-scoped throughout, and the compiler enforces more than JavaScript's."
              },
              {
                "code": "const double Rate = 0.2;     // compile-time constant\nvar total = 0m;              // inferred, still statically typed\nreadonly List<int> items;    // the field cannot be reassigned",
                "lang": "csharp"
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say, for the first, what the\nanswer would have been had both declarations used `let` — and, for the fourth,\nwhat `f()` does if `var x` becomes `let x`."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A colleague's pull request changes every `var` in a 400-line file to `let`. The\ntests pass."
            },
            {
              "p": "Two positions:"
            },
            {
              "list": [
                "**Merge it** — `let` is scoped the way people expect, and the tests agree.",
                "**Do not** — passing tests do not prove the scopes were equivalent."
              ]
            },
            {
              "p": "Pick one and say why. Then name a specific shape of code where the change would\nalter behaviour without failing an existing test. A loop counter read after its\nloop is one; there is at least one more involving a name declared inside an\n`if` and used after it."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A declaration **creates a binding**, **places it in a scope**, and **initialises it**. The keyword decides the scope.",
                "`let`/`const` belong to the nearest **block**; in a `for` header, to the loop. `var` belongs to the nearest **function** and cannot see blocks.",
                "A module file's top level is its own scope. An undeclared assignment makes a **global** — by accident."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "let",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/let",
              "note": "read its comparison with `var`; the block-versus-function split is the opening question."
            },
            {
              "title": "var",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/var",
              "note": "and the section on why re-declaration is allowed."
            },
            {
              "title": "Scope",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Scope",
              "note": "the glossary entry: global, module, function and block."
            }
          ]
        }
      ],
      "_source": "lessons/declarations.md",
      "practice": {
        "id": "declarations",
        "outcome": 4,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict, then name the first step",
            "prompt": "The first two are one keyword apart — type the final value, or `error`. For the third, pick one.\n",
            "items": [
              {
                "id": "p1",
                "code": "var a = 1; { var a = 2; }  →  a",
                "accept": [
                  "2"
                ],
                "why": "`var` belongs to the function (or the file), so the braces made no new scope. One binding, assigned twice."
              },
              {
                "id": "p2",
                "code": "let b = 1; { let b = 2; }  →  b",
                "accept": [
                  "1"
                ],
                "why": "`let` belongs to the block, so the braces created a second binding, which ended at the closing brace."
              },
              {
                "id": "p0",
                "question": "To name the scope a declaration creates, what do you look at first?",
                "choices": [
                  "The keyword",
                  "Where it runs",
                  "The value it is given"
                ],
                "accept": [
                  "The keyword"
                ],
                "miss": "**If you picked where it runs or the value — neither changes the scope.** The keyword decides what region the binding looks for; where it is written decides which region that is.\n",
                "why": "The keyword. `let`/`const` look for the nearest block, `var` for the nearest function — then you walk outward from where the declaration is written."
              }
            ],
            "extra": [
              {
                "id": "p3",
                "code": "for (var i=0;i<3;i++){}  →  i",
                "accept": [
                  "3"
                ],
                "why": "The counter outlived the loop, because `var` belongs to the enclosing function or file."
              },
              {
                "id": "p4",
                "code": "for (let j=0;j<3;j++){}  →  j",
                "accept": [
                  "error",
                  "referenceerror",
                  "ReferenceError"
                ],
                "why": "`j` belonged to the loop and is gone."
              },
              {
                "id": "p5",
                "code": "function f(){ if (true) { const t = 1; } return typeof t; }  →  f()",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "`t` belonged to the `if` block. Outside it there is no `t` at all — and `typeof` is the one question you can ask of a missing name without an error."
              }
            ],
            "hints": [
              {
                "when": "both of the first two answers were the same",
                "say": "They differ only in the keyword. Ask which of the two can see a `{ }` block at all."
              },
              {
                "when": "you picked \"where it runs\" for the third",
                "say": "Scope is fixed by the code as written, before anything runs. Start from the declaration itself: which word introduces it?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict four lines, then run them",
            "prompt": "Predict each line the program prints, in order — type the word it prints. Then run it. Two declarations are in the same block — only one of them stays there.\n",
            "code": "var level = \"top\";\nlet mode = \"light\";\n\nfunction configure(flag) {\n  if (flag) {\n    var level = \"function\";\n    let mode = \"dark\";\n    console.log(mode);\n  }\n  console.log(level);\n  console.log(mode);\n}\n\nconfigure(true);\nconsole.log(level);\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "dark",
                  "\"dark\"",
                  "'dark'"
                ],
                "why": "Inside the `if` block, the block's own `let mode` is there to be read."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "function",
                  "\"function\"",
                  "'function'"
                ],
                "why": "`var level` could not see the `if` block, so its binding belongs to all of `configure` — and it still holds `\"function\"` after the block ends."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "light",
                  "\"light\"",
                  "'light'"
                ],
                "why": "The block's `let mode` ended at its closing brace. The only `mode` left is the one declared at the top."
              },
              {
                "id": "r4",
                "question": "Fourth line printed",
                "accept": [
                  "top",
                  "\"top\"",
                  "'top'"
                ],
                "why": "`configure`'s `var level` belongs to `configure`, not to the top level. The top-level `level` is a different binding and was never assigned."
              }
            ],
            "hints": [
              {
                "when": "you answered \"dark\" for the third line",
                "say": "Find the closing brace of the block that declared the inner `mode`. After it, that binding does not exist."
              },
              {
                "when": "you answered \"function\" for the fourth line",
                "say": "Ask which scope `var level` on line 6 created its binding in. `var` walks out to the nearest function — which is not the top level."
              },
              {
                "when": "you answered \"top\" for the second line",
                "say": "That is what the line would print if `var level` had stayed in the `if` block. Which region does `var` look for?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Name each binding's scope",
            "prompt": "For each marked name, pick the scope its binding belongs to.\n",
            "items": [
              {
                "id": "t1",
                "code": "function save(doc) { if (doc.dirty) { let stamp = Date.now(); } }  →  stamp",
                "choices": [
                  "block",
                  "loop",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "block"
                ],
                "why": "`let` walks out to the nearest braces, which are the `if`'s."
              },
              {
                "id": "t2",
                "code": "function save(doc) { if (doc.dirty) { var stamp = Date.now(); } }  →  stamp",
                "choices": [
                  "block",
                  "loop",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "function"
                ],
                "why": "One keyword changed. `var` walks past the `if`'s braces to the nearest function, `save`."
              },
              {
                "id": "t3",
                "code": "for (const path of files) { load(path); }  →  path",
                "choices": [
                  "block",
                  "loop",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "loop",
                  "block"
                ],
                "why": "A `const` in a loop header belongs to the loop. After it, there is no `path`."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "(top of a module file)  const LIMIT = 50;  →  LIMIT",
                "choices": [
                  "block",
                  "loop",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "module"
                ],
                "why": "A module file's top level is its own scope. Other files see `LIMIT` only if it is exported."
              },
              {
                "id": "t5",
                "code": "function tally(xs) { total = 0; for (const x of xs) total += x; return total; }  →  total",
                "choices": [
                  "block",
                  "loop",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "global"
                ],
                "why": "`total` was never declared, so in sloppy code assigning it creates a global — shared by every call and every file. In a module or strict code it is a ReferenceError instead."
              },
              {
                "id": "t6",
                "code": "function area(width, height) { return width * height; }  →  width",
                "choices": [
                  "block",
                  "loop",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "function"
                ],
                "why": "A parameter belongs to its function's body, and a fresh one is made for every call."
              }
            ],
            "hints": [
              {
                "when": "you answered \"block\" for the second",
                "say": "Look at the keyword first. Which regions can `var` see, and which does it walk straight past?"
              },
              {
                "when": "you answered \"function\" for the first",
                "say": "`let` stops at the nearest braces of any kind. Are there braces between `let stamp` and the function?"
              },
              {
                "when": "you answered \"function\" or \"block\" for the undeclared total",
                "say": "Look for a keyword on the line that first uses `total`. With none, nothing was declared in the function — so where does sloppy JavaScript put it?"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Keep a value that is chosen inside an if",
            "prompt": "Put the lines in order so `badge(user)` returns `\"gold\"` for a user with more than 100 points and `\"member\"` otherwise. The value is chosen inside the `if` and read after it. Two lines are mistakes — leave them out.\n",
            "lines": [
              "function badge(user) {",
              "  let level = \"member\";",
              "  if (user.points > 100) {",
              "    level = \"gold\";",
              "  }",
              "  return level;",
              "}"
            ],
            "distractors": [
              {
                "line": "    let level = \"gold\";",
                "why": "This declares a second `level` belonging to the `if` block. It ends at the closing brace, and `return` reads the outer one — always `\"member\"`."
              },
              {
                "line": "  const level = \"member\";",
                "why": "Right scope, wrong keyword: a `const` binding cannot be reassigned, so `level = \"gold\"` throws a TypeError for every gold user. `let` is for a binding that changes."
              }
            ],
            "why": "The binding has to belong to a region that contains both the line that sets it and the line that reads it — the function body. So it is declared there, before the `if`, and the `if` only assigns to it. Declaring it inside the braces makes a binding that ends before `return`.\n",
            "hints": [
              {
                "when": "you kept `let level = \"gold\"` inside the if",
                "say": "Count the bindings named `level` in your version, and name the scope each belongs to. Which one does `return` read?"
              },
              {
                "when": "you are unsure where the declaration goes",
                "say": "Find every line that uses `level`. The declaration must sit in a region that contains all of them."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a modernising change",
            "prompt": "An AI assistant was asked to \"replace `var` with `let` everywhere\". It did, and now `shippingLabel` throws on every order. Review it: answer each question, then fix it so the tests pass.\n",
            "filename": "label.js",
            "code": "function shippingLabel(order) {\n  if (order.express) {\n    let speed = \"EXPRESS\";\n  } else {\n    let speed = \"standard\";\n  }\n  return order.id + \" · \" + speed;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line throws? Give the line number.",
                "accept": [
                  "7",
                  "line 7"
                ],
                "why": "Line 7 reads `speed`, and at that line there is no `speed` binding at all."
              },
              {
                "id": "c2",
                "question": "Which kind of error?",
                "choices": [
                  "ReferenceError",
                  "TypeError"
                ],
                "accept": [
                  "ReferenceError"
                ],
                "why": "`ReferenceError: speed is not defined`. The name does not exist here — the value was never the problem."
              },
              {
                "id": "c3",
                "question": "Which scope does line 3's `speed` belong to?",
                "choices": [
                  "block",
                  "function",
                  "module",
                  "global"
                ],
                "accept": [
                  "block"
                ],
                "why": "The `if` branch's block. It ends at line 4. With `var` it belonged to the whole function, which is why the old code worked."
              },
              {
                "id": "c4",
                "question": "Would putting `var` back also fix it?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. `var speed` in each branch is one function-scoped binding, and the old code worked. Declaring `let speed` once above the `if` also works, and keeps `speed` out of the rest of the function. Both are correct; which you choose is the kind of call a review should state, not a rule."
              }
            ],
            "exportName": "shippingLabel",
            "starter": "function shippingLabel(order) {\n  if (order.express) {\n    let speed = \"EXPRESS\";\n  } else {\n    let speed = \"standard\";\n  }\n  return order.id + \" · \" + speed;\n}\n",
            "tests": [
              {
                "label": "an express order is labelled EXPRESS",
                "body": "return subject({ id: 7, express: true }) === '7 · EXPRESS';"
              },
              {
                "label": "any other order is labelled standard",
                "body": "return subject({ id: 8, express: false }) === '8 · standard';"
              },
              {
                "label": "no name leaks into the global scope",
                "body": "delete globalThis.speed; subject({ id: 1, express: true }); return !('speed' in globalThis);"
              }
            ],
            "hints": [
              {
                "when": "it returns \"undefined\" instead of a speed",
                "say": "You declared `speed` before the `if` — good — but the branches still say `let`, so each one declares its own `speed` that ends at its brace. The branches should only *assign*."
              },
              {
                "when": "the global-scope test fails",
                "say": "Removing the keywords made `speed` an undeclared assignment, which sloppy JavaScript turns into a global. It needs one declaration, in a scope that contains both the branches and the `return`."
              },
              {
                "when": "it still throws",
                "say": "Find the region that contains line 3, line 5 and line 7. Only a binding declared there can be read on line 7."
              }
            ]
          }
        ]
      }
    },
    "name-resolution": {
      "id": "name-resolution",
      "title": "Which binding a name means",
      "kind": "read",
      "outcome": 4,
      "shape": "conceptual",
      "why": "Outcome 4 asks a student to say what each name in a program refers to, which is `scope.resolution`'s mastery word for word; shadowing is resolution's consequence when two bindings share a name, so the two are one idea met from two sides (D-029). The bug it prevents is silent: a name that resolves to a different binding than its author meant reads correctly on every line.",
      "concepts": [
        "scope.resolution",
        "scope.shadowing"
      ],
      "lede": "One word can name several bindings at once, in different scopes. Which one a line means is decided by where the line is written — never by where the code is called from.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What a declaration creates · Closures keep their surroundings"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Scope chain** — the scopes around a line, innermost first. **Shadowing** — an inner binding with the same name as an outer one, hiding it inside its scope.",
          "entries": [
            "**Scope chain** — the scopes around a line, innermost first.",
            "**Shadowing** — an inner binding with the same name as an outer one, hiding it inside its scope."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four programs, each with one name used in more than one place. Type the value\nasked for — or pick, where a question offers buttons. A guess is fine; that is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const name = \"outer\"; function f() { const name = \"inner\"; return name; } f();  →  name",
                          "accept": [
                            "\"outer\"",
                            "outer",
                            "'outer'"
                          ],
                          "miss": "**If you answered `\"inner\"`, you read the inner declaration as changing the outer name.** Declaring a name inside a function creates a *new* binding in that scope. It hides the outer one there; it does not overwrite it.\n",
                          "why": "`f` created its own binding. The outer `name` was hidden inside `f` and never modified.\n"
                        },
                        {
                          "code": "let n = 1; function bump() { n = 5; } bump();  →  n",
                          "accept": [
                            "5"
                          ],
                          "miss": "**If you answered `1`, you read `n = 5` as making a local.** Only a declaration — `let`, `const`, `var` — makes a new binding. With no keyword, the name is looked up, and the one found is the outer `n`.\n",
                          "why": "`5`. `bump` declares nothing, so `n` means the outer binding, and the assignment changes it.\n"
                        },
                        {
                          "code": "const v = \"module\"; function read() { return v; } function run() { const v = \"run\"; return read(); }  →  run()",
                          "accept": [
                            "\"module\"",
                            "module",
                            "'module'"
                          ],
                          "miss": "**If you answered `\"run\"`, you followed the call.** `read` was *written* at the top level, so it looks outward from there. Where it was called from never enters the search.\n",
                          "why": "`\"module\"`. `read`'s `v` means the binding around where `read` is written.\n"
                        },
                        {
                          "code": "const size = \"L\"; function outer() { const size = \"M\"; function inner() { return size; } return inner(); }  →  outer()",
                          "accept": [
                            "\"M\"",
                            "M",
                            "'M'"
                          ],
                          "why": "`\"M\"`. `inner` has no `size` of its own, so the search steps out one scope to `outer` and stops at the first `size` it finds.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "name = 'outer'\ndef f():\n    name = 'inner'\nf()\nprint(name)",
                          "accept": [
                            "'outer'",
                            "\"outer\"",
                            "outer"
                          ],
                          "miss": "**If you answered `inner` — in Python, assigning inside a function creates a local.** The outer name is untouched unless you say `global`.\n",
                          "why": "Assigning inside a function creates a new local binding rather than changing the outer one — unless you say `global` or `nonlocal`.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Is this allowed? `class C { int name; void F() { string name = \"x\"; } }`",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "miss": "**If you picked no — C# forbids a local shadowing another local, not a field.** They are different kinds of name.\n",
                          "why": "A local *may* shadow a field, because they are different kinds of name. The ban is on shadowing another local.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can read a function and say what each name in it refers to, without running it",
                "I can spot an inner name hiding an outer one, and say which one wins where"
              ]
            }
          ]
        },
        {
          "h": "Look outward, stop at the first"
        },
        {
          "p": "Every name you use has to be matched to a binding. The runtime starts in the\nscope where the name is written and looks outward, one scope at a time, until\nit finds a binding with that name — and then it stops."
        },
        {
          "code": "const tier = \"free\";\n\nfunction show() {\n  const tier = \"pro\";     // a second binding, in show's scope\n  return tier;            // found in show's scope — the search stops here\n}\n\nshow();   // \"pro\"\ntier;     // \"free\" — never touched",
          "lang": "js",
          "file": "resolution.js"
        },
        {
          "p": "That search path is the **scope chain**: this block, the block around it, the\nfunction, the module, the global. Draw each scope as a box inside the next;\nthe search starts in the innermost box around the name and moves out. First\nmatch wins, and nothing further out is consulted. If the search runs out of\nboxes, reading the name throws a `ReferenceError`."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const unit = \"kg\"; function label(w) { return w + unit; }  →  label(3)",
                "accept": [
                  "\"3kg\"",
                  "3kg",
                  "'3kg'"
                ],
                "why": "`\"3kg\"`. `label` has no `unit`, so the search steps out to the module and finds the top-level one.\n"
              },
              {
                "code": "function label(w) { return w + units; }  →  label(3)",
                "accept": [
                  "error",
                  "referenceerror",
                  "reference error",
                  "throws",
                  "it throws"
                ],
                "miss": "**If you answered `\"3undefined\"` — a missing *property* gives `undefined`, but a missing *name* does not.** The search ran out of scopes, and that throws.\n",
                "why": "`ReferenceError: units is not defined`. No scope on the chain has a `units`.\n"
              }
            ]
          }
        },
        {
          "h": "Where it is written, not where it is called"
        },
        {
          "p": "The scope chain is fixed by the text of the program. A function's chain is the\nscopes around where the function is **written**; calling it from somewhere else\ndoes not add that place's names to the search. This is **lexical scoping**."
        },
        {
          "code": "const size = \"large\";\n\nfunction outer() {\n  const size = \"medium\";\n  function inner() {\n    return size;          // written inside outer\n  }\n  return inner();\n}\n\nfunction caller() {\n  const size = \"small\";\n  return outer();         // calls outer from here\n}\n\ncaller();   // \"medium\"",
          "lang": "js",
          "file": "lexical.js"
        },
        {
          "p": "`inner` is written inside `outer`, so its chain is `inner` → `outer` → module.\n`caller`'s `size` is not on it, however the call arrived."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In lexical.js, which line's `size` does `inner` return when `caller()` runs? Give the line number, counting from the top: line 1 is `const size = \"large\"`.",
                "accept": [
                  "4",
                  "line 4"
                ],
                "miss": "**If you said the `\"small\"` line — that is the call path, not the chain.** `inner` was written inside `outer`, so `outer`'s `size` is the first on its chain.\n",
                "why": "Line 4, `\"medium\"`. The chain runs from where `inner` is written.\n"
              }
            ]
          }
        },
        {
          "h": "Shadowing: two bindings, one name"
        },
        {
          "p": "When an inner scope declares a name that an outer scope also has, the inner\nbinding **shadows** the outer one: inside the inner scope, the search finds it\nfirst and stops. Outside, the outer binding is exactly as it was. **Shadowing\nhides; it never overwrites.**"
        },
        {
          "p": "The line that decides it is the declaration. Put the two cases side by side:"
        },
        {
          "code": "let count = 0;\nfunction a() { let count = 5; }   // declares: a new binding in a — shadows\na();\ncount;   // 0\n\nlet total = 0;\nfunction b() { total = 5; }       // no keyword: looks up, finds the outer one\nb();\ntotal;   // 5",
          "lang": "js"
        },
        {
          "p": "Everything is identical except one keyword, and the outcomes are opposite.\nShadowing is legal and sometimes deliberate — a parameter named `user` hiding a\nmodule-level `user` is fine. But a shadow you did not intend reads as a bug\nthat is hard to see, because the code looks correct on every line."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "let best = 0; for (const x of [4, 9]) { if (x > best) { let best = x; } }  →  best",
                "accept": [
                  "0"
                ],
                "miss": "**If you answered `9` — the `let` inside the `if` made a new `best`, belonging to that block.** It shadowed the outer one and ended at the brace. The outer `best` was never assigned.\n",
                "why": "`0`. The inner `let best` is a second binding; the outer one is untouched.\n"
              },
              {
                "code": "const user = \"admin\"; function greet(user) { return \"hi \" + user; }  →  greet(\"ada\")",
                "accept": [
                  "\"hi ada\"",
                  "hi ada",
                  "'hi ada'"
                ],
                "why": "`\"hi ada\"`. The parameter `user` shadows the module's `user` inside `greet` — a deliberate shadow, and a harmless one.\n"
              }
            ]
          }
        },
        {
          "h": "Resolving names, worked"
        },
        {
          "p": "The same three steps every time, whatever the code is about."
        },
        {
          "p": "A report function reads a `limit`:"
        },
        {
          "code": "const limit = 10;\n\nfunction report(rows) {\n  const limit = rows.length;\n  return function first() {\n    return rows.slice(0, limit);\n  };\n}",
          "lang": "js",
          "file": "report.js"
        },
        {
          "code": "// find the scope the name is written in\n//   `limit` is used inside `first`.\n// look there, then step outward until a binding matches\n//   first:  no limit.  report: `const limit = rows.length` — match. Stop.\n// name the binding, and anything it hides\n//   report's limit. It shadows the module's limit, which first never sees."
        },
        {
          "p": "A different shape — a greeting with a parameter and a module name:"
        },
        {
          "code": "let greeting = \"Hello\";\n\nfunction greet(name) {\n  greeting = greeting + \"!\";\n  return greeting + \", \" + name;\n}",
          "lang": "js",
          "file": "greet.js"
        },
        {
          "code": "// find the scope the name is written in\n//   `greeting` is used inside greet.\n// look there, then step outward until a binding matches\n//   greet: only `name` is declared. Module: `let greeting` — match.\n// name the binding, and anything it hides\n//   the module's greeting — and greet ASSIGNS to it, so every call adds\n//   another \"!\". Nothing is shadowed; that is the problem."
        },
        {
          "p": "Now one with the last step left to you:"
        },
        {
          "code": "const rate = 0.1;\n\nfunction price(amount, rate) {\n  return amount + amount * rate;\n}",
          "lang": "js",
          "file": "tax.js"
        },
        {
          "code": "// find the scope the name is written in\n//   `rate` is used inside price.\n// look there, then step outward until a binding matches\n//   price: the parameter `rate` — match. Stop.\n// name the binding, and anything it hides\n//   ______"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "tax.js: `price(100)` — what value does it return?",
                "accept": [
                  "nan",
                  "NaN"
                ],
                "miss": "**If you answered `110` — the parameter `rate` shadows the module's `rate`, and nobody passed one.** Inside `price`, `rate` is the parameter, which is `undefined`.\n",
                "why": "`NaN`. The parameter shadows the module's `rate`; with no second argument it is `undefined`, and `100 * undefined` is `NaN`.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Resolving a name by hand",
            "body": [
              {
                "h": "Resolving a name by hand"
              },
              {
                "code": "// You are looking at a name and want to know what it refers to.\n\n// 1. Is it declared in this block? -> that binding. Stop.\n// 2. No -> step out one block and ask again.\n// 3. Still no -> keep stepping out: function, module, global.\n// 4. Ran out -> reading it throws ReferenceError. (Assigning to it, in\n//    non-strict code, silently creates a global instead.)\n\n// Note what is NOT in the procedure: where the function was CALLED from.\n// Scope is decided by where code is WRITTEN. That is lexical scoping.",
                "lang": "js",
                "file": "resolve.js"
              },
              {
                "p": "Now trace one. Give the value each call returns."
              },
              {
                "code": "let mode = \"light\";\n\nfunction theme() {\n  return mode;\n}\n\nfunction preview() {\n  let mode = \"dark\";\n  return theme();       // ______\n}\n\nfunction toggle() {\n  mode = \"dark\";\n  return theme();       // ______\n}",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`preview()` returns `\"light\"`: its `mode` shadows the module's inside\n`preview`, but `theme` was written at the top level and never sees it.\n`toggle()` returns `\"dark\"`: it declares nothing, so `mode = \"dark\"` changes\nthe module's binding — the one `theme` reads."
              }
            ]
          }
        },
        {
          "video": {
            "title": "Two names, one word, drawn as scopes",
            "caption": "The resolution example with each scope drawn as a box, showing the search stepping outward and stopping at the first match. Watch it if the scope chain is still abstract.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **may an inner name hide an outer one, and what does assigning to an\nouter name take?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Shadowing a local",
                  "Assigning to an outer name from inside a function"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "allowed",
                    "just assign — no declaration means \"the outer one\""
                  ],
                  [
                    "**Python**",
                    "allowed",
                    "needs `global` or `nonlocal`, or the assignment makes a local"
                  ],
                  [
                    "**C#**",
                    "**compile error**",
                    "just assign"
                  ]
                ]
              }
            },
            {
              "p": "JavaScript is permissive on both. An inner declaration may hide an outer name,\nand an assignment with no keyword silently reaches whatever binding the chain\nfinds. C# refuses the shadow at compile time, on the grounds that a shadowed\nlocal is more often a mistake than an intention; Python refuses the silent\nreach-out and makes you say it. Knowing where your language sits tells you\nwhich of the two bugs it will let through."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Shadow on purpose or not at all. A parameter shadowing a module name is\nusually fine; a `let` inside a block that repeats an outer name usually is not."
              },
              {
                "code": "const items = loadAll();\n\nfunction total(items) {          // deliberate: this function works on what it is given\n  let sum = 0;\n  for (const item of items) sum += item.price;\n  return sum;\n}",
                "lang": "js"
              },
              {
                "p": "Linters have a rule, `no-shadow`, for teams that would rather ban it outright."
              }
            ],
            "python": [
              {
                "p": "Reading an outer name works; **assigning** to one needs a declaration that you\nmean the outer binding."
              },
              {
                "code": "count = 0\ndef bump():\n    global count      # without this, the next line creates a local\n    count += 1",
                "lang": "python"
              },
              {
                "p": "`nonlocal` does the same for an enclosing function's binding."
              }
            ],
            "csharp": [
              {
                "p": "Shadowing a local is an error rather than a warning, so a whole class of\naccidental-shadow bug cannot reach you. A local may still shadow a field — the\nreason `this.name = name` is a common constructor line."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then change one thing in the second\nprogram so that `n` stays `1` after `bump()` — and say whether your change is a\nshadow."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "Your team is choosing a lint setting for shadowing."
            },
            {
              "list": [
                "**Ban it** — turn on `no-shadow`, so an inner name may never repeat an outer one, as C# enforces.",
                "**Allow it** — leave shadowing legal and rely on review."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what the ban does to a\nparameter named `user` in a module that also has a `user`, and what allowing it\nrisks in a loop that updates a running total. Name one case where a shadow\nmakes code *safer* — where hiding the outer name stops a function changing it\nby accident."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A name means the **first binding found** looking outward from where it is **written** — the scope chain. Where the code is called from never counts.",
                "**Shadowing** is an inner declaration with an outer name: it **hides** the outer binding there and never changes it.",
                "An assignment with **no keyword** declares nothing, so it reaches the outer binding — or, if there is none, makes a global in sloppy code."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Closures — lexical scoping",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Closures#lexical_scoping",
              "note": "the resolution procedure above, in MDN's words."
            },
            {
              "title": "Scope",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Scope",
              "note": "the kinds of scope the chain passes through."
            }
          ]
        }
      ],
      "_source": "lessons/name-resolution.md",
      "practice": {
        "id": "name-resolution",
        "outcome": 4,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict, then name the first step",
            "prompt": "Type the value for the first two. For the third, pick where the search starts.\n",
            "items": [
              {
                "id": "p1",
                "code": "const t = \"x\"; function f(){ const t = \"y\"; return t; } f();  →  t",
                "accept": [
                  "\"x\"",
                  "x",
                  "'x'"
                ],
                "why": "Shadowing hides the outer binding inside `f`. It never modifies it."
              },
              {
                "id": "p2",
                "code": "const v = \"top\"; function read(){ return v; } function go(){ const v = \"go\"; return read(); }  →  go()",
                "accept": [
                  "\"top\"",
                  "top",
                  "'top'"
                ],
                "why": "`read` was written at the top level, so its search goes from its own body to the module. Being called from `go` adds nothing."
              },
              {
                "id": "p0",
                "question": "A line reads `total`. Which scope do you check first?",
                "choices": [
                  "The block it is written in",
                  "The scope it was called from",
                  "The module",
                  "The global scope"
                ],
                "accept": [
                  "The block it is written in"
                ],
                "why": "Resolution starts in the block the name is written in and steps outward — block, function, module, global — stopping at the first binding it finds. Where the code was called from is not a step at all."
              }
            ],
            "extra": [
              {
                "id": "p3",
                "code": "let n = 1; function f(){ n = 5; } f();  →  n",
                "accept": [
                  "5"
                ],
                "why": "No declaration inside `f`, so `n` resolves outward to the outer binding and the assignment changes it. Only a *declaration* makes a new binding that shadows."
              },
              {
                "id": "p4",
                "code": "let n = 1; function f(){ let n = 5; } f();  →  n",
                "accept": [
                  "1"
                ],
                "why": "One keyword added. `let n` inside `f` is a new binding that shadows the outer one — which stays `1`."
              },
              {
                "id": "p5",
                "code": "function f(){ return missing; }  →  f()",
                "accept": [
                  "error",
                  "referenceerror",
                  "ReferenceError",
                  "reference error"
                ],
                "why": "The search ran out of scopes without finding a `missing`. Reading a name no scope has throws."
              }
            ],
            "hints": [
              {
                "when": "you answered \"go\" for the second",
                "say": "You followed the call. Find where `read` is *written*, and search outward from there."
              },
              {
                "when": "you picked the scope it was called from for the third",
                "say": "That is the model the procedure replaces. Scope is decided by where code is *written*, so the search starts where the name is written."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict four lines, then run them",
            "prompt": "One name, three bindings. Predict each line the program prints, in order — type the word it prints — then run it.\n",
            "code": "let theme = \"light\";\n\nfunction current() {\n  return theme;\n}\n\nfunction preview(theme) {\n  console.log(theme);\n  console.log(current());\n}\n\nfunction apply() {\n  theme = \"dark\";\n  console.log(current());\n}\n\npreview(\"sepia\");\napply();\nconsole.log(theme);\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "sepia",
                  "\"sepia\"",
                  "'sepia'"
                ],
                "why": "Inside `preview`, the parameter `theme` shadows the module's. The search finds it first."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "light",
                  "\"light\"",
                  "'light'"
                ],
                "why": "`current` is written at the top level, so its `theme` is the module's. Being called from inside `preview` adds `preview`'s parameter to nothing."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "dark",
                  "\"dark\"",
                  "'dark'"
                ],
                "why": "`apply` declares nothing, so `theme = \"dark\"` reaches the module's binding — the same one `current` reads."
              },
              {
                "id": "r4",
                "question": "Fourth line printed",
                "accept": [
                  "dark",
                  "\"dark\"",
                  "'dark'"
                ],
                "why": "The module's `theme` was changed by `apply`. `preview`'s parameter was a separate binding and changed nothing out here."
              }
            ],
            "hints": [
              {
                "when": "you answered \"sepia\" for the second line",
                "say": "You followed the call into `current`. Where is `current` written — and is `preview`'s parameter anywhere on the way out from there?"
              },
              {
                "when": "you answered \"light\" for the third or fourth line",
                "say": "Look for a keyword on `apply`'s assignment. Without one, which `theme` does the line find?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Resolve the name",
            "prompt": "Give the value each marked expression produces.",
            "items": [
              {
                "id": "t1",
                "code": "const s=\"large\"; function o(){ const s=\"medium\"; function i(){ return s; } return i(); }  →  o()",
                "accept": [
                  "\"medium\"",
                  "medium",
                  "'medium'"
                ],
                "why": "`i` searches outward and stops at the first `s` it finds, which is `o`'s."
              },
              {
                "id": "t2",
                "code": "function c(){ const s=\"small\"; return o(); }  →  c()",
                "accept": [
                  "\"medium\"",
                  "medium",
                  "'medium'"
                ],
                "why": "Still `\"medium\"`. Scope follows where code is *written*, not where it is called from — `c`'s binding never enters the search."
              }
            ],
            "extra": [
              {
                "id": "t3",
                "code": "const cfg = { size: 1 }; { cfg.size = 2; }  →  cfg.size",
                "accept": [
                  "2"
                ],
                "why": "No new binding was declared inside the block — `cfg.size = 2` found the outer `cfg` and changed the object it points at."
              },
              {
                "id": "t4",
                "code": "const rate = 0.1; function price(a, rate){ return a + a * rate; }  →  price(100)",
                "accept": [
                  "NaN",
                  "nan"
                ],
                "why": "The parameter `rate` shadows the module's inside `price`. Nobody passed one, so it is `undefined`, and the arithmetic gives `NaN`."
              },
              {
                "id": "t5",
                "code": "let msg = \"a\"; { let msg = \"b\"; msg = \"c\"; }  →  msg",
                "accept": [
                  "\"a\"",
                  "a",
                  "'a'"
                ],
                "why": "Inside the block, `msg = \"c\"` finds the block's own `msg` first — the shadow — and changes that. The outer one stays `\"a\"`."
              }
            ],
            "hints": [
              {
                "when": "you answered \"small\" for the second",
                "say": "You followed the call. Scope is lexical — follow where the function was written instead."
              },
              {
                "when": "you answered 110 for the price",
                "say": "Search from inside `price`. Is there a `rate` before you reach the module?"
              },
              {
                "when": "you answered \"c\" for the last extra",
                "say": "The assignment is inside the block. Which `msg` does a name written there find first?"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a pricing change",
            "prompt": "A colleague asked an AI assistant to \"add a 20% EU rate\" and pasted the result. EU quotes still come out at 10%. Review it: answer each question with a line number, then fix it so the tests pass.\n",
            "filename": "quote.js",
            "code": "const rate = 0.1;\n\nfunction withTax(price) {\n  return price + price * rate;\n}\n\nfunction quote(items, region) {\n  const rate = region === \"EU\" ? 0.2 : 0.1;\n  total = 0;\n  for (const item of items) total += item.price;\n  return withTax(total);\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line's `rate` does line 4 read?",
                "accept": [
                  "1",
                  "line 1"
                ],
                "why": "`withTax` was written at the top level, so its search goes from its own body to the module — line 1. Being *called* from `quote` adds nothing to the search."
              },
              {
                "id": "c2",
                "question": "Which line declares a binding nothing ever reads?",
                "accept": [
                  "8",
                  "line 8"
                ],
                "why": "Line 8 shadows line 1 inside `quote` — and nothing inside `quote` reads `rate`. The author wrote it expecting `withTax` to see it."
              },
              {
                "id": "c3",
                "question": "Which line creates a global name by accident?",
                "accept": [
                  "9",
                  "line 9"
                ],
                "why": "`total` is assigned but never declared, so outside strict mode it becomes a global. Every quote now shares — and overwrites — one `total`."
              }
            ],
            "exportName": "quote",
            "starter": "const rate = 0.1;\n\nfunction withTax(price) {\n  return price + price * rate;\n}\n\nfunction quote(items, region) {\n  const rate = region === \"EU\" ? 0.2 : 0.1;\n  total = 0;\n  for (const item of items) total += item.price;\n  return withTax(total);\n}\n",
            "tests": [
              {
                "label": "an EU quote is taxed at 20%",
                "body": "return subject([{ price: 100 }], 'EU') === 120;"
              },
              {
                "label": "any other region is taxed at 10%",
                "body": "return subject([{ price: 100 }], 'US') === 110;"
              },
              {
                "label": "prices are summed before tax",
                "body": "return subject([{ price: 50 }, { price: 50 }], 'EU') === 120;"
              },
              {
                "label": "no name leaks into the global scope",
                "body": "delete globalThis.total; subject([{ price: 1 }], 'US'); return !('total' in globalThis);"
              }
            ],
            "hints": [
              {
                "when": "EU quotes are still 110",
                "say": "Changing which `rate` line 8 declares does not help, because `withTax` never searches `quote`'s scope. The rate `quote` chose has to reach `withTax` some other way — what does a function receive from the call itself?"
              },
              {
                "when": "the global-scope test fails",
                "say": "Every name a function uses as its own needs a declaration in that function. Which keyword fits a value that is reassigned in the loop?"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Remove the shadow",
            "prompt": "`largest` should return the biggest number in a list. It returns the first one instead, with no error. Fix it without adding a new variable. Then, in one sentence: how many bindings named `max` did the original have, and which one did `return` read?\n",
            "exportName": "largest",
            "filename": "largest.js",
            "starter": "// Should return the largest value in the list.\n// Returns the first value instead — silently.\nfunction largest(values) {\n  let max = values[0];\n  for (const v of values) {\n    if (v > max) {\n      let max = v;\n    }\n  }\n  return max;\n}\n",
            "tests": [
              {
                "label": "finds a maximum in the middle",
                "body": "return subject([3, 9, 4]) === 9;"
              },
              {
                "label": "finds a maximum at the end",
                "body": "return subject([1, 2, 3]) === 3;"
              },
              {
                "label": "handles all-negative values",
                "body": "return subject([-2, -7, -1]) === -1;"
              },
              {
                "label": "a single value is its own maximum",
                "body": "return subject([5]) === 5;"
              }
            ],
            "hints": [
              {
                "when": "it still returns the first value",
                "say": "Count the declarations of `max`, and name the scope each one lives in. Then ask which of them the line inside the `if` changes — and which one `return` reads."
              },
              {
                "when": "you renamed the inner variable",
                "say": "That removes the name collision, but the inner binding still ends at its closing brace and the outer `max` never changes. The outer one is the one you meant to update."
              },
              {
                "when": "you changed the inner `let` to `var` and got a SyntaxError",
                "say": "`var` ignores the block, so it tries to declare a second `max` in the same function scope as the `let` — which is refused before anything runs. Ask what the line needs to *do* to the existing binding. Does it need a keyword at all?"
              }
            ]
          }
        ]
      }
    },
    "copy-vs-alias": {
      "id": "copy-vs-alias",
      "title": "Copying versus aliasing",
      "kind": "read",
      "outcome": 2,
      "shape": "conceptual",
      "why": "`b = a` looks like a copy, and for objects and arrays it is not — the family with the least overlap with a first course and a defect students ship. Every later lesson on shared data, and the debugger, starts from this model.",
      "concepts": [
        "data.value-vs-reference",
        "data.mutation-vs-rebinding"
      ],
      "lede": "`b = a` looks like it makes a copy. For some values it does, and for the ones you use most it does not — and the difference is invisible until something changes underneath you.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Equality: == versus === · What a function can change · Making data unchangeable"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Alias** — a second name for the same thing. Not a copy of it.",
          "entries": [
            "**Alias** — a second name for the same thing. Not a copy of it."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four short programs. Type the final value, or pick an answer where the question offers buttons."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "let x = 5; let y = x; y = y + 1;  →  x",
                          "accept": [
                            "5"
                          ],
                          "why": "`x` holds the number itself. `y = x` handed `y` its own copy, and changing `y` could not reach back.\n"
                        },
                        {
                          "code": "let a = [1, 2]; let b = a; b.push(3);  →  a.length",
                          "accept": [
                            "3"
                          ],
                          "miss": "**If you answered `2`, you read `b = a` as making a copy — and it does, but a copy of *what*?** For a number it copies the number. For an array it copies the **reference** — the arrow, not the thing the arrow points at. One arrow duplicated still points at one array.\n",
                          "why": "`a` does not hold the array — it holds a *reference* to it. `b = a` copied the reference, so both names point at one array, and `b.push` changed the thing they both point at.\n"
                        },
                        {
                          "code": "const o = { n: 1 }; const p = o; p.n = 2;  →  o.n",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you expected `const` to have stopped this — `const` is about the name, not the value.** It promises `o` will never point at a different object. It promises nothing about that object's contents.\n",
                          "why": "The same again, and `const` did not help. It is the same object under two names.\n"
                        },
                        {
                          "code": "const arr = [1]; arr.push(2);  →  arr.length",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you expected an error, the rule you want is not \"`push` mutates and `=` does not\".** The root is whether the value is held directly or through a reference. `const` blocks moving the name; it cannot block changing what the name points at.\n",
                          "why": "`const` does not mean unchangeable. It means the *name* cannot be pointed somewhere else. What it points at is as changeable as ever.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "x = 5; y = x; y = y + 1  →  x",
                          "accept": [
                            "5"
                          ],
                          "why": "`x` was rebound to a new integer and `y` still names the original. Numbers are immutable, so nothing could have been changed in place.\n"
                        },
                        {
                          "code": "a = [1, 2]; b = a; b.append(3)  →  len(a)",
                          "accept": [
                            "3"
                          ],
                          "why": "`b = a` made a second name for one list. `append` changed that list, and both names see it.\n"
                        },
                        {
                          "question": "After `t = (1, 2); u = t`, can anything done through `u` change `t`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "A tuple is immutable, so aliasing it is harmless. Immutability is what makes sharing safe — not the assignment.\n"
                        },
                        {
                          "question": "After `a = [1]; a += [2]`, was the list `a` changed in place?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "`+=` on a list mutates it, while `+=` on a number rebinds. The same operator, two behaviours, decided by whether the type is mutable.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "int x = 5; int y = x; y = y + 1;  →  x",
                          "accept": [
                            "5"
                          ],
                          "why": "`int` is a value type. `y` got its own copy of the bits.\n"
                        },
                        {
                          "code": "var a = new List<int>{1,2}; var b = a; b.Add(3);  →  a.Count",
                          "accept": [
                            "3"
                          ],
                          "why": "`List<T>` is a reference type, so `b` is a second name for one list.\n"
                        },
                        {
                          "code": "var p = new Point(1,2); var q = p; q.X = 9;  →  p.X  (Point is a struct)",
                          "accept": [
                            "1"
                          ],
                          "why": "A `struct` is a value type — `q` got a copy. This is the distinction JavaScript cannot show you, because it has no user-defined value types.\n"
                        },
                        {
                          "question": "A field is declared `readonly List<int> nums = new();`. Is `nums.Add(1);` allowed?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "`readonly` stops reassignment of the field, not mutation of the list — exactly what `const` does in JavaScript.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can tell whether assigning a variable copied the data or pointed at the same data, and demonstrate the difference",
                "I can say what `const` does and does not promise",
                "I can predict, for a value in my language, whether two names for it can affect each other"
              ]
            }
          ]
        },
        {
          "h": "Two names, one thing"
        },
        {
          "p": "A variable does not contain a value so much as *refer* to one. For some values\nthe distinction never shows, and for others it is the whole story."
        },
        {
          "code": "// a number: the variable holds it\nlet x = 5;\nlet y = x;      // y gets its own 5\ny = 6;          // x is still 5\n\n// an array: the variable holds a reference to it\nlet a = [1, 2];\nlet b = a;      // b gets its own copy of the REFERENCE\nb.push(3);      // both names see [1, 2, 3]",
          "lang": "js"
        },
        {
          "p": "Both lines copied something. The second copied an arrow."
        },
        {
          "block": "notice",
          "label": "How to see it without running anything",
          "body": [
            {
              "p": "Ask: **could two names ever disagree about this value?** If the value can be\nchanged in place, they cannot disagree — a change through one name is a change\nthe other sees. If the value cannot be changed in place, they can only diverge\nby one of them being pointed somewhere else, which affects nobody."
            }
          ]
        },
        {
          "h": "Which values are which"
        },
        {
          "p": "Every language draws this line somewhere. JavaScript draws it in one place and\nnever moves it:"
        },
        {
          "table": {
            "head": [
              "",
              "Held directly",
              "Held by reference"
            ],
            "rows": [
              [
                "**JavaScript**",
                "number, string, boolean, `null`, `undefined`, symbol, bigint",
                "object, array, function — everything else"
              ]
            ]
          }
        },
        {
          "p": "Strings are worth a second look. They are held directly *and* they are\nimmutable, which is why `s.toUpperCase()` returns a new string rather than\nchanging yours. You cannot alias a string into trouble."
        },
        {
          "h": "Mutation is not rebinding"
        },
        {
          "p": "Two different operations that both use `=` somewhere, and the confusion between\nthem is most of what goes wrong with shared data."
        },
        {
          "code": "const list = [1, 2];\n\nlist.push(3);      // MUTATION — changing the thing. Allowed.\nlist[0] = 99;      // MUTATION — also changing the thing. Allowed.\nlist = [4, 5];     // REBINDING — pointing the name elsewhere. TypeError.",
          "lang": "js"
        },
        {
          "p": "`const` blocks the third line only. If you want the *contents* protected, that is\na different tool — *Making data unchangeable* covers it."
        },
        {
          "scaffold": {
            "label": "Working out what a line did",
            "body": [
              {
                "h": "Working out what a line did"
              },
              {
                "p": "Three questions, in order, and you never need a fourth."
              },
              {
                "code": "// For any line that assigns:\n\n// 1. Is the value on the right held directly, or by reference?\n//    directly  -> a copy was made; the two names are independent. Done.\n//    reference -> keep going.\n\n// 2. Did the line REBIND a name, or MUTATE the thing?\n//    `x = ...`        rebinds — only this name moves.\n//    `x.foo = ...`    mutates — everyone pointing here sees it.\n//    `x.push(...)`    mutates.\n\n// 3. How many names currently point at that thing?\n//    more than one -> the mutation is visible through all of them.",
                "lang": "js",
                "file": "trace.js"
              },
              {
                "p": "Now run it on this. Answer the last line yourself."
              },
              {
                "code": "const config = { retries: 3 };\nconst backup = config;\nconfig.retries = 5;\n\n// 1. held directly or by reference?   ______\n// 2. rebind or mutate?                ______\n// 3. how many names point at it?      ______\n// so backup.retries is              ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "You never had to memorise which methods mutate. You asked whether there was a\nshared reference and whether the line changed the thing or the name."
              }
            ]
          }
        },
        {
          "video": {
            "title": "One array, two names, drawn as it runs",
            "caption": "The same four programs from the opening, with the memory drawn beside the code so you can watch the second arrow appear. Watch it if the arrow idea has not landed; skip it if it has.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis here is **which values a language holds directly**, and it is the one\nplace JavaScript is least generous."
            },
            {
              "table": {
                "head": [
                  "",
                  "Held directly",
                  "Can you define your own?"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "seven built-in primitive types",
                    "**No**"
                  ],
                  [
                    "**C#**",
                    "primitives *and* any `struct` you write",
                    "Yes"
                  ]
                ]
              }
            },
            {
              "p": "That second column is the thing to carry. In C# you can declare that a `Point`\nis a value — copied on assignment, never aliased — and the language enforces it.\nIn JavaScript every type you define is a reference type, always, and the only way\nto get value-like behaviour is to copy deliberately or to freeze."
            },
            {
              "p": "It is not that one is stricter. It is that one lets *you* make the decision per\ntype and the other made it once, for everything."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "The line is fixed and short. Primitives copy; everything else aliases."
              },
              {
                "code": "// copies\nlet n = 1, s = \"hi\", t = true;\n\n// aliases\nlet o = {}, a = [], f = () => {};",
                "lang": "js"
              },
              {
                "p": "`const` on any of these blocks rebinding only. There is no declaration that makes\nan object's contents unchangeable — for that you need `Object.freeze`, in\n*Making data unchangeable*."
              }
            ],
            "python": [
              {
                "p": "Same split, different vocabulary: Python talks about **mutable** and\n**immutable** types rather than value and reference."
              },
              {
                "code": "# immutable — aliasing is harmless\nn, s, t = 1, \"hi\", (1, 2)\n\n# mutable — aliasing is the whole risk\nlst, dct, st = [], {}, set()",
                "lang": "python"
              },
              {
                "p": "Everything in Python is a reference; what differs is whether the referenced thing\ncan be changed. That lands in the same place as JavaScript's split, by a\ndifferent route — and it is why `a += [2]` mutates a list but `n += 1` rebinds a\nnumber."
              }
            ],
            "csharp": [
              {
                "p": "C# is the one track where you choose. `class` gives a reference type, `struct`\ngives a value type, and the choice is yours per type."
              },
              {
                "code": "class Basket  { public int Count; }   // reference — assignment aliases\nstruct Point  { public int X, Y; }    // value     — assignment copies",
                "lang": "csharp"
              },
              {
                "p": "`readonly` behaves like `const` in JavaScript: it stops the field being\nreassigned, not the object being changed. For contents, you want an immutable\ntype or a `record`."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for each, say in three words\nwhat the line did — *copied a value*, *copied a reference*, *mutated the thing*,\nor *rebound the name*. Naming the operation is the skill; the answers follow."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A function receives a list of orders and needs to return them sorted, without\ndisturbing the caller's list."
            },
            {
              "code": "function sortedByDate(orders) {\n  // orders.sort() sorts IN PLACE and returns the same array\n}",
              "lang": "js"
            },
            {
              "p": "Two ways to honour that:"
            },
            {
              "list": [
                "**Copy first, sort the copy** — `[...orders].sort(...)`",
                "**Document that it mutates** — sort in place and say so in the name"
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: how large the list would have to\nget before copying matters, and what happens in the second design when a caller\ndoes not read the name carefully. Both ship in real code; naming the cost is the\nanswer."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Assignment always copies. The question is whether it copied a **value** or a **reference**.",
                "In JavaScript, the seven primitives copy and everything else aliases. The line never moves.",
                "**`const` is about the name.** It blocks rebinding, not mutation.",
                "**Mutation** changes the thing every name can see. **Rebinding** moves one name and affects nobody else.",
                "Aliasing is only dangerous when the thing is changeable — which is why immutable values can be shared freely."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Primitive",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Primitive",
              "note": "the seven types that are held directly, and the one-line reason it matters."
            },
            {
              "title": "const",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/const",
              "note": "read the paragraph that begins \"The const declaration creates block-scoped constants\"; it says explicitly that contents are not protected."
            },
            {
              "title": "Array.prototype.sort()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort",
              "note": "note \"sorts the elements **in place**\", which is the apply prompt above."
            }
          ]
        }
      ],
      "_source": "lessons/copy-vs-alias.md",
      "practice": {
        "id": "copy-vs-alias",
        "outcome": 2,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the final value",
            "prompt": "Three short programs. Type the final value, or pick the answer where there are buttons.\n",
            "items": [
              {
                "id": "p1",
                "code": "let a = [1, 2]; let b = a; b.push(3);  →  a.length",
                "accept": [
                  "3"
                ],
                "why": "`b = a` copied the reference, not the array. One array, two names, and `push` changed the thing both names point at.\n"
              },
              {
                "id": "p2",
                "code": "let c = { v: 1 }; let d = c; d = { v: 2 };  →  c.v",
                "accept": [
                  "1"
                ],
                "why": "`d = { v: 2 }` **rebinds** — it points `d` at a new object and leaves the first one alone. Only a change *to* the object would have reached `c`.\n"
              },
              {
                "id": "p3",
                "question": "`config` is `{ retries: 3 }`, and the line is `const backup = config;`. First question: is the value held directly, or by reference?",
                "choices": [
                  "Held directly",
                  "By reference"
                ],
                "accept": [
                  "By reference"
                ],
                "why": "By reference: `config` is an object, and every object is. So `backup = config` copied an arrow, and the next question is whether anything mutates what the arrow points at.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "let x = 5; let y = x; y = y + 1;  →  x",
                "accept": [
                  "5"
                ],
                "why": "A number is held directly, so `y` got its own copy. Nothing `y` does can reach `x`.\n"
              },
              {
                "id": "p5",
                "code": "const o = { n: 1 }; const p = o; p.n = 9;  →  o.n",
                "accept": [
                  "9"
                ],
                "why": "`const` protects the name, not the contents. Still one object, still two names, and `p.n = 9` changed the object.\n"
              },
              {
                "id": "p6",
                "code": "let s = \"hi\"; let t = s; t = t.toUpperCase();  →  s",
                "accept": [
                  "\"hi\"",
                  "hi",
                  "'hi'"
                ],
                "why": "Strings are held directly *and* immutable, so `toUpperCase` had to return a new one, and `t = …` rebound `t` to it. There is no way to alias a string into trouble.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 2 for the first",
                "say": "You treated `b = a` as making a new array. It made a new *name*. Ask how many arrays exist after that line — the answer is one.\n"
              },
              {
                "when": "you answered 2 for the second",
                "say": "Look at the left of the third `=`. `d = …` moves the name `d`; `d.v = …` would change the object. Only one of those can be seen through `c`.\n"
              },
              {
                "when": "you are unsure which values are held by reference",
                "say": "The line is fixed in JavaScript: the seven primitives — number, string, boolean, `null`, `undefined`, symbol, bigint — are held directly. Everything else, objects and arrays included, is not.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict, then run it",
            "prompt": "One array, a second name for it, and a real copy of it. Predict what each `console.log` prints, then run the program and compare.\n",
            "code": "const scores = [70, 85];\nconst same = scores;\nconst copy = [...scores];\n\nsame.push(90);\ncopy.push(100);\n\nconsole.log(scores.length);\nconsole.log(copy.length);\nconsole.log(same === scores);\nconsole.log(copy === scores);\n\nlet name = \"ada\";\nlet other = name;\nother = other.toUpperCase();\nconsole.log(name);\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(scores.length)",
                "accept": [
                  "3"
                ],
                "why": "`same` is a second name for `scores`, so `same.push(90)` pushed onto the one array both names share. `[70, 85, 90]`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(copy.length)",
                "accept": [
                  "3"
                ],
                "why": "`copy` is a different array that started with the same two numbers. It got its own push, `100`, and nothing else — `[70, 85, 100]`.\n"
              },
              {
                "id": "r3",
                "code": "console.log(same === scores)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`===` on arrays asks \"one array?\", not \"same contents?\". `same` and `scores` are one array, so `true`.\n"
              },
              {
                "id": "r4",
                "code": "console.log(copy === scores)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "Two arrays, even had their contents matched. `[...scores]` built a new one; that is what made it a copy rather than an alias.\n"
              },
              {
                "id": "r5",
                "code": "console.log(name)",
                "accept": [
                  "ada",
                  "\"ada\"",
                  "'ada'"
                ],
                "why": "A string is held directly, and `other = …` rebound `other`. `name` was never reachable from `other`.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 2 for scores.length",
                "say": "Count the pushes that reached the `scores` array, not the pushes written on the name `scores`. Which names point at it?\n"
              },
              {
                "when": "you predicted 4 for copy.length",
                "say": "Only one push was written on `copy`. For `same.push(90)` to reach `copy`, the two would have to be one array — and `[...scores]` made a new one.\n"
              },
              {
                "when": "you predicted false for same === scores",
                "say": "`===` between two objects or arrays is an identity question: is it one thing? Ask how many arrays `const same = scores` created.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace the arrows, line by line",
            "prompt": "One program, five lines. After each line, ask the lesson's three questions: held directly or by reference, rebind or mutate, and how many names point at the thing.\n",
            "items": [
              {
                "id": "t1",
                "code": "1  let a = [1, 2];\n2  let b = a;\n3  b.push(3);\n4  b = [9];\n5  b.push(4);\n\nafter line 3  →  a\n",
                "accept": [
                  "[1,2,3]",
                  "[1, 2, 3]",
                  "1,2,3",
                  "1, 2, 3"
                ],
                "why": "Line 2 made `b` a second name for the one array, and line 3 **mutated** that array. `a` sees it.\n"
              },
              {
                "id": "t2",
                "question": "Same program, line 4 (`b = [9];`): did it mutate or rebind?",
                "choices": [
                  "Mutate",
                  "Rebind"
                ],
                "accept": [
                  "Rebind"
                ],
                "why": "`b = [9]` points the name `b` at a brand-new array. Nothing was changed — one name moved.\n"
              },
              {
                "id": "t3",
                "question": "Same program, after line 5: what is `a`? Type its contents, like `[1, 2]`.",
                "accept": [
                  "[1,2,3]",
                  "[1, 2, 3]",
                  "1,2,3",
                  "1, 2, 3"
                ],
                "why": "After line 4, `b` no longer points at `a`'s array, so line 5's push lands in `[9]`. `a` is exactly what line 3 left it.\n"
              },
              {
                "id": "t4",
                "question": "Same program, after line 5: how many arrays exist? Type a number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "Two: the one made on line 1 (still named by `a`) and the one made on line 4 (named by `b`). Line 2 made a name, not an array.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "const c = { v: 1 }; const d = c; d.v = 2;  →  c.v",
                "accept": [
                  "2"
                ],
                "why": "`d.v = 2` is a **mutation** — it changed the object. Every name pointing at that object sees it.\n"
              },
              {
                "id": "t6",
                "code": "let c = { v: 1 }; let d = c; d = { v: 2 };  →  c.v",
                "accept": [
                  "1"
                ],
                "why": "`d = { v: 2 }` is a **rebinding** — one character away from the line above, and the opposite result.\n"
              },
              {
                "id": "t7",
                "code": "const n = [1]; n.push(2);  →  n.length",
                "accept": [
                  "2"
                ],
                "why": "`const` allowed this because `push` never moves the name. Had you written `n = [1, 2]` you would have had a TypeError.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered [1,2,3,4] after line 5",
                "say": "You kept `b` pointing at `a`'s array after line 4. Line 4 has nothing but a name on the left of `=`: that moves the name. Where does `b` point from then on?\n"
              },
              {
                "when": "you answered [1,2] after line 3",
                "say": "Line 2 copied the reference, so line 3's push happened to the array `a` names. Count arrays after line 2 — one.\n"
              },
              {
                "when": "you answered 3 for how many arrays",
                "say": "Only an array literal — `[…]` — or something that builds one makes a new array. Count the literals, not the names.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Build a function that copies before it changes",
            "prompt": "Put the lines in order to make `addSong(playlist, song)`: it returns a new playlist with the song on the end, and the caller's playlist is untouched. Two lines do not belong.\n",
            "lines": [
              "function addSong(playlist, song) {",
              "  const next = [...playlist];",
              "  next.push(song);",
              "  return next;",
              "}"
            ],
            "distractors": [
              {
                "line": "  const next = playlist;",
                "why": "This copies the reference, not the array. `next` would be a second name for the caller's playlist, and pushing to it pushes to theirs.\n"
              },
              {
                "line": "  playlist.push(song);",
                "why": "This mutates the array the caller handed in. It works, and it changes their playlist — the one thing the function promised not to do.\n"
              }
            ],
            "why": "`[...playlist]` makes a new array holding the same songs, so the push lands in the copy. Copy first, then change the copy: the order of those two lines is the whole function.\n",
            "hints": [
              {
                "when": "you are choosing between the two `const next` lines",
                "say": "Ask whether each one makes a new array or a new name. Only one of them has anything on the right that builds an array.\n"
              },
              {
                "when": "you put the push before the copy",
                "say": "The copy has to exist before you can push to it — and if the push were on `playlist`, it would already have changed the caller's.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Make it stop sorting the caller's list",
            "prompt": "This `sortedByDate` works, and it sorts the caller's array in place — `Array.prototype.sort` changes the array it is called on and returns that same array. Change one behaviour: return a **new** sorted array and leave the caller's exactly as it was. Then, in one sentence: why did the original pass \"sorts oldest first\" and fail everything about the caller's array?\n",
            "exportName": "sortedByDate",
            "filename": "orders.js",
            "starter": "// Sorts oldest-first by `date` — IN PLACE, and returns the same array.\n// Change it to return a NEW array and leave the caller's untouched.\nfunction sortedByDate(orders) {\n  return orders.sort((a, b) => a.date - b.date);\n}\n",
            "tests": [
              {
                "label": "sorts oldest first",
                "body": "const o = [{date: 3}, {date: 1}, {date: 2}]; return subject(o).map(x => x.date).join(',') === '1,2,3';"
              },
              {
                "label": "the caller's array is untouched",
                "body": "const o = [{date: 3}, {date: 1}, {date: 2}]; subject(o); return o.map(x => x.date).join(',') === '3,1,2';"
              },
              {
                "label": "returns a different array, not the same one",
                "body": "const o = [{date: 2}, {date: 1}]; return subject(o) !== o;"
              },
              {
                "label": "10 sorts after 9",
                "body": "return subject([{date: 10}, {date: 9}]).map(x => x.date).join(',') === '9,10';"
              },
              {
                "label": "an empty list is fine",
                "body": "const r = subject([]); return Array.isArray(r) && r.length === 0;"
              },
              {
                "label": "a single order is fine",
                "body": "return subject([{date: 7}]).map(x => x.date).join(',') === '7';"
              }
            ],
            "hints": [
              {
                "when": "the caller's array is still being changed",
                "say": "`sort` mutates the array it is called on. Whatever you call it on must be a new array — so the copy has to happen *before* the sort, not after it.\n"
              },
              {
                "when": "you wrote `[...orders.sort(…)]` and it still fails",
                "say": "Read it inside-out: the sort runs on `orders` first, and then the damage is copied. Move the copy so the sort runs on it.\n"
              },
              {
                "when": "you assigned `const copy = orders` and sorted the copy",
                "say": "That is a second name for the caller's array, not a second array. What builds a new array from an old one?\n"
              },
              {
                "when": "\"sorts oldest first\" and \"10 sorts after 9\" now fail",
                "say": "Did the comparator go missing? Without one, `sort` compares by converting to strings — objects all become the same string, and `\"10\"` sorts before `\"9\"`.\n"
              }
            ]
          }
        ]
      }
    },
    "what-gets-shared": {
      "id": "what-gets-shared",
      "title": "What a function can change",
      "kind": "read",
      "outcome": 2,
      "shape": "conceptual",
      "why": "Outcome 2's first consequence of shared references: a function can change the data you hand it, and the change then shows up somewhere nobody wrote to it. Both are defects students ship (`concepts.md`, family B), and this is where a function that acts (`fn.side-effects`) turns out to include acting on your data (D-014).",
      "concepts": [
        "data.argument-passing",
        "data.aliasing-bugs"
      ],
      "lede": "Passing a value to a function is an assignment in disguise. So a function can reach into the array you passed and change it — and it cannot touch your variable. When that reach goes unnoticed, a value changes somewhere nobody wrote to it.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Copying versus aliasing · Functions, pass 1 — declare, call, return · Meet the debugger"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Parameter** — the function's own name for what it was passed. **Aliasing bug** — a change made through one name that shows up, unwanted, through another name for the same thing.",
          "entries": [
            "**Parameter** — the function's own name for what it was passed.",
            "**Aliasing bug** — a change made through one name that shows up, unwanted, through another name for the same thing."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four programs. Type the final value. A guess is fine; that is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "function f(l) { l.push(9); }   const a = [1];   f(a);   →  a.length",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1` because the function got a copy — it got a copy of the *reference*.** A copied arrow points at the same array. Pushing through it pushes to yours.\n",
                          "why": "`2`. The function received a second arrow to your array, and mutating through it is mutating yours.\n"
                        },
                        {
                          "code": "function g(l) { l = [9]; }   const a = [1];   g(a);   →  a.length",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If you answered `2` here and `2` above — note what changed.** `l = [9]` has only a name on the left. It points the function's `l` somewhere new; your `a` is a different name and did not move.\n",
                          "why": "`1`. The function rebound its own parameter. That moved the function's name and touched nothing of yours. One line different from the last, opposite result.\n"
                        },
                        {
                          "code": "function last(list) { return list.reverse()[0]; }   const s = [1, 2, 3];   last(s);   →  s[0]",
                          "accept": [
                            "3"
                          ],
                          "miss": "**If you answered `1` because `last` only answers a question — it answers it by reversing your array first.** `reverse` works in place, so the caller's array is now `[3, 2, 1]`.\n",
                          "why": "`3`. The function looked like it only answered a question, and on the way it changed the array it was handed.\n"
                        },
                        {
                          "code": "const cache = {};   function user(id) { if (!cache[id]) cache[id] = { name: \"Ada\" }; return cache[id]; }   user(1).name = \"Bo\";   →  user(1).name",
                          "accept": [
                            "\"Bo\"",
                            "Bo",
                            "'Bo'"
                          ],
                          "miss": "**If you answered `\"Ada\"` because the second call looks the user up again — it looks up the same object.** The cache hands every caller the one object it stored, so the first caller's change is the second caller's answer.\n",
                          "why": "`\"Bo\"`. One object, handed to two callers. A change made by one appears for the other, and neither line that reads it wrote to it.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "def f(l): l.append(9)\\n\\na = [1]; f(a)  →  len(a)",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1` — the parameter is another name for your list, not a copy of it.**\n",
                          "why": "The parameter is another name for your list. Appending through it appends to yours.\n"
                        },
                        {
                          "code": "def g(l): l = [9]\\n\\na = [1]; g(a)  →  len(a)",
                          "accept": [
                            "1"
                          ],
                          "why": "Rebinding a parameter rebinds only the function's local name.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "void F(List<int> l) { l.Add(9); }  var a = new List<int>{1}; F(a);  →  a.Count",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1` — `List<T>` is a reference type, so the method got a second reference to your list.**\n",
                          "why": "`List<T>` is a reference type, so the method mutated your list.\n"
                        },
                        {
                          "code": "void G(List<int> l) { l = new(); }  var a = new List<int>{1}; G(a);  →  a.Count",
                          "accept": [
                            "1"
                          ],
                          "why": "Reassigning the parameter moves the method's local name only — unless the parameter was declared `ref`.\n"
                        },
                        {
                          "code": "void H(ref List<int> l) { l = new(); }  var a = new List<int>{1}; H(ref a);  →  a.Count",
                          "accept": [
                            "0"
                          ],
                          "why": "`ref` is C# making the difference explicit at the call site: this method may rebind *your* variable, and both sides have to say so.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can predict whether my data survives a function call unchanged",
                "I can recognise the defect where a value changes somewhere nobody wrote to it, and find the line that shared it"
              ]
            }
          ]
        },
        {
          "h": "A parameter is a second name"
        },
        {
          "p": "When you call `f(a)`, the function's parameter is assigned what you passed —\nas if the first line of the function were `l = a`. Everything from *Copying\nversus aliasing* applies to that hidden assignment."
        },
        {
          "list": [
            "If `a` holds a number or a string, the parameter gets its own copy. Nothing the function does can reach yours.",
            "If `a` holds an array or an object, the parameter gets a copy of the **reference**. Now two names — yours outside, the function's inside — point at one array."
          ]
        },
        {
          "code": "caller          function\n  a ───────┐      l ──┐\n           ▼          │\n         [ 1 ] ◄──────┘       one array, two arrows",
          "lang": "text"
        },
        {
          "p": "That gives the function exactly one power over your data and denies it another."
        },
        {
          "code": "function mutate(list) { list.push(9); }   // CAN — reaches your array\nfunction rebind(list) { list = [9]; }     // CANNOT — moves its own name only\n\nconst mine = [1];\nmutate(mine);   // mine is now [1, 9]\nrebind(mine);   // mine is still [1, 9]",
          "lang": "js"
        },
        {
          "block": "notice",
          "label": "The rule, in one line",
          "body": [
            {
              "p": "A function can change **what you pointed at**. It cannot change **where you\npoint**."
            }
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "function h(o) { o.n = 9; }   const x = { n: 1 };   h(x);   →  x.n",
                "accept": [
                  "9"
                ],
                "why": "`9`. Same rule as an array: `o` and `x` are two names for one object, and `o.n = 9` changed the object.\n"
              },
              {
                "code": "function k(s) { s = s + \"!\"; }   let t = \"hi\";   k(t);   →  t",
                "accept": [
                  "\"hi\"",
                  "hi",
                  "'hi'"
                ],
                "miss": "**If you answered `\"hi!\"` — a string is held directly, so `s` got its own copy.** And `s = …` is a rebinding, which never reaches the caller anyway.\n",
                "why": "`\"hi\"`. There was no shared thing to change. Strings cannot be changed in place, so the only thing the function could do was rebind its own name.\n"
              },
              {
                "code": "function empty(list) { list.length = 0; }   const q = [4, 5];   empty(q);   →  q.length",
                "accept": [
                  "0"
                ],
                "miss": "**If you answered `2` — look at what is on the left of `=`.** It is not `list`; it is `list.length`, a property of the array. That changes the array, and the array is yours.\n",
                "why": "`0`. Writing to a property of the array mutates it — through the function's name, into the caller's array.\n"
              }
            ]
          }
        },
        {
          "h": "Predicting a call, worked"
        },
        {
          "p": "You do not need to read a function's mind. You need to find each line that\ntouches a parameter and ask one question of it: **does this change the thing,\nor move the name?** Two examples. The first labels the four subgoals; the\nsecond asks the same questions without the labels."
        },
        {
          "code": "function applyDiscount(order, percent) {\n  order.total = order.total * (1 - percent / 100);\n}\n\nconst mine = { total: 80 };\napplyDiscount(mine, 25);\n\n// name what the caller passes\n//   mine -> an object, so the parameter `order` is a second name for it.\n//   25   -> a number, so `percent` is a copy. Nothing can reach back.\n// find the lines that touch a parameter\n//   order.total = ...\n// classify it — mutate, or rebind?\n//   `order.total` is on the left: a property of the object. Mutate.\n// say what the caller sees\n//   mine.total is 60. The caller's object was changed.",
          "lang": "js",
          "file": "discount.js"
        },
        {
          "code": "function addItem(list, item) {\n  list = list.concat([item]);\n  return list;\n}\n\nconst basket = [\"tea\"];\nconst bigger = addItem(basket, \"milk\");\n\n// basket is an array, so `list` starts as a second name for it.\n// The one line that touches it: list = list.concat([item]);\n//   `concat` builds a NEW array and leaves the old one alone, and\n//   `list = ...` moves the function's name to the new array. Rebind.\n// basket is still [\"tea\"]. bigger is [\"tea\", \"milk\"] — a different array.",
          "lang": "js",
          "file": "add-item.js"
        },
        {
          "p": "The second function reads almost like the first and does the opposite. What\ndecided it was not the name of the function but **which methods change the\narray they are called on**. `push`, `pop`, `reverse`, `sort` and `splice`\nchange it in place; `concat` and `slice` hand you a new one and leave it\nalone. When you are not sure, the method's documentation says \"in place\" or\n\"returns a new array\"."
        },
        {
          "p": "Now one with the last two answers left to you."
        },
        {
          "code": "function capitalise(names) {\n  names[0] = names[0].toUpperCase();\n  return names;\n}\n\nconst guests = [\"ada\", \"bo\"];\ncapitalise(guests);\n\n// name what the caller passes\n//   guests -> an array; `names` is a second name for it.\n// find the lines that touch a parameter\n//   names[0] = ...\n// classify it — mutate, or rebind?   ______\n// say what the caller sees           ______",
          "lang": "js",
          "file": "capitalise.js"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In `capitalise.js`, what is `guests[0]` after the call? Type the value.",
                "accept": [
                  "\"ADA\"",
                  "ADA",
                  "'ADA'"
                ],
                "miss": "**If you answered `\"ada\"` because `toUpperCase` makes a new string — it does, and then `names[0] = …` puts that new string into the array.** The left side is a slot in the array, so this is a mutation, and the array is the caller's.\n",
                "why": "`\"ADA\"`. `names[0] = …` writes into a slot of the array — mutate — and the array is `guests`.\n"
              }
            ]
          }
        },
        {
          "h": "Two names, one object: the bug it makes"
        },
        {
          "p": "A function that changes its argument is not a bug by itself. `sort` does it on\npurpose, and says so. The bug is when **nobody expected it** — and then the\ndefect has a shape you will learn to recognise:"
        },
        {
          "list": [
            "a value is wrong, and **no line that names it wrote to it**;",
            "it went wrong **far from where it shows** — in another function, on an earlier call;",
            "it throws nothing. Nothing about the program is illegal."
          ]
        },
        {
          "p": "Every time, the cause is the same: two names reached one object, and one of\nthem changed it. You met the mechanism in *Copying versus aliasing*. Here it\ncrosses a function boundary, which is what hides it — the two names live in\ndifferent functions, and you only ever read one function at a time."
        },
        {
          "p": "Two ways it arrives. The first labels the four subgoals; the second works\nthrough the same ones unlabelled."
        },
        {
          "code": "// A module keeps a high-score table, and hands it out for display.\nconst scores = [120, 95, 80];\n\nfunction getScores() {\n  return scores;\n}\n\n// Somewhere else, a leaderboard shows them lowest first:\nconst view = getScores();\nview.reverse();\n\n// find the unexpected change\n//   scores is now [80, 95, 120]. The module never reversed it.\n// list the names that reach it\n//   `scores` inside the module, and `view` in the leaderboard.\n// find the line that shared it\n//   `return scores;` — it handed out the module's own array, not a copy.\n// decide the fix\n//   hand out a copy — `return [...scores];` — or never mutate what you receive.",
          "lang": "js",
          "file": "scores.js"
        },
        {
          "code": "// Two parts of a page both ask for the signed-in user.\nconst cache = {};\n\nfunction loadUser(id) {\n  if (!cache[id]) cache[id] = { id: id, name: \"Ada\", theme: \"dark\" };\n  return cache[id];\n}\n\n// A settings preview tries a theme out:\nconst preview = loadUser(7);\npreview.theme = \"light\";\n\n// Later, the header draws itself:\nloadUser(7).theme;   // \"light\" — though nobody saved it\n\n// The header's theme is \"light\", and the header never set it.\n// Names that reach that object: cache[7], preview, and whatever\n//   loadUser(7) returns next time.\n// Shared by `return cache[id];` — every caller gets the one cached object.\n// Fix: the preview should change a copy, not the object everybody reads.",
          "lang": "js",
          "file": "profile-cache.js"
        },
        {
          "p": "Both fixes are one of two moves. **Copy at the boundary** — the function that\nhands data out, or the one that receives it, makes its own. Or **agree not to\nmutate** what you were handed. Which one, and how deep a copy has to go, are\nthe next lessons. The first skill is seeing the shape."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "function team(players) { return { players: players }; }   const roster = [\"ana\"];   const a = team(roster);   const b = team(roster);   a.players.push(\"bo\");   →  b.players.length",
                "accept": [
                  "2"
                ],
                "miss": "**If you answered `1` because `a` and `b` are two teams — they are two objects holding one array.** `team` stored the caller's `roster` itself, so `a.players`, `b.players` and `roster` are three names for it.\n",
                "why": "`2`. The push went through `a.players` into the one array every team was built with, and `b` reads the same array.\n"
              },
              {
                "question": "In `scores.js`, which line shared the array? Type the line number, counted from the top of the file, or the line itself.",
                "accept": [
                  "5",
                  "line 5",
                  "return scores",
                  "return scores;"
                ],
                "why": "`return scores;` on line 5. The table and the leaderboard now hold one array; the `reverse` on line 10 is only where the damage was done.\n"
              }
            ]
          }
        },
        {
          "video": {
            "title": "A shared array, found with the debugger",
            "caption": "A value changes and nothing in the file appears to have written to it. The reference is found by inspecting, not by reading. Watch it if the aliasing bug shape is still abstract; *Meet the debugger* is the tool in full.\n"
          }
        },
        {
          "scaffold": {
            "label": "Checking a call before you trust it",
            "body": [
              {
                "h": "Checking a call before you trust it"
              },
              {
                "p": "Before you pass your data to a function you did not write:"
              },
              {
                "code": "// 1. Is what I am passing held by reference?\n//    number, string, boolean -> safe. Nothing can reach back. Done.\n//    object or array         -> keep going.\n\n// 2. Does the function change its parameter?\n//    Look for the parameter on the LEFT of an `=` with a `.` or `[ ]`,\n//    or calling a method that works in place (push, sort, reverse, splice).\n//    A plain `param = ...` does not count — that only moves its own name.\n\n// 3. Do I, or anyone else, still use this data afterwards?\n//    yes -> pass a copy, or pick a function that does not mutate.",
                "lang": "js",
                "file": "before-you-call.js"
              },
              {
                "p": "Now one to run through."
              },
              {
                "code": "function withoutFirst(queue) {\n  queue.shift();          // shift removes the first item, in place\n  return queue;\n}\n\nconst waiting = [\"ana\", \"bo\", \"cy\"];\nconst next = withoutFirst(waiting);\n\n// 1. by reference?                ______\n// 2. changes its parameter?       ______\n// 3. so waiting is now            ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`waiting` is an array, so `queue` is a second name for it. `shift` works in\nplace, so `waiting` is now `[\"bo\", \"cy\"]` — and `next` is that same array, not a\nnew one."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **can a function rebind the caller's variable?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Mutate what you passed",
                  "Rebind your variable"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "yes, for objects and arrays",
                    "**never**"
                  ],
                  [
                    "**C#**",
                    "yes, for reference types",
                    "only with `ref`, written at the call site *and* in the method"
                  ]
                ]
              }
            },
            {
              "p": "JavaScript closed one door completely: no function you call can make your\n`const` or `let` point somewhere else. C# leaves that door open, but only where\nboth sides write `ref` — so reading `H(ref a)` tells you, at the call, that `a`\nmay be rebound."
            },
            {
              "p": "Neither language marks the other door. A plain call `f(a)` in either one can\nmutate the object `a` points at, and nothing at the call site says it will. That\nis why a function's name is the only warning you get — `sort` versus a function\nthat returns a sorted copy."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "The mutating array methods are the ones to know by sight, because a call to one\nof them on a parameter changes the caller's array:"
              },
              {
                "code": "list.push(x);  list.pop();  list.shift();  list.unshift(x);\nlist.sort();   list.reverse();  list.splice(i, n);\nlist[i] = x;   list.length = 0;",
                "lang": "js"
              },
              {
                "p": "These return a new array and leave the original alone:"
              },
              {
                "code": "list.concat([x]);  list.slice();  [...list, x];",
                "lang": "js"
              },
              {
                "p": "Newer JavaScript adds `toSorted()`, `toReversed()` and `toSpliced()` — copying\nversions of the three in-place methods. They are recent; the code you will read\nmostly copies first and then sorts: `[...list].sort()`."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the third and fourth, say\nwhich line shared the array or object — and what `last` could have done\ninstead so that `s` came back unchanged."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A settings module stores `{ theme: \"dark\", fontSize: 14 }` and three parts of\nthe app read it. A bug report says the font size sometimes changes on its own."
            },
            {
              "p": "Three ways to write `getSettings()`:"
            },
            {
              "list": [
                "**Return the object itself.** Fast, and every caller sees the latest values.",
                "**Return a copy each time** — `return { ...settings };`",
                "**Return nothing changeable** — callers read with `getSetting(\"theme\")` and change it only through `updateSetting(name, value)`."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: which one can still produce\nthe bug report, which one allocates a new object on every read, and which one\nmakes callers change how they write their code. Naming the cost is the answer."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Passing an argument assigns it to the parameter. For an object or array, the function gets a **second reference** to your data.",
                "A function can **mutate what you passed**. It cannot **rebind your name**. Which one a line does is what decides whether your data survives the call.",
                "An **aliasing bug** is a change that shows up where nobody wrote to it. Find every name that reaches the object, and the line that **shared** it."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array",
              "note": "the section \"Copying methods and mutating methods\" lists which methods change the array they are called on."
            },
            {
              "title": "Functions",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Functions",
              "note": "\"Defining functions\": reassigning a parameter is not seen by the caller, and changing an object's properties is."
            }
          ]
        }
      ],
      "_source": "lessons/what-gets-shared.md",
      "practice": {
        "id": "what-gets-shared",
        "outcome": 2,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict what survives the call",
            "prompt": "Four quick ones. Type the final value, or pick one of the options.\n",
            "items": [
              {
                "id": "p1",
                "code": "function f(l) { l.push(9); }   const a = [1];   f(a);   →  a.length",
                "accept": [
                  "2"
                ],
                "why": "The parameter is a second name for your array. Pushing through it pushes to yours.\n"
              },
              {
                "id": "p2",
                "code": "function g(l) { l = [9]; }   const a = [1];   g(a);   →  a.length",
                "accept": [
                  "1"
                ],
                "why": "Rebinding the parameter moved the function's own name. A function can change what you pointed at; it cannot change where you point.\n"
              },
              {
                "id": "p3",
                "code": "function smallest(list) { return list.sort()[0]; }   const s = [\"b\", \"c\", \"a\"];   smallest(s);   →  s[0]",
                "accept": [
                  "\"a\"",
                  "a",
                  "'a'"
                ],
                "miss": "**If you answered `\"b\"` because `smallest` only answers a question — `sort` works in place.** It sorted your array to find the answer.\n",
                "why": "`\"a\"`. `sort` rearranged the caller's array, and the function never said it would.\n"
              },
              {
                "id": "p4",
                "question": "A value is wrong, and no line that names it ever writes to it. What do you look for first?",
                "choices": [
                  "A typo in its name",
                  "Another name for the same object",
                  "A missing return"
                ],
                "accept": [
                  "Another name for the same object"
                ],
                "why": "Another name for the same object. A change that appears where nobody wrote to it was written through a second name.\n"
              }
            ],
            "extra": [
              {
                "id": "p5",
                "code": "function h(o) { o.n = 9; }   const x = { n: 1 };   h(x);   →  x.n",
                "accept": [
                  "9"
                ],
                "why": "Same as the first, with an object. `o.n = 9` changes the object, and the object is yours.\n"
              },
              {
                "id": "p6",
                "code": "function k(s) { s = s + \"!\"; }   let t = \"hi\";   k(t);   →  t",
                "accept": [
                  "\"hi\"",
                  "hi",
                  "'hi'"
                ],
                "why": "A string is held directly and cannot be changed in place, so there was never a shared thing to mutate. Only rebinding was possible, and that is local.\n"
              },
              {
                "id": "p7",
                "code": "const cache = {};   function get(id) { if (!cache[id]) cache[id] = { qty: 1 }; return cache[id]; }   get(3).qty = 5;   →  get(3).qty",
                "accept": [
                  "5"
                ],
                "why": "`5`. Both calls return the one cached object, so the first caller's change is what the second caller reads.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 1 for the first",
                "say": "You assumed the function got its own array. It got its own *reference* to your array. Count how many arrays exist — one.\n"
              },
              {
                "when": "you answered 2 for the second",
                "say": "`l = [9]` has only a name on the left. It points the function's `l` somewhere new; your `a` is a different name and did not move.\n"
              },
              {
                "when": "you answered \"b\" for the third",
                "say": "Ask whether `sort` builds a new array or rearranges the one it is called on. Then ask whose array that is.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Three functions, one array",
            "prompt": "Predict each line the program prints, then run it.\n",
            "filename": "bonus.js",
            "code": "function addBonus(scores) {\n  scores.push(10);\n}\n\nfunction resetAll(scores) {\n  scores = [];\n}\n\nfunction doubled(scores) {\n  return scores.concat(scores);\n}\n\nconst mine = [5];\naddBonus(mine);\nconsole.log(mine.length);\n\nresetAll(mine);\nconsole.log(mine.length);\n\nconst d = doubled(mine);\nconsole.log(mine.length, d.length);\nconsole.log(d === mine);\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(mine.length)   (after addBonus)",
                "accept": [
                  "2"
                ],
                "why": "`2`. `push` changed the array `scores` points at, which is `mine`.\n"
              },
              {
                "id": "r2",
                "code": "console.log(mine.length)   (after resetAll)",
                "accept": [
                  "2"
                ],
                "miss": "**If you answered `0` — `scores = []` only moved `resetAll`'s own name.** `mine` still points at the array with two numbers in it.\n",
                "why": "`2`. Rebinding the parameter reached nothing of the caller's.\n"
              },
              {
                "id": "r3",
                "code": "console.log(mine.length, d.length)",
                "accept": [
                  "2 4",
                  "2, 4",
                  "2,4"
                ],
                "why": "`2 4`. `concat` built a new array from two copies of the contents and left `mine` alone.\n"
              },
              {
                "id": "r4",
                "code": "console.log(d === mine)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. `d` is the new array `concat` built — a second object, so the identity question says no.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 0 after resetAll",
                "say": "Look at what is on the left of `=` inside `resetAll`: the bare name `scores`. That is a rebinding. Which name did it move?\n"
              },
              {
                "when": "you predicted mine.length would be 4 after doubled",
                "say": "`concat` hands you a new array and leaves the one it was called on alone. The function returned the new one; nothing wrote to `mine`.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace the caller's array through a call",
            "prompt": "One function that both mutates and rebinds its parameter. Answer for the state after line 8 unless the item says otherwise. Write arrays as `[\"a\",\"b\"]`.\n",
            "items": [
              {
                "id": "t1",
                "code": "1  function tidy(tags) {\n2    tags.sort();\n3    tags = tags.slice(0, 2);\n4    tags.push(\"new\");\n5    return tags;\n6  }\n7  const mine = [\"c\", \"a\", \"b\"];\n8  const out = tidy(mine);\n\nafter line 8  →  mine\n",
                "accept": [
                  "[\"a\",\"b\",\"c\"]",
                  "[\"a\", \"b\", \"c\"]",
                  "['a','b','c']",
                  "['a', 'b', 'c']",
                  "a,b,c",
                  "a, b, c",
                  "[a,b,c]",
                  "[a, b, c]"
                ],
                "miss": "**If you answered `[\"c\",\"a\",\"b\"]` — line 2 ran while `tags` still pointed at your array.** And if you answered `[\"a\",\"b\",\"new\"]`, line 3 had already moved `tags` to a new array before the push.\n",
                "why": "`[\"a\",\"b\",\"c\"]`. Line 2 sorted the caller's array in place. Line 3 rebound `tags` to a new, shorter array, so line 4 pushed into that one, not into `mine`.\n"
              },
              {
                "id": "t2",
                "question": "Same program, after line 8: what is `out`?",
                "accept": [
                  "[\"a\",\"b\",\"new\"]",
                  "[\"a\", \"b\", \"new\"]",
                  "['a','b','new']",
                  "['a', 'b', 'new']",
                  "a,b,new",
                  "a, b, new",
                  "[a,b,new]",
                  "[a, b, new]"
                ],
                "why": "`[\"a\",\"b\",\"new\"]` — the array line 3 made, with line 4's push in it.\n"
              },
              {
                "id": "t3",
                "question": "Same program: which is the **last** line that changes `mine`? Type the line number.",
                "accept": [
                  "2",
                  "line 2"
                ],
                "why": "Line 2. After line 3, `tags` is no longer a name for `mine`, so nothing the function does reaches it.\n"
              },
              {
                "id": "t4",
                "code": "const origin = { x: 0 };   function spawn() { return origin; }   const p1 = spawn();   const p2 = spawn();   p1.x = 4;   →  p2.x",
                "accept": [
                  "4"
                ],
                "why": "`4`. `spawn` hands every caller the one `origin` object, so `p1`, `p2` and `origin` are three names for it.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "function clear(list) { list = []; return list; }   const q = [1];   const r = clear(q);   →  q.length",
                "accept": [
                  "1"
                ],
                "why": "`1`. `list = []` moved the function's name to a new array and returned it; `q` still points at the old one.\n"
              },
              {
                "id": "t6",
                "code": "const log = [];   function record(entry, into) { into.push(entry); }   record(\"a\", log);   record(\"b\", log);   →  log.length",
                "accept": [
                  "2"
                ],
                "why": "`2`. The same mechanism, used on purpose: `record` exists to change the array it is given, and its name says so.\n"
              },
              {
                "id": "t7",
                "code": "function team(players) { return { players: players }; }   const roster = [\"ana\"];   const a = team(roster);   a.players.push(\"bo\");   →  roster.length",
                "accept": [
                  "2"
                ],
                "why": "`2`. The team stored the caller's array itself, so pushing through `a.players` pushed into `roster`.\n"
              }
            ],
            "hints": [
              {
                "when": "you gave mine as [\"c\",\"a\",\"b\"]",
                "say": "Take the lines in order. At line 2, which array does `tags` point at? Does `sort` build a new array or rearrange that one?\n"
              },
              {
                "when": "you gave mine with \"new\" in it",
                "say": "Line 3 has the bare name `tags` on the left of `=`. After it runs, which array does `tags` point at — and is that `mine`?\n"
              },
              {
                "when": "you answered 0 for spawn",
                "say": "Count the objects. `{ x: 0 }` is written once, and `spawn` returns it every time.\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Empty the caller's array",
            "prompt": "The notification tray keeps using the same array after `clearAll` runs, so the function has to empty *that* array — not make a new one. Fill the gap with one line. Then, in one sentence: why can `notifications = []` never work here?\n",
            "exportName": "clearAll",
            "filename": "tray.js",
            "starter": "// The caller keeps using the SAME array after this call.\n// Empty it in place. Do not make a new one.\nfunction clearAll(notifications) {\n  /* ___ one line: empty the array the caller passed in ___ */\n}\n",
            "tests": [
              {
                "label": "the caller's array is empty afterwards",
                "body": "const n = ['a', 'b']; subject(n); return n.length === 0;"
              },
              {
                "label": "another name for the same array sees it empty too",
                "body": "const n = ['a']; const tray = { list: n }; subject(tray.list); return n.length === 0;"
              },
              {
                "label": "a long list is emptied",
                "body": "const n = Array.from({ length: 100 }, (_, i) => i); subject(n); return n.length === 0;"
              },
              {
                "label": "an already-empty array is fine",
                "body": "const n = []; subject(n); return n.length === 0;"
              }
            ],
            "hints": [
              {
                "when": "you wrote `notifications = []` and the caller's array is still full",
                "say": "That rebinds the function's own name. The caller's name still points at the old array, with everything in it. A function can change what you pointed at, not where you point — so change the array.\n"
              },
              {
                "when": "you returned an empty array",
                "say": "The caller does not use the return value; it keeps using its own array. The function has to *act* on that array, not answer with a new one.\n"
              },
              {
                "when": "the short lists pass and the long list does not",
                "say": "You removed one item, or a fixed number. The line has to empty the array whatever its length.\n"
              },
              {
                "when": "you are looking for a method that empties an array",
                "say": "Anything that mutates will do. Arrays have a `length` you can write to, and `splice` removes items from the array it is called on.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Stop sorting the caller's scores",
            "prompt": "`topThree` returns the three highest scores, and its tests for that pass. But the leaderboard that calls it shows scores in the order they were earned, and after every call that order is gone. Change `topThree` so the caller's array comes back exactly as it went in. Once it passes: in one sentence, which line was changing the caller's data, and why could nothing in the function's name tell you?\n",
            "exportName": "topThree",
            "filename": "leaderboard.js",
            "starter": "// Returns the three highest scores, highest first.\nfunction topThree(scores) {\n  scores.sort(function (a, b) { return b - a; });   // highest first\n  return scores.slice(0, 3);\n}\n",
            "tests": [
              {
                "label": "returns the three highest, highest first",
                "body": "return subject([40, 90, 10, 70, 55]).join(',') === '90,70,55';"
              },
              {
                "label": "the caller's array keeps its order",
                "body": "const s = [40, 90, 10, 70, 55]; subject(s); return s.join(',') === '40,90,10,70,55';"
              },
              {
                "label": "fewer than three scores is fine",
                "body": "const s = [5, 8]; return subject(s).join(',') === '8,5' && s.join(',') === '5,8';"
              },
              {
                "label": "numbers of different lengths sort as numbers",
                "body": "return subject([5, 100, 40, 9]).join(',') === '100,40,9';"
              },
              {
                "label": "returns a new array, not the caller's",
                "body": "const s = [1, 2, 3]; return subject(s) !== s;"
              }
            ],
            "hints": [
              {
                "when": "the caller's array keeps its order fails",
                "say": "`sort` rearranges the array it is called on, and `scores` is a second name for the caller's array. Sort something that is not the caller's.\n"
              },
              {
                "when": "you wrote `const copy = scores;` and the order still changes",
                "say": "That gave the same array a third name. `=` copies the reference, not the array. You need a new array with the same numbers in it.\n"
              },
              {
                "when": "the three highest come back in the wrong order",
                "say": "Keep the comparison function as it was. Without it, `sort` compares numbers as text, so `100` comes before `40`. Only what it sorts should change.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a to-do store",
            "prompt": "An AI assistant wrote this store for a to-do app. The bug report: \"after I open the A–Z view, the main list stays in A–Z order instead of the order I added things.\" Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "store.js",
            "code": "// A to-do store. Callers get lists to show; the store keeps the real one.\nfunction newStore() {\n  return { todos: [] };\n}\n\nfunction add(store, text) {\n  store.todos.push(text);\n}\n\nfunction all(store) {\n  return store.todos;\n}\n\nfunction alphabetical(store) {\n  return all(store).sort();\n}\n",
            "items": [
              {
                "id": "c1",
                "code": "const s = newStore();   add(s, \"walk\");   add(s, \"buy\");   alphabetical(s);   →  all(s)[0]",
                "accept": [
                  "\"buy\"",
                  "buy",
                  "'buy'"
                ],
                "why": "`\"buy\"`. The A–Z view sorted the store's own list, so the main list is now in A–Z order too. That is the bug report, reproduced.\n"
              },
              {
                "id": "c2",
                "question": "Which line rearranges the store's list? Type the line number.",
                "accept": [
                  "15",
                  "line 15"
                ],
                "why": "Line 15. `sort` works in place, and `all(store)` handed it the store's own array.\n"
              },
              {
                "id": "c3",
                "question": "Which line hands callers the store's own array instead of a copy? Type the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "Line 11. Every caller of `all` gets a second name for `store.todos`, so anything a caller does to its list, it does to the store's.\n"
              },
              {
                "id": "c4",
                "question": "A screen does `all(s).push(\"nap\")`. Does the store now hold `\"nap\"`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes — and line 1's comment says the store keeps the real list. It does not keep it; it lends it out.\n"
              }
            ],
            "exportName": "todoStore",
            "starter": "// A to-do store. Callers get lists to show; the store keeps the real one.\nfunction newStore() {\n  return { todos: [] };\n}\n\nfunction add(store, text) {\n  store.todos.push(text);\n}\n\nfunction all(store) {\n  return store.todos;\n}\n\nfunction alphabetical(store) {\n  return all(store).sort();\n}\n\nconst todoStore = { newStore, add, all, alphabetical };\n",
            "tests": [
              {
                "label": "alphabetical returns A–Z",
                "body": "const s = subject.newStore(); subject.add(s, 'c'); subject.add(s, 'a'); subject.add(s, 'b'); return subject.alphabetical(s).join(',') === 'a,b,c';"
              },
              {
                "label": "all keeps the order things were added, after alphabetical",
                "body": "const s = subject.newStore(); subject.add(s, 'c'); subject.add(s, 'a'); subject.add(s, 'b'); subject.alphabetical(s); return subject.all(s).join(',') === 'c,a,b';"
              },
              {
                "label": "pushing onto what all returns does not change the store",
                "body": "const s = subject.newStore(); subject.add(s, 'walk'); subject.all(s).push('nap'); return subject.all(s).length === 1;"
              },
              {
                "label": "add still adds",
                "body": "const s = subject.newStore(); subject.add(s, 'walk'); subject.add(s, 'buy'); return subject.all(s).join(',') === 'walk,buy';"
              },
              {
                "label": "two stores are separate",
                "body": "const a = subject.newStore(); const b = subject.newStore(); subject.add(a, 'walk'); return subject.all(b).length === 0;"
              }
            ],
            "hints": [
              {
                "when": "the A–Z view is fixed but pushing onto what all returns still changes the store",
                "say": "You copied inside `alphabetical`, which fixes one caller. `all` still lends out the store's own array to every other caller. Fix the line that shares it.\n"
              },
              {
                "when": "you wrote `const list = store.todos; return list;`",
                "say": "That is a second name for the same array, not a copy. You need a new array holding the same items.\n"
              },
              {
                "when": "alphabetical no longer returns A–Z",
                "say": "The sort still has to happen — on an array that is not the store's.\n"
              }
            ]
          }
        ]
      }
    },
    "meet-debugger": {
      "id": "meet-debugger",
      "title": "Meet the debugger",
      "kind": "read",
      "outcome": 10,
      "shape": "procedural",
      "why": "A shared reference produces the first bug in the course that throws nothing and that printing cannot show: a value changes on a line that never names it. The debugger is the tool that shows it, so it arrives with that bug.",
      "concepts": [
        "diag.debugger"
      ],
      "lede": "A shared reference produces the one bug a print statement structurally cannot show you: a value changes, and no line in the file appears to have written to it. The debugger shows you which line did, without adding a line of code.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What a function can change · One level deep, or all the way · When it breaks: reading errors"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Breakpoint** — a line where the runtime pauses and hands you the program, still alive.",
          "entries": [
            "**Breakpoint** — a line where the runtime pauses and hands you the program, still alive."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from earlier lessons",
          "body": [
            {
              "p": "Four questions on what earlier lessons taught. Type the answer, or pick it where there are buttons."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "function tag(o) { o.seen = true; }  const u = {}; tag(u);  →  u.seen",
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you answered `undefined` because the function got a copy — it got a copy of the *reference*.** Both arrows point at one object, so writing through either writes to it.\n",
                          "why": "A function can change what you pointed at. `u` and `o` are two names for one object.\n"
                        },
                        {
                          "code": "function reset(o) { o = {}; }  const u = { n: 1 }; reset(u);  →  u.n",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If you answered `undefined` — `o = {}` moves the function's own name and nothing else.** A function cannot change where *your* name points.\n",
                          "why": "Rebinding a parameter touches nothing of the caller's. Mutation reaches back; rebinding does not.\n"
                        },
                        {
                          "code": "const x = { n: 1 }; const y = { n: 1 };  →  x === y",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "false"
                          ],
                          "miss": "**If you answered `true` because they look the same — `===` on objects asks whether they are one object, not whether they match.** Two literals make two objects.\n",
                          "why": "Identity, not contents. Keep this one: it is the question you will ask the debugger in step 2.\n"
                        },
                        {
                          "question": "A program gives a wrong answer and throws nothing. How many stack traces are there to read? Type a number.",
                          "accept": [
                            "0",
                            "zero",
                            "none"
                          ],
                          "miss": "**A stack trace is printed when something throws.** A silent failure throws nothing, so there is nothing to read — which is why it needs a different tool.\n",
                          "why": "None. A silent failure leaves no trace to read; you have to go and look while the program runs.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can pause a program at a line and read any value in scope without adding a line of code",
                "I can step through a call and watch a value change, and say which line changed it",
                "I can narrow a wrong value to the line that changed it by moving breakpoints, not by guessing"
              ]
            }
          ]
        },
        {
          "p": "Every step below uses this program. It throws nothing and logs nothing, and at\nthe end `defaults.limits.max` is `50` — though nothing was meant to change it.\nSave it as `config.js`."
        },
        {
          "code": "const defaults = { retries: 3, limits: { max: 10 } };\n\nfunction makeConfig(overrides) {\n  const c = { ...defaults };\n  Object.assign(c.limits, overrides);\n  return c;\n}\n\nconst first  = makeConfig({ max: 50 });\nconst second = makeConfig({});",
          "lang": "js",
          "file": "config.js"
        },
        {
          "p": "The line numbers matter: line 4 is `const c = …`, line 5 is `Object.assign`,\nline 9 is the first call and line 10 the second."
        },
        {
          "video": {
            "title": "Finding the shared reference, live",
            "caption": "config.js in the debugger: a breakpoint, a step, a Watch that turns from 10 to 50 on a line that never names `defaults`. Watch it before the steps if you have not opened a debugger before.\n"
          }
        },
        {
          "beat": "step",
          "label": "Pause the program at a line",
          "body": [
            {
              "p": "Run the program under the debugger, outside this site — the site's code runner\nstops any program after two seconds, and a paused one with it."
            },
            {
              "code": "node --inspect-brk config.js",
              "lang": "bash"
            },
            {
              "p": "`--inspect-brk` starts Node paused before the first line. In Chrome, open\n`chrome://inspect`, and under **Remote Target** click **inspect**. A DevTools\nwindow opens on the **Sources** panel with `config.js` showing."
            },
            {
              "p": "**In a Codespace, Chrome cannot reach your program** — it runs on GitHub's\nmachine, not yours. Skip `--inspect-brk` and Chrome: set the breakpoint in the\neditor, then run `node config.js` in the **JavaScript Debug Terminal** (see\n*Set up where your code runs*). The controls below are the same; VS Code calls\nResume **Continue** (F5) and marks a breakpoint with a red dot, not a blue one."
            },
            {
              "p": "Now set a breakpoint: **click the number 5** in the gutter. A blue marker\nappears. Press **Resume** (▶, or F8). The program runs until it reaches line 5\nand stops, with line 5 highlighted."
            },
            {
              "p": "Two things are true at that pause:"
            },
            {
              "list": [
                "**The highlighted line has not run yet.** A breakpoint stops *before* its line.",
                "**Everything in scope is readable.** The **Scope** pane lists the function's own variables under **Local** — `c` and `overrides` here. Hover any name in the code to see its value."
              ]
            },
            {
              "p": "Nothing was added to the file, so nothing has to be removed later."
            },
            {
              "tracks": {
                "js": [
                  {
                    "p": "`debugger;` in your source is a breakpoint you can write — the program pauses\nthere when a debugger is attached and ignores it when none is. Useful for a line\nyou will return to; delete it before you ship."
                  },
                  {
                    "code": "function makeConfig(overrides) {\n  debugger;                  // pauses here when a debugger is attached\n  const c = { ...defaults };\n}",
                    "lang": "js"
                  },
                  {
                    "p": "Conditional breakpoints are the feature people miss: right-click a line number,\nchoose **Add conditional breakpoint**, type `overrides.max === 50`, and it\npauses only on the call where that is true."
                  }
                ],
                "python": [
                  {
                    "p": "`breakpoint()` in your source drops you into `pdb` at that line — the same idea,\nbuilt into the language since 3.7."
                  },
                  {
                    "code": "def make_config(overrides):\n    breakpoint()          # pdb: n(ext), s(tep), c(ontinue), p <expr>\n    c = defaults.copy()",
                    "lang": "python"
                  },
                  {
                    "p": "The commands are letters rather than buttons, and the operations are the\nsame: `n` steps over, `s` steps into, `r` steps out, `c` continues."
                  }
                ],
                "csharp": [
                  {
                    "p": "Visual Studio and VS Code both stop on a red-dot breakpoint, and `Debugger.Break()`\nis the in-source form. C# adds two worth knowing: **conditional breakpoints** in\nthe same dialog, and the **Immediate window**, which evaluates expressions\nagainst the paused program."
                  },
                  {
                    "p": "Typing `ReferenceEquals(first.Limits, second.Limits)` into the Immediate window\nanswers the identity question directly."
                  }
                ]
              }
            },
            {
              "p": "Back at the pause on line 5, in the first call:"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Paused on line 5, first call. Has `Object.assign` run yet?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**The highlighted line is the next one to run, not the last one.** A breakpoint stops before its line, so you can see the state the line is about to act on.\n",
                          "why": "No. Line 4 has run, so `c` exists; line 5 has not.\n"
                        },
                        {
                          "question": "Paused on line 5, first call. What value does `overrides.max` hold?",
                          "accept": [
                            "50"
                          ],
                          "why": "`overrides` is the argument of the call on line 9, `{ max: 50 }`. The Scope pane lists it under Local.\n"
                        },
                        {
                          "question": "Paused on line 5, first call. What value does `defaults.limits.max` hold?",
                          "accept": [
                            "10"
                          ],
                          "miss": "**If you answered `50` — that is the end state, and line 5 has not run.** At this pause the defaults are still what the file says.\n",
                          "why": "Still `10`. This is a point where the value is right, which is half of finding where it goes wrong.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Step over, and watch a value change",
          "body": [
            {
              "p": "Hovering shows a value once. A **Watch** shows it at every pause. In the right\npane, open **Watch**, click **+**, and add two expressions:"
            },
            {
              "code": "defaults.limits.max\nc.limits === defaults.limits",
              "lang": "js"
            },
            {
              "p": "The first is the value that ends up wrong. The second is the question printing\ntheir contents cannot answer: whether two names are **one object**. Print both and you see two\n`{ max: 10 }`, which is what two separate objects would print too. `===` on\nobjects asks identity, and a Watch asks it while the program is paused."
            },
            {
              "p": "Now press **Step over** (↷, or F10). It runs the highlighted line and pauses on\nthe next one, and every Watch is re-evaluated."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Paused on line 5, before stepping. What does the Watch `c.limits === defaults.limits` show?",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you answered `false` because `c` is a new object — it is, but what it holds under `limits` is the same arrow `defaults` holds.** The Watch says so, which is the point of asking it.\n",
                          "why": "One object, two names. This single `true` is the whole bug, and no print of either object would have shown it.\n"
                        },
                        {
                          "question": "After one Step over, paused on line 6. What value does the Watch `defaults.limits.max` show?",
                          "accept": [
                            "50"
                          ],
                          "miss": "**If you answered `10` because line 5 never mentions `defaults` — that is exactly why this bug survives reading.** Line 5 writes to whatever `c.limits` points at, and the Watch above says that is `defaults.limits`.\n",
                          "why": "It changed on line 5, and line 5 never names `defaults`. That contradiction is the finding: two names were one object all along.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Step into a call, and read the call stack",
          "body": [
            {
              "p": "Press **Resume**. The program runs on and stops at line 5 again — now in the\n*second* call."
            },
            {
              "p": "The **Call Stack** pane says which call you are in. The top frame is\n`makeConfig`, at line 5. The frame under it is the code that called it, with the\nline it called from. Click a frame to see that frame's variables."
            },
            {
              "p": "Two more controls, for moving between frames:"
            },
            {
              "table": {
                "head": [
                  "Control",
                  "What it does",
                  "Use it when"
                ],
                "rows": [
                  [
                    "**Step into** (↓, F11)",
                    "descend into the call on this line",
                    "the bug might be in there"
                  ],
                  [
                    "**Step out** (↑, Shift+F11)",
                    "finish this function, pause back in the caller",
                    "you descended by mistake"
                  ]
                ]
              }
            },
            {
              "p": "**Step over** runs a call without descending — use it when you trust the\nfunction being called."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Second pause on line 5. In the Call Stack, the frame under `makeConfig` is at which line? Type the line number.",
                          "accept": [
                            "10",
                            "line 10"
                          ],
                          "why": "Line 10, the second call. Line 9 was the first; the call stack is how you tell two pauses on the same line apart.\n"
                        },
                        {
                          "question": "Second pause on line 5, before this call has changed anything. What value does `defaults.limits.max` hold?",
                          "accept": [
                            "50"
                          ],
                          "miss": "**If you answered `10` because this call has not run line 5 yet — the first call already did.** The damage outlived the call that caused it.\n",
                          "why": "Already wrong before the second call does anything. That is why the symptom — `second.limits.max` is `50` — shows up far from its cause.\n"
                        },
                        {
                          "question": "Paused on line 10, you press **Step into**. Which function are you in now? Type its name.",
                          "accept": [
                            "makeConfig",
                            "makeconfig",
                            "makeConfig()"
                          ],
                          "why": "Step into descends into the call on the line. Step over would have run all of `makeConfig` and paused on the next line of the caller.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Find where a value went wrong",
          "body": [
            {
              "p": "On `config.js` you knew where to look. On a longer program you do not, and you\ndo not hunt for the line — you **bisect** it."
            },
            {
              "code": "// A value is wrong at the end. You do not know where it went wrong.\n\n// 1. Find a point where it is RIGHT.\n//    Breakpoint early. Watch the value; confirm it is what you expect.\n\n// 2. Find a point where it is WRONG.\n//    Breakpoint late. Confirm it is wrong there.\n\n// 3. Break halfway between. Right or wrong? That halves the gap.\n//    Repeat until right and wrong are one line apart.\n\n// 4. That line is where it CHANGED.\n//    Not always where the bug is. The bug is usually that this line\n//    could reach that data at all.",
              "lang": "js",
              "file": "procedure.js"
            },
            {
              "p": "You did this in the steps above: `10` at line 5, `50` at line 6, one line apart.\nLine 5 is where it changed. The bug is that `c.limits` and `defaults.limits`\nwere one object, so line 5 could reach the defaults at all."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "The value is right at a breakpoint on line 12 and wrong at one on line 40. Which line does the next breakpoint go on? Type the line number.",
                          "accept": [
                            "26",
                            "line 26",
                            "25",
                            "27"
                          ],
                          "miss": "**If you picked a line next to 12 or 40 — that checks one line and leaves the rest.** Halfway rules out half the lines whatever the answer is.\n",
                          "why": "About halfway, 26. Each run halves what is left: 28 lines take five runs, not twenty-eight.\n"
                        },
                        {
                          "question": "At line 26 it is right. The change is between line 26 and which line? Type the line number.",
                          "accept": [
                            "40",
                            "line 40"
                          ],
                          "why": "Right at 26, wrong at 40: the change is in that stretch. Break at 33 next.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A **breakpoint** pauses the program before its line runs, still alive, with every value in scope readable — and nothing added to the file.",
                "**Step over** runs one line; **step into** descends into a call; **step out** finishes it. A **Watch** re-evaluates at every pause, so you see the line where a value changed.",
                "A Watch can ask **identity** — `a === b` — which printing contents cannot. The **call stack** says which call you are in.",
                "To find where a value went wrong, **bisect**. The line where it changed is rarely the bug; the bug is that the line could reach that data at all."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "debugger",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/debugger",
              "note": "the `debugger` statement, and what it does when no debugger is attached."
            },
            {
              "title": "Pause your code with breakpoints",
              "href": "https://developer.chrome.com/docs/devtools/javascript/breakpoints",
              "note": "Chrome's reference for every breakpoint kind, including conditional ones."
            },
            {
              "title": "Object.assign()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign",
              "note": "read what it says about copying references rather than values; that sentence is this program's bug."
            }
          ]
        }
      ],
      "_source": "lessons/meet-debugger.md",
      "practice": {
        "id": "meet-debugger",
        "outcome": 10,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict what the debugger shows",
            "prompt": "A program the lesson did not use, with its line numbers. Answer as if it were paused in DevTools.\n",
            "items": [
              {
                "id": "p1",
                "code": "1  const base = { tags: [\"new\"] };\n2  function label(extra) {\n3    const t = base.tags;\n4    t.push(extra);\n5    return t;\n6  }\n7  const a = label(\"sale\");\n8  const b = label(\"gift\");\n\nyou want the program to stop just before line 4 runs  →  what do you set on line 4? (name it)\n",
                "accept": [
                  "a breakpoint",
                  "breakpoint",
                  "breakpoints",
                  "a break point",
                  "break point"
                ],
                "why": "A breakpoint — click the line number. The runtime pauses *before* that line, with everything in scope readable.\n"
              },
              {
                "id": "p2",
                "question": "Same program, paused on line 4 in the first call. What value does the Watch `base.tags.length` show?",
                "accept": [
                  "1"
                ],
                "why": "`1`. A breakpoint stops before its line, so the push has not happened yet: `base.tags` is still `[\"new\"]`.\n"
              },
              {
                "id": "p3",
                "question": "One Step over later, paused on line 5. What value does the Watch `base.tags.length` show?",
                "accept": [
                  "2"
                ],
                "why": "`2`. Line 4 never names `base` — and it changed `base.tags`, because line 3 made `t` a second name for that array.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "Same program, paused on line 4 in the first call. What does the Watch `t === base.tags` show?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "One array, two names. That single `true` is the bug, and it is the question a Watch answers that printing the two arrays does not.\n"
              },
              {
                "id": "p5",
                "question": "You press Resume; it pauses on line 4 again, in the second call. What value does the Watch `base.tags.length` show?",
                "accept": [
                  "2"
                ],
                "why": "`2` — `\"sale\"` is already in there from the first call, and this call has not pushed yet. The damage outlived the call that did it.\n"
              },
              {
                "id": "p6",
                "question": "Paused on line 4. Has `t.push(extra)` run yet?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The highlighted line is the next one to run, not the last one.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 2 for the first pause",
                "say": "The highlighted line has not run. What did `base.tags` hold before anything was pushed?\n"
              },
              {
                "when": "you answered 1 after the Step over",
                "say": "Step over runs the highlighted line. Line 4 pushes to `t` — and what is `t` another name for?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Step through config.js",
            "prompt": "The lesson's `config.js` — line 4 is `const c = …`, line 5 is `Object.assign`, line 6 is `return c`, line 9 the first call, line 10 the second. Follow the moves in order and say what the debugger shows.\n",
            "items": [
              {
                "id": "t1",
                "question": "Paused on line 9, you press **Step into**. Which line are you paused on now? Type the line number.",
                "accept": [
                  "4",
                  "line 4"
                ],
                "why": "Step into descends into the call on the line, and pauses on the first line of `makeConfig`: line 4.\n"
              },
              {
                "id": "t2",
                "question": "You press Step over, now on line 5. What does the Watch `c.limits === defaults.limits` show?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "Line 4 has run, so `c` exists — a new object whose `limits` is the arrow `defaults` holds. One object, two names.\n"
              },
              {
                "id": "t3",
                "question": "Still on line 5. What does the Watch `c === defaults` show?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "The spread did make a new outer object. It is the level *below* that is shared — which is why \"is it a copy?\" has two answers here.\n"
              },
              {
                "id": "t4",
                "question": "You press Step over, now on line 6. In the Call Stack, the frame under `makeConfig` is at which line? Type the line number.",
                "accept": [
                  "9",
                  "line 9"
                ],
                "why": "Line 9: this is the first call. On the second call the same frame would read line 10 — the call stack is how you tell two pauses on one line apart.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "question": "Paused on line 5 in the first call, you press Step over. What value does the Watch `defaults.limits.max` show?",
                "accept": [
                  "50"
                ],
                "why": "`50`, on a line that never names `defaults`. That contradiction is the finding.\n"
              },
              {
                "id": "t6",
                "question": "With a breakpoint only on line 6, you press Resume from the first call. It pauses on line 6 in the second call. What value does the Watch `Object.keys(overrides).length` show?",
                "accept": [
                  "0"
                ],
                "why": "The second call passed `{}`, so there are no keys. The Scope pane lists `overrides` under Local — each frame has its own.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 10 for the line after Step into",
                "say": "Step *over* would have run the whole call and moved to line 10. Step *into* goes inside the function being called. Where does its body start?\n"
              },
              {
                "when": "you answered true for c === defaults",
                "say": "`{ ...defaults }` builds a new object, so the outer objects are two. What the spread copied into it were the *arrows* `defaults` held.\n"
              },
              {
                "when": "you answered makeConfig for the frame under it",
                "say": "The top frame is the function you are paused in. The one under it is the code that called it — here, a line at the bottom of the file.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Put the procedure in order",
            "prompt": "The steps that find where `defaults.limits.max` changes in `config.js`, from a closed terminal to the finding. A Watch on `defaults.limits.max` is already saved in DevTools. Put the steps in order; two do not belong.\n",
            "lines": [
              "node --inspect-brk config.js",
              "open chrome://inspect and click inspect",
              "click line number 5 to set a breakpoint",
              "press Resume (F8) and it pauses on line 5",
              "read the Watch defaults.limits.max: 10",
              "press Step over (F10) and it pauses on line 6",
              "read the Watch defaults.limits.max: 50"
            ],
            "distractors": [
              {
                "line": "node config.js",
                "why": "Without `--inspect-brk`, Node runs the file to the end and exits. There is no debugger attached and nothing left to pause.\n"
              },
              {
                "line": "add console.log(defaults.limits) on line 6",
                "why": "It would print `{ max: 50 }` — the same thing two separate objects would print. It adds a line you must later remove, and cannot show that `c.limits` and `defaults.limits` are one object.\n"
              }
            ],
            "why": "Start paused, so there is time to set a breakpoint; resume to it; read a value that is right; step one line; read it again. Right at line 5, wrong at line 6: line 5 is where it changed.\n",
            "hints": [
              {
                "when": "you put the breakpoint before opening DevTools",
                "say": "The breakpoint is a click in the Sources panel. What has to be open before there is a line number to click?\n"
              },
              {
                "when": "you pressed Resume before setting the breakpoint",
                "say": "With no breakpoint set, Resume runs to the end of the program. The breakpoint is what gives Resume somewhere to stop.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Find the line with the debugger",
            "prompt": "Someone else's grade book. `report` is loaded before any bonus is applied, and yet line 18 prints `[85, 95]` — the curved scores. Nothing throws. Answer as the debugger would, and end at the line to fix.\n",
            "filename": "grades.js",
            "code": "const cache = {};\n\nfunction loadStudent(id) {\n  if (!cache[id]) {\n    cache[id] = { id, scores: [80, 90] };\n  }\n  return cache[id];\n}\n\nfunction withBonus(student, bonus) {\n  const s = student;\n  s.scores = s.scores.map(x => x + bonus);\n  return s;\n}\n\nconst report = loadStudent(7);\nconst curved = withBonus(loadStudent(7), 5);\nconsole.log(report.scores);\n",
            "items": [
              {
                "id": "c1",
                "question": "Right when paused on line 17, wrong on line 18. Paused on line 17, you press **Step into**. Which function are you in? Type its name.",
                "accept": [
                  "loadStudent",
                  "loadstudent",
                  "loadStudent()"
                ],
                "why": "`loadStudent`. The arguments are evaluated before the call they are passed to, so Step into descends into `loadStudent(7)` first. Step out and Step into again to reach `withBonus`.\n"
              },
              {
                "id": "c2",
                "question": "Paused on line 12, inside `withBonus`. In the Call Stack, the frame under `withBonus` is at which line? Type the line number.",
                "accept": [
                  "17",
                  "line 17"
                ],
                "why": "Line 17, the only call to `withBonus`.\n"
              },
              {
                "id": "c3",
                "question": "Paused on line 12, the Watch `cache[7].scores[0]` reads 80. After one Step over, what does it read?",
                "accept": [
                  "85"
                ],
                "why": "`85`. Line 12 wrote to the one student object the cache holds — the same object `report` names.\n"
              },
              {
                "id": "c4",
                "question": "Paused on line 12. What does the Watch `s === cache[7]` show?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`: `withBonus` was handed the cached object itself, and line 11 gave it a third name. That is how line 12 reached `report`.\n"
              },
              {
                "id": "c5",
                "question": "Line 12 is where the scores changed. Which line was meant to make an independent student and instead made a second name for the same one? Type the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "Line 11: `const s = student` copies the reference. The line where a value changed is rarely the bug; the bug is that line 12 could reach the report at all. (A second defensible design moves the copy to line 7, so the cache never hands out the object it keeps.)\n"
              }
            ],
            "hints": [
              {
                "when": "you answered withBonus for Step into on line 17",
                "say": "Before `withBonus` can be called, its arguments need values. What is the first argument, and what does working it out involve?\n"
              },
              {
                "when": "you answered 12 for the line to fix",
                "say": "Line 12 is where it *changed*. Ask what let it reach the report's object — which line made `s` point there?\n"
              },
              {
                "when": "you answered 80 after the Step over",
                "say": "Line 12 assigns a new array to `s.scores`. Whose `scores` property is that — a copy's, or the cached object's?\n"
              }
            ]
          }
        ]
      }
    },
    "watch-a-value": {
      "id": "watch-a-value",
      "title": "Watch a value change",
      "kind": "trial",
      "kicker": "TRIAL 3.4",
      "filename": "items.js",
      "exportName": "itemsFor",
      "brief": "`itemsFor` returns an order's items, or an empty list when it has none. It throws nothing, and for a single order it is correct. Run the trials first: one of them fails because two different orders came back with the same list. Find out why before you change anything — a breakpoint on the `return` and a look at what you are returning will show it faster than reading will. One constraint: when an order *does* have items, return that order's own array — the defect is the shared fallback, not returning a reference.",
      "hint": "`EMPTY` is created once, when the file loads — not once per call. Every order with no items gets that same array, so the first caller to push into it has changed what the next caller receives. Ask what a caller is allowed to do with the value you return.",
      "success": "That is it. Note what the trials caught: not a crash, and not a wrong value on the call you were looking at — a wrong value on somebody else's call, later. That is the shape of every aliasing bug you will meet.",
      "starter": "// Returns an order's items, or an empty list when it has none.\n// Runs fine. Two orders can end up sharing one list.\nconst EMPTY = [];\n\nfunction itemsFor(order) {\n  return order.items || EMPTY;\n}\n",
      "tests": [
        {
          "label": "returns the order's own items when it has them",
          "body": "var r = subject({ id: 1, items: [\"widget\"] }); return r.length === 1 && r[0] === \"widget\";"
        },
        {
          "label": "returns an empty list when the order has none",
          "body": "return subject({ id: 1 }).length === 0;"
        },
        {
          "label": "two order-less orders do not get the SAME list",
          "body": "return subject({ id: 1 }) !== subject({ id: 2 });"
        },
        {
          "label": "pushing into one order's list does not affect another's",
          "body": "var a = subject({ id: 1 }); a.push(\"widget\"); return subject({ id: 2 }).length === 0;"
        },
        {
          "label": "the caller's own items array is returned, not copied",
          "body": "var own = [\"widget\"]; return subject({ id: 1, items: own }) === own;"
        },
        {
          "label": "an order with an empty items array keeps its own array",
          "body": "var own = []; var r = subject({ id: 1, items: own }); r.push(\"x\"); return own.length === 1;"
        }
      ],
      "_source": "trials/watch-a-value.yml"
    },
    "shallow-and-deep": {
      "id": "shallow-and-deep",
      "title": "One level deep, or all the way",
      "kind": "read",
      "outcome": 2,
      "shape": "conceptual",
      "why": "Outcome 2's fix for shared data is a copy, and the copy JavaScript makes most readily — spread — shares everything one level down. `data.shallow-vs-deep` is its own concept in `concepts.md` family B: produce an independent copy, and say when one level is not enough. It is also the mechanism behind the debugger lesson's `config.js` (D-025 item 4).",
      "concepts": [
        "data.shallow-vs-deep"
      ],
      "lede": "\"Just copy it\" is the fix for shared data, and it is half right. The quick copies make a new outer object and fill it with the same arrows the old one held. Whether that is enough depends entirely on what is inside.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What a function can change · Meet the debugger · Making data unchangeable"
        },
        {
          "label": "MARGIN NOTE",
          "body": "A shallow copy is a new box holding the same arrows."
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four programs. Type the final value. A guess is fine; that is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const s = { inner: { b: 2 } };   const c = { ...s };   c.inner.b = 9;   →  s.inner.b",
                          "accept": [
                            "9"
                          ],
                          "miss": "**If you answered `2` because spread makes a copy — it makes a copy one level deep.** Whether that is enough depends on whether the values inside are themselves references. Here `inner` is one.\n",
                          "why": "`9`. Spread copies one level. `c` is a new object, and `c.inner` is the *same* inner object as `s.inner`: a new box holding the same arrow.\n"
                        },
                        {
                          "code": "const s = { a: 1, inner: { b: 2 } };   const c = { ...s };   c.a = 9;   →  s.a",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If you answered `9` because the last one shared — the copy is real.** `a` held a number, so the spread copied the number.\n",
                          "why": "`1`. At the top level the copy is independent. The sharing starts one level down, and only for values that were references.\n"
                        },
                        {
                          "code": "const a = [[1], [2]];   const b = a.slice();   b[0].push(9);   →  a[0].length",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1` because `slice` copies the array — it copies the outer array.** The inner arrays are references, and the copy holds the same ones.\n",
                          "why": "`2`. `slice` is a one-level copy too. `b` is a new outer array holding the same inner arrays, so `b[0]` and `a[0]` are one array.\n"
                        },
                        {
                          "code": "const s = { inner: { b: 2 } };   const c = structuredClone(s);   c.inner.b = 9;   →  s.inner.b",
                          "accept": [
                            "2"
                          ],
                          "why": "`2`. `structuredClone` copies every level, so `c.inner` is a new object and nothing is shared.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "s = {'inner': {'b': 2}}; c = s.copy(); c['inner']['b'] = 9  →  s['inner']['b']",
                          "accept": [
                            "9"
                          ],
                          "miss": "**If you answered `2` — `.copy()` is shallow.** The new dict holds the same inner dict.\n",
                          "why": "`.copy()` is shallow. The new dict holds the same inner dict.\n"
                        },
                        {
                          "code": "import copy; d = copy.deepcopy(s); d['inner']['b'] = 1  →  s['inner']['b']",
                          "accept": [
                            "9",
                            "2"
                          ],
                          "why": "`deepcopy` recursively copies, so `d` is genuinely independent — whatever `s` held at that point is what `s` still holds.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can make a copy that is genuinely independent of the original, and say when a one-level copy is not enough"
              ]
            }
          ]
        },
        {
          "h": "A new box holding the same arrows"
        },
        {
          "p": "You have seen this bug already, from the inside. Recall the program you ran\nunder the debugger:"
        },
        {
          "code": "const defaults = { retries: 3, limits: { max: 10 } };\n\nfunction makeConfig(overrides) {\n  const c = { ...defaults };\n  Object.assign(c.limits, overrides);\n  return c;\n}",
          "lang": "js",
          "file": "config.js"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "Paused after line 4 of `config.js`. What does the Watch `c.limits === defaults.limits` show?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you answered `false` because `c` is a new object — it is.** What it holds under `limits` is the arrow it found in `defaults`.\n",
                "why": "`true`. `c` is a new object, and its `limits` is the same object as `defaults.limits`. That is what let line 5 change the defaults.\n"
              }
            ]
          }
        },
        {
          "p": "`{ ...defaults }` did make a copy. It made a **new outer object** and, for each\nproperty, copied what that property held. `retries` held a number, so the\nnumber was copied. `limits` held a reference, so the *reference* was copied —\nand now two objects point at one inner object."
        },
        {
          "code": "defaults ──► { retries: 3, limits: ─┐ }\n                                    ├──► { max: 10 }\nc ────────► { retries: 3, limits: ─┘ }",
          "lang": "text"
        },
        {
          "p": "That is a **shallow** copy: one level deep. Every quick copy in JavaScript is\none:"
        },
        {
          "code": "const shallow = { ...original };          // objects\nconst also    = Object.assign({}, original);\nconst list2   = [...list];                // arrays\nconst list3   = list.slice();",
          "lang": "js"
        },
        {
          "p": "Here is a shallow copy doing both things at once:"
        },
        {
          "code": "const original = { name: \"ada\", tags: [\"x\"] };\n\nconst copy = { ...original };\ncopy.name = \"bob\";        // independent — a string was copied\ncopy.tags.push(\"y\");      // NOT independent — same array\noriginal.tags;            // [\"x\", \"y\"]",
          "lang": "js"
        },
        {
          "p": "The copy is real. It is the *contents* that are shared, and only the contents\nthat were themselves references."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const p = { pos: { x: 1 }, hp: 10 };   const q = Object.assign({}, p);   q.hp = 0;   q.pos.x = 5;   →  p.hp",
                "accept": [
                  "10"
                ],
                "why": "`10`. `hp` held a number, so the copy has its own. That change stayed in `q`.\n"
              },
              {
                "question": "Same program: what is `p.pos.x`?",
                "accept": [
                  "5"
                ],
                "miss": "**If you answered `1` — `Object.assign({}, p)` is one level, like spread.** `q.pos` is the same object as `p.pos`.\n",
                "why": "`5`. `pos` held a reference, so `q.pos` and `p.pos` are one object.\n"
              }
            ]
          }
        },
        {
          "h": "Copying every level"
        },
        {
          "p": "A **deep** copy recreates every level, so nothing in the copy points into the\noriginal. JavaScript has one built in:"
        },
        {
          "code": "const deep = structuredClone(original);\ndeep.tags.push(\"y\");      // original.tags is untouched",
          "lang": "js"
        },
        {
          "p": "`structuredClone` copies nested objects and arrays, and also dates, maps, sets\nand objects that refer to themselves. It **cannot copy functions** — it throws\na `DataCloneError` — which is usually a sign you were cloning something that\nshould not have been cloned."
        },
        {
          "p": "`structuredClone` is recent — browsers and Node added it in 2021 and 2022. In\ncode written before it, you will often see the older way:"
        },
        {
          "code": "const deep = JSON.parse(JSON.stringify(original));",
          "lang": "js"
        },
        {
          "p": "That turns the object into JSON text and reads it back — a deep copy of\nwhatever JSON can hold, and a quiet loss of whatever it cannot. A date comes\nback as a string. A `Map` or a `Set` comes back as `{}`. A property whose value\nis `undefined` or a function disappears. When the data is plain objects,\narrays, strings and numbers, it works; when it is not, prefer `structuredClone`."
        },
        {
          "table": {
            "head": [
              "Technique",
              "Depth",
              "Use when"
            ],
            "rows": [
              [
                "`{ ...obj }` · `Object.assign({}, obj)` · `[...arr]` · `arr.slice()`",
                "one level",
                "the values inside are primitives, or nobody will change the nested parts"
              ],
              [
                "`structuredClone(obj)`",
                "all levels",
                "you need genuine independence"
              ],
              [
                "`JSON.parse(JSON.stringify(obj))`",
                "all levels, lossy",
                "you will meet it; the data must be plain JSON"
              ],
              [
                "a hand-written copy",
                "whatever you write",
                "the structure is known and small"
              ]
            ]
          }
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const s = { when: new Date(0) };   const c = JSON.parse(JSON.stringify(s));   →  typeof c.when",
                "accept": [
                  "\"string\"",
                  "string",
                  "'string'"
                ],
                "miss": "**If you answered `\"object\"` — JSON has no dates.** `stringify` wrote the date as text, and `parse` read text back.\n",
                "why": "`\"string\"`. The JSON round trip copied every level and turned the date into a string on the way.\n"
              },
              {
                "code": "const s = { list: [1] };   const c = structuredClone(s);   →  c.list === s.list",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. Every level was recreated, so `c.list` is a second array with the same contents.\n"
              }
            ]
          }
        },
        {
          "h": "How deep is deep enough"
        },
        {
          "p": "You do not always need a deep copy. You need the copy to be separate **at every\nlevel someone will change**. Two questions settle it: what is inside, and what\nwill be changed. Worked twice, the first with its subgoals labelled."
        },
        {
          "code": "// A settings panel keeps a draft the user can cancel out of.\nconst saved = { theme: \"dark\", filters: { tags: [\"a\"] } };\n\n// list what is inside\n//   theme   -> a string: copied by any copy.\n//   filters -> an object holding an array: shared by a one-level copy.\n// find what will be changed\n//   the user edits theme, and adds and removes filter tags.\n// choose the depth\n//   filters.tags is two levels down and will be changed -> one level is not enough.\nconst draft = structuredClone(saved);\n\n// check the copy\ndraft.filters.tags.push(\"b\");\nsaved.filters.tags;   // [\"a\"] — cancelling really does restore it",
          "lang": "js",
          "file": "draft.js"
        },
        {
          "code": "// A puzzle game saves the board before each move, so it can undo.\nconst board = { moves: 12, cells: [[\"x\", \"\"], [\"\", \"o\"]] };\n\n// cells is an array of arrays, and each move writes into a row:\n// board.cells[r][c] = \"x\". So the outer array AND every row must be new.\n// A hand-written copy does exactly that — one spread per level:\nconst snapshot = { ...board, cells: [[...board.cells[0]], [...board.cells[1]]] };\n\nboard.cells[0][1] = \"x\";\nsnapshot.cells[0][1];   // \"\" — the undo still has the old board",
          "lang": "js",
          "file": "board.js"
        },
        {
          "p": "The second one copied by hand to show the levels, and it only works because\nthe board has exactly two rows; `structuredClone(board)` does the same for any\nsize. What the two have in common is the order of the questions:\n**what is inside, and what will change** — and only then which tool."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "The board program, with the snapshot made by `{ ...board }` instead. After `board.cells[0][1] = \"x\"`, what is `snapshot.cells[0][1]`?",
                "accept": [
                  "\"x\"",
                  "x",
                  "'x'"
                ],
                "miss": "**If you answered `\"\"` — `{ ...board }` copied the reference to `cells`.** The snapshot and the board share every row.\n",
                "why": "`\"x\"`. One level: the snapshot's `cells` is the board's `cells`, so the move changed the undo copy too.\n"
              },
              {
                "question": "A list of prices, all numbers, and you will change some in the copy. Is `[...prices]` enough?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. The values inside are numbers, so a one-level copy copies them all. There is no second level to share.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Choosing how deep to copy",
            "body": [
              {
                "h": "Choosing how deep to copy"
              },
              {
                "p": "Two questions, and you are done."
              },
              {
                "code": "// You are about to copy something.\n\n// 1. What is inside it?\n//    only primitives (numbers, strings, booleans)  -> shallow is enough.\n//    anything else (objects, arrays, dates, maps)  -> keep going.\n\n// 2. Will anyone change the nested parts?\n//    no  -> shallow is still enough; you are sharing read-only data.\n//    yes -> copy those levels too: structuredClone, or by hand.",
                "lang": "js",
                "file": "how-deep.js"
              },
              {
                "p": "Now a real case."
              },
              {
                "code": "const order = { id: 7, lines: [{ sku: \"A1\", qty: 1 }] };\n\nconst edited = { ...order };\nedited.id = 8;               // ______ does this reach order?\nedited.lines[0].qty = 3;     // ______ does this reach order?",
                "lang": "js",
                "file": "order.js"
              },
              {
                "p": "The first does not: `id` held a number. The second does: `edited.lines` is\n`order.lines`, and even a copied array would still hold the same line\nobjects. The shallow copy was right for `id` and wrong for `lines`, in the same\nobject — which is why \"just spread it\" is not a rule you can apply without\nlooking inside."
              }
            ]
          }
        },
        {
          "tracks": {
            "js": [
              {
                "code": "const copy = { ...obj };                // one level\nconst arr2 = [...arr];                  // one level\nconst deep = structuredClone(obj);      // all levels; no functions",
                "lang": "js"
              },
              {
                "p": "To copy one nested level by hand, spread it too:\n`{ ...obj, inner: { ...obj.inner } }`. Each spread copies one level, so you\nwrite one for every level that will change."
              }
            ],
            "python": [
              {
                "p": "`copy.copy` is shallow, `copy.deepcopy` is recursive, and slicing a list is a\nshallow copy of it."
              },
              {
                "code": "shallow = original.copy()      # or dict(original), list(original), original[:]\ndeep    = copy.deepcopy(original)",
                "lang": "python"
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the first and third, name\nthe smallest change that would leave the original untouched."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A notes app keeps an undo history. Before every edit it saves\n`history.push({ ...doc })`, where\n`doc = { title: \"Trip\", paragraphs: [{ text: \"Day 1\" }, { text: \"Day 2\" }] }`.\nUsers report that undo brings back an old title but never an old paragraph."
            },
            {
              "p": "Three fixes:"
            },
            {
              "list": [
                "**`structuredClone(doc)`** before every edit.",
                "**Copy by hand** — a new `doc`, a new `paragraphs` array, and a new object for each paragraph in it.",
                "**Stop editing paragraphs in place** — every edit replaces the paragraph object it changes with a new one, so a one-level copy of `doc` is safe."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: on a document with a thousand\nparagraphs and an edit on every keystroke, what the first copies each time;\nwhat the second needs when someone adds a `comments` array to each paragraph;\nand what the third needs from every line of code that edits a paragraph."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A **shallow** copy — spread, `Object.assign`, `slice` — is a new container holding the **same references**. It is enough when the contents are primitives, or when nobody changes the nested parts.",
                "A **deep** copy recreates every level. `structuredClone` is built in and cannot copy functions; the older `JSON.parse(JSON.stringify(x))` loses dates, maps, sets and `undefined`.",
                "Choose the depth by asking **what is inside, and what will change** — then copy every level that will."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Spread syntax",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax",
              "note": "read the \"only one level deep\" warning; it is this lesson in one sentence."
            },
            {
              "title": "structuredClone()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/structuredClone",
              "note": "what it can and cannot clone, functions included."
            },
            {
              "title": "Deep copy",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Deep_copy",
              "note": "MDN's glossary entry, with the JSON round trip and what it drops."
            },
            {
              "title": "Shallow copy",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Shallow_copy",
              "note": "the matching entry, with the one-level examples."
            }
          ]
        }
      ],
      "_source": "lessons/shallow-and-deep.md",
      "practice": {
        "id": "shallow-and-deep",
        "outcome": 2,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict how deep the copy went",
            "prompt": "Three quick ones. Type the final value, or pick one of the two options.\n",
            "items": [
              {
                "id": "p1",
                "code": "const s = { inner: { b: 2 } };   const c = { ...s };   c.inner.b = 9;   →  s.inner.b",
                "accept": [
                  "9"
                ],
                "why": "Spread copies one level. `c` is a new object, and `c.inner` is the *same* inner object as `s.inner` — a new box holding the same arrow.\n"
              },
              {
                "id": "p2",
                "code": "const s = { a: 1, inner: { b: 2 } };   const c = { ...s };   c.a = 9;   →  s.a",
                "accept": [
                  "1"
                ],
                "why": "`a` held a number, so the spread copied the number. At the top level, the copy is independent — the sharing starts one level down.\n"
              },
              {
                "id": "p3",
                "question": "`saved` is `{ theme: \"dark\", filters: { tags: [\"a\"] } }`. You need a draft whose `filters.tags` you can push to without touching `saved`. Which is the first step?",
                "choices": [
                  "{ ...saved }",
                  "structuredClone(saved)"
                ],
                "accept": [
                  "structuredClone(saved)"
                ],
                "why": "The first question is what is inside it: `filters` is an object holding an array, and that array is what will be pushed to. A spread would share it, so this needs every level copied.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const a = [[1], [2]];   const b = a.slice();   b[0].push(9);   →  a[0].length",
                "accept": [
                  "2"
                ],
                "why": "`slice` is a one-level copy too. `b` is a new outer array holding the same inner arrays, so `b[0]` and `a[0]` are one array.\n"
              },
              {
                "id": "p5",
                "code": "const s = { inner: { b: 2 } };   const c = structuredClone(s);   c.inner.b = 9;   →  s.inner.b",
                "accept": [
                  "2"
                ],
                "why": "A deep copy recreates every level, so nothing is shared and the original is untouched.\n"
              },
              {
                "id": "p6",
                "code": "const s = { pos: { x: 1 } };   const c = Object.assign({}, s);   c.pos = { x: 7 };   →  s.pos.x",
                "accept": [
                  "1"
                ],
                "why": "`1`. This line puts a *new* object into `c`'s own `pos` property. `c` is a new object, so its properties are its own; nothing was written into the shared `{ x: 1 }`.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 2 for the first",
                "say": "Spread made a new outer object. Ask what it put in that object under `inner` — a new object, or the arrow it found in `s`?\n"
              },
              {
                "when": "you answered 9 for the second",
                "say": "`a` held a number, not an arrow. What does copying a number give you?\n"
              },
              {
                "when": "you chose the spread for the draft",
                "say": "List what is inside `saved`, and find what will be changed. How many levels down is `tags`?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Three copies of one team",
            "prompt": "One object, copied three ways, then each copy changed. Predict each line, then run it.\n",
            "filename": "team.js",
            "code": "const team = { name: \"Owls\", players: [\"ana\", \"bo\"], coach: { name: \"Kim\" } };\n\nconst a = { ...team };\nconst b = structuredClone(team);\nconst c = JSON.parse(JSON.stringify(team));\n\na.players.push(\"cy\");\nb.coach.name = \"Lee\";\nc.players.push(\"dee\");\n\nconsole.log(team.players.length);\nconsole.log(team.coach.name);\nconsole.log(a.players === team.players);\nconsole.log(b.players === team.players);\nconsole.log(c.players.length);\n\nconst d = JSON.parse(JSON.stringify({ founded: new Date(2020, 0, 1) }));\nconsole.log(typeof d.founded);\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(team.players.length)",
                "accept": [
                  "3"
                ],
                "miss": "**If you answered `2` — `a` is a spread, and `a.players` is `team.players`.** The push through `a` landed in the team's array.\n",
                "why": "`3`. The spread shared `players`, so `\"cy\"` went into the team's own array. The pushes into `c` went into `c`'s own copy.\n"
              },
              {
                "id": "r2",
                "code": "console.log(team.coach.name)",
                "accept": [
                  "Kim",
                  "\"Kim\"",
                  "'Kim'"
                ],
                "why": "`Kim`. `b` is a deep copy, so `b.coach` is a different object and `\"Lee\"` went there.\n"
              },
              {
                "id": "r3",
                "code": "console.log(a.players === team.players)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true` — one array, two names. This single `true` is the whole difference between a shallow and a deep copy.\n"
              },
              {
                "id": "r4",
                "code": "console.log(b.players === team.players)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. `structuredClone` built a second array with the same contents.\n"
              },
              {
                "id": "r5",
                "code": "console.log(c.players.length)",
                "accept": [
                  "3"
                ],
                "why": "`3`. `c` was copied before `\"cy\"` was pushed, so it started with two players — its own two — and then got `\"dee\"`.\n"
              },
              {
                "id": "r6",
                "code": "console.log(typeof d.founded)",
                "accept": [
                  "string",
                  "\"string\"",
                  "'string'"
                ],
                "why": "`string`. The JSON round trip copies every level of what JSON can hold, and JSON cannot hold a date.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 2 for team.players.length",
                "say": "Three copies were changed. For each, ask whether its `players` is its own array or the team's.\n"
              },
              {
                "when": "you predicted 4 for c.players.length",
                "say": "When was `c` made — before or after `\"cy\"` was pushed? And is `c.players` the team's array, or its own?\n"
              },
              {
                "when": "you predicted object for typeof d.founded",
                "say": "`JSON.stringify` has to write the date as JSON text. What does `JSON.parse` turn text back into?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace what is shared, level by level",
            "prompt": "A one-level copy, then three changes to it. Give the original's value after each line.\n",
            "items": [
              {
                "id": "t1",
                "code": "1  const orig = { a: 1, inner: { b: 2 }, list: [1] };\n2  const c = { ...orig };\n3  c.a = 9;\n4  c.inner.b = 9;\n5  c.list = [7];\n\nafter line 3  →  orig.a\n",
                "accept": [
                  "1"
                ],
                "why": "`a` held a number, so line 2 copied the number. Changing `c.a` cannot reach `orig.a`.\n"
              },
              {
                "id": "t2",
                "question": "Same program, after line 4: what is `orig.inner.b`?",
                "accept": [
                  "9"
                ],
                "why": "`inner` held a reference, so line 2 copied the reference. `c.inner` and `orig.inner` are one object, and line 4 mutated it.\n"
              },
              {
                "id": "t3",
                "question": "Same program, after line 5: what is `orig.list`?",
                "accept": [
                  "[1]",
                  "1"
                ],
                "why": "Line 5 does not touch the shared array — it **rebinds** `c`'s own `list` property to a new array. `c` is a new object, so its properties are its own. `orig.list` is untouched.\n"
              },
              {
                "id": "t4",
                "code": "const o = { tags: [\"x\"] };   const h = { ...o, tags: [...o.tags] };   h.tags.push(\"y\");   →  o.tags.length",
                "accept": [
                  "1"
                ],
                "why": "`1`. The second spread copied the nested array too — one spread per level — so `h.tags` is its own array.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "const s = { list: [1] };   const c = structuredClone(s);   c.list.push(2);   →  s.list.length",
                "accept": [
                  "1"
                ],
                "why": "`1`. A deep copy recreates every level, so `c.list` is a second array.\n"
              },
              {
                "id": "t6",
                "code": "const rows = [{ n: 1 }];   const copy = [...rows];   copy[0].n = 5;   →  rows[0].n",
                "accept": [
                  "5"
                ],
                "why": "`5`. `[...rows]` is a new outer array holding the same row objects. Writing into `copy[0]` wrote into the object `rows[0]` also names.\n"
              },
              {
                "id": "t7",
                "code": "const s = { hi: undefined, n: 1 };   const c = JSON.parse(JSON.stringify(s));   →  \"hi\" in c",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. JSON has no `undefined`, so `stringify` left the property out and it never came back.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 2 after line 4",
                "say": "Ask what line 2 put in `c.inner`. A spread copies what each property holds — and `inner` holds an arrow, not an object.\n"
              },
              {
                "when": "you answered [7] after line 5",
                "say": "Line 5 has `c.list` on the left of `=`. That changes which array `c`'s property points at; it does not change the array itself. Which object owns the property `c.list`?\n"
              },
              {
                "when": "you answered 2 for the hand-copied tags",
                "say": "Count the spreads in the line that made `h`. Each one copies one level. Which level is `tags`?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Make the draft independent",
            "prompt": "An editor lets the user change a draft and then cancel. `startEdit` must return a copy of `saved` that the editor can change at any depth without touching `saved`. `saved` holds a date, which must still be a date in the draft. Fill the gap with one line. Then, in one sentence: why does `{ ...saved }` pass the first test and fail another?\n",
            "exportName": "startEdit",
            "filename": "edit.js",
            "starter": "// The draft must be changeable at every level without touching `saved`.\n// `saved.updated` is a Date, and must stay one.\nfunction startEdit(saved) {\n  /* ___ one line: return a copy that shares nothing with saved ___ */\n}\n",
            "tests": [
              {
                "label": "the draft has the same settings",
                "body": "const s = { theme: 'dark', filters: { tags: ['a'] }, updated: new Date(2024, 0, 1) }; const d = subject(s); return d.theme === 'dark' && d.filters.tags.join(',') === 'a';"
              },
              {
                "label": "the draft is a different object",
                "body": "const s = { theme: 'dark', filters: { tags: ['a'] }, updated: new Date(2024, 0, 1) }; return subject(s) !== s;"
              },
              {
                "label": "changing the draft's theme leaves saved alone",
                "body": "const s = { theme: 'dark', filters: { tags: ['a'] }, updated: new Date(2024, 0, 1) }; subject(s).theme = 'light'; return s.theme === 'dark';"
              },
              {
                "label": "adding a tag to the draft leaves saved's tags alone",
                "body": "const s = { theme: 'dark', filters: { tags: ['a'] }, updated: new Date(2024, 0, 1) }; subject(s).filters.tags.push('b'); return s.filters.tags.length === 1;"
              },
              {
                "label": "the date is still a date",
                "body": "const s = { theme: 'dark', filters: { tags: ['a'] }, updated: new Date(2024, 0, 1) }; return subject(s).updated instanceof Date;"
              }
            ],
            "hints": [
              {
                "when": "adding a tag to the draft changes saved",
                "say": "Your copy is one level deep. `filters` holds an object holding an array, and the array is what gets pushed to. Every level has to be new.\n"
              },
              {
                "when": "the date is no longer a date",
                "say": "The JSON round trip copies every level of what JSON can hold, and a date comes back as a string. There is a built-in deep copy that keeps dates.\n"
              },
              {
                "when": "the draft is the same object as saved",
                "say": "Returning `saved` gives the caller a second name for it, not a copy. Every change to the draft would be a change to `saved`.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a cart module",
            "prompt": "An AI assistant wrote this cart module and a colleague is about to merge it. Carts are reported \"picking up items from other people's carts\". Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "cart.js",
            "code": "// Every cart starts from these. const, so they can never change.\nconst DEFAULT_CART = { items: [], shipping: { method: \"standard\", cost: 5 } };\n\nfunction newCart() {\n  return { ...DEFAULT_CART };\n}\n\nfunction addItem(cart, item) {\n  cart.items.push(item);\n  return cart;\n}\n\nfunction withExpress(cart) {\n  const updated = Object.assign({}, cart);\n  updated.shipping.method = \"express\";\n  updated.shipping.cost = 15;\n  return updated;\n}\n",
            "items": [
              {
                "id": "c1",
                "code": "const a = newCart(); const b = newCart(); addItem(a, \"pen\");  →  b.items.length",
                "accept": [
                  "1"
                ],
                "why": "`a` and `b` are two objects holding one `items` array, so the pen is in both. That is the bug report, reproduced.\n"
              },
              {
                "id": "c2",
                "question": "Which line makes every cart share one `items` array? Type the line number.",
                "accept": [
                  "5",
                  "line 5"
                ],
                "why": "Line 5. `{ ...DEFAULT_CART }` is a new outer object whose `items` and `shipping` are the defaults' own arrays and objects.\n"
              },
              {
                "id": "c3",
                "question": "What is wrong with line 5?",
                "choices": [
                  "A shallow copy",
                  "A missing return",
                  "A rebinding"
                ],
                "accept": [
                  "A shallow copy"
                ],
                "why": "A shallow copy — one level. Enough for a cart made of numbers and strings; this one holds an array and an object.\n"
              },
              {
                "id": "c4",
                "question": "Which line has a comment that promises something the code does not deliver? Type the line number.",
                "accept": [
                  "1",
                  "line 1"
                ],
                "why": "Line 1. `const` stops `DEFAULT_CART` being pointed at a different object; it does nothing to stop the object's contents changing — and they do, through every cart.\n"
              },
              {
                "id": "c5",
                "question": "Calling `withExpress(a)`: which line first changes the shipping of every other cart, and the defaults? Type the line number.",
                "accept": [
                  "15",
                  "line 15"
                ],
                "why": "Line 15. `Object.assign({}, cart)` is another one-level copy, so `updated.shipping` is the shared shipping object, and line 15 writes into it. Line 16 does it again.\n"
              }
            ],
            "exportName": "shop",
            "starter": "// Every cart starts from these. const, so they can never change.\nconst DEFAULT_CART = { items: [], shipping: { method: \"standard\", cost: 5 } };\n\nfunction newCart() {\n  return { ...DEFAULT_CART };\n}\n\nfunction addItem(cart, item) {\n  cart.items.push(item);\n  return cart;\n}\n\nfunction withExpress(cart) {\n  const updated = Object.assign({}, cart);\n  updated.shipping.method = \"express\";\n  updated.shipping.cost = 15;\n  return updated;\n}\n\nconst shop = { newCart, addItem, withExpress };\n",
            "tests": [
              {
                "label": "two new carts do not share their items",
                "body": "const a = subject.newCart(); const b = subject.newCart(); subject.addItem(a, 'pen'); return b.items.length === 0;"
              },
              {
                "label": "a cart made after others were used is empty",
                "body": "subject.addItem(subject.newCart(), 'pen'); return subject.newCart().items.length === 0;"
              },
              {
                "label": "addItem still adds to the cart it is given",
                "body": "const a = subject.newCart(); subject.addItem(a, 'pen'); return a.items.length === 1;"
              },
              {
                "label": "withExpress returns an express cart",
                "body": "const e = subject.withExpress(subject.newCart()); return e.shipping.method === 'express' && e.shipping.cost === 15;"
              },
              {
                "label": "withExpress leaves the cart it was given alone",
                "body": "const a = subject.newCart(); subject.withExpress(a); return a.shipping.method === 'standard' && a.shipping.cost === 5;"
              },
              {
                "label": "withExpress leaves other carts, and new ones, standard",
                "body": "const a = subject.newCart(); const b = subject.newCart(); subject.withExpress(a); return b.shipping.method === 'standard' && subject.newCart().shipping.cost === 5;"
              },
              {
                "label": "withExpress keeps the items",
                "body": "const a = subject.newCart(); subject.addItem(a, 'pen'); return subject.withExpress(a).items.length === 1;"
              }
            ],
            "hints": [
              {
                "when": "new carts are fixed but withExpress still changes the cart it was given",
                "say": "`withExpress` has the same defect as `newCart` had: its copy is one level, and the level it changes is the second one. It needs its own `shipping` object.\n"
              },
              {
                "when": "you froze DEFAULT_CART and items still leak between carts",
                "say": "`Object.freeze` is one level too. It stops `DEFAULT_CART.items` being pointed elsewhere, not the array being pushed to — and in a plain script a refused write says nothing.\n"
              },
              {
                "when": "carts no longer share items but withExpress changes every cart's shipping",
                "say": "Giving each cart its own `items` fixed one of the two shared things. Look at what else `DEFAULT_CART` holds that is not a number or a string.\n"
              }
            ]
          }
        ]
      }
    },
    "immutability": {
      "id": "immutability",
      "title": "Making data unchangeable",
      "kind": "read",
      "outcome": 2,
      "shape": "conceptual",
      "why": "Outcome 2's last defence against shared data: if nothing can change it, sharing it is harmless. `data.immutability` asks a student to defend that choice and name its cost; `data.value-types` is the other way out — a type whose values are copied — which JavaScript does not have, so it is met here as an idea and assessed by question (non-negotiable 4, D-005, D-023).",
      "concepts": [
        "data.immutability",
        "data.value-types"
      ],
      "lede": "Every aliasing bug needs two things: two names for one object, and a change through one of them. Copying removes the first. Making the data unchangeable removes the second — and JavaScript's support for that is thinner than it looks.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "One level deep, or all the way · What a function can change · Copying versus aliasing"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Immutable** — cannot be changed after it is made. **Value type** — a type whose values are copied on assignment, the way numbers are, instead of shared.",
          "entries": [
            "**Immutable** — cannot be changed after it is made.",
            "**Value type** — a type whose values are copied on assignment, the way numbers are, instead of shared."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four programs. Give the final value — type it, or pick it where there are\nbuttons, and pick `error` if you think the line throws. A guess is fine; that is\nwhat this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const o = Object.freeze({ a: 1 });   o.a = 5;   →  o.a",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If you answered `error` — in a module it would be one.** This site runs code as a plain script, and there a write to a frozen object is refused *silently*: no error, no change.\n",
                          "why": "`1`. The object is frozen, so the write was refused. In a plain script the refusal says nothing; in a module it throws a TypeError.\n"
                        },
                        {
                          "code": "const o = Object.freeze({ a: 1, inner: { b: 2 } });   o.inner.b = 9;   →  o.inner.b",
                          "accept": [
                            "9"
                          ],
                          "miss": "**If you expected `freeze` to make it unchangeable — it makes the object's own properties unwritable and stops there.** Anything those properties point at is untouched.\n",
                          "why": "`9`. `freeze` is one level. It froze `o`, not the object `o.inner` points at.\n"
                        },
                        {
                          "code": "const p = { n: 1, tags: [\"x\"] };   const q = { ...p, n: 2 };   →  p.n",
                          "accept": [
                            "1"
                          ],
                          "why": "`1`. `{ ...p, n: 2 }` builds a new object with `n` replaced. `p` was never written to.\n"
                        },
                        {
                          "question": "In C#, `Point` is declared as a struct. After `var p = new Point { X = 1 }; var q = p; q.X = 9;`, what is `p.X`?",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If you answered `9` — that is JavaScript's rule for objects, and a `struct` opts out of it.** Assigning a struct copies it, the way assigning a number does.\n",
                          "why": "`1`. A struct is a **value type**: `q = p` made a second `Point`, so changing `q` cannot reach `p`. JavaScript has no way to declare one.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "var p = new Point(1,2); var list = new List<Point>{p}; p.X = 9;  →  list[0].X  (Point is a struct)",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If you answered `9` — `Point` is a value type, so the list holds a copy of it.**\n",
                          "why": "The list holds a *copy*, because `Point` is a value type. This is the whole class of bug JavaScript cannot express.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can defend making data unchangeable, and name what it costs",
                "I can say what a struct is, and why a language would offer one"
              ]
            }
          ]
        },
        {
          "h": "If nothing can change, sharing is free"
        },
        {
          "p": "Look back at every aliasing bug so far: a store that lent out its list, a cache\nthat handed one object to two callers, a cart built from shared defaults. Each\nneeded two names for one object **and** a line that changed it through one of\nthem. Remove either, and the bug cannot happen."
        },
        {
          "p": "Copying removes the first. **Immutability removes the second**: if the data\ncannot change, any number of names can share it and none of them can surprise\nanother. You have relied on this already without noticing — strings are\nimmutable, which is why nobody worries about two variables holding the same\nstring."
        },
        {
          "p": "JavaScript gives you three ways to get there with objects:"
        },
        {
          "list": [
            "**Freeze it** — `Object.freeze` makes an object refuse changes.",
            "**Never change it** — treat data as read-only and produce a new value whenever something should be different. This is by far the most common.",
            "**Copy at the boundary** — a *defensive copy*: hand out or take in a copy, so whatever the other side does, it cannot reach yours."
          ]
        },
        {
          "h": "What `Object.freeze` actually promises"
        },
        {
          "code": "const config = Object.freeze({ retries: 3, limits: { max: 10 } });\n\nconfig.retries = 5;      // refused — silently in a script, TypeError in a module\nconfig.extra = true;     // refused — no new properties either\nconfig.limits.max = 99;  // works. freeze is one level deep.",
          "lang": "js"
        },
        {
          "p": "Three things to carry."
        },
        {
          "list": [
            "**`freeze` is one level deep**, like spread. `Object.isFrozen(config)` is `true` while `Object.isFrozen(config.limits)` is `false`. Freezing every level means walking the object and freezing each part.",
            "**How it refuses depends on the mode.** In a module or under `\"use strict\"`, a plain assignment to a frozen object throws a TypeError. In a plain script it does nothing and reports nothing. Silent is the dangerous one. (Methods that change an array — `push`, `sort` — throw on a frozen array in either mode.)",
            "**`const` and `freeze` protect different things.** They are often confused because both sound like \"cannot change\"."
          ]
        },
        {
          "table": {
            "head": [
              "",
              "Stops the name pointing elsewhere",
              "Stops the object's properties changing"
            ],
            "rows": [
              [
                "`const`",
                "yes",
                "no"
              ],
              [
                "`Object.freeze`",
                "no",
                "yes, one level"
              ]
            ]
          }
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "let s = Object.freeze({ v: 1 });   s = { v: 2 };   →  s.v",
                "accept": [
                  "2"
                ],
                "miss": "**If you answered `1` — `freeze` protects the object, not the name.** `let` lets `s` be pointed at a different object, and that is what happened.\n",
                "why": "`2`. The frozen object was never changed; `s` was rebound to a new one.\n"
              },
              {
                "code": "const s = Object.freeze({ list: [1] });   s.list.push(2);   →  s.list.length",
                "accept": [
                  "2"
                ],
                "why": "`2`. `s.list` cannot be pointed at another array, but the array it points at was never frozen.\n"
              }
            ]
          }
        },
        {
          "h": "Producing new values instead of changing old ones"
        },
        {
          "p": "The more common approach is not to freeze at all, but to **never mutate**:\nwhen something should be different, build a new value with the difference in\nit and leave the old one alone. This is sometimes called *copy-on-write*."
        },
        {
          "p": "Two examples. The first labels the four subgoals; the second works through\nthe same ones unlabelled."
        },
        {
          "code": "const config = { retries: 3, timeout: 1000 };\n\n// find what changes\n//   retries, at the top level.\n// copy each level on the way to it\n//   just the outer object.\n// reuse everything else\n//   the spread brings timeout across unchanged.\nconst next = { ...config, retries: 5 };\n\n// leave the old value alone\n//   config.retries is still 3. next.retries is 5.",
          "lang": "js",
          "file": "config.js"
        },
        {
          "code": "const profile = { name: \"ada\", tags: [\"x\"], address: { city: \"Walla Walla\" } };\n\n// The tags array gets a new tag, one level down, so the outer object and\n// the tags array are copied. address is not changing, so the new profile\n// shares it — nobody will write to it.\nconst updated = { ...profile, tags: [...profile.tags, \"y\"] };\n\n// profile.tags is still [\"x\"]. updated.tags is [\"x\", \"y\"].\n// updated.address === profile.address — shared, and safe to share.",
          "lang": "js",
          "file": "profile.js"
        },
        {
          "p": "The second shows what makes this cheap: **you copy only the levels on the path\nto the change**, and share the rest. Sharing is safe precisely because nothing\nis ever mutated. It also gives you something for free: if `next !== config`,\nsomething changed; if a function returns the same object, nothing did. One\n`===` answers \"did anything change?\" without comparing every field."
        },
        {
          "p": "Now one with the middle left to you."
        },
        {
          "code": "const board = { title: \"Week 3\", todo: [\"read\"], done: [\"setup\"] };\n\n// find what changes\n//   \"read\" moves: a new todo list without it, a new done list with it.\n// copy each level on the way to it          ______\n// reuse everything else                     ______\n// leave the old value alone\n//   nothing is written to board itself.",
          "lang": "js",
          "file": "board.js"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In `board.js`, which properties does the new board share with the old one? Type the property name, or names.",
                "accept": [
                  "title",
                  "just title",
                  "only title",
                  "the title"
                ],
                "miss": "**If you listed `todo` or `done` — both change, so both need new arrays.** Only what is not changing can be shared.\n",
                "why": "Only `title`. The outer object is new, and `todo` and `done` each get a new array — for example `{ ...board, todo: [], done: [...board.done, \"read\"] }`.\n"
              },
              {
                "code": "const a = { n: 1 };   const b = { ...a, n: 1 };   →  a === b",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. The spread built a new object even though nothing differs, so `===` says \"changed\". Produce a new value only when something is different, or the cheap check stops meaning anything.\n"
              }
            ]
          }
        },
        {
          "h": "What it costs"
        },
        {
          "p": "Immutability is a choice, and every form of it has a price. Naming the price is\nhalf of defending the choice."
        },
        {
          "table": {
            "head": [
              "Approach",
              "What it buys",
              "What it costs"
            ],
            "rows": [
              [
                "`Object.freeze`",
                "a change that should never happen is refused",
                "one level only; silent in a script; must be applied to every level you want protected"
              ],
              [
                "never mutate",
                "sharing is always safe; `===` detects change",
                "a new object on every change; nested updates take more code; one line that mutates breaks the guarantee, and nothing warns you"
              ],
              [
                "defensive copies",
                "the other side cannot reach yours, whatever it does",
                "a copy on every hand-off — every call, not every change — and every caller has to remember"
              ]
            ]
          }
        },
        {
          "p": "On small data the allocation does not matter. On a list of a hundred thousand\nitems updated on every keystroke, it can. And \"never mutate\" is a promise the\nlanguage does not check: it holds because every line of code keeps it."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "You pass a 10,000-item array to a helper you do not trust, and you do it on every keystroke. Which approach costs a full copy each time?",
                "choices": [
                  "Freeze",
                  "Defensive copy",
                  "Never mutate"
                ],
                "accept": [
                  "Defensive copy"
                ],
                "why": "A defensive copy. It protects you whatever the helper does, and it pays for a new 10,000-item array on every call.\n"
              },
              {
                "question": "A team agrees to never mutate shared data. What stops a new colleague's `push()` breaking that?",
                "choices": [
                  "The language",
                  "Nothing but review"
                ],
                "accept": [
                  "Nothing but review"
                ],
                "why": "Nothing but review. JavaScript does not check the convention; `freeze` is the only part the language enforces.\n"
              }
            ]
          }
        },
        {
          "h": "The other way out: values that are copied"
        },
        {
          "p": "Everything above works around a fact about JavaScript: **every object you make\nis held by reference**. Many languages let the author of a type choose\notherwise."
        },
        {
          "p": "A **struct** — the usual name for one — is a type whose values are\n**copied on assignment**, the way numbers are. Declare a `Point` as a struct,\nand `q = p` makes a second `Point`; changing `q` cannot touch `p`. That is a\n**value type**."
        },
        {
          "p": "Why a language would offer one:"
        },
        {
          "list": [
            "**Small data that is identified by what it holds** — a point, an amount of money, a colour — has no use for being shared. Two `(1, 2)` points are simply the same point.",
            "**The decision is made once, by whoever writes the type**, and every assignment after that obeys it. In JavaScript the same decision is made at every call site, by whoever remembers to copy."
          ]
        },
        {
          "p": "A value type and an immutable object make aliasing harmless by **opposite\nroutes**. An immutable object is **one thing, shared, that cannot change**. A\nvalue type is **many copies, each of which can change** without affecting the\nothers — a struct can be perfectly mutable. The first removes the change; the\nsecond removes the sharing."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In JavaScript, can you declare your own type whose values are copied on assignment instead of shared?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. Every object you make is held by reference, always. The primitives — numbers, strings, booleans — are the only values that are copied, and you cannot add to them.\n"
              },
              {
                "question": "An immutable object and a value type both stop aliasing bugs. Which one removes the sharing?",
                "choices": [
                  "Immutable object",
                  "Value type"
                ],
                "accept": [
                  "Value type"
                ],
                "miss": "**If you answered immutable object — it is still shared.** Every name points at the one object; it just cannot be changed.\n",
                "why": "The value type. Each assignment makes a copy, so there is never a second name for one value. An immutable object is shared and unchangeable.\n"
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **can you declare that a type is copied rather than shared?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Declare a value type",
                  "Make an object unchangeable",
                  "Update by producing a new value"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "**no**",
                    "`Object.freeze`, one level",
                    "`{ ...obj, n: 2 }`"
                  ],
                  [
                    "**C#**",
                    "yes — `struct`, `record struct`",
                    "`readonly` fields, `init`-only properties",
                    "`obj with { N = 2 }`"
                  ]
                ]
              }
            },
            {
              "p": "In C# \"is this copied or shared?\" is a fact about the type, decided once by its\nauthor. In JavaScript it is decided at every call site by whoever remembers to\ncopy — the same correctness property, relocated from the language to your\ndiscipline."
            },
            {
              "p": "That is worth knowing even if your language has no struct. It is why\nJavaScript code leans so hard on conventions — never mutate, copy at the\nboundary — that a language with value types gets from a declaration."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "code": "Object.freeze(obj);                    // one level; silent in a script\nconst next = { ...state, count: 1 };   // update by producing a new value\nconst added = [...list, item];         // instead of list.push(item)\nconst out = [...mine];                 // a defensive copy, handed out",
                "lang": "js"
              },
              {
                "p": "`Object.freeze` is available and shallow. Most codebases use the convention —\nnever mutate, produce new values — rather than freezing, because freezing every\nobject costs more than it catches."
              }
            ],
            "python": [
              {
                "p": "Python gives you immutable types outright — tuples and frozensets — which\nJavaScript does not. A tuple of numbers and strings can be shared with no risk\nat all. Like `Object.freeze`, it is one level: a list inside a tuple can still\nchange."
              }
            ],
            "csharp": [
              {
                "p": "The type decides. A `struct` copies on assignment; a `class` aliases; a `record`\ngives you value-based equality and `with` for non-destructive updates."
              },
              {
                "code": "var next = current with { Retries = 5 };   // new record, original untouched",
                "lang": "csharp"
              },
              {
                "p": "`with` is the language-level version of `{ ...obj, retries: 5 }`, and the fact\nthat it is built in says how central the idea has become."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the second, say what you\nwould have to do so that `o.inner.b = 9` is refused too — and for the fourth,\nsay what JavaScript code would need to do to get the same result with an\nobject."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A cart component keeps `items` in state and passes it to a `total()` helper\nsomeone else wrote. A bug report says items occasionally vanish from the cart."
            },
            {
              "p": "Three designs that would prevent it:"
            },
            {
              "list": [
                "**Pass a copy** — `total([...items])`, at every call site.",
                "**Freeze the state** — so an unexpected change throws instead of succeeding.",
                "**Never mutate, and say so** — the team's rule is that no function changes data it was handed, and `total` is reviewed against it."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what the first does on a large\ncart and how many call sites have to remember, what the second does in a plain\nscript versus a module — and whether it protects the items themselves or only\nthe array — and what the third relies on. All three appear in real code; naming\nthe cost is the answer."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Aliasing bugs need **sharing and a change**. Immutability removes the change, so sharing becomes safe.",
                "**`Object.freeze` is one level**, and refuses *silently* outside strict mode. `const` protects the name; `freeze` protects the object's own properties.",
                "The usual approach is to **never mutate**: produce a new value, copying only the levels on the path to the change. It costs an allocation per change and a promise nothing enforces.",
                "A **value type** (a struct) is copied on assignment, so it is never shared. JavaScript has none; it makes that decision per call site instead."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Object.freeze()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze",
              "note": "the \"shallow freeze\" section, and what happens in strict mode versus a script."
            },
            {
              "title": "Spread syntax",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax",
              "note": "\"Spread in object literals\": how `{ ...obj, n: 2 }` builds the new value, the later property winning."
            },
            {
              "title": "Mutable",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Mutable",
              "note": "MDN's short glossary entry on mutable and immutable values."
            }
          ]
        }
      ],
      "_source": "lessons/immutability.md",
      "practice": {
        "id": "immutability",
        "outcome": 2,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict what stayed unchanged",
            "prompt": "Four quick ones. Type the final value, or pick one of the options.\n",
            "items": [
              {
                "id": "p1",
                "code": "const o = Object.freeze({ a: 1, inner: { b: 2 } });   o.inner.b = 9;   →  o.inner.b",
                "accept": [
                  "9"
                ],
                "why": "`freeze` is one level. It froze `o`'s own properties, not the object `o.inner` points at.\n"
              },
              {
                "id": "p2",
                "code": "const o = Object.freeze({ a: 1 });   o.a = 5;   →  o.a   (run as a plain script)",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered `error` — in a module it would be.** In a plain script a write to a frozen object is refused silently.\n",
                "why": "`1`. The write was refused, and in a plain script the refusal says nothing.\n"
              },
              {
                "id": "p3",
                "question": "You want callers to be unable to change `config`'s own properties. Which is the first step?",
                "choices": [
                  "const",
                  "Object.freeze"
                ],
                "accept": [
                  "Object.freeze"
                ],
                "miss": "**If you answered `const` — `const` protects the name.** Callers who get the object can still change every property.\n",
                "why": "`Object.freeze`. It is the one that stops the object's properties changing; `const` only stops the name being pointed elsewhere.\n"
              },
              {
                "id": "p4",
                "question": "In C#, a struct's values are ___ on assignment.",
                "choices": [
                  "copied",
                  "shared"
                ],
                "accept": [
                  "copied"
                ],
                "why": "Copied — that is what makes it a value type. `q = p` makes a second value, the way it does for a number.\n"
              }
            ],
            "extra": [
              {
                "id": "p5",
                "code": "const p = { n: 1 };   const q = { ...p, n: 2 };   →  p.n",
                "accept": [
                  "1"
                ],
                "why": "`1`. The spread built a new object with `n` replaced; `p` was never written to.\n"
              },
              {
                "id": "p6",
                "code": "const s = [1, 2];   s.push(3);   →  s.length   (s is const, not frozen)",
                "accept": [
                  "3"
                ],
                "why": "`3`. `const` stops `s` being pointed at another array. It does not stop the array changing.\n"
              },
              {
                "id": "p7",
                "code": "let s = Object.freeze({ v: 1 });   s = { v: 2 };   →  s.v",
                "accept": [
                  "2"
                ],
                "why": "`2`. `freeze` protects the object, not the name; `let` let the name move to a new object.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 2 for the first",
                "say": "Ask what `freeze` was given: the outer object. Is `inner` a property holding a number, or a property holding an arrow to another object?\n"
              },
              {
                "when": "you answered 5 for the second",
                "say": "The object is frozen. The only question is how it refuses — loudly or silently — and the value does not change either way.\n"
              },
              {
                "when": "you chose const",
                "say": "Say what `const` stops: pointing the name at a different object. Does that stop anyone changing the object's properties?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "What freeze promises, and what a spread of it is",
            "prompt": "Predict each line the program prints, then run it. The site runs code as a plain script, not a module — that matters for one of these.\n",
            "filename": "settings.js",
            "code": "const settings = Object.freeze({ volume: 5, keys: { jump: \"space\" } });\n\nsettings.volume = 11;\nsettings.keys.jump = \"w\";\n\nconsole.log(settings.volume);\nconsole.log(settings.keys.jump);\nconsole.log(Object.isFrozen(settings.keys));\n\nconst louder = { ...settings, volume: 11 };\nconsole.log(louder.volume, settings.volume);\nconsole.log(louder.keys === settings.keys);\nconsole.log(Object.isFrozen(louder));\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(settings.volume)",
                "accept": [
                  "5"
                ],
                "why": "`settings` is frozen, so `settings.volume = 11` was refused — and in a plain script, refused *silently*. In a module it would have thrown a TypeError. Silent is the dangerous one.\n"
              },
              {
                "id": "r2",
                "code": "console.log(settings.keys.jump)",
                "accept": [
                  "w",
                  "\"w\"",
                  "'w'"
                ],
                "why": "`freeze` is one level deep. It froze `settings`'s own properties; the object `keys` points at was never frozen, so the write went through.\n"
              },
              {
                "id": "r3",
                "code": "console.log(Object.isFrozen(settings.keys))",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "The proof of the last one: the outer object is frozen, the inner one is not.\n"
              },
              {
                "id": "r4",
                "code": "console.log(louder.volume, settings.volume)",
                "accept": [
                  "11 5",
                  "11, 5",
                  "11,5"
                ],
                "why": "`11 5`. The spread built a new object with the volume replaced — the way to get a changed value out of a frozen one — and left `settings` alone.\n"
              },
              {
                "id": "r5",
                "code": "console.log(louder.keys === settings.keys)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. The spread is one level, so both share `keys`. That is only safe if nobody writes to it — and line 4 already did.\n"
              },
              {
                "id": "r6",
                "code": "console.log(Object.isFrozen(louder))",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` — a spread copies properties, not the frozenness.** `louder` is a brand-new, ordinary object.\n",
                "why": "`false`. Freezing is a property of one object. The new one would have to be frozen too.\n"
              }
            ],
            "hints": [
              {
                "when": "you predicted 11 for the first line",
                "say": "`Object.freeze` makes the object's own properties unwritable. What happens to a write it refuses depends on strict mode — but the value does not change either way.\n"
              },
              {
                "when": "you predicted \"space\" for the second line",
                "say": "Ask what `freeze` was given: the outer object. Is `keys` a property holding a string, or a property holding an arrow to another object?\n"
              },
              {
                "when": "you predicted louder would be frozen",
                "say": "A spread builds a new object and copies properties into it. Is being frozen one of the properties?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace what is new and what is shared",
            "prompt": "A copy-on-write update, one line at a time. The last two items ask what a language could do instead.\n",
            "items": [
              {
                "id": "t1",
                "code": "1  const a = { n: 1, tags: [\"x\"], meta: { by: \"ada\" } };\n2  const b = { ...a, n: 2 };\n3  const c = { ...b, tags: [...b.tags, \"y\"] };\n\nafter line 3  →  a.n\n",
                "accept": [
                  "1"
                ],
                "why": "`1`. Neither update wrote to `a`; each built a new object.\n"
              },
              {
                "id": "t2",
                "question": "Same program: what is `b.tags === a.tags`?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. Line 2 changed only `n`, so `tags` was carried across as the same array — shared, and safe while nobody mutates it.\n"
              },
              {
                "id": "t3",
                "question": "Same program: what is `c.tags === a.tags`?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. Line 3 built a new tags array, because tags was the thing changing.\n"
              },
              {
                "id": "t4",
                "question": "Same program: what is `c.meta === a.meta`?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. `meta` never changed, so every version shares it. Copy the path to the change; share the rest.\n"
              },
              {
                "id": "t5",
                "question": "In C#, `Point` is declared as a struct — a value type. After `var p = new Point { X = 1 }; var q = p; q.X = 9;`, what is `p.X`?",
                "accept": [
                  "1"
                ],
                "why": "A value type is copied on assignment, so `q` is a separate `Point`. In a language that has them, \"copied or shared?\" is a fact about the type, decided once by whoever wrote it.\n"
              },
              {
                "id": "t6",
                "question": "In JavaScript, can you declare your own type whose values are copied on assignment instead of shared?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. Every object you make is held by reference, always. Value-like behaviour has to be produced at each call site — by copying, or by never mutating.\n"
              }
            ],
            "extra": [
              {
                "id": "t7",
                "code": "const s = { list: [1] };   const t = { ...s, list: [...s.list, 2] };   →  s.list.length",
                "accept": [
                  "1"
                ],
                "why": "`1`. `t.list` is a new array with `2` added; `s.list` was never pushed to.\n"
              },
              {
                "id": "t8",
                "question": "An immutable object and a value type both stop aliasing bugs. Which one is still shared between names?",
                "choices": [
                  "Immutable object",
                  "Value type"
                ],
                "accept": [
                  "Immutable object"
                ],
                "why": "The immutable object: one object, many names, none of which can change it. A value type is copied, so it is never shared.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered 2 for a.n",
                "say": "Line 2 has `b` on the left, and a spread on the right. Which object did `n: 2` go into?\n"
              },
              {
                "when": "you answered false for b.tags === a.tags",
                "say": "What did line 2 change? A spread copies each property's value, and `tags` holds an arrow — so what does `b.tags` point at?\n"
              },
              {
                "when": "you answered 9 for the struct",
                "say": "That is JavaScript's rule, and the question is about a language where the type can opt out of it. A value type's assignment copies.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Update without mutating",
            "prompt": "`addTag` works — the returned profile has the new tag. But it changes the caller's profile too, and the history screen that keeps old profiles now shows every tag on every version. Change it so it returns a new profile and leaves the original completely untouched, including its array. Then, in one sentence: why is `{ ...profile }` alone not enough?\n",
            "exportName": "addTag",
            "filename": "profile.js",
            "starter": "// Returns the profile with tag appended to tags.\nfunction addTag(profile, tag) {\n  profile.tags.push(tag);\n  return profile;\n}\n",
            "tests": [
              {
                "label": "the new profile has the tag",
                "body": "const p = { name: 'ada', tags: ['x'] }; return subject(p, 'y').tags.join(',') === 'x,y';"
              },
              {
                "label": "other fields survive",
                "body": "const p = { name: 'ada', tags: [] }; return subject(p, 'y').name === 'ada';"
              },
              {
                "label": "the original profile is untouched",
                "body": "const p = { name: 'ada', tags: ['x'] }; subject(p, 'y'); return p.tags.join(',') === 'x';"
              },
              {
                "label": "returns a different object",
                "body": "const p = { name: 'ada', tags: [] }; return subject(p, 'y') !== p;"
              },
              {
                "label": "the tags array is a different array, not the same one",
                "body": "const p = { name: 'ada', tags: ['x'] }; return subject(p, 'y').tags !== p.tags;"
              },
              {
                "label": "works when tags is empty",
                "body": "const p = { name: 'ada', tags: [] }; return subject(p, 'y').tags.join(',') === 'y';"
              }
            ],
            "hints": [
              {
                "when": "the original profile changed even though you spread it first",
                "say": "You spread the profile and then pushed. The spread copied the *reference* to `tags`, so your push landed in the caller's array. Build a new array instead of adding to the old one.\n"
              },
              {
                "when": "you wrote `profile.tags = [...]` and the original still changed",
                "say": "That builds a new array and then writes it into the caller's object — still a change to the profile you were handed. Build a new profile around the new array.\n"
              },
              {
                "when": "you are reaching for structuredClone",
                "say": "It would work, and it is more than you need — it recreates every level when only one of them has to change. Consider what `[...tags, tag]` gives you.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review an \"immutable\" settings module",
            "prompt": "An AI assistant wrote these helpers for a game's settings. Its comments say nothing is ever modified. After a player rebinds a key, every new game starts with that key rebound. Read it, answer the questions, then fix it so the tests pass.\n",
            "filename": "settings.js",
            "code": "// Frozen, so nothing can change the defaults.\nconst DEFAULTS = Object.freeze({ volume: 5, keys: { jump: \"space\" } });\n\n// Returns new settings with the volume changed. Never modifies `settings`.\nfunction setVolume(settings, volume) {\n  const next = settings;\n  next.volume = volume;\n  return next;\n}\n\n// Returns new settings with one key rebound. Never modifies `settings`.\nfunction rebind(settings, action, key) {\n  const next = { ...settings };\n  next.keys[action] = key;\n  return next;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line should have made a copy in `setVolume`, and did not? Type the line number.",
                "accept": [
                  "6",
                  "line 6"
                ],
                "why": "Line 6. `next = settings` gives the same object a second name; line 7 then writes into the caller's settings.\n"
              },
              {
                "id": "c2",
                "code": "const s = { volume: 5, keys: {} };   setVolume(s, 8) === s",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. The \"new\" settings are the old ones. A caller checking `===` to see whether anything changed would be told nothing did.\n"
              },
              {
                "id": "c3",
                "code": "rebind(DEFAULTS, \"jump\", \"w\");   →  DEFAULTS.keys.jump",
                "accept": [
                  "\"w\"",
                  "w",
                  "'w'"
                ],
                "why": "`\"w\"`. The spread on line 13 is one level, so `next.keys` is the defaults' own `keys` object — and `freeze` never reached it.\n"
              },
              {
                "id": "c4",
                "question": "Which line has a comment that promises more than the code delivers — about the defaults? Type the line number.",
                "accept": [
                  "1",
                  "line 1"
                ],
                "why": "Line 1. `freeze` is one level: the defaults' `volume` is protected, their `keys` are not.\n"
              }
            ],
            "exportName": "audio",
            "starter": "// Frozen, so nothing can change the defaults.\nconst DEFAULTS = Object.freeze({ volume: 5, keys: { jump: \"space\" } });\n\n// Returns new settings with the volume changed. Never modifies `settings`.\nfunction setVolume(settings, volume) {\n  const next = settings;\n  next.volume = volume;\n  return next;\n}\n\n// Returns new settings with one key rebound. Never modifies `settings`.\nfunction rebind(settings, action, key) {\n  const next = { ...settings };\n  next.keys[action] = key;\n  return next;\n}\n\nconst audio = { DEFAULTS, setVolume, rebind };\n",
            "tests": [
              {
                "label": "setVolume returns the new volume",
                "body": "const s = { volume: 5, keys: { jump: 'space' } }; return subject.setVolume(s, 8).volume === 8;"
              },
              {
                "label": "setVolume leaves the settings it was given alone",
                "body": "const s = { volume: 5, keys: { jump: 'space' } }; subject.setVolume(s, 8); return s.volume === 5;"
              },
              {
                "label": "setVolume returns a different object",
                "body": "const s = { volume: 5, keys: { jump: 'space' } }; return subject.setVolume(s, 8) !== s;"
              },
              {
                "label": "rebind returns the new key",
                "body": "const s = { volume: 5, keys: { jump: 'space' } }; return subject.rebind(s, 'jump', 'w').keys.jump === 'w';"
              },
              {
                "label": "rebind leaves the settings it was given alone",
                "body": "const s = { volume: 5, keys: { jump: 'space' } }; subject.rebind(s, 'jump', 'w'); return s.keys.jump === 'space';"
              },
              {
                "label": "rebinding from the defaults leaves the defaults alone",
                "body": "subject.rebind(subject.DEFAULTS, 'jump', 'w'); return subject.DEFAULTS.keys.jump === 'space';"
              },
              {
                "label": "rebind keeps the other settings",
                "body": "const s = { volume: 3, keys: { jump: 'space', duck: 'c' } }; const r = subject.rebind(s, 'jump', 'w'); return r.volume === 3 && r.keys.duck === 'c';"
              }
            ],
            "hints": [
              {
                "when": "setVolume still changes the settings it was given",
                "say": "`const next = settings` is a second name, not a copy. Build a new object with the volume replaced.\n"
              },
              {
                "when": "setVolume is fixed but rebind still changes the defaults",
                "say": "`rebind`'s copy is one level, and the key lives one level down, in `keys`. The new settings need their own `keys` object as well.\n"
              },
              {
                "when": "you froze keys too and the rebound key does not change",
                "say": "Now the defaults are safe and `rebind` writes into a frozen object, which a plain script refuses silently. Freezing guards the defaults; `rebind` still has to build its own `keys`.\n"
              }
            ]
          }
        ]
      }
    },
    "choosing-a-collection": {
      "id": "choosing-a-collection",
      "title": "Choosing a collection",
      "kind": "read",
      "shape": "conceptual",
      "why": "PL1 owns \"which structure and why; equality and hashing\" for collections (`program/programming-sequence.md`), and the concept never reached the inventory until D-014 added `data.collections`. It is where identity versus equivalence stops being a quiz answer: a set of equal-looking objects keeps every one of them, and a plain object used as a map merges keys that differ.",
      "concepts": [
        "data.collections"
      ],
      "lede": "A list, a map and a set can all hold the same data. What differs is the question each one answers — and, for a map or a set, what it takes for two keys to count as the same key. That second part is where identity comes back.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Equality: == versus === · Copying versus aliasing"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Key** — the thing you look a value up by, or the thing a set holds. **Membership** — whether something is in a collection, and nothing else about it.",
          "entries": [
            "**Key** — the thing you look a value up by, or the thing a set holds.",
            "**Membership** — whether something is in a collection, and nothing else about it."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four short programs. Type what the last expression produces. A guess is fine;\nthat is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "new Set([3, 1, 3, 2, 1]).size",
                          "accept": [
                            "3"
                          ],
                          "why": "A set keeps one of each. Three different numbers went in, some of them twice, so it holds three. `size` is how a set says how many — not `length`.\n"
                        },
                        {
                          "code": "new Set([{ id: 1 }, { id: 1 }]).size",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1` because the two objects are identical — they look identical, and they are two objects.** Each `{ }` builds a new one. A set decides \"already in here?\" by asking whether it is *the same object*, not whether it holds the same things.\n",
                          "why": "Two objects built separately are two different keys, however alike they look. The set removed nothing.\n"
                        },
                        {
                          "code": "const o = {};   o[\"1\"] = \"text\";   o[1] = \"number\";   o[\"1\"]",
                          "accept": [
                            "\"number\"",
                            "number",
                            "'number'"
                          ],
                          "miss": "**If you answered `\"text\"` because `1` and `\"1\"` are different values — they are, and a plain object cannot tell.** It turns every key into text before storing it, so `o[1]` wrote over the entry that `o[\"1\"]` made.\n",
                          "why": "One key, `\"1\"`, written twice. The second write replaced the first.\n"
                        },
                        {
                          "code": "const m = new Map();   m.set([7], \"x\");   m.get([7])",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you answered `\"x\"` — the `[7]` you asked with is a new array.** It holds the same thing as the one you stored, but it is a second object, and so a second key that was never stored.\n",
                          "why": "`m.get` asked for a key that is not in the map. Nothing went wrong; the array used as the key and the array used to ask are two arrays.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can choose between a list, a map and a set for a job, and say when two keys count as the same key"
              ]
            }
          ]
        },
        {
          "h": "Three questions a collection can answer"
        },
        {
          "p": "Before any code, name the three shapes. Each is built to answer one question\nquickly."
        },
        {
          "list": [
            "A **list** answers *what is at position 3, and in what order did these arrive?* It keeps order, and it keeps duplicates.",
            "A **map** answers *given this key, what goes with it?* Each key appears once and has one value — a price for each product code, a grade for each student.",
            "A **set** answers *is this in here?* Each thing appears once, and there is nothing attached to it."
          ]
        },
        {
          "p": "All three can *store* the same things. The choice is about the question you\nwill ask most often. Asking a list \"is this in here?\" works, but it means\nlooking along the list; a map or a set answers it without looking at every\nitem. Asking a set \"what came third?\" does not work at all — it was never\nbuilt to remember that."
        },
        {
          "tracks": {
            "js": [
              {
                "table": {
                  "head": [
                    "",
                    "List",
                    "Map",
                    "Set"
                  ],
                  "rows": [
                    [
                      "Build",
                      "`[]`",
                      "`new Map()`",
                      "`new Set()`"
                    ],
                    [
                      "Add",
                      "`a.push(x)`",
                      "`m.set(key, value)`",
                      "`s.add(x)`"
                    ],
                    [
                      "Ask",
                      "`a[2]` · `a.includes(x)`",
                      "`m.get(key)` · `m.has(key)`",
                      "`s.has(x)`"
                    ],
                    [
                      "How many",
                      "`a.length`",
                      "`m.size`",
                      "`s.size`"
                    ]
                  ]
                }
              },
              {
                "p": "`size`, not `length`, for a map or a set. `m.get` of a key that is not there\ngives `undefined`, not an error — the same quiet `undefined` as a function that\nnever returned."
              }
            ]
          }
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "A quiz app records every answer a student gives, in order, so it can replay them. List, map or set?",
                "choices": [
                  "List",
                  "Map",
                  "Set"
                ],
                "accept": [
                  "List"
                ],
                "why": "Order matters, and the same answer given twice is two answers. Only a list keeps both.\n"
              },
              {
                "question": "A library needs the due date of a book, given its barcode. List, map or set?",
                "choices": [
                  "List",
                  "Map",
                  "Set"
                ],
                "accept": [
                  "Map"
                ],
                "miss": "**If you answered `set` — a set can tell you the barcode is there, and nothing about it.** The question has something attached: a due date.\n",
                "why": "Given a key (the barcode), what goes with it (the due date)? That is the map's question.\n"
              },
              {
                "question": "A form must refuse a username that is already taken. List, map or set?",
                "choices": [
                  "List",
                  "Map",
                  "Set"
                ],
                "accept": [
                  "Set"
                ],
                "miss": "**If you answered `list` — it works, and it asks the wrong way.** A list has to be looked along to answer \"is this taken?\", and nothing stops it holding the same name twice.\n",
                "why": "Membership, and nothing attached to it: is this name in here? That is exactly what a set answers.\n"
              }
            ]
          }
        },
        {
          "h": "Two worked choices"
        },
        {
          "p": "Same four steps both times. The four comments in the first are the steps to keep."
        },
        {
          "code": "// \"Each student may vote once. Refuse a second vote.\"\n\n// name the question you will ask most\n//   \"has this student already voted?\"  -> membership\n\n// pick the collection that answers it\n//   membership, nothing attached       -> a set\nconst voted = new Set();\n\n// decide what makes two keys the same\n//   the student ID, a string. Strings compare by value,\n//   so \"s-104\" and \"s-104\" are one key wherever they came from.\n\n// build it, then ask\nvoted.add(\"s-104\");\nvoted.has(\"s-104\");   // true\nvoted.has(\"s-221\");   // false",
          "lang": "js",
          "file": "poll.js"
        },
        {
          "code": "// \"Show how many of each product are left, by product code.\"\n\n// asked most: \"given this code, how many are left?\"  -> a lookup\n// a key with a value attached                         -> a map\nconst stock = new Map();\n\n// the key: the product code, a string -> compared by value\nstock.set(\"HAT-RED\", 4);\nstock.set(\"SCARF\", 0);\nstock.get(\"HAT-RED\");  // 4\nstock.set(\"HAT-RED\", 3);   // same key: the value is replaced, not added\nstock.size;            // 2",
          "lang": "js",
          "file": "stock.js"
        },
        {
          "p": "The third step looked like a formality both times, because both keys were\nstrings. It is the step that goes wrong when the key is an object."
        },
        {
          "h": "What makes two keys the same"
        },
        {
          "p": "A map or a set has to decide, every time you add or ask, whether this key is one\nit already has. **It decides the way `===` does.** Two numbers or two strings\nwith the same value are the same key. Two objects are the same key only if they\nare the same object — one thing, reached by two names."
        },
        {
          "p": "That is the identity question from *Equality: == versus ===*, and here is\nwhere it pays off. Same code, one line different:"
        },
        {
          "code": "const p = { x: 1, y: 2 };\nconst one = new Set([p, p]);\none.size;    // 1 — one object, added twice through the same name\n\nconst two = new Set([{ x: 1, y: 2 }, { x: 1, y: 2 }]);\ntwo.size;    // 2 — two objects that look alike",
          "lang": "js",
          "file": "same-or-equal.js"
        },
        {
          "p": "**The second set removed nothing.** It was never asked \"do these hold the same\nthings?\" — that is equivalence, and JavaScript has no operator for it, so a set\ncannot use one. The first set holds one object because `p, p` is one object\ntwice, just as `b = a` gave one array two names."
        },
        {
          "p": "One exception you met in the equality lesson, handled the helpful way: `NaN` is\nnot `===` to itself, but a set or map treats `NaN` as the same key as `NaN`."
        },
        {
          "block": "notice",
          "label": "When \"the same\" means equal-looking",
          "body": [
            {
              "p": "If two separately built objects should count as one — two seat objects for seat\nB4, two product objects with the same code — **do not use the objects as keys.\nBuild a key from the part that decides sameness**: the ID, the code, the row and\nnumber. A string or a number compares by value, so equal things produce equal\nkeys. You are writing equivalence down as a key, because the collection will not\nwork it out for you."
            }
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const s = new Set();   s.add({ code: \"A1\" });   s.has({ code: \"A1\" })",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "The object you asked with is not the object you added. Identity says no.\n"
              },
              {
                "code": "const s = new Set();   s.add(\"A1\");   s.has(\"A1\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "The same question with the code itself as the key. Strings compare by value, so this is the fix for the line above.\n"
              },
              {
                "code": "const s = new Set();   const item = { code: \"A1\" };   s.add(item);   s.has(item)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "One object, asked about through the same name it was added with. Identity says yes.\n"
              }
            ]
          }
        },
        {
          "h": "A plain object is not a map"
        },
        {
          "p": "You will see plain objects used as maps — `counts[word] = counts[word] + 1` — in\na great deal of existing code. For keys that are always text, that works. It\nbreaks on anything else, and it breaks in the opposite direction from a set of\nobjects: instead of keeping too many keys, it keeps too few."
        },
        {
          "code": "const o = {};\no[1] = \"number\";\no[\"1\"] = \"text\";          // one key, \"1\" — this replaced the line above\n\no[{ id: 1 }] = \"first\";\no[{ id: 2 }] = \"second\";\no[{ id: 1 }];             // \"second\"",
          "lang": "js",
          "file": "object-as-map.js"
        },
        {
          "code": "const m = new Map();\nm.set(1, \"number\");\nm.set(\"1\", \"text\");       // two keys, 1 and \"1\"\n\nm.set({ id: 1 }, \"first\");\nm.set({ id: 2 }, \"second\");\nm.get({ id: 1 });         // undefined — a third object, never stored",
          "lang": "js",
          "file": "map.js"
        },
        {
          "p": "A plain object turns every key into text before it stores it. `1` becomes `\"1\"`,\nand **every object becomes the same text, `\"[object Object]\"`** — so `{ id: 1 }`\nand `{ id: 2 }` land on one key and the second overwrites the first. A `Map` keeps\neach key as it is: numbers stay numbers, and objects stay distinct objects,\ncompared by identity."
        },
        {
          "table": {
            "head": [
              "",
              "Plain object",
              "`Map`"
            ],
            "rows": [
              [
                "`1` and `\"1\"`",
                "one key",
                "two keys"
              ],
              [
                "two different objects",
                "one key — both become `\"[object Object]\"`",
                "two keys"
              ],
              [
                "two equal-looking objects",
                "one key",
                "two keys"
              ]
            ]
          }
        },
        {
          "p": "A plain object is the right tool for a record whose field names you know when\nyou write the code — `{ name, email }`. When the keys come from data, reach for a\n`Map`."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const seen = {};   seen[{ id: 5 }] = true;   seen[{ id: 9 }]",
                "accept": [
                  "true"
                ],
                "miss": "**If you answered `undefined` — you were thinking of a `Map`.** A plain object turned both objects into the text `\"[object Object]\"`, so the second question asks about the key the first line stored.\n",
                "why": "Both objects became `\"[object Object]\"`. One key, and it was set to `true`.\n"
              },
              {
                "code": "const m = new Map();   m.set(2, \"two\");   m.get(\"2\")",
                "accept": [
                  "undefined"
                ],
                "why": "A `Map` keeps `2` and `\"2\"` apart. You stored a number and asked for text.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Choosing a collection from a requirement",
            "body": [
              {
                "h": "Choosing a collection from a requirement"
              },
              {
                "p": "The same four steps, with the last two left to you."
              },
              {
                "code": "// \"In a grid game, remember which cells the player has visited,\n//  so the map can shade them. Cells arrive as fresh objects: { x: 3, y: 12 }.\"\n\n// name the question you will ask most\n//   \"has the player been to this cell?\"  -> membership\n\n// pick the collection that answers it\n//   -> a set\n\n// decide what makes two keys the same\n//   the cell objects are fresh every time, so they cannot be the key.\n//   key: ______\n\n// build it, then ask\n//   visited.add(______);\n//   visited.has(______);",
                "lang": "js",
                "file": "visited.js"
              },
              {
                "p": "A key built from both coordinates with a separator: `cell.x + \",\" + cell.y`,\ngiving `\"3,12\"`. The comma is doing work. Without it, `{ x: 1, y: 12 }` and\n`{ x: 11, y: 2 }` both become `\"112\"`, and two different cells collide on one\nkey. A good key is equal for things that are the same *and* different for things\nthat are not."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis is **what makes two keys the same** — and a language that answers\n\"the contents\" has to pay for it somewhere."
            },
            {
              "table": {
                "head": [
                  "",
                  "Two equal-looking keys",
                  "A key that can change"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "two keys — objects compare by identity",
                    "allowed"
                  ],
                  [
                    "**Python**",
                    "one key — built-in values compare by contents",
                    "a list as a key is **refused**: `TypeError: unhashable type`"
                  ]
                ]
              }
            },
            {
              "p": "Python's sets and dictionaries compare numbers, strings and tuples by what they\nhold, so `(1, 2)` and a separately built `(1, 2)` are one key. The price is that\nit refuses a list, which can change: a key filed away under its contents would\nbe lost the moment those contents changed. JavaScript made the other trade.\nComparing objects by identity means any object can be a key and changing it\nnever loses it — and means equal-looking objects are never merged for you."
            }
          ]
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the second and fourth,\nsay what key you would use instead so that the equal-looking values count as\none."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A shop shows each customer their **recently viewed** products. The rule: no\nproduct appears twice. Every page visit fetches the product fresh from the\nserver as `{ sku: \"HAT-RED\", name: \"Red hat\", price: 18 }`, so viewing the same\nproduct twice produces two separate objects."
            },
            {
              "p": "Two designs:"
            },
            {
              "list": [
                "**A set of the product objects**, adding each one as it is viewed.",
                "**A map from `sku` to the product**, setting each one as it is viewed."
              ]
            },
            {
              "p": "Pick one and say why. Then say exactly what the other one does after a customer\nviews the red hat three times — how many entries, and why. Finally, name what\ncounts as \"the same product\" in the design you chose, and say where in the code\nthat decision is written down."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Choose by the question you will ask most: **position and order** → a list; **given a key, what goes with it** → a map; **is it in here** → a set.",
                "A map or a set decides \"same key?\" the way `===` does: primitives by value, **objects by identity**. Equal-looking objects are two keys.",
                "When equal-looking things should count as one, **key them by the part that decides sameness** — an ID, a code — built so different things cannot collide.",
                "A plain object turns every key into text: `1` and `\"1\"` merge, and every object becomes `\"[object Object]\"`. When keys come from data, use a `Map`."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Keyed collections",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Keyed_collections",
              "note": "`Map` and `Set` side by side, and how they compare keys."
            },
            {
              "title": "Map",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map",
              "note": "read the section \"Objects vs. Maps\"; it is the table above in more detail."
            },
            {
              "title": "Set",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set",
              "note": "`add`, `has`, `size`, and \"value equality\" for what counts as the same member."
            }
          ]
        }
      ],
      "_source": "lessons/choosing-a-collection.md",
      "practice": {
        "id": "choosing-a-collection",
        "outcome": 2,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict, then choose",
            "prompt": "Three quick ones. Type what the last expression produces, or pick the collection — list, map or set.\n",
            "items": [
              {
                "id": "p1",
                "code": "new Set([{ sku: \"A1\" }, { sku: \"A1\" }]).size",
                "accept": [
                  "2"
                ],
                "miss": "**If you answered `1` — the two objects look the same and are two objects.** A set asks whether a new member is *the same object* as one it has, not whether it holds the same things.\n",
                "why": "`2`. Each `{ }` builds a new object, and a set compares objects by identity. Nothing was removed.\n"
              },
              {
                "id": "p2",
                "question": "A sign-up form must refuse an email address that has already been used. First step: which collection answers that question?",
                "choices": [
                  "List",
                  "Map",
                  "Set"
                ],
                "accept": [
                  "Set"
                ],
                "miss": "**If you answered `list` — it works, and it answers the question the long way,** looking along every item, and nothing stops the same address going in twice. If you answered `map` — there is nothing to look up; the question is only \"is it in here?\"\n",
                "why": "A set. The question is membership with nothing attached to it — the one question a set is built for.\n"
              },
              {
                "id": "p3",
                "code": "const o = {};   o[1] = \"one\";   o[\"1\"] = \"uno\";   o[1]",
                "accept": [
                  "\"uno\"",
                  "uno",
                  "'uno'"
                ],
                "miss": "**If you answered `\"one\"` — `1` and `\"1\"` are different values, and a plain object cannot tell.** It turns every key into text, so both lines wrote to the key `\"1\"`.\n",
                "why": "`\"uno\"`. One key, written twice; the second write replaced the first.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "new Set([\"red\", \"blue\", \"red\", \"red\"]).size",
                "accept": [
                  "2"
                ],
                "why": "`2`. Strings compare by value, so the three `\"red\"`s are one member."
              },
              {
                "id": "p5",
                "code": "const m = new Map();   m.set([1, 2], \"pair\");   m.get([1, 2])",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined`. The `[1, 2]` you asked with is a new array — a second key, never stored.\n"
              },
              {
                "id": "p6",
                "question": "A shop needs the price of a product, given its product code. List, map or set?",
                "choices": [
                  "List",
                  "Map",
                  "Set"
                ],
                "accept": [
                  "Map"
                ],
                "why": "A map: given a key (the code), what goes with it (the price)?"
              }
            ],
            "hints": [
              {
                "when": "you answered 1 for the set of objects",
                "say": "Count the objects the line builds, the way you did for `===`. A set decides \"already here?\" with the identity question.\n"
              },
              {
                "when": "you answered list or map for the sign-up form",
                "say": "Say the question out loud: \"has this address been used?\" Is anything attached to the answer, and does the order matter?\n"
              },
              {
                "when": "you answered \"one\" for the plain object",
                "say": "Ask what a plain object does to a key before it stores it. Then ask what `1` becomes.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "What the set kept",
            "prompt": "Predict each line's output, then run it.",
            "filename": "keys.js",
            "code": "const tags = new Set([\"red\", \"blue\", \"red\"]);\nconsole.log(tags.size);\n\nconst points = new Set([{ x: 1 }, { x: 1 }]);\nconsole.log(points.size);\n\nconst p = { x: 1 };\nconst same = new Set([p, p]);\nconsole.log(same.size);\n\nconst labels = {};\nlabels[7] = \"seven\";\nlabels[\"7\"] = \"text seven\";\nconsole.log(labels[7]);\n\nconst homes = new Map();\nhomes.set({ x: 1 }, \"home\");\nconsole.log(homes.get({ x: 1 }));\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(tags.size)",
                "accept": [
                  "2"
                ],
                "why": "`2` — `\"red\"` twice is one member. Strings compare by value."
              },
              {
                "id": "r2",
                "code": "console.log(points.size)",
                "accept": [
                  "2"
                ],
                "miss": "**If you answered `1` — this is the line the lesson is about.** Two `{ x: 1 }`s are two objects; the set compares objects by identity.\n",
                "why": "`2`. Two objects built separately, so two members."
              },
              {
                "id": "r3",
                "code": "console.log(same.size)",
                "accept": [
                  "1"
                ],
                "why": "`1`. `p, p` is one object handed over twice. Identity says it is already there.\n"
              },
              {
                "id": "r4",
                "code": "console.log(labels[7])",
                "accept": [
                  "text seven",
                  "\"text seven\""
                ],
                "why": "`text seven`. The plain object stored both writes under the key `\"7\"`; the second replaced the first.\n"
              },
              {
                "id": "r5",
                "code": "console.log(homes.get({ x: 1 }))",
                "accept": [
                  "undefined"
                ],
                "miss": "**If you answered `home` — the `{ x: 1 }` you asked with is a third object.** It was never stored, so the map has nothing for it.\n",
                "why": "`undefined`, with no error. A missing key is a quiet `undefined` — which travels on until something tries to use it.\n"
              }
            ],
            "hints": [
              {
                "when": "points and same gave you the same answer",
                "say": "They differ in one thing: how many objects exist. Count the `{ }`s in each line.\n"
              },
              {
                "when": "you expected homes.get to find \"home\"",
                "say": "Ask whether the key you asked with is the key you stored — the same object — or one that only looks like it.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Count the keys, then choose",
            "prompt": "For the first three, type the result — count the keys as you go. For the last two, pick the collection: list, map or set.\n",
            "items": [
              {
                "id": "t1",
                "code": "const s = new Set();   s.add(\"ada\");   s.add(\"Ada\");   s.size",
                "accept": [
                  "2"
                ],
                "why": "`2`. Strings compare by value, and `\"ada\"` and `\"Ada\"` are different values. If they should count as one, that is a decision you write into the key — lower-case it first.\n"
              },
              {
                "id": "t2",
                "code": "const m = new Map();   const k = [1];   m.set(k, \"a\");   m.set(k, \"b\");   m.size",
                "accept": [
                  "1"
                ],
                "why": "`1`. The same array both times — one key. The second `set` replaced the value; it did not add a key.\n"
              },
              {
                "id": "t3",
                "code": "const a = new Set([1]);   const b = a;   b.add(2);   a.size",
                "accept": [
                  "2"
                ],
                "miss": "**If you answered `1` — `b = a` did not copy the set.** It copied the reference: one set, two names.\n",
                "why": "`2`. A set is an object like any other, so `b = a` gave it a second name, and `b.add` changed the one set both names point at.\n"
              },
              {
                "id": "t4",
                "question": "A game keeps every move a player makes, in order, so the match can be replayed — repeats included. List, map or set?",
                "choices": [
                  "List",
                  "Map",
                  "Set"
                ],
                "accept": [
                  "List"
                ],
                "why": "A list. Order matters and a repeated move is two moves; a set would drop the repeat."
              },
              {
                "id": "t5",
                "question": "A clinic needs each patient's next appointment, found by patient ID. List, map or set?",
                "choices": [
                  "List",
                  "Map",
                  "Set"
                ],
                "accept": [
                  "Map"
                ],
                "why": "A map from patient ID to appointment. A key, and something attached to it."
              }
            ],
            "extra": [
              {
                "id": "t6",
                "code": "new Set([NaN, NaN]).size",
                "accept": [
                  "1"
                ],
                "why": "`1`. `NaN === NaN` is `false`, but a set treats `NaN` as the same member as `NaN` — the one place it departs from `===`, and in the helpful direction.\n"
              },
              {
                "id": "t7",
                "question": "`const tags = new Set(); tags.add(\"x\");` — is the `add` allowed, given the `const`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. `const` stops `tags` being pointed at another set; it does not stop the set changing.\n"
              },
              {
                "id": "t8",
                "code": "const o = {};   o[{ id: 1 }] = \"a\";   o[{ id: 2 }] = \"b\";   o[{ id: 1 }]",
                "accept": [
                  "\"b\"",
                  "b",
                  "'b'"
                ],
                "why": "`\"b\"`. A plain object turned both objects into the text `\"[object Object]\"` — one key, overwritten.\n"
              },
              {
                "id": "t9",
                "question": "A newsletter tool must know which addresses have unsubscribed, and nothing else about them. List, map or set?",
                "choices": [
                  "List",
                  "Map",
                  "Set"
                ],
                "accept": [
                  "Set"
                ],
                "why": "A set: membership, and nothing attached."
              }
            ],
            "hints": [
              {
                "when": "you answered 1 for \"ada\" and \"Ada\"",
                "say": "Strings compare by value — every character, including case. Are these the same characters?\n"
              },
              {
                "when": "you answered 2 for the map with k",
                "say": "Count the arrays. `k` is built once and used twice: one object, one key. Setting a key that exists replaces its value.\n"
              },
              {
                "when": "you chose set for the replay",
                "say": "Would a set keep the second of two identical moves? Would it remember which came first?\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Build the key",
            "prompt": "A room-booking tool stores booked slots in a set. Slots arrive as fresh objects every time — `{ room: \"B12\", hour: 14 }` — and two slots are the same slot when the room and the hour match. Fill the gap in `keyOf` with one line. Then, in one sentence: why can `keyOf` not just return `slot`?\n",
            "exportName": "bookings",
            "filename": "bookings.js",
            "starter": "// Two slots are the same slot when room and hour match.\n// Slot objects are built fresh each time — never the same object twice.\nconst bookings = {\n  keyOf(slot) {\n    /* ___ one line: return a key that is equal for the same slot ___ */\n  },\n  book(booked, slot) {\n    booked.add(bookings.keyOf(slot));\n  },\n  isBooked(booked, slot) {\n    return booked.has(bookings.keyOf(slot));\n  },\n};\n",
            "tests": [
              {
                "label": "a slot built fresh is found after booking",
                "body": "const b = new Set(); subject.book(b, { room: 'B12', hour: 14 }); return subject.isBooked(b, { room: 'B12', hour: 14 }) === true;"
              },
              {
                "label": "another hour in the same room is free",
                "body": "const b = new Set(); subject.book(b, { room: 'B12', hour: 14 }); return subject.isBooked(b, { room: 'B12', hour: 15 }) === false;"
              },
              {
                "label": "the same hour in another room is free",
                "body": "const b = new Set(); subject.book(b, { room: 'B12', hour: 14 }); return subject.isBooked(b, { room: 'C3', hour: 14 }) === false;"
              },
              {
                "label": "B1 at 12 and B11 at 2 are different slots",
                "body": "const b = new Set(); subject.book(b, { room: 'B1', hour: 12 }); return subject.isBooked(b, { room: 'B11', hour: 2 }) === false;"
              },
              {
                "label": "fields written in another order are the same slot",
                "body": "const b = new Set(); subject.book(b, { room: 'B12', hour: 14 }); return subject.isBooked(b, { hour: 14, room: 'B12' }) === true;"
              },
              {
                "label": "booking the same slot twice is one booking",
                "body": "const b = new Set(); subject.book(b, { room: 'B12', hour: 14 }); subject.book(b, { room: 'B12', hour: 14 }); return b.size === 1;"
              }
            ],
            "hints": [
              {
                "when": "a fresh slot is never found",
                "say": "You are using the slot object as the key, so the set asks identity — and a fresh object is never the one you stored. Build a string or number from the parts that decide sameness.\n"
              },
              {
                "when": "B1 at 12 and B11 at 2 count as the same slot",
                "say": "Both give `\"B112\"` when the parts are joined directly. Put something between them that cannot appear in either part.\n"
              },
              {
                "when": "another hour in the same room shows as booked",
                "say": "Your key leaves something out. Two slots are the same only when *both* the room and the hour match.\n"
              },
              {
                "when": "fields in another order are not found",
                "say": "`JSON.stringify` writes fields in the order the object has them, so `{ room, hour }` and `{ hour, room }` give different strings. Build the key from the fields by name, in an order you choose.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "A new question changes the collection",
            "prompt": "`sheet` works: it records who has signed in to a workshop and answers \"has this person signed in?\" A new requirement: it must also answer **when did this person first sign in?** Add `firstSignIn(record, email)`, which returns the time passed to their first `signIn`, or `undefined` if they never signed in. Signing in again must not change the recorded time. Change whatever you need to — including what `start()` returns. Once it passes: in one sentence, what question can the new collection answer that the old one could not?\n",
            "exportName": "sheet",
            "filename": "sheet.js",
            "starter": "// Works today: answers \"has this person signed in?\"\n// `time` is passed in, and nothing keeps it yet.\nconst sheet = {\n  start() {\n    return [];\n  },\n  signIn(record, email, time) {\n    if (!record.includes(email)) record.push(email);\n  },\n  hasSignedIn(record, email) {\n    return record.includes(email);\n  },\n  count(record) {\n    return record.length;\n  },\n};\n",
            "tests": [
              {
                "label": "someone who signed in has signed in",
                "body": "const r = subject.start(); subject.signIn(r, 'ana@x.org', '09:02'); return subject.hasSignedIn(r, 'ana@x.org') === true;"
              },
              {
                "label": "someone who did not, has not",
                "body": "const r = subject.start(); subject.signIn(r, 'ana@x.org', '09:02'); return subject.hasSignedIn(r, 'bo@x.org') === false;"
              },
              {
                "label": "signing in twice counts one person",
                "body": "const r = subject.start(); subject.signIn(r, 'ana@x.org', '09:02'); subject.signIn(r, 'ana@x.org', '09:40'); subject.signIn(r, 'bo@x.org', '09:05'); return subject.count(r) === 2;"
              },
              {
                "label": "firstSignIn gives the time",
                "body": "const r = subject.start(); subject.signIn(r, 'ana@x.org', '09:02'); return subject.firstSignIn(r, 'ana@x.org') === '09:02';"
              },
              {
                "label": "signing in again keeps the first time",
                "body": "const r = subject.start(); subject.signIn(r, 'ana@x.org', '09:02'); subject.signIn(r, 'ana@x.org', '09:40'); return subject.firstSignIn(r, 'ana@x.org') === '09:02';"
              },
              {
                "label": "someone who never signed in has no time",
                "body": "const r = subject.start(); subject.signIn(r, 'ana@x.org', '09:02'); return subject.firstSignIn(r, 'bo@x.org') === undefined;"
              }
            ],
            "hints": [
              {
                "when": "firstSignIn is not a function",
                "say": "The new question needs a new function on `sheet`. But first ask what `start()` should return: which collection answers \"given this person, what goes with them?\"\n"
              },
              {
                "when": "hasSignedIn is false for someone who signed in",
                "say": "If you now store objects like `{ email, time }`, then `includes(email)` is looking for a string among objects — and `includes({ email, time })` asks identity about a brand-new object. Key the record by the email itself.\n"
              },
              {
                "when": "signing in again changes the time",
                "say": "Setting a key that already exists replaces its value. Check whether the person is already there before you record anything.\n"
              },
              {
                "when": "count is wrong",
                "say": "A map or a set says how many with `size`, not `length` — and `.length` on one of them is `undefined`, not an error.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a cart summary",
            "prompt": "A code assistant wrote this to show \"3 different products in your cart\". Each cart line's `product` is fetched fresh from the server — `{ sku: \"A1\", name: \"Hat\" }` — so two lines for the same product hold two separate objects. Two lines are wrong. Find them, then fix the function: two products are the same product when their `sku` matches.\n",
            "filename": "distinct-products.js",
            "code": "// How many different products are in the cart.\nfunction distinctProducts(lines) {\n  const seen = new Set();\n  for (const line of lines) {\n    seen.add(line.product);\n  }\n  return seen.length;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line means duplicates are never removed? Type the line number.",
                "accept": [
                  "5",
                  "line 5",
                  "l5"
                ],
                "why": "Line 5 adds the product object itself. Two lines for the same product hold two objects, and a set compares objects by identity, so both stay.\n"
              },
              {
                "id": "c2",
                "question": "Which of the three equality questions does the set ask about two product objects?",
                "choices": [
                  "Identity",
                  "Equivalence",
                  "Coerced equality"
                ],
                "accept": [
                  "Identity"
                ],
                "why": "Identity — are these the same object? The author meant equivalence: same `sku`. The set cannot ask that, so the key has to carry it.\n"
              },
              {
                "id": "c3",
                "question": "Which line returns the wrong thing for every cart, empty or not? Type the line number.",
                "accept": [
                  "7",
                  "line 7",
                  "l7"
                ],
                "why": "Line 7. A set has `size`, not `length`, so `seen.length` is `undefined` — for every cart, with no error.\n"
              },
              {
                "id": "c4",
                "question": "As written, what does `distinctProducts` return for a cart with two different products? Type the value.",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined`. Line 7 hides line 5: however the set is filled, the function never reports it.\n"
              }
            ],
            "starter": "// How many different products are in the cart.\nfunction distinctProducts(lines) {\n  const seen = new Set();\n  for (const line of lines) {\n    seen.add(line.product);\n  }\n  return seen.length;\n}\n",
            "exportName": "distinctProducts",
            "tests": [
              {
                "label": "two lines for the same product → 1",
                "body": "return subject([{ product: { sku: 'A1', name: 'Hat' }, qty: 1 },\n                { product: { sku: 'A1', name: 'Hat' }, qty: 2 }]) === 1;"
              },
              {
                "label": "two different products → 2",
                "body": "return subject([{ product: { sku: 'A1', name: 'Hat' }, qty: 1 },\n                { product: { sku: 'B7', name: 'Scarf' }, qty: 1 }]) === 2;"
              },
              {
                "label": "an empty cart → 0",
                "body": "return subject([]) === 0;"
              },
              {
                "label": "same sku, name written differently → still one product",
                "body": "return subject([{ product: { sku: 'A1', name: 'Hat' }, qty: 1 },\n                { product: { sku: 'A1', name: 'Hat (red)' }, qty: 1 }]) === 1;"
              }
            ],
            "hints": [
              {
                "when": "every test fails, including the empty cart",
                "say": "Start with line 7. What does a set call its count? `.length` on a set is `undefined`.\n"
              },
              {
                "when": "two lines for the same product give 2",
                "say": "Line 5 is still adding objects, and the set compares them by identity. Add the part that decides sameness instead.\n"
              },
              {
                "when": "the renamed product counts twice",
                "say": "You are keying on more than the `sku` — perhaps the whole object as a string. The spec says the `sku` alone decides.\n"
              }
            ]
          }
        ]
      }
    },
    "check-data": {
      "id": "check-data",
      "title": "Copying and sharing check",
      "kind": "quiz",
      "unlocks": "Week 4 — Control flow and iteration",
      "questions": [
        {
          "id": "q1",
          "skill": "Copying versus aliasing",
          "prompt": "What is `a.length` at the end?",
          "code": "let a = [1, 2];\nlet b = a;\nb.push(3);",
          "answers": [
            {
              "key": "A",
              "text": "`2` — `b = a` copied the array, so `push` only touched `b`",
              "tag": "the common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "`3` — `b = a` copied the *reference*, so both names point at one array",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`3`, but only because `a` was declared with `let`",
              "tag": "right answer, wrong reason",
              "correct": false
            },
            {
              "key": "D",
              "text": "TypeError — you cannot push onto an array held by two names",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Assignment always copies; the question is what it copied. For an array it copies the reference — the arrow, not the thing the arrow points at. One array, two names.",
            "wrong": "Not quite. `b = a` did copy something: the reference. Both names now point at one array, so `b.push(3)` changed the array `a` also names. `let` versus `const` is irrelevant here — neither affects mutation. The answer is B."
          }
        },
        {
          "id": "q2",
          "skill": "Mutation versus rebinding",
          "prompt": "Which of these two functions can change the caller's array?",
          "code": "function f(list) { list.push(9); }\nfunction g(list) { list = [9]; }\n\nconst mine = [1];\nf(mine);\ng(mine);",
          "answers": [
            {
              "key": "A",
              "text": "Both — the parameter refers to the caller's array either way",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "Neither — arguments are passed by value in JavaScript",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`f` only — it mutates the shared array; `g` rebinds its own local name",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`g` only — assignment is the operation that changes things",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. A function can change **what you pointed at**; it cannot change **where you point**. `f` reaches through the shared reference. `g` moves its own parameter and affects nobody. `mine` ends as `[1, 9]`.",
            "wrong": "Not quite. The parameter is a second name for the caller's array, so `f` can mutate it. But `g` only rebinds that second name — the caller's variable still points where it did. The answer is C."
          }
        },
        {
          "id": "q3",
          "skill": "Shallow versus deep copy",
          "prompt": "What is `s.inner.b` at the end?",
          "code": "const s = { a: 1, inner: { b: 2 } };\nconst c = { ...s };\nc.a = 9;\nc.inner.b = 9;",
          "answers": [
            {
              "key": "A",
              "text": "`2` — the spread made a copy, so `c` is independent",
              "tag": "the common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "`9` — the spread copied one level, so `c.inner` *is* `s.inner`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`9` — spread does not copy anything, it only renames",
              "tag": "right answer, wrong reason",
              "correct": false
            },
            {
              "key": "D",
              "text": "`2` — `const` protected `s` from being modified",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right, and note that `s.a` is still `1`. The same spread was deep enough for `a`, which held a number, and not deep enough for `inner`, which held a reference. A shallow copy is a new box holding the same arrows.",
            "wrong": "Not quite. The spread genuinely copied — `c.a = 9` left `s.a` at `1`, which proves it. It copied one level, so `c.inner` and `s.inner` are the same object. `const` never protects contents. The answer is B."
          }
        },
        {
          "id": "q4",
          "skill": "Diagnosis — finding a shared reference",
          "prompt": "Two configs come back with the same value when they should differ. Which single expression most directly confirms that a shared reference is the cause?",
          "answers": [
            {
              "key": "A",
              "text": "`console.log(first, second)` — compare the printed contents",
              "tag": "shows contents, not identity",
              "correct": false
            },
            {
              "key": "B",
              "text": "`JSON.stringify(first) === JSON.stringify(second)`",
              "tag": "two separate objects would also match",
              "correct": false
            },
            {
              "key": "C",
              "text": "`first.limits === second.limits`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "`first.limits.max === second.limits.max`",
              "tag": "compares values, not objects",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `===` on two objects asks the identity question — *are these the same object?* — and that is precisely the thing printing cannot show you. Two independent objects with identical contents log the same and stringify the same; only identity separates them.",
            "wrong": "Not quite. A, B and D all compare **contents**, and two separate objects holding the same values pass every one of them. You need the question that only identity answers, which is `===` between the two objects themselves. The answer is C."
          }
        }
      ],
      "_source": "checks/check-data.yml"
    },
    "checkpoint-1": {
      "id": "checkpoint-1",
      "title": "Checkpoint 1 practice",
      "kind": "quiz",
      "unlocks": "Week 6 — Closures and captured state",
      "questions": [
        {
          "id": "q1",
          "skill": "Types — conversion",
          "prompt": "What is `\"5\" + 1 - 1`?",
          "answers": [
            {
              "key": "A",
              "text": "`50`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`5`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`\"51\"`",
              "tag": "stopped one step early",
              "correct": false
            },
            {
              "key": "D",
              "text": "`NaN`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `+` joined because one side was text, making `\"51\"`; then `-` has no text meaning, so it converted back and subtracted. Two operators, two rules, in sequence.",
            "wrong": "Not quite. Left to right: `\"5\" + 1` is `\"51\"` because `+` joins when either side is text. Then `\"51\" - 1` converts and subtracts, giving `50`. The answer is A."
          }
        },
        {
          "id": "q2",
          "skill": "Types — equality",
          "prompt": "Which comparison asks whether two values are the *same object*?",
          "answers": [
            {
              "key": "A",
              "text": "`==`",
              "tag": "converts, then compares",
              "correct": false
            },
            {
              "key": "B",
              "text": "`===` on two objects",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`Object.is` — it is the identity check",
              "tag": "a common misreading",
              "correct": false
            },
            {
              "key": "D",
              "text": "None — JavaScript has no identity test",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `===` on two objects compares references. `Object.is` is `===` with `NaN` and `-0` corrected — for objects it behaves identically.",
            "wrong": "Not quite. `==` converts first. `Object.is` differs from `===` only on `NaN` and `-0`. For two objects, `===` is the identity test. The answer is B."
          }
        },
        {
          "id": "q3",
          "skill": "Types — truthiness",
          "prompt": "`\"0\"` is truthy. What is `\"0\" == false`?",
          "answers": [
            {
              "key": "A",
              "text": "`true` — and that is not a contradiction",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`false` — a truthy value cannot equal false",
              "tag": "the reasonable assumption",
              "correct": false
            },
            {
              "key": "C",
              "text": "`true`, because `\"0\"` is falsy",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It throws",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Two rule sets. Truthiness is membership of an eight-value list, which `\"0\"` is not on. `==` runs the coercion algorithm, turning both sides into `0`.",
            "wrong": "Not quite. `if (\"0\")` asks whether the value is one of the eight falsy values — it is not. `==` is a different rule: it converts both sides to numbers, and `0 == 0`. The answer is A."
          }
        },
        {
          "id": "q4",
          "skill": "References — aliasing",
          "prompt": "After `const a = [1,2]; const b = a; b.push(3);` what is `a.length`?",
          "answers": [
            {
              "key": "A",
              "text": "`2` — `b = a` copied the array",
              "tag": "the common guess",
              "correct": false
            },
            {
              "key": "B",
              "text": "`3` — one array, two names",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`2` — `const` prevented the change",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It throws — you cannot push to a const",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Assignment copied the *reference*, not the array. `const` blocks rebinding the name, never mutation of what it points at.",
            "wrong": "Not quite. `b = a` copied the arrow, so both names point at one array. `const` stops `b = somethingElse`, not `b.push(...)`. The answer is B."
          }
        },
        {
          "id": "q5",
          "skill": "References — copy depth",
          "prompt": "`const c = { ...s }` where `s.inner` is an object. Changing `c.inner.b` also changes `s.inner.b`. Why?",
          "answers": [
            {
              "key": "A",
              "text": "Spread does not copy anything",
              "tag": "right answer, wrong reason",
              "correct": false
            },
            {
              "key": "B",
              "text": "Spread copies one level, so `c.inner` is the same object as `s.inner`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Because `s` was declared with `const`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Because objects cannot be copied in JavaScript",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The spread genuinely copied — changing `c.a` would leave `s.a` alone. It copied one level, and `inner` held a reference. `structuredClone` copies all levels.",
            "wrong": "Not quite. Spread does copy: a primitive property would be independent. It copies one level only, so a property holding a reference is shared. The answer is B."
          }
        },
        {
          "id": "q6",
          "skill": "Branching — defaults",
          "prompt": "A user sets page size to `0`. What does `size || 10` produce?",
          "answers": [
            {
              "key": "A",
              "text": "`10` — `0` is on the falsy list, so the fallback wins",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`0` — the value was supplied",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`10`, and `size ?? 10` would also be `10`",
              "tag": "half right",
              "correct": false
            },
            {
              "key": "D",
              "text": "It throws",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right, and it is a bug: a deliberate zero is discarded. `??` asks only about `null` and `undefined`, so `size ?? 10` keeps the `0`.",
            "wrong": "Not quite. `||` consults the eight-value falsy list, and `0` is on it. `??` checks only for `null` and `undefined` and would keep the `0`. The answer is A."
          }
        },
        {
          "id": "q7",
          "skill": "Loops — iteration",
          "prompt": "Removing every `2` from `[1,2,2,3]` with a forward index loop and `splice` gives what?",
          "answers": [
            {
              "key": "A",
              "text": "`[1, 3]` — both are removed",
              "tag": "what almost everyone answers",
              "correct": false
            },
            {
              "key": "B",
              "text": "`[1, 2, 3]` — one survives",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "It throws — the collection was modified",
              "tag": "true in C# and Java, not here",
              "correct": false
            },
            {
              "key": "D",
              "text": "An infinite loop",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Removing the first `2` shifted the second into the index just finished, so the loop stepped over it. The same loop is correct on `[1,2,3]` — which is how this ships.",
            "wrong": "Not quite. The loop holds an index, and removal changes what every later index refers to. One `2` survives, silently — JavaScript never checks. The answer is B."
          }
        },
        {
          "id": "q8",
          "skill": "Scope — declarations",
          "prompt": "`var a = 1; { var a = 2; }` — what is `a` afterwards?",
          "answers": [
            {
              "key": "A",
              "text": "`1` — the block made a new scope",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`2` — `var` cannot see blocks",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`2`, and `let` would behave the same way",
              "tag": "half right",
              "correct": false
            },
            {
              "key": "D",
              "text": "A syntax error — `a` was declared twice",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `var` is scoped to the enclosing function, so the braces created no new scope. With `let`, the block would have made a second binding and the answer would be `1`.",
            "wrong": "Not quite. `var` binds to the enclosing *function* and blocks are invisible to it, so there was only ever one `a`. Re-declaring with `var` is legal. The answer is B."
          }
        }
      ],
      "_source": "checks/checkpoint-1.yml"
    },
    "functions-values": {
      "id": "functions-values",
      "title": "Functions as values",
      "kind": "read",
      "outcome": 5,
      "shape": "conceptual",
      "why": "Outcome 5 asks for functions that take and return functions, which needs a function to be a value like any other — something a name can hold and a call can hand over — not only a named block of reusable code. And `fn.first-class` asks the student to know this is not true of every language (D-018, D-033).",
      "concepts": [
        "fn.first-class"
      ],
      "lede": "In JavaScript a function is an ordinary value. A name can hold one, an array or an object can hold one, and a call can hand one to another function. The name on its own is the function; the parentheses are what run it.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Copying versus aliasing · Who calls a callback, and when · How a method finds its object"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**First-class** — treated like any other value: it can be stored, passed and returned without special syntax.",
          "entries": [
            "**First-class** — treated like any other value: it can be stored, passed and returned without special syntax."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four lines that treat functions as data. Type what each produces, or pick\nwhere there are buttons. A guess is fine; that is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const double = x => x * 2;   const twin = double;   twin(4)",
                          "accept": [
                            "8"
                          ],
                          "miss": "**If you expected an error — `twin` is not a copy of some text called `double`, it is the same function under a second name.** `const twin = double` put the function into `twin`, the way `const b = a` puts an array into `b`. Calling `twin(4)` runs it.\n",
                          "why": "A function is a value, so a second name can hold it. `twin(4)` runs the same code `double(4)` would.\n"
                        },
                        {
                          "code": "const double = x => x * 2;   typeof double(4)",
                          "accept": [
                            "\"number\"",
                            "number",
                            "'number'"
                          ],
                          "miss": "**If you answered `\"function\"` — look at the parentheses.** `double(4)` *calls* the function, and the call produces `8`. You asked for the type of the answer, not of the function.\n",
                          "why": "`double` is the function; `double(4)` is what it returns. The parentheses are the difference.\n"
                        },
                        {
                          "code": "const ops = [x => x + 1, x => x * 10];   ops[1](ops[0](2))",
                          "accept": [
                            "30"
                          ],
                          "why": "`ops[0]` is a function sitting in an array. Calling it on `2` gives `3`; `ops[1]` then multiplies that by ten.\n"
                        },
                        {
                          "code": "const a = x => x;   const b = a;   a === b",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you answered `false` — assigning a function does not make a second function.** Like an array or an object, a function is shared, not copied: `a` and `b` are two names for one value.\n",
                          "why": "One function, two names. `===` compares identity, and it is the same function.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "fns = [lambda x: x*2, lambda x: x+1]\\n[f(5) for f in fns]",
                          "accept": [
                            "[10, 6]",
                            "[10,6]",
                            "10,6"
                          ],
                          "why": "Functions are objects in Python too, and go in lists like anything else.\n"
                        },
                        {
                          "code": "callable(lambda: 1)",
                          "choices": [
                            "True",
                            "False"
                          ],
                          "accept": [
                            "True"
                          ],
                          "why": "Python asks \"is it callable?\" rather than \"is its type function\" — classes and objects with `__call__` are callable too.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "var fns = new List<Func<int,int>>{ x => x*2, x => x+1 };  fns.Select(f => f(5))",
                          "accept": [
                            "[10,6]",
                            "10, 6",
                            "10,6"
                          ],
                          "why": "Functions are values, but they need a **type**: `Func<int,int>` names one taking an int and returning an int.\n"
                        },
                        {
                          "code": "Func<int,int> f = x => x + 1;  f.GetType().Name",
                          "accept": [
                            "Func`2",
                            "Func",
                            "Func2"
                          ],
                          "why": "A delegate instance — an object wrapping a method. The ceremony JavaScript does not have.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can store a function in a variable, put one in an array or an object, and pass one to another function",
                "I can tell a function from a call to it by reading the parentheses",
                "I can say whether my language treats functions as ordinary values, and name a language that does not"
              ]
            }
          ]
        },
        {
          "h": "Everywhere a value can go"
        },
        {
          "p": "A value can be held by a name, sit in an array or an object, be passed to a\nfunction and be returned from one. In JavaScript a function can do all five."
        },
        {
          "code": "const double = x => x * 2;                  // held by a name\nconst steps  = [double, x => x + 1];        // in an array\nconst table  = { double, inc: x => x + 1 }; // in an object\n\nfunction applyTo(fn, v) { return fn(v); }   // passed as an argument\nfunction adder(n) { return x => x + n; }    // returned from a call",
          "lang": "js"
        },
        {
          "p": "Nothing there is a special form. `double` is a name bound to a function the\nsame way `rate` is a name bound to `0.2`, and the parameter `fn` in `applyTo` is\nan ordinary parameter that happens to receive a function. The last line —\na function that returns a function — is where the lesson on higher-order\nfunctions picks up."
        },
        {
          "p": "And because a function is an object, assigning it **shares** it, exactly as\n`const b = a` shares an array. `const twin = double` does not copy any code;\nit gives the one function a second name."
        },
        {
          "p": "What does each line produce?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const greet = n => \"hi \" + n;   const table = { greet };   table.greet(\"ada\")",
                "accept": [
                  "\"hi ada\"",
                  "hi ada",
                  "'hi ada'"
                ],
                "why": "`{ greet }` is shorthand for `{ greet: greet }` — the object holds the function under the key `greet`, and `table.greet(\"ada\")` calls it.\n"
              },
              {
                "code": "function applyTo(fn, v) { return fn(v); }   applyTo(Math.abs, -3)",
                "accept": [
                  "3"
                ],
                "miss": "**If you expected an error — `Math.abs` without parentheses is the function itself.** `applyTo` receives it in `fn` and calls it on `-3`.\n",
                "why": "A built-in function is a value too. `fn` held `Math.abs`, and `fn(v)` ran it.\n"
              }
            ]
          }
        },
        {
          "h": "Choosing behaviour by data"
        },
        {
          "p": "Two worked examples of the same move: put functions where data goes, then pick\none with ordinary data and call it."
        },
        {
          "p": "A calculator gets a symbol and two numbers from the user."
        },
        {
          "code": "// store the functions as data\nconst operations = {\n  \"+\": (a, b) => a + b,\n  \"-\": (a, b) => a - b,\n  \"*\": (a, b) => a * b,\n};\n\nfunction calculate(symbol, a, b) {\n  // pick one with data\n  const op = operations[symbol];\n\n  // call the one you picked\n  return op(a, b);\n}\n\ncalculate(\"*\", 6, 7);   // 42",
          "lang": "js",
          "file": "calculator.js"
        },
        {
          "p": "`operations[symbol]` is a property lookup like any other; what comes back is a\nfunction, and `op(a, b)` runs it. Adding division is one new line in the table,\nnot a new branch."
        },
        {
          "p": "A sign-up form checks a password against several rules."
        },
        {
          "code": "const rules = [\n  pw => pw.length >= 8,\n  pw => /[0-9]/.test(pw),\n];\n\nfunction passesAll(pw) {\n  for (const rule of rules) {\n    if (!rule(pw)) return false;\n  }\n  return true;\n}\n\npassesAll(\"hunter22\");   // true\npassesAll(\"hunter\");     // false — too short",
          "lang": "js",
          "file": "password.js"
        },
        {
          "p": "Here the \"data\" doing the picking is the loop: each iteration puts the next function\ninto `rule`, and `rule(pw)` runs it. The surface is different — an object keyed\nby symbol, an array walked in order — and the move is the same."
        },
        {
          "p": "Now finish one. A game maps a key press to a move."
        },
        {
          "code": "// store the functions as data\nconst moves = {\n  left:  pos => pos - 1,\n  right: pos => pos + 1,\n};\n\nfunction step(key, pos) {\n  // pick one with data\n  const move = ______;\n\n  // call the one you picked\n  return ______;\n}",
          "lang": "js",
          "file": "exercise.js"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "The first blank: what goes in `const move = ______`? Type the expression.",
                "accept": [
                  "moves[key]",
                  "moves[key];"
                ],
                "miss": "**If you wrote `moves.key` — that reads a property literally named `\"key\"`.** The name you want is *in* the variable `key`, so it needs square brackets.\n",
                "why": "`moves[key]` looks up the function stored under whatever `key` holds.\n"
              },
              {
                "question": "The second blank: what goes in `return ______`? Type the expression.",
                "accept": [
                  "move(pos)",
                  "move(pos);"
                ],
                "miss": "**If you wrote `move` — that returns the function itself, not a new position.** The caller asked for a position, so call it.\n",
                "why": "`move(pos)` runs the chosen function on the current position and returns the answer.\n"
              }
            ]
          }
        },
        {
          "h": "The name, and the call"
        },
        {
          "p": "Everything above rests on one reading skill: **a function's name on its own is\nthe function; the name followed by parentheses is a call.**"
        },
        {
          "code": "const shout = s => s.toUpperCase() + \"!\";\n\nshout            // the function — a value you can store or pass\nshout(\"hey\")     // a call — runs now, and is replaced by \"HEY!\"",
          "lang": "js"
        },
        {
          "p": "`const t = shout` stores the function. `const t = shout(\"hey\")` runs it and\nstores the string it returned. Both lines are correct JavaScript; they mean\ndifferent things, and the parentheses are the only difference. When you pass a\nfunction to another one, you want the first form — the next lesson is about\nwhat happens once you have handed it over."
        },
        {
          "p": "What does each line produce?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const shout = s => s.toUpperCase() + \"!\";   const t = shout;   typeof t",
                "accept": [
                  "\"function\"",
                  "function",
                  "'function'"
                ],
                "why": "No parentheses, so no call: `t` holds the function.\n"
              },
              {
                "code": "const shout = s => s.toUpperCase() + \"!\";   const t = shout(\"hey\");   t",
                "accept": [
                  "\"HEY!\"",
                  "HEY!",
                  "'HEY!'"
                ],
                "why": "The parentheses ran `shout` once, now, and `t` holds its answer.\n"
              }
            ]
          }
        },
        {
          "h": "Functions carry data"
        },
        {
          "p": "A function is an object, so it has properties like one."
        },
        {
          "code": "function greet(name, greeting) { return greeting + \" \" + name; }\n\ngreet.length;        // 2 — declared parameters\ngreet.name;          // \"greet\"\ngreet.callCount = 0; // your own property, perfectly legal",
          "lang": "js"
        },
        {
          "p": "`typeof` reports `\"function\"` as a convenience, not because functions are a\nseparate kind of thing. Libraries read `length` to decide how to call a function\nyou give them."
        },
        {
          "block": "notice",
          "label": "Two shapes, one idea",
          "body": [
            {
              "p": "`function f() {}` is hoisted complete, so it can be called above the line that\ndeclares it. `const f = () => {}` is a `const` holding a function, with the dead\nzone that implies. Either way, `f` is a name bound to a function value."
            }
          ]
        },
        {
          "p": "What does each line produce?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "function area(w, h) { return w * h; }   area.length",
                "accept": [
                  "2"
                ],
                "why": "`length` counts the declared parameters: `w` and `h`.\n"
              },
              {
                "code": "typeof (() => {})",
                "accept": [
                  "\"function\"",
                  "function",
                  "'function'"
                ],
                "miss": "**If you answered `\"object\"` — it is an object underneath, and `typeof` still says `\"function\"`.** That label is a convenience for asking \"can I call this?\"\n",
                "why": "Its own `typeof` result — but still an object, which is why it can carry properties.\n"
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **is a function's name the function, or a call?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Writing the name alone",
                  "To hold the function as a value"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "the function itself",
                    "nothing needed — `const t = shout`"
                  ],
                  [
                    "**Ruby**, a method made with `def`",
                    "**calls it** — `t = shout` runs `shout` and stores its answer",
                    "ask for it: `method(:shout)` returns a wrapper object, run with `.call`"
                  ]
                ]
              }
            },
            {
              "p": "Not every language treats functions as ordinary values. In Ruby, a method made\nwith `def` is not one: its name alone runs it, and getting it as a value takes\nan explicit request. (Ruby does have function values — blocks and lambdas —\nbut a method is not one of them.) JavaScript makes no such distinction, which is\nwhy the parentheses carry so much weight here: they are the only thing telling\nyou whether a line runs a function or passes it around."
            }
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In Ruby, `def shout ... end` is defined. What does `x = shout` store?",
                "choices": [
                  "The method",
                  "What it returns"
                ],
                "accept": [
                  "What it returns"
                ],
                "why": "What it returns. In Ruby the bare name calls the method; JavaScript would have stored the function.\n"
              },
              {
                "question": "The same line in JavaScript, `const x = shout`. What does it store?",
                "choices": [
                  "The function",
                  "What it returns"
                ],
                "accept": [
                  "The function"
                ],
                "why": "The function. In JavaScript a name without parentheses is always the value it holds.\n"
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say what `typeof double`\ngives, and what one character-pair made the second answer different from it."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A command-line tool reads a command word — `help`, `list`, `add`, `quit` — and\nruns the matching function."
            },
            {
              "p": "Two designs:"
            },
            {
              "list": [
                "**A `switch`** on the command word, with a `case` per command that calls its function.",
                "**A table of functions** — `const commands = { help: showHelp, list: showList, … }` — and one line, `commands[word]()`."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: where does the unknown-command\ncase live in each, and what does `commands[word]()` do when `word` is\n`\"hlep\"`? Finally, in the table version, what goes wrong if someone writes\n`{ help: showHelp(), … }`?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A function is an **ordinary value**: a name, an array or an object can hold one, and a call can pass one. Assigning it **shares** it.",
                "**The name is the function; the parentheses are a call.** `shout` is a value, `shout(\"hey\")` is its answer.",
                "Not every language does this: in Ruby a method's name **calls** it."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Functions",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Functions",
              "note": "the guide, including function expressions versus declarations."
            },
            {
              "title": "First-class function",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/First-class_Function",
              "note": "the glossary entry, with examples of storing, passing and returning."
            }
          ]
        }
      ],
      "_source": "lessons/functions-values.md",
      "practice": {
        "id": "functions-values",
        "outcome": 5,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the result",
            "prompt": "Three quick ones. Type what the first two produce, and pick the answer to the third.",
            "items": [
              {
                "id": "p1",
                "code": "const fns = [x => x * 2, x => x + 1];   fns[0](fns[1](5))",
                "accept": [
                  "12"
                ],
                "why": "`fns[1]` is a function sitting in an array; called on `5` it gives `6`. `fns[0]` doubles that."
              },
              {
                "id": "p2",
                "code": "const shout = s => s + \"!\";   const a = shout;   const b = shout(\"hi\");   typeof a + \" \" + typeof b",
                "accept": [
                  "\"function string\"",
                  "function string",
                  "'function string'"
                ],
                "miss": "**If you answered `function function` — look for parentheses.** `a` got the function; `shout(\"hi\")` ran it, and `b` got the string it returned.\n",
                "why": "No parentheses, no call: `a` is the function. With them, `b` is its answer, `\"hi!\"`."
              },
              {
                "id": "p3",
                "question": "You want a table that holds the function `format` under the key `date`, to call later. Which do you write?",
                "choices": [
                  "{ date: format }",
                  "{ date: format() }"
                ],
                "accept": [
                  "{ date: format }"
                ],
                "why": "The first. `format()` would run it now and store whatever it returned."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const a = x => x;   const b = a;   a === b",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "Assigning a function shares it, as with an array. One function, two names."
              },
              {
                "id": "p5",
                "code": "function greet(name) {}   greet.name",
                "accept": [
                  "\"greet\"",
                  "greet",
                  "'greet'"
                ],
                "why": "A function is an object with properties — `name`, `length`, and any you add."
              },
              {
                "id": "p6",
                "code": "typeof Math.max",
                "accept": [
                  "\"function\"",
                  "function",
                  "'function'"
                ],
                "why": "No parentheses: the built-in function itself, a value like any other."
              },
              {
                "id": "p7",
                "code": "const ops = { half: n => n / 2 };   ops.half",
                "accept": [
                  "The function"
                ],
                "why": "No call, so the expression is the function stored under `half`, not a number."
              }
            ],
            "hints": [
              {
                "when": "you answered function function for the second",
                "say": "Find every pair of parentheses after `shout`. Each one is a call, and a call is replaced by what the function returns."
              },
              {
                "when": "you chose { date: format() }",
                "say": "Ask when `format` should run. Parentheses run it *now*, while the table is being built."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Functions in a table and a list",
            "prompt": "Predict each line this prints, then run it.",
            "filename": "styles.js",
            "code": "const greet = name => \"hi \" + name;\nconst shout = name => name.toUpperCase() + \"!\";\n\nconst styles = { plain: greet, loud: shout };\nconst pick = styles.loud;\n\nconsole.log(pick(\"ada\"));\nconsole.log(styles.plain === greet);\nconsole.log(typeof styles.plain(\"bo\"));\n\nconst all = [greet, shout];\nconsole.log(all.length, all[0](\"cy\"));\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print?",
                "accept": [
                  "ADA!",
                  "\"ADA!\""
                ],
                "why": "`styles.loud` is `shout`; `pick` is a third name for it, and `pick(\"ada\")` runs it."
              },
              {
                "id": "r2",
                "question": "What does the second line print?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "Storing `greet` in the object shared it. `styles.plain` and `greet` are the same function."
              },
              {
                "id": "r3",
                "question": "What does the third line print?",
                "accept": [
                  "string",
                  "\"string\""
                ],
                "miss": "**If you answered `function` — `styles.plain(\"bo\")` is a call.** Its answer is `\"hi bo\"`, a string.\n",
                "why": "The type of the answer, not of the function."
              },
              {
                "id": "r4",
                "question": "What does the fourth line print?",
                "accept": [
                  "2 hi cy",
                  "2 \"hi cy\"",
                  "2 'hi cy'"
                ],
                "why": "An array of two functions; `all[0]` is `greet`, called on `\"cy\"`."
              }
            ],
            "hints": [
              {
                "when": "you predicted false for the second line",
                "say": "Did anything create a second function? `{ plain: greet }` stores a reference, exactly as `const b = a` does with an array."
              },
              {
                "when": "you predicted function for the third line",
                "say": "Look for the parentheses after `styles.plain`. What is `typeof` being asked about — the function, or what it returned?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Look it up, then call it",
            "prompt": "A unit converter keeps one function per unit in a table. Fill the gap so `convert(1500, \"km\")` is `1.5`. When it passes: in one sentence, what does `conversions[unit]` produce on its own, before anything calls it?\n",
            "exportName": "convert",
            "filename": "convert.js",
            "starter": "const conversions = {\n  km: metres => metres / 1000,\n  cm: metres => metres * 100,\n  mm: metres => metres * 1000,\n};\n\n// convert(1500, \"km\") -> 1.5\nfunction convert(metres, unit) {\n  return /* ___ look up the function for unit, and call it ___ */;\n}\n",
            "tests": [
              {
                "label": "converts to km",
                "body": "return subject(1500, 'km') === 1.5;"
              },
              {
                "label": "converts to cm",
                "body": "return subject(2, 'cm') === 200;"
              },
              {
                "label": "converts to mm",
                "body": "return subject(3, 'mm') === 3000;"
              },
              {
                "label": "returns a number, not a function",
                "body": "return typeof subject(1, 'km') === 'number';"
              }
            ],
            "hints": [
              {
                "when": "the first test fails with \"conversions.unit is not a function\"",
                "say": "`conversions.unit` reads a key literally named `unit`. The name you want is *in* the variable `unit` — which kind of property access reads a key from a variable?"
              },
              {
                "when": "only the \"returns a number\" test fails",
                "say": "You looked the function up and returned it. The caller wanted a distance — what turns a function into its answer?"
              },
              {
                "when": "every test fails with a wrong number",
                "say": "Check what you passed to the function you looked up. It needs the distance, `metres`, not the unit name."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Let the caller supply the checks",
            "prompt": "`isReady(order)` works: it runs two fixed checks and says whether every one passes. Change it so the caller supplies the checks — `isReady(order, checks)`, where `checks` is an array of functions — and keep the loop. When it passes: in one sentence, what can a caller do now that it could not before?\n",
            "exportName": "isReady",
            "filename": "ready.js",
            "starter": "// Works today: two fixed checks.\nfunction isReady(order) {\n  const checks = [\n    o => o.items.length > 0,\n    o => o.address != null,\n  ];\n  for (const check of checks) {\n    if (!check(order)) return false;\n  }\n  return true;\n}\n",
            "tests": [
              {
                "label": "uses the checks it is given",
                "body": "return subject({ items: [], address: null }, [function (o) { return o.paid === undefined; }]) === true;"
              },
              {
                "label": "fails when one supplied check fails",
                "body": "return subject({ items: [1], address: 'x' }, [function () { return true; }, function (o) { return o.paid === true; }]) === false;"
              },
              {
                "label": "no checks means ready",
                "body": "return subject({ items: [] }, []) === true;"
              },
              {
                "label": "each check receives the order",
                "body": "const order = { id: 7 }; let seen = null; subject(order, [function (o) { seen = o; return true; }]); return seen === order;"
              }
            ],
            "hints": [
              {
                "when": "the first test fails",
                "say": "Your function still builds its own list. Where do the checks come from now — and what happens to the `const checks = [...]` line?"
              },
              {
                "when": "the \"fails when one supplied check fails\" test fails",
                "say": "Look at the `if`. Are you calling each check on the order, or testing the check itself? A function is always truthy."
              },
              {
                "when": "the \"each check receives the order\" test fails",
                "say": "Each check must be *called*, with the order as its argument."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a command table",
            "prompt": "A teammate wrote a command table: `run(word)` runs the matching command and returns its message. Every call gives the wrong answer. It has three defects. Find them — then, below, fix all three.\n",
            "filename": "commands.js",
            "code": "// run(\"help\") -> \"commands: help, time\"; unknown words get a message.\nfunction help() { return \"commands: help, time\"; }\nfunction time() { return \"it is noon\"; }\n\nconst commands = {\n  help: help(),\n  time: time,\n};\n\nfunction run(word) {\n  const command = commands.word;\n  if (command === undefined) return \"unknown command: \" + word;\n  return command;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line stores a string where the table should hold a function? Type the line number.",
                "accept": [
                  "6",
                  "line 6"
                ],
                "why": "`help()` ran while the table was being built, and its answer — a string — is what got stored."
              },
              {
                "id": "c2",
                "question": "Which line makes every word, even `\"time\"`, come back as an unknown command? Type the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "`commands.word` looks up a key literally named `\"word\"`. It needs `commands[word]`."
              },
              {
                "id": "c3",
                "question": "Once the lookup is fixed, what does `run(\"time\")` return?",
                "choices": [
                  "A message",
                  "A function"
                ],
                "accept": [
                  "A function"
                ],
                "why": "The function `time` itself. Line 13 returns what it looked up without calling it."
              },
              {
                "id": "c4",
                "question": "Which line needs parentheses added? Type the line number.",
                "accept": [
                  "13",
                  "line 13"
                ],
                "why": "`return command();` — call what you looked up, and return its answer."
              }
            ],
            "exportName": "run",
            "starter": "// run(\"help\") -> \"commands: help, time\"; unknown words get a message.\nfunction help() { return \"commands: help, time\"; }\nfunction time() { return \"it is noon\"; }\n\nconst commands = {\n  help: help(),\n  time: time,\n};\n\nfunction run(word) {\n  const command = commands.word;\n  if (command === undefined) return \"unknown command: \" + word;\n  return command;\n}\n",
            "tests": [
              {
                "label": "run('help') gives the help message",
                "body": "return subject('help') === 'commands: help, time';"
              },
              {
                "label": "run('time') gives the time message",
                "body": "return subject('time') === 'it is noon';"
              },
              {
                "label": "an unknown word is reported",
                "body": "return subject('dance') === 'unknown command: dance';"
              }
            ],
            "hints": [
              {
                "when": "everything comes back as an unknown command",
                "say": "Dot access reads a key with a fixed name. The key you want is whatever `word` holds."
              },
              {
                "when": "run('time') passes but run('help') throws \"command is not a function\"",
                "say": "Look at what the table stores under `help`. Is it the function, or what calling it once produced?"
              },
              {
                "when": "the tests fail because a function comes back",
                "say": "After the lookup you have a function. The caller asked for its message."
              }
            ]
          }
        ]
      }
    },
    "callbacks": {
      "id": "callbacks",
      "title": "Who calls a callback, and when",
      "kind": "read",
      "outcome": 5,
      "shape": "conceptual",
      "why": "Every function that takes a function — `map`, `forEach`, `setTimeout`, the higher-order functions outcome 5 asks for — hands control of the call to someone else. A student who thinks they still decide when their function runs, and with what, mispredicts `map(parseInt)` and calls the callback instead of passing it (`fn.callbacks`, module-plan group 15).",
      "concepts": [
        "fn.callbacks"
      ],
      "lede": "A callback is a function you hand to other code, which calls it for you. Once you have handed it over, you no longer decide when it runs, how many times, or what it is given. The code you handed it to does — so that is the code to read.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Functions as values · Where your code runs · How a method finds its object"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Callback** — a function passed to other code so that code can call it. **Caller** — whoever runs the call; for a callback, that is not you.",
          "entries": [
            "**Callback** — a function passed to other code so that code can call it.",
            "**Caller** — whoever runs the call; for a callback, that is not you."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four lines that hand a function to someone else. Type what each produces, or\npick where there are buttons."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "[\"1\", \"2\", \"3\"].map(parseInt)",
                          "accept": [
                            "[1,NaN,NaN]",
                            "[1, NaN, NaN]",
                            "1,NaN,NaN"
                          ],
                          "miss": "**If you answered `[1,2,3]` — `parseInt` does parse numbers, and it also takes a second parameter.** `map` calls its callback with three arguments, (value, index, array), so the second call is `parseInt(\"2\", 1)`: base 1, which cannot represent `2`. You chose the function; `map` chose its arguments.\n",
                          "why": "`map` supplies (value, index, array), and `parseInt` reads the second as a radix. Radix 0 means \"the default\"; radixes 1 and 2 cannot represent `\"2\"` and `\"3\"`.\n"
                        },
                        {
                          "code": "function onEach(xs, visit) { for (const x of xs) visit(x); }   let n = 0;   onEach([4, 5, 6], () => { n++; });   n",
                          "accept": [
                            "3"
                          ],
                          "miss": "**If you answered `1` — you wrote the arrow once, but `onEach` calls it once per element.** How often a callback runs is decided by the code it was handed to.\n",
                          "why": "`onEach` calls `visit` three times, and each call adds one to `n`.\n"
                        },
                        {
                          "code": "function twice(fn) { fn(); fn(); }   function hello() { return \"hi\"; }   twice(hello())",
                          "accept": [
                            "TypeError",
                            "error",
                            "a TypeError",
                            "throws"
                          ],
                          "miss": "**If you expected it to work — look at the parentheses after `hello`.** `hello()` ran right there and its answer, the string `\"hi\"`, was what `twice` received. Calling a string throws `TypeError: fn is not a function`.\n",
                          "why": "The function was called instead of passed. `twice` got `\"hi\"`, not a function.\n"
                        },
                        {
                          "code": "const out = [];   setTimeout(() => out.push(\"later\"), 0);   out.push(\"now\");   out",
                          "accept": [
                            "[\"now\"]",
                            "['now']",
                            "[now]",
                            "now",
                            "[ \"now\" ]"
                          ],
                          "miss": "**If you answered `[\"later\",\"now\"]` — a zero delay does not mean \"now\".** `setTimeout` hands the arrow to the host, and the host calls it only after your code has finished running. When the last line runs, it has not been called yet.\n",
                          "why": "The host calls the arrow later, after this code has finished. At the last line, only `\"now\"` is in the array.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "list(map(int, [\"1\",\"2\",\"3\"]))",
                          "accept": [
                            "[1, 2, 3]",
                            "[1,2,3]",
                            "1,2,3"
                          ],
                          "why": "Python's `map` passes only the value, so the arity trap from JavaScript cannot happen here.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "new[]{\"1\",\"2\",\"3\"}.Select(int.Parse)",
                          "accept": [
                            "[1,2,3]",
                            "1, 2, 3",
                            "1,2,3"
                          ],
                          "why": "A method group as a delegate. The compiler checks the signature matches, so JavaScript's `parseInt` trap is a compile error here rather than silent.\n"
                        },
                        {
                          "question": "When does `Where` actually run its predicate?",
                          "choices": [
                            "When Where is called",
                            "When the result is enumerated"
                          ],
                          "accept": [
                            "When the result is enumerated"
                          ],
                          "why": "Deferred execution — a real difference from JavaScript's eager `filter`, and a source of surprise when the source changes in between.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say who calls a callback I hand over, and when it runs",
                "I can say what arguments a callback will actually receive"
              ]
            }
          ]
        },
        {
          "h": "Handing over is not calling"
        },
        {
          "p": "You already know that a function's name without parentheses is the function\nitself. A **callback** is what you get when you pass that value to other code\nso it can call it. From that moment three things are out of your hands:"
        },
        {
          "list": [
            "**when** it runs — during the call you made, or later;",
            "**how many times** — once, once per item, until some answer comes back, or never;",
            "**with what arguments** — whatever the caller passes, in the order the caller passes them."
          ]
        },
        {
          "p": "Your job shrinks to writing a function that does the right thing *whenever it\nis called with those arguments*. To know when that is, read the code you handed\nit to — or, for a built-in, its documentation."
        },
        {
          "p": "Two worked examples. The subgoals are the same questions each time."
        },
        {
          "code": "// Tries an action up to `times` times; stops at the first success.\nfunction retry(times, attempt) {\n  for (let i = 1; i <= times; i++) {\n    if (attempt(i)) return i;\n  }\n  return -1;\n}\n\nretry(5, n => n * n > 10);\n\n// Who calls it?        retry, on the line `attempt(i)`\n// When, how often?     once per iteration, stopping at the first truthy answer\n// What is it given?    one argument: the attempt number i\n// Result: called with 1, 2, 3, 4 — 16 > 10, so retry returns 4. 5 is never tried.",
          "lang": "js",
          "file": "retry.js"
        },
        {
          "code": "const seen = [];\n\n[\"x\", \"y\", \"z\"].forEach((v, i) => seen.push(i));\n\n// Who calls it?        forEach\n// When, how often?     once per element, before forEach returns\n// What is it given?    (value, index, array) — the second is named i here\n// Result: seen is [0, 1, 2] — the arrow named the index `i`, so it collected indexes.",
          "lang": "js",
          "file": "log-each.js"
        },
        {
          "p": "The arrow's parameters are **named by you but filled by the caller**, in the\ncaller's order. Calling the second one `i` did not make it an index; `forEach`\npassing the index in second position did."
        },
        {
          "p": "What does each line produce?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "retry(3, n => n > 5)",
                "accept": [
                  "-1"
                ],
                "why": "`retry` calls the arrow with 1, 2, 3; none is above 5, so the loop ends and `retry` returns `-1`.\n"
              },
              {
                "code": "const got = [];   [7, 8].forEach((a, b) => got.push(b));   got",
                "accept": [
                  "[0,1]",
                  "[0, 1]",
                  "0,1"
                ],
                "miss": "**If you answered `[7,8]` — the names `a` and `b` mean nothing to `forEach`.** It fills the first parameter with the value and the second with the index, whatever you called them.\n",
                "why": "`b` is in second position, so it receives the index.\n"
              }
            ]
          }
        },
        {
          "h": "Pass the function, not its answer"
        },
        {
          "p": "The commonest callback bug is one pair of parentheses. Written after the name,\nthey run the function *now* and pass its return value instead."
        },
        {
          "code": "function report() { console.log(\"hi\"); }\nfunction onEach(xs, visit) { for (const x of xs) visit(x); }\n\nonEach([1, 2, 3], report);    // \"hi\" three times — onEach calls it\nonEach([1, 2, 3], report());  // \"hi\" once, then TypeError: visit is not a function",
          "lang": "js"
        },
        {
          "p": "In the second call, `report()` runs **before** `onEach` starts — arguments are\nworked out first. It prints once and returns `undefined`, and `undefined` is\nwhat `onEach` receives as `visit`. The first `visit(x)` then throws."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In `function beep() { console.log(\"beep\"); }   setTimeout(beep(), 1000)`, when is \"beep\" printed?",
                "choices": [
                  "Immediately",
                  "After a second"
                ],
                "accept": [
                  "Immediately"
                ],
                "miss": "**If you picked \"After a second\" — look at the parentheses.** `beep()` ran immediately; `setTimeout` was given its answer, `undefined`, which is not a function to call later. Node throws on it; a browser quietly calls nothing.\n",
                "why": "Immediately. `setTimeout(beep, 1000)` — no parentheses — is how you hand it over.\n"
              }
            ]
          }
        },
        {
          "h": "What your callback receives"
        },
        {
          "p": "`map`, `filter` and `forEach` all call back with **(value, index, array)**. A\ncallback that declares one parameter is fine: extra arguments are discarded.\nHanding over an *existing* function is where it goes wrong — if that function\nhas a second parameter, it will be filled with the index whether it wanted one\nor not."
        },
        {
          "p": "That is the whole of the `parseInt` trap, and it is an arity problem, not a\nparsing one. Two fixes, both about controlling the call yourself:"
        },
        {
          "code": "[\"1\", \"2\", \"3\"].map(s => parseInt(s, 10));   // [1, 2, 3] — you decide the arguments\n[\"1\", \"2\", \"3\"].map(Number);                 // [1, 2, 3] — Number reads one argument only",
          "lang": "js"
        },
        {
          "p": "The wrapper is the one to reach for when you did not write the function you are\npassing: it is the only form where you, not the caller, decide what goes in."
        },
        {
          "p": "What does each line produce?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "[\"10\", \"10\", \"10\"].map(parseInt)",
                "accept": [
                  "[10,NaN,2]",
                  "[10, NaN, 2]",
                  "10,NaN,2"
                ],
                "miss": "**Work each call out with its index.** `parseInt(\"10\", 0)`, `parseInt(\"10\", 1)`, `parseInt(\"10\", 2)` — base default, base 1, base 2.\n",
                "why": "Index 0 means the default base (10), base 1 is invalid (`NaN`), and `\"10\"` in base 2 is `2`.\n"
              },
              {
                "code": "[\"a\", \"b\"].map((s, i, arr) => arr.length)",
                "accept": [
                  "[2,2]",
                  "[2, 2]",
                  "2,2"
                ],
                "why": "The third argument is the whole array, the same one on every call.\n"
              }
            ]
          }
        },
        {
          "h": "Now, or later"
        },
        {
          "p": "Some callers call your function **before they return** — `forEach`, `map`,\n`retry`. By the time the next line runs, every call has happened. Others keep\nit and call it **later**. `setTimeout` is the one you have met: it hands your\nfunction to the host, and the host calls it after your code has finished, as the\nlesson on where your code runs showed."
        },
        {
          "code": "console.log(\"A\");\n[1, 2].forEach(n => console.log(\"each \" + n));   // called now, twice\nsetTimeout(() => console.log(\"timer\"), 0);       // called later, by the host\nconsole.log(\"B\");\n// A · each 1 · each 2 · B · timer",
          "lang": "js"
        },
        {
          "p": "The question is the same either way — *who calls it, and when?* — and the answer\nis in the caller, never in the callback. Work that finishes later and reports\nback through a callback is where asynchronous code starts; it gets its own\nlessons."
        },
        {
          "p": "What is `total` at the last line of each?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "let total = 0;   setTimeout(() => { total = 5; }, 0);   total",
                "accept": [
                  "0"
                ],
                "miss": "**If you answered `5` — the arrow has not been called yet.** The host calls it after this code finishes, so the last line still sees `0`.\n",
                "why": "`0`. The assignment happens later, when the host calls the arrow.\n"
              },
              {
                "code": "let total = 0;   [5].forEach(n => { total = n; });   total",
                "accept": [
                  "5"
                ],
                "why": "`5`. `forEach` calls the arrow before it returns, so the assignment has already happened.\n"
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the third, say what one\nchange makes `twice` print nothing and return nothing without throwing — and\nwho calls `hello` after that change."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You are writing `everyItem(list, fn)` for your team: it calls `fn` once per\nitem."
            },
            {
              "p": "Two signatures for the call it makes:"
            },
            {
              "list": [
                "**`fn(item)`** — the item only.",
                "**`fn(item, index, list)`** — the same three arguments `forEach` passes."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what does a teammate lose with\nthe first, and what happens with the second when a teammate writes\n`everyItem(texts, parseInt)`? Name the one thing your documentation must say\neither way."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A callback is **called by the code you hand it to**, not by you. That code decides when, how often, and with what arguments.",
                "**No parentheses to pass it.** `f()` runs it now and passes its answer.",
                "`map`, `filter`, `forEach` pass **(value, index, array)**; `setTimeout`'s callback runs **later**, after your code finishes."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Callback function",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Callback_function",
              "note": "synchronous and asynchronous callbacks, with an example of each."
            },
            {
              "title": "Array.prototype.map()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/map",
              "note": "the callback's three arguments, which is the `parseInt` trap."
            }
          ]
        }
      ],
      "_source": "lessons/callbacks.md",
      "practice": {
        "id": "callbacks",
        "outcome": 5,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the result",
            "prompt": "Three quick ones. Type what the first two produce, and pick the answer to the third.",
            "items": [
              {
                "id": "p1",
                "code": "[\"7\", \"7\", \"7\"].map(parseInt)",
                "accept": [
                  "[7,NaN,NaN]",
                  "[7, NaN, NaN]",
                  "7,NaN,NaN"
                ],
                "miss": "**If you answered `[7,7,7]` — count the arguments `map` passes.** The second call is `parseInt(\"7\", 1)`, the third `parseInt(\"7\", 2)`, and neither base has a digit `7`.\n",
                "why": "`map` passes (value, index, array); `parseInt` reads the index as a base. Base 0 is the default; bases 1 and 2 cannot read `\"7\"`."
              },
              {
                "id": "p2",
                "question": "With `function report() { console.log(\"hi\"); }` and `function onEach(xs, visit) { for (const x of xs) visit(x); }`, the call is `onEach([1, 2, 3], report())`. How many times is \"hi\" printed? Type a number.",
                "accept": [
                  "1",
                  "once",
                  "one"
                ],
                "why": "`report()` ran *once*, right there, before `onEach` started — and its return value, `undefined`, was passed as `visit`. The first `visit(x)` then throws \"visit is not a function\"."
              },
              {
                "id": "p3",
                "question": "In `[3, 1, 2].sort(compare)`, who calls `compare`?",
                "choices": [
                  "You",
                  "sort"
                ],
                "accept": [
                  "sort"
                ],
                "why": "`sort`. You handed `compare` over; `sort` decides how many times to call it, and with which two items."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "[\"1\", \"2\", \"3\"].map(Number)",
                "accept": [
                  "[1,2,3]",
                  "[1, 2, 3]",
                  "1,2,3"
                ],
                "why": "`Number` reads only its first argument, so the index `map` passes is ignored. That is luck, not a rule: it works because of `Number`, not because of `map`."
              },
              {
                "id": "p5",
                "question": "Now the call is `onEach([1, 2, 3], report)`. How many times is \"hi\" printed? Type a number.",
                "accept": [
                  "3",
                  "three"
                ],
                "why": "No parentheses: the function itself is passed, and `onEach` calls it once per element."
              },
              {
                "id": "p6",
                "code": "[\"x\", \"y\"].map((s, i) => i)",
                "accept": [
                  "[0,1]",
                  "[0, 1]",
                  "0,1"
                ],
                "why": "The second parameter is filled with the index, whatever you name it."
              },
              {
                "id": "p7",
                "code": "let got = 0;   setTimeout(() => { got = 1; }, 0);   got",
                "accept": [
                  "0"
                ],
                "why": "The host calls the arrow after this code finishes. At the last line it has not run."
              }
            ],
            "hints": [
              {
                "when": "you answered [7,7,7]",
                "say": "Count the arguments `map` passes to its callback. There are three, and `parseInt` reads the second one."
              },
              {
                "when": "you answered 3 for the report line",
                "say": "Look for parentheses after `report` on the call to `onEach`. With them, `report` runs now and its *result* is what gets passed."
              },
              {
                "when": "you picked You for the third",
                "say": "Find the line that says `compare(...)`. You did not write one — it is inside `sort`."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Now, and later",
            "prompt": "Predict the lines this prints, in order, then run it. One caller calls its callback straight away; the host calls the other later.",
            "filename": "order.js",
            "code": "function onEach(xs, visit) {\n  for (const x of xs) visit(x);\n  console.log(\"onEach done\");\n}\n\nconsole.log(\"start\");\nsetTimeout(() => console.log(\"timer\"), 0);\nonEach([\"a\", \"b\"], x => console.log(\"visit \" + x));\nconsole.log(\"end\");\n",
            "items": [
              {
                "id": "r1",
                "question": "What is the second line printed?",
                "accept": [
                  "visit a"
                ],
                "miss": "**If you answered `timer` — a zero delay is not \"next\".** The host calls the timer's function only after all of this code has run.\n",
                "why": "`onEach` calls `visit` straight away, once per item, before it returns."
              },
              {
                "id": "r2",
                "question": "What is the fourth line printed?",
                "accept": [
                  "onEach done"
                ],
                "why": "Both calls to `visit` happened inside `onEach`, so `\"onEach done\"` follows them."
              },
              {
                "id": "r3",
                "question": "What is the last line printed?",
                "accept": [
                  "timer"
                ],
                "why": "The host calls the timer's callback once the file has finished — after `\"end\"`."
              }
            ],
            "hints": [
              {
                "when": "you put timer before the visits",
                "say": "`setTimeout` hands the function to the host; it does not call it. When does the host call what it holds?"
              },
              {
                "when": "you put onEach done before the visits",
                "say": "Read `onEach` top to bottom. The loop, and every `visit(x)` in it, runs before the `console.log` below it."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Who calls it, how often, with what",
            "prompt": "You hand a function over; something else decides when to call it and with what. Give the result of each.",
            "items": [
              {
                "id": "t1",
                "code": "function retry(times, attempt) { for (let i = 1; i <= times; i++) { if (attempt(i)) return i; } return -1; }   retry(6, n => n * 3 >= 10)",
                "accept": [
                  "4"
                ],
                "why": "`retry` calls `attempt(1)`, `attempt(2)`… and stops at the first truthy answer: `12 >= 10` at `n = 4`."
              },
              {
                "id": "t2",
                "question": "In `retry(6, n => n * 3 >= 10)`, how many times does the arrow run? Type a number.",
                "accept": [
                  "4",
                  "four"
                ],
                "why": "Once each for 1, 2, 3, 4 — `retry` returns at the first truthy answer, so 5 and 6 are never tried. You wrote the test; `retry` owns when it runs."
              },
              {
                "id": "t3",
                "code": "const seen = [];   [5, 6, 7].forEach((v, i) => seen.push(i));   seen",
                "accept": [
                  "[0,1,2]",
                  "[0, 1, 2]",
                  "0,1,2"
                ],
                "why": "`forEach` calls your callback with (value, index, array). You named the second one `i`, so you collected indexes."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "function mapOne(xs, fn) { const out = []; for (const x of xs) out.push(fn(x)); return out; }   mapOne([\"1\", \"2\", \"3\"], parseInt)",
                "accept": [
                  "[1,2,3]",
                  "[1, 2, 3]",
                  "1,2,3"
                ],
                "why": "This caller passes one argument only, so `parseInt` never sees an index. The trap was never in `parseInt` — it is in what the caller passes."
              },
              {
                "id": "t5",
                "question": "Running `retry(2, n => { console.log(n); return false; })`, how many lines are printed? Type a number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "The arrow never succeeds, so `retry` tries every attempt it allows: 2."
              },
              {
                "id": "t6",
                "code": "let total = 0;   [1, 2, 3].forEach(n => { total += n; });   setTimeout(() => { total = 100; }, 0);   total",
                "accept": [
                  "6"
                ],
                "why": "`forEach` called its arrow three times before returning; the timer's arrow has not been called yet."
              }
            ],
            "hints": [
              {
                "when": "you answered 3 or 10 for the first",
                "say": "Walk `retry` by hand: call the arrow with 1, then 2, and so on. Which is the first `n` where `n * 3 >= 10`?"
              },
              {
                "when": "you answered [5,6,7] for the forEach line",
                "say": "The callback's parameters are named by you but filled by `forEach`, in a fixed order. Which position is `i` in?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Be the caller",
            "prompt": "Now you are the code that calls. `countdown(from, onTick, onDone)` must call `onTick` once per number, counting down to 1, and then call `onDone` once — all before it returns. Fill the two gaps. When it passes: in one sentence, which of the three — you, `onTick` or `onDone` — decides how many times `onTick` runs?\n",
            "exportName": "countdown",
            "filename": "countdown.js",
            "starter": "// countdown(3, onTick, onDone) calls onTick(3), onTick(2), onTick(1),\n// then onDone() once.\nfunction countdown(from, onTick, onDone) {\n  for (let n = from; n >= 1; n--) {\n    /* ___ call onTick for this number ___ */\n  }\n  /* ___ call onDone ___ */\n}\n",
            "tests": [
              {
                "label": "ticks 3, 2, 1",
                "body": "const got = []; subject(3, function (n) { got.push(n); }, function () {}); return got.join() === '3,2,1';"
              },
              {
                "label": "done is called once, after the last tick",
                "body": "const log = []; subject(2, function (n) { log.push('t' + n); }, function () { log.push('done'); }); return log.join() === 't2,t1,done';"
              },
              {
                "label": "from 0: no ticks, and done still called once",
                "body": "let ticks = 0, done = 0; subject(0, function () { ticks++; }, function () { done++; }); return ticks === 0 && done === 1;"
              },
              {
                "label": "onTick is given exactly one argument",
                "body": "let counts = []; subject(2, function () { counts.push(arguments.length); }, function () {}); return counts.join() === '1,1';"
              }
            ],
            "hints": [
              {
                "when": "the ticks test fails with nothing collected",
                "say": "Naming `onTick` is not calling it. What turns a function value into a call?"
              },
              {
                "when": "the ticks come out undefined",
                "say": "You called `onTick` without telling it which number. The caller decides the arguments — here, that is you."
              },
              {
                "when": "done is called more than once",
                "say": "Is the call to `onDone` inside the loop? It belongs after the loop has finished."
              },
              {
                "when": "only the one-argument test fails",
                "say": "Your `onTick` receives more than the number. You are the caller now: pass exactly what the comment promises, and nothing else."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a price report",
            "prompt": "Someone wrote a price report: `total` adds up price strings, and `report` works out the total and calls `show` with it once. It has three defects, all about who calls what. Find them — then, below, fix all three.\n",
            "filename": "report.js",
            "code": "// total(texts): price strings like \"12\" become numbers, added up.\n// report(texts, show): works out the total, then calls show(total) once.\nfunction total(texts) {\n  let sum = 0;\n  texts.map(parseInt).forEach(n => { sum += n; });\n  return sum;\n}\n\nfunction report(texts, show) {\n  const t = total(texts);\n  texts.forEach(() => show(t));\n}\n\nfunction print(t) { console.log(\"total: \" + t); }\nreport([\"12\", \"30\"], print());\n\nconst prices = { total, report };\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line makes `total([\"12\", \"30\"])` come back as `NaN`? Type the line number.",
                "accept": [
                  "5",
                  "line 5"
                ],
                "why": "`map` passes the index as `parseInt`'s second argument, so `\"30\"` is read in base 1. Wrap it — `s => parseInt(s, 10)` — or use `Number`."
              },
              {
                "id": "c2",
                "question": "Which line calls `show` more often than the comment promises? Type the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "It calls `show` once per text. \"Once\" means one call, after the total is known: `show(t)`."
              },
              {
                "id": "c3",
                "question": "Once the file loads, how many times would that line call `show` for `[\"12\", \"30\"]`? Type a number.",
                "accept": [
                  "2",
                  "two",
                  "twice"
                ],
                "why": "Once per element of `texts` — `forEach` decides how often, and there are two."
              },
              {
                "id": "c4",
                "question": "Which line throws \"show is not a function\"? Type the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "miss": "**That is where it throws — now find where the bad value came from.** `show` holds whatever line 15 passed.\n",
                "why": "Line 11 is where `show(t)` fails; the cause is on line 15."
              },
              {
                "id": "c5",
                "question": "And which line caused it? Type the line number.",
                "accept": [
                  "15",
                  "line 15"
                ],
                "why": "`print()` runs right there and passes its answer, `undefined`. Pass `print` itself."
              }
            ],
            "exportName": "prices",
            "starter": "// total(texts): price strings like \"12\" become numbers, added up.\n// report(texts, show): works out the total, then calls show(total) once.\nfunction total(texts) {\n  let sum = 0;\n  texts.map(parseInt).forEach(n => { sum += n; });\n  return sum;\n}\n\nfunction report(texts, show) {\n  const t = total(texts);\n  texts.forEach(() => show(t));\n}\n\nfunction print(t) { console.log(\"total: \" + t); }\nreport([\"12\", \"30\"], print());\n\nconst prices = { total, report };\n",
            "tests": [
              {
                "label": "total adds the prices",
                "body": "return subject.total(['12', '30']) === 42;"
              },
              {
                "label": "total is right for three prices",
                "body": "return subject.total(['10', '10', '10']) === 30;"
              },
              {
                "label": "report calls show once, with the total",
                "body": "const got = []; subject.report(['5', '6', '7'], function (t) { got.push(t); }); return got.length === 1 && got[0] === 18;"
              }
            ],
            "hints": [
              {
                "when": "nothing passes, whatever you change in the functions",
                "say": "The file has to load before any test runs, and a line near the bottom throws. Look at what is passed to `report` there."
              },
              {
                "when": "total returns NaN",
                "say": "`map` calls its callback with three arguments. What does `parseInt` do with the second?"
              },
              {
                "when": "report calls show more than once",
                "say": "Who decides how many times `show` runs? Right now it is `forEach`, once per text."
              }
            ]
          }
        ]
      }
    },
    "closures": {
      "id": "closures",
      "title": "Closures keep their surroundings",
      "kind": "read",
      "outcome": 4,
      "shape": "conceptual",
      "why": "Outcome 4 names closures outright, and `scope.closure`'s mastery is the counter: a function that keeps counting after the call that made it has returned is what a name-and-assign model cannot explain. The closure is a moderate model with one misconception to break — that a call's bindings die when it returns — so it is conceptual; the callback made in a loop is the highly interacting half and is its own lesson (D-029).",
      "concepts": [
        "scope.closure"
      ],
      "lede": "A call's bindings normally disappear when it returns. A function made inside that call keeps them alive — and every time you call it, it is working on the same ones.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Which binding a name means · A closure keeps the binding, not the value"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Closure** — a function together with the bindings of the scope it was written in, kept alive for as long as the function is.",
          "entries": [
            "**Closure** — a function together with the bindings of the scope it was written in, kept alive for as long as the function is."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four programs about one counter. Type what each produces. A guess is fine; that\nis what this is for."
            },
            {
              "code": "function makeCounter() {\n  let count = 0;\n  return () => ++count;\n}",
              "lang": "js"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const a = makeCounter(); a(); a();  →  the second call returns",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you expected `1`, you pictured `count` disappearing when `makeCounter` returned, and starting again.** It did not disappear. The returned function still refers to it, so it survives — and each call changes that same `count`.\n",
                          "why": "`count` outlived the call that created it, because the returned function still refers to it. That survival is the closure.\n"
                        },
                        {
                          "code": "makeCounter()(); makeCounter()();  →  the two results",
                          "accept": [
                            "1 1",
                            "1, 1",
                            "1 and 1",
                            "[1,1]",
                            "1,1"
                          ],
                          "miss": "**If you answered `1` then `2`, you are sharing one counter in your head.** Each `makeCounter()` is a *new call*, making a new `count`. You share a counter only by sharing the function that closes over it.\n",
                          "why": "Each `makeCounter()` call makes a **fresh** binding, so each counter starts over.\n"
                        },
                        {
                          "code": "const c = makeCounter(); const d = c; d(); c();  →  the last call returns",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1`, you read `const d = c` as a second counter.** It is the same function under a second name — no new call to `makeCounter`, so no new `count`.\n",
                          "why": "`2`. One call to `makeCounter`, one `count`, reached through two names.\n"
                        },
                        {
                          "code": "const e = makeCounter(); e(); e.count",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you answered `1` — `count` is a binding in `makeCounter`'s scope, not a property of the function.** Nothing outside can reach it by name.\n",
                          "why": "`undefined`. The function has no `count` property. The binding is kept, but only the function can reach it.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "def mk():\\n    n=0\\n    def inc():\\n        nonlocal n; n+=1; return n\\n    return inc\\n\\na=mk(); a(); a()",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you expected `1` — `n` survived `mk` returning, because `inc` still refers to it.**\n",
                          "why": "`nonlocal` is required — without it, `n += 1` would create a local and raise `UnboundLocalError`. Python makes the capture explicit for writes.\n"
                        },
                        {
                          "code": "a=mk(); b=mk(); a(); b()  →  b()'s result",
                          "accept": [
                            "1"
                          ],
                          "why": "Each call to `mk` makes its own binding, exactly as in JavaScript.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "Func<int> Mk(){ int n=0; return () => ++n; }  var a=Mk(); a(); a();",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you expected `1` — `n` outlives `Mk` because the lambda still refers to it.**\n",
                          "why": "C# closes over the variable itself, and the compiler moves it to the heap so it can outlive the method.\n"
                        },
                        {
                          "code": "var a=Mk(); var b=Mk(); a(); b();  →  b()",
                          "accept": [
                            "1"
                          ],
                          "why": "A fresh variable per call, as everywhere.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can explain why a counter built from a closure keeps counting between calls",
                "I can say which functions share a closure's bindings and which have their own"
              ]
            }
          ]
        },
        {
          "h": "The scope did not end when the call did"
        },
        {
          "p": "Every call makes a fresh set of bindings — its parameters and its local names —\nin a new scope. Normally that scope is thrown away when the call returns;\nnothing can reach it any more."
        },
        {
          "p": "A scope survives when something still refers to it."
        },
        {
          "code": "function makeCounter() {\n  let count = 0;          // created by this call\n  return () => ++count;    // written in this scope, so its chain includes count\n}\n\nconst a = makeCounter();",
          "lang": "js"
        },
        {
          "p": "Follow what happens:"
        },
        {
          "list": [
            "`makeCounter()` runs. A new scope is made, holding `count` = 0.",
            "The arrow function is created **inside** that scope. By the lexical rule, its name `count` means this scope's `count`.",
            "`makeCounter` returns the function. The call is over — but `a` holds the function, and the function still refers to the scope. So the scope stays.",
            "`a()` runs `++count`. The search for `count` goes to that kept scope and changes the binding there: now 1. `a()` again: 2."
          ],
          "ordered": true
        },
        {
          "p": "The returned function is not a copy of anything. It is a function **plus** the\nscope it was written in, and as long as you hold the function, that scope is\nalive. That pair is a **closure**. It keeps the binding itself — not a snapshot\nof its value — which is why each call sees what the last one left there."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "function makeTally() { let total = 0; return (n) => total += n; }  const t = makeTally(); t(5); t(3);  →  the second call returns",
                "accept": [
                  "8"
                ],
                "miss": "**If you answered `3`, `total` started again at 0 on the second call.** It lives in `makeTally`'s scope, made once when `makeTally()` ran; each call of `t` adds to that one binding.\n",
                "why": "`8`. `total` is 5 after the first call and survives to the second.\n"
              }
            ]
          }
        },
        {
          "h": "One call, one set of bindings"
        },
        {
          "p": "A closure keeps the scope of **the call that created it**. Call the factory\nagain and you get a new scope, a new binding, and an independent counter."
        },
        {
          "code": "const a = makeCounter();\nconst b = makeCounter();\n\na(); a();     // 1, 2\nb();          // 1 — b's count is a different binding, made by a different call",
          "lang": "js"
        },
        {
          "p": "Giving the same function a second name makes nothing new:"
        },
        {
          "code": "const c = a;  // no call to makeCounter — the same function, the same count\nc();          // 3",
          "lang": "js"
        },
        {
          "p": "So the question for \"do these share state?\" is always: **how many times did\nthe factory run?** Once per independent set of state."
        },
        {
          "h": "Seeing the kept scope in the debugger"
        },
        {
          "p": "You can look at a kept scope directly. Printing cannot show you one: inside the\nfunction, `console.log(count)` prints a number but not where `count` lives, and\noutside, nothing can print it at all — `a.count` is `undefined`."
        },
        {
          "code": "function makeCounter() {\n  let count = 0;\n  return () => {\n    count = count + 1;\n    return count;\n  };\n}\n\nconst a = makeCounter();\nconst b = makeCounter();\na();\na();\nb();",
          "lang": "js",
          "file": "kept.js"
        },
        {
          "p": "Run it under the debugger as in *Meet the debugger* — `node --inspect-brk` and\nChrome, or the JavaScript Debug Terminal in a Codespace — with a breakpoint on\nline 4, and resume. In Chrome, look at the **Scope** pane; VS Code shows the same\nsections in its **Variables** view. **Local** is the arrow function's own names,\nand no line of it declares `count`. `count` is listed under **Closure**: the\nbindings of the call that made this function, still alive after it returned."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "kept.js, first pause on line 4: is `count` listed under **Local** or under **Closure**?",
                "choices": [
                  "Local",
                  "Closure"
                ],
                "accept": [
                  "Closure"
                ],
                "miss": "**If you picked Local — Local is what this function declares, and the arrow function declares nothing.** `count` was made by `makeCounter`'s call; the function reaches it through the kept scope.\n",
                "why": "Closure. The arrow function's own scope is empty; `count` belongs to the call of `makeCounter` that created it.\n"
              },
              {
                "question": "kept.js, third pause on line 4 (the call `b()`): type the value of `count` in the Closure section.",
                "accept": [
                  "0"
                ],
                "miss": "**If you answered `2`, you read `a`'s binding.** `b` came from the second call to `makeCounter`, so its Closure section holds a different `count`, still 0 before line 4 runs.\n",
                "why": "`0`. Line 4 has not run yet, and this `count` belongs to `b`'s call, which nothing has changed.\n"
              }
            ]
          }
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const x = makeCounter(); const y = makeCounter(); x(); x(); y(); x();  →  the last call returns",
                "accept": [
                  "3"
                ],
                "why": "`3`. `x`'s `count` goes 1, 2, then 3; `y`'s call touched its own binding.\n"
              },
              {
                "code": "const p = makeCounter(); const q = p; const r = makeCounter(); p(); q(); r();  →  r()'s result",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered `3` — `r` came from its own call to `makeCounter`.** `p` and `q` share one `count`; `r` has another.\n",
                "why": "`1`. Two calls to `makeCounter`, two bindings: `p` and `q` are one function; `r` is the other.\n"
              }
            ]
          }
        },
        {
          "h": "Two functions, one binding"
        },
        {
          "p": "A closure is not one-to-one with a binding. Everything created in the same call\nshares that call's bindings."
        },
        {
          "code": "function pair() {\n  let n = 0;\n  return {\n    inc:  () => ++n,\n    read: () => n,\n  };\n}\n\nconst p = pair();\np.inc(); p.inc();\np.read();     // 2 — inc and read share one `n`\n\nconst q = pair();\nq.read();     // 0 — a different call, a different binding",
          "lang": "js"
        },
        {
          "p": "That is also the standard way to get **private state** in JavaScript: `n` is\nreachable only through the two functions, and `p.n` is `undefined`. Nothing\noutside can read it or change it except by calling them."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const p = pair(); p.inc(); const q = pair(); q.inc(); q.inc();  →  p.read()",
                "accept": [
                  "1"
                ],
                "why": "`1`. `p`'s two functions share `p`'s `n`; `q`'s calls changed `q`'s.\n"
              },
              {
                "code": "const p = pair(); p.n = 10; p.inc();  →  p.read()",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered `11` — `p.n = 10` adds a property to the object.** It is not the binding `n` in `pair`'s scope, which only `inc` and `read` can reach.\n",
                "why": "`1`. The new `n` property is unrelated to the closed-over binding.\n"
              }
            ]
          }
        },
        {
          "h": "Following a closure's state, worked"
        },
        {
          "p": "The same three steps, whatever the factory makes."
        },
        {
          "p": "An ID generator gives each record a label:"
        },
        {
          "code": "function makeIds(prefix) {\n  let next = 1;\n  return () => prefix + \"-\" + next++;\n}\n\nconst orderId = makeIds(\"ord\");\norderId();   // ?\norderId();   // ?",
          "lang": "js",
          "file": "ids.js"
        },
        {
          "code": "// find the call that created the bindings\n//   makeIds(\"ord\") — one call, one scope: prefix = \"ord\", next = 1.\n// list the functions it created\n//   one arrow function, held as orderId.\n// follow each call through the kept bindings\n//   orderId(): reads next (1), returns \"ord-1\", next becomes 2.\n//   orderId(): reads next (2), returns \"ord-2\", next becomes 3."
        },
        {
          "p": "A different shape — a limit on how often something may happen:"
        },
        {
          "code": "function allowTimes(max) {\n  let used = 0;\n  return () => {\n    if (used >= max) return false;\n    used++;\n    return true;\n  };\n}\n\nconst tryLogin = allowTimes(2);\ntryLogin(); tryLogin(); tryLogin();   // ?",
          "lang": "js",
          "file": "limit.js"
        },
        {
          "code": "// allowTimes(2) created the bindings: max = 2, used = 0.\n// It created one function, held as tryLogin.\n// The calls: true (used 1), true (used 2), false — used has reached max."
        },
        {
          "p": "Now one with the last step left to you:"
        },
        {
          "code": "const left = makeIds(\"L\");\nconst right = makeIds(\"R\");\nleft(); right(); left();   // the last call: ?",
          "lang": "js",
          "file": "steps.js"
        },
        {
          "code": "// find the call that created the bindings\n//   two calls: makeIds(\"L\") and makeIds(\"R\") — two scopes, two `next`s.\n// list the functions it created\n//   left belongs to the first; right to the second.\n// follow each call through the kept bindings\n//   ______"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "steps.js: type the value the last call, `left()`, returns.",
                "accept": [
                  "\"L-2\"",
                  "L-2",
                  "'L-2'"
                ],
                "miss": "**If you answered `\"L-3\"`, you counted `right()` against `left`'s `next`.** They came from two calls, so they have two `next` bindings.\n",
                "why": "`\"L-2\"`. `left`'s `next` was 1, then 2; `right()` changed only its own.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Following a closure's state by hand",
            "body": [
              {
                "h": "Following a closure's state by hand"
              },
              {
                "code": "// A function uses a name that was declared in an outer function.\n\n// 1. Which CALL of the outer function created that binding?\n//    Each call makes its own. Count the calls, not the functions.\n// 2. Which functions were created during that same call?\n//    All of them share the binding. Functions from other calls do not.\n// 3. Each time one of them runs, it reads or changes that one binding —\n//    so its value is whatever the previous run left there.",
                "lang": "js",
                "file": "closure-state.js"
              },
              {
                "p": "Now apply it. Say what the last line returns."
              },
              {
                "code": "function makeStack() {\n  const items = [];\n  return {\n    push: (x) => items.push(x),\n    size: () => items.length,\n  };\n}\n\nconst s = makeStack();\nconst t = makeStack();\ns.push(\"a\"); s.push(\"b\"); t.push(\"z\");\ns.size();                   // ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`2`. `s.push` and `s.size` were created in one call and share its `items`; `t`\ncame from another call with an array of its own."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **can a function keep the scope it was created in alive?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Keeps it alive",
                  "Changing a kept binding from inside"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "yes",
                    "just assign — `count++`"
                  ],
                  [
                    "**Python**",
                    "yes",
                    "must declare `nonlocal count` first"
                  ],
                  [
                    "**C#**",
                    "yes — the compiler moves it to the heap",
                    "just assign"
                  ]
                ]
              }
            },
            {
              "p": "All three keep the variable, not a copy. They differ only in whether *writing*\nto it needs saying. Python makes you mark it, because in Python an assignment\ninside a function would otherwise make a new local. A language whose functions\ncannot outlive the call that made them has no closures, and counters, IDs and\nprivate state have to live on objects instead."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Closures for private state."
              },
              {
                "code": "function counter() {\n  let n = 0;\n  return { inc: () => ++n, read: () => n };\n}",
                "lang": "js"
              },
              {
                "p": "This is the pattern behind module state, memoisation, event handlers that know\nwhat they were attached to, and every \"factory\" function. It predates `#private`\nclass fields and is still the simplest way to get state nothing else can reach."
              }
            ],
            "python": [
              {
                "p": "Reading a captured name works; **writing** it needs `nonlocal`."
              },
              {
                "code": "def counter():\n    n = 0\n    def inc():\n        nonlocal n\n        n += 1\n        return n\n    return inc",
                "lang": "python"
              },
              {
                "p": "Without `nonlocal`, `n += 1` makes a new local and raises `UnboundLocalError`."
              }
            ],
            "csharp": [
              {
                "p": "Lambdas capture variables, and the compiler lifts them to a heap-allocated class\nso they outlive the method."
              },
              {
                "code": "Func<int> Counter() { int n = 0; return () => ++n; }",
                "lang": "csharp"
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say what you would change in\nthe second program so that the two calls print `1` and `2` — and how many\ncalls to `makeCounter` your version makes."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You need a `once(fn)` wrapper and a `memoise(fn)` wrapper. Both keep state\nbetween calls, and both could store that state in a closure or on a property of\nthe returned function."
            },
            {
              "p": "Two designs:"
            },
            {
              "list": [
                "**Closure state** — `let called = false` inside the wrapper.",
                "**A property** — `wrapper.called = false`, set from outside as well as in."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what a caller can do to the\nsecond that they cannot do to the first, and whether there is ever a reason to\n*want* that. Name the case where you would deliberately expose the cache."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A closure is a function **plus the bindings of the scope it was written in**. They survive as long as the function does.",
                "Each **call** of the outer function makes a new set of bindings. Functions made in one call share them; functions from other calls do not.",
                "Closure state is unreachable from outside, which makes it JavaScript's oldest and simplest form of privacy."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Closures",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Closures",
              "note": "the guide, including the emulating-private-methods section."
            }
          ]
        }
      ],
      "_source": "lessons/closures.md",
      "practice": {
        "id": "closures",
        "outcome": 4,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict, then name the first step",
            "prompt": "`makeCounter` returns `() => ++count` over a local `count`. Type what the first two produce. For the third, pick one.\n",
            "items": [
              {
                "id": "p1",
                "code": "const a = makeCounter(); a(); a();  →  the second call",
                "accept": [
                  "2"
                ],
                "why": "`count` outlived the call that made it, because the returned function still refers to it."
              },
              {
                "id": "p2",
                "code": "makeCounter()(); makeCounter()();  →  the two results",
                "accept": [
                  "1 1",
                  "1, 1",
                  "1,1",
                  "[1,1]",
                  "1 and 1"
                ],
                "why": "Two calls, two bindings. You share a counter only by sharing the function that closes over it."
              },
              {
                "id": "p0",
                "question": "Two counter functions exist. To know whether they share a count, what do you count first?",
                "choices": [
                  "The names that hold them",
                  "The calls to makeCounter",
                  "The calls to each counter"
                ],
                "accept": [
                  "The calls to makeCounter"
                ],
                "miss": "**If you picked the names — two names can hold one function.** Bindings are made per *call* of the factory, so that is what to count.\n",
                "why": "The calls to makeCounter. Each call makes one `count`; every function made in that call shares it."
              }
            ],
            "extra": [
              {
                "id": "p3",
                "code": "const c = makeCounter(); const d = c; d(); c();  →  the last call",
                "accept": [
                  "2"
                ],
                "why": "One function under two names — so one binding. The aliasing rule from copying versus sharing, applied to a closure."
              },
              {
                "id": "p4",
                "code": "const e = makeCounter(); e.count",
                "accept": [
                  "undefined"
                ],
                "why": "The binding is unreachable from outside. That inaccessibility is the privacy closures buy you."
              },
              {
                "id": "p5",
                "code": "function mk(){ let n = 10; return () => n--; }  const f = mk(); f(); f();  →  the second call",
                "accept": [
                  "9"
                ],
                "why": "`n--` returns the value before decrementing: 10 on the first call, 9 on the second. The binding survived between them."
              }
            ],
            "hints": [
              {
                "when": "you answered 1 then 2 for the second",
                "say": "Count the calls to `makeCounter`. Each one runs the body again, which creates a new `count`."
              },
              {
                "when": "you answered 1 for the first",
                "say": "You pictured `count` being made again on every call of `a`. `let count = 0` is in `makeCounter`, and `makeCounter` ran once."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict five lines, then run them",
            "prompt": "Predict every line the program prints — type the number — then run it. For each call, ask which call of a factory made the binding it changes.\n",
            "code": "function makeCounter() {\n  let count = 0;\n  return () => ++count;\n}\n\nconst a = makeCounter();\nconst b = makeCounter();\nconsole.log(a());\nconsole.log(a());\nconsole.log(b());\nconst c = a;\nconsole.log(c());\n\nfunction pair() {\n  let n = 0;\n  return { inc: () => ++n, read: () => n };\n}\nconst p = pair();\np.inc();\np.inc();\nconsole.log(p.read());\n",
            "items": [
              {
                "id": "r1",
                "question": "Line 1 printed",
                "accept": [
                  "1"
                ],
                "why": "`a`'s own `count` goes from 0 to 1."
              },
              {
                "id": "r2",
                "question": "Line 2 printed",
                "accept": [
                  "2"
                ],
                "why": "Same function, same binding — it survived the first call."
              },
              {
                "id": "r3",
                "question": "Line 3 printed",
                "accept": [
                  "1"
                ],
                "why": "`b` came from a separate call to `makeCounter`, so it has its own `count`."
              },
              {
                "id": "r4",
                "question": "Line 4 printed",
                "accept": [
                  "3"
                ],
                "why": "`c` is `a` under another name — not a new counter. It carries on from 2."
              },
              {
                "id": "r5",
                "question": "Line 5 printed",
                "accept": [
                  "2"
                ],
                "why": "`inc` and `read` were created in the same call to `pair`, so `read` sees what `inc` did."
              }
            ],
            "hints": [
              {
                "when": "you answered 1 for line 4",
                "say": "`const c = a` makes no new counter — it gives the same function a second name. How many times has that function run before this line?"
              },
              {
                "when": "you answered 0 for line 5",
                "say": "You gave `read` a binding of its own. Which call created the `n` that `read` refers to — and what else was created in that call?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Follow the state",
            "prompt": "Give the value each marked call returns.",
            "items": [
              {
                "id": "t1",
                "code": "function makeIds(p){ let next = 1; return () => p + next++; }  const o = makeIds(\"o\"); o(); o();  →  o()",
                "accept": [
                  "\"o3\"",
                  "o3",
                  "'o3'"
                ],
                "why": "One call to `makeIds`, one `next`: 1, 2, 3 across the three calls."
              },
              {
                "id": "t2",
                "code": "const x = makeIds(\"x\"); const y = makeIds(\"y\"); x(); y(); y();  →  x()",
                "accept": [
                  "\"x2\"",
                  "x2",
                  "'x2'"
                ],
                "why": "Two calls, two `next` bindings. `y`'s calls moved only its own."
              }
            ],
            "extra": [
              {
                "id": "t3",
                "code": "function pair(){ let n=0; return { inc: () => ++n, read: () => n }; }  const p = pair(); p.inc(); p.inc();  →  p.read()",
                "accept": [
                  "2"
                ],
                "why": "`inc` and `read` were created in the same call, so they share one `n`."
              },
              {
                "id": "t4",
                "code": "function allow(max){ let used = 0; return () => used++ < max; }  const g = allow(1); g();  →  g()",
                "accept": [
                  "false"
                ],
                "why": "The first call compared 0 < 1 and moved `used` to 1. The second compares 1 < 1 — the kept binding remembers the first call."
              },
              {
                "id": "t5",
                "code": "function mk(){ return () => { let c = 0; return ++c; }; }  const f = mk(); f(); f();  →  the second call",
                "accept": [
                  "1"
                ],
                "why": "Here `c` is declared inside the returned function, so every call of `f` makes a fresh `c`. A closure keeps only what was in the scope *around* the function."
              }
            ],
            "hints": [
              {
                "when": "you answered \"x1\" or \"x3\" for the second",
                "say": "List which factory call made each of `x` and `y`. Then count only the calls of `x`."
              },
              {
                "when": "you answered 2 for the last extra",
                "say": "Find where `c` is declared. Is it in the scope the function was written in, or inside the function itself?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Run it only once",
            "prompt": "`once(fn)` returns a wrapper. The first call runs `fn` and returns its result; every later call returns that same result without running `fn` again. Two wrappers must be independent. Fill the gap with the declarations the wrapper needs. Then, in one sentence: why must they be written where the gap is and not inside the returned function?\n",
            "exportName": "once",
            "filename": "once.js",
            "starter": "function once(fn) {\n  /* ___ declare what the wrapper must remember ___ */\n  return function () {\n    if (!called) {\n      called = true;\n      result = fn();\n    }\n    return result;\n  };\n}\n",
            "tests": [
              {
                "label": "the first call returns fn's result",
                "body": "return subject(function () { return 42; })() === 42;"
              },
              {
                "label": "fn runs only once, however often the wrapper is called",
                "body": "let runs = 0; const w = subject(function () { runs++; return runs; }); w(); w(); w(); return runs === 1;"
              },
              {
                "label": "later calls return the first result",
                "body": "let runs = 0; const w = subject(function () { return ++runs; }); w(); return w() === 1;"
              },
              {
                "label": "two wrappers are independent",
                "body": "const a = subject(function () { return 'a'; }); const b = subject(function () { return 'b'; }); a(); return b() === 'b';"
              },
              {
                "label": "a result of undefined is remembered too",
                "body": "let runs = 0; const w = subject(function () { runs++; }); w(); w(); return runs === 1;"
              }
            ],
            "hints": [
              {
                "when": "fn runs on every call",
                "say": "Your declarations are made fresh each time the wrapper runs, so `called` is always `false` again. Declare them where the wrapper is *written*, so every call of it finds the same ones."
              },
              {
                "when": "the independent-wrappers test fails",
                "say": "Your state lives outside `once`, so every wrapper shares it. Each call of `once` needs its own — declare it inside `once`."
              },
              {
                "when": "it throws ReferenceError",
                "say": "The wrapper reads `called` and `result`, and nothing declares them. Which keyword fits a binding that the wrapper reassigns?"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Keep state nothing else can reach",
            "prompt": "Write `makeAccount(start)`. It returns an object with `deposit(n)`, `withdraw(n)` and `balance()`. The balance must be reachable only through those three functions, and two accounts must be fully independent. Then, in one sentence: why does each account get its own balance?\n",
            "exportName": "makeAccount",
            "filename": "account.js",
            "starter": "// deposit(n) and withdraw(n) change the balance and return the new one.\n// balance() reads it. Nothing outside may reach or change it directly.\nfunction makeAccount(start) {\n\n}\n",
            "tests": [
              {
                "label": "starts at the opening balance",
                "body": "return subject(100).balance() === 100;"
              },
              {
                "label": "deposit adds and returns the new balance",
                "body": "const a = subject(100); return a.deposit(50) === 150 && a.balance() === 150;"
              },
              {
                "label": "withdraw subtracts",
                "body": "const a = subject(100); a.withdraw(30); return a.balance() === 70;"
              },
              {
                "label": "two accounts are independent",
                "body": "const a = subject(100), b = subject(100); a.deposit(50); return b.balance() === 100;"
              },
              {
                "label": "the three functions share one balance",
                "body": "const a = subject(0); a.deposit(10); a.withdraw(4); return a.balance() === 6;"
              },
              {
                "label": "the balance is not reachable as a property",
                "body": "const a = subject(100); return Object.keys(a).every(function (k) { return typeof a[k] === 'function'; });"
              },
              {
                "label": "assigning a property cannot change the balance",
                "body": "const a = subject(100); a.balance = 999; return subject(100).balance() === 100;"
              }
            ],
            "hints": [
              {
                "when": "the not-reachable test fails",
                "say": "You stored the balance as a property of the returned object, so anyone can read and overwrite it. Keep it as a local binding in `makeAccount` and let the three functions close over it."
              },
              {
                "when": "the two-accounts test fails",
                "say": "Your balance lives outside `makeAccount`, so every account shares one. It has to be declared inside the function, so each call creates its own."
              },
              {
                "when": "deposit returns undefined",
                "say": "The tests read the return value as well as calling `balance()`. Return the new balance from `deposit` and `withdraw`."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a simplified counter",
            "prompt": "An AI assistant \"simplified\" a counter factory. Page views and clicks now count together. Review it — answer each question — then fix it so the tests pass.\n",
            "filename": "counter.js",
            "code": "let count = 0;\n\nfunction makeCounter() {\n  return function () {\n    count = count + 1;\n    return count;\n  };\n}\n",
            "items": [
              {
                "id": "c1",
                "code": "const views = makeCounter(); const clicks = makeCounter(); views(); views();  →  clicks()",
                "accept": [
                  "3"
                ],
                "why": "There is one `count`, at the top level. Both counters were written to reach it, so `clicks` carries on from where `views` left it."
              },
              {
                "id": "c2",
                "question": "Which line needs to move? Give the line number.",
                "accept": [
                  "1",
                  "line 1"
                ],
                "why": "The declaration. Outside `makeCounter` it is made once, for the whole file; inside, it is made once per call — one per counter."
              },
              {
                "id": "c3",
                "question": "Moving it into the returned function instead: what value would `views(); views()` return the second time?",
                "accept": [
                  "1"
                ],
                "why": "`1`. Declared inside the returned function, `count` is made fresh on every call of `views`, so it never gets past 1."
              }
            ],
            "exportName": "makeCounter",
            "starter": "let count = 0;\n\nfunction makeCounter() {\n  return function () {\n    count = count + 1;\n    return count;\n  };\n}\n",
            "tests": [
              {
                "label": "a counter counts up",
                "body": "const c = subject(); c(); c(); return c() === 3;"
              },
              {
                "label": "two counters are independent",
                "body": "const a = subject(), b = subject(); a(); a(); return b() === 1;"
              },
              {
                "label": "a new counter starts from 1, whatever came before",
                "body": "const a = subject(); a(); a(); a(); return subject()() === 1;"
              }
            ],
            "hints": [
              {
                "when": "every call returns 1",
                "say": "You moved `count` into the returned function, so each call makes a new one. It belongs one level out — made once per call of `makeCounter`."
              },
              {
                "when": "the two-counters test fails",
                "say": "`count` is still made only once, for the whole file. Which function runs once per counter?"
              }
            ]
          }
        ]
      }
    },
    "loop-capture": {
      "id": "loop-capture",
      "title": "A closure keeps the binding, not the value",
      "kind": "read",
      "outcome": 4,
      "shape": "procedural",
      "why": "`scope.capture-semantics`' mastery is predicting a callback made inside a loop — the classic misprediction, and the one outcome 4's closures are most often tested on. It is three ideas at once (a closure, a loop form, and when the callback runs), which `01-explanations` rule 8 names as highly interacting — \"closures in loops\" — so it is taught explanation-first, one step at a time (D-029).",
      "concepts": [
        "scope.capture-semantics"
      ],
      "lede": "A function made in a loop does not remember what the loop variable held when it was made. It remembers the variable itself — so what it sees depends on how many of that variable the loop created, and what each holds when the function finally runs.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Closures keep their surroundings · What a declaration creates · How a loop gets its next value"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Capture** — a closure's hold on a binding from the scope it was written in. It holds the binding, not a copy of what the binding held.",
          "entries": [
            "**Capture** — a closure's hold on a binding from the scope it was written in. It holds the binding, not a copy of what the binding held."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on things you have already met. Type what each produces, or\n`error`. Retrieving them is what makes them stick, and this lesson is built from\nall three."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "function makeCounter() { let c = 0; return () => ++c; }  const a = makeCounter(); a(); a();  →  the second call",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1` — the returned function keeps `makeCounter`'s scope alive.** Each call of `a` changes the same `c`. Look back at how a closure keeps its surroundings.\n",
                          "why": "`2`. The closure kept `c`, and the second call found what the first left.\n"
                        },
                        {
                          "code": "for (var i = 0; i < 3; i++) {}  →  i",
                          "accept": [
                            "3"
                          ],
                          "miss": "**If you answered `error` — that is `let`.** `var` belongs to the enclosing function or file, so the counter outlives the loop, holding the value that ended it.\n",
                          "why": "`3`. One `var i` for the whole function; the loop stopped when it reached 3.\n"
                        },
                        {
                          "code": "for (let j = 0; j < 3; j++) {}  →  j",
                          "accept": [
                            "error",
                            "referenceerror",
                            "reference error",
                            "throws",
                            "an error",
                            "it throws"
                          ],
                          "miss": "**If you answered `3` — a `let` in a `for` header belongs to the loop.** After it there is no `j`. Look back at what a declaration creates.\n",
                          "why": "`ReferenceError`. The binding belonged to the loop.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can predict what a callback made inside a loop will see when it runs"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "A closure reads its binding when it runs",
          "body": [
            {
              "p": "You met the counter: the returned function keeps `count` and each call sees\nwhat the last one left. The same fact read from the other side: a closure does\n**not** take a snapshot of a value when it is made. It keeps the **binding**,\nand reads whatever the binding holds **at the moment the function runs**."
            },
            {
              "code": "function build() {\n  let label = \"before\";\n  const read = () => label;   // made now, while label is \"before\"\n  label = \"after\";            // the binding changes\n  return read();              // runs now — reads the binding: \"after\"\n}",
              "lang": "js"
            },
            {
              "p": "Picture `label` as one box. `read` holds an arrow to the box, not a copy of\nwhat was in it. When `read` runs, it follows the arrow and reads what is in the\nbox *then*."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "let x = 1; const f = () => x; x = 2;  →  f()",
                    "accept": [
                      "2"
                    ],
                    "miss": "**If you answered `1`, you pictured `f` copying `x` when it was made.** It keeps the binding. By the time `f` runs, the binding holds `2`.\n",
                    "why": "`2`. `f` reads `x` when it runs, and `x` was reassigned before that.\n"
                  },
                  {
                    "code": "let greeting = \"hi\"; const say = () => greeting + \" ada\"; greeting = \"bye\";  →  say()",
                    "accept": [
                      "\"bye ada\"",
                      "bye ada",
                      "'bye ada'"
                    ],
                    "why": "`\"bye ada\"`. Same binding, read later.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "A `var` loop has one binding for all its callbacks",
          "body": [
            {
              "p": "Now put the function inside a loop. The question that decides everything is:\n**how many bindings of the loop variable exist?**"
            },
            {
              "code": "const fns = [];\nfor (var i = 0; i < 3; i++) {\n  fns.push(() => i);\n}\nfns.map(f => f());   // [3, 3, 3]",
              "lang": "js"
            },
            {
              "p": "Take it slowly:"
            },
            {
              "list": [
                "`var i` is one binding for the whole function (or file). There is **one** box.",
                "Each iteration of the loop makes a new arrow function. All three are written in the same scope, so all three hold an arrow to the **same** box.",
                "The loop ends when `i` reaches 3. The box now holds `3`.",
                "Only now do the functions run. Each follows its arrow to the one box and reads `3`."
              ],
              "ordered": true
            },
            {
              "p": "Nothing went wrong while the functions were being made. The surprise comes\nlater, because they run after the loop has moved the binding on."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "for (var i = 0; i < 3; i++) fns.push(() => i);  →  how many `i` bindings exist?",
                    "accept": [
                      "1",
                      "one"
                    ],
                    "why": "One. `var` makes a single binding for the whole function, and all three closures point at it.\n"
                  },
                  {
                    "code": "const out = []; for (var k = 0; k < 2; k++) out.push(() => k * 10);  →  out[0]()",
                    "accept": [
                      "20"
                    ],
                    "miss": "**If you answered `0`, you read `out[0]` as keeping the `k` of the first iteration.** There is one `k`, and when `out[0]` runs it holds `2`.\n",
                    "why": "`20`. One `k`, holding `2` after the loop; `2 * 10`.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "`let` and `const` in a loop make one binding per iteration",
          "body": [
            {
              "p": "Change one keyword:"
            },
            {
              "code": "const fns = [];\nfor (let i = 0; i < 3; i++) {\n  fns.push(() => i);\n}\nfns.map(f => f());   // [0, 1, 2]",
              "lang": "js"
            },
            {
              "p": "A `let` in a `for` header is special-cased by the language: **each iteration of\nthe loop gets a fresh binding**, starting from the value the previous iteration\nended with. Three iterations, three boxes, and each function holds an arrow to its own. The\nsame is true of `for (const x of list)` — every iteration declares a new `x`."
            },
            {
              "p": "But the rule is about **where the binding is declared**, not about the word\n`let`. A binding declared *outside* the loop is still one box, however it is\ndeclared:"
            },
            {
              "code": "let n = 0;\nconst fns = [];\nfor (const x of [1, 2, 3]) {\n  n = n + x;\n  fns.push(() => n);    // reads n — declared outside the loop: one box\n}\nfns[0]();   // 6",
              "lang": "js"
            },
            {
              "p": "Each iteration has its own `x`, but these functions never read `x`. They read `n`,\nand there is one `n`."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const fns = []; for (let j = 0; j < 3; j++) fns.push(() => j);  →  fns.map(f => f())",
                          "accept": [
                            "[0,1,2]",
                            "[0, 1, 2]",
                            "0,1,2"
                          ],
                          "miss": "**If you answered `[3,3,3]` — that is `var`.** `let` in a `for` header makes a binding per iteration, and each closure keeps its own.\n",
                          "why": "`[0,1,2]`. Three iterations, three bindings, one per closure.\n"
                        },
                        {
                          "code": "let k = 0; const fns = []; while (k < 3) { fns.push(() => k); k++; }  →  fns.map(f => f())",
                          "accept": [
                            "[3,3,3]",
                            "[3, 3, 3]",
                            "3,3,3"
                          ],
                          "miss": "**If you answered `[0,1,2]`, you gave `k` a binding per iteration because it was declared with `let`.** It was declared *before* the loop, once. The per-iteration rule is for a binding declared in the loop.\n",
                          "why": "`[3,3,3]`. One `k`, declared outside the loop, holding `3` when they run.\n"
                        },
                        {
                          "code": "const fns = []; for (const name of [\"ada\", \"bo\"]) fns.push(() => \"hi \" + name);  →  fns[0]()",
                          "accept": [
                            "\"hi ada\"",
                            "hi ada",
                            "'hi ada'"
                          ],
                          "why": "`\"hi ada\"`. Each iteration of `for…of` declares a new `name`.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "fns=[lambda: i for i in range(3)]\\n[f() for f in fns]",
                          "accept": [
                            "[2, 2, 2]",
                            "[2,2,2]",
                            "2,2,2"
                          ],
                          "miss": "**If you answered `[0, 1, 2]` — the comprehension variable is one binding.** All three lambdas read it after the loop.\n",
                          "why": "Python's comprehension variable is one binding, so all three closures see its final value — `2`, because `range(3)` ends at 2. Same bug as `var`, different last number.\n"
                        },
                        {
                          "code": "fns=[lambda i=i: i for i in range(3)]\\n[f() for f in fns]",
                          "accept": [
                            "[0, 1, 2]",
                            "[0,1,2]",
                            "0,1,2"
                          ],
                          "why": "The default-argument trick captures the *value* at definition time. Python's answer to what `let` does in JavaScript.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "var f=new List<Func<int>>(); for(int i=0;i<3;i++) f.Add(()=>i);  →  results",
                          "accept": [
                            "[3,3,3]",
                            "3,3,3",
                            "[3, 3, 3]"
                          ],
                          "miss": "**If you answered `[0,1,2]` — a C# `for` loop has one `i`.** All three lambdas read it after the loop.\n",
                          "why": "The classic C# version of the same bug — one `i` for the whole loop.\n"
                        },
                        {
                          "code": "foreach (var x in new[]{0,1,2}) f.Add(()=>x);  →  results",
                          "accept": [
                            "[0,1,2]",
                            "0,1,2",
                            "[0, 1, 2]"
                          ],
                          "why": "Since C# 5, `foreach` declares a fresh variable per iteration — the language changed specifically because this bug was so common.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Callbacks that run later: the whole procedure",
          "body": [
            {
              "p": "Everything so far ran the functions straight after the loop. Real callbacks run\nlater still — when a timer fires, when a button is clicked — and the loop is\nalways long finished by then. That is why this bug is so common: the code that\nruns later looks as if it belongs to the iteration that wrote it."
            },
            {
              "p": "The procedure, for any function made in a loop:"
            },
            {
              "code": "// 1. Which name does the function read?  (the scope chain — where it is WRITTEN)\n// 2. How many bindings of that name exist?\n//      declared by var, or outside the loop   -> one, shared by every function\n//      declared by let/const in the loop       -> one per iteration\n// 3. What does that binding hold when the function RUNS — not when it was made?",
              "lang": "js",
              "file": "closure-check.js"
            },
            {
              "p": "A timer, worked:"
            },
            {
              "code": "for (var i = 0; i < 3; i++) {\n  setTimeout(() => console.log(i), 0);\n}",
              "lang": "js",
              "file": "timers.js"
            },
            {
              "code": "// find the name the callback reads\n//   i.\n// count the bindings of that name\n//   var i -> one, for the whole function.\n// say what it holds when the callback runs\n//   the timers fire after the loop has finished: 3, 3, 3."
            },
            {
              "p": "With `let i` the middle subgoal changes to \"one per iteration\", and the timers print\n0, 1, 2."
            },
            {
              "p": "A different shape — button handlers, in code of the kind you will meet in older\nprojects:"
            },
            {
              "code": "function makeButtons(labels) {\n  var buttons = [];\n  for (var i = 0; i < labels.length; i++) {\n    buttons.push({\n      label: labels[i],\n      onClick: function () { return \"clicked \" + labels[i]; },\n    });\n  }\n  return buttons;\n}",
              "lang": "js",
              "file": "buttons.js"
            },
            {
              "code": "// onClick reads i (in labels[i]). label: labels[i] is NOT a callback —\n//   it is evaluated immediately, while the object is built.\n// var i -> one binding of i.\n// A click comes after the loop: i is labels.length, one past the end.\n//   Every handler returns \"clicked undefined\"."
            },
            {
              "p": "The two `labels[i]` in that code look the same and do different things. The\none in `label:` runs **now**, on each iteration. The one in the function runs\n**later**, on a click. Only code inside a function made in the loop runs later."
            },
            {
              "p": "In code written before `let` existed you will also see this fix, which works by\nmaking a new *call* — and so a new binding — on each iteration:"
            },
            {
              "code": "for (var i = 0; i < 3; i++) {\n  (function (j) { setTimeout(() => console.log(j), 0); })(i);\n}",
              "lang": "js"
            },
            {
              "p": "`let` in the header does the same job in one keyword."
            },
            {
              "video": {
                "title": "Three closures, one binding, drawn",
                "caption": "The `var` loop with the single binding drawn once and three arrows pointing at it, then the `let` version with three separate bindings. Watch it if \"captures the binding, not the value\" is still just a sentence.\n"
              }
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "buttons.js: `makeButtons([\"save\", \"quit\"])[1].label`",
                    "accept": [
                      "\"quit\"",
                      "quit",
                      "'quit'"
                    ],
                    "miss": "**If you answered `undefined` — `label:` is not inside a function.** It was evaluated on the second iteration, while `i` was 1.\n",
                    "why": "`\"quit\"`. Read immediately, on its own iteration.\n"
                  },
                  {
                    "code": "buttons.js: `makeButtons([\"save\", \"quit\"])[1].onClick()`",
                    "accept": [
                      "\"clicked undefined\"",
                      "clicked undefined",
                      "'clicked undefined'"
                    ],
                    "miss": "**If you answered `\"clicked quit\"`, you let the handler keep the `i` of its iteration.** There is one `i`, and at click time it is `2`.\n",
                    "why": "`\"clicked undefined\"`. `labels[2]` does not exist.\n"
                  },
                  {
                    "code": "for (let t = 0; t < 2; t++) setTimeout(() => console.log(\"tick \" + t), 0);  →  the second line printed",
                    "accept": [
                      "\"tick 1\"",
                      "tick 1",
                      "'tick 1'"
                    ],
                    "why": "`\"tick 1\"`. `let` gave each iteration its own `t`, and each timer reads its own.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A closure keeps the **binding**, not the value: it reads what the binding holds **when the function runs**.",
                "For a function made in a loop, **count the bindings** of the name it reads. `var`, or a name declared outside the loop: **one**, shared. `let`/`const` declared in the loop: **one per iteration**.",
                "Callbacks run **after** the loop, so a shared binding shows its final value."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "let — in for loops",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/let",
              "note": "the per-iteration binding rule."
            },
            {
              "title": "Closures — creating closures in loops",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Closures#creating_closures_in_loops_a_common_mistake",
              "note": "the same mistake, with a form handler."
            },
            {
              "title": "IIFE",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/IIFE",
              "note": "the pre-`let` fix, worth recognising in older code."
            }
          ]
        }
      ],
      "_source": "lessons/loop-capture.md",
      "practice": {
        "id": "loop-capture",
        "outcome": 4,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict, then count the bindings",
            "prompt": "Type what the first produces. For the other two, give a number.\n",
            "items": [
              {
                "id": "p1",
                "code": "const f=[]; for (var i=0;i<3;i++) f.push(()=>i);  →  f.map(g=>g())",
                "accept": [
                  "[3,3,3]",
                  "[3, 3, 3]",
                  "3,3,3"
                ],
                "why": "One `var` binding for the whole function. All three closures point at it, and it holds `3` once the loop ends."
              },
              {
                "id": "p0",
                "code": "for (var i = 0; i < 3; i++) fns.push(() => i);  →  how many `i` bindings exist?",
                "accept": [
                  "1",
                  "one"
                ],
                "why": "One — `var` makes a single binding for the whole function, and all three closures point at it. Counting the bindings is the first step for any callback made in a loop."
              },
              {
                "id": "p2",
                "code": "for (let j = 0; j < 3; j++) fns.push(() => j);  →  how many `j` bindings exist?",
                "accept": [
                  "3",
                  "three"
                ],
                "why": "`let` in a `for` header makes a fresh binding per iteration — three iterations, three bindings, one per closure."
              }
            ],
            "extra": [
              {
                "id": "p3",
                "code": "const f=[]; for (let j=0;j<3;j++) f.push(()=>j);  →  f.map(g=>g())",
                "accept": [
                  "[0,1,2]",
                  "[0, 1, 2]",
                  "0,1,2"
                ],
                "why": "Each closure captured a different binding, so each reads the value from its own iteration."
              },
              {
                "id": "p4",
                "code": "function b(){ let v=\"before\"; const r=()=>v; v=\"after\"; return r(); }  →  b()",
                "accept": [
                  "\"after\"",
                  "after",
                  "'after'"
                ],
                "why": "The closure reads the binding when it *runs*, not when it was created — the loop case from the other direction."
              },
              {
                "id": "p5",
                "code": "let x = 1; const f = () => x; x = 5;  →  f()",
                "accept": [
                  "5"
                ],
                "why": "`f` keeps `x` itself. By the time it runs, `x` holds 5."
              }
            ],
            "hints": [
              {
                "when": "you answered [0,1,2] for the first",
                "say": "You pictured each closure keeping `i` as it was on its iteration. It keeps the binding. How many `i` bindings does `var` make for the whole loop?"
              },
              {
                "when": "you answered 3 for the second",
                "say": "Three closures, but ask which scope `var i` belongs to — and how many times that scope was entered."
              },
              {
                "when": "you answered 1 for the third",
                "say": "That is `var`. What does a `let` in a `for` header give each iteration?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict six lines, then run them",
            "prompt": "Predict every line the program prints, in order — type each line as it would appear — then run it. The timers print last — they fire after everything else has run.\n",
            "code": "const later = [];\nfor (var i = 0; i < 2; i++) later.push(() => \"var \" + i);\nfor (let j = 0; j < 2; j++) later.push(() => \"let \" + j);\nfor (const f of later) console.log(f());\n\nfor (var k = 0; k < 2; k++) {\n  setTimeout(() => console.log(\"timer \" + k), 0);\n}\n",
            "items": [
              {
                "id": "r1",
                "question": "Line 1 printed",
                "accept": [
                  "var 2",
                  "\"var 2\""
                ],
                "why": "One `var i`, holding `2` by the time the functions run."
              },
              {
                "id": "r2",
                "question": "Line 2 printed",
                "accept": [
                  "var 2",
                  "\"var 2\""
                ],
                "why": "The same binding, read again. Both `var` closures share it."
              },
              {
                "id": "r3",
                "question": "Line 3 printed",
                "accept": [
                  "let 0",
                  "\"let 0\""
                ],
                "why": "The first iteration's own `j`, which was 0."
              },
              {
                "id": "r4",
                "question": "Line 4 printed",
                "accept": [
                  "let 1",
                  "\"let 1\""
                ],
                "why": "The second iteration's own `j`."
              },
              {
                "id": "r5",
                "question": "Line 5 printed",
                "accept": [
                  "timer 2",
                  "\"timer 2\""
                ],
                "why": "The timers fire after the loop has finished. One `var k`, holding 2."
              },
              {
                "id": "r6",
                "question": "Line 6 printed",
                "accept": [
                  "timer 2",
                  "\"timer 2\""
                ],
                "why": "Same binding, same value. A delay does not change *which* binding was kept — only how long the loop has had to move it on."
              }
            ],
            "hints": [
              {
                "when": "you answered \"var 0\" for line 1",
                "say": "The function reads `i` when it runs, in the last loop — not when it was pushed. What does the one `var i` hold by then?"
              },
              {
                "when": "you answered \"timer 0\" for line 5",
                "say": "Count the bindings of `k` first. Then ask whether the loop has finished before any timer fires."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Count the bindings",
            "prompt": "Give what each produces. Count the bindings of the name the function reads before you answer.",
            "items": [
              {
                "id": "t1",
                "code": "let k = 0; const fs = []; while (k < 3) { fs.push(() => k); k++; }  →  fs.map(f => f())",
                "accept": [
                  "[3,3,3]",
                  "[3, 3, 3]",
                  "3,3,3"
                ],
                "why": "`k` is declared once, before the loop. The per-iteration rule is for a binding declared *in* the loop, so all three share one `k`."
              },
              {
                "id": "t2",
                "code": "let n = 0; const fs = []; for (const x of [1, 2, 3]) { n = n + x; fs.push(() => n); }  →  fs[0]()",
                "accept": [
                  "6"
                ],
                "why": "`n` is declared outside the loop, so there is one `n` and every closure shares it. By the time `fs[0]` runs, the loop has added all three."
              }
            ],
            "extra": [
              {
                "id": "t3",
                "code": "const fs = []; for (const x of [1, 2, 3]) { fs.push(() => x * 2); }  →  fs[0]()",
                "accept": [
                  "2"
                ],
                "why": "Here the function reads `x`, and `for…of` declares a new `x` each iteration. The first function keeps the first `x`."
              },
              {
                "id": "t4",
                "code": "const fs = []; for (let i = 0; i < 3; i++) { const sq = i * i; fs.push(() => sq); }  →  fs[2]()",
                "accept": [
                  "4"
                ],
                "why": "`sq` is declared inside the loop body, so each iteration makes a new one. The third function keeps the third `sq`, which is 4."
              },
              {
                "id": "t5",
                "code": "const fs = []; for (var i = 0; i < 3; i++) { var sq = i * i; fs.push(() => sq); }  →  fs[0]()",
                "accept": [
                  "4"
                ],
                "why": "`var sq` is one binding for the whole function, even though it is written in the loop body. Every closure reads the last value, 4."
              }
            ],
            "hints": [
              {
                "when": "you answered [0,1,2] for the first",
                "say": "Find the line that declares `k`. Is it inside the loop or before it — and how many times does that line run?"
              },
              {
                "when": "you answered 1 for the second",
                "say": "The loop variable here is `x`, and it is fresh per iteration — but the closure does not read `x`. Where is `n` declared, and how many of it are there?"
              },
              {
                "when": "you answered 0 for the last extra",
                "say": "Being written inside the loop is not enough. Which keyword declares `sq`, and what region does it belong to?"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Give each greeter its position",
            "prompt": "`makeGreeters` works: `makeGreeters([\"ada\", \"bo\"])[0]()` is `\"hi ada\"`. Change one behaviour — each greeter must also say where it sits: `\"hi ada (1 of 2)\"`, `\"hi bo (2 of 2)\"`. Then, in one sentence: how many bindings hold a position in your version, and why does that matter?\n",
            "exportName": "makeGreeters",
            "filename": "greeters.js",
            "starter": "// makeGreeters([\"ada\", \"bo\"])[0]() is \"hi ada\".\nfunction makeGreeters(names) {\n  const greeters = [];\n  for (const name of names) {\n    greeters.push(() => \"hi \" + name);\n  }\n  return greeters;\n}\n",
            "tests": [
              {
                "label": "each greeter says its own position",
                "body": "return subject(['ada', 'bo', 'cy']).map(function (g) { return g(); }).join('|') === 'hi ada (1 of 3)|hi bo (2 of 3)|hi cy (3 of 3)';"
              },
              {
                "label": "a single name is 1 of 1",
                "body": "return subject(['ada'])[0]() === 'hi ada (1 of 1)';"
              },
              {
                "label": "the first greeter does not report the last position",
                "body": "return subject(['a', 'b'])[0]() === 'hi a (1 of 2)';"
              },
              {
                "label": "no names, no greeters",
                "body": "return subject([]).length === 0;"
              },
              {
                "label": "calling a greeter twice gives the same answer",
                "body": "const g = subject(['a', 'b'])[1]; return g() === g() && g() === 'hi b (2 of 2)';"
              }
            ],
            "hints": [
              {
                "when": "every greeter says the last position",
                "say": "Your position counter is one binding declared outside the loop, and every greeter closed over it. By the time any greeter runs, the loop has finished counting. Each greeter needs a binding of its own."
              },
              {
                "when": "you switched to a `for` loop with `var` and got \"undefined\"",
                "say": "`var i` is one binding for the whole function, so every greeter reads the value it had after the loop ended — one past the last name. Which keyword gives a `for` header a fresh binding per iteration?"
              },
              {
                "when": "calling a greeter twice changes its answer",
                "say": "The greeter is doing the counting when it runs. Work the position out while building the greeter, and let the greeter only read it."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a toolbar builder",
            "prompt": "An AI assistant generated this to build toolbar buttons. Every button's click handler reports the wrong label. Review it — answer each question — then fix it so the tests pass.\n",
            "filename": "buttons.js",
            "code": "function makeButtons(labels) {\n  var buttons = [];\n  for (var i = 0; i < labels.length; i++) {\n    buttons.push({\n      label: labels[i],\n      onClick: function () { return \"clicked \" + labels[i]; },\n    });\n  }\n  return buttons;\n}\n",
            "items": [
              {
                "id": "c1",
                "code": "makeButtons([\"save\", \"quit\"])[0].onClick()",
                "accept": [
                  "clicked undefined",
                  "\"clicked undefined\"",
                  "'clicked undefined'"
                ],
                "why": "Every handler reads the one `i`, which is `2` once the loop ends — and `labels[2]` does not exist."
              },
              {
                "id": "c2",
                "question": "Which line reads a binding that has moved on by the time it runs? Give the line number.",
                "accept": [
                  "6",
                  "line 6"
                ],
                "why": "Line 6's function runs later, on a click, and reads `i` then. All the handlers share the single `var i` from line 3."
              },
              {
                "id": "c3",
                "question": "Line 5 also reads `labels[i]`. Is the value it stores right?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. Line 5 is evaluated immediately, while the object is built, so it reads `i` as it is on that iteration. Only a *function* created in the loop reads `i` later."
              },
              {
                "id": "c4",
                "question": "Which line needs the fix? Give the line number.",
                "accept": [
                  "3",
                  "line 3"
                ],
                "why": "The loop header. With a block-scoped counter, each iteration gets its own binding and each handler keeps its own."
              }
            ],
            "exportName": "makeButtons",
            "starter": "function makeButtons(labels) {\n  var buttons = [];\n  for (var i = 0; i < labels.length; i++) {\n    buttons.push({\n      label: labels[i],\n      onClick: function () { return \"clicked \" + labels[i]; },\n    });\n  }\n  return buttons;\n}\n",
            "tests": [
              {
                "label": "the first button reports its own label",
                "body": "return subject(['save', 'quit'])[0].onClick() === 'clicked save';"
              },
              {
                "label": "every button reports its own label",
                "body": "return subject(['a', 'b', 'c']).map(function (b) { return b.onClick(); }).join(',') === 'clicked a,clicked b,clicked c';"
              },
              {
                "label": "labels are still stored",
                "body": "return subject(['a', 'b'])[1].label === 'b';"
              },
              {
                "label": "no labels, no buttons",
                "body": "return subject([]).length === 0;"
              }
            ],
            "hints": [
              {
                "when": "you changed line 2 and the handlers still say undefined",
                "say": "`buttons` was never the problem — it is only read inside the loop. Which binding do the handlers read *after* the loop has finished?"
              },
              {
                "when": "you stored the label in a second `var` inside the loop",
                "say": "A `var` inside the loop body is still one binding for the whole function, so it ends up holding the last label. The declaration needs a keyword that makes one binding per iteration."
              }
            ]
          }
        ]
      }
    },
    "make-greeter": {
      "id": "make-greeter",
      "title": "Return a function that remembers",
      "kind": "trial",
      "kicker": "EXERCISE 2.5",
      "filename": "greeter.js",
      "exportName": "makeGreeter",
      "brief": "Write `makeGreeter(name)` so the function it returns greets that name every time — even after the outer call is long gone.",
      "hint": "The inner function does not need a parameter.",
      "success": "Every trial passes. Your solution captured `name` by reference, which is exactly the point.",
      "starter": "// your solution\nfunction makeGreeter(name) {\n\n}\n",
      "tests": [
        {
          "label": "makeGreeter('Zel')() → 'Hello, Zel!'",
          "body": "return subject('Zel')() === 'Hello, Zel!';"
        },
        {
          "label": "two greeters keep separate names",
          "body": "var a = subject('Ada'), b = subject('Bo'); return a() === 'Hello, Ada!' && b() === 'Hello, Bo!';"
        },
        {
          "label": "inner fn takes no arguments",
          "body": "return subject('Zel').length === 0;"
        }
      ],
      "_source": "trials/make-greeter.yml"
    },
    "method-receiver": {
      "id": "method-receiver",
      "title": "How a method finds its object",
      "kind": "read",
      "outcome": 5,
      "shape": "conceptual",
      "why": "Once methods are passed as values, JavaScript's `this` decides whether they still work: a method handed to a caller that calls it bare loses its object, the failure every callback-heavy codebase meets. D-011 makes this its own concept; Python and C# students meet it only as a contrast (D-016).",
      "concepts": [
        "fn.receiver"
      ],
      "lede": "A method reads its object through `this`. In JavaScript, `this` is not fixed when the method is written — it is supplied by each call. Hand the method to code that calls it bare, and the object does not come with it.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Who calls a callback, and when · Closures keep their surroundings · Designing a type"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Receiver** — the object a method is called on; inside the method it is `this`.",
          "entries": [
            "**Receiver** — the object a method is called on; inside the method it is `this`."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "One object, four calls. Type what each produces, or pick where there are\nbuttons."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const obj = { n: 7, get() { return this.n; } };   obj.get()",
                          "accept": [
                            "7"
                          ],
                          "why": "Called as `obj.get()`, so inside `get`, `this` is `obj` and `this.n` is `7`.\n"
                        },
                        {
                          "code": "const obj = { n: 7, get() { return this.n; } };   const loose = obj.get;   loose()",
                          "accept": [
                            "undefined",
                            "TypeError",
                            "error",
                            "a TypeError",
                            "throws"
                          ],
                          "miss": "**If you answered `7`, you stored the *function* and expected the object to come with it.** In JavaScript `this` is decided by **how a function is called**, not where it was written. `loose()` has nothing in front of it, so there is no object.\n",
                          "why": "Taking a method out of its object leaves `this` behind. In a module this throws `TypeError`; in a plain script `this` is the global object and you get `undefined` instead. Either way the receiver is gone.\n"
                        },
                        {
                          "code": "const obj = { n: 7, get() { return this.n; } };   const other = { n: 1, get: obj.get };   other.get()",
                          "accept": [
                            "1"
                          ],
                          "miss": "**If you answered `7` — the function was written inside `obj`, but it is called as `other.get()`.** The object in front of the dot at the call is the one `this` gets.\n",
                          "why": "The same function, stored on a second object and called through it. `this` is `other`.\n"
                        },
                        {
                          "code": "const c = { n: 0, bump() { this.n++; } };   [1, 2].forEach(() => c.bump());   c.n",
                          "accept": [
                            "2"
                          ],
                          "why": "`forEach` calls the arrow; the arrow makes a method call, `c.bump()`, each time — so `c` is in front of the dot on every call.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "class C:\\n    n = 7\\n    def get(self): return self.n\\n\\nloose = C().get\\nloose()",
                          "choices": [
                            "7",
                            "error"
                          ],
                          "accept": [
                            "7"
                          ],
                          "why": "Different from JavaScript: `C().get` is a *bound* method that keeps its instance. Python binds at attribute access; JavaScript binds at call time.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "var c = new C(); Func<int> loose = c.Get;  loose()",
                          "choices": [
                            "7",
                            "error"
                          ],
                          "accept": [
                            "7"
                          ],
                          "why": "A method group converted to a delegate **keeps its target**. Like Python, unlike JavaScript.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can pass a method as a value and predict whether it keeps its object",
                "I can make a method safe to hand to code that calls it bare"
              ]
            }
          ]
        },
        {
          "h": "Calling decides `this`"
        },
        {
          "p": "Inside a method, `this` is the object it is working on — its **receiver**. The\nquestion is where that object comes from, and in JavaScript the answer is: from\nthe call."
        },
        {
          "code": "const obj = {\n  n: 7,\n  get() { return this.n; },\n};\n\nobj.get();               // 7  — called as a method, so `this` is obj\nconst loose = obj.get;\nloose();                 // TypeError in a module — `this` is undefined",
          "lang": "js"
        },
        {
          "p": "The rule: **`this` comes from the call site.** `obj.get()` supplies `obj`;\n`loose()` supplies nothing. `const loose = obj.get` copied a reference to the\nfunction — the function never belonged to `obj`, it was only stored there."
        },
        {
          "p": "The last lesson said a callback is called by whoever you hand it to. Put the two\ntogether and you have the failure this lesson exists for: hand a method to a\ncaller that calls it bare, and it loses its object."
        },
        {
          "p": "Two worked examples, with the same three subgoals."
        },
        {
          "code": "const counter = { n: 0, bump() { this.n++; } };\n\ncounter.bump();\n// find the call that runs it      → counter.bump()\n// look left of the dot            → counter\n// read the body with it as `this` → counter.n becomes 1\n\nconst other = { n: 10, bump: counter.bump };\nother.bump();\n// other.bump(): other is left of the dot, so other.n becomes 11; counter is untouched",
          "lang": "js",
          "file": "counter.js"
        },
        {
          "code": "const cart = { items: [], add(item) { this.items.push(item); } };\n\n[\"pen\", \"ink\"].forEach(cart.add);\n// find the call that runs it      → not here. forEach runs it, as fn(value, index, array)\n// look left of the dot            → nothing is left of fn\n// read the body with it as `this` → no object: TypeError, reading `items` of undefined",
          "lang": "js",
          "file": "cart.js"
        },
        {
          "p": "In the second example the line you wrote, `forEach(cart.add)`, has a dot in it,\nand that is the trap. The dot there only *reads* the function out of `cart`. The\ncall that decides `this` is the one inside `forEach`, and you have to picture\nit."
        },
        {
          "p": "What does the first line return? Then answer the second."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const dog = { name: \"Rex\", speak() { return this.name; } };   const cat = { name: \"Tom\", speak: dog.speak };   cat.speak()",
                "accept": [
                  "\"Tom\"",
                  "Tom",
                  "'Tom'"
                ],
                "miss": "**If you answered `\"Rex\"` — look at the call, not at where `speak` was written.** `cat` is in front of the dot.\n",
                "why": "`cat.speak()` — `this` is `cat`.\n"
              },
              {
                "question": "With `function run(fn) { return fn(); }`, the call is `run(dog.speak)`. Inside `speak`, is `this` the dog?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "`run` calls `fn()` with nothing in front of it. The receiver is lost — `dog.speak` only read the function out.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Deciding whether you need to bind",
            "body": [
              {
                "h": "Deciding whether you need to bind"
              },
              {
                "code": "// You are about to pass a function somewhere else.\n\n// 1. Does its body use `this`?\n//    no  -> pass it directly. Nothing to lose.\n//    yes -> keep going.\n\n// 2. Is it an arrow function defined inside the object's scope?\n//    yes -> it has no `this` of its own and already closes over the right one.\n//    no  -> keep going.\n\n// 3. Bind it, wrap it, or make it an arrow:\n//    obj.method.bind(obj)        // fixes the receiver\n//    (...a) => obj.method(...a)  // calls it as a method every time",
                "lang": "js",
                "file": "need-bind.js"
              },
              {
                "p": "Now apply it. Which of these work?"
              },
              {
                "code": "const counter = {\n  n: 0,\n  bump()      { this.n++; },\n  bumpArrow: () => { this.n++; },\n};\n\n[1, 2].forEach(counter.bump);        // ______\n[1, 2].forEach(() => counter.bump()); // ______\ncounter.bumpArrow();                  // ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "The first loses `this`. The second works, because `counter.bump()` is a method\ncall every time. The third never worked: an arrow in an object literal closes\nover the *surrounding* scope, not the object."
              }
            ]
          }
        },
        {
          "h": "Three ways to keep the object"
        },
        {
          "p": "Every fix does the same thing: make sure that, whoever calls the function and\nhowever, the object arrives."
        },
        {
          "code": "items.forEach(x => obj.handle(x));   // 1. a wrapper: a method call, every time\nconst handler = obj.handle.bind(obj); // 2. bind: a new function with `this` fixed\nclass C { handle = () => { } }        // 3. a class-field arrow: bound per instance",
          "lang": "js"
        },
        {
          "list": [
            "**A wrapper** is the clearest. The arrow is what gets called bare; it doesn't use `this`, and it makes a proper method call itself.",
            "**`bind`** returns a **new** function with the receiver fixed for good. It creates a new function every time you call it, so two `bind` calls give two different functions — which matters whenever something compares references.",
            "**A class-field arrow** is an arrow created per instance, closing over that instance, so there is no receiver to lose."
          ]
        },
        {
          "p": "Prefer the first. Reach for `bind` when you need a single reference to hand out\nand later hand back."
        },
        {
          "p": "What does each line produce?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const obj = { n: 7, get() { return this.n; } };   obj.get.bind(obj)()",
                "accept": [
                  "7"
                ],
                "why": "`bind` fixed the receiver, so even a bare call uses `obj`.\n"
              },
              {
                "code": "obj.get.bind(obj) === obj.get.bind(obj)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` — each `bind` call makes a new function.** Same behaviour, two different values.\n",
                "why": "Two calls to `bind`, two new functions. `===` compares identity.\n"
              }
            ]
          }
        },
        {
          "video": {
            "title": "One method, three call sites",
            "caption": "One method stored on an object, then called as a method, extracted and called bare, and handed to `forEach` — with the receiver shown at each call site so you can see where `this` comes from. Watch it if the second opening answer surprised you.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **when is a method bound to its object?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "A method extracted from an object"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "**loses its receiver** — bound at each call"
                  ],
                  [
                    "**Python**",
                    "keeps it — bound when the attribute is read"
                  ],
                  [
                    "**C#**",
                    "keeps it — the delegate holds the target"
                  ]
                ]
              }
            },
            {
              "p": "Of the three, JavaScript is the one that binds at **call time**. That is not an\noversight: it is what makes `call`, `apply` and `bind` possible, and what lets\none function serve as a method on many objects."
            },
            {
              "p": "The cost is that passing a method around is not safe by default, and every\nJavaScript codebase has a convention for dealing with it. When you meet a new\nlanguage, \"does extracting a method keep its receiver?\" is worth asking early."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "No ceremony to pass a function, and no protection when it is a method. The\nconventions you will meet in real code: an arrow wrapper at the call site for a\none-off callback, and a class-field arrow for a method a class hands out\nrepeatedly."
              }
            ],
            "python": [
              {
                "p": "Functions are objects and methods stay bound, so passing them around is safe."
              },
              {
                "code": "items = [1, 2]\nlist(map(obj.handle, items))   # works — obj.handle is bound",
                "lang": "python"
              },
              {
                "p": "`functools.partial` is the equivalent of `bind` for pre-supplying arguments, and\ndecorators are higher-order functions with syntax support."
              }
            ],
            "csharp": [
              {
                "p": "Delegates give the same power with a declared type, and method groups keep their\ntarget."
              },
              {
                "code": "Func<int,int> f = x => x * 2;\nitems.Select(obj.Handle);       // method group; keeps obj",
                "lang": "csharp"
              },
              {
                "p": "`Action` for no return value, `Func<>` for one, `Predicate<T>` for a bool. The\ntypes are the main difference you will feel coming from JavaScript."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say what `obj.get.bind(obj)()`\ngives, and what that tells you about where `this` comes from."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "This example uses the **browser's** `addEventListener` and\n`removeEventListener` — only as a caller that stores your function, calls it\nbare later, and removes it by reference. Nothing about them is the point."
            },
            {
              "p": "A teammate writes `button.addEventListener(\"click\", this.handleClick)` inside a\nclass, and the handler throws because `this` is undefined."
            },
            {
              "p": "Three fixes:"
            },
            {
              "list": [
                "**`bind` in the constructor** — `this.handleClick = this.handleClick.bind(this)`",
                "**A class field arrow** — `handleClick = () => { }`",
                "**A wrapper at the call site** — `e => this.handleClick(e)`"
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs, and specifically: which of the\nthree lets you later call `removeEventListener(\"click\", …)` with the same\nreference — it removes only a function `===` to the one added — and why that\nrules one of them out unless you store it."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`this` comes from the call site**: the object in front of the dot when the function is *called*, not where it was written.",
                "A method handed to a caller that calls it bare **loses its receiver**.",
                "A wrapper, `bind` or a class-field arrow keeps it; `bind` makes a **new** function each time."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "this",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/this",
              "note": "the call-site rule, spelled out for each way a function can be called."
            },
            {
              "title": "Function.prototype.bind()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/bind",
              "note": "and the note that it returns a *new* function each time."
            }
          ]
        }
      ],
      "_source": "lessons/method-receiver.md",
      "practice": {
        "id": "method-receiver",
        "outcome": 5,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the result",
            "prompt": "Three quick ones. Say what the first two produce (pick, or type), then type the one word the third asks for.",
            "items": [
              {
                "id": "p1",
                "code": "const obj = { n: 7, get() { return this.n; } };  const loose = obj.get;  loose()",
                "accept": [
                  "undefined",
                  "TypeError",
                  "error",
                  "a TypeError",
                  "throws"
                ],
                "why": "Extracting a method leaves `this` behind. In a module this throws; in a script `this` is the global and you get `undefined`. Either way the receiver is gone."
              },
              {
                "id": "p2",
                "code": "const other = { n: 1, get: obj.get };  other.get()",
                "accept": [
                  "1"
                ],
                "why": "The same function, stored on a second object. Called as `other.get()`, the object in front of the dot is `other` — so `this.n` is `1`, not `7`."
              },
              {
                "id": "p3",
                "question": "You are about to write `[1, 2].forEach(logger.write)`. Which one word in the body of `logger.write` do you look for first, to know whether that is safe?",
                "accept": [
                  "this",
                  "`this`"
                ],
                "why": "Step one of the bind check: if the body never uses `this`, there is no receiver to lose and passing it directly is safe. If it does, keep going."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "obj.get.bind(obj)()",
                "accept": [
                  "7"
                ],
                "why": "`bind` fixes the receiver permanently, so the function carries its object wherever it goes."
              },
              {
                "id": "p5",
                "code": "obj.get.bind(obj) === obj.get.bind(obj)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "Each `bind` returns a new function. Same behaviour, two different values."
              },
              {
                "id": "p6",
                "code": "const c = { n: 0, bump() { this.n++; } };   [1, 2].forEach(() => c.bump());   c.n",
                "accept": [
                  "2"
                ],
                "why": "The arrow makes a method call, `c.bump()`, each time — so the receiver is supplied at the call."
              }
            ],
            "hints": [
              {
                "when": "you answered 7 for the first",
                "say": "`this` comes from the *call site*, not from where the function was written. `loose()` has no object in front of it."
              },
              {
                "when": "you answered 7 for the second",
                "say": "Look at what is in front of the dot at the call. `other.get()` supplies `other`, whatever object the function was first written inside."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Run one function on two objects",
            "prompt": "Predict each line this prints, then run it. One function is stored on two objects.",
            "code": "const tally = {\n  name: \"tally\",\n  n: 0,\n  bump() {\n    this.n = this.n + 1;\n    return this.name;\n  },\n};\nconst panel = { name: \"panel\", n: 10, bump: tally.bump };\n\nconsole.log(tally.bump());\nconsole.log(panel.bump());\nconsole.log(tally.n, panel.n);\nconsole.log(panel.bump === tally.bump);\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print?",
                "accept": [
                  "tally",
                  "\"tally\""
                ],
                "why": "`tally.bump()` — `tally` is in front of the dot, so `this` is `tally`."
              },
              {
                "id": "r2",
                "question": "What does the second line print?",
                "accept": [
                  "panel",
                  "\"panel\""
                ],
                "why": "`panel.bump` holds the very function written inside `tally`, but it was called as `panel.bump()` — so `this` is `panel`. Where it was written does not matter."
              },
              {
                "id": "r3",
                "question": "What does the third line print?",
                "accept": [
                  "1 11",
                  "1, 11",
                  "1,11"
                ],
                "why": "Each call bumped the `n` of the object it was called on: `tally` went 0 → 1 and `panel` went 10 → 11."
              },
              {
                "id": "r4",
                "question": "What does the fourth line print?",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "There is only one function. Storing it on `panel` copied the reference, not the function — a function is a value, and values that are objects are shared (as with arrays)."
              }
            ],
            "hints": [
              {
                "when": "you predicted tally for the second line",
                "say": "You attached the function to the object it was written in. JavaScript does not: find the object in front of the dot on the line that *calls* it."
              },
              {
                "when": "you predicted false for the last line",
                "say": "Did anything create a second function? `bump: tally.bump` copies a reference, exactly as `const b = a` does with an array."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace where this comes from",
            "prompt": "`const counter = { n: 0, bump() { this.n++; } };` For each call, pick the object that `this` is inside `bump` — or `none` if the call supplies no object.\n",
            "items": [
              {
                "id": "t1",
                "code": "counter.bump()",
                "choices": [
                  "counter",
                  "none"
                ],
                "accept": [
                  "counter"
                ],
                "why": "A method call: the object in front of the dot is the receiver."
              },
              {
                "id": "t2",
                "code": "[1, 2].forEach(counter.bump)   →   inside each call",
                "choices": [
                  "counter",
                  "none"
                ],
                "accept": [
                  "none"
                ],
                "why": "`forEach` calls it as `fn(value, index, array)`. There is no object in front of `fn`, so the receiver is lost and `counter.n` stays `0`."
              },
              {
                "id": "t3",
                "code": "[1, 2].forEach(() => counter.bump())   →   inside each call",
                "choices": [
                  "counter",
                  "none"
                ],
                "accept": [
                  "counter"
                ],
                "why": "The arrow is what `forEach` calls; the arrow then makes a *method call*, `counter.bump()`, each time — so the receiver is supplied at the call site."
              },
              {
                "id": "t4",
                "code": "const b = counter.bump.bind(counter);  b()",
                "choices": [
                  "counter",
                  "none"
                ],
                "accept": [
                  "counter"
                ],
                "why": "`bind` returns a new function with the receiver fixed. Calling it bare still uses `counter`."
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "const other = { n: 5, bump: counter.bump };  other.bump()",
                "choices": [
                  "counter",
                  "other",
                  "none"
                ],
                "accept": [
                  "other"
                ],
                "why": "The object in front of the dot at the call is `other`. `counter.n` is untouched; `other.n` becomes `6`."
              },
              {
                "id": "t6",
                "question": "An arrow in the object literal, `{ n: 0, bumpArrow: () => { this.n++; } }`, called as `counter.bumpArrow()`. Is `this` the counter?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "An arrow has no `this` of its own — it uses the surrounding scope's, which is not the object. Arrows are wrong for methods and right for callbacks."
              }
            ],
            "hints": [
              {
                "when": "you said counter for the forEach line",
                "say": "Write out what `forEach` actually does with the function it was given: `fn(value, index, array)`. Is there an object in front of `fn`?"
              },
              {
                "when": "you said none for the arrow line",
                "say": "Two functions are involved. `forEach` calls the arrow with no receiver — fine, the arrow does not use `this`. Then look at how the arrow calls `bump`."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Hand a method to something that calls it bare",
            "prompt": "`button` is given. `button.on(fn)` stores `fn`; `button.click()` calls every stored function as `fn()` — no object in front; `button.off(fn)` removes `fn` only if it is the *same reference* (`===`). Build a program that, in this order: makes a counter, makes one handler for it, registers it, clicks, unregisters it, clicks again, and prints the count. It must print `1`.\n",
            "lines": [
              "const counter = { clicks: 0, handle() { this.clicks++; } };",
              "const handler = counter.handle.bind(counter);",
              "button.on(handler);",
              "button.click();",
              "button.off(handler);",
              "button.click();",
              "console.log(counter.clicks);"
            ],
            "distractors": [
              {
                "line": "button.on(counter.handle);",
                "why": "This hands over the function without its object. `click()` calls it bare, so `this.clicks` is not the counter's and the program prints `0`."
              },
              {
                "line": "button.off(counter.handle.bind(counter));",
                "why": "`bind` returns a *new* function every time it is called. This one is not `===` to the handler you registered, so nothing is removed and the program prints `2`."
              }
            ],
            "why": "`bind` once, keep the reference, and use that same reference to register and unregister. A bound function is a new value, so bind it where you can keep hold of it.",
            "hints": [
              {
                "when": "your program prints 0",
                "say": "Something registered the method without its object. Which line hands `button` a function that still knows about `counter`?"
              },
              {
                "when": "your program would print 2",
                "say": "The second click still counted, so `off` removed nothing. `off` compares by reference — is the function you remove the very one you added?"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a price list an AI tool wrote",
            "prompt": "Someone asked an AI tool for \"a price list that can list the cheap items and give a repriced copy\". This is what came back. It has three defects. Find them — then, below, fix all three.\n",
            "filename": "price-list.js",
            "code": "// A price list: cheap() lists items under the limit;\n// repriced(rule) gives a copy with every price passed through rule.\nfunction makePriceList(items, limit) {\n  return {\n    items,\n    limit,\n    isCheap(item) {\n      return item.price < this.limit;\n    },\n    cheap() {\n      return this.items.filter(this.isCheap);\n    },\n    repriced(rule) {\n      return this.items.map(item => {\n        item.price = rule(item.price);\n        return item;\n      });\n    },\n  };\n}\n\nconst halve = price => price / 2;\nconst list = makePriceList([{ name: \"pen\", price: 2 }, { name: \"ink\", price: 9 }], 5);\nconst sale = list.repriced(halve());\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line makes `list.cheap()` come back wrong? Type the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "`this.isCheap` is handed to `filter` without its object. `filter` calls it bare, so inside `isCheap`, `this.limit` is not the list's limit — in a module it throws; in a script every comparison is `false` and you silently get `[]`."
              },
              {
                "id": "c2",
                "question": "On that line, what does `isCheap` lose? Type one word.",
                "accept": [
                  "this",
                  "receiver",
                  "its receiver",
                  "the receiver",
                  "`this`"
                ],
                "why": "Its receiver. Wrap it — `item => this.isCheap(item)` — so each call is a method call, or `bind` it."
              },
              {
                "id": "c3",
                "question": "Which line makes the program throw \"rule is not a function\"? Type the line number.",
                "accept": [
                  "24",
                  "line 24"
                ],
                "why": "`halve()` *calls* `halve` right there, and its result is what gets passed. A classic in generated code: the callback called instead of passed."
              },
              {
                "id": "c4",
                "question": "On that line, what value does `repriced` actually receive as `rule`? Type the value.",
                "accept": [
                  "NaN"
                ],
                "why": "`halve()` with no argument computes `undefined / 2`, which is `NaN` — a number, not a function."
              },
              {
                "id": "c5",
                "question": "Which line changes the caller's original items, so the pen in `list.items` now costs `1`? Type the line number.",
                "accept": [
                  "15",
                  "line 15"
                ],
                "why": "`map` built a new array, but it is full of the *same* item objects, and line 15 assigns into them. \"A repriced copy\" needs new objects: `{ ...item, price: rule(item.price) }`."
              }
            ],
            "exportName": "makePriceList",
            "starter": "// A price list: cheap() lists items under the limit;\n// repriced(rule) gives a copy with every price passed through rule.\nfunction makePriceList(items, limit) {\n  return {\n    items,\n    limit,\n    isCheap(item) {\n      return item.price < this.limit;\n    },\n    cheap() {\n      return this.items.filter(this.isCheap);\n    },\n    repriced(rule) {\n      return this.items.map(item => {\n        item.price = rule(item.price);\n        return item;\n      });\n    },\n  };\n}\n\nconst halve = price => price / 2;\nconst list = makePriceList([{ name: \"pen\", price: 2 }, { name: \"ink\", price: 9 }], 5);\nconst sale = list.repriced(halve());\n",
            "tests": [
              {
                "label": "cheap() finds the item under the limit",
                "body": "const l = subject([{name:'pen',price:2},{name:'ink',price:9}], 5); const c = l.cheap(); return c.length === 1 && c[0].name === 'pen';"
              },
              {
                "label": "repriced(rule) applies the rule",
                "body": "const r = subject([{name:'pen',price:2}], 5).repriced(function (p) { return p / 2; }); return r[0].price === 1 && r[0].name === 'pen';"
              },
              {
                "label": "repriced leaves the original items alone",
                "body": "const items = [{name:'pen',price:2}]; subject(items, 5).repriced(function (p) { return p / 2; }); return items[0].price === 2;"
              }
            ],
            "hints": [
              {
                "when": "nothing passes, whatever you change in the function",
                "say": "The file has to load before any test runs, and the last line throws. Look at what is passed to `repriced` there."
              },
              {
                "when": "cheap() returns an empty array",
                "say": "`filter` calls whatever it is given with no object in front. Does the function you gave it use `this`?"
              },
              {
                "when": "the original-items test fails",
                "say": "A new array of the same objects is still sharing. The copy has to be a new object per item, not the old one with a changed field."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Hand out a method that survives",
            "prompt": "`makeTimer()` returns an object with `tick()` and `count()`. A caller must be able to write `[1,2,3].forEach(timer.tick)` and still see a count of 3, and hold on to `timer.count` on its own and call it later. When it passes: in one sentence, why can your `tick` not lose its receiver?\n",
            "exportName": "makeTimer",
            "filename": "timer.js",
            "starter": "// tick() increments; count() reads.\n// Both must work even when taken off the object and called bare.\nfunction makeTimer() {\n\n}\n",
            "tests": [
              {
                "label": "tick and count work normally",
                "body": "const t = subject(); t.tick(); t.tick(); return t.count() === 2;"
              },
              {
                "label": "tick survives being passed to forEach",
                "body": "const t = subject(); [1,2,3].forEach(t.tick); return t.count() === 3;"
              },
              {
                "label": "tick survives being extracted to a variable",
                "body": "const t = subject(); const f = t.tick; f(); f(); return t.count() === 2;"
              },
              {
                "label": "count survives being extracted, and reads the live value",
                "body": "const t = subject(); const c = t.count; t.tick(); return c() === 1;"
              },
              {
                "label": "two timers are independent",
                "body": "const a = subject(), b = subject(); a.tick(); return b.count() === 0;"
              }
            ],
            "hints": [
              {
                "when": "the forEach test fails",
                "say": "Your `tick` uses `this`, which is lost when the function is passed on its own. Close over a local binding instead — then there is no receiver to lose."
              },
              {
                "when": "only the count-extraction test fails",
                "say": "`tick` is safe but `count` still reads through `this`. A caller holding `timer.count` on its own calls it bare, exactly like `tick` in `forEach` — the same fix applies to both."
              },
              {
                "when": "the independence test fails",
                "say": "Your counter lives outside `makeTimer`, so every timer shares it. Declare it inside the function, so each call makes a fresh one."
              }
            ]
          }
        ]
      }
    },
    "higher-order": {
      "id": "higher-order",
      "title": "Higher-order functions, built by hand",
      "kind": "read",
      "outcome": 5,
      "shape": "conceptual",
      "why": "Outcome 5 asks for exactly this: functions that take and return functions, used to remove duplication (D-015). When what differs between near-identical functions is a test or a transformation rather than a value, passing behaviour is the only way to collapse them.",
      "concepts": [
        "fn.higher-order"
      ],
      "lede": "Once a function is a value, you can pass behaviour the way you pass data. That is the whole of `map`, `filter` and `reduce` — each one a loop you could write yourself — and the tool for removing the duplication that no other technique reaches.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Who calls a callback, and when · Closures keep their surroundings · Building functions from functions"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Higher-order function** — one that takes a function, returns one, or both.",
          "entries": [
            "**Higher-order function** — one that takes a function, returns one, or both."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four lines passing behaviour around. Type what each produces, or pick where\nthere are buttons."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "[1, 2, 3, 4].filter(n => n % 2 === 0)",
                          "accept": [
                            "[2,4]",
                            "[2, 4]",
                            "2,4"
                          ],
                          "why": "`filter` keeps the values for which the callback returns truthy. It builds a new array and never touches the original.\n"
                        },
                        {
                          "code": "[1, 2, 3].reduce((a, b) => a + b, 0)",
                          "accept": [
                            "6"
                          ],
                          "miss": "**If you thought the `0` was optional — it runs without one, using the first element, and then throws on an empty array.** Supplying the seed is what makes `reduce` total.\n",
                          "why": "`reduce` carries an accumulator across the collection. The `0` is its starting value, and supplying it is what makes an empty array safe.\n"
                        },
                        {
                          "code": "const over = n => x => x > n;   [3, 8, 12].filter(over(5))",
                          "accept": [
                            "[8,12]",
                            "[8, 12]",
                            "8,12"
                          ],
                          "miss": "**If you expected an error — `over(5)` is a call, and it runs first.** It *returns* a function that remembers `5`, and that returned function is what `filter` receives.\n",
                          "why": "One function built another, and a built-in called it on each value.\n"
                        },
                        {
                          "code": "const twice = f => x => f(f(x));   twice(x => x + 1)(0)",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `1`, count the calls.** `twice` returns `x => f(f(x))`, so the single call applies `f` twice. Two sets of parentheses because there are two calls.\n",
                          "why": "A function that takes a function and returns a function. Two calls — one to build the doubled-up version, one to run it.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "[n for n in [1,2,3,4] if n % 2 == 0]",
                          "accept": [
                            "[2, 4]",
                            "[2,4]",
                            "2,4"
                          ],
                          "why": "The comprehension is the idiomatic filter. `filter(lambda n: ..., xs)` exists and is less used.\n"
                        },
                        {
                          "code": "from functools import reduce\\nreduce(lambda a,b: a+b, [1,2,3], 0)",
                          "accept": [
                            "6"
                          ],
                          "why": "`reduce` lives in `functools`, not the builtins — Python steers you toward `sum` or an explicit loop for most cases.\n"
                        },
                        {
                          "code": "sorted([3,1,2], key=lambda x: -x)",
                          "accept": [
                            "[3, 2, 1]",
                            "[3,2,1]",
                            "3,2,1"
                          ],
                          "why": "`key=` is Python's most common higher-order parameter: you supply the behaviour, the library supplies the algorithm.\n"
                        },
                        {
                          "code": "twice = lambda f: lambda x: f(f(x))\\ntwice(lambda x: x+1)(0)",
                          "accept": [
                            "2"
                          ],
                          "why": "Same construction, and `lambda` is limited to one expression — which is why Python programmers reach for `def` and closures more often than JS ones.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "new[]{1,2,3,4}.Where(n => n % 2 == 0)",
                          "accept": [
                            "[2,4]",
                            "2, 4",
                            "2,4"
                          ],
                          "why": "`Where` is LINQ's filter. It returns a lazy sequence — nothing runs until you enumerate it.\n"
                        },
                        {
                          "code": "new[]{1,2,3}.Aggregate(0, (a,b) => a+b)",
                          "accept": [
                            "6"
                          ],
                          "why": "`Aggregate` is reduce, with the seed first rather than last.\n"
                        },
                        {
                          "question": "Does this compile? `Func<Func<int,int>, Func<int,int>> twice = f => x => f(f(x));`",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "It works, and the type annotation is why higher-order code reads more heavily in C#. The idea is identical; the declaration is not.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can write functions that take behaviour as a parameter, and functions that return it",
                "I can collapse near-identical functions into one that takes a function"
              ]
            }
          ]
        },
        {
          "h": "Passing behaviour"
        },
        {
          "p": "A **higher-order function** takes a function, returns one, or both."
        },
        {
          "code": "items.map(fn);          // fn decides what each becomes\nitems.filter(fn);       // fn decides what stays\nitems.sort(fn);         // fn decides the order\nitems.reduce(fn, seed); // fn decides how values combine",
          "lang": "js"
        },
        {
          "p": "In each case the *algorithm* is fixed and the *decision* is yours. The built-in\nowns the loop and calls your function once per item; you supply a small piece of\njudgement and get a well-tested traversal for free."
        },
        {
          "p": "What does each line produce?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "[\"ada\", \"bo\", \"cyd\"].map(s => s.length)",
                "accept": [
                  "[3,2,3]",
                  "[3, 2, 3]",
                  "3,2,3"
                ],
                "why": "`map` owns the loop; your arrow decides what each name becomes — its length.\n"
              },
              {
                "code": "[5, 1, 4].sort((a, b) => b - a)",
                "accept": [
                  "[5,4,1]",
                  "[5, 4, 1]",
                  "5,4,1"
                ],
                "why": "`sort` owns the ordering algorithm; your function answers \"which comes first?\" for any two. `b - a` puts larger values first.\n"
              }
            ]
          }
        },
        {
          "h": "Built by hand"
        },
        {
          "p": "None of these is magic. Each is a loop with one line that calls your function.\nWriting one yourself is how you stop treating them as special — and how you\nbuild one when no built-in fits."
        },
        {
          "p": "`filter`, by hand:"
        },
        {
          "code": "function keep(items, test) {\n  const out = [];\n  for (const item of items) {\n    if (test(item)) {         // the one line that calls your function\n      out.push(item);\n    }\n  }\n  return out;\n}\n\nkeep([5, 12, 8, 20], n => n > 10);   // [12, 20]",
          "lang": "js",
          "file": "keep.js"
        },
        {
          "p": "`map`, by hand — a different question asked of each item, the same shape:"
        },
        {
          "code": "function transformAll(items, change) {\n  const out = [];\n  for (const item of items) {\n    const changed = change(item);   // the one line that calls your function\n    out.push(changed);\n  }\n  return out;\n}\n\ntransformAll([\"ada\", \"bo\"], s => s.length);   // [3, 2]",
          "lang": "js",
          "file": "transform-all.js"
        },
        {
          "p": "The parameter names say the role: `test` answers yes or no, `change` produces\na new value. Neither is called `fn` — the name is the documentation."
        },
        {
          "p": "What does each call return?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "keep([\"a\", \"\", \"b\"], s => s)",
                "accept": [
                  "[\"a\",\"b\"]",
                  "[\"a\", \"b\"]",
                  "['a', 'b']",
                  "a,b",
                  "[a,b]"
                ],
                "miss": "**If you included `\"\"` — `keep` pushes only when the test's answer is truthy.** The test here returns the string itself, and `\"\"` is falsy.\n",
                "why": "`test` returned each string; `\"\"` is falsy, so it was left out.\n"
              },
              {
                "code": "transformAll([2, 3], n => n > 2)",
                "accept": [
                  "[false,true]",
                  "[false, true]",
                  "false,true"
                ],
                "miss": "**If you answered `[3]` — that is what `keep` would give.** `transformAll` pushes the callback's *answer* for every item, so a yes/no test becomes a list of yes/no answers.\n",
                "why": "The same test, handed to the other function: one answer per item.\n"
              }
            ]
          }
        },
        {
          "h": "Removing duplication no other tool reaches"
        },
        {
          "p": "Here is the shape that motivates all of it. Three functions, identical except\nfor one decision each."
        },
        {
          "code": "function activeUsers(users) {\n  const out = [];\n  for (const u of users) if (u.active) out.push(u);\n  return out;\n}\nfunction adminUsers(users) {\n  const out = [];\n  for (const u of users) if (u.role === \"admin\") out.push(u);\n  return out;\n}\nfunction recentUsers(users) {\n  const out = [];\n  for (const u of users) if (u.lastSeen > cutoff) out.push(u);\n  return out;\n}",
          "lang": "js",
          "file": "before.js"
        },
        {
          "p": "Extracting a *variable* cannot help — the differing part is not a value, it is a\n**test**. Extracting a function can:"
        },
        {
          "code": "const activeUsers = users => users.filter(u => u.active);\nconst adminUsers  = users => users.filter(u => u.role === \"admin\");\nconst recentUsers = users => users.filter(u => u.lastSeen > cutoff);",
          "lang": "js",
          "file": "after.js"
        },
        {
          "p": "Nine lines of traversal became zero, because `filter` already owns the traversal.\nThis is the outcome's actual demand: *use them to remove duplication*."
        },
        {
          "scaffold": {
            "label": "Turning duplication into a parameter",
            "body": [
              {
                "h": "Turning duplication into a parameter"
              },
              {
                "code": "// You have two or more functions that are nearly identical.\n\n// 1. Line them up and find what DIFFERS.\n//    a value      -> extract a parameter. Ordinary refactoring.\n//    a test or a\n//    transformation -> extract a FUNCTION parameter. Keep going.\n\n// 2. Does a built-in already own that traversal?\n//    filter (keep some) · map (change each) · reduce (combine)\n//    · sort (order) · some/every (ask a question)\n//    yes -> use it. Stop.\n//    no  -> write the higher-order function yourself.\n\n// 3. Name the parameter after its ROLE, not its type:\n//    `predicate`, `transform`, `measure` — never `fn` or `callback`.",
                "lang": "js",
                "file": "extract.js"
              },
              {
                "p": "Now apply it. These two differ in one place."
              },
              {
                "code": "function cheapest(items) {\n  let best = items[0];\n  for (const i of items) if (i.price < best.price) best = i;\n  return best;\n}\nfunction lightest(items) {\n  let best = items[0];\n  for (const i of items) if (i.weight < best.weight) best = i;\n  return best;\n}\n\n// What differs?            ______\n// Which built-in owns it?  ______\n// The combined version:    function lowestBy(items, ______) { ... }",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "What differs is *which number to compare* — a transformation, from an item to a\nnumber. No built-in says \"the lowest by some measure\", so this is the \"write it\nyourself\" branch: the same loop once, comparing `measure(i) < measure(best)`,\nwith `cheapest = items => lowestBy(items, i => i.price)`."
              }
            ]
          }
        },
        {
          "h": "Returning functions"
        },
        {
          "p": "The other half. A function that builds a function is how you pre-supply part of\nthe work."
        },
        {
          "code": "const startsWith = prefix => s => s.startsWith(prefix);\n\nnames.filter(startsWith(\"a\"));\nnames.filter(startsWith(\"b\"));",
          "lang": "js"
        },
        {
          "p": "`startsWith(\"a\")` returns a **new** function that remembers `prefix` — a closure.\nHigher-order functions and closures are the same machinery seen from two\ndirections. The call runs first and hands its result, a function, to `filter`,\nwhich calls it on each name."
        },
        {
          "p": "What does each line produce?"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const over = n => x => x > n;   over(6)(6)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "Two calls: `over(6)` builds the test, the second call runs it on `6`. `6 > 6` is `false`.\n"
              },
              {
                "code": "const times = k => n => n * k;   [1, 2, 3].map(times(10))",
                "accept": [
                  "[10,20,30]",
                  "[10, 20, 30]",
                  "10,20,30"
                ],
                "why": "`times(10)` returns `n => n * 10`, and `map` calls that on each number.\n"
              }
            ]
          }
        },
        {
          "video": {
            "title": "Three functions becoming one",
            "caption": "The `users` example refactored line by line, then the same move applied to a case where no built-in fits and the higher-order function has to be written.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **how much ceremony does passing behaviour cost?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Passing a function",
                  "Filter"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "none",
                    "`filter`"
                  ],
                  [
                    "**Python**",
                    "none",
                    "a comprehension"
                  ],
                  [
                    "**C#**",
                    "a delegate type",
                    "`Where`"
                  ]
                ]
              }
            },
            {
              "p": "In JavaScript the idea is *obvious* because there is nothing between you and\nit: no type to declare before a function can be handed over."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "`map`, `filter`, `reduce`, `some`, `every`, `sort`, `find`. Reach for them before\nwriting a loop, and write the loop when none of them says what you mean."
              },
              {
                "code": "const total = items.reduce((t, i) => t + i.price, 0);\nconst anyOver = items.some(i => i.price > 100);\nconst byPrice = [...items].sort((a, b) => a.price - b.price);   // copy first!",
                "lang": "js"
              },
              {
                "p": "`sort` mutates and returns the same array — the shared-reference problem again. Copy before sorting\nunless you mean to reorder the caller's data."
              }
            ],
            "python": [
              {
                "p": "Comprehensions cover `map` and `filter` idiomatically; `key=` is the common\nhigher-order parameter."
              },
              {
                "code": "total = sum(i.price for i in items)\nby_price = sorted(items, key=lambda i: i.price)   # returns a new list\nany_over = any(i.price > 100 for i in items)",
                "lang": "python"
              },
              {
                "p": "Note `sorted` returns a new list while `list.sort()` mutates — the same\ndistinction JavaScript draws between `sort` and copying first."
              }
            ],
            "csharp": [
              {
                "p": "LINQ, with the laziness to remember."
              },
              {
                "code": "var total   = items.Sum(i => i.Price);\nvar byPrice = items.OrderBy(i => i.Price);        // lazy, and non-destructive\nvar anyOver = items.Any(i => i.Price > 100);",
                "lang": "csharp"
              },
              {
                "p": "`OrderBy` returns a new sequence rather than sorting in place, so C# avoids the\ncopy-before-sort problem entirely. Call `.ToList()` when you want the work done\nnow rather than later."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then write `keep` from memory, and\nsay which one line of it makes it a higher-order function."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "Your codebase has eleven functions of the shape `getActiveX`, `getPendingX`,\n`getArchivedX` across four entity types — 44 functions, each three lines."
            },
            {
              "p": "Two directions:"
            },
            {
              "list": [
                "**One higher-order function** — `byStatus(status)` returning a predicate, used with `filter` at each call site.",
                "**Leave them** — each is three lines, trivially readable, and named for what it does."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what happens when a fifth entity\ntype arrives, and what a reader loses when `getActiveUsers()` becomes\n`users.filter(byStatus(\"active\"))`. Name the condition under which you would\nchange your answer."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A **higher-order function** takes a function, returns one, or both — and each built-in is a loop you could write, with one line that calls your function.",
                "Extract a function parameter when what differs is a **test or a transformation**, not a value.",
                "Returning a function pre-supplies part of the work; it is a closure doing it."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array.prototype.filter()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/filter",
              "note": "what counts as \"keep\", and that the original is untouched."
            },
            {
              "title": "Array.prototype.reduce()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/reduce",
              "note": "read what happens with no initial value on an empty array."
            },
            {
              "title": "Array.prototype.sort()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort",
              "note": "in place, and the default string comparison that surprises people with numbers."
            }
          ]
        }
      ],
      "_source": "lessons/higher-order.md",
      "practice": {
        "id": "higher-order",
        "outcome": 5,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the result",
            "prompt": "Three quick ones. Type what the first two produce, and pick the answer to the third.",
            "items": [
              {
                "id": "p1",
                "code": "const atLeast = n => x => x >= n;   [4, 9, 2].filter(atLeast(4))",
                "accept": [
                  "[4,9]",
                  "[4, 9]",
                  "4,9"
                ],
                "why": "`atLeast(4)` *returns* a function that remembers `4`; `filter` then calls that function on each value. One function built another, and a built-in called it."
              },
              {
                "id": "p2",
                "code": "[1, 2, 3].map(n => n > 1)",
                "accept": [
                  "[false,true,true]",
                  "[false, true, true]",
                  "false,true,true"
                ],
                "miss": "**If you answered `[2,3]` — that is `filter`.** `map` keeps every item and replaces each with the callback's answer, so a yes/no test becomes a list of yes/no answers.\n",
                "why": "`map` changes each item into whatever the callback returns — here a boolean."
              },
              {
                "id": "p3",
                "question": "`activeUsers` keeps users where `u.active`; `adminUsers` keeps users where `u.role === \"admin\"`. Otherwise identical. What differs between them?",
                "choices": [
                  "A value",
                  "A test"
                ],
                "accept": [
                  "A test"
                ],
                "why": "A test — which is why extracting a variable cannot remove the duplication and extracting a *function parameter* can. That is step one of turning duplication into a parameter."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "[].reduce((a, b) => a + b)",
                "accept": [
                  "TypeError",
                  "error",
                  "a TypeError",
                  "throws"
                ],
                "why": "With no seed, `reduce` uses the first element — and an empty array has none. Supplying the seed is what makes it total."
              },
              {
                "id": "p5",
                "code": "const atLeast = n => x => x >= n;   atLeast(3)(3)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "Two calls: `atLeast(3)` builds the test, the second call runs it on `3`. `3 >= 3` is `true`."
              },
              {
                "id": "p6",
                "code": "const twice = f => x => f(f(x));   twice(x => x * 3)(2)",
                "accept": [
                  "18"
                ],
                "why": "`twice` returns a function that applies `f` two times: `2 * 3 = 6`, then `6 * 3 = 18`."
              }
            ],
            "hints": [
              {
                "when": "you answered with a function, or an error, for the first",
                "say": "`atLeast(4)` is a call, and it runs before `filter` does. What does that call return? That returned thing is what `filter` receives."
              },
              {
                "when": "you answered [2,3] for the second",
                "say": "Which built-in keeps some items, and which changes every item? This one is `map`."
              },
              {
                "when": "you picked \"A value\" for the third",
                "say": "Line the two up. `u.active` and `u.role === \"admin\"` are not the same kind of thing as `5` or `\"admin\"` — each is a question asked of `u`."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Build filter by hand",
            "prompt": "Put these lines in order to write `keepWhere(items, test)`: a hand-built `filter` that returns a new array of the items for which `test(item)` is truthy.\n",
            "lines": [
              "function keepWhere(items, test) {",
              "  const out = [];",
              "  for (const item of items) {",
              "    if (test(item)) out.push(item);",
              "  }",
              "  return out;",
              "}"
            ],
            "distractors": [
              {
                "line": "    if (test) out.push(item);",
                "why": "This asks whether `test` exists, not what it says about `item`. A function is always truthy, so every item is kept."
              },
              {
                "line": "    out.push(test(item));",
                "why": "This pushes the test's *answer* for every item — that is `map`, and it returns a list of booleans."
              }
            ],
            "why": "Own the traversal, ask the behaviour, collect the answer. The only line that makes it higher-order is the one that calls `test`.",
            "hints": [
              {
                "when": "every item comes back",
                "say": "Look at the `if`. Is it calling `test` on the item, or only checking that `test` is there?"
              },
              {
                "when": "you get a list of true and false",
                "say": "A filter keeps the *item* when the test says yes. What is being pushed?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Return the test",
            "prompt": "`where(key, value)` must return a *predicate* — a function that takes one item and says whether `item[key]` is exactly `value` — so that `users.filter(where(\"role\", \"admin\"))` works. Fill the gap. When it passes: in one sentence, how does the returned function still know `key` and `value` after `where` has returned?\n",
            "exportName": "where",
            "filename": "where.js",
            "starter": "// where(\"role\", \"admin\") returns a function: item => true or false.\nfunction where(key, value) {\n  return /* ___ fill this in ___ */;\n}\n\n// How it is used:\n// users.filter(where(\"role\", \"admin\"))\n// orders.filter(where(\"status\", \"open\"))\n",
            "tests": [
              {
                "label": "returns a function",
                "body": "return typeof subject('role', 'admin') === 'function';"
              },
              {
                "label": "works with filter",
                "body": "const u = [{role:'admin'},{role:'dev'},{role:'admin'}]; return u.filter(subject('role','admin')).length === 2;"
              },
              {
                "label": "works for a different key",
                "body": "const o = [{status:'open'},{status:'closed'}]; const r = o.filter(subject('status','open')); return r.length === 1 && r[0].status === 'open';"
              },
              {
                "label": "two predicates do not interfere",
                "body": "const a = subject('n', 1), b = subject('n', 2); return a({n:1}) === true && b({n:1}) === false;"
              },
              {
                "label": "the string '0' does not match the number 0",
                "body": "return subject('n', 0)({n: '0'}) === false;"
              }
            ],
            "hints": [
              {
                "when": "the first test fails, or filter throws",
                "say": "The gap must be a *function*, not a true/false answer — `where` runs before any item exists. What would `filter` call on each item?"
              },
              {
                "when": "the different-key test fails",
                "say": "`item.key` reads a property literally named `key`. To read the property whose name is *in* the variable `key`, use square brackets."
              },
              {
                "when": "only the '0' test fails",
                "say": "Your comparison converts types before comparing. Which equality operator never does?"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Take the test now, the list later",
            "prompt": "`countWhere(items, test)` works. Change it so the test is supplied first and a reusable counter comes back: `const countPassing = countWhere(s => s.score >= 60);` then `countPassing(students)`. Only the call shape changes; the counting does not. When it passes: in one sentence, what does the caller gain from a call that returns a function instead of a number?\n",
            "exportName": "countWhere",
            "filename": "count-where.js",
            "starter": "// Works today: countWhere(students, s => s.score >= 60)\nfunction countWhere(items, test) {\n  let n = 0;\n  for (const item of items) {\n    if (test(item)) n++;\n  }\n  return n;\n}\n",
            "tests": [
              {
                "label": "countWhere(test) returns a function",
                "body": "return typeof subject(function (s) { return s.score >= 60; }) === 'function';"
              },
              {
                "label": "the returned counter counts",
                "body": "const passing = subject(function (s) { return s.score >= 60; }); return passing([{score:70},{score:40},{score:60}]) === 2;"
              },
              {
                "label": "the counter is reusable on another list",
                "body": "const passing = subject(function (s) { return s.score >= 60; }); passing([{score:90}]); return passing([{score:10},{score:99}]) === 1;"
              },
              {
                "label": "an empty list counts zero",
                "body": "return subject(function () { return true; })([]) === 0;"
              },
              {
                "label": "a test that returns a truthy non-boolean still counts",
                "body": "return subject(function (w) { return w.length; })(['a', '', 'bc']) === 2;"
              }
            ],
            "hints": [
              {
                "when": "the first test fails, or \"items is not iterable\"",
                "say": "Your function still expects the list first. It now receives only the test — and must *return* something that will receive the list later."
              },
              {
                "when": "the reusable test fails",
                "say": "The count is shared between calls. Where does `n` start at zero — once when `countWhere` runs, or each time the returned counter runs?"
              },
              {
                "when": "the truthy test fails",
                "say": "You compared the test's answer with `=== true`. The original counted any truthy answer; keep that behaviour."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a task filter an AI tool wrote",
            "prompt": "Someone asked an AI tool for helpers to filter and count tasks. This is what came back. It has three defects, each in a line that passes or combines behaviour. Find them — then, below, fix all three.\n",
            "filename": "tasks.js",
            "code": "// byStatus(status) returns a test for filter: tasks.filter(byStatus(\"done\"))\n// countBy(tasks, test) counts the tasks that pass the test.\n// overdue(tasks, today) lists open tasks whose due day is before today.\nfunction byStatus(status) {\n  return task => task.status = status;\n}\n\nfunction countBy(tasks, test) {\n  return tasks.reduce((n, t) => test(t) ? n + 1 : n);\n}\n\nfunction overdue(tasks, today) {\n  return tasks.filter(byStatus(\"open\") && (t => t.due < today));\n}\n\nconst taskTools = { byStatus, countBy, overdue };\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line makes every task pass `byStatus(\"done\")` — and changes the tasks as it does? Type the line number.",
                "accept": [
                  "5",
                  "line 5"
                ],
                "why": "`=` assigns: it writes `status` into every task and returns it, and a non-empty string is truthy. The test needs `===`."
              },
              {
                "id": "c2",
                "question": "Which line gives a wrong count, or throws on an empty list? Type the line number.",
                "accept": [
                  "9",
                  "line 9"
                ],
                "why": "No seed, so `reduce` starts from the first *task*, not from `0` — and an empty list throws. Pass `0` as the second argument."
              },
              {
                "id": "c3",
                "question": "Which line checks only the due day, never the status? Type the line number.",
                "accept": [
                  "13",
                  "line 13"
                ],
                "why": "`a && b` with two functions evaluates to `b` — `&&` produces one of its operands, and a function is truthy. To combine two tests you need a new function that calls both: `t => isOpen(t) && t.due < today`."
              },
              {
                "id": "c4",
                "question": "On that line, what does `byStatus(\"open\") && (t => t.due < today)` evaluate to?",
                "choices": [
                  "true",
                  "The first function",
                  "The second function"
                ],
                "accept": [
                  "The second function"
                ],
                "why": "The right-hand function. `&&` returns its right operand when the left is truthy, and it never calls either."
              }
            ],
            "exportName": "taskTools",
            "starter": "// byStatus(status) returns a test for filter: tasks.filter(byStatus(\"done\"))\n// countBy(tasks, test) counts the tasks that pass the test.\n// overdue(tasks, today) lists open tasks whose due day is before today.\nfunction byStatus(status) {\n  return task => task.status = status;\n}\n\nfunction countBy(tasks, test) {\n  return tasks.reduce((n, t) => test(t) ? n + 1 : n);\n}\n\nfunction overdue(tasks, today) {\n  return tasks.filter(byStatus(\"open\") && (t => t.due < today));\n}\n\nconst taskTools = { byStatus, countBy, overdue };\n",
            "tests": [
              {
                "label": "byStatus tests without changing the task",
                "body": "const t = { status: 'open' }; const isDone = subject.byStatus('done'); return isDone(t) === false && t.status === 'open' && isDone({ status: 'done' }) === true;"
              },
              {
                "label": "countBy counts the passing tasks",
                "body": "const ts = [{ s: 1 }, { s: 0 }, { s: 1 }]; return subject.countBy(ts, function (t) { return t.s === 1; }) === 2;"
              },
              {
                "label": "countBy of an empty list is 0",
                "body": "return subject.countBy([], function () { return true; }) === 0;"
              },
              {
                "label": "overdue keeps only open tasks that are late",
                "body": "const ts = [{ id: 1, status: 'open', due: 3 }, { id: 2, status: 'done', due: 3 }, { id: 3, status: 'open', due: 9 }]; const r = subject.overdue(ts, 5); return r.length === 1 && r[0].id === 1;"
              }
            ],
            "hints": [
              {
                "when": "byStatus changes the task",
                "say": "Read the arrow in `byStatus` aloud: does it ask a question about the task, or change it?"
              },
              {
                "when": "countBy is off by the first task, or throws on an empty list",
                "say": "Where does the count start? `reduce` needs to be told."
              },
              {
                "when": "overdue keeps a finished task",
                "say": "`filter` receives one function. Is the value you give it a function that asks both questions, or just one of the two?"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Collapse three searches into one",
            "prompt": "A task tracker has three near-identical functions — `firstOverdue`, `firstUnassigned` and `firstUrgent` — each a loop that returns the first task matching one condition, or `null` if none does. Write one `firstWhere(items, test)` that all three could become one line each with. It must stop at the first match. When it passes: in one sentence, why could a string parameter like `\"urgent\"` not replace all three?\n",
            "exportName": "firstWhere",
            "filename": "first-where.js",
            "starter": "// firstWhere(items, test) -> the first item for which test(item) is truthy,\n// or null if there is none. Stops as soon as it finds one.\n//\n// So that:\n//   const firstUrgent = tasks => firstWhere(tasks, t => t.priority > 8);\nfunction firstWhere(items, test) {\n\n}\n",
            "tests": [
              {
                "label": "finds the first match",
                "body": "const r = subject([{ id: 1, p: 2 }, { id: 2, p: 9 }, { id: 3, p: 9 }], function (t) { return t.p > 8; }); return r !== null && r !== undefined && r.id === 2;"
              },
              {
                "label": "works with a different test",
                "body": "const r = subject([{ who: 'ada' }, { who: null }], function (t) { return t.who === null; }); return r !== null && r !== undefined && r.who === null;"
              },
              {
                "label": "no match gives null",
                "body": "return subject([{ p: 1 }], function (t) { return t.p > 8; }) === null;"
              },
              {
                "label": "an empty list gives null",
                "body": "return subject([], function () { return true; }) === null;"
              },
              {
                "label": "stops at the first match",
                "body": "let calls = 0; subject([1, 2, 3, 4], function (n) { calls++; return n === 2; }); return calls === 2;"
              },
              {
                "label": "returns the item, not its position",
                "body": "const item = { id: 7 }; return subject([{ id: 1 }, item], function (t) { return t.id === 7; }) === item;"
              }
            ],
            "hints": [
              {
                "when": "the null tests fail",
                "say": "When nothing matches, what does your function return? `find` and a loop that falls off the end both give `undefined`, and the spec asks for `null`."
              },
              {
                "when": "the stops-at-the-first test fails",
                "say": "Your function keeps asking after it has its answer. `filter` looks at every item; return as soon as one passes."
              },
              {
                "when": "every call returns the first item",
                "say": "Check the condition: are you calling `test` on the item, or asking whether `test` exists?"
              }
            ]
          }
        ]
      }
    },
    "first-class-or-not": {
      "id": "first-class-or-not",
      "title": "Building functions from functions",
      "kind": "read",
      "outcome": 5,
      "shape": "conceptual",
      "why": "Outcome 5 asks for functions that return functions, and composition and partial application are the two ordinary reasons to write one: joining small functions into a larger one, and fixing some arguments now. The order a composition runs in is also the thing people most often get backwards.",
      "concepts": [
        "fn.composition"
      ],
      "lede": "Once functions can be passed and returned, small ones can be joined into a larger one, or given some of their arguments now and the rest later. Both are a function returning a function — and the order they run in is the part people get backwards.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Higher-order functions, built by hand · Functions as values"
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Three questions about composing functions. Type the number each produces."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const compose = (f, g) => x => f(g(x));  compose(n => n + 1, n => n * 2)(5)",
                          "accept": [
                            "11"
                          ],
                          "miss": "**If you answered `12`, you applied the first function first.** `compose(f, g)` means `f(g(x))`, so `g` goes first — the convention comes from mathematics, where *f ∘ g* means the same. `pipe` exists precisely because that order confuses people.\n",
                          "why": "`g` runs first — `5 * 2` is `10` — then `f` adds one. Composition reads right to left, which is the thing people get backwards.\n"
                        },
                        {
                          "code": "const partial = (f, a) => b => f(a, b);  partial((a, b) => a - b, 10)(3)",
                          "accept": [
                            "7"
                          ],
                          "why": "`10` was fixed first, so the call supplies `b`. Pre-supplying arguments is the other half of returning functions.\n"
                        },
                        {
                          "code": "const pipe = (f, g) => x => g(f(x));  pipe(n => n + 1, n => n * 2)(5)",
                          "accept": [
                            "12"
                          ],
                          "miss": "**If this matched your answer above, look at the two definitions side by side.** One nests `g` inside `f` and the other nests `f` inside `g` — one character of difference, opposite results.\n",
                          "why": "The same two functions, the other order — `pipe` reads left to right. One character of difference in the definition, a different answer.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "compose = lambda f,g: lambda x: f(g(x))\\ncompose(lambda n:n+1, lambda n:n*2)(5)",
                          "accept": [
                            "11"
                          ],
                          "why": "Right to left, same as JavaScript.\n"
                        },
                        {
                          "code": "from functools import partial\\npartial(lambda a,b: a-b, 10)(3)",
                          "accept": [
                            "7"
                          ],
                          "why": "`functools.partial` is the standard-library version of the same idea.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "Func<int,int> Compose(Func<int,int> f, Func<int,int> g) => x => f(g(x));  Compose(n=>n+1, n=>n*2)(5)",
                          "accept": [
                            "11"
                          ],
                          "why": "Identical behaviour, right to left.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can build a function from smaller functions and predict the order they run in",
                "I can pre-supply some arguments now and the rest later"
              ]
            }
          ]
        },
        {
          "h": "Composition, in both directions"
        },
        {
          "code": "const compose = (...fns) => x => fns.reduceRight((v, f) => f(v), x);\nconst pipe    = (...fns) => x => fns.reduce((v, f) => f(v), x);\n\nconst slug = pipe(\n  s => s.trim(),\n  s => s.toLowerCase(),\n  s => s.replace(/\\s+/g, \"-\"),\n);\n\nslug(\"  Hello World  \");   // \"hello-world\"",
          "lang": "js"
        },
        {
          "p": "`pipe` reads in execution order and is usually the better default for that\nreason alone. Note the implementation: composition *is* `reduce` over an array of\nfunctions."
        },
        {
          "h": "Partial application"
        },
        {
          "p": "Fix some arguments now, supply the rest later."
        },
        {
          "code": "const log = (level, message) => `[${level}] ${message}`;\nconst warn = message => log(\"warn\", message);       // by hand\nconst error = partial(log, \"error\");                 // with a helper\n\nwarn(\"disk full\");    // \"[warn] disk full\"",
          "lang": "js"
        },
        {
          "p": "The returned function closes over `level`. Again: this is a closure, wearing a\ndifferent name because of what it is used for."
        },
        {
          "h": "Nothing checks the join"
        },
        {
          "p": "The JavaScript `compose` above works on anything. That is a real strength, and it\nis also the whole of its guarantee — nothing checks that the output of `g` is a\nsensible input to `f`."
        },
        {
          "code": "compose(s => s.toUpperCase(), n => n * 2)(5);   // TypeError, at run time",
          "lang": "js"
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Compose with `reduce`, and prefer `pipe` for readability."
              },
              {
                "code": "const pipe = (...fns) => x => fns.reduce((v, f) => f(v), x);",
                "lang": "js"
              },
              {
                "p": "Nothing states what each stage takes and gives, so state it in names and keep the\npieces small. A four-stage `pipe` of well-named one-liners reads as a plan; a\nfour-stage pipe of `fn1, fn2, fn3` does not."
              }
            ],
            "python": [
              {
                "p": "`functools.reduce` and `functools.partial` give you both halves."
              }
            ],
            "csharp": [
              {
                "p": "A `Compose` that works for any types declares type parameters."
              },
              {
                "code": "static Func<A, C> Compose<A, B, C>(Func<B, C> f, Func<A, B> g) => x => f(g(x));",
                "lang": "csharp"
              },
              {
                "p": "Type inference means call sites usually stay clean — `Compose(f, g)` without the\nangle brackets."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening three again from memory. Then say which of `compose` and `pipe`\nyou would name a variable `slug` with, and why the order convention is worth\nstating in a comment either way."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A `formatPrice(p)` function rounds a price, adds tax, formats it as currency and\npads it to a fixed width, all in one twelve-line body."
            },
            {
              "p": "Two directions:"
            },
            {
              "list": [
                "**Leave it** — one function, one name, every step visible in one place.",
                "**Rebuild it as a pipe** — `pipe(round, addTax, toCurrency, pad)`, four small named functions."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what happens when the tax step\nhas to change, and where a stage that returns the wrong kind of value will\nfinally throw. Name the case where you would change your answer."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**`compose` runs right to left; `pipe` runs left to right.** Both are `reduce` over an array of functions.",
                "**Partial application** pre-supplies arguments and is a closure by another name.",
                "Nothing checks that one stage's output suits the next stage's input. A mismatch throws when the call runs."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array.prototype.reduceRight()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/reduceRight",
              "note": "which is `compose`, and why the direction is what it is."
            },
            {
              "title": "Closures",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Closures",
              "note": "partial application is a closure; the guide's factory section is the same pattern."
            }
          ]
        }
      ],
      "_source": "lessons/first-class-or-not.md",
      "practice": {
        "id": "first-class-or-not",
        "outcome": 5,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the order",
            "prompt": "Same two functions, two combinators — then one question about order. Type the number each of the first two produces, and pick the answer to the third.",
            "items": [
              {
                "id": "p1",
                "code": "const compose = (f,g) => x => f(g(x));  compose(n=>n+1, n=>n*2)(5)",
                "accept": [
                  "11"
                ],
                "why": "`g` runs first — `5*2` is `10` — then `f` adds one. Composition reads right to left."
              },
              {
                "id": "p2",
                "code": "const pipe = (f,g) => x => g(f(x));  pipe(n=>n+1, n=>n*2)(5)",
                "accept": [
                  "12"
                ],
                "why": "Left to right: add one, then double. One character of difference in the definition, a different answer."
              },
              {
                "id": "p3",
                "question": "A slug must be trimmed, then lowercased, then hyphenated. Building it with `pipe(…)`, which stage do you list first?",
                "choices": [
                  "trim",
                  "lower",
                  "hyphenate"
                ],
                "accept": [
                  "trim"
                ],
                "why": "`pipe` runs its stages in the order you list them, so the list reads as the plan: trim, lower, hyphenate. With `compose` you would list them backwards."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const partial = (f,a) => b => f(a,b);  partial((a,b)=>a-b, 10)(3)",
                "accept": [
                  "7"
                ],
                "why": "`10` was fixed first, so the later call supplies `b`. The returned function closes over `a`."
              },
              {
                "id": "p5",
                "code": "compose(s => s.toUpperCase(), n => n*2)(5)",
                "accept": [
                  "TypeError",
                  "error",
                  "a TypeError",
                  "throws"
                ],
                "why": "`n*2` gives `10`, and a number has no `toUpperCase`. Nothing checked that `g`'s output suits `f`'s input — JavaScript finds out only when the call runs."
              },
              {
                "id": "p6",
                "question": "With `const compose = (...fns) => x => fns.reduceRight((v, f) => f(v), x);`, in `compose(h, g, f)(x)`, which function runs first?",
                "choices": [
                  "h",
                  "g",
                  "f"
                ],
                "accept": [
                  "f"
                ],
                "why": "`reduceRight` starts from the end of the list. `compose(h, g, f)` means `h(g(f(x)))`."
              }
            ],
            "hints": [
              {
                "when": "you gave the same answer for both",
                "say": "Trace which function touches the 5 first in each. One definition nests `g` inside `f`; the other nests `f` inside `g`."
              },
              {
                "when": "you picked hyphenate for the third",
                "say": "That is the order `compose` wants. Which of the two runs its stages in the order they are written?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace a pipeline",
            "prompt": "`pipe(trim, lower, hyphenate)` applied to `\"  Hello World  \"`. Give the value after each stage.",
            "items": [
              {
                "id": "t1",
                "question": "The value after `trim`",
                "accept": [
                  "\"Hello World\"",
                  "Hello World",
                  "'Hello World'"
                ],
                "why": "Stages run left to right in a pipe, so `trim` sees the raw input first."
              },
              {
                "id": "t2",
                "question": "The value after `lower`",
                "accept": [
                  "\"hello world\"",
                  "hello world",
                  "'hello world'"
                ],
                "why": "Each stage receives the previous stage's output — the accumulator in a `reduce` over functions."
              },
              {
                "id": "t3",
                "question": "The final value, after `hyphenate`",
                "accept": [
                  "\"hello-world\"",
                  "hello-world",
                  "'hello-world'"
                ],
                "why": "Three one-line functions, composed. Each is trivially testable on its own, which is most of the argument for building this way."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "pipe(n => n + 1, n => n * 2, n => n - 3)(4)   →   the value after the second stage",
                "accept": [
                  "10"
                ],
                "why": "`4 + 1` is `5`, then `5 * 2` is `10`. The third stage has not run yet."
              },
              {
                "id": "t5",
                "code": "pipe(n => n + 1, n => n * 2, n => n - 3)(4)   →   the final value",
                "accept": [
                  "7"
                ],
                "why": "`10 - 3`. The seed is `4`, and each stage's return value becomes the next stage's argument."
              }
            ],
            "hints": [
              {
                "when": "you applied them right to left",
                "say": "That is `compose`. `pipe` is the other direction, and it exists because execution order and reading order matching is worth something."
              },
              {
                "when": "your final value still had spaces or capitals",
                "say": "Each stage works on the *previous stage's output*, not on the original input. Carry the value forward one stage at a time."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Build a price from stages",
            "prompt": "`pipe` (from the lesson) is given. Build `finalPrice` so that it takes 10% off, then adds 5 for shipping, then rounds to a whole number — and print `finalPrice(21)`. It should print `24`.\n",
            "lines": [
              "const finalPrice = pipe(",
              "  price => price * 0.9,",
              "  price => price + 5,",
              "  Math.round,",
              ");",
              "console.log(finalPrice(21));"
            ],
            "distractors": [
              {
                "line": "const finalPrice = compose(",
                "why": "`compose` runs its stages last-listed first: it would round, then add shipping, then take 10% off — `23.4`, not `24`. To list stages in the order they happen, use `pipe`."
              },
              {
                "line": "  price => { price * 0.9 },",
                "why": "With braces, an arrow needs `return`. This stage returns `undefined`, the next one computes `undefined + 5`, and the price becomes `NaN`."
              }
            ],
            "why": "`pipe` lists the stages in the order they run, so the code reads as the rule does. `Math.round` goes in as-is — it is already a function of one argument, and each stage is called with exactly one.",
            "hints": [
              {
                "when": "you picked compose",
                "say": "Both combinators accept the same stages. Which one runs the first-listed stage first?"
              },
              {
                "when": "your answer printed NaN",
                "say": "One of the stages returns nothing. Compare the two versions of the discount stage: which one actually hands a value to the next stage?"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Add a stage in the right place",
            "prompt": "`slug` works: it runs `title` through every function in `stages`, in order. Change it so that punctuation is dropped too — `\"Hello, World!\"` must become `\"hello-world\"`. Add one small stage to the list; where you put it matters. When it passes: in one sentence, why does your new stage have to come before `hyphenate`?\n",
            "exportName": "slug",
            "filename": "slug.js",
            "starter": "const trim = s => s.trim();\nconst lower = s => s.toLowerCase();\nconst hyphenate = s => s.replace(/\\s+/g, \"-\");\n\n// The stages, in the order they run.\nconst stages = [trim, lower, hyphenate];\n\nfunction slug(title) {\n  let value = title;\n  for (const stage of stages) value = stage(value);\n  return value;\n}\n",
            "tests": [
              {
                "label": "punctuation is dropped",
                "body": "return subject('Hello, World!') === 'hello-world';"
              },
              {
                "label": "the old behaviour still holds",
                "body": "return subject('  Hello World  ') === 'hello-world';"
              },
              {
                "label": "punctuation between words leaves one hyphen, not two",
                "body": "return subject('Rock & Roll') === 'rock-roll';"
              },
              {
                "label": "digits survive",
                "body": "return subject('Top 10 Tips') === 'top-10-tips';"
              }
            ],
            "hints": [
              {
                "when": "\"Rock & Roll\" gives rock--roll",
                "say": "Your stage runs after `hyphenate`, so the `&` had already been turned into hyphens around it. Stage order is the list order — move it earlier."
              },
              {
                "when": "capital letters disappear",
                "say": "Your stage keeps only lowercase letters, but it runs before `lower` has lowercased anything. Either move it after `lower`, or let it keep capitals too."
              },
              {
                "when": "the digits test fails",
                "say": "Your stage's allowed set is letters and spaces only. \"Punctuation\" is not everything that is not a letter — decide what a slug keeps."
              },
              {
                "when": "the old-behaviour test fails",
                "say": "Something now removes spaces, so `hyphenate` has nothing to replace. Keep spaces in your stage's allowed set; turning them into hyphens is `hyphenate`'s job."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Build pipe",
            "prompt": "The loop in `slug` is general: it works for any list of stages. Write `pipe(...fns)`, which returns a function that passes its argument through every supplied function, left to right. With no functions, it must return its argument unchanged. When it passes: in one sentence, what plays the part of the accumulator, and what is the seed?\n",
            "exportName": "pipe",
            "filename": "pipe.js",
            "starter": "// pipe(f, g, h)(x) === h(g(f(x)))\n// pipe()(x) === x\nfunction pipe() {\n\n}\n",
            "tests": [
              {
                "label": "two stages, left to right",
                "body": "return subject(function (n) { return n + 1; }, function (n) { return n * 2; })(5) === 12;"
              },
              {
                "label": "three stages",
                "body": "const f = subject(function (s) { return s.trim(); },\n                  function (s) { return s.toLowerCase(); },\n                  function (s) { return s.replace(/\\s+/g, '-'); });\nreturn f('  Hello World  ') === 'hello-world';"
              },
              {
                "label": "one stage behaves like the function itself",
                "body": "return subject(function (n) { return n * 3; })(4) === 12;"
              },
              {
                "label": "no stages returns the argument unchanged",
                "body": "return subject()(7) === 7;"
              },
              {
                "label": "a falsy value is still transformed, not skipped",
                "body": "return subject(function (n) { return n + 1; })(0) === 1;"
              },
              {
                "label": "the returned function is reusable",
                "body": "const f = subject(function (n) { return n + 1; }); return f(1) === 2 && f(10) === 11;"
              }
            ],
            "hints": [
              {
                "when": "the no-stages test fails",
                "say": "With no functions there is nothing to apply, so the seed must come straight back. `reduce` over an empty array with a seed does exactly that."
              },
              {
                "when": "your stages run right to left",
                "say": "You used `reduceRight`, which is `compose`. `pipe` is a plain `reduce`."
              },
              {
                "when": "the falsy-value test fails",
                "say": "Something in your implementation is testing the accumulated value for truthiness. The value passing through may legitimately be `0` — pass it along without inspecting it."
              },
              {
                "when": "pipe returns a number, or \"is not a function\" appears",
                "say": "`pipe(...)` must not run anything yet. It *returns a function*; the argument arrives only when that returned function is called."
              }
            ]
          }
        ]
      }
    },
    "loop-next-value": {
      "id": "loop-next-value",
      "title": "How a loop gets its next value",
      "kind": "read",
      "outcome": 3,
      "shape": "conceptual",
      "why": "Outcome 3 names it: explain how a loop obtains successive values, not just that it does. The protocol is why `for…of`, spread and destructuring work on things nobody wrote them for, and why a plain object is refused. Split from the loop forms (D-029), which it builds on.",
      "concepts": [
        "flow.iteration-protocol"
      ],
      "lede": "A `for…of` loop does not reach into an array and read position 0, then 1, then 2. It asks the thing it is looping over for the next value, again and again, and the thing answers. That question is a small public contract, and any object that answers it can be looped over.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Choosing a loop · Mutating while traversing · Rest parameters and spread"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Iterable** — an object with a `Symbol.iterator` method. **Iterator** — what that method returns: an object with `next()`.",
          "entries": [
            "**Iterable** — an object with a `Symbol.iterator` method.",
            "**Iterator** — what that method returns: an object with `next()`."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about what a loop is actually doing. Type the value, or pick\nwhat happens. A guess is fine; that is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const s = new Set([\"a\", \"b\"]);   s[0]",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you answered `\"a\"` — you are picturing a loop reading positions.** A `Set` has no positions, so `s[0]` is `undefined` — and yet `for (const v of s)` gives you `\"a\"` and then `\"b\"`. Something other than indexing is going on.\n",
                          "why": "`undefined`. A `Set` has no index `0`. `for…of` walks it anyway, which is the first clue that `for…of` does not read by position.\n"
                        },
                        {
                          "code": "typeof [1,2][Symbol.iterator]",
                          "accept": [
                            "\"function\"",
                            "function",
                            "'function'"
                          ],
                          "miss": "**If you expected `\"undefined\"`, you may think `for…of` is built into the language specifically for arrays.** It is built for **anything with `Symbol.iterator`**. Arrays qualify by having one, as do strings, `Map`, `Set`, and anything you write.\n",
                          "why": "An array is iterable because it has this method. That is the entire qualification — there is no special-casing of arrays in `for…of`.\n"
                        },
                        {
                          "question": "What happens when this runs: `for (const v of { a: 1 }) { }`?",
                          "choices": [
                            "The body runs once",
                            "The body never runs",
                            "It throws an error"
                          ],
                          "accept": [
                            "It throws an error"
                          ],
                          "miss": "**If you expected it to loop because the object has things in it — having contents is not the qualification, having the method is.**\n",
                          "why": "A plain object has no `Symbol.iterator`, so it is not iterable and `for…of` refuses. `TypeError: {} is not iterable`.\n"
                        },
                        {
                          "code": "const it = [\"x\", \"y\"][Symbol.iterator]();   it.next();   it.next().value",
                          "accept": [
                            "\"y\"",
                            "y",
                            "'y'"
                          ],
                          "miss": "**If you answered `\"x\"` — each `next()` moves on.** The first call used up `\"x\"`; the second returns `\"y\"`.\n",
                          "why": "`\"y\"`. The first `next()` produced `\"x\"`; the second produced the next value. The thing you got from `Symbol.iterator` remembers where it is.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "iter([1,2]).__next__()",
                          "accept": [
                            "1"
                          ],
                          "why": "`iter()` asks for the iterator, `__next__()` pulls one value. Same two-step protocol as JavaScript, different spelling.\n"
                        },
                        {
                          "question": "What happens when this runs: `next(iter([]))`?",
                          "choices": [
                            "It returns None",
                            "It raises an exception"
                          ],
                          "accept": [
                            "It raises an exception"
                          ],
                          "why": "Python signals exhaustion with an **exception**; JavaScript signals it with `done: true`. The contract is the same shape, the ending differs.\n"
                        },
                        {
                          "question": "`for v in {'a': 1}:` — what is `v` on the first iteration?",
                          "accept": [
                            "'a'",
                            "\"a\"",
                            "a"
                          ],
                          "why": "A dict is iterable and yields its keys. Unlike JavaScript, there is no TypeError here — the object simply defines what iterating it means.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Which interface makes `foreach` work? Type its name.",
                          "accept": [
                            "IEnumerable",
                            "IEnumerable<T>",
                            "ienumerable"
                          ],
                          "why": "`IEnumerable<T>` supplies `GetEnumerator()`, which returns an `IEnumerator<T>` with `MoveNext()` and `Current`. The same two-step contract, named explicitly.\n"
                        },
                        {
                          "question": "What type does `MoveNext()` return? Type its name.",
                          "accept": [
                            "bool",
                            "boolean"
                          ],
                          "why": "`true` while values remain. JavaScript folds this into the `done` flag on the returned object; C# keeps the flag and the value separate.\n"
                        },
                        {
                          "question": "Does `foreach` work on a type that merely has `GetEnumerator`, without the interface?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "It does — C# matches the *pattern*, not only the interface. Closer to JavaScript's duck typing than people expect.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can explain how a loop gets each next value — the mechanism, not just that it does"
              ]
            }
          ]
        },
        {
          "h": "A loop does not read a collection"
        },
        {
          "p": "It asks a question, repeatedly. The collection decides what to answer."
        },
        {
          "p": "Two words first. An **iterable** is anything with a method stored under the key\n`Symbol.iterator` — a special built-in key, written in square brackets. Calling\nthat method gives you an **iterator**: an object with a `next()` method. Each\ncall to `next()` returns a small object with two properties, `value` and\n`done`."
        },
        {
          "code": "const it = [\"x\", \"y\"][Symbol.iterator]();\n\nit.next();   // { value: \"x\", done: false }\nit.next();   // { value: \"y\", done: false }\nit.next();   // { value: undefined, done: true }",
          "lang": "js"
        },
        {
          "p": "That is the whole protocol. `for…of` is a loop around exactly those calls:"
        },
        {
          "list": [
            "Ask the iterable for an iterator — once.",
            "Call `next()`. If `done` is `true`, stop.",
            "Otherwise bind `value` to the loop variable, run the body, and go to 2."
          ],
          "ordered": true
        },
        {
          "p": "`done` becomes `true` on the call **after** the last value, not on the call that\ncarries it — the call that returns `\"y\"` still has to hand you `\"y\"`."
        },
        {
          "p": "`break` and `continue` from the loop forms lesson have exact meanings in these\nterms. `continue` goes straight back to step 2, the next `next()`. `break`\nstops calling `next()` at all, so nothing after it is ever produced."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const it = [7][Symbol.iterator]();   it.next().done",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` — the first call carries the `7`.** `done` turns `true` only on the call after the last value.\n",
                "why": "`false`. This call returns `{ value: 7, done: false }`. The next one would say `done: true`.\n"
              },
              {
                "question": "How many times does `for…of` call `next()` to walk `[\"a\", \"b\", \"c\"]`? Type the number.",
                "accept": [
                  "4",
                  "four"
                ],
                "why": "Four: three that carry a value, and one more that says `done: true`.\n"
              }
            ]
          }
        },
        {
          "block": "notice",
          "label": "Two objects, and the split matters",
          "body": [
            {
              "p": "The **iterable** makes iterators. The **iterator** only moves forward. That is\nwhat lets you loop over the same array twice — each `for…of` and each spread\nasks the array for a *fresh* iterator. An array's iterator can itself be\nwalked — its `Symbol.iterator` returns the iterator itself — so you can\nspread it or loop over it, but it carries on from wherever it is. Walk one\niterator twice, and the second walk finds it already finished."
            }
          ]
        },
        {
          "h": "Anything can implement it"
        },
        {
          "p": "Because the contract is public, an object of your own joins in by answering it.\nNothing registers, nothing inherits. Three subgoals, the same every time."
        },
        {
          "code": "const countdown = {\n  from: 3,\n  [Symbol.iterator]() {\n    // create fresh state for this walk\n    let n = this.from;\n    return {\n      next: () => {\n        // say when it is done\n        if (n <= 0) return { value: undefined, done: true };\n        // produce the next value\n        return { value: n--, done: false };\n      }\n    };\n  }\n};\n\n[...countdown];        // [3, 2, 1]\nfor (const n of countdown) { }\nconst [first, second] = countdown;   // 3, 2",
          "lang": "js",
          "file": "countdown.js"
        },
        {
          "p": "Note what came free. Spread and destructuring are **not** array features — they\nare iterator features, so implementing one method got all three."
        },
        {
          "p": "The same three subgoals on a different shape: the days of a week, starting from\nany day and wrapping round."
        },
        {
          "code": "const DAYS = [\"Mon\", \"Tue\", \"Wed\", \"Thu\", \"Fri\", \"Sat\", \"Sun\"];\n\nfunction weekFrom(start) {\n  return {\n    [Symbol.iterator]() {\n      // create fresh state for this walk\n      let step = 0;\n      const offset = DAYS.indexOf(start);\n      return {\n        next() {\n          // say when it is done\n          if (step === 7) return { value: undefined, done: true };\n          // produce the next value\n          const day = DAYS[(offset + step) % 7];\n          step++;\n          return { value: day, done: false };\n        }\n      };\n    }\n  };\n}\n\n[...weekFrom(\"Fri\")];   // [\"Fri\", \"Sat\", \"Sun\", \"Mon\", \"Tue\", \"Wed\", \"Thu\"]",
          "lang": "js",
          "file": "week-from.js"
        },
        {
          "p": "Why is the state — `n`, `step` — created **inside** the `Symbol.iterator`\nmethod? Because every walk calls that method again. State made there is fresh\nfor each walk; state made outside it would be shared, and the second walk would\nstart where the first one stopped."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`countdown` has already been spread once. What is `[...countdown].length` now? Type the number.",
                "accept": [
                  "3",
                  "three"
                ],
                "why": "`3`. The spread asks `countdown` for a fresh iterator, and the method makes a new `n` starting from `from`. The earlier walk does not use it up.\n"
              },
              {
                "question": "An iterator’s `next()` returns `{ value: 5 }` with no `done` property. Does `for…of` stop?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you answered yes — `for…of` stops only when `done` is truthy.** A missing `done` is `undefined`, which is falsy, so the loop goes on.\n",
                "why": "No. Without `done: true` the loop never learns it is finished, and keeps calling `next()`.\n"
              }
            ]
          }
        },
        {
          "p": "A **generator** is the shorthand for the same thing, and it is what you will\nnormally write. `function*` makes a function whose `yield` hands out one value\nper `next()` call — JavaScript writes the `next()` and the `done` for you:"
        },
        {
          "code": "function* countdownTo(n) {\n  while (n > 0) yield n--;\n}\n\n[...countdownTo(3)];   // [3, 2, 1]",
          "lang": "js"
        },
        {
          "scaffold": {
            "label": "Making an object iterable by hand",
            "body": [
              {
                "h": "Making an object iterable by hand"
              },
              {
                "code": "// You want for…of (and spread) to work on your object.\n\n// 1. Add a method under [Symbol.iterator].\n// 2. Inside it, create fresh state for this walk: where you are.\n// 3. Return an object with next(). In next():\n//    - if there is nothing left, return { value: undefined, done: true }\n//    - otherwise return { value: <the next one>, done: false } and move on.\n// Or: make [Symbol.iterator] a generator — *[Symbol.iterator]() { … yield … }",
                "lang": "js",
                "file": "how.js"
              },
              {
                "p": "Now do one. Fill in each line."
              },
              {
                "code": "// A shelf holds books in a private array, and should loop over their titles.\nconst shelf = {\n  books: [{ title: \"Dune\" }, { title: \"Emma\" }],\n  [Symbol.iterator]() {\n    let i = ______;                       // fresh state\n    return {\n      next: () => i < this.books.length\n        ? { value: ______, done: false }  // the next title, then move on\n        : { value: undefined, done: ______ }\n    };\n  }\n};",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`let i = 0`; the value is `this.books[i++].title`; and `done: true`. With that,\n`[...shelf]` is `[\"Dune\", \"Emma\"]`."
              }
            ]
          }
        },
        {
          "video": {
            "title": "The protocol, called by hand",
            "caption": "`next()` driven manually until `done`, then the same array through `for…of` so you can see they are the same calls. Then a hand-written iterable joining in. Watch it if the two-object split has not landed.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what does a language require of a thing before it will loop over it?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "The contract",
                  "Exhaustion signalled by"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "a `Symbol.iterator` method",
                    "`{ done: true }` on the result"
                  ],
                  [
                    "**Python**",
                    "`__iter__` returning something with `__next__`",
                    "raising `StopIteration`"
                  ],
                  [
                    "**C#**",
                    "`GetEnumerator()` — interface *or* matching shape",
                    "`MoveNext()` returning `false`"
                  ]
                ]
              }
            },
            {
              "p": "Three spellings of one design: **ask for an iterator, pull until it says stop.**\nThe interesting difference is the third column. Python's choice to end with an\nexception is why a bare `except:` inside a loop can silently swallow the end of\niteration — an error class that cannot exist in JavaScript, where the ending is a\nflag rather than a throw."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "code": "const it = obj[Symbol.iterator]();   // ask for an iterator\nit.next();                           // { value, done }\nfunction* gen() { yield 1; }         // a generator writes next() for you",
                "lang": "js"
              }
            ],
            "python": [
              {
                "p": "`iter()` and `next()` are the protocol in the open, and generators use `yield`\nexactly as JavaScript's do."
              }
            ],
            "csharp": [
              {
                "p": "`yield return` builds an iterator without writing the interface by hand — the\nsame idea as a generator, and the compiler writes the state machine for you."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say, in protocol terms, why\n`for (const v of new Set([\"a\", \"b\"]))` works when `s[0]` does not — and what one\nmethod would make the plain object in the third question loopable."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You are given a `Playlist` object holding `tracks` privately, and callers want to\nwrite `for (const t of playlist)`."
            },
            {
              "p": "Two ways to allow it:"
            },
            {
              "list": [
                "**Implement `Symbol.iterator`** on `Playlist`, yielding each track.",
                "**Expose a `tracks` array** and let callers loop over that."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what the second lets a caller do\nto your data that the first does not, and what the first costs you if callers\nalso want `playlist.length` and indexing. What you know about aliasing is\ndirectly relevant to the second option — use it."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A loop does not read a collection; it **asks for an iterator and calls `next()` until `done`**. `done` is `true` on the call after the last value.",
                "**Iterable** has `Symbol.iterator`. **Iterator** has `next()`. Two objects, and the split is what lets you loop the same thing twice.",
                "Implementing one method also gets you **spread and destructuring** — they use the same protocol. Create the walk's state inside the method, so each walk is fresh."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Iteration protocols",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Iteration_protocols",
              "note": "the iterable and iterator contracts side by side; the single page to keep."
            },
            {
              "title": "Generators",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function*",
              "note": "`yield`, and why a generator is an iterator you did not have to write."
            }
          ]
        }
      ],
      "_source": "lessons/loop-next-value.md",
      "practice": {
        "id": "loop-next-value",
        "outcome": 3,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict the protocol, and the first move",
            "prompt": "One question about where you would start, then two predictions. Type the answer, or pick one.\n",
            "items": [
              {
                "id": "p1",
                "question": "To make your own object work with `for…of`, which method must it have? Type its name.",
                "accept": [
                  "Symbol.iterator",
                  "[Symbol.iterator]",
                  "[Symbol.iterator]()",
                  "Symbol.iterator()",
                  "a Symbol.iterator method"
                ],
                "why": "That is the entire qualification. `for…of` calls it once to get an iterator, then calls `next()` on that until `done`.\n"
              },
              {
                "id": "p2",
                "question": "What happens when this runs: `for (const v of { a: 1 }) { }`?",
                "choices": [
                  "The body runs once",
                  "The body never runs",
                  "It throws an error"
                ],
                "accept": [
                  "It throws an error"
                ],
                "why": "A plain object has no `Symbol.iterator`, so `for…of` refuses. Having contents is not the qualification; having the method is.\n"
              },
              {
                "id": "p3",
                "code": "const it = [7][Symbol.iterator]();   it.next().done",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "The first call gives the `7`, so it is not done yet. `done` becomes `true` only on the call after the last value.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "typeof ({ a: 1 })[Symbol.iterator]",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "A plain object has no such method, which is exactly why `for…of` throws on it.\n"
              },
              {
                "id": "p5",
                "code": "const m = new Map([[\"k\", 1]]);   typeof m[Symbol.iterator]",
                "accept": [
                  "\"function\"",
                  "function",
                  "'function'"
                ],
                "why": "A `Map` has the method, so `for…of` walks it — no indices needed.\n"
              },
              {
                "id": "p6",
                "code": "const a = [\"x\", \"y\"];   for (const v in a) …  →  v on the first iteration",
                "accept": [
                  "\"0\"",
                  "0",
                  "'0'"
                ],
                "why": "`for…in` does not use the protocol at all: it walks keys, as strings. Only `for…of` asks for an iterator.\n"
              }
            ],
            "hints": [
              {
                "when": "you named next() for the first",
                "say": "`next()` belongs to the iterator. Something has to produce the iterator first — which method on the object does `for…of` call?\n"
              },
              {
                "when": "you answered true for done",
                "say": "The call that returns the `7` has to carry it. The end is reported by the next call, which has nothing to carry.\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "One iterator, walked twice",
            "prompt": "Predict each line this program prints, then run it.\n",
            "filename": "walk-twice.js",
            "code": "const it = [\"a\", \"b\", \"c\"][Symbol.iterator]();\nconsole.log(it.next().value);\nconsole.log([...it]);\nconsole.log([...it]);\n\nconst arr = [\"a\", \"b\"];\nconsole.log([...arr].length, [...arr].length);\n",
            "items": [
              {
                "id": "r1",
                "question": "What does the first line print?",
                "accept": [
                  "a",
                  "\"a\""
                ],
                "why": "The first `next()` hands out `\"a\"` and moves the iterator on.\n"
              },
              {
                "id": "r2",
                "question": "What does the second line print?",
                "accept": [
                  "[\"b\",\"c\"]",
                  "[\"b\", \"c\"]",
                  "['b','c']",
                  "['b', 'c']",
                  "[b,c]",
                  "b,c"
                ],
                "miss": "**If you answered `[\"a\",\"b\",\"c\"]` — spread used the same iterator, which had already given out `\"a\"`.**\n",
                "why": "Spread pulled from the iterator until `done`, starting where it was — after `\"a\"`.\n"
              },
              {
                "id": "r3",
                "question": "What does the third line print?",
                "accept": [
                  "[]",
                  "empty",
                  "an empty array"
                ],
                "why": "The iterator is finished; spreading it again gets `done` straight away.\n"
              },
              {
                "id": "r4",
                "question": "What does the fourth line print?",
                "accept": [
                  "2 2",
                  "2, 2",
                  "2,2"
                ],
                "why": "`arr` is the iterable, not an iterator. Each spread asks it for a fresh iterator, so both see both elements.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected the third line to repeat the second",
                "say": "Which object did both spreads use — the array, or the one iterator? An iterator only moves forward.\n"
              },
              {
                "when": "you expected the fourth line to be 2 0",
                "say": "Here the spreads are given the array itself. What does an iterable do each time something asks it for an iterator?\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Drive the protocol by hand",
            "prompt": "`for…of` is a loop around `next()`. Call it yourself and give what each expression produces, in order.\n",
            "items": [
              {
                "id": "t1",
                "code": "const it = [\"a\", \"b\"][Symbol.iterator]();   it.next().value",
                "accept": [
                  "\"a\"",
                  "a",
                  "'a'"
                ],
                "why": "`next()` returns an object carrying the value and a `done` flag. This is the first value.\n"
              },
              {
                "id": "t2",
                "code": "it.next();   it.next().done   (the third call)",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "The second call gave `\"b\"`; the third found nothing left. `done: true` is how JavaScript signals the end.\n"
              },
              {
                "id": "t3",
                "code": "[...it]   (the same, exhausted iterator)",
                "accept": [
                  "[]",
                  "empty",
                  "an empty array"
                ],
                "why": "An iterator only goes forward, and this one has finished. Spread asked it for values and got `done` straight away.\n"
              },
              {
                "id": "t4",
                "code": "const arr = [\"a\", \"b\"];  [...arr];  [...arr]   →  the second spread",
                "accept": [
                  "[\"a\",\"b\"]",
                  "[\"a\", \"b\"]",
                  "['a','b']",
                  "['a', 'b']",
                  "[a,b]",
                  "[a, b]",
                  "a,b"
                ],
                "why": "The array is the **iterable**, not the iterator. Each spread asks it for a fresh iterator, so it can be walked any number of times.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "const it = \"hi\"[Symbol.iterator]();   it.next();   it.next().value",
                "accept": [
                  "\"i\"",
                  "i",
                  "'i'"
                ],
                "why": "A string is iterable too, one character per `next()`. The second call gives the second character.\n"
              },
              {
                "id": "t6",
                "code": "const it = [][Symbol.iterator]();   it.next().done",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "An empty array's iterator has nothing to give, so the very first call reports `done: true`.\n"
              }
            ],
            "hints": [
              {
                "when": "you expected the exhausted iterator to start again",
                "say": "Two objects: the array, which can make iterators, and the iterator, which only moves forward. Which one did the spread get?\n"
              },
              {
                "when": "you expected done to be true on the call that returned the last value",
                "say": "The call that returns `\"b\"` has to carry `\"b\"`. The end is reported by the next call, which has nothing to carry.\n"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Write out what for…of does",
            "prompt": "Print every track in `playlist` without using `for…of` — do by hand the calls it makes. One of the extra lines is a mistake in getting the iterator; the other is a mistake in knowing when to stop.\n",
            "lines": [
              "const it = playlist[Symbol.iterator]();",
              "let step = it.next();",
              "while (!step.done) {",
              "  console.log(step.value);",
              "  step = it.next();",
              "}"
            ],
            "distractors": [
              {
                "line": "const it = playlist[Symbol.iterator];",
                "why": "That is the method, not an iterator. It has to be called — the iterator is what calling it returns.\n"
              },
              {
                "line": "while (step.value) {",
                "why": "That stops at the first falsy value — a track titled `\"\"`, a count of `0` — as well as at the end. `done` is the only signal that means \"no more\".\n"
              }
            ],
            "why": "Get the iterator once, call `next()`, and keep going until `done`. Print before asking for the next step, or the first value is lost. That is the whole of `for…of`.\n",
            "hints": [
              {
                "when": "the first value never prints",
                "say": "Each `next()` moves the iterator on. Use the value you already have before asking for another.\n"
              },
              {
                "when": "you are unsure which line starts it",
                "say": "Nothing can call `next()` until something has produced the iterator.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review an iterable someone else wrote",
            "prompt": "A colleague wrote `pageNumbers` for a print preview. The first preview shows pages 1 to 2 of 3; every preview after it is blank. Find the two defects, then fix it.\n",
            "filename": "pages.js",
            "code": "// Every page number from 1 to `pages`, for a print preview.\nfunction pageNumbers(pages) {\n  let n = 1;\n  return {\n    [Symbol.iterator]() {\n      return {\n        next() {\n          if (n < pages) return { value: n++, done: false };\n          return { value: n, done: true };\n        }\n      };\n    }\n  };\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line holds state that makes every walk after the first blank? Type the line number.",
                "accept": [
                  "3",
                  "line 3"
                ],
                "why": "`n` is made once, outside `Symbol.iterator`, so every walk shares it. The first walk leaves it at the end, and the next walk starts there. It belongs inside the method, where each walk makes its own.\n"
              },
              {
                "id": "c2",
                "question": "Which line loses the last page? Type the line number.",
                "accept": [
                  "8",
                  "line 8"
                ],
                "why": "`n < pages` stops before `n` reaches `pages`. The last page is then put on the `done: true` result, and `for…of` ignores the value on that one. The end is inclusive, so it needs `<=`.\n"
              }
            ],
            "exportName": "pageNumbers",
            "starter": "// Every page number from 1 to `pages`, for a print preview.\nfunction pageNumbers(pages) {\n  let n = 1;\n  return {\n    [Symbol.iterator]() {\n      return {\n        next() {\n          if (n < pages) return { value: n++, done: false };\n          return { value: n, done: true };\n        }\n      };\n    }\n  };\n}\n",
            "tests": [
              {
                "label": "every page, including the last",
                "body": "return [...subject(3)].join(',') === '1,2,3';"
              },
              {
                "label": "a one-page document",
                "body": "return [...subject(1)].join(',') === '1';"
              },
              {
                "label": "the preview can be shown twice",
                "body": "const p = subject(2); return [...p].join(',') === '1,2' && [...p].join(',') === '1,2';"
              },
              {
                "label": "no pages, no numbers",
                "body": "return [...subject(0)].length === 0;"
              }
            ],
            "hints": [
              {
                "when": "the shown-twice test fails",
                "say": "Where is the counter created — once, when `pageNumbers` runs, or every time a walk asks for an iterator?\n"
              },
              {
                "when": "the last page is missing",
                "say": "Which `next()` call carries page 3? Check the condition on that call, and remember that `for…of` never reads the value on a `done: true` result.\n"
              }
            ]
          },
          {
            "kind": "write",
            "title": "Make your own thing iterable",
            "prompt": "Give `makeRange(from, to)` a `Symbol.iterator` so it yields every integer from `from` up to and including `to`. Implement the one method — the tests check that spread and destructuring work too. Then, in one sentence: why must `Symbol.iterator` be a method rather than an iterator stored on the object?\n",
            "exportName": "makeRange",
            "filename": "range.js",
            "starter": "// Return an object that for…of can walk, yielding from…to inclusive.\n// Implement [Symbol.iterator]. A generator is allowed and is shorter.\nfunction makeRange(from, to) {\n  return {\n\n  };\n}\n",
            "tests": [
              {
                "label": "for…of yields each value",
                "body": "const out = []; for (const n of subject(1, 3)) out.push(n); return out.join(',') === '1,2,3';"
              },
              {
                "label": "the end is inclusive",
                "body": "const out = []; for (const n of subject(5, 5)) out.push(n); return out.join(',') === '5';"
              },
              {
                "label": "spread works, because it uses the same protocol",
                "body": "return [...subject(1, 4)].join(',') === '1,2,3,4';"
              },
              {
                "label": "destructuring works too",
                "body": "const [a, b] = subject(7, 9); return a === 7 && b === 8;"
              },
              {
                "label": "an empty range yields nothing",
                "body": "return [...subject(3, 1)].length === 0;"
              },
              {
                "label": "it can be walked twice — a fresh iterator each time",
                "body": "const r = subject(1, 2); return [...r].join(',') === '1,2' && [...r].join(',') === '1,2';"
              }
            ],
            "hints": [
              {
                "when": "the walk-it-twice test fails and the others pass",
                "say": "Your counter lives outside the method, so the second walk starts from where the first stopped. `Symbol.iterator` must create fresh state every time it is called — that is why it is a method and not an object.\n"
              },
              {
                "when": "spread fails but for…of works",
                "say": "That combination is unusual — check that `next()` returns an object with both `value` and `done`, rather than the value alone.\n"
              },
              {
                "when": "the last value is missing",
                "say": "\"Up to and including\" — check whether your condition stops at `to` or one before it.\n"
              }
            ]
          }
        ]
      }
    },
    "remove-duplication": {
      "id": "remove-duplication",
      "title": "Remove the duplication",
      "kind": "trial",
      "kicker": "TRIAL 7.4",
      "filename": "report.js",
      "exportName": "report",
      "brief": "Three functions, identical except for one decision each. Collapse them into one higher-order function the three delegate to, and export an object with all four. The behaviour must not change — the last two trials check that the shared function exists and is actually being used, because passing tests alone cannot tell a refactor from a rewrite.",
      "hint": "Line the three up and find what differs. It is not a value — it is the *number being added up*, which means it has to arrive as a function. `summarise(items, pick)` where `pick` takes an item and returns a number, and the three named functions become one line each.",
      "success": "That is the move. Note what the last two trials caught that the first four could not: identical behaviour with the duplication still in place. When you refactor, the test that the duplication is gone has to be written separately from the tests that the behaviour is right.",
      "starter": "// Collapse the three into one higher-order function they all use.\n// Export { totalPrice, totalWeight, totalCount, summarise }.\nfunction totalPrice(items) {\n  let t = 0;\n  for (const i of items) t += i.price;\n  return t;\n}\n\nfunction totalWeight(items) {\n  let t = 0;\n  for (const i of items) t += i.weight;\n  return t;\n}\n\nfunction totalCount(items) {\n  let t = 0;\n  for (const i of items) t += i.qty;\n  return t;\n}\n\nconst report = { totalPrice, totalWeight, totalCount };\n",
      "tests": [
        {
          "label": "totalPrice still adds prices",
          "body": "return subject.totalPrice([{price: 2}, {price: 3}]) === 5;"
        },
        {
          "label": "totalWeight still adds weights",
          "body": "return subject.totalWeight([{weight: 10}, {weight: 5}]) === 15;"
        },
        {
          "label": "totalCount still adds quantities",
          "body": "return subject.totalCount([{qty: 1}, {qty: 4}]) === 5;"
        },
        {
          "label": "an empty list totals zero for all three",
          "body": "return subject.totalPrice([]) === 0 && subject.totalWeight([]) === 0 &&\n       subject.totalCount([]) === 0;"
        },
        {
          "label": "summarise exists and takes (items, pick)",
          "body": "return typeof subject.summarise === \"function\" && subject.summarise.length === 2 &&\n       subject.summarise([{n: 2}, {n: 3}], function (i) { return i.n; }) === 5;"
        },
        {
          "label": "summarise works on a computed value, not just a field",
          "body": "return subject.summarise([{q: 2, p: 3}], function (i) { return i.q * i.p; }) === 6;"
        },
        {
          "label": "the three named functions are one line — no loop of their own",
          "body": "return [subject.totalPrice, subject.totalWeight, subject.totalCount]\n  .every(function (f) { return !/\\b(for|while)\\b/.test(f.toString()); });"
        }
      ],
      "_source": "trials/remove-duplication.yml"
    },
    "check-scope": {
      "id": "check-scope",
      "title": "Scope and closures check",
      "kind": "quiz",
      "unlocks": "Week 7 — Functions, pass 3",
      "questions": [
        {
          "id": "q1",
          "skill": "Closures — what is captured",
          "prompt": "What do the three functions log?",
          "code": "for (var i = 0; i < 3; i++) {\n  setTimeout(() => console.log(i), 0);\n}",
          "answers": [
            {
              "key": "A",
              "text": "`0, 1, 2` — each captured `i` as it was",
              "tag": "the natural reading",
              "correct": false
            },
            {
              "key": "B",
              "text": "`3, 3, 3` — one binding, shared by all three",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Nothing — the callbacks never run",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`undefined` three times — `i` is out of scope by then",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `var i` is one binding for the enclosing function, and all three closures refer to it. By the time the timers fire the loop has finished and it holds `3`. Swap `var` for `let` and each iteration gets its own binding.",
            "wrong": "Not quite. A closure captures the binding, not the value at creation. `var` makes one binding for the whole function, so all three see `3`. The answer is B."
          }
        },
        {
          "id": "q2",
          "skill": "Closures — independence",
          "prompt": "`makeCounter` returns `() => ++count`. What does `makeCounter()(); makeCounter()();` produce?",
          "answers": [
            {
              "key": "A",
              "text": "`1` then `1`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "`1` then `2` — the counter keeps going",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`2` then `2`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "An error — you cannot call the result directly",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Each call to `makeCounter` runs the body again and creates a fresh `count`. You share a counter only by sharing the function that closes over it.",
            "wrong": "Not quite. Two calls to `makeCounter` means two separate `count` bindings, each starting at 0. The answer is A."
          }
        },
        {
          "id": "q3",
          "skill": "Closures — shared bindings",
          "prompt": "`pair()` returns `{ inc, read }` closing over one `n`. After `p.inc(); p.inc();` what does `p.read()` give?",
          "answers": [
            {
              "key": "A",
              "text": "`0` — `read` captured `n` before `inc` changed it",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`2` — both functions share one binding",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`undefined` — `read` has its own `n`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`1` — only the last `inc` counts",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Everything created in one call shares that call's bindings. `inc` and `read` are two functions over one `n`, which is also how closures give you private state.",
            "wrong": "Not quite. Both functions were created by the same call, so they close over the same binding. `read` sees whatever `n` holds when it runs. The answer is B."
          }
        },
        {
          "id": "q4",
          "skill": "Closures — privacy",
          "prompt": "Given `const p = pair()`, what is `p.n`?",
          "answers": [
            {
              "key": "A",
              "text": "`0` — the initial value",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`2` — the current value",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`undefined` — `n` is a binding, not a property",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "It throws — `n` is private",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `n` lives in the closure's scope, not on the returned object, so there is no `n` property to read. Nothing outside the two functions can reach or change it — the oldest form of privacy in JavaScript.",
            "wrong": "Not quite. `n` is a binding in `pair`'s scope, not a property of the object it returned. Reading a property that does not exist gives `undefined` rather than throwing. The answer is C."
          }
        }
      ],
      "_source": "checks/check-scope.yml"
    },
    "designing-a-type": {
      "id": "designing-a-type",
      "title": "Designing a type",
      "kind": "read",
      "outcome": 6,
      "shape": "conceptual",
      "why": "Outcome 6 opens with designing a type, and `model.objects` is its first concept: a real thing modelled as state plus operations, including which of its fields may be absent. That last clause is where `types.nullability`, taught in `absence`, is practised most (`concepts.md`, `module-plan.md` group 17) — a model decides once what \"missing\" looks like, and every guard follows from that decision.",
      "concepts": [
        "model.objects"
      ],
      "lede": "Modelling a thing means deciding what state it holds, which of those fields are allowed to be missing, and what operations it offers on that state. Decide the missing ones once, in one place, and every method that reads them knows what to expect.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "When there is nothing there · How a method finds its object · Private state that guards an invariant"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Type** — a kind of thing your program models: the state every one of them holds and the operations it offers. **Instance** — one object of that type, made with `new`. **Optional field** — a field the model allows to be absent.",
          "entries": [
            "**Type** — a kind of thing your program models: the state every one of them holds and the operations it offers.",
            "**Instance** — one object of that type, made with `new`.",
            "**Optional field** — a field the model allows to be absent."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four short programs, each a small class. Type what the last expression\nproduces, or `error` if you think it throws. A guess is fine; that is what this\nis for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "class Counter { constructor() { this.count = 0; } inc() { this.count++; } }   const a = new Counter();  const b = new Counter();  a.inc();  a.inc();   b.count",
                          "accept": [
                            "0"
                          ],
                          "miss": "**If you answered `2` — the class is the recipe, not the thing.** Each `new Counter()` builds a fresh object, and the constructor gives *that* object its own `count`. Calling `a.inc()` changes `a` and nothing else.\n",
                          "why": "`0`. `a` and `b` are two objects with two `count`s. `inc` changed the one it was called on.\n"
                        },
                        {
                          "code": "class Book { constructor(title) { this.title = title; } }   new Book(\"Dune\").borrower",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you answered `error` — reading a field nobody set is not an error.** An instance is an object, and an expression that reads a property the object does not have evaluates to `undefined`, quietly.\n",
                          "why": "`undefined`. The constructor never set `borrower`, so it is absent — the kind of absent nobody chose.\n"
                        },
                        {
                          "code": "class Book { constructor(title, borrower) { this.title = title; this.borrower = borrower; }  label() { return this.title + \", on loan to \" + this.borrower.name; } }   new Book(\"Dune\").label()",
                          "accept": [
                            "error",
                            "typeerror",
                            "type error",
                            "throws",
                            "it throws",
                            "an error"
                          ],
                          "miss": "**If you answered `\"Dune, on loan to undefined\"` — the absence is one step further in.** No borrower was passed, so `this.borrower` is `undefined`, and `label` asks `undefined` for `.name`. That throws. The model allows a book with no borrower; the operation assumed there always is one.\n",
                          "why": "A TypeError: `Cannot read properties of undefined (reading 'name')`. The constructor let `borrower` be missing, and `label` never asked.\n"
                        },
                        {
                          "code": "class Guest { constructor(name, diet) { this.name = name; this.diet = diet ?? null; } }   new Guest(\"Ada\").diet",
                          "accept": [
                            "null"
                          ],
                          "miss": "**If you answered `undefined` — the constructor changed it.** The missing argument arrived as `undefined`, and `?? null` replaced it. This type decided, once, that \"no dietary need\" is stored as `null`.\n",
                          "why": "`null`. `diet ?? null` turns either absence into one chosen value, so every method that reads `diet` has exactly one \"nothing\" to check for.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can model a real thing as state plus operations, including which of its fields may be absent"
              ]
            }
          ]
        },
        {
          "h": "A type is state plus operations"
        },
        {
          "p": "A **type** is a kind of thing your program keeps track of — a book, a contact,\nan order. Designing one is deciding:"
        },
        {
          "list": [
            "**What state it holds** — the fields every one of them has.",
            "**Which of those fields may be absent** — and how \"absent\" is stored.",
            "**What operations it offers** — the things you can ask it or tell it to do, and what each one does to that state."
          ],
          "ordered": true
        },
        {
          "p": "There is a fourth decision — what it *refuses* — and it is the next lesson."
        },
        {
          "p": "In JavaScript the usual way to write a type down is a `class`. Four pieces,\neach doing one job:"
        },
        {
          "code": "class Book {\n  constructor(title, author) {     // runs once, for each new book\n    this.title = title;            // state: set on the new object\n    this.author = author;\n    this.borrower = null;          // state: on the shelf, so nobody has it\n  }\n\n  checkOut(name) {                 // an operation that changes state\n    this.borrower = name;\n  }\n\n  isOnLoan() {                     // an operation that answers a question\n    return this.borrower !== null;\n  }\n}\n\nconst dune = new Book(\"Dune\", \"Herbert\");    // new: make one, run the constructor\ndune.checkOut(\"Ana\");\ndune.isOnLoan();                               // true",
          "lang": "js",
          "file": "book.js"
        },
        {
          "list": [
            "**`constructor`** runs once, when `new Book(...)` makes an object. Its job is to give that object every field it will ever have.",
            "**`this`** inside a method is the object the method was called on. `dune.checkOut(\"Ana\")` supplies `dune` — the call-site rule you met with methods passed as values.",
            "**Methods** are the operations. Each one reads or changes `this`'s state.",
            "**`new`** makes a fresh object each time. Two books have two `borrower`s, which is why the first opening question printed `0`."
          ]
        },
        {
          "p": "The two operations are the two kinds you have met before: `checkOut` changes\nthe world and returns nothing, `isOnLoan` answers a question and changes\nnothing. A good type offers both, and each method is clearly one or the other."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const a = new Book(\"Dune\", \"Herbert\");  const b = new Book(\"Emma\", \"Austen\");  a.checkOut(\"Ana\");   b.isOnLoan()",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` — `checkOut` changed `a`.** `b` is a separate object with its own `borrower`, still `null`.\n",
                "why": "`false`. The call `a.checkOut(\"Ana\")` supplied `a` as `this`, so only `a`'s state changed.\n"
              },
              {
                "code": "new Book(\"Dune\", \"Herbert\").checkOut(\"Ana\")",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined`. `checkOut` acts — it changes the book — and returns nothing. Its effect is on the object, not in the result.\n"
              }
            ]
          }
        },
        {
          "h": "Deciding which fields may be absent"
        },
        {
          "p": "Some fields are always there: a book always has a title. Some are allowed to be\nmissing: a book on the shelf has no borrower; a contact may have no phone. The\nlesson on absence ended with a rule — **guard where absence is expected, and let\na bug fail loudly**. A type is where you decide which is which, once, instead\nof at every line that reads the field."
        },
        {
          "p": "For each field, one of three answers:"
        },
        {
          "table": {
            "head": [
              "The field is…",
              "So the constructor…",
              "And the methods…"
            ],
            "rows": [
              [
                "**Required**",
                "refuses to build the object without it — throws",
                "use it without a guard"
              ],
              [
                "**Optional**",
                "stores one chosen absence, the same way every time — usually `null`",
                "guard it, every time they read it"
              ],
              [
                "**Derived**",
                "does not store it at all",
                "compute it from the other fields"
              ]
            ]
          }
        },
        {
          "p": "Storing one absence matters because JavaScript has two. A guest built with no\ndietary need would otherwise hold `undefined`, and one built from data that said\n`\"diet\": null` would hold `null`; a method checking `=== null` would handle one\nand not the other. Converting both to `null` in the constructor means there is\nexactly one \"nothing\" to check for."
        },
        {
          "p": "Two worked examples. The four comments in the first are the steps to keep."
        },
        {
          "code": "// \"A wedding guest has a name and a table number. Some have a\n//  dietary need; most do not.\"\n\nclass Guest {\n  constructor({ name, table, diet }) {\n    // refuse what is required\n    if (!name || !table) throw new TypeError(\"name and table are required\");\n\n    // store the required state\n    this.name = name;\n    this.table = table;\n\n    // store each optional field as one chosen absence\n    this.diet = diet ?? null;             // missing or null -> null\n  }\n\n  // write the methods, guarding each optional field they read\n  placeCard() {\n    const card = this.name + \" · table \" + this.table;\n    return this.diet === null ? card : card + \" · \" + this.diet;\n  }\n}",
          "lang": "js",
          "file": "guest.js"
        },
        {
          "code": "// \"A parcel has a tracking code and a weight. Until it is delivered,\n//  it has no delivery date.\"\n\nclass Parcel {\n  constructor(code, grams) {\n    if (!code) throw new TypeError(\"a parcel needs a tracking code\");\n\n    this.code = code;\n    this.grams = grams;\n    this.deliveredOn = null;              // optional: not delivered yet\n  }\n\n  deliver(date) { this.deliveredOn = date; }\n\n  status() {\n    if (this.deliveredOn === null) return this.code + \": in transit\";\n    return this.code + \": delivered \" + this.deliveredOn;\n  }\n\n  get kilos() { return this.grams / 1000; }   // derived: never stored\n}",
          "lang": "js",
          "file": "parcel.js"
        },
        {
          "p": "Different things, the same four steps. In both, one method reads the optional\nfield and guards it; nothing guards `name` or `code`, because the constructor\nalready made sure they are there. If one of them is ever missing, that is a\nbug, and you want the loud failure."
        },
        {
          "p": "`get kilos()` is a **getter**: a method you read like a field — `p.kilos`, no\nparentheses. Use one for derived state. Storing `kilos` as well as `grams`\nwould let the two disagree."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "new Parcel(\"ZX9\", 1500).status()",
                "accept": [
                  "\"ZX9: in transit\"",
                  "ZX9: in transit",
                  "'ZX9: in transit'"
                ],
                "why": "`\"ZX9: in transit\"`. The constructor set `deliveredOn` to `null`, and `status` guards exactly that.\n"
              },
              {
                "code": "new Guest({ name: \"Bo\", table: 4, diet: undefined }).placeCard()",
                "accept": [
                  "\"Bo · table 4\"",
                  "Bo · table 4",
                  "'Bo · table 4'"
                ],
                "miss": "**If you answered with `· undefined` on the end — look at what the constructor stores.** `diet ?? null` turned `undefined` into `null`, and `placeCard` checks for `null`. That is the point of deciding it once.\n",
                "why": "`\"Bo · table 4\"`. Missing, `undefined` or `null` — all three arrive as the one absence this type chose, and the one guard catches it.\n"
              },
              {
                "code": "new Parcel(\"ZX9\", 250).kilos",
                "accept": [
                  "0.25"
                ],
                "why": "`0.25`. A getter is read like a field and computed on every read, so it can never fall out of step with `grams`.\n"
              }
            ]
          }
        },
        {
          "h": "An operation belongs on the type when it uses the type's state"
        },
        {
          "p": "Not every function that has something to do with books belongs on `Book`. Ask\none question of each candidate: **does it read or change this object's own\nstate?**"
        },
        {
          "code": "// Does it read or write this object's OWN state?\n//   yes -> a method on the type.\n//   no  -> a plain function. It can live beside the class, not in it.\n\n// isOnLoan()           reads this.borrower           -> method\n// formatCurrency(n)    turns 1099 into \"$10.99\"      -> plain function\n// daysOverdue(today)   reads this.dueOn              -> method",
          "lang": "js",
          "file": "belongs.js"
        },
        {
          "p": "`formatCurrency` touches no book. Putting it on `Book` would mean you need a\nbook to format a price, which is a sign it was never the book's job."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "A `Parcel` is being designed. Method or plain function: `gramsToPounds(g)`, which converts any weight?",
                "choices": [
                  "Method",
                  "Plain function"
                ],
                "accept": [
                  "Plain function"
                ],
                "miss": "**If you answered method — it converts *any* weight.** It does not read a parcel's state; it just takes a number. A getter like `get pounds()` that reads `this.grams` would be a method.\n",
                "why": "A plain function. Nothing in it reads `this`.\n"
              },
              {
                "question": "Method or plain function: `isHeavy()`, which is true when this parcel weighs over 20 kg?",
                "choices": [
                  "Method",
                  "Plain function"
                ],
                "accept": [
                  "Method"
                ],
                "why": "A method (or a getter). It reads `this.grams` — this parcel's own state.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Designing a type from a description",
            "body": [
              {
                "h": "Designing a type from a description"
              },
              {
                "p": "The same four steps, with the middle of them left to you."
              },
              {
                "code": "// \"A support ticket has an id and a subject. It may be assigned to\n//  a staff member, or not yet. When it is closed, it records when.\"\n\nclass Ticket {\n  constructor(id, subject) {\n    // refuse what is required\n    if (!id || !subject) throw new TypeError(\"id and subject are required\");\n\n    // store the required state\n    this.id = id;\n    this.subject = subject;\n\n    // store each optional field as one chosen absence\n    //   ______ = ______;\n    //   ______ = ______;\n  }\n\n  // write the methods, guarding each optional field they read\n  //   summary() -> \"#12 Printer jam (unassigned)\" or \"#12 Printer jam (Kim)\"\n}",
                "lang": "js",
                "file": "ticket.js"
              },
              {
                "p": "Two optional fields: `this.assignee = null;` and `this.closedAt = null;`. And\n`summary` guards the one it reads:"
              },
              {
                "code": "summary() {\n  const who = this.assignee === null ? \"unassigned\" : this.assignee;\n  return \"#\" + this.id + \" \" + this.subject + \" (\" + who + \")\";\n}",
                "lang": "js"
              },
              {
                "p": "It does not guard `id` or `subject`. The constructor already refused a ticket\nwithout them."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the third, change the\n`Book` class in two different ways so that `new Book(\"Dune\").label()` no longer\nthrows: once in `label`, and once in the constructor. Which of the two is the\ndecision a model should make?"
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You are modelling an `Order` that can be draft, placed, or cancelled. A cancelled\norder cannot be placed; a placed order cannot go back to draft. A placed order\nrecords `placedAt`; a draft has no `placedAt` yet."
            },
            {
              "p": "Two designs:"
            },
            {
              "list": [
                "**A `status` field** with methods that check it — `place()` throws if `status !== \"draft\"`, and `placedAt` is `null` until it is placed.",
                "**Separate types** — `DraftOrder` has `place()` and no `placedAt` at all; `PlacedOrder` always has one."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: where the invalid transition is\ncaught in each, which fields may be absent in each and which methods have to\nguard them, and what the second does to code that holds an order and does not\nyet know which kind it is."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A type is **state plus operations**: a `constructor` gives each new object its fields, and methods read or change them through `this`.",
                "Decide each field once: **required** (refuse without it), **optional** (store one chosen absence — `null` — every time), or **derived** (a getter, never stored).",
                "Guard optional fields where they are read; leave required ones unguarded so a bug fails loudly. An operation that uses no state is a plain function."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Classes",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes",
              "note": "constructors, methods and getters; the parts of the page used here."
            },
            {
              "title": "get",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/get",
              "note": "a method read like a property, for derived state."
            },
            {
              "title": "Nullish coalescing (??)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Nullish_coalescing",
              "note": "the constructor's tool for turning either absence into one."
            }
          ]
        }
      ],
      "_source": "lessons/designing-a-type.md",
      "practice": {
        "id": "designing-a-type",
        "outcome": 6,
        "steps": [
          {
            "kind": "predict",
            "title": "Whose state, and which of it may be missing",
            "prompt": "Three quick ones. Type what the last expression produces, or answer the question. Where there are buttons, pick one.\n",
            "items": [
              {
                "id": "p1",
                "code": "class Tally { constructor() { this.n = 0; } add() { this.n++; } }   const x = new Tally();  const y = new Tally();  x.add();  y.add();  y.add();   x.n",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered `3` — each `new Tally()` is its own object.** The constructor gives each one its own `n`, and `add` changes the one it was called on.\n",
                "why": "`1`. `x` was added to once; `y`'s two calls changed `y`."
              },
              {
                "id": "p2",
                "question": "A library card has a number and the holder's name. It may have a fine owed, or none. Before any method: which field may be absent? Name it.",
                "accept": [
                  "fine",
                  "the fine",
                  "fine owed",
                  "the fine owed",
                  "fineOwed",
                  "fine_owed"
                ],
                "miss": "**The first design step is deciding, per field, whether it is always there.** A card always has a number and a holder. \"It may have a fine, or none\" is the description telling you which one is optional.\n",
                "why": "The fine. The number and the holder are required; a card with no fine is normal, so the model must allow it and store one chosen absence for it."
              },
              {
                "id": "p3",
                "code": "class Room { constructor(no, guest) { this.no = no; this.guest = guest ?? null; }  sign() { return this.guest === null ? \"vacant\" : this.guest.name; } }   new Room(12).sign()",
                "accept": [
                  "\"vacant\"",
                  "vacant",
                  "'vacant'"
                ],
                "miss": "**If you answered `error` — the constructor already turned the missing guest into `null`,** and `sign` guards exactly that before it reads `.name`.\n",
                "why": "`\"vacant\"`. The missing argument arrived as `undefined`, `?? null` stored it as `null`, and the guard caught it."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "class Seat { constructor(row) { this.row = row; this.holder = null; }  label() { return this.holder === null ? this.row + \" free\" : this.row + \" \" + this.holder; } }   const s = new Seat(\"B4\");  s.holder = undefined;   s.label()",
                "accept": [
                  "\"B4 undefined\"",
                  "B4 undefined",
                  "'B4 undefined'"
                ],
                "miss": "**If you answered `\"B4 free\"` — the guard asks `=== null`, and somebody stored `undefined`.** The type chose one absence; code that stores the other one slips past every guard written for it.\n",
                "why": "`\"B4 undefined\"`. `undefined === null` is false, so the label treats the seat as held — by `undefined`."
              },
              {
                "id": "p5",
                "code": "The Room class above.   new Room(12, { name: \"Kim\" }).sign()",
                "accept": [
                  "\"Kim\"",
                  "Kim",
                  "'Kim'"
                ],
                "why": "`\"Kim\"`. A guest was given, so `?? null` kept it, and the guard let it through."
              },
              {
                "id": "p6",
                "question": "You are designing a `Recipe` type. Method or plain function: `cupsToGrams(cups)`, which converts any amount of flour?",
                "choices": [
                  "Method",
                  "Plain function"
                ],
                "accept": [
                  "Plain function"
                ],
                "why": "A plain function. It reads no recipe's state — it converts a number. A method is an operation on *this* object."
              }
            ],
            "hints": [
              {
                "when": "you answered 3 for x.n",
                "say": "Count the objects. How many times did `new` run? Each run of the constructor made a separate `this` with its own `n`."
              },
              {
                "when": "you answered error for the Room",
                "say": "Read the constructor before the method. What does `guest ?? null` store when no guest was passed?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Two bikes, one class",
            "prompt": "A bike-share station. Predict the four lines this prints, then run it.\n",
            "filename": "bikes.js",
            "code": "class Bike {\n  constructor(id) {\n    this.id = id;\n    this.rider = null;          // nobody has it yet\n  }\n  rent(name) { this.rider = name; }\n  dock() { this.rider = null; }\n  status() {\n    return this.rider === null ? this.id + \" docked\" : this.id + \" out with \" + this.rider;\n  }\n}\n\nconst b1 = new Bike(\"B1\");\nconst b2 = new Bike(\"B2\");\nb1.rent(\"Ana\");\nconsole.log(b1.status());\nconsole.log(b2.status());\nb2.rent(\"Bo\");\nb1.dock();\nconsole.log(b1.status() + \" / \" + b2.status());\nconsole.log(b1.rent(\"Cy\"));\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "B1 out with Ana",
                  "\"B1 out with Ana\""
                ],
                "why": "`rent` was called on `b1`, so `this` was `b1` and its `rider` became `\"Ana\"`."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "B2 docked",
                  "\"B2 docked\""
                ],
                "miss": "**If you predicted `B2 out with Ana` — `b2` is a separate object.** Its `rider` was set to `null` by its own constructor call, and nothing has changed it.\n",
                "why": "`b2` has its own `rider`, still `null`."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "B1 docked / B2 out with Bo",
                  "\"B1 docked / B2 out with Bo\""
                ],
                "why": "`b1.dock()` put `b1`'s rider back to the chosen absence, `null`; `b2` was rented to Bo."
              },
              {
                "id": "r4",
                "question": "Fourth line printed",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined`. `rent` acts on the bike and returns nothing, so logging its result prints `undefined` — even though the bike did change."
              }
            ],
            "hints": [
              {
                "when": "you predicted Ana on the second line",
                "say": "Ask which object each call was made on. `b1.rent(...)` supplies `b1` as `this`; `b2` was never mentioned."
              },
              {
                "when": "you predicted \"B1 out with Cy\" on the last line",
                "say": "That is what `b1.status()` would say afterwards. The line logs what `rent` *returns* — look for a `return` in it."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "State after each line",
            "prompt": "`class Locker { constructor(no) { this.no = no; this.code = null; this.uses = 0; } lock(code) { this.code = code; this.uses++; } unlock(code) { if (code === this.code) this.code = null; } get locked() { return this.code !== null; } }`. Then: `const l = new Locker(3);` A: `l.lock(\"1234\");` B: `l.unlock(\"0000\");` C: `l.unlock(\"1234\");` D: `l.lock(\"99\");`. Give each value.\n",
            "items": [
              {
                "id": "t1",
                "question": "After line B, `l.locked`",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`\"0000\"` is not the stored code, so `unlock` changed nothing. `code` is still `\"1234\"`, and the getter reports locked."
              },
              {
                "id": "t2",
                "question": "After line C, `l.code`",
                "accept": [
                  "null"
                ],
                "miss": "**If you answered `undefined` — `unlock` stores the absence this type chose,** and the constructor chose `null`.\n",
                "why": "`null`. The right code unlocked it, and `unlock` put `code` back to the one absence the type uses."
              },
              {
                "id": "t3",
                "question": "After line D, `l.uses`",
                "accept": [
                  "2"
                ],
                "why": "`2`. Only `lock` counts, and it ran twice. The unlocks changed `code` and nothing else."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "After line C, `l.locked`",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. A getter is recomputed on every read, so it follows `code` — derived state never goes stale."
              },
              {
                "id": "t5",
                "question": "`const m = new Locker(4);` — after line D, `m.uses`",
                "accept": [
                  "0"
                ],
                "why": "`0`. Lines A–D all called methods on `l`. `m` has its own `uses`, untouched."
              }
            ],
            "hints": [
              {
                "when": "you answered false for l.locked after B",
                "say": "`unlock` only clears the code when the one you pass matches the one stored. Does `\"0000\"` match?"
              },
              {
                "when": "you answered 4 for l.uses",
                "say": "Look for `uses++`. Which method has it?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Decide which field may be absent",
            "prompt": "A contact must have a name and an email; many have no phone. The class decides once, in the constructor, that \"no phone\" is stored as `null` — and `label()` relies on that. Fill the gap. Then, in one sentence: why decide it in the constructor rather than in every method that reads `phone`?\n",
            "exportName": "Contact",
            "filename": "contact.js",
            "starter": "class Contact {\n  constructor({ name, email, phone }) {\n    if (!name || !email) throw new TypeError(\"name and email are required\");\n    this.name = name;\n    this.email = email;\n    this.phone = phone;   // ___ gap: when no phone is given, store null\n  }\n\n  // \"Ada <ada@example.org>\", plus \" · 555-0100\" only when there is a phone.\n  label() {\n    const base = this.name + \" <\" + this.email + \">\";\n    return this.phone === null ? base : base + \" · \" + this.phone;\n  }\n}\n",
            "tests": [
              {
                "label": "a contact with no phone stores null",
                "body": "return new subject({ name: 'Ada', email: 'ada@example.org' }).phone === null;"
              },
              {
                "label": "a contact with no phone labels without one",
                "body": "return new subject({ name: 'Ada', email: 'ada@example.org' }).label() === 'Ada <ada@example.org>';"
              },
              {
                "label": "a contact with a phone labels with it",
                "body": "return new subject({ name: 'Bo', email: 'bo@example.org', phone: '555-0100' }).label() === 'Bo <bo@example.org> · 555-0100';"
              },
              {
                "label": "an explicit null phone is still absent",
                "body": "return new subject({ name: 'Cy', email: 'cy@example.org', phone: null }).label() === 'Cy <cy@example.org>';"
              },
              {
                "label": "a missing email is still refused",
                "body": "try { new subject({ name: 'Di' }); return false; } catch (e) { return e instanceof TypeError; }"
              }
            ],
            "hints": [
              {
                "when": "the label ends in \" · undefined\"",
                "say": "A missing property arrives as `undefined`, and `label()` only checks for `null`. JavaScript has two absences; the constructor is where this type picks one."
              },
              {
                "when": "the label ends in \" · \" with nothing after it",
                "say": "You stored an empty string, which is a value, not an absence. `label()` asks whether `phone` is `null` — store what it asks about."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "One optional field, one required",
            "prompt": "`Member` works as written. Change two things. First: a member may now belong to a team, passed as a third argument, or not. Store \"no team\" as `null`, and have `badge()` add `\" · team <name>\"` only when there is one. Second: a name is required — throw a `TypeError` when it is missing. Then, in one sentence: which of your two changes makes `badge()` safe to call on any member, and why?\n",
            "exportName": "Member",
            "filename": "member.js",
            "starter": "class Member {\n  constructor(name, since) {\n    this.name = name;\n    this.since = since;\n  }\n  badge() {\n    return this.name + \" · since \" + this.since;\n  }\n}\n",
            "tests": [
              {
                "label": "a member with no team keeps the old badge",
                "body": "return new subject('Ada', 2021).badge() === 'Ada · since 2021';"
              },
              {
                "label": "a member with a team shows it",
                "body": "return new subject('Bo', 2023, 'Red').badge() === 'Bo · since 2023 · team Red';"
              },
              {
                "label": "no team is stored as null",
                "body": "return new subject('Ada', 2021).team === null;"
              },
              {
                "label": "an explicit null team is still no team",
                "body": "return new subject('Cy', 2020, null).badge() === 'Cy · since 2020';"
              },
              {
                "label": "a missing name is refused with a TypeError",
                "body": "try { new subject(undefined, 2021); return false; } catch (e) { return e instanceof TypeError; }"
              }
            ],
            "hints": [
              {
                "when": "the null test fails but the badges are right",
                "say": "Your badge copes with a missing team, but the field holds `undefined` when no team is passed. The model stores *one* absence, and the spec picked `null` — decide it in the constructor."
              },
              {
                "when": "the badge ends in \" · team undefined\"",
                "say": "The badge checks for `null` and the field holds `undefined`. Either change what the constructor stores, or check for the absence it actually stores — but the spec asks for `null`."
              },
              {
                "when": "the missing-name test fails",
                "say": "A required field is refused at construction: check it first, and throw before any field is stored."
              }
            ]
          },
          {
            "kind": "write",
            "title": "A subscription, from its description",
            "prompt": "Write a `Subscription` class. It is built as `new Subscription(email, plan)`; both are required (throw a `TypeError` if either is missing). It is active until `cancel(date)` is called; until then `cancelledOn` is `null`. `active` is a getter. `describe()` returns `\"ada@example.org · pro · active\"`, or `\"ada@example.org · pro · cancelled 2026-03-01\"` once cancelled. Then, in one sentence: why is `active` a getter and not a stored field?\n",
            "exportName": "Subscription",
            "filename": "subscription.js",
            "starter": "// new Subscription(email, plan) — both required (TypeError)\n// cancelledOn — null until cancel(date)\n// cancel(date) — records the date\n// get active — true until cancelled\n// describe() — \"email · plan · active\" or \"email · plan · cancelled <date>\"\nclass Subscription {\n\n}\n",
            "tests": [
              {
                "label": "a new subscription is active",
                "body": "return new subject('a@x.org', 'pro').active === true;"
              },
              {
                "label": "cancelledOn starts as null",
                "body": "return new subject('a@x.org', 'pro').cancelledOn === null;"
              },
              {
                "label": "describe while active",
                "body": "return new subject('a@x.org', 'pro').describe() === 'a@x.org · pro · active';"
              },
              {
                "label": "cancel records the date and ends the subscription",
                "body": "const s = new subject('a@x.org', 'pro'); s.cancel('2026-03-01'); return s.active === false && s.cancelledOn === '2026-03-01';"
              },
              {
                "label": "describe once cancelled",
                "body": "const s = new subject('a@x.org', 'basic'); s.cancel('2026-03-01'); return s.describe() === 'a@x.org · basic · cancelled 2026-03-01';"
              },
              {
                "label": "a missing plan is refused",
                "body": "try { new subject('a@x.org'); return false; } catch (e) { return e instanceof TypeError; }"
              },
              {
                "label": "two subscriptions are independent",
                "body": "const a = new subject('a@x.org', 'pro'), b = new subject('b@x.org', 'pro'); a.cancel('2026-01-01'); return b.active === true;"
              }
            ],
            "hints": [
              {
                "when": "the cancelledOn test fails",
                "say": "The field has to exist from the start, holding the absence the spec chose. Set it in the constructor; do not leave it for `cancel` to create."
              },
              {
                "when": "active is a function rather than true or false",
                "say": "The tests read `s.active` without calling it. A getter — `get active() { … }` — is read like a field and computed on every read."
              },
              {
                "when": "active stays true after cancel",
                "say": "If you stored `active` as a field, `cancel` has to remember to change it too — two fields that can disagree. Derive it from `cancelledOn` instead."
              },
              {
                "when": "the missing-plan test fails",
                "say": "Refuse before you store. Check both required arguments at the top of the constructor and throw a `TypeError`."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a driver roster",
            "prompt": "An AI assistant was asked for \"a driver with a required licence and an optional assigned vehicle\". This is what it wrote. Two things are wrong. Answer by line number where asked, then fix the class.\n",
            "filename": "driver.js",
            "code": "class Driver {\n  constructor(name, licence, vehicle) {\n    this.name = name;\n    this.licence = licence;\n    this.vehicle = vehicle || null;\n  }\n  assign(vehicle) { this.vehicle = vehicle; }\n  unassign() { this.vehicle = undefined; }\n  describe() {\n    if (this.vehicle === null) return this.name + \" (no vehicle)\";\n    return this.name + \" drives \" + this.vehicle.plate;\n  }\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "`d.assign({ plate: \"7XK\" });  d.unassign();  d.describe()` — which line throws? Give the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "Line 11 reads `this.vehicle.plate`. The guard on line 10 let `undefined` through, because it asks `=== null`."
              },
              {
                "id": "c2",
                "question": "Which line stores a different absence from the one the constructor chose?",
                "accept": [
                  "8",
                  "line 8"
                ],
                "miss": "**Compare line 5 with line 8.** The constructor chose `null` for \"no vehicle\"; `unassign` stores `undefined`. Every guard written for one absence misses the other.\n",
                "why": "Line 8. The type's one \"nothing\" is `null`; `unassign` must store that too."
              },
              {
                "id": "c3",
                "question": "`new Driver(\"Ada\")` — does it throw?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The licence is required by the description, but the constructor never refuses a missing one, so a driver with `licence: undefined` exists and every later use of it has to cope."
              }
            ],
            "starter": "// A driver has a required name and licence, and an optional vehicle.\nclass Driver {\n  constructor(name, licence, vehicle) {\n    this.name = name;\n    this.licence = licence;\n    this.vehicle = vehicle || null;\n  }\n  assign(vehicle) { this.vehicle = vehicle; }\n  unassign() { this.vehicle = undefined; }\n  describe() {\n    if (this.vehicle === null) return this.name + \" (no vehicle)\";\n    return this.name + \" drives \" + this.vehicle.plate;\n  }\n}\n",
            "exportName": "Driver",
            "tests": [
              {
                "label": "a new driver with no vehicle",
                "body": "return new subject('Ada', 'L-1').describe() === 'Ada (no vehicle)';"
              },
              {
                "label": "an assigned vehicle is described",
                "body": "const d = new subject('Ada', 'L-1'); d.assign({ plate: '7XK' }); return d.describe() === 'Ada drives 7XK';"
              },
              {
                "label": "after unassign, no vehicle and no error",
                "body": "const d = new subject('Ada', 'L-1', { plate: '7XK' }); d.unassign(); return d.describe() === 'Ada (no vehicle)' && d.vehicle === null;"
              },
              {
                "label": "a missing licence is refused with a TypeError",
                "body": "try { new subject('Ada'); return false; } catch (e) { return e instanceof TypeError; }"
              }
            ],
            "hints": [
              {
                "when": "the unassign test fails",
                "say": "`unassign` stores `undefined`, and `describe` guards `null`. Store the absence the constructor chose."
              },
              {
                "when": "the unassign test fails but describe no longer throws",
                "say": "You widened the guard, which stops the crash, but the field still holds a second kind of \"nothing\". The test checks the model stores one absence: fix `unassign`, not only `describe`."
              },
              {
                "when": "the licence test fails",
                "say": "Nothing refuses a driver without a licence. A required field is checked at the top of the constructor, before anything is stored."
              }
            ]
          }
        ]
      }
    },
    "encapsulation": {
      "id": "encapsulation",
      "title": "Private state that guards an invariant",
      "kind": "read",
      "outcome": 6,
      "shape": "conceptual",
      "why": "A type earns its keep by what it refuses, and that only holds if nothing outside can reach the state it guards. `model.encapsulation` was taught under another concept's tag in the combined modeling page until the concept review found it missing (D-014); it is the place aliasing, from copying and sharing, and private state, from closures, meet in one design decision.",
      "concepts": [
        "model.encapsulation"
      ],
      "lede": "An invariant is a fact about an object's state that must always be true — a balance never below zero, no duplicate tracks. A check inside a method guards that method. Only private state makes it a guarantee, and handing that state out undoes it.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Designing a type · Closures keep their surroundings · Copying versus aliasing"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Invariant** — a fact about an object's state that every operation keeps true. **Encapsulation** — keeping that state where only the type's own operations can reach it.",
          "entries": [
            "**Invariant** — a fact about an object's state that every operation keeps true.",
            "**Encapsulation** — keeping that state where only the type's own operations can reach it."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four short programs about keeping state safe. Type what the last expression\nproduces, or `error` if you think it throws. Where there are buttons, pick one."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "class Account { balance = 0;  withdraw(n) { if (n > this.balance) throw new RangeError(\"insufficient funds\"); this.balance -= n; } }   const a = new Account();  a.balance = -50;   a.balance",
                          "accept": [
                            "-50"
                          ],
                          "miss": "**If you answered `0` or `error` — the check guards `withdraw`, not the field.** `balance` is a public field. Any caller can write straight to it, and nothing in the class runs when they do.\n",
                          "why": "`-50`. The invariant \"never below zero\" held only for callers polite enough to use `withdraw`.\n"
                        },
                        {
                          "question": "`class Bag { #xs = [];  get xs() { return this.#xs; } }`, then `const b = new Bag();  b.xs.push(1);` — does the private array now hold 1?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "miss": "**If you answered no because `#xs` is private — the field is private; the array is not.** The getter handed out a reference to the very array the field holds, and `push` went through it.\n",
                          "why": "Yes. `#xs` stops anyone *naming* the field. It does nothing about a reference you give away.\n"
                        },
                        {
                          "code": "function makeTally() { let n = 0;  return { inc: () => ++n }; }   const t = makeTally();  t.inc();   t.n",
                          "accept": [
                            "undefined"
                          ],
                          "miss": "**If you answered `1` — `n` is a variable the closure keeps, not a property of the returned object.** The object has one property, `inc`. Nothing outside can read or change `n` except by calling it.\n",
                          "why": "`undefined`. A closure hides state as completely as a private field does.\n"
                        },
                        {
                          "code": "class Temp { #celsius = 20; }   new Temp().#celsius",
                          "accept": [
                            "error",
                            "syntaxerror",
                            "syntax error",
                            "an error",
                            "it throws",
                            "throws"
                          ],
                          "miss": "**If you answered `20` or `undefined` — a `#` name is not an ordinary property with an odd spelling.** Outside the class body it cannot be written at all; the program is refused before it runs.\n",
                          "why": "A SyntaxError. `#celsius` exists only inside `Temp`'s own braces.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can build a type whose invariant nothing outside can break, and show how handing out its internal state would break it"
              ]
            }
          ]
        },
        {
          "h": "An invariant, and who can break it"
        },
        {
          "p": "An **invariant** is a fact about an object's state that must always be true.\nA basket never holds a line with zero quantity. A balance never goes below zero.\nA playlist never holds the same track twice."
        },
        {
          "p": "A check at the top of a method keeps the invariant true *for that method*. The\nfirst opening question showed what that is worth when the state is a public\nfield: `withdraw` refused the overdraft, and the caller wrote `-50` straight\npast it. Every caller is now responsible for the invariant, and sooner or later\none of them is not."
        },
        {
          "p": "The fix is to make the state unreachable from outside, so the type's own\noperations are the only way in. In a class, that is a **private field**: its\nname starts with `#`, and it exists only inside the class body."
        },
        {
          "p": "Two worked examples. The comments in the first are the steps to keep."
        },
        {
          "code": "// \"A basket never holds a line with a quantity below 1.\"\n\nclass Basket {\n  // make the state private\n  #items = [];\n\n  add(item, qty = 1) {\n    // check before you change\n    if (qty < 1) throw new RangeError(\"qty must be at least 1\");\n    this.#items.push({ item, qty });\n  }\n\n  // hand out only what cannot break it\n  get total() {\n    return this.#items.reduce((t, l) => t + l.item.price * l.qty, 0);\n  }\n}",
          "lang": "js",
          "file": "basket.js"
        },
        {
          "code": "// \"A thermostat's target stays between 10 and 30 degrees.\"\n\nclass Thermostat {\n  #target = 20;\n\n  setTarget(t) {\n    if (t < 10 || t > 30) throw new RangeError(\"target must be 10–30\");\n    this.#target = t;\n  }\n\n  nudge(by) {\n    this.setTarget(this.#target + by);     // goes through the same check\n  }\n\n  get target() { return this.#target; }\n}",
          "lang": "js",
          "file": "thermostat.js"
        },
        {
          "p": "Different invariants, one shape. Each starts by **naming the invariant** — the\ncomment at the top — because you cannot guard a rule you have not stated. The\nstate is private, every operation that changes it checks first — `nudge` by\nrouting through `setTarget` rather than writing `#target` itself — and what goes\nout is a number: a copy of the value, not a way back in. The check comes\n**before** the change; once `#target` has been overwritten there is nothing left\nto refuse."
        },
        {
          "p": "`#items` and `#target` are real privacy, enforced by the language — not a\nnaming convention. Reaching for one from outside the class is a syntax error,\nand `Object.keys` does not list it."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const t = new Thermostat();  t.setTarget(25);  try { t.target = 99; } catch (e) {}   t.target",
                "accept": [
                  "25"
                ],
                "miss": "**If you answered `99` — `target` here is a getter with nothing to set.** Assigning to it does not reach `#target`, which only `setTarget` can change.\n",
                "why": "`25`. The assignment went nowhere: in a module it throws, because the getter has no setter, and in a plain script it is silently ignored. Either way `#target` was never touched.\n"
              },
              {
                "code": "const t = new Thermostat();  t.setTarget(28);  try { t.nudge(5); } catch (e) {}   t.target",
                "accept": [
                  "28"
                ],
                "why": "`28`. `nudge` asked `setTarget` for 33, the check refused before anything changed, and the target stayed where it was.\n"
              }
            ]
          }
        },
        {
          "h": "Handing out the inside"
        },
        {
          "p": "Private fields stop anyone naming the state. They do nothing about a\n**reference** the type gives away itself — and that is the second opening\nquestion."
        },
        {
          "code": "class Waitlist {\n  #names = [];\n  join(name) {\n    if (this.#names.includes(name)) return false;    // nobody twice\n    this.#names.push(name);\n    return true;\n  }\n  get names() { return this.#names; }                // the leak\n}\n\nconst w = new Waitlist();\nw.join(\"Kim\");\nconst list = w.names;        // not a copy: a second name for #names\nlist.push(\"Kim\");            // straight past the duplicate check",
          "lang": "js",
          "file": "waitlist.js"
        },
        {
          "p": "`b = a` copies the reference, not the array — and `return this.#names` does the\nsame. The caller now holds the private array and can push, remove or empty it\nwithout ever calling `add`. **Encapsulation and aliasing are the same problem**:\nthe invariant is only as safe as the last reference you handed out."
        },
        {
          "p": "If a getter must return a collection, return **a copy** — `[...this.#names]` —\nso what the caller changes is theirs, not yours. Returning a number or a string\nis always safe, because those are never shared."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "With `get names() { return [...this.#names]; }`, run `w.join(\"Kim\");  w.names.push(\"Kim\");`. What number is `w.names.length`?",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered `2` — each read of `w.names` builds a new array.** The `push` went into a copy that nobody kept, and the next read copies `#names` again.\n",
                "why": "`1`. The copy took the push; the waitlist never saw it.\n"
              },
              {
                "question": "`class Roster { #names = [\"Ana\"];  get first() { return this.#names[0]; } }` — can a caller use `first` to change `#names`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. `first` hands out a string, and a string cannot be changed through any name. Only handing out the array would leak it.\n"
              }
            ]
          }
        },
        {
          "h": "Closures do the same job"
        },
        {
          "p": "You have already built private state without a class. A closure keeps the\nvariables of the call that made it, and nothing outside can name them:"
        },
        {
          "code": "// \"A thermostat's target stays between 10 and 30 degrees.\"\n\nfunction makeThermostat() {\n  let target = 20;              // private: only the functions made below can reach it\n\n  const setTarget = t => {\n    if (t < 10 || t > 30) throw new RangeError(\"target must be 10–30\");\n    target = t;\n  };\n\n  return {\n    setTarget,\n    nudge: by => setTarget(target + by),\n    target: () => target,       // hands out the number, not the variable\n  };\n}",
          "lang": "js",
          "file": "thermostat-closure.js"
        },
        {
          "p": "Same invariant, same three steps, same guarantee: `target` is reachable only through\nthe three functions made in the same call. Both forms are common in\nJavaScript code you will read. A class with `#` fields is the form to reach for\nwhen you want many objects of one type with methods shared between them; a\nclosure is the form when you want one or two objects, or are already returning\nfunctions."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`const th = makeThermostat();  th.setTarget(28);  th.target = () => 12;` — what does `th.nudge(5)` do?",
                "choices": [
                  "It throws",
                  "It succeeds"
                ],
                "accept": [
                  "It throws"
                ],
                "miss": "**If you answered succeed — replacing the property replaced the reporter, not the state.** `nudge` reads the closure's `target` variable, still `28`, which nothing outside can reach. 28 + 5 is out of range.\n",
                "why": "It throws. A caller can overwrite the object's properties, but not the variable those functions close over. The report can lie; the invariant still holds.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Deciding what a type may hand out",
            "body": [
              {
                "h": "Deciding what a type may hand out"
              },
              {
                "code": "// You are adding something to a class with private state.\n\n// 1. Could an invalid state exist if a caller did this themselves?\n//    yes -> it belongs on the type, and the state it guards is private.\n//    no  -> it may still belong, for convenience. Weaker reason.\n\n// 2. Does it hand out the private state itself?\n//    a number or string -> safe; it cannot be changed through.\n//    an array, object or Map -> the guarantee is gone. Return a copy.",
                "lang": "js",
                "file": "what-goes-out.js"
              },
              {
                "p": "Now apply it to three candidates on `Basket`, which also has a `#budget`."
              },
              {
                "code": "// a) get size()             — returns this.#items.length\n// b) addIfAffordable(item)  — adds only when total + price <= this.#budget\n// c) get items()            — returns this.#items\n\n// Guards an invariant?      ______\n// Safe to hand out?         ______\n// The trap?                 ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`b` guards an invariant using private state — a caller doing that check\nthemselves could skip it. `a` hands out a number, which is safe. `c` is the\ntrap: it hands out the private array, and every guarantee above evaporates."
              }
            ]
          }
        },
        {
          "tracks": {
            "js": [
              {
                "p": "`class` with `#private` fields, and getters for what may be read."
              },
              {
                "code": "class Basket {\n  #items = [];\n  add(item, qty = 1) { }\n  get total() { }\n  get size() { return this.#items.length; }\n}",
                "lang": "js"
              },
              {
                "p": "`#private` is real privacy enforced by the language — not a naming convention.\nReaching for it from outside is a syntax error, not a runtime one. Closures give\nyou the same thing without classes, which is why both patterns are common."
              }
            ],
            "python": [
              {
                "p": "Classes with `__slots__` when you want to forbid stray attributes, and a leading\nunderscore as the privacy convention."
              },
              {
                "code": "class Basket:\n    def __init__(self): self._items = []\n    @property\n    def total(self): return sum(l.item.price * l.qty for l in self._items)",
                "lang": "python"
              },
              {
                "p": "`_items` is private by agreement only — Python deliberately has no enforcement,\non the grounds that \"we are all consenting adults.\" Note the difference from\nJavaScript's `#`, which the language does enforce."
              }
            ],
            "csharp": [
              {
                "p": "Properties, access modifiers and `readonly` collections, all checked."
              },
              {
                "code": "public class Basket {\n    private readonly List<Line> _items = new();\n    public decimal Total => _items.Sum(l => l.Item.Price * l.Qty);\n    public IReadOnlyList<Line> Items => _items;   // hands out a read-only view\n}",
                "lang": "csharp"
              },
              {
                "p": "`IReadOnlyList<T>` is the answer to the trap in the scaffold — a caller can read\nbut not add, without a copy being made."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **is private state enforced, or agreed?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "How you mark it",
                  "What stops an outsider"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "`#name` in a class; a closure's variable",
                    "the language — outside the class, `#name` does not parse"
                  ],
                  [
                    "**Python**",
                    "a leading underscore, `_items`",
                    "nothing — it is a convention every Python programmer agrees to respect"
                  ],
                  [
                    "**C#**",
                    "`private`",
                    "the compiler — code that reaches for it does not build"
                  ]
                ]
              }
            },
            {
              "p": "JavaScript is on the enforced side, alongside C#. It got there late: `#`\nfields are the newer form, and a great deal of existing JavaScript marks private\nstate with an underscore, the way Python does. When you meet `_balance` in a\nJavaScript class, read it as a request, not a wall."
            },
            {
              "p": "None of the three stops the leak in the second section. Every one of them will\nlet a method hand out a reference to the inside; that is a design decision, not\na language feature."
            }
          ]
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then, for the second, rewrite the\n`Bag` getter so the answer becomes \"no\" — and say what a caller who wanted to\nadd to the bag must do instead."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A `Gradebook` keeps each student's grade in a private `#grades` Map, and\nguarantees every grade is between 0 and 100. A report page needs to show every\nstudent's grade."
            },
            {
              "p": "Three designs:"
            },
            {
              "list": [
                "**`get grades()`** that returns `this.#grades`.",
                "**`get grades()`** that returns `new Map(this.#grades)`.",
                "**No getter for the Map at all** — `gradeFor(student)` and `students()`, which returns an array of names."
              ]
            },
            {
              "p": "Pick one and say why. For each of the other two, say exactly how a caller could\nbreak the 0–100 rule or what the report page loses. Then say which design lets\nyou later change `#grades` from a Map to something else without touching the\nreport page."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "An **invariant** is a fact about state every operation keeps true. A check in a method guards that method; only **private state** makes it a guarantee.",
                "In JavaScript, **`#` fields** or a **closure's variables** — both enforced, both reachable only through the type's own operations. Check **before** you change.",
                "**Handing out a reference to the inside breaks it.** Return a copy or a primitive — encapsulation and aliasing are the same problem."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Private properties",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes/Private_properties",
              "note": "`#` fields and methods, and why they are enforced rather than conventional."
            },
            {
              "title": "Closures",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Closures",
              "note": "read \"Emulating private methods with closures\"; it is the third section above."
            },
            {
              "title": "Spread syntax (...)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax",
              "note": "`[...arr]` as a one-level copy to hand out."
            }
          ]
        }
      ],
      "_source": "lessons/encapsulation.md",
      "practice": {
        "id": "encapsulation",
        "outcome": 6,
        "steps": [
          {
            "kind": "predict",
            "title": "Who can reach the state",
            "prompt": "Three quick ones. Type what the last expression produces, or answer the question. Where there are buttons, pick one.\n",
            "items": [
              {
                "id": "p1",
                "code": "class Queue { #items = [];  get items() { return this.#items; }  get size() { return this.#items.length; } }   const q = new Queue();  q.items.push(\"x\");   q.size",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered `0` — the field is private, the array is not.** `q.items` returned the array `#items` holds, and `push` changed it.\n",
                "why": "`1`. The getter handed out a reference, so a caller added to the queue without any method of `Queue` running."
              },
              {
                "id": "p2",
                "question": "A thermostat's target must stay between 10 and 30. Before you write `setTarget`, what do you make private? Name it.",
                "accept": [
                  "target",
                  "the target",
                  "#target",
                  "the target field",
                  "its target"
                ],
                "miss": "**The first step is the state the invariant is about.** A range check in `setTarget` means nothing if a caller can write the target directly.\n",
                "why": "The target. Once it is private, `setTarget` is the only way in, and its check becomes a guarantee."
              },
              {
                "id": "p3",
                "code": "function makeCounter() { let n = 0;  return { inc: () => ++n, read: () => n }; }   const c = makeCounter();  c.n = 50;  c.inc();   c.read()",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered `51` — `c.n = 50` made a new property on the returned object.** The `n` that `inc` and `read` share is a variable in the call that made them; nothing outside can name it.\n",
                "why": "`1`. The closure's `n` started at 0 and was incremented once."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "class Box { #v = 1; }   Object.keys(new Box())",
                "accept": [
                  "[]"
                ],
                "why": "`[]`. A `#` field is not a property at all, so nothing that lists properties finds it."
              },
              {
                "id": "p5",
                "question": "`class Stack { items = [];  push(x) { this.items.push(x); } }`, then `const s = new Stack();  s.items = null;  s.push(1)` — what happens?",
                "choices": [
                  "It works",
                  "It throws"
                ],
                "accept": [
                  "It throws"
                ],
                "why": "A TypeError: `push` asks `null` for `.push`. A public field let a caller put the object in a state its own method cannot survive."
              },
              {
                "id": "p6",
                "question": "`class Tally { #n = 0;  add() { this.#n++; }  get n() { return this.#n; } }` — can a caller change `#n` through the getter `n`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The getter hands out a number, and a number cannot be changed through any name. Only handing out an object or array leaks."
              }
            ],
            "hints": [
              {
                "when": "you answered 0 for q.size",
                "say": "Follow the reference. Does `q.items` return a new array, or the one stored in `#items`? Then ask what `push` changes."
              },
              {
                "when": "you answered 51 for c.read()",
                "say": "Is `n` a property of the object `makeCounter` returned, or a variable inside the call? `c.n = 50` can only reach the first."
              }
            ]
          },
          {
            "kind": "run",
            "title": "A private field, handed out",
            "prompt": "A playlist refuses duplicate tracks. Predict the three lines this prints, then run it.\n",
            "filename": "playlist.js",
            "code": "class Playlist {\n  #tracks = [];\n  add(title) {\n    if (this.#tracks.includes(title)) return false;   // no duplicates\n    this.#tracks.push(title);\n    return true;\n  }\n  get tracks() { return this.#tracks; }\n  get size() { return this.#tracks.length; }\n}\n\nconst p = new Playlist();\np.add(\"Intro\");\np.add(\"Intro\");\nconsole.log(p.size);\n\nconst list = p.tracks;\nlist.push(\"Intro\");\nconsole.log(p.size);\n\nconsole.log(Object.keys(p));\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "1"
                ],
                "why": "The second `add(\"Intro\")` is refused by the check. So far the invariant holds."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "2"
                ],
                "why": "`p.tracks` returned the private array itself, so `list` and `#tracks` are two names for one array. The duplicate went straight past `add` — the invariant is broken from outside."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "[]"
                ],
                "why": "A `#private` field is not a property at all, so `Object.keys` finds nothing. The field is unreachable; the array it holds was not."
              }
            ],
            "hints": [
              {
                "when": "you predicted 1 for the second line",
                "say": "Ask whether `list` is a copy. A getter that returns `this.#tracks` returns a reference — the same aliasing as assigning an array to a second variable."
              },
              {
                "when": "you predicted [\"tracks\"] or similar for the third",
                "say": "`#tracks` is not an ordinary property with an odd name. It is invisible to `Object.keys`, to `for…in`, and to any code outside the class body."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Guard the state before you change it",
            "prompt": "Build an `Account` whose balance can never go below zero, and which nothing outside the class can set directly. Pick the lines in order; two of them do not belong.\n",
            "lines": [
              "class Account {",
              "  #balance = 0;",
              "  withdraw(amount) {",
              "    if (amount > this.#balance) throw new RangeError(\"insufficient funds\");",
              "    this.#balance -= amount;",
              "    return this.#balance;",
              "  }",
              "}"
            ],
            "distractors": [
              {
                "line": "  balance = 0;",
                "why": "A public field. Any caller can write `acct.balance = -500`, so `withdraw`'s check guards a door with no wall beside it."
              },
              {
                "line": "    if (amount > this.balance) throw new RangeError(\"insufficient funds\");",
                "why": "`this.balance` is a different name from `this.#balance` and does not exist, so it is `undefined`. `amount > undefined` is always false — the check never fires, and nothing tells you."
              }
            ],
            "why": "The check comes before the change: once `#balance` has been reduced there is nothing left to refuse. And the state is `#private`, so `withdraw` is the only way to reach it — which is what makes the check a guarantee rather than a suggestion.\n",
            "hints": [
              {
                "when": "the check came after the subtraction",
                "say": "An invariant is guarded by refusing the operation, not by noticing afterwards. What would the balance be if the check ran second and threw?"
              },
              {
                "when": "you chose a line with `this.balance`",
                "say": "Compare the spellings. A private field is only reachable with its `#` — without it you are reading a property nobody set."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Hand out a copy, not the array",
            "prompt": "This is the playlist from the run step, working as written. Change one behaviour: `tracks` must still show the titles, but nothing a caller does to what it returns may change the playlist. `add` must keep working afterwards. Then, in one sentence: what does a caller lose by getting a copy, and why is that the point?\n",
            "exportName": "Playlist",
            "filename": "playlist.js",
            "starter": "class Playlist {\n  #tracks = [];\n  add(title) {\n    if (this.#tracks.includes(title)) return false;\n    this.#tracks.push(title);\n    return true;\n  }\n  get tracks() { return this.#tracks; }\n  get size() { return this.#tracks.length; }\n}\n",
            "tests": [
              {
                "label": "tracks shows the titles in order",
                "body": "const p = new subject(); p.add('a'); p.add('b'); const t = p.tracks; return t.length === 2 && t[0] === 'a' && t[1] === 'b';"
              },
              {
                "label": "duplicates are still refused",
                "body": "const p = new subject(); p.add('a'); return p.add('a') === false && p.size === 1;"
              },
              {
                "label": "pushing onto tracks does not change the playlist",
                "body": "const p = new subject(); p.add('a'); const t = p.tracks; try { t.push('a'); } catch (e) {} return p.size === 1;"
              },
              {
                "label": "emptying tracks does not empty the playlist",
                "body": "const p = new subject(); p.add('a'); const t = p.tracks; try { t.length = 0; } catch (e) {} return p.size === 1;"
              },
              {
                "label": "add still works after tracks has been read",
                "body": "const p = new subject(); p.add('a'); p.tracks; return p.add('b') === true && p.size === 2;"
              }
            ],
            "hints": [
              {
                "when": "the push and emptying tests fail",
                "say": "The getter still returns the private array itself. Every caller holds a second name for it — return a new array with the same contents instead."
              },
              {
                "when": "add fails after tracks has been read",
                "say": "You froze the private array, not a copy of it — so the next `push` inside `add` throws. Freezing is fine, but only on something you are handing out."
              }
            ]
          },
          {
            "kind": "write",
            "title": "A timesheet nobody can pad",
            "prompt": "Write a `Timesheet` class. `log(hours)` records one entry and throws a `RangeError` unless `hours` is more than 0 and at most 24. `total` is the sum of the entries. `entries` returns the entries in order — but nothing a caller does to what it returns may change the timesheet. Nothing outside the class may reach the entries any other way. Then, in one sentence: which test would a class with a public `entries` array fail, and how would a caller break the rule through it?\n",
            "exportName": "Timesheet",
            "filename": "timesheet.js",
            "starter": "// log(hours) — RangeError unless 0 < hours <= 24\n// get total() — sum of the entries\n// get entries() — the entries in order; changing it changes nothing\n// Nothing outside may reach the entries any other way.\nclass Timesheet {\n\n}\n",
            "tests": [
              {
                "label": "total sums the entries",
                "body": "const t = new subject(); t.log(8); t.log(4.5); return t.total === 12.5;"
              },
              {
                "label": "an empty timesheet totals zero",
                "body": "return new subject().total === 0;"
              },
              {
                "label": "zero hours is refused",
                "body": "const t = new subject(); try { t.log(0); return false; } catch (e) { return e instanceof RangeError && t.total === 0; }"
              },
              {
                "label": "25 hours is refused and nothing is recorded",
                "body": "const t = new subject(); t.log(2); try { t.log(25); return false; } catch (e) { return e instanceof RangeError && t.total === 2; }"
              },
              {
                "label": "entries shows the entries in order",
                "body": "const t = new subject(); t.log(3); t.log(5); const e = t.entries; return e.length === 2 && e[0] === 3 && e[1] === 5;"
              },
              {
                "label": "pushing onto entries does not pad the timesheet",
                "body": "const t = new subject(); t.log(3); try { t.entries.push(100); } catch (e) {} return t.total === 3;"
              },
              {
                "label": "the entries are not a property",
                "body": "const t = new subject(); t.log(3); return Object.keys(t).length === 0;"
              },
              {
                "label": "two timesheets are independent",
                "body": "const a = new subject(), b = new subject(); a.log(1); return b.total === 0;"
              }
            ],
            "hints": [
              {
                "when": "the pushing test fails",
                "say": "`entries` hands out the array your class keeps. Return a new array with the same contents, so what a caller changes is theirs."
              },
              {
                "when": "the not-a-property test fails",
                "say": "You stored the entries in a normal field, so any caller can reach them without `log`. Use a `#private` field."
              },
              {
                "when": "25 hours is recorded before the error",
                "say": "Your check runs after the entry is pushed. Refuse first, then change."
              },
              {
                "when": "the two-timesheets test fails",
                "say": "Your entries live outside the class, so every timesheet shares them. A `#entries = []` field is created per instance."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review an inventory",
            "prompt": "A code assistant wrote this for \"an inventory where no count ever goes below zero\". It looks guarded. Answer by line number where asked, then fix it so the rule holds — `stock` may still be read.\n",
            "filename": "inventory.js",
            "code": "class Inventory {\n  #stock = new Map();\n  add(sku, n) {\n    if (n <= 0) throw new RangeError(\"n must be positive\");\n    this.#stock.set(sku, (this.#stock.get(sku) ?? 0) + n);\n  }\n  remove(sku, n) {\n    this.#stock.set(sku, this.#stock.get(sku) - n);\n    if (this.#stock.get(sku) < 0) throw new RangeError(\"not enough stock\");\n  }\n  get stock() { return this.#stock; }\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "`inv.add(\"pen\", 2);  try { inv.remove(\"pen\", 5); } catch (e) {}` — what count does the inventory now hold for `\"pen\"`?",
                "accept": [
                  "-3"
                ],
                "miss": "**If you answered `2` — the error was thrown after the change.** Line 8 wrote `-3`; line 9 noticed and threw, but did not put it back.\n",
                "why": "`-3`. The check runs after the change, so throwing does not undo anything."
              },
              {
                "id": "c2",
                "question": "Which line lets a caller break the rule without calling `add` or `remove`?",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "Line 11 hands out the private Map itself. `inv.stock.set(\"pen\", -9)` goes straight in."
              },
              {
                "id": "c3",
                "question": "`inv.remove(\"ink\", 1)` for an sku never added — what value does line 8 store?",
                "accept": [
                  "NaN"
                ],
                "miss": "**Work it through.** `this.#stock.get(\"ink\")` is `undefined` — the Map's \"nothing here\" — and `undefined - 1` is `NaN`. `NaN < 0` is false, so line 9 lets it stand.\n",
                "why": "`NaN`. A missing key arrives as `undefined`, and arithmetic on it quietly produces `NaN`, which no `< 0` check catches."
              }
            ],
            "starter": "// Rule: no count ever goes below zero. `stock` may be read.\nclass Inventory {\n  #stock = new Map();\n  add(sku, n) {\n    if (n <= 0) throw new RangeError(\"n must be positive\");\n    this.#stock.set(sku, (this.#stock.get(sku) ?? 0) + n);\n  }\n  remove(sku, n) {\n    this.#stock.set(sku, this.#stock.get(sku) - n);\n    if (this.#stock.get(sku) < 0) throw new RangeError(\"not enough stock\");\n  }\n  get stock() { return this.#stock; }\n}\n",
            "exportName": "Inventory",
            "tests": [
              {
                "label": "add and remove within stock",
                "body": "const i = new subject(); i.add('pen', 5); i.remove('pen', 2); return i.stock.get('pen') === 3;"
              },
              {
                "label": "removing too many throws and leaves the count alone",
                "body": "const i = new subject(); i.add('pen', 2); try { i.remove('pen', 5); return false; } catch (e) { return e instanceof RangeError && i.stock.get('pen') === 2; }"
              },
              {
                "label": "removing an sku never added throws, and stores nothing",
                "body": "const i = new subject(); try { i.remove('ink', 1); return false; } catch (e) { return e instanceof RangeError && !(i.stock.get('ink') < 0) && !Number.isNaN(i.stock.get('ink')); }"
              },
              {
                "label": "changing what stock returns changes nothing",
                "body": "const i = new subject(); i.add('pen', 2); try { i.stock.set('pen', -9); } catch (e) {} return i.stock.get('pen') === 2;"
              }
            ],
            "hints": [
              {
                "when": "the too-many test fails",
                "say": "The count is written before it is checked. Work out what the new count *would* be, refuse if it is below zero, and only then store it."
              },
              {
                "when": "the never-added test fails",
                "say": "`get` on a missing key gives `undefined`, and `undefined - 1` is `NaN`, which is not `< 0`. Decide what a missing sku counts as before you subtract."
              },
              {
                "when": "the changing-stock test fails",
                "say": "The getter still returns the private Map. Hand out a copy — `new Map(...)` of it — so a caller's `set` lands on theirs."
              }
            ]
          }
        ]
      }
    },
    "prototypes": {
      "id": "prototypes",
      "title": "How a method is found",
      "kind": "read",
      "outcome": 6,
      "shape": "conceptual",
      "why": "`model.prototypes` is in the course because JavaScript's object model is genuinely different, not a syntax variation: a class is built on a chain of objects that a lookup walks at the moment of the call (`concepts.md`). Its mastery was reworded so every track can answer it — how your language finds a method an object does not define itself — and it is idea-only for the Python and C# tracks (D-016, D-023).",
      "concepts": [
        "model.prototypes"
      ],
      "lede": "An object does not carry its methods. When you call one it does not have, the language goes looking — and in JavaScript it looks along a chain of ordinary objects, at the moment of the call. A `class` is a clearer way of building that chain, not a different mechanism.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Designing a type · How a method finds its object · Inheritance, and what a subclass commits to"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Prototype** — the object a lookup falls back to when a property is not found. **Own property** — one the object holds itself, not one it finds along the chain.",
          "entries": [
            "**Prototype** — the object a lookup falls back to when a property is not found.",
            "**Own property** — one the object holds itself, not one it finds along the chain."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "A few questions about the classes below. Pick an answer where there are\nbuttons; otherwise type what the expression produces."
            },
            {
              "code": "class Animal { constructor(n) { this.name = n; } speak() { return this.name + \" makes a sound\"; } }\nclass Dog extends Animal { speak() { return this.name + \" barks\"; } }\nconst d = new Dog(\"Rex\");",
              "lang": "js"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "Object.hasOwn(d, \"speak\")",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "false"
                          ],
                          "miss": "**If you answered `true` because you called `d.speak()` — you called it *through* `d`.** The lookup checks `d`, does not find it, follows `d`'s prototype to `Dog.prototype`, and finds it there. One `speak` exists no matter how many dogs you make.\n",
                          "why": "`speak` is not on the instance. It lives on `Dog.prototype`, and the lookup walks there when it is not found on `d`.\n"
                        },
                        {
                          "code": "Object.getPrototypeOf(d) === Dog.prototype",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "why": "Every instance points at its class's prototype object. That pointer *is* the inheritance.\n"
                        },
                        {
                          "code": "typeof Dog",
                          "accept": [
                            "\"function\"",
                            "function",
                            "'function'"
                          ],
                          "miss": "**If you expected `\"class\"`, note that `class` gave JavaScript a clearer spelling of prototypes, not a new mechanism.** `Dog.prototype` is still an object and `extends` still sets one prototype's prototype.\n",
                          "why": "A class is a function with a prototype attached. `class` is syntax over the thing that was already there.\n"
                        },
                        {
                          "code": "new Dog(\"a\").speak === new Dog(\"b\").speak",
                          "choices": [
                            "true",
                            "false"
                          ],
                          "accept": [
                            "true"
                          ],
                          "miss": "**If you answered `false` because they are two dogs — they are two dogs with no `speak` of their own.** Both lookups end at the same object, `Dog.prototype`, and find the same function there.\n",
                          "why": "One function, found by both lookups on the same prototype. \"Methods live on the type\" means exactly this.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "\"speak\" in d.__dict__",
                          "choices": [
                            "True",
                            "False"
                          ],
                          "accept": [
                            "False"
                          ],
                          "why": "Methods live on the class, not the instance. The instance dict holds only the attributes you set.\n"
                        },
                        {
                          "question": "What is `type(d).__mro__`? Say in a few words.",
                          "accept": [
                            "the lookup order",
                            "method resolution order",
                            "mro",
                            "the class chain"
                          ],
                          "why": "The method resolution order: the list of classes searched, in order. Python's version of the prototype chain, computed rather than walked.\n"
                        },
                        {
                          "code": "type(Dog)",
                          "accept": [
                            "type",
                            "<class 'type'>",
                            "a class"
                          ],
                          "why": "Classes are objects whose type is `type`. Python and JavaScript both make classes ordinary values.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Does `Dog` have its own copy of `Speak` per instance?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Methods live once on the type. The instance holds only its fields.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can explain how my language finds a method an object does not define itself"
              ]
            }
          ]
        },
        {
          "h": "Where a method actually lives"
        },
        {
          "p": "When you write `d.speak()`, JavaScript has to find a property called `speak`\nbefore it can call anything. It does that with one rule, repeated:"
        },
        {
          "list": [
            "Look on the object itself. If it has an **own property** with that name, use it.",
            "If not, follow the object's **prototype** — a hidden link to one other object — and look there.",
            "Keep following links until the name is found, or the chain ends at `null`."
          ]
        },
        {
          "p": "Two worked examples, the same four steps."
        },
        {
          "code": "// d.speak()\n\n// Look on the object itself:\n//   d has own properties: name.          \"speak\"? no.\n// Follow its prototype:\n//   d -> Dog.prototype                   \"speak\"? yes.\n// Stop at the first match:\n//   Dog.prototype.speak. Animal.prototype.speak is never reached.\n// Call it with the original object:\n//   this = d, so this.name is \"Rex\"  ->  \"Rex barks\"",
          "lang": "js",
          "file": "dog-lookup.js"
        },
        {
          "code": "// const scores = [70, 85];   scores.map(s => s + 5)\n\n// own properties of scores: 0, 1, length.     \"map\"? no.\n// scores -> Array.prototype                    \"map\"? yes.\n// first match: Array.prototype.map — one function, shared by every array.\n// call it with this = scores  ->  [75, 90]",
          "lang": "js",
          "file": "array-lookup.js"
        },
        {
          "p": "Every array you have used found `map`, `push` and `includes` exactly this way.\nNone of them carries its own copy."
        },
        {
          "p": "The last step is the one from *How a method finds its object*: **finding** a\nmethod and **calling it with an object** are two different steps. The lookup\nfinds a function; the call site supplies `this`. Take the function out of the\nobject — `const f = d.speak` — and the lookup still worked, but the call `f()`\nhas no object to supply."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "Object.hasOwn([1, 2], \"push\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` — an array has its items and its `length` as own properties, and nothing else.** `push` is found one link along, on `Array.prototype`.\n",
                "why": "`false`. `push` lives on `Array.prototype`; the lookup finds it there.\n"
              },
              {
                "question": "`d.toString` — which object does the lookup find it on?",
                "choices": [
                  "d",
                  "Dog.prototype",
                  "Animal.prototype",
                  "Object.prototype"
                ],
                "accept": [
                  "Object.prototype"
                ],
                "miss": "**Walk the chain one link at a time.** Not on `d`, not on `Dog.prototype`, not on `Animal.prototype`. What is `Animal.prototype`'s own prototype?\n",
                "why": "`Object.prototype`. The chain runs `d` → `Dog.prototype` → `Animal.prototype` → `Object.prototype` → `null`, and `toString` is on the fourth.\n"
              }
            ]
          }
        },
        {
          "h": "A class is built on the chain"
        },
        {
          "p": "`class` and `extends` do not introduce a new kind of thing. They build the\nchain for you:"
        },
        {
          "code": "Object.getPrototypeOf(d)             === Dog.prototype;      // true — new set this link\nObject.getPrototypeOf(Dog.prototype) === Animal.prototype;   // true — extends set this one\ntypeof Dog;                                                   // \"function\"",
          "lang": "js"
        },
        {
          "list": [
            "`Dog` itself is a function — the constructor.",
            "`Dog.prototype` is an ordinary object holding the methods from the class body.",
            "`new Dog(...)` makes an object whose prototype is `Dog.prototype`.",
            "`extends Animal` makes `Dog.prototype`'s prototype `Animal.prototype`."
          ]
        },
        {
          "p": "That is why methods exist **once per class**, not once per instance, and why a\nchange to the prototype reaches objects that already exist. The lookup happens\nat the moment of the call, and none of them holds a copy:"
        },
        {
          "code": "const rex = new Dog(\"Rex\");\nDog.prototype.speak = function () { return this.name + \" woofs\"; };\nrex.speak();   // \"Rex woofs\" — rex was made before the change, and still sees it",
          "lang": "js"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const b = new Dog(\"Bo\");   Animal.prototype.sleep = function () { return this.name + \" sleeps\"; };   b.sleep()",
                "accept": [
                  "\"Bo sleeps\"",
                  "Bo sleeps",
                  "'Bo sleeps'"
                ],
                "miss": "**If you answered `error` because `b` was made before `sleep` existed — `b` never had a copy of anything.** The call walks the chain now, and `Animal.prototype` has `sleep` now.\n",
                "why": "`\"Bo sleeps\"`. The lookup walks `b` → `Dog.prototype` → `Animal.prototype` at call time and finds the new method.\n"
              },
              {
                "code": "Object.getPrototypeOf(Dog.prototype) === Animal.prototype",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. That link is what `extends` builds.\n"
              }
            ]
          }
        },
        {
          "h": "An own property is found first"
        },
        {
          "p": "Because the lookup stops at the first match, a property on the object itself\n**shadows** anything further along the chain:"
        },
        {
          "code": "const pip = new Dog(\"Pip\");\npip.speak = () => \"Pip whimpers\";     // an own property, on this one dog\npip.speak();                          // \"Pip whimpers\"\nnew Dog(\"Max\").speak();               // \"Max barks\" — every other dog is unchanged",
          "lang": "js"
        },
        {
          "p": "This is also what happens when a constructor assigns a method to `this`:"
        },
        {
          "code": "class Cat {\n  constructor(name) {\n    this.name = name;\n    this.speak = function () { return this.name + \" meows\"; };   // own, per cat\n  }\n}\nnew Cat(\"a\").speak === new Cat(\"b\").speak;   // false — a new function for every cat",
          "lang": "js"
        },
        {
          "p": "It works, and it quietly gives up what the prototype was for: a thousand cats\nnow carry a thousand `speak` functions, and changing `Cat.prototype` reaches\nnone of them, because their own copy is always found first. Methods belong in\nthe class body."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "Object.hasOwn(new Cat(\"a\"), \"speak\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. The constructor put `speak` on the instance, so the lookup finds it at the first step and never reaches the prototype.\n"
              },
              {
                "code": "const pip = new Dog(\"Pip\");  pip.speak = () => \"hi\";   delete pip.speak;   pip.speak()",
                "accept": [
                  "\"Pip barks\"",
                  "Pip barks",
                  "'Pip barks'"
                ],
                "miss": "**If you answered `error` — deleting the own property uncovers the one behind it.** The lookup now misses on `pip` and continues to `Dog.prototype`, exactly as before the shadow was added.\n",
                "why": "`\"Pip barks\"`. With the own `speak` gone, the chain is back to what it was.\n"
              }
            ]
          }
        },
        {
          "video": {
            "title": "A method call, followed along the chain",
            "caption": "`d.speak()` traced through the lookup — instance, `Dog.prototype`, `Animal.prototype` — then the same class drawn as the objects it actually creates. Watch it if \"a class is a prototype chain\" is still a slogan.\n"
          }
        },
        {
          "scaffold": {
            "label": "Reading a chain when something inherited surprises you",
            "body": [
              {
                "h": "Reading a chain when something inherited surprises you"
              },
              {
                "p": "You will not often need this. You will need it when something inherited is not\nwhat you expected, and reading the chain is the fastest way to find out why."
              },
              {
                "code": "class Shape  { describe() { return \"a shape\"; } area() { return 0; } }\nclass Rect   extends Shape { area() { return this.w * this.h; } }\nclass Square extends Rect  { constructor(s) { super(); this.w = s; this.h = s; } }\nconst sq = new Square(3);\n\n// sq.describe()\n// Look on the object itself:\n//   own properties of sq: ______             \"describe\"? ______\n// Follow its prototype:\n//   sq -> ______.prototype                    \"describe\"? no\n//   -> ______.prototype                       \"describe\"? no\n//   -> ______.prototype                       \"describe\"? yes\n// Stop at the first match, and call it with this = sq.",
                "lang": "js"
              },
              {
                "p": "`sq`'s own properties are `w` and `h`. The chain is `Square.prototype` (only a\nconstructor), `Rect.prototype` (only `area`), then `Shape.prototype`, where\n`describe` is found. `sq.area()` would stop one link earlier, at `Rect`'s, and\ngive `9`."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **how does the language find a method the object does not define\nitself?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Where it looks",
                  "When the answer is fixed"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "the object, then a chain of ordinary objects, one link at a time",
                    "at each call — change a prototype and existing objects see it"
                  ],
                  [
                    "**Python**",
                    "the instance's own attributes, then the classes in its method resolution order",
                    "the order is computed from the class declarations; the search happens at each call"
                  ],
                  [
                    "**C#**",
                    "the object's type, which owns its methods",
                    "the methods a type has are fixed when the program is compiled; which override runs is chosen at the call"
                  ]
                ]
              }
            },
            {
              "p": "All three share the idea that matters: the object holds its **state**, and its\n**behaviour** lives once, somewhere shared. JavaScript is the one where that\nsomewhere is an ordinary object you can inspect, reach and change while the\nprogram runs — which is flexible, and is why a change to `Array.prototype` would\nreach every array in the program."
            }
          ]
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening questions again from memory. Then say how many `speak`\nfunctions exist in memory after `new Dog(\"a\")` and `new Dog(\"b\")` — and what\nthat tells you about where methods live."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A teammate wants a `last()` method on every array in the program, and proposes\none line:"
            },
            {
              "code": "Array.prototype.last = function () { return this[this.length - 1]; };",
              "lang": "js"
            },
            {
              "p": "Two alternatives: a plain function `last(arr)`, or a class `List extends Array`\nthat defines `last()` and is used where it is needed."
            },
            {
              "p": "Pick one and say why. Use the lookup to explain what the teammate's line does to\narrays created **by other code** — a library, or a later version of the\nlanguage that adds its own `last` — and say which of the three designs a reader\nof `scores.last()` can trace to its definition fastest."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "An object holds its **own properties**. A method it does not have is found by following its **prototype**, link by link, at the moment of the call.",
                "A JavaScript `class` builds that chain: methods go on `Class.prototype`, once; `new` links each instance to it; `extends` links one prototype to the next.",
                "The first match wins: an **own property shadows** the chain — which is why methods belong in the class body, not assigned in the constructor."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Object prototypes",
              "href": "https://developer.mozilla.org/en-US/docs/Learn_web_development/Extensions/Advanced_JavaScript_objects/Object_prototypes",
              "note": "the chain, drawn, and what `class` compiles to."
            },
            {
              "title": "Object.getPrototypeOf()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/getPrototypeOf",
              "note": "how to read a chain when something inherited surprises you."
            },
            {
              "title": "Object.hasOwn()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/hasOwn",
              "note": "whether a property is the object's own or found along the chain."
            }
          ]
        }
      ],
      "_source": "lessons/prototypes.md",
      "practice": {
        "id": "prototypes",
        "outcome": 6,
        "steps": [
          {
            "kind": "predict",
            "title": "Where does the lookup go?",
            "prompt": "Three quick ones. Type what the expression produces, or pick an answer where there are buttons.\n",
            "items": [
              {
                "id": "p1",
                "code": "Object.hasOwn(new Map(), \"get\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` because every Map can `get` — every Map finds it,** one link along, on `Map.prototype`. A new Map has no own properties at all.\n",
                "why": "`false`. `get` lives once, on `Map.prototype`, and every Map's lookup ends there."
              },
              {
                "id": "p2",
                "question": "`class Dog extends Animal`, `const d = new Dog(\"Rex\")`. `d.speak()` — which object does the lookup check first?",
                "choices": [
                  "d",
                  "Dog.prototype",
                  "Animal.prototype"
                ],
                "accept": [
                  "d"
                ],
                "why": "The instance's own properties first. Only when `speak` is not found there does the lookup follow `d`'s prototype to `Dog.prototype`."
              },
              {
                "id": "p3",
                "code": "[1, 2].includes === [\"a\"].includes",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "miss": "**If you answered `false` because they are two arrays — neither has an `includes` of its own.** Both lookups end at `Array.prototype` and find the same function.\n",
                "why": "`true`. One function, shared by every array through the chain."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "typeof Dog",
                "accept": [
                  "\"function\"",
                  "function",
                  "'function'"
                ],
                "why": "A class is a function with a prototype object attached. `class` is a clearer spelling of what was already there."
              },
              {
                "id": "p5",
                "code": "new Dog(\"a\").speak === new Dog(\"b\").speak",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "One function, found by both lookups on the same prototype. \"Methods live on the type\" means exactly this."
              },
              {
                "id": "p6",
                "code": "const o = { hi() { return 1; } };   Object.getPrototypeOf(o) === Object.prototype",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. An object literal is linked to `Object.prototype` — which is where `o.toString` is found."
              }
            ],
            "hints": [
              {
                "when": "you answered true for new Map()",
                "say": "Ask where `get` was written. It is on `Map.prototype`; a new Map has no own properties until you put some there."
              },
              {
                "when": "you answered Dog.prototype for the first lookup",
                "say": "That is where it is *found*. Where does the search *start*? Always at the object before the dot."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Overrides, late additions, and a shadow",
            "prompt": "Predict the five lines this prints, then run it.\n",
            "filename": "plants.js",
            "code": "class Plant {\n  constructor(name) { this.name = name; }\n  water() { return this.name + \" watered\"; }\n  describe() { return \"a plant called \" + this.name; }\n}\nclass Cactus extends Plant {\n  water() { return this.name + \" does not need it\"; }\n}\n\nconst c = new Cactus(\"Spike\");\nconsole.log(c.water());\nconsole.log(c.describe());\n\nPlant.prototype.repot = function () { return this.name + \" repotted\"; };\nconsole.log(c.repot());\n\nc.water = () => \"just this one\";\nconsole.log(c.water() + \" | \" + new Cactus(\"Prick\").water());\nconsole.log(Object.keys(c));\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "Spike does not need it",
                  "\"Spike does not need it\""
                ],
                "why": "The lookup for `water` stops at `Cactus.prototype`, one link before `Plant`'s."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "a plant called Spike",
                  "\"a plant called Spike\""
                ],
                "why": "`Cactus` defines no `describe`, so the lookup continues to `Plant.prototype`. `this` is still `c`."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "Spike repotted",
                  "\"Spike repotted\""
                ],
                "miss": "**If you predicted an error because `c` was made before `repot` — `c` holds no copy of any method.** The lookup runs now, and `Plant.prototype` has `repot` now.\n",
                "why": "The method was added after `c` existed and `c` still finds it: the chain is walked at the call."
              },
              {
                "id": "r4",
                "question": "Fourth line printed",
                "accept": [
                  "just this one | Prick does not need it",
                  "\"just this one | Prick does not need it\""
                ],
                "why": "The assignment gave `c` an own `water`, found first. Every other cactus still finds `Cactus.prototype.water`."
              },
              {
                "id": "r5",
                "question": "Fifth line printed",
                "accept": [
                  "[\"name\",\"water\"]",
                  "[name, water]",
                  "['name', 'water']",
                  "[\"name\", \"water\"]"
                ],
                "why": "`name` from the constructor and the `water` just assigned are `c`'s own. Nothing found along the chain is listed."
              }
            ],
            "hints": [
              {
                "when": "you predicted \"Spike watered\" first",
                "say": "Start the lookup at `c` and move outward. Which prototype do you reach first, and does it have `water`?"
              },
              {
                "when": "you predicted \"just this one\" for Prick too",
                "say": "`c.water = …` changed one object. `new Cactus(\"Prick\")` has no own `water`, so its lookup goes where it always did."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Follow the lookup",
            "prompt": "`class Animal { speak() {…} }`, `class Dog extends Animal { speak() {…} }`, `const d = new Dog(\"Rex\")`. Say where each lookup ends, or what the call returns.\n",
            "items": [
              {
                "id": "t1",
                "question": "`d.speak()` — where is `speak` found?",
                "choices": [
                  "d",
                  "Dog",
                  "Dog.prototype",
                  "Animal.prototype"
                ],
                "accept": [
                  "Dog.prototype"
                ],
                "why": "Not on `d`, so the lookup follows the prototype pointer to `Dog.prototype`, finds it, and stops. It never reaches `Animal.prototype`."
              },
              {
                "id": "t2",
                "question": "If `Dog` did not define `speak`, where would the lookup find it?",
                "choices": [
                  "Dog.prototype",
                  "Animal.prototype",
                  "Object.prototype"
                ],
                "accept": [
                  "Animal.prototype"
                ],
                "why": "One step further out. `Object.getPrototypeOf(Dog.prototype) === Animal.prototype` — that link is what `extends` built."
              },
              {
                "id": "t3",
                "question": "After `d` exists, someone runs `Dog.prototype.speak = function () { return \"woof\"; };`. What does `d.speak()` return?",
                "accept": [
                  "\"woof\"",
                  "woof",
                  "'woof'"
                ],
                "why": "The lookup happens at the moment of the call, and `d` holds no copy of its own. Change the prototype and every existing dog sees it."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "`d.toString` — which prototype does the lookup end at?",
                "choices": [
                  "Dog.prototype",
                  "Animal.prototype",
                  "Object.prototype"
                ],
                "accept": [
                  "Object.prototype"
                ],
                "why": "Neither class defines it, so the chain runs `d` → `Dog.prototype` → `Animal.prototype` → `Object.prototype`, where it is found."
              },
              {
                "id": "t5",
                "question": "`const f = d.speak;  f()` — what happens?",
                "choices": [
                  "It works",
                  "It throws"
                ],
                "accept": [
                  "It throws"
                ],
                "why": "The lookup found the function, but calling it bare gives it no receiver. Class bodies are strict, so `this` is `undefined` and `this.name` throws. Finding a method and calling it on an object are two different steps."
              }
            ],
            "hints": [
              {
                "when": "you answered Dog rather than Dog.prototype",
                "say": "`Dog` is the constructor function. Methods written in the class body live on the object `Dog.prototype`, which is what every instance points at."
              },
              {
                "when": "you answered \"Rex barks\" for the reassignment",
                "say": "That would be right if each dog carried its own copy of `speak`. Check the first diagnostic answer again — it does not."
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Write the lookup yourself",
            "prompt": "`whereFound(obj, name)` walks the chain the way the language does and returns how many links it followed before finding `name` as an **own** property — `0` if the object has it itself — or `-1` if the chain ends without it. The loop already moves along the chain. Fill the one line. Then, in one sentence: why would `name in current` give the wrong answer here?\n",
            "exportName": "whereFound",
            "filename": "where-found.js",
            "starter": "function whereFound(obj, name) {\n  let links = 0;\n  for (let current = obj; current !== null; current = Object.getPrototypeOf(current)) {\n    // ___ gap: if `current` holds `name` itself, return how many links you followed\n    links++;\n  }\n  return -1;\n}\n",
            "tests": [
              {
                "label": "an own property is found at 0",
                "body": "return subject({ a: 1 }, 'a') === 0;"
              },
              {
                "label": "a method from the class body is 1 link along",
                "body": "class Dog { speak() {} } return subject(new Dog(), 'speak') === 1;"
              },
              {
                "label": "an inherited method is 2 links along",
                "body": "class Animal { speak() {} } class Dog extends Animal {} return subject(new Dog(), 'speak') === 2;"
              },
              {
                "label": "toString on a subclass instance is 3 links along",
                "body": "class Animal {} class Dog extends Animal {} return subject(new Dog(), 'toString') === 3;"
              },
              {
                "label": "push on an array is 1 link along",
                "body": "return subject([1, 2], 'push') === 1;"
              },
              {
                "label": "a name nowhere on the chain gives -1",
                "body": "class Dog {} return subject(new Dog(), 'fly') === -1;"
              }
            ],
            "hints": [
              {
                "when": "every found name gives 0",
                "say": "Your test finds names anywhere along the chain — `in` and `current[name]` both do their own lookup. Ask whether `current` holds the name *itself*."
              },
              {
                "when": "only the own-property test passes",
                "say": "You are checking `obj` on every iteration instead of the object the loop has reached. Check `current`."
              },
              {
                "when": "everything gives -1",
                "say": "The gap is still empty. One `if`, using `Object.hasOwn`, that returns `links`."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a badge class",
            "prompt": "An AI assistant wrote this to render a thousand user badges, and then a style change that \"updates every badge\". The page still shows square brackets. Answer by line number where asked, then fix the class so the method lives where it should.\n",
            "filename": "badges.js",
            "code": "class Badge {\n  constructor(name) {\n    this.name = name;\n    this.render = function () {\n      return \"[\" + this.name + \"]\";\n    };\n  }\n}\n\nconst badges = [];\nfor (let i = 0; i < 1000; i++) badges.push(new Badge(\"user\" + i));\n\nBadge.prototype.render = function () {\n  return \"<\" + this.name + \">\";\n};\nbadges[0].render();\n",
            "items": [
              {
                "id": "c1",
                "question": "After line 11, how many `render` functions exist? Give a number.",
                "accept": [
                  "1000",
                  "one thousand",
                  "a thousand"
                ],
                "miss": "**Look at where `render` is assigned.** Line 4 runs inside the constructor, once per `new Badge`, and makes a new function each time.\n",
                "why": "1000. Each constructor call made its own function and put it on the instance."
              },
              {
                "id": "c2",
                "question": "What does line 16 return?",
                "accept": [
                  "\"[user0]\"",
                  "[user0]",
                  "'[user0]'"
                ],
                "why": "`\"[user0]\"`. Every badge has an own `render`, so the lookup stops on the badge and never reaches the new one on `Badge.prototype`."
              },
              {
                "id": "c3",
                "question": "Which line starts the assignment that should be a method in the class body instead?",
                "accept": [
                  "4",
                  "line 4"
                ],
                "why": "Line 4. Written as `render() { … }` in the class body, it would exist once on `Badge.prototype`, and a later change there would reach every badge."
              }
            ],
            "starter": "class Badge {\n  constructor(name) {\n    this.name = name;\n    this.render = function () {\n      return \"[\" + this.name + \"]\";\n    };\n  }\n}\n",
            "exportName": "Badge",
            "tests": [
              {
                "label": "render still shows the name in square brackets",
                "body": "return new subject('ada').render() === '[ada]';"
              },
              {
                "label": "render is not an own property of each badge",
                "body": "return !Object.hasOwn(new subject('ada'), 'render');"
              },
              {
                "label": "every badge shares one render",
                "body": "return new subject('a').render === new subject('b').render;"
              },
              {
                "label": "a change to the prototype reaches badges that already exist",
                "body": "const b = new subject('ada'); const old = subject.prototype.render; subject.prototype.render = function () { return '<' + this.name + '>'; }; const ok = b.render() === '<ada>'; subject.prototype.render = old; return ok;"
              }
            ],
            "hints": [
              {
                "when": "render is still an own property",
                "say": "Anything assigned to `this` in the constructor lands on the instance. Move `render` out of the constructor and write it as a method in the class body."
              },
              {
                "when": "you wrote render = () => … in the class body",
                "say": "`render = …` in a class body is a field, and a field is set on each instance, exactly as the constructor did. Write it as a method — `render() { … }` — so it goes on the prototype."
              }
            ]
          }
        ]
      }
    },
    "inherit-compose": {
      "id": "inherit-compose",
      "title": "Inheritance, and what a subclass commits to",
      "kind": "read",
      "outcome": 6,
      "shape": "conceptual",
      "why": "Outcome 6 asks for the choice among inheritance, composition and interfaces to be justified, and that needs each one's cost known first. Inheritance's cost is what a subclass commits to — including which method runs when it overrides one the base class calls, the case D-018 added to `model.inheritance`'s mastery.",
      "concepts": [
        "model.inheritance"
      ],
      "lede": "A subclass gets everything its base class has, for free. It also gets everything the base class will ever have, and it depends on how the base class uses its own methods — a promise nobody wrote down, and one a legal change can break.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "How a method is found · Composition: holding parts instead · Contracts, and choosing between the three"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Override** — a subclass method with the same name as one in its base, found first. **Fragile base class** — when changing a base class breaks a subclass that never changed.",
          "entries": [
            "**Override** — a subclass method with the same name as one in its base, found first.",
            "**Fragile base class** — when changing a base class breaks a subclass that never changed."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "A few questions about a list that counts what is added to it. Type the answer,\nor pick one where there are buttons."
            },
            {
              "code": "class List {\n  items = [];\n  add(x)     { this.items.push(x); }\n  addAll(xs) { for (const x of xs) this.add(x); }\n}\nclass Counting extends List {\n  n = 0;\n  add(x) { this.n++; super.add(x); }\n}",
              "lang": "js"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const c = new Counting();  c.addAll([1, 2, 3]);   c.n",
                          "accept": [
                            "3"
                          ],
                          "miss": "**If you answered `0` because `addAll` belongs to `List` — the method was written in `List`, but the object is a `Counting`.** Inside `addAll`, `this.add` starts its lookup at that object and finds the subclass's `add` first.\n",
                          "why": "`addAll` calls `this.add`, which is the *subclass* version — so the counter fires three times. Correct here, and entirely dependent on a base-class implementation detail.\n"
                        },
                        {
                          "question": "`List.addAll` is rewritten to `this.items.push(...xs)` — same result for a plain `List`. Now `new Counting().addAll([1, 2, 3])` leaves `n` at what number?",
                          "accept": [
                            "0"
                          ],
                          "miss": "**If you answered `3` because `Counting` did not change — that is exactly the complaint.** `Counting` depended on an unwritten promise: that `addAll` is implemented in terms of `add`. Nothing stated it and nothing checked it, so a legal base-class change broke a subclass silently.\n",
                          "why": "The subclass silently stops counting. Nothing in `Counting` changed. That is the fragile base class problem in two lines.\n"
                        },
                        {
                          "question": "A later version of `List` adds `clear() { this.items = []; }`. Does a `Counting` object now have `clear()`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "miss": "**If you answered no because `Counting` never mentions it — `Counting` never mentions `addAll` either.** A subclass inherits whatever its base has *now*, and every caller of a `Counting` can call it. Here `clear()` empties the items and leaves `n` counting things that are gone.\n",
                          "why": "Yes. Whatever the base gains, the subclass offers — whether or not it keeps the subclass's own promises.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "The same `List`/`Counting` pair — after `add_all([1,2,3])`, what is `n`?",
                          "accept": [
                            "3"
                          ],
                          "why": "Same mechanism: `self.add` dispatches to the subclass.\n"
                        },
                        {
                          "question": "If `add_all` stopped calling `self.add`, what would `n` be?",
                          "accept": [
                            "0"
                          ],
                          "why": "Same fragility. This is a property of implementation inheritance, not of any one language.\n"
                        },
                        {
                          "question": "`class Robot(CanSpeak, CanWalk): pass` — what decides which `speak()` runs? Name it.",
                          "accept": [
                            "mro",
                            "the mro",
                            "method resolution order",
                            "the order"
                          ],
                          "why": "The MRO, computed from the declaration order. Python allows multiple inheritance and needs a rule for the ambiguity JavaScript avoids by forbidding it.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Does the `List`/`Counting` fragility exist in C#?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "It does — with `virtual`/`override` the same dispatch happens. Static typing does not protect you from this one.\n"
                        },
                        {
                          "question": "What does `sealed` on a class do?",
                          "accept": [
                            "prevents inheritance",
                            "no subclassing",
                            "blocks inheritance",
                            "stops inheritance",
                            "forbids inheritance",
                            "prevents subclassing",
                            "forbids subclassing",
                            "stops subclassing",
                            "no inheritance",
                            "it cannot be inherited",
                            "it cannot be subclassed",
                            "cannot be inherited",
                            "cannot be subclassed"
                          ],
                          "why": "Forbids subclassing entirely. C# makes classes sealed-by-choice and non-virtual by default, both aimed at this problem.\n"
                        },
                        {
                          "question": "What does `virtual` / `override` control?",
                          "accept": [
                            "dispatch",
                            "which method runs",
                            "polymorphism",
                            "overriding",
                            "dynamic dispatch",
                            "whether a method can be overridden",
                            "whether it can be overridden",
                            "override",
                            "method overriding"
                          ],
                          "why": "Whether a derived class may replace the method and whether the call dispatches dynamically. C# makes you opt in; JavaScript has no opt-out.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what a subclass inherits and what it commits to",
                "I can predict which method runs when a subclass overrides one the base class calls"
              ]
            }
          ]
        },
        {
          "h": "What a subclass gets"
        },
        {
          "p": "`class B extends A` makes `B` a **subclass** of `A`, its **base class**. You met\nthe mechanism in the lookup: `B.prototype`'s prototype is `A.prototype`, so\nanything a `B` does not define is found on `A`. A subclass does three things\nwith that:"
        },
        {
          "list": [
            "**adds** what is new — more fields, more methods;",
            "**overrides** what differs — a method with the same name, found first;",
            "**leaves the rest** to the base, untouched."
          ]
        },
        {
          "p": "Two worked examples. The comments in the first are the steps to keep."
        },
        {
          "code": "class User {\n  constructor(name) { this.name = name; }\n  greet() { return \"Hi, \" + this.name; }\n}\n\nclass Admin extends User {\n  constructor(name, perms) {\n    // let the base build its part first\n    super(name);                   // runs User's constructor on this object\n    // add what is new\n    this.perms = perms;\n  }\n  can(p) { return this.perms.includes(p); }\n  // leave the rest to the base: every Admin finds User's greet()\n}\n\nnew Admin(\"Kim\", [\"delete\"]).greet();   // \"Hi, Kim\"",
          "lang": "js",
          "file": "admin.js"
        },
        {
          "code": "class Notice {\n  constructor(text) { this.text = text; }\n  render()  { return this.heading() + \": \" + this.text; }\n  heading() { return \"Notice\"; }\n}\n\nclass Warning extends Notice {\n  // no constructor written: the base's runs, with the same arguments.\n  heading() { return \"WARNING\"; }     // overrides Notice's heading\n  // render() is not written here; every Warning finds Notice's.\n}\n\nnew Warning(\"Wet floor\").render();     // \"WARNING: Wet floor\"",
          "lang": "js",
          "file": "notice.js"
        },
        {
          "p": "`super(...)` must run before a subclass constructor touches `this`: until the\nbase's constructor has run, the object is not there to write to, and reaching\nfor `this` first is a `ReferenceError`. If a subclass writes no constructor at\nall, the base's runs for it."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "new Admin(\"Kim\", [\"delete\"]).can(\"delete\")",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`true`. `can` is `Admin`'s own addition, and `perms` was set after `super` built the rest.\n"
              },
              {
                "question": "`class Guest extends User { constructor(name) { this.visits = 0; super(name); } }` — what happens at `new Guest(\"Bo\")`?",
                "choices": [
                  "It works",
                  "It throws"
                ],
                "accept": [
                  "It throws"
                ],
                "miss": "**If you answered that it works — the order matters.** `this.visits` runs before `super(name)`, and until the base constructor has run there is no object to put `visits` on.\n",
                "why": "A ReferenceError. A subclass constructor must call `super` before it uses `this`.\n"
              }
            ]
          }
        },
        {
          "h": "Which method runs: every `this.` call starts at the object"
        },
        {
          "p": "Look again at `Warning`. `render` was written in `Notice`, and it calls\n`this.heading()`. Which `heading`?"
        },
        {
          "p": "The lookup does not start in the class where the code was written. It starts at\n**the object `this` refers to** — and `this` is the `Warning` you called\n`render` on. So `this.heading()` finds `Warning`'s override first, and the base\nclass's own `render` ends up calling the subclass's code."
        },
        {
          "code": "// new Warning(\"Wet floor\").render()\n//   render     -> not on the object, not on Warning.prototype -> Notice's\n//   this.heading() inside it: start again at the object\n//              -> Warning.prototype has heading -> \"WARNING\"",
          "lang": "js"
        },
        {
          "p": "That is what makes inheritance useful: a base class can write the outline once\nand let each subclass fill in a step. It is also the first opening question —\n`List`'s `addAll` calling `Counting`'s `add` — and it is the root of the next\nsection."
        },
        {
          "p": "**The rule for predicting:** every `this.something()` call starts its lookup at\nthe object, wherever the calling code was written. Walk out from the object, and\nthe first match runs."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "class Timer { start() { return \"Started \" + this.label(); }  label() { return \"timer\"; } }   class EggTimer extends Timer { label() { return \"egg timer\"; } }   new EggTimer().start()",
                "accept": [
                  "\"Started egg timer\"",
                  "Started egg timer",
                  "'Started egg timer'"
                ],
                "miss": "**If you answered `\"Started timer\"` — you read `this.label()` as \"the `label` in this class\".** It means \"the `label` this object finds\", and the object is an `EggTimer`.\n",
                "why": "`\"Started egg timer\"`. `start` is `Timer`'s; the `label` it calls is found from the object outward.\n"
              },
              {
                "code": "Same classes.   new Timer().start()",
                "accept": [
                  "\"Started timer\"",
                  "Started timer",
                  "'Started timer'"
                ],
                "why": "`\"Started timer\"`. A plain `Timer` has no subclass in its chain; the lookup finds the base's own `label`.\n"
              }
            ]
          }
        },
        {
          "h": "The promise nobody wrote down"
        },
        {
          "p": "Back to `Counting`. `new Counting().addAll([1,2,3])` counts three. Now a\nmaintainer optimises `addAll` to push directly, changing no public behaviour —\nand `Counting` silently counts zero."
        },
        {
          "p": "The subclass depended on **which method the base class calls internally**. That\nis not part of any interface, is not documented, and cannot be checked. It is the\n**fragile base class** problem, and it is why \"prefer composition over\ninheritance\" became a slogan."
        },
        {
          "p": "So a subclass commits to three things, and only the first is visible in its own\ncode:"
        },
        {
          "table": {
            "head": [
              "A subclass commits to",
              "Because"
            ],
            "rows": [
              [
                "everything the base offers **now**",
                "callers of the subclass can call all of it — including methods that ignore the subclass's own rules"
              ],
              [
                "everything the base will offer **later**",
                "the chain is walked at the call, so a method added to the base appears on every subclass"
              ],
              [
                "**how the base uses its own methods**",
                "an override is called from inside the base wherever it calls `this.` — and stops being called when the base changes how it does its work"
              ]
            ]
          }
        },
        {
          "p": "The opening's `clear()` is the second row: a method that empties the items and\nleaves `n` counting things that are gone, added to every `Counting` by a change\nto a different class."
        },
        {
          "block": "notice",
          "label": "When inheritance is the right answer",
          "body": [
            {
              "p": "When the relationship is genuinely **is-a** — anywhere the base is expected, the\nsubclass works — the base is **stable** and designed for extension, and you want\n**polymorphic dispatch**: a list of notices where each renders its own heading.\nIf you are reaching for it to reuse three lines of code, it is the wrong tool."
            }
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "The `Timer` maintainer rewrites `start() { return \"Started timer\"; }`. What does `new EggTimer().start()` return now?",
                "accept": [
                  "\"Started timer\"",
                  "Started timer",
                  "'Started timer'"
                ],
                "miss": "**If you answered `\"Started egg timer\"` — `start` no longer calls `this.label()`.** `EggTimer`'s override is still there, and nothing calls it.\n",
                "why": "`\"Started timer\"`. `EggTimer` did not change. It depended on `start` calling `label`, and a legal change to `Timer` withdrew that.\n"
              },
              {
                "question": "An `Admin` extends `User`. `User` later gains `resetPassword()`. Can a caller holding an `Admin` call `resetPassword()`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. Whatever the base offers, every subclass offers, from the moment it is added.\n"
              }
            ]
          }
        },
        {
          "video": {
            "title": "The fragile base class, broken live",
            "caption": "`Counting` working, then a legal one-line change inside `List` that breaks it with no error and no failing base-class test.\n"
          }
        },
        {
          "scaffold": {
            "label": "Predicting a call on a subclass",
            "body": [
              {
                "h": "Predicting a call on a subclass"
              },
              {
                "code": "class Letter {\n  compose()  { return this.greeting() + \" ... \" + this.signoff(); }\n  greeting() { return \"Dear customer\"; }\n  signoff()  { return \"Regards\"; }\n}\nclass Reminder extends Letter { greeting() { return \"Hello again\"; } }\nclass FinalReminder extends Reminder { signoff() { return \"Pay today\"; } }\n\n// new FinalReminder().compose()\n// Find the called method, from the object outward:\n//   compose  -> FinalReminder.prototype? no. Reminder.prototype? no. -> ______\n// For each method it calls on this, start again at the object:\n//   greeting -> FinalReminder.prototype? no. -> ______\n//   signoff  -> ______\n// Put the pieces together:\n//   -> ______",
                "lang": "js"
              },
              {
                "p": "`compose` is `Letter`'s. `greeting` is found on `Reminder.prototype`, `signoff`\non `FinalReminder.prototype`. The result is `\"Hello again ... Pay today\"` — two\nclasses filled in one outline that neither of them wrote."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what happens when two parents define the same method?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Multiple inheritance",
                  "Which one runs"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "not possible — one prototype chain",
                    "the question cannot arise"
                  ],
                  [
                    "**Python**",
                    "allowed — `class Robot(CanSpeak, CanWalk)`",
                    "the first one in the method resolution order, computed from the declaration order"
                  ],
                  [
                    "**C#**",
                    "not possible — one base class",
                    "the question cannot arise for classes"
                  ]
                ]
              }
            },
            {
              "p": "Every one of these languages either forbids inheriting implementation from two\nparents or needs a rule for the ambiguity. JavaScript and C# chose to forbid it;\nPython chose the rule, and a Python programmer reads the MRO before relying on\nwhich method wins. Either way, the ambiguity is real — and it is one reason the\nalternatives in the next two lessons exist."
            }
          ]
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening questions again from memory. Then say, for the second, what one\nsentence of documentation on `List` would have prevented the breakage — and what\nthat sentence cannot do that a check would."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A `ReadOnlyList` is proposed as `class ReadOnlyList extends List`, overriding\n`add` to throw `\"read-only\"`. Elsewhere, a function written for any `List` does\nthis:"
            },
            {
              "code": "function fillWith(list, xs) { list.addAll(xs); return list.items.length; }",
              "lang": "js"
            },
            {
              "p": "Decide whether `ReadOnlyList` should extend `List`. Use the three commitments:\nwhat does a `ReadOnlyList` offer its callers that it cannot honour, what happens\nto `fillWith` today, and what happens to it if `List.addAll` is later rewritten\nto push directly? Then say what the is-a test — \"anywhere a `List` is expected,\nthis works\" — says about the design."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A subclass **adds**, **overrides**, and leaves the rest to its base; its constructor calls `super(...)` before touching `this`.",
                "Every `this.` call starts its lookup at the **object**, so a base class's method calls the subclass's override wherever it calls `this.`.",
                "A subclass commits to everything the base offers now and later, and to **how the base uses its own methods** — the fragile base class: a legal change to the base can silently break a subclass that did not change."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Inheritance and the prototype chain",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Inheritance_and_the_prototype_chain",
              "note": "what `extends` actually builds."
            },
            {
              "title": "Classes — extends",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes/extends",
              "note": "including `super` and why constructor order is what it is."
            },
            {
              "title": "super",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/super",
              "note": "calling the base's constructor, and the base's version of an overridden method."
            }
          ]
        }
      ],
      "_source": "lessons/inherit-compose.md",
      "practice": {
        "id": "inherit-compose",
        "outcome": 6,
        "steps": [
          {
            "kind": "predict",
            "title": "Which method runs, and what is it committed to",
            "prompt": "`class List { items = []; add(x){ this.items.push(x); } addAll(xs){ for (const x of xs) this.add(x); } }` and `class Counting extends List { n = 0; add(x){ this.n++; super.add(x); } }`. Type each answer, or pick one where there are buttons.\n",
            "items": [
              {
                "id": "p1",
                "question": "`new Counting().addAll([1,2,3])` — what is `n` afterwards?",
                "accept": [
                  "3"
                ],
                "why": "`addAll` is `List`'s, but `this` is the `Counting` object, so `this.add` finds the subclass's `add` first. The counter fires three times."
              },
              {
                "id": "p2",
                "question": "`List.addAll` is rewritten to `this.items.push(...xs)`, and nothing else changes. What is `n` now?",
                "accept": [
                  "0"
                ],
                "why": "The subclass counted only because the base happened to call `this.add`. Nothing stated that, and nothing checked it — a legal base-class change broke a subclass that never changed."
              },
              {
                "id": "p3",
                "question": "Inside `List`'s `addAll`, called on a `Counting`: the lookup for `this.add` starts at which object?",
                "choices": [
                  "The Counting object",
                  "Counting.prototype",
                  "List.prototype"
                ],
                "accept": [
                  "The Counting object"
                ],
                "miss": "**The first step of every prediction.** Not the class where the line was written — the object `this` refers to, which is the `Counting` you called `addAll` on.\n",
                "why": "The `Counting` object. From there it walks out: `Counting.prototype` has `add`, so that one runs."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "An `AdminUser` has everything a `User` has, plus permissions. Is it a kind of `User`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Anywhere a `User` is expected, an `AdminUser` works. That substitution is the actual test for inheritance."
              },
              {
                "id": "p5",
                "question": "`List` later gains `clear() { this.items = []; }`. After `c.addAll([1,2]); c.clear();` what is `c.n`?",
                "accept": [
                  "2"
                ],
                "why": "`2`. `clear` arrived on every `Counting` from the base, and it knows nothing about `n`. The subclass now offers an operation that breaks its own count."
              },
              {
                "id": "p6",
                "question": "`class Sub extends List { constructor() { this.tag = \"x\"; super(); } }` — what happens at `new Sub()`?",
                "choices": [
                  "It works",
                  "It throws"
                ],
                "accept": [
                  "It throws"
                ],
                "why": "A ReferenceError. The subclass constructor used `this` before `super()` had built the object."
              }
            ],
            "hints": [
              {
                "when": "you answered 0 for the first",
                "say": "Ask what `this` is inside `addAll` when you called it on a `Counting`. The lookup for `this.add` starts at that object, not at the class the code was written in."
              },
              {
                "when": "you answered 3 for the second",
                "say": "Trace which method the rewritten `addAll` calls. If it no longer calls `this.add`, the subclass's override is never reached."
              }
            ]
          },
          {
            "kind": "run",
            "title": "The base class calls, the subclass answers",
            "prompt": "`render` is written once, in `Report`. Predict the three lines this prints, then run it.\n",
            "filename": "reports.js",
            "code": "class Report {\n  render() { return this.header() + \" | \" + this.body(); }\n  header() { return \"REPORT\"; }\n  body() { return \"(empty)\"; }\n}\nclass SalesReport extends Report {\n  header() { return \"SALES\"; }\n}\nclass AuditReport extends SalesReport {\n  body() { return \"3 entries\"; }\n}\n\nconsole.log(new SalesReport().render());\nconsole.log(new AuditReport().render());\nconsole.log(Object.getPrototypeOf(AuditReport.prototype) === SalesReport.prototype);\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "SALES | (empty)",
                  "SALES|(empty)",
                  "\"SALES | (empty)\""
                ],
                "why": "`render` and `body` come from `Report`, but `this.header()` starts its lookup at the `SalesReport` object and finds the override."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "SALES | 3 entries",
                  "SALES|3 entries",
                  "\"SALES | 3 entries\""
                ],
                "why": "Each `this.` call inside `render` starts again from the object: `header` is found on `SalesReport.prototype`, `body` on `AuditReport.prototype`. Where `render` was written does not matter."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "true"
                ],
                "why": "`extends` links one prototype to the next. That chain is the path each `this.` lookup walks."
              }
            ],
            "hints": [
              {
                "when": "you predicted \"REPORT | (empty)\" for the first",
                "say": "You read `this.header()` as \"the `header` in this class\". It means \"the `header` this object finds\" — and the object is a `SalesReport`."
              },
              {
                "when": "you predicted \"SALES | (empty)\" for the second",
                "say": "`AuditReport` inherits `header` from `SalesReport` and overrides `body`. Look up each method separately, starting from the object."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace each lookup",
            "prompt": "The same three classes. For `new AuditReport().render()`, pick which class's method runs at each call.\n",
            "items": [
              {
                "id": "t1",
                "question": "`render` — which class defines the one that runs?",
                "choices": [
                  "Report",
                  "SalesReport",
                  "AuditReport"
                ],
                "accept": [
                  "Report"
                ],
                "why": "Neither subclass defines `render`, so the lookup walks all the way to `Report.prototype`."
              },
              {
                "id": "t2",
                "question": "`this.header()`, called from inside `render`",
                "choices": [
                  "Report",
                  "SalesReport",
                  "AuditReport"
                ],
                "accept": [
                  "SalesReport"
                ],
                "why": "`this` is the `AuditReport` object. Its lookup for `header` finds nothing on `AuditReport.prototype` and stops at `SalesReport.prototype`."
              },
              {
                "id": "t3",
                "question": "`this.body()`, called from inside `render`",
                "choices": [
                  "Report",
                  "SalesReport",
                  "AuditReport"
                ],
                "accept": [
                  "AuditReport"
                ],
                "why": "Found at the first stop. `Report`'s `body` is still there, one link further out, and never reached."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "Someone adds `render() { return \"draft\"; }` to `SalesReport`. What does `new AuditReport().render()` return now?",
                "accept": [
                  "\"draft\"",
                  "draft",
                  "'draft'"
                ],
                "why": "The lookup for `render` now stops at `SalesReport.prototype`, one link before `Report`'s. A subclass inherits whatever the nearest link offers."
              },
              {
                "id": "t5",
                "question": "`const r = new AuditReport();  const f = r.render;  f()` — what happens?",
                "choices": [
                  "It works",
                  "It throws"
                ],
                "accept": [
                  "It throws"
                ],
                "why": "Called bare, `render` has no receiver, and class bodies are strict — `this` is `undefined`, so `this.header()` throws. Dispatch needs an object to start from."
              }
            ],
            "hints": [
              {
                "when": "you answered Report for the second or third",
                "say": "The call is written in `Report`, but the lookup does not start there. Start it at the object `this` refers to, and walk outward."
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Override through the base, not around it",
            "prompt": "A `TimedLog` is a `Log` that stamps each message with a tick count: `\"[t=1] boot\"`, `\"[t=2] ready\"`. Its `add` counts the tick; fill the one line that stores the stamped message **the way `Log` stores any message** — by asking `Log` to do it. Then, in one sentence: what would `TimedLog` be committing to if it pushed onto `this.lines` itself?\n",
            "exportName": "TimedLog",
            "filename": "timed-log.js",
            "starter": "class Log {\n  constructor() { this.lines = []; }\n  add(msg) { this.lines.push(msg); }\n  get size() { return this.lines.length; }\n}\n\nclass TimedLog extends Log {\n  constructor() {\n    super();\n    this.tick = 0;\n  }\n  add(msg) {\n    this.tick++;\n    // ___ gap: store \"[t=<tick>] <msg>\" using Log's own add\n  }\n}\n",
            "tests": [
              {
                "label": "one message is stamped t=1",
                "body": "const l = new subject(); l.add('boot'); return l.lines[0] === '[t=1] boot';"
              },
              {
                "label": "ticks count up",
                "body": "const l = new subject(); l.add('a'); l.add('b'); return l.lines[1] === '[t=2] b' && l.size === 2;"
              },
              {
                "label": "stores through Log's own add, not its internals",
                "body": "const base = Object.getPrototypeOf(subject.prototype); const orig = base.add; let calls = 0; base.add = function (m) { calls++; return orig.call(this, m); }; try { new subject().add('x'); } finally { base.add = orig; } return calls === 1;"
              },
              {
                "label": "two logs tick separately",
                "body": "const a = new subject(), b = new subject(); a.add('x'); a.add('y'); b.add('z'); return b.lines[0] === '[t=1] z';"
              }
            ],
            "hints": [
              {
                "when": "the tests fail with a stack overflow or RangeError",
                "say": "`this.add(...)` inside `add` calls the method you are in — the lookup starts at the object and finds `TimedLog`'s `add` again, forever. The base's version is reached with `super.`."
              },
              {
                "when": "only the \"through Log's own add\" test fails",
                "say": "You pushed onto `this.lines` yourself. It works today, and it ties `TimedLog` to how `Log` stores lines. Ask `Log` to store it: `super.add(...)`."
              },
              {
                "when": "the stamp shows t=0",
                "say": "The tick is counted on the line before the gap. Use `this.tick` after it has been incremented."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Own the promise instead of depending on it",
            "prompt": "This `Counting` works — while `List.addAll` happens to call `this.add`. Change `Counting` (not `List`) so `n` is right however `List.addAll` is written. The tests rewrite `List.addAll` to push directly and check again. Then, in one sentence: what does your `Counting` no longer depend on?\n",
            "exportName": "Counting",
            "filename": "counting.js",
            "starter": "class List {\n  items = [];\n  add(x) { this.items.push(x); }\n  addAll(xs) { for (const x of xs) this.add(x); }\n}\n\nclass Counting extends List {\n  n = 0;\n  add(x) { this.n++; super.add(x); }\n}\n",
            "tests": [
              {
                "label": "add stores and counts",
                "body": "const c = new subject(); c.add(1); return c.n === 1 && c.items.length === 1;"
              },
              {
                "label": "addAll stores and counts, with List as written",
                "body": "const c = new subject(); c.addAll([1,2,3]); return c.n === 3 && c.items.length === 3;"
              },
              {
                "label": "addAll stores and counts after List.addAll is rewritten to push directly",
                "body": "const base = Object.getPrototypeOf(subject.prototype); const orig = base.addAll; base.addAll = function (xs) { this.items.push(...xs); }; try { const c = new subject(); c.addAll([1,2,3]); return c.n === 3 && c.items.length === 3; } finally { base.addAll = orig; }"
              },
              {
                "label": "add and addAll together",
                "body": "const c = new subject(); c.add(1); c.addAll([2,3]); return c.n === 3 && c.items.length === 3;"
              }
            ],
            "hints": [
              {
                "when": "the rewritten-base test fails",
                "say": "Your count still depends on `List.addAll` calling `this.add`. Give `Counting` its own `addAll`, so the promise is one it keeps rather than one it hopes for."
              },
              {
                "when": "the as-written test shows n = 6",
                "say": "Your `addAll` counts the items and then calls `super.addAll`, which — as written — calls your `add`, which counts them again. Do not route through the base's `addAll` at all; add each item through your own `add`."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a discount cart",
            "prompt": "An AI assistant was asked for \"a cart for members that takes a discount off the total\". Constructing one throws, and fixing that reveals a second bug. Answer by line number where asked, then fix `MemberCart`.\n",
            "filename": "member-cart.js",
            "code": "class Cart {\n  constructor() { this.items = []; }\n  add(price) { this.items.push(price); }\n  total() { return this.items.reduce((a, b) => a + b, 0); }\n  checkout() { return \"Paid \" + this.total(); }\n}\n\nclass MemberCart extends Cart {\n  constructor(discount) {\n    this.discount = discount;\n    super();\n  }\n  total() { return this.total() * (1 - this.discount); }\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "`new MemberCart(0.1)` throws a ReferenceError. Which line throws?",
                "accept": [
                  "10",
                  "line 10"
                ],
                "why": "Line 10 uses `this` before `super()` on line 11 has built the object."
              },
              {
                "id": "c2",
                "question": "With the constructor order fixed, `m.total()` never returns. Which line calls itself?",
                "accept": [
                  "13",
                  "line 13"
                ],
                "miss": "**Look up `this.total` from the object.** The object is a `MemberCart`, so the first `total` found is line 13 — the method that is running.\n",
                "why": "Line 13. `this.total()` finds `MemberCart`'s own `total` again, forever. The base's version is `super.total()`."
              },
              {
                "id": "c3",
                "question": "Fully fixed: `const m = new MemberCart(0.1);  m.add(100);  m.checkout()` — what does it return?",
                "accept": [
                  "\"Paid 90\"",
                  "Paid 90",
                  "'Paid 90'"
                ],
                "miss": "**If you answered `\"Paid 100\"` — `checkout` is `Cart`''s, but its `this.total()` starts at the object.** The object is a `MemberCart`, so the discounted `total` runs.\n",
                "why": "`\"Paid 90\"`. The base's `checkout` calls the subclass's override — the same dispatch that makes the fragile base class possible."
              }
            ],
            "starter": "class Cart {\n  constructor() { this.items = []; }\n  add(price) { this.items.push(price); }\n  total() { return this.items.reduce((a, b) => a + b, 0); }\n  checkout() { return \"Paid \" + this.total(); }\n}\n\nclass MemberCart extends Cart {\n  constructor(discount) {\n    this.discount = discount;\n    super();\n  }\n  total() { return this.total() * (1 - this.discount); }\n}\n",
            "exportName": "MemberCart",
            "tests": [
              {
                "label": "constructing one does not throw",
                "body": "new subject(0.1); return true;"
              },
              {
                "label": "total takes the discount off",
                "body": "const m = new subject(0.25); m.add(40); m.add(40); return m.total() === 60;"
              },
              {
                "label": "checkout charges the discounted total",
                "body": "const m = new subject(0.5); m.add(100); return m.checkout() === 'Paid 50';"
              },
              {
                "label": "a zero discount changes nothing",
                "body": "const m = new subject(0); m.add(7); return m.total() === 7 && m.checkout() === 'Paid 7';"
              }
            ],
            "hints": [
              {
                "when": "the constructor still throws",
                "say": "In a class that `extends`, nothing may touch `this` before `super()` has run. Swap the two lines."
              },
              {
                "when": "total throws a RangeError about the call stack",
                "say": "`this.total()` inside `total` finds the method you are in. To use the base's total, ask for it with `super.`."
              },
              {
                "when": "checkout is discounted but total is not",
                "say": "You overrode `checkout`, so `total()` is still full price for everyone else who calls it. The base's `checkout` already calls `this.total()` — override the step, not the outline."
              }
            ]
          }
        ]
      }
    },
    "composition": {
      "id": "composition",
      "title": "Composition: holding parts instead",
      "kind": "read",
      "outcome": 6,
      "shape": "conceptual",
      "why": "Outcome 6 asks for the choice between inheritance, composition and interfaces to be justified, so composition has to be known as its own design, with its own costs, before it can be chosen. `model.composition`'s mastery is to build behaviour by holding parts rather than extending (`concepts.md`).",
      "concepts": [
        "model.composition"
      ],
      "lede": "Instead of being a kind of something, an object can hold one and pass the work to it. It offers only what it chooses, a change inside the part cannot reach it through a promise nobody wrote down, and the part can be swapped. The price is wiring every piece yourself.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Inheritance, and what a subclass commits to · Private state that guards an invariant · Contracts, and choosing between the three"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Composition** — building an object's behaviour from parts it holds. **Delegate** — pass a job to a part: `this.pricing.calculate(...)`. **Has-a / is-a** — holding a part, versus being a kind of it.",
          "entries": [
            "**Composition** — building an object's behaviour from parts it holds.",
            "**Delegate** — pass a job to a part: `this.pricing.calculate(...)`.",
            "**Has-a / is-a** — holding a part, versus being a kind of it."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four short programs that build behaviour from parts. Type what the last\nexpression produces, or `error` if you think it throws."
            },
            {
              "code": "const canSpeak = name => ({ speak: () => name + \" speaks\" });\nconst canWalk  = name => ({ walk:  () => name + \" walks\"  });",
              "lang": "js"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const robot = { ...canSpeak(\"R2\"), ...canWalk(\"R2\") };   robot.walk()",
                          "accept": [
                            "\"R2 walks\"",
                            "R2 walks",
                            "'R2 walks'"
                          ],
                          "miss": "**If this reads as \"objects with more steps\" — it is objects with *fewer promises*.** Each piece knows nothing about the others, so changing one cannot silently break another. That is the whole trade against inheritance.\n",
                          "why": "Composition: behaviour assembled from independent pieces. No hierarchy, no base class, nothing to be fragile.\n"
                        },
                        {
                          "code": "class Car { constructor(engine) { this.engine = engine; }  start() { return this.engine.start() + \"!\"; } }   new Car({ start: () => \"whirr\" }).start()",
                          "accept": [
                            "\"whirr!\"",
                            "whirr!",
                            "'whirr!'"
                          ],
                          "miss": "**If you answered `error` because that is not an Engine — `Car` never asked for one.** It holds whatever it was given and calls `start` on it. Anything with a `start` method will do.\n",
                          "why": "`\"whirr!\"`. `Car` delegates to its part, and the part is whatever you hand it — an electric engine, a petrol one, or a fake in a test.\n"
                        },
                        {
                          "code": "The same Car.   typeof new Car({ start: () => \"whirr\" }).stop",
                          "accept": [
                            "\"undefined\"",
                            "undefined",
                            "'undefined'"
                          ],
                          "miss": "**If you expected a function because engines stop — `Car` holds an engine; it is not one.** It offers exactly the methods written in `Car`, and nothing its part has unless it forwards it.\n",
                          "why": "`\"undefined\"`. A holder offers what it chooses to offer. Nothing of the part leaks through on its own.\n"
                        },
                        {
                          "code": "const r = { ...canSpeak(\"R2\"), speak: () => \"beep\" };   r.speak()",
                          "accept": [
                            "\"beep\"",
                            "beep",
                            "'beep'"
                          ],
                          "miss": "**If you answered `\"R2 speaks\"` — when two pieces bring the same name, the later one wins.** No error, no warning: the first `speak` is simply overwritten.\n",
                          "why": "`\"beep\"`. Spreading copies properties in order, and a later one replaces an earlier one with the same name.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can build behaviour by holding parts rather than extending"
              ]
            }
          ]
        },
        {
          "h": "Hold it, don't be it"
        },
        {
          "p": "Inheritance says **B is a kind of A**, and B gets all of A. Composition says **B\nhas an A**, and B asks it for help when it needs it. The part lives in a field;\nthe holder's methods **delegate** to it — they call the part to do its job."
        },
        {
          "p": "Two worked examples. The three comments in the first are the steps to keep."
        },
        {
          "code": "// \"An order's total depends on pricing rules, which change by season.\"\n\nclass Order {\n  // take the part in, rather than building it\n  constructor(pricing) {\n    this.pricing = pricing;          // has-a, not is-a\n    this.items = [];\n  }\n  // add only what is new\n  add(item) { this.items.push(item); }\n  // delegate the part's job to the part\n  get total() { return this.pricing.calculate(this.items); }\n}\n\nconst summer = { calculate: items => items.reduce((t, i) => t + i.price, 0) * 0.9 };\nnew Order(summer);                  // any object with calculate() will do",
          "lang": "js",
          "file": "order.js"
        },
        {
          "code": "// \"An alarm is due when the time reaches its set time.\"\n\nfunction makeAlarm(clock, at) {\n  // clock arrives as an argument — it is not created here.\n  return {\n    isDue: () => clock.now() >= at,     // delegates to the clock\n    snooze: minutes => { at += minutes * 60000; },\n  };\n}\n\nconst realClock = { now: () => Date.now() };\nconst testClock = { now: () => 1000 };      // a fake: time stands still\nmakeAlarm(testClock, 500).isDue();          // true",
          "lang": "js",
          "file": "alarm.js"
        },
        {
          "p": "A class and a factory, a price and a time — the same move. Neither builds its\npart (no `new Pricing()` inside `Order`, no `Date.now()` inside `makeAlarm`), so\nthe part can be anything with the right method. That is what lets a test hand\nin a clock that stands still, and what lets `Order` switch pricing each season\nwithout a subclass per season."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const o = new Order({ calculate: items => items.length * 2 });  o.add({ price: 50 });  o.add({ price: 10 });   o.total",
                "accept": [
                  "4"
                ],
                "miss": "**If you answered `60` — `Order` does not add prices itself.** It hands its items to whatever pricing it holds, and this one charges 2 per item.\n",
                "why": "`4`. `total` delegates. The part decides; the holder only asks.\n"
              },
              {
                "code": "const alarm = makeAlarm({ now: () => 100 }, 500);   alarm.isDue()",
                "choices": [
                  "true",
                  "false"
                ],
                "accept": [
                  "false"
                ],
                "why": "`false`. The clock it holds says 100, which has not reached 500.\n"
              }
            ]
          }
        },
        {
          "h": "Assembling from small parts"
        },
        {
          "p": "Holding one part in a field is the commoner form. The other is building an\nobject from several small pieces, each of which knows nothing about the others:"
        },
        {
          "code": "const canSpeak = name => ({ speak: () => `${name} speaks` });\nconst canWalk  = name => ({ walk:  () => `${name} walks`  });\n\nconst robot = name => ({ ...canSpeak(name), ...canWalk(name) });",
          "lang": "js",
          "file": "compose.js"
        },
        {
          "p": "No hierarchy. Each capability is independent and separately testable, and adding\na third changes nothing about the first two."
        },
        {
          "p": "The costs are real, and they are the reverse of inheritance's:"
        },
        {
          "list": [
            "**Wiring.** You assemble explicitly, every time. Nothing arrives for free.",
            "**No dispatch between the pieces.** `canWalk`'s code cannot call a `speak` that some other piece supplies, the way a base class's `this.heading()` found a subclass's override. Each piece works alone.",
            "**Name clashes are silent.** Two pieces with the same method name: the later spread wins, with no error — the fourth opening question."
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const canSwim = name => ({ move: () => name + \" swims\" });  const canRoll = name => ({ move: () => name + \" rolls\" });   ({ ...canRoll(\"B8\"), ...canSwim(\"B8\") }).move()",
                "accept": [
                  "\"B8 swims\"",
                  "B8 swims",
                  "'B8 swims'"
                ],
                "why": "`\"B8 swims\"`. Both pieces bring `move`; the one spread last replaces the other, silently.\n"
              },
              {
                "question": "Composition gives a holder its parts' methods automatically.",
                "choices": [
                  "True",
                  "False"
                ],
                "accept": [
                  "False"
                ],
                "miss": "**Only spreading copies methods across, and only the ones the pieces have at that moment.** An object that holds a part in a field offers none of the part's methods unless it writes one that delegates.\n",
                "why": "False. You choose what the holder offers, one method at a time. That is both the wiring cost and the control.\n"
              }
            ]
          }
        },
        {
          "h": "What the holder chooses to offer"
        },
        {
          "p": "A subclass commits to everything its base offers, now and later. A holder\noffers only what it writes. That difference is easiest to see with a class you\ndid not write."
        },
        {
          "code": "// \"A stack: push on top, pop from the top, peek at the top.\"\n\nclass Stack extends Array {\n  peek() { return this[this.length - 1]; }\n}\n\nconst s = new Stack();\ns.push(1, 2);\ns.unshift(0);        // allowed — every Stack is an Array, with all of Array's methods\ns.splice(1, 1);      // so is this. \"Only from the top\" is not a rule this stack keeps.",
          "lang": "js",
          "file": "stack-extends.js"
        },
        {
          "code": "// \"A stack: push on top, pop from the top, peek at the top.\"\n\nclass Stack {\n  #items = [];         // the part: private, so nothing outside can reach it\n  push(x) { this.#items.push(x); }\n  pop()   { return this.#items.pop(); }\n  peek()  { return this.#items[this.#items.length - 1]; }\n  get size() { return this.#items.length; }\n}\n\nnew Stack().unshift;   // undefined — a stack that holds an array is not an array",
          "lang": "js",
          "file": "stack-holds.js"
        },
        {
          "p": "The second stack offers four operations and keeps its rule. If a later version\nof JavaScript adds a new method to arrays, the first stack offers it too, and\nthe second does not. The holder pays for that control in forwarding: every\noperation it offers is one it wrote."
        },
        {
          "p": "The same applies to the fragile base class. A holder calls its part's public\nmethods and nothing else, so it cannot depend on which of its own methods the\npart happens to call internally — there is no `this` shared between them."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "The first Stack (extends Array).  const s = new Stack();  s.push(1, 2, 3);  s.reverse();   s.peek()",
                "accept": [
                  "1"
                ],
                "miss": "**If you answered `3` — `reverse` is an Array method, and this stack is an Array.** It turned the stack upside down, and `peek` now reads what used to be the bottom.\n",
                "why": "`1`. The subclass offered `reverse` because its base does, and nothing in `Stack` said otherwise.\n"
              },
              {
                "code": "The second Stack (holds #items).  typeof new Stack().reverse",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "`\"undefined\"`. The holder offers only what it wrote. `reverse` was never one of them.\n"
              }
            ]
          }
        },
        {
          "video": {
            "title": "The same model, rebuilt with composition",
            "caption": "The `Counting` list from the inheritance lesson, rebuilt to hold a list instead of extending one — then the same one-line change to the list that broke the subclass, made harmlessly.\n"
          }
        },
        {
          "scaffold": {
            "label": "Building a holder from a description",
            "body": [
              {
                "h": "Building a holder from a description"
              },
              {
                "p": "The same three steps, with the middle left to you."
              },
              {
                "code": "// \"A player plays tracks from a playlist, in an order decided by\n//  a shuffler. The shuffler is swapped when the user taps 'shuffle'.\"\n\nclass Player {\n  // take the part in, rather than building it\n  constructor(tracks, shuffler) {\n    this.tracks = tracks;\n    // ______\n  }\n  // delegate the part's job to the part\n  queue() {\n    // return ______;\n  }\n  // add only what is new\n  useShuffler(s) { this.shuffler = s; }\n}",
                "lang": "js",
                "file": "playlist-player.js"
              },
              {
                "p": "`this.shuffler = shuffler;` in the constructor, and `return\nthis.shuffler.order(this.tracks);` in `queue`. The player does not decide an\norder; it asks the part it holds, and swapping the part with `useShuffler`\nchanges the order without touching `Player`."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say, for the second and third,\nwhat `Car` would offer and what a change to the engine could break if `Car` had\nbeen written as `class Car extends Engine`."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A game has creatures that can fly, swim or bite, in any combination: a duck\nflies and swims, a shark swims and bites, a dragon flies and bites."
            },
            {
              "p": "Two designs:"
            },
            {
              "list": [
                "**A hierarchy** — `Creature`, then `FlyingCreature` and `SwimmingCreature` extending it, then the rest extending those.",
                "**Capabilities held or assembled** — `canFly`, `canSwim`, `canBite`, and each creature built from the ones it needs."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each does when the designers add a flying\nfish, and when they add a fourth ability, `glow`, that some creatures in every\nbranch have. Name one thing the hierarchy gives you that the capabilities do\nnot."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Composition**: an object **holds** a part and **delegates** to it, instead of being a kind of it. Take the part in; do not build it inside.",
                "A holder offers **only what it writes**, and a change inside the part cannot reach it through the part's internal calls.",
                "The price: wiring every piece yourself, no dispatch between pieces, and silent name clashes when pieces are spread together."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Spread syntax (...)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax",
              "note": "how pieces are copied into one object, and why the later one wins."
            },
            {
              "title": "Private properties",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes/Private_properties",
              "note": "a part held where only the holder can reach it."
            }
          ]
        }
      ],
      "_source": "lessons/composition.md",
      "practice": {
        "id": "composition",
        "outcome": 6,
        "steps": [
          {
            "kind": "predict",
            "title": "Holding, not being",
            "prompt": "Three quick ones. Type what the last expression produces, or pick an answer where there are buttons.\n",
            "items": [
              {
                "id": "p1",
                "code": "const canFly = name => ({ fly: () => name + \" flies\" });   const bird = { ...canFly(\"Tweety\") };   bird.fly()",
                "accept": [
                  "\"Tweety flies\"",
                  "Tweety flies",
                  "'Tweety flies'"
                ],
                "why": "`\"Tweety flies\"`. The behaviour was assembled from a piece, not inherited. There is no base class whose internals it depends on."
              },
              {
                "id": "p2",
                "question": "A `Printer` needs to send pages in order, and a `Queue` class exists. Should `Printer` hold a `Queue` or extend it?",
                "choices": [
                  "Hold it",
                  "Extend it"
                ],
                "accept": [
                  "Hold it"
                ],
                "miss": "**The first step is naming the part and the relationship.** A printer *uses* a queue; it is not one. Extending would make every queue operation — including ones that skip the line — part of what a printer offers.\n",
                "why": "Hold it. A printer has a queue and delegates ordering to it; it offers only the printer operations it writes."
              },
              {
                "id": "p3",
                "code": "class Lamp { constructor(bulb) { this.bulb = bulb; }  on() { return this.bulb.glow(); } }   new Lamp({ glow: () => \"warm\" }).on()",
                "accept": [
                  "\"warm\"",
                  "warm",
                  "'warm'"
                ],
                "miss": "**If you answered `error` because that is not a Bulb — `Lamp` never asked for one.** It calls `glow` on whatever it holds.\n",
                "why": "`\"warm\"`. `on` delegates to the part, and any object with a `glow` method will do."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const a = { ...{ label: () => \"first\" }, ...{ label: () => \"second\" } };   a.label()",
                "accept": [
                  "\"second\"",
                  "second",
                  "'second'"
                ],
                "why": "`\"second\"`. Two pieces brought the same name, and the later spread replaced the earlier one — silently."
              },
              {
                "id": "p5",
                "code": "class Stack extends Array {}   const s = new Stack();  s.push(1, 2);  s.unshift(0);   s.length",
                "accept": [
                  "3"
                ],
                "why": "`3`. A subclass of Array offers every Array method, including ones that break \"only from the top\"."
              },
              {
                "id": "p6",
                "question": "The `Lamp` above. `new Lamp().on()` — what happens?",
                "choices": [
                  "It works",
                  "It throws"
                ],
                "accept": [
                  "It throws"
                ],
                "why": "A TypeError: `this.bulb` is `undefined`, and `on` asks it for `.glow`. A holder that is not given its part has nothing to delegate to."
              }
            ],
            "hints": [
              {
                "when": "you answered extend for the Printer",
                "say": "Ask the is-a question: anywhere a Queue is expected, would a Printer make sense? If not, the printer needs a queue rather than being one."
              },
              {
                "when": "you answered error for the Lamp with a bulb",
                "say": "Look at what `Lamp` checks about its part. It checks nothing — it only calls `glow`. Does the object passed in have one?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "A part, swapped",
            "prompt": "`Alerts` holds a sender and passes each alert to it. Predict the four lines this prints, then run it.\n",
            "filename": "alerts.js",
            "code": "const quiet = { send: (to, text) => \"queued for \" + to };\nconst loud  = { send: (to, text) => \"SENT to \" + to + \": \" + text };\n\nclass Alerts {\n  constructor(sender) { this.sender = sender; this.count = 0; }\n  alert(to, text) {\n    this.count++;\n    return this.sender.send(to, text);\n  }\n}\n\nconst a = new Alerts(quiet);\nconsole.log(a.alert(\"kim\", \"hi\"));\na.sender = loud;\nconsole.log(a.alert(\"kim\", \"hi\"));\nconst b = new Alerts(loud);\nconsole.log(a.count + \" \" + b.count);\nconsole.log(typeof a.send);\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "queued for kim",
                  "\"queued for kim\""
                ],
                "why": "`alert` delegated to the part `a` was built with, `quiet`."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "SENT to kim: hi",
                  "\"SENT to kim: hi\""
                ],
                "why": "The part was swapped. `Alerts` did not change; it asks whatever it holds now."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "2 0",
                  "\"2 0\""
                ],
                "why": "The count is the holder's own state — `a` counted two alerts through two different senders; `b` has sent nothing."
              },
              {
                "id": "r4",
                "question": "Fourth line printed",
                "accept": [
                  "undefined",
                  "\"undefined\""
                ],
                "miss": "**If you predicted `function` — `Alerts` holds a sender; it is not one.** It offers `alert`, which it wrote, and nothing of its part.\n",
                "why": "`undefined`. Holding a part does not hand its methods to the holder."
              }
            ],
            "hints": [
              {
                "when": "you predicted \"queued for kim\" twice",
                "say": "`a.sender = loud` replaced the part. `alert` reads `this.sender` at each call, not once at construction."
              },
              {
                "when": "you predicted \"2 2\" or \"1 1\"",
                "say": "`count` belongs to each `Alerts` object and is changed only by that object's `alert`. How many times was each one's `alert` called?"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Hold the part instead of being it",
            "prompt": "Build `makeNotifier(sender, log)`: an object with an `alert(user, text)` that records the alert in `log`, then sends `\"ALERT: \" + text` through whatever `sender` it was given. Pick the lines in order; two do not belong.\n",
            "lines": [
              "function makeNotifier(sender, log) {",
              "  const alert = (user, text) => {",
              "    log.record(\"alert to \" + user.email);",
              "    return sender.send(user.email, \"ALERT: \" + text);",
              "  };",
              "  return { alert };",
              "}"
            ],
            "distractors": [
              {
                "line": "    return EmailSender.send(user.email, \"ALERT: \" + text);",
                "why": "This names one concrete class instead of using the `sender` it was handed. The notifier can now only ever email, and a test cannot pass in a fake — the composition has been undone."
              },
              {
                "line": "  return { alert: alert() };",
                "why": "This calls `alert` once, with no user, while building the object, and stores whatever it returned. The object should hold the function, not the result of calling it."
              }
            ],
            "why": "The notifier holds two parts and delegates to them; it is neither a sender nor a log. Because it uses only `sender.send` and `log.record`, anything with those two methods will do — the contract is the shape, stated by what the code calls.\n",
            "hints": [
              {
                "when": "the return came before the const",
                "say": "`alert` is a `const`. Reading it before its line has run is the temporal dead zone — and a `return` ends the function, so nothing after it runs anyway."
              },
              {
                "when": "the record line came after the send",
                "say": "The arrow function returns what `sender.send` returns, so that line has to be last in its body. Anything after a `return` never runs."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a class hierarchy",
            "prompt": "An AI assistant was asked for \"a payment processor that logs each charge\" and produced this. Constructing one throws, and the design has a problem that would outlast the fix. Answer by line number where asked.\n",
            "filename": "payment.js",
            "code": "class Logger {\n  constructor() { this.lines = []; }\n  log(msg) { this.lines.push(msg); }\n}\n\nclass PaymentProcessor extends Logger {\n  constructor(gateway) {\n    this.gateway = gateway;\n    super();\n  }\n  charge(amount) {\n    this.log(\"charging \" + amount);\n    return this.gateway.charge(amount);\n  }\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "`new PaymentProcessor(g)` throws a ReferenceError. Which line throws?",
                "accept": [
                  "8",
                  "line 8"
                ],
                "why": "A subclass constructor must call `super()` before it touches `this`. Line 8 uses `this` while the object does not exist yet."
              },
              {
                "id": "c2",
                "question": "Which line commits a payment processor to being a kind of logger?",
                "accept": [
                  "6",
                  "line 6"
                ],
                "why": "`extends Logger` makes every processor a logger: it inherits `log` and `lines` as public members and depends on `Logger`'s internals from now on. It needs a logger; it is not one."
              },
              {
                "id": "c3",
                "question": "With only the `super()` order fixed, can a caller run `processor.log(\"refunded\")` and write into its log?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Everything `Logger` offers is now part of what a `PaymentProcessor` offers. Inheritance hands the subclass's callers the base's whole surface, whether you meant to promise it or not."
              }
            ],
            "starter": "class Logger {\n  constructor() { this.lines = []; }\n  log(msg) { this.lines.push(msg); }\n}\n\n// Fix it: a PaymentProcessor holds a logger — constructor(gateway, logger).\nclass PaymentProcessor extends Logger {\n  constructor(gateway) {\n    this.gateway = gateway;\n    super();\n  }\n  charge(amount) {\n    this.log(\"charging \" + amount);\n    return this.gateway.charge(amount);\n  }\n}\n",
            "exportName": "PaymentProcessor",
            "tests": [
              {
                "label": "constructing one does not throw",
                "body": "new subject({ charge: () => 1 }, { log() {} }); return true;"
              },
              {
                "label": "charge returns what the gateway returns",
                "body": "const p = new subject({ charge: a => 'ok:' + a }, { log() {} }); return p.charge(5) === 'ok:5';"
              },
              {
                "label": "charge logs through the logger it was given",
                "body": "const seen = []; const p = new subject({ charge: () => 1 }, { log: m => seen.push(m) }); p.charge(5); return seen[0] === 'charging 5';"
              },
              {
                "label": "a processor does not offer log or lines to its callers",
                "body": "const p = new subject({ charge: () => 1 }, { log() {} }); return p.log === undefined && p.lines === undefined;"
              }
            ],
            "hints": [
              {
                "when": "the constructor still throws",
                "say": "In a class that `extends`, nothing may touch `this` before `super()` has run. If the class no longer extends anything, there is no `super()` to call at all."
              },
              {
                "when": "the logger test fails but construction works",
                "say": "You fixed the order but kept `extends Logger`, so `this.log` writes into the processor's own `lines`. Store the logger you are given and call its `log`."
              },
              {
                "when": "only the last test fails",
                "say": "You hold a logger now, but the class still says `extends Logger` — so every processor still offers `log` and `lines` to whoever holds it. Holding and being are alternatives; drop the one you no longer use."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Replace inheritance with composition",
            "prompt": "`CountingList` used to extend `List` and broke when the base changed. Write it to *hold* a list instead of being one, keeping `add`, `addAll`, `size` and `count`. It must keep counting no matter how the inner list implements `addAll`. Then, in one sentence: which change to `List` can no longer break your count, and why not?\n",
            "exportName": "makeCountingList",
            "filename": "counting.js",
            "starter": "// The inner list — you may not change it, and it may change under you.\nclass List {\n  constructor() { this.items = []; }\n  add(x) { this.items.push(x); }\n  addAll(xs) { for (const x of xs) this.items.push(x); }  // note: NOT this.add\n  get size() { return this.items.length; }\n}\n\n// Return an object with add, addAll, size and count.\n// count must be right regardless of how List.addAll is written.\nfunction makeCountingList() {\n\n}\n",
            "tests": [
              {
                "label": "add stores and counts",
                "body": "const c = subject(); c.add(1); return c.size === 1 && c.count === 1;"
              },
              {
                "label": "addAll stores and counts every item",
                "body": "const c = subject(); c.addAll([1,2,3]); return c.size === 3 && c.count === 3;"
              },
              {
                "label": "counting survives a base class that does not call add",
                "body": "const c = subject(); c.addAll([1,2]); c.add(3); return c.count === 3;"
              },
              {
                "label": "an empty list counts zero",
                "body": "return subject().count === 0;"
              },
              {
                "label": "two counting lists are independent",
                "body": "const a = subject(), b = subject(); a.add(1); return b.count === 0;"
              },
              {
                "label": "addAll with an empty array changes nothing",
                "body": "const c = subject(); c.addAll([]); return c.count === 0 && c.size === 0;"
              }
            ],
            "hints": [
              {
                "when": "the base-class test fails",
                "say": "You are still relying on the inner `addAll` calling `add`. It does not. Count in *your* `addAll` rather than hoping the delegate routes through your `add`."
              },
              {
                "when": "size is always zero",
                "say": "You are counting but not storing. Delegate the storage to the inner list — hold one and call it — while keeping the count yourself."
              },
              {
                "when": "count or size is a function rather than a number",
                "say": "The tests read `c.count` and `c.size` as values. A getter (`get count() { … }`) computes one on each read; a plain method has to be called."
              },
              {
                "when": "the independence test fails",
                "say": "Your list or counter lives outside the factory, so every instance shares it. Create both inside — a closure keeps each one's own."
              }
            ]
          }
        ]
      }
    },
    "contracts": {
      "id": "contracts",
      "title": "Contracts, and choosing between the three",
      "kind": "read",
      "outcome": 6,
      "shape": "conceptual",
      "why": "Outcome 6 does not ask for a favourite; it asks for the choice among inheritance, composition and interface-based abstraction to be justified (`model.choosing`, \"the outcome's actual demand\"). That needs all three in hand, so it sits last, with `model.interface`: a contract stated by convention, which is CS 140's half of interfaces — contracts a compiler enforces are CS 240's (D-019).",
      "concepts": [
        "model.interface",
        "model.choosing"
      ],
      "lede": "A contract is what a caller may rely on, stated apart from how it is done. In JavaScript nothing checks it — having the right methods is the whole qualification — so stating it is your job. With contracts, composition and inheritance all in hand, the outcome's real question is which one a model should use, and why.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Inheritance, and what a subclass commits to · Composition: holding parts instead · What a module promises"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Contract** — what a caller may rely on: the methods, what they take, what they return. **Duck typing** — any object with the right methods will do; nothing checks the relationship.",
          "entries": [
            "**Contract** — what a caller may rely on: the methods, what they take, what they return.",
            "**Duck typing** — any object with the right methods will do; nothing checks the relationship."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "A few questions about objects standing in for each other. Type the answer, or\n`error` if you think it throws. Where there are buttons, pick one."
            },
            {
              "code": "class Dog { constructor(n) { this.name = n; } speak() { return this.name + \" barks\"; } }\nconst d = new Dog(\"Rex\");",
              "lang": "js"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const duck = { speak: () => \"quack\" };   [d, duck].map(x => x.speak())",
                          "accept": [
                            "[\"Rex barks\",\"quack\"]",
                            "[Rex barks, quack]",
                            "Rex barks, quack",
                            "[\"Rex barks\", \"quack\"]"
                          ],
                          "miss": "**If you expected `duck` to fail because it is not a Dog — it has a `speak` method, and that is the entire requirement.** Convenient, and unchecked: nothing warns you when an object *almost* fits.\n",
                          "why": "Nothing relates `duck` to `Dog`. Having the method is the only qualification — duck typing.\n"
                        },
                        {
                          "question": "What does JavaScript check before letting an object be used where a \"speaker\" is expected?",
                          "accept": [
                            "nothing",
                            "none",
                            "no check",
                            "nothing at all"
                          ],
                          "miss": "**If you named something — there is no `interface` keyword and no check.** The contract exists only in your documentation and your head, which is why stating it deliberately is a discipline here rather than a language feature.\n",
                          "why": "Nothing. There is no `interface` keyword and no check — the contract exists only in your head and your documentation.\n"
                        },
                        {
                          "code": "const mute = { speek: () => \"...\" };   [d, mute].map(x => x.speak())",
                          "accept": [
                            "error",
                            "typeerror",
                            "type error",
                            "throws",
                            "it throws",
                            "an error"
                          ],
                          "miss": "**If you answered `[\"Rex barks\", \"...\"]` — look at the spelling.** `mute` has `speek`, not `speak`. Nothing noticed when `mute` was made or put in the array; the failure arrives at the call.\n",
                          "why": "A TypeError: `x.speak is not a function`. A contract nobody checks is broken at the moment it is used, not the moment it is violated.\n"
                        },
                        {
                          "question": "A `PaymentProcessor` needs to log, and a `Logger` class exists. Is a `PaymentProcessor` a kind of `Logger`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes because it logs — it *uses* logging.** Anywhere a Logger is expected, would a payment processor make sense? That substitution is what \"a kind of\" means.\n",
                          "why": "No. It needs a logger; it is not one — which already rules out one of the three designs.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "class Duck:\\n    def speak(self): return \"quack\"\\n\\n[x.speak() for x in (d, Duck())]",
                          "accept": [
                            "[\"Rex barks\", \"quack\"]",
                            "['Rex barks', 'quack']",
                            "Rex barks, quack"
                          ],
                          "why": "Duck typing, and the term comes from here. No shared base class is required.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "What does implementing `interface ISpeak { string Speak(); }` commit you to?",
                          "accept": [
                            "the method",
                            "implementing Speak",
                            "the signature",
                            "the contract",
                            "the Speak method",
                            "a Speak method",
                            "having a Speak method",
                            "the method signature"
                          ],
                          "why": "The signature, checked at compile time. You commit to the shape and nothing else — no state, no implementation.\n"
                        },
                        {
                          "question": "Can a class implement two interfaces? Inherit two classes?",
                          "choices": [
                            "Yes to both",
                            "Two interfaces yes, two classes no",
                            "No to both"
                          ],
                          "accept": [
                            "Two interfaces yes, two classes no"
                          ],
                          "why": "Many interfaces, one base class. The restriction exists precisely because inheriting implementation twice creates ambiguity.\n"
                        },
                        {
                          "question": "`var list = new List<Animal> { new Dog(\"Rex\"), new Duck() };` — will this compile?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Only if `Duck` derives from `Animal`. Sharing a method name is not enough — the type relationship must be declared.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can state what a caller may rely on, separately from how it is done",
                "I can defend inheritance, composition or an interface for a specific model"
              ]
            }
          ]
        },
        {
          "h": "A contract is what a caller may rely on"
        },
        {
          "p": "An **interface**, or **contract**, says *what a caller may rely on*, separately\nfrom how it is done. JavaScript has no keyword for it — which does not mean you\nhave no contract, only that it is unenforced. The contract is the set of methods\nthe caller calls, what it passes them, and what it expects back. Any object that\nmeets it will do: that is **duck typing**, and the first opening question."
        },
        {
          "p": "Because nothing checks it, the only place a contract exists is where you write\nit down. Two worked examples. The steps in the first are the part to keep."
        },
        {
          "code": "// The part an Order holds (from composition).\n\n/**\n * What the caller calls:\n * @typedef {Object} Pricing\n * @property {(items: Item[]) => number} calculate\n *\n * What each method returns, including when there is nothing:\n *   calculate returns the total in cents; 0 for an empty list, never undefined.\n *\n * What the caller must not rely on:\n *   how the total is reached — discounts, tax and rounding are the part's.\n */",
          "lang": "js",
          "file": "pricing.js"
        },
        {
          "code": "/**\n * @typedef {Object} Store\n * @property {(key: string) => any} get\n *   returns the value, or undefined for a key never set. Never null.\n * @property {(key: string, value: any) => void} set\n *   replaces any value already stored under that key.\n *\n * Not part of the contract: where values are kept, or in what order.\n */",
          "lang": "js",
          "file": "store.js"
        },
        {
          "p": "The fourth step is where you write them: **where the caller will see it**. A\nJSDoc comment like these is a real contract — it tells a reader what `Order`\nneeds, and your editor shows it wherever `Order` is used. Nothing enforces it.\n\"Program to a contract\" is a design idea first and a keyword second — in a\nlanguage with no keyword, stating the contract is the whole of it."
        },
        {
          "p": "The second step is the one people skip. A contract that says what `get` returns\nbut not what it returns for a missing key has left the caller guessing between\nJavaScript's two absences — the choice every type in this family has had to make\nonce."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "Under the `Store` contract, what does `store.get(\"never-set\")` return?",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined` — the contract says so. A caller may write `=== undefined` and rely on it.\n"
              },
              {
                "question": "A new `Store` keeps values in an array instead of a `Map`. Does a caller of `get` and `set` need to change?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**The contract names `get` and `set` and what they return — not where values live.** \"Where values are kept\" was listed as something the caller must not rely on.\n",
                "why": "No. That is the point of stating the contract apart from how it is done: the how can change behind it.\n"
              }
            ]
          }
        },
        {
          "h": "Two implementations, one contract"
        },
        {
          "p": "Here are two objects that both claim to be a `Store`, and a caller written\nagainst the contract:"
        },
        {
          "code": "const mapStore = {\n  data: new Map(),\n  get(k)    { return this.data.get(k); },          // missing -> undefined\n  set(k, v) { this.data.set(k, v); },\n};\n\nconst objStore = {\n  data: {},\n  get(k)    { return this.data[k] ?? null; },      // missing -> null\n  set(k, v) { this.data[k] = v; },\n};\n\nfunction greeting(store, id) {\n  const name = store.get(id);\n  return name === undefined ? \"Hello, stranger\" : \"Hello, \" + name;\n}",
          "lang": "js",
          "file": "two-stores.js"
        },
        {
          "p": "Both have `get` and `set`, so both pass the only check JavaScript makes — none.\nBut `objStore` breaks the contract on the one line nobody tests first: a missing\nkey gives `null`, and `greeting(objStore, \"u2\")` says `\"Hello, null\"`. The caller\ndid nothing wrong; it relied on what the contract said."
        },
        {
          "p": "Having the right method names is duck typing's whole test, and it is not the\nwhole contract. What the methods return — especially when there is nothing — is\npart of it too, and only the written contract says so."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "mapStore.set(\"u1\", \"Ada\");   greeting(mapStore, \"u1\")",
                "accept": [
                  "\"Hello, Ada\"",
                  "Hello, Ada",
                  "'Hello, Ada'"
                ],
                "why": "`\"Hello, Ada\"`. The key is there, so `get` returns the name.\n"
              },
              {
                "code": "greeting(objStore, \"u2\")   — u2 was never set",
                "accept": [
                  "\"Hello, null\"",
                  "Hello, null",
                  "'Hello, null'"
                ],
                "miss": "**If you answered `\"Hello, stranger\"` — `objStore` returns `null` for a missing key, and the caller checks for `undefined`.** The two absences are different values, and this implementation picked the one the contract ruled out.\n",
                "why": "`\"Hello, null\"`. `objStore` has the right methods and the wrong absence.\n"
              }
            ]
          }
        },
        {
          "h": "Three ways, three commitments"
        },
        {
          "p": "You now have all three ways of giving an object behaviour it did not define\nitself:"
        },
        {
          "table": {
            "head": [
              "",
              "You get",
              "You commit to"
            ],
            "rows": [
              [
                "**Inheritance**",
                "state *and* behaviour, and polymorphic dispatch",
                "the base class's internals — its changes are your changes"
              ],
              [
                "**Composition**",
                "behaviour, assembled from parts",
                "wiring it yourself; more code, fewer promises"
              ],
              [
                "**Interface**",
                "a shape callers can rely on",
                "implementing every member; no implementation is shared"
              ]
            ]
          }
        },
        {
          "p": "Reading that table is most of the outcome. The rest is applying it, and the\noutcome asks you to **justify** the choice — which means asking the same\nquestions every time, in order:"
        },
        {
          "code": "// You want object B to have behaviour that lives elsewhere.\n\n// 1. Is B genuinely a KIND OF A — everywhere an A is expected, a B works?\n//    no  -> not inheritance. Go to 3.\n//    yes -> keep going.\n\n// 2. Is A stable, and designed to be extended (documented, versioned together)?\n//    no  -> composition. The fragile base class is waiting for you.\n//    yes -> inheritance is defensible. Stop.\n\n// 3. Do several unrelated types need to be usable interchangeably?\n//    yes -> define the contract (a documented shape) and have each meet it.\n//           Compose the behaviour behind it.\n//    no  -> plain composition: hold a collaborator and delegate.",
          "lang": "js",
          "file": "choose.js"
        },
        {
          "p": "Two worked through:"
        },
        {
          "code": "// Notice, Warning and Alert all render a heading and a text. Notice is\n// ours, small, and written to be extended.\n\n// Ask the is-a question:\n//   does a Warning work anywhere a Notice is expected?         yes\n// Ask whether the base is stable and meant for it:\n//   ours, small, designed for subclasses to fill in heading()  yes\n// Name the choice and what it costs:\n//   inheritance — and we accept that Warning depends on Notice.render\n//   calling this.heading().",
          "lang": "js",
          "file": "notices.js"
        },
        {
          "code": "// A report must be exported as CSV, as PDF, or — in tests — to an array.\n// The three share nothing except that each can take rows and produce output.\n\n// Is a PdfExporter a kind of CsvExporter?                  no\n// Must unrelated types be interchangeable?\n//   the report calls one exporter, whichever it is given    yes\n// So: a contract — { export(rows) -> string } — that each meets, and the\n//   report holds whichever exporter it is given. Nothing checks that a new\n//   exporter meets it, so the contract has to be written down.",
          "lang": "js",
          "file": "exporters.js"
        },
        {
          "p": "The second used two of the three together: a **contract** for what the report\nmay rely on, **composition** to hold whichever exporter it gets. That is common,\nand a justified choice often names both."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "A `Checkout` needs to calculate tax, and a `TaxTable` class exists that changes every year. Inheritance, composition or a contract?",
                "choices": [
                  "Inheritance",
                  "Composition",
                  "A contract"
                ],
                "accept": [
                  "Composition"
                ],
                "miss": "**Start with the is-a question.** Is a checkout a kind of tax table? No — so not inheritance. Nothing needs to be interchangeable with anything else, so hold the table and ask it.\n",
                "why": "Composition. A checkout is not a tax table, and holding one means next year's table is a different part, not a different class.\n"
              },
              {
                "question": "`save(thing)` must accept an `Invoice`, a `Photo` or a test fake, which share nothing but `toJSON()`. Inheritance, composition or a contract?",
                "choices": [
                  "Inheritance",
                  "Composition",
                  "A contract"
                ],
                "accept": [
                  "A contract"
                ],
                "why": "A contract. Unrelated types must be usable interchangeably, and all `save` relies on is `toJSON()` — so state that, and let each type meet it.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Choosing between the three",
            "body": [
              {
                "h": "Choosing between the three"
              },
              {
                "p": "Now run the questions on one yourself."
              },
              {
                "code": "// A PaymentProcessor needs to log. A Logger class exists.\n// 1. Is a PaymentProcessor a kind of Logger?   ______\n// 2. So which of the three?                    ______\n// 3. What does that look like in one line?     ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "No — a payment processor is not a kind of logger. So composition: hold one.\n`constructor(logger) { this.logger = logger; }`. And because you now depend on a\n*shape* rather than a class, a test can pass a fake with the same shape."
              }
            ]
          }
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Composition by default; inheritance when the is-a is real and the base is yours."
              },
              {
                "code": "class Order { constructor(pricing) { this.pricing = pricing; } }   // has-a\nclass AdminUser extends User { }                                    // is-a",
                "lang": "js"
              },
              {
                "p": "For contracts, use JSDoc `@typedef` — your editor shows it at call sites and it\ncosts no build step. It is the closest JavaScript gets to stating an interface."
              }
            ],
            "python": [
              {
                "p": "Composition by default, and a docstring stating what a collaborator must\nprovide — duck typing means the contract is whatever you write down. Multiple\ninheritance is available and mixins are idiomatic — read the MRO before relying\non it."
              }
            ],
            "csharp": [
              {
                "p": "Interfaces first, inheritance sparingly, `sealed` by default."
              },
              {
                "code": "public interface IPricing { decimal Calculate(IEnumerable<Item> items); }\npublic sealed class Order { public Order(IPricing pricing) { } }",
                "lang": "csharp"
              },
              {
                "p": "`sealed` and non-virtual-by-default are the language's answer to the fragile base\nclass: you cannot be extended unless the author decided you should be."
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what makes two types interchangeable, and does anything check it?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Qualification",
                  "Stating the contract",
                  "Checked"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "having the method — duck typing",
                    "a comment, such as JSDoc",
                    "never — a missing method fails at the call"
                  ],
                  [
                    "**Python**",
                    "having the method — duck typing",
                    "an abstract base class (`abc`), or a `Protocol`",
                    "an abstract base class **while running**, when you create an object that is missing a method; a `Protocol` only if you run a separate checker"
                  ],
                  [
                    "**C#**",
                    "a declared base class or `interface`",
                    "`interface`, in the language",
                    "**compile time**"
                  ]
                ]
              }
            },
            {
              "p": "JavaScript and Python let any object with the right method stand in, which is\nwhat makes them flexible and what makes a typo a runtime failure. Python can\nalso be asked to check: an abstract base class refuses to create an object that\nhas not implemented every method it marks abstract. C# requires the relationship\nto be stated, so the same mistake is found before the program runs."
            },
            {
              "p": "The checking column is the same *when do you find out* axis as everywhere else.\nThe design advice does not change with it: state your contracts either way. What\nchanges is whether a violation is a compile error, a failing test, or a support\nticket."
            }
          ]
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening questions again from memory. Then, for the third, write the\none-line contract that would have told `mute`'s author what was expected — and\nsay where in the program the mistake would have been found if JavaScript checked\nit."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A `Report` class has three subclasses — `PdfReport`, `CsvReport`, `HtmlReport` —\neach overriding `render()`. A fourth requirement arrives: reports must be\nemailable, but only PDF and HTML make sense as attachments."
            },
            {
              "p": "Three designs:"
            },
            {
              "list": [
                "**A fourth subclass level** — `EmailableReport extends Report`.",
                "**An interface** — `Emailable`, implemented by the two that qualify.",
                "**Composition** — an `Emailer` that takes a report and knows how to attach it."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what the first does when a fifth\nreport type needs emailing but not rendering, what the second costs in a language\nthat will not check it, and where the third puts the knowledge of which formats\nare attachable. Name the design you would choose if the list of formats changed\nmonthly."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A **contract** states what a caller may rely on — the methods, what they return, **including when there is nothing** — apart from how it is done. In JavaScript nothing checks it, so it exists where you write it down.",
                "**Inheritance** commits you to a base's internals; **composition** to wiring; an **interface** to meeting a shape with no shared code.",
                "To justify a choice, ask in order: is B genuinely a kind of A? Is A stable and meant to be extended? Must unrelated types be interchangeable?"
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "JSDoc @typedef",
              "href": "https://jsdoc.app/tags-typedef.html",
              "note": "stating a contract JavaScript will not enforce."
            },
            {
              "title": "Classes",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes",
              "note": "`extends` for the is-a case, and fields for holding a part."
            }
          ]
        }
      ],
      "_source": "lessons/contracts.md",
      "practice": {
        "id": "contracts",
        "outcome": 6,
        "steps": [
          {
            "kind": "predict",
            "title": "What stands in, and which design",
            "prompt": "Three quick ones. Type what the expression produces, or pick an answer where there are buttons.\n",
            "items": [
              {
                "id": "p1",
                "code": "const shelves = [new Map([[\"a\", 1]]), { get: k => \"x\" }];   shelves.map(s => s.get(\"a\"))",
                "accept": [
                  "[1,\"x\"]",
                  "[1, \"x\"]",
                  "[1, 'x']",
                  "1, x",
                  "[1, x]"
                ],
                "miss": "**If you expected the plain object to fail because it is not a Map — `map` only calls `get`.** Anything with a `get` stands in.\n",
                "why": "`[1, \"x\"]`. Duck typing: the only qualification is having the method that is called."
              },
              {
                "id": "p2",
                "question": "A `ShippingQuote` needs to format prices, and a `Formatter` class exists. First question: is a `ShippingQuote` a kind of `Formatter`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**The first question is always the is-a question.** Anywhere a Formatter is expected, would a shipping quote make sense? It uses formatting; it is not a formatter.\n",
                "why": "No. So not inheritance — the quote should hold a formatter."
              },
              {
                "id": "p3",
                "question": "Before `pay(p)` runs `p.charge(10)`, what has JavaScript checked about `p`?",
                "accept": [
                  "nothing",
                  "none",
                  "no check",
                  "nothing at all"
                ],
                "why": "Nothing. If `p` has no `charge`, the call itself is where you find out — a TypeError at that line."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "const outs = [{ write: m => m }, { write: m => m }, { print: m => m }];   outs.map(o => o.write(\"hi\"))",
                "accept": [
                  "error",
                  "typeerror",
                  "type error",
                  "throws",
                  "it throws"
                ],
                "why": "A TypeError on the third: it has `print`, not `write`. Nothing noticed when it was put in the array."
              },
              {
                "id": "p5",
                "question": "`Circle`, `Square` and `Triangle` are each a `Shape`; `Shape` is yours, small and stable; a list of shapes is drawn by calling `draw()` on each. Inheritance, composition or a contract?",
                "choices": [
                  "Inheritance",
                  "Composition",
                  "A contract"
                ],
                "accept": [
                  "Inheritance"
                ],
                "why": "Inheritance is defensible: each is genuinely a kind of Shape, the base is stable and yours, and you want each to draw itself."
              },
              {
                "id": "p6",
                "question": "An `EmailSender` wants the retry logic that an `HttpClient` class has. Is an `EmailSender` a kind of `HttpClient`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. It wants a behaviour the client has, which is a reason to hold one, not to be one."
              }
            ],
            "hints": [
              {
                "when": "you expected the plain object to fail",
                "say": "Look at what the `map` callback does with each item. It calls `get`, and nothing else — does the plain object have one?"
              },
              {
                "when": "you answered yes for the ShippingQuote",
                "say": "Try the substitution: hand a ShippingQuote to code that expects a Formatter. Does that make sense, or does the quote just need one?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Two shelves, one contract",
            "prompt": "The contract says `lookup(isbn)` returns the title, or `undefined` if the shelf does not hold it. `label` was written to that contract. Predict the four lines, then run it.\n",
            "filename": "shelves.js",
            "code": "const listShelf = {\n  books: [{ isbn: \"111\", title: \"Dune\" }],\n  lookup(isbn) { return this.books.find(b => b.isbn === isbn)?.title; },\n};\nconst mapShelf = {\n  books: new Map([[\"111\", \"Dune\"]]),\n  lookup(isbn) { return this.books.get(isbn) || \"unknown\"; },\n};\n\nfunction label(shelf, isbn) {\n  const title = shelf.lookup(isbn);\n  return title === undefined ? isbn + \" (not held)\" : isbn + \": \" + title;\n}\n\nconsole.log(label(listShelf, \"111\"));\nconsole.log(label(mapShelf, \"111\"));\nconsole.log(label(listShelf, \"222\"));\nconsole.log(label(mapShelf, \"222\"));\n",
            "items": [
              {
                "id": "r1",
                "question": "First two lines printed (they match)",
                "accept": [
                  "111: Dune",
                  "\"111: Dune\""
                ],
                "why": "Both shelves hold the book, and both return its title. For a book that is there, they are interchangeable."
              },
              {
                "id": "r2",
                "question": "Third line printed",
                "accept": [
                  "222 (not held)",
                  "\"222 (not held)\""
                ],
                "why": "`find` found nothing, `?.` stopped at `undefined`, and `label` caught exactly what the contract promised."
              },
              {
                "id": "r3",
                "question": "Fourth line printed",
                "accept": [
                  "222: unknown",
                  "\"222: unknown\""
                ],
                "miss": "**If you predicted `222 (not held)` — `mapShelf` does not return `undefined` for a missing book.** `get` gives `undefined`, and `|| \"unknown\"` replaces it with a string. `label` sees a title.\n",
                "why": "`mapShelf` has the right method and breaks the contract on the missing case. The caller did nothing wrong."
              }
            ],
            "hints": [
              {
                "when": "you predicted \"222 (not held)\" twice",
                "say": "Evaluate `mapShelf.lookup(\"222\")` on its own. What does `get` return for a missing key, and what does `||` do with it?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Which of the three, and why",
            "prompt": "For each model, pick inheritance, composition or a contract. Ask the questions in order: is B genuinely a kind of A? Is A stable and meant to be extended? Must unrelated types be interchangeable?\n",
            "items": [
              {
                "id": "t1",
                "question": "A `Checkout` needs to calculate tax, and a `TaxTable` class exists that changes every year.",
                "choices": [
                  "Inheritance",
                  "Composition",
                  "A contract"
                ],
                "accept": [
                  "Composition"
                ],
                "why": "Composition. A checkout is not a tax table; hold the current one and ask it."
              },
              {
                "id": "t2",
                "question": "`save(thing)` must accept an `Invoice`, a `Photo` or a test fake, which share nothing but `toJSON()`.",
                "choices": [
                  "Inheritance",
                  "Composition",
                  "A contract"
                ],
                "accept": [
                  "A contract"
                ],
                "why": "A contract. Unrelated types must be interchangeable, and all `save` relies on is `toJSON()`."
              },
              {
                "id": "t3",
                "question": "`Notice`, `Warning` and `Alert` each render a heading; `Notice` is yours, small, and written for subclasses to fill in `heading()`.",
                "choices": [
                  "Inheritance",
                  "Composition",
                  "A contract"
                ],
                "accept": [
                  "Inheritance"
                ],
                "why": "Inheritance is defensible: each is a kind of Notice, and the base is stable and designed to be extended."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "At shutdown one loop calls `close()` on a `Logger`, a `Database` and a `Cache`, which are otherwise unrelated.",
                "choices": [
                  "Inheritance",
                  "Composition",
                  "A contract"
                ],
                "accept": [
                  "A contract"
                ],
                "why": "A contract: `{ close() }`. The loop relies on one method; each type meets it in its own way."
              },
              {
                "id": "t5",
                "question": "A `Stack` wants `push` and `pop`, and an `Array` has them — along with `unshift`, `splice` and `reverse`.",
                "choices": [
                  "Inheritance",
                  "Composition",
                  "A contract"
                ],
                "accept": [
                  "Composition"
                ],
                "why": "Composition. A stack is not everywhere-an-array: extending would offer operations that break its rule. Hold an array and offer only what a stack allows."
              }
            ],
            "hints": [
              {
                "when": "you answered inheritance for the Checkout or the Stack",
                "say": "Ask the substitution question first. Would a checkout make sense wherever a tax table is expected? Would a stack make sense wherever any array is?"
              },
              {
                "when": "you answered composition for save(thing)",
                "say": "Composition answers \"how does one object get another's behaviour\". Here the question is what `save` may rely on from several unrelated types — that is a contract."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Meet a stated contract",
            "prompt": "Write `makeStore()`, returning an object that meets this contract. `get(key)` returns the value stored under `key`, or `undefined` if nothing was ever stored there — never `null`. `set(key, value)` stores it, replacing any value already under that key. `has(key)` returns `true` or `false`. `keys()` returns the keys in the order first set, as an array the caller may change without affecting the store. How you keep the values is up to you. Then, in one sentence: which line of the contract would a store built on a plain object most easily break, and how?\n",
            "exportName": "makeStore",
            "filename": "store.js",
            "starter": "// Contract:\n//   get(key)        -> the value, or undefined if never set (never null)\n//   set(key, value) -> stores it; replaces any value under that key\n//   has(key)        -> true or false\n//   keys()          -> keys in first-set order, as a copy\nfunction makeStore() {\n\n}\n",
            "tests": [
              {
                "label": "get returns what set stored",
                "body": "const s = subject(); s.set('a', 1); return s.get('a') === 1;"
              },
              {
                "label": "get of a key never set is undefined, not null",
                "body": "return subject().get('nope') === undefined;"
              },
              {
                "label": "set replaces, and keys lists the key once",
                "body": "const s = subject(); s.set('a', 1); s.set('a', 2); return s.get('a') === 2 && s.keys().length === 1;"
              },
              {
                "label": "a stored 0 comes back as 0",
                "body": "const s = subject(); s.set('n', 0); return s.get('n') === 0 && s.has('n') === true;"
              },
              {
                "label": "has is false for a key never set",
                "body": "return subject().has('x') === false;"
              },
              {
                "label": "keys are in first-set order",
                "body": "const s = subject(); s.set('b', 1); s.set('a', 2); s.set('b', 3); const k = s.keys(); return k[0] === 'b' && k[1] === 'a';"
              },
              {
                "label": "changing what keys returns changes nothing",
                "body": "const s = subject(); s.set('a', 1); s.keys().push('z'); return s.keys().length === 1 && s.has('z') === false;"
              },
              {
                "label": "two stores are independent",
                "body": "const a = subject(), b = subject(); a.set('k', 1); return b.has('k') === false;"
              }
            ],
            "hints": [
              {
                "when": "a missing key gives null",
                "say": "The contract rules out `null` for a key never set. A caller checking `=== undefined` will treat `null` as a real value — return the absence the contract names."
              },
              {
                "when": "a stored 0 comes back wrong",
                "say": "Something treats `0` as missing — an `||` fallback, or a truthiness test in `has`. Ask whether the key was set, not whether its value is truthy."
              },
              {
                "when": "the keys-copy test fails",
                "say": "`keys()` hands out the array the store keeps. Return a new array each time, so what the caller changes is theirs."
              },
              {
                "when": "the independence test fails",
                "say": "The storage lives outside `makeStore`, so every store shares it. Create it inside the call."
              },
              {
                "when": "setting a key twice lists it twice",
                "say": "`set` must replace a value already under that key, not add a second entry. Check whether the key is there before you add it."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a checkout that checks the wrong thing",
            "prompt": "A code assistant added \"validation\" to a checkout. Now the test suite cannot construct one, because the tests pass a fake logger. Answer by line number where asked, then fix the check so it tests what the checkout actually relies on.\n",
            "filename": "checkout.js",
            "code": "class ConsoleLogger {\n  log(msg) { console.log(msg); }\n}\n\nclass Checkout {\n  constructor(logger) {\n    if (!(logger instanceof ConsoleLogger)) throw new TypeError(\"logger must be a ConsoleLogger\");\n    this.logger = logger;\n  }\n  pay(amount) {\n    this.logger.log(\"paid \" + amount);\n    return amount > 0;\n  }\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "A test passes `{ log: m => seen.push(m) }`. Which line refuses it?",
                "accept": [
                  "7",
                  "line 7"
                ],
                "why": "Line 7 asks whether the logger is a `ConsoleLogger`. The fake is not, though it has everything the checkout uses."
              },
              {
                "id": "c2",
                "question": "What does `Checkout` actually rely on its logger for? Name the method.",
                "accept": [
                  "log",
                  "log()",
                  "log(msg)",
                  "the log method",
                  "a log method",
                  ".log"
                ],
                "why": "`log`, and nothing else — line 11 is the only use. That one method is the contract."
              },
              {
                "id": "c3",
                "question": "With line 7 deleted entirely, does `new Checkout({})` throw?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**Nothing else looks at the logger in the constructor.** Line 8 just stores it; the first use is in `pay`.\n",
                "why": "No. The broken logger is accepted, and the failure moves to the first `pay` call, far from where the mistake was made. Checking the shape at the door keeps it near."
              }
            ],
            "starter": "class ConsoleLogger {\n  log(msg) { console.log(msg); }\n}\n\nclass Checkout {\n  constructor(logger) {\n    if (!(logger instanceof ConsoleLogger)) throw new TypeError(\"logger must be a ConsoleLogger\");\n    this.logger = logger;\n  }\n  pay(amount) {\n    this.logger.log(\"paid \" + amount);\n    return amount > 0;\n  }\n}\n",
            "exportName": "Checkout",
            "tests": [
              {
                "label": "a fake logger with a log method is accepted",
                "body": "new subject({ log() {} }); return true;"
              },
              {
                "label": "pay logs through whatever logger it holds",
                "body": "const seen = []; new subject({ log: m => seen.push(m) }).pay(5); return seen[0] === 'paid 5';"
              },
              {
                "label": "an object with no log method is refused when constructed",
                "body": "try { new subject({ write() {} }); return false; } catch (e) { return e instanceof TypeError; }"
              },
              {
                "label": "a missing logger is refused when constructed",
                "body": "try { new subject(); return false; } catch (e) { return e instanceof TypeError; }"
              }
            ],
            "hints": [
              {
                "when": "the fake is still refused",
                "say": "`instanceof` asks what class the logger came from. The checkout relies on a method, not a class — ask whether that method is there."
              },
              {
                "when": "the fake is accepted but a bad logger is not refused",
                "say": "You removed the check rather than changing it, so a broken logger gets in and fails later, in `pay`. Check for the one method the contract needs, at the door."
              },
              {
                "when": "a bad logger is refused but the refusal tests still fail",
                "say": "The tests expect a `TypeError` — the kind of failure is part of the contract. \"This object is the wrong shape\" is exactly what `TypeError` says."
              }
            ]
          }
        ]
      }
    },
    "checkpoint-2": {
      "id": "checkpoint-2",
      "title": "Checkpoint 2 practice",
      "kind": "quiz",
      "unlocks": "Week 9 — Modules and dependencies",
      "questions": [
        {
          "id": "q1",
          "skill": "Closures — what is captured",
          "prompt": "What do the three functions return?",
          "code": "const fns = [];\nfor (var i = 0; i < 3; i++) fns.push(() => i);\nfns.map(f => f());",
          "answers": [
            {
              "key": "A",
              "text": "`0, 1, 2` — each captured `i` as it was",
              "tag": "the natural reading",
              "correct": false
            },
            {
              "key": "B",
              "text": "`3, 3, 3` — one binding, shared by all three",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`undefined` three times",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It throws — `i` is out of scope",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. A closure captures the binding, not the value. `var` makes one binding for the whole function, and it holds `3` once the loop finishes. `let` would give each iteration its own.",
            "wrong": "Not quite. All three closures refer to the same `var i`, which is `3` by the time any of them runs. The answer is B."
          }
        },
        {
          "id": "q2",
          "skill": "Closures — privacy",
          "prompt": "`pair()` returns `{ inc, read }` over a local `n`. What is `p.n` for `const p = pair()`?",
          "answers": [
            {
              "key": "A",
              "text": "The current count",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`0`",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "`undefined` — `n` is a binding, not a property",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "It throws",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `n` lives in the closure's scope, not on the returned object. Nothing outside the two functions can reach it, which is the oldest form of privacy in JavaScript.",
            "wrong": "Not quite. `n` is a binding in `pair`'s scope, never a property of the returned object, so reading it gives `undefined`. The answer is C."
          }
        },
        {
          "id": "q3",
          "skill": "How a method finds its object",
          "prompt": "`const loose = obj.get` then `loose()`. Why does it fail?",
          "answers": [
            {
              "key": "A",
              "text": "`get` was not copied, only referenced",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`this` comes from the call site, and there is no object in front of `loose()`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Methods cannot be stored in variables",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "`obj` was declared with `const`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. JavaScript binds `this` at call time, so extracting a method leaves the receiver behind. Python and C# both keep it — this is a genuine difference, and why `bind` and arrow functions exist.",
            "wrong": "Not quite. The function was stored fine. What was lost is the receiver: `obj.get()` supplies `obj`, `loose()` supplies nothing. The answer is B."
          }
        },
        {
          "id": "q4",
          "skill": "Higher-order — callback arity",
          "prompt": "What is `[\"1\",\"2\",\"3\"].map(parseInt)`?",
          "answers": [
            {
              "key": "A",
              "text": "`[1, 2, 3]`",
              "tag": "the reasonable assumption",
              "correct": false
            },
            {
              "key": "B",
              "text": "`[1, NaN, NaN]`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`[NaN, NaN, NaN]`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It throws",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `map` calls back with (value, index, array), and `parseInt` reads the second argument as a radix. Bases 1 and 2 cannot represent \"2\" and \"3\". An arity bug — wrap it as `s => parseInt(s, 10)`.",
            "wrong": "Not quite. `map` passes three arguments and `parseInt` takes two, so the index becomes the radix. The first succeeds (radix 0 means base 10), the rest do not. The answer is B."
          }
        },
        {
          "id": "q5",
          "skill": "Higher-order — removing duplication",
          "prompt": "Three functions differ only in which field they sum. What should be extracted?",
          "answers": [
            {
              "key": "A",
              "text": "A parameter holding the field name as a string",
              "tag": "works, but weaker",
              "correct": false
            },
            {
              "key": "B",
              "text": "A function parameter that selects the number",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Nothing — three short functions are fine",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "A base class the three inherit from",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. What differs is a transformation, not a value — so it has to arrive as a function. `total(items, pick)` also handles computed values like `qty * price`, which a field name cannot.",
            "wrong": "Not quite. A field-name string handles only direct fields and reintroduces stringly-typed lookups. A function parameter covers computed values too. The answer is B."
          }
        },
        {
          "id": "q6",
          "skill": "Object modelling — where methods live",
          "prompt": "After `new Dog(\"a\")` and `new Dog(\"b\")`, how many `speak` functions exist?",
          "answers": [
            {
              "key": "A",
              "text": "Two — one per instance",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "One — it lives on `Dog.prototype`",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Three — one per instance plus the class's",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "None until it is called",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Methods live on the prototype, once. Each instance holds only its own fields, and the lookup walks to the prototype when a property is not found on the object.",
            "wrong": "Not quite. Instances hold their own fields; methods live on `Dog.prototype` and are shared. `new Dog(\"a\").speak === new Dog(\"b\").speak` is `true`. The answer is B."
          }
        },
        {
          "id": "q7",
          "skill": "Object modelling — fragile base class",
          "prompt": "`List.addAll` loops calling `this.add`; `Counting` overrides `add` to count. A maintainer rewrites `addAll` to push directly. What happens?",
          "answers": [
            {
              "key": "A",
              "text": "Nothing — `Counting` was not changed",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`Counting` silently stops counting",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "A TypeError at run time",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "The base class fails to compile",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `Counting` depended on an unwritten promise — that `addAll` is implemented in terms of `add`. Nothing stated it and nothing checked it, so a legal change broke a subclass that never changed. This is the fragile base class.",
            "wrong": "Not quite. The subclass relied on which method the base called internally. That is not part of any interface and cannot be checked, so it breaks silently. The answer is B."
          }
        },
        {
          "id": "q8",
          "skill": "Object modelling — choosing",
          "prompt": "A PaymentProcessor needs to log. A Logger class exists. Which relationship?",
          "answers": [
            {
              "key": "A",
              "text": "Inheritance — `extends Logger`",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "Composition — hold a logger and delegate",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Either; they are equivalent",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Neither — logging should be a global",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The test for inheritance is substitution: could a PaymentProcessor be used everywhere a Logger is expected? No. It *needs* logging, which is has-a — so hold one. That also lets a test pass a fake with the same shape.",
            "wrong": "Not quite. Apply the substitution test — a payment processor is not a kind of logger, so inheritance would commit you to a base class for no reason. The answer is B."
          }
        }
      ],
      "_source": "checks/checkpoint-2.yml"
    },
    "failure-kinds": {
      "id": "failure-kinds",
      "title": "Recoverable and unrecoverable failure",
      "kind": "read",
      "outcome": 8,
      "shape": "conceptual",
      "why": "Outcome 8 treats failure as a design choice, not a crash to prevent. Deciding where an error is handled, and whether to throw or return it, both start from classifying it — and from the failure carrying what a caller needs to act on: a specific message, a distinguishable type, the original cause (D-018).",
      "concepts": [
        "err.recoverable"
      ],
      "lede": "Some failures are part of the job — a file is missing, a user typed nonsense. Others mean your program is wrong. Telling them apart decides how you handle them, and most error-handling mistakes are a confusion between the two. Then there is the error itself: whoever receives it can only act on what you put in it.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Throwing, and where it goes · Where an error should be handled · Returning a failure instead of throwing"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Recoverable** — the caller has a sensible alternative action. **Unrecoverable** — there is nothing sensible to do but stop. **Cause** — the original error, kept inside a new one that adds context.",
          "entries": [
            "**Recoverable** — the caller has a sensible alternative action.",
            "**Unrecoverable** — there is nothing sensible to do but stop.",
            "**Cause** — the original error, kept inside a new one that adds context."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four failures. For each, pick `expected` or `bug`."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "A user types \"abc\" where a number was asked for",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "expected"
                          ],
                          "why": "Entirely expected. Users type wrong things — that is the normal operation of anything that takes input, and the program should have an answer for it.\n"
                        },
                        {
                          "question": "`null.name` — reading a property of null",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "bug"
                          ],
                          "miss": "**If you called this expected, you may be treating every failure the same way.** Nothing a user did makes this reasonable. Handling it like bad input turns a bug into a wrong answer that ships, instead of a stack trace that gets fixed.\n",
                          "why": "A `TypeError` means a value was not what the code assumed. Nothing the user did makes this a reasonable outcome; something upstream is wrong.\n"
                        },
                        {
                          "question": "`fetch()` fails because the network is down",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "expected"
                          ],
                          "miss": "**If you called this a bug because the network should work — networks are unreliable by nature.** Code that assumes otherwise is the bug. Any call out to one without a plan for failure is incomplete rather than unlucky.\n",
                          "why": "Networks fail constantly. Any code calling out to one without a plan for failure is incomplete rather than unlucky.\n"
                        },
                        {
                          "question": "`JSON.parse` fails on a config file you ship yourself",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "bug"
                          ],
                          "miss": "**If you called this expected because parsing can fail — you control this file.** If it does not parse, the build or the deploy is broken, and stopping loudly is the correct response. Stopping is not always bad; continuing with a value you know is wrong is worse.\n",
                          "why": "You control that file. If it does not parse, the build or the deploy is broken — and stopping loudly is the correct response.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`int(\"abc\")` on user input",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "expected"
                          ],
                          "why": "`ValueError`, and it is the normal path for input you did not write.\n"
                        },
                        {
                          "code": "AttributeError: NoneType has no attribute name",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "bug"
                          ],
                          "why": "Python's version of the same signal — a value was not what the code assumed.\n"
                        },
                        {
                          "question": "`FileNotFoundError` opening a path the user chose",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "expected"
                          ],
                          "why": "A user-supplied path may not exist. Handle it; do not let it reach the top.\n"
                        },
                        {
                          "question": "`AssertionError` from an `assert` in your own code",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "bug"
                          ],
                          "why": "An assertion states something you believe is always true. If it fires, the belief was wrong — which is the definition of a bug.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`FormatException` from `int.Parse` on user input",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "expected"
                          ],
                          "why": "Which is why `int.TryParse` exists — it returns a bool instead of throwing, because this failure is ordinary.\n"
                        },
                        {
                          "code": "NullReferenceException",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "bug"
                          ],
                          "why": "The canonical bug: a value was not what the code assumed.\n"
                        },
                        {
                          "question": "`ArgumentNullException` thrown by your own guard clause",
                          "choices": [
                            "expected",
                            "bug"
                          ],
                          "accept": [
                            "bug"
                          ],
                          "why": "It reports a caller's mistake — a programming error, thrown deliberately so it surfaces immediately rather than later.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can classify a failure as recoverable or unrecoverable, and defend the classification",
                "I can design a failure a caller can act on: a specific message, a type it can tell apart, and the original cause kept"
              ]
            }
          ]
        },
        {
          "h": "The question that separates them"
        },
        {
          "p": "**Does the caller have a sensible alternative action?**"
        },
        {
          "list": [
            "**Yes** → recoverable. Show a message, retry, use a default, ask again.",
            "**No** → unrecoverable. Stop, loudly, with the best diagnostic you can give."
          ]
        },
        {
          "p": "This is a question about *the caller*, not about the failure. The same failure\ncan be either, depending on who is asking."
        },
        {
          "code": "JSON.parse(userPastedText);    // recoverable — tell them it is not valid JSON\nJSON.parse(ourOwnConfigFile);  // unrecoverable — the deploy is broken",
          "lang": "js"
        },
        {
          "p": "Identical call, opposite classifications, because the answer to \"is there\nsomething sensible to do instead?\" differs."
        },
        {
          "p": "Two worked classifications, with the same three steps each time."
        },
        {
          "code": "// A photo app opens a file the user picked from a list.\n// The file has been deleted since the list was shown.\n\n// Name the caller: the \"open photo\" screen, which a person is looking at.\n// Ask what it could sensibly do instead: say \"that photo is gone\" and\n//   show the list again.\n// Classify: there is a sensible alternative → recoverable.",
          "lang": "js"
        },
        {
          "code": "// The same app opens its own icon file, which ships inside the app.\n// The file is missing.\n\n// The caller: the app's startup code.\n// What it could sensibly do instead: nothing honest — the install is broken.\n//   Carrying on shows a broken screen.\n// So: no sensible alternative → unrecoverable. Stop, and say which file.",
          "lang": "js"
        },
        {
          "p": "Same failure — a file is not there. The caller changed, so the answer changed."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "A shop's checkout calls a payment provider, and the provider times out. Recoverable or unrecoverable?",
                "choices": [
                  "Recoverable",
                  "Unrecoverable"
                ],
                "accept": [
                  "Recoverable"
                ],
                "miss": "**If you said unrecoverable because the payment did not happen — ask what checkout could do instead.** It can retry, or tell the customer to try again in a minute. Both are sensible.\n",
                "why": "Recoverable. Checkout has sensible alternatives: retry, then tell the customer. Nobody wants the whole shop to stop.\n"
              },
              {
                "question": "A search for \"zebra\" finds no products. Recoverable, unrecoverable, or not a failure?",
                "choices": [
                  "Recoverable",
                  "Unrecoverable",
                  "Not a failure"
                ],
                "accept": [
                  "Not a failure"
                ],
                "miss": "**An empty result is a normal answer to a search.** Calling it a failure makes every caller handle an \"error\" for the most ordinary outcome there is.\n",
                "why": "Not a failure. An empty list is a correct result. Classify only what actually went wrong.\n"
              }
            ]
          }
        },
        {
          "block": "notice",
          "label": "The signal in the error type",
          "body": [
            {
              "p": "JavaScript's built-in types are a rough guide. `TypeError` and `ReferenceError`\nalmost always mean a bug — a value or a name was not what the code assumed.\n`SyntaxError` from parsing *input* is usually recoverable; from your own code it\nis a bug. `RangeError` is usually a caller passing something invalid."
            }
          ]
        },
        {
          "h": "An error is an object, built for whoever receives it"
        },
        {
          "p": "You have written `throw new Error(\"...\")` in guards already. The `new Error(...)`\npart is an ordinary object, and you can build one and look inside it without\nthrowing it at all:"
        },
        {
          "code": "const e = new Error(\"quantity must be at least 1, got 0\");\ne.name       // \"Error\"\ne.message    // \"quantity must be at least 1, got 0\"",
          "lang": "js"
        },
        {
          "p": "When something goes wrong, this object is what travels to the code that has to\ndecide what to do — the next lesson follows its journey. Whatever that code\ndoes, it can only use what you put in the object. Three things make a failure\none a caller can act on."
        },
        {
          "list": [
            "**A specific message**, with the value that failed. \"invalid\" tells nobody anything. \"qty must be >= 1, got 0\" tells you where to look.",
            "**A type the caller can tell apart.** If every failure is a plain `Error`, a caller that wants to handle \"not found\" cannot tell it from a bug.",
            "**The original cause, kept.** When a low-level failure makes a higher-level step fail, the new error adds what the low level did not know and keeps the original inside it."
          ]
        },
        {
          "p": "Two worked designs. The subgoals are the same three."
        },
        {
          "p": "A shop looks up an order by id. Callers need to handle \"no such order\"\ndifferently from everything else."
        },
        {
          "code": "// pick a type the caller can tell apart\nclass NotFound extends Error {\n  constructor(id) {\n    // say what failed, with the value\n    super(`no order ${id}`);\n    this.name = \"NotFound\";\n  }\n}\n// no cause to keep: the order is simply not there\n\nconst e = new NotFound(7);\ne.name                  // \"NotFound\"\ne.message               // \"no order 7\"\ne instanceof NotFound   // true — a caller can ask exactly this\ne instanceof Error      // true — and it is still an Error, with a stack trace",
          "lang": "js",
          "file": "not-found.js"
        },
        {
          "p": "`super(...)` hands the message to `Error`'s own constructor. It must come\nbefore `this.name`: in a class that extends another, `this` does not exist\nuntil the parent constructor has run."
        },
        {
          "p": "An app loads its settings from `settings.json`. The file is there, but the JSON\ninside is broken, so `JSON.parse` failed with its own `SyntaxError`. That error\nknows what went wrong — an unexpected character — but not which file, or what\nthe app was trying to do."
        },
        {
          "code": "// parseError: the SyntaxError JSON.parse produced. How you get hold of it is\n// the next lesson; here, a stand-in built by hand.\nconst parseError = new SyntaxError(\"Unexpected token o in JSON at position 1\");\n\n// A plain Error is enough: startup treats every settings failure alike.\n// The message says what failed; cause keeps the SyntaxError.\nconst e = new Error(\"could not load settings from settings.json\", { cause: parseError });\n\ne.message          // \"could not load settings from settings.json\"\ne.cause.name       // \"SyntaxError\"\ne.cause.message    // \"Unexpected token o in JSON at position 1\"",
          "lang": "js",
          "file": "settings-error.js"
        },
        {
          "p": "If that error reaches the top of a Node program uncaught, the trace prints both:\nthe wrapper's message first, then `[cause]: SyntaxError: Unexpected token o…`\nwith its own stack. The wrapper says what was being attempted; the cause says\nwhat actually broke. Drop the cause and you have a trace that points at your\nwrapper and nothing about the real failure."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "Given `class Missing extends Error {}`, what is `new Missing(\"no such user\").name`? Type the value.",
                "accept": [
                  "\"Error\"",
                  "Error",
                  "'Error'"
                ],
                "miss": "**If you answered `\"Missing\"` — the class name does not set `name`.** Every subclass inherits `name` from `Error` until its constructor changes it, so a caller printing `e.name` sees `Error` and cannot tell this apart from any other failure.\n",
                "why": "`\"Error\"`. Set `this.name` in the constructor, as `NotFound` does, or the type is invisible in every message and log.\n"
              },
              {
                "question": "`throw new Error(\"load failed\")` — name one thing a caller cannot find out from this error. A few words is enough.",
                "accept": [
                  "what failed",
                  "which file",
                  "the cause",
                  "why",
                  "the value",
                  "what caused it",
                  "the original error",
                  "which one",
                  "what went wrong",
                  "the type"
                ],
                "why": "Almost everything: which thing failed to load, why, and whether it is the kind of failure this caller knows how to handle. A specific message, a distinct type and a `cause` are the three answers.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Classifying and designing a failure",
            "body": [
              {
                "h": "Classifying and designing a failure"
              },
              {
                "code": "// Something can fail. Decide what it is, then what it carries.\n\n// 1. Is it a failure at all?\n//    An empty search, a zero count, an unset option are normal results.\n\n// 2. Does the CALLER have a sensible alternative action?\n//    no  -> unrecoverable. Stop loudly, with the best message you can.\n//    yes -> recoverable. The caller will want to handle it.\n\n// 3. Does a caller need to tell this failure from others?\n//    yes -> its own type: class X extends Error, with this.name set.\n//    no  -> a plain Error.\n\n// 4. Say what failed, with the value that failed.\n\n// 5. Did another error cause this one?  { cause: original }",
                "lang": "js",
                "file": "classify.js"
              },
              {
                "p": "Now apply it to three."
              },
              {
                "code": "// a) a required environment variable is missing at startup\n// b) a user's search returns no results\n// c) a payment provider times out\n\n// Which is not a failure at all?  ______\n// Which should stop the program?  ______\n// Which is recoverable?           ______\n// For c, name a type a caller could check for: ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`b` is not a failure — an empty list is a normal result. `a` should stop at\nstartup, as loudly as possible, naming the variable. `c` is recoverable: retry,\nthen tell the user — and a type such as `PaymentTimeout` lets checkout handle\nexactly that failure and nothing else."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then take the network failure and\ndesign its error: what type would you give it, what goes in the message, and\nwhat would you keep as its cause?"
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "`loadAvatar(userId)` reads a user's profile picture from storage. Two places\ncall it: the **profile page**, which a person is looking at, and a **nightly\nbackup job**, which copies every avatar to a second store. Sometimes the picture\nis missing from storage."
            },
            {
              "p": "For each caller, classify the missing picture and defend it. Then design the one\nerror `loadAvatar` throws so that **both** callers can act on it: what type, what\nmessage, and what it keeps as its cause when the storage library's own error is\nwhat failed. Say what each caller would be unable to do if `loadAvatar` threw\n`new Error(\"failed\")` instead."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "The question is **\"does the caller have a sensible alternative action?\"** — it is about the caller, not the failure. Some \"failures\" are just normal results.",
                "An error is an object for whoever receives it. **Say what failed**, with the value; give it a **type** a caller can tell apart, with `this.name` set.",
                "When one failure causes another, **keep the original as `cause`**."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Error",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error",
              "note": "the built-in types, and custom types with `extends Error`."
            },
            {
              "title": "Error.prototype.cause",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause",
              "note": "wrapping an error without losing the original."
            }
          ]
        }
      ],
      "_source": "lessons/failure-kinds.md",
      "practice": {
        "id": "failure-kinds",
        "outcome": 8,
        "steps": [
          {
            "kind": "predict",
            "title": "Classify, then look inside",
            "prompt": "For the first two, pick `recoverable` or `unrecoverable`. For the third, type the value it produces.",
            "items": [
              {
                "id": "p1",
                "question": "A user types \"abc\" into a quantity field",
                "choices": [
                  "recoverable",
                  "unrecoverable"
                ],
                "accept": [
                  "recoverable"
                ],
                "why": "Users type wrong things. That is the normal operation of a form, and the caller has a sensible alternative: ask again."
              },
              {
                "id": "p2",
                "code": "TypeError: Cannot read properties of null (reading \"name\")",
                "choices": [
                  "recoverable",
                  "unrecoverable"
                ],
                "accept": [
                  "unrecoverable"
                ],
                "miss": "If you called this recoverable, ask what the caller could sensibly do instead. Nothing — a value was not what the code assumed, and something upstream is wrong.",
                "why": "A value was not what the code assumed. Nothing a user did makes this a reasonable outcome, so there is no alternative action — only a bug to find."
              },
              {
                "id": "p3",
                "code": "class Expired extends Error {}   new Expired(\"card expired\").name",
                "accept": [
                  "\"Error\"",
                  "Error",
                  "'Error'"
                ],
                "miss": "If you answered `\"Expired\"`: the class name does not set `name`. It is inherited from `Error` until the constructor changes it.",
                "why": "`\"Error\"`. A subclass that does not set `this.name` looks like every other failure in a message or a log."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "`fetch()` fails because the network is down",
                "choices": [
                  "recoverable",
                  "unrecoverable"
                ],
                "accept": [
                  "recoverable"
                ],
                "why": "Networks fail constantly. The caller can retry or tell the user, which makes it recoverable."
              },
              {
                "id": "p5",
                "question": "`JSON.parse` fails on a config file you ship with the app",
                "choices": [
                  "recoverable",
                  "unrecoverable"
                ],
                "accept": [
                  "unrecoverable"
                ],
                "why": "You control that file. If it does not parse, the deploy is broken, and there is nothing sensible to do but stop loudly."
              },
              {
                "id": "p6",
                "question": "A required environment variable is missing at startup",
                "choices": [
                  "recoverable",
                  "unrecoverable"
                ],
                "accept": [
                  "unrecoverable"
                ],
                "why": "There is no sensible alternative — the program was started wrongly. Stop at once, and name the variable in the message."
              },
              {
                "id": "p7",
                "code": "new Error(\"could not save\", { cause: new RangeError(\"disk full\") }).cause.message",
                "accept": [
                  "\"disk full\"",
                  "disk full",
                  "'disk full'"
                ],
                "why": "`cause` holds the original error object, so its message is still there to read."
              }
            ],
            "hints": [
              {
                "when": "you classified a failure by how alarming it sounds",
                "say": "The question is about the caller, not the failure: does whoever called this have a sensible alternative action? The same failure can be either, depending on who is asking."
              },
              {
                "when": "you answered \"Expired\"",
                "say": "Look for a line that sets `name`. A class declaration names the class; it does not change the property every Error inherits."
              }
            ]
          },
          {
            "kind": "run",
            "title": "What a caller would find",
            "prompt": "Predict each line this program prints, then run it. Nothing is thrown — each error is built and then looked at.",
            "filename": "errors.js",
            "code": "class NotFound extends Error {\n  constructor(id) {\n    super(`no order ${id}`);\n    this.name = \"NotFound\";\n  }\n}\nclass Expired extends Error {}\n\nconst a = new NotFound(7);\nconsole.log(a.name + \": \" + a.message);\nconsole.log(a instanceof Error);\n\nconst b = new Expired(\"card expired\");\nconsole.log(b.name);\n\nconst parseFailure = new SyntaxError(\"Unexpected token o\");\nconst wrapped = new Error(\"could not read orders.json\", { cause: parseFailure });\nconsole.log(wrapped.message);\nconsole.log(wrapped.cause.name);\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "NotFound: no order 7"
                ],
                "miss": "If you expected `Error: no order 7`: the constructor sets `this.name`, which replaces the name inherited from `Error`.",
                "why": "The `name` set in the constructor, and the message with the id in it. A caller, or you in a log at 2am, can see which failure and which order."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "true"
                ],
                "why": "`NotFound` extends `Error`, so it is still an Error — with a stack trace, and caught by anything that handles errors in general."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "Error"
                ],
                "miss": "If you expected `Expired`: nothing in `Expired` sets `name`, so it keeps the one it inherited.",
                "why": "`Expired` has its own class but never set `name`. Anything that prints `e.name` sees `Error`."
              },
              {
                "id": "r4",
                "question": "Last line printed",
                "accept": [
                  "SyntaxError"
                ],
                "miss": "If you expected `Error`: the wrapper is a plain `Error`, but `cause` holds the original, which is a `SyntaxError`.",
                "why": "`cause` keeps the original failure inside the new one. The wrapper adds what the low level did not know (which file); the cause keeps what actually went wrong."
              }
            ],
            "hints": [
              {
                "when": "you predicted \"Error: no order 7\"",
                "say": "Every Error subclass starts with the name `Error`. Look for the line in the constructor that changes it — and then look for it in `Expired`."
              },
              {
                "when": "you predicted the wrapper's name for the last line",
                "say": "The last line reads `.cause` first. Which error was passed in as the cause?"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Build an error a caller can act on",
            "prompt": "A checkout calls `reserve(sku, qty)`. When the item has run out, the caller wants to handle that one failure, show which item, and keep the stock service's original error for the logs. Build the error type. Two of the lines are mistakes.\n",
            "lines": [
              "class OutOfStock extends Error {",
              "  constructor(sku, cause) {",
              "    super(`no stock left for ${sku}`, { cause });",
              "    this.name = \"OutOfStock\";",
              "  }",
              "}"
            ],
            "distractors": [
              {
                "line": "    super(\"out of stock\");",
                "why": "A message a caller cannot act on: it does not say which item, and it drops the original error — the `cause` was passed in and then thrown away."
              },
              {
                "line": "class OutOfStock {",
                "why": "Without `extends Error` there is no stack trace, `instanceof Error` is false, and the `super(...)` call has nothing to call."
              }
            ],
            "why": "Three things make a failure actionable: a type the caller can tell apart (`OutOfStock`, with its `name` set), a message with the value that failed, and the original error kept as `cause`. `super(...)` must come before `this.name`: in a subclass, `this` does not exist until the parent constructor has run.\n",
            "hints": [
              {
                "when": "you put this.name before super",
                "say": "In a class that extends another, `this` is created by the parent constructor. Using it before `super(...)` is a ReferenceError."
              },
              {
                "when": "you are unsure between the two super lines",
                "say": "Read the prompt again: which item, and the original error. Which of the two lines keeps both?"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Make one failure distinguishable",
            "prompt": "This works, but both failures throw the same generic Error, so the checkout screen cannot tell \"not enough money\" from a bad amount. Change **only** the second failure: throw an `InsufficientFunds` (already defined) whose message includes both the balance and the amount asked for. A bad amount must still throw — just not as `InsufficientFunds`. Then, in one sentence: what can the checkout screen now do that it could not before?\n",
            "exportName": "withdraw",
            "filename": "withdraw.js",
            "starter": "class InsufficientFunds extends Error {\n  constructor(message) {\n    super(message);\n    this.name = \"InsufficientFunds\";\n  }\n}\n\n// Works today — but every failure looks the same to the caller.\nfunction withdraw(account, amount) {\n  if (!(amount > 0)) throw new RangeError(\"withdraw failed\");\n  if (amount > account.balance) throw new Error(\"withdraw failed\");\n  account.balance -= amount;\n  return account.balance;\n}\n",
            "tests": [
              {
                "label": "a normal withdrawal still works",
                "body": "const acc = { balance: 100 }; return subject(acc, 30) === 70 && acc.balance === 70;"
              },
              {
                "label": "an overdraft throws an InsufficientFunds",
                "body": "try { subject({ balance: 30 }, 50); return false; } catch (e) { return e.name === 'InsufficientFunds' && e instanceof Error; }"
              },
              {
                "label": "its message says the balance and the amount",
                "body": "try { subject({ balance: 30 }, 50); return false; } catch (e) { return e.message.indexOf('30') > -1 && e.message.indexOf('50') > -1; }"
              },
              {
                "label": "a bad amount still throws, but not as InsufficientFunds",
                "body": "try { subject({ balance: 30 }, -5); return false; } catch (e) { return e.name !== 'InsufficientFunds'; }"
              },
              {
                "label": "a failed withdrawal leaves the balance alone",
                "body": "const acc = { balance: 30 }; try { subject(acc, 50); } catch (e) {} return acc.balance === 30;"
              }
            ],
            "hints": [
              {
                "when": "the bad-amount test fails",
                "say": "You made both failures `InsufficientFunds`. A caller that handles it by showing \"top up your balance\" would then show that for a negative amount too. Only the overdraft changes."
              },
              {
                "when": "the message test fails",
                "say": "The message is for a person deciding what to do next. Put the two values that explain the failure into it — a template literal will do."
              },
              {
                "when": "the balance changed even though it threw",
                "say": "Check the order of the lines. The subtraction has to wait until every check has passed."
              }
            ]
          },
          {
            "kind": "write",
            "title": "A failure that says where",
            "prompt": "An importer reads a file line by line. Write a class `LineError` that a caller can tell apart from every other failure. `new LineError(12, original)` makes an error whose `name` is `\"LineError\"`, whose `message` includes the line number, which keeps the line number as a `line` property, and which keeps `original` as its `cause`. It must still be an `Error`. Then, in one sentence: which of the three parts would you miss most at 2am, and why?\n",
            "exportName": "LineError",
            "filename": "line-error.js",
            "starter": "// new LineError(line, cause)\n//   name    \"LineError\"\n//   message mentions the line number\n//   line    the line number\n//   cause   the original error\n",
            "tests": [
              {
                "label": "it is an Error",
                "body": "return new subject(3, new Error('x')) instanceof Error;"
              },
              {
                "label": "a caller can tell it apart",
                "body": "const e = new subject(3, new Error('x')); return e instanceof subject && e.name === 'LineError';"
              },
              {
                "label": "the message says which line",
                "body": "return new subject(12, new Error('x')).message.indexOf('12') > -1;"
              },
              {
                "label": "the line number is kept as .line",
                "body": "return new subject(12, new Error('x')).line === 12;"
              },
              {
                "label": "the original error is kept as cause",
                "body": "const orig = new SyntaxError('bad date'); return new subject(4, orig).cause === orig;"
              },
              {
                "label": "different lines, different messages",
                "body": "return new subject(1, new Error('x')).message !== new subject(2, new Error('x')).message;"
              }
            ],
            "hints": [
              {
                "when": "\"it is an Error\" fails",
                "say": "A class only gets a stack trace and `instanceof Error` by extending `Error`, and then its constructor must call `super(...)` first."
              },
              {
                "when": "the name test fails",
                "say": "The class name does not set `name`. Set `this.name` in the constructor, after `super(...)`."
              },
              {
                "when": "the cause test fails",
                "say": "`super` takes a second argument, an options object. The original error goes in it under one particular key."
              },
              {
                "when": "you get \"Must call super constructor\" or a ReferenceError about this",
                "say": "In a subclass, `this` does not exist until `super(...)` has run. Move `super` to the first line of the constructor."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a coupon check",
            "prompt": "A code assistant wrote this. The checkout that calls it wants to tell the shopper *why* a coupon was refused — \"no such code\" and \"expired\" get different messages on screen. Review it.\n",
            "filename": "coupon.js",
            "code": "function applyCoupon(coupons, code, total, today) {\n  const coupon = coupons.get(code);\n  if (coupon === undefined) {\n    throw new Error(\"Something went wrong\");\n  }\n  if (coupon.expires < today) {\n    throw new Error(\"Something went wrong\");\n  }\n  return total - coupon.amount;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Lines 4 and 7 throw. Can the checkout tell the two failures apart?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. Same type, same message. The checkout could only show one message for both, which is exactly what it said it did not want."
              },
              {
                "id": "c2",
                "question": "What is missing from both messages that a person reading a log would need? A few words is enough.",
                "accept": [
                  "the code",
                  "the coupon code",
                  "which code",
                  "code",
                  "the value",
                  "which coupon"
                ],
                "why": "The coupon code. \"Something went wrong\" says a failure happened, which the stack trace already said. Which coupon, and what about it, is the useful part."
              },
              {
                "id": "c3",
                "question": "Recoverable or unrecoverable, for the checkout?",
                "choices": [
                  "recoverable",
                  "unrecoverable"
                ],
                "accept": [
                  "recoverable"
                ],
                "why": "Recoverable. The shopper can fix a typo or drop the coupon — which is why the checkout needs to know which failure it is."
              }
            ],
            "hints": [
              {
                "when": "you said the failures can be told apart",
                "say": "Compare line 4 and line 7 character by character. What could a caller check to tell them apart?"
              }
            ]
          }
        ]
      }
    },
    "propagation": {
      "id": "propagation",
      "title": "Throwing, and where it goes",
      "kind": "read",
      "outcome": 8,
      "shape": "procedural",
      "why": "Outcome 8 asks where a failure should be handled, and that cannot be answered without knowing where a thrown error goes: up the call stack, abandoning each frame, until something catches it. `err.propagation` is that mechanism. It is taught explanation-first because a throw and the call stack interact — `01-explanations` rule 8.",
      "concepts": [
        "err.propagation"
      ],
      "lede": "A `throw` does not return to the line that called it. It leaves the function, then the function that called that one, and keeps going until a `catch` stops it — or until nothing does, and the host prints the trace you have been reading since the first week.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "When it breaks: reading errors · Recoverable and unrecoverable failure · Where an error should be handled"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Throw** — stop this function and send an error up the call stack. **Catch** — stop an error's journey and run code with it. **Unwind** — leave a frame without finishing it, on the way to a catch.",
          "entries": [
            "**Throw** — stop this function and send an error up the call stack.",
            "**Catch** — stop an error's journey and run code with it.",
            "**Unwind** — leave a frame without finishing it, on the way to a catch."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on things you have already met. Answer from memory."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "A stack trace reads `at initials (badge.js:2)`, then `at badge (badge.js:6)`, then `at Object.<anonymous> (badge.js:9)`. Which function called `initials`? Name it.",
                    "accept": [
                      "badge",
                      "badge()",
                      "`badge`"
                    ],
                    "miss": "**If you said `Object.<anonymous>` — the trace is innermost first.** Each line was called by the line below it, so `initials` was called by the one directly underneath.\n",
                    "why": "`badge`. A stack trace lists the calls that were still running, innermost first; each one was called by the next line down.\n"
                  },
                  {
                    "question": "Given `class NotFound extends Error { constructor(id) { super(`no order ${id}`); this.name = \"NotFound\"; } }`, what is `new NotFound(4).message`? Type the value.",
                    "accept": [
                      "\"no order 4\"",
                      "no order 4",
                      "'no order 4'"
                    ],
                    "why": "`\"no order 4\"` — the text handed to `super`. An error is an object, and the message is one of the things a caller will find in it.\n"
                  },
                  {
                    "question": "Reading `.length` of `undefined` — what kind of error?",
                    "choices": [
                      "ReferenceError",
                      "TypeError",
                      "RangeError"
                    ],
                    "accept": [
                      "TypeError"
                    ],
                    "why": "A **TypeError**: the name existed, and its value was not what the code assumed.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can trace a thrown error up the call stack and say which `catch` receives it",
                "I can say which lines never run once a `throw` happens"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "What a throw does",
          "body": [
            {
              "p": "`throw` takes an error object and **stops the function right there**. The lines\nafter it do not run, and the function does not return — not even `undefined`."
            },
            {
              "code": "function checkQuantity(qty) {\n  console.log(\"checking\", qty);\n  if (qty < 1) throw new RangeError(`quantity must be at least 1, got ${qty}`);\n  console.log(\"quantity ok\");\n  return qty;\n}\n\ncheckQuantity(0);\nconsole.log(\"next order\");",
              "lang": "js",
              "file": "stop.js"
            },
            {
              "p": "The first line prints `checking 0`. Then the `throw` runs — the caret sits under\nit, and nothing else in\nthis program does — not `quantity ok`, not `return`, and not `next order`\neither. Nothing caught the error, so it went all the way out, and the host\nstopped the program and printed what you learned to read in the first week:"
            },
            {
              "code": "checking 0\n/workspaces/cs140/stop.js:3\n  if (qty < 1) throw new RangeError(`quantity must be at least 1, got ${qty}`);\n               ^\n\nRangeError: quantity must be at least 1, got 0\n    at checkQuantity (/workspaces/cs140/stop.js:3:22)\n    at Object.<anonymous> (/workspaces/cs140/stop.js:8:1)\n    ...",
              "lang": "text",
              "file": "node stop.js"
            },
            {
              "p": "**A stack trace is the path a thrown error took.** Each `at` line is a frame it\nleft without finishing. Every error you have met so far travelled this way —\nthe TypeErrors too; the engine threw those for you."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`function f() { console.log(\"a\"); throw new Error(\"x\"); console.log(\"b\"); }  f(); console.log(\"c\");` — list what prints, in order, separated by spaces.",
                    "accept": [
                      "a",
                      "\"a\"",
                      "a only",
                      "just a",
                      "only a"
                    ],
                    "miss": "**If you included `c` — a throw does not come back to the line after the call.** Nothing caught it, so the whole program stopped at the throw.\n",
                    "why": "Only `a`. The throw ends `f` before `b`, and with nothing to catch it, the program ends before `c`.\n"
                  },
                  {
                    "question": "`function g() { throw new Error(\"x\"); return 5; }` — what does `g()` return? Type the value, or error.",
                    "accept": [
                      "nothing",
                      "it does not return",
                      "does not return",
                      "no value",
                      "it throws",
                      "never returns",
                      "it never returns",
                      "error",
                      "an error"
                    ],
                    "miss": "**If you answered `undefined` — that is what a function returns when it runs off its end.** A function that throws does not run off its end; it does not return at all.\n",
                    "why": "Nothing. `g` never returns; it throws. The code that called it never gets a value.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Stopping it: try and catch",
          "body": [
            {
              "p": "To stop an error's journey, put the call inside `try { … }` and write what to do\nin `catch (e) { … }`."
            },
            {
              "list": [
                "If nothing in the `try` throws, the `catch` is skipped.",
                "If anything in the `try` throws — on any line, **at any depth of calls** — the rest of the `try` is abandoned and the `catch` runs.",
                "`e` is **the error object that was thrown**: the same object, with its `name`, `message` and `cause`.",
                "After the `catch` finishes, the program carries on **after** the whole `try`/`catch`, as if nothing had gone wrong."
              ]
            },
            {
              "p": "Two worked examples, with the same subgoals."
            },
            {
              "code": "function ageFrom(text) {\n  const n = Number(text);\n  if (!Number.isInteger(n) || n < 0) throw new RangeError(`not an age: \"${text}\"`);\n  return n;\n}\n\n// run the call that may throw inside try\ntry {\n  const age = ageFrom(\"abc\");\n  console.log(\"age is\", age);          // skipped — ageFrom threw\n} catch (e) {\n  // receive the error in catch\n  console.log(e.name, e.message);      // RangeError not an age: \"abc\"\n}\n// carry on after the try/catch\nconsole.log(\"form checked\");           // runs",
              "lang": "js",
              "file": "parse-age.js"
            },
            {
              "code": "class NotFound extends Error {\n  constructor(id) { super(`no order ${id}`); this.name = \"NotFound\"; }\n}\nfunction findOrder(orders, id) {\n  const order = orders.find(o => o.id === id);\n  if (order === undefined) throw new NotFound(id);\n  return order;\n}\n\ntry {\n  console.log(findOrder([{ id: 1 }], 1).id);   // 1 — nothing threw\n  console.log(findOrder([{ id: 1 }], 7).id);   // throws before printing\n  console.log(\"both found\");                   // skipped\n} catch (e) {\n  console.log(e instanceof NotFound);          // true\n}\nconsole.log(\"done\");                           // runs",
              "lang": "js",
              "file": "find-order.js"
            },
            {
              "p": "The first `findOrder` call succeeds and prints `1`. The second throws, and the\ntwo lines left in the `try` are abandoned. `e instanceof NotFound` is `true`:\nthe catch received exactly the object `findOrder` built — the type you gave it\nin the last lesson is how the catch can tell which failure it has."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`try { console.log(\"one\"); JSON.parse(\"{oops\"); console.log(\"two\"); } catch (e) { console.log(\"three\"); } console.log(\"four\");` — list what prints, in order, separated by spaces.",
                    "accept": [
                      "one three four",
                      "one, three, four",
                      "one,three,four",
                      "one three and four"
                    ],
                    "miss": "**If you included `two` — once `JSON.parse` throws, the rest of the `try` is abandoned.** And `four` does run: the catch stopped the error, so the program carries on after the whole block.\n",
                    "why": "`one`, `three`, `four`. The throw skipped `two`, the catch ran, and the program carried on after the `try`/`catch`.\n"
                  },
                  {
                    "question": "`try { findOrder([], 3); } catch (e) { console.log(e.message); }` — what prints? Type it.",
                    "accept": [
                      "no order 3",
                      "\"no order 3\"",
                      "'no order 3'"
                    ],
                    "why": "`no order 3`. `e` is the `NotFound` that `findOrder` threw, so its message is the one its constructor built.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Up the stack, one frame at a time",
          "body": [
            {
              "p": "The `try` does not have to be in the function that throws. An error travels\n**up the call stack**: from the function that threw, to the function that\ncalled it, to the one that called *that*, until it reaches a frame whose `try`\ncontains the call. Every frame it passes through is **unwound** — left at the\ncall that threw, with nothing after that call run."
            },
            {
              "code": "function c() {\n  console.log(\"c starts\");\n  throw new Error(\"disk full\");\n  console.log(\"c ends\");               // never\n}\nfunction b() {\n  c();\n  console.log(\"b after c\");            // never — b has no catch, so it is unwound\n}\nfunction a() {\n  try {\n    b();\n    console.log(\"a after b\");          // never\n  } catch (e) {\n    console.log(\"a caught \" + e.message);\n  }\n}\n\na();\nconsole.log(\"done\");",
              "lang": "js",
              "file": "unwind.js"
            },
            {
              "p": "Trace it one frame at a time:"
            },
            {
              "table": {
                "head": [
                  "Step",
                  "Where",
                  "What happens"
                ],
                "rows": [
                  [
                    "1",
                    "`a`",
                    "calls `b()` inside its `try`"
                  ],
                  [
                    "2",
                    "`b`",
                    "calls `c()` — no `try` here"
                  ],
                  [
                    "3",
                    "`c`",
                    "prints `c starts`, then throws"
                  ],
                  [
                    "4",
                    "`c`",
                    "unwound: `c ends` never runs"
                  ],
                  [
                    "5",
                    "`b`",
                    "unwound at the call to `c`: `b after c` never runs"
                  ],
                  [
                    "6",
                    "`a`",
                    "the call to `b` was inside a `try`, so the `catch` runs: `a caught disk full`"
                  ],
                  [
                    "7",
                    "top",
                    "`a` finished normally, so `done` prints"
                  ]
                ]
              }
            },
            {
              "p": "Three lines print before `done`, not five. If `a` had no `catch` either, the\nerror would leave `a` too, and the trace the host printed would read `at c`,\n`at b`, `at a` — the frames it unwound, innermost first. That is why a stack\ntrace is the error's route."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`x()` calls `y()` calls `z()`. `z` throws; only `x` has a catch. Which frames are unwound? Name them.",
                          "accept": [
                            "z and y",
                            "y and z",
                            "z then y",
                            "z, y",
                            "y, z"
                          ],
                          "miss": "**If you said only `z` — `y` has no catch, so it cannot stop the error.** Unwinding carries on until a frame with a `try` around the call is found.\n",
                          "why": "`z` and `y`. Neither finishes; the error stops in `x`, whose `try` contained the call.\n"
                        },
                        {
                          "question": "Same program. After `x`'s catch runs, does the line after the call to `x()` run?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "Yes. `x` caught the error, so from outside, `x()` returned normally.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`a()` calls `b()` calls `c()`. `c` raises; only `a` has `except`. Which frames unwind? Name them.",
                          "accept": [
                            "c and b",
                            "b and c",
                            "c then b"
                          ],
                          "why": "Same mechanism. The traceback you see lists exactly this path.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Watch the frames before they unwind",
          "body": [
            {
              "p": "The table above is a claim. The debugger lets you see it. Printing cannot: a\ncaught error prints no trace at all, and by the time `catch` runs, the frames it\nunwound — and their variables — are gone."
            },
            {
              "p": "Run `unwind.js` under the debugger as in *Meet the debugger*. You need no\nbreakpoint; ask it to **pause on exceptions** instead. In Chrome, open the\n**Breakpoints** section of the **Sources** panel and tick **Pause on caught\nexceptions**. In VS Code, the **Breakpoints** section of Run and Debug has a\n**Caught Exceptions** box. There is an *uncaught* box beside each, which pauses only when nothing will\ncatch the error. If the first pause is in a file of Node's own, resume."
            },
            {
              "p": "Resume, and the program pauses **on the `throw` line, before it runs**. The\n**Call Stack** pane lists every frame the error is about to leave: `c` on top,\n`b` under it, `a` under that. Click `b` to see where it is stopped — at its call\nto `c`, with `b after c` still ahead of it, never to run."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`unwind.js`, paused at the throw — the frame under `c` is paused at which line? Give the line number.",
                    "accept": [
                      "7",
                      "line 7"
                    ],
                    "miss": "**Count from line 1 of unwind.js.** `b`'s body is lines 7 and 8, and it is stopped at its call to `c`.\n",
                    "why": "Line 7, `c();`. That is where `b` will be unwound — the line after it never runs.\n"
                  },
                  {
                    "question": "Paused at the throw, the stack reads `c`, `b`, `a`. Which of the three are gone by the time the catch runs? Name them.",
                    "accept": [
                      "c and b",
                      "b and c",
                      "c, b",
                      "b, c",
                      "c then b"
                    ],
                    "miss": "**If you included `a` — the `catch` runs in `a`.** `a` is the frame whose `try` held the call, so it is the one that survives.\n",
                    "why": "`c` and `b`. Both are unwound; the catch runs in `a`.\n"
                  },
                  {
                    "question": "Only the uncaught box is ticked, and `unwind.js` runs. Does it pause?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**`a` catches this error.** Pausing on uncaught exceptions stops only for an error nothing will catch — one that would end the program.\n",
                    "why": "No. `a`'s `catch` receives it, so it is never uncaught.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Sending it on",
          "body": [
            {
              "p": "A `catch` receives **everything** thrown inside its `try` — the failure you had\nin mind, and every other one, bugs included. A `catch` written for \"order not\nfound\" also catches the TypeError from a typo three calls down."
            },
            {
              "p": "So a catch that only knows what to do with one kind of failure checks, and sends\nthe rest on with `throw e`:"
            },
            {
              "code": "try {\n  const order = findOrder(orders, id);\n  show(order);\n} catch (e) {\n  if (!(e instanceof NotFound)) throw e;   // not the one I know about — send it on\n  show(\"That order does not exist.\");\n}",
              "lang": "js"
            },
            {
              "p": "`throw e` throws **the same object** again. Its journey continues upward from\nhere, exactly as if this `catch` had not been in the way, and its message and\nstack are unchanged — the trace still points at where it really started."
            },
            {
              "p": "Which failures a `catch` *should* handle is a design decision — the lesson after\nnext. This step is only the mechanism: an error a `catch` rethrows keeps\ntravelling."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`a()` → `b()` → `c()`. `c` throws a TypeError. `b` catches, checks `e instanceof NotFound`, and does `throw e` otherwise. `a` has a catch too. Which catch finally keeps it?",
                          "choices": [
                            "b's catch",
                            "a's catch"
                          ],
                          "accept": [
                            "a's catch"
                          ],
                          "miss": "**If you said `b` — `b`'s catch ran, but it did not keep the error.** A TypeError is not a NotFound, so `b` threw it again, and it went on up to `a`.\n",
                          "why": "`a`'s. `b` looked, saw it was not a NotFound, and sent it on. The next frame up with a `try` around the call is `a`.\n"
                        },
                        {
                          "question": "`catch (e) { if (!(e instanceof NotFound)) throw e; }` — a NotFound arrives. Does it keep travelling?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "No. The `if` is false, so nothing is thrown, the `catch` finishes, and the error's journey ends here.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "What does `raise` with no argument do inside an `except` block?",
                          "choices": [
                            "Nothing",
                            "Raises a new, empty exception",
                            "Re-raises the current exception"
                          ],
                          "accept": [
                            "Re-raises the current exception"
                          ],
                          "why": "Re-raises the current exception with its traceback intact — the equivalent of `throw e` for the cases you did not handle.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`throw;` versus `throw e;` inside a catch — what differs?",
                          "choices": [
                            "Nothing",
                            "The stack trace",
                            "The exception type"
                          ],
                          "accept": [
                            "The stack trace"
                          ],
                          "why": "Bare `throw;` preserves the original stack trace. `throw e;` resets it to this line, destroying the information you need.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "`throw` **stops the function** at that line. It does not return, and nothing after it runs.",
                "The error travels **up the call stack**, unwinding each frame at the call that threw, until a `try` around the call catches it. Uncaught, the host prints its route: the stack trace.",
                "`catch (e)` receives the thrown object; the program carries on **after** the `try`/`catch`. `throw e` sends it on, unchanged."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "throw",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/throw",
              "note": "what can be thrown, and what happens when nothing catches it."
            },
            {
              "title": "try...catch",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/try...catch",
              "note": "the `catch` binding, and nested `try` blocks."
            }
          ]
        }
      ],
      "_source": "lessons/propagation.md",
      "practice": {
        "id": "propagation",
        "outcome": 8,
        "steps": [
          {
            "kind": "predict",
            "title": "Where does the throw go?",
            "prompt": "Pick an answer, or type one where there is a box — a name, or what prints.",
            "items": [
              {
                "id": "p1",
                "question": "`load()` calls `parse()` calls `readLine()`. `readLine` throws, and only `load` has a catch. Which frames are unwound? Name them.",
                "accept": [
                  "readline and parse",
                  "parse and readline",
                  "readline, parse",
                  "parse, readline",
                  "readline then parse",
                  "readLine and parse",
                  "parse and readLine"
                ],
                "miss": "If you said only readLine: parse has no catch, so it cannot stop the error. Unwinding continues until a frame with a try around the call is found.",
                "why": "Both, and nothing after the failing call runs in either. The error stops in load."
              },
              {
                "id": "p2",
                "question": "`function f() { throw new Error(\"x\"); console.log(\"after\"); }   try { f(); console.log(\"next\"); } catch (e) { console.log(\"caught\"); }` — what prints? Type each line, in order.",
                "accept": [
                  "caught",
                  "\"caught\"",
                  "only caught",
                  "just caught"
                ],
                "miss": "If you included `next`: once `f()` throws, the rest of the `try` is abandoned. And `after` is after the throw inside `f`.",
                "why": "Only `caught`. The throw ends `f`, abandons the rest of the `try`, and the `catch` runs."
              },
              {
                "id": "p3",
                "question": "In the first question's program, whose catch receives the error? Name the function.",
                "accept": [
                  "load",
                  "load's",
                  "load()",
                  "the one in load"
                ],
                "why": "`load`'s — the first frame up the stack whose `try` contains the call."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "After a catch finishes, does the line after the whole `try`/`catch` run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. The catch stopped the error; from there the program carries on as normal."
              },
              {
                "id": "p5",
                "question": "`catch (e) { throw e; }` — does the error keep travelling up?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. `throw e` sends the same object on, as if this catch had not been there."
              },
              {
                "id": "p6",
                "question": "Nothing catches an error anywhere. What does Node do?",
                "choices": [
                  "Skips the line and carries on",
                  "Prints a stack trace and stops"
                ],
                "accept": [
                  "Prints a stack trace and stops"
                ],
                "why": "It stops the program and prints the error with its stack trace — the route the error took, innermost frame first."
              },
              {
                "id": "p7",
                "question": "`function g() { throw new Error(\"x\"); }   const v = g();` — what is `v`? Type the value, or error.",
                "accept": [
                  "nothing",
                  "never assigned",
                  "it is never assigned",
                  "no value",
                  "not assigned",
                  "there is no v",
                  "the assignment never happens",
                  "error",
                  "an error",
                  "it throws"
                ],
                "miss": "If you answered `undefined`: `g` never returns, so there is nothing to assign. The throw leaves this line too.",
                "why": "It is never assigned. `g` throws, so the assignment is abandoned along with everything else until a catch."
              }
            ],
            "hints": [
              {
                "when": "you expected the middle frame to keep running",
                "say": "A throw does not return to the line after the call. Each frame without a try around the call is abandoned at that call."
              },
              {
                "when": "you included \"next\" or \"after\"",
                "say": "Mark the line that throws. Nothing after it in the same try block runs — not in the function that threw, and not in the function that called it."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Sent on, and caught further up",
            "prompt": "Predict each line, then run it. `greeting` handles NotFound and sends everything else on.",
            "filename": "greeting.js",
            "code": "class NotFound extends Error {\n  constructor(id) { super(`no user ${id}`); this.name = \"NotFound\"; }\n}\nfunction lookup(users, id) {\n  const user = users.find(u => u.id === id);\n  if (user === undefined) throw new NotFound(id);\n  return user;\n}\nfunction greeting(users, id) {\n  try {\n    return \"Hi, \" + lookup(users, id).name.toUpperCase();\n  } catch (e) {\n    if (!(e instanceof NotFound)) throw e;\n    return \"Hi, stranger\";\n  }\n}\n\nconst users = [{ id: 1, name: \"ada\" }, { id: 2 }];\ntry {\n  console.log(greeting(users, 1));\n  console.log(greeting(users, 9));\n  console.log(greeting(users, 2));\n  console.log(\"all greeted\");\n} catch (e) {\n  console.log(\"outer caught \" + e.name);\n}\nconsole.log(\"done\");\n",
            "items": [
              {
                "id": "r1",
                "question": "Second line printed",
                "accept": [
                  "Hi, stranger"
                ],
                "why": "There is no user 9, so `lookup` throws a NotFound. `greeting`'s catch recognises it and returns a value instead."
              },
              {
                "id": "r2",
                "question": "Third line printed",
                "accept": [
                  "outer caught TypeError"
                ],
                "miss": "If you expected `Hi, stranger`: user 2 exists, so no NotFound. What does `.toUpperCase()` do to a `name` that is `undefined`?",
                "why": "User 2 has no `name`, so `.toUpperCase()` of `undefined` throws a TypeError. `greeting`'s catch sees it is not a NotFound and throws it on, and the outer catch keeps it."
              },
              {
                "id": "r3",
                "question": "Does \"all greeted\" print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The third call threw out of `greeting`, so the rest of the outer `try` was abandoned."
              },
              {
                "id": "r4",
                "question": "Does \"done\" print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. The outer catch stopped the error, and the program carries on after it."
              }
            ],
            "hints": [
              {
                "when": "you predicted \"Hi, stranger\" for user 2",
                "say": "User 2 is found. Look at what the rest of that line does with the user it found."
              },
              {
                "when": "you predicted \"all greeted\"",
                "say": "Any throw that leaves `greeting` abandons the rest of the outer `try`. Did any of the three calls throw out of it?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace the error up the stack",
            "prompt": "`startup()` calls `loadConfig()`, which calls `readFile()` and then `JSON.parse` on what it read. `loadConfig` has a `catch` that returns `{}` for a `SyntaxError` and does `throw e` for anything else. `startup` has a `catch` too. This time, `readFile()` throws because the file is missing.\n",
            "items": [
              {
                "id": "t1",
                "question": "Does `JSON.parse` run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The throw abandons the rest of the `try` at the call that failed."
              },
              {
                "id": "t2",
                "question": "Does `loadConfig`'s catch run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "miss": "If you said no because it only handles SyntaxError: a catch receives everything thrown in its try. It runs, looks, and decides.",
                "why": "Yes. Every error thrown inside the `try` reaches it. It checks the type — not a SyntaxError — and throws it on."
              },
              {
                "id": "t3",
                "question": "Which function's catch keeps the error? Name the function.",
                "accept": [
                  "startup",
                  "startup()",
                  "startup's"
                ],
                "why": "`startup`'s. `loadConfig` sent it on; the next frame up with a `try` around the call is `startup`."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "The file is there, but its JSON is broken. Which function's catch keeps the error now? Name the function.",
                "accept": [
                  "loadconfig",
                  "loadConfig",
                  "loadConfig's",
                  "loadConfig()"
                ],
                "why": "`loadConfig`'s. `JSON.parse` throws a SyntaxError, which is the one kind `loadConfig` keeps."
              },
              {
                "id": "t5",
                "question": "In that case, what does `startup` receive from `loadConfig`? Type the value.",
                "accept": [
                  "{}",
                  "an empty object",
                  "empty object"
                ],
                "why": "An empty object — the catch returned a value, so to `startup` the call looks like it succeeded."
              }
            ],
            "hints": [
              {
                "when": "you had loadConfig's catch skipped",
                "say": "A catch cannot choose which errors reach it. It receives every one, and only then can it choose which to keep."
              },
              {
                "when": "you had the error stop in loadConfig for a missing file",
                "say": "Read what `loadConfig`'s catch does when the error is not a SyntaxError."
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Catch it at any depth",
            "prompt": "`firstFailure(checks)` runs each check function in order. It returns the message of the first check that throws, or `\"all passed\"`. Checks after a failing one are not run. Fill the gap. Then, in one sentence: a check that calls a helper, which throws — why does your catch still receive it?\n",
            "exportName": "firstFailure",
            "filename": "first-failure.js",
            "starter": "// Runs each check in order. Returns the message of the first one that\n// throws, or \"all passed\". Checks after a failure are not run.\nfunction firstFailure(checks) {\n  for (const check of checks) {\n    // ___ fill the gap: run check(), and if it throws, return its message\n  }\n  return \"all passed\";\n}\n",
            "tests": [
              {
                "label": "no check throws → \"all passed\", and every check ran",
                "body": "let ran = 0; const ok = function () { ran++; }; return subject([ok, ok, ok]) === 'all passed' && ran === 3;"
              },
              {
                "label": "the first failure's message is returned",
                "body": "return subject([function () {}, function () { throw new Error('disk full'); }]) === 'disk full';"
              },
              {
                "label": "checks after the failure are not run",
                "body": "let later = 0; subject([function () { throw new Error('a'); }, function () { later++; }]); return later === 0;"
              },
              {
                "label": "a throw from inside a helper is caught too",
                "body": "function helper() { throw new RangeError('too deep'); } return subject([function () { helper(); }]) === 'too deep';"
              },
              {
                "label": "the first of two failures wins",
                "body": "return subject([function () { throw new Error('first'); }, function () { throw new Error('second'); }]) === 'first';"
              }
            ],
            "hints": [
              {
                "when": "the whole call throws instead of returning a message",
                "say": "The check runs, but nothing is around it to stop what it throws. Put the call inside a `try`."
              },
              {
                "when": "the result is an object, not a message",
                "say": "The catch receives the error object. The test wants the text inside it."
              },
              {
                "when": "\"all passed\" comes back even though a check threw",
                "say": "Your catch received the error and then let the loop carry on. What should the function do as soon as it has a failure to report?"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a batch save",
            "prompt": "A teammate wrote this. They expect that when one record fails to save, the log line still reports how many were saved before it. Review it.\n",
            "filename": "save-all.js",
            "code": "// Saves every record and logs how many were saved.\nfunction saveAll(records, save) {\n  let saved = 0;\n  for (const record of records) {\n    save(record);\n    saved++;\n  }\n  console.log(`saved ${saved} of ${records.length}`);\n  return saved;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Five records; the second save throws. Does line 8 print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "If you said yes: nothing in `saveAll` catches, so the throw leaves the loop and the function together.",
                "why": "No. The error leaves `saveAll` at line 5. Nothing after that call runs — not the rest of the loop, and not the log line."
              },
              {
                "id": "c2",
                "question": "How many times is `save` called? Give a number.",
                "accept": [
                  "2",
                  "two",
                  "twice"
                ],
                "why": "Twice. The second call throws and the loop never reaches the third record."
              },
              {
                "id": "c3",
                "question": "What does the caller of `saveAll` receive?",
                "choices": [
                  "A return value",
                  "The thrown error"
                ],
                "accept": [
                  "The thrown error"
                ],
                "why": "No return value — the error arrives instead, and travels on to the caller's caller unless something catches it."
              }
            ],
            "hints": [
              {
                "when": "you expected the log line to run",
                "say": "Find the first line that throws and follow the error out. Does it pass anything that could stop it inside `saveAll`?"
              }
            ]
          }
        ]
      }
    },
    "logging-to-decide": {
      "id": "logging-to-decide",
      "title": "Logging to tell two explanations apart",
      "kind": "read",
      "outcome": 10,
      "shape": "conceptual",
      "why": "Outcome 10 names runtime diagnostics beside the debugger, and `diag.diagnostics` had no lesson once `meet-debugger` stopped claiming it (D-025). Its mastery (D-018) is a judgment, not a tool: students already print values to find a silent failure, and printing without a question to answer is how a print statement stops being evidence.",
      "concepts": [
        "diag.diagnostics"
      ],
      "lede": "A log line is an experiment. Before you add one, say what could be wrong — two explanations, not one — and log the thing they disagree about. Then one run rules one of them out. A line added \"to see what happens\" prints something every time, and tells you nothing.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "When it breaks: reading errors · Meet the debugger · Throwing, and where it goes"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Explanation** — a guess at the cause that predicts what the program will do. **Deciding log** — a log line whose output would differ depending on which explanation is right.",
          "entries": [
            "**Explanation** — a guess at the cause that predicts what the program will do.",
            "**Deciding log** — a log line whose output would differ depending on which explanation is right."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "`addShipping(order)` returns `\"105\"` where it should return `15`. There are two\nexplanations. **A:** `subtotal` arrived as the text `\"10\"`. **B:** `shipping`\narrived as the text `\"5\"`."
            },
            {
              "p": "Each item below is one log line, and what it printed in Node. Pick which\nexplanation is still standing afterwards — `A`, `B`, or `Both` if the line\ncould not tell them apart. A guess is fine; that is what this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "console.log(subtotal, shipping)   →   printed:  10 5",
                          "choices": [
                            "A",
                            "B",
                            "Both"
                          ],
                          "accept": [
                            "Both"
                          ],
                          "miss": "**If you picked one — look at what each explanation predicts this line prints.** The text `\"10\"` prints as `10`, and the number `5` prints as `5`. Under A it prints `10 5`; under B it prints `10 5`. A line that prints the same thing either way cannot rule either out.\n",
                          "why": "Both. Printed on its own, a string looks exactly like the number it spells. This line reads like evidence and is none.\n"
                        },
                        {
                          "code": "console.log(subtotal + shipping)   →   printed:  105",
                          "choices": [
                            "A",
                            "B",
                            "Both"
                          ],
                          "accept": [
                            "Both"
                          ],
                          "miss": "**If you picked one — `105` is the bug report, printed again.** Both explanations were made up to explain `105`, so both predict it.\n",
                          "why": "Both. Logging the symptom confirms the symptom. It cannot choose between two causes that both produce it.\n"
                        },
                        {
                          "code": "console.log(typeof shipping)   →   printed:  number",
                          "choices": [
                            "A",
                            "B",
                            "Both"
                          ],
                          "accept": [
                            "A"
                          ],
                          "miss": "**If you said B — B says `shipping` is text, so under B this prints `string`.** It printed `number`, which is what A predicts, so B is ruled out.\n",
                          "why": "A. `typeof` prints the *kind* of value, which is exactly what the two explanations disagree about.\n"
                        },
                        {
                          "code": "console.log({ subtotal, shipping })   →   printed:  { subtotal: 10, shipping: '5' }",
                          "choices": [
                            "A",
                            "B",
                            "Both"
                          ],
                          "accept": [
                            "B"
                          ],
                          "miss": "**Look at the quotes.** Inside an object, Node prints text in quotes and numbers without. `'5'` is text; `10` is a number. That is B's prediction.\n",
                          "why": "B. Wrapping the values in `{ }` labels each one *and* shows its type.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can choose what to log so that one run tells two explanations apart",
                "I can say, before I run a log line, what it will print under each explanation — and drop it when the two answers match",
                "I can spot a log line that cannot decide: a value printed without its type, an object read after it changed, a line that never ran"
              ]
            }
          ]
        },
        {
          "h": "A log line is a question"
        },
        {
          "p": "When something is wrong and you do not know why, the pull is to print\neverything near the problem and read it. Every line prints *something*, and\nmost of it looks reasonable — because you never said what wrong would look like.\nYou met the cure for this when finding a silent failure: work out the right\nvalue first, or a wrong one looks like any other. A log line works the same\nway. Its output is evidence only against a prediction."
        },
        {
          "p": "Three words for the pieces:"
        },
        {
          "list": [
            "An **explanation** is a guess at the cause, specific enough to predict what the program will do. \"Something is off with the types\" predicts nothing. \"`shipping` arrived as text\" predicts that `typeof shipping` is `\"string\"`.",
            "A **prediction** is what a given log line would print *if* that explanation is right.",
            "A **deciding log** is a line whose predictions differ between the two explanations. Run it once and one explanation is gone."
          ]
        },
        {
          "p": "So the question to ask before typing `console.log` is not \"what would be\ninteresting to see?\" It is: **what would this print if A is right, what would\nit print if B is right, and are those different?** If they are the same, the\nline cannot help. Pick a different thing to log."
        },
        {
          "p": "Why two explanations and not one? With one, every output looks like\nconfirmation — you read `10 5` and think \"yes, the numbers are there\". A second\nexplanation forces you to ask what *it* would print, and that is where you\nnotice the line cannot tell them apart."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`findUser` returns `undefined`. A: no user has that id. B: the function forgot its `return`. Does `console.log(findUser(7))` decide?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**Write each prediction down.** No match: `find` gives `undefined`, and the log prints `undefined`. No `return`: the call gives `undefined`, and the log prints `undefined`. Same output, so the line cannot choose.\n",
                "why": "No. Both explanations predict `undefined` — which is the symptom again. Something that differs between them is whether a user with id 7 exists.\n"
              },
              {
                "question": "A report is never sent. A: the loop over `orders` never runs. B: it runs, but the `if` inside never matches. Where does a deciding log go?",
                "choices": [
                  "Inside the loop",
                  "After the loop"
                ],
                "accept": [
                  "Inside the loop"
                ],
                "miss": "**After the loop, a log runs under both explanations** — the loop finishing is not in dispute. Put the log where A and B disagree about whether it runs.\n",
                "why": "Inside the loop, before the `if`. Under A it never prints; under B it prints once per order. Nothing printing is an answer.\n"
              }
            ]
          }
        },
        {
          "h": "Two bugs, one line each"
        },
        {
          "p": "The same four steps both times. The step that does the work is the second:\nwriting down what each explanation predicts."
        },
        {
          "p": "**A total that came out as text.** The case from the opening."
        },
        {
          "code": "function addShipping(order) {\n  return order.subtotal + order.shipping;\n}\n// addShipping(order) gives \"105\". It should give 15.",
          "lang": "js",
          "file": "add-shipping.js"
        },
        {
          "code": "// State two explanations:\n//   A: order.subtotal is the text \"10\".\n//   B: order.shipping is the text \"5\".\n\n// Say what each predicts a log will print:\n//   console.log(order.subtotal, order.shipping)\n//     A → 10 5        B → 10 5          same: cannot decide\n//   console.log(typeof order.subtotal, typeof order.shipping)\n//     A → string number   B → number string   different: decides\n\n// Log the thing that differs:\nconsole.log(typeof order.subtotal, typeof order.shipping);\n\n// Run once, and rule one out:\n//   printed: number string   → A is out. shipping is text; find where it came from.",
          "lang": "js"
        },
        {
          "p": "The two explanations disagree about **types**, not about values, so the log has\nto print types. The values were never in dispute."
        },
        {
          "p": "**Two lists that change together.** A music app keeps a `queue` and a\n`history`. Adding a song to the queue also adds it to the history."
        },
        {
          "code": "// A: queue and history are one array with two names.\n// B: they are two arrays, and some code copies each addition across.\n\n// console.log(queue, history)\n//   A → [\"Song 2\"] [\"Song 2\"]   B → [\"Song 2\"] [\"Song 2\"]   same: cannot decide\n// console.log(queue === history)\n//   A → true                     B → false                   different: decides\n\nconsole.log(queue === history);\n// printed: true   → B is out. Find the line that made history = queue.",
          "lang": "js"
        },
        {
          "p": "Printing both arrays shows two identical lists, which is exactly what both\nexplanations predict. The disagreement is about **identity** — one object or\ntwo — so the log asks that question and prints its answer. You can log a\ncomparison, not just a value."
        },
        {
          "p": "**The same shape, different surface.** In both examples, the obvious line — print\nthe values involved — was the one that could not decide, because the two\nexplanations agreed about what the values would *look* like. What they disagreed\nabout was something the obvious print hides: a type, an identity."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`isOpen(shop)` returns `shop.closed !== true`, and it says a closed shop is open. A: `shop.closed` is the text `\"true\"`. B: `shop.closed` was never set. You log `{ closed: shop.closed }` and it prints `{ closed: 'true' }`. Which explanation is standing?",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "A"
                ],
                "miss": "**Look for the quotes.** Inside an object, Node quotes text. `'true'` is the four-letter string, which is A. Under B it would print `{ closed: undefined }`.\n",
                "why": "A. The text `\"true\"` is not the boolean `true`, so `!== true` holds. The next question is where the text came from.\n"
              },
              {
                "question": "Two form objects, `draft` and `saved`, always hold the same fields. A: they are one object. B: `save()` copies every field across. Type the one expression you would log.",
                "accept": [
                  "draft === saved",
                  "saved === draft",
                  "console.log(draft === saved)",
                  "console.log(saved === draft)",
                  "draft == saved",
                  "saved == draft"
                ],
                "miss": "**If you would log `draft` and `saved` — write down what each explanation predicts.** Both say the contents match, so the contents cannot decide. What they disagree about is whether there is one object.\n",
                "why": "`draft === saved`. `true` under A, `false` under B. On objects, `===` asks identity — the one question that separates them.\n"
              }
            ]
          }
        },
        {
          "h": "Lines that cannot decide"
        },
        {
          "p": "Some log lines look like they answer a question and cannot. Each of these\nprints the same thing for two different situations. The fix is to log\nsomething that differs."
        },
        {
          "table": {
            "head": [
              "You log",
              "Situation one",
              "Situation two",
              "Both print",
              "Log instead"
            ],
            "rows": [
              [
                "`x`",
                "`x` is `5`",
                "`x` is `\"5\"`",
                "`5`",
                "`typeof x`, or `{ x }`"
              ],
              [
                "`x`",
                "`x` is `undefined`",
                "`x` is `\"undefined\"`",
                "`undefined`",
                "`JSON.stringify(x)` — prints `undefined` and `\"undefined\"`"
              ],
              [
                "`code`",
                "`code` is `\"SAVE10\"`",
                "`code` is `\"SAVE10 \"`",
                "`SAVE10` — the space is invisible",
                "`JSON.stringify(code)` — the quotes show where the text ends"
              ],
              [
                "`a, b`",
                "one object, two names",
                "two equal objects",
                "the same contents twice",
                "`a === b`"
              ]
            ]
          }
        },
        {
          "p": "Two more cases are about **when** the log runs, not what it prints."
        },
        {
          "p": "**An object printed, then changed** (browser only). In a browser console,\n`console.log(order)` shows a collapsed object. Expand it later and the browser\nreads the object *at that moment* — so if the code changed `order.total` after\nthe log, you see the new total, not the one that was there when the line ran.\nNode prints the object as it was when the line ran. In a browser, log a\nsnapshot that cannot change: `JSON.stringify(order)`, or just the field you\ncare about, `order.total`."
        },
        {
          "p": "**A line that never ran.** A log that prints nothing is still an answer — *if*\nyou placed it where the two explanations disagree about whether it runs. Put it\nas the first line of the function you suspect is never called, and silence\ndecides. Two things silence it that are not about your question: the function\nwas never called, or something above it **threw**. A thrown error skips every\nline after it until a `catch` — so a log just after a call that throws never\nprints, however correct the rest is."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`let note;   const label = \"undefined\";   console.log(JSON.stringify(note), JSON.stringify(label))` — type what it prints.",
                "accept": [
                  "undefined \"undefined\""
                ],
                "miss": "**`JSON.stringify` of text adds the quotes; of `undefined` it gives `undefined` back.** So the two, which printed alike with a plain log, now differ.\n",
                "why": "`undefined \"undefined\"`. One is the absence; the other is a nine-letter string. A plain `console.log(note, label)` prints `undefined undefined`.\n"
              },
              {
                "question": "`try { const cfg = loadConfig(); console.log(\"loaded\", cfg); } catch (e) { }` — nothing prints. A: `loadConfig` threw. B: `loadConfig` returned an empty object. Which is standing?",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "A"
                ],
                "miss": "**Under B the log runs and prints `loaded {}`.** Nothing printed, so the line never ran — and the only way past it inside this `try` is a throw, which the empty `catch` then swallowed.\n",
                "why": "A. The throw jumped straight to `catch`, skipping the log. The empty `catch` is also why there was no error message to read.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Choosing a log line",
            "body": [
              {
                "h": "Choosing a log line"
              },
              {
                "code": "// Something is wrong and you do not know why.\n\n// 1. State two explanations. Each must predict something:\n//    \"x is text\", not \"something is off with x\".\n// 2. For a candidate log line, write what it prints under each.\n//    Same output → it cannot decide. Pick another.\n// 3. Log what they disagree about: a type, an identity,\n//    whether a line runs, how many times, which call.\n// 4. Run once. The output should rule one out.\n//    If it rules out both, you need a third explanation.",
                "lang": "js",
                "file": "how.js"
              },
              {
                "p": "Now do one. Fill in each line."
              },
              {
                "code": "// clearCart() is meant to empty the cart. Afterwards the cart still has items.\n//   A: clearCart is never called.\n//   B: clearCart runs, but empties a copy of the cart.\n\n// 1. two explanations:        (given above)\n// 2. a line that cannot decide: ______\n// 3. a line that decides:       ______   under A: ______   under B: ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`console.log(cart)` after the call cannot decide: both explanations predict a\ncart with items. `console.log(\"clearCart ran\")` as the first line of `clearCart`\ndecides: under A it never prints, under B it prints. (To go further under B,\nlog the cart it empties and your cart side by side with `===`.)"
              }
            ]
          }
        },
        {
          "tracks": {
            "js": [
              {
                "p": "`console` has more than `.log`. Each one is worth reaching for when it prints\nexactly the thing your two explanations disagree about."
              },
              {
                "code": "console.log({ price, qty });       // labels each value, and quotes text: { price: '12', qty: 3 }\nconsole.log(typeof price);         // the kind of value: \"string\" vs \"number\"\nconsole.log(JSON.stringify(code)); // text with its edges visible, and a snapshot of an object\nconsole.log(a === b);              // a comparison: one object, or two?\n\nconsole.count(\"save\");             // how many times did this line run?  save: 1, save: 2 …\nconsole.trace(\"who called me\");    // which chain of calls reached this line\nconsole.assert(total === 15, \"total is\", total);\n                                   // prints only when the prediction is wrong\nconsole.table(orders);             // an array of objects as rows — which item differs?\nconsole.error(\"bad input\", input); // an error-level message: red in a browser; in Node,\n                                   // written to the error stream, apart from normal output",
                "lang": "js"
              },
              {
                "p": "`console.count` decides \"called once or twice?\"; `console.trace` decides \"called\nfrom here or from there?\"; in Node, `console.table` quotes text in its cells, so\na `'2'` among `2`s stands out. The practice runner on this site prints `log` and\n`error` only — try the others in Node or a browser console."
              },
              {
                "p": "Delete diagnostic logs once they have answered their question. A log line left\nin is a question nobody is asking any more."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then rewrite the first line so that\nit decides, and say what your line prints under A and under B."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A settings page has a \"Dark mode\" switch. Users report that turning it off\ndoes nothing: the page stays dark after a reload. The setting is saved with\n`saveSetting(\"dark\", value)` and read back on load with `loadSetting(\"dark\")`."
            },
            {
              "p": "Three explanations are on the table:"
            },
            {
              "list": [
                "**A** — the value is saved as the text `\"false\"`, and the load code tests it with `if (dark)`.",
                "**B** — `saveSetting` is never called when the switch is turned off.",
                "**C** — the value is saved correctly, and then some other code calls `saveSetting(\"dark\", true)` on every page load."
              ]
            },
            {
              "p": "Pick the two you would test first and say why that pair. Then write the one\nlog line you would add, say where it goes, and write down what it prints under\neach of your two. Finally: under the third explanation, what would your line\nprint — and would it mislead you?"
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A log line is an experiment: before adding it, state **two explanations** and what the line would print under **each**.",
                "If the two predictions match, the line cannot decide. Log what they **disagree about** — a type, an identity, whether a line runs.",
                "Plain logs hide things: `\"5\"` prints as `5`, `\"undefined\"` as `undefined`, trailing spaces not at all. `typeof`, `{ x }` and `JSON.stringify` show them.",
                "**Silence is evidence** only where the explanations disagree about whether the line runs — and a throw above it silences it too."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "console",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/console",
              "note": "every method, with what each prints. Browser-focused; Node's `console` follows it closely."
            },
            {
              "title": "console.table()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/console/table_static",
              "note": "rows and columns from an array of objects."
            },
            {
              "title": "console.assert()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/console/assert_static",
              "note": "prints only when the condition is false, and does not stop the program."
            },
            {
              "title": "JSON.stringify()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify",
              "note": "why it is also a way to log a snapshot of an object, and what it leaves out."
            }
          ]
        }
      ],
      "_source": "lessons/logging-to-decide.md",
      "practice": {
        "id": "logging-to-decide",
        "outcome": 10,
        "steps": [
          {
            "kind": "predict",
            "title": "Does this line decide?",
            "prompt": "Three quick ones. Each gives two explanations for a bug. Pick an answer, or type what prints where there is a box.\n",
            "items": [
              {
                "id": "p1",
                "question": "A: `id` is the number 42. B: `id` is the text \"42\". `console.log(id)` prints `42`. Which is standing — A, B, or both?",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "Both"
                ],
                "miss": "**Write down what each explanation predicts the line prints.** The number `42` prints `42`. The text `\"42\"` prints `42`. Same output, so neither is ruled out.\n",
                "why": "Both. A plain log prints text without its quotes, so a string and the number it spells look the same.\n"
              },
              {
                "id": "p2",
                "question": "A: `items` is empty. B: `items` has entries, but none match. `result` is `items.find(...)`. Which line decides?",
                "choices": [
                  "console.log(result)",
                  "console.log(items.length)"
                ],
                "accept": [
                  "console.log(items.length)"
                ],
                "miss": "**Predict `result` under each.** An empty list finds nothing: `undefined`. A list with no match finds nothing: `undefined`. The two explanations disagree about how many entries there are.\n",
                "why": "`items.length`. `0` under A, more than `0` under B. `result` is `undefined` either way — it is the symptom.\n"
              },
              {
                "id": "p3",
                "question": "You add `console.log(\"saving\")` as the first line of `onSave`, then press Save. Nothing prints. A: `onSave` ran and failed. B: `onSave` was never called. Which is standing?",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "B"
                ],
                "miss": "**If `onSave` ran, its first line ran, and that line prints.** Nothing printed, so it never started — whatever it would have done after.\n",
                "why": "B. The log sat where the two explanations disagree about whether it runs, so its silence is an answer.\n"
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "A: `a` and `b` are one object. B: they are two objects with the same contents. `console.log(a, b)` prints `{\"n\":1} {\"n\":1}`. Which is standing?",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "Both"
                ],
                "why": "Both. One object printed twice and two equal objects printed once each look identical.\n"
              },
              {
                "id": "p5",
                "question": "Same two explanations. `console.log(a === b)` prints `true`. Which is standing?",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "A"
                ],
                "why": "A. On objects, `===` asks whether they are one object. Log the question, not the values.\n"
              },
              {
                "id": "p6",
                "code": "console.log(JSON.stringify(\"42\"))",
                "accept": [
                  "\"42\""
                ],
                "why": "`\"42\"`, with the quotes. `JSON.stringify` writes text as text, so a string can no longer pass for a number.\n"
              }
            ],
            "hints": [
              {
                "when": "you picked A or B for a plain console.log of a value",
                "say": "Write the line's output under A, then under B. If you wrote the same thing twice, the line cannot choose.\n"
              },
              {
                "when": "you picked console.log(result)",
                "say": "`result` is what went wrong — both explanations were made up to explain it. Look for something they disagree about.\n"
              },
              {
                "when": "you read silence as \"the log failed\"",
                "say": "A log line does not fail quietly. If it prints nothing, it did not run. Which explanation says it would not run?\n"
              }
            ]
          },
          {
            "kind": "run",
            "title": "What a plain log hides",
            "prompt": "Predict each line's output, then run it.",
            "filename": "hidden.js",
            "code": "const price = \"12\";\nconst qty = 3;\nconsole.log(price, qty);\nconsole.log({ price, qty });\nconsole.log(typeof price, typeof qty);\nconsole.log(price * qty, price + qty);\n\nlet note;\nconst label = \"undefined\";\nconsole.log(note, label);\nconsole.log(JSON.stringify(note), JSON.stringify(label));\n\nfunction onSave() {\n  console.log(\"saving\");\n}\nconsole.log(\"done\");\n",
            "items": [
              {
                "id": "r1",
                "code": "console.log(price, qty)",
                "accept": [
                  "12 3"
                ],
                "why": "`12 3`. Nothing says the first is text.\n"
              },
              {
                "id": "r2",
                "question": "`console.log({ price, qty })` — the site prints objects as JSON",
                "accept": [
                  "{\"price\":\"12\",\"qty\":3}",
                  "{ price: '12', qty: 3 }",
                  "{price: \"12\", qty: 3}",
                  "{price: '12', qty: 3}"
                ],
                "miss": "**Inside an object, text keeps its quotes.** The site prints `{\"price\":\"12\",\"qty\":3}`; Node prints `{ price: '12', qty: 3 }`. Either way, the quotes are on one value and not the other.\n",
                "why": "`{\"price\":\"12\",\"qty\":3}`. The `{ }` labels each value and shows which one is text. Same values as the line above; now they differ.\n"
              },
              {
                "id": "r3",
                "code": "console.log(price * qty, price + qty)",
                "accept": [
                  "36 123"
                ],
                "miss": "**`*` converts text to a number; `+` with text joins.** `\"12\" * 3` is `36`; `\"12\" + 3` is `\"123\"`.\n",
                "why": "`36 123`. One string gives two different bugs depending on the operator — and the plain log on line 3 hid the cause of both.\n"
              },
              {
                "id": "r4",
                "code": "console.log(note, label)",
                "accept": [
                  "undefined undefined"
                ],
                "why": "`undefined undefined`. One is the absence; the other is a nine-letter string. They print alike.\n"
              },
              {
                "id": "r5",
                "code": "console.log(JSON.stringify(note), JSON.stringify(label))",
                "accept": [
                  "undefined \"undefined\""
                ],
                "why": "`undefined \"undefined\"`. The quotes show which one is text.\n"
              },
              {
                "id": "r6",
                "question": "Does \"saving\" print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. `onSave` is declared and never called, so the log inside it never runs. Silence here says \"not called\" — not \"nothing to say\".\n"
              }
            ],
            "hints": [
              {
                "when": "you expected quotes on the plain log of price",
                "say": "`console.log` prints text on its own without quotes — that is what makes it easy to read, and easy to be fooled by. Only inside an object or array, or through `JSON.stringify`, do the quotes show.\n"
              },
              {
                "when": "you expected saving to print",
                "say": "Find where `onSave` is called. A function's body runs when it is called, not when it is declared.\n"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Which explanation is standing?",
            "prompt": "Each item gives two explanations, a log line, and what it printed. Pick `A`, `B`, or `Both` if the line could not tell them apart.\n",
            "items": [
              {
                "id": "t1",
                "question": "`if (user.active)` lets a deactivated user in. A: `user.active` is the text \"false\". B: `user.active` is `false`, and a different branch lets them in. `console.log(user.active)` prints `false`.",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "Both"
                ],
                "miss": "**The text `\"false\"` prints as `false`.** So does the boolean. Same output under A and B.\n",
                "why": "Both. And it matters: `\"false\"` is a non-empty string, so it is truthy — A is a real possibility that this line cannot see.\n"
              },
              {
                "id": "t2",
                "question": "Same bug. `console.log(typeof user.active)` prints `string`.",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "A"
                ],
                "why": "A. Under B it would print `boolean`. The explanations disagreed about the type, so logging the type decides.\n"
              },
              {
                "id": "t3",
                "question": "Same bug. `console.log(\"active branch\")` is the first line inside the `if`. It prints nothing.",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "B"
                ],
                "miss": "**Under A, `\"false\"` is truthy, so the `if` body runs and the log prints.** Nothing printed, so the body never ran — they got in some other way.\n",
                "why": "B. The line sat where the two explanations disagree about whether it runs.\n"
              },
              {
                "id": "t4",
                "question": "`try { const cfg = parseConfig(text); console.log(\"parsed\", cfg); } catch (e) { }` — the program behaves as if there were no config. A: `parseConfig` threw. B: it returned `{}`. Nothing prints.",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "A"
                ],
                "miss": "**Under B, `parseConfig` returns and the next line runs: `parsed {}`.** Nothing printed, so the line was skipped — and a throw is what skips the rest of a `try`.\n",
                "why": "A. The throw jumped to the empty `catch`, which is also why there was no error message.\n"
              }
            ],
            "extra": [
              {
                "id": "t5",
                "question": "`scores.sort()` gives `[1, 10, 9]`. A: `sort` compares them as text. B: the array holds text, not numbers. `console.log(scores)` prints `[1,10,9]`.",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "A"
                ],
                "miss": "**Inside an array, text is quoted.** Under B the line would print `[\"1\",\"10\",\"9\"]`. There are no quotes, so they are numbers.\n",
                "why": "A. They are numbers — and `sort` with no argument still compares them as text, which is why `10` comes before `9`.\n"
              },
              {
                "id": "t6",
                "question": "Editing `first.tags` also changes `second.tags`. A: one tags array, two names. B: two arrays, and an update copies each change. `console.log(first.tags === second.tags)` prints `false`.",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "B"
                ],
                "why": "B. Two separate arrays. Now look for the code that copies the change.\n"
              },
              {
                "id": "t7",
                "question": "A: `code` is \"SAVE10\". B: `code` is \"SAVE10 \" with a trailing space. `console.log(code)` prints `SAVE10`.",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "Both"
                ],
                "why": "Both. A trailing space prints as nothing. `JSON.stringify(code)` would print `\"SAVE10 \"`, and the quote would show where the text ends.\n"
              }
            ],
            "hints": [
              {
                "when": "you answered from what you think the bug probably is",
                "say": "The question is only what this line's output rules out. Write what it prints under A and under B, and compare.\n"
              },
              {
                "when": "you read a printed false or 1 as a boolean or a number",
                "say": "A plain log prints text without quotes. Ask whether the output could have come from text that spells the same thing.\n"
              },
              {
                "when": "you thought nothing printing told you nothing",
                "say": "Ask which explanation says the line would never run. That is the one left standing.\n"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "One line that decides",
            "prompt": "Two panels show the same shopping cart, and adding an item in one adds it in the other. **A:** both panels hold one cart object. **B:** they hold two cart objects, and a sync step copies every change across. Fill in the one `console.log` so that its output differs between A and B. Then, in one sentence: why can printing both carts not decide?\n",
            "exportName": "inspectCarts",
            "filename": "inspect-carts.js",
            "starter": "// Called with the cart each panel holds.\nfunction inspectCarts(leftCart, rightCart) {\n  // ___ one console.log that tells A from B\n}\n",
            "tests": [
              {
                "label": "logs exactly one line",
                "body": "const real = console.log; let n = 0; console.log = function () { n++; }; const c = { items: [\"tea\"] }; try { subject(c, c); } finally { console.log = real; } return n === 1;"
              },
              {
                "label": "A (one cart) and B (two equal carts) print differently",
                "body": "const real = console.log; const out = []; const fmt = function (v) { return typeof v === 'string' ? v : v === undefined ? 'undefined' : JSON.stringify(v); }; console.log = function () { out.push(Array.prototype.map.call(arguments, fmt).join(' ')); }; let a, b; try {\n  const one = { items: [\"tea\", \"pen\"] };\n  subject(one, one); a = out.join('|'); out.length = 0;\n  subject({ items: [\"tea\", \"pen\"] }, { items: [\"tea\", \"pen\"] }); b = out.join('|');\n} finally { console.log = real; } return out.length > 0 && a !== b;"
              },
              {
                "label": "decides for a different cart too",
                "body": "const real = console.log; const out = []; const fmt = function (v) { return typeof v === 'string' ? v : v === undefined ? 'undefined' : JSON.stringify(v); }; console.log = function () { out.push(Array.prototype.map.call(arguments, fmt).join(' ')); }; let a, b; try {\n  const one = { items: [\"ink\"] };\n  subject(one, one); a = out.join('|'); out.length = 0;\n  subject({ items: [\"ink\"] }, { items: [\"ink\"] }); b = out.join('|');\n} finally { console.log = real; } return out.length > 0 && a !== b;"
              }
            ],
            "hints": [
              {
                "when": "A and B print the same",
                "say": "Write what your line prints under each. Both explanations say the two carts hold the same items — so anything about their contents prints the same. What do they disagree about?\n"
              },
              {
                "when": "you compared the carts with JSON.stringify",
                "say": "That compares contents, and the contents match under A and under B. The question is whether there is one cart or two.\n"
              },
              {
                "when": "logs exactly one line fails",
                "say": "The gap is one `console.log`. Check it is inside the function, and that there is only one.\n"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "A log that could not decide",
            "prompt": "`addShipping` returns `\"105\"` for some orders where it should return `15`. A colleague added a log, ran it, saw `10 5`, and was none the wiser. Change **only the log line**. First: make its output differ between **A** — `subtotal` is the text `\"10\"` — and **B** — `shipping` is the text `\"5\"`. Second: a third explanation fits the same report, **C** — both are text. Make the line tell all three apart. Once it passes: in one sentence, what did the three explanations disagree about that the old line did not print?\n",
            "exportName": "addShipping",
            "filename": "add-shipping.js",
            "starter": "function addShipping(order) {\n  console.log(order.subtotal, order.shipping);\n  return order.subtotal + order.shipping;\n}\n",
            "tests": [
              {
                "label": "still returns what it returned — only the log changes",
                "body": "const real = console.log; console.log = function () {}; let r1, r2, r3; try { r1 = subject({ subtotal: 10, shipping: 5 }); r2 = subject({ subtotal: \"10\", shipping: 5 }); r3 = subject({ subtotal: 10, shipping: \"5\" }); } finally { console.log = real; } return r1 === 15 && r2 === \"105\" && r3 === \"105\";"
              },
              {
                "label": "logs exactly one line per call",
                "body": "const real = console.log; let n = 0; console.log = function () { n++; }; try { subject({ subtotal: 10, shipping: 5 }); } finally { console.log = real; } return n === 1;"
              },
              {
                "label": "A and B print differently",
                "body": "const real = console.log; const out = []; const fmt = function (v) { return typeof v === 'string' ? v : v === undefined ? 'undefined' : JSON.stringify(v); }; console.log = function () { out.push(Array.prototype.map.call(arguments, fmt).join(' ')); }; let a, b; try {\n  subject({ subtotal: \"10\", shipping: 5 }); a = out.join('|'); out.length = 0;\n  subject({ subtotal: 10, shipping: \"5\" }); b = out.join('|');\n} finally { console.log = real; } return a !== '' && a !== b;"
              },
              {
                "label": "C prints differently from both A and B",
                "body": "const real = console.log; const out = []; const fmt = function (v) { return typeof v === 'string' ? v : v === undefined ? 'undefined' : JSON.stringify(v); }; console.log = function () { out.push(Array.prototype.map.call(arguments, fmt).join(' ')); }; let a, b, c; try {\n  subject({ subtotal: \"10\", shipping: 5 }); a = out.join('|'); out.length = 0;\n  subject({ subtotal: 10, shipping: \"5\" }); b = out.join('|'); out.length = 0;\n  subject({ subtotal: \"10\", shipping: \"5\" }); c = out.join('|');\n} finally { console.log = real; } return c !== '' && c !== a && c !== b;"
              }
            ],
            "hints": [
              {
                "when": "A and B print the same",
                "say": "A and B agree about the values — 10 and 5 — and disagree about something a plain log does not show. Write what your line prints under each.\n"
              },
              {
                "when": "A and B differ but C matches one of them",
                "say": "Your line looks at one of the two values. C differs from A only in the other one. The line has to show both.\n"
              },
              {
                "when": "you labelled the values and it still fails",
                "say": "A label says *which* value is which, not what kind it is. `\"subtotal: 10\"` reads the same for the number and the text.\n"
              },
              {
                "when": "still returns what it returned fails",
                "say": "The bug is not yours to fix here — only the log changes. Put the `return` line back as it was.\n"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review — four logs, one answer",
            "prompt": "Users report the coupon `SAVE10` gives no discount. A code assistant was asked to find out why, and added four logs. The two explanations on the table: **A** — the code passed in does not exactly match the stored code. **B** — the coupon is found, but its `amount` is `0`. It ran the function once with the user's input. Review the logs it added.\n",
            "filename": "apply-coupon.js",
            "code": "function applyCoupon(order, code) {\n  console.log(\"here\");\n  console.log(order);\n  const coupon = coupons.find(c => c.code === code);\n  console.log(code);\n  if (coupon) {\n    order.total = order.total - coupon.amount;\n    console.log(\"discount applied\");\n  }\n  return order.total;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "`console.log(\"here\")` printed `here`. Which explanation is standing — A, B, or both?",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "Both"
                ],
                "why": "Both. It says the function was called, which nobody doubted. A line whose output was never in question is not an experiment.\n"
              },
              {
                "id": "c2",
                "question": "`console.log(code)` printed `SAVE10`. Does that rule out A?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**A says the code does not *exactly* match.** `\"SAVE10 \"` with a trailing space, and `\"SAVE10\"`, both print `SAVE10`.\n",
                "why": "No. A plain log of text hides its edges. The line looks like it settles A and does not.\n"
              },
              {
                "id": "c3",
                "question": "What should that line log instead, so a stray space would show? Type the expression.",
                "accept": [
                  "json.stringify(code)",
                  "console.log(json.stringify(code))",
                  "json.stringify",
                  "{ code }",
                  "{code}",
                  "console.log({ code })"
                ],
                "why": "`JSON.stringify(code)` — it prints `\"SAVE10 \"`, and the closing quote shows where the text ends. `{ code }` works too.\n"
              },
              {
                "id": "c4",
                "question": "`console.log(\"discount applied\")` printed nothing. Which explanation is standing?",
                "choices": [
                  "A",
                  "B",
                  "Both"
                ],
                "accept": [
                  "A"
                ],
                "miss": "**Under B the coupon is found, so the `if` body runs and the line prints — even though the discount is `0`.** It did not print, so no coupon was found.\n",
                "why": "A. This was the deciding line all along — silence exactly where A and B disagree. The other three logs were noise around it.\n"
              },
              {
                "id": "c5",
                "question": "In a browser, you expand the object from `console.log(order)` after a run where a coupon worked. Which total do you see?",
                "choices": [
                  "Before the discount",
                  "After the discount"
                ],
                "accept": [
                  "After the discount"
                ],
                "miss": "**A browser reads a logged object when you expand it, not when it was logged.** Line 7 changed `order.total` after the log, so you see the changed total. Node prints it as it was.\n",
                "why": "After. In a browser, log `JSON.stringify(order)` or `order.total` when you need the value at that moment.\n"
              }
            ],
            "hints": [
              {
                "when": "you counted a log that printed something as evidence",
                "say": "Ask what it would have printed under the other explanation. If the same, it ruled nothing out.\n"
              },
              {
                "when": "you read the silent line as telling you nothing",
                "say": "Under which explanation does the `if` body run? The line inside it prints exactly when that is true.\n"
              },
              {
                "when": "you said before for the browser object",
                "say": "The collapsed preview is taken when the line runs; the expanded object is read when you click. The code changed it in between.\n"
              }
            ]
          }
        ]
      }
    },
    "where-to-handle": {
      "id": "where-to-handle",
      "title": "Where an error should be handled",
      "kind": "read",
      "outcome": 8,
      "shape": "conceptual",
      "why": "Defending where an error is handled rather than passed up is outcome 8's actual demand (`concepts.md`, `err.placement`), and a catch in the wrong place is how failures go silent. It builds on the two lessons before it: what kind of failure it is, and where a thrown one travels.",
      "concepts": [
        "err.placement"
      ],
      "lede": "A thrown error travels up the call stack until something catches it. Choosing where that happens is a design decision you should be able to defend — because the default, catching wherever it is convenient, is usually wrong.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Recoverable and unrecoverable failure · Throwing, and where it goes · Meet the debugger"
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Three questions about where a failure is dealt with. Pick the answer."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`catch (e) { console.log(e); }` — what has this done to the error?",
                          "choices": [
                            "Handled it",
                            "Swallowed it"
                          ],
                          "accept": [
                            "Swallowed it"
                          ],
                          "miss": "**If you counted logging as handling it — it records that something went wrong and then lets the program continue as though it had not.** If you cannot say what the program should *do* differently, you should not be catching there.\n",
                          "why": "Stopped it. The caller now believes the call succeeded, and the program carries on with whatever partial state it has. Logging is not handling.\n"
                        },
                        {
                          "question": "`priceOf(row)` calls `parsePrice`, catches any failure, and returns `0`. Right level, or too low?",
                          "choices": [
                            "Right level",
                            "Too low"
                          ],
                          "accept": [
                            "Too low"
                          ],
                          "miss": "**If you said right — `priceOf` cannot know what a bad price should mean.** Show a message? Skip the row? Abort the import? Returning `0` picks an answer nobody asked for.\n",
                          "why": "Too low. The code that knows what the price is *for* — a checkout, an import job — is the one that can decide. Here a failure turns into a plausible wrong number.\n"
                        },
                        {
                          "question": "`startup()` → `loadConfig()` → `parseConfig()`. The config file is missing. Which of the three should catch it?",
                          "choices": [
                            "startup",
                            "loadConfig",
                            "parseConfig"
                          ],
                          "accept": [
                            "startup"
                          ],
                          "miss": "**If you picked `parseConfig` or `loadConfig` — neither knows what the program should do without a config.** Only `startup` knows it is starting a program, and can stop with a message that says so.\n",
                          "why": "`startup`. The two below it have nothing sensible to do instead, so they let it travel to the level that does.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`except Exception: pass` — what has this done to the error?",
                          "choices": [
                            "Handled it",
                            "Swallowed it"
                          ],
                          "accept": [
                            "Swallowed it"
                          ],
                          "why": "The most destructive two lines in Python. Everything is caught, including bugs, and nothing is reported.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`catch (Exception) { }` — what is wrong with it?",
                          "choices": [
                            "Nothing — it handles every failure",
                            "It catches bugs too, and hides them"
                          ],
                          "accept": [
                            "It catches bugs too, and hides them"
                          ],
                          "why": "It catches programming errors alongside expected ones and reports nothing.\n"
                        },
                        {
                          "question": "When should you catch `Exception` (the base type)?",
                          "choices": [
                            "Wherever something might fail",
                            "Almost never — only at the top level"
                          ],
                          "accept": [
                            "Almost never — only at the top level"
                          ],
                          "why": "Effectively never below the top level. Catching everything catches the bugs too, and hides them.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can defend handling a failure *here* rather than passing it up",
                "I can say what a catch that only logs has actually done",
                "I can add context on the way past without losing the original error"
              ]
            }
          ]
        },
        {
          "h": "Handle it where you can do something about it"
        },
        {
          "p": "The rule is short and it does most of the work: **catch at the level that has\nenough context to act.**"
        },
        {
          "code": "function parsePrice(text) {\n  const n = Number(text);\n  if (text.trim() === \"\" || Number.isNaN(n)) throw new RangeError(`not a price: \"${text}\"`);\n  return n;\n}\n\n// too low — priceOf cannot know what the program should do with a bad price\nfunction priceOf(row) {\n  try { return parsePrice(row.price); }\n  catch (e) { return 0; }                 // invents an answer nobody asked for\n}\n\n// right — the import knows it is importing, and what a bad row means to it\nfunction importRow(row, report) {\n  try {\n    return { name: row.name, price: parsePrice(row.price) };\n  } catch (e) {\n    if (!(e instanceof RangeError)) throw e;   // not mine — send it on\n    report.push(`${row.name}: ${e.message}`);\n    return null;\n  }\n}",
          "lang": "js"
        },
        {
          "p": "`priceOf` does not know whether a bad price means \"show a message\", \"use the\nlast known value\" or \"skip this row\". The import does: it sets the row aside\nand writes down why. Catching low invents an answer and destroys the\ninformation needed to choose a better one."
        },
        {
          "p": "Look at what else `priceOf`'s catch received. If `row` is `undefined` because\nof a bug somewhere else, `row.price` throws a TypeError — and `priceOf` turns\nthat into `0` too. A catch receives *everything* thrown in its `try`."
        },
        {
          "block": "notice",
          "label": "Returning 0 is worse than crashing",
          "body": [
            {
              "p": "A caught error that produces a plausible wrong value is the most expensive\noutcome available. The program continues, the wrong number flows onward, and the\neventual symptom is nowhere near the cause. The silent aliasing bug the debugger\nlesson hunts is the same shape."
            }
          ]
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "A catch that only runs `console.log(e)` — what does the code after it believe about the call?",
                "choices": [
                  "That it failed",
                  "That it succeeded"
                ],
                "accept": [
                  "That it succeeded"
                ],
                "why": "That it succeeded. From outside, a caught error is invisible: the function returned, so the next line runs on whatever state is left.\n"
              },
              {
                "question": "In `priceOf`, `row` is `undefined` because of a bug elsewhere. What does `priceOf` return? Type the value, or error.",
                "accept": [
                  "0"
                ],
                "miss": "**If you expected a TypeError — `priceOf`'s catch receives that too.** A catch does not know which failures you had in mind; it gets all of them.\n",
                "why": "`0`. `row.price` throws a TypeError, and the catch turns a bug into a price.\n"
              }
            ]
          }
        },
        {
          "h": "Three places, and what each knows"
        },
        {
          "table": {
            "head": [
              "Level",
              "Knows",
              "Should"
            ],
            "rows": [
              [
                "**Where it happened**",
                "what failed, and why",
                "throw, with a specific message"
              ],
              [
                "**In between**",
                "that it called something",
                "usually nothing — or add context and rethrow"
              ],
              [
                "**The boundary**",
                "there is a user, a request, a job",
                "decide, report, recover"
              ]
            ]
          }
        },
        {
          "p": "Most code should do nothing at all. A function with no `try` is not missing error\nhandling; it is correctly letting failures travel to somewhere that can act. The\nboundary is where the program meets whatever it is for: the screen a person is\nlooking at, the job runner that writes tonight's report, `startup` for a program\nthat cannot start."
        },
        {
          "p": "Two worked placements. The subgoals are the same four each time."
        },
        {
          "code": "// A to-do app saves a list to storage. Storage can be full.\n// save(list) → writeStorage(key, text), called when the user clicks Save.\n\n// Name the failure and classify it:\n//   storage full — recoverable: the user can delete something and try again.\n// Find the lowest level that can act:\n//   writeStorage: knows nothing about users.  save: knows about lists.\n//   the Save-button handler: knows there is a person to tell. → here.\n// Catch there, and only this failure:\n//   if it is a storage-full error, show \"Storage is full — remove something\".\n// Send everything else on:\n//   any other error: throw e. A bug in save is not \"storage full\".",
          "lang": "js"
        },
        {
          "code": "// A report job reads 200 sensor files and averages them.\n// runReport() → readAll() → readSensor(file) → JSON.parse.\n// One file is corrupt.\n\n// The failure: one corrupt file — recoverable for the report: 199 are fine.\n// Who can act:\n//   JSON.parse and readSensor: know one file, not the report.\n//   readAll: knows it is gathering many — it can skip one and say which. → here.\n// Catch only a SyntaxError from one file: record the file name and move on.\n// Everything else goes on: a missing folder is not \"one bad file\".\n//   throw e, and let runReport fail.",
          "lang": "js"
        },
        {
          "p": "In the second, the level is **not** the top. `runReport` could catch too, but by\nthen the loop is over: it can only fail the whole report. The level that can\nact is the one that can still do something *instead*."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In the report job, a bug makes `readSensor` throw a TypeError on every file. Should `readAll` skip it like the corrupt file?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you said yes — \"skip a bad file\" is a plan for one corrupt file, not for a bug.** Skipping would produce a report of zero files that looks like it worked.\n",
                "why": "No. `readAll` has a plan for one kind of failure. Everything else goes on up, where it stops the report loudly.\n"
              },
              {
                "question": "A function has no `try` at all. Is its error handling missing?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No — if it has nothing sensible to do instead, letting failures travel to a level that does is the correct handling.\n"
              }
            ]
          }
        },
        {
          "h": "Adding context on the way past"
        },
        {
          "p": "Sometimes a middle level knows one thing the low level did not — *which file*,\n*which customer* — and the error would be much more useful with it. Then it\ncatches, adds that, and throws a new error that keeps the original as its\n`cause`:"
        },
        {
          "code": "try {\n  return JSON.parse(readFileSync(path, \"utf8\"));   // readFileSync is Node's\n} catch (e) {\n  throw new Error(`could not load config from ${path}`, { cause: e });\n}",
          "lang": "js"
        },
        {
          "p": "This catch is not handling anything. It adds one fact and sends the failure on.\nThe message now says what was being attempted, and the trace still shows the\nparse failure underneath."
        },
        {
          "p": "The same idea done carelessly destroys what it meant to improve:"
        },
        {
          "code": "catch (e) { throw new Error(\"failed\"); }    // the cause is gone forever",
          "lang": "js"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`catch (e) { throw new Error(\"failed\"); }` — what is lost?",
                "choices": [
                  "Nothing",
                  "The message",
                  "The original error"
                ],
                "accept": [
                  "The original error"
                ],
                "why": "Everything about what actually failed. `{ cause: e }` is the one addition that keeps it.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Deciding whether to catch here",
            "body": [
              {
                "h": "Deciding whether to catch here"
              },
              {
                "code": "// You are about to write a try/catch. Answer before you write it.\n\n// 1. What will you DO in the catch?\n//    \"log it\" / \"nothing\" -> you are hiding it. Do not catch here.\n//    \"show a message\", \"retry\", \"use the default\", \"skip and report\" -> handling.\n\n// 2. Does this level have the context to make that decision?\n//    no  -> let it travel. Somebody above knows more than you do.\n//    yes -> catch here.\n\n// 3. Can you tell the failure you are handling from every other?\n//    no  -> check its type, and throw e for the rest.\n\n// 4. Are you only adding context on the way past?\n//    then rethrow WITH { cause: e }. Never a bare new Error.",
                "lang": "js",
                "file": "should-i-catch.js"
              },
              {
                "p": "Now apply it."
              },
              {
                "code": "// startup() -> loadConfig() -> parseConfig().\n// The file is missing.\n// 1. Should parseConfig catch it?        ______\n// 2. Should startup catch it?            ______\n// 3. What should startup DO?             ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "No — `parseConfig` cannot decide what a missing config means. Yes — `startup`\ncan. And what it should do is stop with a clear message, because a missing\nconfig is unrecoverable — there is no sensible alternative — and continuing\nwith defaults would hide a broken deployment."
              }
            ]
          }
        },
        {
          "video": {
            "title": "One throw, four frames",
            "caption": "A failure travelling up a four-deep stack, with the same error caught at each level in turn — showing what information is available and what is destroyed at each choice.\n"
          }
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Catch at boundaries — the handler for a button, a request, a job. Everywhere\nelse, let it travel, or add context and rethrow."
              },
              {
                "code": "try { risky(); }\ncatch (e) {\n  if (!(e instanceof KnownError)) throw e;   // rethrow what is not yours\n  recover();\n}",
                "lang": "js"
              },
              {
                "p": "That rethrow is the important line. A catch that handles one case must rethrow\nthe rest, or it becomes the place bugs go to die."
              }
            ],
            "python": [
              {
                "p": "Narrow `except` clauses, and `raise ... from e` for context."
              },
              {
                "code": "try:\n    load()\nexcept FileNotFoundError:\n    return fallback()\nexcept Exception as e:\n    raise ConfigError(\"could not load config\") from e",
                "lang": "python"
              },
              {
                "p": "A bare `except:` catches `KeyboardInterrupt` and `SystemExit` too — never write\none."
              }
            ],
            "csharp": [
              {
                "p": "Exception filters to be precise, and bare `throw;` to preserve the trace."
              },
              {
                "code": "try { Risky(); }\ncatch (HttpRequestException e) when (e.StatusCode == HttpStatusCode.NotFound) { Recover(); }\ncatch { throw; }                              // preserve the original trace",
                "lang": "csharp"
              },
              {
                "p": "`throw;` rather than `throw e;` — the second resets the stack trace to that line\nand throws away where the failure actually happened."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening three again from memory. Then say what information\n`catch (e) { throw new Error(\"failed\"); }` destroys, and what one addition to\nthat line would keep it."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A nightly import job processes 10,000 records. One record has a malformed date."
            },
            {
              "p": "Three designs:"
            },
            {
              "list": [
                "**Fail the whole job** — the data is wrong and someone should know tonight.",
                "**Skip the record and continue**, collecting failures into a report.",
                "**Skip silently** and log."
              ]
            },
            {
              "p": "Pick one and say at which level its `catch` sits — the date parser, the\nper-record function, or the job. Then say what each costs: what the first does\nto the 9,999 good records, what the second requires of whoever reads the\nreport, and what the third guarantees about nobody noticing for six months. Say\nwhat would change your answer if the malformed record were a payment rather\nthan a date — and connect that back to \"does the caller have a sensible\nalternative action?\""
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Catch where there is enough context to act** — often a boundary, sometimes a middle level that can still do something instead. Everywhere else, let it travel.",
                "A catch that only logs has **swallowed** the error. A catch that handles one failure must **rethrow the rest**.",
                "Adding context means **rethrowing with `cause`**, never a bare `new Error(\"failed\")`."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "try...catch",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/try...catch",
              "note": "including the conditional catch pattern."
            },
            {
              "title": "Error.prototype.cause",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause",
              "note": "the one addition that keeps the original failure."
            }
          ]
        }
      ],
      "_source": "lessons/where-to-handle.md",
      "practice": {
        "id": "where-to-handle",
        "outcome": 8,
        "steps": [
          {
            "kind": "predict",
            "title": "Which level should decide?",
            "prompt": "Pick the answer.",
            "items": [
              {
                "id": "p1",
                "question": "`priceOf(row)` catches its own failure and returns `0`. Is that the right level, or too low?",
                "choices": [
                  "Right level",
                  "Too low"
                ],
                "accept": [
                  "Too low"
                ],
                "miss": "If you said right: priceOf cannot know whether a bad price means show a message, keep the last value, or skip the row. It has invented an answer.",
                "why": "Too low. The level that knows what the price is for — a checkout, an import job — is the one that should decide."
              },
              {
                "id": "p2",
                "question": "`catch (e) { console.log(e); }` — what has this done to the error?",
                "choices": [
                  "Handled it",
                  "Swallowed it"
                ],
                "accept": [
                  "Swallowed it"
                ],
                "why": "Stopped it. The caller now believes the call succeeded and carries on with partial state. Logging is not handling."
              },
              {
                "id": "p3",
                "question": "`onSaveClick()` → `saveNote()` → `writeStorage()`. Storage is full, and the user could delete something and try again. Which function should catch it?",
                "choices": [
                  "onSaveClick",
                  "saveNote",
                  "writeStorage"
                ],
                "accept": [
                  "onSaveClick"
                ],
                "miss": "If you picked writeStorage or saveNote: neither knows there is a person to tell. The alternative action — \"delete something and try again\" — needs someone to show it to.",
                "why": "`onSaveClick`. It is the only level that knows there is a user, which is what the recovery needs."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "`catch (e) { throw new Error(\"failed\"); }` — what is lost?",
                "choices": [
                  "Nothing",
                  "The message",
                  "The original error"
                ],
                "accept": [
                  "The original error"
                ],
                "why": "Everything about what actually failed. `{ cause: e }` is the one addition that keeps it."
              },
              {
                "id": "p5",
                "question": "`catch (e) { if (e instanceof NotFound) showMissing(); }` — a TypeError arrives. What happens to it?",
                "choices": [
                  "It travels on to the caller",
                  "It is swallowed"
                ],
                "accept": [
                  "It is swallowed"
                ],
                "why": "It is swallowed. The `if` is false, the catch ends, and the bug vanishes. A catch that handles one case must `throw e` for the rest."
              },
              {
                "id": "p6",
                "question": "Should most functions have a `try`/`catch`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. Most functions have nothing sensible to do instead, so letting failures travel is their correct handling."
              }
            ],
            "hints": [
              {
                "when": "you picked the level where the failure happened",
                "say": "Ask what each level knows. The one that throws knows what failed; only a higher one knows what the program was trying to do, and so what to do instead."
              },
              {
                "when": "you counted a catch as handling because it printed something",
                "say": "Ask what the program does differently because of that catch. If the answer is \"nothing, but it printed something\", the error was swallowed."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Follow the invented answer",
            "prompt": "`priceOf(row)` is `try { return parsePrice(row.price); } catch (e) { return 0; }`, and `parsePrice` throws a RangeError for text that is not a number. A till adds up `priceOf` for every row. Trace what it produces.\n",
            "items": [
              {
                "id": "t1",
                "question": "The rows are `{ price: \"2\" }` and `{ price: \"abc\" }`. What is the total? Type the value, or error.",
                "accept": [
                  "2"
                ],
                "why": "`2`. The bad row threw inside `priceOf`, was caught, and became `0`. The till has no idea anything went wrong."
              },
              {
                "id": "t2",
                "question": "Does anything tell the cashier that a price was bad?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The failure was turned into a number at the lowest level, so nothing above it can report it."
              },
              {
                "id": "t3",
                "question": "The rows are `{ price: \"2\" }` and `undefined` (a bug elsewhere). What is the total? Type the value, or error.",
                "accept": [
                  "2"
                ],
                "miss": "If you expected a TypeError to stop the till: `row.price` throws inside the `try`, and the catch receives it like any other failure.",
                "why": "`2` again. The TypeError from reading `.price` of `undefined` was caught too, and a bug became a free item."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "`priceOf` is changed to have no `try` at all. The rows are `{ price: \"2\" }` and `{ price: \"abc\" }`. Does the till get a total?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No — the RangeError travels up to the till, which now has to decide. That is the point: the till knows there is a cashier to tell."
              },
              {
                "id": "t5",
                "question": "Which is easier to find later?",
                "choices": [
                  "A total that is wrong by one item",
                  "A RangeError naming \"abc\""
                ],
                "accept": [
                  "A RangeError naming \"abc\""
                ],
                "why": "The RangeError. It names the value and the line. The wrong total names nothing, and may not be noticed for weeks."
              }
            ],
            "hints": [
              {
                "when": "you expected the bad row to stop the total",
                "say": "Look at what `priceOf` does in its catch. Whatever arrives there leaves as a number."
              },
              {
                "when": "you expected the bug to be reported",
                "say": "Which failures does `priceOf`'s catch receive? All of them — it does not look at `e` at all."
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Catch only what you can handle",
            "prompt": "Preferences are optional: a missing file just means the user never changed anything, so use the defaults. Fill the gap in the `catch`. Handle **only** a missing file; every other failure — no permission, a bug — is not this function's to decide, and must reach the caller unchanged. Then, in one sentence: why is a missing file recoverable here?\n",
            "exportName": "readPrefs",
            "filename": "prefs.js",
            "starter": "// read(path) returns the file's text, or throws.\n// A missing file throws an error whose .code is \"ENOENT\".\nfunction readPrefs(path, read, defaults) {\n  try {\n    return read(path);\n  } catch (e) {\n    // ___ fill the gap: defaults for a missing file,\n    //     and let every other error through, unchanged.\n  }\n}\n",
            "tests": [
              {
                "label": "an existing file is returned",
                "body": "return subject('/p.json', function () { return 'saved'; }, 'dflt') === 'saved';"
              },
              {
                "label": "a missing file gives the defaults",
                "body": "const missing = Object.assign(new Error('no such file'), { code: 'ENOENT' }); return subject('/p.json', function () { throw missing; }, 'dflt') === 'dflt';"
              },
              {
                "label": "a permission error reaches the caller",
                "body": "const denied = Object.assign(new Error('permission denied'), { code: 'EACCES' }); try { subject('/p.json', function () { throw denied; }, 'dflt'); return false; } catch (e) { return e === denied; }"
              },
              {
                "label": "a bug reaches the caller, unchanged",
                "body": "const bug = new TypeError('read is broken'); try { subject('/p.json', function () { throw bug; }, 'dflt'); return false; } catch (e) { return e === bug; }"
              }
            ],
            "hints": [
              {
                "when": "every failure gives the defaults",
                "say": "That catch is handling failures it knows nothing about. Check which failure this is before deciding what to do."
              },
              {
                "when": "the other errors disappear instead of reaching the caller",
                "say": "Handling one case is not enough — a catch that does not rethrow the rest has swallowed them. What does the `catch` do when the code is not \"ENOENT\"?"
              },
              {
                "when": "the caller gets an error, but not the same one",
                "say": "Rethrow the error you caught, not a new one. A new Error without the original loses what actually went wrong."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Move the decision up",
            "prompt": "`priceOf` catches too low: a bad price becomes `0` and is imported as free. Make two changes. First, `priceOf` stops catching — let the failure travel. Second, `importRows` decides: a row whose price is bad goes into `rejected` as `{ name, reason }`, where `reason` is the error's message, and is left out of `accepted`. Anything that is not a bad price — a bug, such as a row that is `null` — must still reach the caller.\n",
            "exportName": "importRows",
            "filename": "import-rows.js",
            "starter": "function parsePrice(text) {\n  const n = Number(text);\n  if (text.trim() === \"\" || Number.isNaN(n)) throw new RangeError(`not a price: \"${text}\"`);\n  return n;\n}\n\nfunction priceOf(row) {\n  try { return parsePrice(row.price); }\n  catch (e) { return 0; }\n}\n\n// The import knows what a bad row means: set it aside, and say why.\nfunction importRows(rows) {\n  const accepted = [], rejected = [];\n  for (const row of rows) {\n    accepted.push({ name: row.name, price: priceOf(row) });\n  }\n  return { accepted, rejected };\n}\n",
            "tests": [
              {
                "label": "good rows are accepted with their prices",
                "body": "const r = subject([{ name: 'pen', price: '2' }, { name: 'ink', price: '3.5' }]); return r.accepted.length === 2 && r.accepted[1].price === 3.5 && r.rejected.length === 0;"
              },
              {
                "label": "a bad price is not imported",
                "body": "const r = subject([{ name: 'pen', price: '2' }, { name: 'ink', price: 'abc' }]); return r.accepted.length === 1 && r.accepted[0].name === 'pen';"
              },
              {
                "label": "a bad price is rejected with the reason",
                "body": "const r = subject([{ name: 'ink', price: 'abc' }]); return r.rejected.length === 1 && r.rejected[0].name === 'ink'\n  && r.rejected[0].reason.indexOf('abc') > -1;"
              },
              {
                "label": "the rows after a bad one are still imported",
                "body": "const r = subject([{ name: 'ink', price: '' }, { name: 'pad', price: '4' }]); return r.accepted.length === 1 && r.accepted[0].name === 'pad' && r.rejected.length === 1;"
              },
              {
                "label": "a bug still reaches the caller",
                "body": "try { subject([{ name: 'pen', price: '2' }, null]); return false; } catch (e) { return e instanceof TypeError; }"
              }
            ],
            "hints": [
              {
                "when": "the bad price is still accepted as 0",
                "say": "`priceOf` still has its catch, so `importRows` never sees a failure. Remove the handling from the level that cannot decide."
              },
              {
                "when": "the whole import stops at the bad row",
                "say": "The failure now reaches `importRows`, and nothing there catches it. Catch around the one row, so the loop can carry on."
              },
              {
                "when": "the null row ends up in rejected",
                "say": "Your catch handles every failure as a bad price. Check which kind of error it is — `parsePrice` throws a RangeError — and `throw e` for anything else."
              },
              {
                "when": "the reason test fails",
                "say": "The reason is already written: it is the message of the error you caught."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a config loader",
            "prompt": "A colleague's AI assistant wrote this. `loadConfig` is called once, at startup, and the app cannot run without its config. Review it, then fix it.\n",
            "filename": "config.js",
            "code": "function loadConfig(path, read, parse) {\n  try {\n    return parse(read(path));\n  } catch (error) {\n    if (error.code === \"ENOENT\") {\n      console.log(\"Config file not found, using defaults\");\n      return {};\n    }\n    throw new Error(\"Failed to load config\");\n  }\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line throws away what actually went wrong? Give the line number.",
                "accept": [
                  "9",
                  "line 9"
                ],
                "why": "Line 9 builds a new Error from nothing. The original — the parse error, the permission failure — is gone, and the message does not say which file."
              },
              {
                "id": "c2",
                "question": "Which line invents an answer nobody asked for? Give the line number.",
                "accept": [
                  "7",
                  "line 7",
                  "6",
                  "line 6"
                ],
                "why": "Lines 6–7 turn a missing config into an empty one and carry on. Here that is unrecoverable — the deploy is broken — and continuing on `{}` hides it until something far away fails."
              },
              {
                "id": "c3",
                "question": "The prefs step used defaults for a missing file. Is a missing config at startup recoverable too?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "The failure is identical; the caller is not. Missing preferences have a sensible alternative (defaults). A missing config at startup has none — the question is always about the caller."
              }
            ],
            "starter": "function loadConfig(path, read, parse) {\n  try {\n    return parse(read(path));\n  } catch (error) {\n    if (error.code === \"ENOENT\") {\n      console.log(\"Config file not found, using defaults\");\n      return {};\n    }\n    throw new Error(\"Failed to load config\");\n  }\n}\n",
            "exportName": "loadConfig",
            "tests": [
              {
                "label": "returns the parsed object on success",
                "body": "return subject('/a.json', function () { return '{}'; },\n               function () { return { ok: 1 }; }).ok === 1;"
              },
              {
                "label": "a missing file is not turned into an empty config",
                "body": "const missing = Object.assign(new Error('ENOENT'), { code: 'ENOENT' }); try { subject('/m.json', function () { throw missing; }, function () { return {}; }); return false; } catch (e) { return true; }"
              },
              {
                "label": "the failure mentions the path",
                "body": "try { subject('/missing.json', function () { throw new Error('EACCES'); },\n              function () { return {}; }); return false; }\ncatch (e) { return e.message.indexOf('/missing.json') > -1; }"
              },
              {
                "label": "a read failure keeps the original as cause",
                "body": "const orig = Object.assign(new Error('ENOENT'), { code: 'ENOENT' }); try { subject('/m.json', function () { throw orig; }, function () { return {}; }); return false; } catch (e) { return e.cause === orig; }"
              },
              {
                "label": "a parse failure keeps the original as cause",
                "body": "const orig = new SyntaxError('bad'); try { subject('/a.json', function () { return 'x'; }, function () { throw orig; }); return false; } catch (e) { return e.cause === orig && e instanceof Error; }"
              }
            ],
            "hints": [
              {
                "when": "you are not sure line 7 is a defect",
                "say": "Ask whether startup has a sensible alternative to a missing config. Running with no settings is not one; it is a broken deployment that looks like a working one."
              },
              {
                "when": "the cause test fails",
                "say": "You built a new Error without passing the original. The second argument to `new Error(message, …)` is where it goes."
              },
              {
                "when": "the path is missing from the message",
                "say": "The context you add on the way past is the one thing the lower level did not know: which file."
              }
            ]
          }
        ]
      }
    },
    "error-as-value": {
      "id": "error-as-value",
      "title": "Returning a failure instead of throwing",
      "kind": "read",
      "outcome": 8,
      "shape": "conceptual",
      "why": "Outcome 8's design choice has two shapes: a failure can interrupt, or it can be an ordinary value the caller must look at (`concepts.md`, `err.as-value`). JavaScript leaves the choice to the author, so it has to be made on purpose.",
      "concepts": [
        "err.as-value"
      ],
      "lede": "A throw cannot be ignored — it goes up until something catches it. A returned failure is just a value, and the caller decides whether to look. That one difference decides which to use, and the deciding question is how often the failure happens.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "When there is nothing there · Recoverable and unrecoverable failure · Where an error should be handled"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Result object** — a returned value that says whether the call worked: `{ ok: true, value }` or `{ ok: false, error }`.",
          "entries": [
            "**Result object** — a returned value that says whether the call worked: `{ ok: true, value }` or `{ ok: false, error }`."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about failures that are *returned*, not thrown. A guess is fine.\nWhere there is a box, type the value — or `error` if it throws."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const users = [{ id: 1, name: \"Ada\" }];   users.find(u => u.id === 9).name",
                          "accept": [
                            "error",
                            "typeerror",
                            "type error",
                            "throws",
                            "it throws",
                            "an error"
                          ],
                          "miss": "**If you answered `undefined` — `find` returned its failure as a value, and nobody looked.** \"No match\" came back as `undefined`, and `.name` on it throws — here, one step later, not where the search failed.\n",
                          "why": "A TypeError. `find` reports \"not found\" by returning `undefined`, which nothing checked before `.name` used it.\n"
                        },
                        {
                          "question": "`parseQuantity(\"abc\")` returns `{ ok: false, error: \"not a number\" }`. After `let total = 10;   total += parseQuantity(\"abc\").value;` what is `total`?",
                          "accept": [
                            "NaN",
                            "nan"
                          ],
                          "miss": "**If you expected an error — nothing here throws.** `.value` on the failure object is `undefined`, and `10 + undefined` is `NaN`. The failure was right there, and the code walked past it.\n",
                          "why": "`NaN`, silently. A returned failure can be ignored, and ignoring it does not stop anything.\n"
                        },
                        {
                          "question": "`findUser(id)` comes back empty on about half its calls. Throw, or return?",
                          "choices": [
                            "Throw",
                            "Return"
                          ],
                          "accept": [
                            "Return"
                          ],
                          "miss": "**If you chose throw — count the calls.** A failure on half of them is part of the function's normal output, not an exception to it, and every caller would need a `try` around an ordinary answer.\n",
                          "why": "Return. Frequency is the practical test: when a miss is ordinary, it belongs in the return value.\n"
                        },
                        {
                          "question": "A function throws on failure, and a caller forgets the `try`. What happens when it fails?",
                          "choices": [
                            "The failure is ignored",
                            "The error travels up and stops the program"
                          ],
                          "accept": [
                            "The error travels up and stops the program"
                          ],
                          "why": "The error travels up until something catches it — and if nothing does, the program stops with a trace. Loud, and hard to miss.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can compare returning a failure with throwing one: who can ignore it, and what each costs a caller",
                "I can choose between them for a given function, and defend the choice by how often it fails"
              ]
            }
          ]
        },
        {
          "h": "Two ways to report the same failure"
        },
        {
          "p": "**Throwing** interrupts. The function stops, and the error travels up until\nsomething catches it."
        },
        {
          "code": "function parsePrice(text) {\n  const n = Number(text);\n  if (text.trim() === \"\" || Number.isNaN(n)) throw new RangeError(`not a price: \"${text}\"`);\n  return n;\n}",
          "lang": "js"
        },
        {
          "p": "**Returning** makes the failure an ordinary value the caller has to look at."
        },
        {
          "code": "function parsePrice(text) {\n  const n = Number(text);\n  if (text.trim() === \"\" || Number.isNaN(n)) return { ok: false, error: `not a price: \"${text}\"` };\n  return { ok: true, value: n };\n}",
          "lang": "js"
        },
        {
          "p": "Same check, same message. What changes is who has to do what:"
        },
        {
          "table": {
            "head": [
              "",
              "Throwing",
              "Returning"
            ],
            "rows": [
              [
                "A caller who forgets to deal with it",
                "the error travels up — **loud**",
                "the program carries on — **silent**"
              ],
              [
                "A caller who does not care this time",
                "still has to catch, or let it travel",
                "can ignore it"
              ],
              [
                "Fits a `map` over many inputs",
                "one failure stops the whole `map`",
                "each element carries its own result"
              ],
              [
                "Where the failure is visible",
                "in the `catch`, away from the call",
                "in the value, at the call"
              ]
            ]
          }
        },
        {
          "p": "The first row is the trade in one line. **A thrown failure cannot be ignored;\na returned one can.**"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`[\"3\", \"x\", \"5\"].map(parsePrice)` with the throwing version — how many prices do you get? Give a number, or error.",
                "accept": [
                  "none",
                  "0",
                  "zero",
                  "no prices",
                  "it throws",
                  "none — it throws",
                  "error",
                  "an error"
                ],
                "miss": "**If you answered 2 — the throw on `\"x\"` does not skip one element; it leaves `map` altogether.** Nothing comes back, not even the good ones.\n",
                "why": "None. `\"x\"` throws inside the `map`, and the whole `map` is abandoned.\n"
              },
              {
                "question": "Same, with the returning version. How many results come back? Give a number.",
                "accept": [
                  "3",
                  "three"
                ],
                "why": "Three — two with `ok: true` and one with `ok: false`. Each element carries its own answer, and the caller can filter them.\n"
              }
            ]
          }
        },
        {
          "h": "The weakest return: nothing"
        },
        {
          "p": "You have already met the simplest returned failure. `find` reports \"no match\"\nas `undefined`; `Map.get` does the same for a missing key; a regular-expression\nmatch that fails returns `null`. Each is a function returning its failure as a\nvalue — an absence — rather than throwing."
        },
        {
          "p": "It is the weakest form, for two reasons."
        },
        {
          "list": [
            "**It carries no reason.** `undefined` says *that* there is nothing, never *why*. A lookup that can fail three ways cannot tell the caller which.",
            "**It can collide with a real answer.** If `null` is a legitimate value — a field that is allowed to be empty — then \"not found\" and \"found, and it is `null`\" look identical."
          ]
        },
        {
          "p": "A **result object** fixes both: `ok` says which case you are in, `error` says\nwhy, and `value` holds the answer, whatever it is."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`prices.get(sku)` returns `undefined`. Can you tell \"unknown SKU\" from \"discontinued\"?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. `undefined` has one meaning — nothing here. A result object with an `error` could say which.\n"
              }
            ]
          }
        },
        {
          "h": "Choosing, worked"
        },
        {
          "p": "Two worked choices. The subgoals are the same each time."
        },
        {
          "p": "A quantity field on an order form. People type all sorts of things into it."
        },
        {
          "code": "// Ask how often it fails:\n//   often — every mistyped field. It is part of the normal output.\n// Choose the shape:\n//   return a result: the form will look at every one, and show the reason.\nfunction parseQuantity(text) {\n  const n = Number(text);\n  // Make every path return that shape:\n  if (text.trim() === \"\") return { ok: false, error: \"enter a quantity\" };\n  if (!Number.isInteger(n) || n < 1) return { ok: false, error: `not a quantity: \"${text}\"` };\n  return { ok: true, value: n };\n}\n\n// Check before using the value:\nconst r = parseQuantity(input);\nif (!r.ok) showMessage(r.error);\nelse addToCart(item, r.value);",
          "lang": "js",
          "file": "quantity.js"
        },
        {
          "p": "A program reads its own settings file when it starts."
        },
        {
          "code": "// Fails almost never — only when the install is broken.\n// So throw: no caller has a sensible alternative, and none should have to\n//   check a result that is fine every day. Forgetting must be loud.\nfunction loadSettings(text) {\n  return JSON.parse(text);     // already throws on bad input — let it\n}",
          "lang": "js",
          "file": "settings.js"
        },
        {
          "p": "The rule of thumb: **throw for the unexpected, return for the expected.** If a\nfailure happens on a large share of calls, it is part of the function's normal\noutput and belongs in the return value. If it means something is broken, throw,\nso that no caller can carry on by forgetting to look."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`getTaxRate(country)` is called at checkout. A missing rate means the rate table shipped broken. Throw, or return?",
                "choices": [
                  "Throw",
                  "Return"
                ],
                "accept": [
                  "Throw"
                ],
                "miss": "**If you chose return — ask what a checkout that forgot to check would do.** Charge the wrong tax, silently. A broken table should be loud.\n",
                "why": "Throw. It is rare, it means something is broken, and a caller that forgot to check would charge the wrong amount without a word.\n"
              },
              {
                "question": "`isValidEmail(text)` is run on every sign-up form. Most failures are typos. Throw, or return?",
                "choices": [
                  "Throw",
                  "Return"
                ],
                "accept": [
                  "Return"
                ],
                "why": "Return. A typo is the ordinary case, and the form looks at every answer.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Choosing throw or return",
            "body": [
              {
                "h": "Choosing throw or return"
              },
              {
                "code": "// A function can fail. Decide how it tells its caller.\n\n// 1. How often does it fail?\n//    rarely, and only when something is broken -> throw.\n//    often, as part of normal use              -> return it.\n\n// 2. If you return it: can \"nothing\" be a real answer?\n//    yes, or the caller needs the reason -> a result object { ok, value, error }.\n//    no, and there is only one way to fail -> undefined or null is enough.\n\n// 3. Every path must give the chosen shape. A function that returns\n//    results must never throw for bad input — including input you did\n//    not think of.\n\n// 4. Every caller of a returning function checks before it uses the value.",
                "lang": "js",
                "file": "choose.js"
              },
              {
                "p": "Now apply it."
              },
              {
                "code": "// a) lookupWord(word) in a spell-checker — most words typed are in the dictionary,\n//    but plenty are not.\n// b) connectToDatabase() at server start — the database is always meant to be up.\n\n// a: throw or return?  ______   what shape?  ______\n// b: throw or return?  ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`a` returns — a missing word is the point of a spell-checker. `undefined` is\nenough if the only answer is \"not in the dictionary\"; a result object if the\ncaller needs to know why. `b` throws: a server that cannot reach its database\nhas nothing sensible to do but stop, loudly."
              }
            ]
          }
        },
        {
          "video": {
            "title": "The same error, handled three ways",
            "caption": "One failing call handled by throwing, by returning a result object, and by catching too broadly — with the stack trace shown in each so you can see what the third one destroys.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **how does the language report a failure the caller should expect?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Expected failure",
                  "Unexpected"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "throw or return — your choice",
                    "throw"
                  ],
                  [
                    "**C#**",
                    "the `TryParse` pattern — return a bool",
                    "throw"
                  ]
                ]
              }
            },
            {
              "p": "C# ships both shapes for the same job: `int.Parse` throws, `int.TryParse`\nreturns whether it worked. The pairing exists because both are right, for\ndifferent callers. JavaScript gives you only one parse per job and leaves the\nchoice to whoever writes the function — which is why it is worth making on\npurpose. (Some statically typed languages return a result type that the compiler\nwill not let a caller ignore. That is a later course.)"
            }
          ]
        },
        {
          "tracks": {
            "csharp": [
              {
                "p": "`Try...` methods where the failure is ordinary, exceptions where it is not."
              },
              {
                "code": "if (!int.TryParse(input, out var n)) return BadRequest();   // expected\nvar order = Load(id) ?? throw new NotFoundException(id);     // unexpected",
                "lang": "csharp"
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say, in one line, which of the\ntwo shapes can be ignored by a caller, and why that is sometimes exactly what\nyou want."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "`findUser(id)` is called from forty places. Sometimes the user does not exist."
            },
            {
              "p": "Three designs:"
            },
            {
              "list": [
                "**Return `null`** — callers check.",
                "**Throw `NotFound`** — callers that care catch it.",
                "**Return a result object** — `{ ok, value, error }`."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs across forty call sites: which\none can be ignored silently, which one forces every caller to write something\neven when they do not care, and which produces the clearest failure when a\ncaller forgets. Say whether your answer changes if \"not found\" happens on half\nthe calls rather than one in a thousand — and why that is the frequency\nquestion again."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A **thrown** failure cannot be ignored; a **returned** one can. That is the trade in one line.",
                "**Throw for the unexpected, return for the expected.** How often it fails is the practical test.",
                "`null` and `undefined` are the weakest returned failure: no reason, and they can collide with a real answer. A **result object** carries both."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array.prototype.find()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/find",
              "note": "a built-in that returns its failure as `undefined`."
            },
            {
              "title": "throw",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/throw",
              "note": "the other shape."
            }
          ]
        }
      ],
      "_source": "lessons/error-as-value.md",
      "practice": {
        "id": "error-as-value",
        "outcome": 8,
        "steps": [
          {
            "kind": "predict",
            "title": "Throw, return, and who looks",
            "prompt": "Pick the answer, or where there is a box, type the value — or `error` if it throws.",
            "items": [
              {
                "id": "p1",
                "question": "`findUser(id)` comes back empty on about half its calls. Throw, or return?",
                "choices": [
                  "Throw",
                  "Return"
                ],
                "accept": [
                  "Return"
                ],
                "miss": "If you chose throw, count the calls. A failure on half of them is part of the function's normal output, not an exception to it.",
                "why": "Frequency is the practical test. When a miss is ordinary, it belongs in the return value, where the caller looks at it every time."
              },
              {
                "id": "p2",
                "question": "A returned `{ ok: false }` that the caller never checks — what happens?",
                "choices": [
                  "The program stops",
                  "Nothing — it is ignored"
                ],
                "accept": [
                  "Nothing — it is ignored"
                ],
                "miss": "If you expected an error: a result object is just an object. Nothing about it stops the program.",
                "why": "Nothing, silently, and the program carries on. That is the cost of returning: a thrown failure cannot be ignored, a returned one can."
              },
              {
                "id": "p3",
                "question": "`[\"2\", \"x\", \"5\"].map(parse)`, where `parse` throws on `\"x\"`. How many results come back? Give a number, or error.",
                "accept": [
                  "none",
                  "0",
                  "zero",
                  "no results",
                  "it throws",
                  "none — it throws",
                  "error",
                  "an error"
                ],
                "why": "None. The throw on `\"x\"` leaves the `map` entirely; the good ones are lost with it."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "A required environment variable is missing at startup. Throw, or return?",
                "choices": [
                  "Throw",
                  "Return"
                ],
                "accept": [
                  "Throw"
                ],
                "why": "Rare, and there is no sensible alternative. Throw early and loudly rather than return something every caller must remember to check."
              },
              {
                "id": "p5",
                "code": "const r = { ok: false, error: \"empty\" };   r.value",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined`. A failed result has no value, and reading one anyway gives `undefined` — which then flows on."
              },
              {
                "id": "p6",
                "question": "`[\"2\", \"x\", \"5\"].map(parse)`, where `parse` returns result objects. How many have `ok: true`? Give a number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "Two. Each element carries its own result, so one failure costs only itself."
              }
            ],
            "hints": [
              {
                "when": "you chose throw for the frequent miss",
                "say": "Ask how often it happens. If it happens on a large share of calls, the exceptional path is not exceptional, and every caller ends up wrapping a `try` around a normal result."
              },
              {
                "when": "you expected an ignored result to stop the program",
                "say": "A returned failure is an ordinary value. Only a throw interrupts; a value waits for someone to look at it."
              }
            ]
          },
          {
            "kind": "run",
            "title": "A pipeline of results",
            "prompt": "Predict each line, then run it.",
            "filename": "quantities.js",
            "code": "function parseQty(text) {\n  const n = Number(text);\n  if (text.trim() === \"\" || !Number.isInteger(n) || n < 1) {\n    return { ok: false, error: `bad quantity \"${text}\"` };\n  }\n  return { ok: true, value: n };\n}\n\nconst results = [\"2\", \"abc\", \"\", \"3\"].map(parseQty);\nconsole.log(results.length);\n\nconst good = results.filter(r => r.ok).map(r => r.value);\nconsole.log(good);\nconsole.log(results.filter(r => !r.ok)[0].error);\n\nlet total = 0;\nfor (const r of results) total += r.value;\nconsole.log(total);\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "4",
                  "four"
                ],
                "why": "Four. Nothing threw, so every input produced a result — two of them failures."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "[2,3]",
                  "[2, 3]"
                ],
                "why": "The values of the two `ok` results. Filtering on `ok` is how a caller keeps what worked."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "bad quantity \"abc\""
                ],
                "why": "The first failure's `error`. The reason travelled with the failure, which `undefined` could not have done."
              },
              {
                "id": "r4",
                "question": "Last line printed",
                "accept": [
                  "NaN"
                ],
                "miss": "If you expected `5`: the loop adds every result's `value`, and a failed result has none.",
                "why": "`NaN`. The loop never checked `ok`, so it added `undefined` twice. No error — just a wrong number."
              }
            ],
            "hints": [
              {
                "when": "you expected the last line to be 5",
                "say": "Which results have a `value`? What is `2 + undefined`?"
              },
              {
                "when": "you expected the empty string to parse as 0",
                "say": "Look at the first check in `parseQty`. What does it do with text that is only whitespace, or nothing?"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Say why, not just nothing",
            "prompt": "`priceFor` returns `null` for two different failures, so the till cannot say which happened. Change it to return result objects: `{ ok: true, value: price }`, or `{ ok: false, error }` where the error names the SKU and differs between an unknown SKU and a discontinued item. A free item (price `0`) is not a failure. Then, in one sentence: why could `null` not do this job?\n",
            "exportName": "priceFor",
            "filename": "price-for.js",
            "starter": "// catalog is a Map from SKU to { price, discontinued }.\nfunction priceFor(catalog, sku) {\n  const item = catalog.get(sku);\n  if (item === undefined) return null;\n  if (item.discontinued) return null;\n  return item.price;\n}\n",
            "tests": [
              {
                "label": "a price comes back as { ok: true, value }",
                "body": "const r = subject(new Map([['pen', { price: 2, discontinued: false }]]), 'pen'); return r.ok === true && r.value === 2;"
              },
              {
                "label": "a free item is a success, not a failure",
                "body": "const r = subject(new Map([['bag', { price: 0, discontinued: false }]]), 'bag'); return r.ok === true && r.value === 0;"
              },
              {
                "label": "an unknown SKU fails, naming the SKU",
                "body": "const r = subject(new Map(), 'zz9'); return r.ok === false && typeof r.error === 'string' && r.error.indexOf('zz9') > -1;"
              },
              {
                "label": "a discontinued item fails, naming the SKU",
                "body": "const r = subject(new Map([['ink', { price: 4, discontinued: true }]]), 'ink'); return r.ok === false && typeof r.error === 'string' && r.error.indexOf('ink') > -1;"
              },
              {
                "label": "the two failures can be told apart",
                "body": "const gone = new Map([['ink', { price: 4, discontinued: true }]]); return subject(gone, 'ink').error !== subject(new Map(), 'ink').error;"
              }
            ],
            "hints": [
              {
                "when": "the free item fails",
                "say": "Check how you test for a price. `0` is a real price; only the two named failures should give `ok: false`."
              },
              {
                "when": "the two failures cannot be told apart",
                "say": "Both failures now have a message, but the same one. The till needs to show a different reason for each."
              },
              {
                "when": "a success comes back as a bare number",
                "say": "Every path must give the same shape. A caller that checks `.ok` on a number gets `undefined`, and reads it as a failure."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Report a failure the caller must look at",
            "prompt": "Write `parseQuantity(text)`. Return `{ ok: true, value }` for a positive whole number, and `{ ok: false, error }` with a specific message otherwise. Bad input is ordinary here, so nothing should throw. Then, in one sentence: what input would break a version that forgot the empty-string check?\n",
            "exportName": "parseQuantity",
            "filename": "quantity.js",
            "starter": "// { ok: true, value: n } for a positive whole number.\n// { ok: false, error: \"<reason>\" } otherwise. Never throw.\nfunction parseQuantity(text) {\n\n}\n",
            "tests": [
              {
                "label": "a valid quantity",
                "body": "const r = subject('5'); return r.ok === true && r.value === 5;"
              },
              {
                "label": "whitespace is tolerated",
                "body": "const r = subject('  7  '); return r.ok === true && r.value === 7;"
              },
              {
                "label": "text is rejected with a reason",
                "body": "const r = subject('abc'); return r.ok === false && typeof r.error === 'string' && r.error.length > 0;"
              },
              {
                "label": "empty is rejected, not treated as zero",
                "body": "return subject('').ok === false;"
              },
              {
                "label": "zero is rejected — a quantity must be positive",
                "body": "return subject('0').ok === false;"
              },
              {
                "label": "a fraction is rejected",
                "body": "return subject('2.5').ok === false;"
              },
              {
                "label": "it never throws, whatever it is given",
                "body": "const inputs = ['abc', '', '0', '-1', '2.5', 'NaN', '  ']; try { inputs.forEach(function (i) { subject(i); }); return true; } catch (e) { return false; }"
              }
            ],
            "hints": [
              {
                "when": "the empty test passes as a valid quantity",
                "say": "`Number(\"\")` is `0` — the one case where conversion invents a value out of nothing. An empty field means nothing was typed, which is not a quantity."
              },
              {
                "when": "it throws on some input",
                "say": "The whole point of the returned-result shape is that bad input is ordinary. Every path must produce an object, including the ones you did not anticipate."
              },
              {
                "when": "the error message is generic",
                "say": "The test only checks a message exists, but write one you would want at 2am — say what was wrong with the input, and include the input."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a sign-up",
            "prompt": "A code assistant wrote this sign-up. `parseAge` returns results, which is the right shape for a form field. Review what happens to them, then fix `signUp` so a bad age is never saved and the person sees the reason.\n",
            "filename": "sign-up.js",
            "code": "function parseAge(text) {\n  const n = Number(text);\n  if (text.trim() === \"\" || !Number.isInteger(n) || n < 0) {\n    return { ok: false, error: `not an age: \"${text}\"` };\n  }\n  return { ok: true, value: n };\n}\n\nfunction signUp(form, save) {\n  const age = parseAge(form.age);\n  save({ name: form.name, age: age.value });\n  return \"Welcome!\";\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line uses the result without checking it? Give the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "Line 11 reads `age.value` and saves it whether or not `age.ok` is true."
              },
              {
                "id": "c2",
                "question": "`form.age` is `\"abc\"`. What age is saved? Type the value.",
                "accept": [
                  "undefined"
                ],
                "why": "`undefined` — a failed result has no `value`. The reason for the failure was right there in `age.error`, unread."
              },
              {
                "id": "c3",
                "question": "And what does the person see? Type the message.",
                "accept": [
                  "welcome!",
                  "\"welcome!\"",
                  "welcome"
                ],
                "why": "\"Welcome!\" A returned failure that nobody checks looks exactly like success."
              }
            ],
            "starter": "function parseAge(text) {\n  const n = Number(text);\n  if (text.trim() === \"\" || !Number.isInteger(n) || n < 0) {\n    return { ok: false, error: `not an age: \"${text}\"` };\n  }\n  return { ok: true, value: n };\n}\n\nfunction signUp(form, save) {\n  const age = parseAge(form.age);\n  save({ name: form.name, age: age.value });\n  return \"Welcome!\";\n}\n",
            "exportName": "signUp",
            "tests": [
              {
                "label": "a good age is saved as a number, and the person is welcomed",
                "body": "let saved = null; const out = subject({ name: 'Kim', age: '30' }, function (u) { saved = u; }); return out === 'Welcome!' && saved && saved.age === 30 && saved.name === 'Kim';"
              },
              {
                "label": "a bad age is not saved",
                "body": "let calls = 0; subject({ name: 'Kim', age: 'abc' }, function () { calls++; }); return calls === 0;"
              },
              {
                "label": "a bad age returns the reason, not \"Welcome!\"",
                "body": "const out = subject({ name: 'Kim', age: 'abc' }, function () {}); return typeof out === 'string' && out !== 'Welcome!' && out.indexOf('abc') > -1;"
              },
              {
                "label": "an age of 0 is allowed",
                "body": "let saved = null; subject({ name: 'Baby', age: '0' }, function (u) { saved = u; }); return saved && saved.age === 0;"
              }
            ],
            "hints": [
              {
                "when": "a bad age is still saved",
                "say": "The result says whether it worked. Look at `ok` before anything uses `value`."
              },
              {
                "when": "the reason test fails",
                "say": "The reason is already written — it is in the result's `error`. Return it instead of the welcome."
              },
              {
                "when": "the age of 0 is refused",
                "say": "Check what you tested. `!age.value` is true for `0`; the result's `ok` is what says whether it worked."
              }
            ]
          }
        ]
      }
    },
    "cleanup": {
      "id": "cleanup",
      "title": "Cleanup on every way out",
      "kind": "read",
      "outcome": 8,
      "shape": "conceptual",
      "why": "A throw leaves a function without running the rest of it, so anything that must be undone — a connection closed, a lock released — needs a construct that runs however the function ends (`concepts.md`, `err.cleanup`). Scoped disposal is the deterministic-cleanup idea JavaScript's established code does not have, so it is met here as an idea (D-014, D-023); `finally` is written, and JavaScript's recent `using` is named as new (D-030).",
      "concepts": [
        "err.cleanup"
      ],
      "lede": "A function can end three ways: it runs off the end, it returns, or something in it throws. Cleanup written after the work runs on only some of them. `finally` runs on all of them — and some languages go further and do the cleanup for you when a block ends.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Throwing, and where it goes · Where an error should be handled"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Way out** — any route by which control leaves a block: the end, a `return`, a `throw`. **Scoped disposal** — a language construct that releases a resource when its block ends, however it ends.",
          "entries": [
            "**Way out** — any route by which control leaves a block: the end, a `return`, a `throw`.",
            "**Scoped disposal** — a language construct that releases a resource when its block ends, however it ends."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about code that has to run at the end. Pick an answer, or where there is a box, type the value."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`function f() { try { return \"done\"; } finally { console.log(\"closing\"); } }   f();` — does \"closing\" print?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "miss": "**If you said no because the `return` comes first — `finally` runs on the way out, and a `return` is a way out.** Leaving the `try` by any route runs it.\n",
                          "why": "Yes. The `return` decides the value, then `finally` runs, then the caller gets the value.\n"
                        },
                        {
                          "question": "Same `f()`. What does it return? Type the value.",
                          "accept": [
                            "\"done\"",
                            "done",
                            "'done'"
                          ],
                          "miss": "**If you expected `finally` to change the answer — it runs after the value is decided.** It cleans up; it does not replace what `try` returned.\n",
                          "why": "`\"done\"`. The value was settled by the `return` before `finally` ran.\n"
                        },
                        {
                          "question": "`function g() { try { throw new Error(\"x\"); } finally { console.log(\"closing\"); } }` — does the error reach `g`'s caller?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "miss": "**If you said no — `finally` is not `catch`.** It runs on the way out and then lets the error carry on up the stack.\n",
                          "why": "Yes. `closing` prints, then the error continues its journey. `finally` cleans up; it never stops an error.\n"
                        },
                        {
                          "question": "`const conn = open();  const result = work(conn);  conn.close();` — `work` throws. Does `close` run?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you said yes — a throw abandons every line after it.** The close was written for one way out: the one where nothing goes wrong.\n",
                          "why": "No. The throw leaves at `work(conn)`, and the connection stays open.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`f` is `try: return \"try\"` followed by `finally: print(\"f\")`. What does `f()` return? Type the value.",
                          "accept": [
                            "\"try\"",
                            "try",
                            "'try'"
                          ],
                          "miss": "**If you expected `finally` to change the answer — it runs after the value is decided.**\n",
                          "why": "Identical behaviour — `finally` runs on the way out without changing the return value.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Does `finally` run when an exception is unhandled?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "miss": "**If you said no — `finally` runs as the stack unwinds, whatever happens above it.**\n",
                          "why": "Yes, as the stack unwinds — which is what makes `using` and `IDisposable` reliable for cleanup.\n"
                        },
                        {
                          "question": "What does `using` guarantee?",
                          "choices": [
                            "That no exception escapes",
                            "That Dispose is called on every way out"
                          ],
                          "accept": [
                            "That Dispose is called on every way out"
                          ],
                          "why": "`Dispose()` runs on the way out, exception or not. You do not write the cleanup call at all.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what runs when a `try` returns or throws",
                "I can write cleanup that runs on every way out, and name the construct my language offers for scoped disposal"
              ]
            }
          ]
        },
        {
          "h": "Every way out"
        },
        {
          "p": "Some work comes in pairs: open and close, lock and unlock, show a spinner and\nhide it. The second half must happen **however the first half ends**."
        },
        {
          "p": "A block can end three ways:"
        },
        {
          "list": [
            "it runs off its end,",
            "a `return` leaves it early,",
            "something inside it throws."
          ],
          "ordered": true
        },
        {
          "p": "Cleanup written after the work runs only on the first — and not even that, if a\n`return` is added later. `finally` runs on **all three**:"
        },
        {
          "code": "try {\n  // the work — may return, may throw\n} finally {\n  // runs on every way out of the try\n}",
          "lang": "js"
        },
        {
          "p": "`try`/`finally` with no `catch` is legal, and it is the usual shape for cleanup.\nThis function cannot decide what a failure *means* — that is somewhere up the\nstack — but it can make sure it leaves nothing open on the way past."
        },
        {
          "p": "Two worked examples. The subgoals are the same three."
        },
        {
          "p": "A shop's stock count must not be changed by two checkouts at once, so a\ncheckout holds a lock while it works."
        },
        {
          "code": "function reserve(stock, lock, sku, qty) {\n  // acquire before the try\n  lock.acquire();\n  try {\n    // do the work inside the try\n    if (stock.get(sku) < qty) throw new RangeError(`only ${stock.get(sku)} ${sku} left`);\n    stock.set(sku, stock.get(sku) - qty);\n    return stock.get(sku);\n  } finally {\n    // release in finally\n    lock.release();\n  }\n}",
          "lang": "js",
          "file": "reserve.js"
        },
        {
          "p": "A page shows a \"loading\" spinner while it reads a file."
        },
        {
          "code": "function loadReport(spinner, read, path) {\n  spinner.show();\n  try {\n    return read(path);\n  } finally {\n    spinner.hide();\n  }\n}",
          "lang": "js",
          "file": "load-report.js"
        },
        {
          "p": "In both, the acquire is **before** the `try`, not inside it. If `acquire()`\nitself throws, there is nothing to release — and a `finally` that released a\nlock you never got would be a new bug. In both, a `return` and a `throw` pass\nthrough the `finally` on their way out: the lock is released, the spinner\nhidden, and then the value or the error carries on to the caller exactly as it\nwould have."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`reserve()` is asked for 5 of an item with 2 left. Is the lock released?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "miss": "**If you said no because it threw — that is the way out `finally` exists for.** The RangeError leaves the `try`, and leaving runs `finally`.\n",
                "why": "Yes. The throw passes through `finally` on its way to the caller.\n"
              },
              {
                "question": "And does the RangeError reach `reserve`'s caller?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. `finally` released the lock and let the error carry on. Deciding what it means is the caller's job.\n"
              }
            ]
          }
        },
        {
          "h": "What finally does not do"
        },
        {
          "p": "`finally` is for cleanup, and it has three edges worth knowing so you never use\nthem."
        },
        {
          "list": [
            "**It does not catch.** An error passing through a `finally` keeps travelling after it. To stop an error you need a `catch`, at a level that can act.",
            "**It does not change the answer** — unless you `return` inside it. A `return` in `finally` overrides the one in `try`, and it also discards an error that was travelling. Legal, confusing, and never needed for cleanup.",
            "**An error thrown inside `finally` replaces the one that was travelling.** If closing the file fails while a \"file missing\" error is on its way out, the caller sees the close failure and the original is lost. Keep cleanup code small and simple."
          ]
        },
        {
          "code": "function g() {\n  try { return 1; }\n  finally { return 2; }        // overrides — g() returns 2. Do not do this.\n}",
          "lang": "js"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`function h() { try { throw new Error(\"x\"); } finally { return \"ok\"; } }` — what does `h()` return? Type the value, or error.",
                "accept": [
                  "\"ok\"",
                  "ok",
                  "'ok'"
                ],
                "miss": "**If you expected the error — the `return` in `finally` discarded it.** That is why a `finally` should clean up and never return.\n",
                "why": "`\"ok\"`, and the error is gone without a trace. A `return` in `finally` swallows the failure as thoroughly as an empty `catch`.\n"
              },
              {
                "question": "A `try` throws \"file missing\"; its `finally` throws \"close failed\". Which error does the caller get?",
                "choices": [
                  "file missing",
                  "close failed"
                ],
                "accept": [
                  "close failed"
                ],
                "why": "`close failed`. The error thrown from `finally` replaced the one travelling through it.\n"
              }
            ]
          }
        },
        {
          "h": "Cleanup the language does for you"
        },
        {
          "p": "`finally` makes you write the release yourself, and remember to. Some languages\ngo further: **scoped disposal**. You declare the resource at the top of a block,\nand the language releases it when the block ends — by running off the end, by\n`return`, or by a throw. No cleanup call appears in your code at all."
        },
        {
          "list": [
            "**Python** has `with`: `with open(path) as f:` closes `f` when the block ends.",
            "**C#** has `using`: `using var conn = new SqlConnection(cs);` disposes `conn` when its scope ends."
          ]
        },
        {
          "p": "Established JavaScript has no such construct, which is why file handles and\nconnections need explicit `try`/`finally` here. **JavaScript has recently added\nits own `using` declarations**, which do the same job for objects that provide a\ncleanup method. They are new: as of 2026 they run in current Chrome, Edge,\nFirefox and Node, but not in Safari, and you will not find them in the code you\nread. Know the name; write `finally`."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In established JavaScript, what do you write to release a resource on every way out? Name the keyword.",
                "accept": [
                  "finally",
                  "try/finally",
                  "a finally block",
                  "try finally",
                  "try...finally"
                ],
                "why": "`try`/`finally`. JavaScript's `using` is new and not yet everywhere; `finally` is what you will write, and what existing code uses.\n"
              },
              {
                "question": "Python's `with` and C#'s `using` — what is the idea called?",
                "choices": [
                  "Garbage collection",
                  "Scoped disposal"
                ],
                "accept": [
                  "Scoped disposal"
                ],
                "why": "Scoped disposal: the resource is released when its block ends, however it ends, without a cleanup call in your code.\n"
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what does the language give you for cleanup that must always happen?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Write the cleanup yourself",
                  "The language releases it at the end of the block"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "`finally`",
                    "`using` — new; not yet in every host, not in existing code"
                  ],
                  [
                    "**Python**",
                    "`finally`",
                    "`with`"
                  ],
                  [
                    "**C#**",
                    "`finally`",
                    "`using`"
                  ]
                ]
              }
            },
            {
              "p": "Every one of them has `finally`. The difference is whether a resource can carry\nits own cleanup and have it run for you. In a language without that, every\nplace that opens something must remember a `finally`."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "code": "const conn = open();\ntry {\n  return query(conn);\n} finally {\n  conn.close();            // closed on every way out\n}",
                "lang": "js"
              }
            ],
            "python": [
              {
                "code": "with open(path) as f:          # closed on the way out, exception or not\n    return json.load(f)",
                "lang": "python"
              },
              {
                "p": "`with` is scoped disposal — the resource is released at the end of the block\nregardless of how you leave it."
              }
            ],
            "csharp": [
              {
                "code": "using var conn = new SqlConnection(cs);      // disposed at scope exit\nreturn Query(conn);",
                "lang": "csharp"
              },
              {
                "p": "`using` calls `Dispose()` when the scope ends, exception or not. You write no\n`finally` for it."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then rewrite the last one — `open`,\n`work`, `close` — so the connection is closed however `work` ends, and the\nerror still reaches the caller."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A function takes a lock, updates a shared counter, and must release the lock.\nA colleague offers three versions:"
            },
            {
              "list": [
                "**A** — `release()` on the line after the work.",
                "**B** — `catch (e) { release(); throw e; }`, plus `release()` after the `try`.",
                "**C** — `release()` in a `finally`."
              ]
            },
            {
              "p": "Pick one and defend it. For each, say which ways out it covers, and what happens\nwhen someone later adds an early `return` inside the work. Then say what your\nlanguage offers that would make the question disappear, and whether you would\nuse it in code a team maintains today."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A block has **three ways out**: the end, `return`, `throw`. Cleanup after the work covers only one.",
                "**`finally` runs on every way out**, without catching the error or changing the answer. Never `return` or throw from it.",
                "**Scoped disposal** — `with`, `using` — releases a resource at the end of its block for you. JavaScript's `using` is new; write `finally`."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "try...catch",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/try...catch",
              "note": "the `finally` block, and how it interacts with `return`."
            },
            {
              "title": "using",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/using",
              "note": "JavaScript's new scoped disposal. Note the browser compatibility table."
            }
          ]
        }
      ],
      "_source": "lessons/cleanup.md",
      "practice": {
        "id": "cleanup",
        "outcome": 8,
        "steps": [
          {
            "kind": "predict",
            "title": "What runs on the way out?",
            "prompt": "Pick an answer, or where there is a box, type the value.",
            "items": [
              {
                "id": "p1",
                "question": "`function f() { try { return \"try\"; } finally { console.log(\"f\"); } }` — what does `f()` return?",
                "accept": [
                  "\"try\"",
                  "try",
                  "'try'"
                ],
                "miss": "If you expected `finally` to be skipped or to change the answer: it runs on the way out, after the return value is decided.",
                "why": "`finally` runs on the way out — after the return value is computed, before the caller gets it. It cleans up; it does not change the answer."
              },
              {
                "id": "p2",
                "question": "`conn.close()` must run whether `work(conn)` returns or throws. Where do you put it?",
                "choices": [
                  "After the call to work",
                  "In a catch",
                  "In a finally"
                ],
                "accept": [
                  "In a finally"
                ],
                "miss": "If you said after the work: a throw skips every line after it. If you said in a catch: then it only runs when something fails.",
                "why": "In a `finally`. It is the one place that runs on every way out of the `try`."
              },
              {
                "id": "p3",
                "question": "`function g() { try { throw new Error(\"x\"); } finally { console.log(\"g\"); } }` — does the error reach the caller?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "miss": "If you said no: `finally` is not `catch`. It runs, then the error carries on up.",
                "why": "Yes. `g` is printed, then the error continues up the stack."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "`function g() { try { return 1; } finally { return 2; } }` — what does `g()` return?",
                "accept": [
                  "2"
                ],
                "why": "A `return` inside `finally` overrides the one in `try`. Legal, confusing, and the reason `finally` should clean up and never return."
              },
              {
                "id": "p5",
                "question": "`function h() { try { throw new Error(\"x\"); } finally { return \"ok\"; } }` — what does `h()` return? Type the value, or error.",
                "accept": [
                  "\"ok\"",
                  "ok",
                  "'ok'"
                ],
                "why": "`\"ok\"` — and the error is discarded. A `return` in `finally` swallows a failure as completely as an empty catch."
              },
              {
                "id": "p6",
                "question": "The `try` throws \"missing\"; its `finally` throws \"close failed\". Which does the caller get?",
                "choices": [
                  "missing",
                  "close failed"
                ],
                "accept": [
                  "close failed"
                ],
                "why": "The close failure. An error thrown from `finally` replaces the one that was travelling — another reason cleanup code should be simple."
              }
            ],
            "hints": [
              {
                "when": "you expected finally to be skipped by a return",
                "say": "List the ways out of a `try`: its end, a `return`, a `throw`. `finally` runs on every one of them."
              },
              {
                "when": "you expected finally to stop the error",
                "say": "Only a `catch` stops an error. `finally` runs and lets whatever was leaving — a value or an error — carry on."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Watch it unwind",
            "prompt": "Predict, then run. Only `a` has a catch; `b` has a `finally`.",
            "filename": "unwind.js",
            "code": "function c() {\n  console.log(\"c starts\");\n  throw new Error(\"disk full\");\n  console.log(\"c ends\");\n}\n\nfunction b() {\n  try {\n    c();\n    console.log(\"b after c\");\n  } finally {\n    console.log(\"b cleans up\");\n  }\n}\n\nfunction a() {\n  try {\n    b();\n  } catch (e) {\n    console.log(\"a caught \" + e.message);\n  }\n}\n\na();\nconsole.log(\"done\");\n",
            "items": [
              {
                "id": "r1",
                "question": "How many lines does it print? Give a number.",
                "accept": [
                  "4",
                  "four"
                ],
                "why": "`c starts`, `b cleans up`, `a caught disk full`, `done`. Two `console.log` lines never run: the one after the throw and the one after the call to `c`."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "b cleans up"
                ],
                "miss": "If you expected `a caught disk full` second: `b` is left on the way up, and leaving a `try` by a throw runs its `finally` first.",
                "why": "The error leaves `b` on its way to `a`, and a `finally` runs on every exit — including this one. Cleanup happens before the handler above sees the error."
              },
              {
                "id": "r3",
                "question": "Does \"done\" print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "`a` caught the error, so from the outside `a()` returned normally and the next line runs."
              }
            ],
            "hints": [
              {
                "when": "you included \"b after c\" or \"c ends\"",
                "say": "After a `throw`, nothing else in that `try` block runs — not in the function that threw, and not in the function that called it."
              },
              {
                "when": "you put \"b cleans up\" after \"a caught\"",
                "say": "The catch is further up the stack than the `finally`. The error has to pass through `b` to get there."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace the cleanup",
            "prompt": "`startup()` calls `loadConfig()`, which opens a file and calls `readFile()`. `loadConfig` has `try { … } finally { file.close(); }` and no catch. Only `startup` has a `catch`. `readFile()` throws because the file is corrupt.\n",
            "items": [
              {
                "id": "t1",
                "question": "Does the line after `readFile()` inside `loadConfig` run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The throw abandons the rest of the `try` block at the call that failed."
              },
              {
                "id": "t2",
                "question": "What runs next?",
                "choices": [
                  "loadConfig's finally",
                  "startup's catch"
                ],
                "accept": [
                  "loadConfig's finally"
                ],
                "why": "The `finally`. The error is still travelling up, and it leaves `loadConfig` on the way — so `file.close()` runs before `startup` sees anything."
              },
              {
                "id": "t3",
                "question": "Which function's catch receives the error? Name the function.",
                "accept": [
                  "startup",
                  "startup()",
                  "startup's"
                ],
                "why": "`startup`, the first frame up the stack with a `catch`. The `finally` cleaned up and let the error carry on."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "`loadConfig`'s `finally` itself throws while closing the file. Which error does `startup` see?",
                "choices": [
                  "The corrupt-file error",
                  "The close error"
                ],
                "accept": [
                  "The close error"
                ],
                "why": "The close failure. An error thrown from `finally` replaces the one that was travelling — another reason cleanup code should be simple."
              },
              {
                "id": "t5",
                "question": "The file reads fine and `loadConfig` returns the config. Does `file.close()` run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. A `return` is a way out too; the `finally` runs, then the caller gets the config."
              }
            ],
            "hints": [
              {
                "when": "you had startup's catch run before the finally",
                "say": "Follow the error one frame at a time. It cannot reach `startup` without leaving `loadConfig`, and leaving a `try` runs its `finally`."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Hold a lock, and always let it go",
            "prompt": "Build `withLock(lock, work)`: take the lock, run `work`, return what it returns, and release the lock however `work` ends — without stopping any error it throws. Two of the lines are mistakes.\n",
            "lines": [
              "function withLock(lock, work) {",
              "  lock.acquire();",
              "  try {",
              "    return work();",
              "  } finally {",
              "    lock.release();",
              "  }",
              "}"
            ],
            "distractors": [
              {
                "line": "  } catch (e) {",
                "why": "A `catch` in place of `finally` releases the lock only when `work` fails, and stops the error there — this function has no way to decide what a failure means."
              },
              {
                "line": "    return lock.release();",
                "why": "A `return` inside `finally` overrides the value from `work`, and discards any error that was on its way out."
              }
            ],
            "why": "Acquire before the `try` — if acquiring fails, there is nothing to release. The work goes inside the `try`, and the release in `finally`, the one block that runs on every way out. With no `catch`, an error from `work` passes through the `finally` and on to the caller.\n",
            "hints": [
              {
                "when": "you put lock.acquire() inside the try",
                "say": "If `acquire()` throws, the `finally` would release a lock this function never got. Take it first, then start the `try`."
              },
              {
                "when": "you are unsure between catch and finally",
                "say": "Which one runs when `work` succeeds?"
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Clean up on every exit",
            "prompt": "This closes the connection when `work` succeeds. When `work` throws, the connection is never closed. Change it so the connection is closed however `work` ends — and the error still reaches the caller. Then, in one sentence: why is `catch` the wrong tool here?\n",
            "exportName": "withConnection",
            "filename": "connection.js",
            "starter": "// open() returns a connection with a close() method.\n// work(conn) returns a result, or throws.\nfunction withConnection(open, work) {\n  const conn = open();\n  const result = work(conn);\n  conn.close();\n  return result;\n}\n",
            "tests": [
              {
                "label": "returns what work returns",
                "body": "return subject(function () { return { close: function () {} }; },\n               function () { return 42; }) === 42;"
              },
              {
                "label": "closes after success",
                "body": "let closed = 0; subject(function () { return { close: function () { closed++; } }; },\n        function () { return 1; });\nreturn closed === 1;"
              },
              {
                "label": "closes when work throws",
                "body": "let closed = 0; try { subject(function () { return { close: function () { closed++; } }; },\n              function () { throw new Error('query failed'); }); } catch (e) {}\nreturn closed === 1;"
              },
              {
                "label": "the error still reaches the caller",
                "body": "const err = new Error('query failed'); try { subject(function () { return { close: function () {} }; },\n              function () { throw err; }); return false; }\ncatch (e) { return e === err; }"
              }
            ],
            "hints": [
              {
                "when": "it closes on the throwing path but the error disappears",
                "say": "You caught the error to close the connection, and then it stopped there. Cleanup is not handling — this function cannot decide what a failed query means. Which construct runs code on the way out without catching anything?"
              },
              {
                "when": "the result is undefined",
                "say": "A `return` inside `finally` overrides the one in `try`. Close there; do not return there."
              },
              {
                "when": "it closes twice after success",
                "say": "The close now happens on the way out of the `try`. The old close after it is left over."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a report export",
            "prompt": "A code assistant wrote this. The file must be closed whether the export works or not, and a failure must reach the caller, who decides what to tell the user. Review it, then fix it.\n",
            "filename": "export-report.js",
            "code": "function exportReport(openFile, rows) {\n  const file = openFile(\"report.csv\");\n  try {\n    for (const row of rows) file.write(row.join(\",\"));\n    return rows.length;\n  } catch (e) {\n    file.close();\n  }\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Every row writes fine. Is the file closed?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "If you said yes: the only `close()` is in the catch, and the catch runs only when something throws.",
                "why": "No. The `return` on line 5 leaves without passing the catch. The file is left open on every successful export."
              },
              {
                "id": "c2",
                "question": "A write throws. What does the caller receive? Type the value.",
                "accept": [
                  "undefined",
                  "nothing"
                ],
                "miss": "If you expected the error: the catch stopped it, closed the file, and the function ran off its end.",
                "why": "`undefined`. The catch swallowed the error, so the caller cannot tell the export failed."
              },
              {
                "id": "c3",
                "question": "Which keyword should replace `catch (e)` on line 6?",
                "accept": [
                  "finally"
                ],
                "why": "`finally` runs on both ways out — the `return` and the throw — and lets the error carry on to the caller."
              }
            ],
            "starter": "function exportReport(openFile, rows) {\n  const file = openFile(\"report.csv\");\n  try {\n    for (const row of rows) file.write(row.join(\",\"));\n    return rows.length;\n  } catch (e) {\n    file.close();\n  }\n}\n",
            "exportName": "exportReport",
            "tests": [
              {
                "label": "returns the number of rows written",
                "body": "const f = { write: function () {}, close: function () {} }; return subject(function () { return f; }, [[1, 2], [3, 4]]) === 2;"
              },
              {
                "label": "closes the file after a successful export",
                "body": "let closed = 0; const f = { write: function () {}, close: function () { closed++; } }; subject(function () { return f; }, [[1]]); return closed === 1;"
              },
              {
                "label": "closes the file when a write fails",
                "body": "let closed = 0; const f = { write: function () { throw new Error('disk full'); }, close: function () { closed++; } }; try { subject(function () { return f; }, [[1]]); } catch (e) {} return closed === 1;"
              },
              {
                "label": "a failed write reaches the caller",
                "body": "const err = new Error('disk full'); const f = { write: function () { throw err; }, close: function () {} }; try { subject(function () { return f; }, [[1]]); return false; } catch (e) { return e === err; }"
              }
            ],
            "hints": [
              {
                "when": "the successful export does not close the file",
                "say": "Which ways out of the `try` reach your `close()`? A `return` does not pass through a `catch`."
              },
              {
                "when": "the failure no longer reaches the caller",
                "say": "A `catch` that does not rethrow ends the error's journey. This function cannot decide what a failed export means — it only has to close the file."
              },
              {
                "when": "the file is closed twice",
                "say": "You added a `finally` and kept a `close()` elsewhere. One place, on every way out, is enough."
              }
            ]
          }
        ]
      }
    },
    "work-that-waits": {
      "id": "work-that-waits",
      "title": "Promises: a value for work not done yet",
      "kind": "read",
      "outcome": 9,
      "shape": "procedural",
      "why": "Outcome 9 asks for code that waits without blocking, and most existing code waits with promise chains — a student who has only `await` cannot read it (D-017). Taught explanation-first: async work with errors is the highly interacting material `01-explanations` rule 8 names (D-029).",
      "concepts": [
        "async.promises"
      ],
      "lede": "Some work takes time that has nothing to do with your program — a network round trip, a disk read. A promise is how the language hands you that work before it has finished: an object you can hold now, chain the next step onto, and catch the failure from — though not with the `try` you already know.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Who calls a callback, and when · Throwing, and where it goes · async and await"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Promise** — an object standing for a value that is not here yet. **Settle** — to finish, one way or the other: fulfil with a value, or reject with an error. A promise settles once and never changes again.",
          "entries": [
            "**Promise** — an object standing for a value that is not here yet.",
            "**Settle** — to finish, one way or the other: fulfil with a value, or reject with an error. A promise settles once and never changes again."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Three questions on things you have already met. Type what each prints."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which word prints first? `setTimeout(() => console.log(\"later\"), 0); console.log(\"now\");`",
                    "accept": [
                      "now",
                      "\"now\""
                    ],
                    "miss": "**If you answered `later` because the delay is `0` — handing a function to `setTimeout` is not calling it.** The host calls it later, and never in the middle of your file, so every line after it runs first.\n",
                    "why": "`now`. The arrow function was handed to the host, which calls it once the script has finished — whatever the delay.\n"
                  },
                  {
                    "question": "Which line prints last? `[3, 1].forEach(n => console.log(n)); console.log(\"done\");`",
                    "accept": [
                      "done",
                      "\"done\""
                    ],
                    "miss": "**If you answered `1` — `forEach` does not hand your callback to anyone.** It calls it itself, once per element, before it returns. Who calls a callback, and when, depends on what you gave it to.\n",
                    "why": "`done`. `forEach` calls your function right now, for `3` and then `1`, and only then returns — so `done` is last.\n"
                  },
                  {
                    "question": "What prints? Type every line it prints. `function parse() { throw new Error(\"bad\"); } function load() { parse(); console.log(\"loaded\"); } try { load(); } catch (e) { console.log(\"caught \" + e.message); }`",
                    "accept": [
                      "caught bad",
                      "\"caught bad\""
                    ],
                    "miss": "**If `loaded` printed too — a throw leaves every function between it and the `try` at once.** `load` never reaches its `console.log`; the error climbs the call stack to the nearest `catch`.\n",
                    "why": "Only `caught bad`. The throw left `parse`, then `load`, and landed in the `catch` that was waiting further up the stack.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what state a promise is in: pending, fulfilled or rejected",
                "I can chain work onto a promise with `.then` and `.catch`, and say what each link receives",
                "I can predict whether a rejection is caught by a surrounding `try`"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "A promise stands for a value that is not here yet",
          "body": [
            {
              "p": "Before promises, a function that took time asked you for a callback, and called\nit when it was done — the `setTimeout` style from the first question. A\n**promise** turns that round. The function returns straight away, and what it\nreturns is an object you can hold: a stand-in for the result."
            },
            {
              "p": "Four words to have before any code:"
            },
            {
              "list": [
                "**pending** — the work has not finished. Every promise starts here.",
                "**fulfilled** — it finished, and the promise now holds a value.",
                "**rejected** — it failed, and the promise now holds an error.",
                "**settled** — fulfilled or rejected. A promise settles **once**, and after that it never changes again."
              ]
            },
            {
              "code": "const p = fetch(\"/orders\");   // fetch is the host's; it returns a promise at once\nconsole.log(p);               // Promise { <pending> } — nothing has arrived yet",
              "lang": "js",
              "file": "states.js"
            },
            {
              "p": "`p` exists on the very next line. The response does not. Your code kept going\nwhile the request was in flight; the waiting is done somewhere else, by the\nhost."
            },
            {
              "p": "Two ready-made promises are handy for trying things out, because they are\nsettled from the start:"
            },
            {
              "code": "Promise.resolve(5);                  // already fulfilled, holding 5\nPromise.reject(new Error(\"no\"));     // already rejected, holding that error",
              "lang": "js",
              "file": "settled.js"
            },
            {
              "p": "And this is the shape you will see when callback-style work is wrapped up as a\npromise. You do not need to write it yet — only to read it:"
            },
            {
              "code": "function wait(ms) {\n  // setTimeout calls resolve after ms, and calling resolve fulfils the promise\n  return new Promise(resolve => setTimeout(resolve, ms));\n}\n\nconst w = wait(1000);   // pending now; fulfilled a second from now",
              "lang": "js",
              "file": "wait.js"
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`const w = wait(1000);` — on the next line, what state is `w` in?",
                    "choices": [
                      "pending",
                      "fulfilled",
                      "rejected"
                    ],
                    "accept": [
                      "pending"
                    ],
                    "miss": "**If you answered fulfilled — `wait` returned straight away, and a second has not passed.** Returning the promise and finishing the work are two different moments.\n",
                    "why": "Pending. The promise exists; the second it stands for has not gone by.\n"
                  },
                  {
                    "question": "A promise has fulfilled with `5`. Can it reject later?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you answered yes — a promise settles once.** After that it holds its value for good, which is what makes it safe to hand around and attach handlers to late.\n",
                    "why": "No. Settled is final. Anyone who looks at it later sees the same `5`.\n"
                  },
                  {
                    "question": "`const p = fetch(url);` — is the response in `p` on the next line?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you answered yes — `p` is the stand-in, not the response.** The request is still in flight and your code has carried on.\n",
                    "why": "No. `p` is a pending promise. The response arrives later, and the promise fulfils with it then.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Chain the next step with .then",
          "body": [
            {
              "p": "You cannot reach into a pending promise and take its value. Instead you hand it\na function: **`.then(fn)`** means \"when you fulfil, call `fn` with the value.\""
            },
            {
              "p": "Three rules make chains work:"
            },
            {
              "list": [
                "**`fn` is always called later**, never during the line that attaches it — even when the promise is already fulfilled. The promise calls it, not you, and only after the code that is running now has finished.",
                "**`.then` returns a new promise**, which fulfils with whatever `fn` returns. So you can attach another `.then` to it, and that one receives `fn`'s return value.",
                "**If `fn` returns a promise, the next link waits for it.** That is how you put one slow step after another."
              ]
            },
            {
              "p": "Suppose `getOrder(id)` returns a promise for an order, and\n`getPrices(items)` returns a promise for an array of prices."
            },
            {
              "code": "getOrder(42)\n  .then(order => getPrices(order.items))               // returns a promise — the next link waits for it\n  .then(prices => prices.reduce((a, b) => a + b, 0))   // adds up the prices\n  .then(total => console.log(\"total\", total));",
              "lang": "js",
              "file": "order-total.js"
            },
            {
              "p": "Each arrow receives what the link above it produced. The second `.then` gets an\n**array of prices**, not a promise, because the chain waited for `getPrices`\nbefore calling it."
            },
            {
              "p": "The same shape with a different job. `findUser(name)` and `getTeam(id)` each\nreturn a promise:"
            },
            {
              "code": "findUser(\"ada\")\n  .then(user => getTeam(user.teamId))\n  .then(team => team.name)\n  .then(name => console.log(name));",
              "lang": "js",
              "file": "team-name.js"
            },
            {
              "p": "Now one gap to fill in your head. `getWeather(city)` returns a promise for\n`{ temp: 18 }`. What goes in the middle link so the last one prints `18`?"
            },
            {
              "code": "getWeather(\"Walla Walla\")\n  .then(/* ______ */)\n  .then(temp => console.log(temp));",
              "lang": "js",
              "file": "weather.js"
            },
            {
              "p": "`reading => reading.temp`. A link that returns a plain value passes it straight\non; only a returned promise makes the chain wait."
            },
            {
              "block": "notice",
              "label": "The missing return",
              "body": [
                {
                  "code": "getOrder(42)\n  .then(order => { getPrices(order.items); })   // braces, no return\n  .then(prices => console.log(prices));         // undefined",
                  "lang": "js"
                },
                {
                  "p": "With braces, an arrow returns nothing unless you write `return`. The next link\nreceives `undefined` — and does not wait for `getPrices` at all. It is the\nfunction that logs instead of returning, again."
                }
              ]
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "What value does the last promise fulfil with? `Promise.resolve(2).then(n => n * 10).then(n => n + 1)`",
                    "accept": [
                      "21"
                    ],
                    "why": "`2`, then `20`, then `21`. Each link receives what the one above returned.\n"
                  },
                  {
                    "question": "What prints? `Promise.resolve(2).then(n => { n * 10; }).then(n => console.log(n))`",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**If you answered `20` — the braces changed the arrow.** With braces it needs `return`, and without one the next link receives `undefined`.\n",
                    "why": "`undefined`. The first link computed `20` and threw it away.\n"
                  },
                  {
                    "question": "`findUser(\"ada\").then(user => getTeam(user.teamId)).then(t => …)` — is `t` a promise or a team?",
                    "choices": [
                      "A promise",
                      "A team"
                    ],
                    "accept": [
                      "A team"
                    ],
                    "miss": "**If you answered a promise — a link that returns a promise makes the chain wait for it.** The next link is called with what that promise fulfilled with.\n",
                    "why": "A team. The chain waited for `getTeam`'s promise and handed on its value.\n"
                  },
                  {
                    "question": "Which letter prints first? `Promise.resolve(1).then(() => console.log(\"A\")); console.log(\"B\");`",
                    "accept": [
                      "B",
                      "\"B\"",
                      "b"
                    ],
                    "miss": "**If you answered `A` because the promise is already fulfilled — a `.then` callback is never called on the spot.** It waits until the code running now has finished, the way a `setTimeout` callback does.\n",
                    "why": "`B`. The promise is fulfilled, but its callback still waits for the running script to finish. Exactly when it runs relative to timers is the event loop lesson's.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "A rejection skips to the nearest .catch",
          "body": [
            {
              "p": "When something inside a link **throws**, the promise that link returned\n**rejects** with that error. A rejected promise skips every `.then` below it\nuntil it reaches a **`.catch(fn)`**, which is called with the error."
            },
            {
              "p": "What happens after that depends on what the `.catch` function does:"
            },
            {
              "list": [
                "**It returns normally** — the failure is handled. The promise `.catch` returned fulfils with that return value, and the chain carries on from there.",
                "**It throws** — the chain stays rejected, and the next `.catch` down gets it."
              ]
            },
            {
              "p": "It is the rule you already know for `throw`, turned on its side. A throw climbs\nthe call stack to the nearest `catch`; a rejection travels down the chain to the\nnearest `.catch`."
            },
            {
              "code": "getOrder(42)\n  .then(order => {\n    if (order.items.length === 0) throw new Error(\"empty order\");\n    return getPrices(order.items);\n  })\n  .then(prices => prices.reduce((a, b) => a + b, 0))\n  .catch(err => {                     // a failure anywhere above lands here\n    console.log(\"could not total: \" + err.message);\n    return 0;\n  })\n  .then(total => console.log(\"total\", total));",
              "lang": "js",
              "file": "order-total-safe.js"
            },
            {
              "p": "Trace it for an order with no items:"
            },
            {
              "table": {
                "head": [
                  "Link",
                  "What happens"
                ],
                "rows": [
                  [
                    "first `.then`",
                    "throws — its promise rejects with `empty order`"
                  ],
                  [
                    "second `.then`",
                    "**skipped** — the promise above it rejected"
                  ],
                  [
                    "`.catch`",
                    "called with the error; logs `could not total: empty order`; returns `0`"
                  ],
                  [
                    "last `.then`",
                    "called with `0`; logs `total 0`"
                  ]
                ]
              }
            },
            {
              "p": "A second failure, from somewhere else: `getPrices` itself rejects because the\nnetwork dropped. That rejection starts one link lower, skips the same `.then`,\nand lands in the same `.catch`. **One `.catch` at the end covers every link above\nit**, whichever one failed."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`order-total-safe.js` — `getPrices` rejects. Does the `reduce` link run?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you answered yes — a rejected promise skips every `.then` until a `.catch`.** The `reduce` link is only for a fulfilled value.\n",
                    "why": "No. The rejection passes the `reduce` link by and lands in the `.catch`.\n"
                  },
                  {
                    "question": "`order-total-safe.js` — `getOrder` rejects. What does the last line print?",
                    "accept": [
                      "total 0",
                      "\"total 0\""
                    ],
                    "miss": "**If you answered nothing — the `.catch` returned normally.** That handles the failure, and the chain carries on with the value it returned.\n",
                    "why": "`total 0`. The `.catch` logged, returned `0`, and the last link received it.\n"
                  },
                  {
                    "question": "What is the second line printed? `Promise.reject(new Error(\"x\")).catch(e => console.log(e.message)).then(v => console.log(v))`",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**If you expected the error again — the `.catch` handled it.** It returned nothing, so the chain fulfils with `undefined`.\n",
                    "why": "`x`, then `undefined`. The `.catch` returned nothing, and nothing is `undefined`.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "A surrounding try does not see a rejection",
          "body": [
            {
              "p": "`try`/`catch` catches what is thrown **while the `try` block is running**. A\npromise rejects later — after the `try` block has already finished. So wrapping a\ncall that returns a promise in `try` catches nothing that the promise does:"
            },
            {
              "code": "try {\n  saveOrder(order);          // returns a pending promise; it rejects a moment later\n  console.log(\"saved\");      // prints — nothing has failed yet\n} catch (err) {\n  console.log(\"not saved\");  // never — the rejection comes after the try is over\n}",
              "lang": "js",
              "file": "save.js"
            },
            {
              "p": "`saved` prints, and then the rejection arrives with nobody listening. It is\nreported as an **unhandled rejection**, usually far from this code."
            },
            {
              "p": "It is the first question on this page again, with an error in it. A callback\nhanded to `setTimeout` runs after the file is done — and so after any `try` around\nit has finished:"
            },
            {
              "code": "try {\n  setTimeout(() => { throw new Error(\"late\"); }, 0);\n} catch (err) {\n  console.log(\"caught\");     // never — the throw happens after the try is over\n}",
              "lang": "js",
              "file": "late-throw.js"
            },
            {
              "p": "With a promise, the handler goes **on the chain**, where the rejection travels:"
            },
            {
              "code": "saveOrder(order)\n  .then(() => console.log(\"saved\"))\n  .catch(err => console.log(\"not saved: \" + err.message));   // the rejection arrives here",
              "lang": "js",
              "file": "save-fixed.js"
            },
            {
              "p": "And a function that starts promise work must **return** the promise, or no\ncaller can catch anything either:"
            },
            {
              "code": "function orderCount(userId) {\n  fetchOrders(userId).then(orders => orders.length);          // returns undefined\n}\n\nfunction orderCountFixed(userId) {\n  return fetchOrders(userId).then(orders => orders.length);   // returns the promise\n}",
              "lang": "js",
              "file": "count.js"
            },
            {
              "p": "The caller of `orderCountFixed` can attach its own `.then` and `.catch`. The\ncaller of `orderCount` gets `undefined`, and the count — and any failure — is lost.\nThere is a way to make a `try` see a rejection, and it is the next lesson."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`save.js` — `saveOrder` rejects. Does `not saved` print?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you answered yes — the `try` block finished while the promise was still pending.** By the time it rejects, there is no `try` running to catch it.\n",
                    "why": "No. `saved` prints, and the rejection goes unhandled.\n"
                  },
                  {
                    "question": "`late-throw.js` — does `caught` print?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "why": "No. The host calls the arrow function after the whole script — `try` included — has finished.\n"
                  },
                  {
                    "question": "`count.js` — what value does `orderCount(7)` return?",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**If you answered a promise or a number — look for the word `return`.** The `.then` makes a promise, but `orderCount` never returns it.\n",
                    "why": "`undefined`. The promise is made and dropped inside the function.\n"
                  },
                  {
                    "question": "To handle a rejection from `saveOrder(order)` without `await`, what do you use?",
                    "choices": [
                      "a try around the call",
                      ".catch on the promise"
                    ],
                    "accept": [
                      ".catch on the promise"
                    ],
                    "why": "`.catch` on the promise. That is where the rejection travels; the `try` is long finished when it arrives.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A chain loads a user, then their settings, then their saved theme:"
            },
            {
              "code": "getUser(id)\n  .then(user => getSettings(user))\n  .then(settings => getTheme(settings))\n  .then(theme => apply(theme));",
              "lang": "js"
            },
            {
              "p": "A missing theme is common and harmless — the default theme will do. A missing\nuser means something is badly wrong."
            },
            {
              "p": "Where would you put `.catch`: once at the end, after the theme step only, or\nboth? Say what each placement recovers, what it lets through, and what the page\nshows the user in each case when the user cannot be loaded."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "A **promise** stands for a value that is not here yet: **pending**, then **fulfilled** with a value or **rejected** with an error — once, for good.",
                "**`.then(fn)`** calls `fn` later with the value and returns a new promise for what `fn` returns; a returned promise makes the next link wait. A throw **rejects**, and the rejection skips to the nearest **`.catch`**.",
                "A `try` around a call **does not** catch its promise's rejection — the `try` has finished first. Handle it on the chain, and **return** the promise so callers can."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Using promises",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Using_promises",
              "note": "chaining, and error handling down a chain."
            },
            {
              "title": "Promise",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise",
              "note": "the three states, and every method on a promise."
            }
          ]
        }
      ],
      "_source": "lessons/work-that-waits.md",
      "practice": {
        "id": "work-that-waits",
        "outcome": 9,
        "steps": [
          {
            "kind": "predict",
            "title": "Before you start",
            "prompt": "Three questions, from what you already know.",
            "items": [
              {
                "id": "p1",
                "question": "`try { saveOrder(order); } catch (e) { retry(); }` — `saveOrder` returns a promise, and it rejects. Does `retry()` run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "If you said yes: the `try` block finishes while the promise is still pending. When it rejects, the `try` is long gone.",
                "why": "No. A `try` only sees what is thrown while it is running, and a rejection arrives later. The handler belongs on the promise: `.catch`."
              },
              {
                "id": "p2",
                "question": "What does `.then(fn)` return? A few words.",
                "accept": [
                  "a promise",
                  "a new promise",
                  "promise",
                  "another promise",
                  "a new Promise"
                ],
                "miss": "If you said the value, or `fn`'s result: `.then` returns before `fn` has even been called. What it returns is a new promise for what `fn` will return.",
                "why": "A new promise, which fulfils with whatever `fn` returns. That is the whole reason a chain can continue."
              },
              {
                "id": "p3",
                "question": "What value does the last promise fulfil with? `Promise.resolve(3).then(n => n + 1).then(n => n * 2)`",
                "accept": [
                  "8"
                ],
                "why": "`3`, then `4`, then `8`. Each link receives what the link above returned."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "`const p = fetch(url);` — is the request finished on the next line?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "`p` is a promise in the pending state. The request is in flight and your code kept running."
              },
              {
                "id": "p5",
                "question": "A promise has fulfilled. Can it reject later?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "A promise settles exactly once and never changes again. That is what makes it safe to attach handlers late."
              },
              {
                "id": "p6",
                "question": "What prints? `Promise.resolve(3).then(n => { n + 1; }).then(n => console.log(n))`",
                "accept": [
                  "undefined"
                ],
                "miss": "If you said 4: with braces, an arrow returns nothing unless it says `return`.",
                "why": "`undefined`. The first link computed `4` and returned nothing, so the next link received `undefined`."
              }
            ],
            "hints": [
              {
                "when": "you expected the catch to see the rejection",
                "say": "Ask when the rejection happens and whether the `try` block is still running then. The rejection travels down the chain, not up the stack."
              },
              {
                "when": "you said .then returns the value",
                "say": "When `.then` returns, has `fn` run yet? It cannot return a value it does not have — so what does it return instead?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Follow a chain",
            "prompt": "Each `.then` returns a new promise. Predict, then run.",
            "filename": "chain.js",
            "code": "const p = Promise.resolve(2);\n\np.then(n => n * 10)\n  .then(n => {\n    console.log(\"got \" + n);\n    throw new Error(\"too big\");\n  })\n  .then(() => console.log(\"next step\"))\n  .catch(e => console.log(\"caught \" + e.message))\n  .then(() => console.log(\"after catch\"));\n\nconsole.log(\"end of script\");\n",
            "items": [
              {
                "id": "r1",
                "question": "Type the first line printed.",
                "accept": [
                  "end of script"
                ],
                "miss": "If you expected `got 20` first: `p` is already fulfilled, but a `.then` callback still never runs on the spot. It waits for the current script to finish.",
                "why": "Every `.then` callback is called later, even on a promise that has already fulfilled. The synchronous script finishes first."
              },
              {
                "id": "r2",
                "question": "Type the second line printed.",
                "accept": [
                  "got 20"
                ],
                "why": "The first `.then` returned `20`, and a value returned from `.then` is what the next `.then` receives."
              },
              {
                "id": "r3",
                "question": "Does `next step` print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "If you said yes: the throw rejects the promise that `.then` returned, and a rejected promise skips every `.then` until a `.catch`.",
                "why": "No. The throw rejects that link of the chain, and the rejection skips straight past `.then` to the first `.catch`."
              },
              {
                "id": "r4",
                "question": "Does `after catch` print?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. `.catch` handled the rejection and returned normally, so the promise it returns fulfils, and the chain carries on."
              }
            ],
            "hints": [
              {
                "when": "you predicted \"got 20\" before \"end of script\"",
                "say": "A `.then` callback is called by the promise, later — never during the line that attaches it. What is still running when the chain is built?"
              },
              {
                "when": "you expected every line to print",
                "say": "A throw inside `.then` is how a chain rejects. Follow the rejection: which kinds of handler does it skip?"
              },
              {
                "when": "you expected nothing after the catch",
                "say": "A `.catch` that returns normally has handled the failure. What state is the promise it returns in?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "The state of every link",
            "prompt": "`const a = Promise.resolve(1);   const b = a.then(n => { throw new Error(\"no\"); });   const c = b.catch(e => 5);   const d = c.then(n => n * 2);` — four promises. Give each one's state or value once everything has settled, unless the question says otherwise.\n",
            "items": [
              {
                "id": "t1",
                "question": "`b` — fulfilled or rejected?",
                "choices": [
                  "fulfilled",
                  "rejected"
                ],
                "accept": [
                  "rejected"
                ],
                "why": "Its link threw, so the promise `.then` returned rejects with that error."
              },
              {
                "id": "t2",
                "question": "What value does `c` fulfil with?",
                "accept": [
                  "5"
                ],
                "miss": "If you said it rejects: `c` is what `.catch` returned, and the `.catch` function returned `5` normally. Handled means fulfilled.",
                "why": "`5`. The `.catch` function ran with the error and returned `5`, so `c` fulfils with it."
              },
              {
                "id": "t3",
                "question": "What value does `d` fulfil with?",
                "accept": [
                  "10"
                ],
                "why": "`10`. `d`'s link received `c`'s `5` and doubled it."
              },
              {
                "id": "t4",
                "question": "Right after the script's last line — before anything queued has run — what state is `d` in?",
                "choices": [
                  "pending",
                  "fulfilled",
                  "rejected"
                ],
                "accept": [
                  "pending"
                ],
                "miss": "If you said fulfilled: no `.then` or `.catch` callback has been called yet — they all wait for the script to finish.",
                "why": "Pending. `a` is fulfilled already, but none of the callbacks has been called yet, so `b`, `c` and `d` are all still pending."
              }
            ],
            "extra": [
              {
                "id": "t5",
                "question": "`Promise.reject(new Error(\"x\")).then(v => v + 1)` — fulfilled or rejected?",
                "choices": [
                  "fulfilled",
                  "rejected"
                ],
                "accept": [
                  "rejected"
                ],
                "why": "Rejected. The `.then` has no handler for a rejection, so its promise rejects with the same error — the skip."
              },
              {
                "id": "t6",
                "question": "`Promise.reject(new Error(\"x\")).catch(e => { throw e; })` — fulfilled or rejected?",
                "choices": [
                  "fulfilled",
                  "rejected"
                ],
                "accept": [
                  "rejected"
                ],
                "why": "Rejected. A `.catch` that throws has not handled anything; the next `.catch` down would get the error."
              }
            ],
            "hints": [
              {
                "when": "you said c is rejected",
                "say": "Look at what the `.catch` function does with the error. Does it throw, or return?"
              },
              {
                "when": "you said everything is fulfilled at the last line",
                "say": "Being fulfilled needs the link's function to have run. When is any `.then` or `.catch` callback first called?"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Build a chain",
            "prompt": "Put the lines in order to make `teamName(userId)`: look up the user, then their team, and return a promise for the team's name — or `\"no team\"` if any step fails. `getUser` and `getTeam` return promises. Two of the lines are mistakes — leave them out.\n",
            "lines": [
              "function teamName(userId) {",
              "  return getUser(userId)",
              "    .then(user => getTeam(user.teamId))",
              "    .then(team => team.name)",
              "    .catch(() => \"no team\");",
              "}"
            ],
            "distractors": [
              {
                "line": "  getUser(userId)",
                "why": "Without `return`, `teamName` makes the promise and drops it. Its caller gets `undefined` — and cannot wait for the name or catch a failure."
              },
              {
                "line": "    .then(user => { getTeam(user.teamId); })",
                "why": "Braces with no `return`: the link returns `undefined`, so the next link does not wait for the team and receives `undefined` instead."
              }
            ],
            "why": "The function returns the chain, so its caller holds a promise for the name. The first link returns `getTeam`'s promise, so the next waits for the team; the second returns a plain value; the `.catch` at the end covers both lookups and turns any failure into `\"no team\"`.\n",
            "hints": [
              {
                "when": "you are unsure which getUser line to use",
                "say": "Ask what `teamName(7)` hands its caller. Can the caller attach a `.then` to it?"
              },
              {
                "when": "you are unsure which getTeam line to use",
                "say": "Braces around an arrow's body change what it returns. What does the next link receive?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "A fallback for a failed lookup",
            "prompt": "`priceOrZero(id)` returns a promise for the product's price — or for `0` if `getProduct` rejects. Fill the gap. Then, in one sentence: why does a `try` around `getProduct(id)` not do this job?\n",
            "exportName": "priceOrZero",
            "filename": "price-or-zero.js",
            "starter": "// getProduct(id) returns a promise for { name, price }.\n// It rejects when there is no product with that id.\nfunction getProduct(id) {\n  return id === 1\n    ? Promise.resolve({ name: \"pen\", price: 3 })\n    : Promise.reject(new Error(\"no product \" + id));\n}\n\nfunction priceOrZero(id) {\n  return getProduct(id)\n    .then(product => product.price)\n    /* ___ */;\n}\n",
            "tests": [
              {
                "label": "a product that exists gives its price",
                "body": "return (await subject(1)) === 3;"
              },
              {
                "label": "a failed lookup gives 0",
                "body": "return (await subject(99)) === 0;"
              },
              {
                "label": "a free product is 0 because it costs 0, not because it failed",
                "body": "function getProduct(id) { return new Promise(function (ok) { setTimeout(function () { ok({ name: 'sample', price: 0 }); }, 20); }); } const priceOrZero = eval('(' + subject.toString() + ')'); return (await priceOrZero(5)) === 0;"
              },
              {
                "label": "a lookup that fails later still gives 0",
                "body": "function getProduct(id) { return new Promise(function (ok, fail) { setTimeout(function () { fail(new Error('no product ' + id)); }, 20); }); } const priceOrZero = eval('(' + subject.toString() + ')'); return (await priceOrZero(7)) === 0;"
              },
              {
                "label": "still returns a promise",
                "body": "const p = subject(1); if (p && typeof p.catch === 'function') p.catch(() => {}); return p instanceof Promise;"
              }
            ],
            "hints": [
              {
                "when": "a product that exists gives 0",
                "say": "Your fallback runs whether or not the lookup failed. Which method is called only for a rejection?"
              },
              {
                "when": "a failed lookup gives undefined",
                "say": "The handler ran, but what did it return? The promise fulfils with the handler's return value — braces with no `return` give `undefined`."
              },
              {
                "when": "a failed lookup gives nothing at all",
                "say": "The rejection is still travelling down the chain. It needs a handler after the `.then`, where it arrives."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a profile loader",
            "prompt": "A teammate's code, partly written with an AI tool. `getUser` and `getTeam` return promises; `showName`, `showTeam` and `showError` put text on the page. Review it.\n",
            "filename": "profile.js",
            "code": "function loadProfile(id) {\n  try {\n    getUser(id).then(user => {\n      showName(user.name);\n    });\n  } catch (err) {\n    showError(\"Could not load profile\");\n  }\n}\n\nfunction followerCount(id) {\n  getUser(id).then(user => user.followers.length);\n}\n\nfunction showUserTeam(id) {\n  return getUser(id)\n    .then(user => { getTeam(user.teamId); })\n    .then(team => showTeam(team.name));\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "`getUser` rejects. Does line 7 run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The `try` finishes as soon as `getUser` has returned its pending promise. The rejection arrives later, with no `.catch` on the chain — an unhandled rejection."
              },
              {
                "id": "c2",
                "question": "What value does `followerCount(3)` return?",
                "accept": [
                  "undefined"
                ],
                "why": "There is no `return` on line 12, so the function returns `undefined` and the promise — the only thing that will ever hold the count — is dropped."
              },
              {
                "id": "c3",
                "question": "`showUserTeam(3)`, when both lookups succeed: on which line is an error thrown? Type the line number.",
                "accept": [
                  "18",
                  "line 18"
                ],
                "miss": "If you said 17: line 17 runs without complaint. It starts `getTeam` and returns nothing.",
                "why": "Line 18. Line 17's braces have no `return`, so `team` is `undefined` and `team.name` throws — which rejects the chain, with nothing to catch it."
              },
              {
                "id": "c4",
                "question": "Type the one-word fix for line 17.",
                "accept": [
                  "return",
                  "add return",
                  "a return"
                ],
                "why": "`return getTeam(user.teamId);` — or drop the braces. Then the next link waits for the team and receives it."
              }
            ],
            "hints": [
              {
                "when": "you expected the catch on line 6 to run",
                "say": "A `try` sees only what is thrown while it runs. Is the `try` still running when `getUser`'s promise rejects?"
              },
              {
                "when": "you said followerCount returns a promise or a number",
                "say": "Read line 12 for the word `return`. The `.then` makes a promise, but does `followerCount` return it?"
              },
              {
                "when": "you picked line 17 for the error",
                "say": "Line 17 does not fail — it quietly returns nothing. Which line then tries to use what it returned?"
              }
            ]
          }
        ]
      }
    },
    "await-syntax": {
      "id": "await-syntax",
      "title": "async and await",
      "kind": "read",
      "outcome": 9,
      "shape": "procedural",
      "why": "Outcome 9 asks for code that waits without blocking, and most new code writes that wait with `async`/`await`. It is syntax over the promise model the previous lesson built, so it is taught apart from it (D-017) and practised as conversion between the two forms — `async.await`'s own mastery (D-029). Async work with errors is highly interacting, so explanation comes first (`01-explanations` rule 8).",
      "concepts": [
        "async.await"
      ],
      "lede": "A promise chain says \"then do this, then this\". `async` and `await` let you write the same steps one under another, like ordinary code — but the function still returns a promise, and a `try` around an `await` finally catches what a `try` around a promise could not.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Promises: a value for work not done yet · Throwing, and where it goes · The event loop, slowly"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**`async` function** — a function that always returns a promise. **`await`** — inside one, \"pause this function until that promise settles, then give me its value\".",
          "entries": [
            "**`async` function** — a function that always returns a promise.",
            "**`await`** — inside one, \"pause this function until that promise settles, then give me its value\"."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "label": "from memory",
          "body": [
            {
              "p": "Four questions on promises. Pick or type what each produces."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`getOrder(42).then(order => getPrices(order.items)).then(prices => …)` — is `prices` an array or a promise?",
                    "choices": [
                      "An array",
                      "A promise"
                    ],
                    "accept": [
                      "An array"
                    ],
                    "miss": "**If you answered a promise — a link that returns a promise makes the chain wait.** The next link is called with what that promise fulfilled with.\n",
                    "why": "An array. The chain waited for `getPrices` and handed on its value.\n"
                  },
                  {
                    "question": "What prints? `Promise.resolve(2).then(n => { n * 3; }).then(n => console.log(n))`",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**If you answered `6` — braces need a `return`.** Without one, the next link receives `undefined`.\n",
                    "why": "`undefined`. The link computed `6` and returned nothing.\n"
                  },
                  {
                    "question": "`try { saveOrder(order); } catch (e) { console.log(\"failed\"); }` — `saveOrder`'s promise rejects. Does `failed` print?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you answered yes — the `try` finished while the promise was pending.** The rejection arrives after, and travels down the chain, not up the stack.\n",
                    "why": "No. The rejection goes unhandled; a `.catch` on the promise would have seen it.\n"
                  },
                  {
                    "question": "`Promise.reject(new Error(\"x\")).catch(() => 0)` — is the promise it returns fulfilled or rejected?",
                    "choices": [
                      "fulfilled",
                      "rejected"
                    ],
                    "accept": [
                      "fulfilled"
                    ],
                    "why": "Fulfilled, with `0`. A `.catch` that returns normally has handled the failure.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can rewrite a promise chain with `await`, and back again",
                "I can say what an `async` function returns"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "An async function always returns a promise",
          "body": [
            {
              "p": "Write **`async`** before `function` and two things change. You may use `await`\ninside it — the next step. And **it always returns a promise**, whatever its body\nsays:"
            },
            {
              "table": {
                "head": [
                  "The body does",
                  "The call returns"
                ],
                "rows": [
                  [
                    "`return 5`",
                    "a promise that fulfils with `5`"
                  ],
                  [
                    "returns nothing",
                    "a promise that fulfils with `undefined`"
                  ],
                  [
                    "`throw new Error(\"no\")`",
                    "a promise that **rejects** with that error"
                  ],
                  [
                    "`return somePromise`",
                    "a promise that settles the way `somePromise` does"
                  ]
                ]
              }
            },
            {
              "code": "async function double(n) {\n  return n * 2;\n}\n\nconsole.log(double(4));                    // Promise { 8 } — not 8\ndouble(4).then(v => console.log(v));       // 8, a moment later",
              "lang": "js",
              "file": "async-returns.js"
            },
            {
              "p": "That last row of the table is why a throw inside an `async` function never\nreaches the line that called it. It becomes a rejection, and travels the way the\nlast lesson's rejections did."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "async function f() { return 1; }   —   typeof f()",
                    "accept": [
                      "\"object\"",
                      "object",
                      "'object'"
                    ],
                    "miss": "**If you answered `\"number\"` — an `async` function never returns its value directly.** It returns a promise for it, and a promise is an object.\n",
                    "why": "`\"object\"`. The call returns a promise that fulfils with `1`.\n"
                  },
                  {
                    "question": "`async function g() { throw new Error(\"x\"); }` — does the line `g();` throw at the call site?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you answered yes — inside an `async` function, a throw rejects the returned promise.** Nothing is thrown where `g` was called.\n",
                    "why": "No. `g()` returns a rejected promise. With nothing to catch it, that is an unhandled rejection.\n"
                  },
                  {
                    "question": "`async function h() { console.log(\"hi\"); }` — what value does `h()` fulfil with?",
                    "accept": [
                      "undefined"
                    ],
                    "why": "`undefined` — the body returns nothing. It still returns a promise.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "await pauses this function, and only this one",
          "body": [
            {
              "p": "Inside an `async` function, **`await promise`** means: pause this function until\nthe promise settles. If it fulfils, `await` gives you the value. If it rejects,\n`await` **throws** the error, right there on that line."
            },
            {
              "p": "Pausing is not blocking. The function steps aside, and whoever called it carries\non — the call has already returned a pending promise. When the awaited promise\nsettles, the function picks up where it paused."
            },
            {
              "code": "async function load() {\n  console.log(\"A\");                     // runs at once, as ordinary code\n  const n = await Promise.resolve(5);   // pauses until the promise settles\n  console.log(\"B \" + n);                // runs after the pause\n}\n\nconsole.log(\"1\");\nload();              // runs load's body now, up to the await\nconsole.log(\"2\");    // prints before B — load is paused\n// Prints: 1, A, 2, B 5",
              "lang": "js",
              "file": "pause.js"
            },
            {
              "p": "Read that output closely. Calling `load()` **runs its body straight away**, up to\nthe first `await`, so `A` prints before `2`. Only the part after the `await` waits."
            },
            {
              "p": "The same thing with a different job. `getUser` returns a promise:"
            },
            {
              "code": "async function greet(id) {\n  console.log(\"looking up \" + id);\n  const user = await getUser(id);\n  return \"Hello, \" + user.name;\n}\n\ngreet(7).then(text => console.log(text));\nconsole.log(\"asked\");\n// Prints: looking up 7, asked, Hello, Ada (for a user named Ada)",
              "lang": "js",
              "file": "greet.js"
            },
            {
              "p": "`await` works only inside an `async` function — and at the top level of a\nmodule. Anywhere else it is a `SyntaxError` before the code runs."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Inside an `async` function, what does `console.log(await Promise.resolve(7))` print?",
                          "accept": [
                            "7"
                          ],
                          "miss": "**If you answered a promise — that is what `await` removes.** `async` wraps values into promises; `await` unwraps them.\n",
                          "why": "`7`. `await` paused until the promise fulfilled, then gave you its value.\n"
                        },
                        {
                          "question": "`greet.js` — which prints first, `asked` or `Hello, Ada`? Type the line.",
                          "accept": [
                            "asked",
                            "\"asked\""
                          ],
                          "miss": "**If you answered `Hello, Ada` — `await` paused `greet`, not the program.** `greet(7)` returned a pending promise and the next line ran.\n",
                          "why": "`asked`. `greet` is paused at its `await`; the script carries on, and the greeting arrives later.\n"
                        },
                        {
                          "question": "Type the four letters in the order they print. `async function f() { console.log(\"b\"); await null; console.log(\"c\"); } console.log(\"a\"); f(); console.log(\"d\");`",
                          "accept": [
                            "a b d c",
                            "a,b,d,c",
                            "a, b, d, c",
                            "abdc"
                          ],
                          "miss": "**If `b` came after `d` — calling an async function runs its body at once, up to the first `await`.** Only the rest waits.\n",
                          "why": "`a`, then `b` (f runs until its `await`), then `d`, then `c` when f resumes.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`coro = f()`, where `f` is an `async def` — has the body of `f` run?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Calling an async function creates a **coroutine object** and runs nothing. Different from JavaScript, where the body runs synchronously up to the first `await`.\n"
                        },
                        {
                          "question": "What makes the coroutine actually run? Name the keyword or the call.",
                          "accept": [
                            "await",
                            "awaiting it",
                            "asyncio.run",
                            "the event loop"
                          ],
                          "why": "Awaiting it, or handing it to the loop. Python is lazier than JavaScript here, and forgetting is a common bug in both.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`var t = DoAsync();` — has the method started?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "It runs synchronously to the first `await`, like JavaScript and unlike Python.\n"
                        },
                        {
                          "question": "What does `.Result` do to the calling thread that `await` does not? A word or two.",
                          "accept": [
                            "blocks",
                            "blocks the thread",
                            "deadlocks",
                            "blocks and can deadlock"
                          ],
                          "why": "Blocks the calling thread — and in some contexts deadlocks. `await` pauses the method and gives the thread back.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "See the function resume",
          "body": [
            {
              "p": "\"Picks up where it paused\" is something the debugger can show you. Printing\ncannot: a print after the `await` says the line ran, but not who ran it, and a\nstack trace printed there lists only the paused function itself."
            },
            {
              "code": "async function load() {\n  console.log(\"A\");\n  const n = await Promise.resolve(5);\n  console.log(\"B \" + n);\n}\n\nfunction start() {\n  load();\n  console.log(\"2\");\n}\n\nstart();",
              "lang": "js",
              "file": "resume.js"
            },
            {
              "p": "Run it under the debugger as in *Meet the debugger*, with a breakpoint on\nline 4, the line after the `await`. When it pauses, read the **Call Stack**.\n`load` is on top — and nothing of yours is under it. `start` is not its caller\nany more: `start` finished long ago, and the host resumed `load` from the queue\nonce its promise had settled."
            },
            {
              "p": "Below that, both Chrome DevTools and VS Code draw a divider and then the\n**async** part of the stack: the frames that led to the `await`, among them\n`start` at line 8. That is where the work was started, kept for you by the\ndebugger because the real stack no longer holds it."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`resume.js`, paused on line 4 — has `2` printed yet?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "miss": "**If you answered no — `await` paused `load`, not `start`.** `load()` returned a pending promise at line 3, and `start` carried on to print `2`.\n",
                    "why": "Yes. The output so far is `A`, `2`; `B 5` is the line you are paused on.\n"
                  },
                  {
                    "question": "Paused on line 4, the part of the Call Stack above the divider — which of your functions is in it?",
                    "choices": [
                      "load",
                      "start",
                      "load and start"
                    ],
                    "accept": [
                      "load"
                    ],
                    "miss": "**If you included `start` — it returned before `load` resumed.** It appears only below the divider, as where the work began.\n",
                    "why": "Only `load`. Nothing of yours called it this time; the host resumed it.\n"
                  },
                  {
                    "question": "Paused on line 4, the async part of the Call Stack — `load` was started from `start` at which line? Type the line number.",
                    "accept": [
                      "8",
                      "line 8"
                    ],
                    "why": "Line 8, `load();` inside `start`. That frame is gone from the real stack; the async stack remembers it.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "The same chain, written with await",
          "body": [
            {
              "p": "You now have both forms. Here is one job written each way — the order total from\nthe promises lesson. The comments mark the same three subgoals in each; only\nthe syntax moved."
            },
            {
              "p": "**As a chain:**"
            },
            {
              "code": "function orderTotal(id) {\n  // get the order\n  return getOrder(id)\n    // get its prices\n    .then(order => getPrices(order.items))\n    // add up the prices\n    .then(prices => prices.reduce((a, b) => a + b, 0));\n}",
              "lang": "js",
              "file": "order-total-chain.js"
            },
            {
              "p": "**With `await`:**"
            },
            {
              "code": "async function orderTotal(id) {\n  // get the order\n  const order = await getOrder(id);\n  // get its prices\n  const prices = await getPrices(order.items);\n  // add up the prices\n  return prices.reduce((a, b) => a + b, 0);\n}",
              "lang": "js",
              "file": "order-total-await.js"
            },
            {
              "p": "The conversion is mechanical, in either direction:"
            },
            {
              "table": {
                "head": [
                  "Chain",
                  "`await`"
                ],
                "rows": [
                  [
                    "`return getOrder(id)` — the function returns the chain",
                    "`async function` — the function returns a promise anyway"
                  ],
                  [
                    "`.then(order => …)` — the link's parameter",
                    "`const order = await …;` — a variable"
                  ],
                  [
                    "the link's `return` value goes to the next link",
                    "the next line uses the variable"
                  ],
                  [
                    "the last link's value is what the promise fulfils with",
                    "`return` it"
                  ]
                ]
              }
            },
            {
              "p": "Both functions return a promise for the same total, so a caller cannot tell them\napart: `orderTotal(42).then(…)` works on either."
            },
            {
              "p": "A second pair, with a different shape — the team lookup:"
            },
            {
              "code": "function teamName(userId) {\n  return getUser(userId)\n    .then(user => getTeam(user.teamId))\n    .then(team => team.name);\n}",
              "lang": "js",
              "file": "team-name-chain.js"
            },
            {
              "code": "async function teamName(userId) {\n  const user = await getUser(userId);\n  const team = await getTeam(user.teamId);\n  return team.name;\n}",
              "lang": "js",
              "file": "team-name-await.js"
            },
            {
              "block": "notice",
              "label": "The missing await",
              "body": [
                {
                  "code": "async function orderTotal(id) {\n  const order = getOrder(id);                    // no await: order is a pending promise\n  const prices = await getPrices(order.items);   // order.items is undefined\n}",
                  "lang": "js"
                },
                {
                  "p": "It is the missing `return` from the chain, in the other form. Nothing fails on\nthe line that forgot; the next line gets a promise where it expected an order."
                }
              ]
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`return getUser(1).then(user => user.email);` — the `await` version is two lines. The second is `return user.email;`. Type the first.",
                    "accept": [
                      "const user = await getUser(1)",
                      "let user = await getUser(1)",
                      "const user=await getUser(1)",
                      "let user=await getUser(1)"
                    ],
                    "miss": "**If you left out `await` — `user` would be the pending promise, and `user.email` would be `undefined`.** The link's parameter becomes a variable holding the *awaited* value.\n",
                    "why": "`const user = await getUser(1);`. The link's parameter became a variable, and the link's return value became the function's.\n"
                  },
                  {
                    "question": "`async function f() { const n = await count(); return n + 1; }` — written as a chain, the body is `return count().then(______)`. Type what fills the blank.",
                    "accept": [
                      "n => n + 1",
                      "n=>n+1",
                      "(n) => n + 1",
                      "(n)=>n+1",
                      "n => n+1",
                      "n=> n + 1",
                      "n =>n + 1",
                      "function (n) { return n + 1; }",
                      "function(n) { return n + 1; }"
                    ],
                    "why": "`n => n + 1`. The awaited variable became the link's parameter, and the line after it became the link's body.\n"
                  },
                  {
                    "question": "`order-total-await.js` without the word `async` — what happens? A few words, or the name of the error.",
                    "accept": [
                      "SyntaxError",
                      "syntax error",
                      "error",
                      "a syntax error",
                      "a SyntaxError",
                      "it does not run",
                      "nothing runs"
                    ],
                    "why": "A `SyntaxError`: `await` is only allowed inside an `async` function (or at a module's top level). The file does not run at all.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "A try around an await catches the rejection",
          "body": [
            {
              "p": "In the promises lesson, a `try` around a call caught nothing its promise did —\nthe `try` had finished before the rejection arrived. **`await` changes that.** The\nfunction pauses *inside* the `try` until the promise settles, and a rejection\ncomes back as a throw on the `await` line. The `try` is still running, so its\n`catch` gets it."
            },
            {
              "p": "So a chain's `.catch` becomes a `try`/`catch` around the awaits:"
            },
            {
              "code": "function orderTotal(id) {\n  return getOrder(id)\n    .then(order => getPrices(order.items))\n    .then(prices => prices.reduce((a, b) => a + b, 0))\n    .catch(err => {                       // a failure anywhere above lands here\n      console.log(\"could not total: \" + err.message);\n      return 0;\n    });\n}",
              "lang": "js",
              "file": "order-total-safe-chain.js"
            },
            {
              "code": "async function orderTotal(id) {\n  try {\n    const order = await getOrder(id);\n    const prices = await getPrices(order.items);\n    return prices.reduce((a, b) => a + b, 0);\n  } catch (err) {                         // a failure anywhere above lands here\n    console.log(\"could not total: \" + err.message);\n    return 0;\n  }\n}",
              "lang": "js",
              "file": "order-total-safe-await.js"
            },
            {
              "p": "The `try` must cover the same steps the `.catch` covered — every `await` whose\nfailure it should handle."
            },
            {
              "p": "**`await` pauses only the function it is written in.** That is the rule behind\nthe commonest async bug with a `try` in it:"
            },
            {
              "code": "async function saveAll(orders) {\n  try {\n    orders.forEach(async order => {\n      await saveOrder(order);    // pauses this little arrow function — not saveAll\n    });\n    console.log(\"all saved\");    // prints at once; nothing has finished\n  } catch (err) {\n    console.log(\"failed\");       // never sees a rejection\n  }\n}",
              "lang": "js",
              "file": "save-all.js"
            },
            {
              "p": "Each arrow function is its own `async` function, called by `forEach`, which\nthrows away the promise each one returns. `saveAll` awaits nothing, so its `try`\nis the one from the promises lesson again. A `for...of` loop with `await` in\n`saveAll`'s own body waits for each save in turn, and its `try` sees a failure."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`order-total-safe-await.js` — `getPrices` rejects. What value does `orderTotal(42)` fulfil with?",
                    "accept": [
                      "0"
                    ],
                    "miss": "**If you answered that it rejects — the `await` turned the rejection into a throw inside the `try`.** The `catch` handled it and returned `0`.\n",
                    "why": "`0`. The rejection came back as a throw on the second `await`, and the `catch` returned `0`.\n"
                  },
                  {
                    "question": "`try { await saveOrder(order); } catch (e) { console.log(\"failed\"); }` — `saveOrder` rejects. Does `failed` print?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "miss": "**If you answered no — that was true without the `await`.** With it, the function waits inside the `try`, so the rejection arrives while it is still running.\n",
                    "why": "Yes. `await` keeps the `try` running until the promise settles.\n"
                  },
                  {
                    "question": "`save-all.js` — when `all saved` prints, how many orders have finished saving? Type a number.",
                    "accept": [
                      "0",
                      "none",
                      "zero",
                      "no orders"
                    ],
                    "why": "None. Every save has started and none has settled. `saveAll` never waited, because the `await`s are inside the arrow functions.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "step",
          "label": "Start them all, then wait",
          "body": [
            {
              "p": "`await` one after another makes each step wait for the last. That is right when\na step needs the previous result — the prices need the order. When two pieces of\nwork do not depend on each other, waiting in turn is the commonest async\nperformance mistake:"
            },
            {
              "code": "// about 2 seconds if each takes 1 — the second does not start until the first is done\nconst user = await getUser(id);\nconst orders = await getOrders(id);",
              "lang": "js",
              "file": "one-at-a-time.js"
            },
            {
              "p": "**`Promise.all(promises)`** takes an array of promises and returns one promise,\nwhich fulfils with an array of all their values, in the same order — once every\none has fulfilled:"
            },
            {
              "code": "// start both — nothing is awaited yet\nconst userP = getUser(id);\nconst ordersP = getOrders(id);\n// then wait for both at once\nconst [user, orders] = await Promise.all([userP, ordersP]);\n// about 1 second: both were in flight at the same time",
              "lang": "js",
              "file": "together.js"
            },
            {
              "p": "Starting and waiting can share one line:"
            },
            {
              "code": "const [user, orders] = await Promise.all([getUser(id), getOrders(id)]);",
              "lang": "js",
              "file": "together-short.js"
            },
            {
              "p": "Two things to know about it:"
            },
            {
              "list": [
                "**It rejects as soon as any one rejects**, with that error — and the other results are lost. **`Promise.allSettled`** waits for every one and reports each outcome; use it when one failure should not discard the rest.",
                "It starts nothing. The work starts when you **call** `getUser(id)`; `Promise.all` only waits. So call everything first, then wait."
              ]
            },
            {
              "tracks": {
                "js": [
                  {
                    "p": "`async`/`await` by default, `Promise.all` for independent work, and always\n`await` inside `try`."
                  },
                  {
                    "code": "const [user, orders] = await Promise.all([getUser(id), getOrders(id)]);",
                    "lang": "js"
                  },
                  {
                    "p": "For independent work in a loop, do not `await` inside the loop — that is the\none-at-a-time version. Build an array of promises and `Promise.all` it."
                  },
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`getA` and `getB` each take 1 second and do not depend on each other. `const a = await getA(); const b = await getB();` — about how long? Type a number of seconds.",
                          "accept": [
                            "2 seconds",
                            "2",
                            "2s",
                            "two seconds",
                            "2 s",
                            "about 2 seconds"
                          ],
                          "why": "About 2 seconds. `getB` is not called until `getA`'s promise has settled.\n"
                        },
                        {
                          "question": "`await Promise.all([getA(), getB()])` — about how long? Type a number of seconds.",
                          "accept": [
                            "1 second",
                            "1",
                            "1s",
                            "one second",
                            "1 s",
                            "about 1 second"
                          ],
                          "why": "About 1 second. Both were called — both started — before anything waited.\n"
                        },
                        {
                          "question": "`await Promise.all([Promise.resolve(1), Promise.reject(new Error(\"x\")), Promise.resolve(3)])` — does it fulfil or throw?",
                          "choices": [
                            "It fulfils",
                            "It throws"
                          ],
                          "accept": [
                            "It throws"
                          ],
                          "why": "It throws `x` at the `await`: `Promise.all` rejects as soon as one input rejects. `Promise.allSettled` would report all three.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "p": "`async`/`await` over `asyncio`, and remember a coroutine does nothing until\nawaited."
                  },
                  {
                    "code": "user, orders = await asyncio.gather(get_user(id), get_orders(id))",
                    "lang": "python"
                  },
                  {
                    "p": "`asyncio.gather` is `Promise.all`. Python also has real threads and processes,\nso \"async\" and \"concurrent\" are genuinely separate choices here in a way they are\nnot in JavaScript."
                  }
                ],
                "csharp": [
                  {
                    "p": "`async`/`await` over tasks, `Task.WhenAll` for parallel work."
                  },
                  {
                    "code": "var results = await Task.WhenAll(GetUser(id), GetOrders(id));",
                    "lang": "csharp"
                  },
                  {
                    "p": "Never `.Result` or `.Wait()` — they block a thread and can deadlock. Because the\nwork may be genuinely parallel, two tasks can touch shared state at once — the\nconcern JavaScript does not have in ordinary code."
                  }
                ]
              }
            }
          ],
          "n": 6
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A page loads a user, then their orders, then each order's items — 40 requests,\nawaited one after another in nested loops. It takes three seconds."
            },
            {
              "p": "Two directions:"
            },
            {
              "list": [
                "**Start the independent requests together** with `Promise.all`.",
                "**Change the API** so one request returns the whole tree."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what 40 requests at once do to\nthe server, what happens to the first approach when one request fails, and what\nthe second costs in flexibility. Say which you would do first if you owned both\nsides, and which if you owned only the page."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "An **`async` function always returns a promise**: its `return` fulfils it, its `throw` rejects it. Its body runs at once, up to the first `await`.",
                "**`await`** pauses **only this function** until the promise settles, then gives the value — or throws the rejection, so a **`try` around an `await` catches it**.",
                "A chain and its `await` version are the same work: each `.then` parameter is an awaited variable, `.catch` is `try`/`catch`. For independent work, **start it all, then `await Promise.all`**."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "async function",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/async_function",
              "note": "including that the body runs synchronously to the first `await`."
            },
            {
              "title": "await",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/await",
              "note": "what it does with a rejection, and where it is allowed."
            },
            {
              "title": "Promise.all()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all",
              "note": "and how it differs from `allSettled` when one input rejects."
            }
          ]
        }
      ],
      "_source": "lessons/await-syntax.md",
      "practice": {
        "id": "await-syntax",
        "outcome": 9,
        "steps": [
          {
            "kind": "predict",
            "title": "Before you start",
            "prompt": "Three questions, from what you already know.",
            "items": [
              {
                "id": "p1",
                "code": "async function f() { return 1; }   —   typeof f()",
                "accept": [
                  "\"object\"",
                  "object",
                  "'object'"
                ],
                "miss": "If you said \"number\": an `async` function never returns its value directly. It returns a promise for it.",
                "why": "An `async` function always returns a promise, even when its body returns a plain value and never awaits."
              },
              {
                "id": "p2",
                "question": "You are rewriting a promise chain with `await`. Type the word that must go in front of `function`.",
                "accept": [
                  "async"
                ],
                "why": "`async`. Without it, `await` in the body is a SyntaxError — and it is what keeps the function returning a promise, as the chain did."
              },
              {
                "id": "p3",
                "question": "`async function save() { try { await saveOrder(order); } catch (e) { retry(); } }` — `saveOrder` rejects. Does `retry()` run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "miss": "If you said no: that was true of a `try` around a promise with no `await`. With `await`, the function waits inside the `try`, and the rejection comes back as a throw there.",
                "why": "Yes. `await` turns the rejection into a throw on that line, while the `try` is still running."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "What prints? `async function f() { return 1; } f().then(v => console.log(v))`",
                "accept": [
                  "1"
                ],
                "why": "`1`. The promise `f()` returned fulfils with what the body returned."
              },
              {
                "id": "p5",
                "question": "Inside an `async` function, what does `console.log(await Promise.resolve(7))` print?",
                "accept": [
                  "7"
                ],
                "why": "`await` unwraps the promise and gives you the value. `async` wraps values into promises; `await` unwraps them."
              },
              {
                "id": "p6",
                "question": "`async function f() { throw new Error(\"x\"); }` — does the line `f();` throw at the call site?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. Inside an `async` function a throw rejects the returned promise. Nothing is thrown at the call site, and without `await` or `.catch` it becomes an unhandled rejection."
              }
            ],
            "hints": [
              {
                "when": "you said f() returns a number",
                "say": "Look at the word before `function`. That keyword changes what every call returns, whatever the body says."
              },
              {
                "when": "you expected the catch not to run",
                "say": "Compare with the promises lesson: what was missing there that is here? Ask where the function is while the promise is pending."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "When does an async body run?",
            "prompt": "`async function load() { console.log(\"A\"); await null; console.log(\"B\"); return 5; }` — then the script runs `console.log(\"1\"); const r = load(); console.log(\"2\"); r.then(v => console.log(v));`\n",
            "items": [
              {
                "id": "t1",
                "question": "What has printed by the time `load()` returns to the script? Type each line, in order.",
                "accept": [
                  "1 A",
                  "1, A",
                  "1,A",
                  "1A"
                ],
                "miss": "If you said only 1: calling an async function runs its body straight away, up to the first `await`.",
                "why": "The body runs synchronously up to the first `await`, so `A` prints during the call. At the `await`, `load` pauses and control returns to the script."
              },
              {
                "id": "t2",
                "question": "`r` is a promise. What state is it in when `2` prints?",
                "choices": [
                  "pending",
                  "fulfilled",
                  "rejected"
                ],
                "accept": [
                  "pending"
                ],
                "why": "Pending. `load` is paused at its `await` and has not reached `return 5` yet."
              },
              {
                "id": "t3",
                "question": "Type the full output, in order.",
                "accept": [
                  "1 A 2 B 5",
                  "1, A, 2, B, 5",
                  "1,A,2,B,5",
                  "1A2B5"
                ],
                "why": "Synchronous: `1`, `A` (the body up to `await`), `2`. Then `load` resumes: `B`, and its promise fulfils with `5`, which the `.then` prints."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "`async function f() { await null; throw new Error(\"x\"); }` — `const p = f();` What state is `p` in once everything has run?",
                "choices": [
                  "pending",
                  "fulfilled",
                  "rejected"
                ],
                "accept": [
                  "rejected"
                ],
                "why": "Rejected. A throw inside an `async` function rejects the promise it returned — here after the `await`, so `p` was pending first."
              },
              {
                "id": "t5",
                "question": "What value does `f()` fulfil with? `async function f() { const a = await Promise.resolve(2); const b = await Promise.resolve(a * 3); return a + b; }`",
                "accept": [
                  "8"
                ],
                "why": "`a` is `2`, `b` is `6`, and the function returns `8` — which is what its promise fulfils with."
              }
            ],
            "hints": [
              {
                "when": "you put A after 2",
                "say": "Calling an async function is not the same as scheduling it. It starts running at once, and only the `await` hands control back."
              },
              {
                "when": "you said r was fulfilled",
                "say": "The promise fulfils when the function body reaches its `return`. Where is `load` when \"2\" prints?"
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Rewrite the chain with await",
            "prompt": "Rewrite this with `async`/`await`, same behaviour: `return getOrder(id).then(order => getPrices(order.items)).then(prices => prices.reduce((a, b) => a + b, 0)).catch(err => { throw new Error(…, { cause: err }); })`. Two of the lines are mistakes.\n",
            "lines": [
              "async function loadTotal(id) {",
              "  try {",
              "    const order = await getOrder(id);",
              "    const prices = await getPrices(order.items);",
              "    return prices.reduce((a, b) => a + b, 0);",
              "  } catch (err) {",
              "    throw new Error(`could not total order ${id}`, { cause: err });",
              "  }",
              "}"
            ],
            "distractors": [
              {
                "line": "function loadTotal(id) {",
                "why": "Without `async`, `await` inside the function is a SyntaxError. The keyword is also what makes it return a promise, as the chain did."
              },
              {
                "line": "    const order = getOrder(id);",
                "why": "The missing `await`. `order` would be a pending promise, not an order, so `order.items` is `undefined` and `getPrices` is handed nothing."
              }
            ],
            "why": "Each `.then` becomes an `await` on its own line, and `.catch` becomes the `try`/`catch` around them — which works only because every promise inside is awaited. `loadTotal` still returns a promise, because it is `async`: the caller awaits it exactly as it awaited the chain.\n",
            "hints": [
              {
                "when": "you are unsure which getOrder line to use",
                "say": "The next line reads `order.items`. What is `order` if you did not wait for it?"
              },
              {
                "when": "the catch is in the wrong place",
                "say": "The `.catch` sat at the end of the chain, covering every step before it. The `try` has to cover the same steps."
              }
            ]
          },
          {
            "kind": "complete",
            "title": "And back — the chain from the await version",
            "prompt": "The comment shows `firstItemName` written with `await`. The chain below is the same function with one link missing. Fill the gap so it gives the name of the order's first item. Then, in one sentence: what in the `await` version became the parameter of your link?\n",
            "exportName": "firstItemName",
            "filename": "first-item.js",
            "starter": "// getOrder(id) returns a promise for { itemIds: [...] }.\n// getItem(itemId) returns a promise for { name }.\nfunction getOrder(id) { return Promise.resolve({ itemIds: [7, 8] }); }\nfunction getItem(itemId) { return Promise.resolve({ name: itemId === 7 ? \"pen\" : \"ink\" }); }\n\n// The await version:\n//   async function firstItemName(orderId) {\n//     const order = await getOrder(orderId);\n//     const item = await getItem(order.itemIds[0]);\n//     return item.name;\n//   }\nfunction firstItemName(orderId) {\n  return getOrder(orderId)\n    .then(/* ___ */)\n    .then(item => item.name);\n}\n",
            "tests": [
              {
                "label": "gives the first item's name",
                "body": "return (await subject(1)) === 'pen';"
              },
              {
                "label": "looks the item up, for a different order too",
                "body": "function getOrder(id) { return new Promise(function (ok) { setTimeout(function () { ok({ itemIds: [8] }); }, 20); }); } function getItem(itemId) { return Promise.resolve({ name: itemId === 7 ? 'pen' : 'ink' }); } const firstItemName = eval('(' + subject.toString() + ')'); return (await firstItemName(2)) === 'ink';"
              },
              {
                "label": "stays a chain — no async, no await",
                "body": "return subject.constructor.name === 'Function' && !/\\bawait\\b/.test(subject.toString());"
              },
              {
                "label": "returns a promise",
                "body": "const p = subject(1); if (p && typeof p.catch === 'function') p.catch(() => {}); return p instanceof Promise;"
              }
            ],
            "hints": [
              {
                "when": "the name comes back undefined",
                "say": "Look at what your link returns. If it is the id, or nothing, the last link is not holding an item. It has to return the promise `getItem` returns."
              },
              {
                "when": "nothing comes back at all",
                "say": "Braces around an arrow's body need `return`. Without it, the last link receives `undefined` and throws on `.name`."
              },
              {
                "when": "the file will not run",
                "say": "`await` is not allowed in a function without `async` — and this one stays a chain. The link's parameter is what the `await` version called `order`."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Rewrite a chain with await",
            "prompt": "`orderTotal` works. Rewrite it with `async` and `await` — same behaviour, no `.then` or `.catch` left. Then, in one sentence: what does `orderTotal(1)` return now, and how is that different from before?\n",
            "exportName": "orderTotal",
            "filename": "order-total.js",
            "starter": "// getOrder(id) returns a promise for { items: [...] }; it rejects for an unknown id.\n// getPrices(items) returns a promise for an array of prices; it rejects for no items.\nfunction getOrder(id) {\n  return id === 1\n    ? Promise.resolve({ items: [\"pen\", \"ink\"] })\n    : Promise.reject(new Error(\"no order \" + id));\n}\nfunction getPrices(items) {\n  return items.length > 0\n    ? Promise.resolve(items.map(() => 5))\n    : Promise.reject(new Error(\"no prices\"));\n}\n\nfunction orderTotal(id) {\n  return getOrder(id)\n    .then(order => getPrices(order.items))\n    .then(prices => prices.reduce((a, b) => a + b, 0))\n    .catch(err => {\n      throw new Error(\"could not total order \" + id, { cause: err });\n    });\n}\n",
            "tests": [
              {
                "label": "orderTotal is an async function",
                "body": "return subject.constructor.name === 'AsyncFunction';"
              },
              {
                "label": "no .then or .catch left",
                "body": "return !/\\.(then|catch)\\s*\\(/.test(subject.toString());"
              },
              {
                "label": "totals an order",
                "body": "return (await subject(1)) === 10;"
              },
              {
                "label": "a failed order lookup throws an error naming the order",
                "body": "try { await subject(2); return false; } catch (e) { return e.message === 'could not total order 2' && !!e.cause && e.cause.message === 'no order 2'; }"
              },
              {
                "label": "a failed price lookup is caught the same way",
                "body": "function getOrder(id) { return new Promise(function (ok) { setTimeout(function () { ok({ items: [] }); }, 20); }); } function getPrices(items) { return items.length > 0 ? Promise.resolve(items.map(function () { return 5; })) : Promise.reject(new Error('no prices')); } const orderTotal = eval('(' + subject.toString() + ')'); try { await orderTotal(3); return false; } catch (e) { return e.message === 'could not total order 3' && !!e.cause && e.cause.message === 'no prices'; }"
              }
            ],
            "hints": [
              {
                "when": "totals an order fails",
                "say": "Check every call that returns a promise. If one has no `await`, the next line is handed a pending promise instead of an order or a list of prices."
              },
              {
                "when": "the error is not the one naming the order",
                "say": "The `.catch` became a `try`/`catch`. Does your `try` cover both `await`s, and does your `catch` throw the new error with the old one as its `cause`?"
              },
              {
                "when": "the failed order is caught but the failed price lookup is not",
                "say": "The `.catch` covered every link above it. Your `try` has to start before the first `await` and end after the last."
              }
            ]
          },
          {
            "kind": "write",
            "title": "Start them all, then wait",
            "prompt": "Write `startAll(tasks)`. Each task is a function returning a promise. Call **every** task immediately and return an array of the promises — do not wait for one before starting the next. Return `[]` for no tasks. Then, in one sentence: how would a caller wait for all of them at once?\n",
            "exportName": "startAll",
            "filename": "start-all.js",
            "starter": "// tasks is an array of functions, each returning a promise.\n// Call them all now; return the array of promises.\nfunction startAll(tasks) {\n\n}\n",
            "tests": [
              {
                "label": "returns one promise per task",
                "body": "const t = [function () { return Promise.resolve(1); },\n           function () { return Promise.resolve(2); }];\nreturn subject(t).length === 2;"
              },
              {
                "label": "every task was started",
                "body": "let n = 0; const t = [function () { n++; return Promise.resolve(); },\n           function () { n++; return Promise.resolve(); },\n           function () { n++; return Promise.resolve(); }];\nsubject(t); return n === 3;"
              },
              {
                "label": "all three started before the function returned",
                "body": "const order = []; const t = [function () { order.push('a'); return Promise.resolve(); },\n           function () { order.push('b'); return Promise.resolve(); }];\nsubject(t); return order.join(',') === 'a,b';"
              },
              {
                "label": "the returned values are promises",
                "body": "const t = [function () { return Promise.resolve(1); }]; return typeof subject(t)[0].then === 'function';"
              },
              {
                "label": "no tasks gives an empty array",
                "body": "return Array.isArray(subject([])) && subject([]).length === 0;"
              },
              {
                "label": "the caller's array is not modified",
                "body": "const t = [function () { return Promise.resolve(1); }]; subject(t); return t.length === 1 && typeof t[0] === 'function';"
              }
            ],
            "hints": [
              {
                "when": "only the first task ran",
                "say": "You are awaiting inside the loop, so nothing after the first call starts until it settles. Call them all first — waiting comes after."
              },
              {
                "when": "the returned values are not promises",
                "say": "You are calling the tasks and discarding what they return. Collect the promise each one returns — that is what the caller waits on."
              },
              {
                "when": "you made startAll async",
                "say": "It does not need to be. Starting work is synchronous; only *waiting* for it is not. Returning the promises lets the caller choose how to wait."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a save routine",
            "prompt": "An AI assistant produced this, and it looks finished: there is a `try`, a log line, an `await`. `saveOrder`, `getUser`, `getNews` and `getWeather` return promises; the last three take about a second each and do not depend on each other. Review it.\n",
            "filename": "save.js",
            "code": "async function saveAll(orders) {\n  try {\n    orders.forEach(async (order) => {\n      await saveOrder(order);\n    });\n    console.log(\"All orders saved\");\n  } catch (err) {\n    console.error(\"Failed to save orders:\", err);\n  }\n}\n\nasync function dashboard(id) {\n  const user = await getUser(id);\n  const news = await getNews();\n  const weather = await getWeather();\n  return { user, news, weather };\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line starts work that `saveAll` never waits for? Type the line number.",
                "accept": [
                  "3",
                  "line 3"
                ],
                "why": "`forEach` calls the async callback for each order and throws away the promise each call returns. The `await` on line 4 pauses only the callback, never `saveAll`."
              },
              {
                "id": "c2",
                "question": "When line 6 prints, how many orders have finished saving? Type a number.",
                "accept": [
                  "0",
                  "none",
                  "zero",
                  "no orders"
                ],
                "why": "None. Every save has started and none has settled — line 6 runs straight after `forEach` returns. The log line is a false report."
              },
              {
                "id": "c3",
                "question": "One `saveOrder` rejects. Does line 8 run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The rejection happens inside a promise `saveAll` never awaited, after its `try` has finished. It surfaces as an unhandled rejection, far from this code."
              },
              {
                "id": "c4",
                "question": "About how long does `dashboard(1)` take? Type a number of seconds.",
                "accept": [
                  "3 seconds",
                  "3",
                  "3s",
                  "three seconds",
                  "3 s",
                  "about 3 seconds"
                ],
                "why": "About three. Each `await` waits for its request before the next one is even started, though none needs the others' results."
              },
              {
                "id": "c5",
                "question": "What should lines 13–15 become, to take about one second? Name the call they should share.",
                "accept": [
                  "promise.all",
                  "Promise.all",
                  "await Promise.all",
                  "one await Promise.all",
                  "Promise.all([getUser(id), getNews(), getWeather()])"
                ],
                "why": "`const [user, news, weather] = await Promise.all([getUser(id), getNews(), getWeather()]);` — start all three, then wait once."
              }
            ],
            "hints": [
              {
                "when": "you picked line 4 for the work nobody waits for",
                "say": "Line 4 does wait — for one order, inside its own callback. Look at what calls that callback, and at what it does with the promise each call returns."
              },
              {
                "when": "you expected the catch to run",
                "say": "A `try` sees a rejection only through an `await` inside the same function. Which promise does `saveAll` itself await?"
              },
              {
                "when": "you said dashboard takes about a second",
                "say": "When is `getNews()` called — before or after `getUser`'s promise has settled?"
              }
            ]
          }
        ]
      }
    },
    "event-loop": {
      "id": "event-loop",
      "title": "The event loop, slowly",
      "kind": "read",
      "outcome": 9,
      "shape": "conceptual",
      "why": "Outcome 9 asks the student to explain how the runtime schedules work that does not finish immediately. `async.scheduling` is that model — the loop, its two queues, and the one thread they share — shown by predicting output order and by spotting code that blocks (D-015). The queues were taught twice, in `work-that-waits` and here; they are taught once, here (D-017, D-029).",
      "concepts": [
        "async.scheduling"
      ],
      "lede": "A promise callback, a timer, a click: each waits for its turn on the one thread that runs your code. The loop that hands out those turns follows a fixed rule, so the order things happen in is predictable — and code that never gives the thread back freezes everything else.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "async and await · The same code in another host · Event loops, threads and coroutines"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Call stack** — the functions running right now, innermost on top. **Task** — a callback the host has queued: a timer, a click, a finished read. **Microtask** — a promise callback, or the rest of an `async` function after an `await`.",
          "entries": [
            "**Call stack** — the functions running right now, innermost on top.",
            "**Task** — a callback the host has queued: a timer, a click, a finished read.",
            "**Microtask** — a promise callback, or the rest of an `async` function after an `await`."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about who gets the thread, and when. A guess is fine; that is\nwhat this is for."
            },
            {
              "code": "console.log(\"a\");\nsetTimeout(() => console.log(\"b\"), 0);\nPromise.resolve().then(() => console.log(\"c\"));\nconsole.log(\"d\");",
              "lang": "js",
              "file": "order.js"
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`order.js` — type the four letters in the order they print.",
                          "accept": [
                            "a d c b",
                            "a,d,c,b",
                            "a, d, c, b",
                            "adcb"
                          ],
                          "miss": "**If `b` came before `c`, you read `setTimeout(…, 0)` as \"now\", or as \"first in line because it was queued first\".** A promise callback waits in a different queue, and that queue is always emptied before the next timer runs.\n",
                          "why": "`a` and `d` first — they run now. Then `c`, a promise callback. Then `b`, the timer, even at `0`.\n"
                        },
                        {
                          "question": "`setTimeout(fn, 0)` — is `fn` guaranteed to run in 0ms?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes — `0` means \"no extra delay\", not \"now\".** `fn` waits until the code running now has finished, and every promise callback ahead of it too.\n",
                          "why": "No. The real wait is however long the thread stays busy.\n"
                        },
                        {
                          "question": "`while (true) {}` runs in a browser tab. What still responds?",
                          "choices": [
                            "Clicks and scrolling",
                            "Nothing"
                          ],
                          "accept": [
                            "Nothing"
                          ],
                          "miss": "**If you expected clicks or scrolling to keep working — one thread runs your code *and* handles them.** Occupy it and the page is frozen, not slow.\n",
                          "why": "Nothing. The clicks, the painting and the timers all need the thread your loop is holding.\n"
                        },
                        {
                          "question": "Once both calls have finished, what value is `stock`? `let stock = 1; async function buy() { if (stock > 0) { await pay(); stock--; } } buy(); buy();`",
                          "accept": [
                            "-1"
                          ],
                          "miss": "**If you answered `0` — \"one thread\" does not mean \"one thing at a time from start to finish\".** Each `buy` checks `stock`, then steps aside at its `await`. The second one checks while the first is paused.\n",
                          "why": "`-1`. Both calls checked `stock > 0` before either reached `stock--`, because each paused at `await` in between.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Type the three numbers in the order they print. `print(1); asyncio.get_event_loop().call_soon(lambda: print(4)); print(2)`",
                          "accept": [
                            "1 2 4",
                            "1,2,4",
                            "124"
                          ],
                          "miss": "**If `4` came second — `call_soon` queues the callback; it does not call it.** The loop runs it once the current code is finished.\n",
                          "why": "Callbacks scheduled on the loop run after the current synchronous code finishes. Same principle, different API.\n"
                        },
                        {
                          "question": "Is there a microtask/macrotask split in `asyncio`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Not as two distinct queues. `asyncio` has one ready queue, which makes the ordering simpler and less surprising than JavaScript's.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can explain what happens between starting asynchronous work and its completion running",
                "I can predict the output order of interleaved synchronous and asynchronous code",
                "I can spot code that blocks the only thread"
              ]
            }
          ]
        },
        {
          "h": "One thread, and who is doing the waiting"
        },
        {
          "p": "There is one **call stack** — the functions running right now. When your code\nruns, it runs **to completion**: a function that starts finishes before anything\nelse of yours gets a turn. No timer callback, no click handler, no promise\ncallback can cut into the middle of it."
        },
        {
          "p": "So what happens to a request that takes two seconds?"
        },
        {
          "code": "const p = fetch(\"/orders\");\nconsole.log(\"asked\");",
          "lang": "js",
          "file": "wait.js"
        },
        {
          "p": "Your thread is not waiting. `fetch` handed the request to the host — its network\ncode, running outside your thread — and returned a pending promise at once.\n`asked` prints. When the response arrives, the host **queues** your next step, and\nit runs when your thread is free."
        },
        {
          "p": "That is why `await` does not block, and why one thread can have thousands of\nrequests in flight. The waiting is real, and it happens somewhere else. What comes\nback to your thread is only the work to do once the wait is over."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`wait.js` — while the request is in flight, is your thread waiting for it?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you answered yes — the host does the waiting.** Your thread has moved on to the next line, and to anything else there is to do.\n",
                "why": "No. The host holds the request; your thread is free.\n"
              },
              {
                "question": "`fetch(url).then(show); while (true) {}` — does `show` ever run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you answered yes, once the response arrives — arriving is not running.** The host queues `show`, and the queue is only read when the stack is empty. It never is.\n",
                "why": "Never. The response can arrive, but `show` needs the thread, and the loop never gives it up.\n"
              }
            ]
          }
        },
        {
          "h": "Two queues, and the loop that reads them"
        },
        {
          "p": "What comes back is put in one of two queues."
        },
        {
          "table": {
            "head": [
              "Queue",
              "Holds",
              "When it runs"
            ],
            "rows": [
              [
                "**Microtasks**",
                "promise callbacks (`.then`, `.catch`), the rest of an `async` function after `await`, `queueMicrotask`",
                "as soon as the stack is empty — **all of them**, including any added while they run"
              ],
              [
                "**Tasks**",
                "timers, finished I/O, clicks and other events",
                "**one** at a time, and only when the microtask queue is empty"
              ]
            ]
          }
        },
        {
          "p": "The **event loop** is the rule for reading them:"
        },
        {
          "list": [
            "Run the code in front of you until the call stack is empty.",
            "Run every microtask, until the microtask queue is empty — including microtasks queued by microtasks.",
            "Take **one** task and run it to completion.",
            "Back to 2."
          ],
          "ordered": true
        },
        {
          "p": "That is why `c` beat `b` in the opening: the promise callback was a microtask,\nand every microtask runs before the next task — whichever was queued first."
        },
        {
          "video": {
            "title": "Two queues, stepped through",
            "caption": "The opening program run one step at a time, with the call stack and both queues drawn as each line runs. Pauses before the output to let you predict.\n"
          }
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "A microtask, while it runs, queues another microtask. A timer is already waiting. Which runs next?",
                "choices": [
                  "The new microtask",
                  "The timer"
                ],
                "accept": [
                  "The new microtask"
                ],
                "miss": "**If you answered the timer because it was waiting first — step 2 runs *every* microtask, including ones added during the drain.** The timer waits until the queue is empty.\n",
                "why": "The new microtask. The drain is not finished until the microtask queue is empty, however the microtasks got there.\n"
              },
              {
                "question": "Two timers are due, and one promise callback is queued. How many timers can run before the promise callback? Type a number.",
                "accept": [
                  "0",
                  "none",
                  "zero",
                  "no timers"
                ],
                "why": "None. Microtasks drain before the loop takes any task.\n"
              }
            ]
          }
        },
        {
          "h": "Predicting the order"
        },
        {
          "p": "The rule becomes a procedure: synchronous code, then microtasks, then one task,\nthen microtasks again. Here it is on the opening program."
        },
        {
          "code": "// run every synchronous line, top to bottom\nconsole.log(\"a\");                                  // prints a\nsetTimeout(() => console.log(\"b\"), 0);             // queues a task\nPromise.resolve().then(() => console.log(\"c\"));    // queues a microtask\nconsole.log(\"d\");                                  // prints d\n\n// drain the microtasks                   -> c\n// run one task, then drain again         -> b\n// Output: a d c b",
          "lang": "js",
          "file": "order.js"
        },
        {
          "p": "The same procedure, with an `async` function in it. Recall that calling one runs\nits body at once, up to the first `await`."
        },
        {
          "code": "async function report() {\n  console.log(\"report starts\");\n  await null;\n  console.log(\"report resumes\");\n}\n\n// synchronous lines, top to bottom\nsetTimeout(() => console.log(\"timer\"), 0);   // queues a task\nreport();                                    // prints \"report starts\"; the rest is queued as a microtask\nconsole.log(\"script ends\");                  // prints \"script ends\"\n\n// then the microtasks                       -> report resumes\n// then one task                             -> timer",
          "lang": "js",
          "file": "report.js"
        },
        {
          "p": "`report starts` prints with the rest of the synchronous code, because the call ran it.\nEverything after the `await` is a microtask, so it beats the timer."
        },
        {
          "scaffold": {
            "label": "Predicting the order",
            "body": [
              {
                "h": "Predicting the order"
              },
              {
                "code": "// 1. Run every synchronous line, top to bottom. These all go first,\n//    no exceptions — including an async function's body up to its\n//    first await.\n\n// 2. Drain the MICROTASK queue completely.\n//    .then callbacks, await resumptions, queueMicrotask.\n//    A microtask that queues another microtask runs it in this same drain.\n\n// 3. Run ONE task (a timer, I/O, an event).\n\n// 4. Drain microtasks again. Then the next task. Repeat.",
                "lang": "js",
                "file": "order-steps.js"
              },
              {
                "p": "Now apply it."
              },
              {
                "code": "console.log(\"a\");\nsetTimeout(() => console.log(\"b\"), 0);\nPromise.resolve().then(() => {\n  console.log(\"c\");\n  Promise.resolve().then(() => console.log(\"d\"));\n});\nconsole.log(\"e\");\n\n// Order: ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "`a e c d b`. `d` was queued *during* the microtask drain, so it runs in that same\ndrain — before the timer, even though the timer was queued first."
              }
            ]
          }
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "Type the three letters in the order they print. `setTimeout(() => console.log(\"t\"), 0); queueMicrotask(() => console.log(\"m\")); console.log(\"s\");`",
                "accept": [
                  "s m t",
                  "s,m,t",
                  "s, m, t",
                  "smt"
                ],
                "why": "`s` runs now; `m` is a microtask; `t` is a task and waits for every microtask.\n"
              },
              {
                "question": "Type what prints, in order. `setTimeout(() => { console.log(\"t1\"); Promise.resolve().then(() => console.log(\"m\")); }, 0); setTimeout(() => console.log(\"t2\"), 0);`",
                "accept": [
                  "t1 m t2",
                  "t1,m,t2",
                  "t1, m, t2",
                  "t1mt2"
                ],
                "miss": "**If `t2` came before `m` — the loop drains microtasks after every task, not only after the script.** `m` was queued during `t1`, so it runs before `t2`.\n",
                "why": "`t1`, then the microtask it queued, then `t2`. One task, then drain, then the next task.\n"
              }
            ]
          }
        },
        {
          "h": "Across an `await`, other code gets a turn"
        },
        {
          "p": "Run to completion covers the code between one pause and the next. An `async`\nfunction pauses at every `await`, and while it is paused, anything else that is\nqueued runs — including another call to the same function."
        },
        {
          "code": "let stock = 1;\n\nasync function buy(name) {\n  if (stock > 0) {             // check\n    await pay(name);           // pause: other code runs here\n    stock--;                   // act on the check\n    console.log(name + \" bought one; stock is \" + stock);\n  }\n}\n\nbuy(\"ana\");\nbuy(\"ben\");",
          "lang": "js",
          "file": "buy.js"
        },
        {
          "p": "Step through it:"
        },
        {
          "table": {
            "head": [
              "Moment",
              "What runs",
              "`stock`"
            ],
            "rows": [
              [
                "`buy(\"ana\")`",
                "checks `stock > 0` — true; pauses at `await pay`",
                "1"
              ],
              [
                "`buy(\"ben\")`",
                "checks `stock > 0` — still true; pauses at `await pay`",
                "1"
              ],
              [
                "ana's payment settles",
                "`stock--`, logs",
                "0"
              ],
              [
                "ben's payment settles",
                "`stock--`, logs",
                "**-1**"
              ]
            ]
          }
        },
        {
          "p": "No two lines ever ran at the same moment. The check and the change were simply\nseparated by an `await`, and the other call ran in the gap. Every `await` is a\nplace where the world can change under you: what you checked before it may no\nlonger be true after it."
        },
        {
          "p": "A second shape of the same thing, with a different job. A settings page saves\nwhatever is in `draft`:"
        },
        {
          "code": "let draft = \"hello\";\n\nasync function autosave() {\n  const text = draft;          // read the state\n  await upload(text);          // pause: other code runs here\n  console.log(\"saved: \" + text + \", draft is now: \" + draft);\n}\n\nautosave();\ndraft = \"hello world\";    // runs while autosave is paused",
          "lang": "js",
          "file": "autosave.js"
        },
        {
          "p": "It logs `saved: hello, draft is now: hello world`. The edit ran while `autosave`\nwas paused, and the save that finished is already out of date."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`buy.js` with `stock` starting at `2`, and three calls — once all have finished, what value is `stock`?",
                "accept": [
                  "-1"
                ],
                "miss": "**If you answered `0` or `-2` — trace the checks first.** All three check `stock > 0` while it is still `2`, then all three subtract.\n",
                "why": "`-1`. All three checks see `2`, so all three pause at `await`, and all three subtract: 2 − 3.\n"
              },
              {
                "question": "`buy.js` — could `stock` go below zero if `pay` were an ordinary function with no `await`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. Then each `buy` would check and subtract in one run, to completion, before the next call started.\n"
              }
            ]
          }
        },
        {
          "h": "Blocking the only thread"
        },
        {
          "p": "The other side of run to completion: while your code runs, **nothing else\ncan**. Not a timer, not a promise callback, not a click, not the browser painting\nthe page."
        },
        {
          "code": "const start = Date.now();\nwhile (Date.now() - start < 3000) { }   // 3 seconds of holding the thread",
          "lang": "js",
          "file": "busy.js"
        },
        {
          "p": "In a browser tab that is three seconds of a frozen page — no clicks, no scrolling,\nno animation. The browser may offer to kill the tab. In a language where your code\nhas several threads, a loop like this holds one of them and the program carries\non. Here it holds *the* thread."
        },
        {
          "p": "Work does not have to be a `while` loop to block. Sorting a huge array, a\n`JSON.parse` of a very large string, a synchronous file read in Node — anything\nthat takes a long time without giving the thread back does the same."
        },
        {
          "p": "Worked out, in a browser tab (the click handler is browser code, as a vehicle):"
        },
        {
          "code": "// queued for later\nbutton.addEventListener(\"click\", () => console.log(\"clicked\"));  // browser-only\nsetTimeout(() => console.log(\"timer\"), 0);\n\n// hold the thread\nconst start = Date.now();\nwhile (Date.now() - start < 2000) { }      // 2 seconds of synchronous work\nconsole.log(\"done\");\n\n// A user clicks at 1 second. Order: done, timer, clicked",
          "lang": "js",
          "file": "click.js"
        },
        {
          "p": "The click happened at one second and was queued — the stack was not empty, so it\nwaited. `done` prints first because it is synchronous. Then the timer, which was\nqueued before the click was."
        },
        {
          "p": "Microtasks can block too. Because the loop drains **every** microtask before the\nnext task, a promise chain that keeps queueing more microtasks starves every timer\nand click behind it — a way to freeze a page without writing a loop at all."
        },
        {
          "video": {
            "title": "A frozen page, then the same work in pieces",
            "caption": "A three-second loop freezing a page while clicks pile up in the queue, then the same work split into chunks with a timer between them, so the page keeps responding.\n"
          }
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`click.js` — the user clicks at 1 second. Type the three lines it prints, in order.",
                "accept": [
                  "done timer clicked",
                  "done, timer, clicked",
                  "done,timer,clicked"
                ],
                "miss": "**If `clicked` came first — the click was queued, not run.** Nothing queued runs until the synchronous work ends.\n",
                "why": "`done`, then `timer`, then `clicked`. The timer was queued before the click.\n"
              },
              {
                "question": "A function sorts a million records synchronously, taking 800ms. A timer is due in 10ms. When does its callback run?",
                "choices": [
                  "At 10ms, in the middle of the sort",
                  "After the sort finishes"
                ],
                "accept": [
                  "After the sort finishes"
                ],
                "why": "After the sort finishes. The timer is due, and queued, but it needs the thread.\n"
              }
            ]
          }
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Keep the thread free. Work that takes a long time goes somewhere else, or goes\nin pieces."
              },
              {
                "code": "// Browser: a worker is a separate thread; the data is copied to it\nconst worker = new Worker(\"./heavy.js\");\nworker.postMessage(data);\nworker.onmessage = e => render(e.data);",
                "lang": "js"
              },
              {
                "p": "If a worker is too much, break long synchronous work into chunks with a\n`setTimeout` between them — each chunk is one task, and between tasks the loop\ncan paint and handle clicks."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say what a user experiences\nduring a three-second synchronous loop in a browser tab, and why \"the page is\nslow\" is the wrong description."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A report page runs a 4-second calculation on the client. Users say the page\n\"crashes\" — it stops responding entirely and the browser offers to kill the tab."
            },
            {
              "p": "Three fixes:"
            },
            {
              "list": [
                "**A worker** — move the calculation to a separate thread; the data is copied to it and the result copied back.",
                "**Chunk it** — process 100 rows, give the thread back with a timer, repeat.",
                "**Move it to the server** — calculate there, send the result."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what copying does when the data\nis large, what chunking does to the *total* time and why, and what the third\nassumes about where the data already is. Say which you would choose if the\ncalculation needed to read the page itself — and what that rules out\nimmediately."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**One thread, run to completion.** The host does the waiting; what comes back is queued as a **microtask** (promise callbacks, `await` resumptions) or a **task** (timers, I/O, events).",
                "**The loop:** synchronous code, then **every** microtask, then **one** task, then microtasks again. That makes output order predictable — and at each `await`, other code runs in the gap.",
                "**Blocking freezes everything**: code that does not give the thread back holds up every timer, click and paint behind it."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "JavaScript execution model",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Execution_model",
              "note": "run to completion, the job queues, and never blocking."
            },
            {
              "title": "Using microtasks",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/HTML_DOM_API/Microtask_guide",
              "note": "tasks versus microtasks, and `queueMicrotask`."
            }
          ]
        }
      ],
      "_source": "lessons/event-loop.md",
      "practice": {
        "id": "event-loop",
        "outcome": 9,
        "steps": [
          {
            "kind": "predict",
            "title": "Who holds the thread?",
            "prompt": "Four quick ones.",
            "items": [
              {
                "id": "p1",
                "question": "Type the five letters in the order they print. `async function f() { console.log(\"b\"); await null; console.log(\"c\"); } console.log(\"a\"); setTimeout(() => console.log(\"d\"), 0); f(); console.log(\"e\");`",
                "accept": [
                  "a b e c d",
                  "a,b,e,c,d",
                  "a, b, e, c, d",
                  "abecd"
                ],
                "miss": "If you put b after e: calling `f` runs its body at once, up to the `await`. If you put d before c: the resumption after `await` is a microtask, and the timer waits for all of them.",
                "why": "Synchronous first: `a`, then `b` (f runs until its `await`), then `e`. Then the microtask — f resumes, `c`. Then the timer, `d`."
              },
              {
                "id": "p2",
                "question": "To predict the output order of async code, what do you work out first? A few words.",
                "accept": [
                  "the synchronous code",
                  "synchronous code",
                  "sync code",
                  "the sync code",
                  "every synchronous line",
                  "all synchronous lines",
                  "synchronous lines",
                  "sync lines",
                  "synchronous",
                  "sync"
                ],
                "why": "Every synchronous line, top to bottom — nothing queued runs until the call stack is empty. Then drain the microtasks, then one task, and repeat."
              },
              {
                "id": "p3",
                "question": "`while (true) {}` runs in a browser tab. What still responds?",
                "choices": [
                  "Clicks and scrolling",
                  "Nothing"
                ],
                "accept": [
                  "Nothing"
                ],
                "miss": "If you expected clicks or painting to carry on: one thread runs your code and handles them. Occupy it and the page is frozen, not slow.",
                "why": "Nothing. One thread runs your code, the clicks and the rendering. Occupy it and the page is frozen, not slow."
              },
              {
                "id": "p4",
                "question": "Two callbacks both do `count++` on a shared variable, with no `await` anywhere. Can one interrupt the other halfway?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "If you said yes, you are importing an assumption from a language where your code runs on several threads. Here each callback runs to completion before the next starts.",
                "why": "Never. Each callback runs to completion before the next starts. Only an `await` lets other code in partway through."
              }
            ],
            "extra": [
              {
                "id": "p5",
                "question": "Type the five letters in the order they print. `console.log(\"a\"); setTimeout(() => console.log(\"b\"), 0); Promise.resolve().then(() => { console.log(\"c\"); Promise.resolve().then(() => console.log(\"d\")); }); console.log(\"e\");`",
                "accept": [
                  "a e c d b",
                  "a,e,c,d,b",
                  "a, e, c, d, b",
                  "aecdb"
                ],
                "why": "`d` is queued during the microtask drain, and a drain includes microtasks added while it runs. The timer waits for all of them."
              },
              {
                "id": "p6",
                "question": "`setTimeout(fn, 0)` — is `fn` guaranteed to run in 0ms?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "`0` means no *additional* delay. The real wait is however long the thread stays busy, plus every pending microtask."
              },
              {
                "id": "p7",
                "question": "A click handler is registered, a `setTimeout(…, 0)` is queued, then 2 seconds of synchronous work logs `done`. The user clicks at 1 second. Type `done`, `timer` and `clicked` in the order they print.",
                "accept": [
                  "done timer clicked",
                  "done, timer, clicked",
                  "done,timer,clicked"
                ],
                "why": "Nothing queued runs until the synchronous work ends. Then the timer, which was queued before the click."
              },
              {
                "id": "p8",
                "question": "Type the four letters in the order they print. `console.log(\"w\"); setTimeout(() => console.log(\"x\"), 0); Promise.resolve().then(() => console.log(\"y\")); console.log(\"z\");`",
                "accept": [
                  "w z y x",
                  "w,z,y,x",
                  "w, z, y, x",
                  "wzyx"
                ],
                "miss": "If you put x before y, you are reading `setTimeout(…, 0)` as \"now\". It waits for all synchronous code and every pending promise callback.",
                "why": "Synchronous lines first, then the promise callback (a microtask), then the timer. Every microtask runs before the next task."
              },
              {
                "id": "p9",
                "question": "In the last one, why does `y` print before `x` when the timer was queued first? A few words.",
                "accept": [
                  "microtask",
                  "microtasks first",
                  "microtask queue",
                  "different queue",
                  "different queues",
                  "it is a microtask",
                  "microtasks run first"
                ],
                "why": "Two queues. Promise callbacks are microtasks, and the microtask queue drains completely before the next task runs."
              }
            ],
            "hints": [
              {
                "when": "you expected something queued to run before the synchronous code finished",
                "say": "Queues are read only when the call stack is empty. Finish every synchronous line first — including the part of an async function before its first `await`."
              },
              {
                "when": "you said the page keeps rendering",
                "say": "Rendering happens on the same thread as your code. If your code never yields, the browser never gets a turn to paint."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Two queues, and an await",
            "prompt": "Eight lines print. Predict the ones asked about, then run it.",
            "filename": "order.js",
            "code": "console.log(\"start\");\n\nsetTimeout(() => console.log(\"timer 20\"), 20);\nsetTimeout(() => {\n  console.log(\"timer 0\");\n  Promise.resolve().then(() => console.log(\"micro in timer\"));\n}, 0);\n\nasync function job() {\n  console.log(\"job starts\");\n  await null;\n  console.log(\"job resumes\");\n}\njob();\n\nPromise.resolve().then(() => console.log(\"micro\"));\nconsole.log(\"end\");\n",
            "items": [
              {
                "id": "r1",
                "question": "Type the second line printed.",
                "accept": [
                  "job starts"
                ],
                "why": "Calling `job()` runs its body synchronously up to the first `await`. The timers above it are queued, not run."
              },
              {
                "id": "r2",
                "question": "Type the line printed right after `end`.",
                "accept": [
                  "job resumes"
                ],
                "miss": "If you said `micro`: both are microtasks, and they run in the order they were queued. `job` reached its `await` before the `.then` on the next line was attached.",
                "why": "Both are microtasks, queued in order: `job`'s resumption when it hit `await`, then the `.then`. Microtasks run first-in, first-out."
              },
              {
                "id": "r3",
                "question": "Type the line printed right after `timer 0`.",
                "accept": [
                  "micro in timer"
                ],
                "miss": "If you said `timer 20`: after each task, the microtask queue drains before the next one — and the promise callback was queued during `timer 0`.",
                "why": "After each task the loop drains microtasks again, so the promise callback queued inside `timer 0` runs before any other timer."
              },
              {
                "id": "r4",
                "question": "Type the last line printed.",
                "accept": [
                  "timer 20"
                ],
                "why": "It was queued first, but it asked for 20ms. Everything else was ready sooner."
              }
            ],
            "hints": [
              {
                "when": "you put \"job starts\" after \"end\"",
                "say": "Calling an async function is not scheduling it. It runs at once, and hands control back only at `await`."
              },
              {
                "when": "you put \"micro\" before \"job resumes\"",
                "say": "Find the moment each microtask was queued. `job` reached its `await` on the line where it was called."
              },
              {
                "when": "you put \"timer 20\" before \"micro in timer\"",
                "say": "The loop takes one task, then drains microtasks, then the next task. Which queue did \"micro in timer\" join, and when?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Two calls, one function, one thread",
            "prompt": "`let seats = 1;   async function book(who) { if (seats > 0) { await confirm(who); seats--; log.push(who); } }   book(\"kim\"); book(\"lee\");` — `confirm` returns a promise that fulfils a moment later, and `log` starts as `[]`. Trace `seats`.\n",
            "items": [
              {
                "id": "t1",
                "question": "What value is `seats` when `book(\"lee\")` checks it?",
                "accept": [
                  "1"
                ],
                "miss": "If you said 0: `kim` has not subtracted yet. It checked, then paused at `await` — the subtraction is after the pause.",
                "why": "`1`. `book(\"kim\")` checked and paused at its `await`; `lee` runs its check before `kim` resumes."
              },
              {
                "id": "t2",
                "question": "How many of the two calls pass the `seats > 0` check? Type a number.",
                "accept": [
                  "2",
                  "two",
                  "both"
                ],
                "why": "Both. Each check ran while `seats` was still `1`."
              },
              {
                "id": "t3",
                "question": "What value is `seats` once both have finished?",
                "accept": [
                  "-1"
                ],
                "why": "`-1`. Two checks passed, so two subtractions ran: 1 − 2."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "The same, but `book` has no `await` — `confirm` is called without it. What value is `seats` once both have finished?",
                "accept": [
                  "0"
                ],
                "why": "`0`. Without the `await`, each call checks and subtracts in one run, to completion. `lee` checks after `kim` has already subtracted."
              },
              {
                "id": "t5",
                "question": "What value is `n` once all have finished? `let n = 0; async function add() { const seen = n; await tick(); n = seen + 1; } add(); add(); add();`",
                "accept": [
                  "1"
                ],
                "why": "`1`. All three read `n` as `0` before any of them resumed, and each wrote `0 + 1`."
              }
            ],
            "hints": [
              {
                "when": "you said the second check sees 0",
                "say": "Find the line where the first call pauses. Is the subtraction before or after it?"
              },
              {
                "when": "you expected the thread to finish one call before starting the next",
                "say": "Run to completion covers the code up to the next `await`. At the `await`, the function steps aside, and the next queued thing — here, the second call — runs."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Stop holding the thread",
            "prompt": "This processes every item in one go, which holds the thread — a big list freezes the page. Change it to process the items in batches of `size`, calling `schedule(next)` between batches so the loop gets a turn. Still return the number of batches. Then, in one sentence: what can run between two of your batches that could not run before?\n",
            "exportName": "chunked",
            "filename": "chunked.js",
            "starter": "// process(item) is called for each item.\n// schedule(fn) stands in for setTimeout(fn, 0) — it runs fn on a later turn.\n// Returns the number of batches used.\n// Works today, as one batch: nothing else runs until every item is done.\nfunction chunked(items, size, process, schedule) {\n  for (const item of items) process(item);\n  return items.length === 0 ? 0 : 1;\n}\n",
            "tests": [
              {
                "label": "every item is processed, in order",
                "body": "const seen = []; const run = []; subject([1,2,3,4,5], 2, function (i) { seen.push(i); },\n        function (f) { run.push(f); });\nwhile (run.length) run.shift()(); return seen.join(',') === '1,2,3,4,5';"
              },
              {
                "label": "reports the number of batches",
                "body": "const run = []; const n = subject([1,2,3,4,5], 2, function () {}, function (f) { run.push(f); }); while (run.length) run.shift()(); return n === 3;"
              },
              {
                "label": "only the first batch runs before the function returns",
                "body": "const seen = []; let scheduled = 0; subject([1,2,3,4], 2, function (i) { seen.push(i); },\n        function () { scheduled++; });\nreturn seen.length === 2 && scheduled === 1;"
              },
              {
                "label": "an exact multiple does not add an empty batch",
                "body": "const run = []; let scheduled = 0; const n = subject([1,2,3,4], 2, function () {},\n                  function (f) { scheduled++; run.push(f); });\nwhile (run.length) run.shift()(); return n === 2 && scheduled === 1;"
              },
              {
                "label": "with a real timer, a click that arrives during a batch runs before the next one",
                "body": "const seen = []; subject([1,2,3,4,5,6], 2,\n        function (i) { seen.push(i); if (i % 2 === 0 && i < 6) setTimeout(function () { seen.push('click'); }, 0); },\n        function (f) { setTimeout(f, 0); });\nawait new Promise(function (r) { setTimeout(r, 50); }); return seen.join(',') === '1,2,click,3,4,click,5,6';"
              },
              {
                "label": "an empty list needs no batches",
                "body": "return subject([], 2, function () {}, function () {}) === 0;"
              },
              {
                "label": "one batch needs no scheduling at all",
                "body": "let scheduled = 0; const n = subject([1], 5, function () {}, function () { scheduled++; }); return n === 1 && scheduled === 0;"
              }
            ],
            "hints": [
              {
                "when": "every item was processed before the function returned",
                "say": "That is still the version that freezes the page. Process one batch, then hand the rest to `schedule` and return."
              },
              {
                "when": "the later batches never run, or start from the beginning again",
                "say": "The function you give `schedule` runs later, after `chunked` has returned. It needs to remember where it got to — a variable it closes over will do."
              },
              {
                "when": "an exact multiple produced an extra empty batch",
                "say": "Check the boundary. After the last full batch there is nothing left — do not schedule another turn to process zero items."
              },
              {
                "when": "the batch count is wrong",
                "say": "The count is known before any batch runs: it depends only on how many items there are and how big a batch is."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a save button",
            "prompt": "Code from a pull request, partly written with an AI tool, for a browser page. `showToast` puts a message on screen. Review it.\n",
            "filename": "save-button.js",
            "code": "let saving = false;\nlet saves = 0;\n\nfunction sleep(ms) {\n  const end = Date.now() + ms;\n  while (Date.now() < end) {}\n}\n\n// Guard against two clicks saving at the same time.\nfunction onSaveClick() {\n  while (saving) sleep(10);\n  saving = true;\n  saves++;\n  saving = false;\n  sleep(300);              // give the UI time to show \"Saved\"\n  showToast(\"Saved\");\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line holds the only thread while it waits? Type the line number.",
                "accept": [
                  "6",
                  "line 6"
                ],
                "why": "Line 6 spins until the time is up. Nothing else can run meanwhile — no clicks, no painting, no timers. `sleep` does not wait; it keeps the thread busy."
              },
              {
                "id": "c2",
                "question": "While line 15 runs, can the browser paint anything?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The comment says it gives the UI time, but painting needs the thread `sleep` is holding. The page freezes for 300ms per click and the toast appears after."
              },
              {
                "id": "c3",
                "question": "When line 11 runs, can `saving` ever be `true`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. `saving` is set and cleared inside one callback with no `await` in it, and a callback runs to completion — so no other click can ever observe it `true`. The guard guards nothing."
              },
              {
                "id": "c4",
                "question": "A second click arrives while the first click's handler is on line 15. When does the second handler start?",
                "choices": [
                  "Straight away, during line 15",
                  "After the first handler finishes"
                ],
                "accept": [
                  "After the first handler finishes"
                ],
                "why": "After the first handler has finished — line 16 included. The click is queued as a task, and a task needs the thread. The clicks are already one at a time; that is what the loop guarantees."
              }
            ],
            "hints": [
              {
                "when": "you picked line 15 for the line that holds the thread",
                "say": "Line 15 calls it. Which line inside `sleep` is the one that is actually running, over and over, for 300ms?"
              },
              {
                "when": "you think the guard could matter",
                "say": "For `saving` to be true at line 11, another call would have to be paused between lines 12 and 14. Is there anything in between where a function could pause?"
              },
              {
                "when": "you expected the second click to run straight away",
                "say": "A click is a task. When can the loop take the next task?"
              }
            ]
          }
        ]
      }
    },
    "same-code-elsewhere": {
      "id": "same-code-elsewhere",
      "title": "The same code in another host",
      "kind": "read",
      "outcome": 9,
      "shape": "conceptual",
      "why": "Outcome 9 asks the student to explain why the same code behaves differently in a different runtime. `async.runtime-difference` is `runtime.host`'s second pass (D-009): the host idea met in week 1, now applied to what asynchronous code depends on — timers, I/O, globals. Page loading stays the web course's (D-013).",
      "concepts": [
        "async.runtime-difference"
      ],
      "lede": "You already know the host, not the language, supplies `document` and `process`. It also supplies the timer, the network and the rule for what happens to a failure nobody caught — so code that runs cleanly in one host can run slower, never finish, or crash in another, without a single line being wrong.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What JavaScript is · Where your code runs · The event loop, slowly"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Host** — the program that runs your JavaScript and decides what else it can reach: a browser tab, Node, a worker. **Global object** — the object holding every name your code can use without importing it; `globalThis` in every host.",
          "entries": [
            "**Host** — the program that runs your JavaScript and decides what else it can reach: a browser tab, Node, a worker.",
            "**Global object** — the object holding every name your code can use without importing it; `globalThis` in every host."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about one line of code in two hosts. A guess is fine; that is\nwhat this is for."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`setInterval(tick, 100)` in a browser tab you have switched away from — about how often does `tick` run? Give a rough interval.",
                          "accept": [
                            "once a second",
                            "about once a second",
                            "once per second",
                            "every second",
                            "1 second",
                            "1s",
                            "1000ms",
                            "1000 ms",
                            "every 1000ms",
                            "less often",
                            "slower"
                          ],
                          "miss": "**If you answered every 100ms — the host decided otherwise.** A browser slows timers in tabs nobody is looking at, to save battery. The language did not change.\n",
                          "why": "Roughly once a second, sometimes less. The browser throttles timers in hidden tabs; Node has no tabs and does not.\n"
                        },
                        {
                          "question": "In Node, what does `typeof setTimeout(f, 1000)` produce?",
                          "accept": [
                            "\"object\"",
                            "object",
                            "'object'"
                          ],
                          "miss": "**If you answered `\"number\"` — that is the browser's answer.** Each host writes its own `setTimeout`, and Node's returns an object.\n",
                          "why": "`\"object\"` — a `Timeout` object. In a browser the same call returns a number.\n"
                        },
                        {
                          "question": "`Promise.reject(new Error(\"x\"));` with no `.catch` anywhere — in recent Node, does the program keep running?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes, because a browser just logs it — Node is stricter.** An unhandled rejection ends a Node program with an error.\n",
                          "why": "No. Node treats it as a crash and exits. A browser prints `Uncaught (in promise)` in the console and carries on.\n"
                        },
                        {
                          "question": "A page on your site fetches another site's API, and the browser blocks the response. Is the same fetch in Node blocked?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes — the rule belongs to the browser, not to JavaScript.** Node does not apply it.\n",
                          "why": "No. The browser's cross-origin rule (CORS) protects the person using the page. Node has no such person to protect, and sends the request.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "The same code, in ASP.NET and in a console app — what can differ? Name it in a few words.",
                          "accept": [
                            "the synchronization context",
                            "context",
                            "thread pool",
                            "scheduling"
                          ],
                          "miss": "**If you answered nothing — the host decides where the rest of an `async` method runs.** `ConfigureAwait(false)` controls which context a continuation resumes on, and getting it wrong is a common source of deadlocks.\n",
                          "why": "The synchronisation context, which decides which thread a continuation resumes on — the same code, a different runtime.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can explain why identical code behaves differently in another host"
              ]
            }
          ]
        },
        {
          "h": "The same line, a different host"
        },
        {
          "p": "The language is identical everywhere. What `document`, `process` and `console`\nare, the host decides — and a name the host does not supply fails at the\nmoment it is used:"
        },
        {
          "table": {
            "head": [
              "You type",
              "In a browser tab",
              "In Node"
            ],
            "rows": [
              [
                "`typeof document`",
                "`\"object\"`",
                "`\"undefined\"`"
              ],
              [
                "`typeof process`",
                "`\"undefined\"`",
                "`\"object\"`"
              ],
              [
                "`typeof Math`",
                "`\"object\"`",
                "`\"object\"`"
              ]
            ]
          }
        },
        {
          "p": "That was the first kind of difference. The second is less visible: some names exist in **both**\nhosts and still behave differently, because each host wrote its own. `setTimeout`\nis one. `fetch` is another. What happens to a failure nobody caught is a third.\nNothing throws `is not defined`. The code runs — just not the same way."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "Whose is `setTimeout` — the language's, or the host's?",
                "choices": [
                  "The language's",
                  "The host's"
                ],
                "accept": [
                  "The host's"
                ],
                "miss": "**If you answered the language — it is in every host, but no host got it from the language.** Each one supplies its own, which is why they can differ.\n",
                "why": "The host's. Every host happens to supply one, and each wrote its own.\n"
              },
              {
                "question": "`console.log(document.title)` run in Node — what happens? Type what it prints, or the error it throws.",
                "accept": [
                  "ReferenceError",
                  "referenceerror",
                  "error",
                  "document is not defined",
                  "ReferenceError: document is not defined",
                  "it throws",
                  "throws",
                  "a ReferenceError"
                ],
                "why": "`ReferenceError: document is not defined`, at that line. Node has no page.\n"
              }
            ]
          }
        },
        {
          "h": "Timers: the host keeps the clock"
        },
        {
          "p": "A timer is a request to the host: \"call this later.\" How much later, and what a\npending one does to the program, is the host's decision."
        },
        {
          "table": {
            "head": [
              "",
              "Browser tab",
              "Node"
            ],
            "rows": [
              [
                "`setTimeout(…)` returns",
                "a number",
                "a `Timeout` object"
              ],
              [
                "a timer still pending at the end of the script",
                "the page stays open anyway",
                "**keeps the program alive** until it fires"
              ],
              [
                "a tab nobody is looking at",
                "timers throttled — about once a second, sometimes less",
                "no tabs; not throttled"
              ],
              [
                "timers that keep scheduling timers",
                "after a few levels, clamped to at least 4ms",
                "not clamped"
              ]
            ]
          }
        },
        {
          "p": "Worked through, a heartbeat that works in one host and not the other:"
        },
        {
          "code": "// setInterval and console are the host's, not the language's.\n//   Browser: calls tick every 100ms — until the tab is hidden, then ~once a second.\n//   Node: calls tick every 100ms, and never lets the program end.\nlet beats = 0;\nsetInterval(() => {\n  beats++;\n  console.log(\"beat \" + beats);\n}, 100);",
          "lang": "js",
          "file": "heartbeat.js"
        },
        {
          "p": "In a visible tab it counts ten beats a second. Switch tabs for a minute and come\nback: roughly sixty beats were counted, not six hundred. In Node, the count never\nstops — the script has no last line to reach, because a pending interval holds\nthe program open. Neither host is wrong; they were built for different jobs."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`heartbeat.js` run in Node — does the program end on its own?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. A pending interval keeps a Node program alive, and this one never stops.\n"
              },
              {
                "question": "A countdown shows 60 seconds by calling `setTimeout(tick, 1000)` sixty times in a row. The student switches tabs for a while. Is the count still right?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "Not reliably. Hidden-tab timers can be delayed well past a second each. A countdown that must be right reads the clock (`Date.now()`) rather than counting ticks.\n"
              }
            ]
          }
        },
        {
          "h": "I/O: what the host lets your code reach"
        },
        {
          "p": "Everything that leaves your program — the network, files, storage — is the\nhost's, and each host draws the line in a different place."
        },
        {
          "table": {
            "head": [
              "",
              "Browser tab",
              "Node"
            ],
            "rows": [
              [
                "the network",
                "`fetch`, limited by the browser's cross-origin rule (CORS)",
                "`fetch` in recent versions, no CORS"
              ],
              [
                "files",
                "none, except a file the user picks",
                "`node:fs` — any file the program may read"
              ],
              [
                "blocking reads",
                "none",
                "`readFileSync` and friends: hold the thread until done"
              ]
            ]
          }
        },
        {
          "p": "Neither line is drawn by the language. A browser tab and Node were built for\ndifferent jobs, and each hands your code what its job needs. Why the browser\ndraws its line where it does is the web and security courses' material; what\nthis course needs is that the line moves when the host does."
        },
        {
          "p": "That last row joins the event loop lesson. In a Node script that reads its\nsettings once at start-up, a blocking read is harmless — nothing else is waiting.\nIn a Node server, the same line holds the only thread while the disk works, and\nevery other request waits behind it."
        },
        {
          "code": "// Node only — node:fs is Node's. A module file, so import and top-level await work\nimport { readFileSync } from \"node:fs\";\nimport { readFile } from \"node:fs/promises\";\n\nconst a = readFileSync(\"settings.json\", \"utf8\");     // holds the thread until the read is done\nconst b = await readFile(\"settings.json\", \"utf8\");   // hands the read to the host; the thread is free\n// Same text in a and b. In a server, only the second lets other requests run meanwhile.",
          "lang": "js",
          "file": "settings.mjs"
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`settings.mjs` loaded into a browser page as a module — does any of its lines run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "**If you expected it to run until a line failed — imports are resolved before any line runs.** The browser cannot find `node:fs`, so the whole module fails to load.\n",
                "why": "No. `node:fs` is Node's, and a browser has nothing to give it, so the module never starts.\n"
              },
              {
                "question": "`readFileSync` in a Node server, reading a large file for one request — what happens to the other requests?",
                "choices": [
                  "They run meanwhile",
                  "They wait"
                ],
                "accept": [
                  "They wait"
                ],
                "why": "They wait. The read holds the only thread, so no other request's code can run until it is done.\n"
              }
            ]
          }
        },
        {
          "h": "Globals, and a failure nobody caught"
        },
        {
          "p": "The name for the global object itself differs: `window` in a browser tab, `global`\nin Node, `self` in a browser and its workers. **`globalThis`** is the one name\nevery host supplies, and code meant to run anywhere uses it."
        },
        {
          "p": "And the host decides what an uncaught failure does. You met the unhandled\nrejection in the promises lesson — a promise that rejects with no `.catch` on the\nchain:"
        },
        {
          "table": {
            "head": [
              "An unhandled rejection",
              "Browser tab",
              "Node (recent versions)"
            ],
            "rows": [
              [
                "what you see",
                "`Uncaught (in promise) Error: …` in the console",
                "the error, and a stack trace"
              ],
              [
                "what happens next",
                "the page carries on",
                "**the program exits**"
              ]
            ]
          }
        },
        {
          "p": "The same forgotten `.catch` is a console message in one host and a crashed server\nin the other. The browser keeps going because a page with one broken button is\nbetter than a blank page. Node stops because a server in an unknown state is worse\nthan one that restarts."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "Code meant to run in every host needs the global object. Type the name it uses.",
                "accept": [
                  "globalThis",
                  "globalthis"
                ],
                "miss": "**If you answered `window` — that is the browser tab's name for it.** Node calls it `global`, a worker `self`. Only `globalThis` is in all of them.\n",
                "why": "`globalThis`, the one name every host supplies.\n"
              },
              {
                "question": "A Node server calls a function that returns a promise, and forgets the `.catch`. The promise rejects. What happens to the server?",
                "choices": [
                  "It logs the error and carries on",
                  "It exits"
                ],
                "accept": [
                  "It exits"
                ],
                "miss": "**If you answered that it logs the error and carries on — that is what a browser tab does.** Node ends the program.\n",
                "why": "It exits. In recent Node an unhandled rejection ends the program; the same code in a tab would print a console message and keep going.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Moving code to another host",
            "body": [
              {
                "h": "Moving code to another host"
              },
              {
                "code": "// Before you run code in a host it was not written for:\n\n// 1. Find every name the host supplies — anything not declared in the\n//    file and not part of the language (document, process, setTimeout,\n//    fetch, fs, localStorage…).\n// 2. For each: does the new host supply it at all?\n//    no  -> it fails at that line: \"… is not defined\".\n// 3. For each it does supply: does it behave the same? Timers, network\n//    rules, blocking, what an uncaught failure does.\n// 4. Predict the difference before you run it.",
                "lang": "js",
                "file": "move.js"
              },
              {
                "p": "Now apply it. This was written for Node; say what happens in a browser tab."
              },
              {
                "code": "const started = Date.now();\nsetTimeout(() => {\n  console.log(\"waited \" + (Date.now() - started) + \"ms\");\n  process.exit(0);\n}, 500);\n\n// In a browser tab: ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "It waits about 500ms — longer if the tab is hidden — logs, then throws\n`ReferenceError: process is not defined` at `process.exit`. Everything up to\nthere was the language or a name both hosts supply."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then name one thing that differs\nbetween a browser tab and Node in each of: timers, I/O, and failures."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You are writing a small library that saves a user's recent searches and clears\nold ones on a timer. Your team wants it in the browser app **and** in the Node\ncommand-line tool."
            },
            {
              "p": "Two approaches:"
            },
            {
              "list": [
                "**One file that checks its host** at run time — `typeof document`, `typeof process` — and picks what to use.",
                "**Two entry points**, one per host, sharing the code that is only language."
              ]
            },
            {
              "p": "Pick one and say why. Then say what each costs: what the first must get right\nabout every name it touches, what the second duplicates, and which one lets a\nreader see at a glance what runs where. Say which host's timer behaviour — a\nhidden tab, or a program that will not exit — your clearing timer has to be\nwritten for, and why it might be both."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "The language is the same everywhere; the **host supplies timers, I/O and globals**, and each host writes its own — so a name can exist in both and still behave differently.",
                "**Timers**: throttled in hidden tabs; a pending one keeps Node alive. **I/O**: the browser limits the network and has no files; Node has files, and blocking reads that hold the thread. **Failures**: an unhandled rejection is a console message in a browser and an exit in Node.",
                "Before moving code, find each host-supplied name and ask whether the new host has it, and whether it behaves the same."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "setTimeout — reasons for delays longer than specified",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/setTimeout#reasons_for_delays_longer_than_specified",
              "note": "clamping and background-tab throttling."
            },
            {
              "title": "globalThis",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/globalThis",
              "note": "the one name for the global object in every host."
            },
            {
              "title": "Cross-Origin Resource Sharing (CORS)",
              "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/CORS",
              "note": "the browser's rule, and why Node does not have it."
            }
          ]
        }
      ],
      "_source": "lessons/same-code-elsewhere.md",
      "practice": {
        "id": "same-code-elsewhere",
        "outcome": 9,
        "steps": [
          {
            "kind": "predict",
            "title": "Same line, which host?",
            "prompt": "Three quick ones.",
            "items": [
              {
                "id": "p1",
                "code": "typeof process   —   in a browser tab",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "miss": "If you said \"object\": `process` is Node's — the arguments, the environment, the exit code. A browser tab supplies no such name.",
                "why": "`\"undefined\"`. `process` belongs to Node."
              },
              {
                "id": "p2",
                "question": "`Promise.reject(new Error(\"x\"));` with nothing to catch it — in recent Node, does the program keep running?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "If you said yes: that is the browser's choice. Node ends the program on an unhandled rejection.",
                "why": "No. Node prints the error and exits. A browser logs `Uncaught (in promise)` and carries on."
              },
              {
                "id": "p3",
                "question": "`setInterval(tick, 100)` in a browser tab nobody is looking at — about how often does `tick` run? Give a rough interval.",
                "accept": [
                  "once a second",
                  "about once a second",
                  "once per second",
                  "every second",
                  "1 second",
                  "1s",
                  "1000ms",
                  "1000 ms",
                  "every 1000ms",
                  "less often",
                  "slower"
                ],
                "miss": "If you said every 100ms: the host decided otherwise. Hidden tabs have their timers throttled; Node has no tabs.",
                "why": "Roughly once a second, sometimes less. The interval is a request; the host decides when it runs."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "code": "typeof window   —   in Node",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "No page, so no window. The one global name every host supplies is `globalThis`."
              },
              {
                "id": "p5",
                "code": "typeof setTimeout(f, 1000)   —   in Node",
                "accept": [
                  "\"object\"",
                  "object",
                  "'object'"
                ],
                "why": "A `Timeout` object. The browser's `setTimeout` returns a number; each host wrote its own."
              },
              {
                "id": "p6",
                "question": "`setTimeout(done, 5000)` is the last thing a Node script does. Does the program end before `done` runs?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. A pending timer keeps a Node program alive until it fires."
              }
            ],
            "hints": [
              {
                "when": "you gave the same answer for both hosts",
                "say": "Ask who supplies the name. If it is not the language's, each host decides whether it exists — and what it does."
              },
              {
                "when": "you expected a hidden tab to keep time",
                "say": "The interval is a request, not a promise. The host decides when a timer runs, and a browser saves battery on tabs no one is looking at."
              }
            ]
          },
          {
            "kind": "run",
            "title": "This runner is a host too",
            "prompt": "The code you run on these pages does not run in the page, and not in Node. It runs in a browser **worker** — a separate thread with its own global object and no page. Predict what it supplies, then run it.\n",
            "filename": "which-host.js",
            "code": "console.log(\"Math: \" + typeof Math);\nconsole.log(\"document: \" + typeof document);\nconsole.log(\"process: \" + typeof process);\nconsole.log(\"self: \" + typeof self);\nconsole.log(\"globalThis: \" + typeof globalThis);\n",
            "items": [
              {
                "id": "r1",
                "question": "Type what follows `document: `.",
                "accept": [
                  "undefined",
                  "\"undefined\""
                ],
                "miss": "If you said object: a worker is part of the browser, but it has no page. No page, no `document`.",
                "why": "`undefined`. A worker has no page, so the browser gives it no `document`."
              },
              {
                "id": "r2",
                "question": "Type what follows `process: `.",
                "accept": [
                  "undefined",
                  "\"undefined\""
                ],
                "why": "`undefined`. `process` is Node's, and this is a browser."
              },
              {
                "id": "r3",
                "question": "Type what follows `self: `.",
                "accept": [
                  "object",
                  "\"object\""
                ],
                "why": "`object`. In a worker, `self` is the global object — as `window` is in a tab."
              },
              {
                "id": "r4",
                "question": "Which of the five lines print the same in every host? Name each by what it reports on.",
                "accept": [
                  "math",
                  "Math",
                  "the first",
                  "math and globalthis",
                  "Math and globalThis",
                  "globalthis and math",
                  "the first and the last",
                  "first and last"
                ],
                "why": "`Math` and `globalThis`. `Math` is the language's; `globalThis` is the one name every host is required to supply."
              }
            ],
            "hints": [
              {
                "when": "you expected document to be there because this is a browser",
                "say": "`document` is the page. Ask whether the code is running in the page, or beside it."
              },
              {
                "when": "you expected process to be there",
                "say": "Is this Node? What makes you think so — and what would tell you?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Same code, different host",
            "prompt": "The language is the same everywhere; the host supplies the rest. Fill in what each host gives you.\n",
            "items": [
              {
                "id": "t1",
                "code": "typeof document — in a browser tab",
                "accept": [
                  "\"object\"",
                  "object",
                  "'object'"
                ],
                "why": "The page has a DOM, and `document` is the browser handing it to you."
              },
              {
                "id": "t2",
                "code": "typeof document — in Node",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "Node has no page, so it has no DOM. Code that touches `document` fails there with a ReferenceError the moment it runs."
              },
              {
                "id": "t3",
                "code": "typeof process — in a browser tab",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "`process` is Node's — the environment variables, the arguments, the exit code. A browser tab has no process for your script to own."
              },
              {
                "id": "t4",
                "question": "`setTimeout(tick, 100)` repeated in a background browser tab — about how often does `tick` run? Give a rough interval.",
                "accept": [
                  "once a second",
                  "about once a second",
                  "once per second",
                  "every second",
                  "1 second",
                  "1s",
                  "1000ms",
                  "1000 ms",
                  "every 1000ms"
                ],
                "miss": "If you said every 100ms: the host decided otherwise. Background tabs clamp timers, and Node does not.",
                "why": "Roughly once a second. The browser throttles timers in tabs you cannot see; the language did not change, the host did."
              }
            ],
            "extra": [
              {
                "id": "t5",
                "code": "typeof window — in Node",
                "accept": [
                  "\"undefined\"",
                  "undefined",
                  "'undefined'"
                ],
                "why": "No window, because no page. The global object in both is `globalThis`."
              },
              {
                "id": "t6",
                "question": "A page fetches another site's API and the browser blocks the response. Type the name of the browser rule that Node does not apply.",
                "accept": [
                  "cors",
                  "CORS",
                  "cross-origin",
                  "same-origin policy",
                  "same origin policy"
                ],
                "why": "CORS. The same `fetch` call succeeds in Node, because the rule belongs to the browser, not to JavaScript."
              },
              {
                "id": "t7",
                "question": "`readFileSync(\"big.log\")` in a Node server — while it reads, can the server run another request's code?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. A blocking read holds the only thread. `readFile` from `node:fs/promises` hands the read to the host instead."
              }
            ],
            "hints": [
              {
                "when": "you gave the same answer for both hosts",
                "say": "Ask who supplies the name. `typeof`, `Math` and `Promise` belong to the language; `document` and `process` each belong to one host."
              },
              {
                "when": "you expected a timer to keep its interval in a hidden tab",
                "say": "The interval is a request, not a promise. The host decides when a timer runs, and a browser saves battery on tabs no one is looking at."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a script moved to Node",
            "prompt": "A student found this in a browser tutorial and ran it with Node, as a command-line tool. Review it — for Node.\n",
            "filename": "notes.js",
            "code": "const notes = [];\n\nfunction addNote(text) {\n  notes.push(text);\n  document.title = notes.length + \" notes\";\n}\n\nconst timer = setInterval(() => {\n  console.log(notes.length + \" notes so far\");\n}, 60000);\n\nconsole.log(typeof timer);\naddNote(\"buy milk\");\n",
            "items": [
              {
                "id": "c1",
                "question": "In Node, on which line is an error thrown? Type the line number.",
                "accept": [
                  "5",
                  "line 5"
                ],
                "miss": "If you said 13: line 13 calls the function. Which line inside it uses a name Node does not supply?",
                "why": "Line 5: `ReferenceError: document is not defined`. The page title is the browser's; Node has no page."
              },
              {
                "id": "c2",
                "question": "In Node, what does line 12 print?",
                "accept": [
                  "object",
                  "\"object\""
                ],
                "why": "`object` — Node's `Timeout`. In a browser the same line prints `number`. Nothing is wrong; the hosts differ."
              },
              {
                "id": "c3",
                "question": "Delete line 5. Does the Node program ever end on its own?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The interval on line 8 is still pending, and a pending timer keeps Node running. In a tab, that would have been invisible — the page stays open anyway."
              },
              {
                "id": "c4",
                "question": "Which line inside `addNote` behaves the same in every host? Type the line number.",
                "accept": [
                  "4",
                  "line 4"
                ],
                "why": "Line 4. `push` on an array is the language; the host has no say in it."
              }
            ],
            "hints": [
              {
                "when": "you picked line 13 for the error",
                "say": "The call is on line 13, but the error is reported where the missing name is used. Find the name in `addNote` that is not the language's."
              },
              {
                "when": "you said the program ends",
                "say": "What is still waiting to be called when the last line has run? Recall what Node does while it still holds a function you handed it."
              }
            ]
          }
        ]
      }
    },
    "concurrency-models": {
      "id": "concurrency-models",
      "title": "Event loops, threads and coroutines",
      "kind": "read",
      "outcome": 9,
      "shape": "conceptual",
      "why": "Outcome 9's comparison of concurrency models had dropped out of the concept inventory with no note, and is restored as `async.shared-memory` (D-017). JavaScript's ordinary code has one thread, so a JavaScript student meets threads and data races only as ideas — the concept layer has to supply them (non-negotiable 4; `concept-only`, D-023), assessed by direct question (D-005). What prevents a race is CS 240's (D-017).",
      "concepts": [
        "async.shared-memory"
      ],
      "lede": "\"Doing several things at once\" means three different things, depending on the model your language gives you. What each lets run at the same moment decides which bugs you can have — and one of them, the race condition, can reach you even with a single thread.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "The event loop, slowly · async and await · The same code in another host"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Thread** — a line of execution with its own call stack. Several can run in one program, sharing its memory. **Race condition** — two pieces of work touch the same data, and the result depends on which gets there first.",
          "entries": [
            "**Thread** — a line of execution with its own call stack. Several can run in one program, sharing its memory.",
            "**Race condition** — two pieces of work touch the same data, and the result depends on which gets there first."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about what can run at once. A guess is fine; that is what this is\nfor."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "A laptop has 8 cores. Ordinary JavaScript, no workers — how many cores run your code at the same instant? Type a number.",
                          "accept": [
                            "1",
                            "one"
                          ],
                          "miss": "**If you answered 8 — the machine can, but your code has one thread.** The host may use other cores for its own work, such as the network; your functions run one at a time.\n",
                          "why": "One. There is one call stack for your code, and the event loop hands it one piece of work at a time.\n"
                        },
                        {
                          "question": "Two click handlers both do `count++` on one variable, with no `await`. Can one interrupt the other halfway through `count++`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes, you are importing an assumption from a threaded language.** Each handler runs to completion before the next one starts.\n",
                          "why": "No. A handler runs to completion, so `count++` is never split.\n"
                        },
                        {
                          "question": "In a language with threads, two threads each run `count++` once on the same variable, starting at `0`. Is the result always `2`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "miss": "**If you answered yes — `count++` is three steps, not one.** Read, add, write. Two threads can both read `0` before either writes.\n",
                          "why": "No. Sometimes 2, sometimes 1 — it depends on how the two threads' steps fall. That is a race condition.\n"
                        },
                        {
                          "question": "An `async` function checks a variable, then awaits, then uses the variable. While it is paused, can other code change it?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "miss": "**If you answered no because JavaScript has one thread — one thread still takes turns.** At an `await` the function steps aside, and anything queued runs in the gap.\n",
                          "why": "Yes. Every `await` is a point where other code gets a turn.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "Does `asyncio` use more than one thread?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "One thread, one loop — the same model as JavaScript for `async` code.\n"
                        },
                        {
                          "question": "Does Python have real threads as well?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "It does, and that is the difference. You can choose the threaded model, and then races become possible.\n"
                        },
                        {
                          "question": "What did the GIL historically prevent? A few words.",
                          "accept": [
                            "parallel bytecode",
                            "true parallelism",
                            "two threads running python at once",
                            "parallel execution"
                          ],
                          "why": "Two threads executing Python bytecode simultaneously — which limited parallelism without eliminating race conditions.\n"
                        },
                        {
                          "question": "With threads, can two `count += 1` interleave?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "Yes — it is several bytecodes, and a switch can land in the middle.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "`Console.WriteLine(1); var t = Task.Run(() => Console.WriteLine(4)); Console.WriteLine(2);` — is the order guaranteed?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "`Task.Run` uses a **thread pool** — genuinely parallel. The ordering is not determined, which is the deepest difference from JavaScript.\n"
                        },
                        {
                          "question": "Does a blocking call freeze the whole program?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Only that thread. Others keep running — which is the freedom JavaScript does not have and the danger it does not have either.\n"
                        },
                        {
                          "question": "Two threads both do `count++` — is the result always `2`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "A race: read, increment and write are separate steps, and both threads can read the same value before either writes.\n"
                        },
                        {
                          "question": "Can two tasks touch the same variable at the same instant?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "Yes — a data race, which ordinary single-threaded JavaScript cannot have.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can compare an event loop, threads and coroutines by what each lets run at once",
                "I can say what a race condition is"
              ]
            }
          ]
        },
        {
          "h": "Three models, one question"
        },
        {
          "p": "Every concurrency model answers one question: **what can be running at the same\nmoment?** Two words keep the answers apart."
        },
        {
          "list": [
            "**Concurrent** — several pieces of work are *in progress* at once. They may take turns.",
            "**Parallel** — several pieces of work are *executing* at the same instant, on different cores."
          ]
        },
        {
          "p": "An **event loop** is what you have met: one thread, and a queue of work. One piece\nruns at a time, to completion; the next runs when it finishes. Lots of work is in\nprogress — requests in flight, timers pending — but only one piece of *your* code\nis ever executing. Concurrent, not parallel."
        },
        {
          "p": "**Threads** are several lines of execution in one program, each with its own call\nstack, all sharing the program's memory. On several cores they run at the same\ninstant. Even on one core the system switches between them whenever it likes —\n**anywhere**, including halfway through a statement. Concurrent, and often\nparallel."
        },
        {
          "p": "A **coroutine** is a function that can pause partway and be resumed later, at\npoints it marks itself. An `async` function is one: every `await` is a marked\npause. Coroutines on an event loop take turns, and a switch can happen **only at\nthose marks**. Python's `async def` works the same way."
        },
        {
          "table": {
            "head": [
              "",
              "What executes at the same instant",
              "Where a switch can happen",
              "Shared variables"
            ],
            "rows": [
              [
                "**Event loop**",
                "one piece of your code",
                "only between pieces of work",
                "yes — but never touched mid-piece"
              ],
              [
                "**Coroutines on a loop**",
                "one piece of your code",
                "only at a marked pause (`await`)",
                "yes — and can change across a pause"
              ],
              [
                "**Threads**",
                "several, on several cores",
                "**anywhere**, even mid-statement",
                "yes — and can be touched at the same instant"
              ]
            ]
          }
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "A coroutine on an event loop — where can another piece of code get a turn in the middle of it?",
                "choices": [
                  "Anywhere",
                  "Only at an await"
                ],
                "accept": [
                  "Only at an await"
                ],
                "why": "Only at a marked pause — in JavaScript, an `await`. Between two of them it runs to completion.\n"
              },
              {
                "question": "Threads — can a switch happen halfway through `count++`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. A thread can be paused between any two steps, and `count++` is several.\n"
              },
              {
                "question": "An event loop with a thousand requests in flight — concurrent, parallel, or both?",
                "choices": [
                  "Concurrent",
                  "Parallel",
                  "Both"
                ],
                "accept": [
                  "Concurrent"
                ],
                "why": "Concurrent. Many are in progress; one piece of your code executes at a time.\n"
              }
            ]
          }
        },
        {
          "h": "What a race condition is"
        },
        {
          "p": "A **race condition** is two pieces of work touching the same data, where the\nresult depends on which gets there first. Nothing is wrong with either piece on\nits own. The bug lives in the timing between them."
        },
        {
          "p": "The classic one needs threads. `count++` looks like one step, but it is three:\nread `count`, add one, write it back. Two threads, each running `count++` once,\nstarting at `5`:"
        },
        {
          "table": {
            "head": [
              "Moment",
              "Thread A",
              "Thread B",
              "`count`"
            ],
            "rows": [
              [
                "1",
                "reads `5`",
                "",
                "5"
              ],
              [
                "2",
                "",
                "reads `5`",
                "5"
              ],
              [
                "3",
                "writes `6`",
                "",
                "6"
              ],
              [
                "4",
                "",
                "writes `6`",
                "**6**"
              ]
            ]
          }
        },
        {
          "p": "Two increments, and `count` went up by one. Had B read after A wrote, it would\nhave been `7`. When the reads and writes happen at the same instant on shared\nmemory, it is called a **data race**."
        },
        {
          "p": "What makes races hard is not understanding them. It is that they **do not\nreproduce**: the steps fall differently on every run, so the program works a\nhundred times and fails on the hundred-and-first, and a test rarely catches it."
        },
        {
          "p": "JavaScript's ordinary code cannot write this one. There is no second thread to\ninterleave with `count++`, and a callback runs to completion. You can still meet\nit in C#, Java, Go, Rust — and in Python once you choose its threads. Stopping one\nis a later course's material; this one asks only that you know it when you see it."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "The table — if thread B read after thread A wrote, what value would `count` end at?",
                "accept": [
                  "7"
                ],
                "why": "`7`. The result depends on the order the steps fall in — which is what makes it a race.\n"
              },
              {
                "question": "A race condition needs two things: two pieces of work, and ______ they both touch. Type what fills the blank.",
                "accept": [
                  "data",
                  "shared data",
                  "the same data",
                  "the same variable",
                  "a shared variable",
                  "shared state",
                  "the same state"
                ],
                "why": "Data they both touch — and a result that depends on which touches it first.\n"
              }
            ]
          }
        },
        {
          "h": "Where JavaScript can still race"
        },
        {
          "p": "One thread does not mean one thing at a time from start to finish. The event loop\nlesson had two calls to `buy` both passing a `stock > 0` check, because each\npaused at `await` before subtracting:"
        },
        {
          "code": "async function buy(name) {\n  if (stock > 0) {          // check\n    await pay(name);        // pause — the other call runs here\n    stock--;                // act on a check that may be stale\n  }\n}",
          "lang": "js",
          "file": "buy.js"
        },
        {
          "p": "That is a race condition: two pieces of work, one variable, and a result that\ndepends on the order the payments settle. It is **not** a data race — no two lines\never ran at the same instant, and `stock--` was never split. The switch can only\nhappen at the `await`, which is what makes it findable: in a coroutine you can\npoint to every place the world may change under you. With threads, it is every\nline."
        },
        {
          "p": "JavaScript's workers are the one way to get a second thread. They share nothing\nby default: data sent to a worker is **copied**, not shared, so two threads never\nhold the same variable. Sharing memory between them takes a special kind of\nbuffer that ordinary code does not use."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "`buy.js` — is it a race condition?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. Two calls touch `stock`, and the result depends on the order they run in.\n"
              },
              {
                "question": "`buy.js` — did two lines ever execute at the same instant?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. One thread; the calls took turns at the `await`. A race condition, not a data race.\n"
              }
            ]
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what can run at the same instant, and where can a switch happen?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Threads for your code",
                  "A switch can happen",
                  "Data race on one variable",
                  "Race across a pause"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "one (workers copy, not share)",
                    "at `await`",
                    "no",
                    "**yes**"
                  ],
                  [
                    "**Python (`asyncio`)**",
                    "one",
                    "at `await`",
                    "no",
                    "yes"
                  ],
                  [
                    "**Python (threads)**",
                    "many",
                    "anywhere",
                    "yes",
                    "yes"
                  ],
                  [
                    "**C#**",
                    "many (thread pool)",
                    "anywhere",
                    "yes",
                    "yes"
                  ]
                ]
              }
            },
            {
              "p": "The trade runs in both directions. One thread means no data races, and a single\nlong function freezes everything. Many threads mean real parallelism, and a bug\nthat does not reproduce. Neither is the winner — and knowing which model you are\nin is the first thing to settle before you reason about shared state."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Your model is an event loop with coroutines on it. Two things follow:"
              },
              {
                "list": [
                  "Between two `await`s, your code has the thread to itself. Nothing can change a variable under you there.",
                  "At every `await`, anything can. A check made before one is a check about the past."
                ]
              }
            ],
            "python": [
              {
                "p": "Choose the model deliberately: `asyncio` for I/O, threads for blocking libraries,\nprocesses for CPU work."
              },
              {
                "code": "async def main():\n    await asyncio.gather(fetch_a(), fetch_b())   # one thread, turns at each await",
                "lang": "python"
              },
              {
                "p": "Because you *can* choose threads, you can also choose data races. Know which model\nyou are in before reasoning about shared state."
              }
            ],
            "csharp": [
              {
                "p": "Real parallelism: two threads can run your code at the same instant, so shared\nstate can race."
              },
              {
                "code": "// C#, two threads\ncount++;   // read, add, write — three steps",
                "lang": "csharp"
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say, for each of the three\nmodels, where a switch between pieces of work can happen."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You are picking a model for two jobs:"
            },
            {
              "list": [
                "**A chat server** holding 10,000 open connections, each idle nearly all the time.",
                "**A photo tool** that resizes 500 large images, each taking a second of pure calculation."
              ]
            },
            {
              "p": "For each job, choose an event loop or threads, and say why — using what each lets\nrun at the same instant. Then say what your choice costs: for the event loop, what\none slow calculation does to the other 9,999 connections; for threads, what can\nnow happen to a variable two of them update."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Event loop**: one piece of your code executes at a time, to completion. **Coroutines** (`async` functions) switch only at marked pauses — `await`. **Threads** execute at the same instant and can switch anywhere.",
                "A **race condition** is two pieces of work touching the same data with a result that depends on which gets there first. A **data race** needs threads; ordinary JavaScript cannot have one.",
                "JavaScript **can** still race **across an `await`**: a check made before it may be stale after it."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "JavaScript execution model",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Execution_model",
              "note": "agents, run to completion, and why one thread."
            },
            {
              "title": "Using Web Workers",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Using_web_workers",
              "note": "real threads that copy messages instead of sharing memory."
            }
          ]
        }
      ],
      "_source": "lessons/concurrency-models.md",
      "practice": {
        "id": "concurrency-models",
        "outcome": 9,
        "steps": [
          {
            "kind": "predict",
            "title": "What runs at once?",
            "prompt": "Three quick ones.",
            "items": [
              {
                "id": "p1",
                "question": "Ordinary JavaScript, no workers, on an 8-core laptop — how many pieces of your code execute at the same instant? Type a number.",
                "accept": [
                  "1",
                  "one"
                ],
                "miss": "If you said 8: the machine has the cores, but your code has one thread. The event loop hands it one piece of work at a time.",
                "why": "One. Many things can be in progress; one piece of your code executes at a time."
              },
              {
                "id": "p2",
                "question": "Two threads each run `count++` once on the same variable, starting at `0`. Is the result always `2`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "miss": "If you said yes: `count++` is read, add, write. Both threads can read `0` before either writes.",
                "why": "No. It can be 1 — both read `0`, both write `1`. The result depends on how the steps fall: a race condition."
              },
              {
                "id": "p3",
                "question": "An `async` function checks a variable, awaits, then uses the variable. While it is paused, can other code change the variable?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "miss": "If you said no because there is one thread: the thread takes turns, and an `await` is where this function gives up its turn.",
                "why": "Yes. At the `await` the function steps aside, and anything queued runs — including code that changes what it checked."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "An event loop with a thousand requests in flight — concurrent, parallel, or both?",
                "choices": [
                  "Concurrent",
                  "Parallel",
                  "Both"
                ],
                "accept": [
                  "Concurrent"
                ],
                "why": "Concurrent: a thousand are in progress. Not parallel: one piece of your code executes at a time."
              },
              {
                "id": "p5",
                "question": "A coroutine on an event loop can be switched out only at one kind of place. Which, in JavaScript? Name the keyword.",
                "accept": [
                  "await",
                  "an await",
                  "at an await",
                  "at await",
                  "at each await"
                ],
                "why": "An `await` — the marked pause. Between two of them, the function runs to completion."
              },
              {
                "id": "p6",
                "question": "Threads — can a switch happen halfway through a single statement?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. The system can pause a thread between any two steps, and one statement can be several steps."
              }
            ],
            "hints": [
              {
                "when": "you counted the machine's cores",
                "say": "Count call stacks for your code, not cores. How many does ordinary JavaScript give you?"
              },
              {
                "when": "you said count++ is always safe with threads",
                "say": "Break `count++` into the steps the machine does. Could the other thread do something between two of them?"
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Two threads, one variable",
            "prompt": "Two threads, A and B, each add one to `count`, which starts at `10`. Each does it in three steps: **read** `count`, **add** one to what it read, **write** the result back. Give `count` at the end for each order of steps.\n",
            "items": [
              {
                "id": "t1",
                "code": "A read, A add, A write, B read, B add, B write   —   count at the end",
                "accept": [
                  "12"
                ],
                "why": "`12`. B read after A wrote, so B started from `11`."
              },
              {
                "id": "t2",
                "code": "A read, B read, A add, B add, A write, B write   —   count at the end",
                "accept": [
                  "11"
                ],
                "miss": "If you said 12: look at what B read. A had not written yet.",
                "why": "`11`. Both read `10`, both wrote `11`, and one increment was lost."
              },
              {
                "id": "t3",
                "code": "A read, A add, B read, B add, B write, A write   —   count at the end",
                "accept": [
                  "11"
                ],
                "why": "`11`. B read before A wrote, so both started from `10`. The last write wins, and it is `11` either way."
              },
              {
                "id": "t4",
                "question": "The same two increments as click handlers in ordinary JavaScript, with no `await` — which of the three orders can happen?",
                "choices": [
                  "Only the first",
                  "Only the second and third",
                  "All three"
                ],
                "accept": [
                  "Only the first"
                ],
                "why": "Only the first. Each handler runs to completion, so one finishes all three steps before the other starts."
              }
            ],
            "extra": [
              {
                "id": "t5",
                "question": "Three threads each add one to `count`, starting at `0`. What is the smallest value `count` can end at?",
                "accept": [
                  "1"
                ],
                "why": "`1`. If all three read `0` before any writes, all three write `1`."
              },
              {
                "id": "t6",
                "code": "A read, B read, B add, B write, A add, A write   —   count at the end, starting at 10",
                "accept": [
                  "11"
                ],
                "why": "`11`. A read `10` before B wrote, so A's write of `11` overwrites B's."
              }
            ],
            "hints": [
              {
                "when": "you got 12 for an order where both reads come first",
                "say": "Track what each thread read, not what `count` is when it writes. A thread adds one to the value it read — which may be stale."
              },
              {
                "when": "you picked an interleaved order for JavaScript",
                "say": "A handler with no `await` runs to completion. Can another handler get a turn between its read and its write?"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a coupon",
            "prompt": "Code from a checkout page, with a comment an AI assistant added. `checkCoupon` returns a promise that fulfils with `true` for a valid code. Review it — including the comment.\n",
            "filename": "coupon.js",
            "code": "let couponUsed = false;\nlet total = 100;\n\n// Safe: JavaScript is single-threaded, so this cannot race.\nasync function applyCoupon(code) {\n  if (!couponUsed) {\n    const ok = await checkCoupon(code);\n    if (ok) {\n      total -= 20;\n      couponUsed = true;\n    }\n  }\n}\n\napplyCoupon(\"SAVE20\");\napplyCoupon(\"SAVE20\");\n",
            "items": [
              {
                "id": "c1",
                "question": "On which line can the second call run in the middle of the first? Type the line number.",
                "accept": [
                  "7",
                  "line 7"
                ],
                "why": "Line 7. At the `await` the first call steps aside, and the second call runs its check on line 6 while `couponUsed` is still `false`."
              },
              {
                "id": "c2",
                "question": "The code is valid both times. What value is `total` once both calls have finished?",
                "accept": [
                  "60"
                ],
                "why": "`60`. Both checks on line 6 ran before either call reached line 10, so the discount was taken twice."
              },
              {
                "id": "c3",
                "question": "Is the comment on line 4 right?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. One thread rules out a data race, not a race condition. Two calls interleave at the `await`, and the result depends on the order."
              },
              {
                "id": "c4",
                "question": "Did line 9 ever run in both calls at the same instant?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No. The two calls took turns — each `total -= 20` ran to completion. It is a race condition, not a data race."
              },
              {
                "id": "c5",
                "question": "In a language with threads, and with no `await` at all, could two calls both be between line 6 and line 10 at once?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. With several threads, a switch can happen between any two steps, so both can pass line 6 before either reaches line 10. With threads, there is no line where you can say \"the switch happens here\"."
              }
            ],
            "hints": [
              {
                "when": "you picked line 6 for where the other call gets in",
                "say": "Line 6 is where the second call does its damage. Which line in the first call gave it the turn?"
              },
              {
                "when": "you said total is 80",
                "say": "Trace the order: which lines does the first call reach before it pauses, and what does the second call see on line 6?"
              },
              {
                "when": "you agreed with the comment",
                "say": "Single-threaded rules out two lines at the same instant. Does a race need that — or only a result that depends on the order two pieces of work run in?"
              }
            ]
          }
        ]
      }
    },
    "splitting-modules": {
      "id": "splitting-modules",
      "title": "Where to put the seam",
      "kind": "read",
      "outcome": 7,
      "shape": "conceptual",
      "why": "Outcome 7 asks for a program split into modules with deliberate interfaces, and modules stayed essential when dependency management was demoted. Splitting is easy; defending where the seam went is the part a student can get wrong.",
      "concepts": [
        "mod.boundary"
      ],
      "lede": "Splitting a program is easy. Splitting it in the right place is the skill — and the test is not file size, it is how much two halves have to know about each other.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What a module promises · Adding and pinning a dependency"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Coupling** — how much one module must know about another's internals to work.",
          "entries": [
            "**Coupling** — how much one module must know about another's internals to work."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about module boundaries. Pick an answer, or where there is a box, type the value — or `error` if it throws."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "A module has `export let count = 0; export function bump(){ count++; }` and bumps twice. What value does an importer of `count` see?",
                          "accept": [
                            "2"
                          ],
                          "miss": "**If you answered `0`, you are expecting an import to copy the value.** In JavaScript it does not — you get a live view of the binding.\n",
                          "why": "Imported bindings are **live**. You import the binding, not a copy of its value, so the importer sees changes made inside the module.\n"
                        },
                        {
                          "question": "In an importing file: `import { count } from \"./c.mjs\";  count = 99;` — what is `count` afterwards? Type the value, or error.",
                          "accept": [
                            "error",
                            "typeerror",
                            "TypeError",
                            "an error",
                            "it throws"
                          ],
                          "why": "An imported binding is read-only at the importing end. Only the owning module may change it — which is what makes live bindings safe.\n"
                        },
                        {
                          "question": "`greet();` comes before `import { greet } from \"./g.mjs\";` in the file. Does `greet()` run?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "miss": "**If you said no, you are reading \"imports at the top\" as a rule rather than a convention.** It is a convention *because* imports are hoisted — position genuinely does not matter, so putting them elsewhere only misleads readers.\n",
                          "why": "Imports are hoisted and resolved before any of the module body executes, so position in the file does not matter.\n"
                        },
                        {
                          "question": "`a.mjs` imports `b.mjs`, which imports `a.mjs` and reads a `const` from it. What does it read? Type the value, or error.",
                          "accept": [
                            "error",
                            "referenceerror",
                            "ReferenceError",
                            "an error",
                            "it throws"
                          ],
                          "miss": "**If you expected `undefined` — with `var`-era modules it often was.** With `const` and `let` you get the temporal dead zone instead, which is louder and better. Either way a cycle is a design problem to fix, not a syntax problem to work around.\n",
                          "why": "`ReferenceError: Cannot access 'a' before initialization` — the temporal dead zone, reached through a circular import. The binding exists and is not yet initialised.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "After `from c import count`, the module bumps `count` from 0 to 1. What value does the importer see?",
                          "accept": [
                            "0"
                          ],
                          "why": "Python **copies** the reference at import time, so the importer keeps the old value. `import c` then `c.count` sees the change — a real difference from JavaScript's live bindings.\n"
                        },
                        {
                          "question": "Is an `import` at the bottom of a file valid?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "Valid but unusual — `import` is an ordinary statement that runs in order, unlike JavaScript's hoisted declarations.\n"
                        },
                        {
                          "question": "`a.py` imports `b.py`, which imports `a.py` and reads a name from it. What happens? Type the value it reads, or error.",
                          "accept": [
                            "error",
                            "importerror",
                            "ImportError",
                            "an error",
                            "partial module"
                          ],
                          "why": "Usually `ImportError: cannot import name` — the module object exists but is only partly populated. Same underlying problem, different symptom.\n"
                        },
                        {
                          "question": "What makes a directory a package? Name the file.",
                          "accept": [
                            "__init__.py",
                            "init file",
                            "an __init__.py",
                            "__init__",
                            "init.py"
                          ],
                          "why": "Historically an `__init__.py`. It also decides what the package exposes, which is the module-interface question.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "What is the unit of modularity — the file, or something else? Name it in a word or two.",
                          "accept": [
                            "the assembly",
                            "assembly",
                            "an assembly",
                            "namespace",
                            "the namespace",
                            "the project",
                            "project"
                          ],
                          "why": "The **assembly** (a project) for deployment and access; the namespace for naming. Files are organisational and carry no meaning of their own.\n"
                        },
                        {
                          "question": "Where is an `internal` member visible?",
                          "choices": [
                            "This file only",
                            "This assembly only",
                            "Anywhere that references it"
                          ],
                          "accept": [
                            "This assembly only"
                          ],
                          "why": "Visible inside this assembly, invisible outside — a genuine module boundary the compiler enforces. JavaScript has nothing equivalent below the file.\n"
                        },
                        {
                          "question": "Can two assemblies reference each other?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Circular project references are rejected outright. C# makes the dependency graph acyclic by construction.\n"
                        },
                        {
                          "question": "What must a file declare to use a type from another namespace? Name the keyword.",
                          "accept": [
                            "using",
                            "a using directive",
                            "using directive",
                            "using statement"
                          ],
                          "why": "`using`. It affects name resolution only — the reference to the assembly is separate, which is a distinction JavaScript folds into one line.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can split a program and defend where the seam went",
                "I can say what makes one boundary better than another",
                "I can recognise a circular dependency and say what it means about the design"
              ]
            }
          ]
        },
        {
          "h": "The test is coupling, not size"
        },
        {
          "p": "A 400-line file is not automatically a problem, and four 100-line files are not\nautomatically an improvement. The question is: **how much does each side have to\nknow about the other's internals?**"
        },
        {
          "code": "// Weak coupling — the caller knows a name and a shape.\nimport { formatPrice } from \"./money.js\";\nformatPrice(1099);\n\n// Strong coupling — the caller knows how it works inside.\nimport { CURRENCY_TABLE, roundingMode, applyRounding } from \"./money.js\";\napplyRounding(1099 * CURRENCY_TABLE.usd, roundingMode);",
          "lang": "js"
        },
        {
          "p": "The second is four files' worth of knowledge in one call site. Changing anything\ninside `money.js` now breaks it. That is the real cost of a bad seam, and it has\nnothing to do with line count."
        },
        {
          "block": "notice",
          "label": "A seam is a promise",
          "body": [
            {
              "p": "Every export is a promise you have to keep. A module with three exports has made\nthree promises; one with thirty has made thirty. **Export the fewest things that\nlet callers do their job** — the rest stays yours to change."
            }
          ]
        },
        {
          "h": "Signs you cut in the wrong place"
        },
        {
          "list": [
            "**Two modules change together, always.** They are one module wearing two filenames. The seam is not where the joint is.",
            "**A circular import.** `a` needs `b` and `b` needs `a`. Something belongs in a third module, or in only one of them.",
            "**A module exports its internals** so another can do the work. The behaviour should have moved, not the data.",
            "**The import list at the top is longer than the function below it.** The file is a coordinator, which is fine — but check that it is *only* that."
          ]
        },
        {
          "h": "Cohesion: what belongs together"
        },
        {
          "p": "The counterpart to coupling. A module is cohesive when everything in it is there\nfor the same reason."
        },
        {
          "code": "money.js       formatPrice, parsePrice, CURRENCIES      — cohesive\nhelpers.js     formatPrice, sendEmail, parseDate, uuid  — not"
        },
        {
          "p": "`helpers.js` and `utils.js` are where cohesion goes to die: nothing has a reason\nto be there beyond \"it did not fit elsewhere\", so nothing ever leaves."
        },
        {
          "scaffold": {
            "label": "Deciding where the seam goes",
            "body": [
              {
                "h": "Deciding where the seam goes"
              },
              {
                "code": "// You are splitting a file, or deciding whether to.\n\n// 1. List what the candidate module would EXPORT.\n//    more than about five things -> you are splitting a namespace, not a\n//    module. Look for a smaller boundary inside it.\n\n// 2. For each export, ask: could its implementation change without the\n//    caller changing?\n//    no -> that export leaks an internal. Move the behaviour, not the data.\n\n// 3. Would the two halves ever change for DIFFERENT reasons?\n//    no  -> they are one module. Leave them.\n//    yes -> the seam is real. Cut there.\n\n// 4. Does the new module import back from its caller?\n//    yes -> a cycle. Something belongs in a third place.",
                "lang": "js",
                "file": "seam.js"
              },
              {
                "p": "Now apply it."
              },
              {
                "code": "// checkout.js is 300 lines: validation, tax calculation, and receipt formatting.\n// 1. Do they change for different reasons?   ______\n// 2. How many exports would tax need?        ______\n// 3. Which way would the imports point?      ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "Yes — tax rules change with legislation, receipt formatting with design, and\nvalidation with business rules. Tax needs roughly one export,\n`taxFor(items, region)`. And imports point one way: `checkout` imports `tax`,\nnever the reverse."
              }
            ]
          }
        },
        {
          "video": {
            "title": "One file becoming three",
            "caption": "The checkout example split live, with each candidate seam tested against the four questions — including one split that looks reasonable and creates a cycle.\n"
          }
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what is the unit of modularity, and what can the language hide?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Unit",
                  "Can hide",
                  "Cycles"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "the file",
                    "anything not exported",
                    "allowed — and they hit the dead zone"
                  ],
                  [
                    "**Python**",
                    "the file / package",
                    "nothing (`_name` is convention)",
                    "allowed — partial module"
                  ],
                  [
                    "**C#**",
                    "the assembly",
                    "`internal`, enforced",
                    "**rejected at build time**"
                  ]
                ]
              }
            },
            {
              "p": "Two things to carry. First, only C# has a hiding level between \"in this file\" and\n\"public to everyone\", and `internal` is genuinely enforced — a facility worth\nknowing exists when you meet it."
            },
            {
              "p": "Second, C# refuses circular project references outright. JavaScript and Python\nallow them and let you discover the problem at run time. That is the same\n*when do you find out* axis again, applied to architecture rather than to values."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "One concept per file, named exports, imports at the top."
              },
              {
                "code": "export function formatPrice(cents) { }     // named — the default\nexport { formatPrice, parsePrice };         // or gathered at the bottom",
                "lang": "js"
              },
              {
                "p": "Prefer named exports over `export default`: they are greppable, renaming is a\nrename rather than a convention change, and an editor can autocomplete them.\nA default export lets each importer choose a different name for the same thing,\nwhich is rarely what you want."
              }
            ],
            "python": [
              {
                "p": "One concept per module, packages for grouping, and `__all__` to state the\ninterface explicitly."
              },
              {
                "code": "__all__ = [\"format_price\", \"parse_price\"]   # what `from m import *` exposes",
                "lang": "python"
              },
              {
                "p": "Remember that `from m import x` copies the reference. If `x` is rebound inside\n`m` later, the importer will not see it — import the module instead when that\nmatters."
              }
            ],
            "csharp": [
              {
                "p": "Assemblies for real boundaries, namespaces for naming, `internal` for anything\nthe outside world should not see."
              },
              {
                "code": "internal sealed class TaxTable { }         // invisible outside this assembly\npublic static decimal TaxFor(...) { }      // the promise you are keeping",
                "lang": "csharp"
              },
              {
                "p": "Because the compiler rejects circular project references, the dependency graph is\nacyclic by construction — a constraint that prevents a whole class of design\ndrift."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say what a circular import tells\nyou about a design, and why \"import it lazily inside the function\" is a\nworkaround rather than a fix."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A 900-line `orders.js` contains order creation, payment, email notification and\nan audit log. A colleague proposes splitting it into four files of about 225\nlines each."
            },
            {
              "p": "Two positions:"
            },
            {
              "list": [
                "**Split by those four** — they change for different reasons and have different owners.",
                "**Do not split yet** — until you know which parts change together, four files with mutual imports is worse than one file."
              ]
            },
            {
              "p": "Pick one and say why. Then say what evidence would settle it: name something you\ncould look at in the repository's history that shows whether two parts change\ntogether. If the split creates a cycle between payment and audit, say what a\nthird module would have to contain to break it."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "The test for a seam is **coupling**, not file size: how much each side must know about the other's internals.",
                "**Every export is a promise.** Export the fewest things that let callers do their job.",
                "A **circular import** is a design signal, not a syntax problem — in JavaScript it surfaces as the temporal dead zone.",
                "Imports are **hoisted**; putting them at the top is convention because position genuinely does not matter.",
                "JavaScript imports are **live bindings**, read-only at the importing end. Python copies the reference instead."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "JavaScript modules",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Modules",
              "note": "the guide, including default versus named exports."
            },
            {
              "title": "import",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/import",
              "note": "hoisting, and that imported bindings are live and read-only."
            },
            {
              "title": "export",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/export",
              "note": "the forms, and re-exporting for a package's public face."
            }
          ]
        }
      ],
      "_source": "lessons/splitting-modules.md",
      "practice": {
        "id": "splitting-modules",
        "outcome": 7,
        "steps": [
          {
            "kind": "predict",
            "title": "Where the seam goes, and what crosses it",
            "prompt": "Three questions about module boundaries. Pick an answer, or where there is a box, type the value — or `error` if it throws.",
            "items": [
              {
                "id": "p1",
                "question": "`checkout.js` holds validation, tax and receipt formatting. Tax changes with legislation, receipts with the design team. First question answered — is there a seam between tax and receipts?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "They change for different reasons, on different schedules, at different people's request. That is what a real seam looks like — not the file's length."
              },
              {
                "id": "p2",
                "question": "`c.mjs`: `export let count = 0; export function bump() { count++; }`. An importer calls `bump()` twice, then reads `count`. What value does it see?",
                "accept": [
                  "2"
                ],
                "why": "Imported bindings are **live** — you import the binding, not a copy of its value, so the importer sees what the owning module did."
              },
              {
                "id": "p3",
                "question": "`a.mjs` imports `b.mjs`; `b.mjs` imports `a.mjs` and reads a `const` from it while loading. What does it read? Type the value, or error.",
                "accept": [
                  "ReferenceError",
                  "referenceerror",
                  "error",
                  "an error",
                  "it throws"
                ],
                "why": "`b` runs first and reaches for `a`'s `const` before `a`'s body has run — the temporal dead zone, through a cycle. A cycle says something belongs in a third module."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "In an importing file: `import { count } from \"./c.mjs\";   count = 99;` — what is `count` afterwards? Type the value, or error.",
                "accept": [
                  "TypeError",
                  "typeerror",
                  "error",
                  "an error",
                  "it throws"
                ],
                "why": "Read-only at the importing end: `Assignment to constant variable`. Only the owning module may change its binding, which is what makes a live binding safe to share."
              },
              {
                "id": "p5",
                "question": "`greet();` comes before `import { greet } from \"./g.mjs\";` in the file. Does `greet()` run?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Imports are hoisted and resolved before any of the module body runs. \"Imports at the top\" is a convention for readers, not a rule the language needs."
              },
              {
                "id": "p6",
                "code": "function taxFor(items, region, rates = RATES) {}   taxFor.length",
                "accept": [
                  "2"
                ],
                "why": "`length` counts parameters up to the first one with a default. A parameter with a default is optional, and `length` reports only what a caller must pass."
              }
            ],
            "hints": [
              {
                "when": "you answered no for the first",
                "say": "Ask why each part would change. If the answers are different people and different calendars, a change to one should not have to touch the other."
              },
              {
                "when": "you answered 0 for the second",
                "say": "That is what Python's `from c import count` does — it copies the reference once. A JavaScript import is a live view of the binding."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Judge the seam",
            "prompt": "For each symptom, pick whether the boundary is in the right place, or answer the question asked.",
            "items": [
              {
                "id": "t1",
                "question": "Two modules always change in the same commit. Right place?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "They are one module wearing two filenames. A seam should separate things that change for different reasons."
              },
              {
                "id": "t2",
                "question": "A module exports its internal lookup table so a caller can do the calculation. Right place?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "The behaviour should have moved into the module, not the data out of it. The caller now depends on the table's shape, and the module can never change it."
              },
              {
                "id": "t3",
                "question": "`tax.js` has been split out of `checkout.js`. Should `tax.js` import anything from `checkout.js`?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "Imports point one way: `checkout` uses `tax`, never the reverse. If tax needs something from checkout, that something belongs in tax or in a third module."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "Module A imports B, and B imports A. Right place?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "A cycle means something belongs in a third module, or in only one of the two. It is a design signal, not a syntax problem to work around."
              },
              {
                "id": "t5",
                "question": "`helpers.js` holds `formatPrice`, `sendEmail`, `parseDate` and `uuid`. Is it cohesive?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "Nothing in it is there for the same reason, so nothing ever has a reason to leave. Cohesion is \"these change together\", not \"these are small\"."
              }
            ],
            "hints": [
              {
                "when": "you judged the first acceptable",
                "say": "Ask what the split bought you. If every change touches both files, the boundary is costing imports and buying nothing."
              },
              {
                "when": "you said tax may import from checkout",
                "say": "Then checkout imports tax and tax imports checkout — a cycle. Draw the arrows before you cut."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a split",
            "prompt": "A colleague split a checkout file in two and asked for review. The two files are shown one after the other. It runs today; the seam is in the wrong place. Answer by line number where asked.\n",
            "filename": "tax.js + checkout.js",
            "code": "// ---- tax.js ----\nimport { formatReceipt } from \"./checkout.js\";\nexport const RATES = { wa: 0.065, or: 0 };\nexport function taxFor(items, region) {\n  let sub = 0;\n  for (const i of items) sub += i.price;\n  return sub * RATES[region];\n}\n\n// ---- checkout.js ----\nimport { taxFor, RATES } from \"./tax.js\";\nexport function checkout(order) {\n  if (!order.items.length) throw new RangeError(\"empty order\");\n  const sub = order.items.reduce((t, i) => t + i.price, 0);\n  const rate = RATES[order.region];\n  return sub + sub * rate;\n}\nexport function formatReceipt(total) { return \"$\" + total.toFixed(2); }\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line creates a circular import? Give the line number.",
                "accept": [
                  "2",
                  "line 2"
                ],
                "why": "tax.js imports from checkout.js, which imports from tax.js. Tax has no use for receipts at all — the line is not even needed, and it turns two modules into a knot."
              },
              {
                "id": "c2",
                "question": "Which line in `checkout.js` reaches into tax's internals instead of asking tax to do the work? Give the line number.",
                "accept": [
                  "15",
                  "line 15"
                ],
                "why": "Checkout reads the rate table and does the tax arithmetic itself. The behaviour should have stayed behind the seam; now a change to how tax works has to change checkout too — and `taxFor` is exported and never called."
              },
              {
                "id": "c3",
                "question": "Once both are fixed, how many names should `tax.js` export? Give a number.",
                "accept": [
                  "1",
                  "one"
                ],
                "why": "Just `taxFor`. `RATES` was exported only so checkout could reach in; with the calculation behind `taxFor`, the table is tax's own business again."
              }
            ],
            "hints": [
              {
                "when": "you named line 11 for the cycle",
                "say": "Line 11 is the import that should exist — checkout uses tax. Look for the arrow pointing the other way."
              },
              {
                "when": "you named line 7 for the second",
                "say": "Line 7 is inside tax.js, which is allowed to read its own table. The question is which line *outside* tax needs to know the table exists."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Cut the seam",
            "prompt": "This file works: `checkout` validates, totals and taxes an order, with the rate table built in. Change one thing: expose a `taxFor(items, region, rates)` that computes the tax on its own, from whatever rate table it is handed, so tax can be tested and changed without building an order. `checkout` must keep behaving exactly as it does. Then, in one sentence: why take `rates` as a parameter rather than read `RATES`?\n",
            "exportName": "checkoutModule",
            "filename": "checkout.js",
            "starter": "const RATES = { wa: 0.065, or: 0 };\n\nfunction checkout(order) {\n  if (!order.items.length) throw new RangeError(\"empty order\");\n  let sub = 0;\n  for (const i of order.items) sub += i.price;\n  return sub + sub * RATES[order.region];\n}\n\n// What this file exposes.\nconst checkoutModule = { checkout };\n",
            "tests": [
              {
                "label": "checkout still totals with tax",
                "body": "return subject.checkout({ items: [{price: 100}], region: 'wa' }) === 106.5;"
              },
              {
                "label": "a zero-rate region still works",
                "body": "return subject.checkout({ items: [{price: 100}], region: 'or' }) === 100;"
              },
              {
                "label": "an empty order is still rejected",
                "body": "try { subject.checkout({ items: [], region: 'wa' }); return false; } catch (e) { return e instanceof RangeError; }"
              },
              {
                "label": "taxFor is exposed and declares three parameters",
                "body": "return typeof subject.taxFor === 'function' && subject.taxFor.length === 3;"
              },
              {
                "label": "taxFor works with a rate table it is given",
                "body": "return subject.taxFor([{price: 200}], 'x', { x: 0.1 }) === 20;"
              },
              {
                "label": "taxFor does not depend on the module's own table",
                "body": "return subject.taxFor([{price: 100}], 'wa', { wa: 0.5 }) === 50;"
              }
            ],
            "hints": [
              {
                "when": "the last two tests fail but checkout still works",
                "say": "Your `taxFor` is reading the module's `RATES` instead of the table it was handed. A parameter that is ignored is not a seam — the point is that tax can be exercised with any rates."
              },
              {
                "when": "taxFor.length is not 3",
                "say": "A parameter with a default is not counted by `length`, because a caller need not pass it. Declare all three plainly and have `checkout` pass `RATES`."
              },
              {
                "when": "taxFor is undefined",
                "say": "Writing the function is half the change; the object at the bottom is what the file exposes. Add it there."
              }
            ]
          }
        ]
      }
    },
    "interfaces": {
      "id": "interfaces",
      "title": "What a module promises",
      "kind": "read",
      "outcome": 7,
      "shape": "conceptual",
      "why": "Outcome 7 asks for modules with deliberate interfaces, and an export is a promise a module cannot quietly take back. The build step is here because JavaScript runs source directly, so the concept layer has to supply \"source → artifact → running program\" as an idea before later courses assume it.",
      "concepts": [
        "mod.interface",
        "mod.build-step"
      ],
      "lede": "A module's exports are its contract. Everything else is yours to change. That line is the most valuable thing a module boundary gives you — and it only holds if you are deliberate about where you draw it.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Where to put the seam · Contracts, and choosing between the three"
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "Four questions about what a module commits to. Pick an answer, or type one where there is a box."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "A module exports `format` and a helper `pad` used only by `format`. How many promises has it made? Give a number.",
                          "accept": [
                            "2",
                            "two"
                          ],
                          "miss": "**If you answered one because `pad` is obviously internal — someone will import it, the week you are on holiday, because it did exactly what they needed.** Exported means public. The only thing that makes a name private is not exporting it.\n",
                          "why": "Two. Anything exported can be imported, and someone will. `pad` is now part of the contract whether you meant it to be or not.\n"
                        },
                        {
                          "question": "The same module, exporting only `format`. Can you rename `pad`?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "Yes"
                          ],
                          "why": "Freely. Unexported names are invisible outside the file — that invisibility is the whole benefit of a boundary.\n"
                        },
                        {
                          "question": "`export default function format(){}` — what name does the importer use?",
                          "choices": [
                            "format",
                            "Any name they choose"
                          ],
                          "accept": [
                            "Any name they choose"
                          ],
                          "miss": "**If you expected `format`, a default export hands naming to each caller.** It is one *thing*, named differently in every file that uses it — which sounds tidy until you try to find every use of it.\n",
                          "why": "Any name at all. A default export hands naming to each caller, which makes the symbol ungreppable across a codebase.\n"
                        },
                        {
                          "question": "JavaScript runs source directly. What does a build step produce that the source is not? Name it in a word or two.",
                          "accept": [
                            "an artifact",
                            "a bundle",
                            "artifact",
                            "the artifact",
                            "a build artifact",
                            "bundle",
                            "a build output",
                            "build output",
                            "bundled output"
                          ],
                          "miss": "**If you decided this does not apply because JavaScript needs no build — it applies the moment you bundle, minify, or ship anything to a browser.** You can write a great deal of JavaScript without meeting it, which is why it is worth meeting on purpose.\n",
                          "why": "An **artifact** — one file, minified, its imports resolved. \"Source → artifact → running program\" is the idea, and running source directly is how you avoid meeting it.\n"
                        }
                      ]
                    }
                  }
                ],
                "python": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "A module defines `format` and `_pad`. What does the underscore enforce?",
                          "choices": [
                            "Nothing",
                            "Other modules cannot import _pad"
                          ],
                          "accept": [
                            "Nothing"
                          ],
                          "why": "Nothing. It is a convention that says \"do not import this\", and Python deliberately declines to enforce it.\n"
                        },
                        {
                          "question": "What does `__all__` control?",
                          "choices": [
                            "Which names can be imported at all",
                            "Which names from m import * brings in"
                          ],
                          "accept": [
                            "Which names from m import * brings in"
                          ],
                          "why": "Which names `from m import *` brings in — an explicit statement of the module's public face, and documentation even when nobody star-imports.\n"
                        },
                        {
                          "question": "Does Python have a build step?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Not for running. `.pyc` files are a cache, not an artifact you ship — so Python students share this gap with JavaScript ones.\n"
                        },
                        {
                          "question": "What does a wheel (`.whl`) contain? Answer in a few words.",
                          "accept": [
                            "the package",
                            "built distribution",
                            "a built distribution",
                            "a built package",
                            "built package",
                            "the built package",
                            "the module files",
                            "package files"
                          ],
                          "why": "A built distribution — the closest Python comes to an artifact, and the place the idea does show up.\n"
                        }
                      ]
                    }
                  }
                ],
                "csharp": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "question": "What are the four access levels that matter at a module boundary? List them, separated by commas.",
                          "accept": [
                            "public private internal protected",
                            "public, private, internal, protected",
                            "public internal private protected",
                            "public, internal, private, protected",
                            "public, internal, protected, private",
                            "public internal protected private",
                            "public, protected, internal, private",
                            "public protected internal private"
                          ],
                          "why": "`public`, `internal`, `protected`, `private` — and `internal` is the one JavaScript has no analogue for.\n"
                        },
                        {
                          "question": "A helper is marked `internal`. Can another assembly use it?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Not without `InternalsVisibleTo`. The compiler enforces the boundary, so it is a real promise rather than a documented one.\n"
                        },
                        {
                          "question": "What does `dotnet build` produce? Name the kind of file.",
                          "accept": [
                            "a dll",
                            "dll",
                            ".dll",
                            "an assembly",
                            "a .dll",
                            "an artifact",
                            "assembly"
                          ],
                          "why": "A `.dll` — the artifact. C# students meet \"source → artifact → running program\" on day one; it is the step JavaScript lets you skip.\n"
                        },
                        {
                          "question": "Can you run a `.cs` file directly?",
                          "choices": [
                            "Yes",
                            "No"
                          ],
                          "accept": [
                            "No"
                          ],
                          "why": "Not traditionally — the compile step is mandatory. `dotnet run` hides it, but it still happens.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can say what a module exposes and what it keeps",
                "I can state a contract in a language that will not check it",
                "I can say what a build step produces and why the distinction matters"
              ]
            }
          ]
        },
        {
          "h": "Exports are the contract"
        },
        {
          "code": "const RATES = { usd: 1, eur: 0.92 };          // private — not exported\nfunction pad(n) { return String(n).padStart(2, \"0\"); }  // private\n\nexport function formatPrice(cents, currency = \"usd\") {\n  const v = (cents / 100) * RATES[currency];\n  return `${Math.floor(v)}.${pad(Math.round((v % 1) * 100))}`;\n}",
          "lang": "js",
          "file": "money.js"
        },
        {
          "p": "One export, one promise: *give me cents and a currency, get a string*. `RATES`\nand `pad` can be rewritten, renamed or deleted tomorrow. That freedom is what the\nboundary bought, and it exists only because they are not exported."
        },
        {
          "block": "notice",
          "label": "The one-line test",
          "body": [
            {
              "p": "For each export, ask: **could I change how this works without any caller\nchanging?** If not, the export is leaking an internal, and the behaviour should\nmove into the module rather than the data moving out."
            }
          ]
        },
        {
          "h": "Naming the promise"
        },
        {
          "p": "Named exports over default, for three reasons that all come down to tooling:"
        },
        {
          "code": "export function formatPrice() { }   // greppable, autocompletable, renameable\nexport default function () { }       // each importer picks its own name",
          "lang": "js"
        },
        {
          "p": "A default export is defensible when a module genuinely *is* one thing — a React\ncomponent, a configuration object. Even then, a named export costs nothing."
        },
        {
          "p": "Re-exporting is how a folder presents one face:"
        },
        {
          "code": "// money/index.js\nexport { formatPrice, parsePrice } from \"./format.js\";\nexport { convert } from \"./rates.js\";",
          "lang": "js"
        },
        {
          "p": "Callers import from `money/`, and the internal file layout stays yours."
        },
        {
          "scaffold": {
            "label": "Deciding what to export",
            "body": [
              {
                "h": "Deciding what to export"
              },
              {
                "code": "// For each name in a module you are about to export:\n\n// 1. Does a caller need it to do their job?\n//    no  -> do not export it. It is not a convenience, it is a promise.\n//    yes -> keep going.\n\n// 2. Could its implementation change without callers changing?\n//    no  -> it leaks an internal. Move the BEHAVIOUR out instead of the data.\n//    yes -> export it.\n\n// 3. Is it the module's single reason to exist?\n//    yes -> a default export is defensible. A named one still costs nothing.\n//    no  -> named.",
                "lang": "js",
                "file": "export-check.js"
              },
              {
                "p": "Now apply it to four names in a `session.js`."
              },
              {
                "code": "// a) login(user, pass)      — the main operation\n// b) SESSION_KEY            — the localStorage key it uses\n// c) isExpired(session)     — used by login, and by a UI component\n// d) hashPassword(pass)     — used only by login\n\n// Export: ______    Keep private: ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "Export `login` and `isExpired` — both are needed by callers and both could be\nreimplemented freely. Keep `SESSION_KEY` private: exporting it invites callers to\nread storage directly, and then you can never change the key. Keep\n`hashPassword` private; nobody outside needs it and exporting it invites misuse."
              }
            ]
          }
        },
        {
          "h": "Source is not an artifact"
        },
        {
          "p": "JavaScript source runs directly, so nothing in this course makes you meet a\n**build step**. The idea is worth having anyway."
        },
        {
          "code": "source          what you write and read\n   ↓  build     bundle, resolve imports, minify, transpile, typecheck\nartifact        one file, unreadable, what actually ships\n   ↓  run\nrunning program"
        },
        {
          "p": "Three consequences worth carrying:"
        },
        {
          "list": [
            "**The thing that runs is not the thing you wrote.** A stack trace points at the artifact, which is why source maps exist — the debugger reads them.",
            "**The build is a place where things can be checked.** Types, lint rules, tests can all fail the build before anything ships. JavaScript's default is that nothing does.",
            "**Dependencies are resolved at build time**, so what you ship is fixed, even if the registry changes tomorrow."
          ]
        },
        {
          "beat": "compare",
          "body": [
            {
              "p": "The axis: **what can a module actually hide, and what checks the contract?**"
            },
            {
              "table": {
                "head": [
                  "",
                  "Hiding",
                  "Enforced",
                  "Build artifact"
                ],
                "rows": [
                  [
                    "**JavaScript**",
                    "anything unexported",
                    "yes, by the module system",
                    "only if you add one"
                  ],
                  [
                    "**Python**",
                    "nothing (`_` is convention)",
                    "no",
                    "a wheel, for distribution"
                  ],
                  [
                    "**C#**",
                    "`public` / `internal` / `private`",
                    "**by the compiler**",
                    "always — a `.dll`"
                  ]
                ]
              }
            },
            {
              "p": "JavaScript is stronger than Python here and weaker than C#. An unexported name\nis genuinely unreachable — better than Python's honour system — but there is no\nlevel between \"this file\" and \"everyone\", which is what `internal` gives C#."
            },
            {
              "p": "The third column is the gap. A JavaScript student can finish this course without\never producing an artifact, and then meets bundling, source maps and build-time\nchecks all at once in a later course. Knowing the shape now makes that a\nvocabulary problem rather than a conceptual one."
            }
          ]
        },
        {
          "tracks": {
            "js": [
              {
                "p": "Named exports, a barrel file per folder, and nothing exported that callers do not\nneed."
              },
              {
                "code": "export { formatPrice } from \"./format.js\";   // the folder's public face",
                "lang": "js"
              },
              {
                "p": "You have no `internal`, so the folder boundary is a convention your team keeps.\nWrite it down: a short comment at the top of `index.js` saying what the folder\npromises is worth more than it costs."
              }
            ],
            "python": [
              {
                "p": "`__all__` states the interface, the underscore prefix signals intent, and neither\nis enforced."
              },
              {
                "code": "__all__ = [\"format_price\", \"parse_price\"]",
                "lang": "python"
              },
              {
                "p": "Because nothing enforces it, the documented interface *is* the interface. Python\nleans harder on convention than either other track, and on the culture that\nrespects it."
              }
            ],
            "csharp": [
              {
                "p": "`internal` by default for anything not deliberately public, and the project file\nis the boundary."
              },
              {
                "code": "internal static class Rates { }             // this assembly only\npublic static string FormatPrice(...) { }   // the promise",
                "lang": "csharp"
              },
              {
                "p": "Because it is compiler-enforced, \"what does this assembly promise?\" has an exact\nanswer you can compute rather than one you have to trust."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then say what you would lose by\nexporting `pad` \"just in case\" — and name the specific future change it would\nprevent."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "A folder of twelve files currently has an `index.js` re-exporting everything from\nall of them — 40 exports. A new teammate proposes cutting it to the six things\nthe rest of the app actually imports."
            },
            {
              "p": "Two positions:"
            },
            {
              "list": [
                "**Cut it** — 40 exports is 40 promises, and the other 34 were never intended.",
                "**Leave it** — something may already depend on them, and removing an export is a breaking change."
              ]
            },
            {
              "p": "Pick one and say why. Then say how you would find out whether the 34 are used,\nand what you would do about one that is used exactly once from a place that\nshould not be reaching in. Name what you would write down so this does not\nregrow."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Exports are the contract.** Everything unexported is yours to change, which is the entire benefit of a boundary.",
                "The test for each export: **could its implementation change without callers changing?**",
                "**Named exports** over default — greppable, renameable, autocompletable.",
                "A **build step** turns source into an artifact. What runs is not what you wrote, which is why source maps exist and where build-time checks live.",
                "JavaScript hides more than Python and less than C#: unexported is genuinely unreachable, but there is no `internal`."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "export",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/export",
              "note": "named, default, and re-exporting for a folder's public face."
            },
            {
              "title": "JavaScript modules",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Modules",
              "note": "including why a module's top-level names are not global."
            },
            {
              "title": "Source map",
              "href": "https://developer.mozilla.org/en-US/docs/Glossary/Source_map",
              "note": "the artifact-to-source link the debugger uses."
            }
          ]
        }
      ],
      "_source": "lessons/interfaces.md",
      "practice": {
        "id": "interfaces",
        "outcome": 7,
        "steps": [
          {
            "kind": "predict",
            "title": "What a module has promised",
            "prompt": "A module has `format`, and a helper `pad` that only `format` uses. Pick an answer, or type one where there is a box.",
            "items": [
              {
                "id": "p1",
                "question": "If both are exported, how many promises has the module made? Give a number.",
                "accept": [
                  "2",
                  "two"
                ],
                "why": "Two. Anything exported can be imported, and eventually will be — exported means public whether you meant it or not."
              },
              {
                "id": "p2",
                "question": "Deciding about `pad`, the first question: does a caller need it to do their job?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No — callers need `format`; `pad` is how `format` does it. A name no caller needs is not exported, so it stays yours to change."
              },
              {
                "id": "p3",
                "question": "JavaScript source runs directly. What does a build step produce that the source is not? Name it in a word or two.",
                "accept": [
                  "an artifact",
                  "artifact",
                  "the artifact",
                  "a build artifact",
                  "a bundle",
                  "a build output",
                  "bundled output"
                ],
                "why": "An **artifact** — imports resolved, minified, often one file. What runs is not what you wrote."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "Only `format` is exported. Can you rename `pad` without any other file changing?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Unexported names are unreachable from outside the file. That invisibility is the entire benefit of the boundary."
              },
              {
                "id": "p5",
                "question": "`export default function format() {}` — what name must an importer use?",
                "choices": [
                  "format",
                  "Any name they choose"
                ],
                "accept": [
                  "Any name they choose"
                ],
                "why": "Any name at all. A default export hands naming to each caller, which makes one thing hard to find across a codebase."
              },
              {
                "id": "p6",
                "question": "After a build, which one actually runs in production?",
                "choices": [
                  "The source you wrote",
                  "The artifact"
                ],
                "accept": [
                  "The artifact"
                ],
                "why": "The artifact. That is why a stack trace from production points at a line you never wrote, and why the build is a place checks can run before anything ships."
              }
            ],
            "hints": [
              {
                "when": "you answered 1 for the first",
                "say": "Count the exports, not the ones you consider important. A caller cannot tell which exports you meant to be public."
              },
              {
                "when": "you said a caller needs pad",
                "say": "Callers need what `pad` makes possible, and `format` already gives them that. Needing the result is not needing the helper."
              }
            ]
          },
          {
            "kind": "run",
            "title": "An exported object is a shared object",
            "prompt": "A settings module is imitated here with a function that runs once and returns what the module \"exports\". Predict the three lines it prints, then run it.\n",
            "code": "const settings = (() => {\n  const defaults = { theme: \"light\", pageSize: 20 };  // meant to be internal\n  function get(key) { return defaults[key]; }\n  return { get, defaults };                           // ...but exported\n})();\n\nconst mine = settings.defaults;\nmine.pageSize = 5;\nconsole.log(settings.get(\"pageSize\"));\nconsole.log(Object.keys(settings).length);\n\nconst tidy = (() => {\n  const defaults = { theme: \"light\", pageSize: 20 };\n  return { get: key => defaults[key] };\n})();\nconsole.log(\"defaults\" in tidy);\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "5"
                ],
                "why": "`settings.defaults` is the module's own object, not a copy. The caller changed it, and now every other caller's `get(\"pageSize\")` answers 5 — the module's behaviour was changed from outside."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "2"
                ],
                "why": "Two names exposed, so two promises. The module can never again restructure `defaults` without risking a caller like `mine`."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "false"
                ],
                "why": "Not exposed, so not reachable — by any name. The closure still holds `defaults`, and only `get` can read it."
              }
            ],
            "hints": [
              {
                "when": "you predicted 20 for the first",
                "say": "Ask whether `mine` is a copy. Exporting an object hands out a reference to it — the same aliasing as passing an object to a function."
              },
              {
                "when": "you predicted true for the third",
                "say": "`tidy` still has a `defaults` variable inside its function, but the returned object never got a property of that name. Unexported means not a property of what was returned."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Decide what to export, and what ships",
            "prompt": "A `session.js` has four names; pick export or keep private for each. Then two questions about the build.",
            "items": [
              {
                "id": "t1",
                "question": "`SESSION_KEY` — the localStorage key the module uses",
                "choices": [
                  "Export",
                  "Keep private"
                ],
                "accept": [
                  "Keep private"
                ],
                "why": "Exporting it invites callers to read storage directly, and then the key can never change. Export the behaviour, not the data."
              },
              {
                "id": "t2",
                "question": "`isExpired(session)` — used by login, and by a UI component",
                "choices": [
                  "Export",
                  "Keep private"
                ],
                "accept": [
                  "Export"
                ],
                "why": "A caller genuinely needs it, and its implementation could change without that caller changing. Both tests pass, so it is part of the contract."
              },
              {
                "id": "t3",
                "question": "A production stack trace points at `app.min.js:1:48213`. What maps that back to a line in your source? Name it.",
                "accept": [
                  "a source map",
                  "source map",
                  "the source map",
                  "sourcemap",
                  "source maps"
                ],
                "why": "A source map, produced by the build alongside the artifact. It exists because what runs is not what you wrote."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "`hashPassword(pass)` — used only inside login",
                "choices": [
                  "Export",
                  "Keep private"
                ],
                "accept": [
                  "Keep private"
                ],
                "why": "Nobody outside needs it, and exporting it invites misuse of something security-sensitive. Not a convenience — a promise."
              },
              {
                "id": "t5",
                "question": "The artifact was built on Monday. On Tuesday a dependency publishes a new version. Does Monday's artifact change?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "Dependencies are resolved when you build, so the artifact contains the versions you had. What ships is fixed until you build again."
              }
            ],
            "hints": [
              {
                "when": "you exported the key",
                "say": "Apply the one-line test: could you change the storage key tomorrow without breaking a caller? Not once they can read it."
              },
              {
                "when": "you named the debugger or the minifier for the stack trace",
                "say": "The debugger reads the mapping; it does not make it. Ask what file the build writes that records which artifact position came from which source line."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Give the module one face",
            "prompt": "This module works, and exposes five names. Change what it exposes: exactly `formatPrice` and `parsePrice`, with the behaviour identical. Then, in one sentence: which change to the module could you make tomorrow that you could not make before?\n",
            "exportName": "money",
            "filename": "money.js",
            "starter": "const RATES = { usd: 1, eur: 0.92 };\nfunction pad(n) { return String(n).padStart(2, \"0\"); }\nfunction toCents(s) { return Math.round(parseFloat(s) * 100); }\n\nfunction formatPrice(cents, cur = \"usd\") {\n  const v = (cents / 100) * RATES[cur];\n  return Math.floor(v) + \".\" + pad(Math.round((v % 1) * 100));\n}\nfunction parsePrice(s) { return toCents(s); }\n\n// What this module exposes.\nconst money = { formatPrice, parsePrice, RATES, pad, toCents };\n",
            "tests": [
              {
                "label": "formatPrice still works",
                "body": "return subject.formatPrice(1099) === '10.99';"
              },
              {
                "label": "parsePrice still works",
                "body": "return subject.parsePrice('10.99') === 1099;"
              },
              {
                "label": "a currency conversion still applies",
                "body": "return subject.formatPrice(1000, 'eur') === '9.20';"
              },
              {
                "label": "exactly two names are exposed",
                "body": "return Object.keys(subject).length === 2;"
              },
              {
                "label": "the internals are not reachable",
                "body": "return subject.RATES === undefined && subject.pad === undefined &&\n       subject.toCents === undefined;"
              }
            ],
            "hints": [
              {
                "when": "the internals are still reachable",
                "say": "They are still on the exposed object. Leave them declared in the module and simply do not include them — that is the whole mechanism."
              },
              {
                "when": "formatPrice broke after you removed RATES",
                "say": "Removing a name from what is *exposed* is not the same as deleting it. Keep the declarations; only stop exposing them."
              }
            ]
          }
        ]
      }
    },
    "dependencies": {
      "id": "dependencies",
      "title": "Adding and pinning a dependency",
      "kind": "read",
      "outcome": 7,
      "shape": "procedural",
      "why": "Almost every project runs code its authors did not write, and \"it works on my machine\" is most often two machines that installed different versions from the same package.json. Outcome 7 asks for a dependency added and pinned; the lockfile is where a student can check that it was.",
      "concepts": [
        "mod.dependency"
      ],
      "lede": "`package.json` says what you asked for. The lockfile says what you got. This lesson is a handful of npm commands and one file to read — and the habit of reading the second before you trust the first.\n",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What a module promises · Where to put the seam"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Range** — the versions `package.json` will accept, such as `^18.0.12`. **Lockfile** — `package-lock.json`: the exact versions actually installed.",
          "entries": [
            "**Range** — the versions `package.json` will accept, such as `^18.0.12`.",
            "**Lockfile** — `package-lock.json`: the exact versions actually installed."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "body": [
            {
              "p": "Three questions on modules — what an export promises, and what a build step\nmakes. A package you install is somebody else's module, and both ideas come back\nin this lesson."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`money.js` exports only `formatPrice`. You rename its unexported helper `pad`. How many other files must change? Give a number.",
                    "accept": [
                      "0",
                      "none",
                      "zero",
                      "no files",
                      "no other files"
                    ],
                    "miss": "**If you counted the files that call `formatPrice` — they never named `pad`, so they cannot notice it moved.** Only an export can be imported, so only an export can break a caller.\n",
                    "why": "None. Anything unexported is yours to change; the exports are the contract.\n"
                  },
                  {
                    "question": "A new release of a module removes one of its exports. For code that imported it, is that a breaking change?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "miss": "**If you said no because the rest of the module still works — the callers wrote that name into their own code.** Their import now fails.\n",
                    "why": "Yes. Removing or renaming an export breaks every caller that used it — which is exactly what a version number has to warn you about.\n"
                  },
                  {
                    "question": "A build step turns source into what? Name it in a word or two.",
                    "accept": [
                      "an artifact",
                      "artifact",
                      "the artifact",
                      "a build artifact",
                      "build artifact"
                    ],
                    "why": "An artifact — the thing that actually ships and runs, as opposed to the source you wrote. Which versions of other people's code go into it is decided when you install, and that is this lesson.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can add a package with npm and say what it wrote to `package.json`",
                "I can read a version range and say which versions it allows",
                "I can read the lockfile and say which version I actually got",
                "I can install exactly what the lockfile says, and pin a version exactly"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Add a package",
          "body": [
            {
              "p": "Every npm project has a `package.json` at its root. Create one once, then add\nthe package:"
            },
            {
              "code": "npm init -y          # once per project: writes package.json\nnpm install marked   # adds marked, a Markdown parser, as a dependency",
              "lang": "bash"
            },
            {
              "p": "That second command does three things:"
            },
            {
              "list": [
                "**`package.json`** gains a line under `\"dependencies\"`: `\"marked\": \"^18.0.12\"`. That is a **range**, not a version — npm took the newest version and put a `^` in front of it.",
                "**`package-lock.json`** — the **lockfile** — records the exact version it installed, and the exact version of every package *that* package needs.",
                "**`node_modules/`** gets the code: the package and everything it depends on."
              ],
              "ordered": true
            },
            {
              "p": "**You commit `package.json` and `package-lock.json`.** You do not commit\n`node_modules/` — it goes in `.gitignore`, because the two files are enough to\nrebuild it."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which file holds the range you asked for, like `^18.0.12`?",
                    "choices": [
                      "package.json",
                      "package-lock.json"
                    ],
                    "accept": [
                      "package.json"
                    ],
                    "miss": "**If you said `package-lock.json` — the lockfile holds exact versions, with no `^`.** The range lives in the file you would edit by hand.\n",
                    "why": "`package.json`. It records what you asked for, which is a range.\n"
                  },
                  {
                    "question": "Which file records the exact version that was installed?",
                    "choices": [
                      "package.json",
                      "package-lock.json"
                    ],
                    "accept": [
                      "package-lock.json"
                    ],
                    "why": "`package-lock.json`, the lockfile. `package.json` and the lockfile answer different questions: what you will accept, and what you got.\n"
                  },
                  {
                    "question": "Do you commit `node_modules/`?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes so a teammate gets the same code — the lockfile already gives them that,** and far more reliably than a folder of thousands of files.\n",
                    "why": "No. It is rebuilt from `package.json` and the lockfile, so it goes in `.gitignore`.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Read a range",
          "body": [
            {
              "p": "A version number is three numbers, **major.minor.patch**, and each one is a\npromise from the package's author:"
            },
            {
              "list": [
                "**patch** (`18.0.12` → `18.0.13`) — bug fixes only.",
                "**minor** (`18.0.13` → `18.1.0`) — new features; nothing you use is removed.",
                "**major** (`18.1.0` → `19.0.0`) — something may break. An export you import may be gone or renamed."
              ]
            },
            {
              "p": "A range says which of those promises you are willing to take on trust:"
            },
            {
              "table": {
                "head": [
                  "In `package.json`",
                  "Allows",
                  "Of 1.4.9 · 1.5.0 · 2.0.0 it accepts"
                ],
                "rows": [
                  [
                    "`1.4.2`",
                    "that version only",
                    "none of them"
                  ],
                  [
                    "`~1.4.2`",
                    "patch updates",
                    "1.4.9"
                  ],
                  [
                    "`^1.4.2`",
                    "minor and patch updates",
                    "1.4.9 and 1.5.0"
                  ]
                ]
              }
            },
            {
              "p": "**The caret is what `npm install` writes by default**, so unless you say\notherwise, you have agreed to any later version below the next major. One\nexception: below 1.0.0 the caret is stricter — `^0.4.2` allows 0.4.9 but not\n0.5.0, because before 1.0 a minor bump is allowed to break things."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`^2.3.1` — does it allow 2.9.0?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "why": "Yes. A caret allows every minor and patch update below the next major, 3.0.0.\n"
                  },
                  {
                    "question": "`^2.3.1` — does it allow 3.0.0?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes because it is \"newer\" — a major version is where the author is allowed to break you,** and the caret stops just short of it.\n",
                    "why": "No. 3.0.0 is the next major, and the caret excludes it.\n"
                  },
                  {
                    "question": "`~2.3.1` — does it allow 2.4.0?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you read the tilde like the caret — the tilde is narrower.** It allows patch updates only, so the minor number must stay at 3.\n",
                    "why": "No. `~2.3.1` allows 2.3.1 up to, but not including, 2.4.0.\n"
                  },
                  {
                    "question": "`~2.3.1` — does it allow 2.3.8?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "why": "Yes. 2.3.8 is a patch update, which is exactly what the tilde allows.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Read the lockfile",
          "body": [
            {
              "p": "Here is a real project's `package.json`, and the part of its lockfile that\nanswers the question \"which version did I get?\""
            },
            {
              "code": "\"dependencies\": {\n  \"js-yaml\": \"^4.1.0\",\n  \"marked\": \"^18.0.12\"\n}",
              "lang": "json",
              "file": "package.json"
            },
            {
              "code": "\"packages\": {\n  \"\": {\n    \"dependencies\": { \"js-yaml\": \"^4.1.0\", \"marked\": \"^18.0.12\" }\n  },\n  \"node_modules/argparse\": { \"version\": \"2.0.1\" },\n  \"node_modules/js-yaml\": {\n    \"version\": \"4.3.2\",\n    \"dependencies\": { \"argparse\": \"^2.0.1\" }\n  },\n  \"node_modules/marked\": { \"version\": \"18.0.13\" }\n}",
              "lang": "json",
              "file": "package-lock.json"
            },
            {
              "p": "(The real file also carries a download address and a checksum for each\npackage; they are left out here.) To read it:"
            },
            {
              "list": [
                "**Skip the entry named `\"\"`.** That is your own project, and it only repeats the ranges from `package.json`.",
                "**Find `\"node_modules/<name>\"`** and read its **`\"version\"`**. That is the version you actually got.",
                "**An entry you never asked for** is a package one of yours depends on — a *transitive* dependency."
              ]
            },
            {
              "p": "To check from the command line, `npm ls marked` prints the version installed in\n`node_modules/` — after an install, the same one the lockfile names."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`package.json` asks for `^18.0.12`. Which version of marked did this project actually get? Give the version number.",
                    "accept": [
                      "18.0.13"
                    ],
                    "miss": "**If you answered 18.0.12 — that is the bottom of the range, copied from `package.json`.** Read `\"version\"` under `\"node_modules/marked\"`.\n",
                    "why": "18.0.13 — a patch release newer than the one `package.json` names, which the range allowed and the lockfile recorded.\n"
                  },
                  {
                    "question": "Which version of js-yaml did it get? Give the version number.",
                    "accept": [
                      "4.3.2"
                    ],
                    "miss": "**If you answered 4.1.0 — that is the range again, from the `\"\"` entry.** The installed version is under `\"node_modules/js-yaml\"`.\n",
                    "why": "4.3.2 — a minor update the caret allowed.\n"
                  },
                  {
                    "question": "Nobody listed argparse in `package.json`. Why is it in the lockfile?",
                    "choices": [
                      "It was installed by mistake",
                      "js-yaml depends on it"
                    ],
                    "accept": [
                      "js-yaml depends on it"
                    ],
                    "why": "js-yaml depends on it — its own `\"dependencies\"` asks for `^2.0.1`. The lockfile records that version too, which is how it fixes the whole tree and not only the packages you named.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Install exactly what the lockfile says",
          "body": [
            {
              "p": "Take one `package.json` line, `\"js-yaml\": \"^4.1.0\"`, and install it two ways:"
            },
            {
              "table": {
                "head": [
                  "Lockfile",
                  "Command",
                  "js-yaml you get"
                ],
                "rows": [
                  [
                    "committed, says `4.1.0`",
                    "`npm ci`",
                    "**4.1.0**"
                  ],
                  [
                    "not committed",
                    "`npm install`",
                    "**4.3.2** — the newest 4.x on the day you run it"
                  ]
                ]
              }
            },
            {
              "p": "Same `package.json`, two different installs. The lockfile is what makes them\nagree, which is why it is committed."
            },
            {
              "p": "**`npm ci`** is the command that installs exactly the lockfile:"
            },
            {
              "list": [
                "it deletes `node_modules/` and installs every version the lockfile names;",
                "it never changes `package.json` or the lockfile;",
                "it **stops with an error** if there is no lockfile, or if the lockfile and `package.json` disagree."
              ]
            },
            {
              "p": "`npm install` also follows a lockfile that fits `package.json`, but it will\nrewrite the lockfile when they differ. So use `npm install` when you are adding\nor changing a dependency, and `npm ci` when you want exactly what was committed —\na fresh clone, or an automated build."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Lockfile committed at 4.1.0; 4.3.2 is out. A teammate clones and runs `npm ci`. Which version do they get?",
                    "choices": [
                      "4.1.0",
                      "4.3.2"
                    ],
                    "accept": [
                      "4.1.0"
                    ],
                    "miss": "**If you said 4.3.2 because the range allows it — `npm ci` does not look for the newest match.** It installs the version the lockfile names.\n",
                    "why": "4.1.0, the lockfile's version.\n"
                  },
                  {
                    "question": "Same project, but the lockfile was never committed. They run `npm install`. Which version?",
                    "choices": [
                      "4.1.0",
                      "4.3.2"
                    ],
                    "accept": [
                      "4.3.2"
                    ],
                    "why": "4.3.2. With no lockfile, npm resolves the range fresh and takes the newest 4.x — not the version you tested with.\n"
                  },
                  {
                    "question": "You change `package.json` to `^5.0.0` by hand and run `npm ci`. What happens?",
                    "choices": [
                      "It installs 5.x",
                      "It keeps 4.1.0",
                      "It stops with an error"
                    ],
                    "accept": [
                      "It stops with an error"
                    ],
                    "miss": "**If you expected it to install 5.x — `npm ci` never changes the lockfile,** and the lockfile still says 4.1.0, which `^5.0.0` does not allow.\n",
                    "why": "It stops with an error: the two files are out of sync. Run `npm install` to update the lockfile, then commit both.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "step",
          "label": "Pin a version exactly",
          "body": [
            {
              "p": "A committed lockfile fixes every version for anyone who runs `npm ci`. But the\nrange in `package.json` still decides what the *next* resolve picks — the next\n`npm update`, or a lockfile deleted and rebuilt. To make `package.json` itself\nname one version, pin it:"
            },
            {
              "code": "npm install --save-exact marked@18.0.12   # writes \"marked\": \"18.0.12\"\nnpm install -E marked@18.0.12             # the same, short form",
              "lang": "bash"
            },
            {
              "p": "**Watch the trap:** `npm install marked@18.0.12` without the flag installs\n18.0.12 *today* but still writes `\"^18.0.12\"` to `package.json`. Typing a version\nchooses what gets installed; only `--save-exact` stops npm writing a range."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "You run `npm install marked@18.0.12`. What does `package.json` now say for marked?",
                    "choices": [
                      "18.0.12",
                      "^18.0.12"
                    ],
                    "accept": [
                      "^18.0.12"
                    ],
                    "miss": "**If you said `18.0.12` — the `@` picks what to install now, not what gets written.** npm still puts a caret in front.\n",
                    "why": "`\"^18.0.12\"`. The installed version is 18.0.12, but the range allows any later 18.x.\n"
                  },
                  {
                    "question": "Which flag makes it write `18.0.12` with no caret? Type the flag.",
                    "accept": [
                      "--save-exact",
                      "-e",
                      "save-exact",
                      "--save-exact or -e",
                      "-e or --save-exact"
                    ],
                    "why": "`--save-exact`, or `-E`. `package.json` then allows 18.0.12 and nothing else.\n"
                  }
                ]
              }
            }
          ],
          "n": 5
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "`npm install <pkg>` writes a **range** to `package.json` and an **exact version** to `package-lock.json`. They are not the same thing.",
                "`^1.4.2` allows minor and patch updates; `~1.4.2` allows patch only; `1.4.2` allows nothing else.",
                "**Which version did you get?** Read `\"version\"` under `\"node_modules/<name>\"` in the lockfile — not the range.",
                "**Commit the lockfile**, and use `npm ci` to install exactly what it says.",
                "Pin with `--save-exact`; `pkg@1.2.3` alone still saves a caret."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "package.json dependencies",
              "href": "https://docs.npmjs.com/cli/configuring-npm/package-json#dependencies",
              "note": "the range syntax, including what `^` and `~` each allow."
            },
            {
              "title": "package-lock.json",
              "href": "https://docs.npmjs.com/cli/configuring-npm/package-lock-json",
              "note": "what each field of the lockfile records."
            },
            {
              "title": "npm ci",
              "href": "https://docs.npmjs.com/cli/commands/npm-ci",
              "note": "and exactly how it differs from `npm install`."
            },
            {
              "title": "About semantic versioning",
              "href": "https://docs.npmjs.com/about-semantic-versioning",
              "note": "major, minor, patch, and what publishers are promising."
            }
          ]
        }
      ],
      "_source": "lessons/dependencies.md",
      "practice": {
        "id": "dependencies",
        "outcome": 7,
        "steps": [
          {
            "kind": "predict",
            "title": "What a range accepts, and where to look",
            "prompt": "Three questions about what an install committed you to. Pick each answer.",
            "items": [
              {
                "id": "p1",
                "question": "`\"^1.4.2\"` — does it allow 1.5.0?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "The caret allows anything below the next major, so minor and patch releases arrive on the next resolve — code you have not tested."
              },
              {
                "id": "p2",
                "question": "You need to know which version of marked your project actually got. Which file do you open first?",
                "choices": [
                  "package.json",
                  "package-lock.json"
                ],
                "accept": [
                  "package-lock.json"
                ],
                "why": "`package-lock.json`. `package.json` holds the range you asked for; the lockfile holds the exact version that was installed."
              },
              {
                "id": "p3",
                "question": "`\"~1.4.2\"` — does it allow 1.5.0?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "The tilde allows patch releases only: 1.4.2 up to, but not including, 1.5.0."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "`\"^1.4.2\"` — does it allow 2.0.0?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "A major version is where the author may remove or rename an export you import, so the caret stops short of it."
              },
              {
                "id": "p5",
                "question": "`\"^0.4.2\"` — does it allow 0.5.0?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "Below 1.0.0 the caret is stricter: a minor bump there is allowed to break things, so `^0.4.2` stays within 0.4.x."
              },
              {
                "id": "p6",
                "question": "You run `npm install marked@18.0.12`. What does `package.json` now say for marked?",
                "choices": [
                  "18.0.12",
                  "^18.0.12"
                ],
                "accept": [
                  "^18.0.12"
                ],
                "why": "Typing a version picks what gets installed today; npm still writes a range with a caret. Only `--save-exact` (or `-E`) writes `18.0.12`."
              }
            ],
            "hints": [
              {
                "when": "you answered package.json for the second",
                "say": "`package.json` answers \"what will I accept?\" — a range. The question here is \"what did I get?\", and only one file records that."
              },
              {
                "when": "you read the tilde like the caret",
                "say": "The tilde keeps the minor number fixed. `~1.4.2` can move to 1.4.9, never to 1.5.0."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Read the lockfile",
            "prompt": "`package.json` asks for `\"debug\": \"^4.3.4\"` and `\"date-fns\": \"~3.6.0\"`. The lockfile's `\"packages\"` section holds `\"node_modules/debug\"` with `\"version\": \"4.4.1\"`, `\"node_modules/date-fns\"` with `\"version\": \"3.6.0\"`, and `\"node_modules/ms\"` with `\"version\": \"2.1.3\"`. Answer from the lockfile.\n",
            "items": [
              {
                "id": "t1",
                "question": "Which version of debug is installed? Give the version number.",
                "accept": [
                  "4.4.1"
                ],
                "why": "Read `\"version\"` under `\"node_modules/debug\"`. 4.3.4 is only the bottom of the range `package.json` allows."
              },
              {
                "id": "t2",
                "question": "Which version of date-fns is installed? Give the version number.",
                "accept": [
                  "3.6.0"
                ],
                "why": "Here it happens to equal the range's floor — the tilde allowed 3.6.x, and 3.6.0 is what was resolved. Read it; do not assume either way."
              },
              {
                "id": "t3",
                "question": "Nobody listed ms in `package.json`. Why is it in the lockfile?",
                "choices": [
                  "It was installed by mistake",
                  "debug depends on it"
                ],
                "accept": [
                  "debug depends on it"
                ],
                "why": "debug depends on it — its own `\"dependencies\"` asks for `ms` `^2.1.3`. The lockfile fixes that version too, which is how it pins the whole tree and not only what you named."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "A teammate clones the repository and runs `npm ci`. Suppose debug 4.9.0 is out. Which debug do they get?",
                "choices": [
                  "4.4.1",
                  "4.9.0"
                ],
                "accept": [
                  "4.4.1"
                ],
                "why": "`npm ci` installs exactly the lockfile's versions. The range would allow 4.9.0; `npm ci` does not look for it."
              },
              {
                "id": "t5",
                "question": "The lockfile was never committed, and debug 4.9.0 is out. The teammate runs `npm install`. Which debug?",
                "choices": [
                  "4.4.1",
                  "4.9.0"
                ],
                "accept": [
                  "4.9.0"
                ],
                "why": "With no lockfile, npm resolves the range fresh and takes the newest 4.x — not the version you tested with."
              },
              {
                "id": "t6",
                "question": "You edit `package.json` to `\"debug\": \"^5.0.0\"` and run `npm ci`. What happens?",
                "choices": [
                  "It installs debug 5.x",
                  "It keeps 4.4.1",
                  "It stops with an error"
                ],
                "accept": [
                  "It stops with an error"
                ],
                "why": "The lockfile still says 4.4.1, which `^5.0.0` does not allow, and `npm ci` never rewrites the lockfile — it stops with an error. `npm install` updates the lockfile; then commit both."
              }
            ],
            "hints": [
              {
                "when": "you answered 4.3.4 for debug",
                "say": "That number is the range from package.json. Find `\"node_modules/debug\"` in the lockfile and read its `\"version\"`."
              },
              {
                "when": "you said ms was installed by mistake",
                "say": "Packages have dependencies of their own. The lockfile lists every package in `node_modules/`, not only the ones you named."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Pin it, and let a teammate get the same",
            "prompt": "Start a project, add marked pinned to exactly 18.0.12, commit what a teammate needs, and on their machine install exactly what you tested. Pick the commands in order; two do not belong.\n",
            "lines": [
              "npm init -y",
              "npm install --save-exact marked@18.0.12",
              "git add package.json package-lock.json",
              "git commit -m \"Add marked 18.0.12, pinned\"",
              "git push",
              "git clone https://github.com/you/notes.git && cd notes",
              "npm ci"
            ],
            "distractors": [
              {
                "line": "npm install marked@18.0.12",
                "why": "This installs 18.0.12 today but writes `\"^18.0.12\"` to package.json — a range, not a pin. The next fresh resolve can move you to any 18.x."
              },
              {
                "line": "git add node_modules package.json package-lock.json",
                "why": "`node_modules/` belongs in `.gitignore`. The two files are enough to rebuild it exactly, and committing thousands of installed files adds nothing the lockfile does not already fix."
              }
            ],
            "why": "`package.json` must exist before a dependency can be written to it, the two files must be committed and pushed before anyone else can have them, and the teammate's `npm ci` installs exactly the lockfile — the version you tested, not the newest one the range allows.\n",
            "hints": [
              {
                "when": "you used plain `npm install marked@18.0.12`",
                "say": "Look at what that writes to package.json, not what it installs. The task asked for a pin."
              },
              {
                "when": "the teammate ran npm install instead of npm ci",
                "say": "There is no `npm install` among the lines — and on a fresh clone, the command that installs exactly what was committed is `npm ci`."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a setup script",
            "prompt": "Two teammates get different versions of the same package. An AI assistant proposed this script \"to fix the version mismatch for good\". Answer by line number, or with the command, where asked.\n",
            "filename": "fix-deps.sh",
            "code": "# fix-deps.sh — run whenever versions drift\nrm -rf node_modules\nrm package-lock.json\nnpm install\ngit add node_modules package.json package-lock.json\ngit commit -m \"Sync dependencies\"\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line throws away the record of which versions were tested?",
                "accept": [
                  "3",
                  "line 3"
                ],
                "why": "Deleting the lockfile makes line 4 resolve every range fresh, taking the newest allowed version of every package. Each run of this script can produce a *different* set of versions — the opposite of a fix."
              },
              {
                "id": "c2",
                "question": "Which line commits something that belongs in `.gitignore`?",
                "accept": [
                  "5",
                  "line 5"
                ],
                "why": "`node_modules/` is rebuilt from package.json and the lockfile, so committing it adds thousands of files and nothing the lockfile does not already fix."
              },
              {
                "id": "c3",
                "question": "Which single command should a teammate run instead of lines 2–4, to get exactly the committed versions? Type the command.",
                "accept": [
                  "npm ci"
                ],
                "why": "`npm ci` deletes `node_modules/`, installs exactly what the lockfile names, and never changes either file. The mismatch came from someone not using it — or from a lockfile that was never committed."
              }
            ],
            "hints": [
              {
                "when": "you named line 2 for the first",
                "say": "Deleting `node_modules/` is harmless — it is rebuilt from the two files. Which line deletes one of those two files?"
              },
              {
                "when": "you answered npm install for the command",
                "say": "`npm install` follows a lockfile that fits package.json, but it may rewrite it. Which command promises never to change it?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Asked for, and got",
            "prompt": "`versionReport(pkg, lock, name)` takes the parsed `package.json` and `package-lock.json` and reports both answers for one package: the range you asked for, and the version you got. `asked` is written; fill the gap for `got`. Then, in one sentence: why can `asked` and `got` differ with nothing wrong?\n",
            "exportName": "versionReport",
            "filename": "version-report.js",
            "starter": "// pkg:  { dependencies: { marked: \"^18.0.12\", ... } }\n// lock: { packages: { \"\": {...}, \"node_modules/marked\": { version: \"18.0.13\" }, ... } }\nfunction versionReport(pkg, lock, name) {\n  return {\n    asked: pkg.dependencies[name],       // the range, or undefined if you never asked\n    got: undefined,                      // ___ gap: the version the lockfile says was installed\n  };\n}\n",
            "tests": [
              {
                "label": "marked: asked ^18.0.12, got 18.0.13",
                "body": "const pkg = { dependencies: { marked: '^18.0.12', 'js-yaml': '^4.1.0' } }; const lock = { packages: { '': { dependencies: pkg.dependencies },\n  'node_modules/marked': { version: '18.0.13' },\n  'node_modules/js-yaml': { version: '4.3.2', dependencies: { argparse: '^2.0.1' } },\n  'node_modules/argparse': { version: '2.0.1' } } };\nconst r = subject(pkg, lock, 'marked'); return r.asked === '^18.0.12' && r.got === '18.0.13';"
              },
              {
                "label": "js-yaml: got the lockfile's version, not the range",
                "body": "const pkg = { dependencies: { marked: '^18.0.12', 'js-yaml': '^4.1.0' } }; const lock = { packages: { '': { dependencies: pkg.dependencies },\n  'node_modules/marked': { version: '18.0.13' },\n  'node_modules/js-yaml': { version: '4.3.2', dependencies: { argparse: '^2.0.1' } },\n  'node_modules/argparse': { version: '2.0.1' } } };\nreturn subject(pkg, lock, 'js-yaml').got === '4.3.2';"
              },
              {
                "label": "a transitive dependency: never asked for, still got",
                "body": "const pkg = { dependencies: { 'js-yaml': '^4.1.0' } }; const lock = { packages: { '': { dependencies: pkg.dependencies },\n  'node_modules/js-yaml': { version: '4.3.2' },\n  'node_modules/argparse': { version: '2.0.1' } } };\nconst r = subject(pkg, lock, 'argparse'); return r.asked === undefined && r.got === '2.0.1';"
              },
              {
                "label": "a package in neither file: got is undefined, not a crash",
                "body": "const pkg = { dependencies: { 'js-yaml': '^4.1.0' } }; const lock = { packages: { '': { dependencies: pkg.dependencies },\n  'node_modules/js-yaml': { version: '4.3.2' } } };\nreturn subject(pkg, lock, 'chalk').got === undefined;"
              }
            ],
            "hints": [
              {
                "when": "got comes back as \"^18.0.12\"",
                "say": "You read the `\"\"` entry, which is your own project repeating the ranges from package.json. The installed version is under a different key."
              },
              {
                "when": "got is always undefined",
                "say": "The keys in `lock.packages` are not bare package names. Look at the comment above the function: each one is a path."
              },
              {
                "when": "only the last test fails, with an error",
                "say": "For a package that was never installed, there is no entry — so reading `.version` from it throws. Handle the absent entry before you read from it."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Teach the range checker the tilde",
            "prompt": "`allows(range, version)` answers whether a package.json range accepts a version. It works for an exact version and for the caret. Change it to handle the tilde as well: `~1.4.2` accepts 1.4.2 up to, but not including, 1.5.0. The caret and exact cases must keep working. Then, in one sentence: what does the tilde refuse that the caret accepts?\n",
            "exportName": "allows",
            "filename": "allows.js",
            "starter": "// Ranges from 0.1.0 upward. version is always a plain \"major.minor.patch\".\nfunction allows(range, version) {\n  const [maj, min, pat] = version.split(\".\").map(Number);\n\n  if (range.startsWith(\"^\")) {\n    const [rMaj, rMin, rPat] = range.slice(1).split(\".\").map(Number);\n    if (rMaj === 0) return maj === 0 && min === rMin && pat >= rPat;\n    return maj === rMaj && (min > rMin || (min === rMin && pat >= rPat));\n  }\n\n  return range === version;\n}\n",
            "tests": [
              {
                "label": "~2.3.1 accepts 2.3.8",
                "body": "return subject('~2.3.1', '2.3.8') === true;"
              },
              {
                "label": "~2.3.1 refuses 2.4.0",
                "body": "return subject('~2.3.1', '2.4.0') === false;"
              },
              {
                "label": "~2.3.1 refuses 2.3.0, below the range",
                "body": "return subject('~2.3.1', '2.3.0') === false;"
              },
              {
                "label": "~1.4.2 accepts 1.4.10",
                "body": "return subject('~1.4.2', '1.4.10') === true;"
              },
              {
                "label": "~0.4.2 accepts 0.4.9 and refuses 0.5.0",
                "body": "return subject('~0.4.2', '0.4.9') === true && subject('~0.4.2', '0.5.0') === false;"
              },
              {
                "label": "the caret still works",
                "body": "return subject('^2.3.1', '2.9.0') === true && subject('^2.3.1', '3.0.0') === false && subject('^0.4.2', '0.5.0') === false;"
              },
              {
                "label": "an exact version still accepts only itself",
                "body": "return subject('1.4.2', '1.4.2') === true && subject('1.4.2', '1.4.3') === false;"
              }
            ],
            "hints": [
              {
                "when": "~2.3.1 accepts 2.4.0",
                "say": "You treated the tilde like the caret. The caret lets the minor number move; the tilde must keep it equal to the range's."
              },
              {
                "when": "~1.4.2 refuses 1.4.10",
                "say": "Compare the numbers as numbers. As strings, `\"10\" >= \"2\"` is false, because strings compare character by character."
              },
              {
                "when": "~2.3.1 accepts 2.3.0",
                "say": "The range has a floor as well as a ceiling. The patch number must be at least the range's patch."
              }
            ]
          }
        ]
      }
    },
    "reading-docs": {
      "id": "reading-docs",
      "title": "Answering it from the documentation",
      "kind": "read",
      "outcome": 10,
      "shape": "procedural",
      "why": "Outcome 10 names official documentation as a diagnostic tool alongside the debugger and runtime errors, and nothing else in the course teaches it (D-024 cut it from what-js-is; D-025 item 1). It sits with dependencies because a package arrives with its own documentation and a version, and the lockfile is how a student knows which version's documentation to read.",
      "concepts": [
        "diag.documentation"
      ],
      "lede": "Every question about a function you did not write — what it returns, what it throws, whether it exists where your code runs — has an answer written down by the people who maintain it. This lesson is where to find that page, the order to read it in, and how to check it describes the version you actually have.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "What JavaScript is · Adding and pinning a dependency"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Primary documentation** — written and maintained by the people who make the thing: the language's reference, the host's, the package's own. **Reference page** — one page per function or method, always in the same order of sections.",
          "entries": [
            "**Primary documentation** — written and maintained by the people who make the thing: the language's reference, the host's, the package's own.",
            "**Reference page** — one page per function or method, always in the same order of sections."
          ]
        }
      ],
      "body": [
        {
          "beat": "recall",
          "body": [
            {
              "p": "Three questions from earlier lessons — on where a name comes from, on the\nlockfile, and on reading an error. All three come back in this lesson as\nquestions you answer from a page instead of from memory."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`setTimeout` — the language, or the host?",
                    "choices": [
                      "The language",
                      "The host"
                    ],
                    "accept": [
                      "The host"
                    ],
                    "miss": "**If you said the language because every tutorial uses it — the host supplies it,** which is why a browser and Node can each do it their own way. You will see one of those differences in writing below.\n",
                    "why": "The host. The language has no timers; each host hands your code its own `setTimeout`.\n"
                  },
                  {
                    "question": "`package.json` says `\"js-yaml\": \"^4.1.0\"`. The lockfile has `\"node_modules/js-yaml\": { \"version\": \"4.3.2\" }`. Which version do you have?",
                    "choices": [
                      "4.1.0",
                      "4.3.2"
                    ],
                    "accept": [
                      "4.3.2"
                    ],
                    "miss": "**If you said 4.1.0 — that is the bottom of the range you asked for.** The lockfile's `\"version\"` is what was installed.\n",
                    "why": "4.3.2. Hold on to the habit: the version you have is a fact you look up, not assume, and the last step of this lesson needs it.\n"
                  },
                  {
                    "question": "Node stops at `sales.findLast(isRefund)` with `sales.findLast is not a function`. What type of error is that?",
                    "choices": [
                      "ReferenceError",
                      "TypeError",
                      "SyntaxError"
                    ],
                    "accept": [
                      "TypeError"
                    ],
                    "miss": "**If you said ReferenceError — `sales` is a name that exists.** Asking it for `findLast` gave `undefined`, and calling `undefined` is using a value as the wrong kind of thing.\n",
                    "why": "A **TypeError**. `sales` is a real array, yet it has no `findLast`. Why an array would lack a standard method is a question the documentation answers — step 4.\n"
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can find the answer in the official documentation before searching anywhere else",
                "I can say whether a question belongs to the language's documentation, the host's, or a package's",
                "I can read a reference page for what a function takes, returns and throws, and which parameters I can leave out",
                "I can check that the documentation I am reading describes the version I actually have"
              ]
            }
          ]
        },
        {
          "beat": "step",
          "label": "Decide whose documentation it is",
          "body": [
            {
              "p": "A search engine gives you forum answers, blog posts and snippets first. Each\none was written for somebody else's code — often another host, often an older\nversion, sometimes another language. The documentation is written by the people\nwho maintain the thing, and it is updated when the thing changes. So you open it\n**first**, and only search further when it has not answered you."
            },
            {
              "p": "There is no single \"the documentation\". Before you look anything up, decide\n**whose** name it is — the same question you ask of any name you did not\ndefine:"
            },
            {
              "table": {
                "head": [
                  "The name comes from",
                  "Open",
                  "Examples"
                ],
                "rows": [
                  [
                    "**The language**",
                    "MDN's JavaScript reference",
                    "`Array.prototype.find`, `JSON.parse`, `String.prototype.padStart`"
                  ],
                  [
                    "**The host — a browser**",
                    "MDN's Web APIs section",
                    "`document.querySelector`, `localStorage`"
                  ],
                  [
                    "**The host — Node**",
                    "Node's own documentation, nodejs.org/api",
                    "`fs.readFile`, `process`"
                  ],
                  [
                    "**A package**",
                    "The package's README and API docs, on its npm page or repository",
                    "`marked.parse`"
                  ]
                ]
              }
            },
            {
              "p": "(The language is defined by a specification written for the people who build\nengines. MDN's JavaScript reference is written from it for the people who use\nthe language, and it is what you read.)"
            },
            {
              "p": "The choice matters most when two hosts share a name. `setTimeout` exists in\nboth, and each host documents its own:"
            },
            {
              "table": {
                "head": [
                  "",
                  "MDN — the browser's `setTimeout`",
                  "Node's `setTimeout`"
                ],
                "rows": [
                  [
                    "Returns",
                    "\"A positive integer … that uniquely identifies the timer\"",
                    "a `Timeout` object"
                  ],
                  [
                    "`delay` left out",
                    "\"Defaults to 0\"",
                    "\"**Default:** `1`\""
                  ]
                ]
              }
            },
            {
              "p": "Both pages are correct. A forum answer that says \"`setTimeout` returns a number\"\nis correct too — for a browser. If your code runs in Node, only one of these\npages is about your code."
            },
            {
              "p": "(AI assistants fail in these ways and some of their own: they can describe a\nfunction that does not exist, with complete confidence. A later lesson is about\nexactly that. The documentation is how you check them too.)"
            },
            {
              "p": "For each name, pick whose documentation answers it: **language**, **browser**,\n**node** or **package**."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "code": "`Array.prototype.find`",
                    "choices": [
                      "Language",
                      "Browser",
                      "Node",
                      "Package"
                    ],
                    "accept": [
                      "Language"
                    ],
                    "miss": "**If you said browser because you have used it in a browser — it works in every host.** Anything that works everywhere is the language's.\n",
                    "why": "The language — MDN's JavaScript reference.\n"
                  },
                  {
                    "code": "`fs.readFile`",
                    "choices": [
                      "Language",
                      "Browser",
                      "Node",
                      "Package"
                    ],
                    "accept": [
                      "Node"
                    ],
                    "why": "Node. Files belong to the host, and only Node's documentation describes Node's `fs`.\n"
                  },
                  {
                    "code": "`document.querySelector`",
                    "choices": [
                      "Language",
                      "Browser",
                      "Node",
                      "Package"
                    ],
                    "accept": [
                      "Browser"
                    ],
                    "why": "The browser — MDN's Web APIs section. There is no `document` in Node at all.\n"
                  },
                  {
                    "question": "`marked.parse`, from a package you installed with npm",
                    "choices": [
                      "Language",
                      "Browser",
                      "Node",
                      "Package"
                    ],
                    "accept": [
                      "Package"
                    ],
                    "miss": "**If you said language or node — neither has ever heard of it.** Somebody else's module is documented by somebody else: the people who publish it.\n",
                    "why": "The package. Its README and API docs are the primary source; MDN and Node say nothing about it.\n"
                  },
                  {
                    "question": "Your program runs in Node. According to the documentation that is primary for your code, what does `setTimeout` return?",
                    "choices": [
                      "A number",
                      "A Timeout object"
                    ],
                    "accept": [
                      "A Timeout object"
                    ],
                    "miss": "**If you said a number — that is the browser's page.** It is correct, and it is not about your program.\n",
                    "why": "A `Timeout` object. Node's documentation is primary for Node; MDN's `setTimeout` page documents the browser's.\n"
                  }
                ]
              }
            }
          ],
          "n": 1
        },
        {
          "beat": "step",
          "label": "Read a reference page in order",
          "body": [
            {
              "p": "Every page in MDN's reference is laid out the same way, so you never read one\ntop to bottom. You go to the section that answers your question. Here are the\nparts of the page for `Array.prototype.find()`, in the order they appear:"
            },
            {
              "list": [
                "**A one-paragraph summary**, and a **Try it** box you can run.",
                "**Syntax** — every way to call it:  ```js find(callbackFn) find(callbackFn, thisArg) ```  - **Parameters** — each one, what it is for, and whether it is optional. - **Return value** — for `find()`: \"The first element in the array that   satisfies the provided testing function. Otherwise, `undefined` is   returned.\" - **Exceptions** — what it throws, and when. A page leaves this out when   there is nothing to say.",
                "**Description** — the full behaviour, edge cases included.",
                "**Examples** — working code, each with a heading saying what it shows.",
                "**Specifications**, then **Browser compatibility** — a table of which version of each browser, and of Node.js, first had it."
              ],
              "ordered": true
            },
            {
              "p": "The order to read in, for \"what does this do with my values?\":"
            },
            {
              "list": [
                "**Syntax**, to see how it is called.",
                "**Parameters**, to see what each argument must be.",
                "**Return value** — what the call returns, *including* when things do not go well. Most bugs live in that \"otherwise\".",
                "**Exceptions**, if there is the section — whatever can stop your program.",
                "**Examples**, only if you are still unsure. They show the common case, not yours."
              ],
              "ordered": true
            },
            {
              "p": "A second page, the same order. `Array.prototype.reduce()` has an Exceptions\nsection:"
            },
            {
              "p": "**Exceptions** — `TypeError`: Thrown if the array contains no elements and\n`initialValue` is not provided."
            },
            {
              "p": "Answer from the page, not from memory."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "Which section of the `find()` page says what you get when nothing matches?",
                    "choices": [
                      "Parameters",
                      "Return value",
                      "Exceptions",
                      "Examples",
                      "Browser compatibility"
                    ],
                    "accept": [
                      "Return value"
                    ],
                    "miss": "**If you said Examples — they might show it, or might not.** The section that must say it is the one about what comes back.\n",
                    "why": "**Return value.** It always covers the \"otherwise\" — here, `undefined`.\n"
                  },
                  {
                    "question": "So what does `[3, 8].find(n => n > 10)` return? Type the value.",
                    "accept": [
                      "undefined"
                    ],
                    "miss": "**If you said `-1` — that is `findIndex`, a different method with a different page.** Read `find()`'s own Return value.\n",
                    "why": "`undefined`. No element passed the test, and the page says what happens then.\n"
                  },
                  {
                    "question": "`[].reduce((sum, n) => sum + n)` — what does it throw? Name the error.",
                    "accept": [
                      "typeerror",
                      "a typeerror",
                      "type error"
                    ],
                    "why": "A **TypeError** — the array is empty and no `initialValue` was given, which is the exact case the Exceptions section names.\n"
                  },
                  {
                    "question": "Which section tells you whether `find()` works in the version of Node you have?",
                    "choices": [
                      "Parameters",
                      "Return value",
                      "Exceptions",
                      "Examples",
                      "Browser compatibility"
                    ],
                    "accept": [
                      "Browser compatibility"
                    ],
                    "why": "**Browser compatibility.** Despite the name, its table includes Node.js too. Step 4 reads it.\n"
                  }
                ]
              }
            }
          ],
          "n": 2
        },
        {
          "beat": "step",
          "label": "Read the optional parameters",
          "body": [
            {
              "p": "A parameter you can leave out is marked, and the page says what is used\ninstead. Two sites mark it two ways."
            },
            {
              "p": "**MDN lists each way to call it**, and labels the parameter **Optional**. From\n`String.prototype.padStart()`:"
            },
            {
              "code": "padStart(targetLength)\npadStart(targetLength, padString)",
              "lang": "js"
            },
            {
              "p": "`padString` **Optional** — The string to pad the current `str` with. … The\ndefault value is the space character (U+0020)."
            },
            {
              "p": "**Node writes one line with square brackets** around what you can leave out,\nand gives the default on its own line. From Node's `fs` and timers pages:"
            },
            {
              "code": "fs.readFile(path[, options], callback)\nsetTimeout(callback[, delay[, ...args]])",
              "lang": "text"
            },
            {
              "p": "`delay` — The number of milliseconds to wait before calling the `callback`.\n**Default:** `1`."
            },
            {
              "p": "Brackets inside brackets mean *in that order*: you can give `callback` alone,\nor `callback` and `delay`, but you cannot give `...args` without a `delay`\nbefore them."
            },
            {
              "p": "Leaving out an optional parameter does not mean \"nothing happens\". It means the\ndefault happens, and the page tells you which. `reduce()`'s `initialValue` is\noptional, and its Parameters section says what the first call gets without it:"
            },
            {
              "p": "`accumulator` — … On the first call, its value is `initialValue` if the\nlatter is specified; otherwise its value is `array[0]`."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`\"7\".padStart(3, \"0\")` returns what? Type the value.",
                    "accept": [
                      "007",
                      "\"007\"",
                      "'007'"
                    ],
                    "why": "`\"007\"` — padded at the start with `\"0\"` until it is 3 characters long.\n"
                  },
                  {
                    "question": "`fs.readFile(path[, options], callback)` — which parameter can you leave out?",
                    "choices": [
                      "path",
                      "options",
                      "callback"
                    ],
                    "accept": [
                      "options"
                    ],
                    "miss": "**If you said `callback` — it sits outside the brackets,** so it is required.\n",
                    "why": "`options`. Only what is inside the square brackets is optional.\n"
                  },
                  {
                    "question": "`[10, 20, 30].reduce((sum, n) => sum + n)` has no `initialValue`. On the first call, what is `sum`? Type the value.",
                    "accept": [
                      "10",
                      "array[0]",
                      "the first element"
                    ],
                    "miss": "**If you said 0 — nothing in the call says 0.** The page says what a missing `initialValue` is replaced with: the first element.\n",
                    "why": "`10` — `array[0]`. The total still comes out right here, but an array of objects would start its sum *with an object*. The practice has one.\n"
                  }
                ]
              }
            }
          ],
          "n": 3
        },
        {
          "beat": "step",
          "label": "Check the version the page describes",
          "body": [
            {
              "p": "A reference page describes the **newest** version of the thing. You are\nrunning a particular one. So the last question is always: *is what I read true\nof what I have?*"
            },
            {
              "p": "**For the language, and for the browser's APIs**, read MDN's **Browser\ncompatibility** table. It has a column for each browser and one for **Node.js**,\nand each cell is the first version that has the feature. For\n`Array.prototype.findLast()` the Node.js cell says **18.0.0**."
            },
            {
              "p": "**For Node's own APIs**, every function on nodejs.org carries an **Added in**\nline under its heading — `setTimeout`'s says `Added in: v0.0.1` — and a\n**History** table when its behaviour changed since."
            },
            {
              "p": "To find what you have, ask Node:"
            },
            {
              "code": "node --version    # prints something like v20.19.0",
              "lang": "bash"
            },
            {
              "p": "**For a package**, the README on its npm page is the one published with its\nnewest release. The version you have is in your lockfile. If the two differ by\na major version, read the documentation for yours — npm's **Versions** tab lists\nevery release, and most repositories tag each release with its version number."
            },
            {
              "ask": {
                "label": null,
                "items": [
                  {
                    "question": "`node --version` prints `v16.20.2`. MDN's table says `findLast` arrived in Node.js 18.0.0. Does `sales.findLast` exist?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "miss": "**If you said yes because it is on MDN — MDN documents the newest language.** The table says which versions have it, and 16 is older than 18.\n",
                    "why": "No — and that is the recall's `sales.findLast is not a function`. Nothing is wrong with the array; the host is older than the method.\n"
                  },
                  {
                    "question": "The table says `Object.groupBy` arrived in Node.js 21.0.0. You run v20.19.0. Will it work?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "No"
                    ],
                    "why": "No. v20 is older than 21.0.0. Upgrade Node, or use something older that does the same job.\n"
                  },
                  {
                    "question": "Node's docs say `structuredClone` — `Added in: v17.0.0`. You run v20.19.0. Will it work?",
                    "choices": [
                      "Yes",
                      "No"
                    ],
                    "accept": [
                      "Yes"
                    ],
                    "why": "Yes. 20 is later than 17, so it has been there since before your version.\n"
                  },
                  {
                    "question": "Your lockfile says marked `4.3.0`. The README on npm is for 18.0.13. Which version's documentation should you read?",
                    "choices": [
                      "4.3.0",
                      "18.0.13"
                    ],
                    "accept": [
                      "4.3.0"
                    ],
                    "miss": "**If you said 18.0.13 — that describes code you do not have.** Across major versions, functions and options can be renamed or removed.\n",
                    "why": "4.3.0 — the version the lockfile says you got.\n"
                  }
                ]
              }
            }
          ],
          "n": 4
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "**Documentation first.** A forum answer was written for someone else's host, version or language; the primary documentation is written for the thing itself.",
                "**Whose name is it?** The language → MDN's JavaScript reference. The browser → MDN's Web APIs. Node → nodejs.org/api. A package → its own README and docs.",
                "**Read in order:** Syntax, Parameters (which are *Optional*, or in `[brackets]`, and their defaults), Return value — including the \"otherwise\" — then Exceptions. Examples last.",
                "**Check the version:** MDN's compatibility table, Node's *Added in*, or the lockfile for a package — against what you actually run."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "JavaScript reference",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference",
              "note": "The language. Start here for anything that works in every host."
            },
            {
              "title": "Web APIs",
              "href": "https://developer.mozilla.org/en-US/docs/Web/API",
              "note": "What the browser adds. Reference, not reading."
            },
            {
              "title": "Node.js documentation",
              "href": "https://nodejs.org/api/",
              "note": "What Node adds, with *Added in* and *History* on every function."
            },
            {
              "title": "Array.prototype.reduce()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/reduce",
              "note": "A full reference page, Exceptions section included. The practice uses it."
            }
          ]
        }
      ],
      "_source": "lessons/reading-docs.md",
      "practice": {
        "id": "reading-docs",
        "outcome": 10,
        "steps": [
          {
            "kind": "predict",
            "title": "Whose page, and what it says",
            "prompt": "Three questions. For the first, pick language, browser, node or package. For the other two, answer from the documentation quoted in the question.\n",
            "items": [
              {
                "id": "p1",
                "question": "`fs.readFile` — whose documentation answers questions about it?",
                "choices": [
                  "Language",
                  "Browser",
                  "Node",
                  "Package"
                ],
                "accept": [
                  "Node"
                ],
                "why": "Node. Files belong to the host, and `fs` is Node's — no browser has it, and the language has no files at all."
              },
              {
                "id": "p2",
                "question": "MDN, `find()`, Return value: \"The first element in the array that satisfies the provided testing function. Otherwise, undefined is returned.\" What does `[3, 8].find(n => n > 10)` return? Type the value.",
                "accept": [
                  "undefined"
                ],
                "why": "Nothing is greater than 10, so the \"otherwise\" applies: `undefined`. The Return value section always says what happens when things do not go well."
              },
              {
                "id": "p3",
                "question": "Node: `fs.readFile(path[, options], callback)`. Which parameter can you leave out?",
                "choices": [
                  "path",
                  "options",
                  "callback"
                ],
                "accept": [
                  "options"
                ],
                "why": "Only what sits inside the square brackets is optional. `callback` is outside them, so it is required."
              }
            ],
            "extra": [
              {
                "id": "p4",
                "question": "`Array.prototype.includes` — whose documentation answers questions about it?",
                "choices": [
                  "Language",
                  "Browser",
                  "Node",
                  "Package"
                ],
                "accept": [
                  "Language"
                ],
                "why": "The language. It works in every host, so MDN's JavaScript reference documents it."
              },
              {
                "id": "p5",
                "question": "MDN: `padStart(targetLength, padString)`, `padString` Optional — \"The default value is the space character\". What does `\"42\".padStart(5, \"0\")` return? Type the value.",
                "accept": [
                  "00042",
                  "\"00042\"",
                  "'00042'"
                ],
                "why": "`\"00042\"` — padded at the start with `\"0\"` until it is 5 characters long."
              },
              {
                "id": "p6",
                "question": "Node: `setTimeout(callback[, delay[, ...args]])`, and for `delay`, \"Default: 1\". In Node, `setTimeout(save)` waits how many milliseconds? Give a number.",
                "accept": [
                  "1",
                  "1ms",
                  "1 ms",
                  "one"
                ],
                "why": "1. Leaving out an optional parameter does not mean nothing happens — it means the default happens. (The browser's own page says 0 — the other host's default.)"
              }
            ],
            "hints": [
              {
                "when": "you answered browser or language for fs.readFile",
                "say": "Ask whether it would work in every host. Reading a file is something a browser page is not allowed to do, and the language has no files."
              },
              {
                "when": "you answered -1 for find",
                "say": "That is `findIndex`, a different method. Read the Return value quoted in the question — its last sentence."
              },
              {
                "when": "you answered callback for readFile",
                "say": "Look for the square brackets. What they surround is what you may leave out."
              }
            ]
          },
          {
            "kind": "run",
            "title": "Read it, predict it, run it",
            "prompt": "Three lines, each answerable from a page you have already read. From MDN's `includes()`: \"`fromIndex` Optional — Zero-based index at which to start searching.\" Predict each printed line, then run it and see whether the documentation was right.\n",
            "filename": "from-the-docs.js",
            "code": "console.log([5, 12, 8].find(n => n > 100));\nconsole.log(\"7\".padStart(3, \"0\"));\nconsole.log([1, 2, 3].includes(1, 1));\n",
            "items": [
              {
                "id": "r1",
                "question": "First line printed",
                "accept": [
                  "undefined"
                ],
                "miss": "**If you answered `-1` or `null` — neither is on `find()`'s page.** Its Return value says what comes back when nothing matches.\n",
                "why": "`undefined`. Nothing is over 100."
              },
              {
                "id": "r2",
                "question": "Second line printed",
                "accept": [
                  "007",
                  "\"007\""
                ],
                "why": "`007`. `padString` is given, so it pads with `\"0\"` rather than the default space."
              },
              {
                "id": "r3",
                "question": "Third line printed",
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` because the array holds a 1 — read what the second argument means.** The search starts at index 1, and the 1 is at index 0.\n",
                "why": "`false`. `fromIndex` is 1, so the search starts at `2` and never sees the `1`. An optional parameter you did not mean to pass changes the answer silently."
              }
            ],
            "hints": [
              {
                "when": "you answered true for the third",
                "say": "The second argument is not a second value to look for. The quoted Parameters line says what it is."
              },
              {
                "when": "you answered \"  7\" or \"7\" for the second",
                "say": "The call passes a second argument. The default space is only used when you leave it out."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Answer from one reference page",
            "prompt": "From MDN's page for `Array.prototype.reduce()`. **Syntax:** `reduce(callbackFn)` and `reduce(callbackFn, initialValue)`. **Parameters:** `accumulator` — \"On the first call, its value is `initialValue` if the latter is specified; otherwise its value is `array[0]`.\" `currentValue` — \"On the first call, its value is `array[0]` if `initialValue` is specified; otherwise its value is `array[1]`.\" `initialValue` **Optional**. **Exceptions:** `TypeError` — \"Thrown if the array contains no elements and `initialValue` is not provided.\" **Edge cases:** \"if `initialValue` is provided but the array is empty, the solo value will be returned without calling `callbackFn`.\" Answer from the page.\n",
            "items": [
              {
                "id": "t1",
                "question": "Which parameter can you leave out?",
                "choices": [
                  "callbackFn",
                  "initialValue"
                ],
                "accept": [
                  "initialValue"
                ],
                "why": "`initialValue` — marked Optional, and it is the second of the two ways to call it in Syntax."
              },
              {
                "id": "t2",
                "question": "`[].reduce((sum, n) => sum + n)` — what does it return? Type the value, or error.",
                "accept": [
                  "typeerror",
                  "a typeerror",
                  "type error",
                  "it throws a typeerror",
                  "throws a typeerror",
                  "throws",
                  "it throws",
                  "error"
                ],
                "why": "It throws a TypeError: the array is empty and there is no `initialValue` — the exact case the Exceptions section names."
              },
              {
                "id": "t3",
                "question": "`[].reduce((sum, n) => sum + n, 0)` — what does it return? Type the value.",
                "accept": [
                  "0",
                  "zero"
                ],
                "why": "`0`. With an `initialValue` and an empty array, the edge case says it is returned without calling the function. One argument turns a crash into the right answer."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "question": "`[10, 20].reduce((sum, n) => sum + n)` — on the first call, what is `n`? Type the value.",
                "accept": [
                  "20"
                ],
                "why": "No `initialValue`, so `sum` starts as `array[0]` (10) and `n` as `array[1]` (20). The function runs once, not twice."
              },
              {
                "id": "t5",
                "question": "`[10, 20].reduce((sum, n) => sum + n, 0)` — on the first call, what is `sum`? Type the value.",
                "accept": [
                  "0",
                  "zero"
                ],
                "why": "With an `initialValue`, `sum` starts as it, and `n` starts at `array[0]`."
              },
              {
                "id": "t6",
                "question": "`[{ kg: 2 }, { kg: 3 }].reduce((sum, box) => sum + box.kg)` — on the first call, what is `sum`? Type the value.",
                "accept": [
                  "{ kg: 2 }",
                  "{kg: 2}",
                  "{kg:2}",
                  "{ kg: 2}",
                  "{\"kg\":2}",
                  "the first box",
                  "the first element",
                  "array[0]",
                  "an object",
                  "the object"
                ],
                "why": "The first *box* — `array[0]`, an object, not a number. Adding a number to an object makes a string, so the total comes out as `\"[object Object]3\"`. Nothing throws; the page predicted it."
              }
            ],
            "hints": [
              {
                "when": "you said the empty reduce returns 0 or undefined",
                "say": "Read the Exceptions section again. It names one situation — check whether this call is it."
              },
              {
                "when": "you said sum starts at 0 without an initialValue",
                "say": "No argument in the call says 0. Read what the `accumulator` line says happens \"otherwise\"."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Look it up in order",
            "prompt": "Your project runs on Node, and you want to know whether you can use `Object.groupBy`. Put the steps in order. Two of the lines do not belong.\n",
            "lines": [
              "Decide whose name it is: Object is built into the language, so MDN's JavaScript reference",
              "Open the reference page for Object.groupBy()",
              "Read Syntax, Parameters and Return value: what it takes and what it returns",
              "Read Browser compatibility: the Node.js column says 21.0.0",
              "Run node --version and compare it with 21.0.0"
            ],
            "distractors": [
              {
                "line": "Search Node's own documentation for Object.groupBy",
                "why": "Node's documentation covers what Node adds. Its globals page says the objects it lists are specific to Node.js, and sends you to MDN for the built-in objects that are part of the language itself."
              },
              {
                "line": "Search a forum for \"groupBy not working\" and use the top answer",
                "why": "Documentation first. A forum answer was written for someone else's Node version — and the one fact you need, which version added it, is on the reference page already."
              }
            ],
            "why": "Whose name it is decides which page to open; the page says what the method does, then which versions have it; and only your own `node --version` says which of those you run.\n",
            "hints": [
              {
                "when": "you put node --version first",
                "say": "Your version means nothing until you know which version you need. Where does that number come from?"
              },
              {
                "when": "you kept the Node documentation line",
                "say": "Is `Object` something Node adds, or something every host has?"
              }
            ]
          },
          {
            "kind": "complete",
            "title": "Fix it from the page",
            "prompt": "`dailyTotal(sales)` should add up the `amount` of every sale. It throws on a day with no sales, and on a normal day it returns `\"[object Object]7\"` instead of `12`. The trace step's reduce page says why for both. Fill the gap. Then, in one sentence: which section of the page predicted each failure?\n",
            "exportName": "dailyTotal",
            "filename": "daily-total.js",
            "starter": "// sales: [{ amount: 5 }, { amount: 7 }, ...]  — may be empty\nfunction dailyTotal(sales) {\n  return sales.reduce((sum, sale) => sum + sale.amount /* , ___ gap */);\n}\n",
            "tests": [
              {
                "label": "two sales add up to 12",
                "body": "return subject([{ amount: 5 }, { amount: 7 }]) === 12;"
              },
              {
                "label": "a day with no sales is 0, not an error",
                "body": "return subject([]) === 0;"
              },
              {
                "label": "one sale returns its amount, not the sale object",
                "body": "return subject([{ amount: 3 }]) === 3;"
              },
              {
                "label": "three sales add up to 20",
                "body": "return subject([{ amount: 2 }, { amount: 8 }, { amount: 10 }]) === 20;"
              }
            ],
            "hints": [
              {
                "when": "you get \"[object Object]7\" or similar",
                "say": "On the first call, `sum` is whatever the page says `accumulator` is when there is no `initialValue`. What is `array[0]` here?"
              },
              {
                "when": "you checked sales.length first and return 0",
                "say": "That stops the crash, but a normal day still starts its sum with a sale object. One argument fixes both."
              },
              {
                "when": "you get \"57\" or \"0512\"",
                "say": "Your starting value is a string, so `+` joins instead of adding. What kind of value should a total start as?"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review code written from a forum answer",
            "prompt": "A teammate wrote this for a Node program, following a forum answer and their memory of the browser. Answer with a line number, or pick where asked.\n",
            "filename": "reminders.js",
            "code": "// reminders.js — runs in Node\nfunction schedule(reminders) {\n  const ids = reminders.map(r => setTimeout(r.send, r.delay));\n  const saved = JSON.stringify(ids);   // timer ids are numbers, so they save as JSON\n  const total = reminders.map(r => r.delay).reduce((a, b) => a + b);\n  const label = String(reminders.length).padStart(\"0\", 3);\n  return { saved, total, label };\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Line 4's comment says timer ids are numbers. In which host is that true?",
                "choices": [
                  "Browser",
                  "Node"
                ],
                "accept": [
                  "Browser"
                ],
                "why": "The browser: MDN's `setTimeout` returns \"a positive integer\". Node's own page says it returns a `Timeout` object — and `JSON.stringify` of those throws, because a `Timeout` object holds references that lead back to itself. The comment is true of a different host."
              },
              {
                "id": "c2",
                "question": "Suppose line 4 is fixed. Which line throws when `reminders` is empty?",
                "accept": [
                  "5",
                  "line 5"
                ],
                "miss": "**If you said 3 — `map` on an empty array gives an empty array.** Which call's Exceptions section names an empty array?\n",
                "why": "Line 5. `reduce` with no `initialValue` on an empty array throws a TypeError. `reduce((a, b) => a + b, 0)` fixes it."
              },
              {
                "id": "c3",
                "question": "Which line passes its arguments in the wrong order, so it silently does nothing?",
                "accept": [
                  "6",
                  "line 6"
                ],
                "why": "Line 6. The signature is `padStart(targetLength, padString)`. Here the target length is `\"0\"`, and the page says a target no longer than the string returns it as-is — so `\"2\"` stays `\"2\"`, with no error. It should be `padStart(3, \"0\")`."
              }
            ],
            "hints": [
              {
                "when": "you answered node for the first",
                "say": "Open the host's own page for `setTimeout` and read its Returns line. Which page does \"a positive integer\" come from?"
              },
              {
                "when": "you could not find the padStart line's problem",
                "say": "Compare each call's arguments with the Syntax line on its page, by position — which one is first?"
              }
            ]
          }
        ]
      }
    },
    "ai-evaluation": {
      "id": "ai-evaluation",
      "title": "What AI-generated code gets wrong",
      "kind": "read",
      "outcome": 10,
      "shape": "conceptual",
      "why": "Outcome 10 asks students to evaluate AI-generated code for correctness and idiom before adopting it. Reviewing any code they did not write is practised in every family's critique tasks; this lesson takes the human author out so the failure patterns particular to generated code can be seen on their own (D-022). It comes last because each pattern is caught with a concept from an earlier family.",
      "concepts": [
        "diag.ai-evaluation"
      ],
      "lede": "Generated code arrives fluent, formatted and confident, and it reads the same whether it is right or wrong. It also goes wrong in its own ways, which are different from the ways people go wrong. Once you know the four patterns, you can check for each one on purpose.",
      "margins": [
        {
          "label": "SEE ALSO",
          "body": "Truthiness · How a loop gets its next value · Copying versus aliasing · Higher-order functions, built by hand"
        },
        {
          "label": "GLOSSARY",
          "boxed": true,
          "body": "**Idiom** — the usual way to say something in a particular language. `for (const x of xs)` is JavaScript's idiom for \"each item\"; Python's is `for x in xs`. **Premise** — a claim that later reasoning takes as true.",
          "entries": [
            "**Idiom** — the usual way to say something in a particular language. `for (const x of xs)` is JavaScript's idiom for \"each item\"; Python's is `for x in xs`.",
            "**Premise** — a claim that later reasoning takes as true."
          ]
        }
      ],
      "body": [
        {
          "beat": "hook",
          "label": "before anything else",
          "body": [
            {
              "p": "An assistant produced each of these. Type what the last expression produces, or\n`error` if it throws. Every one of them looks reasonable, and that is why you\nare guessing before you are told."
            },
            {
              "tracks": {
                "js": [
                  {
                    "ask": {
                      "label": null,
                      "items": [
                        {
                          "code": "const out = [];   for (const x in [\"a\", \"b\"]) out.push(x);   out",
                          "accept": [
                            "[\"0\",\"1\"]",
                            "[\"0\", \"1\"]",
                            "['0','1']",
                            "['0', '1']",
                            "[0,1]",
                            "[0, 1]",
                            "0,1"
                          ],
                          "miss": "**If you answered `[\"a\",\"b\"]` — that is what this loop means in Python**, where `for x in list` gives you the items. In JavaScript, `for…in` gives you the keys, as strings. The code was written in one language's idiom and runs under another's rules.\n",
                          "why": "`[\"0\",\"1\"]`. `for…in` walks an object's keys, and an array's keys are its indices as strings. The loop that gives items is `for…of`.\n"
                        },
                        {
                          "code": "function pick(xs) { if (xs.length === 0) return xs.first(); return xs[0]; }   pick([])",
                          "accept": [
                            "error",
                            "typeerror",
                            "TypeError",
                            "TypeError: xs.first is not a function"
                          ],
                          "miss": "**If you answered `undefined` — arrays have no `first` method.** The name is plausible, and some other languages' libraries have one, but reading a method that does not exist gives `undefined`, and calling `undefined` throws. `pick([7, 8])` returns `7` without a murmur, because the example never reaches that line.\n",
                          "why": "`TypeError: xs.first is not a function`. The code runs perfectly on any non-empty list, which is exactly how a call to a method that does not exist survives a quick try.\n"
                        },
                        {
                          "question": "Turn 1 claims: \"`const` makes an array read-only.\" Turn 2 builds on it: `const LIMITS = [10, 20]; function getLimits() { return LIMITS; } getLimits().push(99); LIMITS.length`",
                          "accept": [
                            "3"
                          ],
                          "miss": "**If you answered `2` — you accepted turn 1's claim, and turn 2 is built on it.** `const` stops the name `LIMITS` being pointed at a different array. It does nothing to stop the array itself changing. Every caller of `getLimits` gets the same array, and any of them can push to it.\n",
                          "why": "`3`. Turn 2's code is sensible *if* turn 1 were true. It is not, so the shared list is changed by the first caller that pushes.\n"
                        },
                        {
                          "code": "const cart = [];   cart ? \"checkout\" : \"cart is empty\"",
                          "accept": [
                            "\"checkout\"",
                            "checkout",
                            "'checkout'"
                          ],
                          "miss": "**If you answered `\"cart is empty\"` — an empty list is false in Python.** In JavaScript an empty array is an object, and every object is truthy. The check never sees an empty cart.\n",
                          "why": "`\"checkout\"`. The test asks whether `cart` exists, not whether it has anything in it. The question that was meant is `cart.length === 0`.\n"
                        }
                      ]
                    }
                  }
                ]
              }
            }
          ]
        },
        {
          "beat": "outcomes",
          "body": [
            {
              "list": [
                "I can look at code an AI wrote and say whether it is correct, idiomatic, and appropriate — and explain what I changed and why",
                "I can name the failure patterns particular to generated code: a call to something that does not exist, an idiom from another language, a solution bigger than the problem, and a wrong premise built on"
              ]
            }
          ]
        },
        {
          "h": "Why it fails differently"
        },
        {
          "p": "An assistant writes code by producing the text most likely to come next, given\neverything written so far: your request, the conversation, and its own earlier\nanswers. Three consequences follow, and each one produces a pattern."
        },
        {
          "list": [
            "**Producing the code does not require running it.** Unless you have a reason to know the code was run, assume it was not. A name that *looks* right is as easy to produce as one that *is* right.",
            "**It has read code in many languages.** A name or a habit that is correct in one of them can turn up in another where it means something else, or nothing.",
            "**Earlier text is part of what it continues from — including its own mistakes.** A wrong claim made early is treated as settled, and later answers are built to agree with it."
          ]
        },
        {
          "p": "And it writes every answer in the same fluent, confident style. **The style\ncarries no information about whether the code is right.** Neither does the\nfact that it ran once on the example you had in mind."
        },
        {
          "h": "The four patterns"
        },
        {
          "table": {
            "head": [
              "Pattern",
              "What it looks like",
              "The check that catches it"
            ],
            "rows": [
              [
                "**A call to something that does not exist**",
                "`xs.last()`, `s.contains(\"x\")`, `xs.remove(item)`, `Object.deepEqual(a, b)`",
                "Look the name up. It throws only when that line runs — and reading a *property* that does not exist, like `xs.size`, gives `undefined` and throws nothing"
              ],
              [
                "**An idiom from another language**",
                "`if (items)` for \"is it non-empty\"; `for (x in list)` for \"each item\"; `list == []` for \"is it empty\"",
                "Ask what the line means *in your language*, not in the one it came from"
              ],
              [
                "**A solution bigger than the problem**",
                "Three classes and a strategy object for what one `filter` does",
                "Measure it against what you asked for. Correct code can still be the wrong code"
              ],
              [
                "**A wrong premise, built on**",
                "An early claim (\"`const` makes it read-only\") that later code quietly depends on",
                "Find the claim the code depends on and test the claim itself"
              ]
            ]
          }
        },
        {
          "p": "The first two break at runtime, sometimes loudly and sometimes silently. The\nthird never breaks: it is correct, and it costs every person who reads or\nchanges it afterwards. The fourth makes the code *consistent*, which is why it\nis hard to see: each line agrees with the lines around it, and the error is\nunderneath all of them."
        },
        {
          "p": "Three more. Type what the last expression produces, or `error` if it throws."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "code": "const words = [\"a\", \"b\"];   words.contains(\"a\")",
                "accept": [
                  "error",
                  "typeerror",
                  "TypeError",
                  "TypeError: words.contains is not a function"
                ],
                "miss": "**If you answered `true` — the name is `includes`.** `contains` is what several other languages call it. In JavaScript the method does not exist, so the call throws.\n",
                "why": "`TypeError: words.contains is not a function` — a call to something that does not exist. The real method is `includes`.\n"
              },
              {
                "code": "const queue = [];   queue == []",
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` — that is Python, which compares list contents with `==`.** In JavaScript, `==` on two objects asks whether they are the same object, and `[]` makes a new one every time it is written.\n",
                "why": "`false`, always, even for an empty `queue`: two different arrays are never the same object. An idiom from another language, broken by what equality means here.\n"
              },
              {
                "code": "const seats = [\"A1\", \"A2\"];   seats.size > 0",
                "accept": [
                  "false"
                ],
                "miss": "**If you answered `true` — arrays have `length`; `size` belongs to `Map` and `Set`.** Reading a property that is not there gives `undefined`, and `undefined > 0` is `false`. No error at all.\n",
                "why": "`false`. The silent version of a call to something that does not exist: a missing property is `undefined`, not an error.\n"
              }
            ]
          }
        },
        {
          "h": "Reviewing generated code"
        },
        {
          "p": "The review has four checks, one for each pattern, and the same four labels are\nused in every example below. Run them in this order: the cheap mechanical\nchecks first, then the judgment."
        },
        {
          "p": "**Example 1.** You asked for: *\"Given a playlist and the title playing now,\nreturn the next title. After the last song, wrap around to the first. For an\nempty playlist, return `null`.\"* You got this, and tried it once:"
        },
        {
          "code": "function nextTitle(playlist, current) {\n  if (!playlist) {\n    return null;\n  }\n  const i = playlist.findIndex(s => s.title === current);\n  if (i === playlist.length - 1) {\n    return playlist.first().title;\n  }\n  return playlist[i + 1].title;\n}\n\nnextTitle([{ title: \"Intro\" }, { title: \"Verse\" }, { title: \"Outro\" }], \"Intro\");\n// \"Verse\" — it works",
          "lang": "js",
          "file": "next-title.js"
        },
        {
          "code": "// Check 1: run it where the example did not go\n//   the last song -> TypeError: playlist.first is not a function\n//   []            -> TypeError as well: the empty check did not stop it\n\n// Check 2: look up every name it calls\n//   findIndex -> exists.   first -> does not. The first item is playlist[0].\n\n// Check 3: read each idiom in this language\n//   if (!playlist) -> \"is it empty\" in Python. In JavaScript [] is truthy,\n//   so this only catches a missing playlist. Meant: playlist.length === 0\n\n// Check 4: measure it against what you asked for\n//   right size: one function, no machinery. Nothing to cut.",
          "lang": "js",
          "file": "review.js"
        },
        {
          "p": "**The example worked because it went through the one path with nothing wrong\nin it.** Both defects live in the branches it skipped — the two cases the\nrequest mentioned by name. Trying generated code once, on the case you had in\nmind, tests the part it is least likely to get wrong."
        },
        {
          "code": "function nextTitle(playlist, current) {\n  if (playlist.length === 0) {\n    return null;\n  }\n  const i = playlist.findIndex(s => s.title === current);\n  if (i === playlist.length - 1) {\n    return playlist[0].title;\n  }\n  return playlist[i + 1].title;\n}",
          "lang": "js",
          "file": "next-title.js"
        },
        {
          "p": "**Example 2.** A different request: *\"Give me the names of the products that\nare out of stock.\"*"
        },
        {
          "code": "class StockRule {\n  matches(product) {\n    throw new Error(\"subclass must implement matches\");\n  }\n}\n\nclass OutOfStockRule extends StockRule {\n  matches(product) {\n    return product.stock === 0;\n  }\n}\n\nclass ProductQuery {\n  constructor(rule) {\n    this.rule = rule;\n  }\n  names(products) {\n    const result = [];\n    for (const p of products) {\n      if (this.rule.matches(p)) result.push(p.name);\n    }\n    return result;\n  }\n}\n\nfunction outOfStockNames(products) {\n  return new ProductQuery(new OutOfStockRule()).names(products);\n}",
          "lang": "js",
          "file": "out-of-stock.js"
        },
        {
          "code": "// Check 1: run it where the example did not go\n//   []             -> []   fine\n//   nothing out    -> []   fine\n\n// Check 2: look up every name it calls\n//   nothing but the language's own class syntax. Fine.\n\n// Check 3: read each idiom in this language\n//   classes, extends, for...of: all ordinary JavaScript. Fine.\n\n// Check 4: measure it against what you asked for\n//   25 lines and three classes, for:\n//   products.filter(p => p.stock === 0).map(p => p.name)",
          "lang": "js",
          "file": "review.js"
        },
        {
          "p": "It passes three checks and fails the fourth. It is **correct and idiomatic,\nand not appropriate**. The only line that says what the function keeps —\n`product.stock === 0` — is buried in the middle of a class. The hierarchy\nexists for a family of rules nobody asked for, and the base class exists only\nto throw. A class earns its place by guarding state or carrying several\nbehaviours; a single test on each item is what a function parameter is for,\nand `filter` already takes one. **Replacing it is the review, not a matter of\ntaste:** every line you adopt is a line someone has to read and change later."
        },
        {
          "p": "**Example 3.** A premise across a conversation. This exchange is illustrative,\nnot a real transcript."
        },
        {
          "p": "**You:** If I declare the defaults list with `const`, can other code change it?\n\n**Assistant:** No — `const` makes the array read-only, so it's safe to share.\n\n**You:** Then give me a function that hands each new post the default tags.\n\n**Assistant:** Since `DEFAULT_TAGS` is `const`, you can return it directly —\nno copy needed:"
        },
        {
          "code": "const DEFAULT_TAGS = [\"news\", \"local\"];\n\nfunction tagsForNewPost() {\n  return DEFAULT_TAGS;\n}\n\nconst post = { tags: tagsForNewPost() };\npost.tags.push(\"sport\");\ntagsForNewPost();   // [\"news\", \"local\", \"sport\"] — every later post has it too",
          "lang": "js",
          "file": "tags.js"
        },
        {
          "code": "// Check 1: run it where the example did not go\n//   add a tag to one post, then make another -> the second has it too\n\n// Check 2: look up every name it calls\n//   all real.\n\n// Check 3: read each idiom in this language\n//   fine.\n\n// Check 4: measure it against what you asked for\n//   fine — one line.\n\n// ...and the defect is still there. Find the premise underneath:\n//   \"const makes the array read-only.\" Test the CLAIM, not the code:\n//   const xs = [1]; xs.push(2); xs  ->  [1, 2]\n//   const stops the NAME being pointed elsewhere. The array is shared\n//   and changeable. Fix: return a copy -> return [...DEFAULT_TAGS];",
          "lang": "js",
          "file": "review.js"
        },
        {
          "p": "The fourth answer is not a new mistake. Given the second answer, it is exactly\nright — and that is what makes it convincing. **When code in a conversation\ndepends on an earlier claim, the claim is what you check**, and you settle it\nwith the runtime or the documentation. Asking again in the same conversation\ncontinues from the same wrong text."
        },
        {
          "ask": {
            "label": null,
            "items": [
              {
                "question": "In Example 1, which check finds `playlist.first()` without your having to guess which input to try?",
                "choices": [
                  "1",
                  "2",
                  "3",
                  "4"
                ],
                "accept": [
                  "2"
                ],
                "why": "Check 2, looking up every name it calls. Check 1 finds it too, on the last song — but only if you think to try that case. A lookup does not depend on guessing.\n"
              },
              {
                "question": "Example 2 — correct, idiomatic, appropriate: which one fails?",
                "choices": [
                  "correct",
                  "idiomatic",
                  "appropriate"
                ],
                "accept": [
                  "appropriate"
                ],
                "miss": "**If you answered `correct` — every input gives the right names.** The defect is its size, not its output, which is why no test can find it.\n",
                "why": "Appropriate. It is right and written in ordinary JavaScript, and it is the size of a problem you did not have.\n"
              },
              {
                "question": "Example 3 — which turn of the conversation (1–4) holds the defect?",
                "choices": [
                  "1",
                  "2",
                  "3",
                  "4"
                ],
                "accept": [
                  "2"
                ],
                "why": "The second — the assistant's first answer. The code in the fourth turn is correct *if* the second were true.\n"
              }
            ]
          }
        },
        {
          "scaffold": {
            "label": "Running the four checks yourself",
            "body": [
              {
                "h": "Running the four checks yourself"
              },
              {
                "p": "You asked: *\"Is `'urgent'` one of the tags? An empty list never is.\"* Fill in\neach check."
              },
              {
                "code": "function isUrgent(tags) {\n  if (tags == []) return false;\n  return tags.contains(\"urgent\");\n}",
                "lang": "js",
                "file": "is-urgent.js"
              },
              {
                "code": "// Check 1: run it where the example did not go     ______\n// Check 2: look up every name it calls             ______\n// Check 3: read each idiom in this language        ______\n// Check 4: measure it against what you asked for   ______",
                "lang": "js",
                "file": "exercise.js"
              },
              {
                "p": "Running it on anything at all throws — `contains` does not exist; the method\nis `includes`. The first line is Python's \"is it empty\": in JavaScript\n`tags == []` compares against a brand-new array and is always `false`, so it\nnever returns early. The size is right. Fixed:\n`return tags.includes(\"urgent\");` — and that already returns `false` for an\nempty list, so the first line can go."
              }
            ]
          }
        },
        {
          "tracks": {
            "js": [
              {
                "p": "**Checking a name exists** takes seconds. In the console, `typeof [].last`\nprints `\"undefined\"`: no such method. MDN's page for `Array` lists every method\nan array has. These are the ones generated code most often invents, and what\nJavaScript actually has:"
              },
              {
                "table": {
                  "head": [
                    "Invented",
                    "What JavaScript has"
                  ],
                  "rows": [
                    [
                      "`xs.last()`, `xs.first()`",
                      "`xs.at(-1)`, `xs[0]`"
                    ],
                    [
                      "`s.contains(x)`, `xs.contains(x)`",
                      "`s.includes(x)`, `xs.includes(x)`"
                    ],
                    [
                      "`xs.remove(x)`",
                      "`xs.filter(y => y !== x)` for a new array; `splice` to change this one"
                    ],
                    [
                      "`xs.size`",
                      "`xs.length` — `size` is on `Map` and `Set`"
                    ],
                    [
                      "`Object.deepEqual(a, b)`",
                      "nothing built into the language. Compare the fields you care about"
                    ]
                  ]
                }
              },
              {
                "p": "The reverse happens too: a newer method that is real can look invented.\n`xs.at(-1)` and `xs.findLast(fn)` are both in the language. The lookup\nsettles it either way; your memory does not."
              }
            ]
          }
        },
        {
          "beat": "revisit",
          "label": "no scrolling up",
          "body": [
            {
              "p": "Answer the opening four again from memory. Then name the pattern behind each\none — and for the second, say why `pick([7, 8])` returning `7` told you nothing\nabout whether `pick` is correct."
            }
          ]
        },
        {
          "beat": "apply",
          "body": [
            {
              "p": "You asked for *\"the three most recent orders, newest first\"* and got this, with\nthe note: *\"`sort` returns a new sorted array, so your original list is\nuntouched.\"* Each order's `date` is a `Date`."
            },
            {
              "code": "function recentOrders(orders) {\n  return orders.sort((a, b) => b.date - a.date).slice(0, 3);\n}",
              "lang": "js",
              "file": "recent.js"
            },
            {
              "p": "It returns the right three orders on every input you try."
            },
            {
              "p": "Three choices:"
            },
            {
              "list": [
                "**Adopt it** as it is.",
                "**Fix it** — change the smallest thing that makes it right.",
                "**Reject it** and write your own."
              ]
            },
            {
              "p": "Pick one and say why. Run the four checks and say which one decides it, and\nwhich pattern the note is. If you fix it, say exactly what you changed and why\nthat change is enough. Then say whether your choice would be different if the\nonly caller were a script that printed the three orders and never used the list\nagain."
            }
          ]
        },
        {
          "beat": "summary",
          "body": [
            {
              "list": [
                "Generated code reads the same whether it is right or wrong. **Neither the style nor one successful run is evidence.**",
                "Four patterns: **a call to something that does not exist, an idiom from another language, a solution bigger than the problem, a wrong premise built on.**",
                "Four checks, cheap to dear: run it where the example did not go, look up every name, read each idiom in this language, measure it against the ask.",
                "In a conversation, **check the claim the code depends on**, with the runtime or the documentation — not with another turn."
              ]
            }
          ]
        },
        {
          "mdn": [
            {
              "title": "Array",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array",
              "note": "the \"Instance methods\" list is the lookup for any array method you have not seen before."
            },
            {
              "title": "String",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String",
              "note": "the same list for strings; `includes` is there, `contains` is not."
            },
            {
              "title": "Array.prototype.sort()",
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort",
              "note": "read what it returns before deciding the apply question above."
            }
          ]
        }
      ],
      "_source": "lessons/ai-evaluation.md",
      "practice": {
        "id": "ai-evaluation",
        "outcome": 10,
        "steps": [
          {
            "kind": "predict",
            "title": "Predict, then name the first step",
            "prompt": "An assistant wrote each of the first two. Type what each produces, or `error` if it throws. For the third, answer in a word or two.\n",
            "items": [
              {
                "id": "p1",
                "code": "const tags = [];   tags ? \"tagged\" : \"untagged\"",
                "accept": [
                  "\"tagged\"",
                  "tagged",
                  "'tagged'"
                ],
                "why": "An empty array is an object, and every object is truthy in JavaScript. \"Empty is false\" is Python's rule, carried in. The check meant here is `tags.length === 0`."
              },
              {
                "id": "p2",
                "code": "\"hello world\".contains(\"world\")",
                "accept": [
                  "error",
                  "typeerror",
                  "TypeError"
                ],
                "why": "Strings have no `contains` method, so the call throws `TypeError`. The method JavaScript has is `includes`. A confident call to something that does not exist."
              },
              {
                "id": "p0",
                "question": "Generated code calls `xs.removeAll(x)`, which you have never seen. Where do you find out whether it exists?",
                "accept": [
                  "the docs",
                  "docs",
                  "documentation",
                  "the documentation",
                  "mdn",
                  "MDN",
                  "the reference",
                  "reference",
                  "official docs",
                  "the official documentation",
                  "the console",
                  "console",
                  "run it",
                  "typeof"
                ],
                "why": "In the documentation (MDN's `Array` page lists every method), or in one line in the console — `typeof [].removeAll` prints `\"undefined\"`. Not by asking the assistant: that continues from the same text that produced the name."
              }
            ],
            "extra": [
              {
                "id": "p3",
                "code": "[3, 5, 8].at(-1)",
                "accept": [
                  "8"
                ],
                "why": "`at` is real, and a negative index counts from the end. A newer method can look invented; the lookup settles it either way."
              },
              {
                "id": "p4",
                "code": "const seats = [\"A1\", \"A2\"];   seats.size",
                "accept": [
                  "undefined"
                ],
                "why": "Arrays have `length`; `size` is on `Map` and `Set`. Reading a property that is not there gives `undefined` and throws nothing — the silent version of an invented name."
              },
              {
                "id": "p5",
                "code": "const out = [];   for (const k in [\"x\", \"y\"]) out.push(k);   out",
                "accept": [
                  "[\"0\",\"1\"]",
                  "[\"0\", \"1\"]",
                  "['0','1']",
                  "['0', '1']",
                  "0,1"
                ],
                "why": "`for…in` gives keys, as strings. Python's `for x in list` gives items, and JavaScript's loop for that is `for…of`."
              }
            ],
            "hints": [
              {
                "when": "you answered \"untagged\" for the first",
                "say": "That is the right answer in a language whose empty list is false. Ask what JavaScript's truthiness list says about an object — any object."
              },
              {
                "when": "you answered true for the second",
                "say": "Check the name, not the idea. Does a JavaScript string have a method with exactly that name?"
              },
              {
                "when": "you answered \"ask the assistant\" for the third",
                "say": "The assistant produced the name from the same text that might be wrong. What source describes the language itself?"
              }
            ]
          },
          {
            "kind": "run",
            "title": "Predict three lines, then run it",
            "prompt": "An assistant wrote this for \"say whether any tickets are open, then list the open tickets' ids, then show the most recent open id\". Predict the three things that appear, in order — the third may be an error. Then run it.\n",
            "filename": "tickets.js",
            "code": "const tickets = [\n  { id: 7, open: true },\n  { id: 9, open: false },\n];\nconst open = tickets.filter(t => t.open);\n\nconsole.log(open.size > 0 ? \"some open\" : \"none open\");\n\nfor (const i in open) {\n  console.log(\"ticket \" + i);\n}\n\nconsole.log(open.last().id);\n",
            "items": [
              {
                "id": "r1",
                "question": "Type the first line printed.",
                "accept": [
                  "none open",
                  "\"none open\"",
                  "'none open'"
                ],
                "why": "Ticket 7 is open, so this should say \"some open\". `open.size` is `undefined` — arrays have `length` — and `undefined > 0` is `false`. Silent."
              },
              {
                "id": "r2",
                "question": "Type the second line printed.",
                "accept": [
                  "ticket 0",
                  "\"ticket 0\"",
                  "'ticket 0'"
                ],
                "why": "`for…in` gives the key `\"0\"`, not the ticket. The id `7` was wanted; getting it needs `for (const t of open)` and `t.id`."
              },
              {
                "id": "r3",
                "question": "Type what the last line produces, or `error` if it throws.",
                "accept": [
                  "error",
                  "typeerror",
                  "TypeError",
                  "TypeError: open.last is not a function"
                ],
                "why": "`TypeError: open.last is not a function`. The one defect of the three that announces itself — and it is the last to run."
              }
            ],
            "hints": [
              {
                "when": "you answered \"some open\" for the first",
                "say": "Look up `size`. Which kinds of collection have it, and what does reading a property an array does not have give you?"
              },
              {
                "when": "you answered \"ticket 7\" for the second",
                "say": "Ask what `for…in` walks — the values, or the keys? And what type are those keys?"
              },
              {
                "when": "you answered 7 for the last",
                "say": "Is `last` a method arrays actually have? Calling something that is not there is not `undefined`; it is an error."
              }
            ]
          },
          {
            "kind": "trace",
            "title": "Trace it past the example",
            "prompt": "An assistant wrote `average` and showed it working on `[2, 4]`. Give what it produces for each call.\n",
            "items": [
              {
                "id": "t1",
                "code": "function average(xs) { if (!xs) return 0; return xs.reduce((a, b) => a + b, 0) / xs.length; }   average([2, 4])",
                "accept": [
                  "3"
                ],
                "why": "The example. `6 / 2` is `3`, and it is the one case the assistant showed you."
              },
              {
                "id": "t2",
                "code": "average([])",
                "accept": [
                  "NaN"
                ],
                "why": "`[]` is truthy, so the guard does not fire. The sum is `0`, the length is `0`, and `0 / 0` is `NaN`. The guard was written in Python's idiom, where an empty list is false."
              },
              {
                "id": "t3",
                "question": "Which of these does the `if (!xs)` guard catch?",
                "choices": [
                  "[]",
                  "null",
                  "both"
                ],
                "accept": [
                  "null"
                ],
                "why": "`null` is falsy, `[]` is not. The guard catches a missing list and lets the empty one through — the opposite of the case it was written for."
              }
            ],
            "extra": [
              {
                "id": "t4",
                "code": "average([5])",
                "accept": [
                  "5"
                ],
                "why": "One item works. Only the empty list breaks it — which is exactly the case a one-line demo never tries."
              },
              {
                "id": "t5",
                "code": "average(undefined)",
                "accept": [
                  "0"
                ],
                "why": "`undefined` is falsy, so the guard returns `0`. It handles the inputs nobody asked about and misses the one that matters."
              }
            ],
            "hints": [
              {
                "when": "you answered 0 for average([])",
                "say": "Run the guard by hand first. Is `[]` truthy or falsy in JavaScript? If the guard does not return, what is `0 / 0`?"
              },
              {
                "when": "you answered \"[]\" for the third",
                "say": "List the falsy values. Is an empty array one of them?"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Review a cart function",
            "prompt": "An assistant wrote this for the request in its comment, and showed it taking one pen out of a cart holding three. Answer each question with a line number or a word, then fix it so the tests pass.\n",
            "filename": "take-one.js",
            "code": "// Take one of an item out of the cart. When its quantity reaches zero,\n// remove it from the cart. Return the cart. An empty cart comes back\n// unchanged.\nfunction takeOne(cart, name) {\n  if (!cart) {\n    return cart;\n  }\n  const line = cart.find(l => l.name === name);\n  line.qty -= 1;\n  if (line.qty === 0) {\n    cart.remove(line);\n  }\n  return cart;\n}\n",
            "items": [
              {
                "id": "c1",
                "question": "Which line calls a method arrays do not have? Type the line number.",
                "accept": [
                  "11",
                  "line 11"
                ],
                "why": "Line 11. Arrays have no `remove`. To change this cart, `splice` out the index; for a new array, `filter`."
              },
              {
                "id": "c2",
                "question": "The demo with three pens did not throw. Does line 11 run when the quantity goes from 3 to 2?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "No"
                ],
                "why": "No — line 11 runs only when the quantity reaches zero. The invented call sits in the branch the demo never took."
              },
              {
                "id": "c3",
                "question": "Which line is an idiom from another language? Type the line number.",
                "accept": [
                  "5",
                  "line 5"
                ],
                "why": "Line 5. `if (!cart)` means \"is it empty\" in Python. In JavaScript `[]` is truthy, so an empty cart goes straight past it — and line 9 then throws, because `find` returned `undefined`."
              },
              {
                "id": "c4",
                "question": "Which pattern is line 11?",
                "choices": [
                  "A call to something that does not exist",
                  "An idiom from another language",
                  "Bigger than the problem",
                  "A wrong premise"
                ],
                "accept": [
                  "A call to something that does not exist"
                ],
                "why": "A call to something that does not exist: fluent, plausible, and a `TypeError` the first time the branch runs."
              }
            ],
            "exportName": "takeOne",
            "starter": "// Take one of an item out of the cart. When its quantity reaches zero,\n// remove it from the cart. Return the cart. An empty cart comes back\n// unchanged.\nfunction takeOne(cart, name) {\n  if (!cart) {\n    return cart;\n  }\n  const line = cart.find(l => l.name === name);\n  line.qty -= 1;\n  if (line.qty === 0) {\n    cart.remove(line);\n  }\n  return cart;\n}\n",
            "tests": [
              {
                "label": "takes one away",
                "body": "const r = subject([{ name: 'pen', qty: 3 }], 'pen'); return r.length === 1 && r[0].qty === 2;"
              },
              {
                "label": "an item that reaches zero leaves the cart",
                "body": "const r = subject([{ name: 'pen', qty: 1 }, { name: 'ink', qty: 2 }], 'pen'); return r.length === 1 && r[0].name === 'ink';"
              },
              {
                "label": "the rest of the cart keeps its order",
                "body": "const r = subject([{ name: 'a', qty: 1 }, { name: 'b', qty: 1 }, { name: 'c', qty: 1 }], 'b'); return r.length === 2 && r[0].name === 'a' && r[1].name === 'c';"
              },
              {
                "label": "an empty cart comes back empty, without an error",
                "body": "const r = subject([], 'pen'); return Array.isArray(r) && r.length === 0;"
              }
            ],
            "hints": [
              {
                "when": "the empty-cart test throws \"Cannot read properties of undefined\"",
                "say": "The empty cart got past your first check, so `find` found nothing. What does the check need to ask about the array — whether it exists, or how many items it has?"
              },
              {
                "when": "the item-reaches-zero test still fails, or \"cart.remove is not a function\"",
                "say": "Look up what arrays really have for taking an element out. One method changes this array at an index; another builds a new array without it — if you use that one, return what it builds."
              },
              {
                "when": "the length stays the same but one slot is empty",
                "say": "`delete` on an array leaves a hole and does not change `length`. You need a method that closes the gap."
              }
            ]
          },
          {
            "kind": "assemble",
            "title": "Build the version that was meant",
            "prompt": "`latestOpenId(tickets)` should return the id of the last open ticket, or `null` when none is open. Build it from these lines. Three of them are lines an assistant might produce — each is one of the patterns.\n",
            "lines": [
              "function latestOpenId(tickets) {",
              "  const open = tickets.filter(t => t.open);",
              "  if (open.length === 0) return null;",
              "  return open.at(-1).id;",
              "}"
            ],
            "distractors": [
              {
                "line": "  if (!open) return null;",
                "why": "Python's \"is it empty\". In JavaScript `open` is an array, and every array is truthy — this guard never fires, and the next line throws on `undefined.id`.\n"
              },
              {
                "line": "  return open.last().id;",
                "why": "Arrays have no `last` method. The one that exists is `at`, which takes `-1` for the last element.\n"
              },
              {
                "line": "  if (open.size === 0) return null;",
                "why": "`size` belongs to `Map` and `Set`. On an array it is `undefined`, and `undefined === 0` is `false`, so this guard never fires either.\n"
              }
            ],
            "why": "Filter to the open tickets, handle \"none\" with the array's `length`, then read the last one with `at(-1)`. Every distractor reads naturally and is wrong for a reason you can look up.\n",
            "hints": [
              {
                "when": "you are unsure which guard is real",
                "say": "Try each guard on `[]` in your head. Which one is `true` for an empty array?"
              },
              {
                "when": "you are unsure which last-element line is real",
                "say": "One of them is a method in the language and one is not. `typeof [].at` and `typeof [].last` would tell you."
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Find where the conversation went wrong",
            "prompt": "An illustrative exchange, not a real transcript. The comments are the conversation; the code is the assistant's final answer. `send` returns a promise that settles once the email is out. Answer with a line number or a word.\n",
            "filename": "send-all.js",
            "code": "// You: If I await inside forEach, does each send finish before the next?\n// Assistant: Yes. await pauses the loop until each send completes,\n//   so the emails go out one after another.\n// You: Good. Then make sendAll return how many were sent.\n// Assistant: Since every send has finished by the time forEach\n//   returns, the count is ready straight after the loop:\nasync function sendAll(emails) {\n  let sent = 0;\n  emails.forEach(async (email) => {\n    await send(email);\n    sent++;\n  });\n  return sent;\n}\n",
            "items": [
              {
                "id": "s1",
                "question": "Which line holds the false claim the rest is built on? Type the line number.",
                "accept": [
                  "2",
                  "line 2"
                ],
                "why": "Line 2. `await` pauses only the callback it is in. `forEach` calls each callback, ignores the promise it returns, and moves on — nothing waits for the sends."
              },
              {
                "id": "s2",
                "question": "What value does `await sendAll([\"a\", \"b\", \"c\"])` give?",
                "accept": [
                  "0"
                ],
                "why": "`0`. All three sends have started, none has finished, and `return sent` runs straight after `forEach` returns. `sent++` happens later, after the caller already has its answer."
              },
              {
                "id": "s3",
                "question": "If line 2 were true, would lines 5–6 be sound reasoning?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes — and that is the pattern. The last answer is not a new mistake; it is correct on a wrong premise, which is why it reads so convincingly. Check the claim, not the consistency."
              },
              {
                "id": "s4",
                "question": "Which loop keeps the `await` inside, and actually waits for each send? Name the loop.",
                "accept": [
                  "for...of",
                  "for…of",
                  "for of",
                  "for-of",
                  "for (const email of emails)",
                  "a for...of loop"
                ],
                "why": "`for (const email of emails) { await send(email); sent++; }` — here the `await` is in `sendAll` itself, so `sendAll` pauses at each send."
              }
            ],
            "hints": [
              {
                "when": "you picked a line in the code for the first question",
                "say": "The code is consistent with what the assistant said earlier. Go back up to the claim the code was written to agree with."
              },
              {
                "when": "you answered 3 for the second",
                "say": "Which function does each `await` pause — `sendAll`, or the small arrow function handed to `forEach`? What does `forEach` do with the promise that arrow returns?"
              }
            ]
          },
          {
            "kind": "critique",
            "title": "Measure it against the ask",
            "prompt": "You asked for \"the members whose membership is active\". This came back. Answer each question with a word or a line number.\n",
            "filename": "active-members.js",
            "code": "class FilterStrategy {\n  matches(member) {\n    throw new Error(\"subclass must implement matches\");\n  }\n}\n\nclass ActiveStrategy extends FilterStrategy {\n  matches(member) {\n    return member.active === true;\n  }\n}\n\nclass MemberFilter {\n  constructor(strategy) {\n    this.strategy = strategy;\n  }\n  apply(members) {\n    const result = [];\n    for (const m of members) {\n      if (this.strategy.matches(m)) result.push(m);\n    }\n    return result;\n  }\n}\n\nfunction activeMembers(members) {\n  return new MemberFilter(new ActiveStrategy()).apply(members);\n}\n",
            "items": [
              {
                "id": "m1",
                "question": "Does `activeMembers` return the right members for every list?",
                "choices": [
                  "Yes",
                  "No"
                ],
                "accept": [
                  "Yes"
                ],
                "why": "Yes. Every input gives the right answer, and every name is real JavaScript. No test can find what is wrong with it."
              },
              {
                "id": "m2",
                "question": "Which single line says what the function keeps? Type the line number.",
                "accept": [
                  "9",
                  "line 9"
                ],
                "why": "Line 9 — the only line that is about members at all, buried inside the second of three classes."
              },
              {
                "id": "m3",
                "question": "Which built-in array method does `MemberFilter.apply` do the job of? Type its name.",
                "accept": [
                  "filter",
                  ".filter",
                  "filter()",
                  "Array.prototype.filter"
                ],
                "why": "`filter` takes a test and keeps what passes. The whole file is `members.filter(m => m.active === true)`."
              },
              {
                "id": "m4",
                "question": "Correct, idiomatic, appropriate — which one does it fail?",
                "choices": [
                  "correct",
                  "idiomatic",
                  "appropriate"
                ],
                "accept": [
                  "appropriate"
                ],
                "why": "Appropriate. It is bigger than the problem: machinery for a family of rules nobody asked for. Every line you adopt, someone reads and changes later — the next step makes that concrete."
              }
            ],
            "hints": [
              {
                "when": "you answered \"no\" to the first",
                "say": "Trace it on a list with one active and one inactive member. The output is right; the question is about something the output cannot show."
              },
              {
                "when": "you answered \"correct\" or \"idiomatic\" to the last",
                "say": "Classes and `extends` are ordinary JavaScript, and the answers are right. What is left is whether it is the size of what you asked for."
              }
            ]
          },
          {
            "kind": "modify",
            "title": "Change what it keeps",
            "prompt": "The same generated code, and a new requirement: `activeMembers(members, minYears)` keeps active members who have been members for at least `minYears` years (`member.years`). Called without `minYears`, it keeps every active member, as before. Change it however you judge best. When it passes: in one sentence, did you add to the classes or replace them — and what does that choice say about adopting them in the first place?\n",
            "exportName": "activeMembers",
            "filename": "active-members.js",
            "starter": "class FilterStrategy {\n  matches(member) {\n    throw new Error(\"subclass must implement matches\");\n  }\n}\n\nclass ActiveStrategy extends FilterStrategy {\n  matches(member) {\n    return member.active === true;\n  }\n}\n\nclass MemberFilter {\n  constructor(strategy) {\n    this.strategy = strategy;\n  }\n  apply(members) {\n    const result = [];\n    for (const m of members) {\n      if (this.strategy.matches(m)) result.push(m);\n    }\n    return result;\n  }\n}\n\nfunction activeMembers(members) {\n  return new MemberFilter(new ActiveStrategy()).apply(members);\n}\n",
            "tests": [
              {
                "label": "keeps active members with enough years",
                "body": "const ms = [{ name: 'a', active: true, years: 5 }, { name: 'b', active: true, years: 1 }]; const r = subject(ms, 2); return r.length === 1 && r[0].name === 'a';"
              },
              {
                "label": "exactly minYears counts",
                "body": "const r = subject([{ name: 'a', active: true, years: 2 }], 2); return r.length === 1;"
              },
              {
                "label": "an inactive member never counts, however many years",
                "body": "const r = subject([{ name: 'a', active: false, years: 10 }], 2); return r.length === 0;"
              },
              {
                "label": "without minYears, every active member counts",
                "body": "const r = subject([{ name: 'a', active: true, years: 0 }, { name: 'b', active: false, years: 3 }]); return r.length === 1 && r[0].name === 'a';"
              },
              {
                "label": "the caller's list is untouched",
                "body": "const ms = [{ name: 'a', active: true, years: 1 }, { name: 'b', active: true, years: 5 }]; subject(ms, 2); return ms.length === 2;"
              }
            ],
            "hints": [
              {
                "when": "the no-minYears test fails",
                "say": "Called with one argument, `minYears` is `undefined`, and `5 >= undefined` is `false`. A parameter can have a value it takes when the caller leaves it out."
              },
              {
                "when": "the exactly-minYears test fails",
                "say": "\"At least\" includes the number itself. Check the comparison operator."
              },
              {
                "when": "the inactive test fails",
                "say": "The new condition is added to the old one, not swapped for it. Both have to hold."
              },
              {
                "when": "the caller's-list test fails",
                "say": "Something removed items from the array you were handed. Keeping some items is a job for a method that builds a new array."
              }
            ]
          }
        ]
      }
    },
    "final-checkpoint": {
      "id": "final-checkpoint",
      "title": "Final checkpoint practice",
      "kind": "quiz",
      "unlocks": "Course complete",
      "questions": [
        {
          "id": "q1",
          "skill": "Outcome 1 — types",
          "prompt": "`\"0\"` is truthy. Why is `\"0\" == false` also `true`?",
          "answers": [
            {
              "key": "A",
              "text": "Two different rule sets — truthiness is a list, `==` coerces",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "It is a bug in the language",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "Because `\"0\"` is actually falsy",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It is not true — that comparison is `false`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `if` asks whether the value is one of eight falsy values; `==` runs the coercion algorithm and turns both sides into `0`. Two independent rules that usually agree, and this is where they do not.",
            "wrong": "Not quite. Truthiness is membership of a fixed list. `==` converts. Both are defined, and they disagree here. Revisit week 2's Truthiness. The answer is A."
          }
        },
        {
          "id": "q2",
          "skill": "Outcome 2 — references",
          "prompt": "`const a = {n:1}; const b = a; b.n = 2;` — what is `a.n`, and why?",
          "answers": [
            {
              "key": "A",
              "text": "`1` — `const` prevented the change",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "`2` — one object, two names; `const` blocks rebinding, not mutation",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`1` — `b = a` copied the object",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It throws",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Assignment copied the reference. `const` promises the name will not point somewhere else; it promises nothing about the object's contents.",
            "wrong": "Not quite. `b = a` copied the arrow, not the object, so both names reach one object. `const` stops `b = {...}`, not `b.n = 2`. Revisit week 3. The answer is B."
          }
        },
        {
          "id": "q3",
          "skill": "Outcome 3 — iteration",
          "prompt": "Why does a forward index loop with `splice` skip elements?",
          "answers": [
            {
              "key": "A",
              "text": "`splice` is unreliable",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "Removing shifts later elements left, past an index the loop has already used",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "The loop caches the array length at the start",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It does not — that behaviour is a myth",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `a.length` is re-read each iteration; the problem is that removal moves elements down into indices the loop has finished with. Build a new array with `filter` instead.",
            "wrong": "Not quite. `splice` does exactly what it promises. The loop holds an index, and removal changes what every later index refers to. Revisit week 4. The answer is B."
          }
        },
        {
          "id": "q4",
          "skill": "Outcome 4 — scope",
          "prompt": "Why do three closures made in a `var` loop all report the same value?",
          "answers": [
            {
              "key": "A",
              "text": "They captured the value at creation, and it was the same",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "They captured the binding, and `var` makes one for the whole function",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`setTimeout` runs them all at once",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Closures cannot capture loop variables",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. A closure captures the binding, not the value — so all three read whatever it holds when they run. `let` in a `for` header creates a fresh binding per iteration, which is the fix.",
            "wrong": "Not quite. If they had captured values, they would differ. They captured one shared binding. Revisit week 6. The answer is B."
          }
        },
        {
          "id": "q5",
          "skill": "Outcome 5 — functions as values",
          "prompt": "What is `[\"1\",\"2\",\"3\"].map(parseInt)`?",
          "answers": [
            {
              "key": "A",
              "text": "`[1, 2, 3]`",
              "tag": "the reasonable assumption",
              "correct": false
            },
            {
              "key": "B",
              "text": "`[1, NaN, NaN]` — `map` passes the index as a radix",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "`[NaN, NaN, NaN]`",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "It throws",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `map` calls back with (value, index, array) and `parseInt` reads the second argument as a radix. An arity problem — wrap it as `s => parseInt(s, 10)`.",
            "wrong": "Not quite. The callback receives three arguments and `parseInt` accepts two. Revisit week 7. The answer is B."
          }
        },
        {
          "id": "q6",
          "skill": "Outcome 6 — modelling",
          "prompt": "A base class's `addAll` stops calling `this.add`, and a subclass silently breaks. What is this called?",
          "answers": [
            {
              "key": "A",
              "text": "A race condition",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "The fragile base class",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "A circular dependency",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Shadowing",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The subclass depended on which method the base called internally — not part of any interface, not documented, not checkable. It is the strongest argument for composition.",
            "wrong": "Not quite. This is the fragile base class: a legal change to a base breaks a subclass that never changed. Revisit week 8. The answer is B."
          }
        },
        {
          "id": "q7",
          "skill": "Outcome 7 — modules",
          "prompt": "What does `package-lock.json` record that `package.json` does not?",
          "answers": [
            {
              "key": "A",
              "text": "The exact versions actually installed",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "B",
              "text": "The ranges you asked for",
              "tag": "that is package.json",
              "correct": false
            },
            {
              "key": "C",
              "text": "Which packages are dev-only",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Nothing — it is a cache",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. `package.json` holds ranges like `^1.4.2`; the lockfile holds the resolved versions. Committing it is what makes your machine, a teammate's and CI install the same bytes.",
            "wrong": "Not quite. The ranges live in `package.json`. The lockfile records what those ranges resolved to. Revisit week 9. The answer is A."
          }
        },
        {
          "id": "q8",
          "skill": "Outcome 8 — errors",
          "prompt": "What has `catch (e) { console.log(e); }` done?",
          "answers": [
            {
              "key": "A",
              "text": "Handled the error",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "Swallowed it — the caller now believes the call succeeded",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Rethrown it after logging",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Nothing; logging does not affect control flow",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The error stopped there. The program continues with whatever partial state it has, and the eventual symptom appears far from the cause. If you cannot say what the program does differently, do not catch.",
            "wrong": "Not quite. Logging records that something failed and then lets execution continue as though it had not. Revisit week 10's Where an error should be handled. The answer is B."
          }
        },
        {
          "id": "q9",
          "skill": "Outcome 9 — async",
          "prompt": "Why can two JavaScript callbacks never interleave mid-statement?",
          "answers": [
            {
              "key": "A",
              "text": "Because `const` makes variables atomic",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "One thread, and callbacks run to completion",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Because the engine locks shared variables",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "They can — that is a race condition",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. There is one thread and nothing interrupts a running callback, so `count++` cannot be caught halfway. This is why a race condition is unreachable in JavaScript — and why every threaded language you meet next can have one.",
            "wrong": "Not quite. No locking is involved and nothing is atomic by declaration. There is simply one thread, and a callback finishes before the next starts. Revisit week 10's event loop. The answer is B."
          }
        },
        {
          "id": "q10",
          "skill": "Outcome 10 — diagnosis",
          "prompt": "A value is wrong and no line appears to have written to it. What finds it fastest?",
          "answers": [
            {
              "key": "A",
              "text": "Adding `console.log` at each step",
              "tag": "shows contents, not identity",
              "correct": false
            },
            {
              "key": "B",
              "text": "A breakpoint, then checking whether two names are the same object",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Reading the stack trace",
              "tag": "there is no throw",
              "correct": false
            },
            {
              "key": "D",
              "text": "Rewriting the function",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. Printing shows contents, and two independent objects with the same contents print identically. Only `===` between the two objects answers the identity question — and there is no stack trace, because nothing threw.",
            "wrong": "Not quite. There is no error, so no trace; and logging cannot distinguish one shared object from two equal ones. Revisit week 3's Meet the debugger. The answer is B."
          }
        },
        {
          "id": "q11",
          "skill": "Crossing outcomes 1 and 2",
          "prompt": "Why does `[1,2] === [1,2]` return `false`?",
          "answers": [
            {
              "key": "A",
              "text": "`===` compares types, and arrays have no type",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "It asks whether they are the same object, and they are not",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "C",
              "text": "Arrays cannot be compared in JavaScript",
              "tag": "",
              "correct": false
            },
            {
              "key": "D",
              "text": "Because the arrays were declared separately with `const`",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right, and it is the correct answer to the question asked. Two separately built arrays are two objects. What you almost always mean is equivalence — for which JavaScript has no operator at all, so you write it.",
            "wrong": "Not quite. Both are arrays, so a type check would pass. `===` on two objects compares identity. Revisit week 2's Equality: == versus ===. The answer is B."
          }
        },
        {
          "id": "q12",
          "skill": "Crossing outcomes 3 and 8",
          "prompt": "A nightly job hits one malformed record out of 10,000. Which is hardest to defend?",
          "answers": [
            {
              "key": "A",
              "text": "Fail the whole job",
              "tag": "",
              "correct": false
            },
            {
              "key": "B",
              "text": "Skip it, collecting failures into a report",
              "tag": "",
              "correct": false
            },
            {
              "key": "C",
              "text": "Skip it silently and log",
              "tag": "correct",
              "correct": true
            },
            {
              "key": "D",
              "text": "All three are equally defensible",
              "tag": "",
              "correct": false
            }
          ],
          "explain": {
            "right": "Right. The first two are defensible trade-offs with visible consequences. The third guarantees nobody notices for six months — logging without a report is how a data-quality problem becomes invisible rather than handled.",
            "wrong": "Not quite. Failing loudly and collecting a report are both honest choices. Silently skipping hides a problem that nobody is now responsible for. Revisit week 10. The answer is C."
          }
        }
      ],
      "_source": "checks/final-checkpoint.yml"
    },
    "primitives-game": {
      "kind": "assignment",
      "title": "Primitives, put to work in a game",
      "lede": "Ten small functions — not, and, or, remainder, flipSign, and a few on strings and on nothing — each one line with no if. They make up a toolkit, and a small dungeon game uses every one: finish a function, run the game, and watch its line of the turn report come right.",
      "exercises": [
        "ops.logical",
        "ops.arithmetic",
        "values.strings",
        "types.nullability"
      ],
      "repo": {
        "name": "cs140-primitives-game",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "Ten small functions: three on booleans, two on numbers, two on strings, and\nthree on nothing (`null` and `undefined`). Each one is a single line, and none\nof them needs `if`. The operators from the *Values, types and operators* module\nare enough."
        },
        {
          "p": "Together they make a small toolkit, and `src/game.js` uses every one of them. It\nprints the turn report for a two-player dungeon game: whose turn it is, what a\ntrap did to the hero's health, what she can do next, and so on. You write the\ntoolkit; the game is already written. Each function you finish fixes a line of\nthe report."
        },
        {
          "h": "What each part leans on"
        },
        {
          "table": {
            "head": [
              "Part",
              "File",
              "Lessons"
            ],
            "rows": [
              [
                "1 `not`, `and`, `or`",
                "`src/booleans.js`",
                "Comparing and combining"
              ],
              [
                "2 `remainder`, `flipSign`",
                "`src/numbers.js`",
                "Arithmetic operators"
              ],
              [
                "3 `firstAndLastCharacter`, `snakecase`",
                "`src/strings.js`",
                "Working with strings"
              ],
              [
                "4 `isNull`, `isUndefined`, `isNil`",
                "`src/nothing.js`",
                "When there is nothing there; Equality: == versus ==="
              ]
            ]
          }
        },
        {
          "p": "Every part also leans on *Writing a function's answer*: where `input`, `a` and\n`b` get their values, how to turn a function's description into the line you\nwrite, and why `not` cannot answer by calling `not(input)`: that call would\ncall `not` again, and again, and never finish."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-primitives-game-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/primitives-game), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the game:  ```sh npm start ```  Most of the report says `undefined` or `NaN`. `undefined` is what a function returns when it has no `return` yet, and arithmetic with `undefined` gives `NaN`. That is the starting point.",
            "Run the tests:  ```sh npm test ```  Every test fails at first. That is the starting point too, not a problem."
          ],
          "ordered": true
        },
        {
          "p": "While you work on one part, run just its tests:"
        },
        {
          "code": "node --test test/2-numbers.test.js",
          "lang": "sh"
        },
        {
          "p": "The same tests run on GitHub every time you push. The **Actions** tab shows\nthe result."
        },
        {
          "h": "How to write each answer"
        },
        {
          "p": "Every function is already written except its body, the part between the\nbraces. Each one looks like this:"
        },
        {
          "code": "export function double(n) {\n  // TODO: put your code here\n}",
          "lang": "js"
        },
        {
          "p": "Write your answer between the braces, and put `return` in front of it.\n`return` sends the value out of the function, so the tests and the game can use\nit. The Functions module, later in the course, explains functions and\n`return` in full."
        },
        {
          "code": "export function double(n) {\n  return n * 2;\n}",
          "lang": "js"
        },
        {
          "p": "The name in the parentheses, `n` here, is the value handed in. Use that name in\nyour answer."
        },
        {
          "p": "Leave `export` in front of each function. It is what lets the tests and the game\nuse it."
        },
        {
          "p": "Stuck? `console.log(something)` inside a function prints it when the tests or\nthe game run. The tests do not look at what you print, so print as much as you\nlike."
        },
        {
          "h": "The parts"
        },
        {
          "p": "The comment above each function says what it receives and what it returns,\nwith examples. When a test fails, its message says what came back and what to\nlook at."
        },
        {
          "h": "Part 1 · Booleans — `src/booleans.js`"
        },
        {
          "p": "`not`, `and` and `or` do what `!`, `&&` and `||` do. The game uses them to\ndecide whether the hero can act this turn and whether she needs a potion."
        },
        {
          "h": "Part 2 · Numbers — `src/numbers.js`"
        },
        {
          "p": "`remainder(7, 4)` is `3`: what is left over after dividing, not how many times\nit fits. The game uses it to work out whose turn it is. Players take turns in a\ncircle, and the remainder of the turn number divided by the number of players\nsays where in the circle you are."
        },
        {
          "p": "`flipSign(5)` is `-5`, and `flipSign(-5)` is `5`. The game uses it to turn a\ntrap's damage into a change in health."
        },
        {
          "h": "Part 3 · Strings — `src/strings.js`"
        },
        {
          "p": "`firstAndLastCharacter(\"JavaScript\")` is `\"Jt\"`. The door in the game opens for\nthe first and last letters of the hero's name."
        },
        {
          "p": "`snakecase(\"JavaScript Rules!\")` is `\"javascript_rules!\"`. The game names its\nsave file with it. This one uses a method the lessons have not shown:\n`text.replaceAll(\" \", \"_\")` gives a copy of `text` with every space swapped for\nan underscore."
        },
        {
          "h": "Part 4 · Nothing — `src/nothing.js`"
        },
        {
          "p": "`isNull` asks \"is this `null`?\", `isUndefined` asks \"is this `undefined`?\", and\n`isNil` asks \"is this either one?\". In the game, the hero's shield broke, so\nher shield is `null`; she never learned a spell, so her spell is `undefined`.\n`0`, `\"\"` and `false` are values that are *there*, and all three functions return\n`false` for them."
        },
        {
          "h": "Done means"
        },
        {
          "list": [
            "`npm test` passes every test.",
            "`npm start` prints a report with no `undefined` and no `NaN` in it."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/primitives-game"
    },
    "types-assignment": {
      "kind": "assignment",
      "title": "Values, one at a time",
      "lede": "Eight small functions, each about one thing JavaScript does with a value: name its type, ask arithmetic and comparison questions that answer true or false, check a code's text, convert on purpose, and keep \"nothing\" apart from zero and false. Every answer is one line, with no if.",
      "exercises": [
        "types.location",
        "ops.arithmetic",
        "ops.comparison",
        "ops.logical",
        "values.strings",
        "types.conversion",
        "types.equality",
        "types.truthiness",
        "types.nullability"
      ],
      "repo": {
        "name": "cs140-types",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "Eight small functions, each about one thing JavaScript does with a value:\nwhat type it is, what arithmetic and comparisons evaluate to, checking text, and\ntelling \"nothing\" apart from zero and false."
        },
        {
          "p": "Everything here uses numbers, strings, booleans, `null` and `undefined` — the\nvalues from the *Values, types and operators* module. Every answer is one line:\na `return` and one expression. None of them needs `if`."
        },
        {
          "h": "What each part leans on"
        },
        {
          "table": {
            "head": [
              "Part",
              "Lessons"
            ],
            "rows": [
              [
                "1 `typeName`",
                "Where the type actually lives"
              ],
              [
                "2 `isWholeNumber`",
                "Arithmetic operators; Comparing and combining"
              ],
              [
                "3 `isInRange`",
                "Comparing and combining"
              ],
              [
                "4 `isValidCode`",
                "Working with strings; Comparing and combining"
              ],
              [
                "5 `isCorrect`",
                "Conversion — when a value does not fit; Equality: == versus ==="
              ],
              [
                "6 `wasAnswered`",
                "When there is nothing there; Truthiness"
              ],
              [
                "7 `labelOf`",
                "When there is nothing there; Conversion — when a value does not fit"
              ],
              [
                "8 `formatPrice`",
                "Arithmetic operators; Working with strings"
              ]
            ]
          }
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-types-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/types-assignment), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the tests:  ```sh npm test ```  Every test fails at first. That is the starting point, not a problem."
          ],
          "ordered": true
        },
        {
          "p": "While you work on one task, run just its tests:"
        },
        {
          "code": "node --test test/2-whole-number.test.js",
          "lang": "sh"
        },
        {
          "p": "The same tests run on GitHub every time you push. The **Actions** tab shows\nthe result."
        },
        {
          "h": "How to write each answer"
        },
        {
          "p": "All eight functions are in `src/values.js`, already written except for their\nbodies, the part between the braces. Each one looks like this:"
        },
        {
          "code": "export function double(n) {\n  // TODO: put your code here\n}",
          "lang": "js"
        },
        {
          "p": "Write your answer between the braces, and put `return` in front of it —\n`return` sends the value out of the function so the tests can check it. The\nFunctions module, later in the course, explains functions and `return` in\nfull."
        },
        {
          "code": "export function double(n) {\n  return n * 2;\n}",
          "lang": "js"
        },
        {
          "p": "The name in the parentheses — `n` here — is the value the test hands in. Use\nthat name in your answer. You can also make your own names with `const` inside\nthe braces, as you would anywhere else."
        },
        {
          "p": "Several tasks return `true` or `false`. A comparison already gives one —\n`n > 3` *is* `true` or `false` — so return it directly: `return n > 3;`.\nJoin two questions with `&&` (both must be true) or `||` (either will do)."
        },
        {
          "p": "Leave `export` in front of each function. It is what lets the tests use it."
        },
        {
          "p": "Stuck? `console.log(something)` inside a function prints it when the tests\nrun. The tests do not look at what you print, so print as much as you like."
        },
        {
          "h": "The tasks"
        },
        {
          "p": "The comment above each function in `src/values.js` says what it receives and\nwhat it returns, with examples. The tests say the rest: each failure message\nsays what came back and what to look at."
        },
        {
          "h": "1. Name the type — `typeName(value)`"
        },
        {
          "p": "Return what `typeof` says. That includes `null`, which `typeof` calls\n`\"object\"` — a mistake in the language that was never fixed. Return\n`\"object\"` for `null` too, just as `typeof` does."
        },
        {
          "h": "2. Is it whole? — `isWholeNumber(n)`"
        },
        {
          "p": "`4` is whole; `4.5` is not. The remainder operator `%` gives what is left\nover after dividing: a whole number divided by 1 leaves nothing over."
        },
        {
          "h": "3. In range? — `isInRange(n, low, high)`"
        },
        {
          "p": "`true` when `n` is at least `low` **and** at most `high`. Both ends count."
        },
        {
          "h": "4. A ticket code — `isValidCode(code)`"
        },
        {
          "p": "Exactly 6 characters, starting with `\"WW\"`, with no lowercase letters. Three\nchecks, joined with `&&`. A string with no lowercase letters is unchanged by\n`toUpperCase()`."
        },
        {
          "h": "5. Mark an answer — `isCorrect(answer, expected)`"
        },
        {
          "p": "`answer` is text a student typed; `expected` is a number. Convert `answer` to\na number, then compare it with `expected` using `===`. A blank answer is never correct, even when the right answer\nis `0` — and `Number(\"\")` is `0`, so check for a blank first."
        },
        {
          "h": "6. Was it answered? — `wasAnswered(value)`"
        },
        {
          "p": "`undefined`, `null` and `\"\"` mean no answer. Everything else is an answer —\nincluding `0` and `false`. Both are falsy, so a truthiness test would treat\nthem as no answer."
        },
        {
          "h": "7. Show a setting — `labelOf(value)`"
        },
        {
          "p": "Any value as text — `0` gives `\"0\"`, `false` gives `\"false\"` — except that\n`null` and `undefined` give `\"(none)\"`. `??` replaces only `null` and\n`undefined`; `||` would replace `0` and `false` too."
        },
        {
          "h": "8. Cents to dollars — `formatPrice(cents)`"
        },
        {
          "p": "`1205` gives `\"$12.05\"`, and `1250` gives `\"$12.50\"`, not `\"$12.5\"`.\n`number.toFixed(2)` gives text with exactly two digits after the dot."
        },
        {
          "h": "Done means"
        },
        {
          "p": "`npm test` passes every test."
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/types-assignment"
    },
    "flow-calendar": {
      "kind": "assignment",
      "title": "Calendar dates: if, switch, the ternary operator, && and ??",
      "lede": "Six small functions for a calendar: format a day count, apply the leap-year rule, name a month, count its days, check that a date exists, and fix a teammate's bug where || treats 0 as missing. A small calendar program uses every one.",
      "exercises": [
        "flow.branching",
        "flow.multiway",
        "ops.logical",
        "flow.ternary",
        "flow.short-circuit"
      ],
      "repo": {
        "name": "cs140-flow-calendar",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "Five small functions that each choose between paths, and one bug to fix. The\nfive functions answer these questions: is a count written \"1 day\" or\n\"29 days\", is this year a leap year, what is this month called, how many days\ndoes this month have, and does this date exist? You make the choices with the\nternary operator, with `if` and `else`, with a `switch` statement and with\n`&&`. The sixth part is a function a teammate wrote, with one bug in it: you\nfind the bug and fix it. All of this comes from the lessons of the\n*Branching* module."
        },
        {
          "p": "Together the functions make a small toolkit. The program in `src/calendar.js`\nuses all of them to print facts about a few months, years and dates. You\nwrite the toolkit; the calendar program is already written. Each function you\nfinish fixes part of the calendar's output. When all six parts are done,\n`npm start` prints this:"
        },
        {
          "code": "=== Calendar ===\n2028 is a leap year: true\n2100 is a leap year: false\n2000 is a leap year: true\n\nJanuary 2028: 31 days\nFebruary 2028: 29 days (leap year)\nApril 2028: 30 days\nFebruary 2026: 28 days\nFebruary 2100: 28 days\nFebruary 2000: 29 days (leap year)\n\nOn October 30, 2028: 1 day left in October\nMonth 13: Unknown month, 0 days\n\nFebruary 29, 2028 exists: true\nFebruary 29, 2026 exists: false\nApril 31, 2028 exists: false\nDecember 31, 2028 exists: true\n\nRent: remind 3 days before\nBirthday: remind 7 days before\nDentist: remind 0 days before",
          "lang": "text"
        },
        {
          "h": "What each part leans on"
        },
        {
          "list": [
            "`formatDayCount` — *The ternary operator*",
            "`isLeapYear` — *The if statement*, and *What &&, || and ?? evaluate to*",
            "`monthName` — *Choosing among many paths*",
            "`daysInMonth` — *Choosing among many paths*",
            "`isValidDate` — *Comparing and combining*, and *What &&, || and ?? evaluate to*",
            "`reminderDays`, fixing a bug — *What &&, || and ?? evaluate to*"
          ],
          "ordered": true
        },
        {
          "p": "Parts 1 to 5 are in one file, `src/dates.js`. Part 6 is in `src/reminders.js`.\nEvery part also leans on *Writing a function's answer*. That lesson shows\nwhere parameters such as `month` and `year` get their values when a function\nis called, and why a function that answers by calling itself with the same\nvalues never returns an answer: it stops with a RangeError."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-flow-calendar-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/flow-calendar), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the calendar:  ```sh npm start ```  Most of the output says `undefined`. A function returns `undefined` when the path it took has no `return`, and none of your five functions has a `return` yet. That is the expected starting point.",
            "Run the tests:  ```sh npm test ```  Almost every test fails at first. That is the starting point too, not a problem."
          ],
          "ordered": true
        },
        {
          "p": "While you work on one part, run just its tests:"
        },
        {
          "code": "node --test test/2-leap-year.test.js",
          "lang": "sh"
        },
        {
          "p": "The same tests run on GitHub every time you push. The **Actions** tab shows\nthe result."
        },
        {
          "h": "Every path needs a return"
        },
        {
          "p": "A function that chooses has more than one path through it, and each path\nhas to reach a `return`. Here is one that chooses with `if` and `else`:"
        },
        {
          "code": "export function sign(n) {\n  if (n < 0) {\n    return \"negative\";\n  } else {\n    return \"not negative\";\n  }\n}",
          "lang": "js"
        },
        {
          "p": "Suppose you leave out the `else` and its `return`. Then `sign(5)` checks\n`5 < 0`, which is `false`, so it skips the block and reaches the end of the\nfunction without running a `return`. It returns `undefined`. When a test\nreceives `undefined`, its message names the call that returned it, such as\n`sign(5)`. Follow that input through your function by hand to find the path\nthat has no `return`."
        },
        {
          "p": "There is a second way to write a function that chooses. Declare a variable\nwith `let` before the `if`, have each branch set that variable, and write one\n`return` after the `if` that returns the variable. *The if statement* shows both ways. Either way is fine here,\nas long as every path through the function ends up returning a value."
        },
        {
          "p": "Leave `export` in front of each function. `export` is what lets the tests and\nthe calendar use the function."
        },
        {
          "p": "Stuck? Put `console.log(something)` inside a function, and it prints the value\nof `something` each time the tests or the calendar run that line. The tests do\nnot look at what you print, so print as much as you like."
        },
        {
          "h": "The parts"
        },
        {
          "p": "The comment above each function says what the function takes and what it\nreturns, with examples. When a test fails, its message says what came back and what to\nlook at."
        },
        {
          "h": "Part 1 · `formatDayCount`"
        },
        {
          "p": "`formatDayCount(1)` is `\"1 day\"`, and `formatDayCount(29)` is `\"29 days\"`.\nEvery number except 1 takes the plural, \"days\", and that includes 0:\n`formatDayCount(0)` is `\"0 days\"`. Write `formatDayCount` with the ternary\noperator (`condition ? a : b`), which chooses between two values in one line.\nThe calendar uses `formatDayCount` for every count of days it prints."
        },
        {
          "h": "Part 2 · `isLeapYear`"
        },
        {
          "p": "A leap year has 366 days, with a 29th of February. The rule has three steps:"
        },
        {
          "list": [
            "A year divisible by 4 is a leap year.",
            "A century, a year divisible by 100, is an exception: it is not a leap year.",
            "A century that is also divisible by 400 is an exception to the exception: it is a leap year."
          ]
        },
        {
          "p": "So 2028 is a leap year, 2100 is not, and 2000 is."
        },
        {
          "p": "The first step is written for you: if the year is not divisible by 4, the\nfunction returns `false` straight away. Only years divisible by 4 reach the\ncode you write, and your code applies the century rule and the 400 rule to\nthem. Return the boolean `true` or `false`, not some other value that\nhappens to be truthy: the tests check for a boolean."
        },
        {
          "h": "Part 3 · `monthName`"
        },
        {
          "p": "`monthName(1)` is `\"January\"`, and `monthName(12)` is `\"December\"`. Write\n`monthName` with a `switch`, one `case` per month. For any other number,\n`monthName` returns `\"Unknown month\"`. That answer goes in the `default`,\nwhich runs when no `case` matches."
        },
        {
          "p": "A `switch` compares its value with each `case` using `===`, so a `case` only\nmatches a value of the same type. `month` arrives as a number, so write each\n`case` as a number: `case 1:`, not `case \"1\":`."
        },
        {
          "h": "Part 4 · `daysInMonth`"
        },
        {
          "p": "`daysInMonth(4, 2026)` is `30`. April, June, September and November have 30\ndays. February has 28, or 29 in a leap year. The other seven months have 31.\nWrite `daysInMonth` with a `switch`. Months with the same number of days can\nshare one `return`: stack their `case` labels one after another, with nothing\nbetween them, and put the `return` after the last label. For a month number\noutside 1 to 12, return `0` from the `default`."
        },
        {
          "p": "For February, call your own `isLeapYear` from Part 2. Calling a different\nfunction you wrote is fine, and it keeps the leap-year rule in one place. What\ndoes not work is a function that calls *itself* with the same values: a call\nto `daysInMonth` inside `daysInMonth` never returns an answer, and stops with\na RangeError (\"Maximum call stack size exceeded\")."
        },
        {
          "h": "Part 5 · `isValidDate`"
        },
        {
          "p": "`isValidDate(29, 2, 2028)` is `true`, because 2028 is a leap year, and\n`isValidDate(29, 2, 2026)` is `false`. A date exists when the month is from 1\nto 12 and the day is from 1 to the number of days in that month. Each of\nthose is two comparisons, a lower bound and an upper one, so the whole check\nis four comparisons combined with `&&`. You can write them as one expression,\nor as an `if` with a `return` in each branch; either is fine."
        },
        {
          "p": "For the number of days in the month, call your own `daysInMonth` from Part 4.\nIt already knows about leap years, because it calls `isLeapYear`. Calling\nyour own other functions is fine: each rule then lives in one place."
        },
        {
          "p": "Watch the ends of each range. The last day of a month exists, and so does\nmonth 12; day 0 and month 13 do not. The tests check each end."
        },
        {
          "h": "Part 6 · `reminderDays`: fix a teammate's bug"
        },
        {
          "p": "This part is different. You do not write a function: you find and fix a bug\nin one that someone else wrote. A teammate wrote `reminderDays` in\n`src/reminders.js`. It returns how many days before an event to send its\nreminder. `null` or `undefined` means the person made no choice, so the\ndefault of 7 days applies; any number they chose is used as it is, and 0\nmeans on the day itself."
        },
        {
          "p": "The function is complete, and one line is wrong. Run its tests:"
        },
        {
          "code": "node --test test/6-reminder-days.test.js",
          "lang": "sh"
        },
        {
          "p": "One of them fails. Read its message, work out why the line returns what it\ndoes for that input, and fix that one line. *What &&, || and ?? evaluate to*\nexplains what is going on. Leave the rest of the function as it is."
        },
        {
          "h": "Done means"
        },
        {
          "list": [
            "`npm test` passes every test.",
            "`npm start` prints the calendar above, with no `undefined` and no `NaN` in it."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/flow-calendar"
    },
    "flow-loops": {
      "kind": "assignment",
      "title": "Loops that build, count and search",
      "lede": "Four functions, one loop each: the reps logged on a whiteboard, counted a character at a time, the years a savings account needs to reach a goal, the first vowel in a word, and a kilogram-to-pound table. A lifting club's weekly sheet uses every one: finish a function, run the sheet, and watch its section come right.",
      "exercises": [
        "flow.for-of",
        "flow.while",
        "flow.for",
        "flow.break-continue",
        "flow.loop-forms"
      ],
      "repo": {
        "name": "cs140-flow-loops",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "Four small functions, each built around one loop. The loop in `totalReps`\nwalks a string one character at a time and counts. The loop in `yearsToGoal`\ncounts how many iterations it takes to reach a goal. The loop in\n`firstVowelAt` searches a word and stops as soon as it finds what it is\nlooking for. The loop in `kgTable` builds up text a line at a time."
        },
        {
          "p": "The program in `src/club-sheet.js` uses all four functions to work out a\nlifting club's weekly sheet. The sheet shows the reps members logged this week, a\nkilogram-to-pound table for the dumbbell rack, how many years the club must\nsave for a new squat rack, and the locker tags for new members. You write the\nfunctions; the club sheet program is already written. Each function you finish\nfixes one section of the sheet."
        },
        {
          "h": "What each part leans on"
        },
        {
          "table": {
            "head": [
              "Part",
              "File",
              "Lessons"
            ],
            "rows": [
              [
                "1 `totalReps`",
                "`src/reps.js`",
                "The for…of loop; The if statement"
              ],
              [
                "2 `yearsToGoal`",
                "`src/savings.js`",
                "The while loop; Arithmetic operators"
              ],
              [
                "3 `firstVowelAt`",
                "`src/vowels.js`",
                "The for loop; Leaving a loop early: break and continue; Working with strings"
              ],
              [
                "4 `kgTable`",
                "`src/weights.js`",
                "The for loop; Arithmetic operators"
              ]
            ]
          }
        },
        {
          "p": "Every part also leans on *Writing a function's answer*."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-flow-loops-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/flow-loops), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the club sheet:  ```sh npm start ```  Every section of the sheet says `undefined`. A function returns `undefined` when it has no `return`, and none of your four functions has a `return` yet. That is the expected starting point.",
            "Run the tests:  ```sh npm test ```  Every test fails at first. That is the starting point too, not a problem."
          ],
          "ordered": true
        },
        {
          "p": "While you work on one part, run just its tests:"
        },
        {
          "code": "node --test test/2-savings.test.js",
          "lang": "sh"
        },
        {
          "p": "The same tests run on GitHub every time you push. The **Actions** tab shows\nthe result."
        },
        {
          "h": "How to write each answer"
        },
        {
          "p": "Each function is already written except for the code between its braces.\nWrite your code there, where it says `// TODO: put your code here`. Leave\n`export` in front of each function: `export` is what lets the tests and the\nclub sheet use the function."
        },
        {
          "p": "Each of these functions has a loop in it. There are two ways to send the\nfunction's answer back, and both work:"
        },
        {
          "list": [
            "keep the answer in a `let` variable, change it as the loop runs, and `return` it after the loop; or",
            "`return` from inside the loop the moment you have the answer."
          ]
        },
        {
          "p": "Whichever you choose, **every path through the function has to reach a `return`**,\nincluding the path where the loop finishes without finding anything. A path\nwith no `return` returns `undefined`, and a failing test's message names the\ninput that took that path."
        },
        {
          "p": "Stuck? Put `console.log(something)` inside a loop, and it prints the value of\n`something` on every iteration, which shows you what the loop is doing. The tests do not look at what you print, so\nprint as much as you like."
        },
        {
          "h": "The parts"
        },
        {
          "p": "The comment above each function, in its own file, says what the function\nreceives and what it returns, with examples. When a test fails, its message\nsays what came back and what to look at. When a message shows text, each space\nappears as `·` and each line end, the newline character `\"\\n\"`, appears as\n`⏎`. That way an extra space or line end is easy to see."
        },
        {
          "p": "If a loop never stops, the tests stop it after one second and say so in the\nmessage. The club sheet program has no such limit. If `npm start` seems to\nhang, a loop is probably running forever: press **Ctrl+C** to stop the\nprogram."
        },
        {
          "h": "Part 1 · The week's reps — `src/reps.js`"
        },
        {
          "p": "Members log their training on the club's whiteboard: one `|` for each rep,\nwith a space between sets. `\"||||| |||| |||\"` is three sets, of 5, 4 and 3\nreps. `totalReps` returns how many reps a log holds: here, `12`. The spaces\nonly separate the sets, so they are not counted. An empty log, `\"\"`, is `0`\nreps."
        },
        {
          "p": "Each iteration needs one character, and never its position, so this is a job\nfor a `for…of` loop. Count a character when it is a `|`."
        },
        {
          "h": "Part 2 · Years to a savings goal — `src/savings.js`"
        },
        {
          "p": "The club is saving for a new squat rack. Its savings earn **interest**: once a\nyear, the bank adds a percentage of what is saved. `yearsToGoal` takes three\nnumbers:"
        },
        {
          "list": [
            "`savings`, the amount saved at the start;",
            "`rate`, the interest rate, as a percentage: 10 means 10% a year;",
            "`goal`, the price of the rack."
          ]
        },
        {
          "p": "It returns how many whole years it takes for the savings to reach the goal."
        },
        {
          "p": "**One year's interest** is `rate` percent of what is saved, which you write as\n`savings * rate / 100`. Add it to the savings, and that is the end of one year.\n(JavaScript's `%` is not percent: it is the remainder operator, from\n*Arithmetic operators*.) Here is `yearsToGoal(1000, 10, 1331)`, a year at a\ntime:"
        },
        {
          "table": {
            "head": [
              "Year",
              "Saved at the start",
              "Interest: saved × 10 / 100",
              "Saved at the end"
            ],
            "rows": [
              [
                "1",
                "1000",
                "100",
                "1100"
              ],
              [
                "2",
                "1100",
                "110",
                "1210"
              ],
              [
                "3",
                "1210",
                "121",
                "1331"
              ]
            ]
          }
        },
        {
          "p": "After 3 years the savings are 1331, which is the goal. Reaching the goal\nexactly counts, so the answer is `3`. If `savings` already reaches `goal`\nbefore any years pass, the answer is `0`."
        },
        {
          "p": "Before the loop starts, you do not know how many years it will take; finding\nthat out is the function's job. A `while` loop suits a job like that: it keeps\nrepeating for as long as its condition is `true`, however many iterations that\ntakes."
        },
        {
          "h": "Part 3 · The first vowel — `src/vowels.js`"
        },
        {
          "p": "`firstVowelAt(\"Grace\")` is `2`: G is at index 0, r at 1, a at 2. A vowel is a,\ne, i, o or u, in lowercase or in capitals; \"y\" is not one here. A word with no\nvowel gives `-1`."
        },
        {
          "p": "Go through the word's indexes in order, starting at 0. The first vowel you\nfind is the answer, so there is no reason to keep looking after it.\n*Leaving a loop early: break and continue* shows how to leave a loop at that point.\n`\"aeiou\".includes(letter)` is `true` when `letter` is one of those five\nlowercase vowels. A capital such as the I in \"Ingrid\" matches only after you\nchange it to lowercase with `toLowerCase()`, from *Working with strings*."
        },
        {
          "h": "Part 4 · A kilogram-to-pound table — `src/weights.js`"
        },
        {
          "p": "`kgTable(1, 5, 2)` is"
        },
        {
          "code": "1 kg = 2.2 lb\n3 kg = 6.6 lb\n5 kg = 11 lb",
          "lang": "text"
        },
        {
          "p": "with the lines joined by `\"\\n\"` and no `\"\\n\"` after the last line. The table\nstarts at `from` kilograms and goes up by `step` on each line. It includes\n`to` when the steps land on it exactly, and it never goes past `to`."
        },
        {
          "p": "One kilogram is 2.2 pounds. But in JavaScript, `3 * 2.2` is\n`6.6000000000000005`, because decimal numbers are not stored exactly\n(*Arithmetic operators* explains why). The comment above `kgTable` shows how\nto round the pounds to one decimal place. The columns do not need to line up:\nput one space on each side of `kg`, `=` and `lb`."
        },
        {
          "h": "Done means"
        },
        {
          "list": [
            "`npm test` passes every test.",
            "`npm start` prints a sheet with no `undefined` in it."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/flow-loops"
    },
    "flow-patterns": {
      "kind": "assignment",
      "title": "Number patterns with loops inside loops",
      "lede": "Four functions, patternA to patternD, each building a pattern of numbers row by row with a loop inside a loop: rows that grow, rows that shrink, and two that line up on the right with spaces in front. A small gallery prints all four, so each finished function fills in one block.",
      "exercises": [
        "flow.nesting",
        "flow.for",
        "flow.branching"
      ],
      "repo": {
        "name": "cs140-flow-patterns",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "Four functions, `patternA` to `patternD`. Each one builds a pattern of\nnumbers, row by row, and returns it as a string. Here they are at size 6:"
        },
        {
          "code": "Pattern A          Pattern B          Pattern C          Pattern D\n1                  1 2 3 4 5 6                  1        1 2 3 4 5 6\n1 2                1 2 3 4 5                  2 1          1 2 3 4 5\n1 2 3              1 2 3 4                  3 2 1            1 2 3 4\n1 2 3 4            1 2 3                  4 3 2 1              1 2 3\n1 2 3 4 5          1 2                  5 4 3 2 1                1 2\n1 2 3 4 5 6        1                  6 5 4 3 2 1                  1",
          "lang": "text"
        },
        {
          "p": "Each pattern is a loop inside a loop. The outer loop makes the rows. For\nevery row, an inner loop makes that row: its numbers and, in C and D, the\nspaces in front of them."
        },
        {
          "p": "You may notice that each row of pattern A is the row before it plus one\nnumber on the end, so A could be built with one loop that keeps extending the\nlast row. That shortcut does not work for B, C or D. Build each row from an\nempty string with its own inner loop, in A too. Practising that is the point\nof this homework."
        },
        {
          "p": "The program in `src/gallery.js` prints all four patterns. You write the\npattern functions; the gallery program is already written. Each function you\nfinish fills in one of the gallery's four blocks."
        },
        {
          "h": "What each part leans on"
        },
        {
          "p": "The parts are in order: each part adds one new thing to the part before it,\nand the **Adds** column says what."
        },
        {
          "table": {
            "head": [
              "Part",
              "Adds",
              "Lessons"
            ],
            "rows": [
              [
                "A `patternA`",
                "an inner loop that counts up, one number more each row",
                "Loops inside loops"
              ],
              [
                "B `patternB`",
                "rows that get shorter",
                "Loops inside loops"
              ],
              [
                "C `patternC`",
                "spaces in front, and numbers counting down",
                "Loops inside loops"
              ],
              [
                "D `patternD`",
                "spaces in front, and rows that get shorter",
                "Loops inside loops"
              ]
            ]
          }
        },
        {
          "p": "Every part also leans on *The for loop* (a `for` loop that\nbuilds up a string, and spotting an off-by-one) and on *The if statement* (adding a space only between two numbers)."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-flow-patterns-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/flow-patterns), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the gallery:  ```sh npm start ```  Each pattern says `undefined`. A function returns `undefined` when it has no `return`, and none of your four functions has a `return` yet. That is the expected starting point.  To see the patterns at another size, put the size after `--`:  ```sh npm start -- 3 ```",
            "Run the tests:  ```sh npm test ```  Every test fails at first. That is the starting point too, not a problem."
          ],
          "ordered": true
        },
        {
          "p": "While you work on one pattern, run just its tests:"
        },
        {
          "code": "node --test --test-name-pattern=\"Pattern C\"",
          "lang": "sh"
        },
        {
          "p": "The same tests run on GitHub every time you push. The **Actions** tab shows\nthe result."
        },
        {
          "h": "The rules for every pattern"
        },
        {
          "p": "The tests compare your string with the expected one character by character,\nso these matter:"
        },
        {
          "list": [
            "Rows are joined with `\"\\n\"`. There is no `\"\\n\"` after the last row.",
            "Numbers in a row are separated by **one** space. No space before the first number in A and B, and no space after the last number in any row.",
            "In C and D, the rows line up on the right, so the shorter rows start with spaces. A row that is missing some numbers puts two spaces in place of each missing one: one for the digit, and one for the space that would have followed it.",
            "`size` is a whole number from 0 to 9, so every number is one digit. Size 0 gives `\"\"`: no rows at all."
          ]
        },
        {
          "p": "A space at the end of a row is invisible when the gallery prints it. The\ntests make it visible: in a failing test's message, every space appears as `·`\nand every line end, the newline character `\"\\n\"`, appears as `⏎`. Here is\n`patternC(3)`, written as the string it must return:"
        },
        {
          "code": "\"    1\\n  2 1\\n3 2 1\"",
          "lang": "js"
        },
        {
          "p": "The comment above each function shows its pattern at size 3, both as a\npicture and as a string like this one."
        },
        {
          "p": "Build the spaces with a loop, the same way you build the numbers. Do not use\nthe string methods `padStart`, `padEnd` or `repeat`: the lessons have not shown them, and making\nthe spaces with a loop is part of what this homework practises. The tests\ncannot see how you built your string, so this rule is yours to keep."
        },
        {
          "h": "How to write each answer"
        },
        {
          "p": "Each function is already written except for the code between its braces:"
        },
        {
          "code": "export function patternA(size) {\n  // TODO: put your code here\n}",
          "lang": "js"
        },
        {
          "p": "Write your loops between the braces. Build the pattern in a string that\nstarts empty, add to it with `+=`, and `return` it at the end. `return` sends\nthe string out of the function, so the tests and the gallery can use it.\n`console.log` only prints the string, and the tests do not see what you\nprint."
        },
        {
          "p": "Leave `export` in front of each function. `export` is what lets the tests and\nthe gallery use the function."
        },
        {
          "p": "Stuck? Trace your function by hand. Pick size 3, and write down the value of\neach loop's counter variable every time the inner loop's body runs. If your\ncounters are called `row` and `n`, then `console.log(row, n)` inside the inner\nloop prints those same values while the tests or the gallery run."
        },
        {
          "h": "The parts"
        },
        {
          "p": "When a test fails, its message shows your pattern next to the expected one,\nsays where they first differ, and names the mistake when it recognises it: a\nspace at the end of a row, a `⏎` after the last row, a row too many or too\nfew, a row that carries on from the row before, or spaces in front that are\noff by one."
        },
        {
          "h": "Part A · `patternA`"
        },
        {
          "p": "Row 1 counts from 1 to 1, row 2 from 1 to 2, and the last row from 1 to\n`size`. The outer loop counts the rows; the inner loop counts the numbers in\none row. How far the inner loop counts depends on which row the outer loop is\non."
        },
        {
          "h": "Part B · `patternB`"
        },
        {
          "p": "The first row counts from 1 to `size`, and each row after it stops one number\nsooner. As in pattern A, the inner loop counts up from 1. What changes is the\nnumber it stops at."
        },
        {
          "h": "Part C · `patternC`"
        },
        {
          "p": "Each row is two pieces: first the spaces, then the numbers counting down to"
        },
        {
          "list": [
            "Each piece gets its own inner loop, and the two inner loops run one after the other inside the loop that makes the rows. How many gaps does row 1 need at size 3? How many does row 3 need?"
          ],
          "ordered": true
        },
        {
          "h": "Part D · `patternD`"
        },
        {
          "p": "Pattern D is pattern B lined up on the right. Each row has one number fewer\nthan the row above it, and one more gap in front."
        },
        {
          "h": "Done means"
        },
        {
          "list": [
            "`npm test` passes every test.",
            "`npm start` prints all four patterns, with no `undefined`."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/flow-patterns"
    },
    "arrays-scoreboard": {
      "kind": "assignment",
      "title": "Arrays at game night: reading, walking, changing and nesting",
      "lede": "Seven functions on arrays: read a line of scores into numbers, tally a player's rounds, find first and second place, insert and remove by hand without splice, total each round of a jagged board, and add two nights' boards without changing either. A game-night scoreboard uses every one.",
      "exercises": [
        "arr.basics",
        "arr.change",
        "arr.query",
        "arr.traversal",
        "flow.mutation-during-traversal",
        "arr.nested"
      ],
      "repo": {
        "name": "cs140-arrays-scoreboard",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "Seven small functions, each built around an array. `parsePoints` turns a\nline of text into an array of numbers. `tally` and `topTwo` walk an array and\nwork out a result from it. `insertAt` and `removeAt` change an array in\nplace, doing by hand what `splice` does. `roundTotals` and\n`addNights` work on an array of arrays: a grid of scores, with one row for\neach player."
        },
        {
          "p": "The program in `src/game-night.js` uses all seven to keep score at a game\nnight: the turn order as players arrive and leave, each player's scores and\ntally, the podium, the points scored in each round, and a two-night\ntournament. You write the functions; the scoreboard program is already\nwritten. Each function you finish fixes one part of the scoreboard, and\nseveral parts build on earlier ones: the tallies are worked out from the\nscores that Part 1 reads."
        },
        {
          "h": "What each part leans on"
        },
        {
          "table": {
            "head": [
              "Part",
              "Lessons"
            ],
            "rows": [
              [
                "1 `parsePoints`",
                "Finding, slicing and joining; Adding and removing elements; Conversion — when a value does not fit"
              ],
              [
                "2 `tally`",
                "Walking an array; Lists of values"
              ],
              [
                "3 `topTwo`",
                "Walking an array; Lists of values"
              ],
              [
                "4 `insertAt`",
                "Adding and removing elements; Mutating while traversing"
              ],
              [
                "5 `removeAt`",
                "Adding and removing elements; Mutating while traversing"
              ],
              [
                "6 `roundTotals`",
                "Arrays of arrays"
              ],
              [
                "7 `addNights`",
                "Arrays of arrays; Finding, slicing and joining"
              ]
            ]
          }
        },
        {
          "p": "Every part also leans on *Writing a function's answer*, and on the loops from\nthe Loops module."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-arrays-scoreboard-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/arrays-scoreboard), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the scoreboard:  ```sh npm start ```  The scoreboard is mostly `undefined`, and the parts that need an earlier part say which one they are waiting for. A function returns `undefined` when it has no `return`, and none of your seven functions has a `return` yet. That is the expected starting point.",
            "Run the tests:  ```sh npm test ```  All but two of the tests fail at first. That is the starting point too, not a problem. The two that pass check that Parts 4 and 5 do not use certain methods, and they keep passing unless you use one."
          ],
          "ordered": true
        },
        {
          "p": "While you work on one part, run just its tests:"
        },
        {
          "code": "node --test test/4-insert.test.js",
          "lang": "sh"
        },
        {
          "p": "The same tests run on GitHub every time you push. The **Actions** tab shows\nthe result."
        },
        {
          "h": "How to write each answer"
        },
        {
          "p": "Each function is already written except for the code between its braces.\nWrite your code there, where it says `// TODO: put your code here`. Leave\n`export` in front of each function: `export` is what lets the tests and the\nscoreboard use the function."
        },
        {
          "p": "Before you write a part, ask the question from *Finding, slicing and\njoining*: does this function change the array it is given, or return a new\nvalue? A function that changes it is **mutating**, and one that leaves it as\nit was is **non-mutating**. The comment above each function says which."
        },
        {
          "list": [
            "`parsePoints`, `tally`, `topTwo`, `roundTotals` and `addNights` are non-mutating: they leave the arrays they are given as they were, and return a new value. When that value is an array, one way to build it is with `push`, starting from an empty array.",
            "`insertAt` and `removeAt` are mutating: they change the array they are given, in place. They still end with a `return`: `insertAt` returns the new length, as `push` does, and `removeAt` returns the element it removed, as `pop` does."
          ]
        },
        {
          "p": "Whichever it is, **every path through the function has to reach a `return`**.\nA path with no `return` returns `undefined`, and a failing test's message\nnames the input that took that path."
        },
        {
          "p": "Stuck? Put `console.log(something)` inside a loop, and it prints the value of\n`something` on every iteration, which shows you what the loop is doing. The\ntests do not look at what you print, so print as much as you like."
        },
        {
          "h": "The parts"
        },
        {
          "p": "The comment above each function, in its own file, says what the function\nreceives and what it returns, with examples. When a test fails, its message\nsays what came back and what to look at. When a message shows text, each\nspace appears as `·`, so that an extra space is easy to see."
        },
        {
          "p": "If a loop never stops, the tests stop it after one second and say so in the\nmessage. The scoreboard program has no such limit. If `npm start` seems to\nhang, a loop is probably running forever: press **Ctrl+C** to stop the\nprogram."
        },
        {
          "h": "Part 1 · From a line of text to a row of scores — `src/points.js`"
        },
        {
          "p": "The scorekeeper types each player's scores into one line, one score for each\nround, with spaces between them: `\"3 -2 5\"` is three rounds, scoring 3, -2\nand 5. `parsePoints` returns those scores as an array of numbers:\n`[3, -2, 5]`."
        },
        {
          "p": "The scorekeeper types fast, so a line can start or end with a space, and\nthere can be more than one space between two scores. `\" 4  0 -1 \"` is still\nthree scores, `[4, 0, -1]`. The spaces only separate the scores."
        },
        {
          "p": "`split(\" \")` cuts a string at each space and returns the pieces, as text.\n`Number(...)` turns a piece of text into a number. Where two spaces are next\nto each other, though, `split(\" \")` returns an empty string, `\"\"`, between\nthem, and a space at either end of the line leaves an empty string at that\nend. `Number(\"\")` is `0`, not `NaN`, so once converted, an empty piece looks\njust like a real score of 0. Try `\" 4  0\".split(\" \")` in Node to see the\npieces, and decide which of them are scores while they are still text."
        },
        {
          "h": "Part 2 · One player's tally — `src/tally.js`"
        },
        {
          "p": "`tally` takes one player's row of round scores and returns four numbers in\nan array: `[gains, losses, net, average]`."
        },
        {
          "list": [
            "`gains` is how many rounds scored above 0, and `losses` how many scored below 0. A round that scored 0 is neither.",
            "`net` is every round's score added up.",
            "`average` is the net divided by the number of rounds. A round of 0 still counts as a round here."
          ]
        },
        {
          "p": "`tally([3, -2, 0, 5])` is `[2, 1, 6, 1.5]`: two gains, one loss, a net of 6,\nand 6 divided by 4 rounds."
        },
        {
          "p": "A player who played no rounds has an empty row, `[]`, and gets\n`[0, 0, 0, 0]`. An average of no rounds has no value: in JavaScript,\n`0 / 0` is `NaN`. The scoreboard prints 0 for it, so `tally` returns 0."
        },
        {
          "p": "Which position holds which number is something every caller of `tally` has\nto remember: position 2 is the net. The Objects module, next, gives each\nvalue a name instead."
        },
        {
          "h": "Part 3 · First and second place — `src/podium.js`"
        },
        {
          "p": "`topTwo(names, totals)` returns the names of the players with the highest and\nthe second highest totals, as `[first, second]`. The two arrays are parallel:\n`totals[i]` is the total of the player `names[i]`."
        },
        {
          "list": [
            "Totals can be below 0, because a round can lose points.",
            "In a tie, the player who comes first in `names` ranks higher.",
            "With one player, there is no second place, so `second` is `undefined`. With no players, both are."
          ]
        },
        {
          "p": "`topTwo([\"Ada\", \"Ben\", \"Cleo\"], [4, 9, 7])` is `[\"Ben\", \"Cleo\"]`."
        },
        {
          "p": "The loop keeps track of two players as it goes: the leader so far, and the\nsecond so far. Think about what happens to the leader when a higher total\ncomes along."
        },
        {
          "h": "Parts 4 and 5 · Changing the turn order — `src/turns.js`"
        },
        {
          "p": "The players take turns in the order of an array of names. A late arrival\njoins the turn order in the middle, and a player who has to leave is taken\nout. Both functions change the array they are given, in place, the way\n`splice` does, because the scoreboard keeps using that same array all night."
        },
        {
          "p": "**`splice`, `unshift`, `shift`, `slice` and `concat` are off limits in these\ntwo functions.** These two parts rebuild what `splice` does, and each of\nthose five methods would do the moving for you: `unshift` moves every element\nup to make room at the front, `shift` moves every element down, and `slice`\nand `concat` build a new array with the elements already where they go.\n`push` and `pop` are allowed: they make the array one longer or one shorter\nat the end, and move nothing. A test reads `src/turns.js` and names any of\nthe five that `insertAt` or `removeAt` uses."
        },
        {
          "p": "**Part 4.** `insertAt(list, item, index)` puts `item` into `list` at `index`.\nNothing is replaced: every element from `index` on moves up one index, and\nthe array gets one longer. Here is `insertAt(order, \"Cleo\", 2)`, where\n`order` is `[\"Ada\", \"Ben\", \"Dev\"]`:"
        },
        {
          "table": {
            "head": [
              "",
              "index 0",
              "index 1",
              "index 2",
              "index 3"
            ],
            "rows": [
              [
                "Before",
                "Ada",
                "Ben",
                "Dev",
                ""
              ],
              [
                "`\"Dev\"` copied up from index 2 to index 3",
                "Ada",
                "Ben",
                "Dev",
                "Dev"
              ],
              [
                "`\"Cleo\"` written at index 2",
                "Ada",
                "Ben",
                "Cleo",
                "Dev"
              ]
            ]
          }
        },
        {
          "p": "With one element to move, the order of the moves does not matter. With two\nor more, it does. Try `[1, 2, 4, 5]` with `3` at index 2 on paper, moving\nfrom index 2 forwards: what happens to the `5`? *Mutating while traversing*\nis about the same kind of choice."
        },
        {
          "p": "`index` is from 0 up to `list.length`. At `list.length`, the item goes on the\nend. `insertAt` returns the new length, as `push` does."
        },
        {
          "p": "**Part 5.** `removeAt(list, index)` takes the element at `index` out of\n`list`. No gap is left: every element after it moves down one index, and the\narray gets one shorter. It returns the element it removed, as `pop` does:\nthe element itself, not an array holding it (which is what `splice`\nreturns)."
        },
        {
          "p": "`index` is always an index that holds an element, from 0 up to\n`list.length - 1`."
        },
        {
          "p": "Both functions work on an array of anything. The scoreboard uses them on\nnames, and the tests use numbers and whole rows of scores too."
        },
        {
          "h": "Part 6 · Round by round — `src/rounds.js`"
        },
        {
          "p": "A board is an array of arrays: one row for each player, and in each row, one\nscore for each round. Column 0 is round 1. `roundTotals(board)` returns one\ntotal for each round: every player's score for that round, added up."
        },
        {
          "p": "`roundTotals([[1, 2, 3], [4, 5, 6]])` is `[5, 7, 9]`."
        },
        {
          "p": "A player who leaves early has a shorter row, so the rows can have different\nlengths: the board is a **jagged array**, in the words of *Arrays of\narrays*. The board has as many rounds as its longest row, and a row too\nshort to reach a round adds nothing to it. `roundTotals([[2, 1, 4], [3]])`\nis `[5, 1, 4]`. An empty board, `[]`, has no rounds, so the result is `[]`."
        },
        {
          "h": "Part 7 · Two nights, one tournament — `src/nights.js`"
        },
        {
          "p": "A tournament is played over two nights by the same players, in the same\norder, with the same number of rounds each night. `addNights(a, b)` returns\na new board where each element is the sum of the elements at the same row\nand column of `a` and `b`:"
        },
        {
          "code": "addNights([[1, 2, 3], [4, 5, 6]], [[2, 2, 2], [1, 1, 1]])\n// [[3, 4, 5], [5, 6, 7]]",
          "lang": "js"
        },
        {
          "p": "The two boards always have the same shape: the same number of rows, and the\nrow at each index the same length in both. The rows of one board can still\nhave different lengths, as in a jagged array. Both boards can be empty, and\nthen so is the result."
        },
        {
          "p": "Each night's board is still needed afterwards, to show what happened that\nnight. So `addNights` leaves both boards as they were, and builds a new board\nto return. The scoreboard checks that night one's board is unchanged, and\nsays so if it is not."
        },
        {
          "h": "Done means"
        },
        {
          "list": [
            "`npm test` passes every test.",
            "`npm start` prints a scoreboard with no `undefined` in it, and nothing waiting for an earlier part."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/arrays-scoreboard"
    },
    "data-assignment": {
      "kind": "assignment",
      "title": "Recipe box and meal plan",
      "lede": "Build a recipe box that lends out recipes nobody can change, and a meal plan that resizes them, edits its own copies and adds up a shopping list — deciding at every hand-off what to copy, how deep, what to freeze and which collection holds it.",
      "exercises": [
        "data.value-vs-reference",
        "data.argument-passing",
        "data.aliasing-bugs",
        "data.shallow-vs-deep",
        "data.immutability",
        "data.collections",
        "diag.debugger",
        "diag.code-review"
      ],
      "repo": {
        "name": "cs140-data",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "You are building two small modules. A **recipe box** keeps recipes and lends them\nout. A **meal plan** takes recipes from the box, resizes them, lets the planner\nedit them, and adds up a shopping list."
        },
        {
          "p": "Every part of it hands data to another part. The box lends a recipe to the plan.\nThe plan resizes it, and the planner changes it. The shopping list adds it up.\nAt each hand-off you decide the same things: **copy or share, how deep, what to\nfreeze, and which collection holds it.** Get one wrong and nothing throws. A\nvalue just changes somewhere nobody wrote to it. That is what this module is\nabout."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-data-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/data-assignment), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the tests:  ```bash npm test ```  They all fail at first, except a few that pass because an empty answer happens to satisfy them. To run one task's tests only, name the file:  ```bash node --test test/1-box.test.js ```"
          ],
          "ordered": true
        },
        {
          "p": "The tests also run on every push; the **Actions** tab on your repository shows\nthe result. Each failing test says what it expected, what came back, and which\nlesson to look at. It does not say what to write."
        },
        {
          "h": "What is in the repository"
        },
        {
          "table": {
            "head": [
              "File",
              "What it is"
            ],
            "rows": [
              [
                "`src/recipes.js`",
                "The recipe box. Tasks 1 and 2."
              ],
              [
                "`src/plan.js`",
                "The meal plan. Tasks 3 and 4."
              ],
              [
                "`src/freeze.js`",
                "`deepFreeze`, written for you. Read it before Task 1."
              ],
              [
                "`test/`",
                "The tests, one file per task. Read them: they are part of the spec."
              ],
              [
                "`review/favourites.js`",
                "A teammate's code, for the review."
              ],
              [
                "`REVIEW.md`",
                "Where your review goes."
              ]
            ]
          }
        },
        {
          "p": "Each function in `src/` has a comment saying exactly what it must do. Read it\nbefore you start the function."
        },
        {
          "p": "A recipe looks like this:"
        },
        {
          "code": "{\n  id: 'pancakes',\n  title: 'Pancakes',\n  serves: 4,\n  ingredients: [\n    { name: 'flour', qty: 200, unit: 'g' },\n    { name: 'milk',  qty: 300, unit: 'ml' },\n    { name: 'egg',   qty: 2,   unit: '' },\n  ],\n  steps: ['Whisk everything', 'Rest ten minutes', 'Fry'],\n  tags: ['breakfast', 'vegetarian'],\n}",
          "lang": "js"
        },
        {
          "p": "Count the levels. A recipe is an object. Inside it, `ingredients` is an array,\nand inside that each ingredient is an object again. Most of this assignment is\ndeciding which of those levels someone will change."
        },
        {
          "h": "The tasks"
        },
        {
          "p": "Do them in order. Each one has its own test file."
        },
        {
          "h": "Task 1 — Keep what you are given, and lend it safely"
        },
        {
          "p": "In `src/recipes.js`, write `createBox`, `addRecipe` and `getRecipe`."
        },
        {
          "p": "The box must hold on to a recipe that nobody else can change: not the author,\nwho may go on editing their own object, and not anyone the box lends it to.\nBut adding a recipe must not freeze anything the author still holds."
        },
        {
          "list": [
            "Choose what the box keeps its recipes in. You will justify it in question 3.",
            "`getRecipe` lends out the recipe the box keeps — the same object every time.",
            "Tests: `test/1-box.test.js`."
          ]
        },
        {
          "h": "Task 2 — Resize without changing"
        },
        {
          "p": "In `src/recipes.js`, finish `scaleRecipe`. It returns a recipe for a different\nnumber of people. The loop is written for you; what goes inside it is yours."
        },
        {
          "p": "The recipe you are given may be frozen, so you cannot change it — and you must\nnot, even when it is not frozen. Build a new recipe. Copy only the parts that\nchange, and share the rest. If nothing changes, return the original."
        },
        {
          "list": [
            "Tests: `test/2-scale.test.js`."
          ]
        },
        {
          "h": "Task 3 — The planner's own copy"
        },
        {
          "p": "In `src/plan.js`, write `createPlan` and `planMeal`."
        },
        {
          "p": "A plan entry is a resized recipe that the planner is free to edit: swap milk for\noat milk, add blueberries, change a quantity. None of that may reach the box,\nthe recipe passed in, or another entry. Plan the same recipe for Monday and\nFriday, and the two must still be independent."
        },
        {
          "list": [
            "Use your `scaleRecipe`. Then look at what it can return, and decide what `planMeal` has to do before the planner edits it.",
            "There is more than one correct depth here. Question 2 asks what you chose.",
            "Tests: `test/3-plan.test.js`."
          ]
        },
        {
          "h": "Task 4 — The shopping list"
        },
        {
          "p": "In `src/plan.js`, finish `shoppingList`. It adds up every ingredient across the\nplan into one line each, and leaves off what is already in the pantry."
        },
        {
          "p": "The hard part is deciding when two ingredients are **the same line**. They come\nfrom different entries, so they are always different objects. `'Flour'` and\n`'flour'` are one line. `300 ml milk` and `1 cup milk` are two."
        },
        {
          "p": "And the plan must come out of this exactly as it went in. A caller may tick off\na line on the list without changing the plan."
        },
        {
          "list": [
            "The `Map` and the loops are given. The key, and what you store under it, are yours.",
            "Tests: `test/4-shopping.test.js`."
          ]
        },
        {
          "h": "The review"
        },
        {
          "p": "`review/favourites.js` is a teammate's first go at letting users keep favourite\nrecipes. They tried it with one user and it worked. It has **three problems**,\neach one of this module's."
        },
        {
          "p": "Write your review in `REVIEW.md`. For each problem:"
        },
        {
          "list": [
            "**the lines** where it is;",
            "**what goes wrong**, in a sentence or two;",
            "**an input that shows it** — a few lines of code that call the teammate's functions, and what they give;",
            "**the fix**."
          ]
        },
        {
          "p": "Then use the debugger on one of the problems — the one where a value changes on\na line that never names it. Pause inside the teammate's code, add a Watch that\nshows two names are one object, and step until the value changes. Record where\nyou paused, what you watched, and the line where it changed. *Meet the debugger*\nhas the steps; in a Codespace, use the JavaScript Debug Terminal."
        },
        {
          "p": "`REVIEW.md` is read by a person. The tests do not check it."
        },
        {
          "h": "Your answers"
        },
        {
          "p": "Write two or three sentences for each, here, in this file."
        },
        {
          "list": [
            "**Lending without copying.** `getRecipe` hands out the object the box keeps. In *What a function can change*, handing out a module's own object was the bug. Why is it safe here? And what does the box pay for that safety, and when?  *Your answer:*",
            "**How deep `planMeal` copies.** Which levels of an entry did you copy, and why those? Name one part of an entry that could safely be shared with the box instead. Would it still be safe if the recipe passed in was not frozen?  *Your answer:*",
            "**Which collection.** The box, the plan and the pantry each use a different collection. For each, say which one and the question it answers. For the shopping list, say what makes two ingredients the same line — and what the list would do if the key were the ingredient object itself.  *Your answer:*"
          ],
          "ordered": true
        },
        {
          "h": "Done means"
        },
        {
          "list": [
            "`npm test` passes every test.",
            "`REVIEW.md` has all three problems and what the debugger showed.",
            "The three answers above are written."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/data-assignment"
    },
    "functions-assignment": {
      "kind": "assignment",
      "title": "Chat bot command pipeline",
      "lede": "Build the inside of a chat bot: guards that wrap command handlers, a chain of middleware joined into one function, and a method that has to keep its object when you hand it over.",
      "exercises": [
        "fn.first-class",
        "fn.callbacks",
        "fn.receiver",
        "fn.higher-order",
        "fn.composition"
      ],
      "repo": {
        "name": "cs140-functions",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "You are building the inside of a chat bot. People type messages like\n`!add 2 3` or `!ban spammer`. Each message passes through a chain of small\nsteps — write it to a log, slow down anyone sending too fast, count it — and\nthen a command handler replies."
        },
        {
          "p": "Every piece of that is a function handed to another function. The command\ntable holds functions. Each step in the chain is given a function, `next`, and\ndecides whether to call it. The guards you write take a handler and return a\nnew one. The chain itself is one function built from many. And one step is a\nmethod, which has to keep its object when you hand it over."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-functions-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/functions-assignment), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the tests:  ``` npm test ```  There is nothing to install. Almost every test fails at first — that is the starting point. The same tests run on every push, so you can see the result on GitHub too."
          ],
          "ordered": true
        },
        {
          "p": "The files:"
        },
        {
          "table": {
            "head": [
              "File",
              "What it is"
            ],
            "rows": [
              [
                "`src/given.js`",
                "Written by a teammate: `parseCommand`, `createRouter` and `createStats`. Read it; do not change it."
              ],
              [
                "`src/bot.js`",
                "Your work. Each function's contract is in the comment above it."
              ],
              [
                "`src/commands.js`",
                "The bot's commands. They work, and they repeat themselves."
              ],
              [
                "`test/`",
                "The tests. Read them — they are part of the spec."
              ],
              [
                "`review/mute.js`",
                "Code to review. See \"The review\" below."
              ],
              [
                "`REVIEW.md`",
                "Where your review goes."
              ]
            ]
          }
        },
        {
          "p": "The `import` and `export` lines are written for you. Modules get their own\nlessons later; for now, `export` makes a function visible to the tests."
        },
        {
          "p": "Two shapes appear everywhere:"
        },
        {
          "code": "// a handler: one command. Called by the router as handler(args, msg).\n(args, msg) => \"a reply\"\n\n// a middleware: one step every message passes through.\n(msg, next) => {\n  // ... before the rest runs\n  const reply = next(msg);   // runs everything after this step\n  // ... after it\n  return reply;\n}",
          "lang": "js"
        },
        {
          "p": "A message is `{ user: \"ada\", role: \"member\", text: \"!add 2 3\" }`, and `role`\nis `\"member\"` or `\"mod\"`. A reply is a string, or `null` for \"say nothing\"."
        },
        {
          "h": "The tasks"
        },
        {
          "p": "Do them in order. Each has its own test file, so you can see one task pass\nbefore starting the next."
        },
        {
          "h": "Task 1 — Take out the repeated checks"
        },
        {
          "p": "Tests: `test/guards.test.js`"
        },
        {
          "p": "Look at `src/commands.js`. Every handler starts by checking its argument\ncount, and two of them also check the user's role. Only the usage text, the\ncount and the role change from one to the next."
        },
        {
          "p": "Write two functions in `src/bot.js` that each take a handler and return a new\none:"
        },
        {
          "list": [
            "`withUsage(count, usage, handler)` — replies `\"usage: \" + usage` when the number of arguments is wrong, and otherwise calls `handler`.",
            "`onlyFor(role, handler)` — replies `\"only a \" + role + \" can do that\"` to anyone else, and otherwise calls `handler`."
          ]
        },
        {
          "code": "const add = withUsage(2, \"!add <a> <b>\", args => String(Number(args[0]) + Number(args[1])));\n\nadd([\"2\", \"3\"], msg);   // \"5\"\nadd([\"2\"], msg);        // \"usage: !add <a> <b>\"",
          "lang": "js"
        },
        {
          "p": "Then rewrite `src/commands.js` so that no handler does its own checking. Each\none should be left with only the work that makes it different. The tests that\nalready pass must still pass. For `!ban` and `!clear`, a member is refused\nbefore their arguments are looked at."
        },
        {
          "h": "Task 2 — Join the chain"
        },
        {
          "p": "Tests: `test/chain.test.js`"
        },
        {
          "p": "Write `runChain(middlewares, final)`. It returns one function, `msg => reply`,\nthat runs the message through each middleware in list order and then through\n`final`."
        },
        {
          "p": "You do not call the middleware one after another yourself. You give each one\na `next`, and it decides whether and when to call it. A middleware that\nreturns without calling `next` stops the message there."
        },
        {
          "code": "const shout = (msg, next) => next(msg) + \"!\";\nconst handle = runChain([shout, shout], msg => \"hi\");\n\nhandle({ user: \"ada\", role: \"member\", text: \"\" });   // \"hi!!\"",
          "lang": "js"
        },
        {
          "p": "Build the chain once, when `runChain` is called. Nothing should run until a\nmessage arrives."
        },
        {
          "h": "Task 3 — Two middleware factories"
        },
        {
          "p": "Tests: `test/middleware.test.js`"
        },
        {
          "p": "Write two functions that each return a middleware."
        },
        {
          "list": [
            "`logTo(lines)` writes each message to the array `lines` before the rest of the chain runs, and the reply after it:  ``` > ada: !add 2 3 < 5 ```",
            "`cooldown(seconds, now)` refuses a user's command if their last accepted command was fewer than `seconds` ago. It replies `\"slow down, ada\"` and does not call `next`. `now` is a function that returns the current time in seconds. You are given it; your middleware decides when to call it."
          ]
        },
        {
          "p": "The tests hand `cooldown` a clock they control, so they never wait."
        },
        {
          "h": "Task 4 — Assemble the bot"
        },
        {
          "p": "Tests: `test/bot.test.js`"
        },
        {
          "p": "Write `createBot({ commands, stats, now, log, seconds })`. It builds one\nchain, in this order, and returns `{ handle }`:"
        },
        {
          "list": [
            "`logTo(log)` — so refused commands are logged too",
            "`cooldown(seconds, now)`",
            "`stats.record` — counts only commands that got through",
            "`createRouter(commands)` — finds the handler and runs it"
          ],
          "ordered": true
        },
        {
          "p": "Two things to know. `stats` comes from `createStats()` in `src/given.js`, and\n`record` is one of its methods. And the chat connection will call your\n`handle` on its own, as `handle(msg)`, with no object in front of it."
        },
        {
          "h": "Task 5 — Mods skip the cooldown"
        },
        {
          "p": "Also in `test/bot.test.js`."
        },
        {
          "p": "A mod's commands are never slowed down. A member's still are."
        },
        {
          "p": "There is more than one place this rule could live. Pick one, make the tests\npass, and defend your choice in the answers below."
        },
        {
          "h": "The review"
        },
        {
          "p": "`review/mute.js` was written by an AI assistant, asked for \"a mute list mods\ncan manage, and a filter that stars out banned words, wired into the bot\". It\nhas four defects. Three are about this module's ideas; one is about\nan earlier module's."
        },
        {
          "p": "Write your review in `REVIEW.md`. For each defect, give:"
        },
        {
          "list": [
            "the line or lines;",
            "what goes wrong, in a sentence or two;",
            "a concrete input or call that shows it;",
            "the fix."
          ]
        },
        {
          "p": "Write it for the teammate who asked the assistant. They should be able to act\non it without asking you anything."
        },
        {
          "p": "The tests do not read `REVIEW.md`. A person does."
        },
        {
          "h": "Your answers"
        },
        {
          "p": "Fill these in, here in this README."
        },
        {
          "p": "**Task 4.** In one sentence: how does `stats.record` still have its `stats`\nobject when the chain calls it?"
        },
        {
          "p": "*Your answer.*"
        },
        {
          "p": "**Task 5.** Where did you put the rule that mods skip the cooldown, and why\nthere? Then say what your choice costs when a second exception arrives — say,\nthe bot's own announcements must skip the cooldown too."
        },
        {
          "p": "*Your answer.*"
        },
        {
          "h": "What \"done\" means"
        },
        {
          "list": [
            "`npm test` shows every test passing.",
            "`REVIEW.md` has a finding for each defect you found.",
            "Both answers above are filled in."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/functions-assignment"
    },
    "modeling-assignment": {
      "kind": "assignment",
      "title": "Workshop bookings",
      "lede": "Build the type that keeps a makerspace's workshop bookings — one whose rules nothing outside can break, which holds a policy it reaches only through a contract — then add a drop-in variant in a design you choose and defend.",
      "exercises": [
        "model.objects",
        "model.encapsulation",
        "model.interface",
        "model.composition",
        "model.inheritance",
        "model.choosing",
        "model.prototypes",
        "types.nullability",
        "diag.code-review"
      ],
      "repo": {
        "name": "cs140-modeling",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "The makerspace runs workshops: soldering, wood turning, a repair café. Each\nsession has a title, a number of places, and maybe a room. People book, and\nwhen it is full they wait. Some sessions are for members only."
        },
        {
          "p": "You will build the type that keeps those bookings. It has to keep its own rules\nwhatever a caller does, decide which of its fields may be missing, hold a part\nthat decides who may book, and then grow a variant — a drop-in session — in a\nway you choose and defend."
        },
        {
          "p": "That is the whole of *Objects and modeling* in one program: designing a type,\nprivate state that guards an invariant, a contract nothing checks for you, and\nchoosing between inheritance and composition."
        },
        {
          "p": "Allow about three hours for the build and the review together."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-modeling-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/modeling-assignment), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the tests:  ```sh npm test ```  Nothing to install. Every test fails at first — that is the starting point. The same tests run on GitHub every time you push, so you can see where you stand from the repository page."
          ],
          "ordered": true
        },
        {
          "p": "The code you write goes in `src/`. The tests in `test/` are part of the spec:\nread them. Each one checks one behaviour, and its name says which. When one\nfails, its message says what it expected, what came back, and which idea to\nlook at again."
        },
        {
          "table": {
            "head": [
              "File",
              "What it is"
            ],
            "rows": [
              [
                "`src/session.js`",
                "the `Session` type — tasks 1, 2 and 3"
              ],
              [
                "`src/policies.js`",
                "admission policies and the contract they meet — task 3"
              ],
              [
                "`src/drop-in.js`",
                "the drop-in variant — task 4"
              ],
              [
                "`review/tool-shelf.js`",
                "code to review — not yours to fix in place"
              ],
              [
                "`REVIEW.md`",
                "your review"
              ]
            ]
          }
        },
        {
          "h": "The tasks"
        },
        {
          "p": "Work in order. Each task's tests are named `task 1: …`, `task 2: …`, and so on,\nso you can see which ones are yours to pass next."
        },
        {
          "h": "Task 1 — What a session holds"
        },
        {
          "p": "Write the constructor, the getters `title`, `capacity` and `room`, and\n`label()`."
        },
        {
          "code": "const s = new Session({ title: 'Intro to soldering', capacity: 8, room: 'Room B' });\ns.label();   // 'Intro to soldering · Room B · 8 of 8 places left'",
          "lang": "js"
        },
        {
          "p": "Decide each field once, in the constructor:"
        },
        {
          "list": [
            "`title` is **required**: a non-empty string. Anything else throws a `TypeError`.",
            "`capacity` is **required**: a whole number, 1 or more. Anything else throws a `RangeError`.",
            "`room` is **optional**. Missing, `undefined` and `null` all mean \"not decided yet\", and `room` reads as `null` for all three. `label()` says `room to be confirmed` instead."
          ]
        },
        {
          "h": "Task 2 — Bookings nothing outside can break"
        },
        {
          "p": "A session keeps three rules, whatever a caller does:"
        },
        {
          "list": [
            "never more people booked than `capacity`;",
            "nobody appears twice, across booked and waiting together;",
            "nobody waits while a place is free."
          ],
          "ordered": true
        },
        {
          "p": "Write `book(person)`, `cancel(name)`, `statusOf(name)` and the getters\n`spacesLeft`, `attendees` and `waiting`. Their contracts are in the JSDoc in\n`src/session.js`. In short:"
        },
        {
          "code": "const s = new Session({ title: 'Wood turning', capacity: 1 });\ns.book({ name: 'Ana' });   // 'booked'\ns.book({ name: 'Bo' });    // 'waiting'\ns.statusOf('Cy');          // null\ns.cancel('Ana');           // true — and Bo now has the place\ns.attendees;               // ['Bo']",
          "lang": "js"
        },
        {
          "p": "The rules only hold if nothing outside can reach the state they are about. So:"
        },
        {
          "list": [
            "Keep the state in `#private` fields. A check inside `book` protects `book`; only private state protects the rules.",
            "`attendees` and `waiting` hand out **copies**. A caller may push onto what they receive; the session must not notice.",
            "`capacity` can be read, not changed.",
            "Keep the person's **name**, not the object they booked with. The caller still holds that object and may change it later.",
            "Check before you change. A refused booking leaves the session exactly as it was."
          ]
        },
        {
          "h": "Task 3 — Who may book: a contract"
        },
        {
          "p": "Some sessions are for members only; some are by invitation. A session does not\ndecide this itself. It **holds a policy** and asks it."
        },
        {
          "p": "A policy is any object with a `reasonToRefuse(person)` method that returns\n`null` when the person may book, or a short reason when they may not. The full\ncontract is the comment at the top of `src/policies.js`. Read it closely — what\nthe method returns when there is *nothing* to say is part of it."
        },
        {
          "list": [
            "In `src/policies.js`, write `openToAll`, `membersOnly()`, `invitedOnly(names)` and `allOf(...policies)`. `allOf` holds other policies and refuses with the first reason any of them gives.",
            "In `Session`, accept an optional `policy` in the constructor. `book` asks it first. If it refuses, `book` throws an `Error` whose message is the reason, and nobody is booked or waitlisted.",
            "With no policy, everyone may book. Decide how a missing policy is stored so that `book` stays simple — `openToAll` is already imported for you, if you want it."
          ],
          "ordered": true
        },
        {
          "code": "const s = new Session({ title: 'Laser cutter induction', capacity: 6, policy: membersOnly() });\ns.book({ name: 'Bo' });   // throws Error: members only",
          "lang": "js"
        },
        {
          "p": "JavaScript checks none of this. A plain object literal with a\n`reasonToRefuse` method is as good a policy as anything in `policies.js`, and\nthe tests hand your `Session` one. Do not check what class a policy is."
        },
        {
          "h": "Task 4 — A drop-in session, and the choice behind it"
        },
        {
          "p": "The repair café is a **drop-in**. It works like any session, except that it\nhas no waitlist: when it is full, `book` throws `Error('full')`, and nobody is\nadded to a waitlist. Everything else — the rules, the policy, the getters,\n`label`, `cancel` — works exactly as it does for a `Session`. Code written for\n\"a session\" must work when handed a drop-in."
        },
        {
          "p": "Make `createDropIn(details)` in `src/drop-in.js` return one. There is more than\none reasonable design:"
        },
        {
          "list": [
            "**Inheritance** — `class DropInSession extends Session`, overriding what differs.",
            "**Composition** — an object that holds a `Session` and forwards to it, changing only what differs.",
            "**An option on `Session`** — a `waitlist: false` detail, and `createDropIn` passes it."
          ]
        },
        {
          "p": "Pick one and build it. The tests only call `createDropIn` and what a session\noffers, so every one of these can pass them. The tests cannot tell whether you\nchose well. Your answers below say that."
        },
        {
          "h": "The review"
        },
        {
          "p": "`review/tool-shelf.js` was written by an AI assistant for the makerspace's\ntool library. It runs, and its two examples print the right thing. It still has\nproblems of the kind this module is about, and at least one from earlier in the\ncourse."
        },
        {
          "p": "Find at least three. For each, write in `REVIEW.md`:"
        },
        {
          "list": [
            "**which lines**;",
            "**what goes wrong** — in terms of the idea, not only the symptom;",
            "**a concrete input that shows it** — a few lines of code someone could run, and what they print or do;",
            "**the fix** — the changed lines, not a rewrite of the file."
          ]
        },
        {
          "p": "Write it for the teammate who will fix it. Do not change `tool-shelf.js`\nitself. There are no tests for the review: a person reads `REVIEW.md`."
        },
        {
          "h": "Your answers"
        },
        {
          "p": "Answer under each question, in a few sentences. A person reads these too."
        },
        {
          "h": "1. Which design did you choose for the drop-in, and why?"
        },
        {
          "p": "Use the three questions from *Contracts, and choosing between the three*: is a\ndrop-in genuinely a kind of session, is `Session` stable and meant to be\nextended, and must unrelated types be interchangeable? Say which one decided it."
        },
        {
          "p": "*Your answer.*"
        },
        {
          "h": "2. What does your choice commit you to?"
        },
        {
          "p": "Next term, `Session` gains a new method, `moveTo(room)`. What happens to your\ndrop-in — does it get `moveTo`, and is that what you want? Then name one change\nto `Session` that could break your drop-in without anyone touching\n`drop-in.js`, or say why none can."
        },
        {
          "p": "*Your answer.*"
        },
        {
          "h": "3. How is `cancel` found?"
        },
        {
          "p": "Someone calls `dropIn.cancel('Ana')` on one of your drop-ins. Your drop-in's\ncode may not define `cancel` itself. Say where the method that runs is found,\nstep by step — which objects are checked, in what order, and where it stops."
        },
        {
          "p": "*Your answer.*"
        },
        {
          "h": "4. The contract of \"a session\""
        },
        {
          "p": "A caller holds a session and does not know whether it is a drop-in. Write what\nthey may rely on: each member, what it returns — including when there is\nnothing — and one thing they must not rely on."
        },
        {
          "p": "*Your answer.*"
        },
        {
          "h": "Done means"
        },
        {
          "list": [
            "`npm test` passes, locally and on GitHub.",
            "`REVIEW.md` names at least three problems, each with lines, what goes wrong, an input that shows it, and a fix.",
            "The four answers above are filled in."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/modeling-assignment"
    },
    "errors-assignment": {
      "kind": "assignment",
      "title": "Gear locker",
      "lede": "Build the program that runs a club's gear-loan scripts, and make it fail well: bad lines returned and reported, conflicts thrown and handled where the run can act, bugs sent on with their cause, and the journal closed on every way out. Then review an assistant's importer that fails badly.",
      "exercises": [
        "err.recoverable",
        "err.propagation",
        "err.placement",
        "err.as-value",
        "err.cleanup",
        "diag.diagnostics",
        "diag.code-review"
      ],
      "repo": {
        "name": "cs140-errors",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "h": "What you are building"
        },
        {
          "p": "An outdoor club keeps its gear in a locker: a tent, a stove, a lantern, a kayak.\nA volunteer types the day's loans into a small script, one command per line:"
        },
        {
          "code": "lend tent Sam\nstatus lantern\nreturn tent",
          "lang": "text"
        },
        {
          "p": "You are building the program that runs those scripts. Most of it is about what\nhappens when a line cannot be done. People mistype. They lend a lantern someone\nalready has. They name a canoe the club does not own. Now and then the program\nitself is wrong."
        },
        {
          "p": "Each of those failures needs a different answer. Some are returned, some are\nthrown. Some are recorded and the run carries on, and some should stop it\nloudly. The journal file has to be closed whichever way the run ends. Getting\nthat right needs all of this module together: what kind of failure it is, where\nit travels, where it is handled, whether it is thrown or returned, and what runs\non the way out."
        },
        {
          "p": "The program is five small modules in `src/`:"
        },
        {
          "table": {
            "head": [
              "File",
              "What it does",
              "Task"
            ],
            "rows": [
              [
                "`errors.js`",
                "the error types this program throws",
                "1"
              ],
              [
                "`parse.js`",
                "turns one typed line into a command, or a reason it is not one",
                "2"
              ],
              [
                "`loans.js`",
                "applies one command to the locker",
                "3"
              ],
              [
                "`run.js`",
                "runs a whole script and reports on it",
                "4, 5"
              ],
              [
                "`load.js`",
                "reads a script from a file (Node only)",
                "6"
              ]
            ]
          }
        },
        {
          "p": "`src/cli.js` is written for you. It is the program's top level, and it runs in\nNode only."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-errors-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/errors-assignment), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the tests:  ```text npm test ```  Almost all of them fail. That is the starting point. There is nothing to install."
          ],
          "ordered": true
        },
        {
          "p": "The tests run again on every push, and the result shows on your repository's\n**Actions** tab. The tests are part of the spec. Each file in `test/` is named\nfor its task. When one fails, read its message: it says what was expected, what\ncame back, and which lesson to look at."
        },
        {
          "p": "Once Task 4 passes, you can run a real script:"
        },
        {
          "code": "node src/cli.js test/fixtures/weekend.txt",
          "lang": "text"
        },
        {
          "h": "The tasks"
        },
        {
          "p": "Do them in order. Each builds on the one before. The contract for every function\nis in the comment above it in `src/` — read that before you start a task."
        },
        {
          "h": "1. Errors a caller can act on"
        },
        {
          "p": "In `src/errors.js`, write the three constructors: `UnknownItem`, `LoanConflict`\nand `ScriptError`."
        },
        {
          "p": "Each one needs a message that says what failed, with the value. Each needs a\n`name` a caller can check. `ScriptError` also keeps the error that caused it."
        },
        {
          "p": "Tests: `test/1-errors.test.js`."
        },
        {
          "h": "2. A failure returned as a value"
        },
        {
          "p": "In `src/parse.js`, write `parseCommand(text)`. It turns one line into a\ncommand, or into a reason it is not one:"
        },
        {
          "code": "parseCommand(\"lend tent Sam\")    // { ok: true, value: { verb: \"lend\", item: \"tent\", member: \"Sam\" } }\nparseCommand(\"borrow kayak Ana\") // { ok: false, error: 'unknown command \"borrow\"' }",
          "lang": "js"
        },
        {
          "p": "It **returns** its failures. It never throws, whatever it is given. Your\nwording for `error` can differ from the one above, as long as it does what the\ncontract says."
        },
        {
          "p": "Tests: `test/2-parse.test.js`."
        },
        {
          "h": "3. Throwing what a caller can tell apart"
        },
        {
          "p": "In `src/loans.js`, write `applyCommand(loans, command)`. The locker is a `Map`\nfrom item to holder, with `null` for an item on the shelf."
        },
        {
          "p": "This one **throws**: `UnknownItem` for an item the club does not have, and\n`LoanConflict` for a lend or a return that cannot happen. A command that throws\nmust leave the locker as it was."
        },
        {
          "p": "Tests: `test/3-loans.test.js`."
        },
        {
          "h": "4. Handling it where you can act"
        },
        {
          "p": "In `src/run.js`, finish `runScript(text, loans, journal)`. The loop and the\nskipping of blank lines and comments are written. You write what happens to\neach line."
        },
        {
          "p": "A line that does not parse, an unknown item, and a loan conflict are all **bad\nlines**. `runScript` records each one in `problems`, with its line number, and\ncarries on. It is the first level that knows it is running a whole script."
        },
        {
          "p": "Anything else is not a bad line. It means the program is wrong. `runScript` has\nno plan for it, so it must not file it under `problems`. It adds the one thing\nit knows — the line number — as a `ScriptError`, keeps the original as the\ncause, and lets it go on up."
        },
        {
          "p": "Tests: `test/4-run.test.js`."
        },
        {
          "h": "5. Closing the journal on every way out"
        },
        {
          "p": "Still in `runScript`: open the journal once, before the first line. Close it\nexactly once, after the last write, however `runScript` ends — by returning or\nby throwing."
        },
        {
          "p": "If `journal.open()` itself throws, there is nothing to close. That error goes to\nthe caller as it is."
        },
        {
          "p": "Tests: `test/5-journal.test.js`."
        },
        {
          "h": "6. Adding context on the way past"
        },
        {
          "p": "In `src/load.js`, finish `loadScript(path, read)`. It already reads a file.\nMake it fail well. When the read throws, throw a new error that names the path\nand keeps the reader's error as its cause."
        },
        {
          "p": "`loadScript` uses Node's `readFileSync`, so it runs in Node only. The tests hand\nit their own `read` function, or read a file inside this repository."
        },
        {
          "p": "Then open `src/cli.js` and find where a failed load is caught. You need it for\nquestion 2."
        },
        {
          "p": "Tests: `test/6-load.test.js`."
        },
        {
          "h": "7. Your answers"
        },
        {
          "p": "Answer the four questions in **Your answers**, at the end of this file. A few\nsentences each. These are read by a person, not by the tests."
        },
        {
          "h": "The review"
        },
        {
          "p": "`review/import-members.js` was written by an AI assistant, from the request\nquoted at the top of the file. It runs, and it looks careful. It has more than\none defect."
        },
        {
          "p": "Review it in `REVIEW.md`. For each defect you find, write:"
        },
        {
          "list": [
            "**Where** — the line numbers.",
            "**What goes wrong** — what the code does, and why that is a problem.",
            "**An input that shows it** — a concrete file, roster or audit log, and what happens with it.",
            "**The fix** — the change you would make, as code or in a sentence."
          ]
        },
        {
          "p": "Write it as a review a teammate could act on. Most of the defects are about this\nmodule. One is from earlier in the course."
        },
        {
          "p": "The tests do not check `REVIEW.md`. A person reads it."
        },
        {
          "h": "What done means"
        },
        {
          "list": [
            "`npm test` passes: every test, locally and on your latest push.",
            "`REVIEW.md` has a review of each defect you found.",
            "Every question in **Your answers** has an answer."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        },
        {
          "h": "Your answers"
        },
        {
          "h": "1. Throw or return"
        },
        {
          "p": "`parseCommand` returns its failures. `applyCommand` throws them. Say why each\nchoice fits that function. Use how often each one fails, and what happens when a\ncaller forgets to check."
        },
        {
          "p": "*Your answer:*"
        },
        {
          "h": "2. Where each failure is handled"
        },
        {
          "p": "Follow a `LoanConflict` from the line that throws it to the line that deals with\nit. Why is `runScript` the right level to decide what it means, and not\n`applyCommand`? Then do the same for a missing script file, which `cli.js`\ncatches. Finally, say why `cli.js` does **not** catch a `ScriptError`. Is that\nfailure recoverable or unrecoverable? Defend it."
        },
        {
          "p": "*Your answer:*"
        },
        {
          "h": "3. Cleanup"
        },
        {
          "p": "Say which ways out of `runScript` your `finally` covers, and what would go wrong\nin `cli.js` if one of them skipped `journal.close()`. Then name the construct\nyour language offers for scoped disposal. Say whether you would use it for the\njournal in code a team maintains today, and why."
        },
        {
          "p": "*Your answer:*"
        },
        {
          "h": "4. A log line that decides"
        },
        {
          "p": "A volunteer runs a script. At the end, `status tent` prints\n`tent is on the shelf`. But the script has `lend tent Sam` on line 4, and the\nreport lists no problem on line 4. There are two explanations:"
        },
        {
          "list": [
            "**A** — line 4 ran, and a later line in the script returned the tent.",
            "**B** — line 4 never reached `applyCommand`. `runScript` skipped it."
          ]
        },
        {
          "p": "First, write one log line that **cannot** tell A from B, and say why. Then write\none log line that **can**. Say where in `run.js` it goes, and what it prints\nunder A and under B."
        },
        {
          "p": "*Your answer:*"
        }
      ],
      "_source": "assignments/cs140/errors-assignment"
    },
    "async-assignment": {
      "kind": "assignment",
      "title": "A bookshop client that waits well",
      "lede": "Build a small client for a bookshop's API over a fake transport: chained and awaited requests, independent ones started together, a delay, a timeout that cleans up after itself, a retry, and a shelf that shows what loaded. Then review an assistant's helper file and answer five questions on order, hosts and races.",
      "exercises": [
        "async.promises",
        "async.await",
        "async.scheduling",
        "async.runtime-difference",
        "async.shared-memory",
        "diag.code-review"
      ],
      "repo": {
        "name": "cs140-async",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "You are building a small client for a bookshop's API: the part of a program\nthat asks a server for books and authors, and returns what came."
        },
        {
          "p": "Almost nothing in it is hard to *compute*. All of it is about *waiting*. A\nrequest takes time. It can fail, or never answer. Some requests need the one\nbefore; others do not. And while any of it waits, the thread must stay free.\nThat is this module: promises, `await`, the event loop, and the host that keeps\nthe clock."
        },
        {
          "p": "There is no network. Every request goes to a **fake transport** in\n`src/fake-transport.js` — a pretend server that answers after a few\nmilliseconds, using the host's timers. Your code cannot tell it is fake."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-async-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/async-assignment), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the tests:"
          ],
          "ordered": true
        },
        {
          "code": "npm test",
          "lang": "bash"
        },
        {
          "p": "Every test fails at first. That is where you start. The tests also run on\nevery push, and GitHub shows the result next to the commit."
        },
        {
          "p": "**The tests are part of the spec.** They live in `test/`, one file per task.\nRead them when a sentence here leaves you unsure. When one fails, its message\nsays what came back and where in the lessons to look."
        },
        {
          "h": "The API and the transport"
        },
        {
          "p": "The bookshop answers two kinds of path:"
        },
        {
          "table": {
            "head": [
              "Path",
              "Body"
            ],
            "rows": [
              [
                "`/books/<id>`",
                "`{ id, title, authorId }`"
              ],
              [
                "`/authors/<id>`",
                "`{ id, name }`"
              ]
            ]
          }
        },
        {
          "p": "A **transport** is a function. `transport(path)` returns a promise that:"
        },
        {
          "list": [
            "**fulfils** with `{ status, body }` whenever the server answered — a `404` or a `500` is still an answer;",
            "**rejects** with an `Error` when there was no answer at all."
          ]
        },
        {
          "p": "The fake one writes each request to `transport.log` as it happens —\n`start /books/1` when it is made, `end /books/1` when its answer arrives. Some\ntests read that log to see which requests were in flight at once."
        },
        {
          "h": "The tasks"
        },
        {
          "p": "The functions are in `src/client.js` and `src/timing.js`. Each has a comment\nstating its contract. Replace each `throw new Error('not implemented')`."
        },
        {
          "p": "Do them in order: later tasks use earlier ones."
        },
        {
          "h": "Task 1 · `getJSON(transport, path)`"
        },
        {
          "p": "Ask the transport for `path`. Return a promise for the body when the status\nis 200–299. For any other status, reject with `httpError(path, status)` — the\nhelper is written for you. If the transport rejects, let that error through\nuntouched."
        },
        {
          "p": "Write it with `.then`, not `await`. Most code you will read is written this\nway. Tests: `test/1-get-json.test.js`."
        },
        {
          "h": "Task 2 · `getBookWithAuthor(transport, id)`"
        },
        {
          "p": "Load the book, then its author. Fulfil with `{ title, author }`, where\n`author` is the author's name. If the author lookup answers `404`, the author\nis `null` and the book still loads. Any other failure rejects."
        },
        {
          "p": "Write it with `async` and `await`. Tests: `test/2-book-with-author.test.js`."
        },
        {
          "h": "Task 3 · `getBooks(transport, ids)`"
        },
        {
          "p": "Load several books. Fulfil with them in the order of `ids`, whatever order the\nanswers come back in. None of these requests needs another's result, so make\nevery request before waiting for any. If one fails, reject with its error.\nTests: `test/3-get-books.test.js`."
        },
        {
          "h": "Task 4 · `delay(ms)`"
        },
        {
          "p": "Return a promise that fulfils after `ms` milliseconds. While it waits, other\ncode must be able to run. Tests: `test/4-delay.test.js`."
        },
        {
          "h": "Task 5 · `withTimeout(promise, ms)`"
        },
        {
          "p": "Give a promise a time limit. If it settles in time, settle the same way. If\nnot, reject with `timeoutError(ms)`. Whichever happens first, leave no timer\npending. One test checks that by asking Node which timers it is still\nholding. Tests: `test/5-with-timeout.test.js`."
        },
        {
          "h": "Task 6 · `retry(task, { attempts, delayMs })`"
        },
        {
          "p": "`task` is a function that returns a promise. Call it. If it rejects, wait\n`delayMs` — with `delay` — and call it again, up to `attempts` calls in all.\nFulfil with the first success, or reject with the last error. Tests:\n`test/6-retry.test.js`."
        },
        {
          "h": "Task 7 · `loadShelf(transport, ids, { timeoutMs })`"
        },
        {
          "p": "A page shows a shelf of books. It should show whatever loaded, and list what\ndid not. Request every book at once, each with its own `withTimeout`. Fulfil\nwith `{ books, missing }`: the books that loaded and the ids that did not, both\nin `ids` order. It never rejects because one book failed. Tests:\n`test/7-load-shelf.test.js`."
        },
        {
          "p": "Then answer question 5 below: it asks you to defend a choice this task leaves\nopen."
        },
        {
          "h": "The review"
        },
        {
          "p": "`review/reading-list.js` was written by an AI assistant for the command-line\ntool that prints a reading list. It runs in Node. It looks finished. It is not."
        },
        {
          "p": "Find its defects and write them up in `REVIEW.md`. For each one, give:"
        },
        {
          "list": [
            "**the lines** it is on;",
            "**what goes wrong**, in a sentence or two;",
            "**a concrete input that shows it** — the call, and what happens;",
            "**the fix**."
          ]
        },
        {
          "p": "There is more than one defect, and not all of them are about waiting. You may\nrun the file against the fake transport to confirm what you suspect. Do not fix\nit in place; the fix goes in `REVIEW.md`."
        },
        {
          "p": "No test checks the review. A person reads it."
        },
        {
          "h": "Answers"
        },
        {
          "p": "Write your answers under each question, in this file. A person reads them."
        },
        {
          "p": "**1. The other form.** Write `getJSON` from Task 1 again, with `async` and\n`await` this time. It must behave exactly as your chain does. Then say what\nyour chain's `.then` callback became in the new version."
        },
        {
          "p": "Your answer:"
        },
        {
          "p": "**2. Order of events.** `examples/order.js` prints five lines. Write down the\norder you expect, *before* you run it. Then run `node examples/order.js`. If\nyour prediction was wrong, say which line you placed wrong, and which queue\nit was really waiting in. If it was right, say why `C` does not print before\n`B`, although the fake server answers in 0 ms."
        },
        {
          "p": "Your answer:"
        },
        {
          "p": "**3. Two hosts.** Suppose `withTimeout` did not clear its timer. You run this\nNode script, and the book arrives in a few milliseconds:"
        },
        {
          "code": "const book = await withTimeout(getJSON(transport, '/books/1'), 10_000);\nconsole.log(book.title);",
          "lang": "js"
        },
        {
          "p": "When does the program end, and why? Then say what someone would notice if the\nsame code ran in a browser tab instead, and what that tells you about who\ndecides what a pending timer does."
        },
        {
          "p": "Your answer:"
        },
        {
          "p": "**4. One thread, two calls.** A teammate adds a cache:"
        },
        {
          "code": "const cache = new Map();\n\nasync function cachedBook(transport, id) {\n  if (!cache.has(id)) {\n    const book = await getJSON(transport, `/books/${id}`);\n    cache.set(id, book);\n  }\n  return cache.get(id);\n}\n\nawait Promise.all([cachedBook(transport, 1), cachedBook(transport, 1)]);",
          "lang": "js"
        },
        {
          "p": "How many requests for `/books/1` does the transport see, and why? Is this a\nrace condition? Is it a data race? Then say where the switch between the two\ncalls can happen here, and where it could happen if each call ran on its own\nthread in a language with threads."
        },
        {
          "p": "Your answer:"
        },
        {
          "p": "**5. Your design.** Should `loadShelf` retry a book that failed? Say which of\nthese you would retry, and why: a `404`, a `500`, no answer at all, a timeout.\nThen say what retrying costs the person looking at the page. A few sentences."
        },
        {
          "p": "Your answer:"
        },
        {
          "h": "What done means"
        },
        {
          "list": [
            "`npm test` passes: every test in `test/`.",
            "`REVIEW.md` has a section for each defect you found.",
            "Every question under **Answers** has an answer."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/async-assignment"
    },
    "modules-assignment": {
      "kind": "assignment",
      "title": "Study log, in modules",
      "lede": "Split a one-file program into modules that each change for one reason and promise only what callers need, then add one package, pinned, and answer a question about it from its own documentation.",
      "exercises": [
        "mod.boundary",
        "mod.interface",
        "mod.dependency",
        "diag.documentation",
        "diag.code-review"
      ],
      "repo": {
        "name": "cs140-modules",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "You are taking a small working program and giving it seams. It reads a study\nlog — one line per session — and prints how long you spent on each course:"
        },
        {
          "code": "Study log · 2026-09-28 to 2026-10-01 · 6 sessions\ncs140     1h40m\nmath      1h40m\nenglish   0h50m\ntotal     4h10m",
          "lang": "text"
        },
        {
          "p": "Today the whole program is one file, `src/studylog.js`, and every name in it is\nexported. You will split it into modules that each change for one reason. Each\nmodule will promise only what its callers need. Then you will add one\nthird-party package and pin it, read the lockfile to see what you got, and\nanswer a question from the package's own documentation."
        },
        {
          "p": "Very little is new. The program works when you start, and it works when you\nfinish. What changes is how much each part has to know about the\nothers. That is what this module is about."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for this homework, the term and your username — `<term>-cs140-modules-<you>`. On [this homework's page](https://wwcc.dev/#/lesson/modules-assignment), type your GitHub username and click **Open my Codespace**. On your own computer, clone it with GitHub Desktop (**Code**, then **Open with GitHub Desktop**) and check that `node --version` prints 22 or later. The lesson *How a homework works* walks through both.",
            "Run the tests:  ```bash npm test ```  Most fail at first. A few pass already, because the empty modules already export the right names. To run one module's tests only, name the file:  ```bash node --test test/parse.test.js ```",
            "Run the program itself:  ```bash npm run report ```  That runs `node src/cli.js sample.log`. Run it after every task. It should print the same report each time."
          ],
          "ordered": true
        },
        {
          "p": "Tasks 1–5 need nothing installed. Task 6 is where you install a package."
        },
        {
          "p": "The tests also run on every push; the **Actions** tab on your repository shows\nthe result. Each failing test says what it expected, what came back, and which\nlesson to look at. It does not say what to write."
        },
        {
          "h": "What is in the repository"
        },
        {
          "table": {
            "head": [
              "File",
              "What it is"
            ],
            "rows": [
              [
                "`src/studylog.js`",
                "The whole program, in one file. It becomes the public face."
              ],
              [
                "`src/parse.js`, `src/totals.js`, `src/report.js`",
                "Empty modules, each with a comment saying what it promises."
              ],
              [
                "`src/cli.js`",
                "The program you run. Node only: it reads a file."
              ],
              [
                "`sample.log`",
                "A week of study sessions."
              ],
              [
                "`test/`",
                "The tests, one file per module. They are part of the spec."
              ],
              [
                "`review/`",
                "Someone else's code, for the review."
              ],
              [
                "`REVIEW.md`",
                "Where your review goes."
              ]
            ]
          }
        },
        {
          "h": "The seams"
        },
        {
          "p": "Your team has already agreed where the seams go. There are three reasons this\nprogram changes, and each gets its own module:"
        },
        {
          "table": {
            "head": [
              "Module",
              "Changes when",
              "Promises"
            ],
            "rows": [
              [
                "`parse.js`",
                "the log's format changes",
                "`parseLog`"
              ],
              [
                "`totals.js`",
                "what we count changes",
                "`totalsByCourse`, `dateRange`"
              ],
              [
                "`report.js`",
                "the output's design changes",
                "`formatReport`"
              ],
              [
                "`studylog.js`",
                "never, ideally",
                "`summarize`"
              ]
            ]
          }
        },
        {
          "p": "\"Promises\" means exports: each module exports those names and **nothing else**.\nThe tests check that. A module may hold as many other functions and constants as\nit likes, as long as it does not export them."
        },
        {
          "p": "Read the comment above each function in `src/` before you write it. It is the\ncontract. The old code does not meet every contract, so moving it is not always\nenough."
        },
        {
          "h": "The tasks"
        },
        {
          "h": "1. Cut out the parser"
        },
        {
          "p": "Move the log-reading code into `src/parse.js`. `parseLog` is the only export;\n`parseLine` comes too, as a private helper."
        },
        {
          "p": "Tests: `test/parse.test.js`, the tests named `task 1`."
        },
        {
          "h": "2. Cut out the arithmetic"
        },
        {
          "p": "Move `totalsByCourse` and `dateRange` into `src/totals.js`. Check both against\ntheir contracts. One of them breaks a promise its contract makes. Nothing\nthrows when it does."
        },
        {
          "p": "Tests: `test/totals.test.js`."
        },
        {
          "h": "3. Cut out the report"
        },
        {
          "p": "Write `formatReport` in `src/report.js`, from the old `summarize`. It takes a\nsummary object — how many sessions, the date range, the totals — and returns\ntext. It does not parse, and it does not add anything up. Its helpers stay\nprivate. For now you can ignore the `style` parameter. That is task 6."
        },
        {
          "p": "Tests: `test/report.test.js`, the tests named `task 3`."
        },
        {
          "h": "4. Leave one face"
        },
        {
          "p": "Now rewrite `src/studylog.js` so it holds only `summarize`. It calls the other\nthree modules and exports nothing else. `cli.js` imports `summarize` and nothing\nelse, so it keeps working."
        },
        {
          "p": "The old `summarize` has a bug that only shows on a second call. Find it before\nyou move it. Then watch for it coming back in a new form: a count kept in one\nmodule and read by another. An exported `let` is a live binding, so every\nimporter sees whatever the owner last did to it (*Where to put the seam*). Hand\nthe number across as a value instead."
        },
        {
          "p": "Tests: `test/studylog.test.js`, the tests named `task 4`. Run `npm run report`\nagain. Same report?"
        },
        {
          "h": "5. The format grows"
        },
        {
          "p": "People want to write durations the way the report prints them. From now on a\nlog line may give its duration in any of these forms:"
        },
        {
          "table": {
            "head": [
              "Written",
              "Minutes"
            ],
            "rows": [
              [
                "`90`",
                "90"
              ],
              [
                "`45m`",
                "45"
              ],
              [
                "`2h`",
                "120"
              ],
              [
                "`1h40m`",
                "100"
              ]
            ]
          }
        },
        {
          "p": "After the `h`, the minutes are 0 to 59. Anything else is an error that names\nits line, as before."
        },
        {
          "p": "The reading goes in `parse.js`. But the notation now lives in two places. The\nreport writes it and the parser reads it, and if one changes, the other must\ntoo. **Decide where that knowledge goes.** The export tests still hold, so\n`report.js` cannot start exporting a helper for `parse.js` to borrow. You could:"
        },
        {
          "list": [
            "put the notation in a small module of its own, which both import;",
            "keep writing in `report.js` and reading in `parse.js`, each private;",
            "or something else you can defend."
          ]
        },
        {
          "p": "There is no single right answer. There is a wrong one: a cycle, or a promise\nthat grew just so another module could reach in. Explain your choice in\n**Your answers**, question 1."
        },
        {
          "p": "Add a line in the new notation to `sample.log`, and run the program."
        },
        {
          "p": "Tests: the tests named `task 5`."
        },
        {
          "h": "6. Add a dependency, and pin it"
        },
        {
          "p": "The report should be easier to read in a terminal: header and total in **bold**.\nYou will use [picocolors](https://www.npmjs.com/package/picocolors), a small\npackage with no dependencies of its own."
        },
        {
          "list": [
            "Add `picocolors` as a dependency with npm, **pinned to one exact version**. In `package.json` it must be a bare version, with no `^` or `~`. If you have already installed it with a range, install it again, pinned. Do not edit `package.json` by hand; let npm write it and the lockfile together.",
            "Commit **both** `package.json` and `package-lock.json`. `node_modules/` stays out, and `.gitignore` already says so.",
            "Make the report bold without `report.js` knowing picocolors exists. `formatReport(summary, style)` takes a `style` object with a `bold` function. It passes the first line and the total line through `style.bold`. With no style, the text stays plain. `summarize(text, style)` passes the style on. Only `cli.js` imports picocolors, and hands it inward — the picocolors object has a `bold` function, so it fits.",
            "Give `cli.js` a `--plain` flag, so `node src/cli.js sample.log --plain` prints no colour codes, even in a terminal. Find how in **picocolors' own documentation**, for the version your lockfile says you have. Not a forum, not an AI answer: the package's README. Then answer question 3 below."
          ],
          "ordered": true
        },
        {
          "p": "Tests: `test/dependency.test.js`, and the tests named `task 6`. The dependency\ntests read `package.json`, `package-lock.json` and `.gitignore` as files. None of\nthem loads picocolors, so they give the same answer with or without\n`node_modules/`."
        },
        {
          "p": "Once a `package-lock.json` is committed, the workflow runs `npm ci` before the\ntests. It installs exactly what the lockfile says, and stops with an error if\nthe lockfile and `package.json` disagree. After the tests it runs the program\non `sample.log`, so a dependency you use but never installed shows up there\ntoo."
        },
        {
          "h": "Your answers"
        },
        {
          "p": "Answer each in two to four sentences, under its question. A person reads these."
        },
        {
          "p": "**1. Where did the duration notation go, and why there?** Name one change to the\nnotation someone might ask for, and say which files it would touch in your\nversion."
        },
        {
          "p": "Your answer."
        },
        {
          "p": "**2. Which version of picocolors did you get?** Copy the lockfile entry that\nsays so: the key and its `\"version\"` line. Then: a teammate clones your\nrepository a year from now, after a newer picocolors is out, and runs `npm ci`.\nWhich version do they get, and which file decides it?"
        },
        {
          "p": "Your answer."
        },
        {
          "p": "**3. How does `--plain` turn colour off?** Quote the line of picocolors'\ndocumentation you used, and link to it. Say how you know that page describes\nthe version in your lockfile, and not only the newest one."
        },
        {
          "p": "Your answer."
        },
        {
          "p": "**4. Why does `report.js` take a style instead of importing picocolors?** Say\nwhat would change about testing `report.js`, and about its promise, if it\nimported the package itself."
        },
        {
          "p": "Your answer."
        },
        {
          "h": "The review"
        },
        {
          "p": "`review/loans.js` and `review/fines.js` are code an AI assistant wrote when\nasked to split some library code into two modules. The header of `loans.js`\nalso shows two setup changes it proposed. It is illustrative code, written for\nthis review."
        },
        {
          "p": "It has problems, of the kinds this module is about, and at least one from\nearlier in the course. Find at least three. For each one, write in `REVIEW.md`:"
        },
        {
          "list": [
            "**where** — the file and the lines;",
            "**what goes wrong** — in terms of what the code does, not only what it looks like;",
            "**a concrete input or command that shows it** — something the author could run and see fail;",
            "**the fix**."
          ]
        },
        {
          "p": "You can run the review code yourself. This is a start:"
        },
        {
          "code": "node -e 'import(\"./review/loans.js\")'",
          "lang": "bash"
        },
        {
          "p": "Finish with a short summary for the author. The tests do not check `REVIEW.md`.\nA person reads it."
        },
        {
          "h": "What done means"
        },
        {
          "list": [
            "`npm test` passes, locally and in the **Actions** tab.",
            "`npm run report` prints the report, and `node src/cli.js sample.log --plain` prints it with no colour codes.",
            "`package.json` and `package-lock.json` are both committed, and agree.",
            "The four answers above are filled in.",
            "`REVIEW.md` has at least three problems and a summary."
          ]
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for this homework (`.vscode/settings.json`), so the code you hand\nin is your own. Chat is on, and it follows `AGENTS.md`: ask it about an error,\na failing test or an idea."
        }
      ],
      "_source": "assignments/cs140/modules-assignment"
    },
    "final-toolkit": {
      "kind": "assignment",
      "title": "The final: a utility library of your own",
      "lede": "About forty small functions in the spirit of Lodash, in five categories, one for each part of the course. Each problem opens when the module that teaches it does. There are far more points than you need: each category counts up to 45, and the final is graded out of 180.",
      "exercises": [
        "types.equality",
        "types.truthiness",
        "types.nullability",
        "flow.loop-forms",
        "arr.traversal",
        "obj.keys",
        "data.mutation-vs-rebinding",
        "flow.mutation-during-traversal",
        "fn.higher-order",
        "fn.callbacks",
        "scope.closure",
        "model.encapsulation",
        "mod.interface",
        "err.as-value",
        "err.recoverable",
        "async.promises",
        "async.scheduling",
        "diag.ai-evaluation"
      ],
      "repo": {
        "name": "cs140-final",
        "org": "WWCC-Ferraro",
        "studentsOrg": "WWCC-Students",
        "published": true
      },
      "body": [
        {
          "p": "[Lodash](https://lodash.com) is a JavaScript library of small, general\nfunctions: split an array into chunks, pick some properties out of an object,\ncall a function only once. Millions of programs use it. In this final you write\nyour own versions of about forty functions like them, most of them Lodash's\nown."
        },
        {
          "p": "The problems are grouped into five categories, one for each part of the\ncourse. The whole final is here from the start of the term, so you can see what\nyou are building towards. Each problem **opens** when the module that teaches\nwhat it needs is finished: once you have finished *Loops*, every problem marked\n\"opens after Loops\" uses only what you have been taught. Work on problems as\nthey open; you do not have to save the final for the end."
        },
        {
          "h": "How the final is scored"
        },
        {
          "p": "The final is graded out of **180 points**, and it offers far more than that."
        },
        {
          "table": {
            "head": [
              "Category",
              "Offered",
              "Counts up to"
            ],
            "rows": [
              [
                "Values and types",
                "70",
                "45"
              ],
              [
                "Arrays and objects",
                "75",
                "45"
              ],
              [
                "Functions",
                "85",
                "45"
              ],
              [
                "References, classes and modules",
                "105",
                "45"
              ],
              [
                "Errors and async",
                "80",
                "45"
              ]
            ]
          }
        },
        {
          "p": "Each category counts up to 45 points, however many you earn in it. Five\ncategories of 45 make 225, so you can reach 180 without every problem, and\nwithout every category. You cannot reach it from two or three categories alone,\nthough: a full score shows most of the course, not one corner of it."
        },
        {
          "p": "**Why it works this way.** You are learning all term, and a final that counted\nevery slip would mostly measure the slips. A student who mastered closures\nand promises should not end up with a B because of something small in the\nfirst module. With more points than you need, a problem you never quite got, or\none you get slightly wrong, can be made up by the rest of your work."
        },
        {
          "p": "**Each problem's points.** A problem whose tests all pass earns its full\npoints. A problem whose tests fail can still earn part of them, depending on\nhow close it is: a small slip costs a point or two, a real attempt keeps part\nof the problem's points, and a problem that was not attempted earns nothing.\nOne stray character can fail every test, so the tests alone do not decide\nthe score."
        },
        {
          "p": "To see where you stand:"
        },
        {
          "code": "npm run score",
          "lang": "sh"
        },
        {
          "p": "It lists every problem, and adds up each category and the total the way the\nfinal is graded. It counts only problems whose tests all pass, so it shows the\nleast you have so far. The same table appears on GitHub each time you push:\nopen the **Actions** tab, then the latest run."
        },
        {
          "h": "Getting started"
        },
        {
          "list": [
            "Open **your repository**. It is made for you: private, and named for the final, the term and your username, `<term>-cs140-final-<you>`. - **In a Codespace:** on your repository's page on GitHub, click **Code**,   then the **Codespaces** tab, then **Create codespace on main**. The next   time, the same tab lists the Codespace you already have; open that one. - **On your own computer:** clone it with GitHub Desktop (**Code**, then   **Open with GitHub Desktop**), and check that `node --version` prints 22   or later.  The lesson *How a homework works* walks through both, and the final works the same way as every homework.",
            "Run the score:  ```sh npm run score ```  Every problem shows 0. That is the starting point."
          ],
          "ordered": true
        },
        {
          "p": "The final runs all term, so it is worth knowing your way around:"
        },
        {
          "table": {
            "head": [
              "To",
              "Run"
            ],
            "rows": [
              [
                "See where you stand",
                "`npm run score`"
              ],
              [
                "See one category, with the first failing test of each problem",
                "`npm run score -- functions`"
              ],
              [
                "See one problem",
                "`npm run score -- chunk`"
              ],
              [
                "See every failing test of one problem",
                "`node --test test/collections/chunk.test.js`"
              ],
              [
                "Run every test at once",
                "`npm test`"
              ]
            ]
          }
        },
        {
          "p": "**In a Codespace, push, then stop it when you are done.** A running\nCodespace uses your free monthly hours, and it keeps running for 30 minutes\nafter you close the tab. A stopped Codespace keeps its files."
        },
        {
          "h": "Working on the final"
        },
        {
          "p": "**Write each answer between the function's braces**, where it says\n`// TODO: put your code here`. Leave `export` in front of each function: it is\nwhat lets the tests, and other files, use it."
        },
        {
          "p": "**Use what the course has taught by the time the problem opens.** The final\nis evidence of what you learned in this course. JavaScript has many built-in\nfunctions that are not taught here; a solution built on one of them is not\nwrong, but it shows less of what the problem is about, and it is one of the\nthings that gets a closer look. A few problems rule out the built-in that\nwould do the whole job, such as `padStart` for `pad`. Each of those says so."
        },
        {
          "p": "**Commit and push as you finish each problem.** Your history is part of how\nthe final is read. A final built up over the term reads differently from one\nthat arrives all at once at the end."
        },
        {
          "p": "**It is your own work.** Help from other people, from the documentation or\nfrom the web is fine. List it in a comment at the top of the file it helped\nwith, as the syllabus asks. Copying a solution is not."
        },
        {
          "p": "**You may be asked to show your work.** Depending on what is handed in, the\ninstructor may ask you to make a small change to some of your code while they\nwatch, and to explain it. That is most likely when:"
        },
        {
          "list": [
            "the work arrives all at once;",
            "it uses things this course has not taught;",
            "it matches another student's, line for line;",
            "or it is the kind of code that is hard to explain without having written it."
          ]
        },
        {
          "p": "What you show then counts for those problems. A student who wrote their own\ncode has nothing to prepare."
        },
        {
          "h": "How a review problem works"
        },
        {
          "p": "Each category ends with a review problem. An AI assistant was given a request,\nand its answer is in `src/review/<name>/generated.js`, with the request at the\ntop. The answer runs, and for many inputs it is right. For some, it is not.\nReading generated code for that kind of defect is what *Judging generated\ncode* teaches, so every review problem opens after that module."
        },
        {
          "p": "Each review problem has three steps, all in its folder in `src/review/`:"
        },
        {
          "list": [
            "**Show the defect.** In `example.js`, write `example`: a function that takes the generated function (or class), calls it the way that goes wrong, and returns the result. Set `expected` to what a correct version returns for that call. The tests run your example on `generated.js`, where it should come out wrong, and on your `fixed.js`, where it should come out right.",
            "**Fix it** in `fixed.js`, which starts as a copy of `generated.js`. Fix what the request asked for, not only your example: when the final is graded, the fix is also tested on inputs you do not see.",
            "**Say what was wrong**, in a sentence or two, in the comment at the end of `example.js`."
          ],
          "ordered": true
        },
        {
          "p": "Leave `generated.js` as it is. The tests read it, and the copy that is graded\nis the original."
        },
        {
          "h": "Using an AI assistant"
        },
        {
          "p": "`AGENTS.md` in this repository tells AI coding assistants how this course wants\nthem to help: as a tutor who explains errors, asks questions and gives hints,\nnot by writing your answers. Most assistants read it automatically. It is in\nthe open, so read it too. It says what good AI help looks like. For the review\nproblems, it also asks assistants not to point out the defect: finding it is\nthe problem."
        },
        {
          "p": "In VS Code, in a Codespace or on your own computer, AI code suggestions are\nswitched off for the final (`.vscode/settings.json`), so the code you hand in\nis your own. Chat is on, and it follows `AGENTS.md`: ask it about an error, a\nfailing test or an idea."
        },
        {
          "h": "The problems"
        },
        {
          "p": "Every problem is in its own file. The file's comment says the same as its\nsection here, with examples."
        },
        {
          "h": "Values and types"
        },
        {
          "p": "Outcomes 1 and 3. This category offers **70 points**, and up to **45** of them count."
        },
        {
          "table": {
            "head": [
              "Problem",
              "Points",
              "Opens after",
              "File"
            ],
            "rows": [
              [
                "`isString`",
                "5",
                "Values, types and operators",
                "`src/values/is-string.js`"
              ],
              [
                "`isNumber`",
                "5",
                "Values, types and operators",
                "`src/values/is-number.js`"
              ],
              [
                "`capitalize`",
                "5",
                "Values, types and operators",
                "`src/values/capitalize.js`"
              ],
              [
                "`clamp`",
                "5",
                "Values, types and operators",
                "`src/values/clamp.js`"
              ],
              [
                "`inRange`",
                "10",
                "Branching",
                "`src/values/in-range.js`"
              ],
              [
                "`pad`",
                "10",
                "Loops",
                "`src/values/pad.js`"
              ],
              [
                "`range`",
                "10",
                "Arrays",
                "`src/values/range.js`"
              ],
              [
                "`isEmpty`",
                "10",
                "Objects",
                "`src/values/is-empty.js`"
              ],
              [
                "Review: `defaultTo`",
                "10",
                "Judging generated code",
                "`src/review/default-to/`"
              ]
            ]
          }
        },
        {
          "h": "`isString(value)`"
        },
        {
          "p": "Return `true` when `value` is a string, and `false` for anything else."
        },
        {
          "code": "isString(\"abc\")    // true\nisString(\"\")       // true: no characters, but still a string\nisString(5)        // false\nisString([\"a\"])    // false: an array that holds a string is an array",
          "lang": "js"
        },
        {
          "h": "`isNumber(value)`"
        },
        {
          "p": "Return `true` when `value` is a number, and `false` for anything else."
        },
        {
          "code": "isNumber(3)        // true\nisNumber(NaN)      // true: NaN means \"not a number\", but its type is number\nisNumber(\"3\")      // false: digits in quotes are a string",
          "lang": "js"
        },
        {
          "h": "`capitalize(string)`"
        },
        {
          "p": "Return `string` with its first character in capitals and every other\ncharacter in lowercase."
        },
        {
          "code": "capitalize(\"fred\")          // \"Fred\"\ncapitalize(\"FRED\")          // \"Fred\"\ncapitalize(\"hello world\")   // \"Hello world\": only the first character\ncapitalize(\"\")              // \"\"",
          "lang": "js"
        },
        {
          "h": "`clamp(number, lower, upper)`"
        },
        {
          "p": "Return `number`, held between `lower` and `upper`. A number below `lower`\nbecomes `lower`, a number above `upper` becomes `upper`, and a number between\nthem comes back as it is. `lower` is never more than `upper`."
        },
        {
          "code": "clamp(10, -5, 5)    // 5\nclamp(-10, -5, 5)   // -5\nclamp(3, -5, 5)     // 3",
          "lang": "js"
        },
        {
          "h": "`inRange(number, start, end)`"
        },
        {
          "p": "Return `true` when `number` is at least `start` and less than `end`: the start\ncounts, and the end does not. With only two arguments, the range runs from 0\nto the second one. If `start` is more than `end`, swap them first, so a range\ngiven backwards still works."
        },
        {
          "code": "inRange(3, 2, 4)     // true\ninRange(4, 2, 4)     // false: the end does not count\ninRange(2, 2, 4)     // true: the start does\ninRange(4, 8)        // true: 0 up to 8\ninRange(-3, -2, -6)  // true: -6 up to -2, after the swap",
          "lang": "js"
        },
        {
          "h": "`pad(string, length, chars = \" \")`"
        },
        {
          "p": "Return `string` padded on both sides to `length` characters, so it sits in the\nmiddle. The padding is `chars`, repeated as many times as it takes and cut off\nwhere it runs out; each side starts again from the beginning of `chars`. If the\npadding cannot split evenly, the right side gets the extra character. If\n`string` is already `length` characters or longer, return it as it is."
        },
        {
          "p": "`chars` is a space unless another is given; `= \" \"` in the function's first\nline sets that default."
        },
        {
          "code": "pad(\"abc\", 8)         // \"  abc   \"\npad(\"abc\", 8, \"_-\")   // \"_-abc_-_\"\npad(\"abc\", 2)         // \"abc\"",
          "lang": "js"
        },
        {
          "p": "Do not use `padStart`, `padEnd` or `repeat`. Each of them builds the padding\nfor you, and building it is the problem."
        },
        {
          "h": "`range(start, end, step)`"
        },
        {
          "p": "Return an array of numbers from `start` up to, but not including, `end`, going\nup by `step` each time. With one argument, the numbers run from 0 to that\nargument. Without a `step`, the step is 1 when `end` is more than `start`, and\n-1 when it is less. A step that goes the wrong way never reaches `end`, so the\narray is empty."
        },
        {
          "code": "range(4)            // [0, 1, 2, 3]\nrange(-4)           // [0, -1, -2, -3]\nrange(1, 5)         // [1, 2, 3, 4]\nrange(0, 20, 5)     // [0, 5, 10, 15]\nrange(5, 1)         // [5, 4, 3, 2]\nrange(1, 4, -1)     // []",
          "lang": "js"
        },
        {
          "p": "*Checking input to chunk and range*, in Errors and async, adds two checks to\nthis function later."
        },
        {
          "h": "`isEmpty(value)`"
        },
        {
          "p": "Return `true` when `value` has nothing in it. A string is empty when it has no\ncharacters, an array when it has no elements, and an object when it has no\nproperties. `null` and `undefined` are empty. A number or a boolean has no\ncontents at all, so it counts as empty."
        },
        {
          "code": "isEmpty(\"\")           // true\nisEmpty([])           // true: [] is truthy, but there is nothing in it\nisEmpty({ a: 1 })     // false\nisEmpty(1)            // true: a number holds nothing",
          "lang": "js"
        },
        {
          "h": "Review: `defaultTo`"
        },
        {
          "p": "An AI assistant was asked for `defaultTo(value, fallback)`, which returns\n`fallback` when `value` is `null`, `undefined` or `NaN`, and `value`\notherwise. Its answer is in `src/review/default-to/generated.js`. It runs, and\nfor many inputs it is right. *How a review problem works*, above, says what to\nhand in."
        },
        {
          "h": "Arrays and objects"
        },
        {
          "p": "Outcomes 2, 3 and 6. This category offers **75 points**, and up to **45** of them count."
        },
        {
          "table": {
            "head": [
              "Problem",
              "Points",
              "Opens after",
              "File"
            ],
            "rows": [
              [
                "`nth`",
                "5",
                "Arrays",
                "`src/collections/nth.js`"
              ],
              [
                "`chunk`",
                "10",
                "Arrays",
                "`src/collections/chunk.js`"
              ],
              [
                "`unique`",
                "10",
                "Arrays",
                "`src/collections/unique.js`"
              ],
              [
                "`keys`",
                "5",
                "Objects",
                "`src/collections/keys.js`"
              ],
              [
                "`pick`",
                "10",
                "Objects",
                "`src/collections/pick.js`"
              ],
              [
                "`omit`",
                "10",
                "Objects",
                "`src/collections/omit.js`"
              ],
              [
                "`get`",
                "15",
                "Objects",
                "`src/collections/get.js`"
              ],
              [
                "Review: `without`",
                "10",
                "Judging generated code",
                "`src/review/without/`"
              ]
            ]
          }
        },
        {
          "p": "Every function in this category returns a new array or a new object, and none\nof them changes the one it was given. The tests check that. A caller who hands\nan array to `chunk` still has their array afterwards, exactly as it was."
        },
        {
          "h": "`nth(array, n = 0)`"
        },
        {
          "p": "Return the element of `array` at index `n`. A negative `n` counts from the\nend: -1 is the last element, -2 the one before it. If there is no element at\nthat position, return `undefined`. `n` is 0 unless another is given; `= 0` in\nthe function's first line sets that default."
        },
        {
          "code": "nth([\"a\", \"b\", \"c\", \"d\"], 1)    // \"b\"\nnth([\"a\", \"b\", \"c\", \"d\"], -1)   // \"d\"\nnth([\"a\", \"b\", \"c\", \"d\"])       // \"a\"\nnth([\"a\", \"b\", \"c\", \"d\"], 9)    // undefined",
          "lang": "js"
        },
        {
          "p": "In JavaScript, `array[-1]` is `undefined`: a negative index is not counted from\nthe end for you. Some languages do count it that way. Python's `items[-1]` is\nthe last element. Do not use the array method `at`, which does exactly what\n`nth` does."
        },
        {
          "h": "`chunk(array, size)`"
        },
        {
          "p": "Split `array` into smaller arrays of `size` elements each, and return an array\nof them. If the elements do not divide evenly, the last chunk holds what is\nleft over."
        },
        {
          "code": "chunk([\"a\", \"b\", \"c\", \"d\"], 2)   // [[\"a\", \"b\"], [\"c\", \"d\"]]\nchunk([\"a\", \"b\", \"c\", \"d\"], 3)   // [[\"a\", \"b\", \"c\"], [\"d\"]]\nchunk([], 2)                     // []",
          "lang": "js"
        },
        {
          "p": "*Checking input to chunk and range*, in Errors and async, adds two checks to\nthis function later."
        },
        {
          "h": "`unique(array)`"
        },
        {
          "p": "Return a new array holding the first occurrence of each element of `array`, in\nthe order they first appear. `NaN` counts as equal to `NaN` here, so it appears\nonce. Two different objects or arrays are different elements, even when they\nhold the same things."
        },
        {
          "code": "unique([2, 1, 2])          // [2, 1]\nunique([\"a\", \"b\", \"a\"])    // [\"a\", \"b\"]\nunique([NaN, 1, NaN])      // [NaN, 1]",
          "lang": "js"
        },
        {
          "p": "Do not use a `Set`. A `Set` keeps one of each value for you, which is the whole\nof this problem."
        },
        {
          "h": "`keys(object)`"
        },
        {
          "p": "Return an array of the names of `object`'s properties, in the order the object\nlists them."
        },
        {
          "code": "keys({ a: 1, b: 2 })      // [\"a\", \"b\"]\nkeys({})                  // []",
          "lang": "js"
        },
        {
          "p": "Do not use `Object.keys`, `Object.values` or `Object.entries`. Each of them\nlists the properties for you."
        },
        {
          "h": "`pick(object, keys)`"
        },
        {
          "p": "Return a new object holding only the properties of `object` whose names are in\nthe array `keys`. A name in `keys` that `object` does not have is left out of\nthe result. It is not added with the value `undefined`."
        },
        {
          "code": "pick({ a: 1, b: 2, c: 3 }, [\"a\", \"c\"])   // { a: 1, c: 3 }\npick({ a: 1 }, [\"a\", \"z\"])               // { a: 1 }",
          "lang": "js"
        },
        {
          "h": "`omit(object, keys)`"
        },
        {
          "p": "Return a new object holding every property of `object` except those whose names\nare in the array `keys`."
        },
        {
          "code": "omit({ a: 1, b: 2, c: 3 }, [\"b\"])   // { a: 1, c: 3 }\nomit({ a: 1 }, [\"z\"])               // { a: 1 }",
          "lang": "js"
        },
        {
          "p": "Deleting the unwanted properties from `object` would return the right-looking\nanswer, but the caller would lose those properties too, so build the new object\ninstead."
        },
        {
          "h": "`get(object, path, defaultValue)`"
        },
        {
          "p": "Follow `path` down into `object` and return the value at the end of it. `path`\nis an array of property names and indexes, one for each step down:\n`[\"user\", \"pets\", 0]` reads `object.user.pets[0]`."
        },
        {
          "p": "If a step on the way meets `null` or `undefined`, there is nothing further down\nto read. Return `defaultValue` instead of stopping with an error. Also return\n`defaultValue` when the value at the end is `undefined`. A value of `null` at\nthe end is returned as `null`: someone chose to store it."
        },
        {
          "code": "const data = { user: { name: \"Ada\", pets: [\"Rex\", \"Tom\"] } };\n\nget(data, [\"user\", \"name\"])            // \"Ada\"\nget(data, [\"user\", \"pets\", 1])         // \"Tom\"\nget(data, [\"user\", \"age\"], 0)          // 0: no age property\nget(data, [\"team\", \"name\"], \"none\")    // \"none\": no team, so no name",
          "lang": "js"
        },
        {
          "p": "Lodash's own `get` also takes the path as one string, `\"user.pets.0\"`. This one\ntakes an array, so there is no string to take apart."
        },
        {
          "h": "Review: `without`"
        },
        {
          "p": "An AI assistant was asked for `without(array, ...values)`, which returns a new\narray holding every element of `array` that is not one of `values`, and must\nnot change `array`. Its answer is in `src/review/without/generated.js`. It\nruns, and for many inputs it is right. *How a review problem works*, above,\nsays what to hand in."
        },
        {
          "h": "Functions"
        },
        {
          "p": "Outcomes 4 and 5. This category offers **85 points**, and up to **45** of them count."
        },
        {
          "table": {
            "head": [
              "Problem",
              "Points",
              "Opens after",
              "File"
            ],
            "rows": [
              [
                "`isFunction`",
                "5",
                "Functions",
                "`src/functions/is-function.js`"
              ],
              [
                "`forEach`",
                "5",
                "Functions as values",
                "`src/functions/for-each.js`"
              ],
              [
                "`map`",
                "5",
                "Functions as values",
                "`src/functions/map.js`"
              ],
              [
                "`filter`",
                "5",
                "Functions as values",
                "`src/functions/filter.js`"
              ],
              [
                "`reject`",
                "5",
                "Functions as values",
                "`src/functions/reject.js`"
              ],
              [
                "`times`",
                "5",
                "Functions as values",
                "`src/functions/times.js`"
              ],
              [
                "`reduce`",
                "10",
                "Functions as values",
                "`src/functions/reduce.js`"
              ],
              [
                "`once`",
                "10",
                "Functions as values",
                "`src/functions/once.js`"
              ],
              [
                "`wrap`",
                "10",
                "Functions as values",
                "`src/functions/wrap.js`"
              ],
              [
                "`memoize`",
                "15",
                "Functions as values",
                "`src/functions/memoize.js`"
              ],
              [
                "Review: `negate`",
                "10",
                "Judging generated code",
                "`src/review/negate/`"
              ]
            ]
          }
        },
        {
          "p": "`forEach`, `map`, `filter`, `reject`, `times` and `reduce` each do a job that\nan array method already does. Do not use the array methods `forEach`, `map`,\n`filter` or `reduce` in them, or `Array.from` in `times`: walking the array\nyourself is the problem. Your own functions are a different matter. `reject`\nmay call your `filter`, and its file already imports it, but that is a choice,\nnot a requirement."
        },
        {
          "h": "`isFunction(value)`"
        },
        {
          "p": "Return `true` when `value` is a function, and `false` for anything else."
        },
        {
          "code": "isFunction((n) => n * 2)    // true\nisFunction(Math.max)        // true: a built-in function is a function\nisFunction(\"function\")      // false: the word in quotes is a string\nisFunction(null)            // false",
          "lang": "js"
        },
        {
          "h": "`forEach(array, iteratee)`"
        },
        {
          "p": "Call `iteratee` once for each element of `array`, in order, with three\narguments: the element, its index, and the array itself. Return the same array\nthat was passed in. Whatever `iteratee` returns is not used."
        },
        {
          "code": "forEach([\"a\", \"b\"], (element, index) => console.log(element, index))\n// logs a 0, then b 1, and returns [\"a\", \"b\"]: the array that was passed in",
          "lang": "js"
        },
        {
          "h": "`map(array, iteratee)`"
        },
        {
          "p": "Return a new array holding what `iteratee` returns for each element of\n`array`. `iteratee` is called with the element, its index, and the array\nitself. The array that was passed in does not change."
        },
        {
          "code": "map([1, 2, 3], (n) => n * 2)                // [2, 4, 6]\nmap([\"a\", \"b\"], (letter, i) => letter + i)  // [\"a0\", \"b1\"]",
          "lang": "js"
        },
        {
          "h": "`filter(array, predicate)`"
        },
        {
          "p": "Return a new array holding the elements of `array` for which `predicate`\nreturns a truthy value. That is any truthy value, not only `true`. `predicate`\nis called with the element, its index, and the array itself. The array that\nwas passed in does not change."
        },
        {
          "code": "filter([1, 2, 3, 4], (n) => n % 2 === 0)   // [2, 4]\nfilter([1, 2, 3], (n) => n - 2)            // [1, 3]: -1 and 1 are truthy; 0 is not",
          "lang": "js"
        },
        {
          "h": "`reject(array, predicate)`"
        },
        {
          "p": "Return a new array holding the elements of `array` that `filter` would leave\nout: those for which `predicate` returns a falsy value. `predicate` is called\nwith the element, its index, and the array itself. The array that was passed\nin does not change."
        },
        {
          "code": "reject([1, 2, 3, 4], (n) => n % 2 === 0)   // [1, 3]\nreject([1, 2, 3], (n) => n - 2)            // [2]",
          "lang": "js"
        },
        {
          "h": "`times(n, iteratee)`"
        },
        {
          "p": "Call `iteratee` `n` times, with 0, then 1, and so on up to `n - 1`, and return\nan array of what it returns. When `n` is 0 or less, return an empty array."
        },
        {
          "code": "times(3, (i) => i * 10)   // [0, 10, 20]\ntimes(0, (i) => i)        // []",
          "lang": "js"
        },
        {
          "h": "`reduce(array, iteratee, accumulator)`"
        },
        {
          "p": "Walk `array`, combining its elements into one value, and return that value.\nFor each element, call `iteratee` with four arguments: the value so far (the\n*accumulator*), the element, its index, and the array itself. What `iteratee`\nreturns becomes the accumulator for the next element."
        },
        {
          "p": "`accumulator` is the starting value. When it is not given, it is `undefined`,\nand the first element is the starting value instead: the walk then starts at\nindex 1. An empty array with no starting value returns `undefined`."
        },
        {
          "code": "reduce([1, 2, 3], (sum, n) => sum + n, 0)      // 6\nreduce([\"a\", \"b\", \"c\"], (all, s) => all + s)   // \"abc\": starts from \"a\"\nreduce([], (sum, n) => sum + n, 10)            // 10",
          "lang": "js"
        },
        {
          "h": "`once(fn)`"
        },
        {
          "p": "Return a new function. The first time it is called, it calls `fn` with the\narguments it was given and returns what `fn` returns. Every later call returns\nthat same first result, without calling `fn` again, whatever arguments it is\ngiven. `fn` may return anything, `undefined` and `0` included, and it is still\ncalled only once."
        },
        {
          "code": "const setUp = once((name) => \"ready, \" + name);\nsetUp(\"Ada\")     // \"ready, Ada\"\nsetUp(\"Grace\")   // \"ready, Ada\": fn is not called again",
          "lang": "js"
        },
        {
          "h": "`wrap(value, wrapper)`"
        },
        {
          "p": "Return a new function. When it is called, it calls `wrapper` with `value`\nfirst, followed by every argument it was given, and returns what `wrapper`\nreturns."
        },
        {
          "code": "const greet = wrap(\"Hello\", (greeting, name) => greeting + \", \" + name);\ngreet(\"Ada\")    // \"Hello, Ada\"\n\nconst add = wrap(10, (start, a, b) => start + a + b);\nadd(1, 2)       // 13",
          "lang": "js"
        },
        {
          "h": "`memoize(fn, resolver)`"
        },
        {
          "p": "Return a new function, `memoized`, that remembers the results of `fn`. Each\ncall works out a *key*. With a `resolver`, the key is what\n`resolver(...args)` returns; without one, the key is the first argument. The\nfirst call with a key calls `fn` with all the arguments and remembers its\nresult under that key. A later call with the same key returns the remembered\nresult without calling `fn`, even when that result is `undefined`, `0` or\nanother falsy value."
        },
        {
          "p": "The remembered results are in a `Map`, `memoized.cache`, so a caller can read\nthem, or clear them with `memoized.cache.clear()` to make `fn` run again."
        },
        {
          "code": "const double = memoize((n) => n * 2);\ndouble(4)              // 8: fn is called\ndouble(4)              // 8: remembered, so fn is not called\ndouble.cache.get(4)    // 8\n\nconst add = memoize((a, b) => a + b, (a, b) => a + \",\" + b);\nadd(1, 2)              // 3\nadd(1, 3)              // 4: a different key, \"1,3\"",
          "lang": "js"
        },
        {
          "h": "Review: `negate`"
        },
        {
          "p": "An AI assistant was asked for `negate(predicate)`, which returns a function\nthat takes any number of arguments, calls `predicate` with all of them, and\nreturns the opposite boolean. Its answer is in `src/review/negate/generated.js`.\nIt runs, and for many inputs it is right. *How a review problem works*, above,\nsays what to hand in."
        },
        {
          "h": "References, classes and modules"
        },
        {
          "p": "Outcomes 2, 6 and 7. This category offers **105 points**, and up to **45** of them count."
        },
        {
          "table": {
            "head": [
              "Problem",
              "Points",
              "Opens after",
              "File"
            ],
            "rows": [
              [
                "`pull`",
                "15",
                "Values and references",
                "`src/references/pull.js`"
              ],
              [
                "`isMatch`",
                "10",
                "Values and references",
                "`src/references/is-match.js`"
              ],
              [
                "`defaults`",
                "10",
                "Values and references",
                "`src/references/defaults.js`"
              ],
              [
                "`cloneDeep`",
                "15",
                "Values and references",
                "`src/references/clone-deep.js`"
              ],
              [
                "`isEqual`",
                "15",
                "Values and references",
                "`src/references/is-equal.js`"
              ],
              [
                "`chain` and `Chain`",
                "20",
                "Objects and classes",
                "`src/references/chain.js`"
              ],
              [
                "The toolkit's index",
                "10",
                "Modules and dependencies",
                "`src/index.js`"
              ],
              [
                "Review: `Queue`",
                "10",
                "Judging generated code",
                "`src/review/queue/`"
              ]
            ]
          }
        },
        {
          "h": "`pull(array, ...values)`"
        },
        {
          "p": "Remove every occurrence of each of `values` from `array` itself, and return\nthat same array."
        },
        {
          "code": "const list = [1, 2, 3, 2];\npull(list, 2)                    // [1, 3]\nlist                             // [1, 3]: the same array, changed\npull([1, 2, 2, 3], 2)            // [1, 3]: both 2s, even side by side\npull([\"a\", \"b\", \"c\"], \"a\", \"c\")  // [\"b\"]",
          "lang": "js"
        },
        {
          "p": "`omit` and the review problem's `without` must leave their input alone, and\n`pull` must change it. Both kinds are legitimate, and Lodash has both: `pull`\nchanges the array, `without` returns a new one. A caller cannot tell which\nkind a function is by calling it once, so the name and the documentation have\nto say. In this toolkit, `pull` and `defaults` are the only functions that\nchange what they are given."
        },
        {
          "p": "Do not use `filter`. It returns a new array, and `pull` changes the one it was\ngiven."
        },
        {
          "h": "`isMatch(object, source)`"
        },
        {
          "p": "Return `true` when `object` has every property that `source` has, each with an\nequal value. `object` may have other properties too; they do not matter."
        },
        {
          "list": [
            "A property that `source` has and `object` does not is not a match, even when the value in `source` is `undefined`.",
            "Equal means `===`, except that `NaN` matches `NaN`.",
            "One level only: a value that is an object or an array matches only when it is the very same object, not another one with the same contents."
          ]
        },
        {
          "code": "isMatch({ a: 1, b: 2 }, { a: 1 })          // true\nisMatch({ a: 1 }, { a: 1, b: 2 })          // false: object has no b\nisMatch({ a: NaN }, { a: NaN })            // true\nisMatch({ tags: [\"x\"] }, { tags: [\"x\"] })  // false: two different arrays\nisMatch({ a: 1 }, {})                      // true: nothing to check",
          "lang": "js"
        },
        {
          "p": "Lodash's own `isMatch` compares contents all the way down, so in Lodash the\n`tags` example is `true`. This one stops at one level on purpose; `isEqual`,\nbelow, goes all the way down. Two arrays\nwith the same contents are equal in one sense and different in another, and\nthis function makes you choose: it is the question from *When two objects are\nequal*."
        },
        {
          "h": "`defaults(object, ...sources)`"
        },
        {
          "p": "Fill in the properties `object` is missing, from each of `sources` in turn, and\nreturn `object` itself. A property is filled in only when its value in\n`object` is `undefined`: a property that is `null`, `0` or `\"\"` has a value,\nand keeps it. When two sources both have a property `object` is missing, the\nfirst source wins. The sources themselves do not change."
        },
        {
          "code": "const settings = { size: 12, color: null };\ndefaults(settings, { size: 10, font: \"serif\" })\n                        // { size: 12, color: null, font: \"serif\" }\nsettings                // the same object, now with font",
          "lang": "js"
        },
        {
          "p": "Do not use `Object.assign`. It copies every property, and `defaults` copies\nonly some."
        },
        {
          "h": "`cloneDeep(value)`"
        },
        {
          "p": "Return a copy of `value` that shares nothing with it, at any depth. An array\nbecomes a new array, and each of its elements is copied the same way; an\nobject becomes a new object, and each property's value is copied the same way.\nAnything else (a number, a string, a boolean, `null`, `undefined`, a function)\ncomes back as it is. Changing the copy, however deep, never changes `value`."
        },
        {
          "code": "const original = { name: \"Ada\", pets: [{ kind: \"cat\" }] };\nconst copy = cloneDeep(original);\ncopy.pets[0].kind = \"dog\";\noriginal.pets[0].kind   // \"cat\"",
          "lang": "js"
        },
        {
          "p": "Copying one level, as spread does, is not enough: the nested arrays and\nobjects would still be shared. Each nested one has to be copied the same way as\nthe whole, which is the job *Walking nested data with recursion* describes. Do\nnot use `structuredClone`, `JSON.parse` or `JSON.stringify`: each of them\ncopies every level for you, and copying each level yourself is the problem."
        },
        {
          "h": "`isEqual(a, b)`"
        },
        {
          "p": "Return `true` when `a` and `b` hold the same values, at every depth."
        },
        {
          "list": [
            "Two arrays are equal when they have the same length and their elements are equal, in order.",
            "Two objects are equal when they have the same property names, in any order, and each property's values are equal.",
            "An array and an object are never equal, even `[1]` and `{ 0: 1 }`.",
            "Anything else is equal when it is `===`, except that `NaN` equals `NaN`."
          ]
        },
        {
          "code": "isEqual({ tags: [\"x\"] }, { tags: [\"x\"] })   // true: same contents\nisEqual([1, [2, 3]], [1, [2, 4]])           // false\nisEqual({ a: 1 }, { a: 1, b: 2 })           // false: b has a property a does not\nisEqual(NaN, NaN)                           // true",
          "lang": "js"
        },
        {
          "p": "This is the other answer to the question *When two objects are equal* asks:\n`===` asks whether two names point at the same object, and `isEqual` asks\nwhether two objects hold the same things."
        },
        {
          "h": "`chain(value)` and the class `Chain`"
        },
        {
          "p": "A `Chain` holds an array, and lets you call one method after another on it:"
        },
        {
          "code": "chain([1, 2, 3, 4])\n  .map((n) => n * 10)\n  .filter((n) => n > 15)\n  .take(1)\n  .value()              // [20]",
          "lang": "js"
        },
        {
          "list": [
            "Keep the array in a private field, so nothing outside the class can reach it except through `value()`.",
            "`map(fn)`, `filter(fn)` and `take(n)` each return a **new** `Chain` holding the new array. The `Chain` they were called on keeps the array it had.",
            "`take(n)` keeps the first `n` elements.",
            "`value()` returns the array.",
            "The array handed to `chain()` is never changed.",
            "`chain(value)` returns a new `Chain` holding `value`."
          ]
        },
        {
          "p": "The methods may use the built-in array methods or loops; either is fine here.\nWhat this problem is about is the class: its private state, and methods that\nreturn new objects instead of changing their own."
        },
        {
          "h": "The toolkit's index, `src/index.js`"
        },
        {
          "p": "Every function in the toolkit lives in its own file. `src/index.js` is the\ntoolkit's public face: a program that wants `chunk` and `map` should be able\nto write"
        },
        {
          "code": "import { chunk, map } from \"./index.js\";",
          "lang": "js"
        },
        {
          "p": "without knowing which file each one is in. Re-export every toolkit function\nfrom its own file, under its own name, and the `Chain` class as well. A\nre-export names what to export and the file it comes from:"
        },
        {
          "code": "export { isString } from \"./values/is-string.js\";",
          "lang": "js"
        },
        {
          "p": "The review problems' functions, in `src/review/`, are not part of the toolkit,\nso they are not exported. `problems.json` lists every problem with its file.\nThis problem counts every function, finished or not: re-exporting `memoize`\nworks whether or not `memoize` passes its own tests."
        },
        {
          "h": "Review: `Queue`"
        },
        {
          "p": "An AI assistant was asked for a `Queue` class: `enqueue(item)` adds an item at\nthe back, `dequeue()` removes and returns the item at the front, `size` is how\nmany items it holds, and `items()` returns the items, front to back. Nothing\noutside the class may change what the queue holds except `enqueue` and\n`dequeue`. Its answer is in `src/review/queue/generated.js`. It keeps its items\nin a private field, it runs, and for many uses it is right. *How a review\nproblem works*, above, says what to hand in. Here `example` takes the `Queue` class,\nuses it, and returns a result."
        },
        {
          "h": "Errors and async"
        },
        {
          "p": "Outcomes 8 and 9. This category offers **80 points**, and up to **45** of them count."
        },
        {
          "table": {
            "head": [
              "Problem",
              "Points",
              "Opens after",
              "File"
            ],
            "rows": [
              [
                "`attempt`",
                "10",
                "Errors and failure",
                "`src/failure/attempt.js`"
              ],
              [
                "Checking input to `chunk` and `range`",
                "10",
                "Errors and failure",
                "`src/collections/chunk.js`, `src/values/range.js`"
              ],
              [
                "`defer`",
                "5",
                "Asynchrony and runtime",
                "`src/failure/defer.js`"
              ],
              [
                "`settle`",
                "15",
                "Asynchrony and runtime",
                "`src/failure/settle.js`"
              ],
              [
                "`parallelLimit`",
                "15",
                "Asynchrony and runtime",
                "`src/failure/parallel-limit.js`"
              ],
              [
                "`debounce`",
                "15",
                "Asynchrony and runtime",
                "`src/failure/debounce.js`"
              ],
              [
                "Review: `mapAsync`",
                "10",
                "Judging generated code",
                "`src/review/map-async/`"
              ]
            ]
          }
        },
        {
          "h": "`attempt(fn, ...args)`"
        },
        {
          "p": "Call `fn` with the rest of the arguments, and return what it returns. If `fn`\nthrows, return the error instead of letting it go up to the caller, so\n`attempt` itself never throws. The failure comes back as a value: the choice\nthat the lesson *Returning a failure instead of throwing* weighs against\nthrowing it."
        },
        {
          "p": "A function can throw something that is not an `Error`, such as a string.\nReturn that as an `Error` whose message is the thrown value as a string:\n`new Error(String(thrown))`. A caller can then read `.message` from whatever\n`attempt` returns."
        },
        {
          "code": "attempt((a, b) => a + b, 2, 3)                  // 5\nattempt(() => { throw new TypeError(\"bad\"); })  // the TypeError, with the message \"bad\"\nattempt(() => { throw \"oops\"; })                // an Error with the message \"oops\"",
          "lang": "js"
        },
        {
          "h": "Checking input to `chunk` and `range`"
        },
        {
          "p": "This problem has no file of its own. It adds checks to two functions you have\nalready written: `chunk`, in Arrays and objects, and `range`, in Values and\ntypes. Put the checks at the top of each function, before it does anything\nelse, so bad input stops it before any work is done."
        },
        {
          "p": "Each check throws one of two kinds of error, and which one says what was wrong:"
        },
        {
          "list": [
            "a **`TypeError`** when an argument is the wrong type: a string where a number belongs;",
            "a **`RangeError`** when an argument is the right type, a number, but not a value the function can use."
          ]
        },
        {
          "p": "The checks for each function:"
        },
        {
          "table": {
            "head": [
              "Function",
              "Throw",
              "When"
            ],
            "rows": [
              [
                "`chunk(array, size)`",
                "`TypeError`",
                "`size` is not a number"
              ],
              [
                "",
                "`RangeError`",
                "`size` is less than 1, or not a whole number"
              ],
              [
                "`range(start, end, step)`",
                "`TypeError`",
                "`start` is not a number, or `end` or `step` is given and is not a number"
              ],
              [
                "",
                "`RangeError`",
                "`step` is 0"
              ]
            ]
          }
        },
        {
          "code": "chunk([1, 2, 3], \"2\")   // throws a TypeError\nchunk([1, 2, 3], 1.5)   // throws a RangeError\nrange(1, 5, 0)          // throws a RangeError: a step of 0 never reaches 5\nrange(3)                // [0, 1, 2]: an end or a step that is not given is fine",
          "lang": "js"
        },
        {
          "p": "Good input has to work exactly as before: the tests check that too. The\nmessage in each error is yours to choose. Write one that tells the caller what\nto change."
        },
        {
          "h": "`defer(fn, ...args)`"
        },
        {
          "p": "Call `fn` with the rest of the arguments, but not now: once the code that is\nrunning now has finished. A timer with a wait of 0 does that, because the\nruntime calls a timer's function only after the code running now has\nfinished. `defer` itself returns nothing."
        },
        {
          "code": "const order = [];\ndefer((word) => order.push(word), \"later\");\norder.push(\"now\");\n// order is [\"now\"] here, and [\"now\", \"later\"] once the code running now has finished",
          "lang": "js"
        },
        {
          "h": "`settle(promises)`"
        },
        {
          "p": "Wait for every promise in `promises`, and return a promise of an array that\nsays how each one ended, in the same order as `promises`:"
        },
        {
          "list": [
            "`{ status: \"fulfilled\", value }` for a promise that fulfilled, with its value;",
            "`{ status: \"rejected\", reason }` for one that rejected, with what it rejected with."
          ]
        },
        {
          "p": "The promise `settle` returns always fulfils, even when some of the promises\nreject: reporting the rejections is the point. Compare `Promise.all`, which\nrejects as soon as one of its promises does."
        },
        {
          "code": "await settle([Promise.resolve(1), Promise.reject(new Error(\"no\"))])\n// [{ status: \"fulfilled\", value: 1 }, { status: \"rejected\", reason: <the Error \"no\"> }]",
          "lang": "js"
        },
        {
          "p": "Do not use `Promise.allSettled`. It does the whole job."
        },
        {
          "h": "`parallelLimit(tasks, limit)`"
        },
        {
          "p": "`tasks` is an array of functions, and each one returns a promise when you call\nit. Call them all, but keep at most `limit` of them running at once: when one\nfinishes, start the next. Return a promise of an array of their results, in\nthe same order as `tasks`, not the order they finished in. If any task's\npromise rejects, the promise `parallelLimit` returns rejects with the same\nerror."
        },
        {
          "code": "const slow = (value, ms) => () => new Promise((resolve) => setTimeout(() => resolve(value), ms));\nawait parallelLimit([slow(\"a\", 30), slow(\"b\", 10), slow(\"c\", 20)], 2)\n// [\"a\", \"b\", \"c\"]: \"a\" and \"b\" start together, and \"c\" starts when \"b\" finishes",
          "lang": "js"
        },
        {
          "p": "This is why `tasks` holds functions rather than promises: a promise is work\nthat has already started, so a list of promises would all be running before\n`parallelLimit` could hold any of them back."
        },
        {
          "h": "`debounce(fn, wait)`"
        },
        {
          "p": "Return a new function. Each call to it starts a wait of `wait` milliseconds\nagain. Once `wait` milliseconds pass with no new call, call `fn`, once, with the\narguments of the latest call. A search box is the usual example: search once\nthe person stops typing, not after every key."
        },
        {
          "code": "const save = debounce((text) => console.log(\"saved\", text), 100);\nsave(\"h\"); save(\"he\"); save(\"hello\");\n// 100 ms after the last call: saved hello, and nothing else",
          "lang": "js"
        },
        {
          "p": "The functions this course teaches are enough: a closure can remember which\ncall was the latest, and a timer that finds a newer call has happened since it\nwas started does nothing. `clearTimeout`, which cancels a timer, is another\nway. The course does not teach it, but its documentation is short, and using it\nis fine."
        },
        {
          "h": "Review: `mapAsync`"
        },
        {
          "p": "An AI assistant was asked for `mapAsync(items, fn)`, where `fn` takes an item\nand returns a promise, and `mapAsync` returns a promise of an array of `fn`'s\nresults, in the same order as `items`. Its answer is in\n`src/review/map-async/generated.js`. It runs, and for some inputs it is right.\nYour example may return the promise `mapAsync` returns: the tests wait for it.\n*How a review problem works*, above, says what to hand in."
        }
      ],
      "_source": "assignments/cs140/final-toolkit"
    }
  },
  "builtAt": "2026-10-28"
}